// src/auth/authService.ts
import { AuthError } from "@supabase/supabase-js";

// src/services/supabaseClient.ts
import { createClient } from "@supabase/supabase-js";
var _overrideClient = null;
function setSupabaseClient(client) {
  _overrideClient = client;
}
var _supabase = null;
function getSupabaseClient() {
  if (_overrideClient) {
    return _overrideClient;
  }
  if (_supabase) {
    return _supabase;
  }
  let supabaseUrl = "";
  let supabaseAnonKey = "";
  if (typeof import.meta !== "undefined" && import.meta.env) {
    supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
    supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  }
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      "Supabase \uD658\uACBD \uBCC0\uC218\uAC00 \uC124\uC815\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. VITE_SUPABASE_URL\uACFC VITE_SUPABASE_ANON_KEY\uB97C \uD655\uC778\uD558\uC138\uC694."
    );
  }
  _supabase = createClient(supabaseUrl, supabaseAnonKey);
  return _supabase;
}
var supabase = new Proxy({}, {
  get(_, prop) {
    const client = getSupabaseClient();
    const value = client[prop];
    if (typeof value === "function") {
      return value.bind(client);
    }
    return value;
  }
});

// src/services/apiClient.ts
import ky from "ky";
var ApiClientError = class extends Error {
  constructor(message, statusCode, response) {
    super(message);
    this.statusCode = statusCode;
    this.response = response;
    this.name = "ApiClientError";
  }
};
var getAccessToken = async () => {
  const { data: data2 } = await supabase.auth.getSession();
  return data2.session?.access_token || null;
};
var getBaseUrl = () => {
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    return "http://localhost:3001/api";
  }
  if (typeof window !== "undefined" && typeof window.location !== "undefined") {
    return `${window.location.origin}/backend-api`;
  }
  return "http://localhost:3001/api";
};
var _overrideApiClient = null;
function setApiClient(client) {
  _overrideApiClient = client;
}
function getApiClient() {
  return _overrideApiClient || _defaultApiClient;
}
var _defaultApiClient = ky.create({
  prefixUrl: getBaseUrl(),
  timeout: 3e4,
  hooks: {
    beforeRequest: [
      async (request) => {
        const token = await getAccessToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      }
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        let errorData;
        try {
          errorData = await response.json();
        } catch {
        }
        const message = errorData?.message || response.statusText || "API \uC694\uCCAD \uC2E4\uD328";
        throw new ApiClientError(message, response.status, errorData);
      }
    ]
  }
});
var apiClient = new Proxy({}, {
  get(_, prop) {
    const client = getApiClient();
    const value = client[prop];
    if (typeof value === "function") {
      return value.bind(client);
    }
    return value;
  }
});
var publicApiClient = ky.create({
  prefixUrl: getBaseUrl(),
  timeout: 3e4
});
var createApiClient = (options = {}, config) => {
  const tokenGetter = config?.getAccessToken || getAccessToken;
  const prefixUrl = config?.baseUrl || getBaseUrl();
  return ky.create({
    prefixUrl,
    timeout: 3e4,
    ...options,
    hooks: {
      beforeRequest: [
        async (request) => {
          const token = await tokenGetter();
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`);
          }
        },
        ...options.hooks?.beforeRequest || []
      ],
      beforeError: [
        async (error) => {
          const { response } = error;
          let errorData;
          try {
            errorData = await response.json();
          } catch {
          }
          const message = errorData?.message || response.statusText || "API \uC694\uCCAD \uC2E4\uD328";
          throw new ApiClientError(message, response.status, errorData);
        },
        ...options.hooks?.beforeError || []
      ]
    }
  });
};

// src/translation/i18n.ts
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// src/locales/en-US/common.json
var common_default = {
  metadata: {
    title: "Orblit | AI Coaching & Culture Assessment via 1-on-1s",
    description: "Elevate leadership and manage teams smarter with Orblit. Turn 1-on-1 data into real-time organizational insights and prevent top talent flight risks.",
    keywords: "orblit, 1-on-1s, leadership, coaching, feedback, mentoring, meeting note, talent management, change management, employee growth, workshop",
    app_description: "The most efficient and meaningful 1on1 meetings"
  },
  action: {
    created: "created",
    deleted: "deleted",
    edited: "edited",
    select: "Select",
    back: "Back",
    next: "Next",
    complete: "Done",
    create: "Create",
    delete: "Delete",
    edit: "Edit",
    cancel: "Cancel",
    save: "Save",
    enter: "Enter",
    reject: "Reject",
    rejected: "Rejected",
    collapse: "Collapse",
    expand: "Expand",
    select_all: "Select All",
    unselect_all: "Deselect All",
    skip: "Skip"
  },
  notification: {
    message: "Please wait for a moment...",
    loading: "Loading..."
  },
  error: {
    page: {
      title: "Page not found",
      description: "The page has been deleted or does not exist.",
      back_button: "Go back to previous page",
      home_button: "Go Home",
      error_code: "Error code"
    },
    message: "Error occurred while loading data",
    unknown_error: "An unknown error occurred",
    not_allowed: {
      note: "Sorry, you do not have permission to access this note.",
      project: "Sorry, you do not have permission to access this project.",
      task: "Sorry, you do not have permission to access this task."
    },
    not_found: {
      note: "Note not found.",
      project: "Project not found.",
      task: "Task not found."
    },
    module: {
      loading: "Reloading the module...",
      refresh: "If this screen persists, please refresh."
    },
    refresh: "An error occurred, please refresh.",
    data_fetch: {
      title: "Failed to load the data.",
      message: "Please try again shortly.\nIf the problem persists, please contact Customer Service.",
      retry_button: "Retry"
    },
    toast: {
      query_failed: "Failed to load data",
      mutation_failed: "Failed to process request",
      try_again: "Please try again later"
    }
  },
  filter: {
    title: "Filter",
    reset: "Reset filter",
    assignee: "Assignee",
    not_completed: "Not completed",
    period: {
      title: "Period",
      all: "All period",
      today: "Today",
      yesterday: "Yesterday",
      week: "Week",
      two_week: "Two week",
      month: "Month",
      hide_all: "Hide all"
    }
  },
  placeholder: {
    task: "Add new tasks",
    description: "Add a description ...",
    priority: "No priority",
    status: "Without state"
  },
  logout: "Log out",
  setting: {
    profile: "Profile setting",
    orblit_lab: {
      description: "Settings for new features prepared for launch.",
      title: "Orblit lab"
    },
    view: {
      option: {
        display: "Attribute display",
        done_task: "Completed task",
        grouping: "Grouping",
        grouping_placeholder: "No grouping",
        order: "Order"
      },
      reset: "Reset settings",
      setting_title: "View setting",
      title: "View"
    }
  },
  button: {
    cancel: "Cancel",
    no: "No",
    save: "Save",
    yes: "Yes",
    confirm: "Confirm",
    close: "Close",
    send: "Send"
  },
  date_picker: {
    selected_date: "selected date",
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday",
    until_today: "Today",
    until_tomorrow: "Tomorrow",
    until_yesterday: "Yesterday"
  },
  feature: {
    planner: "Planner",
    drive: "Drive"
  },
  field: {
    due_date: "Due date",
    category: {
      title: "Category",
      work: "Work",
      meeting: "Meeting",
      communication: "Communication",
      study: "Study",
      rest: "Rest",
      etc: "Etc"
    },
    priority: {
      title: "Priority",
      highest: "Highest",
      high: "High",
      medium: "Medium",
      low: "Low",
      lowest: "Lowest"
    },
    status: {
      title: "Status",
      backlog: "Backlog",
      planned: "Planned",
      in_progress: "In Progress",
      at_risk: "At Risk",
      paused: "Paused",
      completed: "Completed",
      canceled: "Canceled"
    },
    register_date: "Created at"
  },
  time: {
    hour: "hour",
    minute: "minute"
  },
  emoji_reaction: {
    add: "Add reaction"
  },
  deletedUser: "Deleted User",
  team_selector: {
    all: "All",
    none: "No parent team",
    include_sub_teams: "Include sub-teams"
  }
};

// src/locales/en-US/task.json
var task_default = {
  hidden_task: "Hidden task",
  no_hidden_task: "There is no hidden task.",
  reveal: {
    title: "Reveal hidden task",
    processing: {
      title: "Revealing hidden task"
    },
    success: {
      message: "Successfully revealed hidden task."
    },
    error: {
      title: "Failed to reveal hidden task",
      message: "An error occurred while revealing hidden task."
    }
  },
  hidden_task_management: "Hidden task management",
  hidden_task_management_description: "You can bring back the hidden task to Inbox",
  selection: {
    none: "No tasks selected",
    some_one: "{{count}} task selected",
    some_other: "{{count}} tasks selected"
  },
  indicator: {
    error: {
      message: "An error occurred during the indicator change processing.",
      message_not_valid: "The target value should be larger than the initial value.",
      title: "Failed to change indicators"
    },
    initial_value: "Initial value",
    placeholder: "No indicator",
    setting: {
      number_title: "Set indicator value",
      reset: "Reset setting",
      title: "Indicator setting"
    },
    target_value: "Target value",
    title: "Indicator",
    current_value: "Current value",
    dollar: "dollar",
    number: "number",
    percent: "percent",
    won: "won",
    type: "Type"
  },
  field: {
    complete_date: "Completed date",
    content: "Content",
    indicator_type: "Indicator type",
    manager: "Manager",
    parent: "Parent task",
    title: "Title",
    participants: "Contributor",
    todo_label: "Completion / Creation",
    due_date: "Deadline"
  },
  delete: {
    description: "Are you sure you want to delete this task? \nThis work cannot be undone.",
    error: {
      message: "An error occurred during the task deletion.",
      title: "Task deletion failed"
    },
    share_description: "When deleted, the task is deleted from the teams sharing this task.",
    success: {
      message: "Task deletion completed."
    },
    title: "Delete task"
  },
  related: {
    hide: "Hide related tasks",
    show: "Show related tasks",
    placeholder: "There is no related task",
    title: "Related tasks"
  },
  subtask: {
    create: "Create a sub task",
    title: "Sub task"
  },
  title: "Task",
  create: {
    grouping: "Add a new task to this group",
    placeholder: "Add a new task",
    placeholder_label: "Add a new task to {{label}}"
  },
  placeholder: {
    manager: "No manager"
  },
  hierarchy: {
    title: "Task hierarchy",
    show: "Show hierarchy"
  },
  participate: {
    cancel: {
      title: "Withdraw contribution",
      button: "Withdraw",
      description1: "You have some Todo connected to this task.",
      description2: "If you withdraw the contribution, these todos will be detached from the task. \nWould you want to proceed?",
      description3: "Do you want to withdraw your contribution in this task?"
    },
    button: "Join"
  },
  hide: {
    description: "Do you want to hide this task in Inbox? \nYour todos under this task will also be hidden. \nYou can restore hidden tasks and todos at any time.",
    error: {
      message: "An error occurred while processing the task hiding.",
      title: "Failed to Hide Task"
    },
    processing: {
      title: "Hiding Task"
    },
    success: {
      title: "Task Hidden Successfully",
      message: "The task and its related todos have been successfully hidden."
    },
    title: "Task"
  },
  share: {
    error: {
      message: "An error occurred while changing the task sharing settings.",
      title: "Failed to Change Task Sharing Settings"
    },
    subtask: {
      message: "Do you want to share the subtasks as well?",
      title: "Share Subtasks",
      error: "An error occurred while changing the subtask sharing settings."
    },
    success: {
      message_share: "Task successfully shared with the {{teamName}} team.",
      message_unshare: "Task successfully unshared from the {{teamName}} team.",
      title: "Task Sharing Settings Changed"
    }
  }
};

// src/locales/en-US/project.json
var project_default = {
  placeholder: "No project",
  title: "Project",
  share: {
    error: {
      message: "An error occurred while changing the project sharing settings.",
      title: "Failed to Change Project Sharing Settings"
    },
    message: "Do you want to share the project as well?",
    title: "Project Sharing",
    success: {
      message_share: "The project successfully shared with the {{teamName}} team.",
      message_unshare: "The project successfully unshared from the {{teamName}} team.",
      title: "Project Sharing Settings Changed"
    }
  },
  detail: {
    tabs: {
      overview: "Project Overview",
      tasks: "Related Tasks"
    },
    delete: {
      button: "Delete Project",
      title: "Delete Project",
      confirmation: "Are you sure you want to delete this project? This action cannot be undone.",
      cancel: "Cancel",
      confirm: "Delete",
      success: "Project was successfully deleted.",
      error: "An error occurred while deleting the project."
    },
    info: {
      title: "Project Information",
      team: "Participating Team",
      participants: "Participants",
      add_participants: "Add participant",
      cancel_participate: "Withdraw",
      participate: "Join",
      cancel_participate_message: "You have Todos connected to Tasks in this Project.\nIf you withdraw from the Project, your participation in those Tasks will also be cancelled,\nand the connected Todos will be detached from the Project and Tasks.\nWould you like to proceed?"
    },
    description: {
      placeholder: "Add a description..."
    },
    name: {
      edit_error: "Project name edit error"
    },
    title: "Project Detail"
  },
  table: {
    add: "Add new project",
    columns: {
      status: "Status",
      name: "Project Name",
      deadline: "Deadline",
      priority: "Priority",
      leader: "Project Manager",
      register_date: "Created at"
    }
  },
  relatedTasks: {
    title: "Related Tasks"
  },
  filters: {
    status: "Status",
    priority: "Priority",
    leader: "Project Manager",
    noName: "No Name"
  },
  error: {
    no_access: "You do not have access to this project.",
    not_found: "Project not found.",
    load_failed: "Failed to load projects."
  },
  list: {
    select_team: "Please select a team."
  },
  create_modal: {
    title: "Create Project",
    name: {
      label: "Project Name",
      placeholder: "Enter project name"
    },
    team: {
      label: "Select Lead Team",
      placeholder: "Select lead team",
      self: " (Lead team)"
    },
    participants: {
      label: "Select Participants",
      placeholder: "Select participants",
      self: " (Me)"
    },
    notification: {
      in_progress: "Creating project",
      success: {
        title: "Project created successfully",
        message: "The project was successfully created."
      },
      error: {
        title: "Failed to create project",
        message: "Failed to create project. Please try again."
      }
    },
    form: {
      validation: {
        name: {
          required: "Project name is required."
        },
        team: {
          required: "Please select a team."
        }
      }
    }
  }
};

// src/locales/en-US/team.json
var team_default = {
  placeholder: "No team",
  notification: {
    message: "At least one team must be selected.",
    title: "Team selection needed"
  },
  selection: {
    some_one: "{{count}} team",
    some_other: "{{count}} teams"
  },
  title: "Team",
  team_required: "Please select the team.",
  member: {
    title: "Member",
    add_dialog: {
      title: "Invite Members",
      search_placeholder: "Search workspace members or enter email address.",
      workspace_members: "Workspace Members",
      invite_button: "Invite",
      invite_selected: "Invite Selected Members",
      no_members: "No workspace members available to invite.",
      invite_by_email: "Invite new members by email."
    },
    notifications: {
      invalid_email: "Invalid Email",
      enter_valid_email: "Please enter a valid email address.",
      invite_not_possible: "Invitation Not Possible",
      already_member: "{{email}} is already registered as a member.",
      creation_failed: "Failed to create invitation.",
      invite_failed: "Invitation Failed",
      email_error: "An error occurred while sending the email.",
      invite_success: "Invitation Successful",
      email_sent: "Invitation email sent to {{email}}.",
      invite_error: "An error occurred while inviting the member.",
      selection_needed: "Member Selection Required",
      select_members: "Please select members to add to the team.",
      add_success: "Members Added Successfully",
      members_added: "{{count}} members have been added to the team.",
      add_failed: "Failed to Add Members",
      add_error: "An error occurred while adding team members.",
      request_plan: "Request Planner Update",
      request_plan_success: "Planner update request sent to the member.",
      request_plan_failed: "Failed to send planner update request.",
      request_plan_error: "Error occurred while sending planner update request."
    },
    placeholders: {
      team: "Team",
      workspace: "Workspace",
      inviter: "Inviter"
    },
    day_overview: {
      current_todo: "Current Task",
      no_tasks_on_date: "No tasks for the selected date."
    },
    task_list: {
      member_tasks: "{{name}}'s Tasks",
      managing_tasks: "Managing Tasks",
      participating_tasks: "Participating Tasks"
    },
    task_view_settings: {
      view: "View",
      view_settings: "View Settings",
      completed_tasks: "Completed Tasks",
      show_backlog_tasks: "Show Backlog Tasks",
      show_paused_tasks: "Show Paused Tasks",
      display_properties: "Display Properties",
      reset_settings: "Reset Settings",
      column: {
        status: "Status",
        priority: "Priority",
        project: "Project",
        current_value: "Metrics"
      }
    },
    todo_list: {
      member_todos: "{{name}}'s To-do",
      remaining_todos: "Remaining To-do",
      recently_completed_todos: "Recently Completed To-do",
      tasks_without_task: "To-do without Tasks",
      tasks_by_task: "To-do Grouped by Tasks",
      load_more: "Load More",
      no_schedule: "No Schedule"
    },
    pages: {
      detail: {
        team_member: "Team Member",
        non_existent_team: "The team does not exist.",
        non_existent_member: "The member does not exist."
      },
      team: {
        team_members: "Team Members",
        member_count: "{{count}} members",
        no_todos_on_date: "No To-do for the selected date.",
        todo_count: "{{count}} To-do",
        request_plan: "Request Planner Update",
        request_plan_message: "<0>{{name}}</0><1> would you like to send a planner creation request notification?</1>",
        daily_scrum: "Daily Scrum"
      }
    }
  },
  create_dialog: {
    title: "Create New Team",
    workspace: "Workspace",
    select_workspace: "Select a workspace.",
    workspace_required: "Please select a workspace.",
    parent_team: "Parent Team",
    select_parent_team: "Select a parent team.",
    team_name: "Team Name",
    enter_team_name: "Enter team name.",
    name_error: "Team name must be at least 2 characters.",
    team_description: "Team Description",
    enter_team_description: "Enter description about the team.",
    cancel: "Cancel",
    create_team: "Create Team",
    success_title: "Team Created",
    success_message: "Team {{name}} has been successfully created.",
    error_title: "Team Creation Failed",
    error_message: "An error occurred while creating the team. Please try again.\n{{error}}"
  },
  update_dialog: {
    title: "Update Team",
    save: "Save",
    success_title: "Team Updated",
    success_message: "Team {{name}} has been successfully updated.",
    error_title: "Team Update Failed",
    error_message: "An error occurred while updating the team. Please try again.\n{{error}}"
  },
  delete_dialog: {
    title: "Confirm Team Deletion",
    message: "Are you sure you want to delete this team?",
    warning: "This action cannot be undone and will permanently delete all projects and tasks in this team.",
    hierarchy_guide: "Sub-teams will not be deleted and will be automatically linked to the parent team.",
    confirm: "Delete",
    cancel: "Cancel",
    error: "Team deletion failed:",
    success: "Team Deleted",
    success_message: "The team has been successfully deleted.",
    failed: "Team Deletion Failed",
    failed_message: "An error occurred while deleting the team. Please try again."
  },
  main_title: {
    clear_input: "Clear input"
  },
  item: {
    task: "Tasks",
    project: "Projects",
    member: "Members"
  },
  member_table: {
    cancel_invitation: "Cancel Invitation",
    invited_status: "INVITED",
    delete_member_failed: "Team member deletion failed:",
    role_change_failed: "Member role change failed:",
    add_member: "Add Member",
    leave_team: "Leave Team",
    remove_from_team: "Remove from Team",
    manage_members: "Manage Members",
    invite_member: "Invite Member",
    member: "Member",
    email: "Email",
    workspace_permission: "Workspace Permission",
    no_available_members: "No members to show",
    add_member_count: "Add Members ({{count}})",
    current_member_count: "Current Members ({{count}})",
    delete_member_count: "Remove from Team ({{count}})",
    member_list: "Workspace Members",
    placeholder: "Enter the user's name or email address."
  },
  setting_page: {
    access_denied: "Only team members can access this page.",
    delete_team: "Delete Team",
    delete_description: "Deleting a team does not remove personal to-dos and records created by members.",
    delete_tooltip: "Are you sure you want to delete? \u{1F97A}"
  }
};

// src/locales/en-US/todo.json
var todo_default = {
  title: "To-do",
  inbox: "Inbox",
  now: {
    placeholder: "No To-do now"
  },
  private_todo_title: "Private To-do",
  status: {
    cancelled: "Stop",
    completed: "Completed",
    in_progress: "In progress"
  },
  notification: {
    cancel_action: "Stop",
    resume_action: "Resume",
    processing_title: "Processing To-do {{action}}",
    processing_message: "Please wait...",
    success_title: "To-do {{action}} Complete",
    success_message: "The selected To-do has been {{action}}ed.",
    error_title: "Failed to {{action}} To-do",
    error_message: "An error occurred while processing the {{action}}"
  },
  component: {
    inbox: {
      hidden: "Hidden",
      hidden_goals: "Hidden Tasks",
      hidden_goals_count: "Hidden Tasks ({{count}})",
      view: "View"
    },
    todo_textfield: {
      adding: "Adding...",
      add_todo: "Add To-do"
    },
    parent_goal_label: {
      hide: "Hide",
      due_date: "Deadline",
      add_todo: "Add To-do"
    },
    set_todo_display_menu: {
      view_settings: "View Settings",
      show_goals: "Show Tasks",
      show_empty_goals: "Show empty Tasks",
      reset_settings: "Reset settings",
      set_as_default: "Set as default",
      notifications: {
        saving_title: "Saving view settings",
        saved_title: "View settings saved",
        saved_message: "View settings have been saved.",
        save_failed_title: "Failed to save view settings",
        save_failed_message: "An error occurred while saving view settings."
      }
    },
    todo_action_menu: {
      resume: "Resume",
      remove_plan: "Cancel the plan",
      delete_todo: "Delete To-do",
      notifications: {
        removing_plan_title: "Canceling the plan",
        removed_plan_title: "Plan canceled",
        removed_plan_message: "Successfully canceled the plan.",
        remove_plan_failed_title: "Failed to cancel the plan",
        remove_plan_failed_message: "An error occurred while canceling the plan.",
        deleting_todo_title: "Deleting To-do",
        deleted_todo_title: "Deleted To-do",
        deleted_todo_message: "To-do has been successfully deleted.",
        delete_todo_failed_title: "Failed to delete To-do",
        delete_todo_failed_message: "An error occurred while deleting To-do."
      }
    },
    todo_checkbox: {
      complete: "Complete"
    },
    activity: "Activity",
    cancel_checkbox: {
      resume: "Resume",
      cancel: "Stop",
      aria_resume: "Resume To-do",
      aria_cancel: "Cancel To-do"
    },
    confirm_modal: {
      actions: {
        cancel: "Stop",
        remove_planner: "Cancel Plan",
        delete: "Delete All"
      },
      details: {
        cancel: "Choose this option to stop working on this To-do.\nIt will no longer appear in your planner after the selected date.",
        remove_planner: "This only cancels the plan for the selected date, keeping plans for other dates.\nIf today is the only planned date, it will remain in your inbox.",
        delete: "This will remove plans for all dates."
      },
      descriptions: {
        cancel_1: "Would you like to stop working on this?",
        cancel_2: "<0>It will only appear in your planner until\xA0</0><1>{{date}}</1><0>.</0>",
        remove_planner_1: "<0>This will only cancel the To-do plan for\xA0</0><1>{{date}}</1>.<0>Plans for other dates will remain intact.</0>",
        remove_planner_2: "To delete completely, use 'Delete All' option.",
        delete_1: "This will completely delete the To-do and all its scheduled plans from all dates.",
        delete_2: "<0>To cancel only the plan for\xA0</0><1>{{date}}</1><0>\xA0that you selected,</0><0>\xA0select 'Cancel Plan'.</0>"
      },
      other: {
        info_tooltip: "About Orblit's To-do deletion options",
        current_option: "Current selection"
      }
    },
    todoDrawer: {
      not_found_modal: {
        title: "To-do Deleted",
        message: "This To-do has been deleted.",
        message_with_title: "{{title}} has been deleted.",
        confirm: "OK"
      },
      popover: {
        notes: "Notes",
        comments: "Comments ({{count}})",
        view_detail: "View to-do details"
      },
      detail: {
        add_date: "Add date"
      },
      drawer: {
        toggle_visibility: "Change to {{status}}",
        private: "private",
        public: "public"
      },
      planner_list: {
        set_time: "Set time",
        delete_time: "Delete time"
      },
      content: {
        related_goal: "Related task",
        date_and_time: "Date and time",
        todo_title_placeholder: "Enter To-do title"
      },
      category_menu: {
        change_category: "Change category",
        loading: "Loading..."
      },
      due_date_badge: {
        set_due_date: "Set due date"
      },
      goal_selector: {
        connect_goal: "Connect task",
        view_goal_details: "View task details",
        please_select_goal: "Please select a task",
        no_goals: "No tasks available",
        loading_goals: "Loading tasks...",
        go_back: "Go back",
        view_all_goals: "View all tasks",
        actions: {
          connect: "connect",
          disconnect: "disconnect"
        },
        notifications: {
          processing_title: "Processing task {{action}}",
          success_title: "Task {{action}} complete",
          success_message: "Successfully {{action}}ed with the selected task.",
          error_title: "Task {{action}} failed",
          error_message: "An error occurred while processing task {{action}}"
        }
      }
    }
  }
};

// src/locales/en-US/report.json
var report_default = {
  title: "report",
  type: {
    annual: "Annual report",
    custom: "Custom Report",
    daily: "Daily report",
    monthly: "Monthly report",
    quarterly: "Quarterly report",
    weekly: "Weekly report"
  },
  list: {
    title: "Report List",
    sent: "Sent Reports",
    received: "Received Reports",
    noSentReports: "No sent reports.",
    noReceivedReports: "No received reports.",
    createNew: "Create a new report.",
    columns: {
      title: "Title",
      sender: "Sender",
      receiver: "Receiver",
      date: "Date",
      team: "Team"
    },
    unknownUser: "Unknown",
    draft: "Draft",
    noTitle: "No title",
    unknown: "Unknown",
    markAsRead: "Mark as read",
    markAsReadComplete: "Marked as read",
    markAsReadSuccess: "{{count}} reports marked as read.",
    noUnreadReports: "No unread reports.",
    markAsReadError: "Error marking reports as read.",
    loading: "Loading reports...",
    error: "An error occurred while loading reports."
  },
  actions: {
    create: "Create Report",
    edit: "Edit Report",
    delete: "Delete Report",
    send: "Send Report",
    save: "Save Report",
    saveDraft: "Save as Draft",
    confirmDelete: "Are you sure you want to delete this report?",
    cancelGeneration: "Cancel Generation"
  },
  sendReport: {
    button: "Send Report",
    title: "Select Report Recipients",
    selectAll: "Select All",
    noName: "No Name",
    loadingMembers: "Loading members...",
    noMembers: "No members available to send to.",
    cancel: "Cancel",
    send: "Send",
    notifications: {
      selectRecipients: "Select Recipients",
      selectRecipientsMessage: "Please select at least one member to send the report to.",
      sendComplete: "Report Sent",
      newRecipients: "Report sent to {{count}} new recipients.",
      updatedRecipients: "Report updated for {{count}} existing recipients.",
      newAndUpdatedRecipients: "Report sent to {{count}} new recipients and updated for {{updateCount}} existing recipients.",
      sendFailed: "Send Failed",
      sendFailedMessage: "An error occurred while sending the report. Please try again."
    }
  },
  notifications: {
    title: "Notification",
    success: "Success",
    error: "Error"
  },
  reportPage: {
    newReport: "New Report",
    loading: "Loading report...",
    loadingData: "Loading report data...",
    error: {
      loading: "An error occurred while loading the report.",
      unknown: "An unknown error occurred.",
      notFound: "Report not found.",
      notFoundDescription: "The requested report has been deleted or does not exist."
    },
    backToList: "Back to Report List",
    createNewReport: "Create New Report",
    createNewReportDescription: "A form to create a new report will be added here.",
    editing: "Editing...",
    edit: "Edit",
    editComplete: "Complete Editing",
    exportToPdf: "Export to PDF",
    deleteReport: "Delete Report",
    createdDate: "Created Date",
    noCreatedDate: "No creation date information",
    updatedDate: "Updated Date",
    editorArea: "Editor Area",
    emptyState: {
      title: "Please select a report",
      description: "Select a report from the list on the left to view its details."
    },
    translate: "Translate",
    aiTranslation: "AI Translation",
    deleteModal: {
      title: "Confirm Report Deletion",
      confirmMessage: "Are you sure you want to delete this report? Deleted reports will not be displayed in your list, but other users can still view them.",
      successMessage: "Report deleted successfully.",
      errorMessage: "Failed to delete report. Please try again.",
      error: "An error occurred while deleting the report."
    },
    notifications: {
      titleSaveComplete: "Title Save Complete",
      titleSaveMessage: "Report title has been successfully saved.",
      saveFailed: "Save Failed",
      titleSaveErrorMessage: "An error occurred while saving the title. Please try again.",
      reportSaveComplete: "Report Save Complete",
      reportSaveMessage: "Report has been successfully saved.",
      reportSaveErrorMessage: "An error occurred while saving the report. Please try again.",
      exportFailed: "Export Failed",
      exportErrorMessage: "An error occurred. Please try again later."
    }
  },
  common: {
    cancel: "Cancel",
    delete: "Delete",
    save: "Save",
    edit: "Edit",
    error: "Error",
    alert: "Alert",
    success: "Success",
    team: "Team"
  },
  personalReport: {
    title: "Personal Report"
  },
  teamReport: {
    title: "Team Report",
    errors: {
      requiredDataMissing: "Required data is missing.",
      cannotFetchTeamData: "Cannot fetch team report data.",
      teamReportGenerationFailed: "Team report generation failed.",
      teamReportError: "An error occurred while generating the team report."
    },
    notifications: {
      teamReportCancelled: "Team report generation cancelled",
      teamReportCancelledMessage: "Report generation was cancelled by user request.",
      teamReportGenerating: "Generating team report",
      teamReportGeneratingMessage: "Generating {{teamName}} daily report...",
      teamReportFailed: "Team report generation failed",
      teamReportFailedMessage: "Failed to generate team report.",
      teamReportCompleted: "Team report generation completed",
      teamReportCompletedMessage: "{{teamName}} daily report has been generated. Click to view.",
      teamReportSaveFailed: "Failed to save team report",
      teamReportSaveFailedMessage: "An error occurred while saving the report.",
      teamReportErrorMessage: "An error occurred while generating the team report.",
      teamReportCompleteTitle: "\u{1F4CA} Team Report Completed",
      teamReportCompleteBody: "{{teamName}} daily report has been generated.\nClick to view.",
      teamReportCompleteBodyAlt: "Check the daily report for {{teamName}} team."
    },
    reportTitle: {
      teamDaily: "{{teamName}} - {{date}} Daily Report"
    }
  },
  writePage: {
    reportTitleDaily: "Daily Report",
    reportTitleWeekly: "Weekly Report",
    titlePlaceholder: "Enter title",
    saveButton: "Save",
    aiGenerateButton: "Generate with AI",
    regenerateButton: "Regenerate",
    generatingReport: "Generating report...",
    aiHelpText: "Please review the AI-generated report and edit as needed.",
    teamLoading: "Loading team info...",
    notifications: {
      userOrWorkspaceError: "Unable to verify user or workspace information.",
      titleRequired: "Please enter a title.",
      createSuccess: "Report created successfully.",
      createError: "Error creating report."
    },
    teamReportModal: {
      title: "Generate Team Report",
      description1: "Team reports are",
      carefullyWritten: "carefully written",
      description2: " by aggregating team members' activities, which may take",
      timeRequired: "3-5 minutes",
      description3: ".",
      notificationInfo1: "When the report is ready, we'll notify you via the",
      notificationLocation: "notification icon in the top right",
      notificationInfo2: ".",
      tip: "\u{1F4A1} You can continue working on other tasks while the team report is being generated.",
      cancel: "Cancel",
      confirm: "Start Generation"
    }
  },
  creationModal: {
    createTitle: "Create New Report",
    editTitle: "Edit Type and Date",
    description: "Select report type and click Generate Report button.",
    teamDescription: "Select report type and team, then click Generate Report button.",
    selectTeam: "Select Team",
    reportType: "Report Type",
    date: "Date"
  },
  languages: {
    english: "English",
    korean: "Korean",
    vietnamese: "Vietnamese",
    japanese: "Japanese",
    chinese: "Chinese"
  },
  search: {
    placeholder: "Search report title"
  },
  weekdays: {
    sunday: "Sun",
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat"
  },
  categories: {
    work: "Work",
    meeting: "Meeting",
    communication: "Communication",
    study: "Study",
    rest: "Rest",
    other: "Other"
  },
  errors: {
    fetchSourceDataFailed: "Failed to fetch source data",
    reportNotFound: "Report not found",
    addReceiverError: "Error adding report receiver",
    markAsReadFailed: "Failed to mark as read",
    teamInfoFetchFailed: "Failed to fetch team information",
    teamMembersFetchFailed: "Failed to fetch team members",
    plannerDataFetchFailed: "Failed to fetch planner data",
    goalDataFetchFailed: "Failed to fetch goal data",
    noteDataFetchFailed: "Failed to fetch note data",
    commentDataFetchFailed: "Failed to fetch comment data",
    reportGenerationAborted: "Report generation was aborted",
    backendError: "Backend error",
    dataQueryFailed: "Data query failed"
  },
  messages: {
    readProcessOptimisticStart: "Starting optimistic update for marking as read",
    readProcessOptimisticComplete: "Optimistic update completed for marking as read",
    readProcessFailed: "Failed to mark as read: Rolling back to previous state",
    readProcessSuccess: "Successfully marked as read",
    queryInvalidationComplete: "Query invalidation completed after marking as read",
    noTeamMembers: "No team members",
    reportGenerationCancelled: "Report generation cancelled",
    selectedDate: "Selected date",
    userQuery: "User query",
    dataCount: "Data count",
    reportType: "Report type",
    ownerType: "Owner type",
    requestData: "Request data",
    receivedChunk: "Received chunk",
    finalReportLength: "Final report length",
    characters: "characters"
  }
};

// src/locales/en-US/auth.json
var auth_default = {
  agreement: {
    advertising: "Consent to Receive Advertising Information",
    age: "I am 14 years of age or older",
    description: "You can use the service even if you refuse to agree with the options.",
    detail: "Details",
    label: "I agree to the entire terms and conditions (Including the optional information)",
    law_description: "In order to protect the personal information and rights of the information subjects,\nwe manage the Personal Information Protection Act and the relevant laws and regulations. \nFor more information, please visit the Privacy Policy.",
    marketing: "Consent to Promotional and Marketing Use",
    optional: "Optional",
    overseas: "Consent to Provision of Personal Information to Third Parties (Overseas Transfer)",
    personalInfo: "Consent to Collection and Use of Personal Information",
    privacy: "Consent to the Privacy Policy",
    recording: "Consent to Recording of Meeting Content",
    required: "Required",
    service: "Terms of Service",
    thirdParty: "Consent to Provision of Personal Information to Third Parties",
    title: "Agreement"
  },
  auth_callback: {
    confirm: {
      button: "Continue",
      description: "For your security, please click the button below to continue resetting your password.",
      title: "Continue password reset"
    },
    mobile: {
      error: {
        message: "There is a problem with the authentication link. Please request a new one.",
        title: "Authentication error"
      }
    },
    notification: {
      error: {
        message: "An error occurred while processing authentication. Please try again.",
        title: "Authentication error"
      },
      stale_token: {
        message: "The password reset link has expired or has already been used. Please request a new one.",
        title: "Reset link expired"
      }
    }
  },
  change_password: {
    action_title: "Change password",
    button: "Change password",
    new_password: {
      label: "New password",
      placeholder: "Please enter a new password"
    },
    notification: {
      error: "An error occurred during the password change.",
      success: {
        message: "The password has been successfully changed.",
        title: "Password change completed"
      }
    },
    title: "Password change"
  },
  delete_account: {
    action_title: "Delete account",
    description: "If you're a member of a team, todos created while you're a member of the team won't be deleted.",
    modal: {
      message_1: "Are you sure you want to delete your account? \nThis action cannot be undone.",
      message_2: "If you delete your account, your personal information and activity details will be deleted. \nHowever, todos created while you're a member of the team are not deleted."
    },
    notification: {
      error: {
        has_blocking_workspace: "You have workspace(s) with Owner privileges ({{workspaceNames}}). Please transfer ownership first.",
        message: "An error occurred during the account deletion.",
        session: "The authentication session cannot be found. \nPlease log in again.",
        title: "Account deletion failed"
      },
      success: {
        message: "The account has been deleted successfully.",
        title: "Account deletion completed"
      }
    },
    title: "Delete account",
    tooltip: "Are you sure you want to delete your account? \u{1F97A}"
  },
  forgot_password: {
    back_to_login: "Back to login",
    button: "Send reset link",
    description: "Enter the email you used to sign up and we'll send you a password reset link.",
    email_sent: {
      help: "Didn't receive it? Check your spam folder or try again.",
      message: "We've sent a reset link to {{email}}.",
      title: "Check your email"
    },
    notification: {
      error: "An error occurred while sending the password reset email.",
      success: {
        message: "A password reset link has been sent to your email. Please check your inbox.",
        title: "Email sent"
      }
    },
    resend: "Resend",
    resend_in: "Resend in {{seconds}}s",
    title: "Forgot password"
  },
  google_calendar: {
    account_mismatch: {
      attempted_label: "Selected account",
      close: "Close",
      existing_label: "Currently connected",
      go_to_settings: "Go to Settings",
      guide: "Please retry with the same account, or disconnect first in Settings before connecting a different account.",
      title: "Different Google Account"
    },
    button: {
      connect: "Connect",
      disconnect: "Disconnect"
    },
    connected_account: "Connected with {{email}} account",
    connected_date: "Connected on",
    description: {
      connected: "Syncs calendar schedules and video meeting recordings",
      disconnected: "Connect Google to sync calendars and use video meetings",
      expired: "Connection has expired. Please reconnect."
    },
    notification: {
      connect_error: {
        message: "Failed to connect Google.",
        title: "Connection Failed"
      },
      connect_success: {
        message: "Google has been successfully connected.",
        title: "Connection Successful"
      },
      disconnect_error: {
        message: "Failed to disconnect.",
        title: "Error"
      },
      disconnect_success: {
        message: "Google has been disconnected.",
        title: "Disconnected"
      },
      login_error: {
        message: "Google login failed.",
        title: "Login Failed"
      },
      token_expired: {
        message: "Google connection has expired. Please reconnect in settings.",
        title: "Google Connection Expired"
      },
      user_not_found: {
        message: "User information not found.",
        title: "Error"
      }
    },
    status: {
      connected: "Connected",
      reconnect_required: "Reconnect Required"
    },
    title: "Google Integration"
  },
  invitation_list: {
    create_workspace: "Create your own workspace",
    description: "Check your invitations and join workspaces.",
    no_invite: "No new invitations",
    register: "Invitation from {{name}}",
    title: "You have {{count}} invitation(s)"
  },
  logout: {
    description: "Are you sure you want to log out?"
  },
  no_access: {
    description: "Please request 1on1 module activation from the workspace Owner or Admin.",
    title: "You don't have permission to access the service."
  },
  no_permission: {
    dashboard: "Only Owner, Admin of the workspace can view the dashboard.",
    meeting: "Only meeting participants can view 1on1 meeting records.",
    title: "You don't have permission to access this page."
  },
  no_workspace: {
    create_button: "Create Workspace",
    description: "Please create a workspace first to use 1on1.",
    description_mobile: "Please request an invitation from your workspace Admin.",
    title: "Workspace required.",
    title_mobile: "No invited workspace found."
  },
  reset_password: {
    button: "Reset password",
    description: "Please enter your new password.",
    notification: {
      error: "An error occurred while resetting the password.",
      success: {
        message: "Your password has been successfully changed. Please log in.",
        title: "Password reset complete"
      }
    },
    title: "Reset password"
  },
  sign_in: {
    action_title: "Log in",
    no_account: {
      action_title: "Create an account",
      title: "Don't have an account?"
    },
    notification: {
      error: "Login failed",
      invitation: {
        message: "You have a new workspace invitation. Click to view.",
        title: "Workspace Invitation"
      },
      success: {
        message: "Welcome!",
        title: "Successfully logged in"
      }
    },
    redirect_to_mobile: "Redirecting to app...",
    title: "Log in"
  },
  sign_up: {
    action_title: "Sign up",
    step_1: "Agreement",
    step_2: "Information input",
    step_3: "Completed",
    title: "Sign up"
  },
  sign_up_complete: {
    description: {
      first: "We've sent verification mail to the email you entered.",
      second: "You can use the service when the authentication is completed by clicking the link included in the email.",
      third: "If you do not receive the verification email, please check your spam or trash folder."
    },
    title: "Please check your email for authentication."
  },
  sign_up_confirm_email: {
    cancel: "Edit",
    check: "Please make sure your email address is correct.",
    confirm: "Sign up with this address",
    description: "This address will be used for login and a verification email will be sent.",
    title: "Check your email address"
  },
  sign_up_form: {
    email_resend: "Resend verification email",
    email_resending: "Resending...",
    email_resent: {
      message: "Verification email has been resent. Please check your inbox.",
      title: "Verification Email Resent"
    },
    error: {
      email: "Please enter an email.",
      email_exists: "It is an email that already exists.",
      email_exists_message: "Please enter the email again.",
      email_invalid: "It's not the right email format.",
      email_not_confirmed: "The email authentication has not been completed. \nPlease check the email.",
      email_personal: "Orblit is used at the organization level. We recommend signing up with your work email for a smoother transition when your company subscribes.",
      email_personal_contact: "Contact us",
      email_personal_help: "If you don't have a work email, or if your work email is being blocked, please let our team know.",
      invalid_login_credentials: "The email or password is not correct.",
      login: "Login error",
      logout: "There was an error during the logout.",
      password_confirm_invalid: "The password does not match.",
      password_invalid: "The password must be at least 6 characters.",
      same_password: "The new password is the same as the existing password.",
      title: "Error occurs while registering",
      too_many_requests: "There were too many login attempts. \nPlease try again after a while.",
      update_failed: "Failed to update user information."
    },
    helper: {
      email: "Orblit is used at the organization level, so we only accept work email signups."
    },
    label: {
      confirmPassword: "Verify password",
      email: "Email",
      name: "Name",
      password: "Password"
    },
    placeholder: {
      company_email: "Enter your company email address",
      confirmPassword: "Enter your password again",
      email: "Enter your email address",
      name: "Alex",
      password: "Enter your password"
    },
    required: "* The item in the display is required to enter.",
    title: "Membership information input"
  },
  signup_inquiry: {
    action: {
      cancel: "Cancel",
      submit: "Send"
    },
    complete: {
      confirm: "OK",
      description: "We'll review and get back to you shortly. Thank you.",
      title: "Your message was sent"
    },
    description: "Tell us if you don't have a work email or your work email was blocked. We'll get back to you within 2 business days.",
    error: {
      email_invalid: "Please enter a valid email address.",
      submit_failed: "Failed to send. Please try again in a moment."
    },
    field: {
      company: "Company",
      email: "Email",
      message: "Message",
      situation: "What's your situation?"
    },
    placeholder: {
      company: "Your company name (optional)",
      email: "example@company.com",
      message: "Anything you'd like us to know (optional)"
    },
    situation_option: {
      blocked_company_email: "My work email is being blocked",
      etc: "Other",
      personal_email: "I'd like to sign up with a personal email"
    },
    title: "Contact us"
  },
  user_setting: {
    general: "General",
    integration: "Integration",
    label: {
      email: "Email",
      language: "Language",
      mbti: "MBTI",
      name: "Name",
      oneonone_email: "Email notifications",
      password: "Password",
      phone: "Phone number",
      planning_time: "Planning Reminder",
      set_days: "Set Days",
      set_time: "Set Time",
      timezone: "Timezone"
    },
    description: {
      oneonone_email: "Receive emails for 1on1 meeting-related notifications."
    },
    notification: "\bNotification",
    notification_message: {
      error: {
        message: "There was a problem saving your profile information. Please try again.",
        title: "Update Failed"
      },
      name_error: "Name cannot be empty. Please enter your name.",
      planning_time: {
        error: {
          message: "An error occurred while updating your planning reminder.",
          title: "Failed to Update Planning Reminder"
        },
        inProgress: "Updating Planning Reminder",
        success: {
          message: "Your planning reminder has been successfully updated.",
          title: "Planning Reminder Updated"
        }
      },
      success: {
        message: "Your changes have been saved successfully.",
        title: "Profile Updated"
      },
      title: "Update Profile"
    },
    placeholder: {
      email: "Please enter your email",
      language: "Please select your language",
      mbti: "Please select your MBTI",
      name: "Please enter your name",
      phone: "Please enter your phone number"
    },
    profile_card: {
      edit: "Edit",
      title: "Profile Card"
    },
    profile_image: "Profile image",
    push_notifications: {
      go_to_settings: "Please allow notification permissions in your device settings to receive notifications.",
      go_to_settings_title: "Notification Permission Required",
      title: "Push Notifications"
    },
    title: "Profile"
  },
  onboarding: {
    logout: "Log out",
    back: "Back",
    intent: {
      title: "Welcome, {{name}}",
      title_fallback: "Welcome",
      subtitle: "Tell us where you're starting from, and we'll set things up for you.",
      option_leader: {
        title: "I want to set up 1on1s for my team",
        description: "Create a workspace for your team"
      },
      option_member: {
        title: "I'm joining with an invitation",
        description: "Join an existing team"
      }
    },
    workspace_name: {
      title: "What's your workspace name?",
      subtitle: "We recommend your team, organization, or company name.",
      placeholder: "Team or organization name",
      helper_changeable: "You can change this later in workspace settings.",
      helper_solo: "You can try it out on your own before inviting teammates",
      cta: "Create workspace",
      error_required: "Please enter a workspace name",
      error_create_failed: "Failed to create workspace. Please try again."
    },
    member_waiting: {
      title: "Waiting for your invitation",
      body: "We'll email you when your workspace invitation arrives.",
      signup_email_label: "Your sign-up email",
      actually_leader_question: "Want to create a workspace and explore?",
      actually_leader_cta: "Create workspace"
    }
  }
};

// src/locales/en-US/comment.json
var comment_default = {
  comment: {
    header_text: "left a comment.",
    placeholder: "Please leave a comment",
    submit: "Submit",
    no_comments: "No comments yet. Be the first to leave a comment!",
    no_user: "No members to mention"
  },
  reply: {
    add: "Add reply",
    header_text: "left a reply.",
    placeholder: "Please leave a reply",
    title: "Reply"
  },
  notifications: {
    comment_success_title: "Comment Created",
    comment_success_message: "Your comment has been successfully posted.",
    comment_error_title: "Comment Failed",
    comment_error_message: "An error occurred while posting your comment."
  },
  modal: {
    delete: {
      message: "Are you sure you want to delete this comment?",
      reply_message: "Are you sure you want to delete this comment?\nThe replies in the comment will also be deleted.",
      title: "Delete comment"
    }
  }
};

// src/locales/en-US/note.json
var note_default = {
  title: "Note",
  create: {
    title: "Create Note",
    in_new_window: "Create in New Window",
    success: "Note Creation Successful",
    success_message: "A new note has been created.",
    fail: "Note Creation Failed",
    fail_message: "Failed to create note. Please try again."
  },
  actions: {
    menu: "Note Menu",
    export_pdf: "Export to PDF",
    delete: "Delete",
    delete_note: "Delete Note",
    delete_title: "Delete Note",
    delete_confirm: "Are you sure you want to delete this note?",
    cancel: "Cancel",
    open_new_window: "Open in New Window",
    notifications: {
      delete_success: "Note Deleted",
      delete_success_message: "The note has been successfully deleted.",
      delete_fail: "Note Deletion Failed",
      delete_fail_message: "An error occurred while deleting the note. Please try again.",
      export_fail: "Export Failed",
      export_fail_message: "Could not find editor instance."
    }
  },
  pdf: {
    errors: {
      no_editor: "No editor instance available.",
      no_method: "Could not find blocksToFullHTML method.",
      unsupported: "HTML conversion feature is not available.",
      convert_fail: "Unable to convert content.",
      module_load_fail: "Failed to load html2pdf module",
      unknown: "Unknown error",
      pdf_create_fail: "Failed to create PDF:",
      html_convert_fail: "Failed to convert editor content to HTML:",
      container_remove_fail: "Error while removing container:"
    },
    notifications: {
      export_success: "PDF Export Successful",
      export_success_message: "{{title}}.pdf file has been downloaded.",
      export_fail: "PDF Export Failed",
      convert_fail: "Error while converting to PDF:"
    }
  },
  auth: {
    required: "Authentication Required",
    login_required: "Login is required to create a note."
  },
  info: {
    required: "Information Required",
    id_required: "Task ID or To-do ID is required."
  },
  ai: {
    summary: {
      title: "AI Summary",
      generating: "Generating...",
      regenerate: "Regenerate",
      regenerate_message: "Summary was not generated. Please try again.",
      error: "An error occurred while generating the summary. Please try again later."
    },
    translate: {
      button: "Translate"
    },
    chat: {
      search_placeholder: "Search the web...",
      ask_placeholder: "Ask me anything...",
      disable_search: "Disable web search",
      enable_search: "Search the web",
      send: "Send",
      search_preparing: "Preparing search...",
      searching: "Searching",
      search_complete: "Search complete",
      error: "Sorry, an error occurred."
    },
    share: {
      no_title: "No Title",
      share_suffix: "Sharing note link",
      new_link: "New Link",
      copy: "Copy",
      copied: "Copied",
      share_button: "Share Link",
      generating: "Generating summary...",
      modal_title: "Share Note",
      url_share: "Share URL"
    },
    email: {
      send: "Send Summary Email",
      generating: "Generating summary...",
      error: "Cannot open Gmail. Error",
      greeting: "Hello, this is {{name}}.",
      body: "I'm sharing a summary and link to the note: {{title}}.",
      link_text: "Note Link",
      thanks: "Thank you.",
      from: "Best regards,\n{{name}}"
    },
    task: {
      create: "Create Task"
    }
  }
};

// src/locales/en-US/activity.json
var activity_default = {
  title: "Activity",
  and: " and ",
  actor: "<0>{{name}} </0>",
  update: "<1>has changed </1><0>{{label}}</0><1> to </1><0>{{value}}</0>.",
  indicator: {
    default: "has updated Indicator",
    add: "<1>has added </1><0>Indicator</0>",
    update: "<1>has updated </1><0>Indicator</0>"
  },
  change: "<1>has changed </1><0>{{label}}</0><1> to </1><0>{{value}}</0>",
  change_label: "<1>has changed </1><0>{{label}}</0>",
  delete: "<1>has deleted </1><0>{{label}}</0>",
  due_date: {
    delete: "<1>has deleted </1><0>the deadline</0>",
    update: "<1>has changed </1><0>the deadline</0><1> to </1><0>{{value}}</0>"
  },
  remove_project: "<1>has removed the connection to the </1><0>project.</0>",
  set: "<1>has set </1><0>{{label}}</0><1> to </1><0>{{value}}</0>",
  set_label: "<1>has set </1><0>{{label}}</0>",
  share: {
    fallback_text: {
      changed: "task shared status has changed.",
      share: "shared the task with the team.",
      unshare: "unshared the task with the team."
    },
    share_start: "has shared the task with ",
    unshare_start: "has unshared the task with ",
    share_end: ".",
    unshare_end: "."
  },
  task: {
    create: "has created the task.",
    delete: "has deleted the task."
  },
  note: {
    create: "<1>has created a </1><0>Note.</0>"
  }
};

// src/locales/en-US/workspace.json
var workspace_default = {
  component: {
    add_button: {
      create: "Create Workspace",
      description: "Create a new workspace<br />to manage your teams and projects"
    },
    create_dialog: {
      cancel_button: "Cancel",
      create_button: "Create",
      description_label: "Description",
      description_placeholder: "Enter a description for this workspace",
      error_message: "An error occurred while creating the workspace.",
      error_title: "Failed to Create Workspace",
      name_label: "Workspace Name",
      name_placeholder: "Enter workspace name",
      name_required: "Please enter a workspace name",
      success_message: "Workspace has been successfully created.",
      success_title: "Workspace Created",
      title: "Create New Workspace",
      warn_message: "Please turn on the \u2018Active\u2019 toggle on the Workspace Settings page!",
      warn_title: "Member activation is needed."
    },
    item: {
      create_new: "Create New Workspace",
      create_team: "Create Team",
      feedback: "Feedback",
      no_teams: "No teams available",
      performance: "Performance",
      report: {
        personal: "Personal Reports",
        team: "Team Reports",
        title: "Reports"
      },
      setting: "Workspace Settings",
      title: "Workspace",
      view_invitations: "Check Invites ({{count}})"
    },
    main_info: {
      image_update_error: "Failed to update workspace image:",
      image_update_success: "Workspace image updated successfully:",
      name_update_error: "Failed to update workspace name:",
      name_update_success: "Workspace name updated successfully:"
    }
  },
  error: {
    access_check_failed: "An error occurred while checking access permissions.",
    already_member: "User is already a member of the workspace.",
    cannot_check_team_access: "Cannot check team access permissions.",
    create_failed: "Failed to create workspace.",
    data_not_found_after_create: "Data not found after workspace creation.",
    data_not_found_after_update: "Data not found after workspace update.",
    failed_to_get_workspace_list: "Failed to get workspace list.",
    invite_failed_title: "Invitation Failed",
    delete_failed: "The workspace cannot be deleted or has already been deleted.",
    delete_blocked_by_subscription: "This workspace has an active or pending-cancellation subscription. Delete it only after the subscription has fully ended.",
    transfer_ownership_failed: "An error occurred while processing the transfer of owner privileges.",
    unauthorized_user: "Authenticated user not found.",
    unknown_create_error: "An unknown error occurred during workspace creation.",
    unknown_update_error: "An unknown error occurred during workspace update.",
    update_failed: "Failed to update workspace.",
    user_already_member: "User is already a member of the workspace.",
    user_not_found: "User not found for invitation.",
    user_not_found_for_invitation: "User not found for invitation.",
    user_not_logged_in: "User is not logged in.",
    workspace_not_found: "Workspace not found."
  },
  setting: {
    referral: {
      title: "Referral link",
      description: "When someone signs up with this link and creates a workspace, this workspace can analyze 3 more meetings.",
      copy_button: "Copy link",
      copied: "Copied"
    },
    access_denied: "Only workspace members can access.",
    delete_modal: {
      confirm: "Delete",
      description: "This action cannot be undone and will permanently delete all projects and tasks in the workspace.",
      description_1on1: "All members will immediately lose access and the workspace's 1on1 records will no longer be available. Recovery after deletion is not supported.",
      member_warning: "Access will also be removed for {{count}} other active member(s).",
      name_confirmation_label: "Workspace name",
      name_confirmation_description: "Enter {{name}} exactly to continue.",
      message: "Are you sure you want to delete this workspace?",
      error_title: "Workspace deletion failed",
      error_message: "The workspace could not be deleted.",
      subscription_warning: "This workspace cannot be deleted until its subscription has fully ended. Cancel the subscription first and try again after {{endDate}}. It currently supports {{memberCount}} member(s).",
      subscription_inquiry: "For any inquiries, please contact jjchoi@reversemountain.co.kr.",
      title: "Confirm Workspace Deletion"
    },
    general: {
      workspace_name: "Workspace"
    },
    invitation_cancel_modal: {
      confirm: "Cancel Invitation",
      confirm_message: "Are you sure you want to cancel the invitation sent to {{email}}?",
      description: "Once canceled, the user will not be able to join the workspace.",
      title: "Cancel Invitation"
    },
    loading_state: {
      loading_message: "Loading workspace information..."
    },
    member_remove_modal: {
      confirm_message: "Do you want to remove {{name}} from the workspace?",
      description: "Removed members will no longer have access to the workspace and related teams.<br />To-do information connected to the workspace will remain in the member's planner,<br />but removed members cannot modify or delete them, and cannot view notes.",
      title: "Remove Member"
    },
    member_table: {
      access_denied: "Access denied.",
      active: "Active",
      cancel_invitation: "Cancel Invitation",
      description: "Description",
      empty_message: "No members in this workspace",
      enter_description: "Enter your job title, years of service, etc.",
      invite_button: "Invite Members",
      invited: "Invited",
      invited_user: "Invited User",
      manager: "Manager",
      loading_message: "Loading member list...",
      name: "Name",
      pending: "Pending",
      remove_member: "Remove Member",
      role: "Role",
      select_manager: "Select Manager",
      status: "Status",
      team: "Teams",
      title: "Members",
      filter_team: "Filter by team"
    },
    bulk_action: {
      selected_count: "{{count}} selected",
      change_leader: "Bulk change manager",
      change_role: "Bulk change role",
      deselect: "Deselect all",
      select_role: "Select role",
      select_leader: "Select manager",
      cancel: "Cancel",
      apply: "Apply",
      success: "Bulk update completed",
      error: "An error occurred during bulk update"
    },
    not_found: "Workspace not found.",
    tab_container: {
      create_team: "Create Team",
      invite_member: "Invite Member",
      member: "Manage Members",
      members: "Members",
      team: "Manage Teams",
      teams: "Teams",
      custom: "Custom"
    },
    custom: {
      analysis_visibility: {
        title: "Post-Meeting Leader Feedback",
        description: "Choose who can see the leader feedback in the analysis tab. Speaking ratio and member feedback are not affected. Changes apply immediately to all meetings, including past ones.",
        leader_only: "Visible to meeting leader only",
        both: "Visible to both leader and member",
        per_leader: "Per-leader setting",
        per_leader_checkbox: "Share leader feedback from meetings I lead with members.",
        save_success: "Leader feedback visibility setting saved.",
        save_error: "Failed to save setting."
      },
      question_draft: {
        title: "Question Draft Mode",
        description: "Changes apply immediately to all meetings in this workspace.",
        default_mode: "Default: Questions are shared with the other person in real time.",
        draft_mode: "Draft mode: Questions are only shared after clicking 'Publish Questions'.",
        info_title: "About Question Draft Mode",
        info_1: "Useful when you want to finalize all questions before sharing them at once.",
        info_2: "Publishing: Even after you publish, the other person's draft questions remain hidden until they publish.",
        info_3: "Auto-publish: If you start the meeting without publishing, all drafts are automatically shared.",
        info_4: "After publishing: Any questions added or edited afterward are shared in real time.",
        save_success: "Question draft mode setting saved.",
        save_error: "Failed to save setting."
      }
    },
    team_table: {
      add_member: "Add Member",
      create_button: "Create Team",
      delete_team: "Delete Team",
      empty: "No teams available",
      empty_message: "No teams available",
      loading_message: "Loading team list...",
      member_count: "Members",
      name: "Name",
      project_count: "Projects",
      title: "Teams",
      edit: "Manage Team",
      edit_member: "Manage Members",
      expand_all: "Expand All",
      collapse_all: "Collapse All"
    },
    title: "Workspace Information",
    transfer_ownership: {
      title: "Transfer Workspace Ownership",
      message: "Transferring ownership will result in the following changes:",
      warning_list: {
        item1: "The selected member will become the new owner",
        item2: "Your role will be changed to Admin",
        item3: "To become owner again, you must request it from the new owner"
      },
      confirm_message: "Transfer ownership to {{name}}?",
      success: "Ownership has been successfully transferred",
      error: "Failed to transfer ownership",
      select_new_owner: "Select New Owner",
      confirm_button: "Transfer Ownership"
    },
    warning: {
      delete_button: "Delete Workspace",
      delete_description: "Deleting a workspace will delete all projects and tasks.\nMembers' to-do will remain but will be disconnected from tasks.",
      delete_description_1on1: "Deleting a workspace will remove access for all members and make its 1on1 records unavailable.",
      delete_title: "Delete Workspace",
      tooltip: "Are you sure you want to delete? \u{1F97A}"
    }
  }
};

// src/locales/en-US/planner.json
var planner_default = {
  page: {
    title: "Planner Page"
  },
  item: {
    comment: "Comments",
    note: "Notes",
    description: "Description"
  },
  calendar: {
    title: "Calendar"
  },
  todo: {
    selectTeam: "Please select a team",
    markAsComplete: "Mark as complete",
    todayPlanned: "Today's planned To-dos",
    addTodo: "Add To-do for today",
    adding: "Adding...",
    setDate: "Set date",
    incomplete: "Incomplete To-dos",
    updating: "Updating To-do",
    pleaseWait: "Please wait...",
    updateFailed: "To-do update failed",
    updateError: "An error occurred while updating the To-do.",
    updateComplete: "To-do update complete",
    addedToDate: "Added To-do to the selected date."
  },
  timeBlock: {
    collapse: "Collapse",
    expand: "Expand",
    viewMore: "View {{count}} more"
  },
  error: {
    loadFailed: "Failed to load planner."
  },
  dragAndDrop: {
    notifications: {
      updateAction: "Update To-do",
      timeUpdateSuccess: "To-do time information has been successfully updated.",
      timeChangeImpossible: "Cannot Change Time",
      timeSetImpossible: "Cannot Set Time",
      timeOccupied: "This time slot is already occupied by another To-do.",
      addToPlanner: "Add To-do to Today",
      addToPlannerSuccess: "To-do has been added to the time you selected.",
      addToTodayList: "Add To-do to Today's List",
      addToTodayListSuccess: "To-do has been added to today's To-do list.",
      changeGoal: "Change Task Connection",
      changeGoalSuccess: "The connected Task has been changed."
    }
  }
};

// src/locales/en-US/member_invitation.json
var member_invitation_default = {
  invitation_page: {
    already_responded: "You have already responded to this invitation.",
    current_account: "Currently logged in as",
    expired_invitation: "This invitation has been canceled or does not exist.",
    go_home: "Return to Home",
    home: "Home",
    loading: "Loading...",
    logout: "Logout"
  },
  invitation_response: {
    accept_completed: "Invitation Accepted",
    accept_failed: "Invitation Acceptance Failed",
    email_mismatch: "The invitation email and login email do not match.",
    error_during_acceptance: "Error during invitation acceptance:",
    error_occurred: "An error occurred during processing. Please refresh and try again.",
    joined_workspace: "You have joined the workspace by accepting the invitation.",
    participation_failed: "A problem occurred during participation.",
    reject_completed: "Invitation declined",
    reject_failed: "Failed to decline the invitation",
    rejected: "You declined the workspace invitation."
  },
  invite_results: {
    all_already_members: "All emails are already workspace members",
    all_failed: "Invitation Failed",
    all_failed_message: "All invitations failed",
    all_sent: "Invitation sent to {{count}} people",
    already_invited: "An invitation has already been sent to this email address.",
    already_member: "{{email}} is already registered as a member.",
    auth_required: "Authentication is required.",
    copied: "Copied!",
    copy: "Copy",
    default_inviter: "Inviter",
    email_failed_copy_link: "Email sending failed. Please copy the link below and share it directly.",
    email_required: "Please enter an email address",
    email_send_failed_see_link: "Email sending failed. Please check the link below.",
    failed_message: "Invitation failed",
    invalid_email_error: "Invitation Failed",
    invalid_email_message: "Please enter a valid email address",
    invitation_link: "Invitation Link",
    invitation_sent: "Invitation email has been sent to {{email}}",
    invite_failure: "{{email}} invitation failed: {{errorMessage}}",
    invite_success: "{{email}} invitation successful",
    invite_waiting: "Invitation Pending",
    member_invited: "Invited a member to the workspace",
    members_invited: "Invited {{count}} members to {{targetName}}",
    partial_failure: "Some Invitations Failed",
    partial_result: "{{successCount}} invitations successful, {{failCount}} failed",
    partial_success: "Some Invitations Successful",
    processing: "Processing invitation...",
    success: "Invitation Successful",
    warning: "Warning"
  },
  member_invite_modal: {
    activate_description: "If unchecked, members will be added in inactive state (no charge)",
    activate_on_accept: "Invite as active",
    additional_charge: "{{count}} additional charge: {{amount}} KRW",
    already_members_description: "The following {{count}} member(s) are already in the workspace and will be excluded from the invitation",
    already_members_title: "Already workspace members",
    cancel: "Cancel",
    email_description: "Separate multiple emails with commas (,) to invite several people at once.",
    email_input: "Email Address",
    email_placeholder: "example@mail.com, example@mail.com ...",
    emails_description: "Separate multiple emails with commas (,) to invite several people at once",
    emails_input: "Email Address",
    invalid_email: "Invalid email address: {{email}}",
    invite: "Invite",
    invite_members: "Invite Members to {{targetName}}",
    no_charge: "No additional charge",
    payment_info: "Payment Information",
    preview_count: "Inviting {{count}} member(s)",
    preview_title: "Members to be invited",
    slots_used: "Using {{count}} slot(s)",
    assign_manager: "Assign Manager"
  },
  team: "Team",
  workspace: "Workspace",
  workspace_invitation: {
    accept: "Accept",
    invited_to: "You have been invited to the workspace.",
    reject: "Decline",
    role: "Role",
    title: "Workspace Invitation"
  }
};

// src/locales/en-US/landing.json
var landing_default = {
  title_image: "/images/landing/title_text_en.png",
  macbook_image: "/images/landing/macbook_en.png",
  feature1: {
    title: "AI Report Generation",
    description: "No more duplicate work for reporting!<br />Our AI analyzes your work plans and task data to automatically generate reports.",
    imageSrc: "/images/landing/contents_en/content_1.png",
    imageAlt: "AI Report Generation"
  },
  feature2: {
    title: "Organizational Goal-Linked Task Management",
    description: "Design and connect work around team goals to see how everyone contributes in real-time. Create a system in Orblit where team goals CASCADE down and members can contribute FREELY.",
    imageSrc: "/images/landing/contents_en/content_2.png",
    imageAlt: "Organizational Goal-Linked Task Management"
  },
  feature3: {
    title: "Credits & Feedback",
    description: "'Timely' and 'specific' feedback leads to growth.<br />Build an immediate and clear feedback exchange system, not vague and generalized feedback.",
    imageSrc: "/images/landing/contents_en/content_3.png",
    imageAlt: "Credits & Feedback"
  },
  contact: "View Introduction",
  start: "Start for Free",
  announcement: {
    title: "Up to 80% Government Support for Cloud Vouchers",
    button: "Contact Us"
  },
  hero: "ORBLIT: Data-driven HR linking \u2018team goals and individual tasks\u2019",
  cta: {
    first: "Right now,",
    second: "draw your own orbit."
  },
  company: {
    name: "Reversemountain Corp.",
    address: "Seoul Startup Hub M Plus, 14, Magokjungang 8-ro, Gangseo-gu, Seoul, South Korea",
    representative: "CEO Gyeongmin Kim"
  },
  onelanding: {
    title: "1on1",
    move: "Move"
  },
  buttons: {
    useService: "Use Service",
    requestDemo: "Request Demo"
  },
  heroSection: {
    mainTitle: {
      line1: "Say goodbye to wasteful 1on1 preparation and organization,",
      line2: "and 1on1s that fail to lead to growth."
    },
    app_mainTitle: {
      line1: "Wasteful 1on1 preparation and organization,",
      line2: "1on1s detached from management and growth,",
      line3: "say goodbye."
    },
    subTitle: {
      line1: "Don't worry about taking notes, just focus comfortably on the conversation.",
      line2: "We'll summarize the question-centered dialogue and even provide action items!"
    },
    app_subTitle: {
      line1: "Don't worry about taking notes, just focus comfortably on the conversation.",
      line2: "We'll summarize the question-centered dialogue",
      line3: "and even provide action items!"
    }
  },
  valueProposition: "Value Proposition",
  features: {
    feature1: {
      title: "AI Coaching Feedback for Leaders",
      description: [
        "Do you want to become a better leader, step by step?",
        "We'll analyze your speech ratio, content, tone, and more to provide actionable improvement methods!"
      ],
      app_description: [
        "Do you want to become a better leader, step by step?",
        "We'll analyze your speech ratio, content, tone, and more",
        "to provide actionable improvement methods!"
      ]
    },
    feature2: {
      title: "AI-Generated Customized Questions",
      description: [
        "AI will generate customized questions for you!",
        "We'll create guides and customized questionnaires based on meeting records, situations, and desired topics!"
      ],
      app_description: [
        "Instead of repetitive conversations, conduct 1on1s tailored to each individual!",
        "We'll create guides and customized questionnaires reflecting",
        "the other person's meeting records, situation, and desired topics!"
      ]
    },
    feature3: {
      title: "AI Analysis Reports and 9Box Dashboard",
      description: [
        "Strategically manage hidden growth opportunities and risks!",
        "We'll provide analysis reports containing members' intrinsic motivation, strengths and weaknesses,",
        "and a 9Box dashboard for growth management!"
      ],
      app_description: [
        "Strategically manage hidden growth opportunities and risks!",
        "We'll provide analysis reports containing members' intrinsic motivation, strengths and weaknesses,",
        "and a 9Box dashboard for growth management!"
      ]
    }
  },
  bottomSection: {
    item1: {
      line1: "1on1 meeting before and after",
      highlight1: "",
      line2: "",
      highlight2: "preparation/organization automation"
    },
    item2: {
      line1: "Creating better leaders with",
      line2: "",
      highlight: "customized AI leadership coaching"
    },
    item3: {
      line1: "Analyzing 1on1 data to",
      line2: "provide",
      highlight: "9Box and team member reports"
    }
  },
  footer: {
    companyName: "Reverse Mountain Co., Ltd.",
    address: "Seoul Startup Hub M Plus, 14, Magokjungang 8-ro, Gangseo-gu, Seoul, South Korea",
    representative: "CEO: Gyeongmin Kim",
    email: "gmkim@reversemountain.co.kr",
    copyright: "2025 REVERSEMOUNTAIN ALL RIGHTS RESERVED."
  },
  card: {
    card1: {
      text_normal: "Before and after 1on1 meetings",
      text_bold: "Preparation/organization task automation"
    },
    card2: {
      text_normal: "Creating better leaders with",
      text_bold: "Personalized AI leadership coaching"
    },
    card3: {
      text_normal1: "Analyzing 1on1 data to",
      text_normal2: "provide",
      text_bold: "9-box and team member reports"
    }
  },
  landingMerged: {
    title: "Increasing Talent Density",
    subtitle: "AI Feedback & 1on1 Meeting Solution",
    leadershipText: {
      line1: "Save leaders' ",
      line2: "time",
      line3: ", and ",
      line4: "maximize growth and engagement",
      line5: "."
    },
    card: {
      title1: "Orblit 1on1",
      description1: [
        "Say goodbye to time-consuming 1on1 preparation and organization,",
        "and 1on1s that don't lead to growth."
      ],
      title2: "Orblit Works",
      description2: [
        "Escape from inefficiencies due to unclear work status,",
        "time-consuming report writing, and vague",
        "and formal feedback"
      ],
      title3: "Orblit Feedback",
      description3: [
        "Break free from formal, one-time evaluations",
        "and feedback that doesn't lead to real development"
      ]
    },
    benefits: {
      title: "Benefits ",
      highlight: "immediately ",
      suffix: "felt after implementation"
    },
    carousel: {
      card1: {
        title: "Automation of 1on1 related document work",
        description1: "Reduced performance management document work by 20 hours per month",
        description2: [
          "Team leader managing 8 members",
          "at a mid-sized company"
        ],
        description3: [
          "Reports are automatically completed after 1on1 meetings.",
          "I leave the documentation work to Orblit",
          "and can focus more on the team members themselves.",
          "Team member satisfaction has also increased."
        ]
      },
      card2: {
        title: "Strengthening Leadership Management Capabilities",
        description1: "127% increase in employee performance management satisfaction after using Orblit",
        description2: [
          "CEO of a Series A startup",
          "with 120 employees"
        ],
        description3: [
          "Leadership management capability was always a Black Box.",
          "I was always worried because losses could be significant if things went wrong.",
          "But now Orblit coaches the leaders, and I can",
          "understand the leaders' situations as well."
        ]
      },
      card3: {
        title: "Modernizing Performance Management Culture",
        description1: "Modernizing organizational performance management culture including 1on1 meeting implementation",
        description2: [],
        description3: [
          "Paper, IT, Chemical, Construction / 5,700 employees / Large enterprise",
          "Conducting open innovation and designing expansion with the strategy team"
        ]
      },
      card4: {
        title: "Implementing Data-Driven Performance Management",
        description1: "Collecting employee work data and implementing performance management using it",
        description2: [],
        description3: [
          "Aluminum manufacturing / 4,000 employees / Korea, Vietnam",
          "Conducting paid PoC with planning, HR, and general affairs in Vietnam"
        ]
      }
    }
  },
  landingFB: {
    title: [
      "Formal and one-time evaluations",
      "Break free from feedback that doesn't lead to growth."
    ],
    subtitle: [
      "Formal and one-time evaluations,",
      "that don't lead",
      "to growth."
    ],
    subtitle_app: [
      "Specific data in the most acceptable form.",
      "AI feedback coach will guide you to optimal feedback!"
    ],
    section: {
      title1: "Feedback Scoring and Optimization",
      description1: [
        "Let go of the burden of having to write perfect feedback on your own.",
        "We'll identify areas for improvement in your draft and optimize expressions according to your employee's MBTI personality!"
      ],
      app_description1: [
        "Let go of the burden of having to write perfect feedback on your own.",
        "We'll identify areas for improvement in your draft and optimize expressions",
        "according to your employee's MBTI personality!"
      ],
      title2: "Work Data Connection and Traffic Light Evaluation",
      description2: [
        "Clear and specific feedback leads to development.",
        "Connect work data with feedback and maintain consensus on performance."
      ],
      app_description2: [
        "Clear and specific feedback leads to development.",
        "Connect work data with feedback,",
        "and maintain consensus on performance."
      ]
    },
    card: {
      card1: {
        text_bold1: "Complementing each leader's weaknesses to",
        text_bold2: "provide high-quality feedback",
        text_normal: "possible"
      },
      card2: {
        text_normal1: "Traffic light evaluation system by task to maintain",
        text_normal2: "consensus on performance between",
        text_bold: " team leaders and team members"
      },
      card3: {
        text_normal: "MBTI-customized feedback optimization to",
        text_bold: "maximize feedback acceptance"
      }
    }
  },
  landingWorks: {
    title: [
      "Inefficiencies from unclear work status,",
      "Break free from tedious report writing and vague feedback."
    ],
    title_app: [
      "Escape from inefficiency caused by unclear work status,",
      "time-consuming report writing,",
      "and vague feedback."
    ],
    subtitle: [
      "'3 minutes a day', increase collaboration synergy with daily scrum!",
      "From one-click reports to motivating feedback, we'll help you!"
    ],
    subtitle_app: [
      "'3 minutes a day', increase collaboration synergy with daily scrum!",
      "From one-click reports",
      "to motivating feedback, we'll help you!"
    ],
    section: {
      title1: "Real-time sharing of contributions",
      description1: [
        "Manage tasks connected to team goals and share in real-time.",
        "You can conduct thorough and easy daily scrums with AI!"
      ],
      app_description1: [
        "Manage tasks connected to team goals and share in real-time.",
        "You can conduct thorough and easy daily scrums with AI!"
      ],
      title2: "AI Report Auto-Generation",
      description2: [
        "Create tedious reports with just one click!",
        "AI generates report drafts by combining work status, communication logs, and document data."
      ],
      app_description2: [
        "Create tedious reports with just one click!",
        "AI generates report drafts by combining work status,",
        "communication logs, and document data."
      ],
      title3: "AI Feedback Optimization",
      description3: [
        "Give and receive specific feedback based on work data.",
        "You can also enhance content and optimize expressions with AI!"
      ],
      app_description3: [
        "Give and receive specific feedback based on work data.",
        "You can also enhance content and optimize expressions with AI!"
      ]
    },
    card: {
      card1: {
        text_bold1: "Transparent work status sharing",
        text_normal1: "for",
        text_normal2: "improved team synergy"
      },
      card2: {
        text_normal: "With just 3 minutes of daily recording,",
        text_bold: "automate report creation tasks"
      },
      card3: {
        text_normal: "Improve team synergy with",
        text_bold: "work data-linked AI feedback"
      }
    }
  },
  price: {
    title: "We offer reasonable SaaS pricing plans<br />and customized PoC, implementation, and training.",
    apptitle: "We offer reasonable SaaS pricing plans<br />and customized PoC, implementation, and training.",
    contacts: "For on-premises implementation, custom PoC, education/workshop inquiries<br />jjchoi@reversemountain.co.kr",
    badge: "Business Plan",
    workspaceBilling: "Billed per workspace",
    cards: [
      {
        id: "feedback",
        title: "Orblit Feedback",
        price: 6e3,
        unit: "per member",
        period: "month",
        features: [
          {
            text: "Enhanced feedback content using professional AI"
          },
          {
            text: "Free with Works AI subscription"
          }
        ],
        buttonText: "Contact Us"
      },
      {
        id: "standard",
        title: "Orblit Works",
        price: 1e4,
        unit: "per member",
        period: "month",
        features: [
          {
            text: "Personal task management + real-time status sharing"
          },
          {
            text: "Daily/weekly/monthly report auto-generation AI"
          },
          {
            text: "Feedback AI included (\u20A96,000/month value)"
          }
        ],
        buttonText: "Contact Us"
      },
      {
        id: "premium",
        title: "Orblit 1on1",
        iconBgColor: "#EDF0FE",
        iconColor: "#4C6EF5",
        price: 8e3,
        unit: "per member",
        period: "month",
        features: [
          {
            text: "AI meeting transcription (STT)"
          },
          {
            text: "Customized approach for each team member"
          },
          {
            text: "1on1 content recording + summary + analysis"
          },
          {
            text: "Team member status dashboard based on 1on1 content"
          },
          {
            text: "Customized AI for leadership skill improvement"
          }
        ],
        buttonText: "Contact Us"
      }
    ]
  },
  landingNonAdopted: {
    title: 'Hesitating to implement <span style="color: #228BE6">1on1 meetings?</span>',
    title_app: 'Hesitating to implement <span style="color: #228BE6">1on1 meetings?</span>',
    subtitle: 'Orblit AI helps you not only with a successful start but also <br /><span style="color: #228BE6">supports your growth as a leader.</span>',
    subtitle_app: 'Orblit AI<br />helps you not only with a successful start but also <br /><span style="color: #228BE6">supports your growth as a leader.</span>',
    question: "Are you hesitating to implement 1on1 meetings because of these barriers?",
    question_app: "Are you hesitating to implement \n 1on1 meetings because of these barriers?",
    balloons: {
      balloon1: {
        text: `I know 1on1 meetings would benefit both me and my team members,<br />but I'm <span style="font-weight: 600">afraid to start because I lack experience.</span>`,
        text_app: `I know 1on1 meetings would benefit both me and my team members,<br />but I'm <span style="font-weight: 600">afraid to start because I lack experience.</span>`
      },
      balloon2: {
        text: `I already have so much paperwork, <span style="font-weight: 600">I'm hesitant to add more with 1on1 meetings.</span>`,
        text_app: `I already have so much paperwork, <span style="font-weight: 600">I'm hesitant to add more with 1on1 meetings.</span>`
      },
      balloon3: {
        text: `It's difficult to understand my team members' current state and potential.<br /><span style="font-weight: 600">I'm just guessing who's struggling and who has growth potential.</span>`,
        text_app: `It's difficult to understand my team members' current state and potential.<br /><span style="font-weight: 600">I'm just guessing who's struggling and who has growth potential.</span>`
      },
      balloon4: {
        text: `I'm worried that <span style="font-weight: 600">we'll spend valuable time just checking in without any real results.</span>`,
        text_app: `I'm worried that <span style="font-weight: 600">we'll spend valuable time just checking in without any real results.</span>`
      }
    },
    nontitle: `Orblit transforms all the burdens of 1on1 meetings into <span style="color: #228BE6">'opportunities for growth'</span>.`,
    nontitle_mobile: `Orblit transforms all the burdens of 1on1 meetings<br />into <span style="color: #228BE6">'opportunities for growth'</span>.`,
    nontitle_desktop: `Orblit transforms all the burdens of 1on1 meetings into <span style="color: #228BE6">'opportunities for growth'</span>.`
  },
  nonAdoptedCards: [
    {
      number: "1",
      title: "Orblit provides question templates focused on employee growth and diagnosis,<br />and AI that generates questions tailored to your desired topics.",
      appTitle: "Orblit provides question templates focused on<br />employee growth and diagnosis, and AI that<br />generates questions tailored to your desired topics.",
      description: "Orblit 1on1 AI recommends everything from diagnosing team member situations<br />to coaching questions that drive growth. Team leaders can focus solely on<br />the conversation without worrying about what to say.",
      appDescription: "Orblit 1on1 AI recommends everything from diagnosing team member situations<br />to coaching questions that drive growth. Team leaders can focus solely on<br />the conversation without worrying about what to say.",
      imageSrc: "/images/landing_m/en/nonadopted01.png",
      imageAlt: "1on1 Meeting Guide"
    },
    {
      number: "2",
      title: "AI handles all recording and organization for you.",
      appTitle: "AI handles all recording and organization for you.",
      description: "When the meeting ends, transcripts, key summaries, and actionable items<br />are automatically generated. Team leaders can focus on creating the next<br />achievement without additional documentation work after the meeting.",
      appDescription: "When the meeting ends, transcripts, key summaries, and actionable items<br />are automatically generated. Team leaders can focus on creating the next<br />achievement without additional documentation work after the meeting.",
      imageSrc: "/images/landing_m/en/nonadopted02.png",
      imageAlt: "AI Automatic Recording"
    },
    {
      number: "3",
      title: "After the meeting, a 'Leadership Report' is issued for team leaders.",
      appTitle: "After the meeting, a 'Leadership Report'<br />is issued for team leaders.",
      description: 'This is the core of Orblit. AI analyzes the proportion of your statements,<br />types of questions, etc., to provide customized coaching to help you grow<br />as a "listening leader" and a "growth-supporting leader." As you conduct<br />more 1on1s, your leadership capabilities grow as well.',
      appDescription: 'This is the core of Orblit. AI analyzes the proportion of your statements,<br />types of questions, etc., to provide customized coaching to help you grow<br />as a "listening leader" and a "growth-supporting leader."<br />As you conduct more 1on1s, your leadership capabilities grow as well.',
      imageSrc: "/images/landing_m/en/nonadopted03.png",
      imageAlt: "Leadership Report"
    },
    {
      number: "4",
      title: "We provide IC reports that analyze each team member.",
      appTitle: "We provide IC reports that analyze each team member.",
      description: "AI analyzes conversations to create reports that outline what motivates team members,<br />their strengths, weaknesses, and how to manage them. Missing information is<br />recommended as topics for the next meeting, enabling in-depth management of team members.",
      appDescription: "AI analyzes conversations to create reports that outline what motivates team members,<br />their strengths, weaknesses, and how to manage them.<br />Missing information is recommended as topics for the next meeting,<br />enabling in-depth management of team members.",
      imageSrc: "/images/landing_m/en/nonadopted04.png",
      imageAlt: "IC Report"
    },
    {
      number: "5",
      title: "Identify team members' potential and risk signals through data.",
      appTitle: "Identify team members' potential and risk signals through data.",
      description: "Based on AI analysis of each team member,<br />the 9Box dashboard visually displays team members' performance and potential.<br />You can identify and proactively manage team members showing signs of<br />'quiet quitting' or those who should be developed as key talent, all based on data.",
      appDescription: "Based on AI analysis of each team member,<br />the 9Box dashboard visually displays team members' performance and potential.<br />You can identify and proactively manage team members showing signs of<br />'quiet quitting' or those who should be developed as key talent,<br />all based on data.",
      imageSrc: "/images/landing_m/en/nonadopted05.png",
      imageAlt: "Team Member Analysis Dashboard"
    }
  ],
  landingAdopted: {
    title: 'AI makes <span style="color: #228BE6">team leaders into capable coaches and team members into key talents</span>.',
    subtitle: `With Orblit, transform challenging feedback and 1on1s into <br/> <span style="color: #228BE6">your team's most powerful growth engine.</span>`,
    subtitle_app: `With Orblit, transform challenging feedback and 1on1s into <br/> <span style="color: #228BE6">your team's most powerful growth engine.</span>`,
    question: "Are you struggling with these concerns on your own?",
    balloons: [
      {
        text: `The feedback I give might not help my team members grow, <span style="font-weight: 600">I'm always hesitant because it might make our relationship awkward.</span>`,
        text_app: `The feedback I give might not help my team members grow,<br /><span style="font-weight: 600">I'm always hesitant because it might make our relationship awkward.</span>`
      },
      {
        text: `<span style="font-weight: 600">It takes too much time to prepare and organize 1on1 notes <br /></span>I can't organize them properly, so the content often gets lost after the meeting`,
        text_app: `<span style="font-weight: 600">It takes too much time to prepare and organize 1on1 notes <br /></span>I can't organize them properly, so the content<br />often gets lost after the meeting`
      },
      {
        text: `I've had the 1on1 meeting and organized the notes, <span style="font-weight: 600">but I don't know what to do with them afterward.</span>`,
        text_app: `I've had the 1on1 meeting and organized the notes,<br /><span style="font-weight: 600">but I don't know what to do with them afterward.</span>`
      },
      {
        text: `I'm definitely having 1on1s, <span style="font-weight: 600">but without professional skills,<br />the conversations feel superficial and I miss the team member's true feelings.</span>`,
        text_app: `I'm definitely having 1on1s, <span style="font-weight: 600">but without professional skills,<br />the conversations feel superficial and I miss the team member's true feelings.</span>`
      },
      {
        text: `The CEO has expectations, <span style="font-weight: 600">but I've never had proper leadership training, so I always feel anxious when it's time for 1on1s.</span>`,
        text_app: `The CEO has expectations,<br /><span style="font-weight: 600">but I've never had proper leadership training, so I always feel anxious when it's time for 1on1s.</span>`
      }
    ],
    adopted_title: "Orblit supports the growth of both team leaders and team members.",
    adopted_title_app: "Orblit<br />supports the growth of both team leaders and team members."
  },
  adoptedCards: [
    {
      number: "1",
      title: "You get a leadership coach just for you.",
      appTitle: "You get a leadership coach just for you.",
      description: "After a 1on1 meeting, the AI analyzes your conversation style and provides<br />personalized leadership coaching. Without separate training, you can grow the fastest<br />through real-world practice and become a leader trusted by your team members.",
      appDescription: "After a 1on1 meeting, the AI analyzes your conversation style and provides<br />personalized leadership coaching. Without separate training, you can grow the fastest<br />through real-world practice and become a leader trusted by your team members.",
      imageSrc: "/images/landing_m/en/adopted001.png",
      imageAlt: "1on1 Meeting Guide"
    },
    {
      number: "2",
      title: "We provide `growth data` that awakens team members' potential.",
      appTitle: "We provide `growth data` that awakens team members' potential.",
      description: "The AI analyzes 1on1 conversations and work records to create a report on team members' strengths and areas for improvement,<br />and a 9Box dashboard that diagnoses their competencies and status. Based on this data,<br />you can design the essential growth path for each team member and bring out their maximum potential.",
      appDescription: "The AI analyzes 1on1 conversations and work records to create a report on team members'<br />strengths and areas for improvement, and a 9Box dashboard that diagnoses their competencies and status.<br />Based on this data, you can design the essential growth path for each team member<br />and bring out their maximum potential.",
      imageSrc: "/images/landing_m/en/adopted002.png",
      imageAlt: "AI Automatic Recording"
    },
    {
      number: "3",
      title: "Even outside of 1on1s, AI helps with healthy feedback.",
      appTitle: "Even outside of 1on1s, AI helps with healthy feedback.",
      description: "Orblit's Feedback AI also helps when writing routine work feedback.<br />Instead of emotional expressions, it suggests constructive feedback drafts based on data,<br />creating a positive feedback culture that team members can openly accept.",
      appDescription: "Orblit's Feedback AI also helps when writing routine work feedback.<br />Instead of emotional expressions, it suggests constructive feedback drafts based on data,<br />creating a positive feedback culture that team members can openly accept.",
      imageSrc: "/images/landing_m/en/adopted003.png",
      imageAlt: "Leadership Report"
    },
    {
      number: "4",
      title: "Leave all the documentation to the AI and focus only on `growth`.",
      appTitle: "Leave all the documentation to the AI and focus only on `growth`.",
      description: "Meeting notes and feedback records are automatically organized and accumulated like assets.<br />You are completely freed from tedious paperwork and can focus only on the most important essence of `growth`<br />for both the team leader and team members.",
      appDescription: "Meeting notes and feedback records are automatically organized and accumulated like assets.<br />You are completely freed from tedious paperwork and can focus only on the most important essence of `growth`<br />for both the team leader and team members.",
      imageSrc: "/images/landing_m/en/adopted004.png",
      imageAlt: "IC Report"
    }
  ],
  landingMenu: {
    product: "Product",
    solution: "Solution",
    adopted: "Teams running 1on1s",
    nonadopted: "Teams not yet adopting",
    pricing: "Pricing",
    insight: "Insight",
    youtube: "YouTube",
    blog: "Blog",
    newsletter: "Newsletter"
  }
};

// src/locales/en-US/ai.json
var ai_default = {
  chat: {
    history: "Chat History",
    noHandler: "Session {{id}} selected, but no handler provided",
    toggleCollapse: "Toggle collapse handler not provided",
    newChat: "New Chat",
    emptyState: "No chat sessions yet",
    loading: "Loading chat sessions...",
    error: "Error loading chat sessions",
    delete: "Delete chat",
    rename: "Rename chat",
    confirmDelete: "Are you sure you want to delete this chat?",
    search: "Search chats",
    greeting: "Hi, there <strong>{{name}}</strong>!",
    thinking: "Thinking",
    backToPrevious: "Back",
    errorMessage: "An error occurred: {{message}}",
    aiRequestError: "An error occurred while processing your request. Please try again later."
  },
  features: {
    featureSelected: "Feature {{id}} selected, but no handler provided",
    getTodo: {
      title: "View Schedule",
      description: "View schedules for you and your team.",
      example1: 'ex) "What did I do last Thursday?"',
      example2: 'ex) "Tell me what my team is doing today"'
    },
    createTodo: {
      title: "Add Task",
      description: "Easily add new tasks.",
      instruction1: "Enter a schedule creation request including date, time, and title.",
      instruction2: "Including location or attendee information will create a more accurate schedule.",
      example: 'ex) "Schedule a product planning meeting tomorrow from 2 PM to 4 PM"'
    },
    createReport: {
      title: "Generate Report",
      description: "Generate reports about projects and tasks.",
      instructions: "Select the report type and team, then click the generate report button.",
      alternativeInstructions: "Or directly enter a request including the format and period you want.",
      reportType: "Report Type",
      dailyReport: "Daily Report",
      weeklyReport: "Weekly Report",
      date: "Date",
      selectWorkspace: "Select Workspace",
      noWorkspaces: "No workspaces available",
      generateReport: "Generate Report",
      todayDailyReportPersonal: "Write my daily report for today",
      todayDailyReportTeam: "Write {{teamName}}'s daily report for today",
      yesterdayDailyReportPersonal: "Write my daily report for yesterday",
      yesterdayDailyReportTeam: "Write {{teamName}}'s daily report for yesterday",
      dateDailyReportPersonal: "Write my daily report for {{date}}",
      dateDailyReportTeam: "Write {{teamName}}'s daily report for {{date}}",
      weeklyReportPersonal: "Write my weekly report for {{dateRange}}",
      weeklyReportTeam: "Write {{teamName}}'s weekly report for {{dateRange}}"
    },
    dateRangeSelector: {
      date: "Date",
      selectedDate: "Selected Date",
      apply: "Apply",
      cancel: "Cancel",
      clear: "Clear",
      weeklyReportMaxDaysError: "Weekly reports cannot exceed 7 days."
    },
    searchNote: {
      title: "Search Notes",
      description: "Quickly search and find your notes.",
      instruction: "Add # before your search term to search notes.",
      placeholder: "Search notes #keyword",
      examplePrefix: "ex) Search notes ",
      exampleTag: "#sales weekly meeting"
    }
  },
  common: {
    send: "Send",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    loading: "Loading...",
    error: "An error occurred"
  },
  tools: {
    completed: "Completed",
    running: "Running",
    loading: "Running...",
    inputParameters: "Input Parameters",
    visualizationResults: "Visualization results:",
    iframeMode: "iframe mode",
    directRendering: "Direct rendering",
    loadingVisualizationData: "Loading visualization data...",
    unableToDisplayVisualization: "Unable to display visualization data.",
    filename: "Filename:",
    viewRawHtml: "View Raw HTML",
    viewRendered: "View Rendered",
    renderingError: "Error rendering content",
    searchResults: "Search Results",
    noResults: "No results found",
    items: "items",
    item: "item",
    showMore: "Show more",
    showLess: "Show less",
    note: "Note",
    visualization: "Visualization"
  },
  reportCreation: {
    error: "Error",
    success: "Success",
    loginRequired: "Login required",
    workspaceRequired: "Workspace information required",
    createButtonClicked: "Create button clicked",
    reportCreated: "Report has been created",
    reportCreationError: "An error occurred while creating the report",
    creationComplete: "Creation complete",
    aiGeneratedReport: "AI-generated {{reportType}} is ready",
    clickToAdd: "Click the button below to add the {{reportType}}",
    createReport: "Create {{reportType}}",
    workspaceInfo: "Workspace information required"
  },
  searchNote: {
    results: "Search Results",
    itemsFound: "{{count}} items found",
    noTitle: "No title",
    noAuthor: "No author",
    viewNote: "View Note"
  },
  todoCreation: {
    loading: "Loading todo information...",
    loadError: "Unable to load todo information",
    creationComplete: "Todo Creation Complete",
    createdAt: "Created at",
    schedule: "Schedule",
    category: "Category",
    defaultCategory: "Category",
    goToPlanner: "Go to Planner"
  },
  textInput: {
    placeholder: "Enter your message...",
    send: "Send",
    errorMessage: "An error occurred while sending the message. Please try again."
  },
  logs: {
    enterKeyUpdate: "Enter key: Updated with latest content",
    onSendCallback: "onSend callback called - message to send",
    inputFieldCleared: "Input field cleared after message sent",
    messageSendFailed: "Message send failed"
  },
  settings: {
    title: "AI Settings",
    language: "Language",
    theme: "Theme",
    notifications: "Notifications",
    privacy: "Privacy"
  }
};

// src/locales/en-US/notification.json
var notification_default = {
  title: "Notifications",
  defaultMessage: "You have received a new notification",
  emptyState: "No notifications",
  markAsRead: "Mark as read",
  unread_count: "Unread ({{count}})",
  all: "All",
  timeAgo: "{{time}} ago",
  workspace: {
    invite: "{{inviter_name}} invited you to the <0>{{workspace_name}}</0>",
    join: "<0>{{joiner_name}}</0> joined the <0>{{workspace_name}}</0>",
    out: "You have been removed from the <0>{{workspace_name}}</0>"
  },
  team: {
    invite: "{{inviter_name}} invited you to the <0>{{team_name}}</0> team in <0>{{workspace_name}}</0>",
    out: "You have been removed from the <0>{{team_name}}</0> team in <0>{{workspace_name}}</0>"
  },
  task: {
    priorityChanged: "The priority of <0>{{goal_title}}</0> has been changed to <0>{{new_priority}}</0> by {{changer_name}}",
    stateChanged: "The status of <0>{{goal_title}}</0> has been changed to <0>{{new_state}}</0> by {{changer_name}}",
    deadlineChanged: "The deadline of <0>{{goal_title}}</0> has been changed to <0>{{date_range}}</0> by {{changer_name}}",
    leaderChanged: "You have been assigned as the manager of <0>{{goal_title}}</0>",
    deadlineReminder: {
      upcoming: "The deadline for <0>{{title}}</0> is in <0>{{days_delta}}</0> days",
      today: "The deadline for <0>{{title}}</0> is <0>today</0>",
      overdue: "The deadline for <0>{{title}}</0> is overdue by <0>{{days_delta}}</0> day"
    }
  },
  todo: {
    assigned: "{{assigner_name}} assigned <0>{{todo_title}}</0> to you",
    stateChanged: "{{changer_name}} changed the status of <0>{{todo_title}}</0> to <0>{{new_state}}</0>",
    deadlineChanged: "{{changer_name}} changed the deadline of <0>{{todo_title}}</0> to <0>{{date_range}}</0>",
    timeChanged: "{{changer_name}} changed the time of <0>{{todo_title}}</0>",
    deadlineReminder: {
      upcoming: "The due date for <0>{{title}}</0> is in <0>{{days_delta}}</0> days",
      today: "The due date for <0>{{title}}</0> is <0>today</0>",
      overdue: "The due date for <0>{{title}}</0> is overdue by <0>{{days_delta}}</0> day"
    },
    plan_time: "Time to schedule your day. Ready to start another vibrant day?"
  },
  project: {
    priorityChanged: "The priority of <0>{{project_title}}</0> has been changed to <0>{{new_priority}}</0> by {{changer_name}}",
    stateChanged: "The status of <0>{{project_title}}</0> has been changed to <0>{{new_state}}</0> by {{changer_name}}",
    deadlineChanged: "The deadline of <0>{{project_title}}</0> has been changed to <0>{{date_range}}</0> by {{changer_name}}",
    leaderChanged: "You have been assigned as the leader of <0>{{project_title}}</0>",
    deadlineReminder: {
      upcoming: "The deadline for <0>{{title}}</0> is in <0>{{days_delta}}</0> days",
      today: "The deadline for <0>{{title}}</0> is <0>today</0>",
      overdue: "The deadline for <0>{{title}}</0> is overdue by <0>{{days_delta}}</0> day"
    }
  },
  communication: {
    comment: {
      goal: "<0>{{commenter_name}}</0> left a comment on <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      todo: "<0>{{commenter_name}}</0> left a comment on <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      project: "<0>{{commenter_name}}</0> left a comment on <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      note: "<0>{{commenter_name}}</0> left a comment on <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      default: "<0>{{commenter_name}}</0> left a comment on <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'"
    },
    reply: "<0>{{replier_name}}</0> left a reply: '<0>{{reply_text}}</0>'",
    mention: "<0>{{mentioner_name}}</0> mentioned you in <0>{{context_title}}</0>",
    plan_request: "<0>{{requester_name}}</0> asked you to update your Planner for today!",
    comment_mention: {
      note: "<0>{{mentioner_name}}</0> mentioned you in <0>{{context_title}}</0> comment: '<0>{{comment_text}}</0>'",
      todo: "<0>{{mentioner_name}}</0> mentioned you in <0>{{context_title}}</0> comment: '<0>{{comment_text}}</0>'",
      goal: "<0>{{mentioner_name}}</0> mentioned you in <0>{{context_title}}</0> comment: '<0>{{comment_text}}</0>'"
    },
    feedback_comment: "<0>{{commenter_name}}</0> left a comment on <0>{{feedback_content}}</0>: '<0>{{comment_text}}</0>'",
    feedback_reply: "<0>{{commenter_name}}</0> left a reply on <0>{{feedback_content}}</0>: '<0>{{comment_text}}</0>'",
    daily_scrum_comment: "<0>{{commenter_name}}</0> left a comment on the <0>{{daily_scrum_date}}</0> Daily Scrum: '<0>{{comment_text}}</0>'",
    daily_scrum_reply: "<0>{{commenter_name}}</0> left a reply on the <0>{{daily_scrum_date}}</0> Daily Scrum: '<0>{{comment_text}}</0>'"
  },
  performance: {
    feedback_request: "<0>{{requester_name}}</0> has requested your feedback. <0>{{content}}</0>",
    feedback_received: "<0>{{writer_name}}</0> left you feedback. <0>{{content}}</0>",
    feedback_updated: "<0>{{writer_name}}</0> updated and resent the feedback. <0>{{content}}</0>",
    feedback_request_updated: "<0>{{requester_name}}</0> updated and resent the feedback request. <0>{{content}}</0>",
    feedback_request_rejected: "<0>{{name}}</0> declined your feedback request.",
    report_receive: "<0>{{writer_name}}</0> sent you a <0>{{report_type}}</0>.",
    report_updated: "<0>{{writer_name}}</0> resent the <0>{{report_type}}</0>.",
    report_request: "<0>{{requester_name}}</0> requested you to send a <0>{{report_type}}</0>.",
    feedback_reaction: "<0>{{reactioner_name}}</0> reacted to <0>{{feedback_content}}</0>."
  },
  system: {
    announcement: "[Announcement] {{title}}",
    security: "Account Security Alert: {{message}}"
  },
  typeLabel: {
    account_security: "Account security notification",
    comment: {
      goal: "Task comment",
      note: "Note comment",
      todo: "To-do comment"
    },
    feedback_comment: "Feedback comment",
    daily_scrum_comment: "Daily Scrum comment",
    feedback_received: "Feedback",
    feedback_updated: "Feedback",
    feedback_reaction: "Feedback reaction",
    feedback_request: "Feedback request",
    feedback_request_updated: "Feedback request",
    feedback_request_rejected: "Feedback rejection",
    goal_deadline_changed: "Task deadline",
    goal_deadline_reminder: "Task deadline",
    goal_leader_changed: "Task manager",
    goal_priority_changed: "Task priority",
    goal_state_changed: "Task status",
    mention: {
      note: "Note mention"
    },
    comment_mention: {
      note: "Note comment mention",
      todo: "To-do comment mention",
      goal: "Task comment mention"
    },
    plan_request: "Planner request",
    plan_time: "Planning time",
    project_deadline_changed: "Project deadline",
    project_deadline_reminder: "Project deadline",
    project_leader_changed: "Project leader",
    project_priority_changed: "Project priority",
    project_state_changed: "Project status",
    reply: "Reply",
    report_receive: "Report arrival",
    report_request: "Report request",
    report_updated: "Report update",
    system_announcement: "System notice",
    team_invite: "Team invitation",
    team_out: "Team forced exit",
    todo_assigned: "To-do assignment",
    todo_deadline_changed: "To-do due date",
    todo_deadline_reminder: "To-do due date",
    todo_state_changed: "To-do status",
    todo_time_changed: "To-do time",
    workspace_invite: "Workspace invitation",
    workspace_join: "Workspace member",
    workspace_out: "Workspace forced exit"
  }
};

// src/locales/en-US/dailyscrum.json
var dailyscrum_default = {
  template: {
    template_list: "Template List",
    manage_template: "Manage Template",
    new_template: "New Template",
    create_template: "Create New Template",
    edit_template: "Edit Template",
    select_template: "Select Template",
    select_template_message: "Please select a template to view details.",
    no_templates: "No templates available",
    default: "Default",
    edit: "Edit",
    delete: "Delete",
    no_permission: "You don't have permission to modify this template.",
    delete_success: "Template Deleted",
    delete_success_message: "The template has been successfully deleted.",
    delete_error: "Delete Failed",
    delete_error_message: "Failed to delete template. Please try again.",
    default_template_notice: "Default templates cannot be modified or deleted.",
    default_templates: "Default Templates",
    team_templates: "Team Templates",
    create_success: "Template Created",
    create_success_message: "Template has been created successfully.",
    create_error: "Template Creation Failed",
    create_error_message: "An error occurred while creating the template.",
    update_success: "Template Updated",
    update_success_message: "Template has been updated successfully.",
    update_error: "Template Update Failed",
    update_error_message: "An error occurred while updating the template.",
    create_first_template: "Create First Template",
    remove_block: "Remove Block",
    validation_error: "Validation Error",
    title_required: "Title is required",
    default_template_text: "Current team template",
    user_team_info_missing: "User or team information is missing",
    user_info_missing: "User information is missing",
    user_template_info_missing: "User or template information is missing",
    created_date: "Created",
    set_as_default: "Set as Team Template",
    default_set_success: "Team Template Set",
    default_set_success_message: "Team template has been set successfully.",
    default_set_error: "Team Template Setting Failed",
    default_set_error_message: "An error occurred while setting team template.",
    team_info_missing: "Team information is missing",
    default_template_team_notice: "When set as team template, it will be applied to the entire team.",
    delete_confirmation: "Delete Template",
    delete_template_message: "Are you sure you want to delete this template? This action cannot be undone.",
    delete_default_template_message: "This template is currently set as the team template. Do you want to delete it?"
  },
  form: {
    title: "Title",
    title_placeholder: "Enter template title",
    content: "Content",
    content_placeholder: "Enter template content",
    save: "Save",
    cancel: "Cancel",
    back: "Back",
    required: "Required",
    optional: "Optional",
    required_field: "This field is required",
    placeholder: {
      title: "Enter title",
      content: "Enter content"
    }
  },
  modal: {
    create_template: "Create New Template",
    edit_template: "Edit Template",
    delete_confirmation: "Delete Template",
    delete_confirmation_message: "Are you sure you want to delete this template? This action cannot be undone.",
    confirm: "Confirm",
    cancel: "Cancel",
    daily_scrum: "Daily Scrum"
  },
  dailyscrum: {
    title: "Daily Scrum",
    write_title: "Write Daily Scrum",
    cancel: "Cancel",
    complete: "Complete",
    save: "Save",
    ai_generate: "AI Generate Daily Scrum",
    writing_mode: "Writing Mode",
    content_required: "Please enter daily scrum content.",
    save_error: "An error occurred while saving. Please try again.",
    write_today_scrum: "Please write today's daily scrum",
    loading: "Loading daily scrums...",
    error: "An error occurred while loading data.",
    no_scrums: "No daily scrums written on",
    unknown_user: "Unknown user",
    user: "User",
    edit: "Edit",
    delete: "Delete",
    item_update_success: "Update Complete",
    item_update_success_message: "Daily scrum has been successfully updated.",
    item_update_error: "Update Failed",
    item_update_error_message: "An error occurred while updating daily scrum.",
    item_delete_success: "Delete Complete",
    item_delete_success_message: "Daily scrum item has been deleted.",
    item_delete_error: "Delete Failed",
    item_delete_error_message: "An error occurred while deleting daily scrum item.",
    delete_item_title: "Delete Daily Scrum",
    delete_item_message: "Are you sure you want to delete this daily scrum item?\nDeleted content cannot be recovered.",
    unsaved_content_title: "Discard changes?",
    unsaved_content_message: "You have unsaved changes. Are you sure you want to discard them?",
    continue_writing: "Keep editing",
    exit: "Close and discard"
  },
  todolist: {
    loading: "Loading...",
    yesterday_tasks: "Yesterday Tasks",
    today_tasks: "Today Tasks",
    task_history: "Task History"
  }
};

// src/locales/en-US/feedback.json
var feedback_default = {
  title: "Feedback",
  setting: {
    title: "Feedback Settings",
    coreValue: {
      title: "Core Values",
      setup: "Core Value Settings",
      empty: "No core values available.",
      tooltip: "These are the core values that our organization and members aspire to uphold. When sending feedback, you can select values that the member has performed well or needs improvement on, and include them in your feedback.",
      addTitle: "Add Core Value",
      placeholder: "Example: Challenge, Initiative, etc.",
      addButton: "Add",
      addSuccess: "Core Value Added Successfully",
      addSuccessMessage: "'{{value}}' has been added as a core value.",
      addError: "Failed to Add Core Value",
      addErrorMessage: "This core value already exists.",
      deleteSuccess: "Core Value Deleted Successfully",
      deleteSuccessMessage: "Core value '{{value}}' has been deleted.",
      deleteError: "Failed to Delete Core Value",
      deleteConfirmTitle: "Delete Core Value",
      deleteConfirmMessage: "Are you sure you want to delete the core value '{{value}}'?",
      deleteNote: "Deleted core values cannot be recovered, but this will not affect feedback that has already been sent.",
      deleteConfirm: "Delete"
    }
  },
  common: {
    cancel: "Cancel"
  },
  tabs: {
    received: "Received Feedback",
    receivedRequest: "Received Requests",
    sent: "Sent Feedback",
    sentRequest: "Sent Requests"
  },
  action: {
    requestFeedback: "Request Feedback",
    sendFeedback: "Send Feedback",
    delete: "Delete",
    edit: "Edit",
    addBookmark: "Bookmark",
    removeBookmark: "Remove Bookmark",
    cancelRequest: "Cancel Request",
    editRequest: "Edit Request",
    editFeedback: "Edit Feedback",
    resend: "Resend",
    doneEdit: "Editing completed"
  },
  placeHolder: {
    received: "You haven't received any feedback yet!",
    sent: "You haven't sent any feedback yet!",
    receivedRequest: "You haven't received any requests yet!",
    sentRequest: "You haven't sent any requests yet!"
  },
  sendFeedback: {
    title: "Send Feedback to {{name}}",
    success: "Feedback sent successfully.",
    error: "Failed to send feedback. Please try again.",
    requestInvalid: "Canceled Reqeust",
    requestInvalidDetail: "The requester canceled the feedback request.\nWould you like to send feedback though?"
  },
  editFeedback: {
    title: "Edit Feedback"
  },
  error: {
    notPermission: "You don't have permission. Only administrators or owners can modify this.",
    notFound: "An unknown error occurred. Please try again later."
  },
  deleteFeedback: {
    title: "Delete Feedback",
    receiver_message: "This feedback will be removed from your list only.\nThe sender will still be able to see it.",
    sender_message: "This feedback will be removed from your list only.\nThe recipient will still be able to see it.",
    success: "Feedback deleted successfully.",
    error: "Failed to delete feedback. Please try again."
  },
  rejectFeedback: {
    title: "Reject Feedback Request",
    message: "Reject feedback request from {{name}}?",
    success: "Feedback request rejected successfully.",
    error: "Failed to reject feedback request. Please try again."
  },
  sendRequest: {
    success: "Feedback request sent successfully.",
    error: "Failed to send feedback request. Please try again."
  },
  editRequest: {
    title: "Edit Feedback Request"
  },
  deleteRequest: {
    title: "Delete Request",
    message: "Are you sure you want to delete this feedback request?",
    success: "Feedback request deleted successfully.",
    error: "Failed to delete feedback request. Please try again."
  },
  cancelRequest: {
    title: "Cancel Request",
    message: "Are you sure you want to cancel this feedback request?",
    success: "Successfully canceled the request.",
    error: "An error occurred while canceling the request."
  },
  bookmark: {
    title: "Bookmarked Feedback",
    placeHolder: "Try bookmarking feedback\nyou've received.",
    add: {
      title: "Add to Bookmarks",
      message: "Bookmark this feedback?",
      success: "Successfully added to bookmarks."
    },
    remove: {
      title: "Remove Bookmark",
      message: "Remove this feedback from bookmarks?",
      success: "Successfully removed from bookmarks."
    },
    error: "Failed to update bookmark status."
  },
  feedbackCard: {
    requestTitle: "Feedback Request",
    seeRequest: "View Request"
  },
  feedbackModal: {
    requestTitle: "Request Details",
    receiver: "Feedback Recipient",
    requestReceiver: "Request Recipient",
    selectReceiver: "Select recipient",
    selectRequestReceiver: "Select request recipient",
    feedbackPlaceHolder: "Enter your feedback here.",
    requestPlaceHolder: "Enter your feedback request here.",
    selectCoreValue: "Select Core Values",
    positivePlaceHolder: "What stood out in this task?",
    negativePlaceHolder: "What could have been improved in this task?",
    todoList: "{{name}}'s To-do History",
    todoListDescription: {
      respond: "To-dos included in the feedback request.",
      feedback: "Select To-dos related to your feedback.",
      request: "Select To-dos you'd like feedback on."
    },
    receiverPlaceHolder: "Please select a feedback recipient",
    requestReceiverPlaceHolder: "Please select a feedback request recipient",
    aiOptimization: "AI optimization",
    feedbackScoring: "Feedback scoring",
    selectedTodo: "Selected To-dos ({{count}})",
    unsavedContent: "Unsaved Changes",
    unsavedContentDetail: "You have unsaved changes. Are you sure you want to discard them?",
    refreshRequest: {
      button: "Refresh",
      tooltip: "The request has been updated.\nRefresh it to bring up the latest content!"
    }
  },
  todoBadgeList: {
    seeMore: "{{count}} more",
    collapse: "Collapse",
    expand: "Expand"
  },
  todoList: {
    goalTitlePlaceHolder: "No task",
    select: "Select",
    unselect: "Remove",
    feedbackPlaceHolder: "No To-dos found for this period.",
    requestPlaceHolder: "No To-dos included in this request.",
    selectAll: "Select all",
    unselectAll: "Unselect all"
  },
  coreValue: {
    positive: "Positive Feedback",
    negative: "Areas for Improvement",
    allowMultiple: "Multiple selections allowed"
  },
  summary: {
    title: "AI Feedback Summary",
    subTitle: "About {{name}}",
    coreValue: "{{name}}'s Core Values"
  }
};

// src/locales/en-US/drive.json
var drive_default = {
  search_placeholder: "Search note title",
  select_workspace: "Select workspace",
  select_location: "Select location",
  location_todo: "Todo",
  location_goal: "Task",
  start_date: "Start date",
  end_date: "End date",
  no_notes_found: "No notes found",
  no_notes: "No notes found",
  created_by: "Created by",
  load_more: "Load more",
  showing_results: "Showing {{count}} of {{total}} notes",
  filter: {
    title: "Filter",
    workspace: "Workspace",
    creator: "Creator",
    location: "Location",
    date: "Created at",
    bookmark: "Bookmark"
  },
  date_today: "Today",
  date_last7days: "Last 7 days",
  date_last14days: "Last 14 days",
  date_last30days: "Last 30 days",
  column: {
    title: "Title",
    creator: "Creator",
    workspace: "Workspace",
    location: "Location",
    created_at: "Created at",
    updated_at: "Updated at"
  },
  my_created_notes: "My Recent Notes",
  recently_viewed_notes: "Recently Viewed Notes",
  bookmarked_notes: "Bookmarked Notes",
  no_my_notes: "No notes created by you",
  no_recently_viewed: "No recently viewed notes",
  no_bookmarked: "No bookmarked notes",
  my_bookmark: "My Bookmark"
};

// src/locales/en-US/oneonone.json
var oneonone_default = {
  action_items: {
    add: "Add Action Item",
    all_items: "Action Items ({{count}})",
    banner_title: "You have action items to review",
    banner_action: "View",
    assignee_change: "Assignee change",
    assignee_change_failed: "Assignee change failed.",
    assignee_changed: "Assignee has been changed.",
    delete_confirm: "Do you want to delete this action item?",
    delete_message: "Action item has been deleted.",
    new_title: "New Action Items",
    no_action_items: "No action items.",
    old_title: "Previous Action Items",
    tab_title: "Action Items",
    title_label: "Action Item Title",
    title_placeholder: "Enter the action item",
    title_required: "Please enter content",
    user_not_found: "Since the user cannot be found, cannot change the assignee.",
    retro: {
      title: "Previous action items",
      mobile_guide: "Check off completed action items.\nIf you're no longer working on one, tap it and choose Stop showing.",
      ai_estimate: "AI estimate",
      ai_estimate_completed: "Likely done",
      ai_nudge_tooltip: "Based on your meeting conversations, this action item looks already completed \u2014 mark it done if that's right.",
      dismiss: "Stop showing",
      undo: "Undo",
      checkin: {
        guide: "Before you dive in, how about going over the action items carried over from your last meeting together?\nCheck off the ones you've completed.",
        done: "Done reviewing",
        settled_group: "{{count}} already sorted out",
        save_failed: "Couldn't save. Please try again in a moment.",
        revisit_badge: "In progress {{count}} meetings in a row",
        revisit_question: "This one has stayed in progress. How about talking through where it's getting stuck?",
        revisit_summary: "Quite a few of these have carried over from past meetings. How about sorting them out together this time?"
      },
      verdict: {
        pending: "Not started",
        progressing: "In progress",
        completed: "Done",
        not_discussed: "Not discussed",
        dismissed: "Stopped"
      }
    },
    overview: {
      title: "Action Items",
      description: "Review and manage action items from your meetings, by member.",
      no_pairs: "No 1on1 members yet. Create a meeting and they'll show up here.",
      empty: "No action items with this member yet.",
      empty_open: "No action items in progress.",
      stat_open: "In progress",
      stat_completed: "Done",
      next_meeting: "Next meeting",
      group_open: "In progress",
      group_completed: "Done",
      group_dismissed: "Stopped",
      sort_hint: "Longest-running first",
      pair_sort_label: "Sort",
      pair_sort_open: "Most in progress",
      pair_sort_upcoming: "Upcoming meeting",
      pair_sort_recent: "Recent meeting",
      ongoing_since: "Since {{date}}",
      ongoing_since_tooltip: "This item has been ongoing since the {{date}} meeting",
      completed_date: "Done {{date}}",
      show_more: "Show {{count}} more",
      last_meeting: "Last meeting {{date}}",
      upcoming_meeting: "Next: {{date}}",
      rel_today: "today",
      rel_yesterday: "yesterday",
      rel_tomorrow: "tomorrow",
      rel_days_ago: "{{count}} days ago",
      rel_weeks_ago: "{{count}} weeks ago",
      rel_weeks_ago_one: "a week ago",
      rel_months_ago: "{{count}} months ago",
      rel_months_ago_one: "a month ago",
      rel_years_ago: "{{count}} years ago",
      rel_years_ago_one: "a year ago",
      rel_days_later: "in {{count}} days",
      rel_weeks_later: "in {{count}} weeks",
      rel_weeks_later_one: "in a week",
      rel_months_later: "in {{count}} months",
      rel_months_later_one: "in a month",
      no_last_meeting: "No meetings yet",
      no_upcoming: "No upcoming meeting",
      tbd: "TBD",
      topic_open_label: "Open topics",
      topic_unclassified: "Uncategorized",
      no_recent_items: "No records in the last 3 months",
      topic_breakdown_title: "Topic mix in the last 3 months \xB7 {{count}}",
      topic_breakdown_info: "What you talked about over the last 3 months, regardless of progress.",
      timeline_meeting_upcoming: "{{date}} meeting (upcoming)",
      timeline_open_meeting: "Go to meeting",
      timeline_created: "Created this action item",
      timeline_upcoming_check: "You'll go over this item together",
      timeline_no_record: "Nothing was recorded",
      timeline_ai_estimate: "AI estimate: {{state}}",
      timeline_confirmed_completed: "Marked as done",
      timeline_confirmed_dismissed: "Marked as stop showing",
      timeline_confirmed_completed_on: "Marked as done on {{date}}",
      timeline_confirmed_dismissed_on: "Marked as stop showing on {{date}}",
      timeline_confirmed_generic: "Marked as {{state}}",
      timeline_no_history: "Only the completion record is available for this item.",
      linked_from_label: "Continued from a previous action item",
      linked_to_label: "Continued in a new action item",
      topic_info: "AI categorizes action items by topic based on the meeting conversation. Counts cover items in progress, and selecting a topic also filters done and stopped items.",
      load_failed: "Couldn't load action items. Please try again in a moment.",
      save_failed: "Couldn't save the change. Please try again in a moment."
    }
  },
  ai_analysis: {
    failed: {
      title: "AI analysis failed",
      message: "Please try again.",
      retry_button: "Retry analysis",
      max_retries_reached: "Retry limit reached. Please check again later."
    },
    additional_analysis: "Additional analysis information will be displayed here",
    ai_summary_title: "AI Summary",
    brief_view: "Brief",
    expanded_view: "Expanded",
    analysis: "Analysis",
    auto_generated: "Auto-generated",
    close: "Close",
    conversation_content: "Conversation",
    copied: "Copied",
    copy_transcript: "Copy",
    core_content: "Core Content",
    decisions_made: "Decisions Made",
    detailed_summary: "Detailed Summary",
    edited: "Edited",
    feedback_for_leader: "Feedback for Leader",
    improvement_needed: "Areas for improvement",
    leader: "Leader",
    member_feedback: {
      title: "Feedback for Your Next Meeting",
      private_badge: "Only visible to you",
      intro: "Looking back on this meeting, we prepared {{count}} points for you. Keep them in mind as you prepare for your next meeting.",
      intro_one: "Looking back on this meeting, we prepared one point for you. Keep it in mind as you prepare for your next meeting.",
      nudge: {
        message: "{{count}} feedback points are ready, only visible to you. We looked back at what you said in this meeting and noted what to try next time.",
        message_one: "One feedback point is ready, only visible to you. We looked back at what you said in this meeting and noted what to try next time.",
        message_mobile: "{{count}} feedback points are ready, only visible to you.",
        message_mobile_one: "One feedback point is ready, only visible to you.",
        button: "View feedback"
      },
      label: {
        strength: "Landed well",
        not_raised: "Left unsaid",
        improve: "Try saying it this way",
        settled: "Reached a conclusion",
        open_issue: "Pick up next time",
        unknown: "Worth reflecting on"
      },
      source: {
        meeting: "From the meeting",
        my_prep: "From your prep",
        prep_chat: "From your prep chat",
        unknown: "Context"
      }
    },
    loading: {
      analyzing_qa: "Analyzing questions and answers...",
      analyzing_title: "Analyzing 1on1 Meeting",
      browser_notification: "browser notification",
      calculating_ratio: "Calculating speaking ratio...",
      complete_notification_body: 'Analysis of "{{title}}" meeting is complete.',
      complete_notification_body_default: "AI meeting analysis is complete.",
      complete_notification_title: "AI Analysis Complete",
      converting_text: "Converting conversation to text...",
      deriving_insights: "Deriving AI insights...",
      description: "It takes 2\u20135 minutes depending on the recording length.\nWe'll notify you by email and app push when it's done,\nso feel free to go do other work!",
      description_mobile: "It usually takes about 5 minutes,\nand may take longer depending on server conditions.\nFeel free to navigate to other screens!",
      extracting_action_items: "Extracting action items...",
      finding_topics: "Identifying key topics...",
      generating_title: "Generating meeting title...",
      loading_recording: "Loading recording file...",
      organizing_feedback: "Organizing leader feedback...",
      retry_failed_message: "Please try again.",
      retry_failed_title: "Retry Failed",
      summarizing: "Summarizing content...",
      tip_title: "Read while you wait",
      tip_subtitle: "Leadership articles from Orblit",
      title: "AI is analyzing your 1on1 meeting"
    },
    meeting_content: "Content",
    member: "Member",
    no_data: "No AI analysis results yet. Please check again later.",
    no_voice_detected: "No voice detected",
    no_voice_detected_description: "No voice was detected in the recording,\nso analysis results are unavailable.",
    no_meeting_data: "Meeting data not found.",
    no_questions: "No questions and answers written.",
    no_transcript_available: "Transcript is not available",
    notification: {
      save_error: "Failed to save. Please try again.",
      title: "AI Summary"
    },
    tab_feedback_question: "Was this helpful?",
    tab_feedback_question_summary: "Was this summary helpful?",
    tab_feedback_question_analysis: "Was this analysis helpful?",
    tab_feedback_up: "Helpful",
    tab_feedback_down: "Not helpful",
    tab_feedback_submitted_title: "Feedback submitted!",
    tab_feedback_submitted_message: "Thanks for your feedback",
    positive_feedback: "What went well",
    recording_completed: "Recording Completed",
    speaking_ratio: "Speaking Ratio",
    summary: "Summary",
    support_needs: "Support Needs/Blockers",
    title: "AI Analysis",
    transcript_disclaimer: "This is a reference transcript auto-generated from the actual speech. Speaker labels, spelling, and other details may not exactly match the actual conversation.",
    transcript_processing: "Transcript is being processed. Please try again later.",
    transcript_title: "View Full Transcript",
    view_transcript: "View Original"
  },
  aria: {
    create_meeting: "Create meeting",
    create_one_on_one: "Create new 1on1"
  },
  counterpart: {
    empty: "No members available to select.",
    invite_reward: "Invite a teammate to this workspace to set them as a meeting counterpart.\nOnce they accept, you'll get 2 more meeting analyses.",
    invite_where: "In the left sidebar, click 'Invite Members' to invite your teammates.",
    group_my_member: "Direct Reports",
    group_pair: "Periodic 1on1 Members",
    group_same_team: "Team Members",
    label: "Who is your meeting counterpart?"
  },
  self_meeting: {
    or: "or",
    mode_self: "Create without a counterpart",
    self_description: "A solo trial run. Real 1on1s happen with a teammate you've invited.",
    balance_label: "{{count}} left",
    balance_banner: "You have {{count}} meetings left to analyze",
    no_balance: "You've used all your analyses. On a paid plan, you can analyze without limits.",
    balance_warning: "You've used all your analyses. This meeting runs without recording or AI analysis. On a paid plan, you can analyze without limits.",
    card_badge: "Unassigned",
    label: "(Unassigned)",
    detail_nudge: "Invite a teammate to also capture the counterpart's info",
    update_description: "This meeting has no counterpart assigned.\nOnly the date and time can be edited.",
    planned_card_label: "No counterpart",
    planned_card_description: "No counterpart assigned"
  },
  date: {
    days_ago: "{{count}} days ago",
    days_left: "{{count}} days left",
    label: "Please choose the meeting date.",
    overdue: "{{count}} days overdue",
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday"
  },
  day_of_week: {
    label: "Please select the meeting day of the week."
  },
  dialog: {
    complete_button: "Mark as Complete",
    complete_description: "Have you already completed this meeting?\nThe meeting will be marked as completed and the record will be finalized.",
    complete_error: "Failed to complete meeting. Please refresh and try again.",
    create: "Create meeting",
    date_format: "MMMM D",
    delete_description: "Are you sure you want to delete this 1on1 meeting?",
    delete_error: "Failed to delete 1on1 meeting. Please try again.",
    delete_success: "1on1 meeting has been deleted.",
    delete_title: "Delete 1on1 meeting",
    leader: "Leader",
    meeting_time_label: "Meeting time",
    member: "Member",
    no_members: "No members available.",
    role_label: "My role",
    search_member_placeholder: "Enter a name or email address.",
    select_counterpart: "Select your meeting counterpart",
    select_date: "Select a date for your meeting with {{name}}",
    select_date_self: "Select a date for your meeting",
    select_time: "Select meeting time for {{date}} with {{name}}.",
    select_time_self: "Select a time for your {{date}} meeting.",
    title: "Create 1on1 meeting",
    update_title: "Update 1on1 meeting"
  },
  duration: {
    minutes: "{{value}} min"
  },
  error: {
    api_endpoint_missing: "API endpoint URL is not configured.",
    api_request_failed: "API request failed",
    api_response_error: "API error response",
    invalid_input: "Invalid input detected.",
    meeting_id_missing: "Meeting not found.",
    question_generation_failed: {
      message: "Failed to generate questions. Please refresh the page and try again.",
      title: "AI Question Generation Failed"
    }
  },
  frequency: {
    biweekly: "Every 2 weeks",
    every_3_weeks: "Every 3 weeks",
    every_5_weeks: "Every 5 weeks",
    every_6_weeks: "Every 6 weeks",
    every_7_weeks: "Every 7 weeks",
    every_8_weeks: "Every 8 weeks",
    label: "Please select the meeting frequency.",
    monthly: "Every 4 weeks",
    no_schedule: "No regular schedule",
    placeholder: "Select frequency",
    weekly: "Weekly"
  },
  leader: {
    counterpart: "Counterpart",
    info: "The leader can start and end the meeting, check counterpart's work engagement and growth satisfaction, and receive 1on1 analysis insights.",
    label: "Select the leader.",
    me: "Me"
  },
  meeting_type: {
    label: "Select meeting type.",
    offline: "In-person",
    online: "Video",
    video_link_placeholder: "Paste a Google Meet link",
    video_link_description: "Only Google Meet links are supported for now. (Zoom coming soon)",
    invalid_meet_link: "Please enter a valid Google Meet link.",
    create_meet: "Create new meeting",
    create_meet_link: "Create Google Meet link",
    creating_meet_link: "Creating link...",
    meet_link_created: "Google Meet link has been created.",
    meet_link_error: "Failed to create Meet link.",
    additional_scope_required: "Additional permissions are required to use video meeting features.",
    meet_scope_granted: "Google Meet permissions have been connected.",
    grant_permission: "Grant permission",
    no_link: "No meeting link registered yet.",
    add_link: "Add",
    edit_link: "Edit link",
    add_link_placeholder: "Enter a Google Meet link",
    add_link_save: "Save",
    copy_link: "Copy link",
    link_copied: "Meeting link copied.",
    link_saved: "Meeting link saved.",
    link_save_error: "Failed to save meeting link.",
    auto_generate_meet: "Auto-generate Google Meet",
    auto_generate_meet_desc: "Meet link will be created with the {{email}} account",
    manual_input: "Enter manually",
    or_manual_input: "Or enter link manually",
    connect_calendar_hint: "Connect Google Calendar to automatically generate Meet links.",
    connect_calendar_action: "Connect now",
    not_organizer_hint: "This meeting is on your counterpart's calendar, so auto-generation isn't available. Please enter a link manually below.",
    join_meeting: "Join meeting",
    complete_meeting: "Complete 1on1 meeting",
    complete_meeting_tooltip: "If you've ended the Meet call, please complete this meeting!",
    guide: "Join the meeting via the link and click the Start recording button to turn on recording. A recording is required to analyze the meeting.",
    guide_link: "How to record",
    join_issue: "Having trouble joining?",
    join_issue_description: "If you see 'Ask to join' instead of 'Join now' when opening the link, check that the Google account currently signed in to your browser matches the one that created the Meet link.",
    complete_dialog: {
      title: "Have you ended the Meet call?",
      description: "Once the recording is ready on Google Drive (about 10\u201315 min), we'll send you an email.\nAfter receiving it, click [Import recording] to select the file \u2014 AI analysis will start."
    },
    recording_guide_dialog: {
      title: "How to Record on Google Meet",
      as_of_date: "As of April 2026",
      learn_more: "Learn more",
      pre_join_emphasis: "AI analysis is only available for recorded meetings",
      pre_join_subtext_action: "Click [Record] right after the meeting begins",
      pre_join_subtext_plan: "Recording is only available on Google Workspace Business Standard plan or higher",
      dont_show_again: "Don't show again",
      join_now: "Join meeting",
      acknowledge: "OK",
      cancel: "Cancel"
    },
    import_status: {
      polling: "Google Meet recording is in progress.",
      processing: "Preparing the Meet recording file... (usually 5\u201310 min)",
      ready: "Your recording is ready. Tap the import button.",
      ready_hint: "If you didn't start the recording yourself, attaching the file may fail. We recommend that the person who started the recording attaches it.",
      importing: "Importing recording from Google Drive...",
      completed: "Meet recording ready",
      not_found: "Meet recording not found.",
      failed: "Failed to import Meet recording",
      manual_upload_hint: "If you have the recording file locally, use 'Upload recording' at the bottom."
    }
  },
  meet_recording: {
    picker: {
      button_label: "Import recording",
      api_key_missing_title: "Picker not configured",
      api_key_missing_message: "Google Picker API key is missing. Please contact your admin.",
      load_error_title: "Failed to load Picker",
      load_error_message: "Could not load Google Picker. Please try again shortly.",
      attach_success_title: "Recording attached",
      attach_success_message: "Pulling the file from Drive and starting AI analysis.",
      attach_error_title: "Failed to attach recording",
      attach_error_message: "An error occurred while attaching the recording.",
      attach_error_no_access: "This file may live in someone else's Drive. Please ask the person who started the recording to attach it."
    }
  },
  meeting: {
    meeting_home: "Home",
    no_previous_meeting: "No history"
  },
  meeting_detail: {
    prep: "Prep",
    previous_action_items: "Previous Action Items",
    add_question: {
      error: "An error occurred while adding the question.",
      success: "The question has been added to the question list in the Content tab.",
      title: "Add Questions",
      success_bulk: "{{count}} questions have been added to the question list in the Content tab."
    },
    ai_generate: "AI question creation",
    ai_generated: "Generated by AI",
    answer_placeholder: "To edit the answer, click here.",
    answer_placeholder_new: "Please enter the answer.",
    bring_template: "Import template",
    delete_recommended_question: {
      error: "An error occurred while deleting the question.",
      title: "Delete Recommended Question"
    },
    editing_placeholder: "You can enter an answer after saving the question.",
    end_meeting: {
      action_button: "End 1on1 Meeting",
      description: "Would you like to end the one-on-one meeting?",
      error_message: "An error occurred while ending the meeting.",
      error_title: "Meeting End Failed",
      network_notice: "The recording is uploaded automatically when the meeting ends. A stable network environment is ideal for the upload.",
      title: "Meeting End Notice",
      other_device_title: "Looks like another device is recording",
      other_device_body: "Please end the meeting on the device that's recording. If you end it here without a recording, the meeting can't be analyzed.",
      other_device_end_anyway: "End without recording"
    },
    is_editing: "{{editor}} is editing...",
    leader: "Leader",
    my_note: "My Note",
    my_note_placeholder: "This is your personal memo.",
    past_meetings_title: "Previous meetings with {{name}}",
    past_meetings_title_generic: "Previous meetings",
    past_meetings_empty: "No records before this meeting",
    past_meetings_self_unavailable_title: "Previous records unavailable",
    past_meetings_self_unavailable: "You can view previous meeting records in 1:1 meetings with an assigned counterpart.",
    question_delete_modal: {
      description: "Are you sure you want to delete this question?",
      title: "Delete Question"
    },
    question_placeholder: "Please enter the question",
    question_request: "Request question review",
    question_request_success: "Question review request email has been sent successfully.",
    question_request_failed: "Failed to send question review request.\nPlease try again later.",
    questions: "Questions",
    recording_duration: "Recorded",
    recording_time: "Recording",
    draft_badge: "Draft",
    draft_onboarding: "Your questions will only be visible to your counterpart after clicking 'Publish Questions'.",
    publish_questions: "Publish Questions",
    publish_questions_confirm: "{{count}} draft questions will be shared with your counterpart,\nand a question review request email will be sent.\nDo you want to publish?",
    confirm_questions_success: "Questions confirmed successfully.",
    send_questions: "Request question review",
    send_questions_confirm: "Send a question review request email to your counterpart?",
    send_questions_confirm_mobile: "You can request a question review via email and notification.\nWould you like to send a review request to your counterpart?",
    start_meeting: {
      action_button: "Start Meeting",
      agree_description: "Please check if you have obtained consent from the counterpart.\nIf there is no mutual consent, the meeting will proceed without recording.",
      agree_title: "I have obtained consent from the counterpart.",
      ai_consent_title: "I agree that recording data will be sent to third-party AI services for meeting notes and summaries.",
      ai_consent_details_toggle: "View details",
      ai_consent_details_body: "Your recorded audio and transcribed text will be sent to the following third-party AI services for meeting notes generation, content summarization, and speaking ratio analysis.\n- AssemblyAI Inc. (speech-to-text)\n- Google LLC (Vertex AI/Gemini)\n- OpenAI OpCo, L.L.C.\n- Anthropic PBC\n\nYour consent is stored on this device. You will be re-prompted if the privacy policy changes.",
      ai_consent_privacy_policy_link: "View Privacy Policy",
      description_record_on: "Meeting minutes and content summaries will be automatically generated after the meeting.",
      description_record_off: "You can still proceed without recording.\nIf you enable recording, Orblit will help you with meeting minutes and content summaries.",
      draft_notice: "Starting the meeting will publish all draft questions.\nPlease delete any questions you don't want to share before starting.",
      record: "Recording feature",
      recording_notice: "Using the app in the background is fine while recording, but please do not force-quit the app \u2014 the recording file may be lost.",
      title: "Would you like to start the meeting?"
    },
    start_meeting_button: "Start recording",
    survey: {
      button: {
        later: "Do Later",
        save: "Save"
      },
      engagement: {
        description: "How satisfied and engaged is {{name}} with their current work and company?",
        title: "Work Engagement"
      },
      growth: {
        description: "To what extent does {{name}} feel they are growing and developing in the organization?",
        title: "Growth Satisfaction"
      },
      notifications: {
        delete: "Survey has been deleted.",
        error: "Failed to submit survey.",
        success: "Survey has been submitted.",
        title: "Regular Check-in Submission",
        update: "Survey has been updated."
      },
      rating: {
        bad: "Bad",
        fair: "Fair",
        good: "Good",
        very_bad: "Very Bad",
        very_good: "Very Good"
      },
      regular_checkin: "Regular Check-in",
      select_rating: "Select rating",
      title: "Meeting has ended!"
    },
    pair_frequency: {
      set_label: "Set 1on1 frequency",
      manage_title: "1on1 frequency with {{name}}",
      button: {
        save: "Save",
        remove: "Remove frequency"
      },
      remove_confirm: {
        title: "Remove 1on1 frequency",
        description: "Remove the 1on1 frequency with {{name}}? You'll no longer get reminders when it's time to schedule the next meeting."
      },
      notification: {
        title: "1on1 frequency",
        saved: "Meeting frequency saved.",
        removed: "1on1 frequency removed.",
        error: "Something went wrong. Please try again later."
      }
    },
    next_meeting_nudge: {
      create_meeting: {
        title: "Your 1on1 with {{name}} has ended",
        description: "Would you like to schedule the next meeting now?",
        date_label: "Date",
        date_hint: "Suggested based on your {{weeks}}-week frequency.",
        date_hint_other: "Based on your {{weeks}}-week frequency, the suggested date is {{date}}.",
        suggested_label: "Suggested date based on your {{weeks}}-week frequency",
        time_label: "Time",
        time_placeholder: "Select a time",
        duration_label: "Duration",
        button: {
          later: "Later",
          edit_schedule: "Edit schedule",
          confirm_suggested: "Schedule as suggested",
          confirm: "Schedule Next Meeting"
        },
        notification: {
          title: "Schedule Next Meeting",
          success: "Your next meeting has been created.",
          error: "Couldn't create the meeting. Please create it from the meeting list."
        }
      },
      setup_pair: {
        title: "Keep meeting with {{name}} regularly",
        description: "Decide together how often to meet.\nWe recommend every 2 weeks, so the conversation stays warm before it fades.",
        frequency_label: "Meeting Frequency",
        frequency_hint: "Shorter, more frequent meetings every 2 weeks tend to work better than one long meeting a month.",
        reminder_notice: "Once you save a frequency, we'll send an email and app push to remind you if the next meeting isn't scheduled yet.",
        button: {
          later: "Later",
          confirm: "Set Frequency"
        },
        notification: {
          title: "Meeting Frequency",
          success: "Your meeting frequency has been set.",
          success_mobile: "Your meeting frequency has been set. You can change it anytime at the top of the meeting screen.",
          error: "Couldn't set the frequency. Please set it again from the right panel on the meeting page.",
          error_mobile: "Couldn't set the frequency. Please try again later."
        }
      }
    }
  },
  meeting_pdf: {
    modal_title: "Save as PDF",
    modal_description: "Select items to include in the file.",
    header_title: "1on1 Meeting",
    prep: "Prep",
    prep_summary: "Summary",
    prep_detail: "Detail",
    condition: "Condition",
    topic: "Meeting Topic",
    questions: "Questions & Answers",
    ai_summary: "AI Summary",
    action_items: "Action Items",
    previous_action_items: "Previous Action Items",
    current_action_items: "Current Action Items",
    due_date: "Due",
    summary: "Summary",
    decisions_made: "Decisions Made",
    support_needs: "Support Needs",
    analysis: "Analysis",
    speaking_ratio: "Speaking Ratio",
    positive_feedback: "Strengths",
    negative_feedback: "Areas for Improvement",
    download: "Download",
    error: "Failed to generate PDF. Please try again."
  },
  meeting_page: {
    complete: "Mark Meeting Complete",
    download: "Save as PDF",
    edit: "Change schedule",
    empty_records: "No completed meeting records yet.",
    empty_upcoming: "No upcoming meetings.",
    meeting_item: "Meeting item",
    my_meetings: "My meetings",
    my_records: "My meeting records",
    table: {
      actual_date: "Actual date",
      actual_duration: "Actual duration",
      ai_summary: "AI summary",
      counterpart: "Counterpart",
      edit_summary: "Edit summary",
      edit_summary_error: "Failed to update summary. Please try again.",
      edit_summary_placeholder: "Enter the summary",
      edit_summary_success: "Summary has been updated.",
      in_progress: "In progress",
      title: "Title"
    },
    title: "1on1 Meetings",
    unscheduled: "Unscheduled",
    untitled: "Untitled",
    upcoming: "Upcoming Meetings",
    placeholder_first: "Create your first 1on1"
  },
  member_add_dialog: {
    add: "Add Member",
    title: "Add Recurring 1on1 Member"
  },
  member_report: {
    create_button: "Create New Report",
    create_dialog: {
      all_meetings_selected: "All {{total}} meetings",
      cancel: "Cancel",
      create_report: "Create Report",
      creating: "Creating...",
      creation_failed: "Failed to create report",
      data_collection_period: "Select Data Collection Period",
      description: "Create a performance report for your team members.",
      duration: "Duration",
      error: "Failed to create report. Please try again.",
      fill_all_fields: "Please fill in all fields",
      max_three_months: "Data can be selected for up to 3 months",
      meetings_count: "{{selected}}/{{total}} meetings",
      member_not_found: "Selected member not found",
      minutes: "{{minutes}} min",
      no_direct_reports: "No direct reports available",
      no_meetings_in_period: "No 1on1 meetings in this period",
      no_meetings_selected: "No meetings selected",
      no_title: "No title",
      report_created: "Report has been created successfully",
      select_meetings: "Please select 1on1 meetings",
      select_member: "Select Member",
      select_member_and_period: "Please select a member and period first",
      selected_meetings: "Selected 1on1s",
      success: "Report has been created successfully.",
      title: "Create New Report",
      topic: "Topic"
    },
    date_period: {
      custom: "Custom period",
      last_1_month: "Last 1 month",
      last_3_months: "Last 3 months",
      select_period: "Select period"
    },
    detail: {
      action_items: "Action Items",
      ai_loading: {
        analyzing_conversation: "Analyzing conversation content...",
        analyzing_growth: "Analyzing member growth points...",
        collecting_data: "Collecting 1on1 meeting data...",
        creating_management_plan: "Creating customized management plan...",
        deriving_insights: "Deriving leadership insights...",
        evaluating_flight_risk: "Evaluating Flight Risk...",
        finalizing_report: "Finalizing the report...",
        generating_topics: "Generating recommended topics for next 1on1...",
        identifying_topics: "Identifying key topics...",
        organizing_performance: "Organizing performance and improvements..."
      },
      back_to_list: "Back to List",
      cancel: "Cancel",
      contribution: "Contribution",
      delete: "Delete",
      delete_confirm: "Are you sure you want to delete this report?",
      delete_failed: "Delete Failed",
      delete_failed_message: "Failed to delete report.",
      delete_success: "Deleted",
      delete_success_message: "Report has been deleted successfully.",
      demotivation_factors: "Demotivation Factors",
      emotion_wellbeing: "Emotion/Well-being",
      error_title: "Failed to generate report",
      error_description: "An error occurred during AI analysis.\nPlease try again.",
      export_pdf: "Export as PDF",
      flight_risk: "Flight Risk",
      goals: "Goals",
      growth: "Growth",
      interested_projects: "Projects of Interest",
      interests: "Interests",
      loading_description: "Generating report from your 1on1 meeting data.\nPlease wait a moment.",
      management_plan: "Customized Management Plan",
      member: "Member",
      member_report: "{{name}}'s Report",
      motivation: "Motivation",
      motivation_factors: "Motivation Factors",
      network: "Network",
      next_topics: "Recommended Topics for Next 1on1",
      no_action_items: "No action items.",
      no_content: "No content available.",
      no_data: "No data",
      no_topics: "No recommended topics.",
      not_found: "Report not found.",
      personal: "Personal",
      retry: "Retry",
      skills: "Skills",
      strength_weakness: "Strengths and Weaknesses",
      strengths: "Strengths",
      tab: {
        detail: "Detail",
        summary: "Summary"
      },
      weaknesses: "Weaknesses",
      work_blockers: "Work Blockers",
      work_content: "Work Content",
      work_performance: "Work Performance"
    },
    empty: {
      no_reports: "No reports created yet."
    },
    empty_state: "No reports yet. Click 'Create New Report' to get started.",
    list: {
      date: "Created Date",
      member: "Member",
      status: "Status",
      title: "Reports"
    },
    statistics: {
      avg_duration: "Avg Duration",
      completed_meetings: "Completed 1on1s",
      hours: "h",
      meeting_count: "Meeting Count",
      minutes: "m",
      seconds: "s",
      times: "{{count}} time(s)",
      together_1on1: "1on1 Together"
    },
    table: {
      created_date: "Created Date",
      header: {
        content: "Content",
        member: "Member",
        period: "Period",
        register_date: "Created Date"
      },
      ic_report: "IC Report",
      leader_report: "Leader Report",
      member: "Member",
      period: "Period",
      report_type: "Report Type"
    },
    title: "Member Report"
  },
  member_update_dialog: {
    title: "Update Recurring 1on1 Member"
  },
  mobile: {
    tabs: {
      prep: "Prep",
      content: "Content",
      analysis: "Analysis",
      action_item: "Action Items",
      note: "Notes",
      qna: "Q&A",
      summary: "Summary",
      transcript: "Transcript"
    },
    my_note: {
      placeholder: "Write a personal memo to refer to during the meeting.",
      save_failed: "Failed to save your note. Please try again later."
    }
  },
  recording: {
    badge: "Recording",
    credit_notice: "When the recording ends, analysis starts automatically and uses 1 analysis.",
    upload_confirm: {
      title: "Analyze this file?",
      body: "We'll start analyzing the meeting with the selected file right away.",
      credit_notice: "Once analysis starts, 1 analysis will be used.",
      cancel: "Cancel",
      confirm: "Start analysis"
    },
    error: "Recording error",
    error_mic_switch: "Failed to switch microphone. Please check your microphone connection.",
    error_browser_not_supported: "Audio recording is not supported in this browser.",
    error_mic_permission: "Please check your microphone permission.",
    error_update_failed: "Failed to update meeting information.",
    error_save_failed: "An error occurred while saving.",
    error_network_offline: "Network connection was lost. Recording has been stopped.",
    error_recorder_error: "An error occurred during recording.",
    error_track_ended: "Recording stopped due to microphone disconnection.",
    in_progress: "Recording...",
    owned_elsewhere: "Recording is in progress in another tab.",
    max_duration_reached: "Maximum recording time ({{hours}} hours) reached. Recording has been automatically stopped. End the meeting to analyze the existing recording.",
    mic_selector: {
      level_label: "Microphone input level",
      placeholder: "Select a microphone",
      silence_warning: "No sound detected from this microphone.\nPlease check your microphone status.",
      no_device_warning: "No microphone available. Please connect a microphone or check your browser's microphone permissions.",
      unknown_device: "Microphone {{id}}",
      title: "Select Microphone"
    },
    pause: "Pause",
    pending: {
      description: "A previous recording ({{duration}}) was not uploaded. Would you like to upload it now?",
      description_mobile: "There is a previous recording that was not uploaded. Please upload or discard the recording.",
      description_corrupted: "The previous meeting's recording was not saved properly. You can restart the meeting from the beginning, or end it without a recording.",
      description_discard: "Discarding will restart the meeting from the beginning.",
      description_restore: "Uploading will end the meeting.",
      discard: "Discard",
      restore: "Upload",
      title: "Unsaved Recording Found",
      restart_from_beginning: "Restart from Beginning",
      end_without_recording: "End Without Recording"
    },
    restart: "Restart Recording",
    save: "Save Recording",
    start: "Start Recording",
    stop: "Stop",
    title: "Meeting Recording",
    title_with_name: "1:1 with {{name}}",
    title_with_name_mobile: "1:1 with {{name}} in progress",
    title_self_meeting: "1on1 in progress",
    upload_recording: {
      cancel_upload_message: "Are you sure you want to cancel the upload?",
      cancel_upload_title: "Cancel upload",
      continue_upload: "Continue upload...",
      description: "If you already have a recorded file, please upload it.\nAI will analyze the recorded file and automatically generate a meeting summary.",
      duration_error: {
        description: "Recording files are supported up to {{hours}} hours.",
        title: "Recording duration exceeded"
      },
      duration_warn: {
        description: "The file information cannot be read, so the exact time cannot be displayed.",
        error: "Audio file cannot be read",
        title: "Audio duration information not found"
      },
      network_error: {
        alert: "The network connection is unstable. Please check your connection and try again.",
        description: "Please check your network connection.",
        error: "Network has recovered, but file upload failed again. Please refresh and try again.",
        failed: "File upload failed after network recovery.",
        reconnected: "Network restored",
        retry: "Retrying file upload...",
        success: "File uploaded successfully after network recovery. The meeting has ended.",
        title: "Network error"
      },
      size_error: {
        description: "The file size cannot exceed 1GB.",
        title: "File size error"
      },
      start_upload: "Start upload",
      title: "Upload Recording File",
      type_error: {
        description: "Only audio files can be uploaded.",
        title: "File type error"
      },
      upload_cancelled: "Upload cancelled",
      upload_cancelled_message: "Upload has been cancelled.",
      upload_error: "An error occurred while uploading the file. Please refresh and try again.",
      upload_success: "File has been uploaded successfully and the meeting has ended."
    },
    silence_warning: {
      leader: "No microphone input detected. Please check your microphone settings.",
      member: "No sound is being detected in the recording. Please ask the leader to check their microphone."
    },
    warn: {
      message: "Approaching maximum recording time ({{hours}} hours). Recording will automatically stop when {{hours}} hours is reached.",
      title: "Recording Time Limit Notification"
    },
    permission_required_title: "Microphone Permission Required",
    permission_required_description: "Please allow microphone access in Settings.",
    notification_permission_required_title: "Notification Permission Required",
    notification_permission_required_description: "Notification permission is required to show recording status while recording in the background. Please allow notifications in Settings.",
    go_to_settings: "Go to Settings",
    start_failed_title: "Failed to Start Recording",
    stop_failed_title: "Failed to Stop Recording",
    retry_message: "Please try again.",
    start_timeout_message: "The recorder is not responding. Please fully close and reopen the app.",
    stop_timeout_message: "The recording is still being saved. Please wait a moment and try ending again. If this continues, fully close and reopen the app, then follow the recording recovery steps.",
    uploading_title: "Saving your recording...",
    upload_failed_title: "Failed to save recording.",
    retry_upload_button: "Upload Again",
    no_network_title: "No Network Connection",
    no_network_message: "Please check your network connection and try again.",
    file_missing_title: "Recording File Not Found",
    file_missing_message: "The recording file has been removed from your device, so analysis cannot proceed. The meeting will be closed as-is."
  },
  right_panel: {
    add_member: "Add 1on1 member",
    add_pair: {
      error: "Failed to add recurring 1on1 member. Please try again.",
      success: "Recurring 1on1 member has been added.",
      title: "Add Recurring 1on1 Member"
    },
    delete_pair_modal: {
      description: "Are you sure you want to delete this recurring 1on1 member?",
      error: "Failed to delete recurring 1on1 member. Please try again.",
      success: "Recurring 1on1 member has been deleted.",
      title: "Delete Recurring 1on1 Member"
    },
    empty_action_items: "No action items yet.",
    member_list_aria: "1on1 member list",
    member_subtitle: "{{count}} 1on1s \u2022 {{days}} days ago",
    more_actions: "More actions",
    no_history: "No history",
    recurring_title: "Recurring 1on1 members ({{count}})",
    update_pair: {
      error: "Failed to update recurring 1on1 member. Please try again.",
      success: "Recurring 1on1 member has been updated.",
      title: "Update Recurring 1on1 Member"
    }
  },
  sidebar: {
    action_items: "Action Items",
    create_one_on_one: "Create new 1on1",
    dashboard: "Dashboard",
    guide: {
      guide_button: "Guide",
      service_name: "Orblit 1on1"
    },
    invite_members: "Invite Members",
    member_report: "Member Report",
    nine_box: "9Box",
    one_on_one_meeting: "1on1 Meetings",
    guide_menu: {
      trigger: "Help",
      leader_guide: "Leader Start Guide",
      member_guide: "Member Guide",
      service_guide: "Full Service Guide",
      voc: "Contact Us"
    },
    settings: "Settings",
    update_note: "Update Notes",
    whats_new: {
      title: "WHAT'S NEW !",
      headline: "Feedback for you, after every meeting",
      message: "Members now get feedback for their next meeting too",
      go_to_feature: "",
      view_update_note: "View Update Notes",
      dismiss: "Close"
    }
  },
  voc: {
    title: "Contact Orblit",
    description: "Have a question or something not working as expected? Send us the details and we'll reply to your registered email.",
    field: {
      message: "Message",
      message_hint: "If something went wrong, sharing what happened before and after\u2014as much as you're comfortable with\u2014helps us identify the cause."
    },
    placeholder: {
      message: "Tell us what you'd like help with."
    },
    contact_notice: "We'll send our reply to your registered email.",
    action: {
      cancel: "Cancel",
      submit: "Send Inquiry",
      confirm: "Done"
    },
    complete: {
      title: "Inquiry Received",
      description: "We'll review your message and reply to your registered email."
    },
    error: {
      submit_failed: "We couldn't send your inquiry. Please try again in a moment."
    }
  },
  template: {
    add_questions: "Add Questions",
    add_questions_button: "Use Selected Questions ({{count}})",
    add_questions_error: "Failed to add questions",
    ai_template_input_section: {
      ai_suggestion: "AI Suggestions",
      context: "Is there any context or situation to reference for question generation?",
      context_placeholder: " \u2022 Problem situations (performance, collaboration, job change signs, etc.)\n \u2022 Project progress\n \u2022 Career and growth concerns",
      counterpart: "Tell us about your meeting partner.",
      counterpart_placeholder: "Description",
      create: "Create AI Template",
      details: "Detailed adjustments",
      hide_label: "Show less",
      include_multiple_choices: "Include multiple-choice questions",
      last_meeting_content: "Previous Input",
      meeting_prep: "Prep",
      meeting_prep_tooltip: "Prep content will be used to generate questions. If you'd like to include more details, please add them below.",
      previous_meeting: "Previous 1on1 records",
      question_count: {
        Advanced: "Detailed",
        Simple: "Simple",
        Standard: "Standard",
        title: "Number of generated questions"
      },
      question_style: "Please select the style of your question.",
      question_types: {
        "Action/Implementation-focused": {
          description: "What specific actions will you take in the next 3 months?",
          title: "Action/Implementation"
        },
        "Experience/Story-based": {
          description: "What is the most memorable project experience you've had recently?",
          title: "Experience/Story"
        },
        "Growth/Goal-oriented": {
          description: "What do you want to become in a year?",
          title: "Growth/Goal-oriented"
        },
        "Multiple-choice": {
          description: "Please evaluate your current job satisfaction on a scale of 1-5",
          title: "Objective"
        },
        "Reflection/Thought-provoking": {
          description: "What do you find most meaningful in your current role?",
          title: "Reflection/Thinking"
        },
        "Relationship/Collaboration": {
          description: "What is the most helpful aspect of collaborating with colleagues?",
          title: "Relationship/Collaboration"
        },
        title: "Question Type"
      },
      recommended: "Recommended",
      show_label: "Show more",
      subject: "Please select topics you'd like to discuss.",
      subject_default_value: "This is the topic used to generate questions for the previous meeting.",
      subject_options: {
        action_review: "Action Item Check",
        feedback_coaching: "Feedback and Coaching",
        growth_career: "Growth and Career",
        relationship: "Relationships and Recent Updates",
        satisfaction_motivation: "Job Satisfaction and Motivation",
        work_status: "Work Status and Obstacles"
      },
      subject_placeholder: "Select a topic",
      title: "Create AI Template",
      tone: {
        Casual: "Casual",
        Formal: "Formal",
        title: "Tone & Manner"
      },
      use: "Use"
    },
    ai_template_output_section: {
      create_template_button: "Use Selected Questions ({{count}})",
      done: "Created!",
      generate_questions_prompt: {
        description: "AI will create customized questions and guides for your meeting.",
        title: "Fill out the left form and generate questions"
      },
      guide: {
        description: "AI is analyzing the selected questions and\nwriting a personalized 1:1 meeting guide.",
        generate_guide: "Generate question usage guide",
        loading: "Generating guide...",
        title: "AI Guide"
      },
      no_questions: "No questions generated.",
      title: "AI Template"
    },
    copied_to_clipboard: "Copied to clipboard",
    copied_to_clipboard_message: "Guide has been copied.",
    copy: "Copy",
    create: "Create new",
    create_page: {
      add_question: "Add question",
      delete_question: "Delete question",
      delete_question_description: "Are you sure you want to delete this question?",
      description_placeholder: "Please enter the template description",
      owner: "Owner",
      owner_user: "My template",
      owner_workspace: "Team template",
      question_placeholder: "Please enter the question",
      save_button: "Save template",
      title: "Create New Template",
      title_placeholder: "Please enter the template title"
    },
    delete_modal: {
      description: "Once deleted, the template cannot be recovered. Are you sure you want to delete it?",
      error: "An error occurred while deleting the template.",
      success: "Template has been deleted.",
      title: "Delete Template"
    },
    edit_page: {
      save_button: "Save",
      title: "Edit Template"
    },
    generating_loader: {
      analyzing_prep: "Analyzing the prep written by your counterpart...",
      applying_prompt: "Applying your custom instructions...",
      checking_profile: "Referencing your counterpart's communication style and preferences...",
      checking_session: "Checking previous meeting records...",
      composing_topics: "Composing questions based on your selected topics...",
      finalizing: "Finalizing the questions...",
      refining_tone: "Refining questions to match the conversation tone..."
    },
    owner_user: "My template",
    owner_workspace: "Team template",
    page_title: "Import template",
    placeholder: "Please select a template.",
    question_list: "Question List",
    refresh_message: "Please refresh the page and try again.",
    register_date: "Created date",
    success: "Questions have been successfully added.",
    system: "Orblit"
  },
  time: {
    label: "Please choose the meeting time."
  },
  onboarding_tour: {
    next: "Next",
    resume_banner: {
      body: "You have a tutorial in progress.",
      action: "Resume"
    },
    stages: {
      create: "Create meeting",
      questions: "Prepare questions",
      record: "Record",
      analysis: "Review analysis"
    },
    create_meeting: {
      title: "Shall we prepare your first 1:1?",
      body: "We'll show you how to run an effective 1:1.\nStart by tapping \u201CCreate your first 1:1\u201D."
    },
    self_meeting: {
      title: "You don't have to pick a counterpart",
      body: "We recommend inviting a teammate to share 1:1 questions and answers,\nbut you can try it on your own before inviting anyone."
    },
    self_meeting_has_members: {
      title: "Let's skip picking a counterpart for now",
      body: "Normally you'd pick a counterpart from the teammates above for your 1on1.\nSince this is a tutorial, let's try 'Create without a counterpart' for a quick run."
    },
    credit_info: {
      title: "You can analyze {{remaining}} meetings",
      body: "On a paid plan, we analyze your meetings and give the leader feedback. As a welcome gift, we gave you 3 analyses.\nInvite a teammate or refer another leader to get more."
    },
    create_submit: {
      title: "Now let's create the meeting",
      body: "You haven't assigned a teammate yet, so go ahead and create it now.\nFor a real teammate, set a date and time first."
    },
    add_question: {
      title: "This is where you prepare and run your 1on1",
      body: "You can type in your own questions here.\nJot down questions and record answers as the conversation goes."
    },
    load_template: {
      title: "First 1on1? Start with recommended questions",
      body: "You can write your own questions,\nbut loading ready-made ones makes it easier."
    },
    select_template: {
      title: "We recommend this template for a first 1on1",
      body: "Tap the card to see what questions are inside."
    },
    use_template_questions: {
      title: "Add these questions to your meeting?",
      body: "Tap \u201CUse selected questions\u201D\nto add the selected questions to your meeting."
    },
    start_recording: {
      title: "Just record \u2014 Orblit handles the rest",
      body: "In a real meeting, tap \u201CStart recording\u201D or upload a recording file,\nand Orblit organizes and analyzes it for you.\n\nSince this is a tutorial \u2014 want to try a sample recording to see how the analysis works?\n(The sample run doesn't use any of your analyses.)",
      analyze_sample: "Try analysis with a sample",
      analyze_sample_failed: "Sample analysis failed. Please tap \u201CTry analysis with a sample\u201D again."
    },
    sample_analyzing_screen: {
      title: "Preparing your sample analysis",
      body: "Hang tight \u2014 we'll show you an example analysis in a moment."
    },
    view_summary: {
      title: "Want to see this meeting's summary?",
      body: "Tap the 'Summary' tab.\nWe'll lay out the key points and decisions from the conversation at a glance."
    },
    view_analysis: {
      title: "Check out the meeting analysis too",
      body: "Tap the 'Analysis' tab.\nSee how well you drew out the member's voice, and what your strengths and areas to improve are as a leader."
    },
    celebration: {
      title: "You've completed the tutorial!",
      body: "Now prepare a real 1on1 with Orblit.\nInvite teammates to share questions and answers together.",
      confirm: "Done"
    }
  },
  onboarding_intro: {
    title: "Lead through conversation, not control",
    body_value: "One 1on1 conversation with a teammate builds your team's\nperformance and trust more than meetings or directives.",
    body_promise: "Orblit handles everything from prep to analysis.\n{{name}}, you just focus on the conversation.",
    page2_title: "Just 2 minutes\u2014take a quick look around?",
    page2_subtitle: "You can try out Orblit right now.\nGet the hang of it with a quick tutorial.",
    steps: {
      create: "Create a 1on1 meeting",
      questions: "Pick questions to talk through",
      record: "Try analysis with a sample recording",
      analysis: "See how the analysis works"
    },
    next: "Next",
    guide_link: "Want to learn a little more about 1on1?",
    skip: "Skip",
    skip_tooltip: "Hold on! It really only takes 2 minutes.\nSpend just 2 minutes to get the hang of Orblit",
    cta: "Get started"
  }
};

// src/locales/en-US/ninebox.json
var ninebox_default = {
  grid: {
    performance_potential: {
      title: "Performance & Potential",
      x_label: "Performance",
      y_label: "Potential",
      star: "Star",
      star_desc: "Top performer with the highest potential \u2014 a key talent in the organization",
      developing_top: "Developing Top",
      developing_top_desc: "Shows strong performance with high potential to grow into a next-generation leader",
      potential_top: "Potential Top",
      potential_top_desc: "Currently low performance, but has very high learning ability and growth potential",
      strong: "Strong",
      strong_desc: "Delivers outstanding results in their current role as a reliable performer",
      developing: "Developing",
      developing_desc: "Serves as the backbone of the organization with steady performance and expected growth",
      potential: "Potential",
      potential_desc: "Has basic potential but needs additional training or role adjustment to deliver results",
      dependable: "Dependable",
      dependable_desc: "Performs well but is specialized in their current role \u2014 better suited for an expert track than vertical promotion",
      inconsistent: "Inconsistent",
      inconsistent_desc: "Performance is unstable; needs capability development or motivation support",
      unsatisfactory: "Unsatisfactory",
      unsatisfactory_desc: "Both performance and potential are low \u2014 consider role reassignment or performance management"
    },
    flight_risk: {
      title: "Flight Risk Matrix",
      x_label: "Impact of Departure",
      y_label: "Flight Risk",
      critical: "Critical",
      critical_desc: "Both flight risk and impact are at the highest level. Immediate retention strategy required",
      act_fast: "Act Fast",
      act_fast_desc: "Clear signs of attrition. Even with moderate impact, prompt meetings and improved treatment are needed",
      watch_closely: "Watch Closely",
      watch_closely_desc: "Low impact but high likelihood of leaving \u2014 identify root causes and monitor closely",
      alert: "Alert",
      alert_desc: "Signs of attrition detected in a high-impact employee. Intensive relationship management needed",
      manage: "Manage",
      manage_desc: "Standard level of risk management with periodic satisfaction checks",
      monitor: "Monitor",
      monitor_desc: "Not urgent, but monitor for potential flight risk factors",
      key_talent: "Key Talent",
      key_talent_desc: "Very high impact but low flight risk. Maintain stability through ongoing rewards and vision",
      stable: "Stable",
      stable_desc: "Well-settled in the organization with low risk",
      low_risk: "Low Risk",
      low_risk_desc: "Both flight risk and impact are low \u2014 lower priority for management resources"
    }
  },
  right_section: {
    direct_members: "Direct Reports",
    indirect_members: "Indirect Reports"
  },
  member_detail: {
    member_info: "Member Information",
    leader: "Manager",
    work_engagement: "Work Engagement",
    growth_satisfaction: "Growth Satisfaction",
    history: "History",
    loading: "Loading...",
    no_history: "No history available"
  },
  filter: {
    all_members: "Show All Members",
    direct_members_only: "Show Direct Reports Only"
  },
  last_update: "Last Update"
};

// src/locales/en-US/template.json
var template_default = {
  oneonone: {
    career_development: {
      title: "Career Development Template",
      questions: [
        "What was the best aspect and the most disappointing aspect of your work since our last meeting?",
        "What projects would you like to participate in or be more deeply involved with?",
        "Is there something you really want to try but haven't been able to due to lack of time or resources?",
        "What career goals do you want to achieve within the next 6-12 months, and what are your reasons for this?",
        "Would you be willing to share any goals or plans you have for the next 5 years?",
        "Is there anything I can do to help with your career development?",
        "What are you doing for yourself these days?",
        "What is one thing you'd like to do more of outside of work next month?"
      ]
    },
    growth_planning: {
      title: "Growth Plan Discussion",
      questions: [
        "What do you find most positive about your current role?",
        "What is the most challenging aspect of your current role?",
        "What new opportunities or possibilities have you discovered in your current work?",
        "What business goals do you want to achieve in your work?",
        "What personal goals do you want to accomplish outside of work?",
        "What topics interest you most these days, or what skills/experiences would you like to learn?",
        "What networks are you currently utilizing? What networks would you like to expand in the future?",
        "How do you want your colleagues to perceive you? Are there aspects you want to maintain and aspects you want to change?",
        "Let's identify action items for ways you can grow."
      ]
    },
    retention_management: {
      title: "Retention Management",
      questions: [
        "What was the most satisfying moment in your recent work and why?",
        "What tasks do you enjoy most, and which ones do you relatively prefer less?",
        "What do you think is the best aspect of working at our company, and what needs improvement?",
        "If you could change one thing at the company, what would it be?",
        "In what area do you want to be recognized professionally, and what type of recognition motivates you the most?",
        "Is there any talent or strength you feel you're not fully utilizing in your current role or responsibilities?",
        "What factors boost your motivation at our company, and conversely, what factors decrease your enthusiasm?",
        "If you were to leave our company, what would be the main reason?",
        "Let's identify action items to work on before our next meeting."
      ]
    },
    performance_energy: {
      title: "Competency, Performance, and Energy",
      questions: [
        'Can you share a recent moment when you leveraged your strengths and felt "I did well"?',
        `Is there an area where you recently felt "I want to do better," or something you thought "If I could go back, I'd try it this way"?`,
        "If you had to be away for a week, what tasks would likely come to a halt, and who would you feel comfortable handing them over to?",
        "Is there a role/project/task you'd like to try, even on a small scale?",
        "Please share the 2-3 most important tasks you worked on over the past two weeks.",
        "What contribution of yours do you think was essential for the team or service, even if it wasn't visible on the surface?",
        "What issues slowed down or hindered your work progress? If you could solve them, how would you like to address them?",
        "Can you share a recent instance where you positively impacted a colleague's performance?",
        "What motivated you during this past period?",
        `If you could change "just one thing" about our team's current processes or culture, what would it be?`,
        "When did you feel most engaged recently? Conversely, when did you feel most challenged or exhausted?"
      ]
    },
    teamwork_collaboration: {
      title: "Teamwork & Collaboration",
      questions: [
        "What is your unique contribution to the team's success that others might not be aware of?",
        "Who is a colleague you recently felt great synergy with while working together? What specifically made you feel that way?",
        "What feedback from a colleague has been most helpful to you?",
        "What is the most positive aspect of our team culture that you hope we continue to maintain?",
        "Has there been a recent moment when you needed help from someone at work but found it difficult to ask? What made it challenging?",
        "What do you consider your strengths when working with other team members? And what aspects would you like to develop further?",
        "Have you recently experienced resolving a difference of opinion or conflict within the team in a positive way? What role did you play in that process?",
        "Do you have any new ideas you'd like to try to take our team's collaboration to the next level?",
        "Based on what we've discussed today, what action items can we create together for future growth?"
      ]
    },
    performance_improvement_plan: {
      title: "Performance Improvement Plan",
      questions: [
        "Looking at your performance on [Main Task] over the past [Period], it appears your result was (B), compared to the target of (A). I'd like to hear your thoughts on this data and your performance.",
        "What do you feel are the main challenges or primary obstacles making it difficult to achieve your performance goals?",
        "Do you feel your current role aligns well with your strengths and aptitude? Or are there parts of the job you feel are truly not a good fit for you?",
        "What efforts have you personally made so far to improve your performance?",
        "What support from me or the company would be most helpful for you to improve your performance?",
        "Over the next [Improvement Period], what is the key area you want to focus on improving, and what specific target level do you want to achieve?",
        "To reach that target, what are 3 new actions or changed habits you can start implementing this week?",
        "To check if these plans are being implemented well and for me to provide feedback, how about we set up check-in 1-on-1s, perhaps [once a week / once every 2 weeks], for the next [Period]?",
        "The notes from our conversation today will be available in Orblit 1on1. Is there anything else you'd like to say?"
      ]
    },
    performance_improvement_check: {
      title: "Performance Improvement Check-in",
      questions: [
        "How did things go with the [Key Improvement Area] we agreed to focus on over the past [Check-in Period]?",
        "Of the actions we planned, what are some things you feel you executed well or found to be effective?",
        "Conversely, what were the biggest challenges or unexpected obstacles you faced while implementing the plan?",
        "From my observation, I saw improvement in [Improved Area], but [Area for Improvement] still seems to be a challenge. What are your thoughts on this?",
        "Was the [Promised Support] I offered last time helpful? Do you need any additional support or perhaps a different kind of help?",
        "As you went through this process over the [Check-in Period], was there anything you felt you learned or realized?",
        "What should we focus on a bit more between now and our next check-in?",
        "The notes from our discussion today will be available in Orblit 1on1. Is there anything else you'd like to add?"
      ]
    },
    first_oneonone: {
      title: "First 1on1 Template",
      description: "Your first time sitting down together. Filled with questions to get to know each other and set an easy, open tone for the 1on1s ahead.",
      questions: [
        "I'd love to start having these one-on-one conversations with you on a regular basis. This isn't a place for work reviews or evaluations \u2014 it's our time to openly share how things are going for you and to talk together about how I can support you. Feel free to start with whatever's on your mind, whether it's about your work, collaboration, or your career.",
        "Since this is our first 1on1, let's keep it light and just use it to get to know each other a bit. Have you had 1on1s before? If so, I'm curious what worked well and what felt lacking.",
        "Among the things you're working on these days, what's going well, and is there anything that feels a bit stuck or weighs on your mind?",
        "Is there anything you'd like to do more of, or something new you'd like to try out?",
        "Do you have a preferred way of communicating at work? For example, whether you'd rather leave a message on chat or talk things through directly.",
        "Is there any small inconvenience or obstacle I could help you with right away? (e.g., communication with other teams, access permissions, equipment, etc.)",
        "If anything ever comes to mind about how our team works that you wish were a bit better, please feel free to let me know. It's totally fine if nothing comes to mind right now.",
        "How was your first 1on1 today? If you have any last questions or anything else you'd like to share, please feel free. I think it'd be great to keep having these for about an hour every other week \u2014 how does that sound to you?"
      ]
    }
  }
};

// src/locales/en-US/payment.json
var payment_default = {
  cancel_subscription_modal: {
    cancel: "Go Back",
    confirm: "Cancel Subscription",
    inquiry: "For any inquiries, please contact jjchoi@reversemountain.co.kr.",
    message: "Are you sure you want to cancel your subscription?",
    subscription_info: "This workspace is available for {{memberCount}} members with paid features until {{endDate}}.",
    title: "Cancel Subscription"
  },
  confirm_modal: {
    activate_message: "Do you want to activate this member?",
    activate_prorated_charge: "Prorated amount will be charged.",
    activate_slot_available: "Using available slot.",
    activate_title: "Activate Member",
    available_slots: "Available slots: {{count}}",
    cancel: "Cancel",
    confirm: "Confirm",
    deactivate_message: "Do you want to deactivate this member?",
    deactivate_slot_message: "When deactivated, 1 available slot will be added and can be used to activate other members.",
    deactivate_title: "Deactivate Member"
  },
  notifications: {
    activate_member_error_title: "Failed to Activate Member",
    aws_marketplace_cancel_message: "AWS Marketplace subscriptions can be cancelled directly from the AWS Console.",
    aws_marketplace_cancel_path: "AWS Console \u2192 AWS Marketplace Subscriptions \u2192 Manage Subscriptions",
    aws_marketplace_cancel_title: "AWS Marketplace Subscription Cancellation",
    deactivate_member_error_title: "Failed to Deactivate Member",
    member_remove_error_message: "Failed to remove member.",
    member_remove_error_title: "Failed to Remove Member",
    member_removed_message: "Member has been successfully removed.",
    member_removed_title: "Member Removed",
    purchase_slots_error_message: "Failed to purchase slots.",
    purchase_slots_error_title: "Purchase Failed",
    purchase_slots_success_message: "{{count}} slot(s) have been added.",
    purchase_slots_success_title: "Slots Purchased",
    slot_added_after_deactivate: "1 slot has been added and can be used to activate other members.",
    slot_added_message: "1 slot has been added because the removed member was active.",
    slot_added_title: "Slot Added",
    subscription_cancel_error_message: "Failed to cancel subscription.",
    subscription_cancel_error_title: "Cancellation Failed",
    subscription_cancelled_message: "Available until {{endDate}}.",
    subscription_cancelled_title: "Subscription Cancelled",
    subscription_exists_message: "This workspace already has an active subscription.",
    subscription_exists_title: "Already Subscribed",
    subscription_reactivate_error_message: "Failed to reactivate subscription.",
    subscription_reactivate_error_title: "Reactivation Failed",
    subscription_reactivated_message: "Your subscription has been reactivated.",
    subscription_reactivated_title: "Subscription Reactivated",
    toggle_member_error_message: "Failed to activate/deactivate member."
  },
  payment_fail: {
    default_message: "An error occurred during payment processing.",
    error_code: "Error Code",
    go_home_button: "Go Home",
    order_id: "Order ID",
    retry_button: "Retry",
    title: "Payment Failed"
  },
  payment_page: {
    activate_members_first: "Please activate 1on1 members first",
    active_members: "Active 1on1 Members",
    currency: "KRW",
    loading: "Loading...",
    loading_members: "Loading member information...",
    monthly: {
      description: "Automatic monthly payment",
      label: "Monthly Payment",
      per_month: "/month"
    },
    no_members_warning: "Please activate 1on1 members in workspace settings first.",
    payment_summary: {
      monthly: "Monthly Payment Amount",
      per_year_suffix: "/year",
      yearly: "Yearly Payment Amount"
    },
    price_per_member_monthly: "Monthly Price per Member",
    register_button: "Register Card and Pay {{amount}}",
    select_billing_cycle: "Select Billing Cycle",
    service_description: "Your card will be automatically charged once registered.",
    service_title: "1on1 Service Subscription",
    title: "Register Card for Recurring Payment",
    yearly: {
      description: "Pay once a year",
      label: "Yearly Payment",
      per_year: "/year"
    }
  },
  payment_success: {
    auto_payment_info: "Automatic payments will be processed using the registered card.",
    billing_cycle: "Billing Cycle",
    check_setting_info: "You can check payment information in workspace settings.",
    error_description: "An error occurred during payment processing.\nPlease try again, and if the problem persists, let us know via Help > Contact Us.",
    error_title: "Error Details",
    failed_title: "Payment Failed",
    go_home_button: "Go Home",
    home_button: "Home",
    invalid_auth_error: "Authentication information is invalid.",
    monthly_auto: "Monthly (Automatic monthly payment)",
    processing_message: "Please wait...",
    processing_title: "Processing Payment",
    provisioning_failed_message: "We ran into a problem while activating your subscription and our team is looking into it. Your payment went through, so there is no need to pay again. If you have questions, let us know via Help > Contact Us.",
    provisioning_failed_title: "Subscription activation is taking longer than expected. Please hang tight.",
    registration_complete_message: "Your recurring payment has been successfully registered.",
    registration_complete_title: "Recurring Payment Registered",
    retry_button: "Retry",
    success_title: "Payment Complete!",
    view_payment_button: "View Payment Info",
    yearly_auto: "Yearly (Automatic yearly payment)"
  },
  purchase_slots: {
    button: "Purchase Slots",
    cancel_button: "Cancel",
    confirm_button: "Purchase",
    description: "Pre-purchase slots to activate members without additional payments.",
    estimated_amount: "Estimated Payment Amount",
    modal_title: "Purchase Slots",
    slot_count_label: "Number of Slots to Purchase"
  },
  subscription: {
    alert: {
      at_risk_message: "Payment failed. Please check the limit or status of your registered card, and if the problem continues, let us know via Help > Contact Us.",
      at_risk_title: "Payment Issue Detected",
      pending_cancellation_message: "Available until {{endDate}}.",
      pending_cancellation_title: "Subscription Pending Cancellation"
    },
    available_slots: "Available Slots Count",
    billing_cycle: {
      label: "Billing Cycle",
      monthly: "Monthly",
      yearly: "Yearly"
    },
    cancel_button: "Cancel Subscription",
    card_company_placeholder: "Card Company",
    description: "Manage workspace subscription and payments.",
    members_unit: "slots",
    next_billing_date: "Next Billing Date",
    no_payment_method: "No payment method registered",
    paid_members: "Used Slots",
    pay_button: "Subscribe",
    payment_method: "Payment Method",
    reactivate_button: "Reactivate Subscription",
    recent_payments: "Recent Payments",
    registered_card: "Registered Card",
    slots_unit: "slots",
    status: {
      active: "Active",
      at_risk: "Payment Issue",
      cancelled: "Cancelled",
      label: "Subscription Status",
      pending_cancellation: "Pending Cancellation"
    },
    title: "Payment Management",
    total_subscribed_slots: "Total Subscribed Slots"
  },
  subscription_required: {
    app_confirm: "OK",
    app_description: "This feature is available with a workspace subscription.\nPlease contact your workspace administrator for details.",
    app_title: "Subscription Required",
    button: {
      cancel: "Cancel",
      go_to_payment: "Use Paid Plan"
    },
    description: {
      ai_question: "Quickly generate rich, personalized questions tailored to each member.",
      recording: "With the recording feature, we automatically organize answers by question, provide full meeting summaries, and create action items after your meetings.",
      report: "Automatically generate reports on member motivation, strengths, growth, and more by aggregating 1on1 meeting history.",
      prep: "Easily relay meeting details to leaders through simple surveys and AI chat."
    },
    feature: {
      ai_question: "AI Question Generation",
      recording: "Recording File Upload",
      report: "Member Report Generation",
      prep: "Prep"
    },
    title: "Available in Paid Plan!"
  },
  trial: {
    days_remaining: "{{days}} days remaining in your trial.",
    end_date: "End date: {{date}}",
    expired_message: "Your free trial has ended. A subscription is required to continue using AI features.",
    expired_title: "Trial Period Ended",
    last_day: "Today is the last day!",
    title: "Free Trial Period",
    warning: "AI features will be unavailable after the trial period ends."
  },
  credit: {
    title: "Meetings left to analyze",
    balance: "{{count}} left.",
    description: "Each meeting recording & AI analysis uses 1. Subscribe for unlimited use."
  }
};

// src/locales/en-US/aws_marketplace.json
var aws_marketplace_default = {
  landing: {
    page_title: "AWS Marketplace",
    page_subtitle: "Orblit for AWS",
    loading: {
      checking: "Checking AWS Marketplace information...",
      loading_workspaces: "Loading workspaces...",
      connecting: "Connecting AWS Marketplace subscription..."
    },
    auth_required: {
      title: "Login Required",
      description: "Please log in to connect your AWS Marketplace subscription.",
      login_button: "Log In",
      signup_button: "Sign Up"
    },
    no_workspace: {
      title: "No Workspaces Found",
      description: "You need to create a workspace first to connect your AWS Marketplace subscription.",
      instruction: "After creating a workspace, please return to this page.",
      button: "Create Workspace"
    },
    no_owner: {
      title: "Owner Permission Required",
      description: "You need owner permissions to connect an AWS Marketplace subscription.",
      instruction: "If you don't have a workspace you created, please create a new one. After creating a workspace, return to this page.",
      button: "Create Workspace"
    },
    already_subscribed: {
      title: "Already Subscribed",
      description: "Your AWS Marketplace subscription is already connected to the workspace.",
      go_home: "Go to Home"
    },
    success: {
      title: "Subscription Connected",
      description: "Your AWS Marketplace subscription has been successfully connected.",
      redirect_message: "You will be redirected to your workspace shortly."
    },
    error: {
      title: "An Error Occurred",
      no_token: "AWS Marketplace token not found. Please access through the correct link.",
      button: "Go to Home"
    },
    notifications: {
      success_title: "Subscription Connected",
      success_message: "Your AWS Marketplace subscription has been successfully connected.",
      error_title: "Connection Failed",
      error_message: "Failed to connect AWS Marketplace subscription."
    }
  }
};

// src/locales/en-US/dashboard.json
var dashboard_default = {
  title: "Dashboard",
  range_selector: {
    custom_label: "Custom",
    modal_title: "Select Custom Period",
    date_range_label: "Start Date ~ End Date",
    date_range_placeholder: "Select date range",
    cancel: "Cancel",
    apply: "Apply"
  },
  periods: {
    "1month": "This Month",
    "3months": "Last 3 Months",
    "6months": "Last 6 Months",
    "1year": "Last 1 Year"
  },
  days_until: {
    passed: "Passed",
    today: "Today",
    tomorrow: "Tomorrow",
    days_later: "In {{days}} days"
  },
  alerts: {
    title: "Status Check",
    no_meeting: {
      title: "Long Inactive (2+ months)",
      item_with_days: "{{leaderName}}(Manager) - {{memberName}}, {{days}} days",
      item_no_history: "{{leaderName}}(Manager) - {{memberName}}, No meeting history"
    },
    low_vital: {
      title: "Low Vital Members",
      item_with_leader: "{{memberName}} (Manager: {{leaderName}})",
      item_no_leader: "{{memberName}} (No current manager)",
      tooltip: "Members whose average vital score (work engagement, growth satisfaction) is below 3 during the selected period."
    },
    vital_drop: {
      title: "Vital Drop Members",
      item_with_leader: "{{memberName}} (Manager: {{leaderName}})",
      item_no_leader: "{{memberName}} (No current manager)",
      tooltip: "Members whose latest vital score (work engagement, growth satisfaction) dropped by 2 or more points compared to the previous one."
    },
    count: "{{count}}",
    view_all: "View All",
    full_list: "Full List ({{count}})"
  },
  meeting_table: {
    title: "All 1on1 Meeting List",
    tabs: {
      as_leader: "As Manager",
      as_member: "As Member"
    },
    filter: {
      placeholder: "Select meeting partner",
      all: "All partners",
      direct_only: "Direct relationships only"
    },
    columns: {
      counterpart: "Partner",
      relationship: "Relationship",
      date: "Date",
      duration: "Duration",
      action_items: "Action Items",
      engagement: "Work Engagement",
      growth: "Growth Satisfaction",
      summary: "Meeting Summary"
    },
    relationship: {
      direct: "Direct",
      other: "Other"
    },
    duration_minutes: "{{minutes}} min",
    direct_filter_hint: "Only direct relationships are shown by default. Change the filter to see all data."
  },
  hero_metrics: {
    execution_rate: {
      title: "1on1 Execution Rate",
      value: "{{value}}%",
      subtitle: "{{executed}}/{{total}} members"
    },
    average_cycle: {
      title: "Avg. Meeting Cycle",
      value_weeks: "{{weeks}} weeks",
      target_weeks: "Target {{weeks}} weeks",
      no_target: "No target cycle set",
      tooltip: "Average of meeting target cycles set by direct manager-member relationships."
    },
    completed_meetings: {
      title: "Completed Meetings",
      value_count: "{{count}}",
      average_duration: "Avg. {{minutes}} min"
    },
    vital: {
      title: "Vital",
      value_score: "{{score}}/5.0",
      engagement_score: "Work Engagement {{score}}/5.0",
      growth_score: "Growth Satisfaction {{score}}/5.0"
    }
  },
  leader_insights: {
    title: "Team Care",
    direct_members_count: "{{count}} Direct Members",
    management_status: {
      title: "Management Status",
      target_cycle: {
        label: "Target Cycle Set",
        count: "{{set}}/{{total}} members",
        not_set: "Not set: {{names}}"
      },
      cycle_compliance: {
        label: "Cycle Compliance",
        count: "{{compliant}}/{{total}} members",
        delayed: "Delayed: {{items}}"
      },
      delayed_member: "{{name}}(+{{days}} days)",
      scheduled_meetings: {
        label: "Meeting Scheduled",
        count: "{{count}} members"
      },
      vital_stable: {
        label: "Stable Vital (4+ pts)",
        count: "{{stable}}/{{total}} members",
        improvable: "Improvable: {{items}}",
        need_attention: "Needs Attention: {{items}}"
      },
      member_score: "{{name}}(avg. {{score}} pts)"
    },
    care_quality: {
      title: "Care Quality",
      avg_meeting_duration: "Avg. Meeting Duration",
      duration_value: "{{minutes}} min",
      leader_action_items: "Manager's Action Items Completion",
      member_action_items: "Team Action Items Completion",
      action_items_count: "{{completed}}/{{total}}",
      member_vital_avg: "Team Vital Average",
      vital_value: "{{score}}/5.0"
    }
  },
  stats_table: {
    title: "1on1 Meeting Status",
    hint: "Statistics of 1on1 meeting data for direct manager-member relationships.",
    tabs: {
      by_leader: "By Manager",
      by_member: "By Member"
    },
    columns: {
      leader: "Manager",
      member: "Member",
      direct_leader: "Direct Manager",
      direct_members: "Direct Members",
      completed_count: "Completed",
      last_meeting: "Last Meeting",
      avg_duration: "Avg. Duration",
      avg_cycle: "Avg. Cycle",
      target_cycle: "Target Cycle",
      action_items: "Action Items",
      engagement: "Work Engagement",
      growth: "Growth Satisfaction"
    },
    values: {
      count: "{{count}}",
      minutes: "{{minutes}} min",
      weeks: "{{weeks}} weeks",
      not_set: "Not set",
      action_items_rate: "{{rate}}% ({{completed}} / {{total}})",
      overdue_notification: "{{overdueDays}} days past target cycle.",
      cycle_notification: "Average 1on1 cycle exceeds target cycle."
    }
  },
  meeting_count_chart: {
    title: "Monthly 1on1 meeting count",
    legend: {
      total: "All",
      with_direct_leader: "Direct manager-member"
    },
    placeholder: "No 1on1 meetings completed during this period."
  },
  member_header: {
    direct_leader: "Direct Manager",
    direct_members: "Direct Members"
  },
  member_insights: {
    title: "Personal Growth",
    management_status: {
      title: "Status",
      target_cycle: "Target Cycle",
      target_cycle_weeks: "{{weeks}} weeks",
      not_set: "Not set",
      last_meeting: "Last Meeting",
      cycle_overdue: "Overdue",
      scheduled_meeting: "Scheduled Meeting"
    },
    recent_vital: {
      title: "Recent Vital",
      tooltip_line1: "The latest work engagement and growth satisfaction scores.",
      tooltip_line2: "Max 5 points, entered by the manager after 1on1 meetings.",
      engagement: "Work Engagement",
      growth: "Growth Satisfaction",
      no_data: "No data",
      no_comparison: "(No comparison data)",
      previous: "(Previous: {{score}})"
    },
    metrics: {
      avg_meeting_duration: "Avg. Meeting Duration",
      duration_minutes: "{{minutes}} min",
      action_items_rate: "Action Items Completion"
    }
  },
  member_overview: {
    title: "1on1 Overview",
    tooltip_all: "Overview of {{name}}'s 1on1 meetings with direct manager and members.",
    tooltip_leader: "Overview of {{name}}'s 1on1 meetings with direct manager.",
    tooltip_members: "Overview of {{name}}'s 1on1 meetings with direct members.",
    completed_meetings: "Completed Meetings",
    meetings_with_members: "Meetings with Direct Members",
    meetings_with_leader: "Meetings with Direct Manager",
    avg_duration: "Avg. Meeting Duration",
    count: "{{count}}",
    duration_minutes: "{{minutes}} min"
  },
  vital_chart: {
    members_vital_title: "Direct Members' Vital Trend",
    members_vital_description: "Monthly average of work engagement and growth satisfaction scores that {{name}} entered after 1on1 meetings with direct members.",
    user_vital_title: "Personal Vital Trend",
    user_vital_description: "Monthly average of work engagement and growth satisfaction scores for {{name}}, entered by the direct manager after 1on1 meetings.",
    vital_title: "Vital Trend",
    legend: {
      engagement: "Work Engagement",
      growth: "Growth Satisfaction"
    }
  },
  error: {
    member_not_found: "Member not found.",
    member_not_found_message: "This member does not belong to this workspace."
  },
  excel_download: {
    button: "Excel Download",
    modal_title: "Excel Download",
    period_label: "Period",
    team_label: "Organization / Team",
    data_select_label: "Select data to export",
    sheet_manager: "Manager 1on1 Status",
    sheet_member: "Member 1on1 Status",
    sheet_meeting_log: "All 1on1 Meeting Logs",
    include_summary: "Include meeting summary",
    include_summary_desc: "AI meeting summaries will be included. Handle with care as they may contain sensitive information.",
    download: "Download",
    error: "Failed to generate Excel file. Please try again."
  },
  hr: {
    title: "1on1 Status",
    excel_button: "Excel",
    full_excel_button: "Full Excel",
    subhead: "{{leaders}} leaders \xB7 {{period}} \xB7 {{scope}}",
    scope: {
      direct: "Direct reports",
      all: "All meetings",
      tooltip: "Direct reports: counts only 1on1s between a manager and member linked in the org chart.\nAll meetings: counts every 1on1 regardless of the org chart."
    },
    units: {
      count: "{{value}}",
      people: "{{value}}",
      minutes: "{{minutes}} min",
      hours_minutes: "{{hours}} hr {{minutes}} min",
      weeks: "{{weeks}} wk",
      percent: "{{value}}%",
      days_ago: "{{days}} days ago",
      today: "Today",
      no_meeting: "No meetings"
    },
    activation: {
      meeting_count: "Total meetings",
      total_duration: "Total duration",
      execution_rate: "Participation rate",
      execution_hint: "{{executed}} of {{total}} met at least once"
    },
    quality: {
      pre_question_rate: "Leader pre-set question rate",
      prep_rate: "Member prep completion",
      action_item_rate: "Action item completion",
      avg_duration: "Avg per meeting",
      duration_hint: "based on {{value}} timed meetings"
    },
    leader_list: {
      description: "Shows only the size of each value and its position against the organization median or average. No good or bad ratings, grades, or rankings. Click a row to see each team member.",
      sort: {
        coverage: "Fewest members met",
        last: "Longest since last meeting",
        cycle: "Longest cycle",
        perMember: "Lowest avg per member",
        name: "Name"
      },
      zero_filter: "0 meetings only ({{count}})",
      trend_column: "Trend \xB7 last {{count}}mo",
      trend_sub: "Monthly meetings per member",
      coverage_sub: "Share of assigned",
      median: "Median {{value}}",
      average: "Avg {{value}}",
      basis: {
        median: "Median",
        average: "Average"
      },
      member_count: "{{count}} members",
      member_count_all: "{{count}} partners",
      empty: {
        not_computable: "Not computable",
        no_meetings: "0 meetings",
        no_value: "No data"
      },
      legend: {
        bar: "Value",
        median: "Company-wide median (computable leaders only)",
        average: "Company-wide average (computable leaders only)",
        pips: "Members met / assigned",
        showing: "Showing {{count}} leaders \xB7 Leaders with 0 meetings stay in the list"
      },
      zero_row: "No meetings in this period \xB7 last meeting {{last}}",
      zero_row_never: "No meetings in this period \xB7 no past meetings",
      team_empty: "No leaders to show in the selected team"
    },
    leader_stats: {
      title: "Leader execution",
      column: {
        leader: "Leader",
        meeting_count: "Meetings",
        total_count: "Total meetings",
        total_duration: "Total duration",
        avg_duration: "Avg per meeting",
        coverage: "Members met",
        per_member: "Avg per member",
        avg_cycle: "Avg cycle",
        last_meeting: "Last meeting",
        action_item: "Action items"
      }
    },
    leader_detail: {
      quality_title: "{{name}} \xB7 1on1 quality",
      team_title: "By member \xB7 {{count}}",
      excel_button: "Leader detail Excel",
      pre_question_rate: "Pre-set question rate",
      column_monthly: "Monthly",
      column: {
        member: "Member",
        target_cycle: "Target cycle",
        last_meeting: "Last meeting",
        prep_rate: "Prep completion"
      },
      heat_caption: "Monthly cells start with the oldest month \xB7 {{first}} \u2192 {{last}} \xB7 Darker means more meetings that month",
      heat_tooltip: "{{count}} meetings in {{month}}",
      meeting_list_button: "Meeting history ({{count}})"
    },
    meeting_list: {
      title: "Meeting history",
      count: "{{count}} meetings completed in this period",
      count_person: "{{count}} meetings with {{name}}",
      subtitle: "{{period}} \xB7 {{scope}} \xB7 follows the period and basis selected above",
      search_placeholder: "Search leader or member",
      search_empty: "No matching person",
      role: {
        leader: "Leader",
        member: "Member",
        both: "Leader \xB7 Member"
      },
      person_summary: "As leader {{leader}} \xB7 as member {{member}}",
      column: {
        date: "Date",
        leader: "Leader",
        member: "Member",
        relation: "Relation",
        recording: "Recording",
        duration: "Duration"
      },
      relation_other: "Other",
      recorded: "Recorded",
      recording_count: "{{count}} recorded",
      recording_filter: {
        all: "All",
        with: "Recorded",
        without: "Not recorded"
      },
      group: {
        week: "{{monthName}} W{{week}}",
        month: "{{monthName}}",
        meta: "{{count}} meetings \xB7 {{leaders}} leaders \xB7 {{duration}}"
      },
      legend: {
        relation_other: "Only meetings outside a direct relationship are marked \u2018Other\u2019",
        relation_direct: "Direct-relationship basis, so no relation column",
        recording: "Meetings without a recording show nothing",
        duration: "\u2018\u2014\u2019 means duration was not recorded"
      },
      shown: "{{shown}} / {{total}}",
      load_more: "Show more",
      empty: {
        title: "No meetings match this condition",
        body: "No 1on1 was completed in {{period}}. Try a wider period or switch the basis above to \u2018All meetings\u2019.",
        body_person: "{{name}} has no completed meetings in {{period}}. Try a wider period or clear the participant filter.",
        clear_filter: "Clear participant filter",
        clear_recording: "Clear recording filter",
        widen_period: "Widen to 12 months"
      }
    },
    empty_state: {
      badge: "No data",
      title: "No 1on1s to summarize yet",
      description: "This page fills in once leader-member relationships are connected and the first 1on1 is completed. Two things are needed now.",
      step1_title: "Assign direct leaders to members",
      step1_progress: "{{assigned}} of {{total}} members have a leader assigned",
      step1_link: "Go to settings",
      step2_title: "Complete the first 1on1",
      step2_sub: "Counting starts from completed meetings",
      step2_link: "Create a 1on1",
      preview_title: "Here is how it will look"
    },
    excel: {
      sheet: {
        activation: "Activity",
        leader_stats: "LeaderSummary",
        leader_detail: "MemberDetail",
        leader_pairs: "MemberByLeader",
        meetings: "Meetings"
      },
      file: {
        leader_stats: "LeaderSummary",
        leader_detail: "MemberDetail",
        meetings: "Meetings",
        full: "1on1Status"
      },
      value: {
        yes: "Y",
        no: "N"
      },
      column: {
        month: "Month",
        meeting_count: "Meetings",
        total_duration: "Total duration (min)",
        total: "Total",
        scope: "Scope",
        execution_rate: "Participation (joined/total)",
        leader_name: "Leader",
        leader_email: "Leader email",
        org_leader: "Org chart leader",
        avg_duration: "Avg per meeting (min)",
        member_count: "Members",
        met_member_count: "Members met",
        coverage: "Coverage (%)",
        per_member: "Avg meetings per member",
        avg_cycle: "Avg cycle (weeks)",
        target_cycle: "Target cycle (weeks)",
        last_meeting_date: "Last meeting date",
        days_since: "Days since last meeting",
        pre_question_rate: "Pre-set question rate (%)",
        action_item_done: "Action items done",
        action_item_total: "Action items total",
        action_item_rate: "Action item rate (%)",
        member_name: "Member",
        member_email: "Member email",
        direct_relation: "Direct relationship",
        date: "Date",
        recording: "Recorded",
        duration: "Duration (min)",
        prep_rate: "Prep completion (%)"
      }
    }
  }
};

// src/locales/en-US/profile_card.json
var profile_card_default = {
  communicationStyle: {
    category: "Communication Style",
    question: "When discussing work with your leader, what approach helps you focus best?",
    SUMMARY: {
      label: "Key Summary",
      description: "In conclusion, the key point of this task is this, and we'll focus here first."
    },
    CONTEXT: {
      label: "Detailed Context",
      description: "Let me explain the background and history of this task step by step first."
    },
    DATA: {
      label: "Data-Driven",
      description: "Let's look at the data and reference materials together to confirm the direction of the task."
    },
    DISCUSSION: {
      label: "Discussion-Based",
      description: "What do you think about this task? Let's discuss and create an action plan together."
    }
  },
  feedbackStyle: {
    category: "Effective Feedback",
    question: "When receiving feedback from your leader, what style works best for you?",
    DIRECT: {
      label: "Direct Improvement",
      description: "Part A seems to lack logic. This needs to be revised in this way."
    },
    POSITIVE: {
      label: "Positive Reinforcement",
      description: "The overall flow is really good. It would be great if you could improve just this part a bit more."
    },
    SOLUTION: {
      label: "Solution-Oriented",
      description: "This seems to be the issue. There might be some options like this\u2014shall we look for alternatives?"
    },
    SELF_REFLECTION: {
      label: "Self-Reflection",
      description: "What do you think about this? What could you do differently to improve?"
    }
  },
  careerOrientation: {
    category: "Career Orientation",
    question: "How would you like to grow in the company going forward?",
    SPECIALIST: {
      label: "Specialist",
      description: "I want to develop deep expertise in a specific skill or role."
    },
    LEADER: {
      label: "Leader",
      description: "I want to achieve results with team members and develop management skills to help them grow."
    },
    GENERALIST: {
      label: "Coordinator",
      description: "I want to take on a role that connects and coordinates multiple areas to solve complex problems."
    },
    EXPLORER: {
      label: "Pioneer",
      description: "I want to introduce new technologies or build business models from scratch."
    }
  },
  motivation: {
    category: "Motivation Factors",
    question: "When do you feel most motivated?",
    GROWTH: {
      label: "Learning and Growth",
      description: "I feel happy when I learn something new and feel my skills improving."
    },
    AUTONOMY: {
      label: "Authority and Autonomy",
      description: "I feel responsibility and passion when I'm trusted and given full ownership."
    },
    RECOGNITION: {
      label: "Recognition and Reputation",
      description: "I feel proud when my contributions and capabilities are recognized by colleagues and leaders."
    },
    IMPACT: {
      label: "Meaning and Impact",
      description: "I feel fulfilled when my work actually helps customers or contributes to the company."
    }
  },
  create_page: {
    title: "Create Profile Card",
    description: "Complete your own profile card by answering simple questions",
    progress: "Question {{currentStep}} / {{allStep}}",
    save_and_leave: "Save and exit"
  },
  no_profile_card: {
    title: "You haven't set up your profile card yet. It takes less than a minute!",
    title_mobile: "You haven't set up your profile card yet.\nIt takes less than a minute!",
    create: "Create"
  },
  error: {
    update: {
      title: "An error occurred while saving your profile card.",
      message: "Please refresh and try again. If the problem persists, please contact support."
    }
  },
  meeting_prep: {
    collapsed: "The chatbot uses {{name}}'s preferred communication style and growth direction to shape what to discuss in the 1on1.",
    expand: "View details",
    collapse: "Collapse",
    empty_collapsed: "Set up your profile and the chatbot will help you find what to discuss in your 1on1, tailored to you.",
    empty_action: "Set up"
  }
};

// src/locales/en-US/meeting_prep.json
var meeting_prep_default = {
  meeting_status: {
    question_confirm_requested: "Question review requested",
    waiting_counterpart_confirm: "Awaiting question review",
    meeting_ready: "Ready to meet!",
    write_prep: "Write your prep",
    prep_completed: "Prep completed",
    waiting_member_prep: "Awaiting member's prep",
    review_prep_and_create_questions: "Review prep & add questions"
  },
  meeting_status_tooltip: {
    question_confirm_requested: "Please click the 'Confirm Questions' button at the bottom of the meeting page.",
    waiting_counterpart_confirm: "Meeting preparation will be complete once your counterpart finishes reviewing.",
    meeting_ready: "Proceed with the meeting based on your preparation.",
    write_prep: "Use the chatbot to organize your work, situation, and discussion topics to share with your counterpart.",
    prep_completed: "Your counterpart will prepare questions based on the shared prep.\nFeel free to add questions if there's anything you'd like to discuss further.",
    waiting_member_prep: "Once the member completes their prep, you can view recommended questions and the meeting guide.",
    review_prep_and_create_questions: "Use the meeting guide, recommended questions, and templates to create meeting questions."
  },
  confirm_questions: {
    banner: "If you've reviewed all questions, click the 'Confirm Questions' button to let your counterpart know!",
    button: "Confirm Questions"
  },
  create_page: {
    chat_placeholder: "Enter your answer...",
    create_button: "Create a meeting prep",
    error: "An error occurred while creating the prep. Please refresh and try again.",
    success: "Prep has been created successfully.",
    saved: "Saved",
    saving: "Saving...",
    send_button: "Send",
    update: "Update Meeting Prep",
    edit: "Edit",
    header_title: "Write your prep",
    header_description: "Feel free to tell the chatbot what happened at work recently, any thoughts or concerns that came up, and anything you'd like to discuss with your leader.",
    chat_helper: "Start with whatever work or concern comes to mind. Even if your topic isn't clear yet, you can find it together with the chatbot.",
    draft_empty: "After chatting, press Create prep and the AI will summarize your conversation into a draft. You can review and edit it, then share it with your leader."
  },
  delete_dialog: {
    description: "Are you sure you want to delete this prep?",
    title: "Delete prep"
  },
  shared_prep: {
    leader_intro_title: "Prep shared by the member",
    member_intro_title: "Prep shared with your leader",
    leader_intro_description: "Read it before the meeting to see the topics the member wants to cover and what they hope to get out of this conversation."
  },
  prep_shared_nudge: {
    message: "{{name}} shared their prep. A meeting guide and recommended questions are ready for you.",
    button: "View prep",
    message_mobile: "{{name}} shared their prep",
    view: "View"
  },
  ai_summary: {
    button: "Summarize with AI",
    cta_helper: "For a long prep, you can create an AI summary to skim it first.",
    tab_summary: "AI summary",
    tab_original: "Original"
  },
  prep_stage: {
    needs_focus_hint: "Tell us just one thing you'd like to talk about first in this 1on1. If you don't have a specific topic, it's fine to write about what's been on your mind lately or something you'd like your leader's input on.",
    ready_generate: "Put this into my prep",
    ready_update: "Update my prep with this",
    ready_continue: "I'd like to talk a bit more"
  },
  placeholder: {
    member_before: {
      title: "Organize what you'd like to discuss in this 1on1 in advance",
      title_mobile: "Organize your 1on1 topics in advance",
      description: "Writing it down lets your leader understand where you are and prepare. You get a deeper conversation in less time.",
      button: "Write a prep",
      how: "Chat privately with the chatbot and your prep gets organized. Review and edit it, then share."
    },
    deadline: {
      far: "Please share by the day before the meeting. You cannot edit a prep once shared.",
      tomorrow: "Please share today. You cannot edit a prep once shared.",
      today: "Please share before the meeting. You cannot edit a prep once shared."
    },
    member_not_shared: {
      title: "Review your prep and share it with your leader",
      description: "Once shared, your leader can see your main topics and the support you need before the meeting.",
      button: "Continue writing"
    },
    leader_waiting: {
      title: "Once the member shares their prep, you'll see it here",
      description: "You'll get what the member wants to talk about, along with a meeting guide tailored to it. Read it ahead of time to prepare for the conversation.",
      hint: "The 1on1 goes ahead as planned even without a shared prep."
    }
  },
  help: {
    trigger: "How is prep used?",
    title: "What is prep?",
    confirm: "Got it",
    private_badge_member: "Only you can see this",
    private_badge_leader: "Only the member can see this",
    shared_badge_member: "Your leader can see this",
    shared_badge_leader: "You can see this",
    member: {
      lead: "A prep is a note you write ahead of this 1on1 to organize what you want to talk about.",
      step1_title: "You chat privately with the chatbot",
      step1_description: "The AI turns your conversation into a draft prep. Your leader cannot see this chat.",
      step2_title: "You review and edit the draft",
      step2_description: "You can change anything before sharing.",
      boundary: "When you share",
      step3_title: "Your leader receives the prep",
      step3_description: "Only the prep you shared is passed along.",
      step4: "Your leader prepares with the meeting guide and recommended questions that come with it.",
      note: "You cannot edit a prep once shared. Please review it once more before sharing."
    },
    leader: {
      lead: "A prep is a note the member writes ahead of a 1on1 to organize what they want to talk about.",
      step1_title: "The member chats privately with the chatbot",
      step1_description: "The AI turns their conversation into a draft prep. This chat is not visible to you.",
      step2_title: "The member reviews and edits the draft",
      step2_description: "They can change anything before sharing.",
      boundary: "When the member shares",
      step3_title: "The prep is passed along",
      step3_description: "You only see the prep the member shared.",
      step4: "A meeting guide and recommended questions come with it. Use them to prepare for the conversation.",
      note: "If you don't see a prep yet, the member hasn't shared it."
    }
  },
  guide: {
    title: "Meeting Guide",
    description: "Based on the prep your member shared and their profile, this suggests a flow for the meeting \u2014 how to open the conversation, what to explore deeply, and how to wrap up.",
    flow: "Suggested Flow",
    overview: "Overview",
    stage: {
      OPENING: "Opening",
      MAIN: "Main discussion",
      DEEPEN: "Deep dive",
      CLOSING: "Wrap-up"
    },
    key_insight: "Key Insights",
    approach: "Recommendations & Cautions",
    tip: "Conversation Tips"
  },
  profile_card: {
    title: "Member Profile"
  },
  error: {
    access: "Only members can write a prep."
  },
  mobile: {
    tabs: {
      chat: "Chat",
      summary: "Prep"
    },
    title: "Write Prep"
  },
  question_recommend: {
    description: "These questions are prepared for each stage of the suggested flow. Check the ones you want to add to the meeting question list. They stay hidden from your member until added.",
    title: "Recommended Questions",
    grouped_title: "Suggested Flow \xB7 {{count}} questions",
    check_tooltip: "Add as a meeting question",
    delete_tooltip: "Remove from the recommended list",
    adopt_all: "Use all",
    adopt_all_confirm: "Add all {{count}} recommended questions to the meeting question list?",
    adopt_confirm: "Add the {{count}} selected questions to the meeting question list?",
    delete_confirm: "Remove this question from the recommended list?\nThis can't be undone.",
    selected_count: "{{count}} questions selected",
    select_all: "Select all questions",
    unselect_all: "Deselect all questions",
    view_in_content: "View in Content tab"
  },
  share: {
    description: "Once shared, your prep will be visible to your leader and cannot be edited.",
    done: "Share",
    error: {
      message: "An error occurred while saving and sharing. Please try again later.",
      title: "Save Failed"
    },
    success: {
      message: "Your prep has been saved and shared with your leader.",
      title: "Prep Saved & Shared"
    },
    requested: {
      title: "Saving & sharing prep",
      message: "Your prep has been saved. It will be shared with your leader shortly."
    },
    title: "Share with Your Leader"
  },
  survey: {
    skip: "I'm not sure yet",
    skip_description: "I'll find a topic while chatting with the chatbot.",
    section_title: "Conversation input",
    section_helper: "These help surface what matters to you right now and shape what you'll talk about in this 1on1. Pick only the ones you have an answer for.",
    section_expand: "Expand",
    section_collapse: "Collapse",
    section_optional: "Optional",
    summary_profile: "Profile applied",
    summary_topic: "{{count}} selected",
    summary_empty: "Nothing selected yet",
    label_profile: "Profile",
    label_survey: "Topics"
  },
  survey_answers: {
    condition: {
      category: "Work Condition",
      question: "How is your overall work condition these days?",
      helper: "Your selected condition is reference information that helps guide the chat, and it is not shared with your leader as-is. Even when it relates to what you'll discuss, you choose whether to include it in the prep.",
      VERY_GOOD: "Very Good",
      GOOD: "Good",
      NORMAL: "Okay",
      TIRED: "Tired",
      VERY_BAD: "Very Bad"
    },
    topic: {
      category: "Meeting Topic",
      question: "What would you most like to talk about in this 1on1?",
      helper: "Pick the topic closest to how you feel right now. If you're not sure yet, it's fine to find it while chatting with the chatbot.",
      PERFORMANCE_FEEDBACK: "Performance & Feedback",
      PERFORMANCE_FEEDBACK_description: "I'd like to look back on my recent results and how I work, and get feedback on what went well and what could improve.",
      GROWTH_CAREER: "Growth & Career",
      GROWTH_CAREER_description: "I'd like to talk about the skills I want to build, my career direction, and the experience I need.",
      WORK_ISSUES: "Work Challenges & Support",
      WORK_ISSUES_description: "I'd like to look at where I'm stuck or what keeps recurring, and find a way forward or the support I need.",
      TEAM_CULTURE: "Team Communication & Culture",
      TEAM_CULTURE_description: "I'd like to talk about how I feel about the team's communication, collaboration, and atmosphere, and the changes I'd like to see.",
      ROLE_RESPONSIBILITY: "Role & Responsibility",
      ROLE_RESPONSIBILITY_description: "I'd like to clarify the role and responsibilities expected of me, my work priorities, and my decision-making scope."
    }
  },
  title: "Prep"
};

// src/locales/en-US/oneonone_notification.json
var oneonone_notification_default = {
  dday_reminder: {
    title: "Meeting Reminder",
    body_dday: "You have a 1on1 meeting with {{partnerName}} today at {{time}}.",
    body_d1: "You have a 1on1 meeting with {{partnerName}} tomorrow at {{time}}.",
    body_dn: "You have a 1on1 meeting with {{partnerName}} in {{daysUntil}} days."
  },
  meeting_add: {
    title: "New Meeting",
    body: "A new 1on1 meeting with {{partnerName}} has been created."
  },
  meeting_change: {
    title: "Meeting Updated",
    body: "Your 1on1 meeting with {{partnerName}} has been rescheduled to {{newSchedule}}.",
    body_type_only: "Your 1on1 meeting with {{partnerName}} has been changed to {{newMeetingTypeLabel}}.",
    body_both: "Your 1on1 meeting with {{partnerName}} has been rescheduled to {{newSchedule}} ({{newMeetingTypeLabel}}).",
    meeting_type_offline: "in-person",
    meeting_type_online: "video"
  },
  meeting_cancel: {
    title: "Meeting Canceled",
    body: "{{partnerName}} has canceled the 1on1 meeting."
  },
  prep_share: {
    title: "Prep Shared",
    body: "{{partnerName}} has shared their prep."
  },
  question_request: {
    title: "Question Review Request",
    body: "{{partnerName}} has requested a review of meeting questions."
  },
  recording_ready: {
    title: "Recording Ready",
    body: "Your recording with {{partnerName}} is ready. Tap to import and start AI analysis."
  },
  analysis_complete: {
    title: "Analysis Ready",
    body: "Analysis for your meeting with {{partnerName}} is complete. Tap to review the results."
  },
  member_feedback_ready: {
    title: "AI Feedback Ready",
    body: "Review the AI feedback from your meeting with {{partnerName}} and reflect on how it went."
  },
  in_progress_incomplete_reminder: {
    title: "Please end the meeting",
    body: "Your meeting with {{partnerName}} is still marked as in progress."
  },
  cycle_reminder: {
    title: "Schedule your next 1on1",
    body: "It's almost time for your regular 1on1 with {{partnerName}}. Create the meeting and keep the conversation going."
  },
  empty: "No new notifications.",
  fallback: {
    title: "New Notification",
    body: "You have received a new notification."
  }
};

// src/locales/en-US/landing_new.json
var landing_new_default = {
  change: {
    pill: "Orblit, the change-management tool that carries your vision into every corner",
    heroTitle: "Strategy isn't announced \u2014 it's executed in 1on1.",
    heroSubtitle: "From the executive vision to the newest hire, Orblit designs the path it travels.",
    problem: {
      title: "The real reason strategy execution fails",
      quotes: [
        "A month after the all-hands, new hires can't recall the company's direction.",
        "Six weeks after leadership training, 90% revert to old habits.",
        "HR has no idea which team is festering until the 360 review lands.",
        "Only after a top performer resigns do we start asking the real reasons."
      ]
    },
    layers: {
      title: "Change must happen at three levels at once",
      items: [
        {
          title: "Executives (Top)",
          desc: "Embed strategic messages into 1on1 agendas"
        },
        {
          title: "HR (Middle)",
          desc: "Monitor communication and engagement org-wide"
        },
        {
          title: "Leaders (Frontline)",
          desc: "Translate and deliver the message within their teams"
        }
      ]
    },
    toolbox: {
      title: "A new toolbox for HR",
      items: [
        {
          title: "Top-down alignment",
          desc: "Automatically embeds company goals and key strategic messages into 1on1 prep, so strategy reaches every individual contributor."
        },
        {
          title: "Horizontal visibility",
          desc: "Monitors each team's communication frequency and leadership health in real time on a company-wide dashboard."
        },
        {
          title: "Bottom-up insight",
          desc: "Surfaces organizational signals \u2014 burnout symptoms, attrition risk, morale shifts \u2014 drawn from accumulated 1on1 data."
        },
        {
          title: "HR action",
          desc: "Intervenes within the golden window through alerts such as flight-risk warnings."
        }
      ]
    },
    cases: {
      title: "Results in practice",
      h: {
        name: "H Group affiliate",
        sub: "Evidence of culture change",
        body: "They achieved the[d] [b=#2563eb]stable adoption and activation of a 1on1 program[/b] that other vendors and in-house tools couldn't deliver."
      },
      s: {
        name: "S Group affiliate",
        sub: "Transforming the HR infrastructure itself",
        body: "They [b=#4f46e5]rebuilt performance management around 1on1[/b],[d] elevating frontline leadership capability."
      }
    },
    next: {
      label: "Next Step",
      title: "Every change begins with a conversation",
      cta: "Explore Communication"
    }
  },
  communication: {
    pill: "Orblit, the tool that keeps healthy conversation flowing across your org",
    heroTitle: "Communication isn't a personality \u2014 it's a skill.",
    heroSubtitle: "Orblit helps every leader start a good conversation.",
    problem: {
      title: "Awkward 1on1 aren't just your problem",
      subtitle: "It's not a personal flaw \u2014 it's a structural one.",
      quotes: [
        "I had nothing to say, so we just talked about the weather.",
        `Whatever I ask, all I get back is "I'm fine."`,
        "I gave advice, and the next week their face had hardened.",
        "I have no objective way of knowing[d] whether I'm doing well."
      ]
    },
    needs: {
      title: "A good conversation needs three things",
      items: [
        {
          title: "1. Prepared questions",
          desc: 'Not a vague "How are you?" \u2014 but[d] questions tailored to the person and moment'
        },
        {
          title: "2. A safe atmosphere",
          desc: "A pre-set structure designed so[d] the team member speaks first"
        },
        {
          title: "3. Objective feedback",
          desc: "A mirror to reflect on[d] your own communication afterward"
        }
      ]
    },
    how: {
      title: "How does it work?",
      steps: [
        {
          tag: "Before",
          title: "AI meeting prep + suggested questions",
          desc: "Orblit analyzes the condition and concerns your team member noted in advance, then hands the leader a tailored guide."
        },
        {
          tag: "During",
          title: "Automatic STT removes the note-taking burden",
          desc: "Leaders no longer scribble notes. Focus only on listening and connecting."
        },
        {
          tag: "After",
          title: "Leadership coaching from speech analysis",
          desc: '"What was my talk ratio? Did I ask good questions?" \u2014 get data-driven reports and coaching.'
        },
        {
          tag: "Over time",
          title: "Communication-skill report",
          desc: "Track your growth curve across six dimensions: listening, questioning, empathy, feedback, and more."
        }
      ]
    },
    scenario: {
      title: "Team lead Carter's first 1on1",
      p1: `Carter dreaded the first 1on1 with a hire six months in. But Orblit analyzed the hire's prep and flagged that [h]"they're confused about recent OKR alignment"[/h], so Carter opened with a suggested question: [u]"What goal feels least clear to you right now?"[/u]`,
      p2: 'Thirty minutes later, Carter understood for the first time [b=#1a1a1a]"where this person was truly stuck."[/b] Orblit organized the action items automatically after the talk, so Carter could focus 100% on simply listening.'
    },
    transform: {
      title: "How leaders change once Orblit 1on1 take root",
      asis: "A one-way evaluator[d] who forces the right answer",
      tobe: "A growth partner who[d] draws out hidden potential",
      points: [
        {
          before: '"What do I even say?" \u2014[d] the dread you want to avoid',
          after: "The confidence of a leader[d] armed with data and guidance"
        },
        {
          before: "The unease of not knowing[d] what your team really thinks",
          after: "The trust and ease of reading[d] your team through open communication"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "A good conversation is only the start. What's next?",
      cta: "Explore Growth"
    }
  },
  growth: {
    pill: "Orblit, the growth-management tool that grows your people through conversation",
    heroTitle: "Growth is accumulation, not an event.",
    heroSubtitle: "Not one review a year \u2014 it's the weekly 1on1 that builds growth.",
    problem: {
      title: "Why is performance review always so noisy here?",
      quotes: [
        "A team member reviewed once a year who never buys in",
        "A leader squeezing out a year of results right before the review",
        "A great talk, but a month later the action items have vanished",
        "Management that misses 'quiet quitting' until it's too late"
      ]
    },
    cpm: {
      title: "Continuous Performance Management",
      body: "Leading global companies are shifting from the annual review[d] to a [b=#1a1a1a]continuous performance management[/b] paradigm."
    },
    cycle: {
      title: "Automating[m] the growth cycle",
      items: [
        {
          title: "Commit: action items",
          desc: "Action items are created automatically right after a 1on1."
        },
        {
          title: "Track: auto-recall",
          desc: "The next 1on1 automatically pulls in last time's action items."
        },
        {
          title: "Accumulate: AI report",
          desc: "Analyzes each member's motivation, strengths, weaknesses, and flight risk."
        },
        {
          title: "Recognize: achievement archive",
          desc: "Scattered contributions are organized into evaluation evidence automatically."
        },
        {
          title: "Distribute: team-level view",
          desc: "Manage team-level distribution and growth based on the 9-Box model."
        }
      ]
    },
    data: {
      title: "One person's growth, seen in data",
      card: {
        avatar: "M",
        name: "Michael Brown",
        role: "Marketing Team \xB7 Year 2",
        goalLabel: "Key objective",
        goal: "Win 10 key new accounts in Q3",
        progressLabel: "Progress",
        progress: "12 target accounts sourced, 8 meetings done \u2014 on track",
        tasksLabel: "In progress",
        tasks: [
          {
            bold: "Custom proposal for Company B",
            rest: " \u2014 refining content, sending this week"
          },
          {
            bold: "New inbound leads",
            rest: " \u2014 follow-up calls (3x weekly cadence)"
          }
        ],
        motivationLabel: "Key motivators",
        motivations: ["Autonomy", "Achievement"],
        riskLabel: "Flight Risk",
        riskValue: "Stable (Low)"
      },
      features: [
        "See [b=#1a1a1a]Michael's live goal progress[/b] and the [b=#1a1a1a]work[/b] he's focused on right now \u2014 all at a glance.",
        "Find which [b=#1a1a1a]motivators[/b] drive Michael and reflect them in how you assign work.",
        "Spot when [b=#1a1a1a]flight risk[/b] starts climbing and respond before it's too late."
      ]
    },
    transformMember: {
      title: "How members change once Orblit 1on1 take root",
      asis: "A passive doer buried[d] in assigned tasks",
      tobe: "An owner of growth who grasps the why[d] and draws the roadmap",
      points: [
        {
          before: "Anxiety at review time,[d] guessing the leader's mind",
          after: "Predictable stability from clearly[d] knowing your standing and results"
        },
        {
          before: "The isolation of hard work[d] no one notices",
          after: "Engagement and efficacy from[d] feeling your contribution is seen"
        }
      ]
    },
    transformHr: {
      title: "How HR changes once Orblit 1on1 take root",
      asis: "An administrator content[d] with running rules and systems",
      tobe: "A strategic partner who[d] powers leaders' on-the-ground results",
      points: [
        {
          before: "Blindsided by a key talent's sudden[d] resignation notice",
          after: "Catch attrition signals early[d] and defend proactively"
        },
        {
          before: "No way to see what's really happening[d] on the ground",
          after: "Continuous HR analysis that strengthens[d] org health and potential"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "When individual growth adds up, the org changes. What's next?",
      cta: "Explore Change Management"
    }
  },
  home: {
    hero: {
      badge: "Powered by AI",
      title: "Leadership growth [m]through 1on1",
      subtitle: "At the core of leadership is communication.",
      primaryCta: "Start for free",
      secondaryCta: "Get the brochure",
      browserUrl: "1on1.orblit.space",
      tabs: [
        {
          id: "skill",
          label: "Tailored coaching",
          title: "AI conversation coaching & guide",
          desc: "When conversations feel daunting amid many variables by person and situation, AI suggests tailored 1on1 questions and the right direction for each member.",
          sentence: "Not sure what to ask, or whether the conversation is going well?\nLet AI read your conversation style and give you tailored coaching."
        },
        {
          id: "time",
          label: "AI prep & recap",
          title: "One-click live auto-recording",
          desc: "From prep to post-meeting data, it cuts the countless opportunity costs so leaders can focus entirely on the conversation.",
          sentence: "Short on time to prep and organize every single meeting?\nAI handles the tedious steps for you \u2014 from prep to notes to summary."
        },
        {
          id: "context",
          label: "Individual Guides",
          title: "AI tailored agenda suggestions",
          desc: "Understand up front where each member stands and the meaningful topics they want to raise with their leader.",
          sentence: "Frustrated not knowing what your member is wrestling with lately?\nAI organizes what they've logged and proposes a guide with questions."
        },
        {
          id: "continuity",
          label: "Continuous care",
          title: "Continuous growth, nothing lost",
          desc: "So action commitments after a meeting aren't left unmanaged or forgotten, it links smoothly to the next meeting and to overall performance/stability metrics.",
          sentence: "Do your hard-won great conversations end as one-offs?\nWe deliver reports from accumulated data and recommend the next meeting's topics."
        }
      ],
      sidebar: {
        menu: ["1on1", "Member Report", "9 Box", "Dashboard"],
        settings: "Settings",
        profileInitial: "S",
        profileName: "Sarah Miller"
      },
      skillPanel: {
        memberInitial: "E",
        memberName: "Emma Davis",
        memberRatio: "53.6%",
        leaderAvatar: "\u{1F469}\u200D\u{1F4BC}",
        leaderName: "Sarah Miller",
        leaderRatio: "46.4%",
        ownerAvatarAlt: "Action item owner",
        feedbackTitle: "Feedback for the leader",
        strengthLabel: "Strength",
        strengthTitle: "Made room for the member to speak",
        strengthBody: "Leader Sarah Miller stayed below the member's talk ratio (53.6%) and held her share to 46.4%, leaving ample space for the member to speak. Letting the member lead the conversation comfortably is a real strength.",
        improvementLabel: "Watch-out",
        improvementTitle: "Follow-up actions and mutual alignment fell short",
        improvementBody: "The blockers and ideas raised during the conversation could have been turned into concrete follow-up actions (Action Items) and agreed upon more clearly. In the next meeting, try defining specific commitments to execute together and aligning on them explicitly."
      },
      timePanel: {
        summaryTitle: "AI summary",
        meetingTitle: "Reset dashboard project priorities",
        coreLabel: "Key point",
        coreBody: "The meeting confirmed Emma's workload strain from the dashboard revamp and parallel tasks, then aligned on the need to reset priorities for better focus.",
        decisionLabel: "Decisions",
        decisions: [
          "Emma will lead the dashboard priority reset.",
          "Add another teammate if the workload spikes."
        ],
        supportLabel: "Issue and support request",
        issueTag: "[Issue]",
        issueBody: "Too many parallel dashboard tasks are causing context switching and schedule pressure.",
        supportTag: "[Support]",
        supportBody: "Support from other teammates will be coordinated when priorities are reshuffled.",
        actionTitle: "New action items",
        actionAdd: "Add a new action item",
        actionItems: [
          {
            text: "Fix the OKR dashboard data-source issue",
            ai: false
          },
          {
            text: "Create a documentation template",
            ai: true
          },
          {
            text: "Review the cadence of cross-team scheduling syncs",
            ai: true
          },
          {
            text: "Run a weekly milestone progress review for the dashboard",
            ai: true
          }
        ]
      },
      contextPanel: {
        cards: [
          {
            label: "Preferred style",
            value: "Detailed context"
          },
          {
            label: "Best feedback",
            value: "Concrete solutions"
          },
          {
            label: "Career pull",
            value: "Leadership track"
          },
          {
            label: "Motivator",
            value: "Recognition"
          }
        ],
        conditionLabel: "\u{1F464} Current state:",
        conditionValue: "High work engagement and positive energy",
        topicLabel: "\u{1F3AF} Main topic:",
        topicValue: "Progress and analysis of the chatbot upgrade",
        progressTitle: "1. Current work progress",
        progressItems: [
          "QA for the AI upgrade is complete, and server rollout plus stabilization is underway",
          "Core chatbot features are live and new scenario tests are in progress"
        ],
        challengeTitle: "2. Current project difficulties",
        challengeItems: [
          "Urgent requests and short prep time are creating pressure.",
          "A missing prompt-quality benchmark limits stability monitoring."
        ],
        guideTitle: "AI meeting guide",
        insightTitle: "Key insight",
        insightBody: "Strong results are being delivered, but the lack of clear validation standards is creating hidden anxiety.",
        recommendTitle: "Recommendation",
        recommendBody: "Open by celebrating the release, then ask about user reactions and listen closely to any friction points."
      },
      continuityPanel: {
        title: "Emma Davis report",
        flightRiskTitle: "Flight Risk",
        flightRiskLevel: "Low",
        flightRiskBody: "Emma is proactively shaping the dashboard priorities and aiming for visible impact, so current attrition risk is very low.",
        motivationTitle: "Motivators",
        motivationTags: [
          "Ownership",
          "Visible impact",
          "Growth learning",
          "Leader recognition"
        ],
        demotivationTitle: "Drainers",
        demotivationTags: [
          "Vague goals",
          "Resource limits",
          "Poor communication"
        ],
        strengthTitle: "Strengths",
        strengthTags: [
          "Problem solving",
          "Product insight",
          "Proactive attitude"
        ],
        weaknessTitle: "Risks",
        weaknessTags: [
          "Too much coordination",
          "Abstract goals",
          "Schedule pressure"
        ]
      }
    },
    scrollReveal: {
      lines: [
        "At Orblit, 1on1 are not simple check-ins.",
        "Conversation context",
        "becomes data,",
        "and that data drives",
        "the most concrete form of organizational growth."
      ],
      mobileBreakWords: ["Orblit,"]
    },
    coreProblem: {
      title: "Are your 1on1 [m]really working?",
      subtitle: "They matter deeply to both leaders and team members, but real-world friction makes them fail more often than teams expect.",
      items: [
        {
          title: "Are they still just formal performance check-ins?",
          desc: "More than 80% of 1on1 stay stuck at the level of quarterly performance conversations,[m] and their impact drops sharply as the cycle gets longer."
        },
        {
          title: "Are leaders being left alone with the burden?",
          desc: "Managers spend up to 210 hours a year on 1on1-related admin work,[m] which steals time from actual leadership."
        },
        {
          title: "Is your data still fragmented and disposable?",
          desc: "More than 70% of companies lack a real system for 1on1 data,[m] and that pushes practitioner satisfaction below 10%."
        }
      ]
    },
    flowTabs: {
      title: "A continuous growth [m]loop for stronger 1on1",
      subtitle: "See the whole growth cycle at a glance, from meeting prep to follow-through, without any break in continuity.",
      sections: [
        {
          step: "STEP 01",
          title: "Before",
          subtitle: "AI guidance for better prep",
          description: "When a 1on1 is scheduled, AI organizes the member's key context and gives the leader tailored questions plus a ready-to-use guide.",
          features: [
            "Live schedule alerts",
            "AI context chatbot",
            "Tailored questions"
          ],
          nodes: [
            {
              tagLabel: "Trigger",
              tagStatus: "Scheduled",
              tagColor: "gray",
              icon: "solar:calendar-date-bold-duotone",
              title: "Meeting detected and announced",
              subtitle: "The system catches the 1on1 schedule and sends a live alert.",
              muted: true
            },
            {
              tagLabel: "AI Chatbot",
              tagStatus: "Analyzing",
              tagColor: "blue",
              icon: "solar:chat-square-like-bold-duotone",
              title: "AI chatbot gathers member context",
              subtitle: "Past notes and fresh signals are pulled together into a concise briefing."
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "Tailored guide and questions",
              subtitle: "The system suggests the agenda and the questions that best fit this meeting.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 02",
          title: "During",
          subtitle: "Stay fully in the conversation",
          description: "Because AI handles the record automatically, leaders and members can stop chasing notes and focus on listening, eye contact, and real conversation.",
          features: [
            "Meeting recording",
            "Shared notes",
            "Live STT"
          ],
          nodes: [
            {
              tagLabel: "Action",
              tagStatus: "Live",
              tagColor: "red",
              icon: "solar:play-circle-bold-duotone",
              title: "Start meeting recording",
              subtitle: "Voice or video recording can begin right with the meeting."
            },
            {
              tagLabel: "Editor",
              tagStatus: "Syncing",
              tagColor: "blue",
              icon: "solar:pen-new-round-bold-duotone",
              title: "Shared meeting workspace",
              subtitle: "Leader and member can write key topics together in real time.",
              muted: true
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "STT",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI turns the talk into text",
              subtitle: "AI captures the discussion so nothing meaningful is lost.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 03",
          title: "After",
          subtitle: "Summaries and insight right away",
          description: "Once the meeting ends, AI completes the summary from the conversation record and supports everything from action-item capture to leadership coaching.",
          features: [
            "AI summary",
            "Action items",
            "Leadership coaching"
          ],
          timelineNodes: [
            {
              tagLabel: "AI Analyst",
              tagStatus: "Structured",
              tagColor: "blue",
              icon: "solar:document-text-bold-duotone",
              title: "AI summary and structure",
              subtitle: "The full meeting conversation is condensed into a useful summary.",
              marker: "dot"
            },
            {
              tagLabel: "Action Items",
              tagStatus: "Organized",
              tagColor: "green",
              icon: "solar:check-square-bold-duotone",
              title: "Automatic action capture",
              subtitle: "Next actions are pulled out and assigned automatically.",
              marker: "mic"
            },
            {
              tagLabel: "AI Coach",
              tagStatus: "Coached",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI leadership coaching",
              subtitle: "The 1on1 script and communication style are analyzed into concrete feedback.",
              marker: "dot",
              accent: true
            }
          ]
        },
        {
          step: "STEP 04",
          title: "Ongoing",
          subtitle: "A loop that keeps growth moving",
          description: "As meeting data accumulates, it becomes an asset. Member reports and dashboards are generated from it and immediately fed into the next 1on1.",
          features: [
            "Member insight report",
            "Org dashboard",
            "Auto-linked next prep"
          ],
          topBadge: "Accumulated 1on1 meeting data",
          bottomBadge: "Insights feed the next 1on1 prep",
          nodes: [
            {
              tagLabel: "AI Insight",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "Member insight report",
              subtitle: "Meeting history is combined into a report on motivation, strengths, gaps, and risk.",
              accent: true
            },
            {
              tagLabel: "Dashboard",
              tagStatus: "Updated",
              tagColor: "blue",
              icon: "solar:chart-square-bold-duotone",
              title: "Org dashboard",
              subtitle: "Meeting progress and leadership indicators are visualized by team and function."
            }
          ]
        }
      ]
    },
    coreBenefits: {
      title: "What begins with Orblit [m]is a healthier conversation",
      subtitle: "It creates practical, meaningful change for leaders, team members, and HR alike.",
      items: [
        {
          target: "Leader",
          title: "A coach for growth",
          desc: "Leaders step away from prep and note-taking burden so they can focus fully on listening to and understanding the member."
        },
        {
          target: "Member",
          title: "A partner in growth",
          desc: "Members put down the fear of evaluation, talk honestly about their career and concerns, and gain psychological safety."
        },
        {
          target: "HR",
          title: "A data-led org architect",
          desc: "HR moves beyond just tracking meeting completion and starts running the organization with proactive, strategic data."
        }
      ]
    },
    customerInterviews: {
      badge: "Customer story",
      companyPrefix: "Customer",
      goToSlide: "Go to slide {{number}}",
      items: [
        {
          company: "Hansol Home Deco",
          person: "Gyuho Seo, Manager",
          title: "Establishing a 1-on-1 culture that connects vision and strategy",
          body: "For an organization's vision and strategy to translate into how its people execute,\nit needs goal alignment built through continuous one-on-one conversations.\nUsing Orblit, Hansol Home Deco strengthens its leaders' one-on-one capabilities\nand establishes a culture of regular conversations with team members,\nlaying the foundation for its strategy to be executed on the ground."
        },
        {
          company: "Undermilli",
          person: "Hojong Lee, CEO",
          title: "Turning a new leader's vague talks into productive interactions",
          body: "Small talk is easy,\nbut 'conversations for growth and performance' are far vaguer and harder than expected.\nOrblit set the frame for productive conversations\nbased on complete, no-gaps member information.\nEspecially the AI coaching grounded in actual speech\nbecame a pillar of self-awareness and growth for new leaders."
        },
        {
          company: "Grassmedi",
          person: "Jinsik Choi, CEO",
          title: "Turning vague management into the most concrete solution",
          body: "A leader's biggest concerns\u2014'people' and 'delegation'\u2014\nfind their clearest answer in 1on1s with team members.\nCommunicating up close to measure performance and support growth,\nOrblit turned once-vague management into the most certain, concrete solution."
        }
      ]
    },
    whatIsOrblit: {
      title: "Orblit is not [m]just an automation tool.",
      body: "It's a communication tool that keeps healthy conversation flowing through the org,[m] a growth tool that turns conversation into progress,[m] and a change tool that carries leadership vision to the front line.",
      nodeTitle: "Leadership powered by 1on1",
      closing: "All of this begins with [m]strong leadership grounded in 1on1.",
      cards: [
        {
          to: "/why/communication",
          title: "Communication",
          desc: "It helps healthy conversation move through the organization. Leaders know what to ask, and members feel safe enough to share what they really think."
        },
        {
          to: "/why/growth",
          title: "Growth",
          desc: "It turns conversation into growth. Promises made in the meeting don't disappear but connect to performance, evaluation, and development."
        },
        {
          to: "/why/change",
          title: "Change",
          desc: "It spreads executive vision and strategy across the whole company and makes the result visible all the way to the front line."
        }
      ]
    },
    impactStats: {
      title: "The clearest[br]culture impact.",
      subtitle: "Improve the quality of 1on1 and automate measurement with Orblit, and the performance gap becomes visible fast.",
      stats: [
        {
          value: 200,
          suffix: "%",
          label: "Activation rate"
        },
        {
          value: 2,
          suffix: "X",
          label: "Satisfaction lift"
        },
        {
          value: 84,
          prefix: "-",
          suffix: "%",
          label: "Admin time"
        },
        {
          value: 20,
          prefix: "-",
          suffix: "%",
          label: "Attrition rate"
        }
      ]
    },
    momentumProgram: {
      eyebrow: "1on1 adoption solution",
      title: "Orblit Momentum Program",
      plan: "(3-month plan)",
      body: "This is a 3-month package that helps your organization adopt 1on1, the core habit behind lasting leadership, step by step without strain.[br]To overcome the limits of one-off corporate talks, we tightly connect the Orblit tool with hands-on enablement.",
      points: [
        {
          title: "Shared mindset and skill training for leaders and members",
          description: "We build deep agreement on why these conversations matter. Leaders also get practical, ready-to-use techniques for listening, feedback, and questioning."
        },
        {
          title: "Prevent one-off impact through product usage",
          description: "Teams apply the work directly and run real 1on1. Tailored questions and feedback loops keep improving skill and effectiveness over time."
        },
        {
          title: "Field data reports and tailored follow-up training",
          description: "We deliver reports based on accumulated 1on1 data, then connect the discovered issues to follow-up enablement that fits the org."
        }
      ],
      imageAlt: "An Asian trainer leading a corporate 1on1 and leadership workshop in a modern conference room",
      fallbackEyebrow: "Momentum Program",
      fallbackTitle: "Training, product, and reports in one[br]adoption program for 1on1",
      cta: "View the program brochure"
    },
    logos: {
      title: "Designed with leading HR experts from Korea and abroad",
      items: [
        {
          name: "Dell",
          desc: "[Former APAC Vice President]"
        },
        {
          name: "SAMSUNG",
          desc: "[Former Samsung HR / L&D]"
        },
        {
          name: "PennState",
          desc: "[HR / organizational development faculty]"
        },
        {
          name: "George Mason",
          desc: "[Industrial-organizational psychology faculty]"
        }
      ]
    },
    security: {
      title: "Your company's data[br]is protected at the highest level.",
      features: [
        {
          title: "Data Encryption",
          description: "All meeting records are encrypted with AES-256 at rest and TLS 1.3 in transit. Your data is protected end-to-end, from creation to storage."
        },
        {
          title: "Workspace-level Data Isolation",
          description: "Row-Level Security (RLS) and Role-Based Access Control (RBAC) ensure complete data separation between workspaces. Customer data is stored in Orblit's dedicated DB, physically isolated from your existing databases."
        },
        {
          title: "No AI Model Training",
          description: "Your meeting data is never used for AI model training or performance improvement. Our enterprise agreement with the AI analysis provider (Google Vertex AI) explicitly prohibits training on customer data."
        },
        {
          title: "Role-Based Access Control",
          description: "RBAC is applied to manage data access permissions within your organization."
        },
        {
          title: "Auto-deletion of Recordings",
          description: "Audio recordings are automatically deleted within 3 days after analysis is complete. Only the converted text analysis results are retained."
        },
        {
          title: "Change History Audit Logs",
          description: "All changes to critical data are automatically logged. Before/after values, timestamps, and the performing user are recorded for full traceability, and these records cannot be tampered with."
        },
        {
          title: "Secure Communications",
          description: "All API communications are processed over HTTPS only \u2014 HTTP access is blocked. CORS policies restrict access to authorized domains only, running on managed infrastructure in the Seoul region (Supabase & Google Cloud)."
        }
      ]
    },
    faq: {
      title: "Frequently asked questions",
      subtitle: "Answers to the questions people ask most about 1on1 and Orblit.",
      items: [
        {
          q: "What exactly is a 1on1? [m]Isn't it just casual chat over lunch?",
          a: "No. Casual chats over lunch or ad hoc conversations at work are not what we define as a 1on1. A 1on1 is dedicated time where a leader and a team member talk deeply, formally, and reciprocally about the work and the person."
        },
        {
          q: "Why do teams run 1on1?",
          a: "There are two major reasons. First, they align the organization, the team, and the individual around direction and goals. Second, they help people find meaning in their work, feel growth, and lower attrition risk before it becomes visible."
        },
        {
          q: "How is this different from the review meetings we already do?",
          a: "Traditional review meetings often happen on a long cycle and mainly communicate results. A 1on1 happens more regularly and focuses on mutual understanding, process management, and growth through two-way conversation."
        },
        {
          q: "What does this have to do with leadership capability?",
          a: "Leadership needs trust. A one-to-one conversation that protects and supports the member's voice is one of the strongest and most valid ways to build that trust, far beyond top-down instruction in front of a group."
        },
        {
          q: "Does AI take over the conversation and coaching too?",
          a: "No. The human leader remains the owner of the conversation and the coaching. AI acts as a strong support tool that cuts administrative and documentation overhead dramatically and helps the leader focus fully on leading."
        },
        {
          q: "We're busy. Can we still use it if weekly 1on1 are hard?",
          a: "Yes. Higher frequency works best, but even if you use it for longer-cycle review or evaluation meetings, you'll still get a much more objective and structured process than the old approach."
        }
      ]
    },
    cta: {
      title: "Leadership starts [m]inside the 1on1.",
      body: "Let Orblit AI handle the documentation [m]so your leaders can stay focused [m]on coaching and honest conversation.",
      placeholder: "Enter your work email",
      button: "Get the brochure",
      footnote: "Anyone can try the core features right after signing up.",
      footer: {
        companyName: "Reversemountain Inc.",
        address: "Seoul Startup Hub M-Plus, 14 Magokjungang 8-ro, Gangseo-gu, Seoul",
        representative: "CEO: Min Kim",
        email: "gmkim@reversemountain.co.kr",
        copyright: "2026 REVERSEMOUNTAIN ALL RIGHTS RESERVED.",
        links: [
          {
            label: "Terms of Service",
            href: "https://orblit.space/v3/service_terms_en.html"
          },
          {
            label: "Privacy Policy",
            href: "https://orblit.space/v4/privacy_policy_en.html"
          },
          {
            label: "Contact Sales",
            href: "https://featpaper.com/l/QJusPa7y"
          }
        ]
      }
    }
  },
  navbar: {
    service: "Product",
    whyOrblit: "Why Orblit",
    communication: "Communication",
    growth: "Growth",
    change: "Change Management",
    company: "ReversMountain",
    pricing: "Pricing",
    resources: "Resources",
    blog: "Blog",
    newsletter: "Newsletter",
    youtube: "YouTube",
    cta: "Get Started",
    toggleMenu: "Open or close menu"
  },
  company: {
    hero: {
      alt: "Company Culture Background",
      title: "So people can shine[m]inside the orbit of culture",
      subtitle: "Orbit Your Culture,[m] Light Your People"
    },
    missionVision: {
      missionLabel: "Our mission",
      missionBody: "Through healthy communication, we help people discover meaning in their work and stay deeply engaged,[m] while building a culture where the results created in that process are fully recognized.",
      visionLabel: "Our vision",
      visionBody: "We create a world where every individual discovers their unique potential[m] and connects to the opportunity where they can shine the most."
    },
    culture: {
      badge: "Our Culture",
      items: [
        {
          num: "01",
          title: "We respect capability[br]and insight, not rank.",
          lines: [
            "We trust present-day capability more than tenure or title.",
            "We believe leadership authority comes not from command,[m] but from exceptional insight people can align with[m] and from leading by example."
          ]
        },
        {
          num: "02",
          title: "We challenge hard,[br]then move as one.",
          lines: [
            "Honest opinions are essential to reach a better answer.[m] In the room, debate fiercely without reading the room first.",
            "But once a decision is made, we trust one another[m] and commit as if it had been our own idea from the start."
          ]
        },
        {
          num: "03",
          title: "We move past habit[br]and focus on essence.",
          lines: [
            "Instead of saying \u201Cthat is how it has always been done,\u201D we ask \u201Cwhy?\u201D",
            "Rather than copying others, we dig into the real problem[m] and find the solution that fits us best and works best."
          ]
        }
      ]
    },
    cta: {
      title: "People [m]who want to build [m]a better organization [m]with us",
      body: "Grow with Reversemountain [m]and help lead a new way of working.",
      button: "View careers page"
    }
  },
  pricing: {
    heroTitle: "Start without pressure,[br]choose a practical plan for your team",
    heroSubtitle: "From a free team trial to an unlimited business plan built for sustainable growth,[d]we suggest the solution your leaders actually need.",
    billing: {
      monthly: "Monthly billing",
      annual: "Annual billing",
      annualSavings: "Save 17% with annual billing."
    },
    desktop: {
      introTitle: "The right tool for successful 1on1s",
      introSubtitle: "Choose the plan that will upgrade your team's meeting culture.",
      ctaText: "Sign up now and experience a 1on1 meeting culture designed for your team."
    },
    plans: {
      basic: {
        badge: "Try 1on1",
        title: "Basic Plan",
        price: "Free",
        note: "No credit card required. Start right after signup.",
        cta: "Start for free",
        highlights: {
          aiTitle: "Trial access to the AI 1on1 cycle",
          aiDesc: "Includes 3 free recording and analysis sessions at signup, plus 2 more each time you invite a member.",
          commonTitle: "All core 1on1 features included",
          commonDesc: "Question templates, dashboards, 9-Box member management, and shared notes are included.",
          note: "* AI features are limited after trial usage runs out. Core 1on1 features remain available."
        }
      },
      business: {
        badge: "Recommended",
        title: "Business Plan",
        annualPrice: "80,000",
        monthlyPrice: "8,000",
        annualSuffix: "KRW / member \xB7 year",
        monthlySuffix: "KRW / member \xB7 month",
        note: "Billed per workspace",
        cta: "Start trial",
        recommended: "RECOMMENDED",
        highlights: {
          aiTitle: "Unlimited AI 1on1 cycle",
          aiDesc: "Unlimited access to suggested questions, STT recording, real-time summaries, leadership coaching feedback, and more.",
          commonTitle: "All core 1on1 features included",
          commonDesc: "Question templates, dashboards, 9-Box member management, and shared notes are included."
        }
      }
    },
    sections: {
      ai: "AI intelligence features that maximize meeting efficiency and impact",
      common: "Core features for scheduling, running, and managing 1on1s"
    },
    trial: {
      badge: "Trial included",
      lines: [
        "3 sessions included at signup",
        "+2 sessions for each member invite"
      ],
      note: "* AI features are limited after trial usage runs out. Core 1on1 features remain available."
    },
    businessAi: {
      badge: "Unlimited"
    },
    comparison: {
      title: "Detailed feature comparison",
      featureColumn: "Features",
      basicColumn: "Basic",
      businessColumn: "Business",
      trialValue: "Trial",
      unlimitedValue: "Unlimited"
    },
    aiFeatures: [
      {
        name: "Meeting-prep chatbot",
        desc: "AI that helps members prepare for meetings through chat.",
        phase: "Before",
        phaseKey: "before",
        role: "Member"
      },
      {
        name: "Prep-based question suggestions",
        desc: "Tailored questions based on member prep notes and 1on1 history.",
        phase: "Before",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "Member-specific guide",
        desc: "Suggested conversation direction based on prep details and member traits.",
        phase: "Before",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "AI custom template generation",
        desc: "Custom question sets optimized for each person's traits and current context.",
        phase: "Before",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "Conversation script extraction (STT)",
        desc: "People focus on the conversation while AI organizes the discussion.",
        phase: "During",
        phaseKey: "during"
      },
      {
        name: "Key Q&A organization",
        desc: "Automatically matches questions and answers, and captures new questions without missing them.",
        phase: "During",
        phaseKey: "during"
      },
      {
        name: "Detailed summaries and action items",
        desc: "Quickly produces detailed meeting summaries and action items for both sides.",
        phase: "During",
        phaseKey: "during"
      },
      {
        name: "Leader-specific coaching",
        desc: "Analyzes speech patterns to surface strengths and improvement areas in the conversation.",
        phase: "During",
        phaseKey: "during",
        role: "Leader"
      },
      {
        name: "Member report",
        desc: "Synthesizes meetings to suggest attrition risk, management approaches, and more.",
        phase: "After",
        phaseKey: "after",
        role: "Leader"
      }
    ],
    commonFeatures: [
      {
        name: "Meeting cadence management",
        desc: "Cadence settings plus email and push reminders for recurring meetings.",
        icon: "calendar"
      },
      {
        name: "Basic 1on1 question templates",
        desc: "Conversation frameworks for smoother communication across different situations.",
        icon: "compass",
        role: "Leader"
      },
      {
        name: "Shared meeting notes",
        desc: "A collaborative meeting note space that leaders and members can edit together.",
        icon: "pencil"
      },
      {
        name: "Post-meeting member state tracking",
        desc: "A lightweight way to keep tracking member engagement and satisfaction.",
        icon: "check",
        role: "Leader"
      },
      {
        name: "Meeting status dashboard",
        desc: "Monitor 1on1 progress and member state across the organization.",
        icon: "chart",
        role: "Leader"
      },
      {
        name: "9-Box member management",
        desc: "A matrix view for performance-potential and attrition-risk impact.",
        icon: "grid",
        role: "Leader"
      }
    ]
  },
  why1on1: {
    cardCta: "Learn more",
    heroTitle: "Leadership is not built[m]in the meeting room.[br]It is built in 1on1.",
    heroSubtitle: "Orblit designs communication, growth, and change through 1on1.",
    statsTitle: "The force of 1on1, seen in data",
    stats: [
      {
        src: "Gallup",
        stat: "70%",
        color: "#2563eb",
        preLines: ["Of the difference in team engagement"],
        post: " comes from leadership"
      },
      {
        src: "Andy Grove",
        stat: "80 hours",
        color: "#9333ea",
        preLines: ["A leader\u2019s 90-minute 1on1 shapes", "the next"],
        post: " of a team member"
      },
      {
        src: "Adobe",
        stat: "30%",
        color: "#059669",
        preLines: [
          "After adopting regular 1on1,",
          "voluntary attrition"
        ],
        post: " fell"
      }
    ],
    areas: {
      title: "A strong 1on1 is not just a check-in",
      subtitle: "Inside it, three kinds of leadership work happen at the same time.",
      cards: [
        {
          to: "/why/communication",
          title: "Communication",
          desc: "Leaders know what to say, and members feel safe enough to bring out what they truly think."
        },
        {
          to: "/why/growth",
          title: "Growth",
          desc: "Promises made in conversation do not disappear. They connect to performance, evaluation, and development."
        },
        {
          to: "/why/change",
          title: "Change",
          desc: "Strategy from the top is carried to the front line through leaders and made visible in execution."
        }
      ]
    },
    approach: {
      eyebrow: "Our Approach",
      title: "This is not automation.[m]It is leadership reinforcement.",
      commonTitle: "Typical approaches",
      commonItems: [
        "A note-taking tool that only transcribes the conversation",
        "A checklist app for manager confirmation",
        "A generic note tool with no real analysis"
      ],
      orblitBadge: "Orblit",
      orblitTitle: "The Orblit approach",
      orblitItems: [
        "Stronger leaders through prepared questions and feedback",
        "Visibility into organizational health through data",
        "A system for continuous performance management and attrition defense"
      ]
    }
  }
};

// src/locales/en-US/feature_tutorial.json
var feature_tutorial_default = {
  actions: {
    replay: "View tutorial",
    mediaUnavailable: "Tutorial media is not available."
  },
  recording: {
    dashboard: {
      fixture: {
        users: {
          owner: "Ava Mitchell",
          leaderOne: "Noah Parker",
          leaderTwo: "Emma Collins",
          memberOne: "Liam Bennett",
          memberTwo: "Sophia Turner",
          memberThree: "Ethan Carter",
          memberFour: "Lucas Gray",
          memberFive: "Olivia Reed"
        },
        meetings: {
          q1Priorities: "Q1 priorities and onboarding",
          executionReview: "Execution review",
          growthPlan: "Growth plan",
          workload: "Workload alignment",
          priorityReset: "Priority reset",
          roadmap: "Platform roadmap",
          handoff: "Project handoff",
          rituals: "Team rituals",
          reliability: "Reliability review",
          career: "Career direction",
          coaching: "Manager coaching",
          leadership: "Leadership sync"
        }
      }
    },
    member_report: {
      fixture: {
        reports: {
          primary: {
            recommendations: [
              "Growth assignment design: Offer a small database optimization project where they can analyze query bottlenecks in a high-traffic scenario and verify the impact of the fix.",
              "Protected review time: During release weeks, block code review time separately from implementation work so quality feedback does not slip.",
              "Operational exposure: Include them in incident drills or rollback rehearsals so they can broaden their reliability experience.",
              "Make impact visible: Call out CI improvements and shorter deployment time in team retrospectives so technical contribution is tied to team outcomes."
            ]
          },
          collaboration: {
            recommendations: [
              "Early alignment rhythm: Add a short kickoff with adjacent teams to agree on purpose, ownership, and done criteria before work starts.",
              "Priority-change context: When priorities change, share the background, expected outcome, and what should be deprioritized this week to reduce rework.",
              "Clear collaboration touchpoints: Name the decision owner and async channel in advance so questions do not scatter."
            ]
          }
        },
        summary: {
          strengths: ["Ownership", "System thinking"],
          weaknesses: ["Review bandwidth", "Cross-team alignment"],
          growthGoals: [
            "Large-scale data processing",
            "Database operations"
          ],
          growthSkills: ["Query optimization", "Incident response"],
          workContents: [
            "Platform reliability",
            "Payment module stabilization"
          ],
          workBlockers: [
            "Tight release schedule",
            "Limited reviewer pool"
          ],
          workContribution: [
            "Reduced deployment time",
            "Improved CI pipeline"
          ],
          motivationFactors: ["Technical challenge", "Autonomy"],
          demotivationFactors: [
            "Context switching",
            "Unclear priority changes"
          ],
          personalEmotion: ["Stable", "Positive"],
          personalNetwork: ["Values peer collaboration"],
          personalInterest: ["Travel", "Learning communities"],
          growthInterestedProjects: ["High traffic data pipeline"],
          flightRiskLevel: "Low",
          flightRiskSummary: "Engagement stays stable when technical growth opportunities are visible."
        },
        analysis: {
          motivation: {
            factors: [
              "Technical difficulty: They are more engaged by finding performance bottlenecks and improving system reliability than by routine feature work. Motivation rises when they can trace data flow, explain the cause, and confirm the improvement with metrics.",
              "Autonomy and trust: They respond well when they can identify an improvement opportunity, propose the approach, and own the execution. Clear goals paired with decision authority help them move at a steadier pace.",
              "Visible impact: Outcomes such as shorter CI time or a clearer rollback path strengthen motivation because the benefit is visible to the whole team."
            ],
            demotivationFactors: [
              "Frequent context switching: Near the end of a release, payment stabilization, reviews, and operational questions can arrive at the same time, breaking focus and increasing fatigue quickly.",
              "Unclear priority changes: When requests from adjacent teams arrive without enough context or done criteria, it becomes harder to judge the right quality bar.",
              "Review bottlenecks: When the reviewer pool is limited and feedback is delayed, they need to rebuild context for completed work and later tasks are also delayed."
            ]
          },
          strengthWeakness: {
            strengths: [
              "Ownership: They carry reliability work from design and implementation through rollout monitoring, and they keep checking whether the outcome actually holds.",
              "System thinking: They consider how each change affects deployment pipelines, incident response, and long-term maintenance cost.",
              "Documentation and sharing: They organize rollback paths and release checkpoints so the team can respond from the same playbook.",
              "Learning transfer: Even with unfamiliar database operations issues, they narrow the problem with metrics and logs and turn the learning into guidance for later work."
            ],
            weaknesses: [
              "Review bandwidth management: Because they go deep on their own work, peer code review can be pushed back unless review windows are protected early.",
              "Early collaboration alignment: Work with many adjacent-team dependencies would benefit from agreeing on purpose and done criteria earlier.",
              "Delegating operational issues: They tend to solve urgent questions directly, so repeatable response steps should be handed off to the team more actively."
            ]
          },
          growth: {
            goals: "They want to move beyond stable implementation work and directly handle how data storage and retrieval structures create bottlenecks under high traffic. Their growth goal is not just fixing a query, but gaining end-to-end experience from metric definition and root-cause analysis to before-and-after validation.",
            skills: "They want to build skills where development and operations meet, including database index design, execution-plan analysis, pre-release load validation, and communication during incidents. In user-impacting areas such as payment and platform reliability, they need practice making the reasoning behind decisions explicit.",
            interestedProjects: "They are interested in projects with measurable outcomes and team-level impact, such as data pipeline observability, a slow-query tracking dashboard, or rollback rehearsal automation."
          },
          workPerformance: {
            contents: "Scope of work: They focused on reducing risk before and after releases across platform reliability and payment module stabilization. Their work covered CI pipeline improvements, rollback-path documentation, data-processing bottleneck checks, and operational response guidelines.",
            contribution: [
              "Deployment efficiency: They removed repeated CI pipeline steps, reduced deployment waiting time, and clarified release checkpoints.",
              "Operational reliability: They documented the payment module rollback path and incident-check sequence so the team can respond from the same view when issues occur.",
              "Data bottleneck visibility: They reproduced slow retrieval flows and organized metrics that helped the team discuss database optimization priorities.",
              "Collaboration quality: In meetings with adjacent teams, they separated technical constraints from schedule impact and provided information needed for decisions."
            ],
            blockers: [
              "Release schedule pressure: Short stabilization windows make reviews and retrospectives easy to push back, and improvement ideas can slip into the next cycle.",
              "Reviewer shortage: A limited set of reviewers understands this area, which lengthens feedback wait time and increases the cost of sharing context for complex changes.",
              "Cross-team dependency: Payment and platform work depend on adjacent-team timelines and data definitions, so late early alignment can lead to repeated scope adjustments."
            ]
          },
          personal: {
            emotionWellbeing: "Their overall emotional state is stable and their attitude toward work is positive. During release weeks, however, urgent questions and reviews overlap, creating fatigue signals, so recovery time after important stabilization work would help sustain focus.",
            network: "They prefer technical discussion with peers and become more active in asking for help or offering opinions when context is shared early. In platform and payment work involving several teams, they tend to use their own written summaries as the basis for collaboration.",
            interest: "They enjoy travel and engineering learning communities, and they often connect energy from outside work back to learning and problem solving. Recently their interests have leaned toward operational reliability, data processing, and developer productivity."
          },
          nextTopics: [
            "Database optimization project: agree on the bottleneck-analysis scope and success metrics they can own this quarter.",
            "Review bandwidth: decide protected review windows and reviewer distribution before the next release.",
            "Operational response: list recurring payment or platform questions that can be documented or automated.",
            "Growth feedback: give concrete feedback on how CI and stabilization work affected team outcomes."
          ],
          flightRiskDescription: "Current flight risk is low. They are working on technically meaningful problems, and their improvements are visibly connected to faster deployments and better reliability, which keeps engagement stable. However, repeated release pressure, review bottlenecks, and unclear cross-team priorities could accumulate fatigue, so it is important to design both a growth assignment and protected review time."
        },
        sidePanel: {
          actionItems: {
            shareRollbackPlan: "Share the release rollback plan",
            prepareDbOptimizationScope: "Draft the database optimization scope",
            alignReviewWindow: "Align protected review windows before release"
          }
        },
        createDialog: {
          meetings: {
            q2Okr: "Q2 OKR mid-check",
            paymentRetro: "Payment module stabilization retrospective",
            growthPlan: "Growth plan alignment"
          }
        }
      }
    }
  },
  features: {
    dashboard: {
      title: "See your org's 1on1 status at a glance.",
      description: "Check overall completion rates and individual detailed status in the dashboard.",
      steps: {
        overview: {
          title: "See your org's 1on1 status at a glance",
          description: "Check completion rate, average meeting cycle, completed meetings, and vital scores in one place."
        },
        table: {
          title: "Review status by manager and member",
          description: "Use the table to compare manager and member status, then open a detail page from a name."
        },
        detail: {
          title: "Dive deeper on detail pages",
          description: "Review 1on1 status as a manager and as a member, vital trends, and individual meeting summaries."
        }
      }
    },
    member_report: {
      title: "Create reports about members from 1on1 data",
      description: "AI reads 1on1 conversations and organizes a tailored report covering how to support each member, motivation, strengths, and contributions.",
      steps: {
        create: {
          title: "Create a new report",
          description: "Create a report to better understand a member based on past 1on1s."
        },
        select: {
          title: "Choose period and meetings",
          description: "Select the 1on1 meetings to use as report material, then click create report."
        },
        insights: {
          title: "Review AI insights",
          description: "Review comprehensive insights, then continue to next 1on1 topics and action items."
        }
      }
    },
    ninebox: {
      title: "Manage talent with 9Box",
      description: "Move members directly to manage your organization's talent distribution and risk groups.",
      steps: {
        performance: {
          title: "Place by performance and potential",
          description: "Classify talent by performance and potential to see key people in your organization."
        },
        switch: {
          title: "Switch matrices",
          description: "Use the top tabs to alternate between the two talent management views."
        },
        flightRisk: {
          title: "Place on the flight-risk matrix",
          description: "Identify members who need attention based on the impact of a possible departure."
        }
      }
    }
  },
  update_nudge: {
    title: "Feedback for you, after every meeting",
    description: "When a recorded 1on1 is analyzed, members now get feedback for their next meeting too.",
    close: "Close",
    steps: {
      step1: {
        title: "Feedback for your next meeting",
        description: "Things you prepared for this meeting but didn't bring up, topics to raise next time, and more \u2014 organized as cards in the Analysis tab. Only you can see this feedback."
      }
    }
  }
};

// src/locales/en-US/index.ts
var en_US_default = {
  common: common_default,
  task: task_default,
  project: project_default,
  team: team_default,
  todo: todo_default,
  report: report_default,
  auth: auth_default,
  comment: comment_default,
  note: note_default,
  activity: activity_default,
  workspace: workspace_default,
  planner: planner_default,
  member_invitation: member_invitation_default,
  landing: landing_default,
  ai: ai_default,
  notification: notification_default,
  dailyscrum: dailyscrum_default,
  feedback: feedback_default,
  drive: drive_default,
  oneonone: oneonone_default,
  ninebox: ninebox_default,
  template: template_default,
  payment: payment_default,
  aws_marketplace: aws_marketplace_default,
  dashboard: dashboard_default,
  profile_card: profile_card_default,
  meeting_prep: meeting_prep_default,
  oneonone_notification: oneonone_notification_default,
  landing_new: landing_new_default,
  feature_tutorial: feature_tutorial_default
};

// src/locales/ko-KR/common.json
var common_default2 = {
  metadata: {
    title: "\uC624\uBE14\uB9BF | \uC6D0\uC628\uC6D0\uC73C\uB85C AI \uB9AC\uB354\uC2ED \uCF54\uCE6D\uBD80\uD130 \uC870\uC9C1 \uBB38\uD654 \uC9C4\uB2E8\uAE4C\uC9C0",
    description: "\uC624\uBE14\uB9BF\uACFC \uD568\uAED8 \uB9AC\uB354\uC2ED\uC744 \uD5A5\uC0C1\uC2DC\uD0A4\uACE0, \uD300\uC744 \uC2A4\uB9C8\uD2B8\uD558\uAC8C \uAD00\uB9AC\uD558\uC138\uC694. \uC6D0\uC628\uC6D0 \uB370\uC774\uD130\uB97C \uD1B5\uD574 \uC870\uC9C1 \uC778\uC0AC\uC774\uD2B8\uB97C \uB3C4\uCD9C\uD558\uACE0 \uD575\uC2EC \uC778\uC7AC\uC758 \uC774\uC9C1\uC744 \uBC29\uC9C0\uD574\uC694.",
    keywords: "\uC624\uBE14\uB9BF, \uC6D0\uC628\uC6D0 \uBBF8\uD305, 1on1, \uB9AC\uB354\uC2ED, \uCF54\uCE6D, \uD53C\uB4DC\uBC31, \uBA58\uD1A0\uB9C1, \uBBF8\uD305\uB178\uD2B8, \uC778\uC7AC \uAD00\uB9AC, \uBCC0\uD654 \uAD00\uB9AC, \uC9C1\uC6D0 \uC131\uC7A5, \uC6CC\uD06C\uC20D",
    app_description: "\uAC00\uC7A5 \uD6A8\uC728\uC801\uC774\uACE0 \uC758\uBBF8\uC788\uB294 \uC6D0\uC628\uC6D0 \uBBF8\uD305"
  },
  action: {
    select: "\uC120\uD0DD",
    create: "\uC0DD\uC131",
    delete: "\uC0AD\uC81C",
    edit: "\uC218\uC815",
    cancel: "\uCDE8\uC18C",
    save: "\uC800\uC7A5",
    complete: "\uC644\uB8CC",
    reject: "\uAC70\uC808",
    created: "\uC0DD\uC131\uB428",
    deleted: "\uC0AD\uC81C\uB428",
    edited: "\uC218\uC815\uB428",
    rejected: "\uAC70\uC808\uB428",
    back: "\uC774\uC804",
    next: "\uB2E4\uC74C",
    enter: "\uC785\uB825",
    collapse: "\uC811\uAE30",
    expand: "\uD3BC\uCE58\uAE30",
    select_all: "\uC804\uCCB4 \uC120\uD0DD",
    unselect_all: "\uC120\uD0DD \uD574\uC81C",
    skip: "\uAC74\uB108 \uB6F0\uAE30"
  },
  button: {
    yes: "\uB124",
    no: "\uC544\uB2C8\uC624",
    save: "\uC800\uC7A5",
    cancel: "\uCDE8\uC18C",
    confirm: "\uD655\uC778",
    close: "\uB2EB\uAE30",
    send: "\uBCF4\uB0B4\uAE30"
  },
  notification: {
    message: "\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694...",
    loading: "\uB85C\uB529 \uC911..."
  },
  error: {
    page: {
      title: "\uD398\uC774\uC9C0\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      description: "\uC0AD\uC81C\uB418\uC5C8\uAC70\uB098 \uC874\uC7AC\uD558\uC9C0 \uC54A\uB294 \uD398\uC774\uC9C0\uC785\uB2C8\uB2E4.",
      back_button: "\uC774\uC804 \uD398\uC774\uC9C0\uB85C \uC774\uB3D9\uD558\uAE30",
      home_button: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
      error_code: "\uC5D0\uB7EC \uCF54\uB4DC"
    },
    message: "\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911\uC5D0 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
    unknown_error: "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
    refresh: "\uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68\uC744 \uD574\uC8FC\uC138\uC694.",
    not_allowed: {
      task: "\uD574\uB2F9 \uD0DC\uC2A4\uD06C\uC5D0 \uC811\uADFC \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      project: "\uD574\uB2F9 \uD504\uB85C\uC81D\uD2B8\uC5D0 \uC811\uADFC \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      note: "\uD574\uB2F9 \uB178\uD2B8\uC5D0 \uC811\uADFC \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    not_found: {
      task: "\uD0DC\uC2A4\uD06C\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      project: "\uD504\uB85C\uC81D\uD2B8\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      note: "\uB178\uD2B8\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    module: {
      loading: "\uBAA8\uB4C8\uC744 \uB2E4\uC2DC \uB85C\uB4DC\uD558\uB294 \uC911\uC785\uB2C8\uB2E4...",
      refresh: "\uC774 \uD654\uBA74\uC774 \uACC4\uC18D\uB418\uBA74 \uC0C8\uB85C\uACE0\uCE68\uC744 \uD574\uC8FC\uC138\uC694."
    },
    data_fetch: {
      title: "\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
      message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.\n\uBB38\uC81C\uAC00 \uACC4\uC18D\uB418\uBA74 \uACE0\uAC1D\uC13C\uD130\uC5D0 \uBB38\uC758\uD574\uC8FC\uC138\uC694.",
      retry_button: "\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAE30"
    },
    toast: {
      query_failed: "\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4",
      mutation_failed: "\uC694\uCCAD\uC744 \uCC98\uB9AC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4",
      try_again: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694"
    }
  },
  filter: {
    title: "\uD544\uD130",
    reset: "\uD544\uD130 \uCD08\uAE30\uD654",
    assignee: "\uD560 \uC77C \uB2F4\uB2F9\uC790",
    period: {
      title: "\uAE30\uAC04",
      all: "\uC804\uCCB4 \uAE30\uAC04",
      today: "\uC624\uB298",
      yesterday: "\uCD5C\uADFC 1\uC77C",
      week: "\uCD5C\uADFC 7\uC77C",
      two_week: "\uCD5C\uADFC 14\uC77C",
      month: "\uCD5C\uADFC \uD55C\uB2EC",
      hide_all: "\uBAA8\uB450 \uAC00\uB9AC\uAE30"
    },
    not_completed: "\uC644\uB8CC\uD558\uC9C0 \uC54A\uC740 \uD560 \uC77C"
  },
  placeholder: {
    task: "\uC0C8 \uD0DC\uC2A4\uD06C \uCD94\uAC00",
    description: "\uC124\uBA85\uC744 \uCD94\uAC00\uD574\uBCF4\uC138\uC694...",
    status: "\uC0C1\uD0DC \uC5C6\uC74C",
    priority: "\uC6B0\uC120\uC21C\uC704 \uC5C6\uC74C"
  },
  setting: {
    profile: "\uD504\uB85C\uD544 \uC124\uC815",
    orblit_lab: {
      title: "\uC624\uBE14\uB9BF lab",
      description: "\uCD9C\uC2DC \uC900\uBE44 \uC911\uC778 \uC0C8\uB85C\uC6B4 \uAE30\uB2A5\uC5D0 \uB300\uD55C \uC124\uC815\uC785\uB2C8\uB2E4."
    },
    view: {
      title: "\uBCF4\uAE30",
      setting_title: "\uBCF4\uAE30 \uC124\uC815",
      option: {
        grouping: "\uADF8\uB8F9\uD551",
        grouping_placeholder: "\uADF8\uB8F9\uD551 \uC5C6\uC74C",
        order: "\uC21C\uC11C",
        done_task: "\uC644\uB8CC\uD55C \uD0DC\uC2A4\uD06C",
        display: "\uD45C\uC2DC \uC18D\uC131"
      },
      reset: "\uC124\uC815 \uCD08\uAE30\uD654"
    }
  },
  logout: "\uB85C\uADF8\uC544\uC6C3",
  feature: {
    planner: "\uD50C\uB798\uB108",
    drive: "\uB4DC\uB77C\uC774\uBE0C"
  },
  date_picker: {
    today: "\uC624\uB298",
    tomorrow: "\uB0B4\uC77C",
    yesterday: "\uC5B4\uC81C",
    selected_date: "\uC120\uD0DD\uB41C \uB0A0\uC9DC",
    until_yesterday: "\uC5B4\uC81C\uAE4C\uC9C0",
    until_today: "\uC624\uB298\uAE4C\uC9C0",
    until_tomorrow: "\uB0B4\uC77C\uAE4C\uC9C0"
  },
  time: {
    hour: "\uC2DC\uAC04",
    minute: "\uBD84"
  },
  field: {
    category: {
      title: "\uCE74\uD14C\uACE0\uB9AC",
      work: "\uC5C5\uBB34",
      meeting: "\uD68C\uC758",
      communication: "\uCEE4\uBBA4\uB2C8\uCF00\uC774\uC158",
      study: "\uC2A4\uD130\uB514",
      rest: "\uD734\uC2DD",
      etc: "\uAE30\uD0C0"
    },
    status: {
      title: "\uC0C1\uD0DC",
      backlog: "\uBC31\uB85C\uADF8",
      planned: "\uACC4\uD68D\uB428",
      in_progress: "\uC9C4\uD589\uC911",
      at_risk: "\uBB38\uC81C \uBC1C\uC0DD",
      paused: "\uC77C\uC2DC\uC911\uC9C0",
      completed: "\uC644\uB8CC",
      canceled: "\uCDE8\uC18C"
    },
    priority: {
      title: "\uC6B0\uC120\uC21C\uC704",
      highest: "\uAC00\uC7A5 \uB192\uC74C",
      high: "\uB192\uC74C",
      medium: "\uBCF4\uD1B5",
      low: "\uB0AE\uC74C",
      lowest: "\uAC00\uC7A5 \uB0AE\uC74C"
    },
    due_date: "\uB9C8\uAC10\uAE30\uD55C",
    register_date: "\uB4F1\uB85D\uC77C"
  },
  emoji_reaction: {
    add: "\uBC18\uC751 \uCD94\uAC00"
  },
  deletedUser: "\uD0C8\uD1F4\uD55C \uC0AC\uC6A9\uC790",
  team_selector: {
    all: "\uC804\uCCB4",
    none: "\uC0C1\uC704 \uD300 \uC5C6\uC74C",
    include_sub_teams: "\uD558\uC704 \uD300 \uD3EC\uD568"
  }
};

// src/locales/ko-KR/task.json
var task_default2 = {
  title: "\uD0DC\uC2A4\uD06C",
  hidden_task: "\uC228\uAE34 \uD0DC\uC2A4\uD06C",
  hidden_task_management: "\uC228\uAE34 \uD0DC\uC2A4\uD06C \uAD00\uB9AC",
  hidden_task_management_description: "\uC228\uACA8\uB454 \uD0DC\uC2A4\uD06C\uB97C \uC778\uBC15\uC2A4\uB85C \uB2E4\uC2DC \uBD88\uB7EC\uC62C \uC218 \uC788\uC2B5\uB2C8\uB2E4",
  no_hidden_task: "\uC228\uAE34 \uD0DC\uC2A4\uD06C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
  reveal: {
    title: "\uC228\uAE40 \uD574\uC81C",
    processing: {
      title: "\uC228\uAE40 \uD574\uC81C \uC911"
    },
    success: {
      message: "\uC228\uAE40 \uD574\uC81C \uCC98\uB9AC\uAC00 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
    },
    error: {
      title: "\uC228\uAE40 \uD574\uC81C \uC2E4\uD328",
      message: "\uC228\uAE40 \uD574\uC81C \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  hide: {
    title: "\uD0DC\uC2A4\uD06C \uC228\uAE40",
    description: "\uD0DC\uC2A4\uD06C\uB97C \uC778\uBC15\uC2A4\uC5D0\uC11C \uC228\uAE38\uAE4C\uC694? \uD0DC\uC2A4\uD06C \uC544\uB798\uC758 \uD560 \uC77C\uB4E4\uB3C4 \uD568\uAED8 \uC228\uAE40 \uCC98\uB9AC\uB429\uB2C8\uB2E4. \uC228\uAE34 \uD0DC\uC2A4\uD06C\uC640 \uD560 \uC77C\uC740 \uC5B8\uC81C\uB4E0 \uB2E4\uC2DC \uBCF5\uAD6C\uD560 \uC218 \uC788\uC5B4\uC694.",
    processing: {
      title: "\uD0DC\uC2A4\uD06C \uC228\uAE40 \uCC98\uB9AC \uC911"
    },
    success: {
      title: "\uD0DC\uC2A4\uD06C \uC228\uAE40 \uCC98\uB9AC \uC644\uB8CC",
      message: "\uD0DC\uC2A4\uD06C\uC640 \uAD00\uB828 \uD560 \uC77C\uC758 \uC228\uAE40 \uCC98\uB9AC\uB97C \uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4."
    },
    error: {
      title: "\uD0DC\uC2A4\uD06C \uC228\uAE40 \uCC98\uB9AC \uC2E4\uD328",
      message: "\uD0DC\uC2A4\uD06C \uC228\uAE40 \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  selection: {
    none: "0\uAC1C \uC120\uD0DD",
    some_one: "\uD0DC\uC2A4\uD06C {{count}}\uAC1C \uC120\uD0DD",
    some_other: "\uD0DC\uC2A4\uD06C {{count}}\uAC1C \uC120\uD0DD"
  },
  indicator: {
    title: "\uC9C0\uD45C",
    type: "\uD0C0\uC785",
    placeholder: "\uC9C0\uD45C \uC5C6\uC74C",
    initial_value: "\uCD08\uAE30\uAC12",
    target_value: "\uBAA9\uD45C\uAC12",
    current_value: "\uD604\uC7AC \uAC12",
    dollar: "\uB2EC\uB7EC",
    number: "\uC22B\uC790",
    percent: "\uD37C\uC13C\uD2B8",
    won: "\uC6D0",
    setting: {
      title: "\uC9C0\uD45C \uC124\uC815",
      number_title: "\uC9C0\uD45C \uC218\uCE58 \uC124\uC815",
      reset: "\uC124\uC815 \uCD08\uAE30\uD654"
    },
    error: {
      title: "\uC9C0\uD45C \uBCC0\uACBD \uC2E4\uD328",
      message: "\uC9C0\uD45C \uBCC0\uACBD \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      message_not_valid: "\uBAA9\uD45C\uAC12\uC740 \uCD08\uAE30\uAC12\uBCF4\uB2E4 \uCEE4\uC57C \uD569\uB2C8\uB2E4."
    }
  },
  field: {
    title: "\uC81C\uBAA9",
    content: "\uB0B4\uC6A9",
    complete_date: "\uC644\uB8CC\uC77C",
    manager: "\uAD00\uB9AC\uC790",
    participants: "\uCC38\uC5EC\uC790",
    parent: "\uC0C1\uC704 \uD0DC\uC2A4\uD06C",
    indicator_type: "\uC9C0\uD45C \uC720\uD615",
    todo_label: "\uC644\uB8CC / \uC0DD\uC131",
    due_date: "\uB9C8\uAC10\uAE30\uD55C"
  },
  placeholder: {
    manager: "\uAD00\uB9AC\uC790 \uC5C6\uC74C"
  },
  delete: {
    title: "\uD0DC\uC2A4\uD06C \uC0AD\uC81C",
    description: "\uC815\uB9D0\uB85C \uC774 \uD0DC\uC2A4\uD06C\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    share_description: "\uC0AD\uC81C \uC2DC, \uACF5\uC720 \uC911\uC778 \uD300\uC5D0\uC11C\uB3C4 \uD574\uB2F9 \uD0DC\uC2A4\uD06C\uAC00 \uC0AD\uC81C\uB429\uB2C8\uB2E4.",
    success: {
      message: "\uD0DC\uC2A4\uD06C \uC0AD\uC81C \uCC98\uB9AC\uAC00 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
    },
    error: {
      title: "\uD0DC\uC2A4\uD06C \uC0AD\uC81C \uC2E4\uD328",
      message: "\uD0DC\uC2A4\uD06C \uC0AD\uC81C \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  related: {
    title: "\uAD00\uB828 \uD0DC\uC2A4\uD06C",
    placeholder: "\uAD00\uB828 \uD0DC\uC2A4\uD06C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
    show: "\uAD00\uB828 \uD0DC\uC2A4\uD06C \uBCF4\uAE30",
    hide: "\uAD00\uB828 \uD0DC\uC2A4\uD06C \uC228\uAE30\uAE30"
  },
  hierarchy: {
    title: "\uD0DC\uC2A4\uD06C \uC704\uACC4",
    show: "\uC704\uACC4 \uBCF4\uAE30"
  },
  subtask: {
    title: "\uD558\uC704 \uD0DC\uC2A4\uD06C",
    create: "\uD558\uC704 \uD0DC\uC2A4\uD06C \uB9CC\uB4E4\uAE30"
  },
  create: {
    placeholder: "\uC0C8 \uD0DC\uC2A4\uD06C \uCD94\uAC00",
    placeholder_label: "{{label}}\uC5D0 \uC0C8 \uD0DC\uC2A4\uD06C \uCD94\uAC00",
    grouping: "\uADF8\uB8F9\uD551 \uB0B4 \uD0DC\uC2A4\uD06C \uCD94\uAC00"
  },
  participate: {
    button: "\uCC38\uC5EC\uD558\uAE30",
    cancel: {
      title: "\uD0DC\uC2A4\uD06C \uCC38\uC5EC \uCDE8\uC18C",
      button: "\uCC38\uC5EC \uCDE8\uC18C",
      description1: "\uC774 \uD0DC\uC2A4\uD06C\uC5D0 \uC5F0\uACB0\uB41C \uD560 \uC77C\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
      description2: "\uCC38\uC5EC\uB97C \uCDE8\uC18C\uD558\uBA74 \uD574\uB2F9 \uD560 \uC77C\uB4E4\uC774 \uD0DC\uC2A4\uD06C\uC5D0\uC11C \uBD84\uB9AC\uB429\uB2C8\uB2E4. \uACC4\uC18D\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description3: "\uC774 \uD0DC\uC2A4\uD06C\uC5D0\uC11C \uCC38\uC5EC\uB97C \uCDE8\uC18C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
    }
  },
  share: {
    success: {
      title: "\uD0DC\uC2A4\uD06C \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD",
      message_share: "\uD0DC\uC2A4\uD06C\uB97C {{teamName}} \uD300\uC5D0 \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4.",
      message_unshare: "\uD0DC\uC2A4\uD06C\uB97C {{teamName}} \uD300\uC5D0\uC11C \uACF5\uC720 \uD574\uC81C\uD588\uC2B5\uB2C8\uB2E4."
    },
    error: {
      title: "\uD0DC\uC2A4\uD06C \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD \uC2E4\uD328",
      message: "\uD0DC\uC2A4\uD06C \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    subtask: {
      title: "\uD558\uC704 \uD0DC\uC2A4\uD06C \uACF5\uC720",
      message: "\uD558\uC704 \uD0DC\uC2A4\uD06C\uB4E4\uB3C4 \uAC19\uC774 \uACF5\uC720\uD560\uAE4C\uC694?",
      error: "\uD558\uC704 \uD0DC\uC2A4\uD06C \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  }
};

// src/locales/ko-KR/project.json
var project_default2 = {
  title: "\uD504\uB85C\uC81D\uD2B8",
  placeholder: "\uD504\uB85C\uC81D\uD2B8 \uC5C6\uC74C",
  share: {
    title: "\uD504\uB85C\uC81D\uD2B8 \uACF5\uC720",
    message: "\uD504\uB85C\uC81D\uD2B8\uB3C4 \uAC19\uC774 \uACF5\uC720\uD560\uAE4C\uC694?",
    error: {
      title: "\uD504\uB85C\uC81D\uD2B8 \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD \uC2E4\uD328",
      message: "\uD504\uB85C\uC81D\uD2B8 \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    success: {
      title: "\uD504\uB85C\uC81D\uD2B8 \uACF5\uC720 \uC124\uC815 \uBCC0\uACBD",
      message_share: "\uD504\uB85C\uC81D\uD2B8\uB97C {{teamName}} \uD300\uC5D0 \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4.",
      message_unshare: "\uD504\uB85C\uC81D\uD2B8\uB97C {{teamName}} \uD300\uC5D0\uC11C \uACF5\uC720 \uD574\uC81C\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  detail: {
    title: "\uD504\uB85C\uC81D\uD2B8 \uC0C1\uC138",
    tabs: {
      overview: "\uD504\uB85C\uC81D\uD2B8 \uAC1C\uC694",
      tasks: "\uAD00\uB828 \uD0DC\uC2A4\uD06C"
    },
    delete: {
      button: "\uD504\uB85C\uC81D\uD2B8 \uC0AD\uC81C",
      title: "\uD504\uB85C\uC81D\uD2B8 \uC0AD\uC81C",
      confirmation: "\uC815\uB9D0\uB85C \uC774 \uD504\uB85C\uC81D\uD2B8\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      cancel: "\uCDE8\uC18C",
      confirm: "\uC0AD\uC81C",
      success: "\uD504\uB85C\uC81D\uD2B8\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      error: "\uD504\uB85C\uC81D\uD2B8 \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    info: {
      title: "\uD504\uB85C\uC81D\uD2B8 \uC815\uBCF4",
      team: "\uCC38\uC5EC \uD300",
      participants: "\uCC38\uC5EC\uC790",
      add_participants: "\uCC38\uC5EC\uC790 \uCD94\uAC00",
      participate: "\uCC38\uC5EC\uD558\uAE30",
      cancel_participate: "\uCC38\uC5EC \uCDE8\uC18C",
      cancel_participate_message: "\uC774 \uD504\uB85C\uC81D\uD2B8\uC758 \uD0DC\uC2A4\uD06C\uC5D0 \uC5F0\uACB0\uB41C \uD560 \uC77C\uC774 \uC788\uC2B5\uB2C8\uB2E4.\n\uCC38\uC5EC\uB97C \uCDE8\uC18C\uD558\uBA74 \uCC38\uC5EC \uC911\uC778 \uD0DC\uC2A4\uD06C\uC5D0\uC11C\uB3C4 \uCC38\uC5EC\uAC00 \uCDE8\uC18C\uB418\uBA70,\n\uC5F0\uACB0\uB41C \uD560 \uC77C\uB4E4\uC740 \uD504\uB85C\uC81D\uD2B8, \uD0DC\uC2A4\uD06C\uC5D0\uC11C \uBD84\uB9AC\uB429\uB2C8\uB2E4.\n\uACC4\uC18D\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
    },
    description: {
      placeholder: "\uC124\uBA85\uC744 \uCD94\uAC00\uD574\uBCF4\uC138\uC694..."
    },
    name: {
      edit_error: "\uD504\uB85C\uC81D\uD2B8 \uC774\uB984 \uC218\uC815 \uC624\uB958"
    }
  },
  table: {
    add: "\uC0C8 \uD504\uB85C\uC81D\uD2B8 \uCD94\uAC00",
    columns: {
      status: "\uC0C1\uD0DC",
      name: "\uD504\uB85C\uC81D\uD2B8\uBA85",
      deadline: "\uB9C8\uAC10\uAE30\uD55C",
      priority: "\uC6B0\uC120\uC21C\uC704",
      leader: "\uD504\uB85C\uC81D\uD2B8 \uCC45\uC784\uC790",
      register_date: "\uB4F1\uB85D\uC77C"
    }
  },
  relatedTasks: {
    title: "\uAD00\uB828 \uD0DC\uC2A4\uD06C"
  },
  filters: {
    status: "\uC0C1\uD0DC",
    priority: "\uC6B0\uC120\uC21C\uC704",
    leader: "\uD504\uB85C\uC81D\uD2B8 \uCC45\uC784\uC790",
    noName: "\uC774\uB984 \uC5C6\uC74C"
  },
  error: {
    no_access: "\uD574\uB2F9 \uD504\uB85C\uC81D\uD2B8\uC5D0 \uC811\uADFC \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    not_found: "\uD504\uB85C\uC81D\uD2B8\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    load_failed: "\uD504\uB85C\uC81D\uD2B8\uB97C \uBD88\uB7EC\uC624\uB294\uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
  },
  list: {
    select_team: "\uD300\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694."
  },
  create_modal: {
    title: "\uD504\uB85C\uC81D\uD2B8 \uC0DD\uC131",
    name: {
      label: "\uD504\uB85C\uC81D\uD2B8 \uC774\uB984",
      placeholder: "\uD504\uB85C\uC81D\uD2B8 \uC774\uB984\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694"
    },
    team: {
      label: "\uB2F4\uB2F9 \uD300\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      placeholder: "\uB2F4\uB2F9 \uD300 \uC120\uD0DD",
      self: " (\uB2F4\uB2F9 \uD300)"
    },
    participants: {
      label: "\uCC38\uC5EC\uC790\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      placeholder: "\uCC38\uC5EC\uC790 \uC120\uD0DD",
      self: " (\uB098)"
    },
    notification: {
      in_progress: "\uD504\uB85C\uC81D\uD2B8 \uCD94\uAC00 \uC911",
      success: {
        title: "\uD504\uB85C\uC81D\uD2B8 \uCD94\uAC00 \uC644\uB8CC",
        message: "\uD504\uB85C\uC81D\uD2B8\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
      },
      error: {
        title: "\uD504\uB85C\uC81D\uD2B8 \uCD94\uAC00 \uC2E4\uD328",
        message: "\uD504\uB85C\uC81D\uD2B8 \uCD94\uAC00\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uD55C\uBC88 \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
      }
    },
    form: {
      validation: {
        name: {
          required: "\uD504\uB85C\uC81D\uD2B8 \uC774\uB984\uC740 \uD544\uC218\uC785\uB2C8\uB2E4."
        },
        team: {
          required: "\uD300\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694."
        }
      }
    }
  }
};

// src/locales/ko-KR/team.json
var team_default2 = {
  title: "\uD300",
  placeholder: "\uD300 \uC5C6\uC74C",
  team_required: "\uD300\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
  notification: {
    title: "\uD300 \uC120\uD0DD \uD544\uC694",
    message: "\uCD5C\uC18C \uD558\uB098\uC758 \uD300\uC740 \uC120\uD0DD\uB418\uC5B4 \uC788\uC5B4\uC57C \uD569\uB2C8\uB2E4."
  },
  selection: {
    some_one: "{{count}}\uAC1C \uD300",
    some_other: "{{count}}\uAC1C \uD300"
  },
  member: {
    title: "\uBA64\uBC84",
    add_dialog: {
      title: "\uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
      search_placeholder: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84 \uAC80\uC0C9 \uB610\uB294 \uC774\uBA54\uC77C \uC8FC\uC18C \uC785\uB825",
      workspace_members: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84",
      invite_button: "\uCD08\uB300\uD558\uAE30",
      invite_selected: "\uC120\uD0DD\uD55C \uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
      no_members: "\uCD08\uB300\uD560 \uC218 \uC788\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
      invite_by_email: "\uC774\uBA54\uC77C\uB85C \uC0C8 \uBA64\uBC84\uB97C \uCD08\uB300\uD574\uBCF4\uC138\uC694."
    },
    notifications: {
      invalid_email: "\uC720\uD6A8\uD558\uC9C0 \uC54A\uC740 \uC774\uBA54\uC77C",
      enter_valid_email: "\uC720\uD6A8\uD55C \uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      invite_not_possible: "\uCD08\uB300 \uBD88\uAC00",
      already_member: "{{email}} \uBA64\uBC84\uB85C \uC774\uBBF8 \uB4F1\uB85D\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.",
      creation_failed: "\uCD08\uB300\uC7A5 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
      invite_failed: "\uCD08\uB300 \uC2E4\uD328",
      email_error: "\uC774\uBA54\uC77C \uC804\uC1A1 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      invite_success: "\uCD08\uB300 \uC131\uACF5",
      email_sent: "{{email}} \uCD08\uB300 \uBA54\uC77C\uC774 \uC804\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      invite_error: "\uBA64\uBC84 \uCD08\uB300 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      selection_needed: "\uBA64\uBC84 \uC120\uD0DD \uD544\uC694",
      select_members: "\uD300\uC5D0 \uCD94\uAC00\uD560 \uBA64\uBC84\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
      add_success: "\uBA64\uBC84 \uCD94\uAC00 \uC131\uACF5",
      members_added: "{{count}}\uBA85\uC758 \uBA64\uBC84\uAC00 \uD300\uC5D0 \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      add_failed: "\uBA64\uBC84 \uCD94\uAC00 \uC2E4\uD328",
      add_error: "\uD300 \uBA64\uBC84 \uCD94\uAC00 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      request_plan: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD",
      request_plan_success: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD \uC54C\uB9BC\uC744 \uBCF4\uB0C8\uC2B5\uB2C8\uB2E4.",
      request_plan_failed: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD \uC54C\uB9BC \uBC1C\uC1A1 \uC2E4\uD328",
      request_plan_error: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD \uC54C\uB9BC \uBC1C\uC1A1 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    placeholders: {
      team: "\uD300",
      workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
      inviter: "\uCD08\uB300\uC790"
    },
    day_overview: {
      current_todo: "\uD604\uC7AC \uD560 \uC77C",
      no_tasks_on_date: "\uC120\uD0DD\uD55C \uB0A0\uC9DC\uC5D0 \uD560 \uC77C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    task_list: {
      member_tasks: "{{name}}\uB2D8\uC758 \uD0DC\uC2A4\uD06C",
      managing_tasks: "\uAD00\uB9AC\uD558\uB294 \uD0DC\uC2A4\uD06C",
      participating_tasks: "\uCC38\uC5EC\uD558\uB294 \uD0DC\uC2A4\uD06C"
    },
    task_view_settings: {
      view: "\uBCF4\uAE30",
      view_settings: "\uBCF4\uAE30 \uC124\uC815",
      completed_tasks: "\uC644\uB8CC\uD55C \uD0DC\uC2A4\uD06C",
      show_backlog_tasks: "\uBC31\uB85C\uADF8 \uD0DC\uC2A4\uD06C \uD45C\uC2DC",
      show_paused_tasks: "\uC77C\uC2DC\uC911\uC9C0 \uD0DC\uC2A4\uD06C \uD45C\uC2DC",
      display_properties: "\uD45C\uC2DC \uC18D\uC131",
      reset_settings: "\uC124\uC815 \uCD08\uAE30\uD654",
      column: {
        status: "\uC0C1\uD0DC",
        priority: "\uC6B0\uC120\uC21C\uC704",
        project: "\uD504\uB85C\uC81D\uD2B8",
        current_value: "\uC9C0\uD45C"
      }
    },
    todo_list: {
      member_todos: "{{name}}\uB2D8\uC758 \uD560 \uC77C",
      remaining_todos: "\uB0A8\uC740 \uD560 \uC77C",
      recently_completed_todos: "\uCD5C\uADFC \uC644\uB8CC\uD55C \uD560 \uC77C",
      tasks_without_task: "\uD0DC\uC2A4\uD06C\uAC00 \uC5C6\uB294 \uD560 \uC77C",
      tasks_by_task: "\uD0DC\uC2A4\uD06C\uBCC4\uB85C \uADF8\uB8F9\uD654\uB41C \uD560 \uC77C",
      load_more: "\uB354 \uBD88\uB7EC\uC624\uAE30",
      no_schedule: "\uC77C\uC815\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"
    },
    pages: {
      detail: {
        team_member: "\uBA64\uBC84",
        non_existent_team: "\uC874\uC7AC\uD558\uC9C0 \uC54A\uB294 \uD300\uC785\uB2C8\uB2E4.",
        non_existent_member: "\uC874\uC7AC\uD558\uC9C0 \uC54A\uB294 \uBA64\uBC84\uC785\uB2C8\uB2E4."
      },
      team: {
        team_members: "\uBA64\uBC84",
        member_count: "{{count}} \uBA85",
        no_todos_on_date: "\uC120\uD0DD\uD55C \uB0A0\uC9DC\uC5D0 \uACC4\uD68D\uB41C \uD560 \uC77C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
        todo_count: "{{count}}\uAC1C \uD560 \uC77C",
        request_plan: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD",
        request_plan_message: "<0>{{name}} \uB2D8</0><1>\uC5D0\uAC8C \uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD \uC54C\uB9BC\uC744 \uBCF4\uB0BC\uAE4C\uC694?</1>",
        daily_scrum: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC"
      }
    }
  },
  create_dialog: {
    title: "\uC0C8 \uD300 \uB9CC\uB4E4\uAE30",
    workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
    select_workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC120\uD0DD\uD558\uC138\uC694.",
    workspace_required: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    parent_team: "\uC0C1\uC704 \uD300",
    select_parent_team: "\uC0C1\uC704 \uD300\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
    team_name: "\uD300 \uC774\uB984",
    enter_team_name: "\uD300 \uC774\uB984\uC744 \uC785\uB825\uD558\uC138\uC694.",
    name_error: "\uD300 \uC774\uB984\uC740 2\uAE00\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4.",
    team_description: "\uD300 \uC124\uBA85",
    enter_team_description: "\uD300\uC5D0 \uB300\uD55C \uC124\uBA85\uC744 \uC785\uB825\uD558\uC138\uC694.",
    cancel: "\uCDE8\uC18C",
    create_team: "\uD300 \uC0DD\uC131\uD558\uAE30",
    success_title: "\uD300 \uC0DD\uC131 \uC644\uB8CC",
    success_message: "{{name}} \uD300\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    error_title: "\uD300 \uC0DD\uC131 \uC2E4\uD328",
    error_message: "\uD300\uC744 \uC0DD\uC131\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.\n{{error}}"
  },
  update_dialog: {
    title: "\uD300 \uC218\uC815\uD558\uAE30",
    save: "\uC800\uC7A5",
    success_title: "\uD300 \uC5C5\uB370\uC774\uD2B8 \uC644\uB8CC",
    success_message: "{{name}} \uD300 \uC5C5\uB370\uC774\uD2B8\uB97C \uC644\uB8CC\uD588\uC2B5\uB2C8\uB2E4.",
    error_title: "\uD300 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328",
    error_message: "\uD300 \uC815\uBCF4\uB97C \uC5C5\uB370\uC774\uD2B8\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.\n{{error}}"
  },
  delete_dialog: {
    title: "\uD300 \uC0AD\uC81C \uD655\uC778",
    message: "\uC815\uB9D0\uB85C \uD300\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    warning: "\uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC73C\uBA70, \uD300\uC758 \uBAA8\uB4E0 \uD504\uB85C\uC81D\uD2B8\uC640 \uD0DC\uC2A4\uD06C\uAC00 \uC601\uAD6C\uC801\uC73C\uB85C \uC0AD\uC81C\uB429\uB2C8\uB2E4.",
    hierarchy_guide: "\uD558\uC704 \uD300\uB4E4\uC740 \uC0AD\uC81C\uB418\uC9C0 \uC54A\uACE0 \uC0C1\uC704 \uD300\uACFC \uC790\uB3D9\uC73C\uB85C \uC5F0\uACB0\uB429\uB2C8\uB2E4.",
    confirm: "\uC0AD\uC81C",
    cancel: "\uCDE8\uC18C",
    error: "\uD300 \uC0AD\uC81C \uC2E4\uD328:",
    success: "\uD300 \uC0AD\uC81C \uC644\uB8CC",
    success_message: "\uD300\uC744 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uD588\uC2B5\uB2C8\uB2E4.",
    failed: "\uD300 \uC0AD\uC81C \uC2E4\uD328",
    failed_message: "\uD300\uC744 \uC0AD\uC81C\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
  },
  main_title: {
    clear_input: "\uC785\uB825 \uC9C0\uC6B0\uAE30"
  },
  item: {
    task: "\uD0DC\uC2A4\uD06C",
    project: "\uD504\uB85C\uC81D\uD2B8",
    member: "\uBA64\uBC84"
  },
  member_table: {
    cancel_invitation: "\uCD08\uB300 \uCDE8\uC18C",
    invited_status: "\uCD08\uB300\uB428",
    delete_member_failed: "\uD300 \uBA64\uBC84 \uC0AD\uC81C \uC2E4\uD328:",
    role_change_failed: "\uBA64\uBC84 \uC5ED\uD560 \uBCC0\uACBD \uC2E4\uD328:",
    add_member: "\uBA64\uBC84 \uCD94\uAC00",
    member_list: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84",
    add_member_count: "\uD300 \uBA64\uBC84\uB85C \uCD94\uAC00 ({{count}})",
    leave_team: "\uD300\uC5D0\uC11C \uB098\uAC00\uAE30",
    remove_from_team: "\uD300\uC5D0\uC11C \uC81C\uC678\uD558\uAE30",
    manage_members: "\uBA64\uBC84 \uAD00\uB9AC",
    invite_member: "\uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
    member: "\uBA64\uBC84",
    email: "\uC774\uBA54\uC77C",
    workspace_permission: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAD8C\uD55C",
    no_available_members: "\uD45C\uC2DC\uD560 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
    current_member_count: "\uD604\uC7AC \uBA64\uBC84 ({{count}})",
    delete_member_count: "\uD300\uC5D0\uC11C \uC81C\uC678 ({{count}})",
    placeholder: "\uC774\uB984\uC774\uB098 \uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694."
  },
  setting_page: {
    access_denied: "\uD300 \uBA64\uBC84\uB9CC \uC811\uADFC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    delete_team: "\uD300 \uC0AD\uC81C\uD558\uAE30",
    delete_description: "\uD300\uC744 \uC0AD\uC81C\uD574\uB3C4 \uBA64\uBC84\uB4E4\uC774 \uC0DD\uC131\uD55C \uAC1C\uC778 \uD560 \uC77C \uBC0F \uAE30\uB85D\uC740 \uC0AC\uB77C\uC9C0\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    delete_tooltip: "\uC815\uB9D0\uB85C \uC0AD\uC81C\uD558\uC2DC\uB098\uC694? \u{1F97A}"
  }
};

// src/locales/ko-KR/todo.json
var todo_default2 = {
  title: "\uD560 \uC77C",
  inbox: "\uC778\uBC15\uC2A4",
  private_todo_title: "\uBE44\uACF5\uAC1C \uC77C\uC815",
  now: {
    placeholder: "\uD604\uC7AC \uC77C\uC815 \uC5C6\uC74C"
  },
  status: {
    in_progress: "\uC9C4\uD589 \uC911",
    completed: "\uC644\uB8CC",
    cancelled: "\uADF8\uB9CC\uD558\uAE30"
  },
  notification: {
    cancel_action: "\uADF8\uB9CC\uD558\uAE30",
    resume_action: "\uC7AC\uAC1C",
    processing_title: "\uD560 \uC77C {{action}} \uCC98\uB9AC \uC911",
    processing_message: "\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694...",
    success_title: "\uD560 \uC77C {{action}} \uCC98\uB9AC \uC644\uB8CC",
    success_message: "\uC120\uD0DD\uD55C \uD560 \uC77C\uC744 {{action}} \uCC98\uB9AC\uD588\uC2B5\uB2C8\uB2E4.",
    error_title: "\uD560 \uC77C {{action}} \uCC98\uB9AC \uC2E4\uD328",
    error_message: "{{action}} \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4"
  },
  component: {
    inbox: {
      hidden: "\uC228\uAE40",
      hidden_goals: "\uC228\uAE34 \uD0DC\uC2A4\uD06C",
      hidden_goals_count: "\uC228\uAE34 \uD0DC\uC2A4\uD06C ({{count}})",
      view: "\uBCF4\uAE30"
    },
    todo_textfield: {
      adding: "\uCD94\uAC00\uD558\uB294 \uC911...",
      add_todo: "\uD560 \uC77C \uCD94\uAC00"
    },
    parent_goal_label: {
      hide: "\uC228\uAE30\uAE30",
      due_date: "\uB9C8\uAC10\uAE30\uD55C",
      add_todo: "\uD560 \uC77C \uCD94\uAC00"
    },
    set_todo_display_menu: {
      view_settings: "\uBCF4\uAE30 \uC124\uC815",
      show_goals: "\uD0DC\uC2A4\uD06C \uD45C\uC2DC\uD558\uAE30",
      show_empty_goals: "\uBE48 \uD0DC\uC2A4\uD06C \uD45C\uC2DC\uD558\uAE30",
      reset_settings: "\uC124\uC815 \uCD08\uAE30\uD654",
      set_as_default: "\uAE30\uBCF8\uAC12\uC73C\uB85C \uC124\uC815",
      notifications: {
        saving_title: "\uBCF4\uAE30 \uC124\uC815 \uC800\uC7A5 \uC911",
        saved_title: "\uBCF4\uAE30 \uC124\uC815 \uC800\uC7A5",
        saved_message: "\uBCF4\uAE30 \uC124\uC815\uC744 \uC800\uC7A5\uD588\uC2B5\uB2C8\uB2E4.",
        save_failed_title: "\uBCF4\uAE30 \uC124\uC815 \uC800\uC7A5 \uC2E4\uD328",
        save_failed_message: "\uBCF4\uAE30 \uC124\uC815 \uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
      }
    },
    todo_action_menu: {
      resume: "\uB2E4\uC2DC \uC2DC\uC791\uD558\uAE30",
      remove_plan: "\uACC4\uD68D \uCDE8\uC18C\uD558\uAE30",
      delete_todo: "\uC804\uCCB4 \uD560 \uC77C \uC0AD\uC81C",
      notifications: {
        removing_plan_title: "\uACC4\uD68D \uCDE8\uC18C \uC911",
        removed_plan_title: "\uACC4\uD68D \uCDE8\uC18C",
        removed_plan_message: "\uACC4\uD68D\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uCDE8\uC18C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        remove_plan_failed_title: "\uACC4\uD68D \uCDE8\uC18C \uC2E4\uD328",
        remove_plan_failed_message: "\uACC4\uD68D\uC744 \uCDE8\uC18C\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
        deleting_todo_title: "\uD560 \uC77C \uC0AD\uC81C \uC911",
        deleted_todo_title: "\uD560 \uC77C \uC0AD\uC81C",
        deleted_todo_message: "\uD560 \uC77C\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        delete_todo_failed_title: "\uD560 \uC77C \uC0AD\uC81C \uC2E4\uD328",
        delete_todo_failed_message: "\uD560 \uC77C\uC744 \uC0AD\uC81C\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
      }
    },
    todo_checkbox: {
      complete: "\uC644\uB8CC"
    },
    activity: "\uD65C\uB3D9",
    cancel_checkbox: {
      resume: "\uB2E4\uC2DC \uC2DC\uC791\uD558\uAE30",
      cancel: "\uADF8\uB9CC\uD558\uAE30",
      aria_resume: "\uD560 \uC77C \uB2E4\uC2DC \uC2DC\uC791\uD558\uAE30",
      aria_cancel: "\uD560 \uC77C \uADF8\uB9CC\uD558\uAE30"
    },
    confirm_modal: {
      actions: {
        cancel: "\uADF8\uB9CC\uD558\uAE30",
        remove_planner: "\uACC4\uD68D \uCDE8\uC18C",
        delete: "\uC804\uCCB4 \uC0AD\uC81C"
      },
      details: {
        cancel: "\uD560 \uC77C\uC744 \uB354 \uC774\uC0C1 \uC9C4\uD589\uD558\uC9C0 \uC54A\uC744 \uB54C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.\n\uC120\uD0DD\uD55C \uB0A0\uC9DC \uC774\uD6C4\uB85C \uD50C\uB798\uB108\uC5D0\uC11C \uD45C\uC2DC\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
        remove_planner: "\uC120\uD0DD\uD55C \uB0A0\uC9DC\uC758 \uACC4\uD68D\uB9CC \uCDE8\uC18C\uB418\uBA70, \uB2E4\uB978 \uB0A0\uC9DC\uC758 \uACC4\uD68D\uC740 \uC720\uC9C0\uB429\uB2C8\uB2E4.\n\uACC4\uD68D\uD55C \uB0A0\uC9DC\uAC00 \uC624\uB298\uBFD0\uC774\uB77C\uBA74 \uC778\uBC15\uC2A4\uC5D0 \uB0A8\uC544\uC788\uAC8C \uB429\uB2C8\uB2E4.",
        delete: "\uBAA8\uB4E0 \uB0A0\uC9DC\uC758 \uACC4\uD68D\uC774 \uC0AC\uB77C\uC9D1\uB2C8\uB2E4."
      },
      descriptions: {
        cancel_1: "\uB354 \uC774\uC0C1 \uC9C4\uD589\uD558\uC9C0 \uC54A\uC73C\uC2E4 \uAC74\uAC00\uC694?",
        cancel_2: "<0>\uC120\uD0DD\uD558\uC2E0\xA0</0><1>{{date}}</1><0>\uAE4C\uC9C0\uB9CC \uD50C\uB798\uB108\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4.</0>",
        remove_planner_1: "<0>\uC120\uD0DD\uD558\uC2E0\xA0</0><1>{{date}}</1><0>\uC758 \uD560 \uC77C \uB9AC\uC2A4\uD2B8\uC5D0\uC11C \uC774 \uD560 \uC77C\uC744 \uC9C0\uC6C1\uB2C8\uB2E4.</0>",
        remove_planner_2: "\uC0AD\uC81C\uB97C \uC6D0\uD558\uC2DC\uBA74 '\uC804\uCCB4 \uC0AD\uC81C'\uB97C \uD1B5\uD574 \uC0AD\uC81C\uD574\uC8FC\uC138\uC694.",
        delete_1: "\uD560 \uC77C\uC744 \uC644\uC804\uD788 \uC0AD\uC81C\uD569\uB2C8\uB2E4.",
        delete_2: "<0>\uC120\uD0DD\uD558\uC2E0\xA0</0><1>{{date}}</1><0>\uC758 \uACC4\uD68D\uB9CC \uCDE8\uC18C\uD558\uB824\uBA74 '\uACC4\uD68D \uCDE8\uC18C'\uB97C \uC120\uD0DD\uD558\uC138\uC694.</0>"
      },
      other: {
        info_tooltip: "\uC624\uBE14\uB9BF\uC758 \uD560 \uC77C \uC0AD\uC81C \uC885\uB958\uC5D0 \uB300\uD574",
        current_option: "\uD604\uC7AC \uC120\uD0DD\uD55C \uC635\uC158"
      }
    },
    todoDrawer: {
      not_found_modal: {
        title: "\uD560 \uC77C \uC0AD\uC81C\uB428",
        message: "\uC0AD\uC81C\uB41C \uD560 \uC77C\uC785\uB2C8\uB2E4.",
        message_with_title: "{{title}}(\uC740)\uB294 \uC0AD\uC81C\uB41C \uD560 \uC77C\uC785\uB2C8\uB2E4.",
        confirm: "\uD655\uC778"
      },
      popover: {
        notes: "\uB178\uD2B8",
        comments: "\uB313\uAE00 ({{count}})",
        view_detail: "\uD560 \uC77C \uC0C1\uC138 \uBCF4\uAE30"
      },
      detail: {
        add_date: "\uB0A0\uC9DC \uCD94\uAC00\uD558\uAE30"
      },
      drawer: {
        toggle_visibility: "{{status}}\uB85C \uC804\uD658",
        private: "\uBE44\uACF5\uAC1C",
        public: "\uACF5\uAC1C"
      },
      planner_list: {
        set_time: "\uC2DC\uAC04 \uC124\uC815\uD558\uAE30",
        delete_time: "\uC2DC\uAC04 \uC0AD\uC81C"
      },
      content: {
        related_goal: "\uAD00\uB828 \uD0DC\uC2A4\uD06C",
        date_and_time: "\uB0A0\uC9DC \uBC0F \uC2DC\uAC04",
        todo_title_placeholder: "\uD560 \uC77C \uC81C\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694"
      },
      category_menu: {
        change_category: "\uCE74\uD14C\uACE0\uB9AC \uBCC0\uACBD",
        loading: "\uB85C\uB4DC \uC911..."
      },
      due_date_badge: {
        set_due_date: "\uB9C8\uAC10\uAE30\uD55C \uC124\uC815"
      },
      goal_selector: {
        connect_goal: "\uD0DC\uC2A4\uD06C \uC5F0\uACB0",
        view_goal_details: "\uD0DC\uC2A4\uD06C \uC0C1\uC138 \uBCF4\uAE30",
        please_select_goal: "\uD0DC\uC2A4\uD06C\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
        no_goals: "\uD0DC\uC2A4\uD06C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
        loading_goals: "\uD0DC\uC2A4\uD06C \uBD88\uB7EC\uC624\uB294 \uC911...",
        go_back: "\uB3CC\uC544\uAC00\uAE30",
        view_all_goals: "\uC804\uCCB4 \uD0DC\uC2A4\uD06C \uBCF4\uAE30",
        actions: {
          connect: "\uC5F0\uACB0",
          disconnect: "\uC5F0\uACB0 \uD574\uC81C"
        },
        notifications: {
          processing_title: "\uD0DC\uC2A4\uD06C {{action}} \uCC98\uB9AC \uC911",
          success_title: "\uD0DC\uC2A4\uD06C {{action}} \uCC98\uB9AC \uC644\uB8CC",
          success_message: "\uC120\uD0DD\uD55C \uD0DC\uC2A4\uD06C\uC640 {{action}}\uD588\uC2B5\uB2C8\uB2E4.",
          error_title: "\uD0DC\uC2A4\uD06C {{action}} \uCC98\uB9AC \uC2E4\uD328",
          error_message: "\uD0DC\uC2A4\uD06C {{action}} \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4"
        }
      }
    }
  }
};

// src/locales/ko-KR/report.json
var report_default2 = {
  title: "\uBCF4\uACE0\uC11C",
  type: {
    daily: "\uC77C\uC77C \uBCF4\uACE0\uC11C",
    weekly: "\uC8FC\uAC04 \uBCF4\uACE0\uC11C",
    monthly: "\uC6D4\uAC04 \uBCF4\uACE0\uC11C",
    quarterly: "\uBD84\uAE30 \uBCF4\uACE0\uC11C",
    annual: "\uC5F0\uAC04 \uBCF4\uACE0\uC11C",
    custom: "\uCEE4\uC2A4\uD140 \uBCF4\uACE0\uC11C"
  },
  list: {
    title: "\uBCF4\uACE0\uC11C \uBAA9\uB85D",
    sent: "\uBCF4\uB0B8 \uBCF4\uACE0\uC11C",
    received: "\uBC1B\uC740 \uBCF4\uACE0\uC11C",
    noSentReports: "\uBCF4\uB0B8 \uBCF4\uACE0\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    noReceivedReports: "\uBC1B\uC740 \uBCF4\uACE0\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    createNew: "\uC0C8 \uBCF4\uACE0\uC11C\uB97C \uC791\uC131\uD574\uBCF4\uC138\uC694.",
    columns: {
      title: "\uC81C\uBAA9",
      sender: "\uBC1C\uC2E0\uC790",
      receiver: "\uC218\uC2E0\uC790",
      date: "\uB0A0\uC9DC",
      team: "\uD300"
    },
    unknownUser: "\uC54C \uC218 \uC5C6\uC74C",
    draft: "\uCD08\uC548",
    noTitle: "\uC81C\uBAA9 \uC5C6\uC74C",
    unknown: "\uC54C \uC218 \uC5C6\uC74C",
    markAsRead: "\uC77D\uC74C\uC73C\uB85C \uD45C\uC2DC",
    markAsReadComplete: "\uC77D\uC74C \uCC98\uB9AC \uC644\uB8CC",
    markAsReadSuccess: "{{count}}\uAC1C \uBCF4\uACE0\uC11C\uB97C \uC77D\uC74C\uC73C\uB85C \uD45C\uC2DC\uD588\uC2B5\uB2C8\uB2E4.",
    noUnreadReports: "\uC77D\uC9C0 \uC54A\uC740 \uBCF4\uACE0\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    markAsReadError: "\uC77C\uBD80 \uBCF4\uACE0\uC11C\uC758 \uC77D\uC74C \uD45C\uC2DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    loading: "\uBCF4\uACE0\uC11C \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911...",
    error: "\uBCF4\uACE0\uC11C \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  actions: {
    create: "\uBCF4\uACE0\uC11C \uC791\uC131",
    edit: "\uBCF4\uACE0\uC11C \uC218\uC815",
    delete: "\uBCF4\uACE0\uC11C \uC0AD\uC81C",
    send: "\uBCF4\uACE0\uC11C \uBCF4\uB0B4\uAE30",
    save: "\uBCF4\uACE0\uC11C \uC800\uC7A5",
    saveDraft: "\uCD08\uC548\uC73C\uB85C \uC800\uC7A5",
    confirmDelete: "\uC774 \uBCF4\uACE0\uC11C\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    cancelGeneration: "\uC0DD\uC131 \uC911\uB2E8"
  },
  sendReport: {
    button: "\uBCF4\uACE0\uC11C \uBCF4\uB0B4\uAE30",
    title: "\uBCF4\uACE0\uC11C \uC218\uC2E0\uC790 \uC120\uD0DD",
    selectAll: "\uC804\uCCB4\uC120\uD0DD",
    noName: "\uC774\uB984 \uC5C6\uC74C",
    loadingMembers: "\uBA64\uBC84 \uBAA9\uB85D \uB85C\uB529 \uC911...",
    noMembers: "\uBCF4\uB0BC \uC218 \uC788\uB294 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    cancel: "\uCDE8\uC18C",
    send: "\uBCF4\uB0B4\uAE30",
    notifications: {
      selectRecipients: "\uC218\uC2E0\uC790 \uC120\uD0DD \uD544\uC694",
      selectRecipientsMessage: "\uBCF4\uACE0\uC11C\uB97C \uBCF4\uB0BC \uBA64\uBC84\uB97C \uD55C \uBA85 \uC774\uC0C1 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
      sendComplete: "\uBCF4\uACE0\uC11C \uC804\uC1A1 \uC644\uB8CC",
      newRecipients: "{{count}}\uBA85\uC758 \uC218\uC2E0\uC790\uC5D0\uAC8C \uBCF4\uACE0\uC11C\uAC00 \uC804\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      updatedRecipients: "{{count}}\uBA85\uC758 \uC218\uC2E0\uC790\uC5D0\uAC8C \uBCF4\uACE0\uC11C\uAC00 \uC5C5\uB370\uC774\uD2B8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      newAndUpdatedRecipients: "{{count}}\uBA85\uC758 \uC0C8 \uC218\uC2E0\uC790\uC5D0\uAC8C \uBCF4\uACE0\uC11C\uAC00 \uC804\uC1A1\uB418\uC5C8\uACE0, {{updateCount}}\uBA85\uC758 \uAE30\uC874 \uC218\uC2E0\uC790\uC5D0\uAC8C \uC5C5\uB370\uC774\uD2B8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      sendFailed: "\uBCF4\uACE0\uC11C \uC804\uC1A1 \uC2E4\uD328",
      sendFailedMessage: "\uBCF4\uACE0\uC11C \uC804\uC1A1 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    }
  },
  notifications: {
    title: "\uC54C\uB9BC",
    success: "\uC131\uACF5",
    error: "\uC624\uB958"
  },
  reportPage: {
    newReport: "\uC0C8 \uBCF4\uACE0\uC11C",
    loading: "\uBCF4\uACE0\uC11C \uB85C\uB529 \uC911...",
    loadingData: "\uBCF4\uACE0\uC11C \uB370\uC774\uD130 \uB85C\uB529 \uC911...",
    error: {
      loading: "\uBCF4\uACE0\uC11C\uB97C \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      unknown: "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      notFound: "\uBCF4\uACE0\uC11C\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      notFoundDescription: "\uC694\uCCAD\uD55C \uBCF4\uACE0\uC11C\uAC00 \uC0AD\uC81C\uB418\uC5C8\uAC70\uB098 \uC874\uC7AC\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."
    },
    backToList: "\uBCF4\uACE0\uC11C \uBAA9\uB85D\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
    createNewReport: "\uC0C8 \uBCF4\uACE0\uC11C \uC791\uC131",
    createNewReportDescription: "\uC0C8 \uBCF4\uACE0\uC11C\uB97C \uC791\uC131\uD558\uB294 \uC591\uC2DD\uC774 \uC5EC\uAE30\uC5D0 \uCD94\uAC00\uB420 \uC608\uC815\uC785\uB2C8\uB2E4.",
    editing: "\uD3B8\uC9D1 \uC911...",
    edit: "\uD3B8\uC9D1",
    editComplete: "\uD3B8\uC9D1 \uC644\uB8CC",
    exportToPdf: "PDF\uB85C \uB0B4\uBCF4\uB0B4\uAE30",
    deleteReport: "\uBCF4\uACE0\uC11C \uC0AD\uC81C",
    createdDate: "\uC791\uC131\uC77C",
    noCreatedDate: "\uC791\uC131\uC77C \uC815\uBCF4 \uC5C6\uC74C",
    updatedDate: "\uC218\uC815\uC77C",
    editorArea: "\uC5D0\uB514\uD130 \uC601\uC5ED",
    emptyState: {
      title: "\uBCF4\uACE0\uC11C\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      description: "\uC67C\uCABD \uBAA9\uB85D\uC5D0\uC11C \uBCF4\uACE0\uC11C\uB97C \uC120\uD0DD\uD558\uC5EC \uC0C1\uC138 \uB0B4\uC6A9\uC744 \uD655\uC778\uD558\uC138\uC694."
    },
    translate: "\uBC88\uC5ED",
    aiTranslation: "AI \uBC88\uC5ED",
    deleteModal: {
      title: "\uBCF4\uACE0\uC11C \uC0AD\uC81C \uD655\uC778",
      confirmMessage: "\uC774 \uBCF4\uACE0\uC11C\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC0AD\uC81C\uB41C \uBCF4\uACE0\uC11C\uB294 \uBAA9\uB85D\uC5D0 \uD45C\uC2DC\uB418\uC9C0 \uC54A\uC9C0\uB9CC \uB2E4\uB978 \uC0AC\uC6A9\uC790\uB294 \uACC4\uC18D \uBCFC \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      successMessage: "\uBCF4\uACE0\uC11C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      errorMessage: "\uBCF4\uACE0\uC11C \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      error: "\uBCF4\uACE0\uC11C \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    notifications: {
      titleSaveComplete: "\uC81C\uBAA9 \uC800\uC7A5 \uC644\uB8CC",
      titleSaveMessage: "\uBCF4\uACE0\uC11C \uC81C\uBAA9\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      saveFailed: "\uC800\uC7A5 \uC2E4\uD328",
      titleSaveErrorMessage: "\uC81C\uBAA9\uC744 \uC800\uC7A5\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      reportSaveComplete: "\uBCF4\uACE0\uC11C \uC800\uC7A5 \uC644\uB8CC",
      reportSaveMessage: "\uBCF4\uACE0\uC11C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      reportSaveErrorMessage: "\uBCF4\uACE0\uC11C\uB97C \uC800\uC7A5\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      exportFailed: "\uB0B4\uBCF4\uB0B4\uAE30 \uC2E4\uD328",
      exportErrorMessage: "\uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    }
  },
  common: {
    cancel: "\uCDE8\uC18C",
    delete: "\uC0AD\uC81C",
    save: "\uC800\uC7A5",
    edit: "\uD3B8\uC9D1",
    error: "\uC624\uB958",
    alert: "\uC54C\uB9BC",
    success: "\uC131\uACF5",
    team: "\uD300"
  },
  personalReport: {
    title: "\uAC1C\uC778 \uBCF4\uACE0\uC11C"
  },
  teamReport: {
    title: "\uD300 \uBCF4\uACE0\uC11C",
    errors: {
      requiredDataMissing: "\uD544\uC218 \uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
      cannotFetchTeamData: "\uD300 \uBCF4\uACE0\uC11C \uB370\uC774\uD130\uB97C \uAC00\uC838\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      teamReportGenerationFailed: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
      teamReportError: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    notifications: {
      teamReportCancelled: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911\uB2E8\uB428",
      teamReportCancelledMessage: "\uC0AC\uC6A9\uC790 \uC694\uCCAD\uC73C\uB85C \uBCF4\uACE0\uC11C \uC0DD\uC131\uC774 \uC911\uB2E8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      teamReportGenerating: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911",
      teamReportGeneratingMessage: "{{teamName}} \uC77C\uC77C \uBCF4\uACE0\uC11C\uB97C \uC0DD\uC131\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      teamReportFailed: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC2E4\uD328",
      teamReportFailedMessage: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
      teamReportCompleted: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC644\uB8CC",
      teamReportCompletedMessage: "{{teamName}} \uC77C\uC77C \uBCF4\uACE0\uC11C\uAC00 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uD074\uB9AD\uD558\uC5EC \uD655\uC778\uD558\uC138\uC694.",
      teamReportSaveFailed: "\uD300 \uBCF4\uACE0\uC11C \uC800\uC7A5 \uC2E4\uD328",
      teamReportSaveFailedMessage: "\uBCF4\uACE0\uC11C \uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      teamReportErrorMessage: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      teamReportCompleteTitle: "\u{1F4CA} \uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131 \uC644\uB8CC",
      teamReportCompleteBody: "{{teamName}} \uC77C\uC77C \uBCF4\uACE0\uC11C\uAC00 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.\n\uD074\uB9AD\uD558\uC5EC \uD655\uC778\uD558\uC138\uC694.",
      teamReportCompleteBodyAlt: "{{teamName}} \uD300\uC758 \uC77C\uC77C \uBCF4\uACE0\uC11C\uB97C \uD655\uC778\uD558\uC138\uC694."
    },
    reportTitle: {
      teamDaily: "{{teamName}} - {{date}} \uC77C\uC77C \uBCF4\uACE0\uC11C"
    }
  },
  writePage: {
    reportTitleDaily: "\uC77C\uC77C \uBCF4\uACE0\uC11C",
    reportTitleWeekly: "\uC8FC\uAC04 \uBCF4\uACE0\uC11C",
    titlePlaceholder: "\uC81C\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694",
    saveButton: "\uC800\uC7A5",
    aiGenerateButton: "AI\uB85C \uBCF4\uACE0\uC11C \uC0DD\uC131",
    regenerateButton: "\uB2E4\uC2DC \uC0DD\uC131",
    generatingReport: "\uBCF4\uACE0\uC11C\uB97C \uC0DD\uC131\uD558\uB294 \uC911...",
    aiHelpText: "AI\uAC00 \uC791\uC131\uD55C \uBCF4\uACE0\uC11C\uB97C \uD655\uC778\uD558\uACE0 \uD544\uC694\uD55C \uBD80\uBD84\uC744 \uC218\uC815\uD574\uC8FC\uC138\uC694.",
    teamLoading: "\uD300 \uC815\uBCF4 \uB85C\uB529 \uC911...",
    notifications: {
      userOrWorkspaceError: "\uC0AC\uC6A9\uC790 \uB610\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC815\uBCF4\uB97C \uD655\uC778\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      titleRequired: "\uC81C\uBAA9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      createSuccess: "\uBCF4\uACE0\uC11C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      createError: "\uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    teamReportModal: {
      title: "\uD300 \uBCF4\uACE0\uC11C \uC0DD\uC131",
      description1: "\uD300 \uBCF4\uACE0\uC11C\uB294 \uD300\uC6D0\uB4E4\uC758 \uD65C\uB3D9\uC744 \uC885\uD569\uD558\uC5EC",
      carefullyWritten: "\uC138\uC2EC\uD558\uAC8C \uC791\uC131",
      description2: "\uB418\uBBC0\uB85C",
      timeRequired: "\uC57D 3-5\uBD84",
      description3: "\uC774 \uC18C\uC694\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      notificationInfo1: "\uBCF4\uACE0\uC11C\uAC00 \uC900\uBE44\uB418\uBA74",
      notificationLocation: "\uD654\uBA74 \uC6B0\uCE21 \uC0C1\uB2E8 \uC54C\uB9BC",
      notificationInfo2: "\uC73C\uB85C \uC548\uB0B4\uD574\uB4DC\uB9BD\uB2C8\uB2E4.",
      tip: "\u{1F4A1} \uD300 \uBCF4\uACE0\uC11C\uAC00 \uC0DD\uC131\uB418\uB294 \uB3D9\uC548 \uB2E4\uB978 \uC791\uC5C5\uC744 \uACC4\uC18D\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      cancel: "\uCDE8\uC18C",
      confirm: "\uC0DD\uC131 \uC2DC\uC791"
    }
  },
  creationModal: {
    createTitle: "\uC0C8 \uBCF4\uACE0\uC11C \uC791\uC131",
    editTitle: "\uC720\uD615 \uBC0F \uB0A0\uC9DC \uC218\uC815",
    description: "\uBCF4\uACE0\uC11C \uC720\uD615\uC744 \uC120\uD0DD\uD574 \uBCF4\uACE0\uC11C \uC0DD\uC131\uD558\uAE30 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD574\uC8FC\uC138\uC694.",
    teamDescription: "\uBCF4\uACE0\uC11C \uC720\uD615\uACFC \uD300\uC744 \uC120\uD0DD\uD574 \uBCF4\uACE0\uC11C \uC0DD\uC131\uD558\uAE30 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD574\uC8FC\uC138\uC694.",
    selectTeam: "\uD300 \uC120\uD0DD",
    reportType: "\uBCF4\uACE0\uC11C \uC720\uD615",
    date: "\uB0A0\uC9DC"
  },
  languages: {
    english: "\uC601\uC5B4",
    korean: "\uD55C\uAD6D\uC5B4",
    vietnamese: "\uBCA0\uD2B8\uB0A8\uC5B4",
    japanese: "\uC77C\uBCF8\uC5B4",
    chinese: "\uC911\uAD6D\uC5B4"
  },
  search: {
    placeholder: "\uBCF4\uACE0\uC11C \uC81C\uBAA9 \uAC80\uC0C9"
  },
  weekdays: {
    sunday: "\uC77C",
    monday: "\uC6D4",
    tuesday: "\uD654",
    wednesday: "\uC218",
    thursday: "\uBAA9",
    friday: "\uAE08",
    saturday: "\uD1A0"
  },
  categories: {
    work: "\uC5C5\uBB34",
    meeting: "\uD68C\uC758",
    communication: "\uCEE4\uBBA4\uB2C8\uCF00\uC774\uC158",
    study: "\uC2A4\uD130\uB514",
    rest: "\uD734\uC2DD",
    other: "\uAE30\uD0C0"
  },
  errors: {
    fetchSourceDataFailed: "\uCD9C\uCC98 \uB370\uC774\uD130 \uAC00\uC838\uC624\uAE30 \uC2E4\uD328",
    reportNotFound: "\uBCF4\uACE0\uC11C\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
    addReceiverError: "\uBCF4\uACE0\uC11C \uC218\uC2E0\uC790 \uCD94\uAC00 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
    markAsReadFailed: "\uC77D\uC74C \uCC98\uB9AC \uC2E4\uD328",
    teamInfoFetchFailed: "\uD300 \uC815\uBCF4 \uC870\uD68C \uC2E4\uD328",
    teamMembersFetchFailed: "\uD300 \uBA64\uBC84 \uC870\uD68C \uC2E4\uD328",
    plannerDataFetchFailed: "\uD50C\uB798\uB108 \uB370\uC774\uD130 \uC870\uD68C \uC2E4\uD328",
    goalDataFetchFailed: "\uBAA9\uD45C \uB370\uC774\uD130 \uC870\uD68C \uC2E4\uD328",
    noteDataFetchFailed: "\uB178\uD2B8 \uB370\uC774\uD130 \uC870\uD68C \uC2E4\uD328",
    commentDataFetchFailed: "\uB313\uAE00 \uB370\uC774\uD130 \uC870\uD68C \uC2E4\uD328",
    reportGenerationAborted: "\uBCF4\uACE0\uC11C \uC0DD\uC131\uC774 \uC911\uB2E8\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
    backendError: "\uBC31\uC5D4\uB4DC \uC5D0\uB7EC",
    dataQueryFailed: "\uB370\uC774\uD130 \uC870\uD68C \uC2E4\uD328"
  },
  messages: {
    readProcessOptimisticStart: "\uC77D\uC74C \uCC98\uB9AC \uB099\uAD00\uC801 \uC5C5\uB370\uC774\uD2B8 \uC2DC\uC791",
    readProcessOptimisticComplete: "\uC77D\uC74C \uCC98\uB9AC \uB099\uAD00\uC801 \uC5C5\uB370\uC774\uD2B8 \uC644\uB8CC",
    readProcessFailed: "\uC77D\uC74C \uCC98\uB9AC \uC2E4\uD328: \uC774\uC804 \uC0C1\uD0DC\uB85C \uB864\uBC31\uD569\uB2C8\uB2E4",
    readProcessSuccess: "\uC77D\uC74C \uCC98\uB9AC \uC131\uACF5",
    queryInvalidationComplete: "\uC77D\uC74C \uCC98\uB9AC \uD6C4 \uCFFC\uB9AC \uBB34\uD6A8\uD654 \uC644\uB8CC",
    noTeamMembers: "\uD300 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
    reportGenerationCancelled: "\uBCF4\uACE0\uC11C \uC0DD\uC131 \uC911\uB2E8 \uD655\uC778",
    selectedDate: "\uC120\uD0DD\uB41C \uB0A0\uC9DC",
    userQuery: "\uC0AC\uC6A9\uC790 \uCFFC\uB9AC",
    dataCount: "\uC870\uD68C\uB41C \uB370\uC774\uD130 \uAC1C\uC218",
    reportType: "\uBCF4\uACE0\uC11C \uD0C0\uC785",
    ownerType: "\uC18C\uC720\uC790 \uD0C0\uC785",
    requestData: "\uC694\uCCAD \uB370\uC774\uD130",
    receivedChunk: "\uC218\uC2E0\uB41C \uCCAD\uD06C",
    finalReportLength: "\uCD5C\uC885 \uC0DD\uC131\uB41C \uBCF4\uACE0\uC11C \uAE38\uC774",
    characters: "\uAE00\uC790"
  }
};

// src/locales/ko-KR/auth.json
var auth_default2 = {
  agreement: {
    advertising: "\uAD11\uACE0\uC131 \uC815\uBCF4 \uC218\uC2E0 \uB3D9\uC758",
    age: "\uB9CC 14\uC138 \uC774\uC0C1\uC785\uB2C8\uB2E4",
    description: "\uC120\uD0DD \uC0AC\uD56D \uB3D9\uC758\uB97C \uAC70\uBD80\uD558\uC154\uB3C4 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    detail: "\uC0C1\uC138\uBCF4\uAE30",
    label: "\uC804\uCCB4 \uC57D\uAD00\uC5D0 \uB3D9\uC758\uD569\uB2C8\uB2E4 (\uC120\uD0DD \uC815\uBCF4\uB97C \uD3EC\uD568\uD569\uB2C8\uB2E4)",
    law_description: "\uC815\uBCF4\uC8FC\uCCB4\uC758 \uAC1C\uC778\uC815\uBCF4 \uBC0F \uAD8C\uB9AC \uBCF4\uD638\uB97C \uC704\uD574 \u300C\uAC1C\uC778\uC815\uBCF4 \uBCF4\uD638\uBC95\u300D \uBC0F \uAD00\uACC4 \uBC95\uB839\uC774 \uC815\uD55C \uBC14\uB97C \uC900\uC218\uD558\uC5EC \uC548\uC804\uD558\uAC8C \uAD00\uB9AC\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uC790\uC138\uD55C \uC0AC\uD56D\uC740 \uAC1C\uC778\uC815\uBCF4\uCC98\uB9AC\uBC29\uCE68\uC5D0\uC11C \uD655\uC778\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    marketing: "\uD64D\uBCF4 \uBC0F \uB9C8\uCF00\uD305 \uD65C\uC6A9 \uB3D9\uC758",
    optional: "\uC120\uD0DD",
    overseas: "\uAC1C\uC778\uC815\uBCF4 \uC81C3\uC790 \uC81C\uACF5(\uAD6D\uC678\uC774\uC804) \uB3D9\uC758\uC11C",
    personalInfo: "\uAC1C\uC778\uC815\uBCF4 \uC218\uC9D1\uC774\uC6A9 \uB3D9\uC758\uC11C",
    privacy: "\uAC1C\uC778\uC815\uBCF4 \uCC98\uB9AC \uBC29\uCE68 \uB3D9\uC758",
    recording: "\uBBF8\uD305 \uB0B4\uC6A9 \uB179\uC74C\uC5D0 \uB300\uD55C \uB3D9\uC758",
    required: "\uD544\uC218",
    service: "\uC774\uC6A9\uC57D\uAD00",
    thirdParty: "\uAC1C\uC778\uC815\uBCF4 \uC81C3\uC790 \uC81C\uACF5 \uB3D9\uC758\uC11C",
    title: "\uC57D\uAD00 \uB3D9\uC758"
  },
  auth_callback: {
    notification: {
      stale_token: {
        title: "\uC7AC\uC124\uC815 \uB9C1\uD06C \uB9CC\uB8CC",
        message: "\uC7AC\uC124\uC815 \uB9C1\uD06C\uAC00 \uB9CC\uB8CC\uB418\uC5C8\uAC70\uB098 \uC774\uBBF8 \uC0AC\uC6A9\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC694\uCCAD\uD574\uC8FC\uC138\uC694."
      },
      error: {
        title: "\uC778\uC99D \uCC98\uB9AC \uC624\uB958",
        message: "\uC778\uC99D \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
      }
    },
    confirm: {
      title: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uACC4\uC18D\uD558\uAE30",
      description: "\uBCF8\uC778 \uD655\uC778\uC744 \uC704\uD574 \uC544\uB798 \uBC84\uD2BC\uC744 \uB20C\uB7EC \uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815\uC744 \uC9C4\uD589\uD574\uC8FC\uC138\uC694.",
      button: "\uACC4\uC18D"
    },
    mobile: {
      error: {
        title: "\uC778\uC99D \uCC98\uB9AC \uC624\uB958",
        message: "\uC778\uC99D \uB9C1\uD06C\uC5D0 \uBB38\uC81C\uAC00 \uC788\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC694\uCCAD\uD574\uC8FC\uC138\uC694."
      }
    }
  },
  forgot_password: {
    title: "\uBE44\uBC00\uBC88\uD638 \uCC3E\uAE30",
    description: "\uAC00\uC785 \uC2DC \uC0AC\uC6A9\uD55C \uC774\uBA54\uC77C\uC744 \uC785\uB825\uD558\uBA74 \uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uB9C1\uD06C\uB97C \uBCF4\uB0B4\uB4DC\uB9BD\uB2C8\uB2E4.",
    button: "\uC7AC\uC124\uC815 \uB9C1\uD06C \uBCF4\uB0B4\uAE30",
    resend: "\uB2E4\uC2DC \uBCF4\uB0B4\uAE30",
    resend_in: "{{seconds}}\uCD08 \uD6C4 \uB2E4\uC2DC \uBCF4\uB0B4\uAE30",
    back_to_login: "\uB85C\uADF8\uC778\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
    email_sent: {
      title: "\uC774\uBA54\uC77C\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694",
      message: "{{email}}\uB85C \uC7AC\uC124\uC815 \uB9C1\uD06C\uB97C \uBCF4\uB0C8\uC2B5\uB2C8\uB2E4.",
      help: "\uBC1B\uC9C0 \uBABB\uD558\uC168\uB098\uC694? \uC2A4\uD338\uD568\uC744 \uD655\uC778\uD558\uAC70\uB098 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    },
    notification: {
      success: {
        title: "\uC774\uBA54\uC77C \uBC1C\uC1A1 \uC644\uB8CC",
        message: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uB9C1\uD06C\uAC00 \uC774\uBA54\uC77C\uB85C \uBC1C\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uBA54\uC77C\uD568\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
      },
      error: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uC774\uBA54\uC77C \uBC1C\uC1A1 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  reset_password: {
    title: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815",
    description: "\uC0C8\uB85C\uC6B4 \uBE44\uBC00\uBC88\uD638\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694.",
    button: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815",
    notification: {
      success: {
        title: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uC644\uB8CC",
        message: "\uBE44\uBC00\uBC88\uD638\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uB85C\uADF8\uC778\uD574 \uC8FC\uC138\uC694."
      },
      error: "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  change_password: {
    action_title: "\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD\uD558\uAE30",
    button: "\uBCC0\uACBD\uD558\uAE30",
    new_password: {
      label: "\uC0C8 \uBE44\uBC00\uBC88\uD638",
      placeholder: "\uC0C8 \uBE44\uBC00\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694"
    },
    notification: {
      error: "\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      success: {
        message: "\uBE44\uBC00\uBC88\uD638\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        title: "\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD \uC644\uB8CC"
      }
    },
    title: "\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD"
  },
  delete_account: {
    action_title: "\uD0C8\uD1F4\uD558\uAE30",
    description: "\uD300\uC5D0 \uAC00\uC785\uB418\uC5B4 \uC788\uB2E4\uBA74, \uD300\uC5D0 \uC18C\uC18D\uB418\uC5B4 \uB9CC\uB4E0 \uD560 \uC77C\uB4E4\uC740 \uC0AD\uC81C\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    modal: {
      message_1: "\uC815\uB9D0\uB85C \uACC4\uC815\uC744 \uD0C8\uD1F4\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      message_2: "\uACC4\uC815\uC744 \uD0C8\uD1F4\uD558\uBA74 \uAC1C\uC778 \uC815\uBCF4\uC640 \uD65C\uB3D9 \uB0B4\uC5ED\uC774 \uC0AD\uC81C\uB429\uB2C8\uB2E4. \uB2E8, \uD300\uC5D0 \uC18C\uC18D\uB418\uC5B4 \uB9CC\uB4E0 \uD560 \uC77C\uB4E4\uC740 \uC0AD\uC81C\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."
    },
    notification: {
      error: {
        message: "\uACC4\uC815 \uD0C8\uD1F4 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
        session: "\uC778\uC99D \uC138\uC158\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uB85C\uADF8\uC778\uD574\uC8FC\uC138\uC694.",
        title: "\uACC4\uC815 \uD0C8\uD1F4 \uC2E4\uD328",
        has_blocking_workspace: "Owenr \uAD8C\uD55C\uC744 \uAC00\uC9C4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC788\uC2B5\uB2C8\uB2E4 ({{workspaceNames}}). \uBA3C\uC800 \uAD8C\uD55C\uC744 \uC704\uC784\uD574\uC8FC\uC138\uC694."
      },
      success: {
        message: "\uACC4\uC815\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uD0C8\uD1F4\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        title: "\uD0C8\uD1F4 \uC644\uB8CC"
      }
    },
    title: "\uACC4\uC815 \uD0C8\uD1F4",
    tooltip: "\uC815\uB9D0\uB85C \uC0AD\uC81C\uD558\uC2DC\uB098\uC694? \u{1F97A}"
  },
  invitation_list: {
    create_workspace: "\uC9C1\uC811 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB9CC\uB4E4\uAE30",
    description: "\uCD08\uB300\uB97C \uD655\uC778\uD558\uACE0 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCC38\uC5EC\uD574\uBCF4\uC138\uC694.",
    no_invite: "\uC0C8\uB85C\uC6B4 \uCD08\uB300\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
    register: "{{name}}\uB2D8\uC758 \uCD08\uB300",
    title: "{{count}}\uAC1C\uC758 \uCD08\uB300\uAC00 \uC788\uC2B5\uB2C8\uB2E4"
  },
  logout: {
    description: "\uC815\uB9D0 \uB85C\uADF8\uC544\uC6C3 \uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
  },
  no_access: {
    description: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 Owner \uB610\uB294 Admin\uC5D0\uAC8C 1on1 \uBAA8\uB4C8 \uD65C\uC131\uD654\uB97C \uC694\uCCAD\uD558\uC138\uC694.",
    title: "\uC11C\uBE44\uC2A4\uC5D0 \uC811\uADFC\uD560 \uC218 \uC788\uB294 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4."
  },
  no_workspace: {
    create_button: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131\uD558\uAE30",
    description: "1on1\uC744 \uC0AC\uC6A9\uD558\uB824\uBA74 \uBA3C\uC800 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0DD\uC131\uD574\uC8FC\uC138\uC694.",
    title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4.",
    title_mobile: "\uCD08\uB300\uB41C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    description_mobile: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 Admin\uC5D0\uAC8C \uCD08\uB300\uB97C \uC694\uCCAD\uD558\uC138\uC694."
  },
  no_permission: {
    title: "\uD398\uC774\uC9C0\uC5D0 \uC811\uADFC\uD560 \uC218 \uC788\uB294 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    meeting: "1on1 \uBBF8\uD305 \uAE30\uB85D\uC740 \uBBF8\uD305\uC758 \uCC38\uC5EC\uC790\uB9CC \uC870\uD68C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    dashboard: "\uB300\uC2DC\uBCF4\uB4DC\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 \uAD00\uB9AC\uC790\uB9CC \uC870\uD68C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."
  },
  sign_in: {
    action_title: "\uB85C\uADF8\uC778\uD558\uAE30",
    no_account: {
      action_title: "\uACC4\uC815 \uC0DD\uC131\uD558\uAE30",
      title: "\uC544\uC9C1 \uACC4\uC815\uC774 \uC5C6\uC73C\uC2E0\uAC00\uC694?"
    },
    notification: {
      error: "\uB85C\uADF8\uC778 \uC2E4\uD328",
      invitation: {
        message: "\uC0C8\uB85C\uC6B4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300\uAC00 \uC788\uC2B5\uB2C8\uB2E4. \uD074\uB9AD\uD558\uC5EC \uD655\uC778\uD558\uC138\uC694.",
        title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300"
      },
      success: {
        message: "\uD658\uC601\uD569\uB2C8\uB2E4!",
        title: "\uB85C\uADF8\uC778 \uC131\uACF5"
      }
    },
    redirect_to_mobile: "\uC571\uC73C\uB85C \uC774\uB3D9 \uC911...",
    title: "\uB85C\uADF8\uC778"
  },
  sign_up: {
    action_title: "\uAC00\uC785\uD558\uAE30",
    step_1: "\uC57D\uAD00 \uB3D9\uC758",
    step_2: "\uD68C\uC6D0 \uC815\uBCF4 \uC785\uB825",
    step_3: "\uAC00\uC785 \uC644\uB8CC",
    title: "\uD68C\uC6D0\uAC00\uC785"
  },
  sign_up_complete: {
    description: {
      first: "\uC785\uB825\uD558\uC2E0 \uC774\uBA54\uC77C\uB85C \uC778\uC99D \uC548\uB0B4 \uBA54\uC77C\uC744 \uBC1C\uC1A1\uD588\uC2B5\uB2C8\uB2E4.",
      second: "\uC774\uBA54\uC77C\uC5D0 \uD3EC\uD568\uB41C \uB9C1\uD06C\uB97C \uD074\uB9AD\uD574 \uC778\uC99D\uC774 \uC644\uB8CC\uB418\uBA74 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uC2E4 \uC218 \uC788\uC5B4\uC694.",
      third: "\uC778\uC99D \uBA54\uC77C\uC774 \uC624\uC9C0 \uC54A\uB294 \uACBD\uC6B0, \uC2A4\uD338\uBA54\uC77C\uD568 \uB610\uB294 \uD734\uC9C0\uD1B5\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
    },
    title: "\uC778\uC99D \uBA54\uC77C\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694."
  },
  sign_up_confirm_email: {
    title: "\uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694",
    description: "\uC544\uB798 \uC8FC\uC18C\uAC00 \uB85C\uADF8\uC778\uC5D0 \uC0AC\uC6A9\uB418\uBA70, \uC778\uC99D \uBA54\uC77C\uC774 \uBC1C\uC1A1\uB429\uB2C8\uB2E4.",
    check: "\uC774\uBA54\uC77C \uC8FC\uC18C\uAC00 \uC815\uD655\uD55C\uC9C0 \uD655\uC778\uD574\uC8FC\uC138\uC694.",
    cancel: "\uC218\uC815\uD558\uAE30",
    confirm: "\uC774 \uC8FC\uC18C\uB85C \uAC00\uC785\uD558\uAE30"
  },
  sign_up_form: {
    email_resend: "\uC778\uC99D\uBA54\uC77C \uC7AC\uC804\uC1A1\uD558\uAE30",
    email_resending: "\uC7AC\uC804\uC1A1 \uC911...",
    email_resent: {
      message: "\uC778\uC99D\uBA54\uC77C\uC774 \uC7AC\uC804\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uBA54\uC77C\uD568\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694.",
      title: "\uC778\uC99D\uBA54\uC77C \uC7AC\uC804\uC1A1 \uC644\uB8CC"
    },
    error: {
      email: "\uC774\uBA54\uC77C\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      email_exists: "\uC774\uBBF8 \uC874\uC7AC\uD558\uB294 \uC774\uBA54\uC77C\uC785\uB2C8\uB2E4.",
      email_exists_message: "\uC774\uBA54\uC77C\uC744 \uB2E4\uC2DC \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      email_invalid: "\uC62C\uBC14\uB978 \uC774\uBA54\uC77C \uD615\uC2DD\uC774 \uC544\uB2D9\uB2C8\uB2E4.",
      email_personal: "\uC624\uBE14\uB9BF\uC740 \uC870\uC9C1 \uB2E8\uC704\uB85C \uC0AC\uC6A9\uB418\uB294 \uC11C\uBE44\uC2A4\uC785\uB2C8\uB2E4. \uC0AC\uB0B4 \uB3C4\uC785 \uC2DC \uB9E4\uB044\uB7EC\uC6B4 \uC804\uD658\uC744 \uC704\uD574 \uC5C5\uBB34\uC6A9 \uC774\uBA54\uC77C\uC744 \uC0AC\uC6A9\uD574\uC8FC\uC138\uC694.",
      email_personal_help: "\uD68C\uC0AC \uC774\uBA54\uC77C\uC774 \uC5C6\uAC70\uB098, \uD68C\uC0AC \uC774\uBA54\uC77C\uC778\uB370 \uB9C9\uD788\uC168\uB2E4\uBA74 \uC800\uD76C\uC5D0\uAC8C \uC54C\uB824\uC8FC\uC138\uC694.",
      email_personal_contact: "\uBB38\uC758\uD558\uAE30",
      email_not_confirmed: "\uC774\uBA54\uC77C \uC778\uC99D\uC774 \uC644\uB8CC\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. \uC774\uBA54\uC77C\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694.",
      invalid_login_credentials: "\uC774\uBA54\uC77C \uB610\uB294 \uBE44\uBC00\uBC88\uD638\uAC00 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
      login: "\uB85C\uADF8\uC778 \uC624\uB958",
      logout: "\uB85C\uADF8\uC544\uC6C3 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      password_confirm_invalid: "\uBE44\uBC00\uBC88\uD638\uAC00 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
      password_invalid: "\uBE44\uBC00\uBC88\uD638\uB294 \uCD5C\uC18C 6\uC790 \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4.",
      same_password: "\uC0C8 \uBE44\uBC00\uBC88\uD638\uAC00 \uAE30\uC874 \uBE44\uBC00\uBC88\uD638\uC640 \uB3D9\uC77C\uD569\uB2C8\uB2E4.",
      title: "\uD68C\uC6D0\uAC00\uC785 \uC911 \uC624\uB958 \uBC1C\uC0DD",
      too_many_requests: "\uB108\uBB34 \uB9CE\uC740 \uB85C\uADF8\uC778 \uC2DC\uB3C4\uAC00 \uC788\uC5C8\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
      update_failed: "\uC0AC\uC6A9\uC790 \uC815\uBCF4 \uC5C5\uB370\uC774\uD2B8\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
    },
    helper: {
      email: "\uC624\uBE14\uB9BF\uC740 \uC870\uC9C1 \uB2E8\uC704\uB85C \uC0AC\uC6A9\uB418\uB294 \uC11C\uBE44\uC2A4\uB85C, \uD68C\uC0AC \uC774\uBA54\uC77C\uB85C \uAC00\uC785\uC744 \uBC1B\uACE0 \uC788\uC5B4\uC694."
    },
    label: {
      confirmPassword: "\uBE44\uBC00\uBC88\uD638 \uD655\uC778",
      email: "\uC774\uBA54\uC77C",
      name: "\uC774\uB984",
      password: "\uBE44\uBC00\uBC88\uD638"
    },
    placeholder: {
      confirmPassword: "\uBE44\uBC00\uBC88\uD638\uB97C \uB2E4\uC2DC \uC785\uB825\uD558\uC138\uC694",
      company_email: "\uC0AC\uC6A9 \uC911\uC774\uC2E0 \uD68C\uC0AC \uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694",
      email: "\uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD558\uC138\uC694",
      name: "\uD64D\uAE38\uB3D9",
      password: "\uBE44\uBC00\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694"
    },
    required: "* \uD45C\uC2DC\uC758 \uD56D\uBAA9\uC740 \uD544\uC218\uB85C \uC785\uB825\uD574\uC57C \uD558\uB294 \uD56D\uBAA9\uC785\uB2C8\uB2E4.",
    title: "\uD68C\uC6D0 \uC815\uBCF4 \uC785\uB825"
  },
  user_setting: {
    general: "\uC77C\uBC18",
    integration: "\uC678\uBD80 \uC5F0\uB3D9",
    label: {
      email: "\uC774\uBA54\uC77C",
      language: "\uC5B8\uC5B4",
      mbti: "MBTI",
      name: "\uC774\uB984",
      oneonone_email: "\uBA54\uC77C \uC54C\uB9BC \uC218\uC2E0",
      password: "\uBE44\uBC00\uBC88\uD638",
      phone: "\uC804\uD654\uBC88\uD638",
      planning_time: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC",
      set_days: "\uC694\uC77C \uC124\uC815",
      set_time: "\uC2DC\uAC04 \uC124\uC815",
      timezone: "\uC2DC\uAC04\uB300"
    },
    description: {
      oneonone_email: "1on1 \uBBF8\uD305 \uAD00\uB828 \uC54C\uB9BC\uC744 \uBA54\uC77C\uB85C \uBC1B\uC2B5\uB2C8\uB2E4."
    },
    notification: "\uC54C\uB9BC",
    notification_message: {
      error: {
        message: "\uD504\uB85C\uD544 \uC815\uBCF4\uB97C \uC800\uC7A5\uD558\uB294 \uC911 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
        title: "\uD504\uB85C\uD544 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328"
      },
      name_error: "\uC774\uB984\uC740 \uBE44\uC6CC\uB458 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC774\uB984\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      planning_time: {
        error: {
          message: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC \uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
          title: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328"
        },
        inProgress: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC \uC5C5\uB370\uC774\uD2B8",
        success: {
          message: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC5C5\uB370\uC774\uD2B8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
          title: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC \uC5C5\uB370\uC774\uD2B8 \uC644\uB8CC"
        }
      },
      success: {
        message: "\uBCC0\uACBD\uD558\uC2E0 \uC815\uBCF4\uAC00 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        title: "\uD504\uB85C\uD544 \uC5C5\uB370\uC774\uD2B8 \uC644\uB8CC"
      },
      title: "\uD504\uB85C\uD544 \uC5C5\uB370\uC774\uD2B8"
    },
    placeholder: {
      email: "\uC774\uBA54\uC77C\uC744 \uC785\uB825\uD558\uC138\uC694",
      mbti: "MBTI\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      name: "\uC774\uB984\uC744 \uC785\uB825\uD558\uC138\uC694",
      phone: "\uC804\uD654\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694",
      language: "\uC5B8\uC5B4\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694"
    },
    profile_card: {
      title: "\uD504\uB85C\uD544 \uCE74\uB4DC",
      edit: "\uC218\uC815\uD558\uAE30"
    },
    profile_image: "\uD504\uB85C\uD544 \uC0AC\uC9C4",
    push_notifications: {
      title: "\uD478\uC2DC \uC54C\uB9BC",
      go_to_settings: "\uC54C\uB9BC\uC744 \uBC1B\uC73C\uB824\uBA74 \uAE30\uAE30 \uC124\uC815\uC5D0\uC11C \uC54C\uB9BC \uAD8C\uD55C\uC744 \uD5C8\uC6A9\uD574\uC8FC\uC138\uC694.",
      go_to_settings_title: "\uC54C\uB9BC \uAD8C\uD55C \uD544\uC694"
    },
    title: "\uD504\uB85C\uD544"
  },
  google_calendar: {
    title: "Google \uC5F0\uB3D9",
    status: {
      connected: "\uC5F0\uB3D9\uB428",
      reconnect_required: "\uC7AC\uC5F0\uB3D9 \uD544\uC694"
    },
    description: {
      connected: "\uCE98\uB9B0\uB354 \uC77C\uC815 \uB3D9\uAE30\uD654 \uBC0F \uD654\uC0C1 \uBBF8\uD305 \uB179\uC74C\uC744 \uC5F0\uB3D9\uD569\uB2C8\uB2E4",
      expired: "\uC5F0\uB3D9\uC774 \uB9CC\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC5F0\uB3D9\uD574\uC8FC\uC138\uC694.",
      disconnected: "Google \uC5F0\uB3D9\uC73C\uB85C \uCE98\uB9B0\uB354 \uB3D9\uAE30\uD654 \uBC0F \uD654\uC0C1 \uBBF8\uD305\uC744 \uC774\uC6A9\uD558\uC138\uC694"
    },
    connected_date: "\uC5F0\uB3D9\uC77C",
    connected_account: "{{email}} \uACC4\uC815\uC73C\uB85C \uC5F0\uB3D9\uB428",
    button: {
      connect: "\uC5F0\uB3D9\uD558\uAE30",
      disconnect: "\uC5F0\uB3D9 \uD574\uC81C"
    },
    notification: {
      connect_success: {
        title: "\uC5F0\uB3D9 \uC644\uB8CC",
        message: "Google \uC5F0\uB3D9\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
      },
      connect_error: {
        title: "\uC5F0\uB3D9 \uC2E4\uD328",
        message: "Google \uC5F0\uB3D9\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
      },
      disconnect_success: {
        title: "\uC5F0\uB3D9 \uD574\uC81C",
        message: "Google \uC5F0\uB3D9\uC774 \uD574\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
      },
      disconnect_error: {
        title: "\uC624\uB958",
        message: "\uC5F0\uB3D9 \uD574\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
      },
      login_error: {
        title: "\uB85C\uADF8\uC778 \uC2E4\uD328",
        message: "Google \uB85C\uADF8\uC778\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
      },
      user_not_found: {
        title: "\uC624\uB958",
        message: "\uC0AC\uC6A9\uC790 \uC815\uBCF4\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
      },
      token_expired: {
        title: "Google \uC5F0\uB3D9 \uB9CC\uB8CC",
        message: "Google \uC5F0\uB3D9\uC774 \uB9CC\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uC124\uC815\uC5D0\uC11C \uB2E4\uC2DC \uC5F0\uB3D9\uD574\uC8FC\uC138\uC694."
      }
    },
    account_mismatch: {
      title: "\uB2E4\uB978 Google \uACC4\uC815\uC785\uB2C8\uB2E4",
      existing_label: "\uD604\uC7AC \uC5F0\uB3D9\uB41C \uACC4\uC815",
      attempted_label: "\uC120\uD0DD\uD55C \uACC4\uC815",
      guide: "\uAC19\uC740 \uACC4\uC815\uC73C\uB85C \uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAC70\uB098, \uC124\uC815 \uD398\uC774\uC9C0\uC5D0\uC11C \uBA3C\uC800 \uC5F0\uB3D9\uC744 \uD574\uC81C\uD55C \uB4A4 \uB2E4\uB978 \uACC4\uC815\uC73C\uB85C \uC5F0\uB3D9\uD574\uC8FC\uC138\uC694.",
      go_to_settings: "\uC124\uC815\uC73C\uB85C \uC774\uB3D9",
      close: "\uB2EB\uAE30"
    }
  },
  signup_inquiry: {
    title: "\uAC00\uC785 \uBB38\uC758 \uBCF4\uB0B4\uAE30",
    description: "\uD68C\uC0AC \uC774\uBA54\uC77C\uC774 \uC5C6\uAC70\uB098 \uD68C\uC0AC \uC774\uBA54\uC77C\uC784\uC5D0\uB3C4 \uB9C9\uD614\uB2E4\uBA74 \uC54C\uB824\uC8FC\uC138\uC694. \uC601\uC5C5\uC77C \uAE30\uC900 2\uC77C \uB0B4\uB85C \uD68C\uC2E0\uB4DC\uB9B4\uAC8C\uC694.",
    field: {
      email: "\uC774\uBA54\uC77C",
      situation: "\uC5B4\uB5A4 \uC0C1\uD669\uC778\uAC00\uC694?",
      company: "\uD68C\uC0AC\uBA85",
      message: "\uBA54\uC2DC\uC9C0"
    },
    situation_option: {
      personal_email: "\uAC1C\uC778 \uC774\uBA54\uC77C\uB85C \uAC00\uC785\uD558\uACE0 \uC2F6\uC5B4\uC694",
      blocked_company_email: "\uD68C\uC0AC \uC774\uBA54\uC77C\uC778\uB370 \uAC00\uC785\uC774 \uC548 \uB3FC\uC694",
      etc: "\uAE30\uD0C0"
    },
    placeholder: {
      email: "example@company.com",
      company: "\uD68C\uC0AC\uBA85\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694 (\uC120\uD0DD)",
      message: "\uC804\uB2EC\uD558\uACE0 \uC2F6\uC740 \uB0B4\uC6A9\uC744 \uC790\uC720\uB86D\uAC8C \uC791\uC131\uD574\uC8FC\uC138\uC694 (\uC120\uD0DD)"
    },
    action: {
      submit: "\uBCF4\uB0B4\uAE30",
      cancel: "\uCDE8\uC18C"
    },
    error: {
      submit_failed: "\uC804\uC1A1\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      email_invalid: "\uC62C\uBC14\uB978 \uC774\uBA54\uC77C \uD615\uC2DD\uC774 \uC544\uB2C8\uC5D0\uC694."
    },
    complete: {
      title: "\uBB38\uC758\uAC00 \uC804\uB2EC\uB418\uC5C8\uC5B4\uC694",
      description: "\uD655\uC778 \uD6C4 \uBE60\uB974\uAC8C \uD68C\uC2E0\uB4DC\uB9B4\uAC8C\uC694. \uAC10\uC0AC\uD569\uB2C8\uB2E4.",
      confirm: "\uD655\uC778"
    }
  },
  onboarding: {
    logout: "\uB85C\uADF8\uC544\uC6C3",
    back: "\uB4A4\uB85C",
    intent: {
      title: "{{name}}\uB2D8, \uD658\uC601\uD574\uC694",
      title_fallback: "\uD658\uC601\uD574\uC694",
      subtitle: "\uC9C0\uAE08 \uC0C1\uD669\uC744 \uC54C\uB824\uC8FC\uC138\uC694. \uB9DE\uCDA4 \uD658\uACBD\uC744 \uC900\uBE44\uD574\uB4DC\uB9B4\uAC8C\uC694.",
      option_leader: {
        title: "\uC6B0\uB9AC \uD300\uC5D0 1on1\uC744 \uB3C4\uC785\uD574\uBCF4\uACE0 \uC2F6\uC5B4\uC694",
        description: "\uD300\uC744 \uC704\uD55C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uB9CC\uB4E4\uC5B4\uC694"
      },
      option_member: {
        title: "\uCD08\uB300\uB97C \uBC1B\uC544\uC11C \uAC00\uC785\uD574\uC694",
        description: "\uC774\uBBF8 \uC788\uB294 \uD300\uC5D0 \uD569\uB958\uD574\uC694"
      }
    },
    workspace_name: {
      title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984\uC744 \uC54C\uB824\uC8FC\uC138\uC694",
      subtitle: "\uD300\uC774\uB098 \uC870\uC9C1, \uD68C\uC0AC \uC774\uB984\uC744 \uCD94\uCC9C\uD574\uC694.",
      placeholder: "\uD300\uC774\uB098 \uC870\uC9C1 \uC774\uB984",
      helper_changeable: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC124\uC815\uC5D0\uC11C \uBCC0\uACBD\uD560 \uC218 \uC788\uC5B4\uC694.",
      helper_solo: "\uD300\uC6D0\uC744 \uCD08\uB300\uD558\uAE30 \uC804\uC5D0 \uD63C\uC790 \uC0AC\uC6A9\uD574\uBCF4\uC2E4 \uC218 \uC788\uC5B4\uC694",
      cta: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB9CC\uB4E4\uAE30",
      error_required: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
      error_create_failed: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    },
    member_waiting: {
      title: "\uCD08\uB300\uB97C \uAE30\uB2E4\uB9AC\uACE0 \uC788\uC5B4\uC694",
      body: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300\uAC00 \uB3C4\uCC29\uD558\uBA74 \uC774\uBA54\uC77C\uB85C \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694.",
      signup_email_label: "\uAC00\uC785\uD558\uC2E0 \uC774\uBA54\uC77C",
      actually_leader_question: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uB9CC\uB4E4\uACE0 \uC9C1\uC811 \uB458\uB7EC\uBCF4\uC2DC\uACA0\uC5B4\uC694?",
      actually_leader_cta: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB9CC\uB4E4\uAE30"
    }
  }
};

// src/locales/ko-KR/comment.json
var comment_default2 = {
  comment: {
    placeholder: "\uB313\uAE00\uC744 \uB0A8\uACA8\uC8FC\uC138\uC694",
    header_text: "\uB313\uAE00\uC744 \uB0A8\uACBC\uC2B5\uB2C8\uB2E4.",
    submit: "\uC785\uB825",
    no_comments: "\uC544\uC9C1 \uB313\uAE00\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uCC98\uC74C\uC73C\uB85C \uB313\uAE00\uC744 \uB0A8\uACA8\uBCF4\uC138\uC694!",
    no_user: "\uBA58\uC158\uD560 \uC218 \uC788\uB294 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4"
  },
  reply: {
    title: "\uB2F5\uAE00",
    add: "\uB2F5\uAE00 \uB0A8\uAE30\uAE30",
    placeholder: "\uB2F5\uAE00\uC744 \uB0A8\uACA8\uC8FC\uC138\uC694",
    header_text: "\uB2F5\uAE00\uC744 \uB0A8\uACBC\uC2B5\uB2C8\uB2E4."
  },
  notifications: {
    comment_success_title: "\uB313\uAE00 \uC791\uC131 \uC644\uB8CC",
    comment_success_message: "\uB313\uAE00\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC791\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    comment_error_title: "\uB313\uAE00 \uC791\uC131 \uC2E4\uD328",
    comment_error_message: "\uB313\uAE00 \uC791\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  modal: {
    delete: {
      title: "\uB313\uAE00 \uC0AD\uC81C",
      message: "\uC815\uB9D0\uB85C \uC774 \uB313\uAE00\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      reply_message: "\uC815\uB9D0\uB85C \uC774 \uB313\uAE00\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?\n\uB313\uAE00\uC5D0 \uB2EC\uB9B0 \uB2F5\uAE00\uB3C4 \uD568\uAED8 \uC0AD\uC81C\uB429\uB2C8\uB2E4."
    }
  }
};

// src/locales/ko-KR/note.json
var note_default2 = {
  title: "\uB178\uD2B8",
  create: {
    title: "\uB178\uD2B8 \uB9CC\uB4E4\uAE30",
    in_new_window: "\uC0C8 \uCC3D\uC5D0\uC11C \uB9CC\uB4E4\uAE30",
    success: "\uB178\uD2B8 \uC0DD\uC131 \uC131\uACF5",
    success_message: "\uC0C8 \uB178\uD2B8\uAC00 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    fail: "\uB178\uD2B8 \uC0DD\uC131 \uC2E4\uD328",
    fail_message: "\uB178\uD2B8 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
  },
  actions: {
    menu: "\uB178\uD2B8 \uBA54\uB274",
    export_pdf: "PDF\uB85C \uB0B4\uBCF4\uB0B4\uAE30",
    delete: "\uC0AD\uC81C",
    delete_note: "\uB178\uD2B8 \uC0AD\uC81C\uD558\uAE30",
    delete_title: "\uB178\uD2B8 \uC0AD\uC81C",
    delete_confirm: "\uC815\uB9D0\uB85C \uC774 \uB178\uD2B8\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    cancel: "\uCDE8\uC18C",
    open_new_window: "\uC0C8 \uCC3D\uC73C\uB85C \uC5F4\uAE30",
    notifications: {
      delete_success: "\uB178\uD2B8 \uC0AD\uC81C \uC644\uB8CC",
      delete_success_message: "\uB178\uD2B8\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      delete_fail: "\uB178\uD2B8 \uC0AD\uC81C \uC2E4\uD328",
      delete_fail_message: "\uB178\uD2B8 \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      export_fail: "\uB0B4\uBCF4\uB0B4\uAE30 \uC2E4\uD328",
      export_fail_message: "\uC5D0\uB514\uD130 \uC778\uC2A4\uD134\uC2A4\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
    }
  },
  pdf: {
    errors: {
      no_editor: "\uC5D0\uB514\uD130 \uC778\uC2A4\uD134\uC2A4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
      no_method: "blocksToFullHTML \uBA54\uC11C\uB4DC\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      unsupported: "HTML \uBCC0\uD658 \uAE30\uB2A5\uC744 \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      convert_fail: "\uB0B4\uC6A9\uC744 \uBCC0\uD658\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      module_load_fail: "html2pdf \uBAA8\uB4C8 \uB85C\uB4DC \uC2E4\uD328",
      unknown: "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958",
      pdf_create_fail: "PDF\uB97C \uC0DD\uC131\uD558\uB294 \uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4:",
      html_convert_fail: "\uC5D0\uB514\uD130 \uB0B4\uC6A9\uC744 HTML\uB85C \uBCC0\uD658\uD558\uB294 \uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4:",
      container_remove_fail: "\uCEE8\uD14C\uC774\uB108 \uC81C\uAC70 \uC911 \uC624\uB958:"
    },
    notifications: {
      export_success: "PDF \uB0B4\uBCF4\uB0B4\uAE30 \uC131\uACF5",
      export_success_message: "{{title}}.pdf \uD30C\uC77C\uC774 \uB2E4\uC6B4\uB85C\uB4DC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      export_fail: "PDF \uB0B4\uBCF4\uB0B4\uAE30 \uC2E4\uD328",
      convert_fail: "PDF \uBCC0\uD658 \uC911 \uC624\uB958:"
    }
  },
  auth: {
    required: "\uC778\uC99D \uD544\uC694",
    login_required: "\uB178\uD2B8\uB97C \uC0DD\uC131\uD558\uB824\uBA74 \uB85C\uADF8\uC778\uC774 \uD544\uC694\uD569\uB2C8\uB2E4."
  },
  info: {
    required: "\uC815\uBCF4 \uD544\uC694",
    id_required: "Task ID \uB610\uB294 To-do ID\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4."
  },
  ai: {
    summary: {
      title: "AI \uC694\uC57D",
      generating: "\uC694\uC57D \uC0DD\uC131 \uC911...",
      regenerate: "\uC694\uC57D \uB2E4\uC2DC \uC0DD\uC131",
      regenerate_message: "\uC694\uC57D\uC774 \uC0DD\uC131\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      error: "\uC694\uC57D \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB098\uC911\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    },
    translate: {
      button: "\uBC88\uC5ED"
    },
    chat: {
      search_placeholder: "\uC6F9 \uAC80\uC0C9\uC73C\uB85C \uC9C8\uBB38\uD558\uAE30...",
      ask_placeholder: "\uC9C8\uBB38\uD558\uAE30...",
      disable_search: "\uC6F9 \uAC80\uC0C9 \uBAA8\uB4DC \uD574\uC81C",
      enable_search: "\uC6F9 \uAC80\uC0C9\uC73C\uB85C \uC9C8\uBB38\uD558\uAE30",
      send: "\uC804\uC1A1",
      search_preparing: "\uAC80\uC0C9 \uC900\uBE44 \uC911...",
      searching: "\uAC80\uC0C9\uC911",
      search_complete: "\uAC80\uC0C9 \uC644\uB8CC",
      error: "\uC8C4\uC1A1\uD569\uB2C8\uB2E4. \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    share: {
      no_title: "\uC81C\uBAA9 \uC5C6\uC74C",
      share_suffix: "\uB178\uD2B8 \uB9C1\uD06C\uB97C \uACF5\uC720\uD569\uB2C8\uB2E4",
      new_link: "\uC0C8 \uB9C1\uD06C",
      copy: "\uBCF5\uC0AC",
      copied: "\uBCF5\uC0AC\uB428",
      share_button: "\uB9C1\uD06C \uACF5\uC720\uD558\uAE30",
      generating: "\uC694\uC57D \uC0DD\uC131 \uC911...",
      modal_title: "\uB178\uD2B8 \uACF5\uC720",
      url_share: "URL \uACF5\uC720\uD558\uAE30"
    },
    email: {
      send: "\uC694\uC57D \uBA54\uC77C \uC804\uC1A1",
      generating: "\uC694\uC57D \uC0DD\uC131 \uC911...",
      error: "Gmail \uCC3D\uC744 \uC5F4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC624\uB958",
      greeting: "\uC548\uB155\uD558\uC138\uC694, {{name}} \uC785\uB2C8\uB2E4.",
      body: "{{title}} \uB178\uD2B8 \uC694\uC57D\uACFC \uB9C1\uD06C \uCCA8\uBD80\uD558\uC5EC \uBCF4\uB0B4\uB4DC\uB9BD\uB2C8\uB2E4.",
      link_text: "\uB178\uD2B8 \uB9C1\uD06C",
      thanks: "\uAC10\uC0AC\uD569\uB2C8\uB2E4.",
      from: "{{name}} \uB4DC\uB9BC."
    },
    task: {
      create: "\uC77C\uC815 \uB9CC\uB4E4\uAE30"
    }
  }
};

// src/locales/ko-KR/activity.json
var activity_default2 = {
  title: "\uD65C\uB3D9",
  and: " \uBC0F ",
  actor: "<0>{{name}}</0><1> \uB2D8\uC774 </1>",
  set: "<0>{{label}}</0><1>{{action}} </1><0>{{value}}</0><1>(\uC73C)\uB85C \uC124\uC815\uD588\uC2B5\uB2C8\uB2E4.</1>",
  change: "<0>{{label}}</0><1>{{action}} </1><0>{{value}}</0><1>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.</1>",
  set_label: "<0>{{label}}</0><1>(\uC73C)\uB85C \uC124\uC815\uD588\uC2B5\uB2C8\uB2E4.</1>",
  change_label: "<0>{{label}}</0><1>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.</1>",
  delete: "<0>{{label}}</0><1>\uB97C \uC0AD\uC81C\uD588\uC2B5\uB2C8\uB2E4.</1>",
  update: "<0>{{label}}</0><1>{{action}}</1> <0>{{value}}</0><1>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.</1>",
  task: {
    create: "\uD0DC\uC2A4\uD06C\uB97C \uC0DD\uC131\uD588\uC2B5\uB2C8\uB2E4.",
    delete: "\uD0DC\uC2A4\uD06C\uB97C \uC0AD\uC81C\uD588\uC2B5\uB2C8\uB2E4."
  },
  indicator: {
    default: "\uC9C0\uD45C \uC815\uBCF4\uB97C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.",
    add: "<0>\uC9C0\uD45C</0><1>\uB97C \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4.</1>",
    update: "<0>\uC9C0\uD45C</0><1>\uB97C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.</1>"
  },
  share: {
    fallback_text: {
      changed: "\uD0DC\uC2A4\uD06C \uACF5\uC720 \uC0C1\uD0DC\uAC00 \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      share: "\uD0DC\uC2A4\uD06C\uB97C \uD300\uC5D0 \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4.",
      unshare: "\uD300\uC5D0 \uD0DC\uC2A4\uD06C \uACF5\uC720\uB97C \uD574\uC81C\uD588\uC2B5\uB2C8\uB2E4."
    },
    share_start: " ",
    share_end: " \uD300\uC5D0 \uD0DC\uC2A4\uD06C\uB97C \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4.",
    unshare_start: " ",
    unshare_end: " \uD300\uC5D0 \uD0DC\uC2A4\uD06C \uACF5\uC720\uB97C \uD574\uC81C\uD588\uC2B5\uB2C8\uB2E4."
  },
  due_date: {
    delete: "<0>\uB9C8\uAC10\uAE30\uD55C</0><1>\uC744 \uC0AD\uC81C\uD588\uC2B5\uB2C8\uB2E4.</1>",
    update: "<0>\uB9C8\uAC10\uAE30\uD55C</0><1>\uC744 </1><0>{{value}}</0><1>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4.</1>"
  },
  remove_project: "<0>\uD504\uB85C\uC81D\uD2B8</0><1> \uC5F0\uACB0\uC744 \uD574\uC81C\uD588\uC2B5\uB2C8\uB2E4.</1>",
  note: {
    create: "<0>\uB178\uD2B8</0><1>\uB97C \uB9CC\uB4E4\uC5C8\uC2B5\uB2C8\uB2E4.</1>"
  }
};

// src/locales/ko-KR/workspace.json
var workspace_default2 = {
  component: {
    add_button: {
      create: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB9CC\uB4E4\uAE30",
      description: "\uC0C8\uB85C\uC6B4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0DD\uC131\uD558\uC5EC<br />\uD300\uACFC \uD504\uB85C\uC81D\uD2B8\uB97C \uAD00\uB9AC\uD558\uC138\uC694"
    },
    item: {
      create_new: "\uC0C8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB9CC\uB4E4\uAE30",
      view_invitations: "\uCD08\uB300 \uD655\uC778 ({{count}})",
      no_teams: "\uD300\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
      performance: "\uC131\uACFC\uAD00\uB9AC",
      report: {
        title: "\uBCF4\uACE0\uC11C",
        team: "\uD300 \uBCF4\uACE0\uC11C",
        personal: "\uAC1C\uC778 \uBCF4\uACE0\uC11C"
      },
      feedback: "\uD53C\uB4DC\uBC31",
      title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
      setting: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC124\uC815",
      create_team: "\uD300 \uB9CC\uB4E4\uAE30"
    },
    main_info: {
      image_update_success: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC131\uACF5:",
      image_update_error: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328:",
      name_update_success: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984 \uC5C5\uB370\uC774\uD2B8 \uC131\uACF5:",
      name_update_error: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328:"
    },
    create_dialog: {
      title: "\uC0C8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131",
      name_label: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984",
      name_placeholder: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984\uC744 \uC785\uB825\uD558\uC138\uC694",
      name_required: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
      description_label: "\uC124\uBA85",
      description_placeholder: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uB300\uD55C \uC124\uBA85\uC744 \uC785\uB825\uD558\uC138\uC694",
      cancel_button: "\uCDE8\uC18C",
      create_button: "\uC0DD\uC131",
      success_title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uC644\uB8CC",
      success_message: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      error_title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uC2E4\uD328",
      error_message: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      warn_title: "\uBA64\uBC84 \uD65C\uC131\uD654\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4.",
      warn_message: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC138\uD305 \uD398\uC774\uC9C0\uC5D0\uC11C '\uD65C\uC131' \uD1A0\uAE00\uC744 \uCF1C\uC8FC\uC138\uC694!"
    }
  },
  setting: {
    referral: {
      title: "\uCD94\uCC9C \uB9C1\uD06C",
      description: "\uC774 \uB9C1\uD06C\uB85C \uAC00\uC785\uD55C \uBD84\uC774 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uB9CC\uB4E4\uBA74 \uC774 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0\uC11C \uBBF8\uD305\uC744 3\uD68C \uB354 \uBD84\uC11D\uD560 \uC218 \uC788\uC5B4\uC694.",
      copy_button: "\uB9C1\uD06C \uBCF5\uC0AC",
      copied: "\uBCF5\uC0AC\uB428"
    },
    general: {
      workspace_name: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4"
    },
    not_found: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC815\uBCF4",
    member_remove_modal: {
      title: "\uBA64\uBC84 \uC0AD\uC81C",
      confirm_message: "{{name}}\uB2D8\uC744 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0\uC11C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description: "\uC0AD\uC81C\uB41C \uBA64\uBC84\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBC0F \uAD00\uB828 \uD300\uC5D0 \uB354 \uC774\uC0C1 \uC811\uADFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.<br />\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC640 \uC5F0\uACB0\uB41C \uD22C\uB450 \uC815\uBCF4\uB294 \uC774\uD6C4\uC5D0\uB3C4 \uD574\uB2F9 \uBA64\uBC84\uC758 \uD50C\uB798\uB108\uC5D0 \uB0A8\uC9C0\uB9CC,<br />\uC0AD\uC81C \uBA64\uBC84\uB294 \uC774\uB97C \uC218\uC815 \uBC0F \uC0AD\uC81C\uD560 \uC218 \uC5C6\uC73C\uBA70 \uB178\uD2B8 \uB610\uD55C \uC5F4\uB78C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    invitation_cancel_modal: {
      title: "\uCD08\uB300 \uCDE8\uC18C",
      confirm_message: "{{email}}\uB2D8\uC5D0\uAC8C \uBCF4\uB0B8 \uCD08\uB300\uB97C \uCDE8\uC18C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description: "\uCD08\uB300\uAC00 \uCDE8\uC18C\uB418\uBA74 \uC0AC\uC6A9\uC790\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCC38\uC5EC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      confirm: "\uCDE8\uC18C\uD558\uAE30"
    },
    loading_state: {
      loading_message: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uB294 \uC911..."
    },
    member_table: {
      title: "\uBA64\uBC84",
      invite_button: "\uBA64\uBC84 \uCD08\uB300",
      name: "\uC774\uB984",
      team: "\uC18C\uC18D \uD300",
      role: "\uC5ED\uD560",
      status: "\uC0C1\uD0DC",
      manager: "\uB9E4\uB2C8\uC800",
      access_denied: "\uC811\uADFC \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      active: "\uD65C\uC131",
      description: "\uB514\uC2A4\uD06C\uB9BD\uC158",
      enter_description: "\uC9C1\uBB34, \uC5F0\uCC28 \uB4F1\uC744 \uC785\uB825\uD558\uC138\uC694.",
      pending: "\uB300\uAE30 \uC911",
      cancel_invitation: "\uCD08\uB300 \uCDE8\uC18C",
      remove_member: "\uBA64\uBC84 \uC0AD\uC81C",
      loading_message: "\uBA64\uBC84 \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911...",
      empty_message: "\uD604\uC7AC \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
      invited_user: "\uCD08\uB300\uB41C \uC0AC\uC6A9\uC790",
      invited: "\uCD08\uB300\uB428",
      select_manager: "\uB9E4\uB2C8\uC800 \uC120\uD0DD",
      filter_team: "\uD300 \uD544\uD130"
    },
    bulk_action: {
      selected_count: "{{count}}\uBA85 \uC120\uD0DD\uB428",
      change_leader: "\uB9E4\uB2C8\uC800 \uC77C\uAD04 \uBCC0\uACBD",
      change_role: "\uC5ED\uD560 \uC77C\uAD04 \uBCC0\uACBD",
      deselect: "\uC120\uD0DD \uD574\uC81C",
      select_role: "\uBCC0\uACBD\uD560 \uC5ED\uD560 \uC120\uD0DD",
      select_leader: "\uC9C0\uC815\uD560 \uB9E4\uB2C8\uC800 \uC120\uD0DD",
      cancel: "\uCDE8\uC18C",
      apply: "\uC801\uC6A9",
      success: "\uC77C\uAD04 \uBCC0\uACBD \uC644\uB8CC",
      error: "\uC77C\uAD04 \uBCC0\uACBD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4"
    },
    team_table: {
      title: "\uD300",
      create_button: "\uD300 \uC0DD\uC131",
      name: "\uC774\uB984",
      member_count: "\uBA64\uBC84",
      project_count: "\uD504\uB85C\uC81D\uD2B8",
      delete_team: "\uD300 \uC0AD\uC81C",
      add_member: "\uBA64\uBC84 \uCD94\uAC00",
      empty: "\uD300\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
      empty_message: "\uD300\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
      loading_message: "\uD300 \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911...",
      edit: "\uD300 \uAD00\uB9AC",
      edit_member: "\uBA64\uBC84 \uAD00\uB9AC",
      expand_all: "\uBAA8\uB450 \uD3BC\uCE58\uAE30",
      collapse_all: "\uBAA8\uB450 \uC811\uAE30"
    },
    tab_container: {
      members: "\uBA64\uBC84",
      teams: "\uD300",
      member: "\uBA64\uBC84 \uAD00\uB9AC",
      team: "\uD300 \uAD00\uB9AC",
      invite_member: "\uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
      create_team: "\uD300 \uB9CC\uB4E4\uAE30",
      custom: "\uCEE4\uC2A4\uD140"
    },
    custom: {
      analysis_visibility: {
        title: "\uBBF8\uD305 \uC774\uD6C4 \uB9AC\uB354 \uD53C\uB4DC\uBC31",
        description: "\uBD84\uC11D \uD0ED\uC758 \uB9AC\uB354 \uD53C\uB4DC\uBC31\uC744 \uB204\uAD6C\uC5D0\uAC8C \uBCF4\uC5EC\uC904\uC9C0 \uC815\uD569\uB2C8\uB2E4. \uBC1C\uD654 \uBE44\uC728\uACFC \uBA64\uBC84 \uD53C\uB4DC\uBC31\uC740 \uC774 \uC124\uC815\uACFC \uBB34\uAD00\uD569\uB2C8\uB2E4. \uBCC0\uACBD \uC2DC \uACFC\uAC70 \uBBF8\uD305\uC744 \uD3EC\uD568\uD55C \uBAA8\uB4E0 \uBBF8\uD305\uC5D0 \uC989\uC2DC \uC801\uC6A9\uB429\uB2C8\uB2E4.",
        leader_only: "\uBBF8\uD305 \uB9AC\uB354\uC5D0\uAC8C\uB9CC \uB178\uCD9C",
        both: "\uBBF8\uD305 \uB9AC\uB354\uC640 \uBA64\uBC84 \uBAA8\uB450\uC5D0\uAC8C \uB178\uCD9C",
        per_leader: "\uB9AC\uB354\uBCC4 \uAC1C\uBCC4 \uC124\uC815",
        per_leader_checkbox: "\uB0B4\uAC00 \uB9AC\uB354\uB85C\uC11C \uC9C4\uD589\uD55C \uBBF8\uD305\uC758 \uB9AC\uB354 \uD53C\uB4DC\uBC31\uC744 \uBA64\uBC84\uB4E4\uC5D0\uAC8C \uACF5\uAC1C\uD569\uB2C8\uB2E4.",
        save_success: "\uB9AC\uB354 \uD53C\uB4DC\uBC31 \uACF5\uAC1C \uC124\uC815\uC774 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        save_error: "\uC124\uC815 \uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
      },
      question_draft: {
        title: "\uC9C8\uBB38 \uCD08\uC548 \uBAA8\uB4DC",
        description: "\uC124\uC815 \uBCC0\uACBD \uC2DC, \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB0B4 \uBAA8\uB4E0 \uBBF8\uD305\uC5D0 \uC989\uC2DC \uC801\uC6A9\uB429\uB2C8\uB2E4.",
        default_mode: "\uAE30\uBCF8 \uBAA8\uB4DC: \uC791\uC131\uD55C \uC9C8\uBB38\uC774 \uC0C1\uB300\uBC29\uC5D0\uAC8C \uC2E4\uC2DC\uAC04\uC73C\uB85C \uACF5\uAC1C\uB429\uB2C8\uB2E4.",
        draft_mode: "\uCD08\uC548 \uBAA8\uB4DC: \uC791\uC131 \uD6C4 '\uC9C8\uBB38 \uACF5\uAC1C'\uB97C \uB20C\uB7EC\uC57C \uB0B4 \uC9C8\uBB38\uC774 \uC0C1\uB300\uBC29\uC5D0\uAC8C \uACF5\uAC1C\uB429\uB2C8\uB2E4.",
        info_title: "\uC9C8\uBB38 \uCD08\uC548 \uBAA8\uB4DC \uC548\uB0B4",
        info_1: "\uC9C8\uBB38\uC744 \uBAA8\uB450 \uC815\uB9AC\uD55C \uB4A4 \uD55C\uAEBC\uBC88\uC5D0 \uACF5\uAC1C\uD558\uACE0 \uC2F6\uC744 \uB54C \uC720\uC6A9\uD55C \uBAA8\uB4DC\uC785\uB2C8\uB2E4.",
        info_2: "\uACF5\uAC1C \uC2DC\uC810: \uB0B4\uAC00 '\uC9C8\uBB38 \uACF5\uAC1C'\uB97C \uB20C\uB7EC\uB3C4, \uC0C1\uB300\uBC29\uC774 \uACF5\uAC1C\uD558\uC9C0 \uC54A\uC740 \uC9C8\uBB38(\uCD08\uC548)\uC740 \uB098\uC5D0\uAC8C \uBCF4\uC774\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
        info_3: "\uC790\uB3D9 \uACF5\uAC1C: \uC9C8\uBB38\uC744 \uACF5\uAC1C\uD558\uC9C0 \uC54A\uC558\uB354\uB77C\uB3C4, '\uBBF8\uD305 \uC2DC\uC791' \uBC84\uD2BC\uC744 \uB204\uB974\uBA74 \uBAA8\uB4E0 \uCD08\uC548\uC774 \uC0C1\uB300\uBC29\uC5D0\uAC8C \uC989\uC2DC \uACF5\uAC1C\uB429\uB2C8\uB2E4.",
        info_4: "\uACF5\uAC1C \uC774\uD6C4: \uD55C \uBC88 \uACF5\uAC1C\uD55C \uB4A4\uC5D0 \uCD94\uAC00\uD558\uAC70\uB098 \uC218\uC815\uD55C \uC9C8\uBB38\uC740 \uC2E4\uC2DC\uAC04\uC73C\uB85C \uC11C\uB85C\uC5D0\uAC8C \uACF5\uAC1C\uB429\uB2C8\uB2E4.",
        save_success: "\uC9C8\uBB38 \uCD08\uC548 \uBAA8\uB4DC \uC124\uC815\uC774 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        save_error: "\uC124\uC815 \uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
      }
    },
    warning: {
      delete_title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C\uD558\uAE30",
      delete_description: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD558\uBA74 \uBAA8\uB4E0 \uD504\uB85C\uC81D\uD2B8\uC640 \uD0DC\uC2A4\uD06C\uAC00 \uC0AD\uC81C\uB429\uB2C8\uB2E4.\n\uBA64\uBC84\uB4E4\uC758 \uD560 \uC77C\uB4E4\uC740 \uD0DC\uC2A4\uD06C\uC640\uC758 \uC5F0\uACB0\uC774 \uB04A\uAE34\uCC44\uB85C \uC0AC\uB77C\uC9C0\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
      delete_description_1on1: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD558\uBA74 \uBAA8\uB4E0 \uBA64\uBC84\uC758 \uC811\uADFC\uC774 \uC911\uB2E8\uB418\uACE0 1on1 \uAE30\uB85D\uC744 \uB354 \uC774\uC0C1 \uC774\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      delete_button: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C\uD558\uAE30",
      tooltip: "\uC815\uB9D0\uB85C \uC0AD\uC81C\uD558\uC2DC\uB098\uC694? \u{1F97A}"
    },
    delete_modal: {
      title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C \uD655\uC778",
      message: "\uC815\uB9D0\uB85C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description: "\uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC73C\uBA70, \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 \uBAA8\uB4E0 \uD504\uB85C\uC81D\uD2B8\uC640 \uD0DC\uC2A4\uD06C\uAC00 \uC601\uAD6C\uC801\uC73C\uB85C \uC0AD\uC81C\uB429\uB2C8\uB2E4.",
      description_1on1: "\uBAA8\uB4E0 \uBA64\uBC84\uC758 \uC811\uADFC\uC774 \uC989\uC2DC \uC911\uB2E8\uB418\uACE0 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 1on1 \uAE30\uB85D\uC744 \uB354 \uC774\uC0C1 \uC774\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC0AD\uC81C \uD6C4 \uBCF5\uAD6C\uB294 \uC9C0\uC6D0\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
      member_warning: "\uBCF8\uC778 \uC678 \uD65C\uC131 \uBA64\uBC84 {{count}}\uBA85\uC758 \uC811\uADFC\uB3C4 \uD568\uAED8 \uC911\uB2E8\uB429\uB2C8\uB2E4.",
      name_confirmation_label: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC774\uB984",
      name_confirmation_description: "\uACC4\uC18D\uD558\uB824\uBA74 {{name}}\uC744(\uB97C) \uC815\uD655\uD788 \uC785\uB825\uD558\uC138\uC694.",
      confirm: "\uC0AD\uC81C",
      error_title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C \uC2E4\uD328",
      error_message: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
      subscription_warning: "\uAD6C\uB3C5\uC774 \uC644\uC804\uD788 \uC885\uB8CC\uB418\uAE30 \uC804\uC5D0\uB294 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uBA3C\uC800 \uAD6C\uB3C5\uC744 \uCDE8\uC18C\uD55C \uD6C4 {{endDate}} \uC774\uD6C4\uC5D0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694. \uD604\uC7AC {{memberCount}}\uBA85\uC774 \uC774\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      subscription_inquiry: "\uAD00\uB828 \uBB38\uC758\uC0AC\uD56D\uC774 \uC788\uB2E4\uBA74 jjchoi@reversemountain.co.kr\uB85C \uBB38\uC758 \uBD80\uD0C1\uB4DC\uB9BD\uB2C8\uB2E4."
    },
    access_denied: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uB9CC \uC811\uADFC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    transfer_ownership: {
      title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC624\uB108 \uAD8C\uD55C \uC704\uC784",
      message: "\uC624\uB108 \uAD8C\uD55C\uC744 \uC704\uC784\uD558\uBA74 \uB2E4\uC74C\uACFC \uAC19\uC740 \uBCC0\uACBD\uC774 \uBC1C\uC0DD\uD569\uB2C8\uB2E4:",
      warning_list: {
        item1: "\uC120\uD0DD\uD55C \uBA64\uBC84\uAC00 \uC0C8\uB85C\uC6B4 \uC624\uB108\uAC00 \uB429\uB2C8\uB2E4",
        item2: "\uADC0\uD558\uC758 \uAD8C\uD55C\uC740 \uAD00\uB9AC\uC790(Admin)\uB85C \uBCC0\uACBD\uB429\uB2C8\uB2E4",
        item3: "\uB2E4\uC2DC \uC624\uB108\uAC00 \uB418\uB824\uBA74 \uC0C8\uB85C\uC6B4 \uC624\uB108\uC5D0\uAC8C \uC694\uCCAD\uD574\uC57C \uD569\uB2C8\uB2E4"
      },
      confirm_message: "{{name}}\uB2D8\uC5D0\uAC8C \uC624\uB108 \uAD8C\uD55C\uC744 \uC704\uC784\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      success: "\uC624\uB108 \uAD8C\uD55C\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC704\uC784\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
      error: "\uC624\uB108 \uAD8C\uD55C \uC704\uC784\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
      select_new_owner: "\uC0C8 \uC624\uB108 \uC120\uD0DD",
      confirm_button: "\uAD8C\uD55C \uC704\uC784"
    }
  },
  error: {
    invite_failed_title: "\uCD08\uB300 \uC2E4\uD328",
    unauthorized_user: "\uC778\uC99D\uB41C \uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    delete_failed: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD560 \uC218 \uC5C6\uAC70\uB098 \uC774\uBBF8 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    delete_blocked_by_subscription: "\uD65C\uC131 \uB610\uB294 \uCDE8\uC18C \uC608\uC815\uC778 \uAD6C\uB3C5\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uAD6C\uB3C5\uC774 \uC644\uC804\uD788 \uC885\uB8CC\uB41C \uD6C4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0AD\uC81C\uD574 \uC8FC\uC138\uC694.",
    create_failed: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    data_not_found_after_create: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uD6C4 \uB370\uC774\uD130\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    unknown_create_error: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uC911 \uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    workspace_not_found: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    user_not_logged_in: "\uC0AC\uC6A9\uC790\uAC00 \uB85C\uADF8\uC778\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    failed_to_get_workspace_list: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBAA9\uB85D\uC744 \uAC00\uC838\uC624\uB294\uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    update_failed: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC5C5\uB370\uC774\uD2B8\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    data_not_found_after_update: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC5C5\uB370\uC774\uD2B8 \uD6C4 \uB370\uC774\uD130\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    unknown_update_error: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC5C5\uB370\uC774\uD2B8 \uC911 \uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    user_not_found: "\uCD08\uB300\uD560 \uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    user_not_found_for_invitation: "\uCD08\uB300\uD560 \uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    user_already_member: "\uC774\uBBF8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uAC00\uC785\uB41C \uC0AC\uC6A9\uC790\uC785\uB2C8\uB2E4.",
    already_member: "\uC774\uBBF8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uAC00\uC785\uB41C \uC0AC\uC6A9\uC790\uC785\uB2C8\uB2E4.",
    cannot_check_team_access: "\uD300 \uC811\uADFC \uAD8C\uD55C\uC744 \uD655\uC778\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    access_check_failed: "\uC811\uADFC \uAD8C\uD55C \uD655\uC778 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    transfer_ownership_failed: "\uC624\uB108 \uAD8C\uD55C \uC591\uB3C4 \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  }
};

// src/locales/ko-KR/planner.json
var planner_default2 = {
  page: {
    title: "\uD50C\uB798\uB108 \uD398\uC774\uC9C0"
  },
  item: {
    description: "\uC124\uBA85",
    comment: "\uB313\uAE00",
    note: "\uB178\uD2B8"
  },
  calendar: {
    title: "\uCE98\uB9B0\uB354"
  },
  todo: {
    selectTeam: "\uD300\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    markAsComplete: "\uC644\uB8CC\uB85C \uD45C\uC2DC",
    todayPlanned: "\uC624\uB298 \uACC4\uD68D\uD55C \uD560 \uC77C",
    addTodo: "\uC624\uB298 \uD560 \uC77C \uCD94\uAC00",
    adding: "\uCD94\uAC00\uD558\uB294 \uC911...",
    setDate: "\uB0A0\uC9DC \uC9C0\uC815",
    incomplete: "\uB05D\uB0B4\uC9C0 \uBABB\uD55C \uD560 \uC77C",
    updating: "\uD560 \uC77C \uC5C5\uB370\uC774\uD2B8 \uC911",
    pleaseWait: "\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694...",
    updateFailed: "\uD560 \uC77C \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328",
    updateError: "\uD560 \uC77C \uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    updateComplete: "\uD560 \uC77C \uC5C5\uB370\uC774\uD2B8 \uC644\uB8CC",
    addedToDate: "\uC120\uD0DD\uD558\uC2E0 \uB0A0\uC9DC\uC5D0 \uD560 \uC77C\uC744 \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4."
  },
  timeBlock: {
    collapse: "\uC811\uAE30",
    expand: "\uD3BC\uCE58\uAE30",
    viewMore: "{{count}}\uAC1C \uB354 \uBCF4\uAE30"
  },
  dragAndDrop: {
    notifications: {
      updateAction: "\uD560 \uC77C \uC5C5\uB370\uC774\uD2B8",
      timeUpdateSuccess: "\uD560 \uC77C\uC758 \uC2DC\uAC04 \uC815\uBCF4\uB97C \uC131\uACF5\uC801\uC73C\uB85C \uC5C5\uB370\uC774\uD2B8\uD588\uC2B5\uB2C8\uB2E4.",
      timeChangeImpossible: "\uC2DC\uAC04 \uBCC0\uACBD \uBD88\uAC00",
      timeSetImpossible: "\uC2DC\uAC04 \uC9C0\uC815 \uBD88\uAC00",
      timeOccupied: "\uD574\uB2F9 \uC2DC\uAC04\uC5D0\uB294 \uC774\uBBF8 \uB2E4\uB978 \uC77C\uC815\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
      addToPlanner: "\uC624\uB298 \uD560 \uC77C\uB85C \uCD94\uAC00",
      addToPlannerSuccess: "\uC120\uD0DD\uD55C \uC2DC\uAC04\uC5D0 \uD560 \uC77C\uC744 \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4.",
      addToTodayList: "\uC624\uB298 \uD560 \uC77C \uB9AC\uC2A4\uD2B8\uC5D0 \uCD94\uAC00",
      addToTodayListSuccess: "\uC624\uB298 \uD560 \uC77C \uB9AC\uC2A4\uD2B8\uC5D0 \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4.",
      changeGoal: "\uC5F0\uACB0\uB41C \uD0DC\uC2A4\uD06C \uBCC0\uACBD",
      changeGoalSuccess: "\uD560 \uC77C\uC758 \uD0DC\uC2A4\uD06C\uB97C \uBCC0\uACBD\uD588\uC2B5\uB2C8\uB2E4."
    }
  },
  error: {
    loadFailed: "\uD50C\uB798\uB108\uB97C \uBD88\uB7EC\uC624\uB294 \uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
  }
};

// src/locales/ko-KR/member_invitation.json
var member_invitation_default2 = {
  invitation_page: {
    already_responded: "\uC774\uBBF8 \uC751\uB2F5\uD558\uC168\uC2B5\uB2C8\uB2E4.",
    current_account: "\uD604\uC7AC \uB85C\uADF8\uC778\uB41C \uACC4\uC815",
    expired_invitation: "\uCD08\uB300\uAC00 \uCDE8\uC18C\uB418\uC5C8\uAC70\uB098, \uC874\uC7AC\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    go_home: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
    home: "\uD648\uC73C\uB85C",
    loading: "\uB85C\uB529 \uC911...",
    logout: "\uB85C\uADF8\uC544\uC6C3"
  },
  invitation_response: {
    accept_completed: "\uCD08\uB300 \uC218\uB77D \uC644\uB8CC",
    accept_failed: "\uCD08\uB300 \uC218\uB77D \uC2E4\uD328",
    email_mismatch: "\uCD08\uB300 \uBC1B\uC740 \uC774\uBA54\uC77C\uACFC \uB85C\uADF8\uC778 \uC774\uBA54\uC77C\uC774 \uB2E4\uB985\uB2C8\uB2E4.",
    error_during_acceptance: "\uCD08\uB300 \uC218\uB77D \uC911 \uC624\uB958:",
    error_occurred: "\uCC98\uB9AC \uC911 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    joined_workspace: "\uCD08\uB300\uB97C \uC218\uB77D\uD558\uC5EC \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCC38\uC5EC\uD558\uAC8C \uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    participation_failed: "\uCC38\uC5EC \uC911 \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    reject_completed: "\uCD08\uB300 \uAC70\uC808 \uC644\uB8CC",
    reject_failed: "\uCD08\uB300 \uAC70\uC808 \uC2E4\uD328",
    rejected: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300\uB97C \uAC70\uC808\uD588\uC2B5\uB2C8\uB2E4."
  },
  invite_results: {
    all_already_members: "\uBAA8\uB4E0 \uC774\uBA54\uC77C\uC774 \uC774\uBBF8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uC785\uB2C8\uB2E4",
    all_failed: "\uCD08\uB300 \uC2E4\uD328",
    all_failed_message: "\uBAA8\uB4E0 \uCD08\uB300\uAC00 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
    all_sent: "{{count}}\uBA85\uC5D0\uAC8C \uCD08\uB300\uB97C \uC804\uC1A1\uD588\uC2B5\uB2C8\uB2E4",
    already_invited: "\uC774\uBBF8 \uCD08\uB300\uD55C \uACC4\uC815\uC785\uB2C8\uB2E4.",
    already_member: "{{email}}\uC740 \uBA64\uBC84\uB85C \uC774\uBBF8 \uB4F1\uB85D\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.",
    auth_required: "\uB85C\uADF8\uC778 \uC815\uBCF4\uAC00 \uB9CC\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uB85C\uADF8\uC778 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    copied: "\uBCF5\uC0AC\uB428!",
    copy: "\uBCF5\uC0AC",
    default_inviter: "\uCD08\uB300\uC790",
    email_failed_copy_link: "\uC774\uBA54\uC77C \uBC1C\uC1A1\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uC544\uB798 \uCD08\uB300 \uB9C1\uD06C\uB97C \uBCF5\uC0AC\uD558\uC5EC \uC9C1\uC811 \uACF5\uC720\uD574\uC8FC\uC138\uC694.",
    email_required: "\uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694",
    email_send_failed_see_link: "\uC774\uBA54\uC77C \uBC1C\uC1A1\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uCD08\uB300 \uB9C1\uD06C\uB97C \uBCF5\uC0AC\uD574 \uC9C1\uC811 \uACF5\uC720\uD574\uC8FC\uC138\uC694.",
    failed_message: "\uCD08\uB300\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
    invalid_email_error: "\uCD08\uB300 \uC2E4\uD328",
    invalid_email_message: "\uC720\uD6A8\uD55C \uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694",
    invitation_link: "\uCD08\uB300 \uB9C1\uD06C",
    invitation_sent: "{{email}}\uC73C\uB85C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300\uB97C \uC804\uC1A1\uD588\uC2B5\uB2C8\uB2E4",
    invite_failure: "{{email}} \uCD08\uB300 \uC2E4\uD328: {{errorMessage}}",
    invite_success: "{{email}} \uCD08\uB300 \uC131\uACF5",
    invite_waiting: "\uCD08\uB300 \uB300\uAE30",
    member_invited: "\uBA64\uBC84\uB97C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4",
    members_invited: "{{count}}\uBA85\uC758 \uBA64\uBC84\uB97C {{targetName}}\uC5D0 \uCD08\uB300\uD588\uC2B5\uB2C8\uB2E4",
    partial_failure: "\uC77C\uBD80 \uCD08\uB300 \uC2E4\uD328",
    partial_result: "{{successCount}}\uBA85 \uCD08\uB300 \uC131\uACF5, {{failCount}}\uBA85 \uCD08\uB300 \uC2E4\uD328",
    partial_success: "\uC77C\uBD80 \uCD08\uB300 \uC131\uACF5",
    processing: "\uCD08\uB300 \uCC98\uB9AC \uC911...",
    success: "\uCD08\uB300 \uC131\uACF5",
    warning: "\uC774\uBA54\uC77C \uBC1C\uC1A1 \uC2E4\uD328"
  },
  member_invite_modal: {
    activate_description: "\uCCB4\uD06C \uD574\uC81C \uC2DC \uC218\uB77D\uD574\uB3C4 \uBE44\uD65C\uC131\uD654 \uC0C1\uD0DC\uB85C \uCD94\uAC00\uB429\uB2C8\uB2E4 (\uACB0\uC81C \uC5C6\uC74C)",
    activate_on_accept: "\uD65C\uC131\uD654 \uC0C1\uD0DC\uB85C \uCD08\uB300",
    additional_charge: "{{count}}\uBA85 \uCD94\uAC00 \uACB0\uC81C: {{amount}}\uC6D0",
    already_members_description: "\uB2E4\uC74C {{count}}\uBA85\uC740 \uC774\uBBF8 \uBA64\uBC84\uC774\uBBC0\uB85C \uCD08\uB300\uC5D0\uC11C \uC81C\uC678\uB429\uB2C8\uB2E4",
    already_members_title: "\uC774\uBBF8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uC785\uB2C8\uB2E4",
    cancel: "\uCDE8\uC18C",
    email_description: "",
    email_input: "\uC774\uBA54\uC77C \uC785\uB825",
    email_placeholder: "example@mail.com, example@mail.com ...",
    emails_description: "\uC27C\uD45C(,)\uB85C \uAD6C\uBD84\uD558\uC5EC \uC5EC\uB7EC \uBA85\uC744 \uD55C\uBC88\uC5D0 \uCD08\uB300\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4",
    emails_input: "\uC774\uBA54\uC77C \uC8FC\uC18C",
    invalid_email: "\uC720\uD6A8\uD558\uC9C0 \uC54A\uC740 \uC774\uBA54\uC77C \uC8FC\uC18C: {{email}}",
    invite: "\uCD08\uB300\uD558\uAE30",
    invite_members: "{{targetName}} \uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
    no_charge: "\uCD94\uAC00 \uACB0\uC81C \uC5C6\uC74C",
    payment_info: "\uACB0\uC81C \uC815\uBCF4",
    preview_count: "{{count}}\uBA85\uC758 \uBA64\uBC84\uB97C \uCD08\uB300\uD569\uB2C8\uB2E4",
    preview_title: "\uCD08\uB300 \uC608\uC815 \uBA64\uBC84",
    slots_used: "\uC2AC\uB86F {{count}}\uAC1C \uC0AC\uC6A9",
    assign_manager: "\uB9E4\uB2C8\uC800 \uC9C0\uC815"
  },
  team: "\uD300",
  workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
  workspace_invitation: {
    accept: "\uC218\uB77D\uD558\uAE30",
    invited_to: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCD08\uB300\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    reject: "\uAC70\uC808\uD558\uAE30",
    role: "\uC5ED\uD560",
    title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300"
  }
};

// src/locales/ko-KR/landing.json
var landing_default2 = {
  title_image: "/images/landing/title_text.png",
  macbook_image: "/images/landing/macbook_ko.png",
  feature1: {
    title: "AI \uBCF4\uACE0\uC11C \uC0DD\uC131",
    description: "'\uBCF4\uACE0\uB97C \uC704\uD55C \uC911\uBCF5 \uC791\uC5C5'\uC740 \uADF8\uB9CC!<br />\uC5C5\uBB34 \uACC4\uD68D\uACFC \uC791\uC5C5 \uB370\uC774\uD130\uB97C \uD30C\uC545\uD55C AI\uAC00 \uBCF4\uACE0\uC11C\uB97C \uC790\uB3D9 \uC0DD\uC131\uD569\uB2C8\uB2E4.",
    imageSrc: "/images/landing/contents/content_1.png",
    imageAlt: "AI \uBCF4\uACE0\uC11C \uC0DD\uC131"
  },
  feature2: {
    title: "\uC870\uC9C1 \uBAA9\uD45C \uC5F0\uB3D9 \uC5C5\uBB34/\uB370\uC774\uD130 \uAD00\uB9AC",
    description: "\uD300\uC758 \uBAA9\uD45C\uB97C \uC911\uC2EC\uC73C\uB85C \uC5C5\uBB34\uB97C \uC124\uACC4\uD558\uACE0 \uC5F0\uACB0\uD574 \uC11C\uB85C\uAC00 \uC5B4\uB5BB\uAC8C \uAE30\uC5EC\uD558\uACE0 \uC788\uB294\uC9C0 \uC2E4\uC2DC\uAC04\uC73C\uB85C \uD655\uC778\uD558\uC138\uC694. \uC624\uBE14\uB9BF\uC5D0\uC11C \uD300 \uBAA9\uD45C\uB97C Cascading\uD558\uACE0 \uD300\uC6D0\uC774 \u201C\uB9C8\uC74C\uAECF\u201D \uAE30\uC5EC\uD560 \uC218 \uC788\uB294 \uCCB4\uACC4\uB97C \uB9CC\uB4DC\uC138\uC694.",
    imageSrc: "/images/landing/contents/content_2.png",
    imageAlt: "\uC870\uC9C1 \uBAA9\uD45C \uC5F0\uB3D9 \uC5C5\uBB34/\uB370\uC774\uD130 \uAD00\uB9AC"
  },
  feature3: {
    title: "\uD06C\uB808\uB527&\uD53C\uB4DC\uBC31",
    description: "'\uC81C\uB54C\uC758', '\uAD6C\uCCB4\uC801\uC778' \uD53C\uB4DC\uBC31\uC774 \uC131\uC7A5\uC73C\uB85C \uC774\uC5B4\uC9D1\uB2C8\uB2E4.<br />\uD750\uB9BF\uD558\uACE0 \uBB49\uB6B1\uADF8\uB824\uC9C4 \uD53C\uB4DC\uBC31\uC774 \uC544\uB2CC, \uC989\uAC01\uC801\uC774\uACE0 \uBA85\uB8CC\uD55C \uD53C\uB4DC\uBC31 \uAD50\uD658 \uC2DC\uC2A4\uD15C\uC744 \uAD6C\uCD95\uD558\uC138\uC694.",
    imageSrc: "/images/landing/contents/content_3.png",
    imageAlt: "\uD06C\uB808\uB527&\uD53C\uB4DC\uBC31"
  },
  contact: "\uC18C\uAC1C\uC11C \uBCF4\uAE30",
  start: "\uBB34\uB8CC\uB85C \uC2DC\uC791\uD558\uAE30",
  announcement: {
    title: "\uD074\uB77C\uC6B0\uB4DC \uBC14\uC6B0\uCC98 \uCD5C\uB300 80% \uC815\uBD80\uC9C0\uC6D0",
    button: "\uBB38\uC758\uD558\uAE30"
  },
  hero: "'\uD300 \uBAA9\uD45C-\uAC1C\uC778 \uC5C5\uBB34' \uC5F0\uACC4 \uB370\uC774\uD130 \uAE30\uBC18 HR \uAD00\uB9AC, \uC624\uBE14\uB9BF",
  cta: {
    first: "\uC9C0\uAE08 \uBC14\uB85C,",
    second: "\uC6B0\uB9AC\uB9CC\uC758 \uADA4\uB3C4\uB97C \uADF8\uB824\uBCF4\uC138\uC694."
  },
  company: {
    name: "(\uC8FC) \uB9AC\uBC84\uC2A4\uB9C8\uC6B4\uD2F4",
    address: "\uC11C\uC6B8 \uAC15\uC11C\uAD6C \uB9C8\uACE1\uC911\uC5598\uB85C 14 \uC11C\uC6B8\uCC3D\uC5C5\uD5C8\uBE0C \uC5E0\uD50C\uB7EC\uC2A4",
    representative: "\uB300\uD45C\uC790 \uAE40\uACBD\uBBFC"
  },
  onelanding: {
    title: "1on1",
    move: "\uC774\uB3D9\uD558\uAE30"
  },
  buttons: {
    useService: "\uC11C\uBE44\uC2A4 \uC774\uC6A9\uD558\uAE30",
    requestDemo: "\uB370\uBAA8 \uC608\uC57D\uD558\uAE30"
  },
  heroSection: {
    mainTitle: {
      line1: "\uC18C\uBAA8\uC801\uC778 1on1 \uC900\uBE44\uC640 \uC815\uB9AC,",
      line2: "\uAD00\uB9AC/\uC131\uC7A5\uACFC \uB3D9\uB5A8\uC5B4\uC9C4 1on1\uACFC \uC774\uBCC4\uD558\uC138\uC694."
    },
    app_mainTitle: {
      line1: "\uC18C\uBAA8\uC801\uC778 1on1 \uC900\uBE44\uC640 \uC815\uB9AC,",
      line2: "\uAD00\uB9AC/\uC131\uC7A5\uACFC \uB3D9\uB5A8\uC5B4\uC9C4 1on1\uACFC",
      line3: "\uC774\uBCC4\uD558\uC138\uC694."
    },
    subTitle: {
      line1: "\uAE30\uB85D\uC740 \uAC71\uC815 \uB9D0\uACE0, \uD3B8\uD558\uAC8C \uB300\uD654\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694.",
      line2: "\uC9C8\uBB38\uC744 \uC911\uC2EC\uC73C\uB85C \uC624\uACE0 \uAC04 \uB300\uD654\uB97C \uC694\uC57D\uD574 \uC561\uC158 \uC544\uC774\uD15C\uAE4C\uC9C0 \uC81C\uC2DC\uD574\uB4DC\uB9B4\uAC8C\uC694!"
    },
    app_subTitle: {
      line1: "\uAE30\uB85D\uC740 \uAC71\uC815 \uB9D0\uACE0, \uD3B8\uD558\uAC8C \uB300\uD654\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694.",
      line2: "\uC9C8\uBB38\uC744 \uC911\uC2EC\uC73C\uB85C \uC624\uACE0 \uAC04 \uB300\uD654\uB97C \uC694\uC57D\uD574",
      line3: "\uC561\uC158 \uC544\uC774\uD15C\uAE4C\uC9C0 \uC81C\uC2DC\uD574\uB4DC\uB9B4\uAC8C\uC694!"
    }
  },
  features: {
    feature1: {
      title: "\uB9AC\uB354\uC5D0\uAC8C \uC81C\uACF5\uB418\uB294 AI \uCF54\uCE6D \uD53C\uB4DC\uBC31",
      description: [
        "\uD55C \uAC78\uC74C\uC529 \uAFB8\uC900\uD788, \uB354 \uB098\uC740 \uB9AC\uB354\uAC00 \uB418\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uBC1C\uD654 \uBE44\uC728, \uB0B4\uC6A9, \uD1A4 \uB4F1\uC744 \uBD84\uC11D\uD574 \uBC14\uB85C \uC2E4\uD589 \uAC00\uB2A5\uD55C \uBC1C\uC804 \uBC29\uBC95\uC744 \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694!"
      ],
      app_description: [
        "\uD55C \uAC78\uC74C\uC529 \uAFB8\uC900\uD788, \uB354 \uB098\uC740 \uB9AC\uB354\uAC00 \uB418\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uBC1C\uD654 \uBE44\uC728, \uB0B4\uC6A9, \uD1A4 \uB4F1\uC744 \uBD84\uC11D\uD574",
        "\uBC14\uB85C \uC2E4\uD589 \uAC00\uB2A5\uD55C \uBC1C\uC804 \uBC29\uBC95\uC744 \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694!"
      ]
    },
    feature2: {
      title: "AI \uB9DE\uCDA4\uD615 \uC9C8\uBB38 \uC0DD\uC131",
      description: [
        "\uBC18\uBCF5\uB418\uB294 \uB300\uD654\uB9D0\uACE0, \uAC01\uC790\uC5D0\uAC8C \uC54C\uB9DE\uC740 1on1\uC744 \uC9C4\uD589\uD558\uC138\uC694!",
        "\uC0C1\uB300\uC758 \uBBF8\uD305 \uAE30\uB85D\uACFC \uC0C1\uD669, \uC6D0\uD558\uB294 \uC8FC\uC81C\uB97C \uBC18\uC601\uD558\uC5EC \uAC00\uC774\uB4DC\uC640 \uB9DE\uCDA4\uD615 \uC9C8\uBB38\uC9C0\uB97C \uC0DD\uC131\uD574\uB4DC\uB9B4\uAC8C\uC694!"
      ],
      app_description: [
        "\uBC18\uBCF5\uB418\uB294 \uB300\uD654\uB9D0\uACE0, \uAC01\uC790\uC5D0\uAC8C \uC54C\uB9DE\uC740 1on1\uC744 \uC9C4\uD589\uD558\uC138\uC694!",
        "\uC0C1\uB300\uC758 \uBBF8\uD305 \uAE30\uB85D\uACFC \uC0C1\uD669, \uC6D0\uD558\uB294 \uC8FC\uC81C\uB97C \uBC18\uC601\uD558\uC5EC",
        "\uAC00\uC774\uB4DC\uC640 \uB9DE\uCDA4\uD615 \uC9C8\uBB38\uC9C0\uB97C \uC0DD\uC131\uD574\uB4DC\uB9B4\uAC8C\uC694!"
      ]
    },
    feature3: {
      title: "AI\uBD84\uC11D \uB9AC\uD3EC\uD2B8\uC640 9Box \uB300\uC2DC\uBCF4\uB4DC",
      description: [
        "\uC228\uACA8\uC9C4 \uC131\uC7A5 \uAE30\uD68C\uC640 \uB9AC\uC2A4\uD06C\uB97C \uC804\uB7B5\uC801\uC73C\uB85C \uAD00\uB9AC\uD558\uC138\uC694!",
        "\uAD6C\uC131\uC6D0\uB4E4\uC758 \uB0B4\uC801 \uB3D9\uAE30, \uAC15\uC810\uACFC \uC57D\uC810 \uB4F1\uC774 \uB2F4\uAE34 \uBD84\uC11D \uB9AC\uD3EC\uD2B8\uC640",
        "\uC131\uC7A5 \uAD00\uB9AC\uB97C \uC704\uD55C 9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C \uC81C\uACF5\uD574\uB4DC\uB9B4\uAC8C\uC694!"
      ],
      app_description: [
        "\uC228\uACA8\uC9C4 \uC131\uC7A5 \uAE30\uD68C\uC640 \uB9AC\uC2A4\uD06C\uB97C \uC804\uB7B5\uC801\uC73C\uB85C \uAD00\uB9AC\uD558\uC138\uC694!",
        "\uAD6C\uC131\uC6D0\uB4E4\uC758 \uB0B4\uC801 \uB3D9\uAE30, \uAC15\uC810\uACFC \uC57D\uC810 \uB4F1\uC774 \uB2F4\uAE34 \uBD84\uC11D \uB9AC\uD3EC\uD2B8\uC640",
        "\uC131\uC7A5 \uAD00\uB9AC\uB97C \uC704\uD55C 9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C \uC81C\uACF5\uD574\uB4DC\uB9B4\uAC8C\uC694!"
      ]
    }
  },
  valueProposition: "\uC81C\uACF5\uD558\uB294 \uAC00\uCE58",
  bottomSection: {
    item1: {
      line1: "1on1 \uBBF8\uD305 \uC804\uD6C4",
      highlight1: "",
      line2: "",
      highlight2: "\uC900\uBE44/\uC815\uB9AC \uC791\uC5C5 \uC790\uB3D9\uD654"
    },
    item2: {
      line1: "\uB354 \uC88B\uC740 \uB9AC\uB354\uB97C \uB9CC\uB4DC\uB294",
      line2: "",
      highlight: "\uB9DE\uCDA4\uD615 AI \uB9AC\uB354\uC2ED \uCF54\uCE58"
    },
    item3: {
      line1: "1on1 \uB370\uC774\uD130\uB97C \uBD84\uC11D\uD574",
      line2: "\uC81C\uACF5\uD558\uB294",
      highlight: "9box\uC640 \uD300\uC6D0 \uB9AC\uD3EC\uD2B8"
    }
  },
  footer: {
    companyName: "(\uC8FC) \uB9AC\uBC84\uC2A4\uB9C8\uC6B4\uD2F4",
    address: "\uC11C\uC6B8 \uAC15\uC11C\uAD6C \uB9C8\uACE1\uC911\uC5598\uB85C 14 \uC11C\uC6B8\uCC3D\uC5C5\uD5C8\uBE0C \uC5E0\uD50C\uB7EC\uC2A4",
    representative: "\uB300\uD45C\uC790 : \uAE40\uACBD\uBBFC",
    email: "gmkim@reversemountain.co.kr",
    copyright: "2025 REVERSEMOUNTAIN ALL RIGHTS RESERVED."
  },
  card: {
    card1: {
      text_normal: "1on1 \uBBF8\uD305 \uC804\uD6C4",
      text_bold: "\uC900\uBE44/\uC815\uB9AC \uC791\uC5C5 \uC790\uB3D9\uD654"
    },
    card2: {
      text_normal: "\uB354 \uC88B\uC740 \uB9AC\uB354\uB97C \uB9CC\uB4DC\uB294",
      text_bold: "\uB9DE\uCDA4\uD615 AI \uB9AC\uB354\uC2ED \uCF54\uCE58"
    },
    card3: {
      text_normal1: "1on1 \uB370\uC774\uD130\uB97C \uBD84\uC11D\uD574",
      text_normal2: "\uC81C\uACF5\uD558\uB294",
      text_bold: "9box\uC640 \uD300\uC6D0 \uB9AC\uD3EC\uD2B8"
    }
  },
  landingMerged: {
    title: "\uC778\uC7AC \uBC00\uB3C4\uB97C \uB192\uC774\uB294",
    subtitle: "AI \uD53C\uB4DC\uBC31 & 1on1 \uBBF8\uD305 \uC194\uB8E8\uC158",
    leadershipText: {
      line1: "\uB9AC\uB354\uC758 ",
      line2: "\uC2DC\uAC04",
      line3: "\uC740 \uC544\uB07C\uACE0, ",
      line4: "\uC131\uC7A5\uACFC \uBAB0\uC785\uC740 \uADF9\uB300\uD654",
      line5: "\uD558\uC138\uC694."
    },
    card: {
      title1: "\uC624\uBE14\uB9BF 1on1",
      description1: [
        "\uC18C\uBAA8\uC801\uC778 1on1 \uC900\uBE44\uC640 \uC815\uB9AC,",
        "\uC131\uC7A5\uC73C\uB85C \uC774\uC5B4\uC9C0\uC9C0 \uBABB\uD558\uB294 1on1\uACFC \uC774\uBCC4\uD558\uC138\uC694."
      ],
      title2: "\uC624\uBE14\uB9BF Works",
      description2: [
        "\uC5C5\uBB34 \uD604\uD669\uC774 \uBD88\uD22C\uBA85\uD574 \uBC1C\uC0DD\uD558\uB294 \uBE44\uD6A8\uC728,",
        "\uC18C\uBAA8\uC801\uC778 \uBCF4\uACE0\uC11C \uC791\uC131 \uC2DC\uAC04, \uBAA8\uD638\uD558\uACE0",
        "\uD615\uC2DD\uC801\uC778 \uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694"
      ],
      title3: "\uC624\uBE14\uB9BF Feedback",
      description3: [
        "\uD615\uC2DD\uC801\uC774\uACE0 \uC77C\uD68C\uC801\uC778 \uD3C9\uAC00, \uC131\uC7A5\uC73C\uB85C",
        "\uC774\uC5B4\uC9C0\uC9C0 \uBABB\uD558\uB294 \uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694"
      ]
    },
    benefits: {
      title: "\uB3C4\uC785",
      highlight: "\uC9C1\uD6C4",
      suffix: "\uCCB4\uAC10\uB418\uB294 \uD3B8\uC775"
    },
    carousel: {
      card1: {
        title: "1on1 \uAD00\uB828 \uBB38\uC11C \uC5C5\uBB34 \uC790\uB3D9\uD654",
        description1: "\uC131\uACFC\uAD00\uB9AC \uBB38\uC11C \uC5C5\uBB34 \uC6D4 20\uC2DC\uAC04 \uB2E8\uCD95",
        description2: ["8\uBA85\uC758 \uD300\uC6D0\uC744 \uB9E4\uB2C8\uC9D5 \uC911\uC778", "\uC911\uACAC\uAE30\uC5C5 \uD300\uC7A5\uB2D8"],
        description3: [
          "1on1 \uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uC790\uB3D9\uC73C\uB85C \uB9AC\uD3EC\uD2B8\uAC00 \uC644\uC131\uB429\uB2C8\uB2E4.",
          "\uBB38\uC11C \uC5C5\uBB34\uB294 \uC624\uBE14\uB9BF\uC5D0 \uB9E1\uAE30\uACE0 ",
          "\uD300\uC6D0 \uC790\uCCB4\uC5D0\uAC8C \uB354 \uC9D1\uC911\uD560\uC218 \uC788\uAC8C \uB418\uC5C8\uC2B5\uB2C8\uB2E4. ",
          "\uD300\uC6D0\uC758 \uB9CC\uC871\uB3C4 \uB610\uD55C \uB192\uC544\uC84C\uC2B5\uB2C8\uB2E4."
        ]
      },
      card2: {
        title: "\uB9AC\uB354\uC758 \uB9E4\uB2C8\uC9D5 \uC5ED\uB7C9 \uAC15\uD654",
        description1: "\uC624\uBE14\uB9BF \uC0AC\uC6A9 \uD6C4 \uC9C1\uC6D0 \uC131\uACFC\uAD00\uB9AC \uB9CC\uC871\uB3C4 127% \uC99D\uAC00",
        description2: ["120\uBA85 \uADDC\uBAA8\uC758", "\uC2DC\uB9AC\uC988 A \uC2A4\uD0C0\uD2B8\uC5C5 \uB300\uD45C\uB2D8"],
        description3: [
          "\uB9AC\uB354\uC758 \uB9E4\uB2C8\uC9D5 \uC5ED\uB7C9\uC740 \uB298 Black Box \uC600\uC2B5\uB2C8\uB2E4.",
          "\uC798\uBABB\uB420 \uACBD\uC6B0 \uC190\uC2E4\uB3C4 \uD06C\uAE30\uC5D0 \uB298 \uAC71\uC815\uB418\uC5C8\uC8E0.",
          "\uD558\uC9C0\uB9CC \uC774\uC81C \uC624\uBE14\uB9BF\uC774 \uB9AC\uB354\uB97C \uCF54\uCE6D\uD558\uACE0,",
          "\uC800\uB3C4 \uB9AC\uB354\uC758 \uC0C1\uD669\uC744 \uD30C\uC545\uD560 \uC218 \uC788\uAC8C \uB410\uC2B5\uB2C8\uB2E4."
        ]
      },
      card3: {
        title: "\uC131\uACFC \uAD00\uB9AC \uBB38\uD654 \uC120\uC9C4\uD654",
        description1: "1on1 \uBBF8\uD305 \uB3C4\uC785 \uB4F1 \uC870\uC9C1 \uC131\uACFC \uAD00\uB9AC \uBB38\uD654 \uC120\uC9C4\uD654",
        description2: [],
        description3: [
          "\uC81C\uC9C0, IT, \uD654\uD559, \uAC74\uC124 / 5,700\uBA85 / \uB300\uAE30\uC5C5",
          "\uC624\uD508 \uC774\uB178\uBCA0\uC774\uC158 \uC9C4\uD589 \uBC0F \uC804\uB7B5\uD300\uACFC \uD655\uC7A5 \uC124\uACC4 \uC911"
        ]
      },
      card4: {
        title: "\uB370\uC774\uD130\uB97C \uD65C\uC6A9\uD55C \uC131\uACFC\uAD00\uB9AC \uC2E4\uD604",
        description1: "\uC9C1\uC6D0\uBCC4 \uC5C5\uBB34 \uB370\uC774\uD130 \uC218\uC9D1 \uBC0F \uC774\uB97C \uD65C\uC6A9\uD55C \uC131\uACFC \uAD00\uB9AC \uC2E4\uD604",
        description2: [],
        description3: [
          "\uC54C\uB8E8\uBBF8\uB284 \uC81C\uC870/4,000\uBA85 / \uD55C\uAD6D, \uBCA0\uD2B8\uB0A8",
          "\uBCA0\uD2B8\uB0A8 \uAE30\uD68D, \uC778\uC0AC, \uCD1D\uBB34\uC2E4\uACFC \uC720\uB8CC PoC \uC9C4\uD589\uC911"
        ]
      }
    }
  },
  landingFB: {
    title: [
      "\uD615\uC2DD\uC801\uC774\uACE0 \uC77C\uD68C\uC801\uC778 \uD3C9\uAC00",
      "\uC131\uC7A5\uC73C\uB85C \uC774\uC5B4\uC9C0\uC9C0 \uBABB\uD558\uB294 \uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694."
    ],
    subtitle: [
      "\uD615\uC2DD\uC801\uC774\uACE0 \uC77C\uD68C\uC801\uC778 \uD3C9\uAC00,",
      "\uC131\uC7A5\uC73C\uB85C \uC774\uC5B4\uC9C0\uC9C0 \uBABB\uD558\uB294",
      "\uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694."
    ],
    subtitle_app: [
      "\uAD6C\uCCB4\uC801\uC778 \uB370\uC774\uD130\uC5D0 \uB300\uD574, \uAC00\uC7A5 \uC218\uC6A9\uD558\uAE30 \uC26C\uC6B4 \uD615\uD0DC\uB85C.",
      "AI \uD53C\uB4DC\uBC31 \uCF54\uCE58\uAC00 \uCD5C\uC801\uC758 \uD53C\uB4DC\uBC31\uC744 \uAC00\uC774\uB4DC\uD574 \uB4DC\uB9B4\uAC8C\uC694!"
    ],
    section: {
      title1: "\uD53C\uB4DC\uBC31 \uC2A4\uCF54\uC5B4\uB9C1\uACFC \uCD5C\uC801\uD654",
      description1: [
        "\uD63C\uC790\uC11C \uC644\uBCBD\uD55C \uD53C\uB4DC\uBC31\uC744 \uC791\uC131\uD574\uC57C \uD55C\uB2E4\uB294 \uBD80\uB2F4\uC744 \uBC84\uB9AC\uC138\uC694.",
        "\uCD08\uC548\uC758 \uAC1C\uC120\uC810\uC744 \uBC1C\uACAC\uD558\uACE0, \uC9C1\uC6D0\uC758 MBTI \uC131\uD5A5\uC5D0 \uB9DE\uAC8C \uD45C\uD604\uC744 \uCD5C\uC801\uD654\uD574 \uB4DC\uB9B4\uAC8C\uC694!"
      ],
      app_description1: [
        "\uD63C\uC790\uC11C \uC644\uBCBD\uD55C \uD53C\uB4DC\uBC31\uC744 \uC791\uC131\uD574\uC57C \uD55C\uB2E4\uB294 \uBD80\uB2F4\uC744 \uBC84\uB9AC\uC138\uC694.",
        "\uCD08\uC548\uC758 \uAC1C\uC120\uC810\uC744 \uBC1C\uACAC\uD558\uACE0, \uC9C1\uC6D0\uC758 MBTI \uC131\uD5A5\uC5D0 \uB9DE\uAC8C",
        "\uD45C\uD604\uC744 \uCD5C\uC801\uD654\uD574 \uB4DC\uB9B4\uAC8C\uC694!"
      ],
      title2: "\uC5C5\uBB34 \uB370\uC774\uD130 \uC5F0\uACB0\uACFC \uC2E0\uD638\uB4F1 \uD3C9\uAC00",
      description2: [
        "\uAD6C\uCCB4\uC801\uC774\uACE0 \uBA85\uD655\uD55C \uD53C\uB4DC\uBC31\uC774 \uBC1C\uC804\uC73C\uB85C \uC774\uC5B4\uC9D1\uB2C8\uB2E4.",
        "\uC5C5\uBB34 \uB370\uC774\uD130\uC640 \uD53C\uB4DC\uBC31\uC744 \uC5F0\uACB0\uD558\uACE0, \uC131\uACFC\uC5D0 \uB300\uD55C \uACF5\uAC10\uB300\uB97C \uC720\uC9C0\uD558\uC138\uC694."
      ],
      app_description2: [
        "\uAD6C\uCCB4\uC801\uC774\uACE0 \uBA85\uD655\uD55C \uD53C\uB4DC\uBC31\uC774 \uBC1C\uC804\uC73C\uB85C \uC774\uC5B4\uC9D1\uB2C8\uB2E4.",
        "\uC5C5\uBB34 \uB370\uC774\uD130\uC640 \uD53C\uB4DC\uBC31\uC744 \uC5F0\uACB0\uD558\uACE0,",
        "\uC131\uACFC\uC5D0 \uB300\uD55C \uACF5\uAC10\uB300\uB97C \uC720\uC9C0\uD558\uC138\uC694."
      ]
    },
    card: {
      card1: {
        text_bold1: "\uB9AC\uB354 \uAC01\uC790\uC758 \uC57D\uC810\uC744 \uBCF4\uC644\uD574",
        text_bold2: "\uC9C8 \uB192\uC740 \uD53C\uB4DC\uBC31 \uC81C\uACF5",
        text_normal: "\uAC00\uB2A5"
      },
      card2: {
        text_normal1: "\uC5C5\uBB34\uBCC4 \uC2E0\uD638\uB4F1 \uD3C9\uAC00 \uCCB4\uACC4\uB85C \uC131\uACFC\uC5D0",
        text_normal2: "\uB300\uD55C ",
        text_bold: "\uD300\uC7A5\uACFC \uD300\uC6D0\uC758 \uACF5\uAC10\uB300 \uC720\uC9C0"
      },
      card3: {
        text_normal: "MBTI \uB9DE\uCDA4 \uD53C\uB4DC\uBC31 \uCD5C\uC801\uD654\uB85C ",
        text_bold: "\uD53C\uB4DC\uBC31 \uC218\uC6A9\uC131 \uADF9\uB300\uD654"
      }
    }
  },
  landingWorks: {
    title: [
      "\uC5C5\uBB34 \uD604\uD669\uC774 \uBD88\uD22C\uBA85\uD574 \uBC1C\uC0DD\uD558\uB294 \uBE44\uD6A8\uC728,",
      "\uC18C\uBAA8\uC801\uC778 \uBCF4\uACE0\uC11C \uC791\uC131, \uBAA8\uD638\uD55C \uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694."
    ],
    title_app: [
      "\uC5C5\uBB34 \uD604\uD669\uC774 \uBD88\uD22C\uBA85\uD574 \uBC1C\uC0DD\uD558\uB294",
      "\uBE44\uD6A8\uC728, \uC18C\uBAA8\uC801\uC778 \uBCF4\uACE0\uC11C \uC791\uC131,",
      "\uBAA8\uD638\uD55C \uD53C\uB4DC\uBC31\uC5D0\uC11C \uBC97\uC5B4\uB098\uC138\uC694."
    ],
    subtitle: [
      "\u2018\uD558\uB8E8 3\uBD84\u2018, \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC73C\uB85C \uD611\uC5C5 \uC2DC\uB108\uC9C0\uB97C \uB192\uC774\uC138\uC694!",
      " \uD074\uB9AD \uD55C \uBC88\uC5D0 \uC644\uC131\uB418\uB294 \uBCF4\uACE0\uC11C\uBD80\uD130 \uB3D9\uAE30\uB97C \uBD80\uC5EC\uD558\uB294 \uD53C\uB4DC\uBC31\uAE4C\uC9C0 \uB3C4\uC640\uB4DC\uB9B4\uAC8C\uC694!"
    ],
    subtitle_app: [
      "\u2018\uD558\uB8E8 3\uBD84\u2018, \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC73C\uB85C \uD611\uC5C5 \uC2DC\uB108\uC9C0\uB97C \uB192\uC774\uC138\uC694!",
      "\uD074\uB9AD \uD55C \uBC88\uC5D0 \uC644\uC131\uB418\uB294 \uBCF4\uACE0\uC11C\uBD80\uD130",
      "\uB3D9\uAE30\uB97C \uBD80\uC5EC\uD558\uB294 \uD53C\uB4DC\uBC31\uAE4C\uC9C0 \uB3C4\uC640\uB4DC\uB9B4\uAC8C\uC694!"
    ],
    section: {
      title1: "\uC2E4\uC2DC\uAC04\uC73C\uB85C \uACF5\uC720\uB418\uB294 \uC11C\uB85C\uC758 \uAE30\uC5EC",
      description1: [
        "\uD300 \uBAA9\uD45C\uC640 \uC5F0\uACB0\uD558\uC5EC \uC5C5\uBB34\uB97C \uAD00\uB9AC\uD558\uACE0, \uC2E4\uC2DC\uAC04\uC73C\uB85C \uACF5\uC720\uD558\uC138\uC694.",
        "AI\uC640 \uD568\uAED8 \uAF3C\uAF3C\uD558\uACE0 \uC190\uC26C\uC6B4 \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC744 \uC9C4\uD589\uD560 \uC218 \uC788\uC5B4\uC694!"
      ],
      app_description1: [
        "\uD300 \uBAA9\uD45C\uC640 \uC5F0\uACB0\uD558\uC5EC \uC5C5\uBB34\uB97C \uAD00\uB9AC\uD558\uACE0, \uC2E4\uC2DC\uAC04\uC73C\uB85C \uACF5\uC720\uD558\uC138\uC694.",
        "AI\uC640 \uD568\uAED8 \uAF3C\uAF3C\uD558\uACE0 \uC190\uC26C\uC6B4 \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC744 \uC9C4\uD589\uD560 \uC218 \uC788\uC5B4\uC694!"
      ],
      title2: "AI \uBCF4\uACE0\uC11C \uC790\uB3D9 \uC0DD\uC131",
      description2: [
        "\uC18C\uBAA8\uC801\uC778 \uBCF4\uACE0\uC11C, \uC774\uC81C\uB294 \uD074\uB9AD \uD55C \uBC88\uC73C\uB85C \uB9CC\uB4DC\uC138\uC694!",
        "AI\uAC00 \uC5C5\uBB34 \uD604\uD669\uACFC \uC18C\uD1B5 \uB85C\uADF8, \uBB38\uC11C \uB370\uC774\uD130\uB97C \uC885\uD569\uD574 \uBCF4\uACE0\uC11C \uCD08\uC548\uC744 \uC0DD\uC131\uD574\uB4DC\uB824\uC694."
      ],
      app_description2: [
        "\uC18C\uBAA8\uC801\uC778 \uBCF4\uACE0\uC11C, \uC774\uC81C\uB294 \uD074\uB9AD \uD55C \uBC88\uC73C\uB85C \uB9CC\uB4DC\uC138\uC694!",
        "AI\uAC00 \uC5C5\uBB34 \uD604\uD669\uACFC \uC18C\uD1B5 \uB85C\uADF8, \uBB38\uC11C \uB370\uC774\uD130\uB97C \uC885\uD569\uD574",
        "\uBCF4\uACE0\uC11C \uCD08\uC548\uC744 \uC0DD\uC131\uD574\uB4DC\uB824\uC694."
      ],
      title3: "AI \uD53C\uB4DC\uBC31 \uCD5C\uC801\uD654",
      description3: [
        "\uC5C5\uBB34 \uB370\uC774\uD130\uB97C \uBC14\uD0D5\uC73C\uB85C \uAD6C\uCCB4\uC801\uC778 \uD53C\uB4DC\uBC31\uC744 \uC8FC\uACE0 \uBC1B\uC73C\uC138\uC694.",
        "AI\uC640 \uD568\uAED8 \uB0B4\uC6A9\uC744 \uAC15\uD654\uD558\uACE0 \uD45C\uD604\uC744 \uCD5C\uC801\uD654\uD560 \uC218\uB3C4 \uC788\uC5B4\uC694!"
      ],
      app_description3: [
        "\uC5C5\uBB34 \uB370\uC774\uD130\uB97C \uBC14\uD0D5\uC73C\uB85C \uAD6C\uCCB4\uC801\uC778 \uD53C\uB4DC\uBC31\uC744 \uC8FC\uACE0 \uBC1B\uC73C\uC138\uC694.",
        "AI\uC640 \uD568\uAED8 \uB0B4\uC6A9\uC744 \uAC15\uD654\uD558\uACE0 \uD45C\uD604\uC744 \uCD5C\uC801\uD654\uD560 \uC218\uB3C4 \uC788\uC5B4\uC694!"
      ]
    },
    card: {
      card1: {
        text_bold1: "\uD22C\uBA85\uD55C \uC5C5\uBB34 \uD604\uD669 \uACF5\uC720",
        text_normal1: "\uB85C",
        text_normal2: "\uD300 \uC2DC\uB108\uC9C0 \uD6A8\uACFC \uD5A5\uC0C1"
      },
      card2: {
        text_normal: "\uD558\uB8E8 3\uBD84 \uAE30\uB85D\uB9CC\uC73C\uB85C",
        text_bold: "\uBCF4\uACE0\uC11C \uC791\uC131 \uC5C5\uBB34 \uC790\uB3D9\uD654"
      },
      card3: {
        text_normal: "\uC2E4\uD589 \uAC00\uB2A5\uD55C \uAC1C\uC120 \uBC29\uBC95\uC774 \uB2F4\uAE34",
        text_bold: "\uC5C5\uBB34 \uB370\uC774\uD130 \uC5F0\uACC4 AI \uD53C\uB4DC\uBC31"
      }
    }
  },
  price: {
    title: "\uD569\uB9AC\uC801\uC778 SaaS \uC694\uAE08\uC81C\uC640<br />\uB9DE\uCDA4\uD654\uB41C PoC, \uAD6C\uCD95, \uAD50\uC721\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4.",
    apptitle: "\uD569\uB9AC\uC801\uC778 SaaS \uC694\uAE08\uC81C\uC640<br />\uB9DE\uCDA4\uD654\uB41C PoC, \uAD6C\uCD95, \uAD50\uC721\uC744<br />\uC81C\uACF5\uD569\uB2C8\uB2E4.",
    contacts: "\uC628\uD504\uB808\uBBF8\uC2A4 \uAD6C\uCD95, \uB9DE\uCDA4 PoC, \uAD50\uC721/\uC6CC\uD06C\uC0F5 \uC5F0\uACC4 \uBCC4\uB3C4 \uBB38\uC758<br />jjchoi@reversemountain.co.kr",
    badge: "\uBE44\uC988\uB2C8\uC2A4 \uD50C\uB79C",
    workspaceBilling: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB2E8\uC704 \uCCAD\uAD6C",
    cards: [
      {
        id: "feedback",
        title: "\uC624\uBE14\uB9BF Feedback",
        price: 6e3,
        unit: "\uBA64\uBC84\uB2F9",
        period: "\uC6D4",
        features: [
          {
            text: "\uC804\uBB38 AI \uD65C\uC6A9 \uD53C\uB4DC\uBC31 \uB0B4\uC6A9 \uAC15\uD654"
          },
          {
            text: "Works AI \uC0AC\uC6A9 \uC2DC \uBB34\uB8CC \uC81C\uACF5"
          }
        ],
        buttonText: "\uBB38\uC758\uD558\uAE30"
      },
      {
        id: "standard",
        title: "\uC624\uBE14\uB9BF Works",
        price: 1e4,
        unit: "\uBA64\uBC84\uB2F9",
        period: "\uC6D4",
        features: [
          {
            text: "\uAC1C\uC778 \uC5C5\uBB34 \uAD00\uB9AC + \uC2E4\uC2DC\uAC04 \uD604\uD669 \uACF5\uC720"
          },
          {
            text: "\uC77C\uAC04/\uC8FC\uAC04/\uC6D4\uAC04 \uBCF4\uACE0\uC11C \uC790\uB3D9 \uC0DD\uC131 AI"
          },
          {
            text: "Feedback AI \uC81C\uACF5 6,000\u20A9/\uC6D4 \uC0C1\uB2F9"
          }
        ],
        buttonText: "\uBB38\uC758\uD558\uAE30"
      },
      {
        id: "premium",
        title: "\uC624\uBE14\uB9BF 1on1",
        iconBgColor: "#EDF0FE",
        iconColor: "#4C6EF5",
        price: 8e3,
        unit: "\uBA64\uBC84\uB2F9",
        period: "\uC6D4",
        features: [
          {
            text: "AI \uBBF8\uD305 \uB0B4\uC6A9 \uD14D\uC2A4\uD2B8\uD654(STT)"
          },
          {
            text: "\uD300\uC6D0\uBCC4 \uB9DE\uCDA4\uD615 \uC9C4\uD589 \uBC29\uC548 \uC81C\uC2DC"
          },
          {
            text: "1on1 \uB0B4\uC6A9 \uAE30\uB85D + \uC694\uC57D + \uBD84\uC11D"
          },
          {
            text: "1on1 \uB0B4\uC6A9 \uAE30\uBC18 \uD300\uC6D0 \uC0C1\uD0DC\uAD00\uB9AC \uB300\uC2DC\uBCF4\uB4DC"
          },
          {
            text: "\uB9AC\uB354\uC2ED \uC5ED\uB7C9 \uAC1C\uC120\uC744 \uB3D5\uB294 \uB9DE\uCDA4\uD615 AI"
          }
        ],
        buttonText: "\uBB38\uC758\uD558\uAE30"
      }
    ]
  },
  landingNonAdopted: {
    title: '<span style="color: #228BE6">1on1 \uBBF8\uD305,</span> \uB3C4\uC785\uC744 \uB9DD\uC124\uC774\uACE0 \uACC4\uC2E0\uAC00\uC694?',
    title_app: '<span style="color: #228BE6">1on1 \uBBF8\uD305,</span> \uB3C4\uC785\uC744 \uB9DD\uC124\uC774\uACE0 \uACC4\uC2E0\uAC00\uC694?',
    subtitle: '\uC624\uBE14\uB9BF AI\uAC00 \uC131\uACF5\uC801\uC778 \uCCAB \uC2DC\uC791\uC740 \uBB3C\uB860,<br /><span style="color: #228BE6">\uD300\uC7A5\uB2D8\uC758 \uB9AC\uB354\uC2ED \uC131\uC7A5</span>\uAE4C\uC9C0 \uD568\uAED8\uD569\uB2C8\uB2E4.',
    subtitle_app: '\uC624\uBE14\uB9BF AI\uAC00<br /> \uC131\uACF5\uC801\uC778 \uCCAB \uC2DC\uC791\uC740 \uBB3C\uB860, <span style="color: #228BE6">\uD300\uC7A5\uB2D8\uC758<br />\uB9AC\uB354\uC2ED \uC131\uC7A5</span>\uAE4C\uC9C0 \uD568\uAED8\uD569\uB2C8\uB2E4.',
    question: "\uD639\uC2DC \uC774\uB7F0 \uC7A5\uBCBD \uB54C\uBB38\uC5D0 1on1\uC744 \uC8FC\uC800\uD558\uACE0 \uACC4\uC2E0\uAC00\uC694?",
    question_app: "\uD639\uC2DC \uC774\uB7F0 \uC7A5\uBCBD \uB54C\uBB38\uC5D0 \n 1on1\uC744 \uC8FC\uC800\uD558\uACE0 \uACC4\uC2E0\uAC00\uC694?",
    balloons: {
      balloon1: {
        text: '1on1 \uBBF8\uD305\uC774 \uC800\uC640 \uD300\uC6D0 \uBAA8\uB450\uC5D0\uAC8C \uB3C4\uC6C0\uC774 \uB420 \uAC83\uC740 \uC544\uB294\uB370,<br />\uC774\uB7EC\uD55C <span style="font-weight: 600">\uACBD\uD5D8\uC774 \uC5C6\uC5B4\uC11C \uC2DC\uC791\uD558\uAE30\uAC00 \uB450\uB824\uC6CC\uC694.</span>',
        text_app: '1on1 \uBBF8\uD305\uC774 \uC800\uC640 \uD300\uC6D0 \uBAA8\uB450\uC5D0\uAC8C \uB3C4\uC6C0\uC774 \uB420 \uAC83\uC740 \uC544\uB294\uB370,<br />\uC774\uB7EC\uD55C <span style="font-weight: 600">\uACBD\uD5D8\uC774 \uC5C6\uC5B4\uC11C \uC2DC\uC791\uD558\uAE30\uAC00 \uB450\uB824\uC6CC\uC694.</span>'
      },
      balloon2: {
        text: '\uC548 \uADF8\uB798\uB3C4 \uB9CE\uC740 \uBB38\uC11C \uC5C5\uBB34\uAC00 <span style="font-weight: 600">1on1 \uB54C\uBB38\uC5D0 \uB354 \uB298\uC5B4\uB0A0\uAE4C \uBD10 \uC5C4\uB450\uAC00 \uC548 \uB098\uC694.</span>',
        text_app: '\uC548 \uADF8\uB798\uB3C4 \uB9CE\uC740 \uBB38\uC11C \uC5C5\uBB34\uAC00 <br /><span style="font-weight: 600">1on1 \uB54C\uBB38\uC5D0 \uB354 \uB298\uC5B4\uB0A0\uAE4C \uBD10 \uC5C4\uB450\uAC00 \uC548 \uB098\uC694.</span>'
      },
      balloon3: {
        text: '\uD300\uC6D0\uB4E4\uC758 \uD604\uC7AC \uC0C1\uD0DC\uB098 \uC7A0\uC7AC\uB825\uC744 \uD30C\uC545\uD558\uAE30\uAC00 \uC5B4\uB824\uC6CC\uC694.<br /><span style="font-weight: 600">\uB204\uAC00 \uD798\uB4E4\uC5B4\uD558\uB294\uC9C0, \uB204\uAC00 \uB354 \uC131\uC7A5\uD560 \uC218 \uC788\uB294\uC9C0 \uAC10\uC73C\uB85C\uB9CC \uD310\uB2E8\uD558\uACE0 \uC788\uC5B4\uC694.</span>',
        text_app: '\uD300\uC6D0\uB4E4\uC758 \uD604\uC7AC \uC0C1\uD0DC\uB098 \uC7A0\uC7AC\uB825\uC744 \uD30C\uC545\uD558\uAE30\uAC00 \uC5B4\uB824\uC6CC\uC694.<br /><span style="font-weight: 600">\uB204\uAC00 \uD798\uB4E4\uC5B4\uD558\uB294\uC9C0, \uB204\uAC00 \uB354 \uC131\uC7A5\uD560 \uC218 \uC788\uB294\uC9C0 \uAC10\uC73C\uB85C\uB9CC<br />\uD310\uB2E8\uD558\uACE0 \uC788\uC5B4\uC694.</span>'
      },
      balloon4: {
        text: '<span style="font-weight: 600">\uBC14\uC05C \uC2DC\uAC04\uC744 \uC37C\uB294\uB370 \uC2E4\uC9C8\uC801\uC778 \uC131\uACFC \uC5C6\uC774 \uC548\uBD80\uB9CC \uBB3B\uB2E4 \uB05D\uB0A0\uAE4C \uBD10</span> \uAC71\uC815\uB3FC\uC694.',
        text_app: '<span style="font-weight: 600">\uBC14\uC05C \uC2DC\uAC04\uC744 \uC37C\uB294\uB370 \uC2E4\uC9C8\uC801\uC778 \uC131\uACFC \uC5C6\uC774 <br />\uC548\uBD80\uB9CC \uBB3B\uB2E4 \uB05D\uB0A0\uAE4C \uBD10</span> \uAC71\uC815\uB3FC\uC694.'
      }
    },
    nontitle: `\uC624\uBE14\uB9BF\uC774 1on1\uC758 \uBAA8\uB4E0 \uBD80\uB2F4\uC744<br /><span style="color: #228BE6">'\uC131\uC7A5\uC758 \uAE30\uD68C'</span>\uB85C \uBC14\uAFD4\uB4DC\uB9BD\uB2C8\uB2E4.`,
    nontitle_mobile: `\uC624\uBE14\uB9BF\uC774 1on1\uC758 \uBAA8\uB4E0 \uBD80\uB2F4\uC744<br /><span style="color: #228BE6">'\uC131\uC7A5\uC758 \uAE30\uD68C'</span>\uB85C \uBC14\uAFD4\uB4DC\uB9BD\uB2C8\uB2E4.`,
    nontitle_desktop: `\uC624\uBE14\uB9BF\uC774 1on1\uC758 \uBAA8\uB4E0 \uBD80\uB2F4\uC744 <span style="color: #228BE6">'\uC131\uC7A5\uC758 \uAE30\uD68C'</span>\uB85C \uBC14\uAFD4\uB4DC\uB9BD\uB2C8\uB2E4.`
  },
  nonAdoptedCards: [
    {
      number: "1",
      title: "\uC624\uBE14\uB9BF\uC774 \uC9C1\uC6D0 \uC131\uC7A5\uACFC \uC9C4\uB2E8\uC5D0 \uCD08\uC810\uC744 \uB9DE\uCD98 \uC9C8\uBB38 \uD15C\uD50C\uB9BF\uACFC,<br />\uC6D0\uD558\uC2DC\uB294 \uC8FC\uC81C\uC5D0 \uB9DE\uB294 \uC9C8\uBB38\uC744 \uC0DD\uC131\uD574\uC8FC\uB294 AI\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      appTitle: "\uC624\uBE14\uB9BF\uC774 \uC9C1\uC6D0 \uC131\uC7A5\uACFC \uC9C4\uB2E8\uC5D0 \uCD08\uC810\uC744 \uB9DE\uCD98<br />\uC9C8\uBB38 \uD15C\uD50C\uB9BF\uACFC, \uC6D0\uD558\uC2DC\uB294 \uC8FC\uC81C\uC5D0 \uB9DE\uB294 \uC9C8\uBB38\uC744<br />\uC0DD\uC131\uD574\uC8FC\uB294 AI\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      description: "\uC624\uBE14\uB9BF 1on1 AI\uAC00 \uD300\uC6D0\uC758 \uC0C1\uD669\uC744 \uC9C4\uB2E8\uBD80\uD130 \uC131\uC7A5\uC744 \uC774\uB044\uB294 \uCF54\uCE6D \uC9C8\uBB38\uAE4C\uC9C0<br />\uC54C\uC544\uC11C \uCD94\uCC9C\uD569\uB2C8\uB2E4. \uD300\uC7A5\uB2D8\uC740 \uB354 \uC774\uC0C1 \uBB34\uC2A8 \uB9D0\uC744 \uD560\uC9C0 \uACE0\uBBFC\uD560 \uD544\uC694 \uC5C6\uC774<br />\uB300\uD654\uC5D0\uB9CC \uC9D1\uC911\uD558\uBA74 \uB429\uB2C8\uB2E4.",
      appDescription: "\uC624\uBE14\uB9BF 1on1 AI\uAC00 \uD300\uC6D0\uC758 \uC0C1\uD669\uC744 \uC9C4\uB2E8\uBD80\uD130 \uC131\uC7A5\uC744 \uC774\uB044\uB294<br />\uCF54\uCE6D \uC9C8\uBB38\uAE4C\uC9C0 \uC54C\uC544\uC11C \uCD94\uCC9C\uD569\uB2C8\uB2E4. \uD300\uC7A5\uB2D8\uC740 \uB354 \uC774\uC0C1<br />\uBB34\uC2A8 \uB9D0\uC744 \uD560\uC9C0 \uACE0\uBBFC\uD560 \uD544\uC694 \uC5C6\uC774 \uB300\uD654\uC5D0\uB9CC \uC9D1\uC911\uD558\uBA74 \uB429\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/nonadopted01.png",
      imageAlt: "1on1 \uBBF8\uD305 \uAC00\uC774\uB4DC"
    },
    {
      number: "2",
      title: "\uBAA8\uB4E0 \uAE30\uB85D\uACFC \uC815\uB9AC\uB294 AI\uAC00 \uB300\uC2E0\uD574 \uC90D\uB2C8\uB2E4.",
      appTitle: "\uBAA8\uB4E0 \uAE30\uB85D\uACFC \uC815\uB9AC\uB294 AI\uAC00 \uB300\uC2E0\uD574 \uC90D\uB2C8\uB2E4.",
      description: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uB300\uD654\uB85D, \uD575\uC2EC \uC694\uC57D, \uC2E4\uD589\uD560 Action Item\uAE4C\uC9C0<br />\uC790\uB3D9\uC73C\uB85C \uC0DD\uC131\uB429\uB2C8\uB2E4. \uD300\uC7A5\uB2D8\uC740 \uBBF8\uD305 \uD6C4 \uBCC4\uB3C4\uC758 \uBB38\uC11C \uC791\uC5C5 \uC5C6\uC774,<br />\uB2E4\uC74C \uC131\uACFC\uB97C \uB9CC\uB4DC\uB294 \uB370\uB9CC \uC9D1\uC911\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uB300\uD654\uB85D, \uD575\uC2EC \uC694\uC57D, \uC2E4\uD589\uD560 Action Item\uAE4C\uC9C0<br />\uC790\uB3D9\uC73C\uB85C \uC0DD\uC131\uB429\uB2C8\uB2E4. \uD300\uC7A5\uB2D8\uC740 \uBBF8\uD305 \uD6C4 \uBCC4\uB3C4\uC758 \uBB38\uC11C \uC791\uC5C5 \uC5C6\uC774,<br />\uB2E4\uC74C \uC131\uACFC\uB97C \uB9CC\uB4DC\uB294 \uB370\uB9CC \uC9D1\uC911\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/nonadopted02.png",
      imageAlt: "AI \uC790\uB3D9 \uAE30\uB85D"
    },
    {
      number: "3",
      title: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74, \uD300\uC7A5\uB2D8\uC744 \uC704\uD55C `\uB9AC\uB354\uC2ED \uB9AC\uD3EC\uD2B8`\uAC00 \uBC1C\uD589\uB429\uB2C8\uB2E4.",
      appTitle: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74, \uD300\uC7A5\uB2D8\uC744 \uC704\uD55C<br />`\uB9AC\uB354\uC2ED \uB9AC\uD3EC\uD2B8`\uAC00 \uBC1C\uD589\uB429\uB2C8\uB2E4.",
      description: '\uC774\uAC83\uC774 \uC624\uBE14\uB9BF\uC758 \uD575\uC2EC\uC785\uB2C8\uB2E4. AI\uAC00 \uD300\uC7A5\uB2D8\uC758 \uBC1C\uC5B8 \uBE44\uC911, \uC9C8\uBB38\uC758 \uC720\uD615<br />\uB4F1\uC744 \uBD84\uC11D\uD558\uC5EC "\uACBD\uCCAD\uD558\uB294 \uB9AC\uB354", "\uC131\uC7A5\uC744 \uB3D5\uB294 \uB9AC\uB354"\uB85C \uC131\uC7A5\uD560 \uC218 \uC788\uB3C4\uB85D<br />\uB9DE\uCDA4\uD615 \uCF54\uCE6D\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4. 1on1\uC744 \uD560\uC218\uB85D \uD300\uC7A5\uB2D8\uC758 \uB9AC\uB354\uC2ED \uC5ED\uB7C9\uB3C4<br />\uD568\uAED8 \uC131\uC7A5\uD569\uB2C8\uB2E4',
      appDescription: '\uC774\uAC83\uC774 \uC624\uBE14\uB9BF\uC758 \uD575\uC2EC\uC785\uB2C8\uB2E4. AI\uAC00 \uD300\uC7A5\uB2D8\uC758 \uBC1C\uC5B8 \uBE44\uC911,<br />\uC9C8\uBB38\uC758 \uC720\uD615 \uB4F1\uC744 \uBD84\uC11D\uD558\uC5EC "\uACBD\uCCAD\uD558\uB294 \uB9AC\uB354", "\uC131\uC7A5\uC744 \uB3D5\uB294 \uB9AC\uB354"\uB85C<br />\uC131\uC7A5\uD560 \uC218 \uC788\uB3C4\uB85D \uB9DE\uCDA4\uD615 \uCF54\uCE6D\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4.<br />1on1\uC744 \uD560\uC218\uB85D \uD300\uC7A5\uB2D8\uC758 \uB9AC\uB354\uC2ED \uC5ED\uB7C9\uB3C4 \uD568\uAED8 \uC131\uC7A5\uD569\uB2C8\uB2E4',
      imageSrc: "/images/landing_m/nonadopted03.png",
      imageAlt: "\uB9AC\uB354\uC2ED \uB9AC\uD3EC\uD2B8"
    },
    {
      number: "4",
      title: "\uAC01 \uD300\uC6D0\uC744 \uBD84\uC11D\uD55C IC \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      appTitle: "\uAC01 \uD300\uC6D0\uC744 \uBD84\uC11D\uD55C IC \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      description: "AI\uAC00 \uB300\uD654\uB0B4\uC6A9\uC744 \uBD84\uC11D\uD574 \uD300\uC6D0\uC744 \uC6C0\uC9C1\uC774\uB294 \uB3D9\uAE30, \uAC15\uC810, \uB2E8\uC810\uACFC \uC5B4\uB5BB\uAC8C \uAD00\uB9AC\uD574\uC57C<br />\uD558\uB294\uC9C0\uB97C \uC815\uB9AC\uD55C \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4. \uBD80\uC871\uD55C \uC815\uBCF4\uB294 \uB2E4\uC74C \uBBF8\uD305 \uC8FC\uC81C\uB85C<br />\uCD94\uCC9C\uD574 \uD300\uC6D0\uC5D0 \uB300\uD55C \uC2EC\uCE35\uC801\uC778 \uAD00\uB9AC\uAC00 \uAC00\uB2A5\uD574\uC9D1\uB2C8\uB2E4.",
      appDescription: "AI\uAC00 \uB300\uD654\uB0B4\uC6A9\uC744 \uBD84\uC11D\uD574 \uD300\uC6D0\uC744 \uC6C0\uC9C1\uC774\uB294 \uB3D9\uAE30, \uAC15\uC810, \uB2E8\uC810\uACFC<br />\uC5B4\uB5BB\uAC8C \uAD00\uB9AC\uD574\uC57C \uD558\uB294\uC9C0\uB97C \uC815\uB9AC\uD55C \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.<br />\uBD80\uC871\uD55C \uC815\uBCF4\uB294 \uB2E4\uC74C \uBBF8\uD305 \uC8FC\uC81C\uB85C \uCD94\uCC9C\uD574<br />\uD300\uC6D0\uC5D0 \uB300\uD55C \uC2EC\uCE35\uC801\uC778 \uAD00\uB9AC\uAC00 \uAC00\uB2A5\uD574\uC9D1\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/nonadopted04.png",
      imageAlt: "IC \uB9AC\uD3EC\uD2B8"
    },
    {
      number: "5",
      title: "\uD300\uC6D0\uC758 \uC7A0\uC7AC\uB825\uACFC \uC704\uD5D8 \uC2E0\uD638\uB97C \uB370\uC774\uD130\uB85C \uD30C\uC545\uD569\uB2C8\uB2E4.",
      appTitle: "\uD300\uC6D0\uC758 \uC7A0\uC7AC\uB825\uACFC \uC704\uD5D8 \uC2E0\uD638\uB97C \uB370\uC774\uD130\uB85C \uD30C\uC545\uD569\uB2C8\uB2E4.",
      description: "AI\uC758 \uAC01 \uD300\uC6D0\uC5D0 \uB300\uD55C \uBD84\uC11D\uC744 \uBC14\uD0D5\uC73C\uB85C<br />9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C \uD1B5\uD574 \uD300\uC6D0\uC758 \uC131\uACFC\uC640 \uC7A0\uC7AC\uB825\uC744 \uC2DC\uAC01\uC801\uC73C\uB85C \uBCF4\uC5EC\uC90D\uB2C8\uB2E4.<br />`\uC870\uC6A9\uD55C \uD1F4\uC0AC` \uC9D5\uD6C4\uAC00 \uBCF4\uC774\uB294 \uD300\uC6D0\uC774\uB098, \uD575\uC2EC \uC778\uC7AC\uB85C \uD0A4\uC6CC\uC57C \uD560 \uD300\uC6D0\uC744<br />\uB370\uC774\uD130 \uAE30\uBC18\uC73C\uB85C \uD30C\uC545\uD558\uACE0 \uC120\uC81C\uC801\uC73C\uB85C \uAD00\uB9AC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "AI\uC758 \uAC01 \uD300\uC6D0\uC5D0 \uB300\uD55C \uBD84\uC11D\uC744 \uBC14\uD0D5\uC73C\uB85C 9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C<br />\uD1B5\uD574 \uD300\uC6D0\uC758 \uC131\uACFC\uC640 \uC7A0\uC7AC\uB825\uC744 \uC2DC\uAC01\uC801\uC73C\uB85C \uBCF4\uC5EC\uC90D\uB2C8\uB2E4.<br />`\uC870\uC6A9\uD55C \uD1F4\uC0AC` \uC9D5\uD6C4\uAC00 \uBCF4\uC774\uB294 \uD300\uC6D0\uC774\uB098, \uD575\uC2EC \uC778\uC7AC\uB85C<br />\uD0A4\uC6CC\uC57C \uD560 \uD300\uC6D0\uC744 \uB370\uC774\uD130 \uAE30\uBC18\uC73C\uB85C \uD30C\uC545\uD558\uACE0<br />\uC120\uC81C\uC801\uC73C\uB85C \uAD00\uB9AC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/nonadopted05.png",
      imageAlt: "\uD300\uC6D0 \uBD84\uC11D \uB300\uC2DC\uBCF4\uB4DC"
    }
  ],
  landingAdopted: {
    title: 'AI\uAC00 <span style="color: #228BE6">\uD300\uC7A5\uB2D8\uC740 \uC720\uB2A5\uD55C \uCF54\uCE58\uB85C, \uD300\uC6D0\uC740 \uD575\uC2EC \uC778\uC7AC</span>\uB85C \uB9CC\uB4ED\uB2C8\uB2E4.',
    subtitle: '\uC624\uBE14\uB9BF\uC73C\uB85C \uB9C9\uB9C9\uD588\uB358 \uD53C\uB4DC\uBC31\uACFC 1on1\uC744 <br /><span style="color: #228BE6">\uD300\uC758 \uAC00\uC7A5 \uAC15\uB825\uD55C \uC131\uC7A5 \uC5D4\uC9C4\uC73C\uB85C \uBC14\uAFB8\uC138\uC694.</span>',
    subtitle_app: '\uC624\uBE14\uB9BF\uC73C\uB85C \uB9C9\uB9C9\uD588\uB358<br />\uD53C\uB4DC\uBC31\uACFC 1on1\uC744 <span style="color: #228BE6">\uD300\uC758 \uAC00\uC7A5 \uAC15\uB825\uD55C<br />\uC131\uC7A5 \uC5D4\uC9C4\uC73C\uB85C \uBC14\uAFB8\uC138\uC694.</span>',
    question: "\uD639\uC2DC \uC774\uB7F0 \uACE0\uBBFC, \uD63C\uC790 \uB059\uB059 \uC553\uACE0 \uACC4\uC2E0\uAC00\uC694?",
    balloons: [
      {
        text: '\uC81C\uAC00 \uC8FC\uB294 \uD53C\uB4DC\uBC31\uC774 \uD300\uC6D0\uC758 \uC131\uC7A5\uC744 \uB3D5\uB294 \uAC8C \uC544\uB2C8\uB77C, <span style="font-weight: 600">\uC624\uD788\uB824 \uAD00\uACC4\uB97C \uC5B4\uC0C9\uD558\uAC8C \uB9CC\uB4E4\uAE4C \uBD10 \uB298 \uB9DD\uC124\uC5EC\uC838\uC694.</span>',
        text_app: '\uC81C\uAC00 \uC8FC\uB294 \uD53C\uB4DC\uBC31\uC774 \uD300\uC6D0\uC758 \uC131\uC7A5\uC744 \uB3D5\uB294 \uAC8C \uC544\uB2C8\uB77C,<br /><span style="font-weight: 600">\uC624\uD788\uB824 \uAD00\uACC4\uB97C \uC5B4\uC0C9\uD558\uAC8C \uB9CC\uB4E4\uAE4C \uBD10 \uB298 \uB9DD\uC124\uC5EC\uC838\uC694.</span>'
      },
      {
        text: '<span style="font-weight: 600">1on1 \uC900\uBE44\uC640 \uAE30\uB85D \uC815\uB9AC\uC5D0 \uC4F0\uB294 \uC2DC\uAC04\uC774 \uB108\uBB34 \uB9CE\uC774 \uAC78\uB824 <br /></span>\uC81C\uB300\uB85C \uC815\uB9AC\uB97C \uBABB\uD558\uB2C8\uAE4C \uB9C9\uC0C1 \uBBF8\uD305\uD558\uACE0 \uB098\uC11C \uB0B4\uC6A9\uC774 \uD718\uBC1C\uB418\uB294 \uACBD\uC6B0\uAC00 \uB9CE\uC544\uC694',
        text_app: '<span style="font-weight: 600">1on1 \uC900\uBE44\uC640 \uAE30\uB85D \uC815\uB9AC\uC5D0 \uC4F0\uB294 \uC2DC\uAC04\uC774 \uB108\uBB34 \uB9CE\uC774 \uAC78\uB824 <br /></span>\uC81C\uB300\uB85C \uC815\uB9AC\uB97C \uBABB\uD558\uB2C8\uAE4C \uB9C9\uC0C1 \uBBF8\uD305\uD558\uACE0 \uB098\uC11C<br />\uB0B4\uC6A9\uC774 \uD718\uBC1C\uB418\uB294 \uACBD\uC6B0\uAC00 \uB9CE\uC544\uC694'
      },
      {
        text: '1on1 \uBBF8\uD305\uC744 \uD558\uACE0 \uC815\uB9AC\uB3C4 \uD588\uB294\uB370, <span style="font-weight: 600">\uADF8 \uC774\uD6C4\uC5D0 \uC774\uAC78\uB85C \uBB58 \uC5B4\uB5BB\uAC8C \uD574\uC57C\uD560\uC9C0 \uBAA8\uB974\uACA0\uC5B4\uC694.</span>',
        text_app: '1on1 \uBBF8\uD305\uC744 \uD558\uACE0 \uC815\uB9AC\uB3C4 \uD588\uB294\uB370,<br /><span style="font-weight: 600">\uADF8 \uC774\uD6C4\uC5D0 \uC774\uAC78\uB85C \uBB58 \uC5B4\uB5BB\uAC8C \uD574\uC57C\uD560\uC9C0 \uBAA8\uB974\uACA0\uC5B4\uC694.</span>'
      },
      {
        text: '\uBD84\uBA85 1on1\uC744 \uD558\uACE0 \uC788\uB294\uB370, <span style="font-weight: 600">\uC804\uBB38\uC801\uC778 \uC2A4\uD0AC\uC774 \uC5C6\uB2E4 \uBCF4\uB2C8<br />\uB300\uD654\uAC00 \uAC89\uB3CC\uACE0 \uD300\uC6D0\uC758 \uC9C4\uC9DC \uC18D\uB9C8\uC74C\uC740 \uB193\uCE58\uB294 \uAC83 \uAC19\uC544\uC694.</span>',
        text_app: '\uBD84\uBA85 1on1\uC744 \uD558\uACE0 \uC788\uB294\uB370, <span style="font-weight: 600">\uC804\uBB38\uC801\uC778 \uC2A4\uD0AC\uC774 \uC5C6\uB2E4 \uBCF4\uB2C8<br />\uB300\uD654\uAC00 \uAC89\uB3CC\uACE0 \uD300\uC6D0\uC758 \uC9C4\uC9DC \uC18D\uB9C8\uC74C\uC740 \uB193\uCE58\uB294 \uAC83 \uAC19\uC544\uC694.</span>'
      },
      {
        text: '\uB300\uD45C\uB2D8\uC740 \uAE30\uB300\uB97C \uD558\uC2DC\uB294\uB370, <span style="font-weight: 600">\uC81C\uB300\uB85C \uB41C \uB9AC\uB354\uC2ED \uAD50\uC721 \uD55C\uBC88 \uBC1B\uC544\uBCF8 \uC801 \uC5C6\uC5B4 1on1 \uD560 \uB54C\uB9CC \uB418\uBA74 \uB298 \uBD88\uC548\uD569\uB2C8\uB2E4.</span>',
        text_app: '\uB300\uD45C\uB2D8\uC740 \uAE30\uB300\uB97C \uD558\uC2DC\uB294\uB370, <span style="font-weight: 600">\uC81C\uB300\uB85C \uB41C \uB9AC\uB354\uC2ED \uAD50\uC721<br />\uD55C\uBC88 \uBC1B\uC544\uBCF8 \uC801 \uC5C6\uC5B4 1on1 \uD560 \uB54C\uB9CC \uB418\uBA74 \uB298 \uBD88\uC548\uD569\uB2C8\uB2E4.</span>'
      }
    ],
    adopted_title: "\uC624\uBE14\uB9BF\uC774 \uD300\uC7A5\uB2D8\uACFC \uD300\uC6D0 \uBAA8\uB450\uC758 \uC131\uC7A5\uC744 \uD568\uAED8\uD569\uB2C8\uB2E4.",
    adopted_title_app: "\uC624\uBE14\uB9BF\uC774 <br />\uD300\uC7A5\uB2D8\uACFC \uD300\uC6D0 \uBAA8\uB450\uC758 \uC131\uC7A5\uC744 \uD568\uAED8\uD569\uB2C8\uB2E4."
  },
  adoptedCards: [
    {
      number: "1",
      title: "\uD300\uC7A5\uB2D8\uB9CC\uC744 \uC704\uD55C \uB9AC\uB354\uC2ED \uCF54\uCE58\uAC00 \uC0DD\uAE41\uB2C8\uB2E4.",
      appTitle: "\uD300\uC7A5\uB2D8\uB9CC\uC744 \uC704\uD55C \uB9AC\uB354\uC2ED \uCF54\uCE58\uAC00 \uC0DD\uAE41\uB2C8\uB2E4.",
      description: "1on1 \uBBF8\uD305\uC774 \uB05D\uB098\uBA74, AI\uAC00 \uD300\uC7A5\uB2D8\uC758 \uB300\uD654 \uBC29\uC2DD\uC744 \uBD84\uC11D\uD574\n\uAC1C\uC778\uD654\uB41C \uB9AC\uB354\uC2ED \uCF54\uCE6D\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4. \uBCC4\uB3C4\uC758 \uAD50\uC721 \uC5C6\uC774 \uC2E4\uC804\uC744 \uD1B5\uD574\n\uAC00\uC7A5 \uBE60\uB974\uAC8C \uC131\uC7A5\uD558\uC5EC, \uD300\uC6D0\uC5D0\uAC8C \uC2E0\uB8B0\uBC1B\uB294 \uB9AC\uB354\uAC00 \uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "1on1 \uBBF8\uD305\uC774 \uB05D\uB098\uBA74, AI\uAC00 \uD300\uC7A5\uB2D8\uC758 \uB300\uD654 \uBC29\uC2DD\uC744 \uBD84\uC11D\uD574\n\uAC1C\uC778\uD654\uB41C \uB9AC\uB354\uC2ED \uCF54\uCE6D\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4. \uBCC4\uB3C4\uC758 \uAD50\uC721 \uC5C6\uC774 \uC2E4\uC804\uC744 \uD1B5\uD574\n\uAC00\uC7A5 \uBE60\uB974\uAC8C \uC131\uC7A5\uD558\uC5EC, \uD300\uC6D0\uC5D0\uAC8C \uC2E0\uB8B0\uBC1B\uB294 \uB9AC\uB354\uAC00 \uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/adopted001.png",
      imageAlt: "1on1 \uBBF8\uD305 \uAC00\uC774\uB4DC"
    },
    {
      number: "2",
      title: "\uD300\uC6D0\uC758 \uC7A0\uC7AC\uB825\uC744 \uAE68\uC6B0\uB294 `\uC131\uC7A5 \uB370\uC774\uD130`\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      appTitle: "\uD300\uC6D0\uC758 \uC7A0\uC7AC\uB825\uC744 \uAE68\uC6B0\uB294 `\uC131\uC7A5 \uB370\uC774\uD130`\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
      description: "AI\uAC00 1on1 \uB300\uD654\uC640 \uC5C5\uBB34 \uAE30\uB85D\uC744 \uBD84\uC11D\uD574 \uD300\uC6D0\uC758 \uAC15\uC810\uACFC \uBCF4\uC644\uC810\uC744 \uB2F4\uC740 \uB9AC\uD3EC\uD2B8<br />\uC640 \uD300\uC6D0\uC758 \uC5ED\uB7C9\uACFC \uC0C1\uD0DC\uB97C \uC9C4\uB2E8\uD558\uB294 9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C \uC0DD\uC131\uD569\uB2C8\uB2E4. \uC774 \uB370\uC774\uD130\uB97C<br />\uAE30\uBC18\uC73C\uB85C \uD300\uC6D0 \uAC1C\uAC1C\uC778\uC5D0\uAC8C \uAF2D \uD544\uC694\uD55C \uC131\uC7A5 \uACBD\uB85C\uB97C \uD568\uAED8 \uC124\uACC4\uD558\uACE0, \uC7A0\uC7AC\uB825\uC744<br />\uCD5C\uB300\uD55C\uC73C\uB85C \uC774\uB04C\uC5B4\uB0BC \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "AI\uAC00 1on1 \uB300\uD654\uC640 \uC5C5\uBB34 \uAE30\uB85D\uC744 \uBD84\uC11D\uD574 \uD300\uC6D0\uC758<br />\uAC15\uC810\uACFC \uBCF4\uC644\uC810\uC744 \uB2F4\uC740 \uB9AC\uD3EC\uD2B8\uC640 \uD300\uC6D0\uC758 \uC5ED\uB7C9\uACFC \uC0C1\uD0DC\uB97C<br />\uC9C4\uB2E8\uD558\uB294 9Box \uB300\uC2DC\uBCF4\uB4DC\uB97C \uC0DD\uC131\uD569\uB2C8\uB2E4.<br />\uC774 \uB370\uC774\uD130\uB97C \uAE30\uBC18\uC73C\uB85C \uD300\uC6D0 \uAC1C\uAC1C\uC778\uC5D0\uAC8C \uAF2D \uD544\uC694\uD55C<br />\uC131\uC7A5 \uACBD\uB85C\uB97C \uD568\uAED8 \uC124\uACC4\uD558\uACE0, \uC7A0\uC7AC\uB825\uC744<br />\uCD5C\uB300\uD55C\uC73C\uB85C \uC774\uB04C\uC5B4\uB0BC \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/adopted002.png",
      imageAlt: "AI \uC790\uB3D9 \uAE30\uB85D"
    },
    {
      number: "3",
      title: "1on1 \uBC16\uC5D0\uC11C\uB3C4, AI\uAC00 \uAC74\uAC15\uD55C \uD53C\uB4DC\uBC31\uC744 \uB3D5\uC2B5\uB2C8\uB2E4.",
      appTitle: "1on1 \uBC16\uC5D0\uC11C\uB3C4, AI\uAC00 \uAC74\uAC15\uD55C \uD53C\uB4DC\uBC31\uC744 \uB3D5\uC2B5\uB2C8\uB2E4.",
      description: "\uC624\uBE14\uB9BF\uC758 Feedback AI\uB294 \uC77C\uC0C1\uC801\uC778 \uC5C5\uBB34 \uD53C\uB4DC\uBC31\uC744 \uC791\uC131\uD560 \uB54C\uB3C4<br />\uB3C4\uC6C0\uC744 \uC90D\uB2C8\uB2E4. \uAC10\uC815\uC801\uC778 \uD45C\uD604 \uB300\uC2E0 \uB370\uC774\uD130\uC5D0 \uAE30\uBC18\uD55C \uAC74\uC124\uC801\uC778<br />\uD53C\uB4DC\uBC31 \uCD08\uC548\uC744 \uC81C\uC548\uD558\uC5EC, \uD300\uC6D0\uC774 \uB9C8\uC74C \uC5F4\uACE0 \uBC1B\uC544\uB4E4\uC774\uB294<br />\uAE0D\uC815\uC801\uC778 \uD53C\uB4DC\uBC31 \uBB38\uD654\uB97C \uB9CC\uB4E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "\uC624\uBE14\uB9BF\uC758 Feedback AI\uB294 \uC77C\uC0C1\uC801\uC778 \uC5C5\uBB34 \uD53C\uB4DC\uBC31\uC744<br />\uC791\uC131\uD560 \uB54C\uB3C4 \uB3C4\uC6C0\uC744 \uC90D\uB2C8\uB2E4. \uAC10\uC815\uC801\uC778 \uD45C\uD604 \uB300\uC2E0<br />\uB370\uC774\uD130\uC5D0 \uAE30\uBC18\uD55C \uAC74\uC124\uC801\uC778 \uD53C\uB4DC\uBC31 \uCD08\uC548\uC744 \uC81C\uC548\uD558\uC5EC,<br />\uD300\uC6D0\uC774 \uB9C8\uC74C \uC5F4\uACE0 \uBC1B\uC544\uB4E4\uC774\uB294<br />\uAE0D\uC815\uC801\uC778 \uD53C\uB4DC\uBC31 \uBB38\uD654\uB97C \uB9CC\uB4E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/adopted003.png",
      imageAlt: "\uB9AC\uB354\uC2ED \uB9AC\uD3EC\uD2B8"
    },
    {
      number: "4",
      title: "\uBAA8\uB4E0 \uBB38\uC11C \uC791\uC5C5\uC740 AI\uC5D0\uAC8C \uB9E1\uAE30\uACE0, `\uC131\uC7A5`\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694.",
      appTitle: "\uBAA8\uB4E0 \uBB38\uC11C \uC791\uC5C5\uC740 AI\uC5D0\uAC8C \uB9E1\uAE30\uACE0, `\uC131\uC7A5`\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694.",
      description: "\uBBF8\uD305 \uB0B4\uC6A9\uACFC \uD53C\uB4DC\uBC31 \uAE30\uB85D\uC740 \uC790\uB3D9\uC73C\uB85C \uC815\uB9AC\uB418\uACE0 \uC790\uC0B0\uCC98\uB7FC \uC313\uC785\uB2C8\uB2E4.<br />\uD300\uC7A5\uB2D8\uC740 \uADC0\uCC2E\uC740 \uBB38\uC11C \uC791\uC5C5\uC5D0\uC11C \uC644\uC804\uD788 \uD574\uBC29\uB418\uC5B4, \uD300\uC7A5\uACFC \uD300\uC6D0 \uBAA8\uB450\uC758<br />`\uC131\uC7A5`\uC774\uB77C\uB294 \uAC00\uC7A5 \uC911\uC694\uD55C \uBCF8\uC9C8\uC5D0\uB9CC \uC9D1\uC911\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      appDescription: "\uBBF8\uD305 \uB0B4\uC6A9\uACFC \uD53C\uB4DC\uBC31 \uAE30\uB85D\uC740 \uC790\uB3D9\uC73C\uB85C \uC815\uB9AC\uB418\uACE0 \uC790\uC0B0\uCC98\uB7FC \uC313\uC785\uB2C8\uB2E4.<br />\uD300\uC7A5\uB2D8\uC740 \uADC0\uCC2E\uC740 \uBB38\uC11C \uC791\uC5C5\uC5D0\uC11C \uC644\uC804\uD788 \uD574\uBC29\uB418\uC5B4, \uD300\uC7A5\uACFC \uD300\uC6D0 \uBAA8\uB450\uC758<br />`\uC131\uC7A5`\uC774\uB77C\uB294 \uAC00\uC7A5 \uC911\uC694\uD55C \uBCF8\uC9C8\uC5D0\uB9CC \uC9D1\uC911\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      imageSrc: "/images/landing_m/adopted004.png",
      imageAlt: "IC \uB9AC\uD3EC\uD2B8"
    }
  ],
  landingMenu: {
    product: "\uC81C\uD488",
    solution: "\uC194\uB8E8\uC158",
    adopted: "1on1\uC744 \uD558\uACE0 \uC788\uB294 \uD300",
    nonadopted: "\uB3C4\uC785\uC744 \uD558\uC9C0 \uC54A\uC740 \uD300",
    pricing: "\uAC00\uACA9",
    insight: "\uC778\uC0AC\uC774\uD2B8",
    youtube: "\uC720\uD29C\uBE0C",
    blog: "\uBE14\uB85C\uADF8",
    newsletter: "\uB274\uC2A4\uB808\uD130"
  }
};

// src/locales/ko-KR/ai.json
var ai_default2 = {
  chat: {
    history: "\uCC44\uD305 \uD788\uC2A4\uD1A0\uB9AC",
    noHandler: "\uC138\uC158 {{id}}\uC774(\uAC00) \uC120\uD0DD\uB418\uC5C8\uC9C0\uB9CC, \uD578\uB4E4\uB7EC\uAC00 \uC81C\uACF5\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4",
    toggleCollapse: "\uD1A0\uAE00 \uC811\uAE30 \uD578\uB4E4\uB7EC\uAC00 \uC81C\uACF5\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4",
    newChat: "\uC0C8 \uCC44\uD305",
    emptyState: "\uC544\uC9C1 \uCC44\uD305 \uC138\uC158\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
    loading: "\uCC44\uD305 \uC138\uC158 \uB85C\uB529 \uC911...",
    error: "\uCC44\uD305 \uC138\uC158 \uB85C\uB529 \uC911 \uC624\uB958 \uBC1C\uC0DD",
    delete: "\uCC44\uD305 \uC0AD\uC81C",
    rename: "\uCC44\uD305 \uC774\uB984 \uBCC0\uACBD",
    confirmDelete: "\uC774 \uCC44\uD305\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    search: "\uCC44\uD305 \uAC80\uC0C9",
    greeting: "\uC548\uB155\uD558\uC138\uC694 <strong>{{name}}</strong>\uB2D8!",
    thinking: "\uC0DD\uAC01 \uC911",
    backToPrevious: "\uC774\uC804\uC73C\uB85C",
    errorMessage: "\uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4: {{message}}",
    aiRequestError: "AI \uC694\uCCAD \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694."
  },
  features: {
    featureSelected: "\uAE30\uB2A5 {{id}}\uC774(\uAC00) \uC120\uD0DD\uB418\uC5C8\uC9C0\uB9CC, \uD578\uB4E4\uB7EC\uAC00 \uC81C\uACF5\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4",
    getTodo: {
      title: "\uC77C\uC815 \uC870\uD68C",
      description: "\uB098\uC640 \uBA64\uBC84\uC758 \uC77C\uC815\uC744 \uC870\uD68C\uD574\uBCF4\uC138\uC694.",
      example1: 'ex) "\uC800\uBC88 \uC8FC \uBAA9\uC694\uC77C\uC5D0 \uBB34\uC2A8 \uC77C \uD588\uB354\uB77C?"',
      example2: 'ex) "\uC624\uB298 \uD300\uC6D0\uC774 \uC5B4\uB5A4 \uC77C\uC744 \uD558\uB294\uC9C0 \uC54C\uB824\uC918"'
    },
    createTodo: {
      title: "\uD560 \uC77C \uCD94\uAC00",
      description: "\uC0C8\uB85C\uC6B4 \uD560 \uC77C\uC744 \uC190\uC27D\uAC8C \uCD94\uAC00\uD558\uC138\uC694.",
      instruction1: "\uB0A0\uC9DC, \uC2DC\uAC04, \uC81C\uBAA9\uC744 \uD3EC\uD568\uD55C \uC77C\uC815 \uC0DD\uC131 \uC694\uCCAD\uC744 \uC785\uB825\uD558\uC138\uC694.",
      instruction2: "\uC7A5\uC18C\uB098 \uCC38\uC11D\uC790 \uC815\uBCF4\uB3C4 \uD568\uAED8 \uC785\uB825\uD558\uBA74 \uB354 \uC815\uD655\uD55C \uC77C\uC815\uC774 \uC0DD\uC131\uB429\uB2C8\uB2E4.",
      example: 'ex) "\uB0B4\uC77C \uC624\uD6C4 2\uC2DC\uBD80\uD130 4\uC2DC\uAE4C\uC9C0 \uC81C\uD488 \uAE30\uD68D \uD68C\uC758 \uC77C\uC815 \uC7A1\uC544\uC918"'
    },
    createReport: {
      title: "\uBCF4\uACE0\uC11C \uC0DD\uC131",
      description: "\uD504\uB85C\uC81D\uD2B8\uC640 \uD560 \uC77C\uC5D0 \uB300\uD55C \uBCF4\uACE0\uC11C\uB97C \uC0DD\uC131\uD569\uB2C8\uB2E4.",
      instructions: "\uBCF4\uACE0\uC11C \uC720\uD615\uACFC \uD300\uC744 \uC120\uD0DD\uD558\uC2E0 \uD6C4 \uBCF4\uACE0\uC11C \uC0DD\uC131\uD558\uAE30 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC138\uC694.",
      alternativeInstructions: "\uD639\uC740 \uC9C1\uC811 \uC6D0\uD558\uB294 \uD615\uC2DD\uACFC \uAE30\uAC04\uC744 \uD3EC\uD568\uD55C \uC694\uCCAD\uC744 \uC785\uB825\uD558\uC138\uC694.",
      reportType: "\uBCF4\uACE0\uC11C \uC720\uD615",
      dailyReport: "\uC77C\uC77C \uBCF4\uACE0\uC11C",
      weeklyReport: "\uC8FC\uAC04 \uBCF4\uACE0\uC11C",
      date: "\uB0A0\uC9DC",
      selectWorkspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC120\uD0DD",
      noWorkspaces: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
      generateReport: "\uBCF4\uACE0\uC11C \uC0DD\uC131\uD558\uAE30",
      todayDailyReportPersonal: "\uC624\uB298 \uB0B4 \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      todayDailyReportTeam: "\uC624\uB298 {{teamName}} \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      yesterdayDailyReportPersonal: "\uC5B4\uC81C \uB0B4 \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      yesterdayDailyReportTeam: "\uC5B4\uC81C {{teamName}} \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      dateDailyReportPersonal: "{{date}} \uB0B4 \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      dateDailyReportTeam: "{{date}} {{teamName}} \uC77C\uC77C\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      weeklyReportPersonal: "{{dateRange}} \uB0B4 \uC8FC\uAC04\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918",
      weeklyReportTeam: "{{dateRange}} {{teamName}} \uC8FC\uAC04\uBCF4\uACE0\uC11C \uC791\uC131\uD574\uC918"
    },
    dateRangeSelector: {
      date: "\uB0A0\uC9DC",
      selectedDate: "\uC120\uD0DD\uB41C \uB0A0\uC9DC",
      apply: "\uC801\uC6A9",
      cancel: "\uCDE8\uC18C",
      clear: "\uCD08\uAE30\uD654",
      weeklyReportMaxDaysError: "\uC8FC\uAC04 \uBCF4\uACE0\uC11C\uB294 7\uC77C\uC744 \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    searchNote: {
      title: "\uB178\uD2B8 \uAC80\uC0C9",
      description: "\uC791\uC131\uD55C \uB178\uD2B8\uB97C \uBE60\uB974\uAC8C \uAC80\uC0C9\uD558\uACE0 \uCC3E\uC544\uBCF4\uC138\uC694.",
      instruction: "\uAC80\uC0C9\uC5B4 \uC55E\uC5D0 #\uC744 \uBD99\uC5EC \uB178\uD2B8\uB97C \uAC80\uC0C9\uD558\uC138\uC694.",
      placeholder: "\uB178\uD2B8 \uAC80\uC0C9 #\uAC80\uC0C9\uC5B4",
      examplePrefix: "ex) \uB178\uD2B8 \uAC80\uC0C9 ",
      exampleTag: "#\uC601\uC5C5 \uC8FC\uAC04 \uD68C\uC758"
    }
  },
  common: {
    send: "\uBCF4\uB0B4\uAE30",
    cancel: "\uCDE8\uC18C",
    save: "\uC800\uC7A5",
    delete: "\uC0AD\uC81C",
    edit: "\uD3B8\uC9D1",
    loading: "\uB85C\uB529 \uC911...",
    error: "\uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4"
  },
  tools: {
    completed: "\uC644\uB8CC\uB428",
    running: "\uC2E4\uD589 \uC911",
    loading: "\uC2E4\uD589 \uC911...",
    inputParameters: "\uC785\uB825 \uD30C\uB77C\uBBF8\uD130",
    visualizationResults: "\uC2DC\uAC01\uD654 \uACB0\uACFC:",
    iframeMode: "iframe \uBAA8\uB4DC",
    directRendering: "\uC9C1\uC811 \uB80C\uB354\uB9C1",
    loadingVisualizationData: "\uC2DC\uAC01\uD654 \uB370\uC774\uD130 \uB85C\uB529 \uC911...",
    unableToDisplayVisualization: "\uC2DC\uAC01\uD654 \uB370\uC774\uD130\uB97C \uD45C\uC2DC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    filename: "\uD30C\uC77C\uBA85:",
    viewRawHtml: "HTML \uC18C\uC2A4 \uBCF4\uAE30",
    viewRendered: "\uB80C\uB354\uB9C1\uB41C \uBDF0 \uBCF4\uAE30",
    renderingError: "\uCF58\uD150\uCE20 \uB80C\uB354\uB9C1 \uC624\uB958",
    searchResults: "\uAC80\uC0C9 \uACB0\uACFC",
    noResults: "\uACB0\uACFC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
    items: "\uAC74",
    item: "\uAC74",
    showMore: "\uB354 \uBCF4\uAE30",
    showLess: "\uC811\uAE30",
    note: "\uB178\uD2B8",
    visualization: "\uC2DC\uAC01\uD654"
  },
  reportCreation: {
    error: "\uC624\uB958",
    success: "\uC131\uACF5",
    loginRequired: "\uB85C\uADF8\uC778\uC774 \uD544\uC694\uD569\uB2C8\uB2E4",
    workspaceRequired: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC815\uBCF4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4",
    createButtonClicked: "\uC0DD\uC131 \uBC84\uD2BC \uD074\uB9AD",
    reportCreated: "{{reportType}}\uAC00 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
    reportCreationError: "{{reportType}} \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
    creationComplete: "\uC0DD\uC131 \uC644\uB8CC",
    aiGeneratedReport: "AI\uAC00 \uC0DD\uC131\uD55C {{reportType}}\uAC00 \uC900\uBE44\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
    clickToAdd: "\uC544\uB798 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC5EC {{reportType}}\uB97C \uCD94\uAC00\uD558\uC138\uC694",
    createReport: "{{reportType}} \uC0DD\uC131\uD558\uAE30",
    workspaceInfo: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC815\uBCF4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4"
  },
  searchNote: {
    results: "\uAC80\uC0C9 \uACB0\uACFC",
    itemsFound: "{{count}}\uAC1C \uAC80\uC0C9\uB428",
    noTitle: "\uC81C\uBAA9 \uC5C6\uC74C",
    noAuthor: "\uC791\uC131\uC790 \uC5C6\uC74C",
    viewNote: "\uB178\uD2B8 \uBCF4\uAE30"
  },
  todoCreation: {
    loading: "\uD560 \uC77C \uC815\uBCF4 \uB85C\uB529 \uC911...",
    loadError: "\uD560 \uC77C \uC815\uBCF4\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
    creationComplete: "\uD560 \uC77C \uC0DD\uC131 \uC644\uB8CC",
    createdAt: "\uC5D0 \uC0DD\uC131",
    schedule: "\uC77C\uC815",
    category: "\uCE74\uD14C\uACE0\uB9AC",
    defaultCategory: "\uCE74\uD14C\uACE0\uB9AC",
    goToPlanner: "\uD50C\uB798\uB108\uB85C \uC774\uB3D9"
  },
  textInput: {
    placeholder: "\uBA54\uC2DC\uC9C0\uB97C \uC785\uB825\uD558\uC138\uC694...",
    send: "\uC804\uC1A1",
    errorMessage: "\uBA54\uC2DC\uC9C0 \uC804\uC1A1 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694."
  },
  logs: {
    enterKeyUpdate: "\uC5D4\uD130 \uD0A4: \uCD5C\uC2E0 \uB0B4\uC6A9\uC73C\uB85C \uC5C5\uB370\uC774\uD2B8",
    onSendCallback: "onSend \uCF5C\uBC31 \uD638\uCD9C - \uC804\uB2EC\uD560 \uBA54\uC2DC\uC9C0",
    inputFieldCleared: "\uBA54\uC2DC\uC9C0 \uC804\uC1A1 \uD6C4 \uC785\uB825 \uD544\uB4DC \uBE44\uC6C0",
    messageSendFailed: "\uBA54\uC2DC\uC9C0 \uC804\uC1A1 \uC2E4\uD328"
  },
  settings: {
    title: "AI \uC124\uC815",
    language: "\uC5B8\uC5B4",
    theme: "\uD14C\uB9C8",
    notifications: "\uC54C\uB9BC",
    privacy: "\uAC1C\uC778\uC815\uBCF4"
  }
};

// src/locales/ko-KR/notification.json
var notification_default2 = {
  title: "\uC54C\uB9BC",
  defaultMessage: "\uC0C8\uB85C\uC6B4 \uC54C\uB9BC\uC774 \uB3C4\uCC29\uD588\uC2B5\uB2C8\uB2E4",
  emptyState: "\uC54C\uB9BC\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
  markAsRead: "\uC77D\uC74C\uC73C\uB85C \uD45C\uC2DC",
  unread_count: "\uC77D\uC9C0 \uC54A\uC74C ({{count}})",
  all: "\uC804\uCCB4",
  timeAgo: "{{time}} \uC804",
  typeLabel: {
    workspace_invite: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300",
    workspace_join: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uCD08\uB300 \uC218\uB77D",
    workspace_out: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAC15\uC81C \uD1F4\uC7A5",
    team_invite: "\uD300 \uCD08\uB300",
    team_out: "\uD300 \uAC15\uC81C \uD1F4\uC7A5",
    goal_priority_changed: "\uD0DC\uC2A4\uD06C \uC6B0\uC120\uC21C\uC704 \uBCC0\uACBD",
    goal_state_changed: "\uD0DC\uC2A4\uD06C \uC0C1\uD0DC \uBCC0\uACBD",
    goal_deadline_changed: "\uD0DC\uC2A4\uD06C \uB9C8\uAC10\uAE30\uD55C \uBCC0\uACBD",
    goal_leader_changed: "\uD0DC\uC2A4\uD06C \uAD00\uB9AC\uC790 \uBCC0\uACBD",
    goal_deadline_reminder: "\uD0DC\uC2A4\uD06C \uB9C8\uAC10\uAE30\uD55C \uC54C\uB9BC",
    todo_assigned: "\uD560 \uC77C \uD560\uB2F9",
    todo_state_changed: "\uD560 \uC77C \uC0C1\uD0DC \uBCC0\uACBD",
    todo_deadline_changed: "\uD560 \uC77C \uB9C8\uAC10\uAE30\uD55C \uBCC0\uACBD",
    todo_deadline_reminder: "\uD560 \uC77C \uB9C8\uAC10\uAE30\uD55C \uC54C\uB9BC",
    todo_time_changed: "\uD560 \uC77C \uC2DC\uAC04 \uBCC0\uACBD",
    project_priority_changed: "\uD504\uB85C\uC81D\uD2B8 \uC6B0\uC120\uC21C\uC704 \uBCC0\uACBD",
    project_state_changed: "\uD504\uB85C\uC81D\uD2B8 \uC0C1\uD0DC \uBCC0\uACBD",
    project_deadline_changed: "\uD504\uB85C\uC81D\uD2B8 \uB9C8\uAC10\uAE30\uD55C \uBCC0\uACBD",
    project_leader_changed: "\uD504\uB85C\uC81D\uD2B8 \uCC45\uC784\uC790 \uBCC0\uACBD",
    project_deadline_reminder: "\uD504\uB85C\uC81D\uD2B8 \uB9C8\uAC10\uAE30\uD55C \uC54C\uB9BC",
    comment: {
      todo: "\uD560 \uC77C \uB313\uAE00",
      goal: "\uD0DC\uC2A4\uD06C \uB313\uAE00",
      note: "\uB178\uD2B8 \uB313\uAE00"
    },
    reply: "\uB2F5\uAE00",
    mention: {
      note: "\uB178\uD2B8\uC5D0\uC11C \uC5B8\uAE09"
    },
    comment_mention: {
      note: "\uB178\uD2B8 \uB313\uAE00\uC5D0\uC11C \uC5B8\uAE09",
      todo: "\uD560 \uC77C \uB313\uAE00\uC5D0\uC11C \uC5B8\uAE09",
      goal: "\uD0DC\uC2A4\uD06C \uB313\uAE00\uC5D0\uC11C \uC5B8\uAE09"
    },
    feedback_comment: "\uD53C\uB4DC\uBC31 \uB313\uAE00",
    daily_scrum_comment: "\uB370\uC77C\uB9AC \uC2A4\uD06C\uB7FC \uB313\uAE00",
    feedback_request: "\uD53C\uB4DC\uBC31 \uC694\uCCAD",
    feedback_request_updated: "\uD53C\uB4DC\uBC31 \uC694\uCCAD",
    feedback_received: "\uD53C\uB4DC\uBC31",
    feedback_updated: "\uD53C\uB4DC\uBC31",
    feedback_reaction: "\uD53C\uB4DC\uBC31 \uBC18\uC751",
    feedback_request_rejected: "\uD53C\uB4DC\uBC31 \uAC70\uC808",
    report_receive: "\uBCF4\uACE0\uC11C \uB3C4\uCC29",
    report_updated: "\uBCF4\uACE0\uC11C \uC5C5\uB370\uC774\uD2B8",
    report_request: "\uBCF4\uACE0\uC11C \uC694\uCCAD",
    plan_time: "\uACC4\uD68D \uC2DC\uAC04 \uC54C\uB9BC",
    plan_request: "\uD50C\uB798\uB108 \uC791\uC131 \uC694\uCCAD",
    system_announcement: "\uC2DC\uC2A4\uD15C \uACF5\uC9C0",
    account_security: "\uACC4\uC815 \uBCF4\uC548 \uC54C\uB9BC"
  },
  workspace: {
    invite: "{{inviter_name}} \uB2D8\uC774 <0>{{workspace_name}}</0>\uC5D0 \uCD08\uB300\uD588\uC5B4\uC694",
    join: "<0>{{joiner_name}}</0> \uB2D8\uC774 <0>{{workspace_name}}</0>\uC5D0 \uCC38\uC5EC\uD588\uC5B4\uC694",
    out: "<0>{{workspace_name}}</0>\uC5D0\uC11C \uD0C8\uD1F4\uB418\uC5C8\uC5B4\uC694"
  },
  team: {
    invite: "{{inviter_name}} \uB2D8\uC774 <0>{{workspace_name}}</0> \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 <0>{{team_name}}</0> \uD300\uC5D0 \uCD08\uB300\uD588\uC5B4\uC694",
    out: "{{workspace_name}}\uC758 <0>{{team_name}}</0> \uD300\uC5D0\uC11C \uD0C8\uD1F4\uB418\uC5C8\uC5B4\uC694"
  },
  task: {
    priorityChanged: "{{changer_name}} \uB2D8\uC774 <0>{{goal_title}}</0>\uC758 \uC6B0\uC120\uC21C\uC704\uB97C <0>{{new_priority}}</0>\uC73C\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    stateChanged: "{{changer_name}} \uB2D8\uC774 <0>{{goal_title}}</0>\uC758 \uC0C1\uD0DC\uB97C <0>{{new_state}}</0>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    deadlineChanged: "{{changer_name}} \uB2D8\uC774 <0>{{goal_title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC744 <0>{{date_range}}</0>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    leaderChanged: "<0>{{goal_title}}</0>\uC758 \uAD00\uB9AC\uC790\uB85C \uBC30\uC815\uB418\uC5C8\uC5B4\uC694",
    deadlineReminder: {
      upcoming: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uB0A8\uC558\uC5B4\uC694",
      today: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>\uC624\uB298</0>\uAE4C\uC9C0\uC608\uC694",
      overdue: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uC9C0\uB0AC\uC5B4\uC694"
    }
  },
  todo: {
    assigned: "{{assigner_name}} \uB2D8\uC774 <0>{{todo_title}}</0>(\uC744)\uB97C \uD560\uB2F9\uD588\uC5B4\uC694",
    stateChanged: "{{changer_name}} \uB2D8\uC774 <0>{{todo_title}}</0>\uC758 \uC0C1\uD0DC\uB97C <0>{{new_state}}</0>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    deadlineChanged: "{{changer_name}} \uB2D8\uC774 <0>{{todo_title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC744 <0>{{date_range}}</0>(\uC73C)\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    timeChanged: "{{changer_name}} \uB2D8\uC774 <0>{{todo_title}}</0>\uC758 \uC2DC\uAC04\uC744 \uBCC0\uACBD\uD588\uC5B4\uC694",
    deadlineReminder: {
      upcoming: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uB0A8\uC558\uC5B4\uC694",
      today: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>\uC624\uB298</0>\uAE4C\uC9C0\uC608\uC694",
      overdue: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uC9C0\uB0AC\uC5B4\uC694"
    },
    plan_time: "\uC624\uB298 \uD560 \uC77C\uC744 \uACC4\uD68D\uD560 \uC2DC\uAC04\uC785\uB2C8\uB2E4. \uC624\uB298\uB3C4 \uD65C\uAE30\uCC2C \uD558\uB8E8\uB97C \uC2DC\uC791\uD574\uBCFC\uAE4C\uC694?"
  },
  project: {
    priorityChanged: "{{changer_name}} \uB2D8\uC774 <0>{{project_title}}</0>\uC758 \uC6B0\uC120\uC21C\uC704\uB97C <0>{{new_priority}}</0>\uC73C\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    stateChanged: "{{changer_name}} \uB2D8\uC774 <0>{{project_title}}</0>\uC758 \uC0C1\uD0DC\uB97C <0>{{new_state}}</0>\uC73C\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    deadlineChanged: "{{changer_name}} \uB2D8\uC774 <0>{{project_title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC744 <0>{{date_range}}</0>\uC73C\uB85C \uBCC0\uACBD\uD588\uC5B4\uC694",
    leaderChanged: "<0>{{project_title}}</0>\uC758 \uCC45\uC784\uC790\uB85C \uBC30\uC815\uB418\uC5C8\uC5B4\uC694",
    deadlineReminder: {
      upcoming: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uB0A8\uC558\uC5B4\uC694",
      today: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>\uC624\uB298</0>\uAE4C\uC9C0\uC608\uC694",
      overdue: "<0>{{title}}</0>\uC758 \uB9C8\uAC10\uAE30\uD55C\uC774 <0>{{days_delta}}</0>\uC77C \uC9C0\uB0AC\uC5B4\uC694"
    }
  },
  communication: {
    comment: {
      goal: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{parent_title}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      todo: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{parent_title}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      project: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{parent_title}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      note: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{parent_title}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      default: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{parent_title}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'"
    },
    reply: "<0>{{replier_name}}</0> \uB2D8\uC774 \uB2F5\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{reply_text}}</0>'",
    mention: "<0>{{mentioner_name}}</0> \uB2D8\uC774 <0>{{context_title}}</0>\uC5D0\uC11C \uB098\uB97C \uC5B8\uAE09\uD588\uC5B4\uC694",
    plan_request: "<0>{{requester_name}}</0> \uB2D8\uC774 \uD50C\uB798\uB108 \uC791\uC131\uC744 \uC694\uCCAD\uD588\uC5B4\uC694. \uC624\uB298 \uD560 \uC77C\uC744 \uACC4\uD68D\uD574\uC8FC\uC138\uC694!",
    comment_mention: {
      note: "<0>{{mentioner_name}}</0> \uB2D8\uC774 <0>{{context_title}}</0> \uB313\uAE00\uC5D0\uC11C \uB098\uB97C \uC5B8\uAE09\uD588\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      todo: "<0>{{mentioner_name}}</0> \uB2D8\uC774 <0>{{context_title}}</0> \uB313\uAE00\uC5D0\uC11C \uB098\uB97C \uC5B8\uAE09\uD588\uC5B4\uC694: '<0>{{comment_text}}</0>'",
      goal: "<0>{{mentioner_name}}</0> \uB2D8\uC774 <0>{{context_title}}</0> \uB313\uAE00\uC5D0\uC11C \uB098\uB97C \uC5B8\uAE09\uD588\uC5B4\uC694: '<0>{{comment_text}}</0>'"
    },
    feedback_comment: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{feedback_content}}</0>\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
    feedback_reply: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{feedback_content}}</0>\uC5D0 \uB2F5\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
    daily_scrum_comment: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{daily_scrum_date}}</0> \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC5D0 \uB313\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'",
    daily_scrum_reply: "<0>{{commenter_name}}</0> \uB2D8\uC774 <0>{{daily_scrum_date}}</0> \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC5D0 \uB2F5\uAE00\uC744 \uB0A8\uACBC\uC5B4\uC694: '<0>{{comment_text}}</0>'"
  },
  performance: {
    feedback_received: "<0>{{writer_name}}</0> \uB2D8\uC774 \uD53C\uB4DC\uBC31\uC744 \uB0A8\uACBC\uC5B4\uC694. <0>{{content}}</0>",
    feedback_updated: "<0>{{writer_name}}</0> \uB2D8\uC774 \uD53C\uB4DC\uBC31\uC744 \uC218\uC815\uD558\uC5EC \uB2E4\uC2DC \uB0A8\uACBC\uC5B4\uC694. <0>{{content}}</0>",
    feedback_reaction: "<0>{{reactioner_name}}</0> \uB2D8\uC774 <0>{{feedback_content}}</0>\uC5D0 \uBC18\uC751\uC744 \uB0A8\uACBC\uC5B4\uC694.",
    feedback_request: "<0>{{requester_name}}</0> \uB2D8\uC774 \uD53C\uB4DC\uBC31\uC744 \uC694\uCCAD\uD588\uC5B4\uC694. <0>{{content}}</0>",
    feedback_request_updated: "<0>{{requester_name}}</0> \uB2D8\uC774 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uC218\uC815\uD558\uC5EC \uB2E4\uC2DC \uBCF4\uB0C8\uC5B4\uC694. <0>{{content}}</0>",
    feedback_request_rejected: "<0>{{name}}</0> \uB2D8\uC774 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uAC70\uC808\uD588\uC5B4\uC694.",
    report_receive: "<0>{{writer_name}}</0> \uB2D8\uC774 <0>{{report_type}}</0>\uB97C \uBCF4\uB0C8\uC5B4\uC694.",
    report_updated: "<0>{{writer_name}}</0> \uB2D8\uC774 <0>{{report_type}}</0>\uB97C \uC218\uC815\uD574 \uC7AC\uC804\uC1A1\uD588\uC5B4\uC694.",
    report_request: "<0>{{requester_name}}</0> \uB2D8\uC774 <0>{{report_type}}</0> \uBC1C\uC1A1\uC744 \uC694\uCCAD\uD588\uC5B4\uC694."
  },
  system: {
    announcement: "[\uACF5\uC9C0] {{title}}",
    security: "\uACC4\uC815 \uBCF4\uC548 \uC54C\uB9BC: {{message}}"
  }
};

// src/locales/ko-KR/dailyscrum.json
var dailyscrum_default2 = {
  template: {
    template_list: "\uD15C\uD50C\uB9BF \uBAA9\uB85D",
    manage_template: "\uD15C\uD50C\uB9BF \uAD00\uB9AC",
    new_template: "\uC0C8 \uD15C\uD50C\uB9BF",
    create_template: "\uC0C8 \uD15C\uD50C\uB9BF \uC0DD\uC131",
    edit_template: "\uD15C\uD50C\uB9BF \uC218\uC815",
    select_template: "\uD15C\uD50C\uB9BF \uC120\uD0DD",
    select_template_message: "\uC218\uC815\uD560 \uD15C\uD50C\uB9BF\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
    no_templates: "\uD15C\uD50C\uB9BF\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    default: "\uAE30\uBCF8",
    edit: "\uC218\uC815",
    delete: "\uC0AD\uC81C",
    no_permission: "\uC774 \uD15C\uD50C\uB9BF\uC744 \uC218\uC815\uD560 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    delete_success: "\uD15C\uD50C\uB9BF \uC0AD\uC81C \uC644\uB8CC",
    delete_success_message: "\uD15C\uD50C\uB9BF\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    delete_error: "\uC0AD\uC81C \uC2E4\uD328",
    delete_error_message: "\uD15C\uD50C\uB9BF \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    create_success: "\uD15C\uD50C\uB9BF \uC0DD\uC131\uB428",
    create_success_message: "\uD15C\uD50C\uB9BF\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    create_error: "\uD15C\uD50C\uB9BF \uC0DD\uC131 \uC2E4\uD328",
    create_error_message: "\uD15C\uD50C\uB9BF \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    update_success: "\uD15C\uD50C\uB9BF \uC218\uC815 \uC644\uB8CC",
    update_success_message: "\uD15C\uD50C\uB9BF\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC218\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    update_error: "\uD15C\uD50C\uB9BF \uC218\uC815 \uC2E4\uD328",
    update_error_message: "\uD15C\uD50C\uB9BF \uC218\uC815 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    default_template_notice: "\uAE30\uBCF8 \uD15C\uD50C\uB9BF\uC740 \uC218\uC815 \uBC0F \uC0AD\uC81C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    default_templates: "\uAE30\uBCF8 \uD15C\uD50C\uB9BF\uB4E4",
    team_templates: "\uD300 \uD15C\uD50C\uB9BF",
    create_first_template: "\uCCAB \uBC88\uC9F8 \uD15C\uD50C\uB9BF \uB9CC\uB4E4\uAE30",
    remove_block: "\uBE14\uB85D \uC0AD\uC81C",
    validation_error: "\uAC80\uC99D \uC624\uB958",
    title_required: "\uD15C\uD50C\uB9BF \uC81C\uBAA9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    default_template_text: "\uD604\uC7AC \uC801\uC6A9\uB41C \uD300 \uD15C\uD50C\uB9BF",
    user_team_info_missing: "\uC0AC\uC6A9\uC790 \uB610\uB294 \uD300 \uC815\uBCF4\uAC00 \uB204\uB77D\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    user_info_missing: "\uC0AC\uC6A9\uC790 \uC815\uBCF4\uAC00 \uB204\uB77D\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    user_template_info_missing: "\uC0AC\uC6A9\uC790 \uB610\uB294 \uD15C\uD50C\uB9BF \uC815\uBCF4\uAC00 \uB204\uB77D\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    created_date: "\uC0DD\uC131\uC77C",
    set_as_default: "\uD300 \uD15C\uD50C\uB9BF\uC73C\uB85C \uC124\uC815",
    default_set_success: "\uD300 \uD15C\uD50C\uB9BF \uC124\uC815 \uC644\uB8CC",
    default_set_success_message: "\uD300 \uD15C\uD50C\uB9BF\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC124\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    default_set_error: "\uD300 \uD15C\uD50C\uB9BF \uC124\uC815 \uC2E4\uD328",
    default_set_error_message: "\uD300 \uD15C\uD50C\uB9BF \uC124\uC815 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    team_info_missing: "\uD300 \uC815\uBCF4\uAC00 \uB204\uB77D\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    default_template_team_notice: "\uD300 \uD15C\uD50C\uB9BF\uC73C\uB85C \uC9C0\uC815 \uC2DC, \uD574\uB2F9 \uD15C\uD50C\uB9BF\uC774 \uD300 \uC804\uCCB4\uC5D0 \uC801\uC6A9\uB429\uB2C8\uB2E4.",
    delete_confirmation: "\uD15C\uD50C\uB9BF \uC0AD\uC81C",
    delete_template_message: "\uC815\uB9D0\uB85C \uC774 \uD15C\uD50C\uB9BF\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    delete_default_template_message: "\uC774 \uD15C\uD50C\uB9BF\uC740 \uD604\uC7AC \uD300 \uD15C\uD50C\uB9BF\uC73C\uB85C \uC124\uC815\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4. \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
  },
  form: {
    title: "\uC81C\uBAA9",
    title_placeholder: "\uD15C\uD50C\uB9BF \uC81C\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694",
    content: "\uB0B4\uC6A9",
    content_placeholder: "\uD15C\uD50C\uB9BF \uB0B4\uC6A9\uC744 \uC785\uB825\uD558\uC138\uC694",
    save: "\uC800\uC7A5",
    cancel: "\uCDE8\uC18C",
    back: "\uC774\uC804",
    required: "\uD544\uC218",
    optional: "\uC120\uD0DD\uC0AC\uD56D",
    required_field: "\uD544\uC218 \uC785\uB825 \uD56D\uBAA9\uC785\uB2C8\uB2E4",
    placeholder: {
      title: "\uC81C\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694",
      content: "\uB0B4\uC6A9\uC744 \uC785\uB825\uD558\uC138\uC694"
    }
  },
  modal: {
    create_template: "\uC0C8 \uD15C\uD50C\uB9BF \uC0DD\uC131",
    edit_template: "\uD15C\uD50C\uB9BF \uC218\uC815",
    delete_confirmation: "\uD15C\uD50C\uB9BF \uC0AD\uC81C",
    delete_confirmation_message: "\uC815\uB9D0\uB85C \uC774 \uD15C\uD50C\uB9BF\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C? \uC774 \uC791\uC5C5\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    confirm: "\uD655\uC778",
    cancel: "\uCDE8\uC18C",
    daily_scrum: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC"
  },
  dailyscrum: {
    title: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC",
    write_title: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC791\uC131",
    cancel: "\uCDE8\uC18C",
    complete: "\uC644\uB8CC",
    save: "\uC800\uC7A5",
    ai_generate: "AI \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC0DD\uC131",
    writing_mode: "\uC791\uC131 \uBAA8\uB4DC",
    content_required: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uB0B4\uC6A9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    save_error: "\uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    write_today_scrum: "\uC624\uB298\uC758 \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC744 \uC791\uC131\uD574\uC8FC\uC138\uC694",
    loading: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC744 \uBD88\uB7EC\uC624\uB294 \uC911...",
    error: "\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    no_scrums: "\uC5D0 \uC791\uC131\uB41C \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    unknown_user: "\uC54C \uC218 \uC5C6\uB294 \uC0AC\uC6A9\uC790",
    user: "\uC0AC\uC6A9\uC790",
    edit: "\uC218\uC815\uD558\uAE30",
    delete: "\uC0AD\uC81C\uD558\uAE30",
    item_update_success: "\uC218\uC815 \uC644\uB8CC",
    item_update_success_message: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC218\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    item_update_error: "\uC218\uC815 \uC2E4\uD328",
    item_update_error_message: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC218\uC815 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    item_delete_success: "\uC0AD\uC81C \uC644\uB8CC",
    item_delete_success_message: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC544\uC774\uD15C\uC774 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    item_delete_error: "\uC0AD\uC81C \uC2E4\uD328",
    item_delete_error_message: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC544\uC774\uD15C \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    delete_item_title: "\uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC0AD\uC81C",
    delete_item_message: "\uC774 \uB370\uC77C\uB9AC\uC2A4\uD06C\uB7FC \uC544\uC774\uD15C\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?\n\uC0AD\uC81C\uB41C \uB0B4\uC6A9\uC740 \uBCF5\uAD6C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    unsaved_content_title: "\uBCC0\uACBD\uC0AC\uD56D\uC744 \uCDE8\uC18C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    unsaved_content_message: "\uC800\uC7A5\uB418\uC9C0 \uC54A\uC740 \uBCC0\uACBD\uC0AC\uD56D\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uB098\uAC00\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    continue_writing: "\uACC4\uC18D \uD3B8\uC9D1\uD558\uAE30",
    exit: "\uB098\uAC00\uAE30"
  },
  todolist: {
    loading: "\uB85C\uB529 \uC911...",
    yesterday_tasks: "\uC5B4\uC81C \uC5C5\uBB34",
    today_tasks: "\uC624\uB298 \uC5C5\uBB34",
    task_history: "\uC5C5\uBB34\uB0B4\uC5ED"
  }
};

// src/locales/ko-KR/feedback.json
var feedback_default2 = {
  title: "\uD53C\uB4DC\uBC31",
  setting: {
    title: "\uD53C\uB4DC\uBC31 \uC124\uC815",
    coreValue: {
      title: "\uD575\uC2EC\uAC00\uCE58",
      setup: "\uD575\uC2EC\uAC00\uCE58 \uC124\uC815",
      empty: "\uD575\uC2EC\uAC00\uCE58\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
      tooltip: "\uC6B0\uB9AC \uC870\uC9C1\uACFC \uAD6C\uC131\uC6D0\uC774 \uCD94\uAD6C\uD558\uB294 \uD575\uC2EC\uC801\uC778 \uAC00\uCE58\uC785\uB2C8\uB2E4. \uD53C\uB4DC\uBC31\uC744 \uBCF4\uB0BC \uB54C \uAD6C\uC131\uC6D0\uC774 \uC798 \uC218\uD589\uD588\uAC70\uB098 \uBCF4\uC644\uC774 \uD544\uC694\uD55C \uAC00\uCE58\uB97C \uC120\uD0DD\uD558\uC5EC \uD568\uAED8 \uC804\uB2EC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      addTitle: "\uD575\uC2EC\uAC00\uCE58 \uCD94\uAC00",
      placeholder: "\uC608: \uB3C4\uC804, \uC8FC\uB3C4\uC131 \uB4F1",
      addButton: "\uCD94\uAC00",
      addSuccess: "\uD575\uC2EC\uAC00\uCE58 \uCD94\uAC00 \uC131\uACF5",
      addSuccessMessage: "'{{value}}'\uC774(\uAC00) \uD575\uC2EC\uAC00\uCE58\uB85C \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      addError: "\uD575\uC2EC\uAC00\uCE58 \uCD94\uAC00 \uC2E4\uD328",
      addErrorMessage: "\uC774\uBBF8 \uC874\uC7AC\uD558\uB294 \uD575\uC2EC\uAC00\uCE58\uC785\uB2C8\uB2E4.",
      deleteSuccess: "\uD575\uC2EC\uAC00\uCE58 \uC0AD\uC81C \uC131\uACF5",
      deleteSuccessMessage: "'{{value}}' \uD575\uC2EC\uAC00\uCE58\uAC00 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      deleteError: "\uD575\uC2EC\uAC00\uCE58 \uC0AD\uC81C \uC2E4\uD328",
      deleteConfirmTitle: "\uD575\uC2EC\uAC00\uCE58 \uC0AD\uC81C",
      deleteConfirmMessage: "'{{value}}' \uD575\uC2EC\uAC00\uCE58\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      deleteNote: "\uC0AD\uC81C\uB41C \uD575\uC2EC\uAC00\uCE58\uB294 \uBCF5\uAD6C\uD560 \uC218 \uC5C6\uC73C\uBA70, \uC774\uBBF8 \uBCF4\uB0B8 \uD53C\uB4DC\uBC31\uC5D0\uB294 \uC601\uD5A5\uC744 \uC8FC\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
      deleteConfirm: "\uC0AD\uC81C"
    }
  },
  common: {
    cancel: "\uCDE8\uC18C"
  },
  tabs: {
    received: "\uBC1B\uC740 \uD53C\uB4DC\uBC31",
    sent: "\uBCF4\uB0B8 \uD53C\uB4DC\uBC31",
    receivedRequest: "\uBC1B\uC740 \uC694\uCCAD",
    sentRequest: "\uBCF4\uB0B8 \uC694\uCCAD"
  },
  action: {
    sendFeedback: "\uD53C\uB4DC\uBC31 \uC804\uC1A1",
    requestFeedback: "\uD53C\uB4DC\uBC31 \uC694\uCCAD",
    edit: "\uC218\uC815\uD558\uAE30",
    delete: "\uC0AD\uC81C\uD558\uAE30",
    addBookmark: "\uBD81\uB9C8\uD06C\uD558\uAE30",
    removeBookmark: "\uBD81\uB9C8\uD06C \uD574\uC81C\uD558\uAE30",
    cancelRequest: "\uC694\uCCAD \uCDE8\uC18C",
    resend: "\uC7AC\uC804\uC1A1",
    editRequest: "\uC694\uCCAD \uC218\uC815\uD558\uAE30",
    editFeedback: "\uD53C\uB4DC\uBC31 \uC218\uC815\uD558\uAE30",
    doneEdit: "\uC218\uC815 \uC644\uB8CC"
  },
  placeHolder: {
    received: "\uC544\uC9C1 \uBC1B\uC740 \uD53C\uB4DC\uBC31\uC774 \uC5C6\uC5B4\uC694!",
    sent: "\uC544\uC9C1 \uBCF4\uB0B8 \uD53C\uB4DC\uBC31\uC774 \uC5C6\uC5B4\uC694!",
    receivedRequest: "\uC544\uC9C1 \uBC1B\uC740 \uC694\uCCAD\uC774 \uC5C6\uC5B4\uC694!",
    sentRequest: "\uC544\uC9C1 \uBCF4\uB0B8 \uC694\uCCAD\uC774 \uC5C6\uC5B4\uC694!"
  },
  sendFeedback: {
    title: "{{name}}\uB2D8\uC5D0\uAC8C \uD53C\uB4DC\uBC31 \uBCF4\uB0B4\uAE30",
    success: "\uD53C\uB4DC\uBC31\uC744 \uBCF4\uB0C8\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31\uC744 \uBCF4\uB0B4\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    requestInvalid: "\uCDE8\uC18C\uB41C \uC694\uCCAD",
    requestInvalidDetail: "\uC694\uCCAD\uC790\uAC00 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uCDE8\uC18C\uD588\uC5B4\uC694.\n\uADF8\uB798\uB3C4 \uD53C\uB4DC\uBC31\uC744 \uBCF4\uB0B4\uC2DC\uACA0\uC5B4\uC694?"
  },
  editFeedback: {
    title: "\uD53C\uB4DC\uBC31 \uC218\uC815"
  },
  editRequest: {
    title: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uC218\uC815"
  },
  deleteFeedback: {
    title: "\uD53C\uB4DC\uBC31 \uC0AD\uC81C",
    receiver_message: "\uD53C\uB4DC\uBC31\uC744 \uC0AD\uC81C\uD569\uB2C8\uB2E4. \uB0B4 \uB9AC\uC2A4\uD2B8\uC5D0\uC11C\uB9CC \uC0AD\uC81C\uB418\uACE0,\n\uD53C\uB4DC\uBC31\uC744 \uBCF4\uB0B8 \uC720\uC800\uC5D0\uAC8C\uC11C\uB294 \uC0AD\uC81C\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    sender_message: "\uD53C\uB4DC\uBC31\uC744 \uC0AD\uC81C\uD569\uB2C8\uB2E4. \uB0B4 \uB9AC\uC2A4\uD2B8\uC5D0\uC11C\uB9CC \uC0AD\uC81C\uB418\uACE0,\n\uD53C\uB4DC\uBC31\uC744 \uBC1B\uC740 \uC720\uC800\uC5D0\uAC8C\uC11C\uB294 \uC0AD\uC81C\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    success: "\uD53C\uB4DC\uBC31\uC774 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31 \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  rejectFeedback: {
    title: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uAC70\uC808",
    message: "{{name}}\uB2D8\uC758 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uAC70\uC808\uD560\uAE4C\uC694?",
    success: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uAC70\uC808\uD588\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uAC70\uC808 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  sendRequest: {
    success: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uBCF4\uB0C8\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uBCF4\uB0B4\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  deleteRequest: {
    title: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uC0AD\uC81C",
    message: "\uB0B4\uAC00 \uBCF4\uB0B8 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uC0AD\uC81C\uD569\uB2C8\uB2E4.",
    success: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uC0AD\uC81C\uD588\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  cancelRequest: {
    title: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uCDE8\uC18C",
    message: "\uB0B4\uAC00 \uBCF4\uB0B8 \uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uCDE8\uC18C\uD569\uB2C8\uB2E4.",
    success: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC744 \uCDE8\uC18C\uD588\uC2B5\uB2C8\uB2E4.",
    error: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uCDE8\uC18C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
  },
  bookmark: {
    title: "\uBD81\uB9C8\uD06C\uD55C \uD53C\uB4DC\uBC31",
    placeHolder: "\uBC1B\uC740 \uD53C\uB4DC\uBC31\uC744 \uBD81\uB9C8\uD06C\uD574\uBCF4\uC138\uC694.",
    add: {
      title: "\uBD81\uB9C8\uD06C \uCD94\uAC00",
      message: "\uD53C\uB4DC\uBC31\uC744 \uBD81\uB9C8\uD06C\uD560\uAE4C\uC694?",
      success: "\uBD81\uB9C8\uD06C\uD55C \uD53C\uB4DC\uBC31 \uB9AC\uC2A4\uD2B8\uC5D0 \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4."
    },
    remove: {
      title: "\uBD81\uB9C8\uD06C \uD574\uC81C",
      message: "\uD53C\uB4DC\uBC31\uC758 \uBD81\uB9C8\uD06C\uB97C \uD574\uC81C\uD560\uAE4C\uC694?",
      success: "\uBD81\uB9C8\uD06C\uD55C \uD53C\uB4DC\uBC31 \uB9AC\uC2A4\uD2B8\uC5D0\uC11C \uC81C\uAC70\uD588\uC2B5\uB2C8\uB2E4."
    },
    error: "\uBD81\uB9C8\uD06C \uC5C5\uB370\uC774\uD2B8\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
  },
  feedbackCard: {
    seeRequest: "\uC694\uCCAD \uD655\uC778",
    requestTitle: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uC0AC\uD56D"
  },
  feedbackModal: {
    requestTitle: "\uC694\uCCAD \uC0AC\uD56D",
    receiver: "\uD53C\uB4DC\uBC31 \uB300\uC0C1\uC790",
    requestReceiver: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uB300\uC0C1\uC790",
    selectReceiver: "\uD53C\uB4DC\uBC31 \uB300\uC0C1\uC790 \uC120\uD0DD",
    selectRequestReceiver: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uB300\uC0C1\uC790 \uC120\uD0DD",
    receiverPlaceHolder: "\uD53C\uB4DC\uBC31 \uB300\uC0C1\uC790\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    requestReceiverPlaceHolder: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uB300\uC0C1\uC790\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    selectedTodo: "\uC120\uD0DD\uB41C \uC5C5\uBB34 ({{count}})",
    feedbackPlaceHolder: "\uD53C\uB4DC\uBC31 \uB0B4\uC6A9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    requestPlaceHolder: "\uD53C\uB4DC\uBC31 \uC694\uCCAD \uB0B4\uC6A9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    selectCoreValue: "\uD575\uC2EC\uAC00\uCE58 \uC120\uD0DD",
    positivePlaceHolder: "\uC774\uBC88 \uC5C5\uBB34\uC5D0\uC11C \uB3CB\uBCF4\uC600\uB358 \uC810\uC740 \uBB34\uC5C7\uC774\uC5C8\uB098\uC694?",
    negativePlaceHolder: "\uC774\uBC88 \uC5C5\uBB34\uC5D0\uC11C \uC544\uC26C\uC6E0\uB358 \uC810\uC740 \uBB34\uC5C7\uC774\uC5C8\uB098\uC694?",
    todoList: "{{name}}\uB2D8\uC758 \uC5C5\uBB34 \uB0B4\uC5ED",
    todoListDescription: {
      respond: "\uD53C\uB4DC\uBC31 \uC694\uCCAD\uC5D0 \uD3EC\uD568\uB41C \uC5C5\uBB34 \uBAA9\uB85D\uC785\uB2C8\uB2E4.",
      feedback: "\uD53C\uB4DC\uBC31\uACFC \uAD00\uB828\uB41C \uC5C5\uBB34\uB97C \uC120\uD0DD\uD558\uC5EC \uD0DC\uADF8\uD574\uC8FC\uC138\uC694.",
      request: "\uD53C\uB4DC\uBC31\uC744 \uBC1B\uACE0 \uC2F6\uC740 \uC5C5\uBB34\uB97C \uC120\uD0DD\uD558\uC5EC \uD0DC\uADF8\uD574\uC8FC\uC138\uC694."
    },
    feedbackScoring: "\uD53C\uB4DC\uBC31 \uC810\uC218",
    aiOptimization: "AI \uCD5C\uC801\uD654",
    unsavedContent: "\uC800\uC7A5\uB418\uC9C0 \uC54A\uC740 \uBCC0\uACBD\uC0AC\uD56D",
    unsavedContentDetail: "\uC791\uC131 \uC911\uC778 \uB0B4\uC6A9\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uC791\uC131\uC744 \uCDE8\uC18C\uD558\uC2DC\uACA0\uC5B4\uC694?",
    refreshRequest: {
      button: "\uC0C8\uB85C\uACE0\uCE68",
      tooltip: "\uC694\uCCAD\uC774 \uC5C5\uB370\uC774\uD2B8\uB418\uC5C8\uC5B4\uC694.\n\uCD5C\uC2E0 \uB0B4\uC6A9\uC744 \uBD88\uB7EC\uC62C \uC218 \uC788\uAC8C \uC0C8\uB85C\uACE0\uCE68 \uD574\uC8FC\uC138\uC694!"
    }
  },
  todoBadgeList: {
    seeMore: "\uB354\uBCF4\uAE30 ({{count}})",
    collapse: "\uC811\uAE30",
    expand: "\uC5F4\uAE30"
  },
  todoList: {
    goalTitlePlaceHolder: "\uD0DC\uC2A4\uD06C \uC5C6\uC74C",
    select: "\uC120\uD0DD",
    unselect: "\uC81C\uC678",
    selectAll: "\uC804\uCCB4 \uC120\uD0DD",
    unselectAll: "\uC120\uD0DD \uD574\uC81C",
    feedbackPlaceHolder: "\uD574\uB2F9 \uAE30\uAC04\uC5D0 \uC9C4\uD589\uD55C \uC5C5\uBB34\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    requestPlaceHolder: "\uC694\uCCAD\uC5D0 \uD3EC\uD568\uB41C \uC5C5\uBB34\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4."
  },
  coreValue: {
    positive: "\uAE0D\uC815 \uD53C\uB4DC\uBC31",
    negative: "\uBD80\uC815 \uD53C\uB4DC\uBC31",
    allowMultiple: "\uBCF5\uC218\uC120\uD0DD \uAC00\uB2A5"
  },
  summary: {
    title: "AI \uD53C\uB4DC\uBC31 \uC694\uC57D",
    subTitle: "{{name}}\uB2D8\uC740",
    coreValue: "{{name}}\uB2D8\uC758 \uD575\uC2EC\uAC00\uCE58"
  },
  error: {
    notPermission: "\uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uAD00\uB9AC\uC790\uB098 \uC18C\uC720\uC790\uB9CC \uC218\uC815\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    notFound: "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
  }
};

// src/locales/ko-KR/drive.json
var drive_default2 = {
  search_placeholder: "\uC81C\uBAA9 \uAC80\uC0C9",
  select_workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC120\uD0DD",
  select_location: "\uC704\uCE58 \uC120\uD0DD",
  location_todo: "\uD560 \uC77C",
  location_goal: "\uD0DC\uC2A4\uD06C",
  start_date: "\uC2DC\uC791 \uB0A0\uC9DC",
  end_date: "\uC885\uB8CC \uB0A0\uC9DC",
  no_notes_found: "\uB178\uD2B8\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
  no_notes: "\uB178\uD2B8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  created_by: "\uC791\uC131\uC790",
  load_more: "\uB354 \uBD88\uB7EC\uC624\uAE30",
  showing_results: "\uCD1D {{total}}\uAC1C \uC911 {{count}}\uAC1C \uD45C\uC2DC \uC911",
  filter: {
    title: "\uD544\uD130",
    workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
    creator: "\uC0DD\uC131\uC790",
    location: "\uB85C\uCF00\uC774\uC158",
    date: "\uC791\uC131\uC77C",
    bookmark: "\uBD81\uB9C8\uD06C"
  },
  date_today: "\uC624\uB298",
  date_last7days: "\uC9C0\uB09C 7\uC77C",
  date_last14days: "\uC9C0\uB09C 14\uC77C",
  date_last30days: "\uC9C0\uB09C 30\uC77C",
  column: {
    title: "\uC81C\uBAA9",
    creator: "\uC0DD\uC131\uC790",
    workspace: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4",
    location: "\uB85C\uCF00\uC774\uC158",
    created_at: "\uC791\uC131\uC77C",
    updated_at: "\uC218\uC815\uC77C"
  },
  my_created_notes: "\uB0B4\uAC00 \uC791\uC131\uD55C \uB178\uD2B8",
  recently_viewed_notes: "\uCD5C\uADFC \uC5F4\uC5B4\uBCF8 \uB178\uD2B8",
  bookmarked_notes: "\uBD81\uB9C8\uD06C\uD55C \uB178\uD2B8",
  no_my_notes: "\uC791\uC131\uD55C \uB178\uD2B8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  no_recently_viewed: "\uCD5C\uADFC \uC5F4\uC5B4\uBCF8 \uB178\uD2B8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  no_bookmarked: "\uBD81\uB9C8\uD06C\uD55C \uB178\uD2B8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
  my_bookmark: "\uB0B4 \uBD81\uB9C8\uD06C"
};

// src/locales/ko-KR/oneonone.json
var oneonone_default2 = {
  action_items: {
    add: "\uC561\uC158 \uC544\uC774\uD15C \uCD94\uAC00",
    all_items: "\uC561\uC158 \uC544\uC774\uD15C ({{count}})",
    banner_title: "\uD655\uC778\uD560 \uC561\uC158 \uC544\uC774\uD15C\uC774 \uC788\uC5B4\uC694",
    banner_action: "\uBCF4\uAE30",
    assignee_change: "\uB2F4\uB2F9\uC790 \uBCC0\uACBD",
    assignee_change_failed: "\uB2F4\uB2F9\uC790 \uBCC0\uACBD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    assignee_changed: "\uB2F4\uB2F9\uC790\uAC00 \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    delete_message: "\uC561\uC158 \uC544\uC774\uD15C\uC774 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    delete_confirm: "\uC561\uC158 \uC544\uC774\uD15C\uC744 \uC0AD\uC81C\uD560\uAE4C\uC694?",
    new_title: "\uC0C8 \uC561\uC158 \uC544\uC774\uD15C",
    no_action_items: "\uC561\uC158 \uC544\uC774\uD15C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    old_title: "\uC9C0\uB09C \uC561\uC158 \uC544\uC774\uD15C",
    tab_title: "\uC561\uC158 \uC544\uC774\uD15C",
    title_label: "\uC561\uC158 \uC544\uC774\uD15C \uC81C\uBAA9",
    title_placeholder: "\uC561\uC158 \uC544\uC774\uD15C\uC744 \uC785\uB825\uD558\uC138\uC694",
    title_required: "\uB0B4\uC6A9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
    user_not_found: "\uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC5B4 \uB2F4\uB2F9\uC790\uB97C \uBCC0\uACBD\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    retro: {
      title: "\uC9C0\uB09C \uC561\uC158 \uC544\uC774\uD15C",
      mobile_guide: "\uC561\uC158 \uC544\uC774\uD15C\uC744 \uC644\uB8CC\uD588\uC73C\uBA74 \uCCB4\uD06C\uD574\uC8FC\uC138\uC694.\n\uB0A8\uACA8\uB458 \uD544\uC694\uAC00 \uC5C6\uC73C\uBA74 \uD074\uB9AD\uD574\uC11C \uADF8\uB9CC \uBCF4\uAE30\uB97C \uB20C\uB7EC\uC8FC\uC138\uC694.",
      ai_estimate: "AI \uCD94\uC815",
      ai_estimate_completed: "\uC644\uB8CC \uCD94\uC815",
      ai_nudge_tooltip: "\uBBF8\uD305\uC5D0\uC11C \uB098\uB204\uC2E0 \uB300\uD654\uB97C \uBCF4\uB2C8, \uC774 \uC561\uC158 \uC544\uC774\uD15C\uC740 \uC774\uBBF8 \uC644\uB8CC\uD558\uC2E0 \uAC83 \uAC19\uC544\uC694. \uB9DE\uC73C\uBA74 \uC644\uB8CC\uB97C \uB20C\uB7EC\uC8FC\uC138\uC694.",
      dismiss: "\uADF8\uB9CC \uBCF4\uAE30",
      undo: "\uB418\uB3CC\uB9AC\uAE30",
      checkin: {
        guide: "\uBCF8\uACA9\uC801\uC73C\uB85C \uC2DC\uC791\uD558\uAE30 \uC804\uC5D0, \uC9C0\uB09C \uBBF8\uD305\uC5D0\uC11C \uC774\uC5B4\uC838 \uC628 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uD568\uAED8 \uD655\uC778\uD574\uBCFC\uAE4C\uC694?\n\uC644\uB8CC\uD55C \uD56D\uBAA9\uC740 \uCCB4\uD06C\uD574\uC8FC\uC138\uC694.",
        done: "\uB2E4 \uD655\uC778\uD588\uC5B4\uC694",
        settled_group: "\uBBF8\uB9AC \uC815\uB9AC\uD574 \uB454 \uD56D\uBAA9 {{count}}\uAC1C",
        save_failed: "\uC800\uC7A5\uD558\uC9C0 \uBABB\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
        revisit_badge: "{{count}}\uD68C \uC5F0\uC18D \uC9C4\uD589 \uC911",
        revisit_question: "\uACC4\uC18D \uC9C4\uD589 \uC911\uC73C\uB85C \uB0A8\uC544 \uC788\uB294 \uD56D\uBAA9\uC774\uC5D0\uC694. \uC5B4\uB514\uC11C \uB9C9\uD788\uB294\uC9C0 \uAC19\uC774 \uC598\uAE30\uD574\uBCFC\uAE4C\uC694?",
        revisit_summary: "\uC9C0\uB09C \uBBF8\uD305\uBD80\uD130 \uC774\uC5B4\uC9C4 \uD56D\uBAA9\uC774 \uB9CE\uC544\uC694. \uC774\uBC88\uC5D4 \uAC19\uC774 \uC815\uB9AC\uD574\uBCFC\uAE4C\uC694?"
      },
      verdict: {
        pending: "\uC2DC\uC791 \uC804",
        progressing: "\uC9C4\uD589 \uC911",
        completed: "\uC644\uB8CC",
        not_discussed: "\uBABB \uB2E4\uB8F8",
        dismissed: "\uADF8\uB9CC \uBCF4\uAE30"
      }
    },
    overview: {
      title: "\uC561\uC158 \uC544\uC774\uD15C",
      description: "\uBBF8\uD305\uC5D0\uC11C \uB9CC\uB4E0 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uBBF8\uD305 \uC0C1\uB300\uBCC4\uB85C \uD655\uC778\uD558\uACE0 \uAD00\uB9AC\uD574 \uBCF4\uC138\uC694.",
      no_pairs: "\uC544\uC9C1 1on1 \uBA64\uBC84\uAC00 \uC5C6\uC5B4\uC694. 1on1 \uBBF8\uD305\uC744 \uB9CC\uB4E4\uBA74 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB3FC\uC694.",
      empty: "\uC774 \uBA64\uBC84\uC640 \uD568\uAED8\uD55C \uC561\uC158 \uC544\uC774\uD15C\uC774 \uC544\uC9C1 \uC5C6\uC5B4\uC694.",
      empty_open: "\uC9C4\uD589 \uC911\uC778 \uC561\uC158 \uC544\uC774\uD15C\uC774 \uC5C6\uC5B4\uC694.",
      stat_open: "\uC9C4\uD589 \uC911",
      stat_completed: "\uC644\uB8CC",
      next_meeting: "\uB2E4\uC74C \uBBF8\uD305",
      group_open: "\uC9C4\uD589 \uC911",
      group_completed: "\uC644\uB8CC",
      group_dismissed: "\uADF8\uB9CC \uBCF4\uAE30",
      sort_hint: "\uC624\uB798 \uC774\uC5B4\uC9C4 \uC21C",
      pair_sort_label: "\uC815\uB82C",
      pair_sort_open: "\uC9C4\uD589 \uC911 \uB9CE\uC740 \uC21C",
      pair_sort_upcoming: "\uB2E4\uAC00\uC624\uB294 \uBBF8\uD305 \uC21C",
      pair_sort_recent: "\uCD5C\uADFC \uBBF8\uD305 \uC21C",
      ongoing_since: "{{date}}\uBD80\uD130",
      ongoing_since_tooltip: "{{date}} \uBBF8\uD305\uBD80\uD130 \uC774\uC5B4\uC838 \uC628 \uD56D\uBAA9\uC774\uC5D0\uC694",
      completed_date: "{{date}} \uC644\uB8CC",
      show_more: "{{count}}\uAC1C \uB354\uBCF4\uAE30",
      last_meeting: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305 {{date}}",
      upcoming_meeting: "{{date}} \uC608\uC815",
      rel_today: "\uC624\uB298",
      rel_yesterday: "\uC5B4\uC81C",
      rel_tomorrow: "\uB0B4\uC77C",
      rel_days_ago: "{{count}}\uC77C \uC804",
      rel_weeks_ago: "{{count}}\uC8FC \uC804",
      rel_months_ago: "{{count}}\uAC1C\uC6D4 \uC804",
      rel_years_ago: "{{count}}\uB144 \uC804",
      rel_days_later: "{{count}}\uC77C \uB4A4",
      rel_weeks_later: "{{count}}\uC8FC \uB4A4",
      rel_months_later: "{{count}}\uAC1C\uC6D4 \uB4A4",
      no_last_meeting: "\uC544\uC9C1 \uBBF8\uD305 \uC804",
      no_upcoming: "\uB2E4\uC74C \uBBF8\uD305 \uBBF8\uC815",
      tbd: "\uBBF8\uC815",
      topic_open_label: "\uC9C4\uD589 \uC911 \uC8FC\uC81C",
      topic_unclassified: "\uBBF8\uBD84\uB958",
      no_recent_items: "\uCD5C\uADFC 3\uAC1C\uC6D4 \uAE30\uB85D \uC5C6\uC74C",
      topic_breakdown_title: "\uCD5C\uADFC 3\uAC1C\uC6D4 \uC8FC\uC81C \uAD6C\uC131 {{count}}",
      topic_breakdown_info: "\uC9C4\uD589 \uC0C1\uD669\uACFC \uAD00\uACC4\uC5C6\uC774 \uCD5C\uADFC 3\uAC1C\uC6D4 \uB3D9\uC548 \uB098\uB208 \uB300\uD654 \uC8FC\uC81C\uC608\uC694.",
      timeline_meeting_upcoming: "{{date}} \uBBF8\uD305 \uC608\uC815",
      timeline_open_meeting: "\uBBF8\uD305\uC73C\uB85C \uC774\uB3D9",
      timeline_created: "\uC774 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uB9CC\uB4E4\uC5C8\uC5B4\uC694",
      timeline_upcoming_check: "\uC774 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uD568\uAED8 \uCCB4\uD06C\uD560 \uC608\uC815\uC774\uC5D0\uC694",
      timeline_no_record: "\uBBF8\uD305\uC5D0\uC11C \uB2E4\uB904\uC9C0\uC9C0 \uC54A\uC558\uC5B4\uC694",
      timeline_ai_estimate: "AI \uCD94\uC815: {{state}}",
      timeline_confirmed_completed: "\uC644\uB8CC\uB85C \uD45C\uC2DC\uD588\uC5B4\uC694",
      timeline_confirmed_dismissed: "\uADF8\uB9CC \uBCF4\uAE30\uB85C \uD45C\uC2DC\uD588\uC5B4\uC694",
      timeline_confirmed_completed_on: "{{date}}\uC5D0 \uC644\uB8CC\uB85C \uD45C\uC2DC\uD588\uC5B4\uC694",
      timeline_confirmed_dismissed_on: "{{date}}\uC5D0 \uADF8\uB9CC \uBCF4\uAE30\uB85C \uD45C\uC2DC\uD588\uC5B4\uC694",
      timeline_confirmed_generic: "{{state}} \uC0C1\uD0DC\uB85C \uD45C\uC2DC\uD588\uC5B4\uC694",
      timeline_no_history: "\uC774 \uD56D\uBAA9\uC740 \uC644\uB8CC \uAE30\uB85D\uB9CC \uB0A8\uC544 \uC788\uC5B4\uC694",
      linked_from_label: "\uC774\uC804 \uC561\uC158 \uC544\uC774\uD15C\uC5D0\uC11C \uC774\uC5B4\uC9D0",
      linked_to_label: "\uC0C8 \uC561\uC158 \uC544\uC774\uD15C\uC73C\uB85C \uC774\uC5B4\uC9D0",
      topic_info: "AI\uAC00 \uBBF8\uD305 \uB300\uD654\uB97C \uBC14\uD0D5\uC73C\uB85C \uC561\uC158 \uC544\uC774\uD15C\uC758 \uC8FC\uC81C\uB97C \uBD84\uB958\uD574\uC694. \uC9C4\uD589 \uC911\uC778 \uD56D\uBAA9 \uAE30\uC900\uC774\uACE0, \uC8FC\uC81C\uB97C \uB204\uB974\uBA74 \uC644\uB8CC\xB7\uADF8\uB9CC \uBCF4\uAE30 \uD56D\uBAA9\uB3C4 \uD568\uAED8 \uAC78\uB7EC\uC838\uC694.",
      load_failed: "\uC561\uC158 \uC544\uC774\uD15C\uC744 \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      save_failed: "\uC0C1\uD0DC\uB97C \uC800\uC7A5\uD558\uC9C0 \uBABB\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    }
  },
  ai_analysis: {
    failed: {
      title: "\uBBF8\uD305 \uBD84\uC11D\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694",
      message: "\uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      retry_button: "\uBD84\uC11D \uB2E4\uC2DC \uC2DC\uB3C4",
      max_retries_reached: "\uC7AC\uC2DC\uB3C4 \uD69F\uC218\uB97C \uBAA8\uB450 \uC0AC\uC6A9\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uD655\uC778\uD574\uC8FC\uC138\uC694."
    },
    additional_analysis: "\uCD94\uAC00 \uBD84\uC11D \uC815\uBCF4\uAC00 \uC5EC\uAE30\uC5D0 \uD45C\uC2DC\uB429\uB2C8\uB2E4",
    ai_summary_title: "AI \uC694\uC57D",
    brief_view: "\uAC04\uB7B5\uD788 \uBCF4\uAE30",
    expanded_view: "\uD3BC\uCCD0 \uBCF4\uAE30",
    analysis: "\uBD84\uC11D",
    auto_generated: "\uC790\uB3D9 \uC0DD\uC131\uB428",
    close: "\uB2EB\uAE30",
    conversation_content: "\uB300\uD654 \uB0B4\uC6A9",
    copied: "\uBCF5\uC0AC\uB428",
    copy_transcript: "\uC804\uBB38\uBCF5\uC0AC",
    core_content: "\uD575\uC2EC \uB0B4\uC6A9",
    decisions_made: "\uACB0\uC815 \uC0AC\uD56D",
    detailed_summary: "\uC0C1\uC138 \uC694\uC57D",
    feedback_for_leader: "\uB9AC\uB354\uB97C \uC704\uD55C \uD53C\uB4DC\uBC31",
    improvement_needed: "\uBCF4\uC644\uC810",
    leader: "\uB9AC\uB354",
    member_feedback: {
      title: "\uB2E4\uC74C \uBBF8\uD305\uC744 \uC704\uD55C \uD53C\uB4DC\uBC31",
      private_badge: "\uB098\uB9CC \uBCFC \uC218 \uC788\uC5B4\uC694",
      intro: "\uC774\uBC88 \uBBF8\uD305\uC744 \uB3CC\uC544\uBCF4\uBA70 \uD53C\uB4DC\uBC31 {{count}}\uAC1C\uB97C \uC900\uBE44\uD588\uC5B4\uC694. \uB2E4\uC74C \uBBF8\uD305\uC744 \uC900\uBE44\uD560 \uB54C \uCC38\uACE0\uD574\uBCF4\uC138\uC694.",
      nudge: {
        message: "\uB098\uB9CC \uBCFC \uC218 \uC788\uB294 \uD53C\uB4DC\uBC31 {{count}}\uAC1C\uAC00 \uC900\uBE44\uB410\uC5B4\uC694. \uC774\uBC88 \uBBF8\uD305\uC5D0\uC11C \uB0B4\uAC00 \uD55C \uC774\uC57C\uAE30\uB97C \uB3CC\uC544\uBCF4\uACE0, \uB2E4\uC74C\uC5D0 \uD574\uBCFC \uAC83\uC744 \uC815\uB9AC\uD588\uC5B4\uC694.",
        message_mobile: "\uB098\uB9CC \uBCFC \uC218 \uC788\uB294 \uD53C\uB4DC\uBC31 {{count}}\uAC1C\uAC00 \uC900\uBE44\uB410\uC5B4\uC694.",
        button: "\uD53C\uB4DC\uBC31 \uBCF4\uB7EC \uAC00\uAE30"
      },
      label: {
        strength: "\uC774\uB807\uAC8C \uB9D0\uD55C \uAC8C \uC88B\uC558\uC5B4\uC694",
        not_raised: "\uC900\uBE44\uD588\uC9C0\uB9CC \uBABB \uAEBC\uB0B8 \uC774\uC57C\uAE30",
        improve: "\uB2E4\uC74C\uC5D4 \uC774\uB807\uAC8C \uB9D0\uD574\uBCFC\uAE4C\uC694",
        settled: "\uACB0\uB860\uAE4C\uC9C0 \uB2FF\uC740 \uC774\uC57C\uAE30",
        open_issue: "\uB2E4\uC74C \uBBF8\uD305\uC5D0\uC11C \uB2E4\uB904\uBCFC\uAE4C\uC694",
        unknown: "\uB3CC\uC544\uBCFC \uC810"
      },
      source: {
        meeting: "\uBBF8\uD305 \uC911 \uBC1C\uD654",
        my_prep: "\uB0B4 \uD504\uB819",
        prep_chat: "\uD504\uB819 \uB300\uD654",
        unknown: "\uADFC\uAC70"
      }
    },
    loading: {
      analyzing_qa: "\uC9C8\uBB38\uACFC \uB2F5\uBCC0 \uBD84\uC11D\uD558\uB294 \uC911...",
      analyzing_title: "1on1 \uBBF8\uD305 \uBD84\uC11D\uC911",
      browser_notification: "\uBE0C\uB77C\uC6B0\uC800 \uC54C\uB9BC",
      calculating_ratio: "\uBC1C\uD654 \uBE44\uC728 \uACC4\uC0B0\uD558\uB294 \uC911...",
      complete_notification_body: '"{{title}}" \uBBF8\uD305 \uBD84\uC11D\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.',
      complete_notification_body_default: "AI \uBBF8\uD305 \uBD84\uC11D\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      complete_notification_title: "AI \uBD84\uC11D \uC644\uB8CC",
      converting_text: "\uB300\uD654 \uB0B4\uC6A9 \uD14D\uC2A4\uD2B8\uB85C \uBCC0\uD658\uD558\uB294 \uC911...",
      deriving_insights: "AI \uC778\uC0AC\uC774\uD2B8 \uB3C4\uCD9C\uD558\uB294 \uC911...",
      description: "\uB179\uC74C\uC758 \uAE38\uC774\uC5D0 \uB530\uB77C 2~5\uBD84\uC774 \uC18C\uC694\uB429\uB2C8\uB2E4.\n\uBD84\uC11D\uC774 \uC644\uB8CC\uB418\uBA74 \uC774\uBA54\uC77C\uACFC \uC571 \uD478\uC2DC\uB85C \uC54C\uB824\uB4DC\uB9AC\uB2C8,\n\uD3B8\uD558\uAC8C \uB2E4\uB978 \uC5C5\uBB34\uB97C \uBCF4\uC154\uB3C4 \uAD1C\uCC2E\uC544\uC694!",
      description_mobile: "\uC57D 5\uBD84\uC758 \uC2DC\uAC04\uC774 \uC18C\uC694\uB418\uBA70,\n\uC11C\uBC84 \uD658\uACBD\uC5D0 \uB530\uB77C \uC870\uAE08 \uB354 \uC9C0\uCCB4\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.\n\uB2E4\uB978 \uD654\uBA74\uC73C\uB85C \uC774\uB3D9\uD558\uC154\uB3C4 \uAD1C\uCC2E\uC544\uC694!",
      extracting_action_items: "\uC561\uC158 \uC544\uC774\uD15C \uCD94\uCD9C\uD558\uB294 \uC911...",
      finding_topics: "\uD575\uC2EC \uC8FC\uC81C \uD30C\uC545\uD558\uB294 \uC911...",
      generating_title: "\uBBF8\uD305 \uC81C\uBAA9 \uC0DD\uC131\uD558\uB294 \uC911...",
      loading_recording: "\uB179\uC74C \uD30C\uC77C \uBD88\uB7EC\uC624\uB294 \uC911...",
      organizing_feedback: "\uB9AC\uB354 \uD53C\uB4DC\uBC31 \uC815\uB9AC\uD558\uB294 \uC911...",
      retry_failed_message: "\uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      retry_failed_title: "\uC7AC\uC2DC\uB3C4 \uC2E4\uD328",
      summarizing: "\uC804\uCCB4 \uB0B4\uC6A9 \uC694\uC57D\uD558\uB294 \uC911...",
      tip_title: "\uAE30\uB2E4\uB9AC\uB294 \uB3D9\uC548 \uC77D\uC5B4\uBCF4\uAE30",
      tip_subtitle: "\uC624\uBE14\uB9BF\uC774 \uBC1C\uD589\uD55C \uB9AC\uB354\uC2ED \uC544\uD2F0\uD074",
      title: "AI\uAC00 1on1 \uBBF8\uD305\uC744 \uBD84\uC11D\uD558\uACE0 \uC788\uC5B4\uC694"
    },
    meeting_content: "\uBCF8\uBB38",
    member: "\uBA64\uBC84",
    no_data: "AI \uBD84\uC11D \uACB0\uACFC\uAC00 \uC544\uC9C1 \uC5C6\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uD655\uC778\uD574\uC8FC\uC138\uC694.",
    no_voice_detected: "\uC74C\uC131\uC774 \uAC10\uC9C0\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4",
    no_voice_detected_description: "\uB179\uC74C\uC5D0\uC11C \uC74C\uC131\uC774 \uAC10\uC9C0\uB418\uC9C0 \uC54A\uC544\n\uBD84\uC11D \uACB0\uACFC\uB97C \uC81C\uACF5\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    no_meeting_data: "\uBBF8\uD305 \uB370\uC774\uD130\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    no_questions: "\uC791\uC131\uB41C \uC9C8\uBB38\uACFC \uB2F5\uBCC0\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    no_transcript_available: "\uC2A4\uD06C\uB9BD\uD2B8\uB97C \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
    positive_feedback: "\uAC15\uC810",
    recording_completed: "\uB179\uC74C\uC644\uB8CC",
    speaking_ratio: "\uBC1C\uD654 \uBE44\uC728",
    summary: "\uC694\uC57D",
    support_needs: "\uC9C0\uC6D0 \uD544\uC694\uC0AC\uD56D/\uC7A5\uC560\uBB3C",
    title: "AI \uBD84\uC11D",
    transcript_disclaimer: "\uC2E4\uC81C \uBC1C\uD654\uB97C \uC790\uB3D9 \uBCC0\uD658\uD55C \uCC38\uACE0\uC6A9 \uAE30\uB85D\uC785\uB2C8\uB2E4. \uD654\uC790\xB7\uB9DE\uCDA4\uBC95 \uB4F1\uC774 \uC2E4\uC81C \uB300\uD654\uC640 \uC644\uC804\uD788 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    transcript_processing: "\uC2A4\uD06C\uB9BD\uD2B8\uAC00 \uCC98\uB9AC \uC911\uC785\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
    transcript_title: "\uC2A4\uD06C\uB9BD\uD2B8 \uC804\uBB38 \uBCF4\uAE30",
    view_transcript: "\uC6D0\uBB38 \uBCF4\uAE30",
    edited: "\uC218\uC815\uB428",
    notification: {
      title: "AI \uC694\uC57D",
      save_error: "\uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    },
    tab_feedback_question: "\uC774 \uB0B4\uC6A9\uC774 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
    tab_feedback_question_summary: "\uC774 \uC694\uC57D\uC774 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
    tab_feedback_question_analysis: "\uC774 \uBD84\uC11D\uC774 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
    tab_feedback_up: "\uB3C4\uC6C0\uC774 \uB410\uC5B4\uC694",
    tab_feedback_down: "\uC544\uC26C\uC6CC\uC694",
    tab_feedback_submitted_title: "\uD53C\uB4DC\uBC31 \uC81C\uCD9C \uC644\uB8CC!",
    tab_feedback_submitted_message: "\uC18C\uC911\uD55C \uC758\uACAC \uAC10\uC0AC\uD569\uB2C8\uB2E4"
  },
  aria: {
    create_meeting: "\uBBF8\uD305 \uB9CC\uB4E4\uAE30",
    create_one_on_one: "\uC0C8 1on1 \uB9CC\uB4E4\uAE30"
  },
  counterpart: {
    empty: "\uC120\uD0DD\uD560 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    invite_reward: "\uD300\uC6D0\uC744 \uC774 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uCD08\uB300\uD574 \uBBF8\uD305 \uC0C1\uB300\uB85C \uC9C0\uC815\uD560 \uC218 \uC788\uC5B4\uC694.\n\uCD08\uB300\uB97C \uC218\uB77D\uD558\uBA74 \uBBF8\uD305 \uBD84\uC11D \uD69F\uC218 2\uD68C\uB97C \uB354 \uB4DC\uB824\uC694.",
    invite_where: "\uC67C\uCABD \uC0AC\uC774\uB4DC\uBC14\uC5D0\uC11C '\uBA64\uBC84 \uCD08\uB300\uD558\uAE30'\uB97C \uB20C\uB7EC \uD300\uC6D0\uC744 \uCD08\uB300\uD574\uBCF4\uC138\uC694.",
    group_my_member: "\uC9C1\uC18D \uD558\uC704 \uBA64\uBC84",
    group_pair: "\uC8FC\uAE30\uC801\uC778 1on1 \uBA64\uBC84",
    group_same_team: "\uC18C\uC18D \uD300 \uBA64\uBC84",
    label: "\uBBF8\uD305 \uC0C1\uB300\uB294 \uB204\uAD6C\uC778\uAC00\uC694?"
  },
  self_meeting: {
    or: "\uB610\uB294",
    mode_self: "\uC0C1\uB300 \uC9C0\uC815 \uC5C6\uC774 \uB9CC\uB4E4\uAE30",
    self_description: "\uD63C\uC790 \uBA3C\uC800 \uC368\uBCF4\uB294 \uCCB4\uD5D8\uC6A9\uC774\uC5D0\uC694. \uC2E4\uC81C 1on1\uC740 \uD300\uC6D0\uC744 \uCD08\uB300\uD574 \uD568\uAED8 \uC9C4\uD589\uD574\uC694.",
    balance_label: "\uC794\uC5EC {{count}}\uD68C",
    balance_banner: "\uBD84\uC11D \uAC00\uB2A5\uD55C \uBBF8\uD305\uC774 {{count}}\uD68C \uB0A8\uC558\uC5B4\uC694",
    no_balance: "\uBD84\uC11D \uAC00\uB2A5\uD55C \uD69F\uC218\uB97C \uBAA8\uB450 \uC0AC\uC6A9\uD588\uC5B4\uC694. \uC720\uB8CC \uD50C\uB79C\uC5D0\uC11C\uB294 \uC81C\uD55C \uC5C6\uC774 \uC774\uC6A9\uD560 \uC218 \uC788\uC5B4\uC694.",
    balance_warning: "\uBD84\uC11D \uD69F\uC218\uB97C \uBAA8\uB450 \uC0AC\uC6A9\uD588\uC5B4\uC694. \uC774 \uBBF8\uD305\uC740 \uB179\uC74C\xB7AI \uBD84\uC11D \uC5C6\uC774 \uC9C4\uD589\uB429\uB2C8\uB2E4. \uC720\uB8CC \uD50C\uB79C\uC5D0\uC11C\uB294 \uC81C\uD55C \uC5C6\uC774 \uC774\uC6A9\uD560 \uC218 \uC788\uC5B4\uC694.",
    card_badge: "\uBBF8\uC9C0\uC815",
    label: "(\uBBF8\uC9C0\uC815)",
    detail_nudge: "\uD300\uC6D0\uC744 \uCD08\uB300\uD558\uBA74 \uC0C1\uB300\uBC29 \uC815\uBCF4\uB3C4 \uAE30\uB85D\uD560 \uC218 \uC788\uC5B4\uC694",
    update_description: "\uC0C1\uB300\uBC29 \uC5C6\uC774 \uD63C\uC790 \uC9C4\uD589\uD558\uB294 \uBBF8\uD305\uC774\uC5D0\uC694.\n\uB0A0\uC9DC\xB7\uC2DC\uAC04\uB9CC \uC218\uC815\uD560 \uC218 \uC788\uC5B4\uC694.",
    planned_card_label: "\uBA64\uBC84 \uBBF8\uC9C0\uC815",
    planned_card_description: "\uC0C1\uB300\uBC29\uC744 \uC9C0\uC815\uD558\uC9C0 \uC54A\uC740 \uBBF8\uD305\uC774\uC5D0\uC694."
  },
  date: {
    days_ago: "{{count}}\uC77C \uC804",
    days_left: "{{count}}\uC77C \uD6C4",
    label: "\uBBF8\uD305 \uB0A0\uC9DC\uB97C \uC815\uD574\uC8FC\uC138\uC694.",
    overdue: "{{count}}\uC77C \uC9C0\uB0A8",
    today: "\uC624\uB298",
    tomorrow: "\uB0B4\uC77C",
    yesterday: "\uC5B4\uC81C"
  },
  day_of_week: {
    label: "\uBBF8\uD305 \uC694\uC77C\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694."
  },
  dialog: {
    create: "\uBBF8\uD305 \uB9CC\uB4E4\uAE30",
    delete_description: "\uD574\uB2F9 \uBBF8\uD305\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    delete_error: "\uBBF8\uD305 \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    delete_success: "\uBBF8\uD305\uC774 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    delete_title: "1on1 \uBBF8\uD305 \uC0AD\uC81C\uD558\uAE30",
    title: "1on1 \uBBF8\uD305 \uB9CC\uB4E4\uAE30",
    update_title: "1on1 \uBBF8\uD305 \uC218\uC815\uD558\uAE30",
    complete_description: "\uC774\uBBF8 \uBBF8\uD305\uC744 \uC644\uB8CC\uD558\uC168\uB098\uC694?\n\uBBF8\uD305\uC744 \uC644\uB8CC \uC0C1\uD0DC\uB85C \uBCC0\uACBD\uD558\uACE0 \uAE30\uB85D\uC744 \uB9C8\uBB34\uB9AC\uD569\uB2C8\uB2E4.",
    complete_button: "\uC644\uB8CC \uCC98\uB9AC\uD558\uAE30",
    complete_error: "\uBBF8\uD305 \uC644\uB8CC \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    select_counterpart: "\uBBF8\uD305 \uC0C1\uB300\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    search_member_placeholder: "\uC774\uB984\uC774\uB098 \uC774\uBA54\uC77C \uC8FC\uC18C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    no_members: "\uC120\uD0DD\uD560 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    select_date: "{{name}}\uB2D8\uACFC \uBBF8\uD305\uD560 \uB0A0\uC9DC\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    select_date_self: "\uBBF8\uD305 \uB0A0\uC9DC\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    select_time: "{{name}}\uB2D8\uACFC\uC758 {{date}} \uBBF8\uD305 \uC2DC\uAC04\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    select_time_self: "{{date}} \uBBF8\uD305 \uC2DC\uAC04\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    date_format: "M\uC6D4 D\uC77C",
    meeting_time_label: "\uBBF8\uD305 \uC2DC\uAC04",
    role_label: "\uB098\uC758 \uC5ED\uD560",
    leader: "\uB9AC\uB354",
    member: "\uBA64\uBC84"
  },
  duration: {
    minutes: "{{value}}\uBD84"
  },
  error: {
    api_endpoint_missing: "API \uC5D4\uB4DC\uD3EC\uC778\uD2B8 URL\uC774 \uC124\uC815\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4.",
    api_request_failed: "API \uD638\uCD9C \uC2E4\uD328",
    api_response_error: "API \uC5D0\uB7EC \uC751\uB2F5",
    invalid_input: "\uC62C\uBC14\uB974\uC9C0 \uC54A\uC740 \uC785\uB825\uC774 \uD3EC\uD568\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.",
    meeting_id_missing: "\uBBF8\uD305 \uC815\uBCF4\uB97C \uCC3E\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
    question_generation_failed: {
      title: "AI \uC9C8\uBB38 \uC0DD\uC131 \uC2E4\uD328",
      message: "\uC9C8\uBB38 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uD654\uBA74\uC744 \uC0C8\uB85C\uACE0\uCE68\uD558\uACE0 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    }
  },
  frequency: {
    biweekly: "2\uC8FC\uB9C8\uB2E4",
    every_3_weeks: "3\uC8FC\uB9C8\uB2E4",
    every_5_weeks: "5\uC8FC\uB9C8\uB2E4",
    every_6_weeks: "6\uC8FC\uB9C8\uB2E4",
    every_7_weeks: "7\uC8FC\uB9C8\uB2E4",
    every_8_weeks: "8\uC8FC\uB9C8\uB2E4",
    label: "\uBBF8\uD305 \uC8FC\uAE30\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    monthly: "4\uC8FC\uB9C8\uB2E4",
    no_schedule: "\uC8FC\uAE30 \uC5C6\uC74C",
    placeholder: "\uC8FC\uAE30\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
    weekly: "\uB9E4\uC8FC"
  },
  leader: {
    counterpart: "\uC0C1\uB300\uBC29",
    info: "\uB9AC\uB354\uB294 \uC2DC\uC791, \uC885\uB8CC \uAD8C\uD55C\uACFC \uC0C1\uB300\uC758 \uC5C5\uBB34 \uBAB0\uC785\uB3C4 \uBC0F \uC131\uC7A5 \uB9CC\uC871\uB3C4 \uCCB4\uD06C, 1on1 \uBD84\uC11D \uC778\uC0AC\uC774\uD2B8\uB97C \uC81C\uACF5\uBC1B\uAC8C\uB3FC\uC694.",
    label: "\uB9AC\uB354\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    me: "\uB098"
  },
  meeting_type: {
    label: "\uBBF8\uD305 \uC720\uD615\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    offline: "\uB300\uBA74",
    online: "\uD654\uC0C1",
    video_link_placeholder: "Google Meet \uB9C1\uD06C \uBD99\uC5EC\uB123\uAE30",
    video_link_description: "\uC9C0\uAE08\uC740 Google Meet \uB9C1\uD06C\uB9CC \uAC00\uB2A5\uD574\uC694. (Zoom \uCD94\uD6C4 \uC9C0\uC6D0 \uC608\uC815)",
    invalid_meet_link: "\uC720\uD6A8\uD55C Google Meet \uB9C1\uD06C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    create_meet: "\uC0C8 \uD68C\uC758 \uB9CC\uB4E4\uAE30",
    create_meet_link: "Google Meet \uB9C1\uD06C \uC0DD\uC131",
    creating_meet_link: "\uB9C1\uD06C \uC0DD\uC131 \uC911...",
    meet_link_created: "Google Meet \uB9C1\uD06C\uAC00 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    meet_link_error: "Meet \uB9C1\uD06C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    additional_scope_required: "\uD654\uC0C1 \uBBF8\uD305 \uAE30\uB2A5\uC744 \uC0AC\uC6A9\uD558\uB824\uBA74 \uCD94\uAC00 \uAD8C\uD55C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.",
    meet_scope_granted: "Google Meet \uAD8C\uD55C\uC774 \uC5F0\uACB0\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    grant_permission: "\uAD8C\uD55C \uD5C8\uC6A9",
    no_link: "\uC544\uC9C1 \uB4F1\uB85D\uB41C \uBBF8\uD305 \uB9C1\uD06C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
    add_link: "\uCD94\uAC00\uD558\uAE30",
    edit_link: "\uB9C1\uD06C \uC218\uC815",
    add_link_placeholder: "Google Meet \uB9C1\uD06C\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694",
    add_link_save: "\uC800\uC7A5",
    copy_link: "\uB9C1\uD06C \uBCF5\uC0AC",
    link_copied: "\uBBF8\uD305 \uB9C1\uD06C\uAC00 \uBCF5\uC0AC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    link_saved: "\uBBF8\uD305 \uB9C1\uD06C\uAC00 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    link_save_error: "\uBBF8\uD305 \uB9C1\uD06C \uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    auto_generate_meet: "Google Meet \uC790\uB3D9 \uC0DD\uC131",
    auto_generate_meet_desc: "{{email}} \uACC4\uC815\uC73C\uB85C Meet \uB9C1\uD06C\uAC00 \uC0DD\uC131\uB3FC\uC694",
    manual_input: "\uC9C1\uC811 \uC785\uB825",
    or_manual_input: "\uB610\uB294 \uC9C1\uC811 \uB9C1\uD06C \uC785\uB825",
    connect_calendar_hint: "Google \uCE98\uB9B0\uB354\uB97C \uC5F0\uB3D9\uD558\uBA74 Meet \uB9C1\uD06C\uAC00 \uC790\uB3D9\uC73C\uB85C \uC0DD\uC131\uB429\uB2C8\uB2E4.",
    connect_calendar_action: "\uC5F0\uB3D9\uD558\uB7EC \uAC00\uAE30",
    not_organizer_hint: "\uC774 \uBBF8\uD305\uC740 \uC0C1\uB300\uBC29\uC758 \uCE98\uB9B0\uB354\uC5D0 \uB4F1\uB85D\uB418\uC5B4 \uC788\uC5B4 \uC790\uB3D9 \uC0DD\uC131\uC774 \uBD88\uAC00\uB2A5\uD574\uC694. \uC544\uB798\uC5D0 \uC9C1\uC811 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    join_meeting: "\uBBF8\uD305 \uCC38\uAC00",
    complete_meeting: "1on1 \uBBF8\uD305 \uC644\uB8CC\uD558\uAE30",
    complete_meeting_tooltip: "Google Meet\uC5D0\uC11C \uD68C\uC758\uB97C \uB9C8\uCE58\uC168\uB2E4\uBA74, \uC774 \uBBF8\uD305\uC744 \uC885\uB8CC\uD574\uC8FC\uC138\uC694!",
    guide: "\uB9C1\uD06C\uB85C \uD68C\uC758\uC5D0 \uCC38\uC5EC\uD558\uACE0 \uB179\uD654 \uC2DC\uC791 \uBC84\uD2BC\uC744 \uB20C\uB7EC \uB179\uD654 \uAE30\uB2A5\uC744 \uCF1C\uC8FC\uC138\uC694. \uB179\uD654\uBCF8\uC774 \uC788\uC5B4\uC57C \uBBF8\uD305\uC744 \uBD84\uC11D\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    guide_link: "\uB179\uD654 \uBC29\uBC95 \uBCF4\uAE30",
    join_issue: "\uCC38\uC5EC\uAC00 \uC548 \uB418\uC2DC\uB098\uC694?",
    join_issue_description: "\uB9C1\uD06C \uC811\uC18D \uC2DC '\uC9C0\uAE08 \uCC38\uC5EC\uD558\uAE30'\uAC00 \uC544\uB2CC '\uCC38\uC5EC \uC694\uCCAD'\uC774 \uBCF4\uC778\uB2E4\uBA74, \uD604\uC7AC \uBE0C\uB77C\uC6B0\uC800\uC5D0\uC11C \uB85C\uADF8\uC778\uB41C Google \uACC4\uC815\uC774 Meet \uB9C1\uD06C\uB97C \uB9CC\uB4E0 \uACC4\uC815\uACFC \uB3D9\uC77C\uD55C\uC9C0 \uD655\uC778\uD574\uC8FC\uC138\uC694.",
    complete_dialog: {
      title: "Meet \uD68C\uC758\uB97C \uC885\uB8CC\uD558\uC168\uB098\uC694?",
      description: "\uB179\uD654 \uD30C\uC77C\uC774 Google Drive\uC5D0 \uC900\uBE44\uB418\uBA74(\uC57D 10~15\uBD84) \uBA54\uC77C\uB85C \uC54C\uB824\uB4DC\uB824\uC694.\n\uBA54\uC77C \uD655\uC778 \uD6C4 [\uB179\uD654 \uD30C\uC77C \uAC00\uC838\uC624\uAE30] \uBC84\uD2BC\uC73C\uB85C \uD30C\uC77C\uC744 \uC120\uD0DD\uD558\uC2DC\uBA74 AI \uBD84\uC11D\uC774 \uC2DC\uC791\uB429\uB2C8\uB2E4."
    },
    recording_guide_dialog: {
      title: "Google Meet \uB179\uD654 \uBC29\uBC95",
      as_of_date: "2026\uB144 4\uC6D4 \uAE30\uC900",
      learn_more: "\uB354 \uC54C\uC544\uBCF4\uAE30",
      pre_join_emphasis: "\uB179\uD654\uB41C \uBBF8\uD305\uB9CC AI \uBD84\uC11D\uC774 \uAC00\uB2A5\uD574\uC694",
      pre_join_subtext_action: "\uBBF8\uD305 \uC2DC\uC791 \uC9C1\uD6C4 [\uB179\uD654] \uBC84\uD2BC\uC744 \uAF2D \uB20C\uB7EC\uC8FC\uC138\uC694",
      pre_join_subtext_plan: "\uB179\uD654\uB294 Google Workspace Business Standard \uC774\uC0C1 \uD50C\uB79C\uC5D0\uC11C\uB9CC \uAC00\uB2A5\uD569\uB2C8\uB2E4",
      dont_show_again: "\uB2E4\uC2DC \uBCF4\uC9C0 \uC54A\uAE30",
      join_now: "\uBBF8\uD305 \uCC38\uAC00\uD558\uAE30",
      acknowledge: "\uD655\uC778",
      cancel: "\uCDE8\uC18C"
    },
    import_status: {
      polling: "Google Meet \uB179\uD654\uAC00 \uC9C4\uD589 \uC911\uC785\uB2C8\uB2E4.",
      processing: "Google Meet\uC5D0\uC11C \uB179\uD654 \uD30C\uC77C \uC900\uBE44 \uC911... (\uBCF4\uD1B5 5~10\uBD84)",
      ready: "\uB179\uD654 \uD30C\uC77C\uC774 \uC900\uBE44\uB410\uC5B4\uC694. \uAC00\uC838\uC624\uAE30 \uBC84\uD2BC\uC744 \uB20C\uB7EC\uC8FC\uC138\uC694.",
      ready_hint: "\uB179\uD654\uB97C \uC9C1\uC811 \uC2DC\uC791\uD558\uC9C0 \uC54A\uC73C\uC168\uB2E4\uBA74 \uD30C\uC77C \uCCA8\uBD80\uAC00 \uC548 \uB420 \uC218\uB3C4 \uC788\uC5B4\uC694. \uB179\uD654\uB97C \uC2DC\uC791\uD558\uC2E0 \uBD84\uC774 \uCCA8\uBD80\uD558\uB294 \uAC83\uC744 \uAD8C\uC7A5\uD569\uB2C8\uB2E4.",
      importing: "Google Drive\uC5D0\uC11C \uB179\uD654 \uD30C\uC77C \uAC00\uC838\uC624\uB294 \uC911...",
      completed: "\uD654\uC0C1 \uB179\uD654 \uD30C\uC77C \uC900\uBE44 \uC644\uB8CC",
      not_found: "\uD654\uC0C1 \uB179\uD654 \uD30C\uC77C\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      failed: "\uD654\uC0C1 \uB179\uD654 \uD30C\uC77C \uAC00\uC838\uC624\uAE30 \uC2E4\uD328",
      manual_upload_hint: "\uB179\uD654 \uD30C\uC77C\uC774 \uB85C\uCEEC\uC5D0 \uC788\uB2E4\uBA74 \uD558\uB2E8\uC758 '\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC'\uB97C \uC774\uC6A9\uD574\uC8FC\uC138\uC694."
    }
  },
  meet_recording: {
    picker: {
      button_label: "\uB179\uD654 \uD30C\uC77C \uAC00\uC838\uC624\uAE30",
      api_key_missing_title: "Picker \uC124\uC815 \uB204\uB77D",
      api_key_missing_message: "Google Picker API \uD0A4\uAC00 \uC124\uC815\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD574\uC8FC\uC138\uC694.",
      load_error_title: "Picker \uB85C\uB4DC \uC2E4\uD328",
      load_error_message: "Google Picker\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      attach_success_title: "\uB179\uD654 \uD30C\uC77C \uCCA8\uBD80 \uC644\uB8CC",
      attach_success_message: "Drive\uC5D0\uC11C \uD30C\uC77C\uC744 \uAC00\uC838\uC640 AI \uBD84\uC11D\uC744 \uC2DC\uC791\uD569\uB2C8\uB2E4.",
      attach_error_title: "\uB179\uD654 \uD30C\uC77C \uCCA8\uBD80 \uC2E4\uD328",
      attach_error_message: "\uB179\uD654 \uD30C\uC77C\uC744 \uCCA8\uBD80\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      attach_error_no_access: "\uC774 \uD30C\uC77C\uC740 \uB2E4\uB978 \uBD84\uC758 Drive\uC5D0 \uC788\uC744 \uC218 \uC788\uC5B4\uC694. \uB179\uD654\uB97C \uC2DC\uC791\uD558\uC2E0 \uBD84\uAED8 \uCCA8\uBD80\uB97C \uBD80\uD0C1\uB4DC\uB824\uC694."
    }
  },
  meeting: {
    no_previous_meeting: "\uBBF8\uD305 \uAE30\uB85D \uC5C6\uC74C",
    meeting_home: "\uD648"
  },
  meeting_detail: {
    prep: "\uD504\uB819",
    previous_action_items: "\uC9C0\uB09C \uC561\uC158 \uC544\uC774\uD15C",
    add_question: {
      title: "\uC9C8\uBB38 \uCD94\uAC00\uD558\uAE30",
      success: "\uC9C8\uBB38\uC774 \uBCF8\uBB38 \uD0ED\uC758 \uC9C8\uBB38 \uBAA9\uB85D\uC5D0 \uCD94\uAC00\uB418\uC5C8\uC5B4\uC694.",
      error: "\uC9C8\uBB38\uC744 \uCD94\uAC00\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      success_bulk: "\uC9C8\uBB38 {{count}}\uAC1C\uAC00 \uBCF8\uBB38 \uD0ED\uC758 \uC9C8\uBB38 \uBAA9\uB85D\uC5D0 \uCD94\uAC00\uB418\uC5C8\uC5B4\uC694."
    },
    ai_generate: "AI \uC9C8\uBB38 \uC0DD\uC131",
    ai_generated: "AI\uAC00 \uC0DD\uC131\uD588\uC5B4\uC694!",
    answer_placeholder: "\uB2F5\uBCC0\uC744 \uC218\uC815\uD558\uB824\uBA74 \uD074\uB9AD\uD558\uC138\uC694.",
    answer_placeholder_new: "\uB2F5\uBCC0\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    bring_template: "\uD15C\uD50C\uB9BF \uBD88\uB7EC\uC624\uAE30",
    delete_recommended_question: {
      title: "\uCD94\uCC9C \uC9C8\uBB38 \uC0AD\uC81C\uD558\uAE30",
      error: "\uCD94\uCC9C \uC9C8\uBB38\uC744 \uC0AD\uC81C\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    },
    editing_placeholder: "\uC9C8\uBB38\uC744 \uC800\uC7A5\uD55C \uD6C4 \uB2F5\uBCC0\uC744 \uC785\uB825\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    end_meeting: {
      action_button: "1on1 \uBBF8\uD305 \uC885\uB8CC\uD558\uAE30",
      description: "\uC6D0\uC628\uC6D0 \uBBF8\uD305\uC744 \uC885\uB8CC\uD558\uC2DC\uACA0\uC5B4\uC694?",
      error_message: "\uBBF8\uD305 \uC885\uB8CC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      error_title: "\uBBF8\uD305 \uC885\uB8CC \uC2E4\uD328",
      network_notice: "\uB179\uC74C \uD30C\uC77C\uC740 \uBBF8\uD305 \uC885\uB8CC \uC2DC \uC790\uB3D9\uC73C\uB85C \uC5C5\uB85C\uB4DC\uB429\uB2C8\uB2E4. \uC548\uC815\uC801\uC778 \uB124\uD2B8\uC6CC\uD06C \uD658\uACBD\uC5D0\uC11C \uC5C5\uB85C\uB4DC\uB418\uBA74 \uAC00\uC7A5 \uC88B\uC2B5\uB2C8\uB2E4.",
      title: "\uBBF8\uD305 \uC885\uB8CC \uC548\uB0B4",
      other_device_title: "\uB2E4\uB978 \uAE30\uAE30\uC5D0\uC11C \uB179\uC74C \uC911\uC774\uC2E0 \uAC83 \uAC19\uC544\uC694",
      other_device_body: "\uB179\uC74C \uC911\uC778 \uAE30\uAE30\uC5D0\uC11C \uBBF8\uD305\uC744 \uC885\uB8CC\uD574\uC8FC\uC138\uC694. \uC774 \uAE30\uAE30\uC5D0\uC11C \uB179\uC74C \uC5C6\uC774 \uC885\uB8CC\uD558\uC2DC\uBA74 \uBBF8\uD305\uC744 \uBD84\uC11D\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      other_device_end_anyway: "\uB179\uC74C \uC5C6\uC774 \uC885\uB8CC"
    },
    is_editing: "{{editor}}\uB2D8\uC774 \uD3B8\uC9D1 \uC911...",
    leader: "\uB9AC\uB354",
    my_note: "\uB098\uB9CC\uC758 \uB178\uD2B8",
    my_note_placeholder: "\uB098\uB9CC \uBCFC \uC218 \uC788\uB294 \uBA54\uBAA8\uC785\uB2C8\uB2E4.",
    past_meetings_title: "{{name}} \uB2D8\uACFC\uC758 \uC9C0\uB09C \uBBF8\uD305",
    past_meetings_title_generic: "\uC9C0\uB09C \uBBF8\uD305",
    past_meetings_empty: "\uC774 \uBBF8\uD305 \uC774\uC804\uC758 \uAE30\uB85D\uC774 \uC5C6\uC5B4\uC694",
    past_meetings_self_unavailable_title: "\uC9C0\uB09C \uBBF8\uD305 \uAE30\uB85D\uC744 \uBCFC \uC218 \uC5C6\uC5B4\uC694",
    past_meetings_self_unavailable: "\uC0C1\uB300\uBC29\uC774 \uC9C0\uC815\uB41C 1on1 \uBBF8\uD305\uC5D0\uC11C \uC9C0\uB09C \uBBF8\uD305 \uAE30\uB85D\uC744 \uD655\uC778\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    question_delete_modal: {
      description: "\uC9C8\uBB38\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      title: "\uC9C8\uBB38 \uC0AD\uC81C"
    },
    question_placeholder: "\uC9C8\uBB38\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
    question_request: "\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD \uBA54\uC77C \uBC1C\uC1A1",
    question_request_success: "\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD \uBA54\uC77C \uBC1C\uC1A1\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    question_request_failed: "\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD \uBA54\uC77C \uBC1C\uC1A1\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.\n\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    questions: "\uC9C8\uBB38",
    recording_duration: "\uB179\uC74C \uC644\uB8CC",
    recording_time: "\uB179\uC74C \uC911",
    draft_badge: "\uCD08\uC548",
    draft_onboarding: "\uC791\uC131\uD55C \uC9C8\uBB38\uC740 '\uC9C8\uBB38 \uACF5\uAC1C\uD558\uAE30'\uB97C \uB20C\uB7EC\uC57C \uC0C1\uB300\uBC29\uC5D0\uAC8C \uACF5\uAC1C\uB429\uB2C8\uB2E4.",
    publish_questions: "\uC9C8\uBB38 \uACF5\uAC1C\uD558\uAE30",
    publish_questions_confirm: "\uC791\uC131\uD55C \uCD08\uC548 \uC9C8\uBB38 {{count}}\uAC1C\uAC00 \uC0C1\uB300\uBC29\uC5D0\uAC8C \uACF5\uAC1C\uB418\uBA70,\n\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD \uBA54\uC77C\uC774 \uD568\uAED8 \uBC1C\uC1A1\uB429\uB2C8\uB2E4.\n\n\uACF5\uAC1C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    confirm_questions_success: "\uC9C8\uBB38 \uD655\uC778\uC744 \uC644\uB8CC\uD588\uC5B4\uC694.",
    send_questions: "\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD\uD558\uAE30",
    send_questions_confirm: "\uC0C1\uB300\uBC29\uC5D0\uAC8C \uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD \uBA54\uC77C\uC744 \uBCF4\uB0B4\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    send_questions_confirm_mobile: "\uBA54\uC77C\uACFC \uC54C\uB9BC\uC73C\uB85C \uC9C8\uBB38 \uD655\uC778\uC744 \uC694\uCCAD\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.\n\uC0C1\uB300\uBC29\uC5D0\uAC8C \uC9C8\uBB38 \uD655\uC778\uC744 \uC694\uCCAD\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    start_meeting: {
      action_button: "\uBBF8\uD305 \uC2DC\uC791",
      agree_description: "\uC0C1\uB300\uBC29\uC758 \uB3D9\uC758\uB97C \uAD6C\uD588\uB2E4\uBA74 \uCCB4\uD06C\uD574\uC8FC\uC138\uC694.\n\uC0C1\uD638 \uB3D9\uC758\uAC00 \uC5C6\uB2E4\uBA74 \uB179\uC74C \uC5C6\uC774 \uBBF8\uD305\uC774 \uC9C4\uD589\uB429\uB2C8\uB2E4.",
      agree_title: "\uC0C1\uB300\uBC29\uC758 \uB3D9\uC758\uB97C \uAD6C\uD588\uC5B4\uC694.",
      ai_consent_title: "\uD68C\uC758\uB85D\xB7\uC694\uC57D\uC744 \uC704\uD574 \uB179\uC74C \uB370\uC774\uD130\uAC00 \uC678\uBD80 AI \uC11C\uBE44\uC2A4\uB85C \uC804\uC1A1\uB428\uC5D0 \uB3D9\uC758\uD569\uB2C8\uB2E4.",
      ai_consent_details_toggle: "\uC790\uC138\uD788 \uBCF4\uAE30",
      ai_consent_details_body: "\uB179\uC74C\uB41C \uC74C\uC131\uACFC \uBCC0\uD658\uB41C \uD14D\uC2A4\uD2B8\uB294 \uD68C\uC758\uB85D \uC0DD\uC131, \uB0B4\uC6A9 \uC694\uC57D, \uBC1C\uD654 \uBD84\uC11D \uB4F1\uC744 \uC704\uD574 \uB2E4\uC74C \uC678\uBD80 AI \uC11C\uBE44\uC2A4\uB85C \uC804\uC1A1\xB7\uCC98\uB9AC\uB429\uB2C8\uB2E4.\n- AssemblyAI Inc. (\uC74C\uC131\u2192\uD14D\uC2A4\uD2B8 \uBCC0\uD658)\n- Google LLC (Vertex AI/Gemini)\n- OpenAI OpCo, L.L.C.\n- Anthropic PBC\n\n\uB3D9\uC758 \uB0B4\uC5ED\uC740 \uB514\uBC14\uC774\uC2A4\uC5D0 \uAE30\uB85D\uB418\uBA70, \uAC1C\uC778\uC815\uBCF4 \uCC98\uB9AC\uBC29\uCE68\uC774 \uBCC0\uACBD\uB420 \uACBD\uC6B0 \uB2E4\uC2DC \uC548\uB0B4\uB429\uB2C8\uB2E4.",
      ai_consent_privacy_policy_link: "\uAC1C\uC778\uC815\uBCF4 \uCC98\uB9AC\uBC29\uCE68 \uBCF4\uAE30",
      description_record_on: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uD68C\uC758\uB85D\uACFC \uB0B4\uC6A9 \uC694\uC57D\uC744 \uC790\uB3D9\uC73C\uB85C \uC0DD\uC131\uD574\uB4DC\uB824\uC694.",
      description_record_off: "\uB179\uC74C\uC744 \uD558\uC9C0 \uC54A\uC544\uB3C4 \uBBF8\uD305\uC740 \uC9C4\uD589\uD560 \uC218 \uC788\uC5B4\uC694.\n\uB179\uC74C \uAE30\uB2A5\uC744 \uCF1C\uC2DC\uBA74 \uD68C\uC758\uB85D\uBD80\uD130 \uB0B4\uC6A9 \uC694\uC57D\uAE4C\uC9C0 \uB3C4\uC640\uB4DC\uB824\uC694.",
      draft_notice: "\uBBF8\uD305\uC744 \uC2DC\uC791\uD558\uBA74 \uC11C\uB85C\uC758 \uCD08\uC548 \uC9C8\uBB38\uC774 \uACF5\uAC1C\uB429\uB2C8\uB2E4.\n\uACF5\uAC1C\uD558\uC9C0 \uC54A\uC744 \uC9C8\uBB38\uC740 \uC0AD\uC81C\uD55C \uD6C4\uC5D0 \uBBF8\uD305\uC744 \uC2DC\uC791\uD574\uC8FC\uC138\uC694.",
      record: "\uB179\uC74C \uAE30\uB2A5",
      recording_notice: "\uB179\uC74C \uC911\uC5D0\uB294 \uBC31\uADF8\uB77C\uC6B4\uB4DC \uC0AC\uC6A9\uC740 \uAD1C\uCC2E\uC9C0\uB9CC, \uC571\uC774 \uAC15\uC81C \uC885\uB8CC\uB418\uC9C0 \uC54A\uB3C4\uB85D \uC8FC\uC758\uD574\uC8FC\uC138\uC694. \uB179\uC74C \uD30C\uC77C\uC774 \uC720\uC2E4\uB420 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      title: "\uBBF8\uD305\uC744 \uC2DC\uC791\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
    },
    start_meeting_button: "\uB179\uC74C \uC2DC\uC791\uD558\uAE30",
    survey: {
      button: {
        later: "\uB098\uC911\uC5D0 \uD558\uAE30",
        save: "\uC800\uC7A5\uD558\uAE30"
      },
      engagement: {
        description: "{{name}}\uB2D8\uC758 \uD604\uC7AC \uC5C5\uBB34\uC640 \uD68C\uC0AC\uC5D0 \uB300\uD55C \uC804\uBC18\uC801\uC778 \uB9CC\uC871\uB3C4\uC640 \uBAB0\uC785 \uC218\uC900\uC740 \uC5B4\uB5BB\uC2B5\uB2C8\uAE4C?",
        title: "\uC5C5\uBB34 \uBAB0\uC785\uB3C4"
      },
      growth: {
        description: "{{name}}\uB2D8\uC774 \uC870\uC9C1\uC5D0\uC11C \uC131\uC7A5\uD558\uACE0 \uBC1C\uC804\uD558\uACE0 \uC788\uB2E4\uACE0 \uB290\uB07C\uB294 \uC815\uB3C4\uB294 \uC5B4\uB5BB\uC2B5\uB2C8\uAE4C?",
        title: "\uC131\uC7A5 \uB9CC\uC871\uB3C4"
      },
      notifications: {
        error: "\uC11C\uBCA0\uC774 \uC81C\uCD9C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
        success: "\uC11C\uBCA0\uC774\uAC00 \uC81C\uCD9C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        update: "\uC11C\uBCA0\uC774\uAC00 \uC218\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        delete: "\uC11C\uBCA0\uC774\uAC00 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
        title: "\uC815\uAE30\uC810\uAC80 \uC81C\uCD9C"
      },
      rating: {
        bad: "\uB098\uC068",
        fair: "\uC591\uD638",
        good: "\uC88B\uC74C",
        very_bad: "\uB9E4\uC6B0\uB098\uC068",
        very_good: "\uB9E4\uC6B0\uC88B\uC74C"
      },
      regular_checkin: "\uC815\uAE30 \uC810\uAC80",
      select_rating: "\uD3C9\uAC00\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      title: "\uBBF8\uD305\uC774 \uC885\uB8CC\uB418\uC5C8\uC5B4\uC694!"
    },
    pair_frequency: {
      set_label: "1on1 \uC8FC\uAE30 \uC124\uC815",
      manage_title: "{{name}}\uB2D8\uACFC\uC758 1on1 \uC8FC\uAE30",
      button: {
        save: "\uC800\uC7A5",
        remove: "\uC8FC\uAE30 \uD574\uC81C"
      },
      remove_confirm: {
        title: "1on1 \uC8FC\uAE30 \uD574\uC81C",
        description: "{{name}}\uB2D8\uACFC\uC758 1on1 \uC8FC\uAE30\uB97C \uD574\uC81C\uD560\uAE4C\uC694? \uB2E4\uC74C \uBBF8\uD305\uC744 \uC7A1\uC744 \uB54C\uAC00 \uB410\uB2E4\uB294 \uC54C\uB9BC\uB3C4 \uB354 \uC774\uC0C1 \uC624\uC9C0 \uC54A\uC544\uC694."
      },
      notification: {
        title: "1on1 \uC8FC\uAE30",
        saved: "\uBBF8\uD305 \uC8FC\uAE30\uB97C \uC800\uC7A5\uD588\uC5B4\uC694.",
        removed: "1on1 \uC8FC\uAE30\uB97C \uD574\uC81C\uD588\uC5B4\uC694.",
        error: "\uCC98\uB9AC\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
      }
    },
    next_meeting_nudge: {
      create_meeting: {
        title: "{{name}}\uB2D8\uACFC\uC758 1on1\uC774 \uB05D\uB0AC\uC5B4\uC694",
        description: "\uB2E4\uC74C \uBBF8\uD305\uC744 \uBBF8\uB9AC \uC7A1\uC544\uB458\uAE4C\uC694?",
        date_label: "\uBBF8\uD305 \uB0A0\uC9DC",
        date_hint: "\uC124\uC815\uD558\uC2E0 {{weeks}}\uC8FC \uC8FC\uAE30\uC5D0 \uB9DE\uCDB0 \uCD94\uCC9C\uB41C \uB0A0\uC9DC\uC608\uC694.",
        date_hint_other: "\uC124\uC815\uD558\uC2E0 {{weeks}}\uC8FC \uC8FC\uAE30\uC5D0 \uB9DE\uB294 \uCD94\uCC9C \uB0A0\uC9DC\uB294 {{date}}\uC774\uC5D0\uC694.",
        suggested_label: "{{weeks}}\uC8FC \uC8FC\uAE30 \uAE30\uC900 \uCD94\uCC9C \uB0A0\uC9DC",
        time_label: "\uC2DC\uAC04",
        time_placeholder: "\uC2DC\uAC04\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
        duration_label: "\uBBF8\uD305 \uC2DC\uAC04",
        button: {
          later: "\uB098\uC911\uC5D0",
          edit_schedule: "\uC77C\uC815 \uC218\uC815",
          confirm_suggested: "\uC774 \uC77C\uC815\uC73C\uB85C \uC7A1\uAE30",
          confirm: "\uB2E4\uC74C \uBBF8\uD305 \uC7A1\uAE30"
        },
        notification: {
          title: "\uB2E4\uC74C \uBBF8\uD305 \uC7A1\uAE30",
          success: "\uC120\uD0DD\uD558\uC2E0 \uB0A0\uC9DC\uB85C \uB2E4\uC74C \uBBF8\uD305 \uC77C\uC815\uC774 \uB4F1\uB85D\uB418\uC5C8\uC5B4\uC694.",
          error: "\uBBF8\uD305 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uBBF8\uD305 \uBAA9\uB85D\uC5D0\uC11C \uC9C1\uC811 \uB9CC\uB4E4\uC5B4\uC8FC\uC138\uC694."
        }
      },
      setup_pair: {
        title: "{{name}}\uB2D8\uACFC \uAFB8\uC900\uD788 \uB9CC\uB098\uBCF4\uC138\uC694",
        description: "\uC5BC\uB9C8\uB098 \uC790\uC8FC \uB9CC\uB098\uBA74 \uC88B\uC744\uC9C0 \uD568\uAED8 \uC815\uD574\uBCF4\uC138\uC694.\n\uB300\uD654\uC758 \uC628\uAE30\uAC00 \uC2DD\uAE30 \uC804\uC5D0 \uB2E4\uC2DC \uC774\uC5B4\uC9C8 \uC218 \uC788\uB3C4\uB85D, 2\uC8FC\uC5D0 \uD55C \uBC88\uC744 \uCD94\uCC9C\uD574\uC694.",
        frequency_label: "\uBBF8\uD305 \uC8FC\uAE30",
        frequency_hint: "\uB300\uD654 \uC2DC\uAC04\uC774 \uC880 \uC9E7\uB354\uB77C\uB3C4 \uACA9\uC8FC\uB85C \uC790\uC8FC \uB9CC\uB098\uB294 \uAC8C \uB354 \uD6A8\uACFC\uC801\uC774\uC5D0\uC694.",
        reminder_notice: "\uC8FC\uAE30\uB97C \uC800\uC7A5\uD574\uB450\uBA74, \uB2E4\uC74C \uBBF8\uD305\uC744 \uC7A1\uC744 \uB54C\uAC00 \uB410\uB294\uB370 \uC544\uC9C1 \uC77C\uC815\uC774 \uC5C6\uC744 \uB54C \uC774\uBA54\uC77C\uACFC \uC571 \uD478\uC2DC\uB85C \uC54C\uB824\uB4DC\uB824\uC694.",
        button: {
          later: "\uB098\uC911\uC5D0",
          confirm: "\uC8FC\uAE30 \uC124\uC815\uD558\uAE30"
        },
        notification: {
          title: "\uBBF8\uD305 \uC8FC\uAE30 \uC124\uC815",
          success: "\uBBF8\uD305 \uC8FC\uAE30 \uC124\uC815\uC774 \uC644\uB8CC\uB410\uC5B4\uC694.",
          success_mobile: "\uBBF8\uD305 \uC8FC\uAE30 \uC124\uC815\uC774 \uC644\uB8CC\uB410\uC5B4\uC694. \uBBF8\uD305 \uD654\uBA74 \uC0C1\uB2E8\uC5D0\uC11C \uC5B8\uC81C\uB4E0 \uBC14\uAFC0 \uC218 \uC788\uC5B4\uC694.",
          error: "\uC8FC\uAE30 \uC124\uC815\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uBBF8\uD305 \uBAA9\uB85D \uD654\uBA74\uC758 \uC6B0\uCE21 \uD328\uB110\uC5D0\uC11C \uB2E4\uC2DC \uC124\uC815\uD574\uC8FC\uC138\uC694.",
          error_mobile: "\uC8FC\uAE30 \uC124\uC815\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
        }
      }
    }
  },
  meeting_pdf: {
    modal_title: "PDF \uD30C\uC77C\uB85C \uC800\uC7A5",
    modal_description: "\uC800\uC7A5\uD560 \uD30C\uC77C\uC5D0 \uD3EC\uD568\uD560 \uD56D\uBAA9\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
    header_title: "1on1 \uBBF8\uD305",
    prep: "\uD504\uB819",
    prep_summary: "\uC694\uC57D",
    prep_detail: "\uC0C1\uC138",
    condition: "\uCEE8\uB514\uC158",
    topic: "\uBBF8\uD305 \uC8FC\uC81C",
    questions: "\uC9C8\uBB38 \uBC0F \uB2F5\uBCC0",
    ai_summary: "AI \uC694\uC57D",
    action_items: "\uC561\uC158\uC544\uC774\uD15C",
    previous_action_items: "\uC9C0\uB09C \uC561\uC158\uC544\uC774\uD15C",
    current_action_items: "\uC774\uBC88 \uC561\uC158\uC544\uC774\uD15C",
    due_date: "\uAE30\uD55C",
    summary: "\uC694\uC57D",
    decisions_made: "\uACB0\uC815 \uC0AC\uD56D",
    support_needs: "\uC9C0\uC6D0 \uD544\uC694 \uC0AC\uD56D",
    analysis: "\uBD84\uC11D",
    speaking_ratio: "\uBC1C\uD654 \uBE44\uC728",
    positive_feedback: "\uC798\uD55C \uC810",
    negative_feedback: "\uAC1C\uC120\uD560 \uC810",
    download: "\uB2E4\uC6B4\uB85C\uB4DC",
    error: "PDF \uD30C\uC77C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
  },
  meeting_page: {
    complete: "\uBBF8\uD305 \uC644\uB8CC \uCC98\uB9AC",
    download: "PDF \uD30C\uC77C\uB85C \uC800\uC7A5",
    edit: "\uC77C\uC815 \uBCC0\uACBD",
    empty_records: "\uC544\uC9C1 \uC644\uB8CC\uB41C \uBBF8\uD305 \uAE30\uB85D\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    empty_upcoming: "\uC608\uC815\uB41C \uBBF8\uD305\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    meeting_item: "\uBBF8\uD305 \uD56D\uBAA9",
    my_meetings: "\uB098\uC758 \uBBF8\uD305",
    my_records: "\uB0B4 \uBBF8\uD305 \uAE30\uB85D",
    table: {
      actual_date: "\uC9C4\uD589 \uB0A0\uC9DC",
      actual_duration: "\uC18C\uC694 \uC2DC\uAC04",
      ai_summary: "\uBBF8\uD305 \uC694\uC57D",
      counterpart: "\uC0C1\uB300",
      edit_summary: "\uBBF8\uD305 \uC694\uC57D \uC218\uC815",
      edit_summary_error: "\uBBF8\uD305 \uC694\uC57D \uC218\uC815 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      edit_summary_placeholder: "\uBBF8\uD305 \uC694\uC57D\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694.",
      edit_summary_success: "\uBBF8\uD305 \uC694\uC57D\uC774 \uC218\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      in_progress: "\uC9C4\uD589\uC911",
      title: "\uC81C\uBAA9"
    },
    title: "1on1 \uBBF8\uD305",
    unscheduled: "\uC77C\uC815 \uBBF8\uC815",
    untitled: "\uC81C\uBAA9 \uC5C6\uC74C",
    upcoming: "\uC608\uC815\uB41C \uBBF8\uD305",
    placeholder_first: "\uCCAB 1on1 \uB9CC\uB4E4\uAE30"
  },
  member_add_dialog: {
    add: "\uBA64\uBC84 \uCD94\uAC00",
    title: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uCD94\uAC00"
  },
  member_report: {
    create_button: "\uC0C8 \uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30",
    create_dialog: {
      all_meetings_selected: "\uC804\uCCB4 {{total}}\uAC74",
      cancel: "\uCDE8\uC18C",
      create_report: "\uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30",
      creating: "\uC0DD\uC131 \uC911...",
      creation_failed: "\uBCF4\uACE0\uC11C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
      data_collection_period: "\uB370\uC774\uD130 \uC218\uC9D1 \uAE30\uAC04 \uC120\uD0DD",
      description: "\uD300\uC6D0\uC758 \uC131\uACFC \uB9AC\uD3EC\uD2B8\uB97C \uC791\uC131\uD569\uB2C8\uB2E4.",
      duration: "\uC18C\uC694 \uC2DC\uAC04",
      error: "\uB9AC\uD3EC\uD2B8 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      fill_all_fields: "\uBAA8\uB4E0 \uD544\uB4DC\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694",
      max_three_months: "\uB370\uC774\uD130\uB294 \uCD5C\uB300 3\uAC1C\uC6D4\uAE4C\uC9C0\uB9CC \uC120\uD0DD\uD560 \uC218 \uC788\uC5B4\uC694",
      meetings_count: "{{selected}}/{{total}}\uAC74",
      member_not_found: "\uC120\uD0DD\uD55C \uBA64\uBC84\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
      minutes: "{{minutes}}\uBD84",
      no_direct_reports: "\uC9C1\uC18D \uD558\uC704 \uBA64\uBC84\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
      no_meetings_in_period: "\uD574\uB2F9 \uAE30\uAC04\uC5D0 1on1 \uBBF8\uD305\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
      no_meetings_selected: "\uC120\uD0DD\uB41C \uBBF8\uD305\uC774 \uC5C6\uC2B5\uB2C8\uB2E4",
      no_title: "\uC81C\uBAA9 \uC5C6\uC74C",
      report_created: "\uBCF4\uACE0\uC11C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4",
      select_meetings: "1on1 \uB0B4\uC5ED\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      select_member: "\uBA64\uBC84 \uC120\uD0DD",
      select_member_and_period: "\uBA3C\uC800 \uBA64\uBC84\uC640 \uAE30\uAC04\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      selected_meetings: "\uC120\uD0DD\uB41C 1on1",
      success: "\uB9AC\uD3EC\uD2B8\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      title: "\uC0C8 \uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30",
      topic: "\uC8FC\uC81C"
    },
    date_period: {
      custom: "\uAE30\uAC04 \uC120\uD0DD",
      last_1_month: "\uCD5C\uADFC 1\uAC1C\uC6D4",
      last_3_months: "\uCD5C\uADFC 3\uAC1C\uC6D4",
      select_period: "\uAE30\uAC04\uC744 \uC120\uD0DD\uD558\uC138\uC694"
    },
    detail: {
      action_items: "\uC561\uC158\uC544\uC774\uD15C",
      ai_loading: {
        analyzing_conversation: "\uB300\uD654 \uB0B4\uC6A9\uC744 \uBD84\uC11D\uD558\uB294 \uC911...",
        analyzing_growth: "\uBA64\uBC84 \uC131\uC7A5 \uD3EC\uC778\uD2B8\uB97C \uBD84\uC11D\uD558\uB294 \uC911...",
        collecting_data: "1on1 \uBBF8\uD305 \uB370\uC774\uD130\uB97C \uC218\uC9D1\uD558\uB294 \uC911...",
        creating_management_plan: "\uB9DE\uCDA4\uD615 \uAD00\uB9AC\uBC29\uC548\uC744 \uC791\uC131\uD558\uB294 \uC911...",
        deriving_insights: "\uB9AC\uB354\uC2ED \uC778\uC0AC\uC774\uD2B8\uB97C \uB3C4\uCD9C\uD558\uB294 \uC911...",
        evaluating_flight_risk: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4\uB97C \uD3C9\uAC00\uD558\uB294 \uC911...",
        finalizing_report: "\uCD5C\uC885 \uB9AC\uD3EC\uD2B8\uB97C \uC644\uC131\uD558\uB294 \uC911...",
        generating_topics: "\uB2E4\uC74C 1on1 \uCD94\uCC9C \uC8FC\uC81C\uB97C \uC0DD\uC131\uD558\uB294 \uC911...",
        identifying_topics: "\uD575\uC2EC \uC8FC\uC81C\uB97C \uD30C\uC545\uD558\uB294 \uC911...",
        organizing_performance: "\uC131\uACFC\uC640 \uAC1C\uC120\uC810\uC744 \uC815\uB9AC\uD558\uB294 \uC911..."
      },
      back_to_list: "\uBAA9\uB85D\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
      cancel: "\uCDE8\uC18C",
      contribution: "\uAE30\uC5EC",
      delete: "\uC0AD\uC81C",
      delete_confirm: "\uC815\uB9D0\uB85C \uC774 \uBCF4\uACE0\uC11C\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      delete_failed: "\uC0AD\uC81C \uC2E4\uD328",
      delete_failed_message: "\uBCF4\uACE0\uC11C \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
      delete_success: "\uC0AD\uC81C \uC644\uB8CC",
      delete_success_message: "\uBCF4\uACE0\uC11C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      demotivation_factors: "\uB3D9\uAE30 \uC800\uD558 \uC694\uC778",
      emotion_wellbeing: "\uAC10\uC815/\uC6F0\uBE59",
      error_title: "\uB9AC\uD3EC\uD2B8 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
      error_description: "AI \uBD84\uC11D \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.\n\uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
      export_pdf: "PDF\uB85C \uB0B4\uBCF4\uB0B4\uAE30",
      flight_risk: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4",
      goals: "\uBAA9\uD45C",
      growth: "\uC131\uC7A5",
      interested_projects: "\uD574\uBCF4\uACE0 \uC2F6\uC740 \uD504\uB85C\uC81D\uD2B8",
      interests: "\uAD00\uC2EC\uC0AC",
      loading_description: "1on1 \uBBF8\uD305 \uB370\uC774\uD130\uB97C \uBD84\uC11D\uD558\uC5EC \uB9AC\uD3EC\uD2B8\uB97C \uC0DD\uC131\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4.\n\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694.",
      management_plan: "\uB9DE\uCDA4\uD615 \uAD00\uB9AC \uBC29\uC548",
      member: "\uBA64\uBC84",
      member_report: "{{name}}\uB2D8 \uB9AC\uD3EC\uD2B8",
      motivation: "\uB3D9\uAE30",
      motivation_factors: "\uB3D9\uAE30 \uC694\uC778",
      network: "\uB124\uD2B8\uC6CC\uD06C",
      next_topics: "\uB2E4\uC74C 1on1 \uCD94\uCC9C \uC8FC\uC81C",
      no_action_items: "\uC561\uC158\uC544\uC774\uD15C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      no_content: "\uB0B4\uC6A9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      no_data: "\uB370\uC774\uD130 \uC5C6\uC74C",
      no_topics: "\uCD94\uCC9C \uC8FC\uC81C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.",
      not_found: "\uBCF4\uACE0\uC11C\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
      personal: "\uAC1C\uC778",
      retry: "\uB2E4\uC2DC \uC2DC\uB3C4",
      skills: "\uC2A4\uD0AC",
      strength_weakness: "\uAC15\uC810\uACFC \uC57D\uC810",
      strengths: "\uAC15\uC810",
      tab: {
        summary: "\uC694\uC57D",
        detail: "\uC0C1\uC138"
      },
      weaknesses: "\uC57D\uC810",
      work_blockers: "\uC5C5\uBB34 \uBE14\uB85C\uCEE4",
      work_content: "\uC5C5\uBB34 \uB0B4\uC6A9",
      work_performance: "\uC5C5\uBB34 \uC131\uACFC"
    },
    empty: {
      no_reports: "\uC0DD\uC131\uB41C \uBCF4\uACE0\uC11C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4."
    },
    empty_state: "\uC544\uC9C1 \uB9AC\uD3EC\uD2B8\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4. '\uC0C8 \uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30'\uB97C \uD074\uB9AD\uD558\uC5EC \uC2DC\uC791\uD558\uC138\uC694.",
    list: {
      date: "\uC0DD\uC131 \uB0A0\uC9DC",
      member: "\uBA64\uBC84",
      status: "\uC0C1\uD0DC",
      title: "\uB9AC\uD3EC\uD2B8 \uBAA9\uB85D"
    },
    statistics: {
      avg_duration: "\uD3C9\uADE0 \uC2DC\uAC04",
      completed_meetings: "\uC9C4\uD589\uD55C 1on1",
      hours: "\uC2DC\uAC04",
      meeting_count: "\uBBF8\uD305 \uD69F\uC218",
      minutes: "\uBD84",
      seconds: "\uCD08",
      times: "{{count}}\uD68C",
      together_1on1: "\uD568\uAED8\uD55C 1on1"
    },
    table: {
      created_date: "\uC0DD\uC131\uC77C",
      header: {
        content: "\uB0B4\uC6A9",
        member: "\uBA64\uBC84",
        period: "\uAE30\uAC04",
        register_date: "\uC0DD\uC131\uC77C"
      },
      ic_report: "IC \uBCF4\uACE0\uC11C",
      leader_report: "\uB9AC\uB354 \uBCF4\uACE0\uC11C",
      member: "\uBA64\uBC84",
      period: "\uAE30\uAC04",
      report_type: "\uBCF4\uACE0\uC11C \uC720\uD615"
    },
    title: "\uBA64\uBC84 \uB9AC\uD3EC\uD2B8"
  },
  member_update_dialog: {
    title: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uC218\uC815"
  },
  mobile: {
    tabs: {
      prep: "\uD504\uB819",
      content: "\uBCF8\uBB38",
      analysis: "\uBD84\uC11D",
      action_item: "\uC561\uC158\uC544\uC774\uD15C",
      note: "\uB178\uD2B8",
      qna: "\uC9C8\uBB38",
      summary: "\uC694\uC57D",
      transcript: "\uC2A4\uD06C\uB9BD\uD2B8"
    },
    my_note: {
      placeholder: "\uBBF8\uD305\uC5D0\uC11C \uCC38\uACE0\uD560 \uB098\uB9CC\uC758 \uBA54\uBAA8\uB97C \uC791\uC131\uD574\uBCF4\uC138\uC694.",
      save_failed: "\uB098\uB9CC\uC758 \uB178\uD2B8 \uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
    }
  },
  recording: {
    badge: "\uB179\uC74C",
    credit_notice: "\uB179\uC74C\uC744 \uB9C8\uCE58\uBA74 \uC790\uB3D9\uC73C\uB85C \uBD84\uC11D\uC774 \uC2DC\uC791\uB3FC, \uBD84\uC11D 1\uD68C\uAC00 \uCC28\uAC10\uB3FC\uC694.",
    upload_confirm: {
      title: "\uC774 \uD30C\uC77C\uB85C \uBD84\uC11D\uD560\uAE4C\uC694?",
      body: "\uC120\uD0DD\uD55C \uD30C\uC77C\uB85C \uBC14\uB85C \uBBF8\uD305 \uBD84\uC11D\uC744 \uC2DC\uC791\uD574\uC694.",
      credit_notice: "\uBD84\uC11D\uC774 \uC2DC\uC791\uB418\uBA74 \uBD84\uC11D 1\uD68C\uAC00 \uCC28\uAC10\uB3FC\uC694.",
      cancel: "\uCDE8\uC18C",
      confirm: "\uBD84\uC11D \uC2DC\uC791"
    },
    error: "\uB179\uC74C \uC624\uB958",
    error_browser_not_supported: "\uC774 \uBE0C\uB77C\uC6B0\uC800\uC5D0\uC11C\uB294 \uC624\uB514\uC624 \uB179\uC74C\uC744 \uC9C0\uC6D0\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    error_mic_permission: "\uB9C8\uC774\uD06C \uAD8C\uD55C\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694.",
    error_update_failed: "\uBBF8\uD305 \uC815\uBCF4 \uC5C5\uB370\uC774\uD2B8\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    error_save_failed: "\uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    error_track_ended: "\uB9C8\uC774\uD06C \uC5F0\uACB0\uC774 \uB04A\uACA8 \uB179\uC74C\uC774 \uC911\uB2E8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    error_recorder_error: "\uB179\uC74C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    error_mic_switch: "\uB9C8\uC774\uD06C \uC804\uD658\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB9C8\uC774\uD06C \uC5F0\uACB0 \uC0C1\uD0DC\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694.",
    error_network_offline: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0\uC774 \uB04A\uC5B4\uC84C\uC2B5\uB2C8\uB2E4. \uB179\uC74C\uC774 \uC911\uB2E8\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    in_progress: "\uB179\uC74C \uC911",
    owned_elsewhere: "\uB2E4\uB978 \uD0ED\uC5D0\uC11C \uB179\uC74C \uC911\uC785\uB2C8\uB2E4.",
    pause: "\uC77C\uC2DC\uC815\uC9C0",
    restart: "\uB2E4\uC2DC \uB179\uC74C",
    save: "\uB179\uC74C \uC800\uC7A5",
    start: "\uB179\uC74C \uC2DC\uC791",
    stop: "\uC911\uC9C0",
    title: "\uBBF8\uD305 \uB179\uC74C",
    title_with_name: "{{name}}\uB2D8\uACFC\uC758 1on1",
    title_with_name_mobile: "{{name}}\uB2D8\uACFC\uC758 1on1 \uC9C4\uD589 \uC911",
    title_self_meeting: "1on1 \uC9C4\uD589 \uC911",
    upload_recording: {
      cancel_upload_message: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uC911\uB2E8\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      cancel_upload_title: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC \uC911\uB2E8",
      continue_upload: "\uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uACC4\uC18D\uD569\uB2C8\uB2E4...",
      description: "\uC774\uBBF8 \uB179\uC74C\uB41C \uD30C\uC77C\uC774 \uC788\uB2E4\uBA74 \uC5C5\uB85C\uB4DC\uD574\uC8FC\uC138\uC694.\nAI\uAC00 \uB179\uC74C\uB41C \uD30C\uC77C\uC744 \uBD84\uC11D\uD574\uC11C \uBBF8\uD305 \uC694\uC57D\uC744 \uC0DD\uC131\uD574\uB4DC\uB824\uC694.",
      duration_error: {
        description: "\uB179\uC74C \uD30C\uC77C\uC740 \uCD5C\uB300 {{hours}}\uC2DC\uAC04\uAE4C\uC9C0 \uC9C0\uC6D0\uB429\uB2C8\uB2E4.",
        title: "\uB179\uC74C \uC2DC\uAC04 \uCD08\uACFC"
      },
      duration_warn: {
        description: "\uD30C\uC77C \uC815\uBCF4\uB97C \uC77D\uC744 \uC218 \uC5C6\uC5B4 \uC815\uD655\uD55C \uC2DC\uAC04\uC774 \uD45C\uC2DC\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
        error: "\uC624\uB514\uC624 \uD30C\uC77C\uC744 \uC77D\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
        title: "\uC624\uB514\uC624 \uAE38\uC774 \uC815\uBCF4 \uC5C6\uC74C"
      },
      network_error: {
        alert: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0\uC774 \uBD88\uC548\uC815\uD569\uB2C8\uB2E4. \uC5F0\uACB0 \uD655\uC778 \uD6C4 \uC7AC\uC2DC\uB3C4\uB429\uB2C8\uB2E4.",
        description: "\uB124\uD2B8\uC6CC\uD06C \uC0C1\uD0DC\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694.",
        error: "\uB124\uD2B8\uC6CC\uD06C\uAC00 \uBCF5\uAD6C\uB418\uC5C8\uC9C0\uB9CC \uC5C5\uB85C\uB4DC\uC5D0 \uB2E4\uC2DC \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
        failed: "\uB124\uD2B8\uC6CC\uD06C \uBCF5\uAD6C \uD6C4 \uD30C\uC77C \uC5C5\uB85C\uB4DC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
        reconnected: "\uB124\uD2B8\uC6CC\uD06C \uBCF5\uAD6C\uB428",
        retry: "\uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uC7AC\uC2DC\uB3C4\uD569\uB2C8\uB2E4...",
        success: "\uB124\uD2B8\uC6CC\uD06C \uBCF5\uAD6C \uD6C4 \uD30C\uC77C\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC5C5\uB85C\uB4DC\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uBBF8\uD305\uC744 \uC885\uB8CC\uD569\uB2C8\uB2E4.",
        title: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0 \uC624\uB958"
      },
      size_error: {
        description: "\uD30C\uC77C \uD06C\uAE30\uB294 1GB\uB97C \uCD08\uACFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
        title: "\uD30C\uC77C \uD06C\uAE30 \uCD08\uACFC"
      },
      start_upload: "\uD30C\uC77C \uC5C5\uB85C\uB4DC \uC2DC\uC791",
      title: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC",
      type_error: {
        description: "\uC624\uB514\uC624 \uD30C\uC77C\uB9CC \uC5C5\uB85C\uB4DC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
        title: "\uD30C\uC77C \uD615\uC2DD \uC624\uB958"
      },
      upload_cancelled: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC \uC911\uB2E8",
      upload_cancelled_message: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uC911\uB2E8\uD588\uC2B5\uB2C8\uB2E4.",
      upload_error: "\uC5C5\uB85C\uB4DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      upload_success: "\uD30C\uC77C\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC5C5\uB85C\uB4DC\uB418\uC5B4 \uBBF8\uD305\uC744 \uC885\uB8CC\uD569\uB2C8\uB2E4."
    },
    silence_warning: {
      leader: "\uB9C8\uC774\uD06C \uC785\uB825\uC774 \uAC10\uC9C0\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uB9C8\uC774\uD06C \uC124\uC815\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694.",
      member: "\uB179\uC74C\uC5D0 \uC18C\uB9AC\uAC00 \uAC10\uC9C0\uB418\uC9C0 \uC54A\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uB9AC\uB354\uC5D0\uAC8C \uB9C8\uC774\uD06C \uD655\uC778\uC744 \uC694\uCCAD\uD574\uC8FC\uC138\uC694."
    },
    warn: {
      message: "\uCD5C\uB300 \uB179\uC74C \uC2DC\uAC04({{hours}}\uC2DC\uAC04)\uC5D0 \uAC00\uAE4C\uC6CC\uC9C0\uACE0 \uC788\uC2B5\uB2C8\uB2E4. {{hours}}\uC2DC\uAC04 \uB3C4\uB2EC \uC2DC \uB179\uC74C\uC774 \uC790\uB3D9 \uC911\uC9C0\uB429\uB2C8\uB2E4.",
      title: "\uB179\uC74C \uC2DC\uAC04 \uC81C\uD55C \uC54C\uB9BC"
    },
    max_duration_reached: "\uCD5C\uB300 \uB179\uC74C \uC2DC\uAC04({{hours}}\uC2DC\uAC04)\uC5D0 \uB3C4\uB2EC\uD558\uC5EC \uB179\uC74C\uC774 \uC790\uB3D9 \uC911\uC9C0\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uBBF8\uD305\uC744 \uC885\uB8CC\uD558\uBA74 \uAE30\uC874 \uB179\uC74C\uC774 \uBD84\uC11D\uB429\uB2C8\uB2E4.",
    pending: {
      title: "\uC800\uC7A5\uB418\uC9C0 \uC54A\uC740 \uB179\uC74C \uBC1C\uACAC",
      description: "\uC774\uC804\uC5D0 \uC5C5\uB85C\uB4DC\uB418\uC9C0 \uC54A\uC740 \uB179\uC74C({{duration}})\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uC9C0\uAE08 \uC5C5\uB85C\uB4DC\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description_mobile: "\uC774\uC804\uC5D0 \uC5C5\uB85C\uB4DC\uB418\uC9C0 \uC54A\uC740 \uB179\uC74C\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uB179\uC74C\uC744 \uC5C5\uB85C\uB4DC\uD558\uAC70\uB098 \uC0AD\uC81C\uD574\uC8FC\uC138\uC694.",
      description_corrupted: "\uC774\uC804 \uBBF8\uD305\uC758 \uB179\uC74C\uC774 \uC815\uC0C1\uC801\uC73C\uB85C \uC800\uC7A5\uB418\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4. \uCC98\uC74C\uBD80\uD130 \uB2E4\uC2DC \uC2DC\uC791\uD558\uAC70\uB098, \uB179\uC74C \uC5C6\uC774 \uBBF8\uD305\uC744 \uC885\uB8CC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      description_restore: "\uC5C5\uB85C\uB4DC \uC2DC, \uBBF8\uD305\uC774 \uC885\uB8CC\uB429\uB2C8\uB2E4.",
      description_discard: "\uC0AD\uC81C \uC2DC, \uBBF8\uD305\uC744 \uCC98\uC74C\uBD80\uD130 \uB2E4\uC2DC \uC2DC\uC791\uD569\uB2C8\uB2E4.",
      restore: "\uC5C5\uB85C\uB4DC",
      discard: "\uC0AD\uC81C",
      restart_from_beginning: "\uCC98\uC74C\uBD80\uD130 \uB2E4\uC2DC \uC2DC\uC791",
      end_without_recording: "\uB179\uC74C \uC5C6\uC774 \uC885\uB8CC"
    },
    mic_selector: {
      title: "\uB9C8\uC774\uD06C \uC120\uD0DD",
      placeholder: "\uB9C8\uC774\uD06C\uB97C \uC120\uD0DD\uD558\uC138\uC694",
      level_label: "\uB9C8\uC774\uD06C \uC785\uB825 \uB808\uBCA8",
      silence_warning: "\uC774 \uB9C8\uC774\uD06C\uC5D0\uC11C \uC18C\uB9AC\uAC00 \uAC10\uC9C0\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uB9C8\uC774\uD06C\uC758 \uC0C1\uD0DC\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694.",
      no_device_warning: "\uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uB9C8\uC774\uD06C\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4. \uB9C8\uC774\uD06C\uB97C \uC5F0\uACB0\uD558\uAC70\uB098 \uBE0C\uB77C\uC6B0\uC800\uC758 \uB9C8\uC774\uD06C \uAD8C\uD55C\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694.",
      unknown_device: "\uB9C8\uC774\uD06C {{id}}"
    },
    permission_required_title: "\uB9C8\uC774\uD06C \uAD8C\uD55C \uD544\uC694",
    permission_required_description: "\uC124\uC815\uC5D0\uC11C \uB9C8\uC774\uD06C \uAD8C\uD55C\uC744 \uD5C8\uC6A9\uD574\uC8FC\uC138\uC694.",
    notification_permission_required_title: "\uC54C\uB9BC \uAD8C\uD55C \uD544\uC694",
    notification_permission_required_description: "\uBC31\uADF8\uB77C\uC6B4\uB4DC \uB179\uC74C \uC911 \uC0C1\uD0DC\uB97C \uC54C\uB9AC\uAE30 \uC704\uD574 \uC54C\uB9BC \uAD8C\uD55C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4. \uC124\uC815\uC5D0\uC11C \uC54C\uB9BC\uC744 \uD5C8\uC6A9\uD574\uC8FC\uC138\uC694.",
    go_to_settings: "\uC124\uC815\uC73C\uB85C \uC774\uB3D9",
    start_failed_title: "\uB179\uC74C \uC2DC\uC791 \uC2E4\uD328",
    stop_failed_title: "\uB179\uC74C \uC885\uB8CC \uC2E4\uD328",
    retry_message: "\uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    start_timeout_message: "\uB179\uC74C\uAE30\uAC00 \uC751\uB2F5\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uC571\uC744 \uC644\uC804\uD788 \uC885\uB8CC\uD55C \uB4A4 \uB2E4\uC2DC \uC2E4\uD589\uD574\uC8FC\uC138\uC694.",
    stop_timeout_message: "\uB179\uC74C \uD30C\uC77C \uC800\uC7A5\uC774 \uB05D\uB098\uC9C0 \uC54A\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uAE30\uB2E4\uB9B0 \uB4A4 \uB2E4\uC2DC \uC885\uB8CC\uD574\uC8FC\uC138\uC694. \uACC4\uC18D\uB418\uBA74 \uC571\uC744 \uC644\uC804\uD788 \uC885\uB8CC\uD55C \uB4A4 \uB2E4\uC2DC \uC2E4\uD589\uD574 \uB179\uC74C \uBCF5\uAD6C \uC548\uB0B4 \uC808\uCC28\uB97C \uB530\uB77C\uC8FC\uC138\uC694.",
    uploading_title: "\uB179\uC74C\uC744 \uC800\uC7A5\uD558\uB294 \uC911\uC785\uB2C8\uB2E4.",
    upload_failed_title: "\uB179\uC74C \uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    retry_upload_button: "\uB2E4\uC2DC \uC5C5\uB85C\uB4DC",
    no_network_title: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0 \uC5C6\uC74C",
    no_network_message: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0\uC744 \uD655\uC778\uD55C \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    file_missing_title: "\uB179\uC74C \uD30C\uC77C\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
    file_missing_message: "\uB179\uC74C \uD30C\uC77C\uC774 \uAE30\uAE30\uC5D0\uC11C \uC0AD\uC81C\uB418\uC5B4 \uBD84\uC11D\uC744 \uC9C4\uD589\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uBBF8\uD305\uC744 \uADF8\uB300\uB85C \uC885\uB8CC\uD569\uB2C8\uB2E4."
  },
  right_panel: {
    add_member: "1on1 \uD560 \uBA64\uBC84 \uCD94\uAC00\uD558\uAE30",
    add_pair: {
      error: "\uCD94\uAC00 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      success: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84\uB85C \uCD94\uAC00\uD588\uC2B5\uB2C8\uB2E4.",
      title: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uCD94\uAC00"
    },
    delete_pair_modal: {
      description: "\uD574\uB2F9 \uBA64\uBC84\uC640\uC758 \uC8FC\uAE30\uC801 1on1 \uAD00\uACC4\uB97C \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      error: "\uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      success: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84\uAC00 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      title: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uC0AD\uC81C"
    },
    empty_action_items: "\uC544\uC9C1 \uC561\uC158 \uC544\uC774\uD15C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
    member_list_aria: "1on1 \uBA64\uBC84 \uBAA9\uB85D",
    member_subtitle: "{{count}}\uBC88\uC758 1on1 \u2022 {{days}}\uC77C \uC804",
    more_actions: "\uB354\uBCF4\uAE30",
    no_history: "\uAE30\uB85D \uC5C6\uC74C",
    recurring_title: "\uC8FC\uAE30\uC801\uC778 1on1 \uBA64\uBC84 ({{count}})",
    update_pair: {
      error: "\uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      success: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uC815\uBCF4\uB97C \uC5C5\uB370\uC774\uD2B8\uD588\uC2B5\uB2C8\uB2E4.",
      title: "\uC8FC\uAE30\uC801 1on1 \uBA64\uBC84 \uC5C5\uB370\uC774\uD2B8"
    }
  },
  sidebar: {
    action_items: "\uC561\uC158 \uC544\uC774\uD15C",
    create_one_on_one: "\uC0C8 1on1 \uB9CC\uB4E4\uAE30",
    guide: {
      guide_button: "\uAC00\uC774\uB4DC \uBCF4\uB7EC\uAC00\uAE30",
      service_name: "Orblit 1on1"
    },
    invite_members: "\uBA64\uBC84 \uCD08\uB300\uD558\uAE30",
    member_report: "\uBA64\uBC84 \uB9AC\uD3EC\uD2B8",
    nine_box: "9Box",
    dashboard: "\uB300\uC2DC\uBCF4\uB4DC",
    one_on_one_meeting: "1on1 \uBBF8\uD305",
    settings: "\uC124\uC815",
    update_note: "\uC5C5\uB370\uC774\uD2B8 \uB178\uD2B8",
    guide_menu: {
      trigger: "\uB3C4\uC6C0\uB9D0",
      leader_guide: "\uB9AC\uB354 \uC2DC\uC791 \uAC00\uC774\uB4DC",
      member_guide: "\uBA64\uBC84 \uAC00\uC774\uB4DC",
      service_guide: "\uC11C\uBE44\uC2A4 \uC0C1\uC138 \uAC00\uC774\uB4DC",
      voc: "\uBB38\uC758\uD558\uAE30"
    },
    whats_new: {
      title: "WHAT'S NEW !",
      headline: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uB098\uB97C \uC704\uD55C \uD53C\uB4DC\uBC31\uC774 \uB3C4\uCC29\uD574\uC694",
      message: "\uC774\uC81C \uBA64\uBC84\uC5D0\uAC8C\uB3C4 \uB2E4\uC74C \uBBF8\uD305\uC744 \uC704\uD55C \uD53C\uB4DC\uBC31\uC744 \uB4DC\uB824\uC694",
      go_to_feature: "",
      view_update_note: "\uC5C5\uB370\uC774\uD2B8 \uB178\uD2B8 \uBCF4\uAE30",
      dismiss: "\uB2EB\uAE30"
    }
  },
  voc: {
    title: "\uC624\uBE14\uB9BF\uC5D0 \uBB38\uC758\uD558\uAE30",
    description: "\uC624\uBE14\uB9BF\uC744 \uC774\uC6A9\uD558\uBA70 \uAD81\uAE08\uD55C \uC810\uC774\uB098 \uBD88\uD3B8\uD55C \uC810\uC774 \uC788\uB098\uC694? \uB0B4\uC6A9\uC744 \uB0A8\uACA8\uC8FC\uC2DC\uBA74 \uAC00\uC785\uD558\uC2E0 \uC774\uBA54\uC77C\uB85C \uB2F5\uBCC0\uB4DC\uB9B4\uAC8C\uC694.",
    field: {
      message: "\uBB38\uC758 \uB0B4\uC6A9",
      message_hint: "\uC624\uB958\uAC00 \uC788\uC5C8\uB2E4\uBA74, \uBC1C\uC0DD \uC804\uD6C4 \uC0C1\uD669\uC744 \uAC00\uB2A5\uD55C \uBC94\uC704\uC5D0\uC11C \uC790\uC138\uD788 \uC801\uC5B4\uC8FC\uC2DC\uBA74 \uC6D0\uC778 \uD30C\uC545\uC5D0 \uB3C4\uC6C0\uC774 \uB3FC\uC694."
    },
    placeholder: {
      message: "\uAD81\uAE08\uD55C \uC810\uC774\uB098 \uBD88\uD3B8\uD55C \uC810\uC744 \uC790\uC720\uB86D\uAC8C \uC801\uC5B4 \uC8FC\uC138\uC694."
    },
    contact_notice: "\uB2F5\uBCC0\uC740 \uAC00\uC785\uD558\uC2E0 \uC774\uBA54\uC77C\uB85C \uBCF4\uB0B4\uB4DC\uB824\uC694.",
    action: {
      cancel: "\uCDE8\uC18C",
      submit: "\uBB38\uC758 \uBCF4\uB0B4\uAE30",
      confirm: "\uD655\uC778"
    },
    complete: {
      title: "\uBB38\uC758\uAC00 \uC811\uC218\uB418\uC5C8\uC5B4\uC694",
      description: "\uB0B4\uC6A9\uC744 \uD655\uC778\uD55C \uD6C4 \uAC00\uC785\uD558\uC2E0 \uC774\uBA54\uC77C\uB85C \uB2F5\uBCC0\uB4DC\uB9B4\uAC8C\uC694."
    },
    error: {
      submit_failed: "\uBB38\uC758\uB97C \uBCF4\uB0B4\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694."
    }
  },
  template: {
    add_questions: "\uC9C8\uBB38 \uCD94\uAC00",
    add_questions_button: "\uC120\uD0DD\uD55C \uC9C8\uBB38 \uC0AC\uC6A9\uD558\uAE30 ({{count}}\uAC1C)",
    add_questions_error: "\uC9C8\uBB38 \uCD94\uAC00 \uC2E4\uD328",
    ai_template_input_section: {
      ai_suggestion: "AI \uCD94\uCC9C",
      context: "\uC9C8\uBB38 \uC0DD\uC131\uC5D0 \uCC38\uACE0\uD560 \uB9CC\uD55C \uB0B4\uC6A9 \uB610\uB294 \uC0C1\uD669\uC774 \uC788\uB098\uC694?",
      hide_label: "\uAC04\uB7B5\uD788",
      last_meeting_content: "\uC9C0\uB09C \uC785\uB825 \uB0B4\uC6A9",
      show_label: "\uB354\uBCF4\uAE30",
      context_placeholder: " \u2022 \uBB38\uC81C\uC0C1\uD669(\uD37C\uD3EC\uBA3C\uC2A4, \uD611\uC5C5, \uC774\uC9C1\uC9D5\uD6C4 \uB4F1)\n \u2022 \uD504\uB85C\uC81D\uD2B8 \uC9C4\uD589 \uC0C1\uD669\n \u2022 \uCEE4\uB9AC\uC5B4\uC640 \uC131\uC7A5\uC5D0 \uB300\uD55C \uACE0\uBBFC",
      counterpart: "\uBBF8\uD305 \uC0C1\uB300\uC5D0 \uB300\uD574 \uC54C\uB824\uC8FC\uC138\uC694.",
      counterpart_placeholder: "\uC124\uBA85",
      create: "AI \uD15C\uD50C\uB9BF \uB9CC\uB4E4\uAE30",
      details: "\uC138\uBD80 \uC870\uC815",
      include_multiple_choices: "\uAC1D\uAD00\uC2DD \uC9C8\uBB38 \uD3EC\uD568",
      meeting_prep: "\uD504\uB819",
      meeting_prep_tooltip: "\uD504\uB819 \uB0B4\uC6A9\uC774 \uC9C8\uBB38 \uC0DD\uC131\uC5D0 \uD65C\uC6A9\uB3FC\uC694. \uB354 \uD3EC\uD568\uD558\uACE0 \uC2F6\uC740 \uB0B4\uC6A9\uC774 \uC788\uB2E4\uBA74 \uC544\uB798\uC5D0 \uC801\uC5B4\uC8FC\uC138\uC694.",
      previous_meeting: "\uC9C0\uB09C 1on1 \uAE30\uB85D",
      question_count: {
        Advanced: "\uC2EC\uD654",
        Simple: "\uB2E8\uC21C",
        Standard: "\uD45C\uC900",
        title: "\uC0DD\uC131 \uC9C8\uBB38 \uAC1C\uC218"
      },
      question_style: "\uC9C8\uBB38\uC758 \uC2A4\uD0C0\uC77C\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
      question_types: {
        "Action/Implementation-focused": {
          description: "\uC55E\uC73C\uB85C 3\uAC1C\uC6D4\uAC04 \uC5B4\uB5A4 \uAD6C\uCCB4\uC801\uC778 \uD589\uB3D9\uC744 \uCDE8\uD558\uC2E4 \uAC74\uAC00\uC694?",
          title: "\uD589\uB3D9/\uC2E4\uD589\uD615"
        },
        "Relationship/Collaboration": {
          description: "\uB3D9\uB8CC\uB4E4\uACFC\uC758 \uD611\uC5C5\uC5D0\uC11C \uAC00\uC7A5 \uB3C4\uC6C0\uC774 \uB418\uB294 \uBD80\uBD84\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
          title: "\uAD00\uACC4/\uD611\uC5C5\uD615"
        },
        "Growth/Goal-oriented": {
          description: "1\uB144 \uD6C4\uC5D0 \uC5B4\uB5A4 \uD300\uC7A5\uC774 \uB418\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
          title: "\uC131\uC7A5/\uBAA9\uD45C \uC9C0\uD5A5"
        },
        "Multiple-choice": {
          description: "\uD604\uC7AC \uC5C5\uBB34 \uB9CC\uC871\uB3C4\uB97C 1-5\uB85C \uD3C9\uAC00\uD574\uC8FC\uC138\uC694",
          title: "\uAC1D\uAD00\uC2DD"
        },
        "Reflection/Thought-provoking": {
          description: "\uD604\uC7AC \uC5ED\uD560\uC5D0\uC11C \uAC00\uC7A5 \uC758\uBBF8 \uC788\uB2E4\uACE0 \uB290\uB07C\uB294 \uBD80\uBD84\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
          title: "\uC131\uCC30/\uC0AC\uACE0\uD615"
        },
        "Experience/Story-based": {
          description: "\uCD5C\uADFC \uAC00\uC7A5 \uAE30\uC5B5\uC5D0 \uB0A8\uB294 \uD504\uB85C\uC81D\uD2B8 \uACBD\uD5D8\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
          title: "\uACBD\uD5D8/\uC774\uC57C\uAE30\uD615"
        },
        title: "\uC9C8\uBB38 \uC720\uD615"
      },
      subject: "\uB300\uD654 \uB098\uB204\uACE0 \uC2F6\uC740 \uC8FC\uC81C\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
      subject_default_value: "\uC9C1\uC804 \uBBF8\uD305\uC758 \uC9C8\uBB38 \uC0DD\uC131\uC5D0 \uC0AC\uC6A9\uD55C \uC8FC\uC81C\uC785\uB2C8\uB2E4.",
      subject_options: {
        relationship: "\uAD00\uACC4 \uBC0F \uADFC\uD669",
        work_status: "\uC5C5\uBB34 \uD604\uD669 \uBC0F \uC7A5\uC560\uBB3C",
        growth_career: "\uC131\uC7A5\uACFC \uCEE4\uB9AC\uC5B4",
        satisfaction_motivation: "\uC9C1\uBB34 \uB9CC\uC871\uACFC \uB3D9\uAE30 \uBD80\uC5EC",
        feedback_coaching: "\uD53C\uB4DC\uBC31\uACFC \uCF54\uCE6D",
        action_review: "\uC9C0\uB09C \uC57D\uC18D \uD655\uC778"
      },
      subject_placeholder: "\uC8FC\uC81C\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
      title: "AI \uD15C\uD50C\uB9BF \uB9CC\uB4E4\uAE30",
      tone: {
        Casual: "\uCE90\uC8FC\uC5BC\uD55C",
        Formal: "\uC815\uC911\uD55C",
        title: "\uD1A4 \uC564 \uB9E4\uB108"
      },
      recommended: "\uCD94\uCC9C",
      use: "\uD65C\uC6A9\uD558\uAE30"
    },
    generating_loader: {
      checking_session: "\uC774\uC804 \uBBF8\uD305 \uAE30\uB85D\uC744 \uD655\uC778\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      analyzing_prep: "\uC0C1\uB300\uBC29\uC774 \uC791\uC131\uD55C \uD504\uB819\uC744 \uBD84\uC11D\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      checking_profile: "\uC0C1\uB300\uBC29\uC758 \uC18C\uD1B5 \uC2A4\uD0C0\uC77C\uACFC \uC120\uD638\uB3C4\uB97C \uCC38\uACE0\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      composing_topics: "\uC120\uD0DD\uD558\uC2E0 \uC8FC\uC81C\uC5D0 \uB9DE\uB294 \uC9C8\uBB38\uC744 \uAD6C\uC0C1\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      applying_prompt: "\uC694\uCCAD\uD558\uC2E0 \uB0B4\uC6A9\uC744 \uBC18\uC601\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      refining_tone: "\uB300\uD654 \uD1A4\uC5D0 \uB9DE\uAC8C \uC9C8\uBB38\uC744 \uB2E4\uB4EC\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      finalizing: "\uCD5C\uC885 \uC9C8\uBB38\uC744 \uC815\uB9AC\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4..."
    },
    ai_template_output_section: {
      create_template_button: "\uC120\uD0DD\uD55C \uC9C8\uBB38 \uC0AC\uC6A9\uD558\uAE30 ({{count}}\uAC1C)",
      done: "\uC0DD\uC131 \uC644\uB8CC!",
      generate_questions_prompt: {
        description: "AI\uAC00 \uBBF8\uD305\uC744 \uC704\uD55C \uB9DE\uCDA4 \uC9C8\uBB38\uACFC \uAC00\uC774\uB4DC\uB97C \uB9CC\uB4E4\uC5B4 \uB4DC\uB9BD\uB2C8\uB2E4.",
        title: "\uC88C\uCE21 \uD3FC\uC744 \uCC44\uC6B0\uACE0 \uC9C8\uBB38\uC744 \uC0DD\uC131\uD574\uC8FC\uC138\uC694"
      },
      guide: {
        description: "AI\uAC00 \uC120\uD0DD\uB41C \uC9C8\uBB38\uC744 \uBD84\uC11D\uD558\uC5EC\n\uAC1C\uC778\uD654\uB41C 1on1 \uBBF8\uD305 \uAC00\uC774\uB4DC\uB97C \uC791\uC131 \uC911\uC785\uB2C8\uB2E4",
        generate_guide: "\uC9C8\uBB38 \uC0AC\uC6A9 \uAC00\uC774\uB4DC \uC0DD\uC131",
        loading: "\uAC00\uC774\uB4DC\uB97C \uC0DD\uC131\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
        title: "AI \uAC00\uC774\uB4DC"
      },
      no_questions: "\uC0DD\uC131\uB41C \uC9C8\uBB38\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
      title: "AI \uD15C\uD50C\uB9BF"
    },
    copied_to_clipboard: "\uD074\uB9BD\uBCF4\uB4DC\uC5D0 \uBCF5\uC0AC \uC644\uB8CC",
    copied_to_clipboard_message: "\uAC00\uC774\uB4DC\uAC00 \uBCF5\uC0AC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    copy: "\uBCF5\uC0AC",
    create: "\uC0C8 \uD15C\uD50C\uB9BF",
    create_page: {
      add_question: "\uC9C8\uBB38 \uCD94\uAC00\uD558\uAE30",
      delete_question: "\uC9C8\uBB38 \uC0AD\uC81C",
      delete_question_description: "\uC9C8\uBB38\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      description_placeholder: "\uD15C\uD50C\uB9BF\uC758 \uC124\uBA85\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
      owner: "\uC18C\uC720",
      owner_user: "\uB0B4 \uD15C\uD50C\uB9BF",
      owner_workspace: "\uD300 \uD15C\uD50C\uB9BF",
      question_placeholder: "\uC9C8\uBB38\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
      save_button: "\uD15C\uD50C\uB9BF \uC800\uC7A5",
      title: "\uC0C8 \uD15C\uD50C\uB9BF \uB9CC\uB4E4\uAE30",
      title_placeholder: "\uD15C\uD50C\uB9BF\uC758 \uC81C\uBAA9\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694"
    },
    delete_modal: {
      description: "\uD15C\uD50C\uB9BF\uC744 \uC0AD\uC81C\uD558\uBA74 \uBCF5\uAD6C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uC815\uB9D0 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
      error: "\uD15C\uD50C\uB9BF \uC0AD\uC81C \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      success: "\uD15C\uD50C\uB9BF\uC774 \uC0AD\uC81C\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      title: "\uD15C\uD50C\uB9BF \uC0AD\uC81C"
    },
    edit_page: {
      save_button: "\uC218\uC815 \uC644\uB8CC",
      title: "\uD15C\uD50C\uB9BF \uC218\uC815\uD558\uAE30"
    },
    owner_user: "\uB0B4 \uD15C\uD50C\uB9BF",
    owner_workspace: "\uD300 \uD15C\uD50C\uB9BF",
    page_title: "\uD15C\uD50C\uB9BF \uBD88\uB7EC\uC624\uAE30",
    placeholder: "\uD15C\uD50C\uB9BF\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    question_list: "\uC9C8\uBB38 \uB9AC\uC2A4\uD2B8",
    refresh_message: "\uD398\uC774\uC9C0\uB97C \uC0C8\uB85C \uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    register_date: "\uC0DD\uC131\uC77C",
    success: "\uC9C8\uBB38\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    system: "Orblit"
  },
  time: {
    label: "\uBBF8\uD305 \uC2DC\uAC04\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694."
  },
  onboarding_tour: {
    next: "\uB2E4\uC74C",
    resume_banner: {
      body: "\uC9C4\uD589 \uC911\uC778 \uD29C\uD1A0\uB9AC\uC5BC\uC774 \uC788\uC5B4\uC694.",
      action: "\uC774\uC5B4\uD558\uAE30"
    },
    stages: {
      create: "\uBBF8\uD305 \uC0DD\uC131",
      questions: "\uC9C8\uBB38 \uC900\uBE44",
      record: "\uB179\uC74C",
      analysis: "\uBD84\uC11D \uD655\uC778"
    },
    create_meeting: {
      title: "\uC9C1\uC811 \uB9CC\uB4E4\uC5B4\uBCF4\uBA74\uC11C \uC775\uD600\uBCFC\uAE4C\uC694?",
      body: "\uC9C0\uAE08\uC740 \uD63C\uC790 \uC368\uBCF4\uB294 \uAC70\uB77C \uD3B8\uD558\uAC8C \uB458\uB7EC\uBD10\uB3C4 \uB3FC\uC694.\n\uAC00\uBCCD\uAC8C '\uCCAB 1on1 \uB9CC\uB4E4\uAE30'\uB97C \uB20C\uB7EC\uBCF4\uC138\uC694."
    },
    self_meeting: {
      title: "\uBBF8\uD305 \uC0C1\uB300\uB97C \uC120\uD0DD\uD558\uC9C0 \uC54A\uC544\uB3C4 \uB3FC\uC694",
      body: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uD300\uC6D0\uC744 \uCD08\uB300\uD574 1on1 \uC9C8\uBB38\uACFC \uB2F5\uBCC0\uC744 \uACF5\uC720\uD558\uB294 \uAC83\uC744 \uAD8C\uC7A5\uD558\uC9C0\uB9CC,\n\uCD08\uB300 \uC804\uC5D0 \uD63C\uC790\uC11C\uB3C4 \uC368\uBCFC \uC218 \uC788\uC5B4\uC694."
    },
    self_meeting_has_members: {
      title: "\uC9C0\uAE08\uC740 \uC0C1\uB300\uB97C \uC9C0\uC815\uD558\uC9C0 \uC54A\uACE0 \uD574\uBCFC\uAC8C\uC694",
      body: "\uC6D0\uB798\uB294 \uC704 \uD300\uC6D0 \uC911\uC5D0\uC11C \uC0C1\uB300\uB97C \uACE8\uB77C 1on1\uC744 \uC9C4\uD589\uD574\uC694.\n\uC9C0\uAE08\uC740 \uD29C\uD1A0\uB9AC\uC5BC\uC774\uB2C8, '\uC0C1\uB300 \uC9C0\uC815 \uC5C6\uC774 \uB9CC\uB4E4\uAE30'\uB85C \uAC00\uBCCD\uAC8C \uCCB4\uD5D8\uD574\uBD10\uC694."
    },
    credit_info: {
      title: "{{remaining}}\uD68C\uC758 \uBBF8\uD305\uC744 \uBD84\uC11D\uD560 \uC218 \uC788\uC5B4\uC694",
      body: "\uC720\uB8CC \uD50C\uB79C\uC5D0\uC11C\uB294 \uBBF8\uD305 \uB0B4\uC6A9\uC744 \uBD84\uC11D\uD574 \uD53C\uB4DC\uBC31\uC744 \uB4DC\uB824\uC694. \uAC00\uC785 \uC120\uBB3C\uB85C \uBD84\uC11D 3\uD68C\uB97C \uB4DC\uB838\uC5B4\uC694.\n\uD300\uC6D0\uC744 \uCD08\uB300\uD558\uAC70\uB098 \uB2E4\uB978 \uB9AC\uB354\uC5D0\uAC8C \uCD94\uCC9C\uD558\uBA74 \uB354 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694."
    },
    create_submit: {
      title: "\uC774\uC81C \uBBF8\uD305\uC744 \uB9CC\uB4E4\uC5B4\uBCFC\uAE4C\uC694?",
      body: "\uC9C0\uAE08\uC740 \uD568\uAED8\uD560 \uD300\uC6D0\uC744 \uC9C0\uC815\uD558\uC9C0 \uC54A\uC558\uC73C\uB2C8 \uBC14\uB85C \uB9CC\uB4E4\uC5B4\uB3C4 \uB3FC\uC694.\n\uC2E4\uC81C \uD300\uC6D0\uACFC \uD560 \uB550 \uB0A0\uC9DC\xB7\uC2DC\uAC04\uC744 \uC815\uD574\uC11C \uB9CC\uB4E4\uBA74 \uB429\uB2C8\uB2E4."
    },
    add_question: {
      title: "\uC5EC\uAE30\uC11C 1on1\uC744 \uC900\uBE44\uD558\uACE0 \uC9C4\uD589\uD574\uC694",
      body: "\uD300\uC6D0\uC5D0\uAC8C \uBB3C\uC5B4\uBCFC \uC9C8\uBB38\uC744 \uC9C1\uC811 \uC785\uB825\uD560 \uC218 \uC788\uC5B4\uC694.\n\uC9C8\uBB38\uC744 \uCD94\uAC00\uD558\uBA74 \uD300\uC6D0\uC774 \uBBF8\uD305 \uC804\uC5D0 \uB2F5\uBCC0\uC744 \uC791\uC131\uD560 \uC218 \uC788\uC5B4\uC694."
    },
    load_template: {
      title: "\uCCAB 1on1\uC774\uB77C\uBA74, \uCD94\uCC9C \uC9C8\uBB38\uC73C\uB85C \uC2DC\uC791\uD574\uBCF4\uC138\uC694",
      body: "\uC9C8\uBB38\uC744 \uC9C1\uC811 \uC4F8 \uC218\uB3C4 \uC788\uC9C0\uB9CC,\n\uC900\uBE44\uB41C \uCD94\uCC9C \uC9C8\uBB38\uC744 \uBD88\uB7EC\uC624\uBA74 \uB354 \uC26C\uC6CC\uC694."
    },
    select_template: {
      title: "\uCCAB 1on1\uC5D4 \uC774 \uD15C\uD50C\uB9BF\uC744 \uCD94\uCC9C\uD574\uC694",
      body: "\uCE74\uB4DC\uB97C \uB20C\uB7EC \uC9C8\uBB38\uC744 \uC9C1\uC811 \uC0B4\uD3B4\uBCF4\uC138\uC694."
    },
    use_template_questions: {
      title: "\uC774 \uC9C8\uBB38\uB4E4\uC744 \uBBF8\uD305\uC5D0 \uB123\uC5B4\uBCFC\uAE4C\uC694?",
      body: "\u2018\uC120\uD0DD\uD55C \uC9C8\uBB38 \uC0AC\uC6A9\uD558\uAE30\u2019\uB97C \uB204\uB974\uBA74\n\uC120\uD0DD\uD55C \uC9C8\uBB38\uC774 \uBBF8\uD305\uC5D0 \uCD94\uAC00\uB3FC\uC694."
    },
    start_recording: {
      title: "\uB179\uC74C\uB9CC \uD558\uBA74, \uB098\uBA38\uC9C0\uB294 \uC624\uBE14\uB9BF\uC774 \uD574\uC694",
      body: "\uC2E4\uC81C \uBBF8\uD305\uC5D0\uC120 '\uB179\uC74C \uC2DC\uC791\uD558\uAE30'\uB85C \uB179\uC74C\uD558\uAC70\uB098\n\uB179\uC74C \uD30C\uC77C\uC744 \uC62C\uB9AC\uBA74 \uC624\uBE14\uB9BF\uC774 \uC54C\uC544\uC11C \uC815\uB9AC\uD558\uACE0 \uBD84\uC11D\uD574\uB4DC\uB824\uC694.\n\n\uC9C0\uAE08\uC740 \uD29C\uD1A0\uB9AC\uC5BC\uC774\uB2C8, \uC0D8\uD50C \uB179\uC74C\uC73C\uB85C \uBD84\uC11D\uC774 \uC5B4\uB5BB\uAC8C \uB418\uB294\uC9C0 \uCCB4\uD5D8\uD574\uBCFC\uAE4C\uC694?\n(\uC0D8\uD50C \uCCB4\uD5D8\uC740 \uBD84\uC11D \uD69F\uC218\uAC00 \uCC28\uAC10\uB418\uC9C0 \uC54A\uC544\uC694.)",
      analyze_sample: "\uC0D8\uD50C\uB85C \uBD84\uC11D \uCCB4\uD5D8\uD558\uAE30",
      analyze_sample_failed: "\uC0D8\uD50C \uBD84\uC11D\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \u2018\uC0D8\uD50C\uB85C \uBD84\uC11D \uCCB4\uD5D8\uD558\uAE30\u2019\uB97C \uB2E4\uC2DC \uB20C\uB7EC\uC8FC\uC138\uC694."
    },
    sample_analyzing_screen: {
      title: "\uC0D8\uD50C \uBD84\uC11D\uC744 \uC900\uBE44\uD558\uACE0 \uC788\uC5B4\uC694",
      body: "\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB9AC\uC2DC\uBA74 \uC608\uC2DC \uBD84\uC11D \uACB0\uACFC\uB97C \uBCF4\uC5EC\uB4DC\uB9B4\uAC8C\uC694."
    },
    view_summary: {
      title: "\uC774\uBC88 \uBBF8\uD305\uC758 \uC694\uC57D\uC744 \uD655\uC778\uD574\uBCFC\uAE4C\uC694?",
      body: "'\uC694\uC57D' \uD0ED\uC744 \uB20C\uB7EC\uBCF4\uC138\uC694.\n\uB300\uD654\uC758 \uD575\uC2EC \uB0B4\uC6A9\uACFC \uACB0\uC815 \uC0AC\uD56D\uC744 \uD55C\uB208\uC5D0 \uC815\uB9AC\uD574\uB4DC\uB824\uC694."
    },
    view_analysis: {
      title: "\uBBF8\uD305 \uBD84\uC11D\uB3C4 \uD655\uC778\uD574\uBCF4\uC138\uC694",
      body: "'\uBD84\uC11D' \uD0ED\uC744 \uB20C\uB7EC\uBCF4\uC138\uC694.\n\uBBF8\uD305\uC5D0\uC11C \uD300\uC6D0\uC758 \uBC1C\uD654\uB97C \uC5BC\uB9C8\uB098 \uC774\uB04C\uC5B4\uB0C8\uB294\uC9C0,\n\uB9AC\uB354\uC758 \uAC15\uC810\uACFC \uBCF4\uC644\uC810\uC740 \uBB34\uC5C7\uC778\uC9C0 \uBD84\uC11D\uD574\uB4DC\uB824\uC694."
    },
    celebration: {
      title: "\uD29C\uD1A0\uB9AC\uC5BC\uC744 \uBAA8\uB450 \uB9C8\uCCE4\uC5B4\uC694!",
      body: "\uC2E4\uC81C \uD300\uC6D0\uACFC\uC758 1on1\uC744 \uC624\uBE14\uB9BF\uC73C\uB85C \uC900\uBE44\uD574 \uBCF4\uC138\uC694.\n\uD300\uC6D0\uC744 \uCD08\uB300\uD558\uBA74 \uC9C8\uBB38\uACFC \uB2F5\uBCC0\uC744 \uD568\uAED8 \uB098\uB20C \uC218 \uC788\uC5B4\uC694.",
      confirm: "\uD655\uC778"
    }
  },
  onboarding_intro: {
    title: "\uC88B\uC740 \uB9AC\uB354\uB294 '\uAD00\uB9AC'\uAC00 \uC544\uB2C8\uB77C '\uB300\uD654'\uB85C \uD300\uC744 \uC6C0\uC9C1\uC785\uB2C8\uB2E4",
    body_value: "\uD68C\uC758\uB098 \uC9C0\uC2DC\uBCF4\uB2E4, \uD300\uC6D0 \uD55C \uBA85\uACFC \uB098\uB204\uB294 1on1 \uB300\uD654 \uD55C \uBC88\uC774\n\uD300\uC758 \uC131\uACFC\uC640 \uC2E0\uB8B0\uB97C \uB9CC\uB4E4\uC5B4\uC694.",
    body_promise: "\uC900\uBE44\uBD80\uD130 \uBD84\uC11D\uAE4C\uC9C0\uB294 \uC624\uBE14\uB9BF\uC774 \uB3C4\uC640\uB4DC\uB9B4\uAC8C\uC694.\n{{name}}\uB2D8\uC740 \uB300\uD654\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694.",
    page2_title: "\uB531 2\uBD84, \uAC00\uBCCD\uAC8C \uD55C \uBC14\uD034 \uB458\uB7EC\uBCFC\uAE4C\uC694?",
    page2_subtitle: "\uC9C0\uAE08 \uBC14\uB85C \uC624\uBE14\uB9BF\uC744 \uCCB4\uD5D8\uD574 \uBCFC \uC218 \uC788\uC5B4\uC694.\n\uD29C\uD1A0\uB9AC\uC5BC\uB85C \uC0AC\uC6A9\uBC95\uC744 \uAC00\uBCCD\uAC8C \uC775\uD600\uBD10\uC694.",
    steps: {
      create: "1on1 \uBBF8\uD305\uC744 \uB9CC\uB4E4\uC5B4\uC694",
      questions: "\uB300\uD654 \uB098\uB20C \uC9C8\uBB38\uC744 \uACE8\uB77C\uC694",
      record: "\uC0D8\uD50C \uB179\uC74C \uD30C\uC77C\uB85C \uBD84\uC11D\uC744 \uCCB4\uD5D8\uD574\uC694",
      analysis: "\uBD84\uC11D\uC774 \uC5B4\uB5BB\uAC8C \uB418\uB294\uC9C0 \uD655\uC778\uD574\uC694"
    },
    next: "\uB2E4\uC74C",
    guide_link: "\uD639\uC2DC 1on1\uC5D0 \uB300\uD574 \uC880 \uB354 \uC54C\uC544\uBCF4\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
    skip: "\uAC74\uB108\uB6F0\uAE30",
    skip_tooltip: "\uC7A0\uC2DC\uB9CC\uC694! \uC815\uB9D0 2\uBD84\uBC16\uC5D0 \uC548 \uAC78\uB824\uC694.\n2\uBD84\uB9CC \uD22C\uC790\uD574\uC11C \uC624\uBE14\uB9BF\uC744 \uBC14\uB85C \uC774\uD574\uD574 \uBCF4\uC138\uC694",
    cta: "\uC2DC\uC791\uD574\uBCF4\uAE30"
  }
};

// src/locales/ko-KR/ninebox.json
var ninebox_default2 = {
  grid: {
    performance_potential: {
      title: "\uC131\uACFC \uBC0F \uC7A0\uC7AC\uB825",
      x_label: "\uC131\uACFC",
      y_label: "\uC7A0\uC7AC\uB825",
      star: "\uD575\uC2EC \uC778\uC7AC",
      star_desc: "\uC131\uACFC\uC640 \uC7A0\uC7AC\uB825\uC774 \uBAA8\uB450 \uCD5C\uC0C1\uC778 \uC870\uC9C1\uC758 \uD575\uC2EC \uC778\uC7AC",
      developing_top: "\uC6B0\uC218 \uC7A0\uC7AC \uC778\uC7AC",
      developing_top_desc: "\uB192\uC740 \uC131\uACFC\uB97C \uBCF4\uC774\uBA70 \uCC28\uC138\uB300 \uB9AC\uB354\uB85C \uC131\uC7A5 \uAC00\uB2A5\uC131\uC774 \uD07C",
      potential_top: "\uBBF8\uB798 \uC131\uC7A5 \uC778\uC7AC",
      potential_top_desc: "\uD604\uC7AC \uC131\uACFC\uB294 \uB0AE\uC73C\uB098 \uD559\uC2B5 \uB2A5\uB825\uACFC \uC131\uC7A5 \uC7A0\uC7AC\uB825\uC774 \uB9E4\uC6B0 \uB192\uC74C",
      strong: "\uACE0\uC131\uACFC \uC778\uC7AC",
      strong_desc: "\uD604\uC7AC \uB9E1\uC740 \uC9C1\uBB34\uC5D0\uC11C \uD0C1\uC6D4\uD55C \uC131\uACFC\uB97C \uB0B4\uB294 \uC548\uC815\uC801\uC778 \uC778\uB825",
      developing: "\uC911\uC2EC \uC778\uC7AC",
      developing_desc: "\uC870\uC9C1\uC758 \uD5C8\uB9AC \uC5ED\uD560\uB85C, \uAFB8\uC900\uD55C \uC131\uACFC\uC640 \uC131\uC7A5\uC774 \uAE30\uB300\uB428",
      potential: "\uC721\uC131 \uB300\uC0C1",
      potential_desc: "\uAE30\uBCF8 \uC7A0\uC7AC\uB825\uC740 \uC788\uC73C\uB098 \uC131\uACFC\uB97C \uB0B4\uAE30 \uC704\uD55C \uCD94\uAC00 \uAD50\uC721/\uBC30\uCE58 \uACE0\uB824 \uD544\uC694",
      dependable: "\uC548\uC815\uC801 \uAE30\uC5EC\uC790",
      dependable_desc: "\uC131\uACFC\uB294 \uC88B\uC73C\uB098 \uD604 \uC9C1\uBB34\uC5D0 \uD2B9\uD654\uB418\uC5B4 \uC218\uC9C1\uC801 \uC2B9\uC9C4\uBCF4\uB2E4 \uC804\uBB38\uAC00 \uACBD\uB85C \uC801\uD569",
      inconsistent: "\uC131\uACFC \uAD00\uB9AC \uB300\uC0C1",
      inconsistent_desc: "\uC131\uACFC\uAC00 \uBD88\uC548\uC815\uD558\uBA70, \uC5ED\uB7C9 \uAC1C\uBC1C \uB610\uB294 \uB3D9\uAE30 \uBD80\uC5EC\uAC00 \uD544\uC694\uD55C \uB2E8\uACC4",
      unsatisfactory: "\uC7AC\uBC30\uCE58 \uAC80\uD1A0 \uB300\uC0C1",
      unsatisfactory_desc: "\uC131\uACFC\uC640 \uC7A0\uC7AC\uB825 \uBAA8\uB450 \uB0AE\uC544 \uC9C1\uBB34 \uC7AC\uBC30\uCE58\uB098 \uC800\uC131\uACFC\uC790 \uAD00\uB9AC\uAC00 \uD544\uC694"
    },
    flight_risk: {
      title: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4 \uB9E4\uD2B8\uB9AD\uC2A4",
      x_label: "\uD1F4\uC0AC \uC601\uD5A5\uB3C4",
      y_label: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4",
      critical: "\uCD5C\uC6B0\uC120 \uAD00\uB9AC",
      critical_desc: "\uD1F4\uC0AC \uC601\uD5A5\uB3C4\uC640 \uC704\uD5D8\uB3C4\uAC00 \uBAA8\uB450 \uCD5C\uC0C1. \uC989\uAC01\uC801\uC778 \uB9AC\uD150\uC158(Retention) \uC804\uB7B5 \uD544\uC694",
      act_fast: "\uC989\uAC01 \uB300\uC751",
      act_fast_desc: "\uC774\uD0C8 \uC9D5\uD6C4\uAC00 \uB69C\uB837\uD568. \uC601\uD5A5\uB3C4\uAC00 \uBCF4\uD1B5\uC774\uB354\uB77C\uB3C4 \uBE60\uB978 \uBA74\uB2F4\uACFC \uCC98\uC6B0 \uAC1C\uC120 \uD544\uC694",
      watch_closely: "\uBC00\uCC29 \uAD00\uCC30",
      watch_closely_desc: "\uC601\uD5A5\uB3C4\uB294 \uB0AE\uC73C\uB098 \uD1F4\uC0AC \uAC00\uB2A5\uC131\uC774 \uB192\uC73C\uBBC0\uB85C \uC6D0\uC778 \uD30C\uC545 \uBC0F \uC8FC\uC758 \uD544\uC694",
      alert: "\uC8FC\uC758",
      alert_desc: "\uC601\uD5A5\uB825\uC774 \uD070 \uC778\uC7AC\uC758 \uC774\uD0C8 \uC9D5\uD6C4 \uAC10\uC9C0. \uC9D1\uC911\uC801\uC778 \uAD00\uACC4 \uAD00\uB9AC\uAC00 \uD544\uC694\uD55C \uB2E8\uACC4",
      manage: "\uC0C1\uC2DC \uAD00\uB9AC",
      manage_desc: "\uC77C\uBC18\uC801\uC778 \uC218\uC900\uC758 \uB9AC\uC2A4\uD06C \uAD00\uB9AC \uBC0F \uC8FC\uAE30\uC801\uC778 \uB9CC\uC871\uB3C4 \uCCB4\uD06C",
      monitor: "\uAD00\uCC30",
      monitor_desc: "\uB2F9\uC7A5 \uC2DC\uAE09\uD558\uC9C4 \uC54A\uC73C\uB098 \uC7A0\uC7AC\uC801 \uC774\uD0C8 \uC694\uC778\uC774 \uC788\uB294\uC9C0 \uBAA8\uB2C8\uD130\uB9C1",
      key_talent: "\uD575\uC2EC \uBCF4\uD638",
      key_talent_desc: "\uC601\uD5A5\uB3C4\uB294 \uB9E4\uC6B0 \uB192\uC73C\uB098 \uC774\uD0C8 \uC704\uD5D8\uC740 \uB0AE\uC74C. \uC9C0\uC18D\uC801\uC778 \uBCF4\uC0C1\uACFC \uBE44\uC804 \uC81C\uC2DC\uB85C \uC548\uC815 \uC720\uC9C0",
      stable: "\uC548\uC815",
      stable_desc: "\uC870\uC9C1\uC5D0 \uC548\uC815\uC801\uC73C\uB85C \uC548\uCC29\uD574 \uC788\uC73C\uBA70 \uB9AC\uC2A4\uD06C\uAC00 \uB0AE\uC740 \uC0C1\uD0DC",
      low_risk: "\uC800\uC704\uD5D8",
      low_risk_desc: "\uC774\uD0C8 \uAC00\uB2A5\uC131\uACFC \uC601\uD5A5\uB3C4\uAC00 \uBAA8\uB450 \uB0AE\uC544 \uAD00\uB9AC \uB9AC\uC18C\uC2A4 \uD22C\uC785 \uC6B0\uC120\uC21C\uC704\uAC00 \uB0AE\uC74C"
    }
  },
  right_section: {
    direct_members: "\uC9C1\uC18D \uD558\uC704 \uBA64\uBC84",
    indirect_members: "\uAC04\uC811 \uD558\uC704 \uBA64\uBC84"
  },
  member_detail: {
    member_info: "\uBA64\uBC84\uC815\uBCF4",
    leader: "\uB9E4\uB2C8\uC800",
    work_engagement: "\uC5C5\uBB34\uBAB0\uC785\uB3C4",
    growth_satisfaction: "\uC131\uC7A5 \uB9CC\uC871\uB3C4",
    history: "\uD788\uC2A4\uD1A0\uB9AC",
    loading: "\uB85C\uB529 \uC911...",
    no_history: "\uD788\uC2A4\uD1A0\uB9AC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4"
  },
  filter: {
    all_members: "\uC804\uCCB4 \uBA64\uBC84 \uBCF4\uAE30",
    direct_members_only: "\uC9C1\uC18D \uD558\uC704 \uBA64\uBC84\uB9CC \uBCF4\uAE30"
  },
  last_update: "\uB098\uC758 \uCD5C\uADFC \uC5C5\uB370\uC774\uD2B8"
};

// src/locales/ko-KR/template.json
var template_default2 = {
  oneonone: {
    career_development: {
      title: "\uCEE4\uB9AC\uC5B4 \uAC1C\uBC1C \uD15C\uD50C\uB9BF",
      questions: [
        "\uC9C0\uB09C \uBBF8\uD305 \uC774\uD6C4 \uC5C5\uBB34 \uC911 \uAC00\uC7A5 \uC88B\uC558\uB358 \uC810\uACFC \uAC00\uC7A5 \uC544\uC26C\uC6E0\uB358 \uC810\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC5B4\uB5A4 \uD504\uB85C\uC81D\uD2B8\uC5D0 \uCC38\uC5EC\uD558\uAC70\uB098 \uB354 \uAE4A\uC774 \uAD00\uC5EC\uD558\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uC2DC\uAC04\uC774\uB098 \uC790\uC6D0\uC774 \uBD80\uC871\uD574\uC11C \uC544\uC9C1 \uC2DC\uB3C4\uD574\uBCF4\uC9C0 \uBABB\uD588\uC9C0\uB9CC \uAF2D \uD574\uBCF4\uACE0 \uC2F6\uC740 \uAC83\uC774 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uD5A5\uD6C4 6~12\uAC1C\uC6D4 \uC548\uC5D0 \uB2EC\uC131\uD558\uACE0 \uC2F6\uC740 \uCEE4\uB9AC\uC5B4\uC801\uC778 \uBAA9\uD45C\uB294 \uBB34\uC5C7\uC774\uBA70, \uADF8\uB807\uAC8C \uC0DD\uAC01\uD558\uB294 \uC774\uC720\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uD5A5\uD6C4 5\uB144\uC758 \uBAA9\uD45C\uB098 \uACC4\uD68D\uC774 \uC788\uB2E4\uBA74 \uACF5\uC720\uD574\uC8FC\uC2E4 \uC218 \uC788\uC744\uAE4C\uC694?",
        "\uB2F9\uC2E0\uC758 \uACBD\uB825 \uBC1C\uC804\uC744 \uC704\uD574 \uC81C\uAC00 \uB3C4\uC6B8 \uC218 \uC788\uB294 \uC77C\uC774 \uC788\uC744\uAE4C\uC694?",
        "\uC694\uC998 \uC2A4\uC2A4\uB85C\uB97C \uC704\uD574\uC11C \uBB34\uC5C7\uC744 \uD558\uACE0 \uC788\uB098\uC694?",
        "\uB2E4\uC74C \uB2EC\uC5D0 \uC5C5\uBB34 \uC678\uC801\uC73C\uB85C \uB354 \uD558\uACE0 \uC2F6\uC740 \uD55C \uAC00\uC9C0\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?"
      ]
    },
    growth_planning: {
      title: "\uC131\uC7A5 \uACC4\uD68D \uB17C\uC758 \uD15C\uD50C\uB9BF",
      questions: [
        "\uD604\uC7AC \uC5ED\uD560\uC5D0\uC11C \uAE0D\uC815\uC801\uC73C\uB85C \uB290\uB07C\uB294 \uC810\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uD604\uC7AC \uC5ED\uD560\uC5D0\uC11C \uAC00\uC7A5 \uC5B4\uB824\uC6B4 \uC810\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC9C0\uAE08 \uB9E1\uC740 \uC77C\uC5D0\uC11C \uC0C8\uB86D\uAC8C \uBC1C\uACAC\uD55C \uAE30\uD68C\uB098 \uAC00\uB2A5\uC131\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC5C5\uBB34\uC5D0\uC11C \uB2EC\uC131\uD558\uACE0 \uC2F6\uC740 \uBE44\uC988\uB2C8\uC2A4 \uBAA9\uD45C\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC5C5\uBB34 \uC678 \uAC1C\uC778\uC801\uC73C\uB85C \uC774\uB8E8\uACE0 \uC2F6\uC740 \uBAA9\uD45C\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC694\uC998 \uAC00\uC7A5 \uAD00\uC2EC \uC788\uB294 \uC8FC\uC81C\uB098 \uBC30\uC6B0\uACE0 \uC2F6\uC740 \uAE30\uC220\xB7\uACBD\uD5D8\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uD604\uC7AC \uD65C\uC6A9\uD558\uB294 \uB124\uD2B8\uC6CC\uD06C\uB294 \uC5B4\uB5A4 \uAC83\uB4E4\uC774 \uC788\uB098\uC694? \uC55E\uC73C\uB85C \uD655\uC7A5\uD558\uACE0 \uC2F6\uC740 \uB124\uD2B8\uC6CC\uD06C\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uB3D9\uB8CC\uB4E4\uC774 \uB098\uB97C \uC5B4\uB5BB\uAC8C \uC778\uC2DD\uD558\uAE38 \uBC14\uB77C\uB098\uC694? \uD604\uC7AC\uC5D0\uC11C \uC720\uC9C0\uD558\uACE0 \uC2F6\uC740 \uBD80\uBD84\uACFC \uBC14\uAFB8\uACE0 \uC2F6\uC740 \uBD80\uBD84\uC774 \uC788\uB098\uC694?",
        "\uC131\uC7A5\uD560 \uC218 \uC788\uB294 \uBC29\uBC95\uC5D0 \uB300\uD55C \uC561\uC158 \uC544\uC774\uD15C\uC744 \uC815\uB9AC\uD574\uBD05\uC2DC\uB2E4."
      ]
    },
    retention_management: {
      title: "\uB9AC\uD150\uC158 \uAD00\uB9AC \uD15C\uD50C\uB9BF",
      questions: [
        "\uCD5C\uADFC \uC5C5\uBB34\uC5D0\uC11C \uAC00\uC7A5 \uB9CC\uC871\uC2A4\uB7EC\uC6E0\uB358 \uC21C\uAC04\uACFC \uADF8 \uC774\uC720\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uAC00\uC7A5 \uC88B\uC544\uD558\uB294 \uC5C5\uBB34\uC640, \uC0C1\uB300\uC801\uC73C\uB85C \uC120\uD638\uD558\uC9C0 \uC54A\uB294 \uC5C5\uBB34\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC6B0\uB9AC \uD68C\uC0AC\uC5D0\uC11C \uC77C\uD558\uBA74\uC11C \uAC00\uC7A5 \uC88B\uC740 \uC810\uACFC \uAC1C\uC120\uC774 \uD544\uC694\uD55C \uC810\uC740 \uBB34\uC5C7\uC774\uB77C \uC0DD\uAC01\uD558\uB098\uC694?",
        "\uD68C\uC0AC\uC5D0\uC11C \uB9C8\uC74C\uB300\uB85C \uD55C \uAC00\uC9C0\uB97C \uBC14\uAFC0 \uC218 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC744 \uBC14\uAFB8\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uC5C5\uBB34\uC801\uC73C\uB85C \uC5B4\uB5A4 \uC601\uC5ED\uC5D0\uC11C \uC778\uC815\uBC1B\uACE0 \uC2F6\uACE0, \uC5B4\uB5A4 \uBC29\uC2DD\uC73C\uB85C \uC778\uC815\uBC1B\uC744 \uB54C \uAC00\uC7A5 \uB3D9\uAE30\uBD80\uC5EC\uAC00 \uB418\uB098\uC694?",
        "\uD604\uC7AC \uB9E1\uC740 \uC5C5\uBB34\uB098 \uC5ED\uD560\uC5D0\uC11C \uBC1C\uD718\uD558\uC9C0 \uBABB\uD558\uACE0 \uC788\uB294 \uC7AC\uB2A5\uC774\uB098 \uC7A5\uC810\uC774 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC6B0\uB9AC \uD68C\uC0AC\uC5D0\uC11C \uB3D9\uAE30\uB97C \uB192\uC5EC\uC8FC\uB294 \uC694\uC18C\uC640, \uBC18\uB300\uB85C \uC758\uC695\uC744 \uB5A8\uC5B4\uB728\uB9AC\uB294 \uC694\uC18C\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uB9CC\uC57D \uC6B0\uB9AC \uD68C\uC0AC\uB97C \uB5A0\uB098\uAC8C \uB41C\uB2E4\uBA74, \uADF8 \uC8FC\uC694 \uC774\uC720\uB294 \uBB34\uC5C7\uC77C\uAE4C\uC694?",
        "\uB2E4\uC74C \uBBF8\uD305\uAE4C\uC9C0 \uC9C4\uD589\uD574 \uBCFC \uC561\uC158 \uC544\uC774\uD15C\uC744 \uC815\uB9AC\uD574\uBD05\uC2DC\uB2E4."
      ]
    },
    performance_energy: {
      title: "\uC5ED\uB7C9, \uC131\uACFC, \uC5D0\uB108\uC9C0 \uD15C\uD50C\uB9BF",
      questions: [
        "\uCD5C\uADFC\uC5D0 \uAC15\uC810\uC744 \uBC1C\uD718\uD558\uC5EC '\uB0B4\uAC00 \uC798 \uD588\uB2E4'\uC2F6\uC5C8\uB358 \uC21C\uAC04\uC744 \uC774\uC57C\uAE30\uD574\uC900\uB2E4\uBA74 \uC5B4\uB5A4 \uAC83\uC778\uAC00\uC694?",
        "\uCD5C\uADFC '\uB354 \uC798\uD558\uACE0 \uC2F6\uB2E4'\uACE0 \uB290\uB080 \uBD80\uBD84\uC774\uB098, \uD639\uC740 '\uB2E4\uC2DC \uB3CC\uC544\uAC04\uB2E4\uBA74 \uC774\uB807\uAC8C \uD574\uBCF4\uACE0 \uC2F6\uB2E4'\uACE0 \uC0DD\uAC01\uD588\uB358 \uBD80\uBD84\uC774 \uC788\uB098\uC694?",
        "\uB9CC\uC57D \uC77C\uC8FC\uC77C \uB3D9\uC548 \uC790\uB9AC\uB97C \uBE44\uC6CC\uC57C\uD55C\uB2E4\uBA74 \uBA48\uCD9C \uAC00\uB2A5\uC131\uC774 \uB192\uC740 \uC77C\uC740 \uBB34\uC5C7\uC774\uACE0, \uB204\uAC00 \uC774\uC5B4\uBC1B\uC73C\uBA74 \uC548\uC2EC\uB420\uAE4C\uC694?",
        "\uC791\uAC8C\uB77C\uB3C4 \uD574\uBCF4\uACE0 \uC2F6\uC740 \uC5ED\uD560/\uD504\uB85C\uC81D\uD2B8/\uC5C5\uBB34\uAC00 \uC788\uB2E4\uBA74\uC694?",
        "\uC9C0\uB09C 2\uC8FC\uAC04 \uC9C4\uD589\uD55C \uC5C5\uBB34 \uC911 \uAC00\uC7A5 \uC911\uC694\uD558\uB2E4\uACE0 \uC0DD\uAC01\uD558\uB294 2~3\uAC00\uC9C0\uB97C \uACF5\uC720\uD574\uC8FC\uC138\uC694.",
        "\uAC89\uC73C\uB85C \uB4DC\uB7EC\uB098\uC9C0\uB294 \uC54A\uC9C0\uB9CC, \uD300\uC774\uB098 \uC11C\uBE44\uC2A4\uC5D0 \uAF2D \uD544\uC694\uD588\uB2E4\uACE0 \uC0DD\uAC01\uD558\uB294 \uBCF8\uC778\uC758 \uAE30\uC5EC\uAC00 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uC5C5\uBB34 \uC9C4\uD589\uC5D0 \uC788\uC5B4 \uC18D\uB3C4\uB97C \uB2A6\uCD94\uAC70\uB098 \uBC29\uD574\uAC00 \uB41C \uBB38\uC81C\uB294 \uBB34\uC5C7\uC774\uC5C8\uB098\uC694? \uB9CC\uC57D \uD574\uACB0\uD560 \uC218 \uC788\uB2E4\uBA74 \uC5B4\uB5BB\uAC8C \uD574\uACB0\uD558\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uCD5C\uADFC \uB3D9\uB8CC\uC758 \uC131\uACFC\uC5D0 \uAE0D\uC815\uC801\uC778 \uC601\uD5A5\uC744 \uC900 \uC77C\uC774 \uC788\uC5C8\uB2E4\uBA74 \uACF5\uC720\uD574\uC8FC\uC2E4 \uC218 \uC788\uB098\uC694?",
        "\uC9C0\uB09C \uAE30\uAC04 \uB3D9\uC548 \uB098\uB97C \uC6C0\uC9C1\uC778 \uB3D9\uAE30\uB294 \uBB34\uC5C7\uC774\uC5C8\uB098\uC694?",
        "\uD604\uC7AC \uD300\uC758 \uD504\uB85C\uC138\uC2A4\uB098 \uBB38\uD654\uC5D0\uC11C '\uC774\uAC83 \uD55C \uAC00\uC9C0\uB97C \uBC14\uAFC0 \uC218 \uC788\uB2E4\uBA74' \uC5B4\uB5A4 \uAC83\uC744 \uBC14\uAFB8\uACE0 \uC2F6\uB098\uC694?",
        "\uCD5C\uADFC\uC5D0 \uAC00\uC7A5 \uBAB0\uC785\uD588\uB358 \uC21C\uAC04\uC740 \uC5B8\uC81C\uC600\uB098\uC694? \uBC18\uB300\uB85C \uAC00\uC7A5 \uD798\uB4E4\uAC70\uB098 \uC9C0\uCCE4\uB358 \uC21C\uAC04\uC740 \uC5B8\uC81C\uC600\uB098\uC694?"
      ]
    },
    teamwork_collaboration: {
      title: "\uD300\uC6CC\uD06C \uBC0F \uD611\uC5C5 \uD15C\uD50C\uB9BF",
      questions: [
        "\uD300\uC758 \uC131\uACF5\uC744 \uC704\uD574 \uB2E4\uB978 \uC0AC\uB78C\uB4E4\uC774 \uC798 \uBAA8\uB974\uB294 '\uB098\uB9CC\uC758 \uAE30\uC5EC'\uAC00 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC77C\uAE4C\uC694?",
        "\uCD5C\uADFC\uC5D0 \uD568\uAED8 \uC77C\uD558\uBA74\uC11C \uC2DC\uB108\uC9C0\uAC00 \uC88B\uC558\uB2E4\uACE0 \uB290\uB080 \uB3D9\uB8CC\uB294 \uB204\uAD6C\uC778\uAC00\uC694? \uC5B4\uB5A4 \uC810\uC5D0\uC11C \uADF8\uB807\uAC8C \uB290\uAF08\uB098\uC694?",
        "\uB3D9\uB8CC\uC5D0\uAC8C \uC5B4\uB5A4 \uD53C\uB4DC\uBC31\uC744 \uBC1B\uC558\uC744 \uB54C \uAC00\uC7A5 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694?",
        "\uC6B0\uB9AC \uD300\uC774 \uACC4\uC18D \uC9C0\uCF1C\uB098\uAC14\uC73C\uBA74 \uD558\uB294 \uAC00\uC7A5 \uAE0D\uC815\uC801\uC778 \uBB38\uD654\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uCD5C\uADFC \uC5C5\uBB34 \uC911 \uB2E4\uB978 \uC0AC\uB78C\uC758 \uB3C4\uC6C0\uC774 \uD544\uC694\uD588\uC9C0\uB9CC, \uC694\uCCAD\uD558\uAE30 \uC5B4\uB824\uC6E0\uB358 \uC21C\uAC04\uC774 \uC788\uC5C8\uB098\uC694? \uADF8 \uC774\uC720\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uB2E4\uB978 \uD300\uC6D0\uACFC \uC77C\uD560 \uB54C \uB2F9\uC2E0\uC758 \uAC15\uC810\uC740 \uBB34\uC5C7\uC774\uB77C\uACE0 \uC0DD\uAC01\uD558\uB098\uC694? \uADF8\uB9AC\uACE0 \uC5B4\uB5A4 \uC810\uC744 \uB354 \uBC1C\uC804\uC2DC\uD0A4\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
        "\uCD5C\uADFC\uC5D0 \uD300 \uB0B4 \uC758\uACAC \uCC28\uC774\uB098 \uAC08\uB4F1\uC744 \uAE0D\uC815\uC801\uC73C\uB85C \uD574\uACB0\uD588\uB358 \uACBD\uD5D8\uC774 \uC788\uB098\uC694? \uADF8 \uACFC\uC815\uC5D0\uC11C \uC5B4\uB5A4 \uC5ED\uD560\uC744 \uD558\uC168\uB294\uC9C0 \uAD81\uAE08\uD569\uB2C8\uB2E4.",
        "\uC6B0\uB9AC \uD300\uC758 \uD611\uC5C5 \uBC29\uC2DD\uC744 \uD55C \uB2E8\uACC4 \uB354 \uBC1C\uC804\uC2DC\uD0A4\uAE30 \uC704\uD574 \uC2DC\uB3C4\uD574\uBCF4\uACE0 \uC2F6\uC740 \uC0C8\uB85C\uC6B4 \uC544\uC774\uB514\uC5B4\uAC00 \uC788\uB098\uC694?",
        "\uC624\uB298 \uB17C\uC758\uD55C \uB0B4\uC6A9\uC744 \uBC14\uD0D5\uC73C\uB85C, \uC55E\uC73C\uB85C\uC758 \uC131\uC7A5\uC744 \uC704\uD574 \uC5B4\uB5A4 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uD568\uAED8 \uB9CC\uB4E4\uC5B4 \uBCFC\uAE4C\uC694?"
      ]
    },
    performance_improvement_plan: {
      title: "\uC131\uACFC \uAC1C\uC120 \uACC4\uD68D \uD15C\uD50C\uB9BF",
      questions: [
        "\uC9C0\uB09C [\uAE30\uAC04]\uC758 [\uC8FC\uC694 \uC5C5\uBB34] \uC131\uACFC\uB97C \uBCF4\uBA74, \uBAA9\uD45C\uD588\uB358 (A) \uC218\uC900 \uB300\uBE44 \uD604\uC7AC (B) \uC218\uC900\uC73C\uB85C \uD655\uC778\uB429\uB2C8\uB2E4. \uC774 \uB370\uC774\uD130\uC5D0 \uB300\uD574, \uBCF8\uC778 \uC2A4\uC2A4\uB85C\uB294 \uC5B4\uB5BB\uAC8C \uC0DD\uAC01\uD558\uACE0 \uACC4\uC2E0\uC9C0 \uC758\uACAC\uC744 \uB4E3\uACE0 \uC2F6\uC2B5\uB2C8\uB2E4.",
        "\uC5C5\uBB34\uB97C \uD558\uBA74\uC11C \uAC00\uC7A5 \uC5B4\uB835\uAC8C \uB290\uAEF4\uC9C0\uB294 \uBD80\uBD84\uC774\uB098, \uC131\uACFC\uB97C \uB0B4\uAE30 \uD798\uB4E4\uAC8C \uB9CC\uB4DC\uB294 \uC8FC\uB41C \uC6D0\uC778\uC774 \uC788\uB2E4\uBA74 \uBB34\uC5C7\uC778\uAC00\uC694?",
        "\uD604\uC7AC \uB9E1\uACE0 \uACC4\uC2E0 \uC9C1\uBB34\uAC00 \uBCF8\uC778\uC758 \uAC15\uC810\uC774\uB098 \uC801\uC131\uACFC \uC798 \uB9DE\uB294\uB2E4\uACE0 \uC0DD\uAC01\uD558\uC2DC\uB098\uC694? \uD639\uC740 '\uC774 \uBD80\uBD84\uC740 \uC815\uB9D0 \uB098\uC640 \uB9DE\uC9C0 \uC54A\uB294\uB2E4'\uACE0 \uB290\uB07C\uB294 \uC9C0\uC810\uC774 \uC788\uC73C\uC2E0\uAC00\uC694?",
        "\uC131\uACFC\uB97C \uAC1C\uC120\uD558\uAE30 \uC704\uD574 \uADF8\uB3D9\uC548 \uAC1C\uC778\uC801\uC73C\uB85C \uC5B4\uB5A4 \uB178\uB825\uC744 \uD574\uBCF4\uC168\uB098\uC694?",
        "\uC5C5\uBB34 \uC131\uACFC\uB97C \uB192\uC774\uAE30 \uC704\uD574 \uD68C\uC0AC\uB098 \uC81C\uAC00 \uC5B4\uB5A4 \uC810\uC744 \uC9C0\uC6D0\uD574 \uC8FC\uBA74 \uAC00\uC7A5 \uC2E4\uC9C8\uC801\uC778 \uB3C4\uC6C0\uC774 \uB420 \uC218 \uC788\uC744\uAE4C\uC694?",
        "\uC55E\uC73C\uB85C [\uAC1C\uC120 \uAE30\uAC04] \uB3D9\uC548 \uAC00\uC7A5 \uC9D1\uC911\uD574\uC11C \uAC1C\uC120\uD558\uACE0 \uC2F6\uC740 \uC601\uC5ED\uC740 \uBB34\uC5C7\uC774\uACE0, \uB2EC\uC131\uD558\uACE0 \uC2F6\uC740 '\uAD6C\uCCB4\uC801\uC778 \uBAA9\uD45C \uC218\uC900'\uC740 \uBB34\uC5C7\uC77C\uAE4C\uC694?",
        "\uBAA9\uD45C \uC218\uC900\uC744 \uB2EC\uC131\uD558\uAE30 \uC704\uD574, \uB2F9\uC7A5 \uC774\uBC88 \uC8FC\uBD80\uD130 \uC2DC\uC791\uD560 \uC218 \uC788\uB294 '\uC0C8\uB85C\uC6B4 \uD589\uB3D9'\uC774\uB098 '\uBC14\uAFC0 \uC2B5\uAD00' 3\uAC00\uC9C0\uB9CC \uB9D0\uC500\uD574 \uC8FC\uC2DC\uACA0\uC5B4\uC694?",
        "\uACC4\uD68D\uB4E4\uC774 \uC798 \uC2E4\uD589\uB418\uB294\uC9C0 \uD655\uC778\uD558\uACE0 \uC800\uB3C4 \uD53C\uB4DC\uBC31\uC744 \uB4DC\uB9AC\uAE30 \uC704\uD574, \uC55E\uC73C\uB85C [\uAE30\uAC04] \uB3D9\uC548\uC740 [\uC8FC 1\uD68C, 2\uC8FC 1\uD68C] \uC815\uB3C4 \uC9C0\uAE08\uCC98\uB7FC 1on1\uC73C\uB85C \uC810\uAC80\uD558\uB294 \uC2DC\uAC04\uC744 \uAC00\uC9C0\uBA74 \uC5B4\uB5A8\uAE4C\uC694?",
        "\uC624\uB298 \uC774\uC57C\uAE30 \uB098\uB208 \uB0B4\uC6A9\uB4E4\uC740 Orblit 1on1\uC5D0\uC11C \uD655\uC778 \uAC00\uB2A5\uD569\uB2C8\uB2E4. \uB9C8\uC9C0\uB9C9\uC73C\uB85C \uD558\uACE0 \uC2F6\uC740 \uB9D0\uC500\uC774 \uC788\uC73C\uC2E0\uAC00\uC694?"
      ]
    },
    performance_improvement_check: {
      title: "\uC131\uACFC \uAC1C\uC120 \uC810\uAC80 \uD15C\uD50C\uB9BF",
      questions: [
        "\uC9C0\uB09C [\uC810\uAC80 \uC8FC\uAE30] \uB3D9\uC548, \uC6B0\uB9AC\uAC00 \uC57D\uC18D\uD588\uB358 [\uD575\uC2EC \uAC1C\uC120 \uC601\uC5ED]\uC740 \uC5B4\uB5A0\uC168\uB098\uC694?",
        "\uACC4\uD68D\uD588\uB358 \uD589\uB3D9 \uC911\uC5D0\uC11C, \uC2A4\uC2A4\uB85C '\uC774\uAC74 \uC798 \uC9C0\uCF30\uB2E4' \uB610\uB294 '\uD6A8\uACFC\uAC00 \uC788\uC5C8\uB2E4'\uACE0 \uC0DD\uAC01\uD558\uB294 \uBD80\uBD84\uC774 \uC788\uB2E4\uBA74 \uB9D0\uC500\uD574 \uC8FC\uC138\uC694.",
        "\uBC18\uB300\uB85C, \uACC4\uD68D\uC744 \uC2E4\uD589\uD558\uBA74\uC11C \uAC00\uC7A5 \uC5B4\uB824\uC6E0\uB358 \uC810\uC774\uB098 \uC608\uC0C1\uCE58 \uBABB\uD55C \uC7A5\uC560\uBB3C\uC740 \uBB34\uC5C7\uC774\uC5C8\uB098\uC694?",
        "\uC81C\uAC00 \uC9C0\uCF1C\uBCF8 \uBC14\uB85C\uB294, [\uAC1C\uC120\uB41C \uC810] \uCE21\uBA74\uC740 \uAC1C\uC120\uB41C \uBC18\uBA74, [\uC544\uC26C\uC6B4 \uC810]\uC740 \uC5EC\uC804\uD788 \uC544\uC26C\uC6E0\uC2B5\uB2C8\uB2E4. \uC774 \uBD80\uBD84\uC5D0 \uB300\uD574 \uC5B4\uB5BB\uAC8C \uC0DD\uAC01\uD558\uC2DC\uB098\uC694?",
        "\uC9C0\uB09C\uBC88\uC5D0 \uC81C\uAC00 \uC9C0\uC6D0\uD574 \uB4DC\uB9AC\uAE30\uB85C \uD588\uB358 [\uC57D\uC18D\uB41C \uC9C0\uC6D0]\uC740 \uC2E4\uC9C8\uC801\uC778 \uB3C4\uC6C0\uC774 \uB418\uC5C8\uB098\uC694? \uCD94\uAC00\uB85C \uD544\uC694\uD55C \uC9C0\uC6D0\uC774\uB098 \uB2E4\uB978 \uBC29\uC2DD\uC758 \uB3C4\uC6C0\uC774 \uD544\uC694\uD560\uAE4C\uC694?",
        "\uC774\uBC88 [\uC810\uAC80 \uC8FC\uAE30] \uB3D9\uC548 \uC774 \uACFC\uC815\uC744 \uC9C4\uD589\uD558\uBA74\uC11C, \uC2A4\uC2A4\uB85C '\uC774 \uBD80\uBD84\uC740 \uB0B4\uAC00 \uBC30\uC6B0\uACE0 \uC788\uB2E4' \uB610\uB294 '\uC0C8\uB86D\uAC8C \uAE68\uB2EC\uC558\uB2E4' \uD558\uB294 \uC810\uC774 \uC788\uC5C8\uB098\uC694?",
        "\uB2E4\uC74C \uC810\uAC80\uAE4C\uC9C0 \uC5B4\uB5A4 \uBD80\uBD84\uC5D0 \uC870\uAE08 \uB354 \uC9D1\uC911\uD574 \uBCFC\uAE4C\uC694?",
        "\uC624\uB298 \uC774\uC57C\uAE30 \uB098\uB208 \uB0B4\uC6A9\uB4E4\uC740 Orblit 1on1\uC5D0\uC11C \uD655\uC778 \uAC00\uB2A5\uD569\uB2C8\uB2E4. \uB9C8\uC9C0\uB9C9\uC73C\uB85C \uD558\uACE0 \uC2F6\uC740 \uB9D0\uC500\uC774 \uC788\uC73C\uC2E0\uAC00\uC694?"
      ]
    },
    first_oneonone: {
      title: "\uCCAB 1on1 \uD15C\uD50C\uB9BF",
      description: "\uC11C\uB85C \uCC98\uC74C \uB9C8\uC8FC \uC549\uB294 \uC790\uB9AC. \uC55E\uC73C\uB85C\uC758 1on1\uC744 \uC704\uD574 \uC11C\uB85C\uB97C \uC774\uD574\uD558\uACE0, \uD3B8\uD558\uAC8C \uC774\uC57C\uAE30\uD560 \uC218 \uC788\uB294 \uBD84\uC704\uAE30\uB97C \uB9CC\uB4E4\uAE30 \uC704\uD55C \uC9C8\uBB38\uB4E4\uB85C \uCC44\uC6E0\uC5B4\uC694.",
      questions: [
        "\uC55E\uC73C\uB85C \uC800\uC640 \uC815\uAE30\uC801\uC73C\uB85C \uC774\uB807\uAC8C \uC77C\uB300\uC77C\uB85C \uB300\uD654\uD558\uB294 \uC2DC\uAC04\uC744 \uAC00\uC9C0\uB824\uACE0 \uD574\uC694. \uC5C5\uBB34 \uC810\uAC80\uC774\uB098 \uD3C9\uAC00\uB97C \uC704\uD55C \uC790\uB9AC\uAC00 \uC544\uB2C8\uB77C, \uC77C\uD558\uC2DC\uBA74\uC11C \uB290\uB07C\uB294 \uC810\uC744 \uD3B8\uD558\uAC8C \uB098\uB204\uACE0, \uC81C\uAC00 \uC5B4\uB5BB\uAC8C \uB3C4\uC6B8 \uC218 \uC788\uC744\uC9C0 \uD568\uAED8 \uC774\uC57C\uAE30\uD558\uB294 \uC2DC\uAC04\uC774\uC5D0\uC694. \uC5C5\uBB34, \uD611\uC5C5, \uCEE4\uB9AC\uC5B4 \uACE0\uBBFC \uB4F1 \uD3B8\uD55C \uC8FC\uC81C\uBD80\uD130 \uBD80\uB2F4 \uC5C6\uC774 \uC774\uC57C\uAE30\uD574\uC8FC\uC2DC\uBA74 \uC88B\uACA0\uC5B4\uC694.",
        "\uC624\uB298\uC740 \uCCAB 1on1\uC774\uB2C8\uAE4C \uAC00\uBCCD\uAC8C \uC11C\uB85C \uB9DE\uCDB0\uBCF4\uB294 \uC2DC\uAC04\uC73C\uB85C \uC0DD\uAC01\uD574\uC8FC\uC2DC\uBA74 \uC88B\uC744 \uAC83 \uAC19\uC544\uC694. \uD639\uC2DC \uC774\uC804\uC5D0 1on1\uC744 \uD574\uBCF4\uC2E0 \uC801 \uC788\uC73C\uC138\uC694? \uC788\uC5C8\uB2E4\uBA74 \uC5B4\uB5A4 \uC810\uC774 \uC88B\uC558\uACE0 \uC5B4\uB5A4 \uC810\uC774 \uC544\uC26C\uC6E0\uB294\uC9C0 \uAD81\uAE08\uD574\uC694.",
        "\uC694\uC998 \uB9E1\uC740 \uC77C \uC911\uC5D0 \uC798 \uD480\uB9AC\uACE0 \uC788\uB294 \uAC83\uACFC, \uC870\uAE08 \uB9C9\uD788\uAC70\uB098 \uC2E0\uACBD \uC4F0\uC774\uB294 \uC77C\uC774 \uC788\uB2E4\uBA74 \uC5B4\uB5A4 \uAC74\uAC00\uC694?",
        "\uC694\uC998 \uB354 \uD574\uBCF4\uACE0 \uC2F6\uC740 \uC77C\uC774\uB098 \uC0C8\uB86D\uAC8C \uC2DC\uB3C4\uD574\uBCF4\uACE0 \uC2F6\uC740 \uC77C\uC774 \uC788\uC744\uAE4C\uC694?",
        "\uC77C\uD560 \uB54C \uC120\uD638\uD558\uB294 \uC18C\uD1B5 \uBC29\uC2DD\uC774 \uC788\uC73C\uC138\uC694? \uBA54\uC2E0\uC800\uB85C \uB0A8\uAE30\uB294 \uAC8C \uD3B8\uD55C\uC9C0, \uBC14\uB85C \uC774\uC57C\uAE30\uD558\uB294 \uAC8C \uC88B\uC740\uC9C0\uCC98\uB7FC\uC694.",
        "\uC81C\uAC00 \uB2F9\uC7A5 \uB3C4\uC640\uB4DC\uB9B4 \uC218 \uC788\uB294 \uC0AC\uC18C\uD55C \uBD88\uD3B8\uC774\uB098 \uAC78\uB9BC\uB3CC\uC774 \uC788\uC744\uAE4C\uC694? (\uC608: \uD0C0 \uBD80\uC11C \uC18C\uD1B5, \uAD8C\uD55C, \uC7A5\uBE44 \uB4F1)",
        "\uC6B0\uB9AC \uD300\uC774 \uC77C\uD558\uB294 \uBC29\uC2DD\uC5D0\uC11C \uB098\uC911\uC5D0\uB77C\uB3C4 \uB354 \uAC1C\uC120\uB418\uC5C8\uC73C\uBA74 \uD558\uB294 \uBD80\uBD84\uC774 \uC0DD\uAE30\uBA74 \uD3B8\uD558\uAC8C \uC54C\uB824\uC8FC\uC138\uC694. \uC9C0\uAE08 \uBC14\uB85C \uB5A0\uC624\uB974\uC9C0 \uC54A\uC544\uB3C4 \uAD1C\uCC2E\uC544\uC694.",
        "\uC624\uB298 \uCCAB 1on1 \uC5B4\uB5A0\uC168\uC5B4\uC694? \uB9C8\uC9C0\uB9C9\uC73C\uB85C \uAD81\uAE08\uD55C \uC810\uC774\uB098 \uB354 \uB098\uB204\uACE0 \uC2F6\uC740 \uC774\uC57C\uAE30 \uC788\uC73C\uBA74 \uD3B8\uD558\uAC8C \uB9D0\uC500\uD574\uC8FC\uC138\uC694. \uC774\uB7F0 \uC2DC\uAC04\uC744 \uACA9\uC8FC\uC5D0 1\uC2DC\uAC04 \uC815\uB3C4 \uAFB8\uC900\uD788 \uAC00\uC9C0\uBA74 \uC88B\uC744 \uAC83 \uAC19\uC740\uB370, \uC5B4\uB5A0\uC138\uC694?"
      ]
    }
  }
};

// src/locales/ko-KR/payment.json
var payment_default2 = {
  cancel_subscription_modal: {
    cancel: "\uB3CC\uC544\uAC00\uAE30",
    confirm: "\uAD6C\uB3C5 \uCDE8\uC18C",
    inquiry: "\uAD00\uB828 \uBB38\uC758\uC0AC\uD56D\uC774 \uC788\uB2E4\uBA74 jjchoi@reversemountain.co.kr\uB85C \uBB38\uC758 \uBD80\uD0C1\uB4DC\uB9BD\uB2C8\uB2E4.",
    message: "\uC815\uB9D0 \uAD6C\uB3C5\uC744 \uCDE8\uC18C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    subscription_info: "\uD574\uB2F9 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB294 {{endDate}}\uAE4C\uC9C0 {{memberCount}}\uBA85\uC774 \uC720\uB8CC \uAE30\uB2A5\uC73C\uB85C \uC0AC\uC6A9 \uAC00\uB2A5\uD569\uB2C8\uB2E4.",
    title: "\uAD6C\uB3C5 \uCDE8\uC18C \uD655\uC778"
  },
  confirm_modal: {
    activate_message: "\uBA64\uBC84\uB97C \uD65C\uC131\uD654\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    activate_prorated_charge: "\uC77C\uD560 \uACC4\uC0B0\uB41C \uAE08\uC561\uC774 \uACB0\uC81C\uB429\uB2C8\uB2E4.",
    activate_slot_available: "\uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC2AC\uB86F\uC744 \uC0AC\uC6A9\uD569\uB2C8\uB2E4.",
    activate_title: "\uBA64\uBC84 \uD65C\uC131\uD654",
    available_slots: "\uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC2AC\uB86F \uC218: {{count}}\uAC1C",
    cancel: "\uCDE8\uC18C",
    confirm: "\uD655\uC778",
    deactivate_message: "\uBA64\uBC84\uB97C \uBE44\uD65C\uC131\uD654\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    deactivate_slot_message: "\uBE44\uD65C\uC131\uD654 \uC2DC \uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC2AC\uB86F 1\uAC1C\uAC00 \uCD94\uAC00\uB418\uC5B4 \uB2E4\uB978 \uBA64\uBC84\uB97C \uD65C\uC131\uD654\uD560 \uB54C \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    deactivate_title: "\uBA64\uBC84 \uBE44\uD65C\uC131\uD654"
  },
  notifications: {
    activate_member_error_title: "\uBA64\uBC84 \uD65C\uC131\uD654 \uC2E4\uD328",
    aws_marketplace_cancel_message: "AWS Marketplace \uAD6C\uB3C5\uC740 AWS Console\uC5D0\uC11C \uC9C1\uC811 \uCDE8\uC18C\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    aws_marketplace_cancel_path: "AWS Console \u2192 AWS Marketplace Subscriptions \u2192 Manage Subscriptions",
    aws_marketplace_cancel_title: "AWS Marketplace \uAD6C\uB3C5 \uCDE8\uC18C",
    deactivate_member_error_title: "\uBA64\uBC84 \uBE44\uD65C\uC131\uD654 \uC2E4\uD328",
    member_remove_error_message: "\uBA64\uBC84 \uC81C\uAC70\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    member_remove_error_title: "\uBA64\uBC84 \uC81C\uAC70 \uC2E4\uD328",
    member_removed_message: "\uBA64\uBC84\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uC81C\uAC70\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    member_removed_title: "\uBA64\uBC84 \uC81C\uAC70 \uC644\uB8CC",
    purchase_slots_error_message: "\uC2AC\uB86F \uAD6C\uB9E4\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    purchase_slots_error_title: "\uC2AC\uB86F \uAD6C\uB9E4 \uC2E4\uD328",
    purchase_slots_success_message: "{{count}}\uAC1C\uC758 \uC2AC\uB86F\uC774 \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    purchase_slots_success_title: "\uC2AC\uB86F \uAD6C\uB9E4 \uC644\uB8CC",
    slot_added_after_deactivate: "\uB2E4\uB978 \uBA64\uBC84\uB97C \uD65C\uC131\uD654\uD560 \uC218 \uC788\uB294 \uC2AC\uB86F\uC774 1\uAC1C \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    slot_added_message: "\uC81C\uAC70\uB41C \uBA64\uBC84\uAC00 \uD65C\uC131\uD654 \uC0C1\uD0DC\uC600\uC73C\uBBC0\uB85C \uC2AC\uB86F 1\uAC1C\uAC00 \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    slot_added_title: "\uC2AC\uB86F \uCD94\uAC00",
    subscription_cancel_error_message: "\uAD6C\uB3C5 \uCDE8\uC18C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    subscription_cancel_error_title: "\uAD6C\uB3C5 \uCDE8\uC18C \uC2E4\uD328",
    subscription_cancelled_message: "{{endDate}}\uAE4C\uC9C0 \uC0AC\uC6A9 \uAC00\uB2A5\uD569\uB2C8\uB2E4.",
    subscription_cancelled_title: "\uAD6C\uB3C5 \uCDE8\uC18C \uC644\uB8CC",
    subscription_exists_message: "\uC774 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0\uB294 \uC774\uBBF8 \uD65C\uC131\uD654\uB41C \uAD6C\uB3C5\uC774 \uC874\uC7AC\uD569\uB2C8\uB2E4.",
    subscription_exists_title: "\uC774\uBBF8 \uAD6C\uB3C5 \uC911\uC785\uB2C8\uB2E4",
    subscription_reactivate_error_message: "\uC7AC\uAD6C\uB3C5\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
    subscription_reactivate_error_title: "\uC7AC\uAD6C\uB3C5 \uC2E4\uD328",
    subscription_reactivated_message: "\uAD6C\uB3C5\uC774 \uC7AC\uD65C\uC131\uD654\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    subscription_reactivated_title: "\uC7AC\uAD6C\uB3C5 \uC644\uB8CC",
    toggle_member_error_message: "\uBA64\uBC84 \uD65C\uC131\uD654/\uBE44\uD65C\uC131\uD654\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
  },
  payment_fail: {
    default_message: "\uACB0\uC81C \uCC98\uB9AC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    error_code: "\uC5D0\uB7EC \uCF54\uB4DC",
    go_home_button: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
    order_id: "\uC8FC\uBB38\uBC88\uD638",
    retry_button: "\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAE30",
    title: "\uACB0\uC81C \uC2E4\uD328"
  },
  payment_page: {
    activate_members_first: "1on1 \uBA64\uBC84\uB97C \uBA3C\uC800 \uD65C\uC131\uD654\uD574\uC8FC\uC138\uC694",
    active_members: "1on1 \uD65C\uC131 \uBA64\uBC84 \uC218",
    currency: "\uC6D0",
    loading: "\uB85C\uB529 \uC911...",
    loading_members: "\uBA64\uBC84 \uC815\uBCF4 \uB85C\uB529 \uC911...",
    monthly: {
      description: "\uB9E4\uC6D4 \uC790\uB3D9 \uACB0\uC81C",
      label: "\uC6D4\uAC04 \uACB0\uC81C",
      per_month: "/\uC6D4"
    },
    no_members_warning: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC124\uC815\uC5D0\uC11C 1on1 \uBA64\uBC84\uB97C \uBA3C\uC800 \uD65C\uC131\uD654\uD574\uC8FC\uC138\uC694.",
    payment_summary: {
      monthly: "\uC6D4 \uACB0\uC81C \uAE08\uC561",
      per_year_suffix: "/\uB144",
      yearly: "\uC5F0 \uACB0\uC81C \uAE08\uC561"
    },
    price_per_member_monthly: "\uBA64\uBC84\uB2F9 \uC6D4 \uC694\uAE08",
    register_button: "\uCE74\uB4DC \uB4F1\uB85D\uD558\uACE0 {{amount}} \uACB0\uC81C",
    select_billing_cycle: "\uACB0\uC81C \uC8FC\uAE30 \uC120\uD0DD",
    service_description: "\uCE74\uB4DC\uB97C \uB4F1\uB85D\uD558\uC2DC\uBA74 \uC790\uB3D9\uC73C\uB85C \uACB0\uC81C\uB429\uB2C8\uB2E4.",
    service_title: "1on1 \uC11C\uBE44\uC2A4 \uC815\uAE30\uACB0\uC81C",
    title: "\uC815\uAE30\uACB0\uC81C \uCE74\uB4DC \uB4F1\uB85D",
    yearly: {
      description: "1\uB144\uC5D0 \uD55C \uBC88 \uACB0\uC81C",
      label: "\uC5F0\uAC04 \uACB0\uC81C",
      per_year: "/\uB144"
    }
  },
  payment_success: {
    auto_payment_info: "\uB4F1\uB85D\uB41C \uCE74\uB4DC\uB85C \uC790\uB3D9\uC73C\uB85C \uACB0\uC81C\uAC00 \uC9C4\uD589\uB429\uB2C8\uB2E4.",
    billing_cycle: "\uACB0\uC81C \uC8FC\uAE30",
    check_setting_info: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC124\uC815\uC5D0\uC11C \uACB0\uC81C \uC815\uBCF4\uB97C \uD655\uC778\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    error_description: "\uACB0\uC81C \uACFC\uC815\uC5D0\uC11C \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.\n\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uC2DC\uAC70\uB098 \uBB38\uC81C\uAC00 \uC9C0\uC18D\uB418\uBA74 \uB3C4\uC6C0\uB9D0 > \uBB38\uC758\uD558\uAE30\uB85C \uC54C\uB824\uC8FC\uC138\uC694.",
    error_title: "\uC624\uB958 \uB0B4\uC6A9",
    failed_title: "\uACB0\uC81C \uC2E4\uD328",
    go_home_button: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30",
    home_button: "\uD648\uC73C\uB85C",
    invalid_auth_error: "\uC778\uC99D \uC815\uBCF4\uAC00 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
    monthly_auto: "\uC6D4\uAC04 (\uB9E4\uC6D4 \uC790\uB3D9 \uACB0\uC81C)",
    processing_message: "\uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824\uC8FC\uC138\uC694...",
    processing_title: "\uACB0\uC81C \uCC98\uB9AC \uC911",
    provisioning_failed_message: "\uAD6C\uB3C5 \uD65C\uC131\uD654 \uACFC\uC815\uC5D0\uC11C \uBB38\uC81C\uAC00 \uBC1C\uC0DD\uD574 \uC6B4\uC601\uD300\uC774 \uD655\uC778\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uACB0\uC81C\uB294 \uC815\uC0C1\uC801\uC73C\uB85C \uC644\uB8CC\uB418\uC5C8\uC73C\uB2C8 \uB2E4\uC2DC \uACB0\uC81C\uD558\uC9C0 \uC54A\uC73C\uC154\uB3C4 \uB429\uB2C8\uB2E4. \uBB38\uC758\uC0AC\uD56D\uC740 \uB3C4\uC6C0\uB9D0 > \uBB38\uC758\uD558\uAE30\uB85C \uC54C\uB824\uC8FC\uC138\uC694.",
    provisioning_failed_title: "\uAD6C\uB3C5 \uD65C\uC131\uD654 \uCC98\uB9AC\uAC00 \uC9C0\uC5F0\uB418\uACE0 \uC788\uC5B4\uC694. \uC7A0\uC2DC\uB9CC \uAE30\uB2E4\uB824\uC8FC\uC138\uC694.",
    registration_complete_message: "\uC815\uAE30\uACB0\uC81C\uAC00 \uC131\uACF5\uC801\uC73C\uB85C \uB4F1\uB85D\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    registration_complete_title: "\uC815\uAE30\uACB0\uC81C \uB4F1\uB85D \uC644\uB8CC",
    retry_button: "\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAE30",
    success_title: "\uACB0\uC81C \uC644\uB8CC!",
    view_payment_button: "\uACB0\uC81C \uC815\uBCF4 \uD655\uC778",
    yearly_auto: "\uC5F0\uAC04 (\uB9E4\uB144 \uC790\uB3D9 \uACB0\uC81C)"
  },
  purchase_slots: {
    button: "\uC2AC\uB86F \uAD6C\uB9E4",
    cancel_button: "\uCDE8\uC18C",
    confirm_button: "\uAD6C\uB9E4\uD558\uAE30",
    description: "\uC2AC\uB86F\uC744 \uBBF8\uB9AC \uAD6C\uB9E4\uD558\uBA74 \uCD94\uAC00 \uACB0\uC81C \uC5C6\uC774 \uBA64\uBC84\uB97C \uD65C\uC131\uD654\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
    estimated_amount: "\uC608\uC0C1 \uACB0\uC81C \uAE08\uC561",
    modal_title: "\uC2AC\uB86F \uAD6C\uB9E4",
    slot_count_label: "\uAD6C\uB9E4\uD560 \uC2AC\uB86F \uAC1C\uC218"
  },
  subscription: {
    alert: {
      at_risk_message: "\uACB0\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB4F1\uB85D\uB41C \uCE74\uB4DC\uC758 \uD55C\uB3C4\uB098 \uC0C1\uD0DC\uB97C \uD655\uC778\uD574\uC8FC\uC2DC\uACE0, \uBB38\uC81C\uAC00 \uACC4\uC18D\uB418\uBA74 \uB3C4\uC6C0\uB9D0 > \uBB38\uC758\uD558\uAE30\uB85C \uC54C\uB824\uC8FC\uC138\uC694.",
      at_risk_title: "\uACB0\uC81C \uBB38\uC81C \uBC1C\uC0DD",
      pending_cancellation_message: "{{endDate}}\uAE4C\uC9C0 \uC0AC\uC6A9 \uAC00\uB2A5\uD569\uB2C8\uB2E4.",
      pending_cancellation_title: "\uAD6C\uB3C5 \uCDE8\uC18C \uC608\uC815"
    },
    available_slots: "\uC0AC\uC6A9 \uAC00\uB2A5\uD55C \uC2AC\uB86F \uC218",
    billing_cycle: {
      label: "\uACB0\uC81C \uC8FC\uAE30",
      monthly: "\uC6D4\uAC04",
      yearly: "\uC5F0\uAC04"
    },
    cancel_button: "\uAD6C\uB3C5 \uCDE8\uC18C",
    card_company_placeholder: "\uCE74\uB4DC\uC0AC",
    description: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAD6C\uB3C5 \uBC0F \uACB0\uC81C\uB97C \uAD00\uB9AC\uD569\uB2C8\uB2E4.",
    members_unit: "\uAC1C",
    next_billing_date: "\uB2E4\uC74C \uACB0\uC81C\uC77C",
    no_payment_method: "\uB4F1\uB85D\uB41C \uACB0\uC81C \uC218\uB2E8 \uC5C6\uC74C",
    paid_members: "\uC0AC\uC6A9 \uC911\uC778 \uC2AC\uB86F \uC218",
    pay_button: "\uACB0\uC81C\uD558\uAE30",
    payment_method: "\uACB0\uC81C \uC218\uB2E8",
    reactivate_button: "\uC7AC\uAD6C\uB3C5\uD558\uAE30",
    recent_payments: "\uCD5C\uADFC \uACB0\uC81C \uB0B4\uC5ED",
    registered_card: "\uB4F1\uB85D\uB41C \uCE74\uB4DC",
    slots_unit: "\uAC1C",
    status: {
      active: "\uC815\uC0C1",
      at_risk: "\uACB0\uC81C \uBB38\uC81C",
      cancelled: "\uCDE8\uC18C\uB428",
      label: "\uAD6C\uB3C5 \uC0C1\uD0DC",
      pending_cancellation: "\uCDE8\uC18C \uC608\uC815"
    },
    title: "\uACB0\uC81C \uAD00\uB9AC",
    total_subscribed_slots: "\uAD6C\uB3C5 \uC911\uC778 \uC2AC\uB86F \uC218"
  },
  subscription_required: {
    app_confirm: "\uD655\uC778",
    app_description: "\uC774 \uAE30\uB2A5\uC740 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAD6C\uB3C5 \uC2DC \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD574\uC8FC\uC138\uC694.",
    app_title: "\uAD6C\uB3C5\uC774 \uD544\uC694\uD55C \uAE30\uB2A5\uC785\uB2C8\uB2E4",
    button: {
      cancel: "\uCDE8\uC18C",
      go_to_payment: "\uC720\uB8CC \uD50C\uB79C \uC0AC\uC6A9\uD558\uAE30"
    },
    description: {
      ai_question: "\uBA64\uBC84\uBCC4 \uB9DE\uCDA4 \uC9C8\uBB38\uC744 \uBE60\uB974\uACE0 \uD48D\uBD80\uD558\uAC8C \uB9CC\uB4E4\uC5B4 \uB4DC\uB9BD\uB2C8\uB2E4.",
      recording: "\uB179\uC74C \uAE30\uB2A5\uC744 \uD65C\uC6A9\uD558\uBA74 \uBBF8\uD305 \uC774\uD6C4 \uC9C8\uBB38\uBCC4 \uB2F5\uBCC0 \uC815\uB9AC, \uBBF8\uD305 \uC804\uCCB4 \uC694\uC57D, \uC561\uC158 \uC544\uC774\uD15C\uC744 \uC790\uB3D9\uC73C\uB85C \uB9CC\uB4E4\uC5B4 \uB4DC\uB9BD\uB2C8\uB2E4.",
      report: "1on1 \uBBF8\uD305 \uB0B4\uC5ED\uC744 \uC885\uD569\uD574 \uBA64\uBC84\uC758 \uB3D9\uAE30, \uAC15\uC810, \uC131\uC7A5 \uB4F1\uC758 \uB9AC\uD3EC\uD2B8\uB97C \uC790\uB3D9 \uC0DD\uC131\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      prep: "\uAC04\uB2E8\uD55C \uC11C\uBCA0\uC774\uC640 \uCC57\uBD07\uACFC\uC758 \uB300\uD654\uB97C \uD1B5\uD574 \uBBF8\uD305\uC5D0 \uD544\uC694\uD55C \uC815\uBCF4\uB97C \uC27D\uACE0 \uBA85\uD655\uD558\uAC8C \uB9AC\uB354\uC5D0\uAC8C \uC804\uB2EC\uD569\uB2C8\uB2E4."
    },
    feature: {
      ai_question: "AI \uC9C8\uBB38 \uC0DD\uC131",
      recording: "\uB179\uC74C \uD30C\uC77C \uC5C5\uB85C\uB4DC",
      report: "\uBA64\uBC84 \uB9AC\uD3EC\uD2B8 \uC0DD\uC131",
      prep: "\uD504\uB819"
    },
    title: "\uC720\uB8CC \uD50C\uB79C\uC5D0\uC11C \uC0AC\uC6A9\uD560 \uC218 \uC788\uC5B4\uC694!"
  },
  trial: {
    days_remaining: "\uCCB4\uD5D8 \uAE30\uAC04\uC774 {{days}}\uC77C \uB0A8\uC558\uC2B5\uB2C8\uB2E4.",
    end_date: "\uC885\uB8CC\uC77C: {{date}}",
    expired_message: "\uBB34\uB8CC \uCCB4\uD5D8 \uAE30\uAC04\uC774 \uC885\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4. AI \uAE30\uB2A5\uC744 \uACC4\uC18D \uC0AC\uC6A9\uD558\uC2DC\uB824\uBA74 \uAD6C\uB3C5\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.",
    expired_title: "\uCCB4\uD5D8 \uAE30\uAC04 \uC885\uB8CC",
    last_day: "\uC624\uB298\uC774 \uB9C8\uC9C0\uB9C9 \uB0A0\uC785\uB2C8\uB2E4!",
    title: "\uBB34\uB8CC \uCCB4\uD5D8 \uAE30\uAC04",
    warning: "\uCCB4\uD5D8 \uAE30\uAC04\uC774 \uC885\uB8CC\uB418\uBA74 AI \uAE30\uB2A5\uC744 \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
  },
  credit: {
    title: "\uBD84\uC11D \uAC00\uB2A5\uD55C \uBBF8\uD305",
    balance: "{{count}}\uD68C \uB0A8\uC558\uC2B5\uB2C8\uB2E4.",
    description: "\uBBF8\uD305 \uB179\uC74C\xB7AI \uBD84\uC11D 1\uD68C\uB2F9 1\uD68C\uC529 \uCC28\uAC10\uB429\uB2C8\uB2E4. \uACB0\uC81C\uD558\uBA74 \uBB34\uC81C\uD55C\uC73C\uB85C \uC774\uC6A9\uD560 \uC218 \uC788\uC5B4\uC694."
  }
};

// src/locales/ko-KR/aws_marketplace.json
var aws_marketplace_default2 = {
  landing: {
    page_title: "AWS Marketplace",
    page_subtitle: "Orblit for AWS",
    loading: {
      checking: "AWS Marketplace \uC815\uBCF4\uB97C \uD655\uC778\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4...",
      loading_workspaces: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uBD88\uB7EC\uC624\uB294 \uC911...",
      connecting: "AWS Marketplace \uAD6C\uB3C5\uC744 \uC5F0\uACB0\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4..."
    },
    auth_required: {
      title: "\uB85C\uADF8\uC778\uC774 \uD544\uC694\uD569\uB2C8\uB2E4",
      description: "AWS Marketplace \uAD6C\uB3C5\uC744 \uC5F0\uACB0\uD558\uB824\uBA74 \uBA3C\uC800 \uB85C\uADF8\uC778\uD574\uC8FC\uC138\uC694.",
      login_button: "\uB85C\uADF8\uC778",
      signup_button: "\uD68C\uC6D0\uAC00\uC785"
    },
    no_workspace: {
      title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
      description: "AWS Marketplace \uAD6C\uB3C5\uC744 \uC5F0\uACB0\uD558\uB824\uBA74 \uBA3C\uC800 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB97C \uC0DD\uC131\uD574\uC8FC\uC138\uC694.",
      instruction: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uD6C4 \uC774 \uD398\uC774\uC9C0\uB85C \uB2E4\uC2DC \uB3CC\uC544\uC624\uC138\uC694.",
      button: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131\uD558\uB7EC \uAC00\uAE30"
    },
    no_owner: {
      title: "\uC624\uB108 \uAD8C\uD55C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4",
      description: "AWS Marketplace \uAD6C\uB3C5\uC744 \uC5F0\uACB0\uD558\uB824\uBA74 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 \uC624\uB108 \uAD8C\uD55C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.",
      instruction: "\uBCF8\uC778\uC774 \uC0DD\uC131\uD55C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uAC00 \uC5C6\uB2E4\uBA74 \uC0C8\uB85C \uC0DD\uC131\uD574\uC8FC\uC138\uC694. \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uD6C4 \uC774 \uD398\uC774\uC9C0\uB85C \uB2E4\uC2DC \uB3CC\uC544\uC624\uC138\uC694.",
      button: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131\uD558\uB7EC \uAC00\uAE30"
    },
    already_subscribed: {
      title: "\uC774\uBBF8 \uAD6C\uB3C5 \uC911\uC785\uB2C8\uB2E4",
      description: "AWS Marketplace \uAD6C\uB3C5\uC740 \uC774\uBBF8 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uC5F0\uACB0\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.",
      go_home: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30"
    },
    success: {
      title: "\uAD6C\uB3C5 \uC5F0\uACB0 \uC644\uB8CC",
      description: "AWS Marketplace \uAD6C\uB3C5\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC5F0\uACB0\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      redirect_message: "\uC7A0\uC2DC \uD6C4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uB85C \uC774\uB3D9\uD569\uB2C8\uB2E4."
    },
    error: {
      title: "\uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4",
      no_token: "AWS Marketplace \uD1A0\uD070\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uC62C\uBC14\uB978 \uB9C1\uD06C\uB97C \uD1B5\uD574 \uC811\uC18D\uD574\uC8FC\uC138\uC694.",
      button: "\uD648\uC73C\uB85C \uB3CC\uC544\uAC00\uAE30"
    },
    notifications: {
      success_title: "\uAD6C\uB3C5 \uC5F0\uACB0 \uC644\uB8CC",
      success_message: "AWS Marketplace \uAD6C\uB3C5\uC774 \uC131\uACF5\uC801\uC73C\uB85C \uC5F0\uACB0\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
      error_title: "\uAD6C\uB3C5 \uC5F0\uACB0 \uC2E4\uD328",
      error_message: "AWS Marketplace \uAD6C\uB3C5 \uC5F0\uACB0\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
    }
  }
};

// src/locales/ko-KR/dashboard.json
var dashboard_default2 = {
  title: "\uB300\uC2DC\uBCF4\uB4DC",
  range_selector: {
    custom_label: "\uC9C1\uC811 \uC120\uD0DD",
    modal_title: "\uAE30\uAC04 \uC9C1\uC811 \uC120\uD0DD",
    date_range_label: "\uC2DC\uC791\uC77C ~ \uC885\uB8CC\uC77C",
    date_range_placeholder: "\uB0A0\uC9DC \uBC94\uC704\uB97C \uC120\uD0DD\uD558\uC138\uC694",
    cancel: "\uCDE8\uC18C",
    apply: "\uC801\uC6A9"
  },
  periods: {
    "1month": "\uC774\uBC88 \uB2EC",
    "3months": "\uCD5C\uADFC 3\uAC1C\uC6D4",
    "6months": "\uCD5C\uADFC 6\uAC1C\uC6D4",
    "1year": "\uCD5C\uADFC 1\uB144"
  },
  days_until: {
    passed: "\uC9C0\uB0A8",
    today: "\uC624\uB298",
    tomorrow: "\uB0B4\uC77C",
    days_later: "{{days}}\uC77C \uD6C4"
  },
  alerts: {
    title: "\uC0C1\uD0DC \uCCB4\uD06C",
    no_meeting: {
      title: "\uC7A5\uAE30 \uBBF8\uC2E4\uD589 (2\uAC1C\uC6D4+)",
      item_with_days: "{{leaderName}}(\uB9E4\uB2C8\uC800) - {{memberName}}, {{days}}\uC77C",
      item_no_history: "{{leaderName}}(\uB9E4\uB2C8\uC800) - {{memberName}}, \uBBF8\uD305 \uC774\uB825 \uC5C6\uC74C"
    },
    low_vital: {
      title: "\uBC14\uC774\uD0C8 \uB0AE\uC740 \uBA64\uBC84",
      item_with_leader: "{{memberName}} (\uB9E4\uB2C8\uC800: {{leaderName}})",
      item_no_leader: "{{memberName}} (\uD604\uC7AC \uB9E4\uB2C8\uC800 \uC5C6\uC74C)",
      tooltip: "\uC120\uD0DD\uB41C \uAE30\uAC04 \uB3D9\uC548\uC758 \uBC14\uC774\uD0C8(\uC5C5\uBB34\uBAB0\uC785\uB3C4, \uC131\uC7A5\uB9CC\uC871\uB3C4 \uD3C9\uADE0)\uC758 \uD3C9\uADE0\uAC12\uC774 3\uC810 \uBBF8\uB9CC\uC778 \uBA64\uBC84\uC785\uB2C8\uB2E4."
    },
    vital_drop: {
      title: "\uBC14\uC774\uD0C8 \uAE09\uB77D \uBA64\uBC84",
      item_with_leader: "{{memberName}} (\uB9E4\uB2C8\uC800: {{leaderName}})",
      item_no_leader: "{{memberName}} (\uD604\uC7AC \uB9E4\uB2C8\uC800 \uC5C6\uC74C)",
      tooltip: "\uC120\uD0DD\uB41C \uAE30\uAC04 \uB0B4 \uAC00\uC7A5 \uCD5C\uC2E0 \uBC14\uC774\uD0C8(\uC5C5\uBB34\uBAB0\uC785\uB3C4, \uC131\uC7A5\uB9CC\uC871\uB3C4 \uD3C9\uADE0) \uAC12\uC774 \uC9C1\uC804\uC5D0 \uBE44\uD574 2\uC810 \uC774\uC0C1 \uB5A8\uC5B4\uC9C4 \uBA64\uBC84\uC785\uB2C8\uB2E4."
    },
    count: "{{count}}\uAC74",
    view_all: "\uC804\uCCB4 \uBCF4\uAE30",
    full_list: "\uC804\uCCB4 \uBAA9\uB85D ({{count}}\uAC74)"
  },
  meeting_table: {
    title: "\uC804\uCCB4 1on1 \uBBF8\uD305 \uB9AC\uC2A4\uD2B8",
    tabs: {
      as_leader: "\uB9E4\uB2C8\uC800\uB85C\uC11C",
      as_member: "\uBA64\uBC84\uB85C\uC11C"
    },
    filter: {
      placeholder: "\uBBF8\uD305 \uC0C1\uB300 \uC120\uD0DD",
      all: "\uC804\uCCB4 \uC0C1\uB300",
      direct_only: "\uC9C1\uC18D \uAD00\uACC4\uB9CC"
    },
    columns: {
      counterpart: "\uC0C1\uB300",
      relationship: "\uAD00\uACC4",
      date: "\uB0A0\uC9DC",
      duration: "\uC18C\uC694\uC2DC\uAC04",
      action_items: "\uC561\uC158\uC544\uC774\uD15C",
      engagement: "\uC5C5\uBB34\uBAB0\uC785\uB3C4",
      growth: "\uC131\uC7A5\uB9CC\uC871\uB3C4",
      summary: "\uBBF8\uD305 \uC694\uC57D"
    },
    relationship: {
      direct: "\uC9C1\uC18D",
      other: "\uAE30\uD0C0"
    },
    duration_minutes: "{{minutes}}\uBD84",
    direct_filter_hint: "\uC9C1\uC18D \uAD00\uACC4\uB9CC \uAE30\uBCF8\uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4. \uC804\uCCB4 \uB370\uC774\uD130\uB97C \uBCF4\uB824\uBA74 \uD544\uD130\uB97C \uBCC0\uACBD\uD558\uC138\uC694."
  },
  hero_metrics: {
    execution_rate: {
      title: "1on1 \uC2E4\uD589\uB960",
      value: "{{value}}%",
      subtitle: "{{executed}}/{{total}}\uBA85"
    },
    average_cycle: {
      title: "\uD3C9\uADE0 \uBBF8\uD305 \uC8FC\uAE30",
      value_weeks: "{{weeks}}\uC8FC",
      target_weeks: "\uBAA9\uD45C {{weeks}}\uC8FC",
      no_target: "\uBAA9\uD45C \uC8FC\uAE30 \uBBF8\uC124\uC815",
      tooltip: "\uC9C1\uC18D \uB9E4\uB2C8\uC800-\uBA64\uBC84\uAC00 \uC124\uC815\uD55C \uBBF8\uD305 \uBAA9\uD45C \uC8FC\uAE30\uC758 \uD3C9\uADE0\uAC12\uC785\uB2C8\uB2E4."
    },
    completed_meetings: {
      title: "\uC644\uB8CC\uD55C \uBBF8\uD305",
      value_count: "{{count}}\uAC74",
      average_duration: "\uD3C9\uADE0 {{minutes}}\uBD84"
    },
    vital: {
      title: "\uBC14\uC774\uD0C8",
      value_score: "{{score}}/5.0",
      engagement_score: "\uC5C5\uBB34\uBAB0\uC785\uB3C4 {{score}}/5.0",
      growth_score: "\uC131\uC7A5\uB9CC\uC871\uB3C4 {{score}}/5.0"
    }
  },
  leader_insights: {
    title: "\uD300 \uCF00\uC5B4",
    direct_members_count: "\uB2F4\uB2F9 {{count}}\uBA85",
    management_status: {
      title: "\uAD00\uB9AC \uD604\uD669",
      target_cycle: {
        label: "\uBAA9\uD45C \uC8FC\uAE30 \uC124\uC815",
        count: "{{set}}/{{total}}\uBA85",
        not_set: "\uBBF8\uC124\uC815: {{names}}"
      },
      cycle_compliance: {
        label: "\uC8FC\uAE30 \uC900\uC218",
        count: "{{compliant}}/{{total}}\uBA85",
        delayed: "\uC9C0\uC5F0: {{items}}"
      },
      delayed_member: "{{name}}(+{{days}}\uC77C)",
      scheduled_meetings: {
        label: "\uBBF8\uD305 \uC608\uC815",
        count: "{{count}}\uBA85"
      },
      vital_stable: {
        label: "\uBC14\uC774\uD0C8 \uC548\uC815 (4\uC810 \uC774\uC0C1)",
        count: "{{stable}}/{{total}}\uBA85",
        improvable: "\uAC1C\uC120 \uAC00\uB2A5: {{items}}",
        need_attention: "\uC8FC\uC758 \uD544\uC694: {{items}}"
      },
      member_score: "{{name}}(\uD3C9\uADE0 {{score}}\uC810)"
    },
    care_quality: {
      title: "\uCF00\uC5B4 \uD488\uC9C8",
      avg_meeting_duration: "\uD3C9\uADE0 \uBBF8\uD305 \uC2DC\uAC04",
      duration_value: "{{minutes}}\uBD84",
      leader_action_items: "\uB9E4\uB2C8\uC800 \uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC\uC728",
      member_action_items: "\uB2F4\uB2F9 \uC778\uC6D0 \uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC\uC728",
      action_items_count: "{{completed}}/{{total}}",
      member_vital_avg: "\uB2F4\uB2F9 \uC778\uC6D0 \uBC14\uC774\uD0C8 \uD3C9\uADE0",
      vital_value: "{{score}}/5.0"
    }
  },
  stats_table: {
    title: "1on1 \uBBF8\uD305 \uD604\uD669",
    hint: "\uC9C1\uC18D \uB9E4\uB2C8\uC800\uC640 \uBA64\uBC84\uC758 1on1 \uBBF8\uD305 \uB370\uC774\uD130 \uD1B5\uACC4\uC785\uB2C8\uB2E4.",
    tabs: {
      by_leader: "\uB9E4\uB2C8\uC800\uBCC4 \uD604\uD669",
      by_member: "\uBA64\uBC84\uBCC4 \uD604\uD669"
    },
    columns: {
      leader: "\uB9E4\uB2C8\uC800",
      member: "\uBA64\uBC84",
      direct_leader: "\uC9C1\uC18D \uB9E4\uB2C8\uC800",
      direct_members: "\uB2F4\uB2F9 \uC778\uC6D0",
      completed_count: "\uC644\uB8CC \uAC74\uC218",
      last_meeting: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305\uC77C",
      avg_duration: "\uD3C9\uADE0 \uC2DC\uAC04",
      avg_cycle: "\uD3C9\uADE0 \uC8FC\uAE30",
      target_cycle: "\uBAA9\uD45C \uC8FC\uAE30",
      action_items: "\uC561\uC158\uC544\uC774\uD15C",
      engagement: "\uC5C5\uBB34\uBAB0\uC785\uB3C4",
      growth: "\uC131\uC7A5\uB9CC\uC871\uB3C4"
    },
    values: {
      count: "{{count}}\uAC74",
      minutes: "{{minutes}}\uBD84",
      weeks: "{{weeks}}\uC8FC",
      not_set: "\uBBF8\uC124\uC815",
      action_items_rate: "{{rate}}% ({{completed}} / {{total}})",
      overdue_notification: "\uBAA9\uD45C \uC8FC\uAE30\uB85C\uBD80\uD130 {{overdueDays}}\uC77C\uC774 \uC9C0\uB0AC\uC2B5\uB2C8\uB2E4.",
      cycle_notification: "1on1 \uC218\uD589 \uC8FC\uAE30 \uD3C9\uADE0\uC774 \uBAA9\uD45C \uC8FC\uAE30\uBCF4\uB2E4 \uAE41\uB2C8\uB2E4."
    }
  },
  meeting_count_chart: {
    title: "\uC6D4\uBCC4 1on1 \uC9C4\uD589 \uAC74\uC218",
    legend: {
      total: "\uC804\uCCB4",
      with_direct_leader: "\uC9C1\uC18D \uB9E4\uB2C8\uC800-\uBA64\uBC84"
    },
    placeholder: "\uD574\uB2F9 \uAE30\uAC04\uC5D0 \uC644\uB8CC\uB41C 1on1 \uBBF8\uD305\uC774 \uC5C6\uC2B5\uB2C8\uB2E4."
  },
  member_header: {
    direct_leader: "\uC9C1\uC18D \uB9E4\uB2C8\uC800",
    direct_members: "\uB2F4\uB2F9 \uC778\uC6D0"
  },
  member_insights: {
    title: "\uAC1C\uC778 \uC131\uC7A5",
    management_status: {
      title: "\uAD00\uB9AC \uD604\uD669",
      target_cycle: "\uBAA9\uD45C \uC8FC\uAE30",
      target_cycle_weeks: "{{weeks}}\uC8FC",
      not_set: "\uBBF8\uC124\uC815",
      last_meeting: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305",
      cycle_overdue: "\uC8FC\uAE30 \uCD08\uACFC",
      scheduled_meeting: "\uC608\uC815\uB41C \uBBF8\uD305"
    },
    recent_vital: {
      title: "\uCD5C\uADFC \uBC14\uC774\uD0C8",
      tooltip_line1: "\uAC00\uC7A5 \uCD5C\uADFC\uC758 \uC5C5\uBB34\uBAB0\uC785\uB3C4, \uC131\uC7A5\uB9CC\uC871\uB3C4 \uC810\uC218\uC785\uB2C8\uB2E4.",
      tooltip_line2: "5\uC810\uC774 \uB9CC\uC810\uC774\uBA70, 1on1 \uBBF8\uD305 \uD6C4 \uB9E4\uB2C8\uC800\uAC00 \uC785\uB825\uD569\uB2C8\uB2E4.",
      engagement: "\uC5C5\uBB34\uBAB0\uC785\uB3C4",
      growth: "\uC131\uC7A5\uB9CC\uC871\uB3C4",
      no_data: "\uB370\uC774\uD130 \uC5C6\uC74C",
      no_comparison: "(\uBE44\uAD50 \uB370\uC774\uD130 \uC5C6\uC74C)",
      previous: "(\uC9C1\uC804: {{score}})"
    },
    metrics: {
      avg_meeting_duration: "\uD3C9\uADE0 \uBBF8\uD305 \uC2DC\uAC04",
      duration_minutes: "{{minutes}}\uBD84",
      action_items_rate: "\uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC\uC728"
    }
  },
  member_overview: {
    title: "1on1 \uD604\uD669",
    tooltip_all: "{{name}}\uB2D8\uC774 \uC9C1\uC18D \uB9E4\uB2C8\uC800, \uB2F4\uB2F9 \uC778\uC6D0\uB4E4\uACFC \uC9C4\uD589\uD55C 1on1 \uBBF8\uD305 \uD604\uD669\uC785\uB2C8\uB2E4.",
    tooltip_leader: "{{name}}\uB2D8\uC774 \uC9C1\uC18D \uB9E4\uB2C8\uC800\uC640 \uC9C4\uD589\uD55C 1on1 \uBBF8\uD305 \uD604\uD669\uC785\uB2C8\uB2E4.",
    tooltip_members: "{{name}}\uB2D8\uC774 \uB2F4\uB2F9 \uC778\uC6D0\uB4E4\uACFC \uC9C4\uD589\uD55C 1on1 \uBBF8\uD305 \uD604\uD669\uC785\uB2C8\uB2E4.",
    completed_meetings: "\uC644\uB8CC\uD55C \uBBF8\uD305",
    meetings_with_members: "\uB2F4\uB2F9 \uC778\uC6D0\uACFC\uC758 \uBBF8\uD305",
    meetings_with_leader: "\uC9C1\uC18D \uB9E4\uB2C8\uC800\uC640\uC758 \uBBF8\uD305",
    avg_duration: "\uD3C9\uADE0 \uBBF8\uD305 \uC2DC\uAC04",
    count: "{{count}}\uAC74",
    duration_minutes: "{{minutes}}\uBD84"
  },
  vital_chart: {
    members_vital_title: "\uB2F4\uB2F9 \uC778\uC6D0 \uBC14\uC774\uD0C8 \uCD94\uC774",
    members_vital_description: "\uC9C1\uC18D \uB2F4\uB2F9 \uC778\uC6D0\uB4E4\uACFC\uC758 1on1 \uBBF8\uD305 \uD6C4 {{name}}\uB2D8\uC774 \uC751\uB2F5\uD55C \uC5C5\uBB34\uBAB0\uC785\uB3C4, \uC131\uC7A5\uB9CC\uC871\uB3C4 \uC810\uC218\uB97C \uC6D4\uBCC4\uB85C \uC9D1\uACC4\uD55C \uD3C9\uADE0\uAC12\uC785\uB2C8\uB2E4.",
    user_vital_title: "\uBCF8\uC778 \uBC14\uC774\uD0C8 \uCD94\uC774",
    user_vital_description: "\uC9C1\uC18D \uB9E4\uB2C8\uC800\uC640\uC758 1on1 \uBBF8\uD305 \uD6C4 \uB9E4\uB2C8\uC800\uAC00 \uC751\uB2F5\uD55C {{name}}\uB2D8\uC758 \uC5C5\uBB34\uBAB0\uC785\uB3C4, \uC131\uC7A5\uB9CC\uC871\uB3C4 \uC810\uC218\uB97C \uC6D4\uBCC4\uB85C \uC9D1\uACC4\uD55C \uD3C9\uADE0\uAC12\uC785\uB2C8\uB2E4.",
    vital_title: "\uBC14\uC774\uD0C8 \uCD94\uC774",
    legend: {
      engagement: "\uC5C5\uBB34\uBAB0\uC785\uB3C4",
      growth: "\uC131\uC7A5\uB9CC\uC871\uB3C4"
    }
  },
  error: {
    member_not_found: "\uBA64\uBC84\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.",
    member_not_found_message: "\uD604\uC7AC \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC5D0 \uC18D\uD558\uC9C0 \uC54A\uC740 \uBA64\uBC84\uC785\uB2C8\uB2E4."
  },
  excel_download: {
    button: "Excel \uB2E4\uC6B4\uB85C\uB4DC",
    modal_title: "Excel \uB2E4\uC6B4\uB85C\uB4DC",
    period_label: "\uAE30\uAC04 \uC124\uC815",
    team_label: "\uC870\uC9C1/\uBD80\uC11C \uC124\uC815",
    data_select_label: "\uCD94\uCD9C \uB370\uC774\uD130 \uC120\uD0DD",
    sheet_manager: "\uB9E4\uB2C8\uC800\uBCC4 1on1 \uD604\uD669",
    sheet_member: "\uBA64\uBC84\uBCC4 1on1 \uD604\uD669",
    sheet_meeting_log: "\uC804\uCCB4 1on1 \uBBF8\uD305 \uB85C\uADF8",
    include_summary: "\uBBF8\uD305 \uC694\uC57D \uD3EC\uD568\uD558\uAE30",
    include_summary_desc: "AI \uBBF8\uD305 \uC694\uC57D\uC774 \uD3EC\uD568\uB429\uB2C8\uB2E4. \uBBFC\uAC10\uD55C \uC815\uBCF4\uAC00 \uD3EC\uD568\uB420 \uC218 \uC788\uC73C\uB2C8 \uCDE8\uAE09\uC5D0 \uC8FC\uC758\uD558\uC138\uC694.",
    download: "\uB2E4\uC6B4\uB85C\uB4DC",
    error: "Excel \uD30C\uC77C \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694."
  },
  hr: {
    title: "1on1 \uD604\uD669",
    excel_button: "\uC5D1\uC140",
    full_excel_button: "\uC804\uCCB4 \uC5D1\uC140",
    subhead: "\uB9AC\uB354 {{leaders}}\uBA85 \xB7 {{period}} \xB7 {{scope}} \uAE30\uC900",
    scope: {
      direct: "\uC9C1\uC18D \uAD00\uACC4",
      all: "\uC804\uCCB4 \uBBF8\uD305",
      tooltip: "\uC9C1\uC18D \uAD00\uACC4: \uC870\uC9C1\uB3C4\uC5D0\uC11C \uB9E4\uB2C8\uC800-\uBA64\uBC84\uB85C \uC5F0\uACB0\uB41C 1on1\uB9CC \uC9D1\uACC4\uD569\uB2C8\uB2E4.\n\uC804\uCCB4 \uBBF8\uD305: \uC870\uC9C1\uB3C4\uC640 \uBB34\uAD00\uD558\uAC8C \uC9C4\uD589\uB41C 1on1\uAE4C\uC9C0 \uBAA8\uB450 \uC9D1\uACC4\uD569\uB2C8\uB2E4."
    },
    units: {
      count: "{{value}}\uD68C",
      people: "{{value}}\uBA85",
      minutes: "{{minutes}}\uBD84",
      hours_minutes: "{{hours}}\uC2DC\uAC04 {{minutes}}\uBD84",
      weeks: "{{weeks}}\uC8FC",
      percent: "{{value}}%",
      days_ago: "{{days}}\uC77C \uC804",
      today: "\uC624\uB298",
      no_meeting: "\uBBF8\uD305 \uC5C6\uC74C"
    },
    activation: {
      meeting_count: "\uCD1D \uC9C4\uD589 \uD69F\uC218",
      total_duration: "\uCD1D \uC9C4\uD589 \uC2DC\uAC04",
      execution_rate: "\uC2E4\uD589\uB960",
      execution_hint: "{{total}}\uBA85 \uC911 {{executed}}\uBA85\uC774 1\uD68C \uC774\uC0C1"
    },
    quality: {
      pre_question_rate: "\uB9AC\uB354\uC758 \uC0AC\uC804 \uC9C8\uBB38 \uB4F1\uB85D\uB960",
      prep_rate: "\uBA64\uBC84\uC758 \uD504\uB819 \uC791\uC131\uB960",
      action_item_rate: "\uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC\uC728",
      avg_duration: "\uD68C\uCC28\uBCC4 \uD3C9\uADE0 \uC2DC\uAC04",
      duration_hint: "\uC2DC\uAC04 \uAE30\uB85D {{value}}\uD68C \uAE30\uC900"
    },
    leader_list: {
      description: "\uAC12\uC758 \uD06C\uAE30\uC640 \uC870\uC9C1 \uAE30\uC900\uAC12(\uC911\uC559\uAC12 \uB610\uB294 \uD3C9\uADE0) \uB300\uBE44 \uC704\uCE58\uB9CC \uBCF4\uC5EC\uC90D\uB2C8\uB2E4. \uC6B0\uC218\xB7\uBBF8\uD761 \uD310\uC815, \uB4F1\uAE09, \uC21C\uC704\uB294 \uD45C\uC2DC\uD558\uC9C0 \uC54A\uC544\uC694. \uD589\uC744 \uB204\uB974\uBA74 \uBA64\uBC84\uBCC4\uB85C \uC5F4\uB9BD\uB2C8\uB2E4.",
      sort: {
        coverage: "\uBBF8\uD305\uD55C \uBA64\uBC84 \uC801\uC740 \uC21C",
        last: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305 \uC624\uB798\uB41C \uC21C",
        cycle: "\uC8FC\uAE30 \uAE34 \uC21C",
        perMember: "\uBA64\uBC84\uB2F9 \uD3C9\uADE0 \uD69F\uC218 \uC801\uC740 \uC21C",
        name: "\uC774\uB984\uC21C"
      },
      zero_filter: "\uBBF8\uD305 0\uAC74\uB9CC ({{count}}\uBA85)",
      trend_column: "\uCD94\uC774 \xB7 \uCD5C\uADFC {{count}}\uAC1C\uC6D4",
      trend_sub: "\uB2F4\uB2F9 1\uBA85\uB2F9 \uC6D4 \uC9C4\uD589 \uD69F\uC218",
      coverage_sub: "\uB2F4\uB2F9 \uC911 \uBE44\uC728",
      median: "\uC911\uC559\uAC12 {{value}}",
      average: "\uD3C9\uADE0 {{value}}",
      basis: {
        median: "\uC911\uC559\uAC12",
        average: "\uD3C9\uADE0"
      },
      member_count: "\uB2F4\uB2F9 {{count}}\uBA85",
      member_count_all: "\uC0C1\uB300 {{count}}\uBA85",
      empty: {
        not_computable: "\uC0B0\uCD9C \uBD88\uAC00",
        no_meetings: "\uBBF8\uD305 0\uAC74",
        no_value: "\uAC12 \uC5C6\uC74C"
      },
      legend: {
        bar: "\uAC12",
        median: "\uC804\uC0AC \uC911\uC559\uAC12 (\uC0B0\uCD9C \uAC00\uB2A5\uD55C \uB9AC\uB354\uB9CC)",
        average: "\uC804\uC0AC \uD3C9\uADE0 (\uC0B0\uCD9C \uAC00\uB2A5\uD55C \uB9AC\uB354\uB9CC)",
        pips: "\uBBF8\uD305\uD55C \uBA64\uBC84 / \uB2F4\uB2F9 \uBA64\uBC84",
        showing: "\uB9AC\uB354 {{count}}\uBA85 \uD45C\uC2DC \uC911 \xB7 \uBBF8\uD305 0\uAC74\uC778 \uB9AC\uB354\uB3C4 \uBAA9\uB85D\uC5D0 \uB0A8\uC2B5\uB2C8\uB2E4"
      },
      zero_row: "\uAE30\uAC04 \uB0B4 \uBBF8\uD305 0\uAC74 \xB7 \uB9C8\uC9C0\uB9C9 \uBBF8\uD305 {{last}}",
      zero_row_never: "\uAE30\uAC04 \uB0B4 \uBBF8\uD305 0\uAC74 \xB7 \uB9C8\uC9C0\uB9C9 \uBBF8\uD305 \uC5C6\uC74C",
      team_empty: "\uC120\uD0DD\uD55C \uD300\uC5D0 \uD45C\uC2DC\uD560 \uB9AC\uB354\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4"
    },
    leader_stats: {
      title: "\uB9AC\uB354\uBCC4 \uC2E4\uD589 \uD604\uD669",
      column: {
        leader: "\uB9AC\uB354",
        meeting_count: "\uC9C4\uD589 \uD69F\uC218",
        total_count: "\uCD1D \uD69F\uC218",
        total_duration: "\uCD1D \uC9C4\uD589 \uC2DC\uAC04",
        avg_duration: "\uD68C\uCC28\uBCC4 \uD3C9\uADE0 \uC2DC\uAC04",
        coverage: "\uBBF8\uD305\uD55C \uBA64\uBC84",
        per_member: "\uBA64\uBC84\uB2F9 \uD3C9\uADE0 \uD69F\uC218",
        avg_cycle: "\uD3C9\uADE0 \uC8FC\uAE30",
        last_meeting: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305",
        action_item: "\uC561\uC158\uC544\uC774\uD15C"
      }
    },
    leader_detail: {
      quality_title: "{{name}} \xB7 1on1 \uBC00\uB3C4",
      team_title: "\uB2F4\uB2F9 \uBA64\uBC84\uBCC4 \xB7 {{count}}\uBA85",
      excel_button: "\uC774 \uB9AC\uB354 \uC0C1\uC138 \uC5D1\uC140",
      pre_question_rate: "\uC0AC\uC804 \uC9C8\uBB38 \uB4F1\uB85D\uB960",
      column_monthly: "\uC6D4\uBCC4",
      column: {
        member: "\uBA64\uBC84",
        target_cycle: "\uBAA9\uD45C \uC8FC\uAE30",
        last_meeting: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305",
        prep_rate: "\uD504\uB819 \uC791\uC131\uB960"
      },
      heat_caption: "\uC6D4\uBCC4 \uCE78\uC740 \uC67C\uCABD\uC774 \uC624\uB798\uB41C \uB2EC\uC785\uB2C8\uB2E4 \xB7 {{first}} \u2192 {{last}} \xB7 \uC9C4\uD558\uAE30\uB294 \uADF8 \uB2EC\uC758 \uC9C4\uD589 \uD69F\uC218",
      heat_tooltip: "{{month}}\uC5D0 {{count}}\uD68C \uC9C4\uD589",
      meeting_list_button: "\uBBF8\uD305 \uB0B4\uC5ED {{count}}\uAC74"
    },
    meeting_list: {
      title: "\uBBF8\uD305 \uB0B4\uC5ED",
      count: "\uAE30\uAC04 \uB0B4 \uC644\uB8CC \uBBF8\uD305 {{count}}\uAC74",
      count_person: "{{name}} \uCC38\uC5EC \uBBF8\uD305 {{count}}\uAC74",
      subtitle: "{{period}} \xB7 {{scope}} \uAE30\uC900 \xB7 \uC0C1\uB2E8 \uAE30\uAC04\xB7\uC9D1\uACC4 \uAE30\uC900\uC744 \uB530\uB985\uB2C8\uB2E4",
      search_placeholder: "\uB9AC\uB354\xB7\uBA64\uBC84 \uC774\uB984 \uAC80\uC0C9",
      search_empty: "\uC77C\uCE58\uD558\uB294 \uC0AC\uB78C\uC774 \uC5C6\uC5B4\uC694",
      role: {
        leader: "\uB9AC\uB354",
        member: "\uBA64\uBC84",
        both: "\uB9AC\uB354\xB7\uBA64\uBC84"
      },
      person_summary: "\uB9AC\uB354\uB85C {{leader}}\uAC74 \xB7 \uBA64\uBC84\uB85C {{member}}\uAC74",
      column: {
        date: "\uB0A0\uC9DC",
        leader: "\uB9AC\uB354",
        member: "\uBA64\uBC84",
        relation: "\uAD00\uACC4",
        recording: "\uB179\uC74C",
        duration: "\uC18C\uC694\uC2DC\uAC04"
      },
      relation_other: "\uAE30\uD0C0",
      recorded: "\uB179\uC74C",
      recording_count: "\uB179\uC74C {{count}}\uAC74",
      recording_filter: {
        all: "\uC804\uCCB4",
        with: "\uB179\uC74C \uC788\uC74C",
        without: "\uB179\uC74C \uC5C6\uC74C"
      },
      group: {
        week: "{{month}}\uC6D4 {{week}}\uC8FC",
        month: "{{month}}\uC6D4",
        meta: "{{count}}\uAC74 \xB7 \uB9AC\uB354 {{leaders}}\uBA85 \xB7 {{duration}}"
      },
      legend: {
        relation_other: "\uC9C1\uC18D \uAD00\uACC4\uAC00 \uC544\uB2CC \uBBF8\uD305\uB9CC \u2018\uAE30\uD0C0\u2019\uB85C \uD45C\uC2DC\uD569\uB2C8\uB2E4",
        relation_direct: "\uC9C1\uC18D \uAD00\uACC4 \uAE30\uC900\uC774\uB77C \uAD00\uACC4 \uD45C\uC2DC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4",
        recording: "\uB179\uC74C\uC774 \uC5C6\uB294 \uBBF8\uD305\uC5D0\uB294 \uC544\uBB34\uAC83\uB3C4 \uD45C\uC2DC\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4",
        duration: "\uC18C\uC694\uC2DC\uAC04 \u2018\u2014\u2019\uB294 \uAE30\uB85D\uC774 \uC5C6\uB294 \uBBF8\uD305\uC785\uB2C8\uB2E4"
      },
      shown: "{{shown}} / {{total}}\uAC74",
      load_more: "\uB354 \uBCF4\uAE30",
      empty: {
        title: "\uC774 \uC870\uAC74\uC5D0 \uD574\uB2F9\uD558\uB294 \uBBF8\uD305\uC774 \uC5C6\uC5B4\uC694",
        body: "{{period}}\uC5D0 \uC644\uB8CC\uB41C 1on1\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uAE30\uAC04\uC744 \uB113\uD788\uAC70\uB098 \uC0C1\uB2E8 \uC9D1\uACC4 \uAE30\uC900\uC744 \u2018\uC804\uCCB4 \uBBF8\uD305\u2019\uC73C\uB85C \uBC14\uAFD4 \uBCF4\uC138\uC694.",
        body_person: "{{name}} \uB2D8\uC774 \uCC38\uC5EC\uD55C \uC644\uB8CC \uBBF8\uD305\uC774 {{period}}\uC5D0 \uC5C6\uC2B5\uB2C8\uB2E4. \uAE30\uAC04\uC744 \uB113\uD788\uAC70\uB098 \uCC38\uC5EC\uC790 \uD544\uD130\uB97C \uD574\uC81C\uD574 \uBCF4\uC138\uC694.",
        clear_filter: "\uCC38\uC5EC\uC790 \uD544\uD130 \uD574\uC81C",
        clear_recording: "\uB179\uC74C \uD544\uD130 \uD574\uC81C",
        widen_period: "\uAE30\uAC04 12\uAC1C\uC6D4\uB85C \uB113\uD788\uAE30"
      }
    },
    empty_state: {
      badge: "\uB370\uC774\uD130 \uC5C6\uC74C",
      title: "\uC544\uC9C1 \uC9D1\uACC4\uD560 1on1\uC774 \uC5C6\uC5B4\uC694",
      description: "\uB9AC\uB354-\uBA64\uBC84 \uAD00\uACC4\uAC00 \uC5F0\uACB0\uB418\uACE0 \uCCAB 1on1\uC774 \uC644\uB8CC\uB418\uBA74 \uC774 \uD654\uBA74\uC774 \uCC44\uC6CC\uC9D1\uB2C8\uB2E4. \uC9C0\uAE08 \uD544\uC694\uD55C \uAC74 \uB450 \uAC00\uC9C0\uC608\uC694.",
      step1_title: "\uBA64\uBC84\uC758 \uC9C1\uC18D \uB9AC\uB354 \uC9C0\uC815",
      step1_progress: "\uD604\uC7AC {{total}}\uBA85 \uC911 {{assigned}}\uBA85\uC5D0\uAC8C \uB9AC\uB354\uAC00 \uC9C0\uC815\uB3FC \uC788\uC5B4\uC694",
      step1_link: "\uC124\uC815\uC73C\uB85C",
      step2_title: "\uCCAB 1on1 \uC644\uB8CC",
      step2_sub: "\uC644\uB8CC\uB41C \uBBF8\uD305\uBD80\uD130 \uC9D1\uACC4\uB429\uB2C8\uB2E4",
      step2_link: "1on1 \uB9CC\uB4E4\uAE30",
      preview_title: "\uCC44\uC6CC\uC9C0\uBA74 \uC774\uB807\uAC8C \uBCF4\uC5EC\uC694"
    },
    excel: {
      sheet: {
        activation: "\uC804\uC0AC\uD65C\uC131\uD654",
        leader_stats: "\uB9AC\uB354\uBCC4\uD604\uD669",
        leader_detail: "\uBA64\uBC84\uBCC4\uD604\uD669",
        leader_pairs: "\uB9AC\uB354\uBCC4\uBA64\uBC84\uD604\uD669",
        meetings: "\uBBF8\uD305\uB0B4\uC5ED"
      },
      file: {
        leader_stats: "\uB9AC\uB354\uBCC4\uD604\uD669",
        leader_detail: "\uBA64\uBC84\uBCC4\uD604\uD669",
        meetings: "\uBBF8\uD305\uB0B4\uC5ED",
        full: "1on1\uD604\uD669"
      },
      value: {
        yes: "Y",
        no: "N"
      },
      column: {
        month: "\uC6D4",
        meeting_count: "\uC9C4\uD589 \uD69F\uC218 (\uD68C)",
        total_duration: "\uCD1D \uC9C4\uD589 \uC2DC\uAC04 (\uBD84)",
        total: "\uD569\uACC4",
        scope: "\uC9D1\uACC4 \uAE30\uC900",
        execution_rate: "\uC2E4\uD589\uB960 (\uCC38\uC5EC/\uC804\uCCB4)",
        leader_name: "\uB9AC\uB354\uBA85",
        leader_email: "\uB9AC\uB354 \uC774\uBA54\uC77C",
        org_leader: "\uC870\uC9C1\uB3C4 \uB9AC\uB354",
        avg_duration: "\uD68C\uCC28\uBCC4 \uD3C9\uADE0 \uC2DC\uAC04 (\uBD84)",
        member_count: "\uB2F4\uB2F9 \uBA64\uBC84 \uC218 (\uBA85)",
        met_member_count: "\uBBF8\uD305\uD55C \uBA64\uBC84 \uC218 (\uBA85)",
        coverage: "\uCEE4\uBC84\uB9AC\uC9C0 (%)",
        per_member: "\uBA64\uBC84\uB2F9 \uD3C9\uADE0 \uD69F\uC218 (\uD68C)",
        avg_cycle: "\uD3C9\uADE0 \uC8FC\uAE30 (\uC8FC)",
        target_cycle: "\uBAA9\uD45C \uC8FC\uAE30 (\uC8FC)",
        last_meeting_date: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305\uC77C",
        days_since: "\uB9C8\uC9C0\uB9C9 \uBBF8\uD305 \uACBD\uACFC\uC77C (\uC77C)",
        pre_question_rate: "\uC0AC\uC804 \uC9C8\uBB38 \uB4F1\uB85D\uB960 (%)",
        action_item_done: "\uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC (\uAC74)",
        action_item_total: "\uC561\uC158\uC544\uC774\uD15C \uC804\uCCB4 (\uAC74)",
        action_item_rate: "\uC561\uC158\uC544\uC774\uD15C \uC644\uB8CC\uC728 (%)",
        member_name: "\uBA64\uBC84\uBA85",
        member_email: "\uBA64\uBC84 \uC774\uBA54\uC77C",
        direct_relation: "\uC9C1\uC18D \uAD00\uACC4",
        date: "\uC9C4\uD589 \uB0A0\uC9DC",
        recording: "\uB179\uC74C \uC5EC\uBD80",
        duration: "\uC18C\uC694\uC2DC\uAC04 (\uBD84)",
        prep_rate: "\uD504\uB819 \uC791\uC131\uB960 (%)"
      }
    }
  }
};

// src/locales/ko-KR/profile_card.json
var profile_card_default2 = {
  communicationStyle: {
    category: "\uC120\uD638 \uC18C\uD1B5 \uBC29\uC2DD",
    question: "\uB9AC\uB354\uC640 \uC5C5\uBB34\uC5D0 \uB300\uD55C \uC774\uC57C\uAE30\uB97C \uB098\uB20C \uB54C, \uAC00\uC7A5 \uBAB0\uC785\uC774 \uC798 \uB418\uB294 \uBC29\uC2DD\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
    SUMMARY: {
      label: "\uD575\uC2EC \uC694\uC57D\uD615",
      description: "\uACB0\uB860\uC801\uC73C\uB85C \uC774\uBC88 \uC5C5\uBB34\uC758 \uD575\uC2EC\uC740 \uC774\uAC83\uC774\uACE0, \uC6B0\uB9AC\uB294 \uC6B0\uC120 \uC5EC\uAE30\uC5D0 \uC9D1\uC911\uD560 \uAC70\uC608\uC694."
    },
    CONTEXT: {
      label: "\uC0C1\uC138 \uB9E5\uB77D\uD615",
      description: "\uC774 \uC5C5\uBB34\uC758 \uBC30\uACBD\uACFC \uD788\uC2A4\uD1A0\uB9AC\uB97C \uBA3C\uC800 \uCC28\uADFC\uCC28\uADFC \uC124\uBA85\uD574 \uB4DC\uB9B4\uAC8C\uC694."
    },
    DATA: {
      label: "\uB370\uC774\uD130 \uADFC\uAC70\uD615",
      description: "\uB370\uC774\uD130\uC640 \uB808\uD37C\uB7F0\uC2A4 \uC790\uB8CC\uB97C \uBCF4\uBA74\uC11C, \uC5C5\uBB34\uC758 \uBC29\uD5A5\uC131\uC744 \uAC19\uC774 \uD655\uC778\uD574 \uBCFC\uAC8C\uC694."
    },
    DISCUSSION: {
      label: "\uB300\uD654 \uD1A0\uB860\uD615",
      description: "\uC774 \uC5C5\uBB34\uC5D0 \uB300\uD574 \uC5B4\uB5BB\uAC8C \uC0DD\uAC01\uD558\uC138\uC694? \uB300\uD654 \uB098\uB204\uBA74\uC11C \uC2E4\uD589 \uACC4\uD68D\uC744 \uAC19\uC774 \uC138\uC6CC\uBD10\uC694."
    }
  },
  feedbackStyle: {
    category: "\uD6A8\uACFC\uC801 \uD53C\uB4DC\uBC31",
    question: "\uB9AC\uB354\uC5D0\uAC8C \uD53C\uB4DC\uBC31\uC744 \uBC1B\uC744 \uB54C, \uB098\uC5D0\uAC8C \uAC00\uC7A5 \uD6A8\uACFC\uC801\uC778 \uC2A4\uD0C0\uC77C\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
    DIRECT: {
      label: "\uC9C1\uC811\uC801 \uAC1C\uC120",
      description: "A \uBD80\uBD84\uC740 \uB17C\uB9AC\uAC00 \uBD80\uC871\uD574 \uBCF4\uC785\uB2C8\uB2E4. \uC774 \uBC29\uC2DD\uC73C\uB85C \uC218\uC815\uC774 \uD544\uC694\uD574\uC694."
    },
    POSITIVE: {
      label: "\uAE0D\uC815\uC801 \uAC15\uD654",
      description: "\uC804\uBC18\uC801\uC778 \uD750\uB984\uC740 \uC815\uB9D0 \uC88B\uC2B5\uB2C8\uB2E4. \uC774 \uBD80\uBD84\uB9CC \uC870\uAE08 \uB354 \uBCF4\uC644\uD558\uBA74 \uC88B\uACA0\uC5B4\uC694."
    },
    SOLUTION: {
      label: "\uC194\uB8E8\uC158 \uC81C\uC2DC",
      description: "\uC5EC\uAE30\uAC00 \uBB38\uC81C\uC778 \uB4EF \uD558\uB124\uC694. \uC774\uB7F0 \uBC29\uBC95\uB4E4\uC774 \uC788\uC744 \uAC83 \uAC19\uC740\uB370, \uB300\uC548\uC744 \uCC3E\uC544\uBCFC\uAE4C\uC694?"
    },
    SELF_REFLECTION: {
      label: "\uC790\uC728\uC801 \uC131\uCC30",
      description: "\uC774\uC5D0 \uB300\uD574 \uC5B4\uB5BB\uAC8C \uC0DD\uAC01\uD558\uB098\uC694? \uAC1C\uC120\uD558\uB824\uBA74 \uBB34\uC5C7\uC744 \uB2E4\uB974\uAC8C \uD574\uBCFC \uC218 \uC788\uC744\uAE4C\uC694?"
    }
  },
  careerOrientation: {
    category: "\uC9C0\uD5A5 \uCEE4\uB9AC\uC5B4",
    question: "\uC55E\uC73C\uB85C \uD68C\uC0AC\uC5D0\uC11C \uC5B4\uB5A4 \uBAA8\uC2B5\uC73C\uB85C \uC131\uC7A5\uD558\uACE0 \uC2F6\uC73C\uC2E0\uAC00\uC694?",
    SPECIALIST: {
      label: "\uC804\uBB38\uAC00",
      description: "\uD2B9\uC815 \uAE30\uC220\uC774\uB098 \uC9C1\uBB34\uC5D0\uC11C \uAE4A\uC774 \uC788\uB294 \uC804\uBB38\uC131\uC744 \uAC16\uCD94\uACE0 \uC2F6\uC5B4\uC694."
    },
    LEADER: {
      label: "\uB9AC\uB354",
      description: "\uD300\uC6D0\uB4E4\uACFC \uD568\uAED8 \uC131\uACFC\uB97C \uB0B4\uACE0, \uADF8\uB4E4\uC744 \uC131\uC7A5\uC2DC\uD0A4\uB294 \uB9E4\uB2C8\uC9D5 \uC5ED\uB7C9\uC744 \uD0A4\uC6B0\uACE0 \uC2F6\uC5B4\uC694."
    },
    GENERALIST: {
      label: "\uC870\uC728\uC790",
      description: "\uC5EC\uB7EC \uC601\uC5ED\uC744 \uC5F0\uACB0\uD558\uACE0 \uC870\uC728\uD574\uC11C \uBCF5\uC7A1\uD55C \uBB38\uC81C\uB97C \uD574\uACB0\uD558\uB294 \uC5ED\uD560\uC744 \uB9E1\uACE0 \uC2F6\uC5B4\uC694."
    },
    EXPLORER: {
      label: "\uAC1C\uCC99\uC790",
      description: "\uC0C8\uB85C\uC6B4 \uAE30\uC220\uC744 \uB3C4\uC785\uD558\uAC70\uB098, \uBE44\uC988\uB2C8\uC2A4 \uBAA8\uB378\uC744 \uCC98\uC74C\uBD80\uD130 \uB9CC\uB4E4\uC5B4\uBCF4\uACE0 \uC2F6\uC5B4\uC694."
    }
  },
  motivation: {
    category: "\uB3D9\uAE30\uBD80\uC5EC \uC694\uC18C",
    question: "\uB098\uC5D0\uAC8C \uAC00\uC7A5 \uB3D9\uAE30\uBD80\uC5EC\uB97C \uC8FC\uB294 \uC21C\uAC04\uC740 \uC5B8\uC81C\uC778\uAC00\uC694?",
    GROWTH: {
      label: "\uBC30\uC6C0\uACFC \uC131\uC7A5",
      description: "\uC0C8\uB85C\uC6B4 \uAC83\uC744 \uC54C\uAC8C \uB418\uACE0, \uC81C \uC2E4\uB825\uC774 \uB192\uC544\uC84C\uB2E4\uACE0 \uB290\uB084 \uB54C \uC990\uAC70\uC6CC\uC694."
    },
    AUTONOMY: {
      label: "\uAD8C\uD55C\uACFC \uC790\uC728\uC131",
      description: "\uC800\uB97C \uBBFF\uACE0 \uC804\uC801\uC73C\uB85C \uB9E1\uACA8\uC8FC\uC5C8\uC744 \uB54C \uCC45\uC784\uAC10\uACFC \uC5F4\uC815\uC774 \uC0DD\uACA8\uC694."
    },
    RECOGNITION: {
      label: "\uC778\uC815 \uBC0F \uD3C9\uD310",
      description: "\uB3D9\uB8CC\uC640 \uB9AC\uB354\uB85C\uBD80\uD130 \uC800\uC758 \uAE30\uC5EC\uB3C4\uC640 \uC5ED\uB7C9\uC744 \uC778\uC815\uBC1B\uC558\uC744 \uB54C \uBFCC\uB4EF\uD574\uC694."
    },
    IMPACT: {
      label: "\uC758\uBBF8\uC640 \uC601\uD5A5\uB825",
      description: "\uC800\uC758 \uC77C\uC774 \uC2E4\uC81C\uB85C \uACE0\uAC1D\uC5D0\uAC8C \uB3C4\uC6C0\uC774 \uB418\uAC70\uB098 \uD68C\uC0AC\uC5D0 \uAE30\uC5EC\uD588\uC744 \uB54C \uBCF4\uB78C\uC744 \uB290\uAEF4\uC694."
    }
  },
  create_page: {
    title: "\uD504\uB85C\uD544 \uCE74\uB4DC \uB9CC\uB4E4\uAE30",
    description: "\uAC04\uB2E8\uD55C \uC9C8\uBB38\uC5D0 \uB2F5\uBCC0\uD558\uC5EC \uB098\uB9CC\uC758 \uD504\uB85C\uD544 \uCE74\uB4DC\uB97C \uC644\uC131\uD558\uC138\uC694",
    progress: "\uC9C8\uBB38 {{currentStep}} / {{allStep}}",
    save_and_leave: "\uC800\uC7A5 \uD6C4 \uB098\uAC00\uAE30"
  },
  no_profile_card: {
    title: "\uC544\uC9C1 \uD504\uB85C\uD544 \uCE74\uB4DC\uAC00 \uC124\uC815\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uC124\uC815\uC5D0\uB294 1\uBD84\uB3C4 \uAC78\uB9AC\uC9C0 \uC54A\uC544\uC694!",
    title_mobile: "\uC544\uC9C1 \uD504\uB85C\uD544 \uCE74\uB4DC\uAC00 \uC124\uC815\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.\n\uC124\uC815\uC5D0\uB294 1\uBD84\uB3C4 \uAC78\uB9AC\uC9C0 \uC54A\uC544\uC694!",
    create: "\uB9CC\uB4E4\uAE30"
  },
  error: {
    update: {
      title: "\uD504\uB85C\uD544 \uCE74\uB4DC \uC800\uC7A5 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      message: "\uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694. \uBB38\uC81C\uAC00 \uBC18\uBCF5\uB418\uBA74 \uACE0\uAC1D\uC13C\uD130\uB85C \uC54C\uB824\uC8FC\uC138\uC694."
    }
  },
  meeting_prep: {
    collapsed: "{{name}}\uB2D8\uC774 \uC120\uD638\uD558\uB294 \uB300\uD654 \uBC29\uC2DD\uACFC \uC55E\uC73C\uB85C\uC758 \uC131\uC7A5 \uBC29\uD5A5\uC744 \uCC38\uACE0\uD558\uACE0 \uC788\uC5B4\uC694.",
    expand: "\uC790\uC138\uD788 \uBCF4\uAE30",
    collapse: "\uC811\uAE30",
    empty_collapsed: "\uD504\uB85C\uD544\uC744 \uC124\uC815\uD558\uBA74 \uCC57\uBD07\uC774 \uB098\uC5D0\uAC8C \uB9DE\uCDB0 1on1\uC5D0\uC11C \uB098\uB20C \uC774\uC57C\uAE30\uB97C \uCC3E\uB3C4\uB85D \uB3C4\uC640\uC918\uC694.",
    empty_action: "\uC124\uC815\uD558\uAE30"
  }
};

// src/locales/ko-KR/meeting_prep.json
var meeting_prep_default2 = {
  meeting_status: {
    question_confirm_requested: "\uC0C1\uB300\uAC00 \uC9C8\uBB38 \uD655\uC778\uC744 \uC694\uCCAD\uD588\uC5B4\uC694.",
    waiting_counterpart_confirm: "\uC0C1\uB300\uC758 \uC9C8\uBB38 \uD655\uC778\uC744 \uAE30\uB2E4\uB9AC\uACE0 \uC788\uC5B4\uC694.",
    meeting_ready: "\uBBF8\uD305 \uC900\uBE44\uAC00 \uC644\uB8CC\uB410\uC5B4\uC694!",
    write_prep: "\uD504\uB819\uC744 \uC791\uC131\uD574\uC8FC\uC138\uC694.",
    prep_completed: "\uD504\uB819 \uC791\uC131\uC744 \uC644\uB8CC\uD588\uC5B4\uC694.",
    waiting_member_prep: "\uBA64\uBC84\uC758 \uD504\uB819\uC744 \uAE30\uB2E4\uB9AC\uACE0 \uC788\uC5B4\uC694.",
    review_prep_and_create_questions: "\uD504\uB819\uC744 \uD655\uC778\uD558\uACE0, \uC9C8\uBB38\uC744 \uB9CC\uB4E4\uC5B4\uC8FC\uC138\uC694."
  },
  meeting_status_tooltip: {
    question_confirm_requested: "\uBBF8\uD305 \uD654\uBA74 \uB9E8 \uC544\uB798\uC5D0\uC11C '\uC9C8\uBB38 \uD655\uC778 \uC644\uB8CC\uD558\uAE30' \uBC84\uD2BC\uC744 \uB20C\uB7EC\uC8FC\uC138\uC694.",
    waiting_counterpart_confirm: "\uC0C1\uB300\uBC29\uC774 \uC9C8\uBB38 \uD655\uC778\uC744 \uB9C8\uCE58\uBA74 \uBBF8\uD305 \uC900\uBE44\uAC00 \uC644\uB8CC\uB429\uB2C8\uB2E4.",
    meeting_ready: "\uC900\uBE44\uD55C \uB0B4\uC6A9\uC744 \uBC14\uD0D5\uC73C\uB85C \uBBF8\uD305\uC744 \uC9C4\uD589\uD558\uC138\uC694.",
    write_prep: "\uCC57\uBD07\uC744 \uD1B5\uD574 \uB098\uC758 \uC5C5\uBB34\uC640 \uC0C1\uD669, \uB300\uD654 \uC8FC\uC81C\uB97C \uC815\uB9AC\uD558\uC5EC \uC0C1\uB300\uBC29\uACFC \uACF5\uC720\uD574 \uBCF4\uC138\uC694.",
    prep_completed: "\uACF5\uC720\uD55C \uD504\uB819\uC744 \uBC14\uD0D5\uC73C\uB85C \uC0C1\uB300\uBC29\uC774 \uC9C8\uBB38\uC744 \uC900\uBE44\uD560 \uAC70\uC608\uC694.\n\uBBF8\uD305\uC5D0\uC11C \uB354 \uAE4A\uC774 \uB098\uB204\uACE0 \uC2F6\uC740 \uC774\uC57C\uAE30\uAC00 \uC788\uB2E4\uBA74 \uBBF8\uB9AC \uC9C8\uBB38\uC744 \uCD94\uAC00\uD574\uBCF4\uC138\uC694.",
    waiting_member_prep: "\uC0C1\uB300\uBC29\uC774 \uD504\uB819 \uC791\uC131\uC744 \uB9C8\uCE58\uBA74, \uCD94\uCC9C \uC9C8\uBB38\uACFC \uBBF8\uD305 \uAC00\uC774\uB4DC\uB97C \uD655\uC778\uD560 \uC218 \uC788\uC5B4\uC694.",
    review_prep_and_create_questions: "\uBBF8\uD305 \uAC00\uC774\uB4DC\uC640 \uCD94\uCC9C \uC9C8\uBB38, \uD15C\uD50C\uB9BF\uC744 \uD65C\uC6A9\uD558\uC5EC \uBBF8\uD305 \uC9C8\uBB38\uC744 \uC791\uC131\uD574 \uBCF4\uC138\uC694."
  },
  confirm_questions: {
    banner: "\uC9C8\uBB38\uC744 \uBAA8\uB450 \uD655\uC778\uD588\uB2E4\uBA74, '\uC9C8\uBB38 \uD655\uC778 \uC644\uB8CC\uD558\uAE30' \uBC84\uD2BC\uC744 \uB20C\uB7EC \uC0C1\uB300\uBC29\uC5D0\uAC8C \uD655\uC778\uD588\uC74C\uC744 \uC54C\uB824\uC8FC\uC138\uC694!",
    button: "\uC9C8\uBB38 \uD655\uC778 \uC644\uB8CC\uD558\uAE30"
  },
  create_page: {
    chat_placeholder: "\uB2F5\uBCC0\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694...",
    create_button: "\uD504\uB819 \uC0DD\uC131\uD558\uAE30",
    error: "\uD504\uB819 \uC0DD\uC131 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC0C8\uB85C\uACE0\uCE68 \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    saved: "\uC800\uC7A5\uB428",
    saving: "\uC800\uC7A5 \uC911...",
    send_button: "\uC804\uC1A1",
    success: "\uD504\uB819 \uC0DD\uC131\uC774 \uC644\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    update: "\uD504\uB819 \uC5C5\uB370\uC774\uD2B8\uD558\uAE30",
    edit: "\uD3B8\uC9D1",
    header_title: "\uD504\uB819 \uC791\uC131\uD558\uAE30",
    header_description: "\uCD5C\uADFC \uC5C5\uBB34\uB97C \uD558\uBA70 \uC788\uC5C8\uB358 \uC77C\uACFC \uADF8 \uACFC\uC815\uC5D0\uC11C \uB4E0 \uC0DD\uAC01\uC774\uB098 \uACE0\uBBFC, \uB9AC\uB354\uC640 \uC774\uC57C\uAE30\uD574 \uBCF4\uACE0 \uC2F6\uC740 \uC810 \uB4F1\uC744 \uCC57\uBD07\uC5D0\uAC8C \uD3B8\uD558\uAC8C \uB9D0\uD574\uC8FC\uC138\uC694.",
    chat_helper: "\uC9C0\uAE08 \uB5A0\uC624\uB974\uB294 \uC5C5\uBB34\uB098 \uACE0\uBBFC\uBD80\uD130 \uD3B8\uD558\uAC8C \uC774\uC57C\uAE30\uD574 \uC8FC\uC138\uC694. \uC544\uC9C1 \uC8FC\uC81C\uAC00 \uBA85\uD655\uD558\uC9C0 \uC54A\uC544\uB3C4 \uCC57\uBD07\uACFC \uB300\uD654\uD558\uBA74\uC11C \uD568\uAED8 \uCC3E\uC544\uBCFC \uC218 \uC788\uC5B4\uC694.",
    draft_empty: "\uB300\uD654\uB97C \uB098\uB208 \uB4A4 \uD504\uB819 \uC0DD\uC131\uD558\uAE30\uB97C \uB204\uB974\uBA74, AI\uAC00 \uCC44\uD305 \uB0B4\uC6A9\uC744 \uC694\uC57D\uD574 \uCD08\uC548\uC744 \uB9CC\uB4E4\uC5B4\uC918\uC694. \uC9C1\uC811 \uD655\uC778\uD558\uACE0 \uC218\uC815\uD55C \uB4A4 \uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD560 \uC218 \uC788\uC5B4\uC694."
  },
  shared_prep: {
    leader_intro_title: "\uBA64\uBC84\uAC00 \uACF5\uC720\uD55C \uD504\uB819",
    member_intro_title: "\uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD55C \uD504\uB819",
    leader_intro_description: "\uBBF8\uD305 \uC804\uC5D0 \uB0B4\uC6A9\uC744 \uC77D\uACE0, \uBA64\uBC84\uAC00 \uB2E4\uB8E8\uACE0 \uC2F6\uC740 \uC8FC\uC81C\uC640 \uC774\uBC88 \uB300\uD654\uC5D0\uC11C \uC5BB\uACE0 \uC2F6\uC740 \uACB0\uACFC\uB97C \uD655\uC778\uD574 \uBCF4\uC138\uC694."
  },
  prep_shared_nudge: {
    message: "{{name}}\uB2D8\uC774 \uD504\uB819\uC744 \uACF5\uC720\uD588\uC5B4\uC694. \uBBF8\uD305 \uAC00\uC774\uB4DC\uC640 \uCD94\uCC9C \uC9C8\uBB38\uC774 \uC900\uBE44\uB418\uC5B4 \uC788\uC5B4\uC694.",
    button: "\uD504\uB819 \uBCF4\uB7EC \uAC00\uAE30",
    message_mobile: "{{name}}\uB2D8\uC774 \uD504\uB819\uC744 \uACF5\uC720\uD588\uC5B4\uC694",
    view: "\uBCF4\uAE30"
  },
  ai_summary: {
    button: "AI\uB85C \uC694\uC57D\uD558\uAE30",
    cta_helper: "\uAE34 \uD504\uB819\uC774\uB77C\uBA74 \uC694\uC57D\uC744 \uB9CC\uB4E4\uC5B4 \uD6D1\uC5B4\uBCFC \uC218 \uC788\uC5B4\uC694.",
    tab_summary: "AI \uC694\uC57D",
    tab_original: "\uC6D0\uBB38"
  },
  prep_stage: {
    needs_focus_hint: "\uC774\uBC88 1on1\uC5D0\uC11C \uAC00\uC7A5 \uBA3C\uC800 \uC774\uC57C\uAE30\uD558\uACE0 \uC2F6\uC740 \uAC83\uC744 \uD55C \uAC00\uC9C0\uB9CC \uC54C\uB824\uC8FC\uC138\uC694. \uAD6C\uCCB4\uC801\uC778 \uC8FC\uC81C\uAC00 \uC5C6\uB2E4\uBA74 \uCD5C\uADFC \uAC00\uC7A5 \uC2E0\uACBD \uC4F0\uC600\uB358 \uC77C\uC774\uB098 \uB9AC\uB354\uC758 \uC758\uACAC\uC774 \uD544\uC694\uD55C \uC77C\uC744 \uC801\uC5B4\uB3C4 \uAD1C\uCC2E\uC544\uC694.",
    ready_generate: "\uD504\uB819\uC73C\uB85C \uC815\uB9AC\uD574\uC8FC\uC138\uC694",
    ready_update: "\uD504\uB819\uC5D0 \uBC18\uC601\uD574\uC8FC\uC138\uC694",
    ready_continue: "\uC870\uAE08 \uB354 \uC774\uC57C\uAE30\uD560\uAC8C\uC694"
  },
  placeholder: {
    member_before: {
      title: "\uC774\uBC88 1on1\uC5D0\uC11C \uB098\uB204\uACE0 \uC2F6\uC740 \uC774\uC57C\uAE30\uB97C \uBBF8\uB9AC \uC815\uB9AC\uD574\uBCF4\uC138\uC694",
      title_mobile: "1on1\uC5D0\uC11C \uB098\uB20C \uC774\uC57C\uAE30\uB97C \uBBF8\uB9AC \uC815\uB9AC\uD574\uBCF4\uC138\uC694",
      description: "\uBBF8\uD305 \uC804\uC5D0 \uD504\uB819\uC744 \uC791\uC131\uD574 \uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD558\uBA74, \uB9AC\uB354\uAC00 \uD504\uB819\uC744 \uD655\uC778\uD558\uACE0 \uB300\uD654\uB97C \uC900\uBE44\uD574\uC694. \uC9E7\uC740 \uC2DC\uAC04\uC5D0 \uB354 \uAE4A\uC740 \uC774\uC57C\uAE30\uB97C \uB098\uB20C \uC218 \uC788\uC5B4\uC694.",
      button: "\uD504\uB819 \uC791\uC131\uD558\uAE30",
      how: "\uCC57\uBD07\uACFC \uBE44\uACF5\uAC1C\uB85C \uB300\uD654\uD558\uBA74 \uD504\uB819\uC774 \uC815\uB9AC\uB3FC\uC694. \uD655\uC778\uD558\uACE0 \uC218\uC815\uD55C \uB4A4 \uACF5\uC720\uD560 \uC218 \uC788\uC5B4\uC694."
    },
    deadline: {
      far: "\uBBF8\uD305 \uC804\uB0A0\uAE4C\uC9C0 \uACF5\uC720\uD574\uC8FC\uC138\uC694. \uACF5\uC720 \uD6C4\uC5D0\uB294 \uB0B4\uC6A9\uC744 \uC218\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694.",
      tomorrow: "\uC624\uB298 \uACF5\uC720\uD574\uC8FC\uC138\uC694. \uACF5\uC720 \uD6C4\uC5D0\uB294 \uB0B4\uC6A9\uC744 \uC218\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694.",
      today: "\uBBF8\uD305 \uC804\uC5D0 \uACF5\uC720\uD574\uC8FC\uC138\uC694. \uACF5\uC720 \uD6C4\uC5D0\uB294 \uB0B4\uC6A9\uC744 \uC218\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
    },
    member_not_shared: {
      title: "\uD504\uB819\uC744 \uD655\uC778\uD558\uACE0 \uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD574\uC8FC\uC138\uC694",
      description: "\uACF5\uC720\uD558\uBA74 \uB9AC\uB354\uAC00 \uBBF8\uD305 \uC804\uC5D0 \uC911\uC2EC \uC8FC\uC81C\uC640 \uD544\uC694\uD55C \uC9C0\uC6D0\uC744 \uD655\uC778\uD560 \uC218 \uC788\uC5B4\uC694.",
      button: "\uC774\uC5B4\uC11C \uC791\uC131\uD558\uAE30"
    },
    leader_waiting: {
      title: "\uBA64\uBC84\uAC00 \uD504\uB819\uC744 \uACF5\uC720\uD558\uBA74 \uC774\uACF3\uC5D0\uC11C \uD655\uC778\uD560 \uC218 \uC788\uC5B4\uC694",
      description: "\uBA64\uBC84\uAC00 \uB098\uB204\uACE0 \uC2F6\uC740 \uC774\uC57C\uAE30\uC640 \uADF8\uC5D0 \uB9DE\uB294 \uBBF8\uD305 \uC9C4\uD589 \uAC00\uC774\uB4DC\uB97C \uD568\uAED8 \uB4DC\uB824\uC694. \uBBF8\uB9AC \uC77D\uC5B4\uBCF4\uACE0 \uB300\uD654\uB97C \uC900\uBE44\uD574\uBCF4\uC138\uC694.",
      hint: "\uD504\uB819\uC774 \uACF5\uC720\uB418\uC9C0 \uC54A\uC544\uB3C4 1on1\uC740 \uC608\uC815\uB300\uB85C \uC9C4\uD589\uD560 \uC218 \uC788\uC5B4\uC694."
    }
  },
  help: {
    trigger: "\uD504\uB819\uC740 \uC5B4\uB5BB\uAC8C \uC0AC\uC6A9\uB418\uB098\uC694?",
    title: "\uD504\uB819\uC774\uB780?",
    confirm: "\uD655\uC778",
    private_badge_member: "\uB098\uB9CC \uBCFC \uC218 \uC788\uC5B4\uC694",
    private_badge_leader: "\uBA64\uBC84\uB9CC \uBCFC \uC218 \uC788\uC5B4\uC694",
    shared_badge_member: "\uB9AC\uB354\uAC00 \uBCFC \uC218 \uC788\uC5B4\uC694",
    shared_badge_leader: "\uB9AC\uB354\uAC00 \uBCFC \uC218 \uC788\uC5B4\uC694",
    member: {
      lead: "\uD504\uB819\uC740 \uC774\uBC88 1on1\uC5D0\uC11C \uB098\uB20C \uC774\uC57C\uAE30\uB97C \uBBF8\uB9AC \uC815\uB9AC\uD55C \uAE00\uC774\uC5D0\uC694.",
      step1_title: "\uCC57\uBD07\uACFC \uBE44\uACF5\uAC1C\uB85C \uB300\uD654\uD574\uC694",
      step1_description: "\uB300\uD654 \uB0B4\uC6A9\uC744 \uBC14\uD0D5\uC73C\uB85C AI\uAC00 \uD504\uB819 \uCD08\uC548\uC744 \uC815\uB9AC\uD574\uC918\uC694. \uB9AC\uB354\uB294 \uC774 \uB300\uD654\uB97C \uBCFC \uC218 \uC5C6\uC5B4\uC694.",
      step2_title: "\uCD08\uC548\uC744 \uD655\uC778\uD558\uACE0 \uC218\uC815\uD574\uC694",
      step2_description: "\uACF5\uC720 \uC804\uC5D0 \uB0B4\uC6A9\uC744 \uACE0\uCE60 \uC218 \uC788\uC5B4\uC694.",
      boundary: "\uACF5\uC720\uD558\uAE30\uB97C \uB204\uB974\uBA74",
      step3_title: "\uB9AC\uB354\uC5D0\uAC8C \uD504\uB819\uC774 \uC804\uB2EC\uB3FC\uC694",
      step3_description: "\uB0B4\uAC00 \uACF5\uC720\uD55C \uD504\uB819\uB9CC \uC804\uB2EC\uB3FC\uC694.",
      step4: "\uB9AC\uB354\uB294 \uD568\uAED8 \uC81C\uACF5\uB418\uB294 \uC9C4\uD589 \uAC00\uC774\uB4DC\uC640 \uCD94\uCC9C \uC9C8\uBB38\uC73C\uB85C \uB300\uD654\uB97C \uC900\uBE44\uD574\uC694.",
      note: "\uACF5\uC720\uD55C \uB4A4\uC5D0\uB294 \uC218\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694. \uACF5\uC720 \uC804\uC5D0 \uD55C \uBC88 \uB354 \uD655\uC778\uD574\uC8FC\uC138\uC694."
    },
    leader: {
      lead: "\uD504\uB819\uC740 \uBA64\uBC84\uAC00 1on1\uC5D0\uC11C \uB098\uB20C \uC774\uC57C\uAE30\uB97C \uBBF8\uB9AC \uC815\uB9AC\uD55C \uAE00\uC774\uC5D0\uC694.",
      step1_title: "\uBA64\uBC84\uAC00 \uCC57\uBD07\uACFC \uBE44\uACF5\uAC1C\uB85C \uB300\uD654\uD574\uC694",
      step1_description: "\uB300\uD654 \uB0B4\uC6A9\uC744 \uBC14\uD0D5\uC73C\uB85C AI\uAC00 \uD504\uB819 \uCD08\uC548\uC744 \uC815\uB9AC\uD574\uC918\uC694. \uC774 \uB300\uD654\uB294 \uACF5\uAC1C\uB418\uC9C0 \uC54A\uC544\uC694.",
      step2_title: "\uBA64\uBC84\uAC00 \uCD08\uC548\uC744 \uD655\uC778\uD558\uACE0 \uC218\uC815\uD574\uC694",
      step2_description: "\uACF5\uC720 \uC804\uC5D0 \uBA64\uBC84\uAC00 \uB0B4\uC6A9\uC744 \uACE0\uCCD0\uC694.",
      boundary: "\uBA64\uBC84\uAC00 \uACF5\uC720\uD558\uBA74",
      step3_title: "\uD504\uB819\uC774 \uC804\uB2EC\uB3FC\uC694",
      step3_description: "\uBA64\uBC84\uAC00 \uACF5\uC720\uD55C \uD504\uB819\uB9CC \uBCFC \uC218 \uC788\uC5B4\uC694.",
      step4: "\uC9C4\uD589 \uAC00\uC774\uB4DC\uC640 \uCD94\uCC9C \uC9C8\uBB38\uC774 \uD568\uAED8 \uC81C\uACF5\uB3FC\uC694. \uB300\uD654\uB97C \uC900\uBE44\uD560 \uB54C \uCC38\uACE0\uD558\uC138\uC694.",
      note: "\uC544\uC9C1 \uD504\uB819\uC774 \uBCF4\uC774\uC9C0 \uC54A\uB294\uB2E4\uBA74 \uBA64\uBC84\uAC00 \uACF5\uC720\uD558\uAE30 \uC804\uC774\uC5D0\uC694."
    }
  },
  delete_dialog: {
    description: "\uD504\uB819\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?",
    title: "\uD504\uB819 \uC0AD\uC81C\uD558\uAE30"
  },
  guide: {
    title: "\uBBF8\uD305 \uAC00\uC774\uB4DC",
    description: "\uBA64\uBC84\uAC00 \uACF5\uC720\uD55C \uD504\uB819\uACFC \uD504\uB85C\uD544\uC744 \uBC14\uD0D5\uC73C\uB85C, \uC5B4\uB5A4 \uC774\uC57C\uAE30\uB85C \uC2DC\uC791\uD574 \uBB34\uC5C7\uC744 \uAE4A\uC774 \uB2E4\uB8E8\uACE0 \uC5B4\uB5BB\uAC8C \uB9C8\uBB34\uB9AC\uD558\uBA74 \uC88B\uC744\uC9C0 \uBBF8\uD305\uC758 \uD750\uB984\uC744 \uC81C\uC548\uD574 \uB4DC\uB824\uC694.",
    flow: "\uCD94\uCC9C \uC9C4\uD589 \uD750\uB984",
    overview: "\uAC1C\uC694",
    stage: {
      OPENING: "\uB3C4\uC785",
      MAIN: "\uBCF8\uB860",
      DEEPEN: "\uC2EC\uD654",
      CLOSING: "\uB9C8\uBB34\uB9AC"
    },
    key_insight: "\uD575\uC2EC \uC778\uC0AC\uC774\uD2B8",
    approach: "\uAD8C\uC7A5\uC0AC\uD56D \uBC0F \uC8FC\uC758\uC0AC\uD56D",
    tip: "\uB300\uD654 \uC6B4\uC601 \uD301"
  },
  profile_card: {
    title: "\uBA64\uBC84 \uD504\uB85C\uD544"
  },
  error: {
    access: "\uD504\uB819\uC740 \uBA64\uBC84\uB9CC \uC791\uC131\uD560 \uC218 \uC788\uC5B4\uC694."
  },
  mobile: {
    tabs: {
      chat: "\uCC44\uD305",
      summary: "\uD504\uB819"
    },
    title: "\uD504\uB819 \uC791\uC131"
  },
  question_recommend: {
    description: "\uCD94\uCC9C \uC9C4\uD589 \uD750\uB984\uC5D0 \uB9DE\uCDB0 \uB2E8\uACC4\uBCC4\uB85C \uC900\uBE44\uB41C \uC9C8\uBB38\uB4E4\uC774\uC5D0\uC694. \uC0AC\uC6A9\uD560 \uC9C8\uBB38\uC744 \uCCB4\uD06C\uD574 \uBBF8\uD305 \uC9C8\uBB38 \uBAA9\uB85D\uC5D0 \uCD94\uAC00\uD574 \uC8FC\uC138\uC694. \uCD94\uAC00 \uC804\uAE4C\uC9C0\uB294 \uBA64\uBC84\uC5D0\uAC8C \uBCF4\uC774\uC9C0 \uC54A\uC544\uC694.",
    title: "\uCD94\uCC9C \uC9C8\uBB38",
    grouped_title: "\uCD94\uCC9C \uC9C4\uD589 \uD750\uB984 \xB7 \uC9C8\uBB38 {{count}}\uAC1C",
    check_tooltip: "\uBBF8\uD305\uC758 \uC9C8\uBB38\uC73C\uB85C \uCD94\uAC00\uD558\uAE30",
    delete_tooltip: "\uCD94\uCC9C \uC9C8\uBB38 \uBAA9\uB85D\uC5D0\uC11C \uC0AD\uC81C\uD558\uAE30",
    adopt_all: "\uC804\uCCB4 \uC0AC\uC6A9\uD558\uAE30",
    adopt_all_confirm: "\uCD94\uCC9C \uC9C8\uBB38 {{count}}\uAC1C\uB97C \uBAA8\uB450 \uBBF8\uD305 \uC9C8\uBB38 \uBAA9\uB85D\uC5D0 \uCD94\uAC00\uD560\uAE4C\uC694?",
    adopt_confirm: "\uC120\uD0DD\uD55C \uC9C8\uBB38 {{count}}\uAC1C\uB97C \uBBF8\uD305 \uC9C8\uBB38 \uBAA9\uB85D\uC5D0 \uCD94\uAC00\uD560\uAE4C\uC694?",
    delete_confirm: "\uC774 \uC9C8\uBB38\uC744 \uCD94\uCC9C \uC9C8\uBB38 \uBAA9\uB85D\uC5D0\uC11C \uC0AD\uC81C\uD560\uAE4C\uC694?\n\uC0AD\uC81C\uD55C \uC9C8\uBB38\uC740 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC5B4\uC694.",
    selected_count: "\uC9C8\uBB38 {{count}}\uAC1C \uC120\uD0DD\uB428",
    select_all: "\uC9C8\uBB38 \uC804\uCCB4 \uC120\uD0DD",
    unselect_all: "\uC9C8\uBB38 \uC120\uD0DD \uD574\uC81C",
    view_in_content: "\uBCF8\uBB38\uC5D0\uC11C \uBCF4\uAE30"
  },
  share: {
    description: "\uACF5\uC720\uD558\uBA74 \uB9AC\uB354\uC5D0\uAC8C \uD504\uB819\uC774 \uACF5\uAC1C\uB418\uBA70, \uC774\uD6C4\uC5D0\uB294 \uC218\uC815\uC774 \uBD88\uAC00\uB2A5\uD569\uB2C8\uB2E4.",
    done: "\uACF5\uC720\uD558\uAE30",
    error: {
      message: "\uD504\uB819 \uC800\uC7A5 \uBC0F \uACF5\uC720 \uACFC\uC815\uC5D0\uC11C \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
      title: "\uC800\uC7A5 \uC2E4\uD328"
    },
    requested: {
      title: "\uD504\uB819 \uC800\uC7A5 \uBC0F \uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720",
      message: "\uD504\uB819\uC744 \uC800\uC7A5\uD588\uC2B5\uB2C8\uB2E4. \uB9AC\uB354\uC5D0\uAC8C \uACE7 \uC804\uB2EC\uB429\uB2C8\uB2E4."
    },
    success: {
      message: "\uD504\uB819\uC744 \uC800\uC7A5\uD558\uACE0 \uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4.",
      title: "\uD504\uB819 \uC791\uC131 \uBC0F \uACF5\uC720 \uC644\uB8CC"
    },
    title: "\uB9AC\uB354\uC5D0\uAC8C \uACF5\uC720\uD558\uAE30"
  },
  survey: {
    skip: "\uC544\uC9C1 \uC798 \uBAA8\uB974\uACA0\uC5B4\uC694",
    skip_description: "\uCC57\uBD07\uACFC \uC774\uC57C\uAE30\uD558\uBA74\uC11C \uC8FC\uC81C\uB97C \uCC3E\uC544\uBCFC\uAC8C\uC694.",
    section_title: "\uB300\uD654 \uC7AC\uB8CC",
    section_helper: "\uC9C0\uAE08 \uB098\uC5D0\uAC8C \uC911\uC694\uD55C \uC774\uC57C\uAE30\uB97C \uCC3E\uACE0, \uC774\uBC88 1on1\uC5D0\uC11C \uBB34\uC5C7\uC744 \uC5B4\uB5BB\uAC8C \uC774\uC57C\uAE30\uD560\uC9C0 \uAD6C\uCCB4\uD654\uD558\uB294 \uB370 \uD65C\uC6A9\uB3FC\uC694. \uB5A0\uC624\uB974\uB294 \uB2F5\uC774 \uC788\uB294 \uAC83\uB9CC \uC790\uC720\uB86D\uAC8C \uC120\uD0DD\uD574\uC8FC\uC138\uC694.",
    section_expand: "\uD3BC\uCE58\uAE30",
    section_collapse: "\uC811\uAE30",
    section_optional: "\uC120\uD0DD",
    summary_profile: "\uD504\uB85C\uD544 \uBC18\uC601\uB428",
    summary_topic: "\uC8FC\uC81C {{count}}\uAC1C \uC120\uD0DD",
    summary_empty: "\uC544\uC9C1 \uC120\uD0DD\uD55C \uD56D\uBAA9\uC774 \uC5C6\uC5B4\uC694",
    label_profile: "\uD504\uB85C\uD544",
    label_survey: "\uC774\uC57C\uAE30 \uC8FC\uC81C"
  },
  survey_answers: {
    condition: {
      category: "\uC5C5\uBB34 \uCEE8\uB514\uC158",
      question: "\uC694\uC998 \uB098\uC758 \uC804\uBC18\uC801\uC778 \uC5C5\uBB34 \uCEE8\uB514\uC158\uC740 \uC5B4\uB5A4\uAC00\uC694?",
      helper: "\uC120\uD0DD\uD55C \uCEE8\uB514\uC158\uC740 \uCC57\uBD07\uACFC\uC758 \uB300\uD654\uB97C \uB3D5\uB294 \uCC38\uACE0 \uC815\uBCF4\uC774\uBA70, \uB9AC\uB354\uC5D0\uAC8C \uADF8\uB300\uB85C \uACF5\uC720\uB418\uC9C0 \uC54A\uC544\uC694. \uC774\uBC88\uC5D0 \uB098\uB20C \uC774\uC57C\uAE30\uC640 \uAD00\uB828\uC774 \uC788\uC744 \uB54C\uB3C4 \uD504\uB819\uC5D0 \uD3EC\uD568\uD560\uC9C0\uB294 \uC9C1\uC811 \uC120\uD0DD\uD560 \uC218 \uC788\uC5B4\uC694.",
      VERY_GOOD: "\uB9E4\uC6B0 \uC88B\uC74C",
      GOOD: "\uC88B\uC74C",
      NORMAL: "\uC591\uD638\uD568",
      TIRED: "\uC9C0\uCE68",
      VERY_BAD: "\uB9E4\uC6B0 \uB098\uC068"
    },
    topic: {
      category: "\uBBF8\uD305 \uC8FC\uC81C",
      question: "\uC774\uBC88 1on1\uC5D0\uC11C \uAC00\uC7A5 \uC774\uC57C\uAE30\uD558\uACE0 \uC2F6\uC740 \uAC83\uC740 \uBB34\uC5C7\uC778\uAC00\uC694?",
      helper: "\uC544\uC9C1 \uC798 \uBAA8\uB974\uACA0\uB2E4\uBA74 \uCC57\uBD07\uACFC \uB300\uD654\uB97C \uBC14\uB85C \uC2DC\uC791\uD574\uBCF4\uC138\uC694.",
      PERFORMANCE_FEEDBACK: "\uC131\uACFC\uC640 \uD53C\uB4DC\uBC31",
      PERFORMANCE_FEEDBACK_description: "\uCD5C\uADFC \uC5C5\uBB34\uC758 \uC131\uACFC\uC640 \uC77C\uD558\uB294 \uBC29\uC2DD\uC744 \uD568\uAED8 \uB3CC\uC544\uBCF4\uACE0, \uC798\uD55C \uC810\uACFC \uBCF4\uC644\uD560 \uC810\uC5D0 \uB300\uD55C \uD53C\uB4DC\uBC31\uC744 \uBC1B\uACE0 \uC2F6\uC5B4\uC694.",
      GROWTH_CAREER: "\uC131\uC7A5\uACFC \uCEE4\uB9AC\uC5B4",
      GROWTH_CAREER_description: "\uC55E\uC73C\uB85C \uD0A4\uC6B0\uACE0 \uC2F6\uC740 \uC5ED\uB7C9\uACFC \uCEE4\uB9AC\uC5B4 \uBC29\uD5A5, \uD544\uC694\uD55C \uACBD\uD5D8\uC5D0 \uB300\uD574 \uC774\uC57C\uAE30\uD558\uACE0 \uC2F6\uC5B4\uC694.",
      WORK_ISSUES: "\uC5C5\uBB34 \uACE0\uBBFC\uACFC \uC9C0\uC6D0",
      WORK_ISSUES_description: "\uC5C5\uBB34\uC5D0\uC11C \uB9C9\uD788\uAC70\uB098 \uBC18\uBCF5\uB418\uB294 \uC5B4\uB824\uC6C0\uC744 \uC0B4\uD3B4\uBCF4\uACE0, \uD574\uACB0 \uBC29\uD5A5\uC774\uB098 \uD544\uC694\uD55C \uC9C0\uC6D0\uC744 \uCC3E\uACE0 \uC2F6\uC5B4\uC694.",
      TEAM_CULTURE: "\uD300 \uC18C\uD1B5\uACFC \uBB38\uD654",
      TEAM_CULTURE_description: "\uD300\uC758 \uC18C\uD1B5\xB7\uD611\uC5C5 \uBC29\uC2DD\uC774\uB098 \uBD84\uC704\uAE30\uC5D0\uC11C \uB290\uB080 \uC810\uC774\uB098 \uBCC0\uD654\uAC00 \uD544\uC694\uD55C \uBD80\uBD84\uC5D0 \uB300\uD574 \uC774\uC57C\uAE30\uD558\uACE0 \uC2F6\uC5B4\uC694.",
      ROLE_RESPONSIBILITY: "\uC5ED\uD560 \uBC0F \uCC45\uC784",
      ROLE_RESPONSIBILITY_description: "\uB098\uC5D0\uAC8C \uAE30\uB300\uB418\uB294 \uC5ED\uD560\uACFC \uCC45\uC784, \uC5C5\uBB34 \uC6B0\uC120\uC21C\uC704\uB098 \uC758\uC0AC\uACB0\uC815 \uBC94\uC704\uB97C \uBA85\uD655\uD788 \uD558\uACE0 \uC2F6\uC5B4\uC694."
    }
  },
  title: "\uD504\uB819"
};

// src/locales/ko-KR/oneonone_notification.json
var oneonone_notification_default2 = {
  dday_reminder: {
    title: "\uBBF8\uD305 \uB9AC\uB9C8\uC778\uB4DC",
    body_dday: "\uC624\uB298 {{time}} {{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
    body_d1: "\uB0B4\uC77C {{time}} {{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
    body_dn: "{{daysUntil}}\uC77C \uD6C4 {{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305\uC774 \uC608\uC815\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4."
  },
  meeting_add: {
    title: "\uBBF8\uD305 \uB4F1\uB85D",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 \uC0C8 1on1 \uBBF8\uD305\uC774 \uC0DD\uC131\uB418\uC5C8\uC2B5\uB2C8\uB2E4."
  },
  meeting_change: {
    title: "\uBBF8\uD305 \uC815\uBCF4 \uBCC0\uACBD",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305 \uC77C\uC815\uC774 {{newSchedule}}(\uC73C)\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    body_type_only: "{{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305 \uBC29\uC2DD\uC774 {{newMeetingTypeLabel}}(\uC73C)\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    body_both: "{{partnerName}}\uB2D8\uACFC\uC758 1on1 \uBBF8\uD305 \uC77C\uC815\uC774 {{newSchedule}}(\uC73C)\uB85C, \uBC29\uC2DD\uC774 {{newMeetingTypeLabel}}(\uC73C)\uB85C \uBCC0\uACBD\uB418\uC5C8\uC2B5\uB2C8\uB2E4.",
    meeting_type_offline: "\uB300\uBA74 \uBBF8\uD305",
    meeting_type_online: "\uD654\uC0C1 \uBBF8\uD305"
  },
  meeting_cancel: {
    title: "\uBBF8\uD305 \uCDE8\uC18C",
    body: "{{partnerName}}\uB2D8\uC774 1on1 \uBBF8\uD305\uC744 \uCDE8\uC18C\uD588\uC2B5\uB2C8\uB2E4."
  },
  prep_share: {
    title: "\uD504\uB819 \uC791\uC131 \uC644\uB8CC",
    body: "{{partnerName}}\uB2D8\uC774 \uD504\uB819\uC744 \uACF5\uC720\uD588\uC2B5\uB2C8\uB2E4."
  },
  question_request: {
    title: "\uC9C8\uBB38 \uD655\uC778 \uC694\uCCAD",
    body: "{{partnerName}}\uB2D8\uC774 \uBBF8\uD305 \uC9C8\uBB38 \uD655\uC778\uC744 \uC694\uCCAD\uD588\uC2B5\uB2C8\uB2E4."
  },
  recording_ready: {
    title: "\uB179\uD654 \uD30C\uC77C \uC900\uBE44 \uC644\uB8CC",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 \uBBF8\uD305 \uB179\uD654 \uD30C\uC77C\uC774 \uC900\uBE44\uB410\uC5B4\uC694. \uAC00\uC838\uC624\uAE30\uB97C \uB20C\uB7EC AI \uBD84\uC11D\uC744 \uC2DC\uC791\uD574\uC8FC\uC138\uC694."
  },
  analysis_complete: {
    title: "\uBD84\uC11D \uC644\uB8CC",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 \uBBF8\uD305 \uBD84\uC11D\uC774 \uC644\uB8CC\uB410\uC5B4\uC694. \uACB0\uACFC\uB97C \uD655\uC778\uD574\uBCF4\uC138\uC694."
  },
  member_feedback_ready: {
    title: "AI \uD53C\uB4DC\uBC31 \uB3C4\uCC29",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 \uBBF8\uD305\uC5D0\uC11C \uC0DD\uC131\uB41C \uD53C\uB4DC\uBC31\uC744 \uD655\uC778\uD558\uBA70 \uC774\uBC88 \uBBF8\uD305\uC744 \uD68C\uACE0\uD574\uBCF4\uC138\uC694."
  },
  in_progress_incomplete_reminder: {
    title: "\uBBF8\uD305\uC744 \uC885\uB8CC\uD574\uC8FC\uC138\uC694",
    body: "{{partnerName}}\uB2D8\uACFC\uC758 \uBBF8\uD305\uC774 \uC9C4\uD589 \uC911\uC778 \uC0C1\uD0DC\uB85C \uB0A8\uC544\uC788\uC5B4\uC694."
  },
  cycle_reminder: {
    title: "\uB2E4\uC74C 1on1\uC744 \uB4F1\uB85D\uD574\uC8FC\uC138\uC694",
    body: "{{partnerName}}\uB2D8\uACFC \uC57D\uC18D\uD55C \uC8FC\uAE30\uAC00 \uB2E4\uAC00\uC654\uC5B4\uC694. \uBBF8\uD305\uC744 \uB9CC\uB4E4\uC5B4 \uC815\uAE30\uC801\uC778 \uC18C\uD1B5\uC744 \uC774\uC5B4\uAC00\uBCF4\uC138\uC694."
  },
  empty: "\uC0C8\uB85C\uC6B4 \uC54C\uB9BC\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  fallback: {
    title: "\uC0C8\uB85C\uC6B4 \uC54C\uB9BC",
    body: "\uC0C8\uB85C\uC6B4 \uC54C\uB9BC\uC774 \uC788\uC2B5\uB2C8\uB2E4."
  }
};

// src/locales/ko-KR/landing_new.json
var landing_new_default2 = {
  change: {
    pill: "\uACBD\uC601\uC9C4\uC758 \uBE44\uC804\uACFC \uC804\uB7B5\uC744 \uAD6C\uC11D\uAD6C\uC11D \uD37C\uB728\uB9AC\uB294[m] \uBCC0\uD654 \uAD00\uB9AC \uD234, \uC624\uBE14\uB9BF",
    heroTitle: "\uC804\uB7B5\uC740 \uBC1C\uD45C\uAC00 \uC544\uB2C8\uB77C[m] 1on1\uC5D0\uC11C \uC2E4\uD589\uB429\uB2C8\uB2E4.",
    heroSubtitle: "\uACBD\uC601\uC9C4\uC758 \uBE44\uC804\uC774 \uD604\uC7A5 \uB9C9\uB0B4\uC5D0\uAC8C \uB2FF\uAE30\uAE4C\uC9C0,[m] \uC624\uBE14\uB9BF\uC740 \uADF8 \uD1B5\uB85C\uB97C \uC124\uACC4\uD569\uB2C8\uB2E4.",
    problem: {
      title: "\uC804\uB7B5\uC758 \uC2E4\uD589\uB960\uC774 \uB0AE\uC740 \uC9C4\uC9DC \uC774\uC720",
      quotes: [
        "\uC804\uC0AC \uD0C0\uC6B4\uD640 \uD6C4 \uD55C \uB2EC \uB4A4,[m] \uC2E0\uC785\uC740 \uD68C\uC0AC\uC758 \uBC29\uD5A5\uC744[d] \uAE30\uC5B5\uD558\uC9C0 \uBABB\uD569\uB2C8\uB2E4.",
        "\uB9AC\uB354\uC2ED \uAD50\uC721 \uD6C4 6\uC8FC\uAC00 \uC9C0\uB098\uBA74[m] 90%\uAC00 \uC6D0\uC0C1\uD0DC\uB85C[d] \uD68C\uADC0\uD569\uB2C8\uB2E4.",
        "HR\uC740 \uB2E4\uBA74\uD3C9\uAC00 \uACB0\uACFC\uC9C0\uB97C \uBC1B\uAE30 \uC804\uAE4C\uC9C0[m] \uC5B4\uB290 \uD300\uC774 \uACEA\uACE0[d] \uC788\uB294\uC9C0 \uBAA8\uB985\uB2C8\uB2E4.",
        "\uD575\uC2EC \uC778\uC7AC\uC758 \uD1F4\uC0AC \uD1B5\uBCF4\uB97C \uBC1B\uACE0 \uB098\uC11C\uC57C[m] \uC9C4\uC9DC \uC774\uC720\uB97C[d] \uBB3B\uAE30 \uC2DC\uC791\uD569\uB2C8\uB2E4."
      ]
    },
    layers: {
      title: "\uBCC0\uD654\uB294 \uC138 \uCE35\uC704\uC5D0\uC11C[m] \uB3D9\uC2DC\uC5D0 \uC77C\uC5B4\uB098\uC57C \uD569\uB2C8\uB2E4",
      items: [
        {
          title: "\uACBD\uC601\uC9C4 (Top)",
          desc: "\uC804\uB7B5 \uBA54\uC2DC\uC9C0\uB97C 1on1 \uC5B4\uC820\uB2E4\uC5D0 \uBC18\uC601"
        },
        {
          title: "HR (Middle)",
          desc: "\uC870\uC9C1 \uC804\uBC18\uC758 \uC18C\uD1B5\xB7\uBAB0\uC785\uB3C4 \uAD00\uC81C"
        },
        {
          title: "\uB9AC\uB354 (Frontline)",
          desc: "\uC790\uC2E0\uC758 \uD300\uC5D0\uC11C \uBA54\uC2DC\uC9C0\uB97C \uBC88\uC5ED\xB7\uC804\uB2EC"
        }
      ]
    },
    toolbox: {
      title: "HR\uC744 \uC704\uD55C \uC0C8\uB85C\uC6B4 \uB3C4\uAD6C\uC0C1\uC790",
      items: [
        {
          title: "\uD558\uD5A5 \uC815\uB82C",
          desc: "\uC804\uC0AC \uBAA9\uD45C \uBC0F \uD575\uC2EC \uC804\uB7B5 \uBA54\uC2DC\uC9C0\uB97C 1on1 \uD504\uB819\uC5D0 \uC790\uB3D9 \uC784\uBCA0\uB4DC\uD558\uC5EC \uC2E4\uBB34\uC790\uAE4C\uC9C0 \uC804\uB7B5\uC774 \uC804\uB2EC\uB418\uAC8C \uD569\uB2C8\uB2E4."
        },
        {
          title: "\uC218\uD3C9 \uAC00\uC2DC\uD654",
          desc: "\uD300\uBCC4 \uC18C\uD1B5 \uBE48\uB3C4 \uBC0F \uB9AC\uB354\uC2ED \uCEE8\uB514\uC158\uC744 \uC804\uC0AC \uB300\uC2DC\uBCF4\uB4DC\uB85C \uC2E4\uC2DC\uAC04 \uAD00\uC81C\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uC0C1\uD5A5 \uC778\uC0AC\uC774\uD2B8",
          desc: "\uB204\uC801 1on1 \uB370\uC774\uD130\uC5D0\uC11C \uCD94\uCD9C\uB41C \uC870\uC9C1 \uC2DC\uADF8\uB110 (\uBC88\uC544\uC6C3 \uC9D5\uD6C4, \uC774\uD0C8 \uC704\uD5D8, \uC0AC\uAE30 \uBCC0\uD654)\uC744 \uD30C\uC545\uD569\uB2C8\uB2E4."
        },
        {
          title: "HR \uC561\uC158",
          desc: "\uC774\uC9C1 \uC704\uD5D8 \uB4F1 \uC54C\uB9BC\uC744 \uD1B5\uD574 \uACE8\uB4E0\uD0C0\uC784 \uB0B4\uC5D0 \uAC1C\uC785\uD569\uB2C8\uB2E4."
        }
      ]
    },
    cases: {
      title: "\uB3C4\uC785 \uD6A8\uACFC \uC0AC\uB840",
      h: {
        name: "H \uADF8\uB8F9 \uACC4\uC5F4\uC0AC",
        sub: "\uC870\uC9C1\uBB38\uD654 \uBCC0\uD654\uC758 \uC99D\uAC70",
        body: "\uD0C0\uC0AC \uBC0F \uC790\uCCB4 \uC11C\uBE44\uC2A4\uB85C\uB294 \uBD88\uAC00\uD588\uB358[m][d] [b=#2563eb]\uC6D0\uC628\uC6D0 \uC81C\uB3C4\uC758 \uC548\uC815\uC801\uC778 \uC548\uCC29\uACFC \uD65C\uC131\uD654[/b]\uC5D0[m] \uC131\uACF5\uD588\uC2B5\uB2C8\uB2E4."
      },
      s: {
        name: "S \uADF8\uB8F9 \uACC4\uC5F4\uC0AC",
        sub: "\uC778\uC0AC \uC778\uD504\uB77C \uC790\uCCB4\uC758 \uC804\uD658",
        body: "\uC131\uACFC\uAD00\uB9AC \uCCB4\uC81C \uC804\uBC18\uC744[m] [b=#4f46e5]\uC6D0\uC628\uC6D0 \uC911\uC2EC\uC73C\uB85C \uAC1C\uD3B8[/b]\uD558\uC5EC[m][d] \uD604\uC7A5 \uB9AC\uB354\uC2ED \uC5ED\uB7C9\uC744 \uB04C\uC5B4\uC62C\uB838\uC2B5\uB2C8\uB2E4."
      }
    },
    next: {
      label: "Next Step",
      title: "\uBAA8\uB4E0 \uBCC0\uD654\uC758 \uC2DC\uC791\uC740 \uB300\uD654\uC785\uB2C8\uB2E4",
      cta: "\uC18C\uD1B5 \uAC15\uD654 \uC54C\uC544\uBCF4\uAE30"
    }
  },
  communication: {
    pill: "\uC870\uC9C1\uC5D0 \uAC74\uAC15\uD55C \uB300\uD654\uAC00 \uD750\uB974\uAC8C \uD558\uB294[m] \uC18C\uD1B5 \uAC15\uD654 \uD234, \uC624\uBE14\uB9BF",
    heroTitle: "\uC18C\uD1B5\uC740 \uC131\uACA9\uC774 \uC544\uB2C8\uB77C[m] \uC2A4\uD0AC\uC785\uB2C8\uB2E4.",
    heroSubtitle: "\uC624\uBE14\uB9BF\uC740 \uBAA8\uB4E0 \uB9AC\uB354\uAC00 \uC88B\uC740 \uB300\uD654\uB97C[m] \uC2DC\uC791\uD560 \uC218 \uC788\uB3C4\uB85D \uB3D5\uC2B5\uB2C8\uB2E4.",
    problem: {
      title: "\uC6D0\uC628\uC6D0\uC774 \uC5B4\uC0C9\uD55C \uAC74[m] \uB2F9\uC2E0\uB9CC\uC774 \uC544\uB2D9\uB2C8\uB2E4",
      subtitle: "\uC774\uAC74 \uAC1C\uC778\uC758 \uBB38\uC81C\uAC00 \uC544\uB2C8\uB77C \uAD6C\uC870\uC758 \uBB38\uC81C\uC785\uB2C8\uB2E4.",
      quotes: [
        "\uD560 \uB9D0\uC774 \uC5C6\uC5B4\uC11C \uACB0\uAD6D \uB0A0\uC528 \uC598\uAE30\uB9CC \uD588\uB2E4",
        "\uBB58 \uBB3C\uC5B4\uB3C4 '\uAD1C\uCC2E\uC544\uC694'\uB9CC \uB3CC\uC544\uC628\uB2E4",
        "\uC870\uC5B8\uD588\uB354\uB2C8 \uB2E4\uC74C \uC8FC\uBD80\uD130 \uD45C\uC815\uC774 \uAD73\uC5C8\uB2E4",
        "\uB0B4\uAC00 \uC798\uD558\uACE0 \uC788\uB294 \uAC74\uC9C0 \uAC1D\uAD00\uC801\uC73C\uB85C[m] \uC54C \uAE38\uC774 \uC5C6\uB2E4"
      ]
    },
    needs: {
      title: "\uC88B\uC740 \uB300\uD654\uC5D0\uB294[m] \uC138 \uAC00\uC9C0\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4",
      items: [
        {
          title: "1. \uC900\uBE44\uB41C \uC9C8\uBB38",
          desc: '\uB9C9\uC5F0\uD55C "\uC798 \uC9C0\uB0B4?"\uAC00 \uC544\uB2CC,[br] \uADF8 \uC0AC\uB78C\xB7\uADF8 \uC0C1\uD669\uC5D0 \uB9DE\uCD98 \uC9C8\uBB38'
        },
        {
          title: "2. \uC548\uC804\uD55C \uBD84\uC704\uAE30",
          desc: "\uD300\uC6D0\uC774 \uBA3C\uC800 \uB9D0\uD560 \uC218 \uC788\uB3C4\uB85D[br] \uC124\uACC4\uB41C \uC0AC\uC804 \uC900\uBE44 \uAD6C\uC870"
        },
        {
          title: "3. \uAC1D\uAD00\uC801 \uD53C\uB4DC\uBC31",
          desc: "\uB300\uD654 \uD6C4 \uB9AC\uB354 \uC790\uC2E0\uC758 \uC18C\uD1B5\uC744[br] \uB3CC\uC544\uBCFC \uC218 \uC788\uB294 \uAC70\uC6B8"
        }
      ]
    },
    how: {
      title: "\uC5B4\uB5BB\uAC8C \uC791\uB3D9\uD558\uB098\uC694?",
      steps: [
        {
          tag: "\uB300\uD654 \uC804",
          title: "AI \uBBF8\uD305 \uD504\uB819 + \uCD94\uCC9C \uC9C8\uBB38",
          desc: "\uD300\uC6D0\uC774 \uBBF8\uB9AC \uC791\uC131\uD55C \uCEE8\uB514\uC158\xB7\uACE0\uBBFC\uC744 AI\uAC00 \uBD84\uC11D\uD574 \uB9AC\uB354\uC5D0\uAC8C \uB9DE\uCDA4 \uAC00\uC774\uB4DC\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4."
        },
        {
          tag: "\uB300\uD654 \uC911",
          title: "\uC790\uB3D9 STT\uB85C \uAE30\uB85D \uBD80\uB2F4 \uC81C\uAC70",
          desc: "\uB9AC\uB354\uB294 \uB354 \uC774\uC0C1 \uBC1B\uC544\uC801\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. \uC624\uC9C1 '\uB4E3\uAE30'\uC640 '\uC18C\uD1B5'\uC5D0\uB9CC \uC9D1\uC911\uD558\uC138\uC694."
        },
        {
          tag: "\uB300\uD654 \uD6C4",
          title: "\uBC1C\uD654 \uBD84\uC11D \uAE30\uBC18 \uB9AC\uB354\uC2ED \uCF54\uCE6D",
          desc: '"\uB0B4 \uBC1C\uD654 \uBE44\uC728\uC740? \uC88B\uC740 \uC9C8\uBB38\uC744 \uD588\uB294\uAC00?" \uB370\uC774\uD130 \uAE30\uBC18\uC73C\uB85C \uB9AC\uD3EC\uD2B8\uC640 \uCF54\uCE6D\uC744 \uBC1B\uC2B5\uB2C8\uB2E4.'
        },
        {
          tag: "\uB204\uC801",
          title: "\uC18C\uD1B5 \uC5ED\uB7C9 \uB9AC\uD3EC\uD2B8",
          desc: "\uACBD\uCCAD, \uC9C8\uBB38, \uACF5\uAC10, \uD53C\uB4DC\uBC31 \uB4F1 6\uAC1C \uCC28\uC6D0\uC5D0\uC11C \uC790\uC2E0\uC758 \uC18C\uD1B5 \uC5ED\uB7C9 \uC131\uC7A5 \uACE1\uC120\uC744 \uD655\uC778\uD569\uB2C8\uB2E4."
        }
      ]
    },
    scenario: {
      title: "\uAE40 \uD300\uC7A5\uC758 \uCCAB 1on1 \uC2DC\uB098\uB9AC\uC624",
      p1: '\uAE40 \uD300\uC7A5\uC740 \uC785\uC0AC 6\uAC1C\uC6D4 \uCC28 \uC2E0\uC785\uACFC\uC758 \uCCAB \uC6D0\uC628\uC6D0\uC774 \uBD80\uB2F4\uC2A4\uB7EC\uC6E0\uC2B5\uB2C8\uB2E4. \uD558\uC9C0\uB9CC \uC624\uBE14\uB9BF\uC774 \uC2E0\uC785\uC758 \uC0AC\uC804 \uD504\uB819\uC744 \uBD84\uC11D\uD574 [h]"\uCD5C\uADFC OKR \uC815\uB82C\uC5D0 \uD63C\uB780\uC744 \uACAA\uACE0 \uC788\uC74C"[/h]\uC744 \uC54C\uB824\uC92C\uACE0, \uCD94\uCC9C \uC9C8\uBB38 \uC911 [u]"\uC9C0\uAE08 \uAC00\uC7A5 \uBA85\uD655\uD558\uC9C0 \uC54A\uC740 \uBAA9\uD45C\uB294 \uBB34\uC5C7\uC778\uAC00\uC694?"[/u]\uB85C \uB300\uD654\uB97C \uC5F4\uC5C8\uC2B5\uB2C8\uB2E4.',
      p2: '30\uBD84 \uB4A4, \uAE40 \uD300\uC7A5\uC740 \uCC98\uC74C\uC73C\uB85C [b=#1a1a1a]"\uC774 \uCE5C\uAD6C\uAC00 \uC9C4\uC9DC \uB9C9\uB9C9\uD574\uD558\uB294 \uC9C0\uC810"[/b]\uC744 \uC54C\uAC8C \uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uC624\uBE14\uB9BF\uC740 \uB300\uD654\uAC00 \uB05D\uB09C \uD6C4 \uC561\uC158 \uC544\uC774\uD15C\uC744 \uC790\uB3D9\uC73C\uB85C \uC815\uB9AC\uD574\uC8FC\uC5C8\uACE0, \uAE40\uD300\uC7A5\uC740 \uB4E3\uB294 \uAC83\uC5D0\uB9CC 100% \uC9D1\uC911\uD560 \uC218 \uC788\uC5C8\uC2B5\uB2C8\uB2E4.'
    },
    transform: {
      title: "\uC624\uBE14\uB9BF 1on1 \uC815\uCC29\uC5D0 \uB530\uB978 \uB9AC\uB354\uC758 \uBCC0\uD654",
      asis: "\uC815\uB2F5\uC744 \uAC15\uC694\uD558\uB294[br] \uB2E8\uBC29\uD5A5 \uD3C9\uAC00\uC790",
      tobe: "\uC7A0\uC7AC\uB825\uC744 \uC774\uB04C\uC5B4\uB0B4\uB294[br] \uC131\uC7A5\uC758 \uD30C\uD2B8\uB108",
      points: [
        {
          before: '"\uBB34\uC2A8 \uB9D0\uC744 \uD560\uAE4C"[br] \uD68C\uD53C\uD558\uACE0 \uC2F6\uC740 \uB9C9\uB9C9\uD568',
          after: "\uB370\uC774\uD130\uC640 \uAC00\uC774\uB4DC\uB85C \uBB34\uC7A5\uD55C[br] \uB9AC\uB354\uC758 \uC790\uC2E0\uAC10"
        },
        {
          before: "\uD300\uC6D0\uC758 \uC18D\uB9C8\uC74C\uC744 \uC54C \uC218 \uC5C6\uB294[br] \uC2EC\uB9AC\uC801 \uBD88\uC548\uAC10",
          after: "\uD22C\uBA85\uD55C \uC18C\uD1B5\uC73C\uB85C \uD300\uC6D0\uC758 \uC0C1\uD0DC\uB97C[br] \uD30C\uC545\uD558\uB294 \uC548\uB3C4\uC640 \uC2E0\uB8B0"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "\uC88B\uC740 \uB300\uD654\uB294 \uCD9C\uBC1C\uC77C \uBFD0\uC785\uB2C8\uB2E4. \uADF8\uB2E4\uC74C\uC740?",
      cta: "\uC131\uC7A5 \uAD00\uB9AC \uC54C\uC544\uBCF4\uAE30"
    }
  },
  growth: {
    pill: "\uC18C\uD1B5\uC744 \uD1B5\uD574 \uAD6C\uC131\uC6D0\uB4E4\uC758 \uC131\uC7A5\uC744 \uC774\uB044\uB294[m] \uC131\uC7A5 \uAD00\uB9AC \uD234, \uC624\uBE14\uB9BF",
    heroTitle: "\uC131\uC7A5\uC740 \uC774\uBCA4\uD2B8\uAC00 \uC544\uB2C8\uB77C \uB204\uC801\uC785\uB2C8\uB2E4.",
    heroSubtitle: "\uC5F0 1\uD68C \uD3C9\uAC00\uAC00 \uC544\uB2C8\uB77C,[m] \uB9E4\uC8FC\uC758 1on1\uC774 \uC131\uC7A5\uC744 \uB9CC\uB4ED\uB2C8\uB2E4.",
    problem: {
      title: "\uC65C \uC6B0\uB9AC \uD68C\uC0AC \uD3C9\uAC00\uB294[m] \uB298 \uC2DC\uB044\uB7EC\uC6B4\uAC00",
      quotes: [
        "1\uB144\uC5D0 \uD55C \uBC88 \uD3C9\uAC00\uBC1B\uACE0 \uB0A9\uB4DD \uBABB \uD558\uB294 \uD300\uC6D0",
        "\uD3C9\uAC00 \uD68C\uC758 \uC9C1\uC804\uC5D0\uC57C[m] 1\uB144 \uCE58 \uC131\uACFC\uB97C \uC950\uC5B4\uC9DC\uB294 \uB9AC\uB354",
        "\uC88B\uC740 \uB300\uD654\uB97C \uD588\uC9C0\uB9CC \uD55C \uB2EC \uB4A4\uC5D4[m] \uC561\uC158 \uC544\uC774\uD15C \uC99D\uBC1C",
        "'\uC870\uC6A9\uD55C \uD1F4\uC0AC' \uC9C1\uC804\uAE4C\uC9C0 \uC54C\uC544\uCC44\uC9C0[m] \uBABB\uD558\uB294 \uB9E4\uB2C8\uC9C0\uBA3C\uD2B8"
      ]
    },
    cpm: {
      title: "\uC5F0\uC18D\uC801 \uC131\uACFC \uAD00\uB9AC",
      body: "\uAE00\uB85C\uBC8C \uC120\uB3C4 \uAE30\uC5C5\uB4E4\uC740[m] \uC5F0 1\uD68C \uD3C9\uAC00\uC5D0\uC11C [b=#1a1a1a]\uC5F0\uC18D\uC801 \uC131\uACFC \uAD00\uB9AC[/b] \uD328\uB7EC\uB2E4\uC784\uC73C\uB85C[m][d] \uC804\uD658\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4."
    },
    cycle: {
      title: "\uC131\uC7A5 \uC0AC\uC774\uD074\uC758 \uC790\uB3D9\uD654",
      items: [
        {
          title: "\uC57D\uC18D: \uC561\uC158 \uC544\uC774\uD15C",
          desc: "1on1 \uC9C1\uD6C4 \uC561\uC158 \uC544\uC774\uD15C\uC774 \uC790\uB3D9 \uC0DD\uC131\uB429\uB2C8\uB2E4."
        },
        {
          title: "\uCD94\uC801: \uC790\uB3D9 \uD68C\uC218",
          desc: "\uB2E4\uC74C 1on1\uC5D0\uC11C \uC9C0\uB09C \uC561\uC158 \uC544\uC774\uD15C\uC744[m] \uC790\uB3D9\uC73C\uB85C \uAC00\uC838\uC635\uB2C8\uB2E4."
        },
        {
          title: "\uB204\uC801: AI \uB9AC\uD3EC\uD2B8",
          desc: "\uD300\uC6D0\uBCC4 \uB3D9\uAE30, \uAC15\uC810\xB7\uC57D\uC810, \uC774\uC9C1 \uC704\uD5D8\uC744 \uBD84\uC11D\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uC778\uC815: \uC131\uACFC \uBCF4\uAD00\uD568",
          desc: "\uD769\uC5B4\uC9C4 \uAE30\uC5EC\uAC00 \uD3C9\uAC00 \uADFC\uAC70\uB85C \uC790\uB3D9 \uC815\uB9AC\uB429\uB2C8\uB2E4."
        },
        {
          title: "\uBD84\uD3EC: \uD300 \uB2E8\uC704 \uAD00\uB9AC",
          desc: "9Box \uBAA8\uB378 \uAE30\uBC18\uC73C\uB85C \uD300 \uB2E8\uC704 \uBD84\uD3EC\uC640[m] \uC131\uC7A5\uC744 \uAD00\uB9AC\uD569\uB2C8\uB2E4."
        }
      ]
    },
    data: {
      title: "\uB370\uC774\uD130\uB85C \uBCF4\uB294 \uD55C \uBA85\uC758 \uC131\uC7A5",
      card: {
        avatar: "\uAE40",
        name: "\uAE40\uD604\uC6B0 \uD300\uC6D0",
        role: "\uB9C8\uCF00\uD305\uD300 / \uC785\uC0AC 2\uB144\uCC28",
        goalLabel: "\uD575\uC2EC \uBAA9\uD45C \uC131\uACFC",
        goal: "Q3 \uD575\uC2EC \uC2E0\uADDC \uACE0\uAC1D\uC0AC 10\uACF3 \uD655\uBCF4",
        progressLabel: "\uC9C4\uD589 \uD604\uD669",
        progress: "\uD0C0\uAC9F \uAE30\uC5C5 12\uAC1C\uC0AC \uBC1C\uAD74 \uBC0F[m] 8\uAC1C\uC0AC \uBBF8\uD305 \uC644\uB8CC\uB85C \uC21C\uD56D \uC911",
        tasksLabel: "\uC9C4\uD589 \uC911\uC778 \uC5C5\uBB34",
        tasks: [
          {
            bold: "B\uC0AC \uB9DE\uCDA4\uD615 \uC81C\uC548\uC11C",
            rest: " \uB0B4\uC6A9 \uBCF4\uAC15 \uBC0F[m] \uC774\uBC88 \uC8FC \uB0B4 \uC1A1\uBD80 \uC608\uC815"
          },
          {
            bold: "\uC2E0\uADDC \uC778\uBC14\uC6B4\uB4DC \uB9AC\uB4DC",
            rest: " \uD6C4\uC18D \uCF5C \uC9C4\uD589[m] (\uC8FC 3\uD68C \uC815\uAE30 \uC5F0\uB77D \uB2F4\uB2F9)"
          }
        ],
        motivationLabel: "\uC8FC\uC694 \uB3D9\uAE30 \uC694\uC778",
        motivations: ["\uC790\uC728\uC131", "\uC131\uCDE8\uAC10"],
        riskLabel: "\uC774\uC9C1 \uC704\uD5D8\uB3C4 (Flight Risk)",
        riskValue: "\uC548\uC815 (Low)"
      },
      features: [
        "\uD604\uC6B0\uB2D8\uC758 \uC2E4\uC2DC\uAC04 [b=#1a1a1a]\uBAA9\uD45C \uC9C4\uD589 \uD604\uD669[/b]\uACFC[m] [d]\uD604\uC7AC \uC5B4\uB5A4 [b=#1a1a1a]\uC5C5\uBB34[/b]\uC5D0 \uC9D1\uC911\uD558\uACE0 \uC788\uB294\uC9C0[m] \uD55C\uB208\uC5D0 \uD30C\uC545\uD558\uC138\uC694.",
        "\uC5B4\uB5A4 [b=#1a1a1a]\uB3D9\uAE30 \uC694\uC778[/b]\uC774 \uD604\uC6B0\uB2D8\uC744 \uC6C0\uC9C1\uC774\uB294\uC9C0[m] \uD655\uC778\uD558\uACE0,[d] \uC5C5\uBB34 \uBC30\uC815\uC5D0 \uC801\uADF9 \uBC18\uC601\uD558\uC138\uC694.",
        "[b=#1a1a1a]\uC774\uC9C1 \uC704\uD5D8\uB3C4[/b]\uAC00 \uC5B4\uB290 \uC2DC\uC810\uBD80\uD130 \uC62C\uB77C\uAC00\uACE0[m] \uC788\uB294\uC9C0[d] \uD30C\uC545\uD574 \uC120\uC81C\uC801\uC73C\uB85C \uB300\uC751\uD558\uC138\uC694."
      ]
    },
    transformMember: {
      title: "\uC624\uBE14\uB9BF 1on1 \uC815\uCC29\uC5D0 \uB530\uB978[m] \uAD6C\uC131\uC6D0\uC758 \uBCC0\uD654",
      asis: "\uC9C0\uC2DC \uACFC\uC5C5 \uCC98\uB9AC\uC5D0 \uBD84\uC8FC\uD55C[br] \uC218\uB3D9\uC801 \uC218\uD589\uC790",
      tobe: "\uC758\uBBF8\uB97C \uC774\uD574\uD558\uACE0 \uB85C\uB4DC\uB9F5\uC744[br] \uADF8\uB9AC\uB294 \uC131\uC7A5\uC758 \uC8FC\uC778\uACF5",
      points: [
        {
          before: "\uB9AC\uB354\uC758 \uC18D\uB9C8\uC74C\uC744 \uBAB0\uB77C[br] \uC804\uC804\uAE0D\uAE0D\uD558\uB294 \uD3C9\uAC00 \uBD88\uC548",
          after: "\uB0B4 \uC704\uCE58\uC640 \uC131\uACFC\uB97C \uBA85\uD655\uD788[m] \uC778\uC9C0\uD558\uB294[d] \uC608\uCE21 \uAC00\uB2A5\uD55C \uC548\uC815\uAC10"
        },
        {
          before: "\uC5F4\uC2EC\uD788 \uD574\uB3C4 \uC54C\uC544\uC8FC\uB294 \uC774 \uC5C6\uB294[br] \uC2EC\uB9AC\uC801 \uC18C\uC678",
          after: "\uB0B4 \uAE30\uC5EC\uAC00 \uC778\uC815\uBC1B\uACE0 \uC788\uC74C\uC744[br] \uCCB4\uAC10\uD558\uB294 \uC870\uC9C1 \uBAB0\uC785\uACFC \uD6A8\uB2A5\uAC10"
        }
      ]
    },
    transformHr: {
      title: "\uC624\uBE14\uB9BF 1on1 \uC815\uCC29\uC5D0 \uB530\uB978[m] HR\uC758 \uBCC0\uD654",
      asis: "\uADDC\uC815\uACFC \uC81C\uB3C4 \uC6B4\uC601\uC5D0 \uB9CC\uC871\uD558\uB294[br] \uD589\uC815 \uAD00\uB9AC\uC790",
      tobe: "\uB9AC\uB354\uB4E4\uC758 \uD604\uC7A5 \uC131\uACFC\uB97C \uB3D5\uB294[br] \uC804\uB7B5\uC801 \uD30C\uD2B8\uB108",
      points: [
        {
          before: "\uD575\uC2EC \uC778\uC7AC\uC758 \uAC11\uC791\uC2A4\uB7EC\uC6B4[m] \uD1F4\uC0AC \uD1B5\uBCF4\uC5D0[d] \uC18D\uC218\uBB34\uCC45",
          after: "\uC774\uD0C8 \uC9D5\uD6C4\uB97C \uC870\uAE30\uC5D0 \uD3EC\uCC29\uD558\uACE0[br] \uC120\uC81C\uC801 \uBC29\uC5B4 \uAC00\uB2A5"
        },
        {
          before: "\uD604\uC7A5\uC758 \uC9C4\uC9DC \uD604\uD669\uC744[m] \uC54C \uBC29\uB3C4\uAC00 \uC5C6\uB294[d] \uAE5C\uAE5C\uC774 \uAD00\uB9AC",
          after: "\uC870\uC9C1 \uC804\uBC18\uC758 \uAC74\uAC15/\uD3EC\uD150\uC15C\uC744[m] \uAC15\uD654\uD558\uB294[d] \uC5F0\uC18D\uC801\uC778 HR \uBD84\uC11D"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "\uD55C \uC0AC\uB78C\uC758 \uC131\uC7A5\uC774 \uBAA8\uC774\uBA74 \uC870\uC9C1\uC774[m] \uBC14\uB01D\uB2C8\uB2E4. \uADF8\uB2E4\uC74C\uC740?",
      cta: "\uBCC0\uD654 \uAD00\uB9AC \uC54C\uC544\uBCF4\uAE30"
    }
  },
  home: {
    hero: {
      badge: "Powered by AI",
      title: "\uC6D0\uC628\uC6D0 \uAE30\uBC18[br]\uB9AC\uB354\uC2ED \uC5ED\uB7C9 \uAC15\uD654 \uC194\uB8E8\uC158",
      subtitle: "\uB9AC\uB354\uC2ED\uC758 \uBCF8\uC9C8\uC740 \uC18C\uD1B5\uC5D0 \uC788\uC2B5\uB2C8\uB2E4.",
      primaryCta: "\uBB34\uB8CC\uB85C \uC2DC\uC791\uD558\uAE30",
      secondaryCta: "\uC18C\uAC1C\uC11C \uBC1B\uC544\uBCF4\uAE30",
      browserUrl: "1on1.orblit.space",
      tabs: [
        {
          id: "skill",
          label: "\uB9DE\uCDA4 \uB9AC\uB354\uC2ED \uCF54\uCE6D",
          title: "AI \uB300\uD654 \uCF54\uCE6D \uBC0F \uAC00\uC774\uB4DC",
          desc: "\uB300\uC0C1\uACFC \uC0C1\uD669\uC5D0 \uB530\uB978 \uBCC0\uC218\uAC00 \uB9CE\uC544 \uB300\uD654\uAC00 \uB9C9\uB9C9\uD560 \uB54C, \uD300\uC6D0 \uB9DE\uCDA4\uD615 1on1 \uC9C8\uBB38\uACFC \uB300\uD654\uC758 \uBC29\uD5A5\uC131\uC744 AI\uAC00 \uC81C\uC548\uD569\uB2C8\uB2E4.",
          sentence: "\uBB34\uC2A8 \uC9C8\uBB38\uC744 \uD574\uC57C \uD560\uC9C0, \uB300\uD654\uB97C \uC798\uD558\uACE0 [m]\uC788\uB294\uC9C0 \uB9C9\uB9C9\uD558\uC2DC\uC8E0?\nAI \uBD84\uC11D\uC73C\uB85C \uB300\uD654 \uC2A4\uD0C0\uC77C\uC744 \uD30C\uC545\uD558\uACE0, [m]\uB9DE\uCDA4 \uCF54\uCE6D\uC744 \uBC1B\uC544\uBCF4\uC138\uC694."
        },
        {
          id: "time",
          label: "AI \uC900\uBE44\xB7\uC815\uB9AC",
          title: "\uC6D0\uD074\uB9AD \uC2E4\uC2DC\uAC04 \uC790\uB3D9 \uAE30\uB85D",
          desc: "\uC0AC\uC804 \uC900\uBE44\uBD80\uD130 \uC0AC\uD6C4 \uB370\uC774\uD130\uD654\uAE4C\uC9C0 \uAC78\uB9AC\uB294 \uC218\uB9CE\uC740 \uAE30\uD68C\uBE44\uC6A9\uC744 \uC904\uC774\uACE0 \uB9AC\uB354\uAC00 \uB300\uD654\uC5D0\uB9CC \uC628\uC804\uD788 \uC9D1\uC911\uD560 \uC218 \uC788\uB3C4\uB85D \uB3D5\uC2B5\uB2C8\uB2E4.",
          sentence: "\uB9E4\uBC88 \uC900\uBE44\uD558\uACE0 \uB0B4\uC6A9\uC744 \uC815\uB9AC\uD560 \uC2DC\uAC04\uC774 [m]\uBD80\uC871\uD558\uC2DC\uC8E0?\n\uC0AC\uC804 \uC900\uBE44\uBD80\uD130 \uAE30\uB85D, \uC694\uC57D\uAE4C\uC9C0 [m]\uBC88\uAC70\uB85C\uC6B4 \uACFC\uC815\uC744 AI\uAC00 \uB300\uC2E0\uD574 \uB4DC\uB824\uC694."
        },
        {
          id: "context",
          label: "\uD300\uC6D0\uBCC4 \uAC00\uC774\uB4DC",
          title: "AI \uB9DE\uCDA4 \uC544\uC820\uB2E4 \uCD94\uCC9C",
          desc: "\uAD6C\uC131\uC6D0 \uAC01\uC790\uAC00 \uC5B4\uB5A4 \uC0C1\uD669\uC5D0 \uCC98\uD574\uC788\uACE0 \uB9AC\uB354\uC640 \uC5B4\uB5A4 \uC18C\uC911\uD55C \uC774\uC57C\uAE30\uB97C \uB098\uB204\uACE0 \uC2F6\uC740\uC9C0 \uC0AC\uC804\uC5D0 \uBA85\uD655\uD788 \uD30C\uC545\uD569\uB2C8\uB2E4.",
          sentence: "\uD300\uC6D0\uC774 \uC694\uC998 \uC5B4\uB5A4 \uACE0\uBBFC\uC744 \uD558\uACE0 \uC788\uB294\uC9C0[m] \uBAB0\uB77C \uB2F5\uB2F5\uD558\uC2DC\uC8E0?\n\uD300\uC6D0\uC774 \uAE30\uB85D\uD55C \uC815\uBCF4\uB97C AI\uAC00 \uC815\uB9AC\uD558\uACE0, [m]\uC9C8\uBB38\uACFC \uD568\uAED8 \uAC00\uC774\uB4DC\uB97C \uC81C\uC548\uD574\uB4DC\uB824\uC694."
        },
        {
          id: "continuity",
          label: "\uC5F0\uC18D\uC801 \uAD00\uB9AC",
          title: "\uD718\uBC1C \uC5C6\uB294 \uC5F0\uC18D\uC801 \uC131\uC7A5 \uAD00\uB9AC",
          desc: "\uBBF8\uD305 \uC774\uD6C4 \uC2E4\uCC9C \uC57D\uC18D\uB4E4\uC774 \uC81C\uB300\uB85C \uAD00\uB9AC\uB418\uC9C0 \uC54A\uACE0 \uD718\uBC1C\uB418\uC9C0 \uC54A\uB3C4\uB85D, \uB2E4\uC74C \uBBF8\uD305\uACFC \uC885\uD569 \uC131\uACFC/\uC548\uC815\uC131 \uC9C0\uD45C\uB85C \uB9E4\uB044\uB7FD\uAC8C \uC5F0\uACB0\uD569\uB2C8\uB2E4.",
          sentence: "\uC5B4\uB835\uAC8C \uB098\uB208 \uC88B\uC740 \uB300\uD654\uAC00 \uC77C\uD68C\uC131\uC73C\uB85C [m]\uB05D\uB098\uC2DC\uB098\uC694?\n\uB204\uC801 \uB370\uC774\uD130\uB97C \uAE30\uBC18\uC73C\uB85C \uB9AC\uD3EC\uD2B8\uB97C [m]\uC81C\uACF5\uD558\uACE0 \uB2E4\uC74C \uBBF8\uD305 \uC8FC\uC81C\uB97C [m]\uCD94\uCC9C\uB4DC\uB824\uC694."
        }
      ],
      sidebar: {
        menu: ["1on1 \uBBF8\uD305", "\uBA64\uBC84 \uB9AC\uD3EC\uD2B8", "9Box", "\uB300\uC2DC\uBCF4\uB4DC"],
        settings: "\uC124\uC815",
        profileInitial: "\uC774",
        profileName: "\uC774\uC720\uB9AC"
      },
      skillPanel: {
        memberInitial: "\uAE40",
        memberName: "\uAE40\uBA85\uC9C0",
        memberRatio: "53.6%",
        leaderAvatar: "\u{1F469}\u200D\u{1F4BC}",
        leaderName: "\uC774\uC720\uB9AC",
        leaderRatio: "46.4%",
        ownerAvatarAlt: "\uC561\uC158 \uC544\uC774\uD15C \uB2F4\uB2F9\uC790",
        feedbackTitle: "\uB9AC\uB354\uB97C \uC704\uD55C \uD53C\uB4DC\uBC31",
        strengthLabel: "\uAC15\uC810",
        strengthTitle: "\uBA64\uBC84 \uBC1C\uD654 \uAE30\uD68C \uBCF4\uC7A5",
        strengthBody: "\uC774\uC720\uB9AC \uB9AC\uB354\uB2D8\uC740 \uBA64\uBC84\uC758 \uBC1C\uD654 \uBE44\uC728(53.6%)\uBCF4\uB2E4 \uB0AE\uC740 46.4%\uB97C \uC720\uC9C0\uD558\uC5EC \uCDA9\uBD84\uD55C \uC774\uC57C\uAE30 \uAE30\uD68C\uB97C \uC81C\uACF5\uD588\uC2B5\uB2C8\uB2E4. \uBA64\uBC84\uAC00 \uB300\uD654\uB97C \uC2A4\uC2A4\uB85C \uC8FC\uB3C4\uD558\uBA70 \uD3B8\uC548\uD558\uAC8C \uC758\uACAC\uC744 \uC804\uD558\uB3C4\uB85D \uC774\uB048 \uC810\uC740 \uD6CC\uB96D\uD55C \uAC15\uC810\uC785\uB2C8\uB2E4.",
        improvementLabel: "\uBCF4\uC644\uC810",
        improvementTitle: "\uD6C4\uC18D \uC870\uCE58 \uC218\uB9BD \uBC0F \uC0C1\uD638 \uD569\uC758 \uBD80\uC871",
        improvementBody: "\uB300\uD654 \uB3C4\uC911 \uB3C4\uCD9C\uB41C \uAD6C\uC131\uC6D0\uC758 \uC560\uB85C\uC0AC\uD56D\uC774\uB098 \uC544\uC774\uB514\uC5B4\uC5D0 \uB300\uD574 \uAD6C\uCCB4\uC801\uC778 \uD6C4\uC18D \uC870\uCE58(Action Item)\uB97C \uD655\uC815\uD558\uACE0 \uD569\uC758\uD558\uB294 \uBD80\uBD84\uC774 \uB2E4\uC18C \uBBF8\uD761\uD588\uC2B5\uB2C8\uB2E4. \uB2E4\uC74C \uBBF8\uD305\uC5D0\uC11C\uB294 \uC0C1\uD638 \uC2E4\uD589\uD560 \uAD6C\uCCB4\uC801\uC778 \uC57D\uC18D\uC744 \uC815\uC758\uD558\uACE0 \uBA85\uD655\uD558\uAC8C \uC815\uB82C\uD574 \uBCF4\uC2DC\uAE30\uB97C \uC81C\uC548\uD569\uB2C8\uB2E4."
      },
      timePanel: {
        summaryTitle: "AI \uC694\uC57D",
        meetingTitle: "\uB300\uC2DC\uBCF4\uB4DC \uD504\uB85C\uC81D\uD2B8 \uC6B0\uC120\uC21C\uC704 \uC870\uC815",
        coreLabel: "\uD575\uC2EC \uB0B4\uC6A9",
        coreBody: "\uB300\uC2DC\uBCF4\uB4DC \uAC1C\uD3B8\uACFC \uB2E4\uC911 \uC791\uC5C5 \uBCD1\uD589\uC5D0 \uB530\uB978 \uAE40\uBA85\uC9C0\uB2D8\uC758 \uC5C5\uBB34 \uBD80\uB2F4\uAC10\uC744 \uD655\uC778\uD558\uACE0, \uC5C5\uBB34 \uD6A8\uC728 \uC81C\uACE0\uB97C \uC704\uD574 \uC6B0\uC120\uC21C\uC704 \uC870\uC815 \uD544\uC694\uC131\uC5D0 \uB300\uD574 \uC0C1\uD638 \uC870\uC728\uD568.",
        decisionLabel: "\uACB0\uC815\uC0AC\uD56D",
        decisions: [
          "\uAE40\uBA85\uC9C0\uB2D8\uC774 \uB300\uC2DC\uBCF4\uB4DC \uC6B0\uC120\uC21C\uC704 \uC815\uB9BD\uC744 \uC8FC\uB3C4\uD568.",
          "\uC5C5\uBB34 \uACFC\uBD80\uD558 \uC2DC \uD0C0 \uD300\uC6D0\uC744 \uCD94\uAC00 \uBC30\uCE58\uD568."
        ],
        supportLabel: "\uBB38\uC81C \uBC0F \uC9C0\uC6D0\uC694\uCCAD",
        issueTag: "[\uBB38\uC81C]",
        issueBody: "\uB300\uC2DC\uBCF4\uB4DC \uB0B4 \uB2E4\uC218 \uC5C5\uBB34 \uB3D9\uC2DC \uC9C4\uD589\uC73C\uB85C \uC778\uD55C \uC9D1\uC911 \uBD84\uC0B0 \uBC0F \uC77C\uC815 \uBD80\uB2F4.",
        supportTag: "[\uC9C0\uC6D0 \uC694\uCCAD]",
        supportBody: "\uC6B0\uC120\uC21C\uC704 \uC870\uC815 \uACFC\uC815\uC5D0\uC11C \uD0C0 \uD300\uC6D0 \uC9C0\uC6D0 \uBC0F \uD544\uC694 \uC2DC \uC870\uC728.",
        actionTitle: "\uC0C8 \uC561\uC158 \uC544\uC774\uD15C",
        actionAdd: "\uC0C8 \uC561\uC158 \uC544\uC774\uD15C \uCD94\uAC00",
        actionItems: [
          {
            text: "OKR \uB300\uC2DC\uBCF4\uB4DC \uB370\uC774\uD130 \uC18C\uC2A4 \uBB38\uC81C \uD574\uACB0",
            ai: false
          },
          {
            text: "\uBB38\uC11C \uC791\uC131 \uD15C\uD50C\uB9BF \uC81C\uC791",
            ai: true
          },
          {
            text: "\uD300 \uAC04 \uC77C\uC815 \uC870\uC728 \uD68C\uC758 \uC8FC\uAE30 \uAC80\uD1A0",
            ai: true
          },
          {
            text: "\uB300\uC2DC\uBCF4\uB4DC \uB9C8\uC77C\uC2A4\uD1A4 \uC8FC\uAC04 \uC9C4\uD589\uB3C4 \uD3C9\uAC00 \uC9C4\uD589",
            ai: true
          }
        ]
      },
      contextPanel: {
        cards: [
          {
            label: "\uC120\uD638 \uC18C\uD1B5\uBC29\uC2DD",
            value: "\uC0C1\uC138 \uB9E5\uB77D\uD615"
          },
          {
            label: "\uD6A8\uACFC\uC801 \uD53C\uB4DC\uBC31",
            value: "\uC194\uB8E8\uC158 \uC81C\uC2DC"
          },
          {
            label: "\uC9C0\uD5A5 \uCEE4\uB9AC\uC5B4",
            value: "\uB9AC\uB354\uD615"
          },
          {
            label: "\uB3D9\uAE30\uBD80\uC5EC \uC694\uC18C",
            value: "\uC778\uC815 \uBC0F \uD3C9\uD310"
          }
        ],
        conditionLabel: "\u{1F464} \uCEE8\uB514\uC158:",
        conditionValue: "\uC5C5\uBB34 \uBAB0\uC785\uB3C4 \uB192\uC74C \uBC0F \uAE0D\uC815\uC801\uC778 \uC5D0\uB108\uC9C0",
        topicLabel: "\u{1F3AF} \uD575\uC2EC \uC8FC\uC81C:",
        topicValue: "\uCC57\uBD07 \uAC1C\uBC1C \uACE0\uB3C4\uD654 \uC9C4\uD589 \uD604\uD669 \uBC0F \uBD84\uC11D",
        progressTitle: "1. \uC8FC\uC694 \uC5C5\uBB34 \uC9C4\uD589 \uD604\uD669",
        progressItems: [
          "AI \uACE0\uB3C4\uD654 QA \uC644\uB8CC \uD6C4 \uBA54\uC778 \uC11C\uBC84 \uBC18\uC601 \uBC0F \uC548\uC815\uD654",
          "\uCC57\uBD07 \uD575\uC2EC \uAE30\uB2A5 \uB9B4\uB9AC\uC988 \uBC0F \uC2E0\uADDC \uC2DC\uB098\uB9AC\uC624 \uD14C\uC2A4\uD2B8 \uC9C4\uD589 \uC911"
        ],
        challengeTitle: "2. \uD504\uB85C\uC81D\uD2B8 \uC911 \uC9C1\uBA74\uD55C \uC5B4\uB824\uC6C0",
        challengeItems: [
          "\uAE34\uAE09 \uC694\uAD6C\uC0AC\uD56D \uBC0F \uC9E7\uC740 \uC900\uBE44 \uC2DC\uAC04\uC73C\uB85C \uC5C5\uBB34 \uC555\uBC15\uAC10 \uCCB4\uAC10.",
          "\uD504\uB86C\uD504\uD2B8 \uD488\uC9C8 \uD3C9\uAC00 \uAE30\uC900 \uBD80\uC7AC\uB85C \uC778\uD55C \uC548\uC815\uC131 \uBAA8\uB2C8\uD130\uB9C1 \uD55C\uACC4."
        ],
        guideTitle: "AI \uBBF8\uD305 \uAC00\uC774\uB4DC",
        insightTitle: "\uD575\uC2EC \uC778\uC0AC\uC774\uD2B8",
        insightBody: "\uC5C5\uBB34 \uC131\uACFC\uB97C \uB3C4\uCD9C\uD558\uC600\uC73C\uB098 \uAC80\uC99D \uAE30\uC900 \uBD80\uC7AC\uB85C \uC778\uD55C \uC2EC\uB9AC\uC801 \uBD88\uC548\uC744 \uC778\uC9C0\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4.",
        recommendTitle: "\uAD8C\uC7A5\uC0AC\uD56D \uBC0F \uC8FC\uC758\uC0AC\uD56D",
        recommendBody: "\uB9C8\uAC10\uAE30\uD55C \uB0B4\uC5D0 \uC131\uACF5\uC801\uC73C\uB85C \uAE30\uB2A5 \uAD6C\uD604\uC744 \uD55C \uAC83\uC5D0 \uB300\uD574 \uCD95\uD558\uD558\uBA70 \uC0AC\uC6A9\uC790 \uBC18\uC751\uC5D0 \uB300\uD55C \uC9C8\uBB38\uC73C\uB85C \uB300\uD654\uB97C \uD480\uACE0 \uC560\uB85C\uC0AC\uD56D\uC744 \uACBD\uCCAD\uD558\uC2ED\uC2DC\uC624."
      },
      continuityPanel: {
        title: "\uAE40\uBA85\uC9C0\uB2D8 \uB9AC\uD3EC\uD2B8",
        flightRiskTitle: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4",
        flightRiskLevel: "\uB0AE\uC74C (Low)",
        flightRiskBody: "\uD604\uC7AC \uB300\uC2DC\uBCF4\uB4DC \uD504\uB85C\uC81D\uD2B8\uC758 \uC6B0\uC120\uC21C\uC704\uB97C \uC8FC\uB3C4\uC801\uC73C\uB85C \uC815\uC758\uD558\uACE0 \uC131\uACFC \uAE30\uC5EC\uB97C \uC9C0\uD5A5\uD558\uBBC0\uB85C \uC774\uC9C1 \uC704\uD5D8\uC740 \uB9E4\uC6B0 \uB0AE\uC740 \uC218\uC900\uC785\uB2C8\uB2E4.",
        motivationTitle: "\uB3D9\uAE30 \uC694\uC778",
        motivationTags: [
          "\uC8FC\uB3C4\uC801 \uD504\uB85C\uC81D\uD2B8",
          "\uC131\uACFC \uAC00\uCE58 \uC99D\uBA85",
          "\uC131\uC7A5 \uD559\uC2B5 \uAE30\uD68C",
          "\uB9AC\uB354 \uC778\uC815"
        ],
        demotivationTitle: "\uB3D9\uAE30 \uC800\uD558 \uC694\uC778",
        demotivationTags: [
          "\uBD88\uBA85\uD655\uD55C \uBAA9\uD45C",
          "\uB9AC\uC18C\uC2A4 \uD55C\uACC4",
          "\uC18C\uD1B5 \uBE44\uD6A8\uC728\uC131"
        ],
        strengthTitle: "\uAC15\uC810",
        strengthTags: [
          "\uBB38\uC81C \uD574\uACB0 \uB2A5\uB825",
          "\uC11C\uBE44\uC2A4 \uD1B5\uCC30\uB825",
          "\uC8FC\uB3C4\uC801 \uD0DC\uB3C4"
        ],
        weaknessTitle: "\uC57D\uC810",
        weaknessTags: [
          "\uACFC\uB3C4\uD55C \uC870\uC728 \uCC45\uC784",
          "\uCD94\uC0C1\uC801 \uBAA9\uD45C",
          "\uC77C\uC815 \uB2E4\uAE09\uC131"
        ]
      }
    },
    scrollReveal: {
      lines: [
        "\uC624\uBE14\uB9BF\uC5D0\uC11C 1on1\uC740 \uB2E8\uC21C\uD55C \uBA74\uB2F4\uC774 \uC544\uB2D9\uB2C8\uB2E4.",
        "\uB300\uD654\uC758 \uB9E5\uB77D\uC774 \uB370\uC774\uD130\uAC00 \uB418\uACE0,",
        "\uADF8 \uB370\uC774\uD130\uAC00 \uC870\uC9C1\uC758 \uC2E4\uC9C8\uC801\uC778 \uC131\uC7A5\uC744 \uC774\uB044\uB294",
        "\uAC00\uC7A5 \uD655\uC2E4\uD55C \uC9C0\uD45C\uAC00 \uB429\uB2C8\uB2E4."
      ],
      mobileBreakWords: ["1on1\uC740", "\uC870\uC9C1\uC758"]
    },
    coreProblem: {
      title: "1on1 \uBBF8\uD305,[m][d] [b=#0052FF]\uC815\uB9D0 \uC81C\uB300\uB85C[/b] \uD558\uACE0 \uACC4\uC2E0\uAC00\uC694?",
      subtitle: "\uB9AC\uB354\uC640 \uD300\uC6D0 \uBAA8\uB450\uC5D0\uAC8C \uC911\uC694\uD558\uC9C0\uB9CC,[m] \uD604\uC2E4\uC758 \uBCBD\uC5D0 \uBD80\uB52A\uD600 \uC2E4\uD328\uD558\uB294 \uACBD\uC6B0\uAC00 \uB9CE\uC2B5\uB2C8\uB2E4.",
      items: [
        {
          title: "\uD615\uC2DD\uC801\uC778 \uC131\uACFC\uBCF4\uACE0\uC5D0 \uBA38\uBB3C\uACE0 \uC788\uC9C0 \uC54A\uB098\uC694?",
          desc: "1on1\uC758 80% \uC774\uC0C1\uC774 '\uBD84\uAE30\uBCC4 \uC131\uACFC \uBA74\uB2F4' \uC218\uC900\uC5D0 \uBA38\uBB34\uB974\uBA70,[br]\uC8FC\uAE30\uAC00 \uAE38\uC5B4\uC9C8\uC218\uB85D \uD6A8\uACFC\uB294 \uAE09\uAC10\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uB9AC\uB354\uB4E4\uC758 \uBD80\uB2F4\uC744 \uBC29\uCE58\uD558\uACE0 \uC788\uC9C0 \uC54A\uB098\uC694?",
          desc: "1on1 \uC5F0\uAD00 \uBB38\uC11C \uC791\uC5C5\uC5D0 \uAD00\uB9AC\uC790 1\uC778\uB2F9 \uC5F0 210\uC2DC\uAC04\uC744 \uC18C\uBAA8\uD569\uB2C8\uB2E4.[br]\uB9AC\uB354\uC2ED\uC5D0 \uC9D1\uC911\uD560 \uC2DC\uAC04\uC744 \uBE7C\uC557\uAE41\uB2C8\uB2E4."
        },
        {
          title: "\uB370\uC774\uD130\uAC00 \uD30C\uD3B8\uD654/\uD718\uBC1C\uB418\uACE0 \uC788\uC9C0 \uC54A\uB098\uC694?",
          desc: "\uAE30\uC5C5\uC758 70% \uC774\uC0C1\uC774 \uB370\uC774\uD130\uD654 \uCCB4\uACC4\uAC00 \uC5C6\uC73C\uBA70,[br]\uC774\uB294 \uC2E4\uBB34\uC790 \uB9CC\uC871\uB3C4\uB97C 10% \uC774\uD558\uB85C \uB5A8\uC5B4\uD2B8\uB9BD\uB2C8\uB2E4."
        }
      ]
    },
    flowTabs: {
      title: "\uC644\uBCBD\uD55C 1on1\uC744 \uC704\uD55C [m][b=#0052FF]\uB04A\uC784\uC5C6\uB294 \uC131\uC7A5 \uC21C\uD658 \uAD6C\uC870[/b]",
      subtitle: "\uBBF8\uD305 \uC900\uBE44\uBD80\uD130 \uD6C4\uC18D \uAD00\uB9AC\uAE4C\uC9C0, \uB04A\uC5B4\uC9C0\uC9C0 \uC54A\uACE0 \uC790\uC5F0\uC2A4\uB7FD\uAC8C \uC774\uC5B4\uC9C0\uB294 \uC131\uC7A5\uC758 \uC0AC\uC774\uD074\uC744 \uD55C\uB208\uC5D0 \uD655\uC778\uD558\uC138\uC694.",
      sections: [
        {
          step: "STEP 01",
          title: "\uBBF8\uD305 \uC804",
          subtitle: "\uC644\uBCBD\uD55C \uC900\uBE44\uB97C \uC704\uD55C AI \uAC00\uC774\uB4DC",
          description: "1on1 \uC77C\uC815\uC774 \uB4F1\uB85D\uB418\uBA74 AI\uAC00 \uD300\uC6D0\uC758 \uC8FC\uC694 \uC815\uBCF4\uB97C \uC790\uB3D9\uC73C\uB85C \uC815\uB9AC\uD558\uACE0, \uBBF8\uD305\uC5D0 \uBC14\uB85C \uC0AC\uC6A9\uD560 \uC218 \uC788\uB294 \uB9DE\uCDA4\uD615 \uC9C8\uBB38\uACFC \uAC00\uC774\uB4DC\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
          features: [
            "\uC2E4\uC2DC\uAC04 \uC77C\uC815 \uC54C\uB9BC",
            "\uC815\uBCF4 \uC815\uB9AC AI \uCC57\uBD07",
            "\uB9DE\uCDA4\uD615 \uC9C8\uBB38/\uAC00\uC774\uB4DC"
          ],
          nodes: [
            {
              tagLabel: "Trigger",
              tagStatus: "Scheduled",
              tagColor: "gray",
              icon: "solar:calendar-date-bold-duotone",
              title: "\uC77C\uC815 \uB4F1\uB85D \uBC0F \uC54C\uB9BC",
              subtitle: "1on1 \uBBF8\uD305 \uC77C\uC815\uC744 \uAC10\uC9C0\uD574 \uC2E4\uC2DC\uAC04\uC73C\uB85C \uC54C\uB9BC\uC744 \uBC1C\uC1A1\uD569\uB2C8\uB2E4.",
              muted: true
            },
            {
              tagLabel: "AI Chatbot",
              tagStatus: "Analyzing",
              tagColor: "blue",
              icon: "solar:chat-square-like-bold-duotone",
              title: "AI \uCC57\uBD07: \uD300\uC6D0 \uC815\uBCF4 \uC815\uB9AC \uBC0F \uACF5\uC720",
              subtitle: "\uD300\uC6D0\uC758 \uACFC\uAC70 \uBA74\uB2F4 \uC774\uB825\uACFC \uCD5C\uC2E0 \uB370\uC774\uD130\uB97C \uCC3E\uC544\uC11C \uC694\uC57D \uC815\uB9AC\uD569\uB2C8\uB2E4."
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "\uB9DE\uCDA4\uD615 \uAC00\uC774\uB4DC \uBC0F \uC9C8\uBB38 \uC81C\uACF5",
              subtitle: "\uD574\uB2F9 \uBBF8\uD305 \uBAA9\uC801\uC5D0 \uAF2D \uC54C\uB9DE\uC740 \uC9C8\uBB38 \uB9AC\uC2A4\uD2B8\uC640 \uC544\uC820\uB2E4\uB97C \uC81C\uC548\uD569\uB2C8\uB2E4.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 02",
          title: "\uBBF8\uD305 \uC911",
          subtitle: "\uB300\uD654\uC5D0\uB9CC 100% \uBAB0\uC785",
          description: "\uAE30\uB85D\uC740 AI\uAC00 \uC790\uB3D9\uC73C\uB85C \uCC98\uB9AC\uD574 \uC8FC\uBBC0\uB85C, \uB9AC\uB354\uC640 \uD300\uC6D0\uC740 \uD0C0\uC774\uD551\uC5D0 \uCAD3\uAE30\uC9C0 \uC54A\uACE0 \uC624\uC9C1 \uC11C\uB85C \uB208\uC744 \uB9DE\uCD94\uBA70 \uAE4A\uC774 \uC788\uB294 \uB300\uD654\uC640 \uAD50\uAC10\uC5D0\uB9CC \uC628\uC804\uD788 \uC9D1\uC911\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
          features: [
            "\uBBF8\uD305 \uB179\uC74C",
            "\uACF5\uB3D9 \uAE30\uB85D \uACF5\uAC04",
            "\uB300\uD654 \uB0B4\uC6A9 STT"
          ],
          nodes: [
            {
              tagLabel: "Action",
              tagStatus: "Live",
              tagColor: "red",
              icon: "solar:play-circle-bold-duotone",
              title: "\uBBF8\uD305 \uB179\uC74C (\uC74C\uC131/\uD654\uC0C1)",
              subtitle: "\uBBF8\uD305\uC744 \uC2DC\uC791\uD560 \uB54C \uB179\uC74C\uC744 \uC2E4\uD589\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."
            },
            {
              tagLabel: "Editor",
              tagStatus: "Syncing",
              tagColor: "blue",
              icon: "solar:pen-new-round-bold-duotone",
              title: "\uBBF8\uD305 \uACF5\uB3D9 \uAE30\uB85D \uACF5\uAC04",
              subtitle: "\uB9AC\uB354\uC640 \uD300\uC6D0\uC774 \uBBF8\uD305 \uC8FC\uC81C\uC640 \uB0B4\uC6A9\uC744 \uC2E4\uC2DC\uAC04\uC73C\uB85C \uD568\uAED8 \uAE30\uB85D\uD569\uB2C8\uB2E4.",
              muted: true
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "STT",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI \uBBF8\uD305 \uB0B4\uC6A9 \uD14D\uC2A4\uD2B8\uD654",
              subtitle: "\uB300\uD654\uAC00 \uD718\uBC1C\uB418\uC9C0 \uC54A\uB3C4\uB85D AI\uAC00 \uB193\uCE68 \uC5C6\uC774 \uAE30\uB85D\uD569\uB2C8\uB2E4.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 03",
          title: "\uBBF8\uD305 \uD6C4",
          subtitle: "\uBC14\uB85C \uC81C\uACF5\uB418\uB294 \uC694\uC57D\uACFC \uC778\uC0AC\uC774\uD2B8",
          description: "\uBBF8\uD305\uC774 \uB9C8\uBB34\uB9AC\uB418\uBA74 \uB300\uD654 \uAE30\uB85D\uC744 \uAE30\uBC18\uC73C\uB85C AI\uAC00 \uC694\uC57D \uB9AC\uD3EC\uD2B8\uB97C \uC790\uB3D9 \uC644\uC131\uD558\uACE0, \uC2E4\uD589 \uACFC\uC81C \uC815\uB9AC\uBD80\uD130 \uB9DE\uCDA4\uD615 \uB9AC\uB354\uC2ED \uCF54\uCE6D \uD53C\uB4DC\uBC31\uAE4C\uC9C0 \uC644\uBCBD\uD558\uAC8C \uC9C0\uC6D0\uD569\uB2C8\uB2E4.",
          features: ["AI \uC790\uB3D9 \uC694\uC57D", "\uC561\uC158\uC544\uC774\uD15C", "\uB9AC\uB354\uC2ED \uCF54\uCE6D"],
          timelineNodes: [
            {
              tagLabel: "AI Analyst",
              tagStatus: "Structured",
              tagColor: "blue",
              icon: "solar:document-text-bold-duotone",
              title: "AI \uC790\uB3D9 \uC694\uC57D \uBC0F \uAD6C\uC870\uD654",
              subtitle: "\uBBF8\uD305\uC5D0\uC11C \uB098\uB208 \uC804\uCCB4 \uB300\uD654 \uB0B4\uC6A9\uC744 \uC2A4\uB9C8\uD2B8\uD558\uAC8C \uC790\uB3D9 \uC694\uC57D\uD569\uB2C8\uB2E4.",
              marker: "dot"
            },
            {
              tagLabel: "Action Items",
              tagStatus: "Organized",
              tagColor: "green",
              icon: "solar:check-square-bold-duotone",
              title: "\uC561\uC158\uC544\uC774\uD15C \uC790\uB3D9 \uC815\uB9AC",
              subtitle: "\uB2F4\uB2F9\uC790\uBCC4 \uB2E4\uC74C \uC2E4\uD589 \uACFC\uC81C\uB97C \uC790\uB3D9\uC73C\uB85C \uCD94\uCD9C\uD558\uC5EC \uB4F1\uB85D\uD569\uB2C8\uB2E4.",
              marker: "mic"
            },
            {
              tagLabel: "AI Coach",
              tagStatus: "Coached",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI \uB9AC\uB354\uC2ED \uCF54\uCE6D",
              subtitle: "1on1 \uC2A4\uD06C\uB9BD\uD2B8\uC640 \uB9AC\uB354\uC758 \uC18C\uD1B5 \uBC29\uC2DD\uC744 \uBD84\uC11D\uD574 \uB9DE\uCDA4\uD615 \uD53C\uB4DC\uBC31\uC744 \uC81C\uACF5\uD569\uB2C8\uB2E4.",
              marker: "dot",
              accent: true
            }
          ]
        },
        {
          step: "STEP 04",
          title: "\uC5F0\uC18D\uC801 \uAD00\uB9AC",
          subtitle: "\uC9C0\uC18D\uC801\uC778 \uC131\uC7A5\uACFC \uC5F0\uC18D\uC131\uC744 \uB9CC\uB4DC\uB294 \uC21C\uD658 \uAD6C\uC870",
          description: "\uBBF8\uD305 \uB370\uC774\uD130\uAC00 \uCD95\uC801\uB418\uBA74 \uAC00\uCE58 \uC788\uB294 \uC790\uC0B0\uC774 \uB429\uB2C8\uB2E4.[d]\uC774\uB97C \uD1A0\uB300\uB85C \uD300\uC6D0 \uB9AC\uD3EC\uD2B8\uC640 \uD604\uD669 \uB300\uC2DC\uBCF4\uB4DC\uAC00 \uC0DD\uC131\uB418\uACE0,[d]\uB2E4\uC74C 1on1 \uBBF8\uD305 \uC900\uBE44\uC5D0 \uC989\uAC01 \uC5F0\uACB0\uB429\uB2C8\uB2E4.",
          features: [
            "\uD300\uC6D0 \uC778\uC0AC\uC774\uD2B8 \uB9AC\uD3EC\uD2B8",
            "\uC870\uC9C1 \uD604\uD669 \uB300\uC2DC\uBCF4\uB4DC",
            "\uB2E4\uC74C 1on1 \uBBF8\uD305 \uC900\uBE44\uC5D0 \uC790\uB3D9 \uC5F0\uACB0"
          ],
          topBadge: "1on1 \uBBF8\uD305 \uB370\uC774\uD130 \uB204\uC801",
          bottomBadge: "\uBD84\uC11D \uB370\uC774\uD130\uAC00 \uB2E4\uC74C 1on1 \uC900\uBE44\uC5D0 \uBC18\uC601",
          nodes: [
            {
              tagLabel: "AI Insight",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "\uD300\uC6D0 \uC778\uC0AC\uC774\uD2B8 \uB9AC\uD3EC\uD2B8",
              subtitle: "\uBBF8\uD305 \uB0B4\uC5ED\uB4E4\uC744 \uC885\uD569\uD574 \uD300\uC6D0\uC758 \uB3D9\uAE30, \uAC15\uC57D\uC810, \uB9AC\uC2A4\uD06C\uB97C \uBCF4\uC5EC\uC8FC\uB294 \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4.",
              accent: true
            },
            {
              tagLabel: "Dashboard",
              tagStatus: "Updated",
              tagColor: "blue",
              icon: "solar:chart-square-bold-duotone",
              title: "\uC870\uC9C1 \uD604\uD669 \uB300\uC2DC\uBCF4\uB4DC",
              subtitle: "\uC804\uC0AC \uBD80\uC11C\uBCC4/\uD300\uBCC4 \uBBF8\uD305 \uC9C4\uD589 \uD604\uD669\uACFC \uB9AC\uB354\uC2ED \uC9C0\uD45C\uB97C \uC2DC\uAC01\uD654\uD569\uB2C8\uB2E4."
            }
          ]
        }
      ]
    },
    coreBenefits: {
      title: "Orblit\uACFC \uD568\uAED8\uD558\uB294 [m][b=#0052FF]\uAC74\uAC15\uD55C \uB300\uD654\uC758 \uC2DC\uC791[/b]",
      subtitle: "\uB9AC\uB354, \uD300\uC6D0, \uC778\uC0AC\uB2F4\uB2F9\uC790 \uBAA8\uB450\uC5D0\uAC8C \uC2E4\uC9C8\uC801\uC774\uACE0 [m]\uC720\uC758\uBBF8\uD55C \uBCC0\uD654\uB97C \uB9CC\uB4E4\uC5B4\uB0C5\uB2C8\uB2E4.",
      items: [
        {
          target: "\uB9AC\uB354",
          title: "\uC131\uC7A5\uC744 \uB3D5\uB294 \uCF54\uCE58",
          desc: "\uAE30\uB85D\uACFC \uC900\uBE44\uC758 \uBD80\uB2F4\uC5D0\uC11C \uBC97\uC5B4\uB098, \uD300\uC6D0\uC758 \uBAA9\uC18C\uB9AC\uC5D0 100% \uC9D1\uC911\uD558\uACE0 \uACF5\uAC10\uD558\uB294 \uB9AC\uB354\uAC00 \uB429\uB2C8\uB2E4."
        },
        {
          target: "\uD300\uC6D0",
          title: "\uC8FC\uB3C4\uC801 \uC131\uC7A5\uC758 \uD30C\uD2B8\uB108",
          desc: "\uD3C9\uAC00\uC5D0 \uB300\uD55C \uB450\uB824\uC6C0\uC744 \uB0B4\uB824\uB193\uACE0, \uCEE4\uB9AC\uC5B4\uC640 \uACE0\uBBFC\uC744 \uC9C4\uC194\uD558\uAC8C \uB098\uB204\uBA70 \uC2EC\uB9AC\uC801 \uC548\uC804\uAC10\uC744 \uC5BB\uC2B5\uB2C8\uB2E4."
        },
        {
          target: "\uC778\uC0AC \uB2F4\uB2F9\uC790",
          title: "\uB370\uC774\uD130 \uAE30\uBC18 \uC870\uC9C1 \uC124\uACC4\uC790",
          desc: "\uB2E8\uC21C \uBA74\uB2F4 \uC9C4\uD589\uB960 \uCCB4\uD06C\uB97C \uB118\uC5B4, \uAC1D\uAD00\uC801\uC778 \uB370\uC774\uD130\uC5D0 \uAE30\uBC18\uD55C \uC120\uC81C\uC801\uC774\uACE0 \uC804\uB7B5\uC801\uC778 \uC870\uC9C1 \uC6B4\uC601\uC774 \uAC00\uB2A5\uD574\uC9D1\uB2C8\uB2E4."
        }
      ]
    },
    customerInterviews: {
      badge: "Customer story",
      companyPrefix: "\uACE0\uAC1D\uC0AC",
      goToSlide: "{{number}}\uBC88 \uC2AC\uB77C\uC774\uB4DC\uB85C \uC774\uB3D9",
      items: [
        {
          company: "\uD55C\uC194\uD648\uB370\uCF54",
          person: "\uC11C\uADDC\uD638 \uCC45\uC784",
          title: "\uBE44\uC804\uACFC \uC804\uB7B5\uC744 \uC5F0\uACB0\uD558\uB294 \uC6D0\uC628\uC6D0 \uBB38\uD654\uC758 \uC815\uCC29",
          body: "\uC870\uC9C1\uC758 \uBE44\uC804\uACFC \uC804\uB7B5\uC774 \uAD6C\uC131\uC6D0\uC758 \uC2E4\uD589\uC73C\uB85C \uC774\uC5B4\uC9C0\uAE30 \uC704\uD574\uC11C\uB294\n\uC9C0\uC18D\uC801\uC778 \uC6D0\uC628\uC6D0 \uB300\uD654\uB97C \uD1B5\uD55C \uBAA9\uD45C \uC815\uB82C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.\n\uD55C\uC194\uD648\uB370\uCF54\uB294 \uC624\uBE14\uB9BF\uC744 \uD65C\uC6A9\uD558\uC5EC \uB9AC\uB354\uC758 \uC6D0\uC628\uC6D0 \uC5ED\uB7C9\uC744 \uAC15\uD654\uD558\uACE0,\n\uAD6C\uC131\uC6D0\uACFC\uC758 \uC815\uAE30\uC801\uC778 \uB300\uD654 \uBB38\uD654\uB97C \uC815\uCC29\uC2DC\uCF1C \uC870\uC9C1\uC758 \uC804\uB7B5\uC774 \uD604\uC7A5\uC5D0\uC11C\n\uC2E4\uD589\uB420 \uC218 \uC788\uB294 \uAE30\uBC18\uC744 \uB9C8\uB828\uD558\uACE0 \uC788\uC2B5\uB2C8\uB2E4."
        },
        {
          company: "\uC5B8\uB354\uBC00\uB9AC",
          person: "\uC774\uD638\uC885 \uB300\uD45C",
          title: "\uCD08\uC784 \uB9AC\uB354\uC758 \uB9C9\uC5F0\uD55C \uB300\uD654\uB97C \uC0DD\uC0B0\uC801\uC778 \uC0C1\uD638\uC791\uC6A9\uC73C\uB85C",
          body: "\uC0AC\uB2F4\uC740 \uC27D\uC9C0\uB9CC,\n'\uC131\uC7A5\uACFC \uC131\uACFC\uB97C \uC704\uD55C \uB300\uD654'\uB294 \uC0DD\uAC01 \uC774\uC0C1\uC73C\uB85C \uB9C9\uC5F0\uD558\uACE0 \uC5B4\uB835\uC2B5\uB2C8\uB2E4.\n\uC624\uBE14\uB9BF\uC740 \uB204\uB77D \uC5C6\uB294 \uAD6C\uC131\uC6D0 \uC815\uBCF4\uB97C\n\uBC14\uD0D5\uC73C\uB85C \uC0DD\uC0B0\uC801\uC778 \uB300\uD654\uC758 \uD2C0\uC744 \uC7A1\uC544\uC8FC\uC5C8\uC2B5\uB2C8\uB2E4.\n\uD2B9\uD788 \uC2E4\uC81C \uBC1C\uD654 \uAE30\uBC18 AI \uCF54\uCE6D\uC740,\n\uCD08\uC784 \uB9AC\uB354\uB4E4\uC758 \uC790\uAE30\uAC1D\uAD00\uD654\uC640 \uC131\uC7A5\uC758 \uBC84\uD300\uBAA9\uC774 \uB418\uC5C8\uC2B5\uB2C8\uB2E4."
        },
        {
          company: "\uADF8\uB77C\uC2A4\uBA54\uB514",
          person: "\uCD5C\uC9C4\uC2DD \uB300\uD45C",
          title: "\uB9C9\uC5F0\uD588\uB358 \uB9E4\uB2C8\uC9C0\uBA3C\uD2B8\uB97C \uAC00\uC7A5 \uD655\uC2E4\uD55C \uC194\uB8E8\uC158\uC73C\uB85C",
          body: "\uB9AC\uB354\uC758 \uAC00\uC7A5 \uD070 \uACE0\uBBFC\uC778 '\uC778\uC0AC'\uC640 '\uC704\uC784',\n\uADF8 \uBA85\uCF8C\uD55C \uD574\uB2F5\uC740 \uD300\uC6D0\uACFC\uC758 \uC6D0\uC628\uC6D0\uC5D0 \uC788\uC2B5\uB2C8\uB2E4.\n\uAC00\uC7A5 \uAC00\uAE4C\uC6B4 \uACF3\uC5D0\uC11C \uC18C\uD1B5\uD558\uBA70 \uC131\uACFC\uB97C \uCE21\uC815\uD558\uACE0 \uC131\uC7A5\uC744 \uB3D5\uB294 \uACBD\uD5D8,\n\uC624\uBE14\uB9BF\uC740 \uB9C9\uC5F0\uD588\uB358 \uB9E4\uB2C8\uC9C0\uBA3C\uD2B8\uB97C \uAC00\uC7A5 \uD655\uC2E4\uD558\uACE0 \uAD6C\uCCB4\uC801\uC778 \uC194\uB8E8\uC158\uC73C\uB85C \uBC14\uAFD4\uC8FC\uC5C8\uC2B5\uB2C8\uB2E4."
        }
      ]
    },
    whatIsOrblit: {
      title: "\uC624\uBE14\uB9BF\uC740 \uB2E8\uC21C\uD55C \uC790\uB3D9\uD654 \uD234\uC774 \uC544\uB2D9\uB2C8\uB2E4.",
      body: "\uC870\uC9C1\uC5D0 \uAC74\uAC15\uD55C \uB300\uD654\uAC00 \uD750\uB974\uAC8C \uD558\uB294 \uC18C\uD1B5 \uAC15\uD654 \uD234\uC774\uACE0,[d]\uC18C\uD1B5\uC744 \uD1B5\uD574 \uD300\uC6D0\uB4E4\uC758 \uC131\uC7A5\uC744 \uC774\uB044\uB294 \uC131\uC7A5 \uAD00\uB9AC \uD234\uC774\uBA70,[d]\uACBD\uC601\uC9C4\uC758 \uBE44\uC804\uACFC \uC804\uB7B5\uC744 \uAD6C\uC11D\uAD6C\uC11D \uD37C\uB728\uB9AC\uB294 \uBCC0\uD654 \uAD00\uB9AC \uD234\uC785\uB2C8\uB2E4.",
      nodeTitle: "1on1 \uAE30\uBC18 \uB9AC\uB354\uC2ED",
      closing: "\uC774 \uBAA8\uB4E0 \uAC83\uC740 1on1\uC5D0 \uAE30\uBC18\uD55C [m]\uAC15\uB825\uD55C \uB9AC\uB354\uC2ED\uC5D0\uC11C \uC2DC\uC791\uB429\uB2C8\uB2E4.",
      cards: [
        {
          to: "/why/communication",
          title: "\uC18C\uD1B5 \uAC15\uD654",
          desc: '\uC870\uC9C1\uC5D0 \uAC74\uAC15\uD55C \uB300\uD654\uAC00 \uD750\uB974\uAC8C \uD569\uB2C8\uB2E4. \uB9AC\uB354\uAC00 "\uBB34\uC2A8 \uB9D0\uC744 \uD560\uC9C0" \uC54C\uAC8C \uB418\uACE0, \uD300\uC6D0\uC774 "\uC9C4\uC9DC \uC18D\uB9C8\uC74C\uC744 \uAEBC\uB0BC \uC218 \uC788\uAC8C" \uB9CC\uB4ED\uB2C8\uB2E4.'
        },
        {
          to: "/why/growth",
          title: "\uC131\uC7A5 \uAD00\uB9AC",
          desc: "\uC18C\uD1B5\uC744 \uD1B5\uD574 \uD300\uC6D0\uB4E4\uC758 \uC131\uC7A5\uC744 \uC774\uB055\uB2C8\uB2E4. \uB300\uD654\uC5D0\uC11C \uB098\uC628 \uC57D\uC18D\uC744 \uD758\uB824\uBCF4\uB0B4\uC9C0 \uC54A\uACE0 \uC131\uACFC\xB7\uD3C9\uAC00\xB7\uC131\uC7A5\uC73C\uB85C \uC5F0\uACB0\uD569\uB2C8\uB2E4."
        },
        {
          to: "/why/change",
          title: "\uBCC0\uD654 \uAD00\uB9AC",
          desc: "\uACBD\uC601\uC9C4\uC758 \uBE44\uC804\uACFC \uC804\uB7B5\uC744 \uAD6C\uC11D\uAD6C\uC11D \uD37C\uB728\uB9BD\uB2C8\uB2E4. \uB9AC\uB354\uB4E4\uC758 \uC785\uC744 \uD1B5\uD574 \uD604\uC7A5 \uB05D\uAE4C\uC9C0 \uC804\uB2EC\uD558\uACE0 \uADF8 \uACB0\uACFC\uB97C \uAC00\uC2DC\uD654\uD569\uB2C8\uB2E4."
        }
      ]
    },
    impactStats: {
      title: "\uAC00\uC7A5 \uD655\uC2E4\uD55C[br]\uC870\uC9C1 \uBB38\uD654 \uAC1C\uC120 \uD6A8\uACFC.",
      subtitle: "\uC624\uBE14\uB9BF\uC744 \uD1B5\uD574 1on1\uC758 \uC9C8\uC744 \uB192\uC774\uACE0 \uCE21\uC815\uC744 \uC790\uB3D9\uD654\uD558\uBA74 \uC555\uB3C4\uC801\uC778 \uC131\uACFC \uC218\uCE58\uB97C \uD655\uC778\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      stats: [
        {
          value: 200,
          suffix: "%",
          label: "1on1 \uD65C\uC131\uD654\uC728"
        },
        {
          value: 2,
          suffix: "X",
          label: "\uB9CC\uC871\uB3C4 \uC0C1\uC2B9"
        },
        {
          value: 84,
          prefix: "-",
          suffix: "%",
          label: "\uD589\uC815 \uC2DC\uAC04 \uC808\uAC10"
        },
        {
          value: 20,
          prefix: "-",
          suffix: "%",
          label: "\uC774\uC9C1\uB960 \uAC10\uC18C"
        }
      ]
    },
    momentumProgram: {
      eyebrow: "1on1 \uC548\uCC29 \uC194\uB8E8\uC158",
      title: "\uC624\uBE14\uB9BF \uBAA8\uBA58\uD140 \uD504\uB85C\uADF8\uB7A8",
      plan: "(3\uAC1C\uC6D4 \uD50C\uB79C)",
      body: "\uB9AC\uB354\uC2ED \uC815\uCC29\uC758 \uD575\uC2EC\uC778 1on1\uC744 \uC6B0\uB9AC \uC870\uC9C1\uC5D0 \uB2E8\uACC4\uC801\uC73C\uB85C \uBB34\uB9AC \uC5C6\uC774 \uC548\uCC29\uC2DC\uD0A4\uB294 3\uAC1C\uC6D4 \uD328\uD0A4\uC9C0 \uD504\uB85C\uADF8\uB7A8\uC785\uB2C8\uB2E4.[br]\uC77C\uBC18\uC801\uC778 \uB2E8\uBC1C\uC131 \uAE30\uC5C5 \uAC15\uC5F0\uC758 \uD55C\uACC4\uB97C \uC815\uBC00\uD558\uAC8C \uADF9\uBCF5\uD558\uAE30 \uC704\uD574 \uC624\uBE14\uB9BF \uD234\uACFC \uC2E4\uBB34 \uAD50\uC721\uC744 [n]\uAE34\uBC00\uD558\uAC8C \uACB0\uD569\uD588\uC2B5\uB2C8\uB2E4.[/n]",
      points: [
        {
          title: "\uB9AC\uB354\uC640 \uD300\uC6D0\uC758 \uB3D9\uBC18 \uB9C8\uC778\uB4DC\uC14B & \uC2A4\uD0AC \uAD50\uC721",
          description: "\uC65C \uB300\uD654\uB97C \uD574\uC57C \uD558\uB294\uC9C0 \uB9AC\uB354\uC640 \uD300\uC6D0 \uBAA8\uB450\uC758 \uACF5\uAC10\uB300\uB97C \uAE4A\uC219\uC774 \uAD6C\uCD95\uD569\uB2C8\uB2E4. \uB9AC\uB354\uB4E4\uC5D0\uAC8C\uB294 \uACBD\uCCAD, \uD53C\uB4DC\uBC31, \uC9C8\uBB38 \uAE30\uC220 \uB4F1 \uC989\uC2DC \uD65C\uC6A9 \uAC00\uB2A5\uD55C \uC2E4\uC804 \uB178\uD558\uC6B0\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uC624\uBE14\uB9BF \uD234 \uD65C\uC6A9\uC744 \uD1B5\uD55C \uB2E8\uBC1C\uC131 \uD6A8\uACFC \uBC29\uC9C0",
          description: "\uC2E4\uBB34\uC5D0 \uC801\uC6A9\uD558\uACE0 \uC9C1\uC811 \uC6D0\uC628\uC6D0\uC744 \uC9C4\uD589\uD569\uB2C8\uB2E4. \uB9DE\uCDA4\uD615 \uC9C8\uBB38\uACFC \uD53C\uB4DC\uBC31 \uC2DC\uC2A4\uD15C\uC774 1on1 \uC5ED\uB7C9\uACFC \uD6A8\uACFC\uB97C \uC9C0\uC18D\uC801\uC73C\uB85C \uB192\uC785\uB2C8\uB2E4."
        },
        {
          title: "\uC2E4\uC804 \uB370\uC774\uD130 \uB9AC\uD3EC\uD2B8 & \uB9DE\uCDA4\uD615 \uD6C4\uC18D \uAD50\uC721",
          description: "\uCD95\uC801\uB41C 1on1 \uB370\uC774\uD130\uB97C \uBC14\uD0D5\uC73C\uB85C \uB9AC\uD3EC\uD2B8\uB97C \uC81C\uACF5\uD569\uB2C8\uB2E4. \uB370\uC774\uD130 \uBD84\uC11D\uC5D0\uC11C \uBC1C\uACAC\uB41C \uC774\uC288\uB97C \uC885\uD569\uD558\uC5EC \uD6C4\uC18D \uB9DE\uCDA4 \uAD50\uC721 \uC194\uB8E8\uC158\uC744 \uC774\uC5B4\uAC11\uB2C8\uB2E4."
        }
      ],
      imageAlt: "\uD604\uB300\uC801\uC778 \uD68C\uC758\uC2E4\uC5D0\uC11C \uAE30\uC5C5 1on1 \uBC0F \uB9AC\uB354\uC2ED \uD2B8\uB808\uC774\uB2DD\uC744 \uC9C4\uD589\uD558\uB294 \uC544\uC2DC\uC544\uC778 \uB0A8\uC131 \uD2B8\uB808\uC774\uB108",
      fallbackEyebrow: "Momentum Program",
      fallbackTitle: "\uAD50\uC721, \uD234, \uB9AC\uD3EC\uD2B8\uAC00 \uD558\uB098\uB85C \uC774\uC5B4\uC9C0\uB294[br]1on1 \uC548\uCC29 \uD504\uB85C\uADF8\uB7A8",
      cta: "\uD504\uB85C\uADF8\uB7A8 \uC18C\uAC1C\uC11C \uBCF4\uAE30"
    },
    logos: {
      title: "\uAD6D\uB0B4\uC678 \uCD5C\uACE0\uC758 HR \uC804\uBB38\uAC00\uB4E4\uC774 \uD568\uAED8 \uC124\uACC4\uD588\uC2B5\uB2C8\uB2E4",
      items: [
        {
          name: "Dell",
          desc: "[\u524D APAC \uBD80\uC0AC\uC7A5]"
        },
        {
          name: "SAMSUNG",
          desc: "[\u524D \uC0BC\uC131 \uC778\uC0AC/\uAD50\uC721]"
        },
        {
          name: "PennState",
          desc: "[HR/\uC870\uC9C1\uAC1C\uBC1C \uAD50\uC218\uC9C4]"
        },
        {
          name: "George Mason",
          desc: "[\uC0B0\uC5C5\uC2EC\uB9AC\uD559 \uAD50\uC218\uC9C4]"
        }
      ]
    },
    security: {
      title: "\uACE0\uAC1D\uC0AC\uC758 \uB370\uC774\uD130\uB294[br]\uAC00\uC7A5 \uC548\uC804\uD558\uAC8C \uC9C0\uCF1C\uC9D1\uB2C8\uB2E4.",
      features: [
        {
          title: "\uB370\uC774\uD130 \uC554\uD638\uD654",
          description: "\uBAA8\uB4E0 \uBBF8\uD305 \uAE30\uB85D\uC740 \uC800\uC7A5 \uC2DC AES-256, \uC804\uC1A1 \uC2DC TLS 1.3\uC73C\uB85C \uC554\uD638\uD654\uB429\uB2C8\uB2E4. \uC0DD\uC131\uBD80\uD130 \uBCF4\uAD00\uAE4C\uC9C0 \uC804 \uAD6C\uAC04\uC5D0\uC11C \uB370\uC774\uD130\uAC00 \uBCF4\uD638\uB429\uB2C8\uB2E4."
        },
        {
          title: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB2E8\uC704 \uB370\uC774\uD130 \uACA9\uB9AC",
          description: "\uD589 \uC218\uC900 \uBCF4\uC548(RLS)\uACFC \uC5ED\uD560 \uAE30\uBC18 \uC811\uADFC \uC81C\uC5B4(RBAC)\uB85C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uAC04 \uB370\uC774\uD130\uB97C \uC644\uC804\uD788 \uBD84\uB9AC\uD569\uB2C8\uB2E4. \uACE0\uAC1D \uB370\uC774\uD130\uB294 Orblit \uC804\uC6A9 DB\uC5D0 \uC800\uC7A5\uB418\uC5B4 \uACE0\uAC1D\uC0AC \uAE30\uC874 DB\uC640 \uBB3C\uB9AC\uC801\uC73C\uB85C \uBD84\uB9AC\uB429\uB2C8\uB2E4."
        },
        {
          title: "AI \uBAA8\uB378 \uD559\uC2B5 \uBBF8\uC0AC\uC6A9",
          description: "\uC0AC\uC6A9\uC790\uC758 \uBBF8\uD305 \uB370\uC774\uD130\uB294 AI \uBAA8\uB378 \uD559\uC2B5\uC774\uB098 \uC131\uB2A5 \uAC1C\uC120\uC5D0 \uC77C\uC808 \uC0AC\uC6A9\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4. AI \uBD84\uC11D \uC81C\uACF5\uC0AC(Google Vertex AI)\uC640\uC758 \uC5D4\uD130\uD504\uB77C\uC774\uC988 \uC57D\uAD00\uC5D0 \uD559\uC2B5 \uAE08\uC9C0\uAC00 \uBA85\uC2DC\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4."
        },
        {
          title: "\uC5ED\uD560 \uAE30\uBC18 \uC811\uADFC \uC81C\uC5B4",
          description: "\uC5ED\uD560 \uAE30\uBC18 \uC811\uADFC \uC81C\uC5B4(RBAC)\uB97C \uC801\uC6A9\uD558\uC5EC \uC870\uC9C1 \uB0B4 \uB370\uC774\uD130 \uC811\uADFC \uAD8C\uD55C\uC744 \uAD00\uB9AC\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uB179\uC74C \uD30C\uC77C \uC790\uB3D9 \uD3D0\uAE30",
          description: "\uC74C\uC131 \uB179\uC74C \uD30C\uC77C\uC740 \uBD84\uC11D \uC644\uB8CC \uD6C4 3\uC77C \uB0B4 \uC790\uB3D9 \uC0AD\uC81C\uB418\uBA70, \uBCC0\uD658\uB41C \uD14D\uC2A4\uD2B8 \uBD84\uC11D \uACB0\uACFC\uB9CC \uBCF4\uAD00\uB429\uB2C8\uB2E4."
        },
        {
          title: "\uBCC0\uACBD \uC774\uB825 \uAC10\uC0AC \uB85C\uADF8",
          description: "\uD575\uC2EC \uB370\uC774\uD130\uC758 \uBAA8\uB4E0 \uBCC0\uACBD \uC774\uB825\uC744 \uC790\uB3D9 \uAE30\uB85D\uD569\uB2C8\uB2E4. \uBCC0\uACBD \uC804\xB7\uD6C4 \uAC12, \uC2DC\uAC01, \uC218\uD589 \uC0AC\uC6A9\uC790\uAC00 \uB0A8\uC544 \uCD94\uC801\uC774 \uAC00\uB2A5\uD558\uBA70, \uC774 \uAE30\uB85D\uC740 \uC784\uC758 \uC218\uC815\uC774 \uBD88\uAC00\uB2A5\uD569\uB2C8\uB2E4."
        },
        {
          title: "\uD1B5\uC2E0 \uAD6C\uAC04 \uBCF4\uC548",
          description: "\uBAA8\uB4E0 API \uD1B5\uC2E0\uC740 HTTPS\uB85C\uB9CC \uCC98\uB9AC\uB418\uACE0 HTTP \uC811\uADFC\uC740 \uCC28\uB2E8\uB429\uB2C8\uB2E4. \uD5C8\uC6A9\uB41C \uB3C4\uBA54\uC778\uB9CC \uC811\uADFC \uAC00\uB2A5\uD558\uB3C4\uB85D CORS \uC815\uCC45\uC744 \uC801\uC6A9\uD558\uBA70, \uC11C\uC6B8 \uB9AC\uC804 \uAD00\uB9AC\uD615 \uC778\uD504\uB77C(Supabase\xB7Google Cloud) \uC704\uC5D0\uC11C \uC6B4\uC601\uB429\uB2C8\uB2E4."
        }
      ]
    },
    faq: {
      title: "\uC790\uC8FC \uBB3B\uB294 \uC9C8\uBB38",
      subtitle: "1on1 \uBBF8\uD305\uACFC \uC624\uBE14\uB9BF \uC11C\uBE44\uC2A4\uC5D0 \uAD00\uD55C [m]\uAD81\uAE08\uC99D\uC744 \uD574\uACB0\uD574\uB4DC\uB9BD\uB2C8\uB2E4.",
      items: [
        {
          q: "\uC6D0\uC628\uC6D0(1on1)\uC774 \uB3C4\uB300\uCCB4 \uBB50\uC608\uC694? [m]\uADF8\uB0E5 \uBC25 \uBA39\uC73C\uBA74\uC11C \uD558\uB294 \uB300\uD654\uB098 [m]\uC218\uC2DC \uB300\uD654 \uC544\uB2CC\uAC00\uC694?",
          a: "\uC544\uB2D9\uB2C8\uB2E4. \uBC25 \uBA39\uC73C\uBA74\uC11C \uD558\uB294 \uB300\uD654\uB098 \uC5C5\uBB34 \uC911 \uC218\uC2DC\uB85C \uB098\uB204\uB294 \uB300\uD654\uB294 \uC800\uD76C\uAC00 \uC815\uC758\uD558\uB294 1on1\uC774 \uC544\uB2D9\uB2C8\uB2E4. 1on1\uC740 \uC2DC\uAC04\uC744 \uBA85\uD655\uD788 \uC7A1\uC544\uB193\uACE0, \uB9AC\uB354\uC640 \uAD6C\uC131\uC6D0\uC774 1\uB3001\uB85C '\uC77C'\uACFC '\uC0AC\uB78C'\uC5D0 \uB300\uD55C \uB300\uD654\uB97C \uAE4A\uC774 \uC788\uAC8C \uB098\uB204\uB294 \uACF5\uC2DD\uC801\uC774\uACE0 \uC0C1\uD638\uC801\uC778 \uC2DC\uAC04\uC785\uB2C8\uB2E4."
        },
        {
          q: "\uC6D0\uC628\uC6D0\uC744 \uD558\uB294 \uBAA9\uC801\uC774 \uBB54\uAC00\uC694?",
          a: "\uD06C\uAC8C \uB450 \uAC00\uC9C0 \uD575\uC2EC \uBAA9\uC801\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uCCAB\uC9F8, \uC870\uC9C1\uACFC \uD300\uC758 \uBAA9\uD45C \uBC0F \uBC29\uD5A5\uC131 \uC815\uB82C(Alignment)\uC744 \uD1B5\uD55C \uBAB0\uC785\uB3C4 \uD5A5\uC0C1\uC785\uB2C8\uB2E4. \uB458\uC9F8, \uAD6C\uC131\uC6D0\uC774 \uC77C\uC758 \uC758\uBBF8\uB97C \uCC3E\uACE0 \uC131\uC7A5\uC744 \uCCB4\uAC10\uD558\uAC8C \uD558\uC5EC \uC774\uD0C8 \uC704\uD5D8\uC744 \uC120\uC81C\uC801\uC73C\uB85C \uB9C9\uB294 \uC608\uBC29\uC8FC\uC0AC \uC5ED\uD560\uC744 \uD569\uB2C8\uB2E4."
        },
        {
          q: "\uC6B0\uB9AC \uD68C\uC0AC\uAC00 \uD558\uB358 \uBA74\uB2F4, [m]\uD3C9\uAC00 \uBBF8\uD305\uC774\uB791 \uBB50\uAC00 \uB2E4\uB978\uAC00\uC694?",
          a: "\uAE30\uC874 \uD3C9\uAC00 \uBBF8\uD305\uC774 \uAE34 \uC8FC\uAE30\uB85C \uC9C4\uD589\uB418\uACE0 \uB2E8\uC21C\uD788 \uACB0\uACFC\uB97C '\uD1B5\uBCF4'\uD558\uB294 \uC790\uB9AC\uC5D0 \uAC00\uAE5D\uB2E4\uBA74, 1on1\uC740 \uC9E7\uC740 \uC8FC\uAE30\uB85C \uC815\uAE30\uC801\uC778 \uCCB4\uD06C\uB97C \uC9C4\uD589\uD558\uBA70 \uC0C1\uD638 \uC774\uD574\uB97C \uBC14\uD0D5\uC73C\uB85C '\uACFC\uC815 \uAD00\uB9AC'\uC640 '\uC131\uC7A5'\uC5D0 \uCD08\uC810\uC744 \uB9DE\uCD98 \uC30D\uBC29\uD5A5 \uC18C\uD1B5\uC785\uB2C8\uB2E4."
        },
        {
          q: "\uC6D0\uC628\uC6D0\uACFC \uB9AC\uB354\uC2ED \uC5ED\uB7C9 \uAC15\uD654\uAC00 [m]\uBB34\uC2A8 \uC0C1\uAD00\uC774 \uC788\uB098\uC694?",
          a: "\uB9AC\uB354\uC2ED\uC774 \uBC1C\uD718\uB418\uB824\uBA74 \uB9AC\uB354\uC640 \uAD6C\uC131\uC6D0 \uAC04\uC5D0 '\uC2E0\uB8B0'\uAC00 \uD544\uC218\uC801\uC785\uB2C8\uB2E4. \uAD6C\uC131\uC6D0\uC758 \uBC1C\uC5B8\uAD8C\uC744 \uBCF4\uC7A5\uD558\uACE0 \uC9C0\uC6D0\uD574 \uC8FC\uB294 1\uB3001 \uB300\uD654\uC57C\uB9D0\uB85C \uB2E4\uC218 \uC55E\uC5D0\uC11C\uC758 \uC9C0\uC2DC\uBCF4\uB2E4 \uC9C4\uC9DC \uC2E0\uB8B0\uB97C \uAD6C\uCD95\uD558\uAE30 \uC704\uD55C \uAC00\uC7A5 \uAC15\uB825\uD558\uACE0 \uD0C0\uB2F9\uD55C \uBC29\uBC95\uC785\uB2C8\uB2E4."
        },
        {
          q: "AI\uAC00 \uC6D0\uC628\uC6D0 \uB300\uD654\uB3C4 \uB2E4 \uC54C\uC544\uC11C \uD574\uC8FC\uACE0 [m]\uCF54\uCE6D\uB3C4 \uB300\uC2E0 \uD574\uC8FC\uB098\uC694?",
          a: "\uC544\uB2D9\uB2C8\uB2E4. \uB300\uD654\uC640 \uCF54\uCE6D\uC758 \uC8FC\uCCB4\uB294 \uCCA0\uC800\uD788 \uC0AC\uB78C\uC778 '\uB9AC\uB354'\uC785\uB2C8\uB2E4. AI\uB294 \uAD00\uB9AC\uC790\uC758 \uD589\uC815\uC801, \uBB38\uC11C\uD654 \uC18C\uBAA8 \uC2DC\uAC04\uC744 \uADF9\uC801\uC73C\uB85C \uC904\uC5EC\uC8FC\uACE0 \uD310\uB2E8\uC744 \uC9C0\uC6D0\uD558\uB294 \uAC15\uB825\uD55C '\uBCF4\uC870 \uB3C4\uAD6C'\uB85C \uAE30\uB2A5\uD558\uC5EC, \uB9AC\uB354\uAC00 \uB9AC\uB354\uC2ED \uBC1C\uD718\uC5D0 \uC628\uC804\uD788 \uC9D1\uC911\uD558\uB3C4\uB85D \uB3D5\uC2B5\uB2C8\uB2E4."
        },
        {
          q: "\uBC14\uBE60\uC11C \uB9E4\uC8FC \uC6D0\uC628\uC6D0\uC744 \uD558\uAE34 \uD798\uB4E0\uB370, [m]\uC4F8 \uC218 \uC788\uB098\uC694?",
          a: "\uB124, \uAC00\uB2A5\uD569\uB2C8\uB2E4. \uC790\uC8FC \uC9C4\uD589\uD558\uB294 \uAC83\uC774 \uAC00\uC7A5 \uD6A8\uACFC\uC801\uC774\uC9C0\uB9CC \uAE34 \uC8FC\uAE30\uC758 \uD3C9\uAC00/\uB9AC\uBDF0 \uBA74\uB2F4\uC5D0 \uD65C\uC6A9\uD558\uC154\uB3C4 \uAE30\uC874 \uBC29\uC2DD \uB300\uBE44 \uD6E8\uC52C \uB354 \uAC1D\uAD00\uC801\uC774\uACE0 \uCCB4\uACC4\uC801\uC778 \uBA74\uB2F4 \uAD00\uB9AC\uAC00 \uAC00\uB2A5\uD574\uC9D1\uB2C8\uB2E4."
        }
      ]
    },
    cta: {
      title: "\uB9AC\uB354\uC2ED\uC740 1on1\uC5D0\uC11C [m]\uC2DC\uC791\uB429\uB2C8\uB2E4.",
      body: "\uBB38\uC11C \uC791\uC5C5\uC740 \uC624\uBE14\uB9BF AI\uC5D0\uAC8C \uB9E1\uAE30\uACE0 [m]\uC870\uC9C1\uC758 \uC131\uC7A5\uC5D0 \uD544\uC694\uD55C \uCF54\uCE6D\uACFC [m]\uC9C4\uC194\uD55C \uB300\uD654\uC5D0\uB9CC 100% \uBAB0\uC785\uD558\uC138\uC694.",
      placeholder: "\uC5C5\uBB34\uC6A9 \uC774\uBA54\uC77C\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694",
      button: "\uC18C\uAC1C\uC11C \uBC1B\uC544\uBCF4\uAE30",
      footnote: "\uAC00\uC785 \uC989\uC2DC \uC8FC\uC694 \uAE30\uB2A5\uC744 \uB204\uAD6C\uB098 \uC368\uBCFC \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
      footer: {
        companyName: "(\uC8FC) \uB9AC\uBC84\uC2A4\uB9C8\uC6B4\uD2F4",
        address: "\uC11C\uC6B8 \uAC15\uC11C\uAD6C \uB9C8\uACE1\uC911\uC5598\uB85C 14 \uC11C\uC6B8\uCC3D\uC5C5\uD5C8\uBE0C \uC5E0\uD50C\uB7EC\uC2A4",
        representative: "\uB300\uD45C\uC790 : \uAE40\uACBD\uBBFC",
        email: "gmkim@reversemountain.co.kr",
        copyright: "2026 REVERSEMOUNTAIN ALL RIGHTS RESERVED.",
        links: [
          {
            label: "\uC11C\uBE44\uC2A4 \uC774\uC6A9\uC57D\uAD00",
            href: "https://orblit.space/v3/service_terms_ko.html"
          },
          {
            label: "\uAC1C\uC778\uC815\uBCF4\uCC98\uB9AC\uBC29\uCE68",
            href: "https://orblit.space/v4/privacy_policy_ko.html"
          },
          {
            label: "\uB3C4\uC785\uBB38\uC758",
            href: "https://featpaper.com/l/QJusPa7y"
          }
        ]
      }
    }
  },
  navbar: {
    service: "\uC11C\uBE44\uC2A4 \uC18C\uAC1C",
    whyOrblit: "\uC65C \uC624\uBE14\uB9BF\uC778\uAC00",
    communication: "\uC18C\uD1B5 \uAC15\uD654",
    growth: "\uC131\uC7A5 \uAD00\uB9AC",
    change: "\uBCC0\uD654 \uAD00\uB9AC",
    company: "\uB9AC\uBC84\uC2A4\uB9C8\uC6B4\uD2F4",
    pricing: "\uC694\uAE08\uC548\uB0B4",
    resources: "\uB9AC\uC18C\uC2A4",
    blog: "\uBE14\uB85C\uADF8",
    newsletter: "\uB274\uC2A4\uB808\uD130",
    youtube: "\uC720\uD29C\uBE0C",
    cta: "\uC2DC\uC791\uD558\uAE30",
    toggleMenu: "\uBA54\uB274 \uC5F4\uAE30 \uB610\uB294 \uB2EB\uAE30"
  },
  company: {
    hero: {
      alt: "Company Culture Background",
      title: "\uBB38\uD654\uB77C\uB294 \uADA4\uB3C4 \uC704\uC5D0\uC11C[br] \uC778\uC7AC\uAC00 \uBE5B\uB0A0 \uC218 \uC788\uB3C4\uB85D",
      subtitle: "Orbit Your Culture,[m] Light Your People"
    },
    missionVision: {
      missionLabel: "Our mission",
      missionBody: "\uC6B0\uB9AC\uB294 \uAC74\uAC15\uD55C \uC18C\uD1B5\uC744 \uD1B5\uD574[m][d] \uD300\uC6D0\uC774 \uC77C\uC758 \uC758\uBBF8\uB97C[m] \uBC1C\uACAC\uD558\uC5EC \uBAB0\uC785\uD558\uAC8C \uB3D5\uACE0,[m][d] \uADF8 \uACFC\uC815\uC5D0\uC11C \uB9CC\uB4E4\uC5B4\uB0B8 \uC131\uACFC\uAC00[m][d] \uC628\uC804\uD788 \uC778\uC815\uBC1B\uB294 \uBB38\uD654\uB97C \uB9CC\uB4ED\uB2C8\uB2E4.",
      visionLabel: "Our vision",
      visionBody: "\uBAA8\uB4E0 \uAC1C\uC778\uC774[m] \uC790\uC2E0\uC758 \uACE0\uC720\uD55C \uC7A0\uC7AC\uB825\uC744 \uBC1C\uACAC\uD558\uACE0,[m][d] \uAC00\uC7A5 \uBE5B\uB0A0 \uC218 \uC788\uB294 \uCD5C\uC801\uC758 \uAE30\uD68C\uC640[m] \uC5F0\uACB0\uB418\uB294 \uC138\uC0C1\uC744 \uB9CC\uB4ED\uB2C8\uB2E4."
    },
    culture: {
      badge: "Our Culture",
      items: [
        {
          num: "01",
          title: "\uC9C1\uAE09\uC774 \uC544\uB2CC,[br]\uC2E4\uB825\uACFC \uD1B5\uCC30\uC744 \uC874\uC911\uD569\uB2C8\uB2E4.",
          lines: [
            "\uC6B0\uB9AC\uB294 \uC5F0\uCC28\uB098 \uC9C1\uAE09\uBCF4\uB2E4 '\uD604\uC7AC\uC758 \uC5ED\uB7C9'\uC744 \uB354 \uC2E0\uB8B0\uD569\uB2C8\uB2E4.",
            "\uB9AC\uB354\uC758 \uAD8C\uC704\uB294 \uC9C0\uC2DC\uAC00 \uC544\uB2CC,[d]\uD300\uC6D0\uB4E4\uC774 \uACF5\uAC10\uD560 \uC218 \uC788\uB294 \uD0C1\uC6D4\uD55C \uD1B5\uCC30\uB825\uACFC[d]\uC194\uC120\uC218\uBC94\uC5D0\uC11C \uB098\uC628\uB2E4\uACE0 \uBBFF\uC2B5\uB2C8\uB2E4."
          ]
        },
        {
          num: "02",
          title: "\uAC74\uAC15\uD558\uAC8C \uCDA9\uB3CC\uD558\uACE0,[br]\uD55C \uBC29\uD5A5\uC73C\uB85C \uB2EC\uB9BD\uB2C8\uB2E4.",
          lines: [
            "\uB354 \uB098\uC740 \uACB0\uB860\uC744 \uC704\uD574\uC120 \uC194\uC9C1\uD55C \uC758\uACAC\uC774 \uD544\uC218\uC801\uC785\uB2C8\uB2E4.[d]\uD68C\uC758\uC2E4\uC5D0\uC11C\uB294 \uB208\uCE58 \uBCF4\uC9C0 \uC54A\uACE0 \uCE58\uC5F4\uD558\uAC8C \uD1A0\uB860\uD558\uC138\uC694.",
            "\uD558\uC9C0\uB9CC \uACB0\uC815\uB41C \uD6C4\uC5D0\uB294,[d]\uB9C8\uCE58 \uB0B4 \uC758\uACAC\uC774\uC5C8\uB358 \uAC83\uCC98\uB7FC \uC11C\uB85C\uB97C \uBBFF\uACE0 \uD5CC\uC2E0\uD569\uB2C8\uB2E4."
          ]
        },
        {
          num: "03",
          title: "\uAD00\uC2B5\uC744 \uB118\uC5B4[br]\uBCF8\uC9C8\uC5D0 \uC9D1\uC911\uD569\uB2C8\uB2E4.",
          lines: [
            '"\uC6D0\uB798 \uADF8\uB798\uC654\uC73C\uB2C8\uAE4C"\uB77C\uB294 \uB9D0 \uB300\uC2E0 "\uC65C?"\uB77C\uB294 \uC9C8\uBB38\uC744 \uB358\uC9D1\uB2C8\uB2E4.',
            "\uB0A8\uB4E4\uC744 \uB530\uB77C \uD558\uAE30\uBCF4\uB2E4 \uBB38\uC81C\uC758 \uBCF8\uC9C8\uC744 \uD30C\uACE0\uB4E4\uC5B4,[d]\uC6B0\uB9AC\uC5D0\uAC8C \uAC00\uC7A5 \uC798 \uB9DE\uB294 \uAC00\uC7A5 \uD6A8\uACFC\uC801\uC778 \uD574\uACB0\uCC45\uC744 \uCC3E\uC544\uB0C5\uB2C8\uB2E4."
          ]
        }
      ]
    },
    cta: {
      title: "\uD568\uAED8 \uB354 \uB098\uC740 \uC870\uC9C1\uC744 [m]\uB9CC\uB4DC\uC2E4 \uBD84",
      body: "\uB9AC\uBC84\uC2A4\uB9C8\uC6B4\uD2F4\uACFC \uD568\uAED8 \uC131\uC7A5\uD558\uBA70, [m]\uC0C8\uB85C\uC6B4 \uC77C\uD558\uB294 \uBB38\uD654\uB97C \uC774\uB04C\uC5B4\uAC08 \uB3D9\uB8CC\uB97C [m]\uCC3E\uACE0 \uC788\uC2B5\uB2C8\uB2E4.",
      button: "\uCC44\uC6A9 \uD398\uC774\uC9C0 \uBCF4\uAE30"
    }
  },
  pricing: {
    heroTitle: "\uBD80\uB2F4 \uC5C6\uB294 \uC2DC\uC791,[br]\uC6B0\uB9AC \uD300\uC5D0 \uB531 \uB9DE\uB294 \uD569\uB9AC\uC801\uC778 \uD50C\uB79C",
    heroSubtitle: "\uAC00\uBCCD\uAC8C \uC2DC\uC791\uD558\uB294 \uBB34\uB8CC \uD300 \uCCB4\uD5D8\uBD80\uD130 \uC9C0\uC18D \uAC00\uB2A5\uD55C \uC131\uC7A5\uC744 \uC704\uD55C \uBB34\uC81C\uD55C \uBE44\uC988\uB2C8\uC2A4 \uD50C\uB79C\uAE4C\uC9C0,[d]\uB9AC\uB354\uC2ED \uD5A5\uC0C1\uC5D0 \uAF2D \uD544\uC694\uD55C \uC194\uB8E8\uC158\uC744 \uC81C\uC548\uD569\uB2C8\uB2E4.",
    billing: {
      monthly: "\uC6D4\uAC04 \uACB0\uC81C",
      annual: "\uC5F0\uAC04 \uACB0\uC81C",
      annualSavings: "\uC5F0\uAC04 \uACB0\uC81C \uC2DC \uBE44\uC6A9\uC774 17% \uC808\uC57D\uB429\uB2C8\uB2E4."
    },
    desktop: {
      introTitle: "\uC6D0\uC628\uC6D0 \uC131\uACF5\uC744 \uC704\uD55C \uCD5C\uC801\uC758 \uB3C4\uAD6C",
      introSubtitle: "\uC6B0\uB9AC \uD300\uC758 \uBBF8\uD305 \uBB38\uD654\uB97C \uD55C \uB2E8\uACC4 \uC5C5\uADF8\uB808\uC774\uB4DC\uD560 \uD50C\uB79C\uC744 \uC120\uD0DD\uD558\uC138\uC694.",
      ctaText: "\uC9C0\uAE08 \uAC00\uC785\uD558\uC5EC \uC6B0\uB9AC \uD300\uC5D0 \uAF2D \uB9DE\uB294 \uC6D0\uC628\uC6D0 \uBBF8\uD305 \uBB38\uD654\uB97C \uACBD\uD5D8\uD574 \uBCF4\uC138\uC694."
    },
    plans: {
      basic: {
        badge: "\uC6D0\uC628\uC6D0 \uB9DB\uBCF4\uAE30",
        title: "\uBCA0\uC774\uC9C1 \uD50C\uB79C",
        price: "\uBB34\uB8CC",
        note: "\uC2E0\uC6A9\uCE74\uB4DC \uBD88\uD544\uC694, \uAC00\uC785 \uC989\uC2DC \uC2DC\uC791",
        cta: "\uBB34\uB8CC\uB85C \uC2DC\uC791\uD558\uAE30",
        highlights: {
          aiTitle: "AI \uC6D0\uC628\uC6D0 \uC0AC\uC774\uD074 \uAE30\uB2A5 \uD2B8\uB77C\uC774\uC5BC \uC81C\uACF5",
          aiDesc: "\uAC00\uC785 \uC2DC \uBB34\uB8CC \uB179\uC74C/\uBD84\uC11D \uAE30\uD68C \uCD1D 3\uD68C \uAE30\uBCF8 \uC81C\uACF5, \uBA64\uBC84\uB97C \uCD08\uB300\uD560 \uB54C\uB9C8\uB2E4 2\uD68C\uC529 \uCD94\uAC00",
          commonTitle: "\uBAA8\uB4E0 \uC6D0\uC628\uC6D0 \uACF5\uD1B5 \uAE30\uB2A5 \uC81C\uACF5",
          commonDesc: "\uC9C8\uBB38 \uD15C\uD50C\uB9BF, \uB300\uC2DC\uBCF4\uB4DC, 9Box \uBA64\uBC84 \uAD00\uB9AC, \uACF5\uB3D9 \uAE30\uB85D \uAE30\uBCF8 \uC81C\uACF5",
          note: "* \uD69F\uC218 \uBAA8\uB450 \uC18C\uC9C4 \uC2DC AI \uAE30\uB2A5 \uC81C\uD55C (\uAE30\uBCF8 \uAE30\uB2A5\uC740 \uACC4\uC18D \uC0AC\uC6A9 \uAC00\uB2A5)"
        }
      },
      business: {
        badge: "\uCD94\uCC9C \uD50C\uB79C",
        title: "\uBE44\uC988\uB2C8\uC2A4 \uD50C\uB79C",
        annualPrice: "80,000",
        monthlyPrice: "8,000",
        annualSuffix: "\uC6D0 / \uBA64\uBC84 \xB7 \uC5F0",
        monthlySuffix: "\uC6D0 / \uBA64\uBC84 \xB7 \uC6D4",
        note: "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uB2E8\uC704 \uCCAD\uAD6C",
        cta: "\uCCB4\uD5D8\uD558\uAE30",
        recommended: "RECOMMENDED",
        highlights: {
          aiTitle: "AI \uC6D0\uC628\uC6D0 \uC0AC\uC774\uD074 \uBB34\uC81C\uD55C \uC81C\uACF5",
          aiDesc: "\uC9C8\uBB38 \uC81C\uC548, STT \uC790\uB3D9 \uB179\uC74C, \uC2E4\uC2DC\uAC04 \uC694\uC57D, \uB9AC\uB354\uC2ED \uCF54\uCE6D \uD53C\uB4DC\uBC31 \uB4F1 \uC804 \uAE30\uB2A5 \uBB34\uC81C\uD55C",
          commonTitle: "\uBAA8\uB4E0 \uC6D0\uC628\uC6D0 \uACF5\uD1B5 \uAE30\uB2A5 \uC81C\uACF5",
          commonDesc: "\uC9C8\uBB38 \uD15C\uD50C\uB9BF, \uB300\uC2DC\uBCF4\uB4DC, 9Box \uBA64\uBC84 \uAD00\uB9AC, \uACF5\uB3D9 \uAE30\uB85D \uAE30\uBCF8 \uC81C\uACF5"
        }
      }
    },
    sections: {
      ai: "\uBBF8\uD305 \uD6A8\uC728\uC131\uACFC \uD6A8\uACFC\uB97C \uADF9\uB300\uD654\uD558\uAE30 \uC704\uD55C AI \uC778\uD154\uB9AC\uC804\uD2B8 \uAE30\uB2A5",
      common: "\uC6D0\uC628\uC6D0 \uC8FC\uAE30 \uC124\uC815, \uC9C4\uD589, \uAD00\uB9AC\uB97C \uB3D5\uB294 \uAE30\uBCF8 \uAE30\uB2A5"
    },
    trial: {
      badge: "\uD2B8\uB77C\uC774\uC5BC \uC81C\uACF5",
      lines: ["\uAC00\uC785 \uC2DC \uAE30\uBCF8 3\uD68C \uC81C\uACF5", "\uD300\uC6D0 \uCD08\uB300 \uC2DC +2\uD68C \uCD94\uAC00"],
      note: "* \uD69F\uC218 \uBAA8\uB450 \uC18C\uC9C4 \uC2DC AI \uAE30\uB2A5 \uC81C\uD55C [br](\uC6D0\uC628\uC6D0 \uAE30\uBCF8 \uAE30\uB2A5\uC740 \uACC4\uC18D \uC0AC\uC6A9 \uAC00\uB2A5)"
    },
    businessAi: {
      badge: "\uBB34\uC81C\uD55C"
    },
    comparison: {
      title: "\uC0C1\uC138 \uAE30\uB2A5 \uBE44\uAD50",
      featureColumn: "\uC81C\uACF5 \uAE30\uB2A5",
      basicColumn: "\uBCA0\uC774\uC9C1",
      businessColumn: "\uBE44\uC988\uB2C8\uC2A4",
      trialValue: "\uD2B8\uB77C\uC774\uC5BC",
      unlimitedValue: "\uBB34\uC81C\uD55C"
    },
    aiFeatures: [
      {
        name: "\uBBF8\uD305 \uD504\uB819 \uCC57\uBD07",
        desc: "\uCC44\uD305\uC73C\uB85C \uBBF8\uD305 \uC900\uBE44\uB97C \uB3D5\uB294 AI",
        phase: "\uBBF8\uD305 \uC804",
        phaseKey: "before",
        role: "\uBA64\uBC84"
      },
      {
        name: "\uD504\uB819 \uAE30\uBC18 \uC9C8\uBB38 \uC81C\uC548",
        desc: "\uBA64\uBC84\uC758 \uD504\uB819 \uB0B4\uC6A9\uACFC \uC6D0\uC628\uC6D0 \uD788\uC2A4\uD1A0\uB9AC \uAE30\uBC18 \uB9DE\uCDA4 \uC9C8\uBB38 \uC81C\uC548",
        phase: "\uBBF8\uD305 \uC804",
        phaseKey: "before",
        role: "\uB9AC\uB354"
      },
      {
        name: "\uBA64\uBC84 \uB9DE\uCDA4\uD615 \uAC00\uC774\uB4DC",
        desc: "\uD504\uB819\uACFC \uBA64\uBC84 \uC131\uD5A5\uC744 \uBC14\uD0D5\uC73C\uB85C \uB300\uD654 \uC9C4\uD589 \uBC29\uD5A5 \uC81C\uC2DC",
        phase: "\uBBF8\uD305 \uC804",
        phaseKey: "before",
        role: "\uB9AC\uB354"
      },
      {
        name: "AI \uB9DE\uCDA4\uD615 \uD15C\uD50C\uB9BF \uC0DD\uC131",
        desc: "\uAC1C\uC778 \uC131\uD5A5 \uBC0F \uD604\uC7AC \uC0C1\uD669\uC5D0 \uCD5C\uC801\uD654\uB41C \uB9DE\uCDA4 \uC9C8\uBB38 \uC138\uD2B8",
        phase: "\uBBF8\uD305 \uC804",
        phaseKey: "before",
        role: "\uB9AC\uB354"
      },
      {
        name: "\uB300\uD654 \uC2A4\uD06C\uB9BD\uD2B8 \uCD94\uCD9C (STT)",
        desc: "\uC0AC\uB78C\uC740 \uC18C\uD1B5\uC5D0\uB9CC \uC9D1\uC911\uD558\uACE0 \uB300\uD654 \uB0B4\uC6A9\uC740 AI\uAC00 \uC815\uB9AC",
        phase: "\uBBF8\uD305 \uC9C4\uD589",
        phaseKey: "during"
      },
      {
        name: "\uD575\uC2EC \uC9C8\uBB38-\uB2F5\uBCC0 \uC815\uB9AC",
        desc: "\uC9C8\uBB38-\uB2F5\uBCC0\uC744 \uC790\uB3D9 \uB9E4\uCE6D\uD558\uACE0 \uC0C8\uB85C\uC6B4 \uC9C8\uBB38\uB3C4 \uBE60\uC9D0\uC5C6\uC774 \uAE30\uB85D",
        phase: "\uBBF8\uD305 \uC9C4\uD589",
        phaseKey: "during"
      },
      {
        name: "\uC0C1\uC138 \uC694\uC57D \uBC0F \uC561\uC158 \uC544\uC774\uD15C \uCD94\uCD9C",
        desc: "\uBBF8\uD305 \uB0B4\uC6A9\uACFC \uC11C\uB85C\uC758 \uC561\uC158\uC544\uC774\uD15C\uC744 \uBE60\uB974\uACE0 \uC0C1\uC138\uD558\uAC8C \uC815\uB9AC",
        phase: "\uBBF8\uD305 \uC9C4\uD589",
        phaseKey: "during"
      },
      {
        name: "\uB9AC\uB354 \uB9DE\uCDA4 \uB9AC\uB354\uC2ED \uCF54\uCE6D",
        desc: "\uBC1C\uD654 \uB0B4\uC6A9 \uBD84\uC11D\uC744 \uD1B5\uD574 \uB300\uD654\uC5D0\uC11C \uB4DC\uB7EC\uB09C \uAC15\uC810/\uBCF4\uC644\uC810 \uC804\uB2EC",
        phase: "\uBBF8\uD305 \uC9C4\uD589",
        phaseKey: "during",
        role: "\uB9AC\uB354"
      },
      {
        name: "\uBA64\uBC84 \uB9AC\uD3EC\uD2B8",
        desc: "\uBBF8\uD305\uB4E4\uC744 \uC885\uD569\uD558\uC5EC \uBA64\uBC84\uC758 \uD1F4\uC0AC \uC704\uD5D8, \uAD00\uB9AC\uBC29\uC548 \uB4F1 \uC81C\uC2DC",
        phase: "\uBBF8\uD305 \uD6C4",
        phaseKey: "after",
        role: "\uB9AC\uB354"
      }
    ],
    commonFeatures: [
      {
        name: "\uBBF8\uD305 \uC8FC\uAE30 \uAD00\uB9AC",
        desc: "\uC815\uAE30\uC801\uC778 \uBBF8\uD305\uC744 \uC704\uD55C \uC8FC\uAE30 \uC124\uC815\uACFC \uBA54\uC77C/\uD478\uC2DC \uC54C\uB9BC",
        icon: "calendar"
      },
      {
        name: "\uC6D0\uC628\uC6D0 \uAE30\uBCF8 \uC9C8\uBB38 \uD15C\uD50C\uB9BF",
        desc: "\uC6D0\uD65C\uD55C \uC18C\uD1B5\uC744 \uB3D5\uB294 \uC0C1\uD669\uBCC4 \uB300\uD654 \uD504\uB808\uC784\uC6CC\uD06C \uC81C\uACF5",
        icon: "compass",
        role: "\uB9AC\uB354"
      },
      {
        name: "\uBBF8\uD305 \uACF5\uB3D9 \uAE30\uB85D \uACF5\uAC04",
        desc: "\uB9AC\uB354\uC640 \uAD6C\uC131\uC6D0\uC774 \uB3D9\uC2DC \uD3B8\uC9D1\uD558\uB294 \uACF5\uB3D9 \uBBF8\uD305 \uB178\uD2B8",
        icon: "pencil"
      },
      {
        name: "\uBBF8\uD305 \uC774\uD6C4 \uBA64\uBC84 \uC0C1\uD0DC \uAE30\uB85D/\uCD94\uC801",
        desc: "\uBD80\uB2F4 \uC5C6\uC774 \uAFB8\uC900\uD788 \uCCB4\uD06C\uD558\uB294 \uBA64\uBC84\uC758 \uBAB0\uC785\uB3C4\uC640 \uB9CC\uC871\uB3C4",
        icon: "check",
        role: "\uB9AC\uB354"
      },
      {
        name: "\uBBF8\uD305 \uD604\uD669 \uB300\uC2DC\uBCF4\uB4DC",
        desc: "\uC870\uC9C1 \uB0B4 \uC6D0\uC628\uC6D0 \uC9C4\uD589\uB960 \uBC0F \uBA64\uBC84 \uC0C1\uD0DC \uBAA8\uB2C8\uD130\uB9C1",
        icon: "chart",
        role: "\uB9AC\uB354"
      },
      {
        name: "9Box \uBA64\uBC84 \uAD00\uB9AC",
        desc: "\uC131\uACFC-\uC7A0\uC7AC\uB825, \uD1F4\uC0AC\uC704\uD5D8-\uC601\uD5A5\uB3C4\uB97C \uD55C\uB208\uC5D0 \uBCF4\uB294 \uB9E4\uD2B8\uB9AD\uC2A4",
        icon: "grid",
        role: "\uB9AC\uB354"
      }
    ]
  },
  why1on1: {
    cardCta: "\uC790\uC138\uD788 \uBCF4\uAE30",
    heroTitle: "\uB9AC\uB354\uC2ED\uC740 \uD68C\uC758\uC2E4\uC774 \uC544\uB2C8\uB77C[br]1on1\uC5D0\uC11C \uB9CC\uB4E4\uC5B4\uC9D1\uB2C8\uB2E4.",
    heroSubtitle: "\uC624\uBE14\uB9BF\uC740 1on1\uC744 \uD1B5\uD574 \uC870\uC9C1\uC758 \uC18C\uD1B5\xB7\uC131\uC7A5\xB7\uBCC0\uD654\uB97C \uC124\uACC4\uD569\uB2C8\uB2E4.",
    statsTitle: "\uB370\uC774\uD130\uB85C \uBCF4\uB294 1on1\uC758 \uD798",
    stats: [
      {
        src: "Gallup",
        stat: "70%",
        color: "#2563eb",
        preLines: ["\uC870\uC9C1 \uBAB0\uC785\uB3C4 \uCC28\uC774\uC758"],
        post: "\uB294\n\uB9AC\uB354\uC2ED\uC5D0\uC11C \uBC1C\uC0DD"
      },
      {
        src: "Andy Grove",
        stat: "80\uC2DC\uAC04",
        color: "#9333ea",
        preLines: ["\uB9AC\uB354\uC758 90\uBD84 1on1\uC774", "\uD300\uC6D0\uC758 \uD5A5\uD6C4"],
        post: "\uC744 \uACB0\uC815"
      },
      {
        src: "Adobe",
        stat: "30%",
        color: "#059669",
        preLines: ["\uC815\uAE30 1on1 \uB3C4\uC785 \uD6C4", "\uC790\uBC1C\uC801 \uD1F4\uC0AC\uC728"],
        post: " \uAC10\uC18C"
      }
    ],
    areas: {
      title: "\uC88B\uC740 1on1\uC740 \uB2E8\uC21C\uD55C \uBA74\uB2F4\uC774 \uC544\uB2D9\uB2C8\uB2E4",
      subtitle: "\uADF8 \uC548\uC5D0\uB294 \uC138 \uAC00\uC9C0 \uB9AC\uB354\uC758 \uC77C\uC774 \uB3D9\uC2DC\uC5D0 \uC77C\uC5B4\uB0A9\uB2C8\uB2E4.",
      cards: [
        {
          to: "/why/communication",
          title: "\uC18C\uD1B5 \uAC15\uD654",
          desc: '\uB9AC\uB354\uAC00 "\uBB34\uC2A8 \uB9D0\uC744 \uD560\uC9C0" \uC54C\uAC8C \uB418\uACE0, \uD300\uC6D0\uC774 "\uC9C4\uC9DC \uC18D\uB9C8\uC74C\uC744 \uAEBC\uB0BC \uC218 \uC788\uAC8C" \uB9CC\uB4ED\uB2C8\uB2E4.'
        },
        {
          to: "/why/growth",
          title: "\uC131\uC7A5 \uAD00\uB9AC",
          desc: "\uB300\uD654\uC5D0\uC11C \uB098\uC628 \uC57D\uC18D\uC744 \uD758\uB824\uBCF4\uB0B4\uC9C0 \uC54A\uACE0 \uC131\uACFC\xB7\uD3C9\uAC00\xB7\uC131\uC7A5\uC73C\uB85C \uC5F0\uACB0\uD569\uB2C8\uB2E4."
        },
        {
          to: "/why/change",
          title: "\uBCC0\uD654 \uAD00\uB9AC",
          desc: "\uACBD\uC601\uC9C4\uC758 \uC804\uB7B5\uC744 \uB9AC\uB354\uB4E4\uC758 \uC785\uC744 \uD1B5\uD574 \uD604\uC7A5 \uB05D\uAE4C\uC9C0 \uC804\uB2EC\uD558\uACE0 \uADF8 \uACB0\uACFC\uB97C \uAC00\uC2DC\uD654\uD569\uB2C8\uB2E4."
        }
      ]
    },
    approach: {
      eyebrow: "Our Approach",
      title: "\uC790\uB3D9\uD654\uAC00 \uC544\uB2C8\uB77C \uB9AC\uB354\uC2ED \uAC15\uD654\uC785\uB2C8\uB2E4",
      commonTitle: "\uD754\uD55C \uC811\uADFC \uBC29\uC2DD",
      commonItems: [
        "\uB300\uD654\uB97C \uAE30\uACC4\uC801\uC73C\uB85C \uC62E\uACA8 \uC801\uB294 \uD68C\uC758\uB85D \uD234",
        "\uAD00\uB9AC\uC790 \uD655\uC778\uC6A9 \uCCB4\uD06C\uB9AC\uC2A4\uD2B8 \uC571",
        "\uBD84\uC11D\uC774 \uBD88\uAC00\uB2A5\uD55C \uC77C\uBC18\uC801\uC778 \uB178\uD2B8"
      ],
      orblitBadge: "Orblit",
      orblitTitle: "\uC624\uBE14\uB9BF\uC758 \uC811\uADFC",
      orblitItems: [
        "\uC900\uBE44\uB41C \uC9C8\uBB38\uACFC \uD53C\uB4DC\uBC31\uC73C\uB85C \uB9AC\uB354\uC758 \uC5ED\uB7C9 \uD5A5\uC0C1",
        "\uC870\uC9C1\uC758 \uC0C1\uD0DC\uB97C \uB370\uC774\uD130\uB85C \uD30C\uC545\uD558\uB294 \uAC00\uC2DC\uC131",
        "\uC9C0\uC18D\uC801\uC778 \uC131\uACFC \uAD00\uB9AC\uC640 \uC774\uD0C8 \uBC29\uC5B4 \uCCB4\uACC4"
      ]
    }
  }
};

// src/locales/ko-KR/feature_tutorial.json
var feature_tutorial_default2 = {
  actions: {
    replay: "\uD29C\uD1A0\uB9AC\uC5BC \uBCF4\uAE30",
    mediaUnavailable: "\uD29C\uD1A0\uB9AC\uC5BC \uBBF8\uB514\uC5B4\uB97C \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."
  },
  recording: {
    dashboard: {
      fixture: {
        users: {
          owner: "\uAE40\uD558\uC900",
          leaderOne: "\uBC15\uBBFC\uC544",
          leaderTwo: "\uC815\uB178\uC544",
          memberOne: "\uC774\uB3C4\uC724",
          memberTwo: "\uCD5C\uC11C\uC544",
          memberThree: "\uD55C\uC9C0\uD638",
          memberFour: "\uC11C\uC720\uB098",
          memberFive: "\uAD8C\uB808\uC624"
        },
        meetings: {
          q1Priorities: "1\uBD84\uAE30 \uC6B0\uC120\uC21C\uC704\uC640 \uC628\uBCF4\uB529",
          executionReview: "\uC2E4\uD589 \uD604\uD669 \uB9AC\uBDF0",
          growthPlan: "\uC131\uC7A5 \uACC4\uD68D",
          workload: "\uC5C5\uBB34\uB7C9 \uC870\uC728",
          priorityReset: "\uC6B0\uC120\uC21C\uC704 \uC7AC\uC815\uB82C",
          roadmap: "\uD50C\uB7AB\uD3FC \uB85C\uB4DC\uB9F5",
          handoff: "\uD504\uB85C\uC81D\uD2B8 \uD578\uB4DC\uC624\uD504",
          rituals: "\uD300 \uC6B4\uC601 \uBC29\uC2DD",
          reliability: "\uC548\uC815\uC131 \uB9AC\uBDF0",
          career: "\uCEE4\uB9AC\uC5B4 \uBC29\uD5A5",
          coaching: "\uB9E4\uB2C8\uC800 \uCF54\uCE6D",
          leadership: "\uB9AC\uB354\uC2ED \uC2F1\uD06C"
        }
      }
    },
    member_report: {
      fixture: {
        reports: {
          primary: {
            recommendations: [
              "\uC131\uC7A5 \uACFC\uC81C \uC124\uACC4: \uB300\uC6A9\uB7C9 \uD2B8\uB798\uD53D \uD658\uACBD\uC758 \uCFFC\uB9AC \uBCD1\uBAA9\uC744 \uC9C1\uC811 \uBD84\uC11D\uD558\uACE0 \uAC1C\uC120\uC548\uC744 \uAC80\uC99D\uD558\uB294 \uC791\uC740 DB \uCD5C\uC801\uD654 \uACFC\uC81C\uB97C \uB9E1\uACA8\uBCF4\uC138\uC694.",
              "\uB9AC\uBDF0 \uC2DC\uAC04 \uBCF4\uD638: \uB9B4\uB9AC\uC988 \uC8FC\uAC04\uC5D0\uB294 \uAD6C\uD604 \uC77C\uC815\uACFC \uBCC4\uB3C4\uB85C \uCF54\uB4DC \uB9AC\uBDF0 \uC2DC\uAC04\uC744 \uCE98\uB9B0\uB354\uC5D0 \uACE0\uC815\uD574 \uD488\uC9C8 \uD53C\uB4DC\uBC31\uC774 \uBC00\uB9AC\uC9C0 \uC54A\uAC8C \uD574\uC8FC\uC138\uC694.",
              "\uC6B4\uC601 \uACBD\uD5D8 \uD655\uC7A5: \uC7A5\uC560 \uB300\uC751 \uD6C8\uB828\uC774\uB098 \uB864\uBC31 \uB9AC\uD5C8\uC124\uC5D0 \uCC38\uC5EC\uC2DC\uCF1C \uC11C\uBE44\uC2A4 \uC548\uC815\uC131 \uC5C5\uBB34\uB97C \uB354 \uB113\uAC8C \uACBD\uD5D8\uD558\uAC8C \uD574\uC8FC\uC138\uC694.",
              "\uC131\uACFC \uAC00\uC2DC\uD654: CI \uAC1C\uC120, \uBC30\uD3EC \uC2DC\uAC04 \uB2E8\uCD95 \uAC19\uC740 \uC131\uACFC\uB97C \uD300 \uD68C\uACE0\uC5D0\uC11C \uBA85\uD655\uD788 \uC5B8\uAE09\uD574 \uAE30\uC220\uC801 \uAE30\uC5EC\uAC00 \uC870\uC9C1 \uC131\uACFC\uB85C \uC5F0\uACB0\uB41C\uB2E4\uB294 \uB290\uB08C\uC744 \uAC15\uD654\uD574\uC8FC\uC138\uC694."
            ]
          },
          collaboration: {
            recommendations: [
              "\uCD08\uAE30 \uC815\uB82C \uB9AC\uB4EC: \uC778\uC811 \uD300\uACFC \uD611\uC5C5\uC744 \uC2DC\uC791\uD560 \uB54C \uBAA9\uC801, \uCC45\uC784 \uBC94\uC704, \uC644\uB8CC \uAE30\uC900\uC744 \uBA3C\uC800 \uD569\uC758\uD558\uB294 \uC9E7\uC740 \uD0A5\uC624\uD504\uB97C \uACE0\uC815\uD574\uC8FC\uC138\uC694.",
              "\uC6B0\uC120\uC21C\uC704 \uBCC0\uACBD \uC124\uBA85: \uBCC0\uACBD\uC774 \uC0DD\uAE38 \uB54C \uBC30\uACBD\uACFC \uAE30\uB300 \uACB0\uACFC, \uC774\uBC88 \uC8FC\uC5D0 \uD3EC\uAE30\uD560 \uC77C\uC744 \uD568\uAED8 \uACF5\uC720\uD574 \uBD88\uD544\uC694\uD55C \uC7AC\uC791\uC5C5\uC744 \uC904\uC5EC\uC8FC\uC138\uC694.",
              "\uD611\uC5C5 \uC811\uC810 \uBA85\uD655\uD654: \uC758\uC0AC\uACB0\uC815\uC790\uB97C \uBBF8\uB9AC \uC815\uD558\uACE0 \uBE44\uB3D9\uAE30 \uACF5\uC720 \uCC44\uB110\uC744 \uC815\uB9AC\uD574 \uC9C8\uBB38\uC774 \uD769\uC5B4\uC9C0\uC9C0 \uC54A\uAC8C \uD574\uC8FC\uC138\uC694."
            ]
          }
        },
        summary: {
          strengths: ["\uC624\uB108\uC2ED", "\uC2DC\uC2A4\uD15C \uC0AC\uACE0"],
          weaknesses: ["\uB9AC\uBDF0 \uC5EC\uB825", "\uD300 \uAC04 \uC815\uB82C"],
          growthGoals: ["\uB300\uC6A9\uB7C9 \uB370\uC774\uD130 \uCC98\uB9AC", "DB \uC6B4\uC601"],
          growthSkills: ["\uCFFC\uB9AC \uCD5C\uC801\uD654", "\uC7A5\uC560 \uB300\uC751"],
          workContents: ["\uD50C\uB7AB\uD3FC \uC548\uC815\uC131", "\uACB0\uC81C \uBAA8\uB4C8 \uC548\uC815\uD654"],
          workBlockers: ["\uBE60\uB4EF\uD55C \uB9B4\uB9AC\uC988 \uC77C\uC815", "\uC81C\uD55C\uB41C \uB9AC\uBDF0\uC5B4 \uD480"],
          workContribution: [
            "\uBC30\uD3EC \uC2DC\uAC04 \uB2E8\uCD95",
            "CI \uD30C\uC774\uD504\uB77C\uC778 \uAC1C\uC120"
          ],
          motivationFactors: ["\uAE30\uC220\uC801 \uB3C4\uC804", "\uC790\uC728\uC131"],
          demotivationFactors: [
            "\uC7A6\uC740 \uCEE8\uD14D\uC2A4\uD2B8 \uC804\uD658",
            "\uBD88\uBA85\uD655\uD55C \uC6B0\uC120\uC21C\uC704 \uBCC0\uACBD"
          ],
          personalEmotion: ["\uC548\uC815\uC801", "\uAE0D\uC815\uC801"],
          personalNetwork: ["\uB3D9\uB8CC \uD611\uC5C5\uC744 \uC911\uC2DC"],
          personalInterest: ["\uC5EC\uD589", "\uD559\uC2B5 \uCEE4\uBBA4\uB2C8\uD2F0"],
          growthInterestedProjects: ["\uACE0\uD2B8\uB798\uD53D \uB370\uC774\uD130 \uD30C\uC774\uD504\uB77C\uC778"],
          flightRiskLevel: "\uB0AE\uC74C",
          flightRiskSummary: "\uC131\uC7A5 \uAE30\uD68C\uAC00 \uBCF4\uC77C \uB54C \uC5C5\uBB34 \uBAB0\uC785\uC774 \uC548\uC815\uC801\uC73C\uB85C \uC720\uC9C0\uB429\uB2C8\uB2E4."
        },
        analysis: {
          motivation: {
            factors: [
              "\uAE30\uC220\uC801 \uB09C\uC774\uB3C4: \uB2E8\uC21C \uAE30\uB2A5 \uAD6C\uD604\uBCF4\uB2E4 \uC131\uB2A5 \uBCD1\uBAA9\uC744 \uCC3E\uACE0 \uC2DC\uC2A4\uD15C \uC548\uC815\uC131\uC744 \uB192\uC774\uB294 \uBB38\uC81C\uC5D0\uC11C \uBAB0\uC785\uC774 \uB192\uC544\uC9D1\uB2C8\uB2E4. \uD2B9\uD788 \uB370\uC774\uD130 \uD750\uB984\uC744 \uCD94\uC801\uD574 \uC6D0\uC778\uC744 \uC124\uBA85\uD558\uACE0 \uAC1C\uC120 \uD6A8\uACFC\uB97C \uC218\uCE58\uB85C \uD655\uC778\uD560 \uB54C \uC131\uCDE8\uAC10\uC744 \uB290\uB08D\uB2C8\uB2E4.",
              "\uC790\uC728\uC131\uACFC \uC2E0\uB8B0: \uAC1C\uC120\uC774 \uD544\uC694\uD55C \uC9C0\uC810\uC744 \uC9C1\uC811 \uBC1C\uACAC\uD558\uACE0 \uC81C\uC548\uD55C \uB4A4 \uC2E4\uD589\uAE4C\uC9C0 \uB9E1\uC744 \uB54C \uCC45\uC784\uAC10\uC744 \uAE0D\uC815\uC801\uC73C\uB85C \uBC1B\uC544\uB4E4\uC785\uB2C8\uB2E4. \uBA85\uD655\uD55C \uBAA9\uD45C\uC640 \uACB0\uC815 \uAD8C\uD55C\uC774 \uD568\uAED8 \uC8FC\uC5B4\uC9C0\uBA74 \uC5C5\uBB34 \uC18D\uB3C4\uAC00 \uB354 \uC548\uC815\uC801\uC785\uB2C8\uB2E4.",
              "\uC131\uACFC\uC758 \uAC00\uC2DC\uC131: CI \uC2DC\uAC04 \uB2E8\uCD95\uC774\uB098 \uB864\uBC31 \uACBD\uB85C \uC815\uB9AC\uCC98\uB7FC \uD300 \uC804\uCCB4\uC758 \uC791\uC5C5 \uC18D\uB3C4\uC640 \uC548\uC815\uC131\uC5D0 \uC601\uD5A5\uC744 \uC8FC\uB294 \uACB0\uACFC\uAC00 \uB208\uC5D0 \uBCF4\uC77C \uB54C \uB3D9\uAE30\uAC00 \uAC15\uD654\uB429\uB2C8\uB2E4."
            ],
            demotivationFactors: [
              "\uC7A6\uC740 \uCEE8\uD14D\uC2A4\uD2B8 \uC804\uD658: \uB9B4\uB9AC\uC988 \uB9C9\uBC14\uC9C0\uC5D0 \uACB0\uC81C \uC548\uC815\uD654, \uB9AC\uBDF0, \uC6B4\uC601 \uBB38\uC758\uAC00 \uB3D9\uC2DC\uC5D0 \uBAB0\uB9AC\uBA74 \uC9D1\uC911 \uC2DC\uAC04\uC774 \uAE68\uC9C0\uACE0 \uD53C\uB85C\uAC00 \uBE60\uB974\uAC8C \uB204\uC801\uB429\uB2C8\uB2E4.",
              "\uBD88\uBA85\uD655\uD55C \uC6B0\uC120\uC21C\uC704 \uBCC0\uACBD: \uC778\uC811 \uD300 \uC694\uCCAD\uC758 \uBC30\uACBD\uC774\uB098 \uC644\uB8CC \uAE30\uC900\uC774 \uB2A6\uAC8C \uACF5\uC720\uB418\uBA74 \uC5B4\uB5A4 \uD488\uC9C8 \uC218\uC900\uC73C\uB85C \uB9C8\uBB34\uB9AC\uD574\uC57C \uD558\uB294\uC9C0 \uD310\uB2E8\uD558\uAE30 \uC5B4\uB824\uC6CC\uC9D1\uB2C8\uB2E4.",
              "\uB9AC\uBDF0 \uBCD1\uBAA9: \uB9AC\uBDF0\uC5B4 \uD480\uC774 \uC81C\uD55C\uB418\uC5B4 \uD53C\uB4DC\uBC31\uC774 \uB2A6\uC5B4\uC9C8 \uB54C \uBCF8\uC778\uC774 \uC774\uBBF8 \uB05D\uB0B8 \uC791\uC5C5\uC758 \uB9E5\uB77D\uC744 \uB2E4\uC2DC \uBCF5\uAD6C\uD574\uC57C \uD558\uBA70, \uD6C4\uC18D \uC791\uC5C5 \uCC29\uC218\uB3C4 \uC9C0\uC5F0\uB429\uB2C8\uB2E4."
            ]
          },
          strengthWeakness: {
            strengths: [
              "\uC624\uB108\uC2ED: \uC548\uC815\uC131 \uACFC\uC81C\uB97C \uC124\uACC4, \uAD6C\uD604, \uBC30\uD3EC \uC774\uD6C4 \uBAA8\uB2C8\uD130\uB9C1\uAE4C\uC9C0 \uB04C\uACE0 \uAC00\uBA70 \uACB0\uACFC\uB97C \uB05D\uAE4C\uC9C0 \uD655\uC778\uD558\uB824\uB294 \uD0DC\uB3C4\uAC00 \uAC15\uD569\uB2C8\uB2E4.",
              "\uC2DC\uC2A4\uD15C \uC0AC\uACE0: \uD604\uC7AC \uC218\uC815\uC774 \uBC30\uD3EC \uD30C\uC774\uD504\uB77C\uC778, \uC7A5\uC560 \uB300\uC751, \uC720\uC9C0\uBCF4\uC218 \uBE44\uC6A9\uC5D0 \uC5B4\uB5A4 \uC601\uD5A5\uC744 \uC8FC\uB294\uC9C0 \uD568\uAED8 \uACE0\uB824\uD569\uB2C8\uB2E4.",
              "\uBB38\uC11C\uD654\uC640 \uACF5\uC720: \uB864\uBC31 \uACBD\uB85C\uC640 \uB9B4\uB9AC\uC988 \uCCB4\uD06C \uD3EC\uC778\uD2B8\uB97C \uC815\uB9AC\uD574 \uD300\uC774 \uAC19\uC740 \uAE30\uC900\uC73C\uB85C \uB300\uC751\uD560 \uC218 \uC788\uAC8C \uB9CC\uB4ED\uB2C8\uB2E4.",
              "\uD559\uC2B5 \uC804\uD658 \uC18D\uB3C4: \uB0AF\uC120 DB \uC6B4\uC601 \uC774\uC288\uB3C4 \uC9C0\uD45C\uC640 \uB85C\uADF8\uB97C \uAE30\uBC18\uC73C\uB85C \uC881\uD600\uAC00\uBA70 \uB2E4\uC74C \uC791\uC5C5\uC5D0 \uC801\uC6A9 \uAC00\uB2A5\uD55C \uD615\uD0DC\uB85C \uC815\uB9AC\uD569\uB2C8\uB2E4."
            ],
            weaknesses: [
              "\uB9AC\uBDF0 \uC5EC\uB825 \uAD00\uB9AC: \uBCF8\uC778 \uC791\uC5C5\uC744 \uAE4A\uAC8C \uD30C\uACE0\uB4DC\uB294 \uB9CC\uD07C \uB3D9\uB8CC \uCF54\uB4DC \uB9AC\uBDF0 \uC2DC\uAC04\uC774 \uB4A4\uB85C \uBC00\uB9B4 \uB54C\uAC00 \uC788\uC5B4 \uC77C\uC815 \uCD08\uBC18\uBD80\uD130 \uB9AC\uBDF0 \uC2DC\uAC04\uC744 \uD655\uBCF4\uD560 \uD544\uC694\uAC00 \uC788\uC2B5\uB2C8\uB2E4.",
              "\uD611\uC5C5 \uCD08\uAE30 \uC815\uB82C: \uC778\uC811 \uD300\uACFC\uC758 \uC758\uC874\uC131\uC774 \uD070 \uC791\uC5C5\uC5D0\uC11C\uB294 \uBAA9\uC801\uACFC \uC644\uB8CC \uAE30\uC900\uC744 \uB354 \uC774\uB978 \uC2DC\uC810\uC5D0 \uB9DE\uCD94\uBA74 \uC7AC\uC791\uC5C5\uC744 \uC904\uC77C \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
              "\uC6B4\uC601 \uC774\uC288 \uC704\uC784: \uC7A5\uC560 \uB300\uC751\uC774\uB098 \uAE34\uAE09 \uBB38\uC758\uB97C \uC9C1\uC811 \uD574\uACB0\uD558\uB824\uB294 \uACBD\uD5A5\uC774 \uC788\uC5B4 \uBC18\uBCF5 \uAC00\uB2A5\uD55C \uB300\uC751 \uC808\uCC28\uB97C \uD300\uC5D0 \uB354 \uC801\uADF9\uC801\uC73C\uB85C \uB118\uAE38 \uD544\uC694\uAC00 \uC788\uC2B5\uB2C8\uB2E4."
            ]
          },
          growth: {
            goals: "\uD604\uC7AC\uB294 \uC548\uC815\uC801\uC778 \uAE30\uB2A5 \uAD6C\uD604\uC744 \uB118\uC5B4, \uACE0\uD2B8\uB798\uD53D \uC0C1\uD669\uC5D0\uC11C \uB370\uC774\uD130 \uC800\uC7A5/\uC870\uD68C \uAD6C\uC870\uAC00 \uC5B4\uB5BB\uAC8C \uBCD1\uBAA9\uC744 \uB9CC\uB4DC\uB294\uC9C0 \uC9C1\uC811 \uB2E4\uB904\uBCF4\uACE0 \uC2F6\uC5B4 \uD569\uB2C8\uB2E4. \uB2E8\uC21C\uD788 \uCFFC\uB9AC\uB97C \uACE0\uCE58\uB294 \uC218\uC900\uBCF4\uB2E4 \uC9C0\uD45C \uC815\uC758, \uC6D0\uC778 \uBD84\uC11D, \uAC1C\uC120 \uC804\uD6C4 \uAC80\uC99D\uAE4C\uC9C0 \uC774\uC5B4\uC9C0\uB294 end-to-end \uACBD\uD5D8\uC744 \uC131\uC7A5 \uBAA9\uD45C\uB85C \uBCF4\uACE0 \uC788\uC2B5\uB2C8\uB2E4.",
            skills: "DB \uC778\uB371\uC2A4 \uC124\uACC4, \uC2E4\uD589 \uACC4\uD68D \uD574\uC11D, \uBC30\uD3EC \uC804 \uBD80\uD558 \uAC80\uC99D, \uC7A5\uC560 \uC0C1\uD669\uC5D0\uC11C\uC758 \uCEE4\uBBA4\uB2C8\uCF00\uC774\uC158\uCC98\uB7FC \uC6B4\uC601\uACFC \uAC1C\uBC1C\uC774 \uB9DE\uB2FF\uB294 \uC5ED\uB7C9\uC744 \uB113\uD788\uACE0 \uC2F6\uC5B4 \uD569\uB2C8\uB2E4. \uD2B9\uD788 \uACB0\uC81C/\uD50C\uB7AB\uD3FC \uC548\uC815\uC131\uCC98\uB7FC \uC0AC\uC6A9\uC790 \uC601\uD5A5\uC774 \uD070 \uC601\uC5ED\uC5D0\uC11C \uD310\uB2E8 \uADFC\uAC70\uB97C \uBA85\uD655\uD788 \uB9CC\uB4DC\uB294 \uD6C8\uB828\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.",
            interestedProjects: "\uB370\uC774\uD130 \uD30C\uC774\uD504\uB77C\uC778 \uAD00\uCE21\uC131 \uAC1C\uC120, \uB290\uB9B0 \uCFFC\uB9AC \uCD94\uC801 \uB300\uC2DC\uBCF4\uB4DC, \uB864\uBC31 \uB9AC\uD5C8\uC124 \uC790\uB3D9\uD654\uCC98\uB7FC \uACB0\uACFC\uB97C \uC218\uCE58\uB85C \uD655\uC778\uD560 \uC218 \uC788\uACE0 \uD300 \uC804\uCCB4\uC758 \uAC1C\uBC1C \uC18D\uB3C4\uC5D0 \uC601\uD5A5\uC744 \uC8FC\uB294 \uD504\uB85C\uC81D\uD2B8\uC5D0 \uAD00\uC2EC\uC774 \uC788\uC2B5\uB2C8\uB2E4."
          },
          workPerformance: {
            contents: "\uB2F4\uB2F9 \uC5C5\uBB34: \uD50C\uB7AB\uD3FC \uC548\uC815\uC131\uACFC \uACB0\uC81C \uBAA8\uB4C8 \uC548\uC815\uD654 \uC5C5\uBB34\uB97C \uC911\uC2EC\uC73C\uB85C \uB9B4\uB9AC\uC988 \uC804\uD6C4\uC758 \uC704\uD5D8\uC744 \uC904\uC774\uB294 \uC791\uC5C5\uC744 \uB9E1\uC558\uC2B5\uB2C8\uB2E4. CI \uD30C\uC774\uD504\uB77C\uC778 \uAC1C\uC120, \uB864\uBC31 \uACBD\uB85C \uC815\uB9AC, \uB370\uC774\uD130 \uCC98\uB9AC \uBCD1\uBAA9 \uD655\uC778, \uC6B4\uC601 \uBB38\uC758 \uB300\uC751 \uAE30\uC900 \uC815\uB9AC\uAE4C\uC9C0 \uAC1C\uBC1C\uACFC \uC6B4\uC601 \uC0AC\uC774\uC758 \uD488\uC9C8\uC744 \uB192\uC774\uB294 \uACFC\uC81C\uC5D0 \uCC38\uC5EC\uD588\uC2B5\uB2C8\uB2E4.",
            contribution: [
              "\uBC30\uD3EC \uD6A8\uC728 \uAC1C\uC120: CI \uD30C\uC774\uD504\uB77C\uC778\uC758 \uBC18\uBCF5 \uB2E8\uACC4\uB97C \uC815\uB9AC\uD574 \uBC30\uD3EC \uB300\uAE30 \uC2DC\uAC04\uC744 \uC904\uC600\uACE0, \uB9B4\uB9AC\uC988 \uC911 \uD655\uC778\uD574\uC57C \uD560 \uCCB4\uD06C \uD3EC\uC778\uD2B8\uB97C \uBA85\uD655\uD788 \uD588\uC2B5\uB2C8\uB2E4.",
              "\uC6B4\uC601 \uC548\uC815\uC131 \uAC15\uD654: \uACB0\uC81C \uBAA8\uB4C8 \uB864\uBC31 \uACBD\uB85C\uC640 \uC7A5\uC560 \uBC1C\uC0DD \uC2DC \uD655\uC778 \uC21C\uC11C\uB97C \uBB38\uC11C\uD654\uD574 \uBB38\uC81C \uBC1C\uC0DD \uC2DC \uD300\uC774 \uBE60\uB974\uAC8C \uAC19\uC740 \uD654\uBA74\uC744 \uBCF4\uBA70 \uB300\uC751\uD560 \uC218 \uC788\uAC8C \uD588\uC2B5\uB2C8\uB2E4.",
              "\uB370\uC774\uD130 \uBCD1\uBAA9 \uAC00\uC2DC\uD654: \uB290\uB9B0 \uC870\uD68C \uD750\uB984\uC744 \uC7AC\uD604\uD558\uACE0 \uC9C0\uD45C\uB97C \uC815\uB9AC\uD574 DB \uCD5C\uC801\uD654 \uACFC\uC81C\uC758 \uC6B0\uC120\uC21C\uC704\uB97C \uB17C\uC758\uD560 \uC218 \uC788\uB294 \uADFC\uAC70\uB97C \uB9CC\uB4E4\uC5C8\uC2B5\uB2C8\uB2E4.",
              "\uD611\uC5C5 \uD488\uC9C8 \uD5A5\uC0C1: \uC778\uC811 \uD300\uACFC\uC758 \uBBF8\uD305\uC5D0\uC11C \uAE30\uC220 \uC81C\uC57D\uACFC \uC77C\uC815 \uC601\uD5A5\uC744 \uAD6C\uBD84\uD574 \uC124\uBA85\uD558\uBA70 \uC758\uC0AC\uACB0\uC815\uC5D0 \uD544\uC694\uD55C \uC815\uBCF4\uB97C \uC81C\uACF5\uD588\uC2B5\uB2C8\uB2E4."
            ],
            blockers: [
              "\uB9B4\uB9AC\uC988 \uC77C\uC815 \uC555\uBC15: \uC548\uC815\uD654 \uAE30\uAC04\uC774 \uC9E7\uC544 \uB9AC\uBDF0\uC640 \uD68C\uACE0\uAC00 \uD6C4\uC21C\uC704\uB85C \uBC00\uB9AC\uAE30 \uC27D\uACE0, \uAC1C\uC120 \uC544\uC774\uB514\uC5B4\uAC00 \uB2E4\uC74C \uC0AC\uC774\uD074\uB85C \uB118\uC5B4\uAC08 \uAC00\uB2A5\uC131\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
              "\uB9AC\uBDF0\uC5B4 \uBD80\uC871: \uD2B9\uC815 \uC601\uC5ED\uC744 \uC774\uD574\uD558\uB294 \uB9AC\uBDF0\uC5B4\uAC00 \uC81C\uD55C\uB418\uC5B4 \uC788\uC5B4 \uD53C\uB4DC\uBC31 \uB300\uAE30 \uC2DC\uAC04\uC774 \uAE38\uC5B4\uC9C0\uACE0, \uBCF5\uC7A1\uD55C \uBCC0\uACBD\uC758 \uB9E5\uB77D \uACF5\uC720 \uBE44\uC6A9\uC774 \uCEE4\uC9D1\uB2C8\uB2E4.",
              "\uD300 \uAC04 \uC758\uC874\uC131: \uACB0\uC81C\uC640 \uD50C\uB7AB\uD3FC \uC791\uC5C5\uC740 \uC778\uC811 \uD300\uC758 \uC77C\uC815\uACFC \uB370\uC774\uD130 \uC815\uC758\uC5D0 \uC601\uD5A5\uC744 \uBC1B\uAE30 \uB54C\uBB38\uC5D0 \uCD08\uBC18 \uD569\uC758\uAC00 \uB2A6\uC73C\uBA74 \uBC94\uC704 \uC870\uC815\uC774 \uBC18\uBCF5\uB429\uB2C8\uB2E4."
            ]
          },
          personal: {
            emotionWellbeing: "\uC804\uBC18\uC801\uC778 \uAC10\uC815 \uC0C1\uD0DC\uB294 \uC548\uC815\uC801\uC774\uACE0 \uC5C5\uBB34\uC5D0 \uB300\uD55C \uD0DC\uB3C4\uB3C4 \uAE0D\uC815\uC801\uC785\uB2C8\uB2E4. \uB2E4\uB9CC \uB9B4\uB9AC\uC988 \uC8FC\uAC04\uC5D0\uB294 \uAE34\uAE09 \uBB38\uC758\uC640 \uB9AC\uBDF0\uAC00 \uACB9\uCE58\uBA70 \uD53C\uB85C \uC2E0\uD638\uAC00 \uBCF4\uC774\uBBC0\uB85C, \uC911\uC694\uD55C \uC548\uC815\uD654 \uC791\uC5C5 \uB4A4\uC5D0\uB294 \uD68C\uBCF5 \uC2DC\uAC04\uC744 \uD655\uBCF4\uD574\uC8FC\uB294 \uAC83\uC774 \uC88B\uC2B5\uB2C8\uB2E4.",
            network: "\uB3D9\uB8CC\uC640\uC758 \uAE30\uC220\uC801 \uD1A0\uB860\uC744 \uC120\uD638\uD558\uBA70, \uB9E5\uB77D\uC774 \uC77C\uCC0D \uACF5\uC720\uB420\uC218\uB85D \uB3C4\uC6C0 \uC694\uCCAD\uACFC \uC758\uACAC \uC81C\uC2DC\uAC00 \uD65C\uBC1C\uD569\uB2C8\uB2E4. \uD2B9\uD788 \uD50C\uB7AB\uD3FC/\uACB0\uC81C\uCC98\uB7FC \uC5EC\uB7EC \uD300\uC774 \uC5BD\uD78C \uC791\uC5C5\uC5D0\uC11C\uB294 \uBCF8\uC778\uC774 \uC815\uB9AC\uD55C \uB0B4\uC6A9\uC744 \uAE30\uC900\uC73C\uB85C \uD611\uC5C5\uC744 \uC774\uC5B4\uAC00\uB824\uB294 \uC131\uD5A5\uC774 \uC788\uC2B5\uB2C8\uB2E4.",
            interest: "\uC5EC\uD589\uACFC \uC5D4\uC9C0\uB2C8\uC5B4\uB9C1 \uD559\uC2B5 \uCEE4\uBBA4\uB2C8\uD2F0\uC5D0 \uAD00\uC2EC\uC774 \uC788\uACE0, \uC5C5\uBB34 \uBC16\uC5D0\uC11C \uC5BB\uC740 \uC5D0\uB108\uC9C0\uB97C \uD559\uC2B5\uACFC \uBB38\uC81C \uD574\uACB0\uB85C \uC5F0\uACB0\uD558\uB294 \uD3B8\uC785\uB2C8\uB2E4. \uCD5C\uADFC\uC5D0\uB294 \uC6B4\uC601 \uC548\uC815\uC131, \uB370\uC774\uD130 \uCC98\uB9AC, \uAC1C\uBC1C \uC0DD\uC0B0\uC131 \uAC1C\uC120\uCC98\uB7FC \uC2E4\uBB34\uC640 \uC9C1\uC811 \uC774\uC5B4\uC9C0\uB294 \uC8FC\uC81C\uC5D0 \uAD00\uC2EC\uC774 \uB192\uC2B5\uB2C8\uB2E4."
          },
          nextTopics: [
            "DB \uCD5C\uC801\uD654 \uACFC\uC81C: \uC774\uBC88 \uBD84\uAE30\uC5D0 \uC9C1\uC811 \uB9E1\uC744 \uC218 \uC788\uB294 \uBCD1\uBAA9 \uBD84\uC11D \uBC94\uC704\uC640 \uC131\uACF5 \uC9C0\uD45C\uB97C \uD568\uAED8 \uC815\uD558\uAE30",
            "\uB9AC\uBDF0 \uC5EC\uB825: \uB2E4\uC74C \uB9B4\uB9AC\uC988 \uC804\uC5D0 \uBCF4\uD638\uB41C \uB9AC\uBDF0 \uC2DC\uAC04\uACFC \uB9AC\uBDF0\uC5B4 \uBD84\uC0B0 \uBC29\uC2DD\uC744 \uD569\uC758\uD558\uAE30",
            "\uC6B4\uC601 \uB300\uC751: \uBC18\uBCF5\uB418\uB294 \uACB0\uC81C/\uD50C\uB7AB\uD3FC \uBB38\uC758\uB97C \uBB38\uC11C\uD654\uD558\uAC70\uB098 \uC790\uB3D9\uD654\uD560 \uC218 \uC788\uB294 \uD6C4\uBCF4 \uC815\uB9AC\uD558\uAE30",
            "\uC131\uC7A5 \uD53C\uB4DC\uBC31: CI \uAC1C\uC120\uACFC \uC548\uC815\uD654 \uAE30\uC5EC\uAC00 \uD300 \uC131\uACFC\uC5D0 \uC5B4\uB5A4 \uC601\uD5A5\uC744 \uB0C8\uB294\uC9C0 \uAD6C\uCCB4\uC801\uC73C\uB85C \uD53C\uB4DC\uBC31\uD558\uAE30"
          ],
          flightRiskDescription: "\uD604\uC7AC \uD1F4\uC0AC \uC704\uD5D8\uC740 \uB0AE\uC740 \uD3B8\uC785\uB2C8\uB2E4. \uAE30\uC220\uC801\uC73C\uB85C \uC758\uBBF8 \uC788\uB294 \uBB38\uC81C\uB97C \uB9E1\uACE0 \uC788\uACE0, \uBCF8\uC778\uC758 \uAC1C\uC120\uC548\uC774 \uC2E4\uC81C \uBC30\uD3EC \uC18D\uB3C4\uC640 \uC548\uC815\uC131 \uD5A5\uC0C1\uC73C\uB85C \uC774\uC5B4\uC9C0\uB294 \uACBD\uD5D8\uC744 \uD558\uACE0 \uC788\uC5B4 \uC5C5\uBB34 \uBAB0\uC785\uC774 \uC720\uC9C0\uB418\uACE0 \uC788\uC2B5\uB2C8\uB2E4. \uB2E4\uB9CC \uB9B4\uB9AC\uC988 \uC555\uBC15, \uB9AC\uBDF0 \uBCD1\uBAA9, \uBD88\uBA85\uD655\uD55C \uD300 \uAC04 \uC6B0\uC120\uC21C\uC704\uAC00 \uBC18\uBCF5\uB418\uBA74 \uD53C\uB85C\uAC00 \uB204\uC801\uB420 \uC218 \uC788\uC73C\uBBC0\uB85C \uC131\uC7A5 \uACFC\uC81C\uC640 \uBCF4\uD638\uB41C \uB9AC\uBDF0 \uC2DC\uAC04\uC744 \uD568\uAED8 \uC124\uACC4\uD558\uB294 \uAC83\uC774 \uC911\uC694\uD569\uB2C8\uB2E4."
        },
        sidePanel: {
          actionItems: {
            shareRollbackPlan: "\uB9B4\uB9AC\uC988 \uB864\uBC31 \uACC4\uD68D \uACF5\uC720\uD558\uAE30",
            prepareDbOptimizationScope: "DB \uCD5C\uC801\uD654 \uACFC\uC81C \uBC94\uC704 \uCD08\uC548 \uC791\uC131\uD558\uAE30",
            alignReviewWindow: "\uB9B4\uB9AC\uC988 \uC804 \uBCF4\uD638\uB41C \uB9AC\uBDF0 \uC2DC\uAC04 \uD569\uC758\uD558\uAE30"
          }
        },
        createDialog: {
          meetings: {
            q2Okr: "2\uBD84\uAE30 OKR \uC911\uAC04 \uC810\uAC80",
            paymentRetro: "\uACB0\uC81C \uBAA8\uB4C8 \uC548\uC815\uD654 \uD68C\uACE0",
            growthPlan: "\uC131\uC7A5 \uACC4\uD68D \uC815\uB82C"
          }
        }
      }
    }
  },
  features: {
    dashboard: {
      title: "\uC870\uC9C1\uC758 1on1 \uD604\uD669\uC744 \uD55C \uB208\uC5D0 \uD655\uC778\uD558\uC138\uC694.",
      description: "\uC804\uCCB4\uC801\uC778 \uC2E4\uD589\uB960\uACFC \uAC1C\uC778\uBCC4 \uC0C1\uC138 \uD604\uD669\uAE4C\uC9C0 \uB300\uC2DC\uBCF4\uB4DC\uC5D0\uC11C \uD655\uC778\uD558\uC138\uC694.",
      steps: {
        overview: {
          title: "\uC870\uC9C1\uC758 1on1 \uD604\uD669\uC744 \uD55C \uB208\uC5D0 \uD655\uC778",
          description: "\uC870\uC9C1\uC758 1on1 \uC2E4\uD589\uB960, \uD3C9\uADE0 \uBBF8\uD305 \uC8FC\uAE30, \uC644\uB8CC\uB41C \uBBF8\uD305 \uC218\uC640 \uBC14\uC774\uD0C8\uC744 \uD655\uC778\uD574\uC694."
        },
        table: {
          title: "\uB9E4\uB2C8\uC800\uBCC4\xB7\uBA64\uBC84\uBCC4 1on1 \uD604\uD669 \uD655\uC778",
          description: "\uD558\uB2E8 \uD45C\uC5D0\uC11C \uB9E4\uB2C8\uC800\uBCC4\xB7\uBA64\uBC84\uBCC4 \uD604\uD669\uC744 \uD655\uC778\uD558\uACE0, \uC774\uB984\uC744 \uB20C\uB7EC \uC0C1\uC138 \uD398\uC774\uC9C0\uB85C \uC774\uB3D9\uD574\uC694."
        },
        detail: {
          title: "\uC0C1\uC138 \uD398\uC774\uC9C0\uC5D0\uC11C \uAE4A\uC774 \uC788\uAC8C \uD30C\uC545",
          description: "\uB9E4\uB2C8\uC800\uB85C\uC11C\xB7\uBA64\uBC84\uB85C\uC11C \uC9C4\uD589\uD55C 1on1 \uD604\uD669\uACFC \uBC14\uC774\uD0C8 \uCD94\uC774, \uAC1C\uBCC4 \uBBF8\uD305 \uC694\uC57D\uC744 \uD655\uC778\uD560 \uC218 \uC788\uC5B4\uC694."
        }
      }
    },
    member_report: {
      title: "1on1 \uB370\uC774\uD130\uB85C \uBA64\uBC84\uC5D0 \uAD00\uD55C \uB9AC\uD3EC\uD2B8\uB97C \uB9CC\uB4E4 \uC218 \uC788\uC5B4\uC694",
      description: "AI\uAC00 1on1 \uB300\uD654\uB97C \uC77D\uACE0, \uBA64\uBC84\uB97C \uC5B4\uB5BB\uAC8C \uCC59\uAE30\uBA74 \uC88B\uC744\uC9C0\uBD80\uD130 \uB3D9\uAE30\xB7\uAC15\uC810\xB7\uAE30\uC5EC\uB3C4\uAE4C\uC9C0 \uB9DE\uCDA4 \uB9AC\uD3EC\uD2B8\uB85C \uC815\uB9AC\uD574 \uB4DC\uB824\uC694.",
      steps: {
        create: {
          title: "\uC0C8 \uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30",
          description: "\uADF8\uB3D9\uC548\uC758 1on1\uC744 \uBC14\uD0D5\uC73C\uB85C \uBA64\uBC84\uB97C \uB354 \uAE4A\uC774 \uC774\uD574\uD558\uAE30 \uC704\uD55C \uB9AC\uD3EC\uD2B8\uB97C \uB9CC\uB4ED\uB2C8\uB2E4."
        },
        select: {
          title: "\uAE30\uAC04\uACFC \uBBF8\uD305 \uC120\uD0DD",
          description: "\uB9AC\uD3EC\uD2B8\uC758 \uC7AC\uB8CC\uAC00 \uB420 1on1 \uBBF8\uD305\uC744 \uC120\uD0DD\uD55C \uB4A4, \uB9AC\uD3EC\uD2B8 \uB9CC\uB4E4\uAE30\uB97C \uB204\uB974\uC138\uC694."
        },
        insights: {
          title: "AI \uC778\uC0AC\uC774\uD2B8 \uD655\uC778",
          description: "\uC885\uD569\uC801\uC778 \uC778\uC0AC\uC774\uD2B8\uBD80\uD130 \uB2E4\uC74C 1on1 \uC8FC\uC81C\uC640 \uC561\uC158 \uC544\uC774\uD15C\uAE4C\uC9C0 \uC774\uC5B4\uC11C \uD655\uC778\uD558\uC138\uC694."
        }
      }
    },
    ninebox: {
      title: "9Box\uB85C \uC778\uC7AC \uAD00\uB9AC\uD558\uAE30",
      description: "\uAD6C\uC131\uC6D0\uC744 \uC9C1\uC811 \uC62E\uACA8 \uC870\uC9C1\uC758 \uC778\uC7AC \uBD84\uD3EC\uC640 \uC704\uD5D8\uAD70\uC744 \uAD00\uB9AC\uD574\uBCF4\uC138\uC694.",
      steps: {
        performance: {
          title: "\uC131\uACFC \uBC0F \uC7A0\uC7AC\uB825 \uBC30\uCE58",
          description: "\uC131\uACFC\uC640 \uC7A0\uC7AC\uB825\uC744 \uAE30\uC900\uC73C\uB85C \uC778\uC7AC\uB97C \uBD84\uB958\uD574, \uC6B0\uB9AC \uC870\uC9C1\uC758 \uD575\uC2EC\uC778\uC7AC\uAC00 \uB204\uAD6C\uC778\uC9C0 \uD30C\uC545\uD560 \uC218 \uC788\uC5B4\uC694."
        },
        switch: {
          title: "\uB9E4\uD2B8\uB9AD\uC2A4 \uC804\uD658",
          description: "\uC0C1\uB2E8 \uD0ED\uC744 \uB204\uB974\uBA74 \uC11C\uB85C \uB2E4\uB978 \uB450 \uAE30\uC900\uC73C\uB85C \uBC88\uAC08\uC544 \uC0B4\uD3B4\uBCFC \uC218 \uC788\uC5B4\uC694."
        },
        flightRisk: {
          title: "\uD1F4\uC0AC \uC704\uD5D8\uB3C4 \uB9E4\uD2B8\uB9AD\uC2A4 \uBC30\uCE58",
          description: "\uD1F4\uC0AC \uC2DC \uC870\uC9C1\uC5D0 \uBBF8\uCE58\uB294 \uC601\uD5A5\uC744 \uAE30\uC900\uC73C\uB85C, \uC8FC\uC758\uB97C \uAE30\uC6B8\uC5EC\uC57C \uD560 \uC778\uC7AC\uB97C \uD30C\uC545\uD560 \uC218 \uC788\uC5B4\uC694."
        }
      }
    }
  },
  update_nudge: {
    title: "\uBBF8\uD305\uC774 \uB05D\uB098\uBA74 \uB098\uB97C \uC704\uD55C \uD53C\uB4DC\uBC31\uC774 \uB3C4\uCC29\uD574\uC694",
    description: "\uB179\uC74C\uD55C 1on1\uC744 \uBD84\uC11D\uD560 \uB54C,\n\uC774\uC81C \uBA64\uBC84\uC5D0\uAC8C\uB3C4 \uB2E4\uC74C \uBBF8\uD305\uC744 \uC704\uD55C \uD53C\uB4DC\uBC31\uC744 \uB4DC\uB824\uC694.",
    close: "\uB2EB\uAE30",
    steps: {
      step1: {
        title: "\uB2E4\uC74C \uBBF8\uD305\uC744 \uC704\uD55C \uD53C\uB4DC\uBC31",
        description: "\uC774\uBC88 \uBBF8\uD305\uC744 \uC704\uD574 \uC900\uBE44\uD588\uC9C0\uB9CC \uBABB \uAEBC\uB0B8 \uC774\uC57C\uAE30, \uB2E4\uC74C\uC5D0 \uB2E4\uB904\uBCFC \uC8FC\uC81C \uB4F1\uC744\n\uCE74\uB4DC\uB85C \uC815\uB9AC\uD574 \uBD84\uC11D \uD0ED\uC5D0\uC11C \uBCF4\uC5EC\uB4DC\uB824\uC694. \uC774 \uD53C\uB4DC\uBC31\uC740 \uB098\uB9CC \uBCFC \uC218 \uC788\uC5B4\uC694."
      }
    }
  }
};

// src/locales/ko-KR/index.ts
var ko_KR_default = {
  common: common_default2,
  task: task_default2,
  project: project_default2,
  team: team_default2,
  todo: todo_default2,
  report: report_default2,
  auth: auth_default2,
  comment: comment_default2,
  note: note_default2,
  activity: activity_default2,
  workspace: workspace_default2,
  planner: planner_default2,
  member_invitation: member_invitation_default2,
  landing: landing_default2,
  ai: ai_default2,
  notification: notification_default2,
  dailyscrum: dailyscrum_default2,
  feedback: feedback_default2,
  drive: drive_default2,
  oneonone: oneonone_default2,
  ninebox: ninebox_default2,
  template: template_default2,
  payment: payment_default2,
  aws_marketplace: aws_marketplace_default2,
  dashboard: dashboard_default2,
  profile_card: profile_card_default2,
  meeting_prep: meeting_prep_default2,
  oneonone_notification: oneonone_notification_default2,
  landing_new: landing_new_default2,
  feature_tutorial: feature_tutorial_default2
};

// src/locales/vi-VN/common.json
var common_default3 = {
  metadata: {
    title: "Orblit | Hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o AI & \u0111\xE1nh gi\xE1 v\u0103n h\xF3a qua 1-on-1",
    description: "N\xE2ng cao n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o v\xE0 qu\u1EA3n l\xFD \u0111\u1ED9i nh\xF3m th\xF4ng minh h\u01A1n v\u1EDBi Orblit. Bi\u1EBFn d\u1EEF li\u1EC7u 1-on-1 th\xE0nh th\xF4ng tin chi ti\u1EBFt v\u1EC1 t\u1ED5 ch\u1EE9c theo th\u1EDDi gian th\u1EF1c v\xE0 ng\u0103n ng\u1EEBa r\u1EE7i ro ngh\u1EC9 vi\u1EC7c c\u1EE7a nh\xE2n t\xE0i ch\u1EE7 ch\u1ED1t.",
    keywords: "orblit, 1-on-1, h\u1ECDp 1-1, l\xE3nh \u0111\u1EA1o, hu\u1EA5n luy\u1EC7n, ph\u1EA3n h\u1ED3i, c\u1ED1 v\u1EA5n, ghi ch\xFA cu\u1ED9c h\u1ECDp, qu\u1EA3n l\xFD nh\xE2n t\xE0i, qu\u1EA3n l\xFD thay \u0111\u1ED5i, ph\xE1t tri\u1EC3n nh\xE2n vi\xEAn, workshop",
    app_description: "Cu\u1ED9c h\u1ECDp 1:1 hi\u1EC7u qu\u1EA3 v\xE0 \xFD ngh\u0129a nh\u1EA5t"
  },
  action: {
    created: "t\u1EA1o",
    deleted: "\u0111\xE3 x\xF3a",
    edited: "\u0111\xE3 ch\u1EC9nh s\u1EEDa",
    select: "Ch\u1ECDn",
    back: "Quay l\u1EA1i",
    next: "K\u1EBF ti\u1EBFp",
    complete: "Ho\xE0n t\u1EA5t",
    create: "T\u1EA1o",
    delete: "X\xF3a",
    edit: "Ch\u1EC9nh s\u1EEDa",
    cancel: "H\u1EE7y",
    save: "L\u01B0u",
    enter: "\u0110i v\xE0o",
    reject: "T\u1EEB ch\u1ED1i",
    rejected: "V\u1EADt b\u1ECB lo\u1EA1i b\u1ECF",
    collapse: "S\u1EE5p \u0111\u1ED5",
    expand: "M\u1EDF ra",
    select_all: "Ch\u1ECDn t\u1EA5t c\u1EA3",
    unselect_all: "B\u1ECF ch\u1ECDn t\u1EA5t c\u1EA3",
    skip: "B\u1ECF qua"
  },
  notification: {
    message: "Vui l\xF2ng \u0111\u1EE3i m\u1ED9t l\xFAc ...",
    loading: "\u0110ang t\u1EA3i..."
  },
  error: {
    page: {
      title: "Kh\xF4ng t\xECm th\u1EA5y trang",
      description: "Trang \u0111\xE3 b\u1ECB x\xF3a ho\u1EB7c kh\xF4ng t\u1ED3n t\u1EA1i.",
      back_button: "Quay l\u1EA1i trang tr\u01B0\u1EDBc",
      home_button: "V\u1EC1 trang ch\u1EE7",
      error_code: "M\xE3 l\u1ED7i"
    },
    message: "L\u1ED7i x\u1EA3y ra trong khi t\u1EA3i d\u1EEF li\u1EC7u",
    unknown_error: "M\u1ED9t l\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh x\u1EA3y ra",
    not_allowed: {
      note: "Xin l\u1ED7i, b\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp ghi ch\xFA n\xE0y.",
      project: "Xin l\u1ED7i, b\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp d\u1EF1 \xE1n n\xE0y.",
      task: "Xin l\u1ED7i, b\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp nhi\u1EC7m v\u1EE5 n\xE0y."
    },
    not_found: {
      note: "L\u01B0u \xFD kh\xF4ng t\xECm th\u1EA5y.",
      project: "Kh\xF4ng t\xECm th\u1EA5y d\u1EF1 \xE1n.",
      task: "Nhi\u1EC7m v\u1EE5 kh\xF4ng t\xECm th\u1EA5y."
    },
    module: {
      loading: "T\u1EA3i l\u1EA1i m\xF4 -\u0111un ...",
      refresh: "N\u1EBFu m\xE0n h\xECnh n\xE0y v\u1EABn t\u1ED3n t\u1EA1i, xin vui l\xF2ng l\xE0m m\u1EDBi."
    },
    refresh: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra, xin vui l\xF2ng l\xE0m m\u1EDBi.",
    data_fetch: {
      title: "Kh\xF4ng th\u1EC3 t\u1EA3i d\u1EEF li\u1EC7u.",
      message: "Vui l\xF2ng th\u1EED l\u1EA1i sau m\u1ED9t l\xFAc.\nN\u1EBFu v\u1EA5n \u0111\u1EC1 v\u1EABn ti\u1EBFp di\u1EC5n, vui l\xF2ng li\xEAn h\u1EC7 v\u1EDBi D\u1ECBch v\u1EE5 Kh\xE1ch h\xE0ng.",
      retry_button: "Th\u1EED l\u1EA1i"
    },
    toast: {
      query_failed: "Kh\xF4ng th\u1EC3 t\u1EA3i d\u1EEF li\u1EC7u",
      mutation_failed: "Kh\xF4ng th\u1EC3 x\u1EED l\xFD y\xEAu c\u1EA7u",
      try_again: "Vui l\xF2ng th\u1EED l\u1EA1i sau"
    }
  },
  filter: {
    title: "B\u1ED9 l\u1ECDc",
    reset: "\u0110\u1EB7t l\u1EA1i b\u1ED9 l\u1ECDc",
    assignee: "Ng\u01B0\u1EDDi \u0111\u01B0\u1EE3c giao",
    period: {
      all: "T\u1EA5t c\u1EA3 c\xE1c th\u1EDDi gian",
      hide_all: "\u1EA8n t\u1EA5t c\u1EA3",
      month: "Th\xE1ng",
      title: "Th\u1EDDi gian",
      today: "H\xF4m nay",
      two_week: "Hai tu\u1EA7n",
      week: "Tu\u1EA7n",
      yesterday: "H\xF4m qua"
    },
    not_completed: "Ch\u01B0a ho\xE0n th\xE0nh"
  },
  placeholder: {
    task: "Th\xEAm nhi\u1EC7m v\u1EE5 m\u1EDBi",
    description: "Th\xEAm m\u1ED9t m\xF4 t\u1EA3 ...",
    priority: "Kh\xF4ng c\xF3 \u01B0u ti\xEAn",
    status: "Kh\xF4ng c\xF3 tr\u1EA1ng th\xE1i"
  },
  logout: "\u0110\u0103ng xu\u1EA5t",
  setting: {
    profile: "C\xE0i \u0111\u1EB7t t\xE0i kho\u1EA3n",
    orblit_lab: {
      description: "C\xE0i \u0111\u1EB7t cho c\xE1c t\xEDnh n\u0103ng m\u1EDBi \u0111\u01B0\u1EE3c chu\u1EA9n b\u1ECB \u0111\u1EC3 ra m\u1EAFt.",
      title: "Ph\xF2ng th\xED nghi\u1EC7m Orblit"
    },
    view: {
      option: {
        display: "Hi\u1EC3n th\u1ECB thu\u1ED9c t\xEDnh",
        done_task: "Ho\xE0n th\xE0nh nhi\u1EC7m v\u1EE5",
        grouping: "Nh\xF3m",
        grouping_placeholder: "Kh\xF4ng c\xF3 nh\xF3m",
        order: "\u0110\u1EB7t h\xE0ng"
      },
      reset: "\u0110\u1EB7t l\u1EA1i c\xE0i \u0111\u1EB7t",
      setting_title: "Xem c\xE0i \u0111\u1EB7t",
      title: "Xem"
    }
  },
  button: {
    cancel: "H\u1EE7y b\u1ECF",
    no: "Kh\xF4ng",
    save: "L\u01B0u",
    yes: "C\xF3",
    confirm: "X\xE1c nh\u1EADn",
    close: "\u0111\xF3ng",
    send: "G\u1EEDi"
  },
  date_picker: {
    selected_date: "Ng\xE0y ch\u1ECDn",
    today: "H\xF4m nay",
    tomorrow: "Ng\xE0y mai",
    until_today: "Ng\xE0y h\xF4m nay",
    until_tomorrow: "Ng\xE0y mai \u0111\u1EBFn h\u1EA1n",
    until_yesterday: "Ng\xE0y h\xF4m qua",
    yesterday: "H\xF4m qua"
  },
  feature: {
    planner: "Planner",
    drive: "Drive"
  },
  field: {
    due_date: "Ng\xE0y \u0111\u1EBFn h\u1EA1n",
    category: {
      title: "Danh m\u1EE5c",
      work: "C\xF4ng vi\u1EC7c",
      meeting: "Cu\u1ED9c h\u1ECDp",
      communication: "Giao ti\u1EBFp",
      study: "H\u1ECDc t\u1EADp",
      rest: "Ngh\u1EC9 ng\u01A1i",
      etc: "Kh\xE1c"
    },
    priority: {
      title: "S\u1EF1 \u01B0u ti\xEAn",
      high: "Cao",
      highest: "Cao nh\u1EA5t",
      low: "Th\u1EA5p",
      lowest: "Th\u1EA5p nh\u1EA5t",
      medium: "Trung b\xECnh"
    },
    status: {
      title: "Tr\u1EA1ng th\xE1i",
      at_risk: "C\xF3 nguy c\u01A1",
      backlog: "Backlog",
      canceled: "B\u1ECB h\u1EE7y b\u1ECF",
      completed: "Ho\xE0n th\xE0nh",
      in_progress: "Trong ti\u1EBFn tr\xECnh",
      paused: "T\u1EA1m d\u1EEBng",
      planned: "K\u1EBF ho\u1EA1ch"
    },
    register_date: "\u0110\u01B0\u1EE3c t\u1EA1o ra t\u1EA1i"
  },
  time: {
    hour: "gi\u1EDD",
    minute: "ph\xFAt"
  },
  emoji_reaction: {
    add: "Th\xEAm ph\u1EA3n \u1EE9ng"
  },
  deletedUser: "Ng\u01B0\u1EDDi d\xF9ng \u0111\xE3 r\u1EDDi \u0111i",
  team_selector: {
    all: "T\u1EA5t c\u1EA3",
    none: "Kh\xF4ng c\xF3 \u0111\u1ED9i cha",
    include_sub_teams: "Bao g\u1ED3m nh\xF3m con"
  }
};

// src/locales/vi-VN/task.json
var task_default3 = {
  hidden_task: "Nhi\u1EC7m v\u1EE5 \u1EA9n",
  hidden_task_management: "Qu\u1EA3n l\xFD nhi\u1EC7m v\u1EE5 \u1EA9n",
  hidden_task_management_description: "B\u1EA1n c\xF3 th\u1EC3 mang l\u1EA1i nhi\u1EC7m v\u1EE5 \u1EA9n cho H\u1ED9p th\u01B0 \u0111\u1EBFn",
  no_hidden_task: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5 \u1EA9n.",
  reveal: {
    error: {
      message: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong khi ti\u1EBFt l\u1ED9 nhi\u1EC7m v\u1EE5 \u1EA9n.",
      title: "Kh\xF4ng ti\u1EBFt l\u1ED9 nhi\u1EC7m v\u1EE5 \u1EA9n"
    },
    processing: {
      title: "Ti\u1EBFt l\u1ED9 nhi\u1EC7m v\u1EE5 \u1EA9n"
    },
    success: {
      message: "Ti\u1EBFt l\u1ED9 th\xE0nh c\xF4ng nhi\u1EC7m v\u1EE5 \u1EA9n."
    },
    title: "Ti\u1EBFt l\u1ED9 nhi\u1EC7m v\u1EE5 \u1EA9n"
  },
  selection: {
    none: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5 \u0111\u01B0\u1EE3c ch\u1ECDn",
    some_one: "{{count}} nhi\u1EC7m v\u1EE5 \u0111\u01B0\u1EE3c ch\u1ECDn",
    some_other: "{{count}} nhi\u1EC7m v\u1EE5 \u0111\u01B0\u1EE3c ch\u1ECDn"
  },
  indicator: {
    error: {
      message: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong qu\xE1 tr\xECnh x\u1EED l\xFD thay \u0111\u1ED5i ch\u1EC9 s\u1ED1.",
      message_not_valid: "Gi\xE1 tr\u1ECB m\u1EE5c ti\xEAu ph\u1EA3i l\u1EDBn h\u01A1n gi\xE1 tr\u1ECB ban \u0111\u1EA7u.",
      title: "Kh\xF4ng th\u1EC3 thay \u0111\u1ED5i c\xE1c ch\u1EC9 s\u1ED1"
    },
    initial_value: "Gi\xE1 tr\u1ECB ban \u0111\u1EA7u",
    placeholder: "Kh\xF4ng c\xF3 ch\u1EC9 s\u1ED1",
    setting: {
      number_title: "\u0110\u1EB7t gi\xE1 tr\u1ECB ch\u1EC9 b\xE1o",
      reset: "\u0110\u1EB7t l\u1EA1i c\xE0i \u0111\u1EB7t",
      title: "C\xE0i \u0111\u1EB7t ch\u1EC9 b\xE1o"
    },
    target_value: "Gi\xE1 tr\u1ECB m\u1EE5c ti\xEAu",
    title: "Ch\u1EC9 b\xE1o",
    current_value: "Gi\xE1 tr\u1ECB hi\u1EC7n t\u1EA1i",
    dollar: "\u0110\xF4 la",
    number: "con s\u1ED1",
    percent: "ph\u1EA7n tr\u0103m",
    won: "th\u1EAFng",
    type: "Ki\u1EC3u"
  },
  field: {
    complete_date: "\u0110\xE3 ho\xE0n th\xE0nh ng\xE0y",
    content: "N\u1ED9i dung",
    indicator_type: "Lo\u1EA1i ch\u1EC9 b\xE1o",
    manager: "Gi\xE1m \u0111\u1ED1c",
    parent: "Nhi\u1EC7m v\u1EE5 cha m\u1EB9",
    title: "Ti\xEAu \u0111\u1EC1",
    participants: "Ng\u01B0\u1EDDi tham gia",
    todo_label: "Ho\xE0n th\xE0nh / s\xE1ng t\u1EA1o",
    due_date: "Th\u1EDDi h\u1EA1n"
  },
  delete: {
    description: "B\u1EA1n c\xF3 ch\u1EAFc l\xE0 b\u1EA1n mu\u1ED1n x\xF3a nhi\u1EC7m v\u1EE5 n\xE0y? \n\nTo-do n\xE0y kh\xF4ng th\u1EC3 \u0111\u01B0\u1EE3c ho\xE0n t\xE1c.",
    error: {
      message: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong qu\xE1 tr\xECnh x\xF3a nhi\u1EC7m v\u1EE5.",
      title: "X\xF3a nhi\u1EC7m v\u1EE5 kh\xF4ng th\xE0nh c\xF4ng"
    },
    share_description: "Khi b\u1ECB x\xF3a, nhi\u1EC7m v\u1EE5 s\u1EBD b\u1ECB x\xF3a kh\u1ECFi c\xE1c \u0111\u1ED9i chia s\u1EBB nhi\u1EC7m v\u1EE5 n\xE0y.",
    success: {
      message: "Ho\xE0n th\xE0nh vi\u1EC7c x\xF3a nhi\u1EC7m v\u1EE5."
    },
    title: "X\xF3a nhi\u1EC7m v\u1EE5"
  },
  related: {
    hide: "\u1EA8n c\xE1c nhi\u1EC7m v\u1EE5 li\xEAn quan",
    show: "Hi\u1EC3n th\u1ECB c\xE1c nhi\u1EC7m v\u1EE5 li\xEAn quan",
    placeholder: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5 li\xEAn quan",
    title: "Nhi\u1EC7m v\u1EE5 li\xEAn quan"
  },
  subtask: {
    create: "T\u1EA1o m\u1ED9t nhi\u1EC7m v\u1EE5 ph\u1EE5",
    title: "Nhi\u1EC7m v\u1EE5 ph\u1EE5"
  },
  title: "Nhi\u1EC7m v\u1EE5",
  create: {
    grouping: "Th\xEAm m\u1ED9t nhi\u1EC7m v\u1EE5 m\u1EDBi cho nh\xF3m n\xE0y",
    placeholder: "Th\xEAm m\u1ED9t t\xE1c v\u1EE5 m\u1EDBi",
    placeholder_label: "Th\xEAm m\u1ED9t t\xE1c v\u1EE5 m\u1EDBi v\xE0o {{nh\xE3n}}"
  },
  placeholder: {
    manager: "Kh\xF4ng c\xF3 ng\u01B0\u1EDDi qu\u1EA3n l\xFD"
  },
  hierarchy: {
    show: "Hi\u1EC3n th\u1ECB ph\xE2n c\u1EA5p",
    title: "Ph\xE2n c\u1EA5p nhi\u1EC7m v\u1EE5"
  },
  participate: {
    cancel: {
      button: "H\u1EE7y tham gia",
      description1: "B\u1EA1n c\xF3 m\u1ED9t s\u1ED1 To-do k\u1EBFt n\u1ED1i v\u1EDBi nhi\u1EC7m v\u1EE5 n\xE0y.",
      description2: "N\u1EBFu b\u1EA1n h\u1EE7y tham gia, c\xE1c To-do k\u1EBFt n\u1ED1i s\u1EBD b\u1ECB t\xE1ch ra kh\u1ECFi nhi\u1EC7m v\u1EE5. B\u1EA1n c\xF3 mu\u1ED1n ti\u1EBFp t\u1EE5c kh\xF4ng?",
      description3: "B\u1EA1n c\xF3 mu\u1ED1n h\u1EE7y tham gia v\xE0o nhi\u1EC7m v\u1EE5 n\xE0y kh\xF4ng?",
      title: "H\u1EE7y tham gia"
    },
    button: "Tham gia"
  },
  hide: {
    description: "B\u1EA1n c\xF3 mu\u1ED1n \u1EA9n nhi\u1EC7m v\u1EE5 n\xE0y trong h\u1ED9p th\u01B0 \u0111\u1EBFn kh\xF4ng?\nC\xE1c To-do theo nhi\u1EC7m v\u1EE5 n\xE0y c\u0169ng s\u1EBD \u0111\u01B0\u1EE3c \u1EA9n.\nB\u1EA1n c\xF3 th\u1EC3 kh\xF4i ph\u1EE5c c\xE1c nhi\u1EC7m v\u1EE5 v\xE0 To-do \u0111\xE3 \u1EA9n b\u1EA5t c\u1EE9 l\xFAc n\xE0o.",
    error: {
      message: "Nhi\u1EC7m v\u1EE5 v\xE0 c\xE1c To-do li\xEAn quan \u0111\xE3 \u0111\u01B0\u1EE3c \u1EA9n th\xE0nh c\xF4ng.",
      title: "Nhi\u1EC7m v\u1EE5 \u1EA9n n\u1EA5p kh\xF4ng th\xE0nh c\xF4ng"
    },
    processing: {
      title: "\u1EA8n nhi\u1EC7m v\u1EE5"
    },
    success: {
      message: "Nhi\u1EC7m v\u1EE5 v\xE0 c\xE1c To-do li\xEAn quan c\u1EE7a n\xF3 \u0111\xE3 \u0111\u01B0\u1EE3c \u1EA9n th\xE0nh c\xF4ng.",
      title: "Nhi\u1EC7m v\u1EE5 \u1EA9n th\xE0nh c\xF4ng"
    },
    title: "Nhi\u1EC7m v\u1EE5"
  },
  share: {
    error: {
      message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong khi thay \u0111\u1ED5i c\xE0i \u0111\u1EB7t chia s\u1EBB t\xE1c v\u1EE5.",
      title: "Kh\xF4ng thay \u0111\u1ED5i c\xE0i \u0111\u1EB7t chia s\u1EBB t\xE1c v\u1EE5"
    },
    subtask: {
      title: "Chia s\u1EBB c\xE1c nhi\u1EC7m v\u1EE5 ph\u1EE5",
      message: "B\u1EA1n c\xF3 mu\u1ED1n chia s\u1EBB c\xE1c nhi\u1EC7m v\u1EE5 ph\u1EE5 kh\xF4ng?",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong khi thay \u0111\u1ED5i c\xE0i \u0111\u1EB7t chia s\u1EBB c\u1EE7a c\xE1c nhi\u1EC7m v\u1EE5 ph\u1EE5."
    },
    success: {
      message_share: "Nhi\u1EC7m v\u1EE5 \u0111\u01B0\u1EE3c chia s\u1EBB th\xE0nh c\xF4ng v\u1EDBi nh\xF3m {{TeamName}}.",
      message_unshare: "Nhi\u1EC7m v\u1EE5 kh\xF4ng \u0111\u01B0\u1EE3c chia s\u1EBB th\xE0nh c\xF4ng t\u1EEB nh\xF3m {{TeamName}}.",
      title: "C\xE0i \u0111\u1EB7t chia s\u1EBB nhi\u1EC7m v\u1EE5 \u0111\xE3 thay \u0111\u1ED5i"
    }
  }
};

// src/locales/vi-VN/project.json
var project_default3 = {
  placeholder: "Kh\xF4ng c\xF3 d\u1EF1 \xE1n",
  title: "D\u1EF1 \xE1n",
  share: {
    error: {
      message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi thay \u0111\u1ED5i c\xE0i \u0111\u1EB7t chia s\u1EBB d\u1EF1 \xE1n.",
      title: "Kh\xF4ng th\u1EC3 thay \u0111\u1ED5i c\xE0i \u0111\u1EB7t chia s\u1EBB d\u1EF1 \xE1n"
    },
    message: "B\u1EA1n c\xF3 mu\u1ED1n chia s\u1EBB d\u1EF1 \xE1n n\xE0y kh\xF4ng?",
    title: "Chia s\u1EBB d\u1EF1 \xE1n",
    success: {
      message_share: "D\u1EF1 \xE1n \u0111\u01B0\u1EE3c chia s\u1EBB th\xE0nh c\xF4ng v\u1EDBi nh\xF3m {{TeamName}}.",
      message_unshare: "D\u1EF1 \xE1n kh\xF4ng \u0111\u01B0\u1EE3c chia s\u1EBB th\xE0nh c\xF4ng t\u1EEB nh\xF3m {{TeamName}}.",
      title: "C\xE0i \u0111\u1EB7t chia s\u1EBB d\u1EF1 \xE1n \u0111\xE3 thay \u0111\u1ED5i"
    }
  },
  detail: {
    tabs: {
      overview: "T\u1ED5ng quan d\u1EF1 \xE1n",
      tasks: "Nhi\u1EC7m v\u1EE5 li\xEAn quan"
    },
    delete: {
      button: "X\xF3a d\u1EF1 \xE1n",
      title: "X\xF3a d\u1EF1 \xE1n",
      confirmation: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a d\u1EF1 \xE1n n\xE0y kh\xF4ng? H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c.",
      cancel: "H\u1EE7y",
      confirm: "X\xF3a",
      success: "D\u1EF1 \xE1n \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a d\u1EF1 \xE1n."
    },
    info: {
      title: "Th\xF4ng tin d\u1EF1 \xE1n",
      team: "\u0110\u1ED9i tham gia",
      participants: "Ng\u01B0\u1EDDi tham gia",
      add_participants: "Ng\u01B0\u1EDDi tham gia th\xEAm",
      cancel_participate: "H\u1EE7y b\u1ECF s\u1EF1 tham gia",
      participate: "S\u1EF1 tham gia",
      cancel_participate_message: "B\u1EA1n c\xF3 m\u1ED9t s\u1ED1 To-do k\u1EBFt n\u1ED1i v\u1EDBi c\xE1c nhi\u1EC7m v\u1EE5 trong d\u1EF1 \xE1n n\xE0y.\nN\u1EBFu b\u1EA1n h\u1EE7y tham gia d\u1EF1 \xE1n, s\u1EF1 tham gia c\u1EE7a b\u1EA1n v\xE0o c\xE1c nhi\u1EC7m v\u1EE5 \u0111\xF3 c\u0169ng s\u1EBD b\u1ECB h\u1EE7y, v\xE0 c\xE1c To-do k\u1EBFt n\u1ED1i s\u1EBD b\u1ECB t\xE1ch ra kh\u1ECFi d\u1EF1 \xE1n v\xE0 nhi\u1EC7m v\u1EE5.\nB\u1EA1n c\xF3 mu\u1ED1n ti\u1EBFp t\u1EE5c kh\xF4ng?"
    },
    description: {
      placeholder: "Th\xEAm m\xF4 t\u1EA3..."
    },
    name: {
      edit_error: "L\u1ED7i ch\u1EC9nh s\u1EEDa t\xEAn d\u1EF1 \xE1n"
    },
    title: "Chi ti\u1EBFt d\u1EF1 \xE1n"
  },
  table: {
    add: "Th\xEAm d\u1EF1 \xE1n m\u1EDBi",
    columns: {
      status: "Tr\u1EA1ng th\xE1i",
      name: "T\xEAn d\u1EF1 \xE1n",
      deadline: "Th\u1EDDi h\u1EA1n",
      priority: "\u0110\u1ED9 \u01B0u ti\xEAn",
      leader: "Qu\u1EA3n l\xFD d\u1EF1 \xE1n",
      register_date: "Ng\xE0y \u0111\u0103ng k\xFD"
    }
  },
  relatedTasks: {
    title: "Nhi\u1EC7m v\u1EE5 li\xEAn quan"
  },
  filters: {
    status: "Tr\u1EA1ng th\xE1i",
    priority: "\u0110\u1ED9 \u01B0u ti\xEAn",
    leader: "Qu\u1EA3n l\xFD d\u1EF1 \xE1n",
    noName: "Kh\xF4ng c\xF3 t\xEAn"
  },
  error: {
    no_access: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp v\xE0o d\u1EF1 \xE1n n\xE0y.",
    not_found: "Kh\xF4ng t\xECm th\u1EA5y d\u1EF1 \xE1n.",
    load_failed: "Kh\xF4ng th\u1EC3 t\u1EA3i d\u1EF1 \xE1n."
  },
  list: {
    select_team: "Vui l\xF2ng ch\u1ECDn m\u1ED9t \u0111\u1ED9i."
  },
  create_modal: {
    title: "T\u1EA1o d\u1EF1 \xE1n",
    name: {
      label: "T\xEAn d\u1EF1 \xE1n",
      placeholder: "Nh\u1EADp t\xEAn d\u1EF1 \xE1n"
    },
    team: {
      label: "Ch\u1ECDn nh\xF3m ph\u1EE5 tr\xE1ch",
      placeholder: "Ch\u1ECDn nh\xF3m ph\u1EE5 tr\xE1ch",
      self: " (nh\xF3m ph\u1EE5 tr\xE1ch)"
    },
    participants: {
      label: "Ch\u1ECDn th\xE0nh vi\xEAn",
      placeholder: "Ch\u1ECDn th\xE0nh vi\xEAn",
      self: " (T\xF4i)"
    },
    notification: {
      in_progress: "\u0110ang t\u1EA1o d\u1EF1 \xE1n",
      success: {
        title: "D\u1EF1 \xE1n \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng",
        message: "D\u1EF1 \xE1n \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng."
      },
      error: {
        title: "Kh\xF4ng th\u1EC3 t\u1EA1o d\u1EF1 \xE1n",
        message: "Kh\xF4ng th\u1EC3 t\u1EA1o d\u1EF1 \xE1n. Vui l\xF2ng th\u1EED l\u1EA1i."
      }
    },
    form: {
      validation: {
        name: {
          required: "T\xEAn d\u1EF1 \xE1n l\xE0 b\u1EAFt bu\u1ED9c."
        },
        team: {
          required: "Vui l\xF2ng ch\u1ECDn m\u1ED9t nh\xF3m."
        }
      }
    }
  }
};

// src/locales/vi-VN/team.json
var team_default3 = {
  placeholder: "Kh\xF4ng c\xF3 \u0111\u1ED9i",
  notification: {
    message: "\xCDt nh\u1EA5t m\u1ED9t \u0111\u1ED9i ph\u1EA3i \u0111\u01B0\u1EE3c ch\u1ECDn.",
    title: "L\u1EF1a ch\u1ECDn \u0111\u1ED9i c\u1EA7n thi\u1EBFt"
  },
  selection: {
    some_one: "{{count}} nh\xF3m",
    some_other: "{{count}} c\xE1c nh\xF3m"
  },
  title: "\u0110\u1ED9i",
  team_required: "Vui l\xF2ng ch\u1ECDn nh\xF3m.",
  member: {
    title: "Th\xE0nh vi\xEAn",
    add_dialog: {
      title: "M\u1EDDi th\xE0nh vi\xEAn",
      search_placeholder: "T\xECm ki\u1EBFm th\xE0nh vi\xEAn ho\u1EB7c nh\u1EADp \u0111\u1ECBa ch\u1EC9 email.",
      workspace_members: "Th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
      invite_button: "M\u1EDDi",
      invite_selected: "M\u1EDDi th\xE0nh vi\xEAn \u0111\xE3 ch\u1ECDn",
      no_members: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c \u0111\u1EC3 m\u1EDDi.",
      invite_by_email: "M\u1EDDi th\xE0nh vi\xEAn m\u1EDBi qua email."
    },
    notifications: {
      invalid_email: "Email kh\xF4ng h\u1EE3p l\u1EC7",
      enter_valid_email: "Vui l\xF2ng nh\u1EADp \u0111\u1ECBa ch\u1EC9 email h\u1EE3p l\u1EC7.",
      invite_not_possible: "Kh\xF4ng th\u1EC3 m\u1EDDi",
      already_member: "{{email}} \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\u0103ng k\xFD l\xE0 th\xE0nh vi\xEAn.",
      creation_failed: "T\u1EA1o l\u1EDDi m\u1EDDi th\u1EA5t b\u1EA1i.",
      invite_failed: "L\u1EDDi m\u1EDDi th\u1EA5t b\u1EA1i",
      email_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi email.",
      invite_success: "M\u1EDDi th\xE0nh c\xF4ng",
      email_sent: "\u0110\xE3 g\u1EEDi email m\u1EDDi \u0111\u1EBFn {{email}}.",
      invite_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi m\u1EDDi th\xE0nh vi\xEAn.",
      selection_needed: "C\u1EA7n ch\u1ECDn th\xE0nh vi\xEAn",
      select_members: "Vui l\xF2ng ch\u1ECDn th\xE0nh vi\xEAn \u0111\u1EC3 th\xEAm v\xE0o \u0111\u1ED9i.",
      add_success: "Th\xEAm th\xE0nh vi\xEAn th\xE0nh c\xF4ng",
      members_added: "{{count}} th\xE0nh vi\xEAn \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0o \u0111\u1ED9i.",
      add_failed: "Th\xEAm th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i",
      add_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi th\xEAm th\xE0nh vi\xEAn \u0111\u1ED9i.",
      request_plan: "Y\xEAu c\u1EA7u c\u1EADp nh\u1EADt Planner",
      request_plan_success: "\u0110\xE3 g\u1EEDi y\xEAu c\u1EA7u c\u1EADp nh\u1EADt Planner \u0111\u1EBFn th\xE0nh vi\xEAn.",
      request_plan_failed: "Kh\xF4ng th\u1EC3 g\u1EEDi y\xEAu c\u1EA7u c\u1EADp nh\u1EADt Planner.",
      request_plan_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi y\xEAu c\u1EA7u c\u1EADp nh\u1EADt Planner."
    },
    placeholders: {
      team: "\u0110\u1ED9i",
      workspace: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
      inviter: "Ng\u01B0\u1EDDi m\u1EDDi"
    },
    day_overview: {
      current_todo: "To-do hi\u1EC7n t\u1EA1i",
      no_tasks_on_date: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5 n\xE0o cho ng\xE0y \u0111\xE3 ch\u1ECDn."
    },
    task_list: {
      member_tasks: "Nhi\u1EC7m v\u1EE5 c\u1EE7a {{name}}",
      managing_tasks: "Nhi\u1EC7m v\u1EE5 qu\u1EA3n l\xFD",
      participating_tasks: "Nhi\u1EC7m v\u1EE5 tham gia"
    },
    task_view_settings: {
      view: "Xem",
      view_settings: "C\xE0i \u0111\u1EB7t hi\u1EC3n th\u1ECB",
      completed_tasks: "Nhi\u1EC7m v\u1EE5 \u0111\xE3 ho\xE0n th\xE0nh",
      show_backlog_tasks: "Hi\u1EC3n th\u1ECB nhi\u1EC7m v\u1EE5 backlog",
      show_paused_tasks: "Hi\u1EC3n th\u1ECB nhi\u1EC7m v\u1EE5 t\u1EA1m d\u1EEBng",
      display_properties: "Thu\u1ED9c t\xEDnh hi\u1EC3n th\u1ECB",
      reset_settings: "\u0110\u1EB7t l\u1EA1i c\xE0i \u0111\u1EB7t",
      column: {
        status: "Tr\u1EA1ng th\xE1i",
        priority: "\u01AF u ti\xEAn",
        project: "D\u1EF1 \xE1n",
        current_value: "Ch\u1EC9 s\u1ED1"
      }
    },
    todo_list: {
      member_todos: "To-do c\u1EE7a {{name}}",
      remaining_todos: "To-do c\xF2n l\u1EA1i",
      recently_completed_todos: "To-do \u0111\xE3 ho\xE0n th\xE0nh g\u1EA7n \u0111\xE2y",
      tasks_without_task: "To-do kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5",
      tasks_by_task: "To-do \u0111\u01B0\u1EE3c nh\xF3m theo nhi\u1EC7m v\u1EE5",
      load_more: "T\u1EA3i th\xEAm",
      no_schedule: "Kh\xF4ng c\xF3 l\u1ECBch tr\xECnh"
    },
    pages: {
      detail: {
        team_member: "Th\xE0nh vi\xEAn nh\xF3m",
        non_existent_team: "Nh\xF3m kh\xF4ng t\u1ED3n t\u1EA1i.",
        non_existent_member: "Th\xE0nh vi\xEAn kh\xF4ng t\u1ED3n t\u1EA1i."
      },
      team: {
        team_members: "Th\xE0nh vi\xEAn nh\xF3m",
        member_count: "{{count}} th\xE0nh vi\xEAn",
        no_todos_on_date: "Kh\xF4ng c\xF3 To-do n\xE0o cho ng\xE0y \u0111\xE3 ch\u1ECDn.",
        todo_count: "{{count}} To-do",
        request_plan: "Y\xEAu c\u1EA7u c\u1EADp nh\u1EADt Planner",
        request_plan_message: "<0>{{name}}</0><1> b\u1EA1n c\xF3 mu\u1ED1n g\u1EEDi th\xF4ng b\xE1o y\xEAu c\u1EA7u t\u1EA1o planner kh\xF4ng?</1>",
        daily_scrum: "Daily Scrum"
      }
    }
  },
  create_dialog: {
    title: "T\u1EA1o nh\xF3m m\u1EDBi",
    workspace: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
    select_workspace: "Ch\u1ECDn kh\xF4ng gian l\xE0m vi\u1EC7c.",
    workspace_required: "Vui l\xF2ng ch\u1ECDn kh\xF4ng gian l\xE0m vi\u1EC7c.",
    parent_team: "\u0110\u1ED9i ph\u1EE5 huynh",
    select_parent_team: "Ch\u1ECDn \u0111\u1ED9i cha.",
    team_name: "T\xEAn nh\xF3m",
    enter_team_name: "Nh\u1EADp t\xEAn nh\xF3m.",
    name_error: "T\xEAn nh\xF3m ph\u1EA3i c\xF3 \xEDt nh\u1EA5t 2 k\xFD t\u1EF1.",
    team_description: "M\xF4 t\u1EA3 nh\xF3m",
    enter_team_description: "Nh\u1EADp m\xF4 t\u1EA3 v\u1EC1 nh\xF3m.",
    cancel: "H\u1EE7y",
    create_team: "T\u1EA1o nh\xF3m",
    success_title: "\u0110\xE3 t\u1EA1o nh\xF3m",
    success_message: "Nh\xF3m {{name}} \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
    error_title: "T\u1EA1o nh\xF3m th\u1EA5t b\u1EA1i",
    error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o nh\xF3m. Vui l\xF2ng th\u1EED l\u1EA1i.\n{{error}}"
  },
  update_dialog: {
    title: "C\u1EADp nh\u1EADt \u0111\u1ED9i ng\u0169",
    save: "L\u01B0u",
    success_title: "\u0110\xE3 c\u1EADp nh\u1EADt nh\xF3m",
    success_message: "Nh\xF3m {{name}} \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt th\xE0nh c\xF4ng.",
    error_title: "C\u1EADp nh\u1EADt nh\xF3m th\u1EA5t b\u1EA1i",
    error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi c\u1EADp nh\u1EADt nh\xF3m. Vui l\xF2ng th\u1EED l\u1EA1i.\n{{error}}"
  },
  delete_dialog: {
    title: "X\xE1c nh\u1EADn x\xF3a nh\xF3m",
    message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a nh\xF3m n\xE0y kh\xF4ng?",
    warning: "H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c v\xE0 s\u1EBD x\xF3a v\u0129nh vi\u1EC5n t\u1EA5t c\u1EA3 c\xE1c d\u1EF1 \xE1n v\xE0 nhi\u1EC7m v\u1EE5 trong nh\xF3m n\xE0y.",
    hierarchy_guide: "C\xE1c nh\xF3m con s\u1EBD kh\xF4ng b\u1ECB x\xF3a v\xE0 s\u1EBD \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng li\xEAn k\u1EBFt v\u1EDBi nh\xF3m cha.",
    confirm: "X\xF3a",
    cancel: "H\u1EE7y",
    error: "X\xF3a nh\xF3m th\u1EA5t b\u1EA1i:",
    success: "\u0110\xE3 x\xF3a nh\xF3m",
    success_message: "Nh\xF3m \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
    failed: "X\xF3a nh\xF3m th\u1EA5t b\u1EA1i",
    failed_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a nh\xF3m. Vui l\xF2ng th\u1EED l\u1EA1i."
  },
  main_title: {
    clear_input: "X\xF3a n\u1ED9i dung"
  },
  item: {
    task: "Nhi\u1EC7m v\u1EE5",
    project: "D\u1EF1 \xE1n",
    member: "Th\xE0nh vi\xEAn"
  },
  member_table: {
    cancel_invitation: "H\u1EE7y l\u1EDDi m\u1EDDi",
    invited_status: "\u0110\xE3 m\u1EDDi",
    delete_member_failed: "X\xF3a th\xE0nh vi\xEAn nh\xF3m th\u1EA5t b\u1EA1i:",
    role_change_failed: "Thay \u0111\u1ED5i vai tr\xF2 th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i:",
    add_member: "Th\xEAm th\xE0nh vi\xEAn",
    leave_team: "R\u1EDDi kh\u1ECFi nh\xF3m",
    remove_from_team: "X\xF3a kh\u1ECFi nh\xF3m",
    manage_members: "Qu\u1EA3n l\xFD th\xE0nh vi\xEAn",
    invite_member: "M\u1EDDi th\xE0nh vi\xEAn",
    member: "Th\xE0nh vi\xEAn",
    email: "Email",
    workspace_permission: "Quy\u1EC1n kh\xF4ng gian l\xE0m vi\u1EC7c",
    no_available_members: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn n\xE0o \u0111\u1EC3 hi\u1EC3n th\u1ECB",
    add_member_count: "Th\xEAm th\xE0nh vi\xEAn ({{count}})",
    current_member_count: "Th\xE0nh vi\xEAn hi\u1EC7n t\u1EA1i ({{count}})",
    delete_member_count: "X\xF3a kh\u1ECFi nh\xF3m ({{count}})",
    member_list: "Th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
    placeholder: "Nh\u1EADp t\xEAn ho\u1EB7c \u0111\u1ECBa ch\u1EC9 email c\u1EE7a ng\u01B0\u1EDDi d\xF9ng."
  },
  setting_page: {
    access_denied: "Ch\u1EC9 th\xE0nh vi\xEAn nh\xF3m m\u1EDBi c\xF3 th\u1EC3 truy c\u1EADp trang n\xE0y.",
    delete_team: "X\xF3a nh\xF3m",
    delete_description: "X\xF3a nh\xF3m kh\xF4ng lo\u1EA1i b\u1ECF c\xE1c To-do v\xE0 h\u1ED3 s\u01A1 c\xE1 nh\xE2n do th\xE0nh vi\xEAn t\u1EA1o ra.",
    delete_tooltip: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n x\xF3a kh\xF4ng? \u{1F97A}"
  }
};

// src/locales/vi-VN/todo.json
var todo_default3 = {
  title: "To-do",
  inbox: "Inbox",
  now: {
    placeholder: "Kh\xF4ng To-do NGAY B\xC2Y GI\u1EDC"
  },
  private_todo_title: "To-do ri\xEAng t\u01B0",
  status: {
    cancelled: "D\u1EEBng l\u1EA1i",
    completed: "Ho\xE0n th\xE0nh",
    in_progress: "Trong ti\u1EBFn tr\xECnh"
  },
  notification: {
    cancel_action: "D\u1EEBng l\u1EA1i",
    resume_action: "Ti\u1EBFp t\u1EE5c",
    processing_title: "\u0110ang x\u1EED l\xFD {{action}} To-do",
    processing_message: "Vui l\xF2ng \u0111\u1EE3i...",
    success_title: "{{action}} To-do ho\xE0n t\u1EA5t",
    success_message: "To-do \u0111\xE3 \u0111\u01B0\u1EE3c {{action}}.",
    error_title: "{{action}} To-do th\u1EA5t b\u1EA1i",
    error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi {{action}} To-do"
  },
  component: {
    inbox: {
      hidden: "\u1EA8n",
      hidden_goals: "Nhi\u1EC7m v\u1EE5 \u1EA9n",
      hidden_goals_count: "Nhi\u1EC7m v\u1EE5 \u1EA9n ({{count}})",
      view: "Xem"
    },
    todo_textfield: {
      adding: "\u0110ang th\xEAm...",
      add_todo: "Th\xEAm To-do"
    },
    parent_goal_label: {
      hide: "\u1EA8n",
      due_date: "H\u1EA1n ch\xF3t",
      add_todo: "Th\xEAm To-do"
    },
    set_todo_display_menu: {
      view_settings: "C\xE0i \u0111\u1EB7t xem",
      show_goals: "Hi\u1EC3n th\u1ECB nhi\u1EC7m v\u1EE5",
      show_empty_goals: "Hi\u1EC3n th\u1ECB nhi\u1EC7m v\u1EE5 tr\u1ED1ng",
      reset_settings: "\u0110\u1EB7t l\u1EA1i c\xE0i \u0111\u1EB7t",
      set_as_default: "\u0110\u1EB7t l\xE0m m\u1EB7c \u0111\u1ECBnh",
      notifications: {
        saving_title: "\u0110ang l\u01B0u c\xE0i \u0111\u1EB7t",
        saved_title: "\u0110\xE3 l\u01B0u c\xE0i \u0111\u1EB7t",
        saved_message: "C\xE0i \u0111\u1EB7t xem \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u.",
        save_failed_title: "L\u01B0u c\xE0i \u0111\u1EB7t th\u1EA5t b\u1EA1i",
        save_failed_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u c\xE0i \u0111\u1EB7t xem."
      }
    },
    todo_action_menu: {
      resume: "Ti\u1EBFp t\u1EE5c",
      delete_todo: "X\xF3a To-do",
      notifications: {
        deleting_todo_title: "\u0110ang x\xF3a To-do",
        deleted_todo_title: "\u0110\xE3 x\xF3a To-do",
        deleted_todo_message: "To-do \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
        delete_todo_failed_title: "X\xF3a To-do th\u1EA5t b\u1EA1i",
        delete_todo_failed_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a To-do.",
        remove_plan_failed_message: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong khi h\u1EE7y b\u1ECF k\u1EBF ho\u1EA1ch.",
        remove_plan_failed_title: "Kh\xF4ng th\u1EC3 h\u1EE7y b\u1ECF k\u1EBF ho\u1EA1ch",
        removed_plan_message: "\u0110\xE3 h\u1EE7y b\u1ECF th\xE0nh c\xF4ng k\u1EBF ho\u1EA1ch.",
        removed_plan_title: "K\u1EBF ho\u1EA1ch h\u1EE7y b\u1ECF",
        removing_plan_title: "H\u1EE7y b\u1ECF k\u1EBF ho\u1EA1ch"
      },
      remove_plan: "H\u1EE7y b\u1ECF k\u1EBF ho\u1EA1ch"
    },
    todo_checkbox: {
      complete: "Ho\xE0n th\xE0nh"
    },
    activity: "Ho\u1EA1t \u0111\u1ED9ng",
    cancel_checkbox: {
      resume: "Ti\u1EBFp t\u1EE5c",
      cancel: "D\u1EEBng l\u1EA1i",
      aria_resume: "Ti\u1EBFp t\u1EE5c To-do",
      aria_cancel: "D\u1EEBng To-do"
    },
    confirm_modal: {
      actions: {
        cancel: "D\u1EEBng l\u1EA1i",
        remove_planner: "H\u1EE7y k\u1EBF ho\u1EA1ch",
        delete: "X\xF3a t\u1EA5t c\u1EA3"
      },
      details: {
        cancel: "Ch\u1ECDn t\xF9y ch\u1ECDn n\xE0y \u0111\u1EC3 d\u1EEBng l\xE0m To-do n\xE0y.\nN\xF3 s\u1EBD kh\xF4ng xu\u1EA5t hi\u1EC7n trong l\u1ECBch c\u1EE7a b\u1EA1n sau ng\xE0y \u0111\xE3 ch\u1ECDn.",
        remove_planner: "T\xF9y ch\u1ECDn n\xE0y ch\u1EC9 h\u1EE7y k\u1EBF ho\u1EA1ch cho ng\xE0y \u0111\xE3 ch\u1ECDn, gi\u1EEF k\u1EBF ho\u1EA1ch cho c\xE1c ng\xE0y kh\xE1c.\nN\u1EBFu h\xF4m nay l\xE0 ng\xE0y duy nh\u1EA5t, n\xF3 s\u1EBD v\u1EABn c\xF2n trong h\u1ED9p th\u01B0 \u0111\u1EBFn.",
        delete: "T\xF9y ch\u1ECDn n\xE0y s\u1EBD x\xF3a k\u1EBF ho\u1EA1ch cho t\u1EA5t c\u1EA3 c\xE1c ng\xE0y."
      },
      descriptions: {
        cancel_1: "B\u1EA1n c\xF3 mu\u1ED1n ng\u1EEBng l\xE0m vi\u1EC7c n\xE0y kh\xF4ng?",
        cancel_2: "<0>N\xF3 s\u1EBD ch\u1EC9 xu\u1EA5t hi\u1EC7n trong tr\xECnh l\u1EADp k\u1EBF ho\u1EA1ch c\u1EE7a b\u1EA1n cho \u0111\u1EBFn</0><1>{{date}}</1><0>.</0>",
        delete_1: "\u0110i\u1EC1u n\xE0y s\u1EBD ho\xE0n to\xE0n x\xF3a b\u1ECF To-do v\xE0 t\u1EA5t c\u1EA3 c\xE1c k\u1EBF ho\u1EA1ch theo l\u1ECBch tr\xECnh c\u1EE7a n\xF3 t\u1EEB t\u1EA5t c\u1EA3 c\xE1c ng\xE0y.",
        delete_2: "<0>Ch\u1EC9 \u0111\u1EC3 h\u1EE7y k\u1EBF ho\u1EA1ch cho ng\xE0y\xA0</0><1>{{date}}</1><0>\xA0m\xE0 b\u1EA1n \u0111\xE3 ch\u1ECDn, h\xE3y ch\u1ECDn 'H\u1EE7y k\u1EBF ho\u1EA1ch'.</0>",
        remove_planner_1: "<0>\u0110i\u1EC1u n\xE0y s\u1EBD ch\u1EC9 h\u1EE7y b\u1ECF k\u1EBF ho\u1EA1ch To-do cho\xA0</0><1>{{date}}</1>.<0>C\xE1c k\u1EBF ho\u1EA1ch cho c\xE1c ng\xE0y kh\xE1c s\u1EBD v\u1EABn c\xF2n nguy\xEAn.</0>",
        remove_planner_2: "\u0110\u1EC3 x\xF3a ho\xE0n to\xE0n, s\u1EED d\u1EE5ng t\xF9y ch\u1ECDn 'X\xF3a t\u1EA5t c\u1EA3'."
      },
      other: {
        info_tooltip: "V\u1EC1 c\xE1c t\xF9y ch\u1ECDn x\xF3a To-do c\u1EE7a Orblit",
        current_option: "L\u1EF1a ch\u1ECDn hi\u1EC7n t\u1EA1i"
      }
    },
    todoDrawer: {
      not_found_modal: {
        title: "To-do \u0111\xE3 b\u1ECB x\xF3a",
        message: "To-do n\xE0y \u0111\xE3 b\u1ECB x\xF3a.",
        message_with_title: "{{title}} \u0111\xE3 b\u1ECB x\xF3a.",
        confirm: "\u0110\u1ED3ng \xFD"
      },
      popover: {
        notes: "Ghi ch\xFA",
        comments: "B\xECnh lu\u1EADn ({{count}})",
        view_detail: "Xem chi ti\u1EBFt To-do"
      },
      detail: {
        add_date: "Th\xEAm ng\xE0y"
      },
      drawer: {
        toggle_visibility: "Chuy\u1EC3n sang {{status}}",
        private: "ri\xEAng t\u01B0",
        public: "c\xF4ng khai"
      },
      planner_list: {
        set_time: "Ch\u1ECDn th\u1EDDi gian",
        delete_time: "X\xF3a th\u1EDDi gian"
      },
      content: {
        related_goal: "Nhi\u1EC7m v\u1EE5 li\xEAn quan",
        date_and_time: "Ng\xE0y v\xE0 gi\u1EDD",
        todo_title_placeholder: "Nh\u1EADp ti\xEAu \u0111\u1EC1 To-do"
      },
      category_menu: {
        change_category: "Thay \u0111\u1ED5i danh m\u1EE5c",
        loading: "\u0110ang t\u1EA3i..."
      },
      due_date_badge: {
        set_due_date: "C\xE0i \u0111\u1EB7t h\u1EA1n ch\xF3t"
      },
      goal_selector: {
        connect_goal: "K\u1EBFt n\u1ED1i nhi\u1EC7m v\u1EE5",
        view_goal_details: "Xem chi ti\u1EBFt nhi\u1EC7m v\u1EE5",
        please_select_goal: "Vui l\xF2ng ch\u1ECDn m\u1ED9t nhi\u1EC7m v\u1EE5",
        no_goals: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5 n\xE0o",
        loading_goals: "\u0110ang t\u1EA3i nhi\u1EC7m v\u1EE5...",
        go_back: "Quay l\u1EA1i",
        view_all_goals: "Xem t\u1EA5t c\u1EA3 nhi\u1EC7m v\u1EE5",
        actions: {
          connect: "k\u1EBFt n\u1ED1i",
          disconnect: "ng\u1EAFt k\u1EBFt n\u1ED1i"
        },
        notifications: {
          processing_title: "\u0110ang x\u1EED l\xFD {{action}} nhi\u1EC7m v\u1EE5",
          success_title: "{{action}} nhi\u1EC7m v\u1EE5 th\xE0nh c\xF4ng",
          success_message: "\u0110\xE3 {{action}} v\u1EDBi nhi\u1EC7m v\u1EE5 \u0111\xE3 ch\u1ECDn.",
          error_title: "{{action}} nhi\u1EC7m v\u1EE5 th\u1EA5t b\u1EA1i",
          error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi {{action}} nhi\u1EC7m v\u1EE5"
        }
      }
    }
  }
};

// src/locales/vi-VN/report.json
var report_default3 = {
  type: {
    monthly: "B\xE1o c\xE1o h\xE0ng th\xE1ng",
    quarterly: "B\xE1o c\xE1o h\xE0ng qu\xFD",
    weekly: "B\xE1o c\xE1o h\xE0ng tu\u1EA7n",
    annual: "B\xE1o c\xE1o th\u01B0\u1EDDng ni\xEAn",
    custom: "B\xE1o c\xE1o t\xF9y ch\u1EC9nh",
    daily: "B\xE1o c\xE1o h\xE0ng ng\xE0y"
  },
  title: "b\xE1o c\xE1o",
  list: {
    title: "Danh s\xE1ch b\xE1o c\xE1o",
    sent: "B\xE1o c\xE1o \u0111\xE3 g\u1EEDi",
    received: "B\xE1o c\xE1o \u0111\xE3 nh\u1EADn",
    noSentReports: "Kh\xF4ng c\xF3 b\xE1o c\xE1o \u0111\xE3 g\u1EEDi.",
    noReceivedReports: "Kh\xF4ng c\xF3 b\xE1o c\xE1o \u0111\xE3 nh\u1EADn.",
    createNew: "T\u1EA1o m\u1ED9t b\xE1o c\xE1o m\u1EDBi.",
    columns: {
      title: "Ti\xEAu \u0111\u1EC1",
      sender: "Ng\u01B0\u1EDDi g\u1EEDi",
      receiver: "Ng\u01B0\u1EDDi nh\u1EADn",
      date: "Ng\xE0y",
      team: "Nh\xF3m"
    },
    unknownUser: "Kh\xF4ng x\xE1c \u0111\u1ECBnh",
    draft: "B\u1EA3n nh\xE1p",
    noTitle: "Kh\xF4ng c\xF3 ti\xEAu \u0111\u1EC1",
    unknown: "Kh\xF4ng x\xE1c \u0111\u1ECBnh",
    markAsRead: "\u0110\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    markAsReadComplete: "\u0110\xE3 \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    markAsReadSuccess: "{{count}} b\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc.",
    noUnreadReports: "Kh\xF4ng c\xF3 b\xE1o c\xE1o ch\u01B0a \u0111\u1ECDc.",
    markAsReadError: "L\u1ED7i khi \u0111\xE1nh d\u1EA5u b\xE1o c\xE1o \u0111\xE3 \u0111\u1ECDc.",
    loading: "\u0110ang t\u1EA3i b\xE1o c\xE1o...",
    error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA3i b\xE1o c\xE1o."
  },
  actions: {
    create: "T\u1EA1o b\xE1o c\xE1o",
    edit: "Ch\u1EC9nh s\u1EEDa b\xE1o c\xE1o",
    delete: "X\xF3a b\xE1o c\xE1o",
    send: "G\u1EEDi b\xE1o c\xE1o",
    save: "L\u01B0u b\xE1o c\xE1o",
    saveDraft: "L\u01B0u nh\u01B0 b\u1EA3n nh\xE1p",
    confirmDelete: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a b\xE1o c\xE1o n\xE0y?",
    cancelGeneration: "H\u1EE7y t\u1EA1o"
  },
  sendReport: {
    button: "G\u1EEDi b\xE1o c\xE1o",
    title: "Ch\u1ECDn ng\u01B0\u1EDDi nh\u1EADn b\xE1o c\xE1o",
    selectAll: "Ch\u1ECDn t\u1EA5t c\u1EA3",
    noName: "Kh\xF4ng c\xF3 t\xEAn",
    loadingMembers: "\u0110ang t\u1EA3i danh s\xE1ch th\xE0nh vi\xEAn...",
    noMembers: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn n\xE0o \u0111\u1EC3 g\u1EEDi.",
    cancel: "H\u1EE7y",
    send: "G\u1EEDi",
    notifications: {
      selectRecipients: "Ch\u1ECDn ng\u01B0\u1EDDi nh\u1EADn",
      selectRecipientsMessage: "Vui l\xF2ng ch\u1ECDn \xEDt nh\u1EA5t m\u1ED9t th\xE0nh vi\xEAn \u0111\u1EC3 g\u1EEDi b\xE1o c\xE1o.",
      sendComplete: "G\u1EEDi b\xE1o c\xE1o th\xE0nh c\xF4ng",
      newRecipients: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn {{count}} ng\u01B0\u1EDDi nh\u1EADn m\u1EDBi.",
      updatedRecipients: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt cho {{count}} ng\u01B0\u1EDDi nh\u1EADn hi\u1EC7n t\u1EA1i.",
      newAndUpdatedRecipients: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn {{count}} ng\u01B0\u1EDDi nh\u1EADn m\u1EDBi v\xE0 c\u1EADp nh\u1EADt cho {{updateCount}} ng\u01B0\u1EDDi nh\u1EADn hi\u1EC7n t\u1EA1i.",
      sendFailed: "G\u1EEDi b\xE1o c\xE1o th\u1EA5t b\u1EA1i",
      sendFailedMessage: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi b\xE1o c\xE1o. Vui l\xF2ng th\u1EED l\u1EA1i."
    }
  },
  notifications: {
    title: "Th\xF4ng b\xE1o",
    success: "Th\xE0nh c\xF4ng",
    error: "L\u1ED7i"
  },
  reportPage: {
    newReport: "B\xE1o c\xE1o m\u1EDBi",
    loading: "\u0110ang t\u1EA3i b\xE1o c\xE1o...",
    loadingData: "\u0110ang t\u1EA3i d\u1EEF li\u1EC7u b\xE1o c\xE1o...",
    error: {
      loading: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA3i b\xE1o c\xE1o.",
      unknown: "\u0110\xE3 x\u1EA3y ra l\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh.",
      notFound: "Kh\xF4ng t\xECm th\u1EA5y b\xE1o c\xE1o.",
      notFoundDescription: "B\xE1o c\xE1o y\xEAu c\u1EA7u \u0111\xE3 b\u1ECB x\xF3a ho\u1EB7c kh\xF4ng t\u1ED3n t\u1EA1i."
    },
    backToList: "Quay l\u1EA1i danh s\xE1ch b\xE1o c\xE1o",
    createNewReport: "T\u1EA1o b\xE1o c\xE1o m\u1EDBi",
    createNewReportDescription: "Bi\u1EC3u m\u1EABu t\u1EA1o b\xE1o c\xE1o m\u1EDBi s\u1EBD \u0111\u01B0\u1EE3c th\xEAm v\xE0o \u0111\xE2y.",
    editing: "\u0110ang ch\u1EC9nh s\u1EEDa...",
    edit: "Ch\u1EC9nh s\u1EEDa",
    editComplete: "Ho\xE0n th\xE0nh ch\u1EC9nh s\u1EEDa",
    exportToPdf: "Xu\u1EA5t ra PDF",
    deleteReport: "X\xF3a b\xE1o c\xE1o",
    createdDate: "Ng\xE0y t\u1EA1o",
    noCreatedDate: "Kh\xF4ng c\xF3 th\xF4ng tin ng\xE0y t\u1EA1o",
    updatedDate: "Ng\xE0y c\u1EADp nh\u1EADt",
    editorArea: "Khu v\u1EF1c ch\u1EC9nh s\u1EEDa",
    emptyState: {
      title: "Vui l\xF2ng ch\u1ECDn m\u1ED9t b\xE1o c\xE1o",
      description: "Ch\u1ECDn m\u1ED9t b\xE1o c\xE1o t\u1EEB danh s\xE1ch b\xEAn tr\xE1i \u0111\u1EC3 xem chi ti\u1EBFt."
    },
    translate: "D\u1ECBch",
    aiTranslation: "D\u1ECBch AI",
    deleteModal: {
      title: "X\xE1c nh\u1EADn x\xF3a b\xE1o c\xE1o",
      confirmMessage: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a b\xE1o c\xE1o n\xE0y kh\xF4ng? B\xE1o c\xE1o \u0111\xE3 x\xF3a s\u1EBD kh\xF4ng hi\u1EC3n th\u1ECB trong danh s\xE1ch c\u1EE7a b\u1EA1n, nh\u01B0ng ng\u01B0\u1EDDi d\xF9ng kh\xE1c v\u1EABn c\xF3 th\u1EC3 xem.",
      successMessage: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
      errorMessage: "Kh\xF4ng th\u1EC3 x\xF3a b\xE1o c\xE1o. Vui l\xF2ng th\u1EED l\u1EA1i.",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a b\xE1o c\xE1o."
    },
    notifications: {
      exportErrorMessage: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra. \nVui l\xF2ng th\u1EED l\u1EA1i sau.",
      exportFailed: "Xu\u1EA5t kh\u1EA9u kh\xF4ng th\xE0nh c\xF4ng",
      reportSaveComplete: "B\xE1o c\xE1o L\u01B0u Ho\xE0n th\xE0nh",
      reportSaveErrorMessage: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong khi l\u01B0u b\xE1o c\xE1o. \nH\xE3y th\u1EED l\u1EA1i.",
      reportSaveMessage: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u th\xE0nh c\xF4ng.",
      saveFailed: "L\u01B0u kh\xF4ng th\xE0nh c\xF4ng",
      titleSaveComplete: "Ti\xEAu \u0111\u1EC1 L\u01B0u Ho\xE0n th\xE0nh",
      titleSaveErrorMessage: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong khi l\u01B0u ti\xEAu \u0111\u1EC1. \nH\xE3y th\u1EED l\u1EA1i.",
      titleSaveMessage: "Ti\xEAu \u0111\u1EC1 b\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u th\xE0nh c\xF4ng."
    }
  },
  common: {
    cancel: "H\u1EE7y",
    delete: "X\xF3a",
    save: "L\u01B0u",
    edit: "Ch\u1EC9nh s\u1EEDa",
    error: "L\u1ED7i",
    alert: "C\u1EA3nh b\xE1o",
    success: "Th\xE0nh c\xF4ng",
    team: "Nh\xF3m"
  },
  personalReport: {
    title: "B\xE1o c\xE1o c\xE1 nh\xE2n"
  },
  teamReport: {
    title: "B\xE1o c\xE1o nh\xF3m",
    errors: {
      requiredDataMissing: "D\u1EEF li\u1EC7u b\u1EAFt bu\u1ED9c b\u1ECB thi\u1EBFu.",
      cannotFetchTeamData: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u b\xE1o c\xE1o nh\xF3m.",
      teamReportGenerationFailed: "T\u1EA1o b\xE1o c\xE1o nh\xF3m th\u1EA5t b\u1EA1i.",
      teamReportError: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o b\xE1o c\xE1o nh\xF3m."
    },
    notifications: {
      teamReportCancelled: "T\u1EA1o b\xE1o c\xE1o nh\xF3m \u0111\xE3 b\u1ECB h\u1EE7y",
      teamReportCancelledMessage: "Vi\u1EC7c t\u1EA1o b\xE1o c\xE1o \u0111\xE3 b\u1ECB h\u1EE7y theo y\xEAu c\u1EA7u ng\u01B0\u1EDDi d\xF9ng.",
      teamReportGenerating: "\u0110ang t\u1EA1o b\xE1o c\xE1o nh\xF3m",
      teamReportGeneratingMessage: "\u0110ang t\u1EA1o b\xE1o c\xE1o h\xE0ng ng\xE0y {{teamName}}...",
      teamReportFailed: "T\u1EA1o b\xE1o c\xE1o nh\xF3m th\u1EA5t b\u1EA1i",
      teamReportFailedMessage: "Kh\xF4ng th\u1EC3 t\u1EA1o b\xE1o c\xE1o nh\xF3m.",
      teamReportCompleted: "Ho\xE0n th\xE0nh t\u1EA1o b\xE1o c\xE1o nh\xF3m",
      teamReportCompletedMessage: "B\xE1o c\xE1o h\xE0ng ng\xE0y {{teamName}} \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o. Nh\u1EA5p \u0111\u1EC3 xem.",
      teamReportSaveFailed: "L\u01B0u b\xE1o c\xE1o nh\xF3m th\u1EA5t b\u1EA1i",
      teamReportSaveFailedMessage: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u b\xE1o c\xE1o.",
      teamReportErrorMessage: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o b\xE1o c\xE1o nh\xF3m.",
      teamReportCompleteTitle: "\u{1F4CA} Ho\xE0n th\xE0nh b\xE1o c\xE1o nh\xF3m",
      teamReportCompleteBody: "B\xE1o c\xE1o h\xE0ng ng\xE0y {{teamName}} \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o.\nNh\u1EA5p \u0111\u1EC3 xem.",
      teamReportCompleteBodyAlt: "Ki\u1EC3m tra b\xE1o c\xE1o h\xE0ng ng\xE0y cho nh\xF3m {{teamName}}."
    },
    reportTitle: {
      teamDaily: "{{teamName}} - B\xE1o c\xE1o h\xE0ng ng\xE0y {{date}}"
    }
  },
  writePage: {
    reportTitleDaily: "B\xE1o c\xE1o h\xE0ng ng\xE0y",
    reportTitleWeekly: "B\xE1o c\xE1o h\xE0ng tu\u1EA7n",
    titlePlaceholder: "Nh\u1EADp ti\xEAu \u0111\u1EC1",
    saveButton: "L\u01B0u",
    aiGenerateButton: "T\u1EA1o b\u1EB1ng AI",
    regenerateButton: "T\u1EA1o l\u1EA1i",
    generatingReport: "\u0110ang t\u1EA1o b\xE1o c\xE1o...",
    aiHelpText: "Vui l\xF2ng xem x\xE9t b\xE1o c\xE1o \u0111\u01B0\u1EE3c AI t\u1EA1o v\xE0 ch\u1EC9nh s\u1EEDa theo nhu c\u1EA7u.",
    teamLoading: "\u0110ang t\u1EA3i th\xF4ng tin nh\xF3m...",
    notifications: {
      userOrWorkspaceError: "Kh\xF4ng th\u1EC3 x\xE1c minh th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng ho\u1EB7c kh\xF4ng gian l\xE0m vi\u1EC7c.",
      titleRequired: "Vui l\xF2ng nh\u1EADp ti\xEAu \u0111\u1EC1.",
      createSuccess: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
      createError: "L\u1ED7i khi t\u1EA1o b\xE1o c\xE1o."
    },
    teamReportModal: {
      title: "T\u1EA1o B\xE1o c\xE1o Nh\xF3m",
      description1: "B\xE1o c\xE1o nh\xF3m \u0111\u01B0\u1EE3c",
      carefullyWritten: "vi\u1EBFt c\u1EA9n th\u1EADn",
      description2: " b\u1EB1ng c\xE1ch t\u1ED5ng h\u1EE3p ho\u1EA1t \u0111\u1ED9ng c\u1EE7a c\xE1c th\xE0nh vi\xEAn, c\xF3 th\u1EC3 m\u1EA5t",
      timeRequired: "3-5 ph\xFAt",
      description3: ".",
      notificationInfo1: "Khi b\xE1o c\xE1o s\u1EB5n s\xE0ng, ch\xFAng t\xF4i s\u1EBD th\xF4ng b\xE1o qua",
      notificationLocation: "bi\u1EC3u t\u01B0\u1EE3ng th\xF4ng b\xE1o \u1EDF g\xF3c tr\xEAn b\xEAn ph\u1EA3i",
      notificationInfo2: ".",
      tip: "\u{1F4A1} B\u1EA1n c\xF3 th\u1EC3 ti\u1EBFp t\u1EE5c l\xE0m vi\u1EC7c kh\xE1c trong khi b\xE1o c\xE1o nh\xF3m \u0111ang \u0111\u01B0\u1EE3c t\u1EA1o.",
      cancel: "H\u1EE7y",
      confirm: "B\u1EAFt \u0111\u1EA7u t\u1EA1o"
    }
  },
  creationModal: {
    createTitle: "T\u1EA1o B\xE1o c\xE1o M\u1EDBi",
    editTitle: "Ch\u1EC9nh s\u1EEDa Lo\u1EA1i v\xE0 Ng\xE0y",
    description: "Ch\u1ECDn lo\u1EA1i b\xE1o c\xE1o v\xE0 nh\u1EA5n n\xFAt T\u1EA1o b\xE1o c\xE1o.",
    teamDescription: "Ch\u1ECDn lo\u1EA1i b\xE1o c\xE1o v\xE0 nh\xF3m, sau \u0111\xF3 nh\u1EA5n n\xFAt T\u1EA1o b\xE1o c\xE1o.",
    selectTeam: "Ch\u1ECDn Nh\xF3m",
    reportType: "Lo\u1EA1i B\xE1o c\xE1o",
    date: "Ng\xE0y"
  },
  languages: {
    english: "Ti\u1EBFng Anh",
    korean: "Ti\u1EBFng H\xE0n",
    vietnamese: "Ti\u1EBFng Vi\u1EC7t",
    japanese: "Ti\u1EBFng Nh\u1EADt",
    chinese: "Ti\u1EBFng Trung"
  },
  search: {
    placeholder: "T\xECm ki\u1EBFm ti\xEAu \u0111\u1EC1 b\xE1o c\xE1o"
  },
  weekdays: {
    sunday: "CN",
    monday: "T2",
    tuesday: "T3",
    wednesday: "T4",
    thursday: "T5",
    friday: "T6",
    saturday: "T7"
  },
  categories: {
    work: "C\xF4ng vi\u1EC7c",
    meeting: "H\u1ECDp",
    communication: "Giao ti\u1EBFp",
    study: "H\u1ECDc t\u1EADp",
    rest: "Ngh\u1EC9 ng\u01A1i",
    other: "Kh\xE1c"
  },
  errors: {
    fetchSourceDataFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u ngu\u1ED3n",
    reportNotFound: "Kh\xF4ng t\xECm th\u1EA5y b\xE1o c\xE1o",
    addReceiverError: "L\u1ED7i khi th\xEAm ng\u01B0\u1EDDi nh\u1EADn b\xE1o c\xE1o",
    markAsReadFailed: "Kh\xF4ng th\u1EC3 \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    teamInfoFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y th\xF4ng tin nh\xF3m",
    teamMembersFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y danh s\xE1ch th\xE0nh vi\xEAn nh\xF3m",
    plannerDataFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u l\u1ECBch tr\xECnh",
    goalDataFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u m\u1EE5c ti\xEAu",
    noteDataFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u ghi ch\xFA",
    commentDataFetchFailed: "Kh\xF4ng th\u1EC3 l\u1EA5y d\u1EEF li\u1EC7u b\xECnh lu\u1EADn",
    reportGenerationAborted: "Vi\u1EC7c t\u1EA1o b\xE1o c\xE1o \u0111\xE3 b\u1ECB h\u1EE7y",
    backendError: "L\u1ED7i backend",
    dataQueryFailed: "Truy v\u1EA5n d\u1EEF li\u1EC7u th\u1EA5t b\u1EA1i"
  },
  messages: {
    readProcessOptimisticStart: "B\u1EAFt \u0111\u1EA7u c\u1EADp nh\u1EADt l\u1EA1c quan cho vi\u1EC7c \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    readProcessOptimisticComplete: "Ho\xE0n th\xE0nh c\u1EADp nh\u1EADt l\u1EA1c quan cho vi\u1EC7c \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    readProcessFailed: "\u0110\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc th\u1EA5t b\u1EA1i: Kh\xF4i ph\u1EE5c v\u1EC1 tr\u1EA1ng th\xE1i tr\u01B0\u1EDBc",
    readProcessSuccess: "\u0110\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc th\xE0nh c\xF4ng",
    queryInvalidationComplete: "Ho\xE0n th\xE0nh v\xF4 hi\u1EC7u h\xF3a truy v\u1EA5n sau khi \u0111\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
    noTeamMembers: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn nh\xF3m",
    reportGenerationCancelled: "X\xE1c nh\u1EADn h\u1EE7y t\u1EA1o b\xE1o c\xE1o",
    selectedDate: "Ng\xE0y \u0111\xE3 ch\u1ECDn",
    userQuery: "Truy v\u1EA5n ng\u01B0\u1EDDi d\xF9ng",
    dataCount: "S\u1ED1 l\u01B0\u1EE3ng d\u1EEF li\u1EC7u",
    reportType: "Lo\u1EA1i b\xE1o c\xE1o",
    ownerType: "Lo\u1EA1i ch\u1EE7 s\u1EDF h\u1EEFu",
    requestData: "D\u1EEF li\u1EC7u y\xEAu c\u1EA7u",
    receivedChunk: "Ph\u1EA7n d\u1EEF li\u1EC7u nh\u1EADn \u0111\u01B0\u1EE3c",
    finalReportLength: "\u0110\u1ED9 d\xE0i b\xE1o c\xE1o cu\u1ED1i c\xF9ng",
    characters: "k\xFD t\u1EF1"
  }
};

// src/locales/vi-VN/auth.json
var auth_default3 = {
  agreement: {
    advertising: "\u0110\u1ED3ng \xFD nh\u1EADn th\xF4ng tin qu\u1EA3ng c\xE1o",
    age: "T\xF4i t\u1EEB 14 tu\u1ED5i tr\u1EDF l\xEAn",
    description: "B\u1EA1n c\xF3 th\u1EC3 s\u1EED d\u1EE5ng d\u1ECBch v\u1EE5 ngay c\u1EA3 khi b\u1EA1n t\u1EEB ch\u1ED1i \u0111\u1ED3ng \xFD v\u1EDBi c\xE1c t\xF9y ch\u1ECDn.",
    detail: "Chi ti\u1EBFt",
    label: "T\xF4i \u0111\u1ED3ng \xFD v\u1EDBi to\xE0n b\u1ED9 \u0111i\u1EC1u kho\u1EA3n s\u1EED d\u1EE5ng (bao g\u1ED3m c\u1EA3 th\xF4ng tin t\xF9y ch\u1ECDn)",
    law_description: "\u0110\u1EC3 b\u1EA3o v\u1EC7 th\xF4ng tin c\xE1 nh\xE2n v\xE0 quy\u1EC1n c\u1EE7a c\xE1c \u0111\u1ED1i t\u01B0\u1EE3ng th\xF4ng tin, ch\xFAng t\xF4i qu\u1EA3n l\xFD \u0110\u1EA1o lu\u1EADt b\u1EA3o v\u1EC7 th\xF4ng tin c\xE1 nh\xE2n v\xE0 c\xE1c lu\u1EADt v\xE0 quy \u0111\u1ECBnh li\xEAn quan. \n\n\u0110\u1EC3 bi\u1EBFt th\xEAm th\xF4ng tin, vui l\xF2ng truy c\u1EADp Ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt.",
    marketing: "\u0110\u1ED3ng \xFD s\u1EED d\u1EE5ng cho m\u1EE5c \u0111\xEDch qu\u1EA3ng b\xE1 v\xE0 ti\u1EBFp th\u1ECB",
    optional: "Kh\xF4ng b\u1EAFt bu\u1ED9c",
    overseas: "\u0110\u1ED3ng \xFD cung c\u1EA5p th\xF4ng tin c\xE1 nh\xE2n cho b\xEAn th\u1EE9 ba (chuy\u1EC3n giao ra n\u01B0\u1EDBc ngo\xE0i)",
    personalInfo: "\u0110\u1ED3ng \xFD thu th\u1EADp v\xE0 s\u1EED d\u1EE5ng th\xF4ng tin c\xE1 nh\xE2n",
    privacy: "\u0110\u1ED3ng \xFD v\u1EDBi ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt",
    recording: "\u0110\u1ED3ng \xFD ghi \xE2m n\u1ED9i dung cu\u1ED9c h\u1ECDp",
    required: "Y\xEAu c\u1EA7u",
    service: "\u0110i\u1EC1u kho\u1EA3n s\u1EED d\u1EE5ng d\u1ECBch v\u1EE5",
    thirdParty: "\u0110\u1ED3ng \xFD cung c\u1EA5p th\xF4ng tin c\xE1 nh\xE2n cho b\xEAn th\u1EE9 ba",
    title: "Hi\u1EC7p \u0111\u1ECBnh"
  },
  auth_callback: {
    confirm: {
      button: "Ti\u1EBFp t\u1EE5c",
      description: "\u0110\u1EC3 b\u1EA3o m\u1EADt, vui l\xF2ng nh\u1EA5p v\xE0o n\xFAt b\xEAn d\u01B0\u1EDBi \u0111\u1EC3 ti\u1EBFp t\u1EE5c \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u.",
      title: "Ti\u1EBFp t\u1EE5c \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u"
    },
    mobile: {
      error: {
        message: "C\xF3 l\u1ED7i v\u1EDBi li\xEAn k\u1EBFt x\xE1c th\u1EF1c. Vui l\xF2ng y\xEAu c\u1EA7u li\xEAn k\u1EBFt m\u1EDBi.",
        title: "L\u1ED7i x\xE1c th\u1EF1c"
      }
    },
    notification: {
      error: {
        message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\u1EED l\xFD x\xE1c th\u1EF1c. Vui l\xF2ng th\u1EED l\u1EA1i.",
        title: "L\u1ED7i x\xE1c th\u1EF1c"
      },
      stale_token: {
        message: "Li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u \u0111\xE3 h\u1EBFt h\u1EA1n ho\u1EB7c \u0111\xE3 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng. Vui l\xF2ng y\xEAu c\u1EA7u li\xEAn k\u1EBFt m\u1EDBi.",
        title: "Li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i \u0111\xE3 h\u1EBFt h\u1EA1n"
      }
    }
  },
  change_password: {
    action_title: "Thay \u0111\u1ED5i m\u1EADt kh\u1EA9u",
    button: "Thay \u0111\u1ED5i m\u1EADt kh\u1EA9u",
    new_password: {
      label: "M\u1EADt kh\u1EA9u m\u1EDBi",
      placeholder: "Vui l\xF2ng nh\u1EADp m\u1EADt kh\u1EA9u m\u1EDBi"
    },
    notification: {
      error: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong qu\xE1 tr\xECnh thay \u0111\u1ED5i m\u1EADt kh\u1EA9u.",
      success: {
        message: "M\u1EADt kh\u1EA9u \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh c\xF4ng.",
        title: "Thay \u0111\u1ED5i m\u1EADt kh\u1EA9u \u0111\xE3 ho\xE0n th\xE0nh"
      }
    },
    title: "Thay \u0111\u1ED5i m\u1EADt kh\u1EA9u"
  },
  delete_account: {
    action_title: "X\xF3a t\xE0i kho\u1EA3n",
    description: "N\u1EBFu b\u1EA1n l\xE0 th\xE0nh vi\xEAn c\u1EE7a m\u1ED9t nh\xF3m, Todos \u0111\u01B0\u1EE3c t\u1EA1o ra trong khi b\u1EA1n l\xE0 th\xE0nh vi\xEAn c\u1EE7a nh\xF3m s\u1EBD kh\xF4ng b\u1ECB x\xF3a.",
    modal: {
      message_1: "B\u1EA1n c\xF3 ch\u1EAFc b\u1EA1n mu\u1ED1n x\xF3a t\xE0i kho\u1EA3n c\u1EE7a m\xECnh kh\xF4ng? \nH\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 \u0111\u01B0\u1EE3c \u0111\u1EA3o ng\u01B0\u1EE3c.",
      message_2: "N\u1EBFu b\u1EA1n x\xF3a t\xE0i kho\u1EA3n c\u1EE7a m\xECnh, th\xF4ng tin c\xE1 nh\xE2n v\xE0 chi ti\u1EBFt ho\u1EA1t \u0111\u1ED9ng c\u1EE7a b\u1EA1n s\u1EBD b\u1ECB x\xF3a. \n\nTuy nhi\xEAn, Todos \u0111\u01B0\u1EE3c t\u1EA1o ra trong khi b\u1EA1n l\xE0 th\xE0nh vi\xEAn c\u1EE7a nh\xF3m kh\xF4ng b\u1ECB x\xF3a."
    },
    notification: {
      error: {
        has_blocking_workspace: "B\u1EA1n c\xF3 kh\xF4ng gian l\xE0m vi\u1EC7c v\u1EDBi quy\u1EC1n ch\u1EE7 s\u1EDF h\u1EEFu ({{workspaceNames}}). Vui l\xF2ng chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu tr\u01B0\u1EDBc.",
        message: "M\u1ED9t l\u1ED7i \u0111\xE3 x\u1EA3y ra trong qu\xE1 tr\xECnh x\xF3a t\xE0i kho\u1EA3n.",
        session: "Phi\xEAn x\xE1c th\u1EF1c kh\xF4ng th\u1EC3 \u0111\u01B0\u1EE3c t\xECm th\u1EA5y. \nVui l\xF2ng \u0111\u0103ng nh\u1EADp l\u1EA1i.",
        title: "X\xF3a t\xE0i kho\u1EA3n kh\xF4ng th\xE0nh c\xF4ng"
      },
      success: {
        message: "T\xE0i kho\u1EA3n \u0111\xE3 b\u1ECB x\xF3a th\xE0nh c\xF4ng.",
        title: "Ho\xE0n th\xE0nh x\xF3a t\xE0i kho\u1EA3n"
      }
    },
    title: "X\xF3a t\xE0i kho\u1EA3n",
    tooltip: "B\u1EA1n c\xF3 ch\u1EAFc b\u1EA1n mu\u1ED1n x\xF3a t\xE0i kho\u1EA3n c\u1EE7a m\xECnh kh\xF4ng? \u{1F97A}"
  },
  forgot_password: {
    back_to_login: "Quay l\u1EA1i \u0111\u0103ng nh\u1EADp",
    button: "G\u1EEDi li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i",
    description: "Nh\u1EADp email b\u1EA1n \u0111\xE3 s\u1EED d\u1EE5ng \u0111\u1EC3 \u0111\u0103ng k\xFD v\xE0 ch\xFAng t\xF4i s\u1EBD g\u1EEDi cho b\u1EA1n li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u.",
    email_sent: {
      help: "Kh\xF4ng nh\u1EADn \u0111\u01B0\u1EE3c? Ki\u1EC3m tra th\u01B0 m\u1EE5c spam ho\u1EB7c th\u1EED l\u1EA1i.",
      message: "Ch\xFAng t\xF4i \u0111\xE3 g\u1EEDi li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i \u0111\u1EBFn {{email}}.",
      title: "Vui l\xF2ng ki\u1EC3m tra email"
    },
    notification: {
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi email \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u.",
      success: {
        message: "Li\xEAn k\u1EBFt \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn email c\u1EE7a b\u1EA1n. Vui l\xF2ng ki\u1EC3m tra h\u1ED9p th\u01B0.",
        title: "\u0110\xE3 g\u1EEDi email"
      }
    },
    resend: "G\u1EEDi l\u1EA1i",
    resend_in: "G\u1EEDi l\u1EA1i sau {{seconds}}s",
    title: "Qu\xEAn m\u1EADt kh\u1EA9u"
  },
  google_calendar: {
    account_mismatch: {
      attempted_label: "T\xE0i kho\u1EA3n \u0111\xE3 ch\u1ECDn",
      close: "\u0110\xF3ng",
      existing_label: "T\xE0i kho\u1EA3n \u0111\xE3 k\u1EBFt n\u1ED1i",
      go_to_settings: "\u0110i \u0111\u1EBFn C\xE0i \u0111\u1EB7t",
      guide: "Vui l\xF2ng th\u1EED l\u1EA1i v\u1EDBi c\xF9ng t\xE0i kho\u1EA3n, ho\u1EB7c ng\u1EAFt k\u1EBFt n\u1ED1i trong C\xE0i \u0111\u1EB7t tr\u01B0\u1EDBc khi k\u1EBFt n\u1ED1i t\xE0i kho\u1EA3n kh\xE1c.",
      title: "T\xE0i kho\u1EA3n Google kh\xE1c"
    },
    button: {
      connect: "K\u1EBFt n\u1ED1i",
      disconnect: "Ng\u1EAFt k\u1EBFt n\u1ED1i"
    },
    connected_account: "\u0110\xE3 k\u1EBFt n\u1ED1i v\u1EDBi t\xE0i kho\u1EA3n {{email}}",
    connected_date: "Ng\xE0y k\u1EBFt n\u1ED1i",
    description: {
      connected: "\u0110\u1ED3ng b\u1ED9 l\u1ECBch tr\xECnh v\xE0 li\xEAn k\u1EBFt b\u1EA3n ghi cu\u1ED9c h\u1ECDp video",
      disconnected: "K\u1EBFt n\u1ED1i Google \u0111\u1EC3 \u0111\u1ED3ng b\u1ED9 l\u1ECBch v\xE0 s\u1EED d\u1EE5ng cu\u1ED9c h\u1ECDp video",
      expired: "K\u1EBFt n\u1ED1i \u0111\xE3 h\u1EBFt h\u1EA1n. Vui l\xF2ng k\u1EBFt n\u1ED1i l\u1EA1i."
    },
    notification: {
      connect_error: {
        message: "Kh\xF4ng th\u1EC3 k\u1EBFt n\u1ED1i Google.",
        title: "K\u1EBFt n\u1ED1i th\u1EA5t b\u1EA1i"
      },
      connect_success: {
        message: "Google \u0111\xE3 \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i th\xE0nh c\xF4ng.",
        title: "K\u1EBFt n\u1ED1i th\xE0nh c\xF4ng"
      },
      disconnect_error: {
        message: "Kh\xF4ng th\u1EC3 ng\u1EAFt k\u1EBFt n\u1ED1i.",
        title: "L\u1ED7i"
      },
      disconnect_success: {
        message: "Google \u0111\xE3 \u0111\u01B0\u1EE3c ng\u1EAFt k\u1EBFt n\u1ED1i.",
        title: "\u0110\xE3 ng\u1EAFt k\u1EBFt n\u1ED1i"
      },
      login_error: {
        message: "\u0110\u0103ng nh\u1EADp Google th\u1EA5t b\u1EA1i.",
        title: "\u0110\u0103ng nh\u1EADp th\u1EA5t b\u1EA1i"
      },
      token_expired: {
        message: "K\u1EBFt n\u1ED1i Google \u0111\xE3 h\u1EBFt h\u1EA1n. Vui l\xF2ng k\u1EBFt n\u1ED1i l\u1EA1i trong c\xE0i \u0111\u1EB7t.",
        title: "K\u1EBFt n\u1ED1i Google h\u1EBFt h\u1EA1n"
      },
      user_not_found: {
        message: "Kh\xF4ng t\xECm th\u1EA5y th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng.",
        title: "L\u1ED7i"
      }
    },
    status: {
      connected: "\u0110\xE3 k\u1EBFt n\u1ED1i",
      reconnect_required: "C\u1EA7n k\u1EBFt n\u1ED1i l\u1EA1i"
    },
    title: "Li\xEAn k\u1EBFt Google"
  },
  invitation_list: {
    create_workspace: "T\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c c\u1EE7a ri\xEAng b\u1EA1n",
    description: "Ki\u1EC3m tra l\u1EDDi m\u1EDDi v\xE0 tham gia kh\xF4ng gian l\xE0m vi\u1EC7c.",
    no_invite: "Kh\xF4ng c\xF3 l\u1EDDi m\u1EDDi m\u1EDBi",
    register: "L\u1EDDi m\u1EDDi t\u1EEB {{name}}",
    title: "B\u1EA1n c\xF3 {{count}} l\u1EDDi m\u1EDDi"
  },
  logout: {
    description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n \u0111\u0103ng xu\u1EA5t kh\xF4ng?"
  },
  no_access: {
    description: "Vui l\xF2ng y\xEAu c\u1EA7u k\xEDch ho\u1EA1t 1on1 t\u1EEB ch\u1EE7 s\u1EDF h\u1EEFu workspace ho\u1EB7c Admin.",
    title: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp v\xE0o d\u1ECBch v\u1EE5."
  },
  no_permission: {
    dashboard: "Ch\u1EC9 ch\u1EE7 s\u1EDF h\u1EEFu (OWNER) v\xE0 qu\u1EA3n tr\u1ECB vi\xEAn (ADMIN) c\u1EE7a kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi c\xF3 th\u1EC3 xem b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n.",
    meeting: "Ch\u1EC9 nh\u1EEFng ng\u01B0\u1EDDi tham gia cu\u1ED9c h\u1ECDp m\u1EDBi c\xF3 th\u1EC3 xem b\u1EA3n ghi cu\u1ED9c h\u1ECDp 1on1.",
    title: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n truy c\u1EADp v\xE0o trang n\xE0y."
  },
  no_workspace: {
    create_button: "T\u1EA1o Workspace",
    description: "Vui l\xF2ng t\u1EA1o workspace tr\u01B0\u1EDBc \u0111\u1EC3 s\u1EED d\u1EE5ng 1on1.",
    description_mobile: "Vui l\xF2ng y\xEAu c\u1EA7u l\u1EDDi m\u1EDDi t\u1EEB Admin workspace.",
    title: "C\u1EA7n workspace.",
    title_mobile: "Kh\xF4ng c\xF3 workspace \u0111\u01B0\u1EE3c m\u1EDDi."
  },
  reset_password: {
    button: "\u0110\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u",
    description: "Vui l\xF2ng nh\u1EADp m\u1EADt kh\u1EA9u m\u1EDBi c\u1EE7a b\u1EA1n.",
    notification: {
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi \u0111\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u.",
      success: {
        message: "M\u1EADt kh\u1EA9u c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh c\xF4ng. Vui l\xF2ng \u0111\u0103ng nh\u1EADp.",
        title: "\u0110\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u ho\xE0n t\u1EA5t"
      }
    },
    title: "\u0110\u1EB7t l\u1EA1i m\u1EADt kh\u1EA9u"
  },
  sign_in: {
    action_title: "\u0110\u0103ng nh\u1EADp",
    no_account: {
      action_title: "T\u1EA1o m\u1ED9t t\xE0i kho\u1EA3n",
      title: "Kh\xF4ng c\xF3 t\xE0i kho\u1EA3n?"
    },
    notification: {
      error: "\u0110\u0103ng nh\u1EADp kh\xF4ng th\xE0nh c\xF4ng",
      invitation: {
        message: "C\xF3 l\u1EDDi m\u1EDDi tham gia kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi. H\xE3y nh\u1EA5p v\xE0o \u0111\u1EC3 x\xE1c nh\u1EADn.",
        title: "M\u1EDDi tham gia kh\xF4ng gian l\xE0m vi\u1EC7c"
      },
      success: {
        message: "Ch\xE0o m\u1EEBng!",
        title: "\u0110\u0103ng nh\u1EADp th\xE0nh c\xF4ng"
      }
    },
    redirect_to_mobile: "\u0110ang chuy\u1EC3n \u0111\u1EBFn \u1EE9ng d\u1EE5ng...",
    title: "\u0110\u0103ng nh\u1EADp"
  },
  sign_up: {
    action_title: "\u0110\u0103ng k\xFD",
    step_1: "Hi\u1EC7p \u0111\u1ECBnh",
    step_2: "Th\xF4ng tin \u0111\u1EA7u v\xE0o",
    step_3: "Ho\xE0n th\xE0nh",
    title: "\u0110\u0103ng k\xFD"
  },
  sign_up_complete: {
    description: {
      first: "Ch\xFAng t\xF4i \u0111\xE3 g\u1EEDi th\u01B0 x\xE1c minh \u0111\u1EBFn email b\u1EA1n \u0111\xE3 nh\u1EADp.",
      second: "B\u1EA1n c\xF3 th\u1EC3 s\u1EED d\u1EE5ng d\u1ECBch v\u1EE5 khi x\xE1c th\u1EF1c \u0111\u01B0\u1EE3c ho\xE0n th\xE0nh b\u1EB1ng c\xE1ch nh\u1EA5p v\xE0o li\xEAn k\u1EBFt bao g\u1ED3m trong email.",
      third: "N\u1EBFu b\u1EA1n kh\xF4ng nh\u1EADn \u0111\u01B0\u1EE3c email x\xE1c nh\u1EADn, vui l\xF2ng ki\u1EC3m tra th\u01B0 m\u1EE5c spam ho\u1EB7c th\xF9ng r\xE1c."
    },
    title: "Vui l\xF2ng ki\u1EC3m tra email c\u1EE7a b\u1EA1n \u0111\u1EC3 x\xE1c nh\u1EADn."
  },
  sign_up_confirm_email: {
    cancel: "Ch\u1EC9nh s\u1EEDa",
    check: "Vui l\xF2ng \u0111\u1EA3m b\u1EA3o \u0111\u1ECBa ch\u1EC9 email c\u1EE7a b\u1EA1n l\xE0 ch\xEDnh x\xE1c.",
    confirm: "\u0110\u0103ng k\xFD v\u1EDBi \u0111\u1ECBa ch\u1EC9 n\xE0y",
    description: "\u0110\u1ECBa ch\u1EC9 n\xE0y s\u1EBD \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 \u0111\u0103ng nh\u1EADp v\xE0 email x\xE1c minh s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi.",
    title: "Ki\u1EC3m tra \u0111\u1ECBa ch\u1EC9 email c\u1EE7a b\u1EA1n"
  },
  sign_up_form: {
    email_resend: "G\u1EEDi l\u1EA1i email x\xE1c nh\u1EADn",
    email_resending: "\u0110ang g\u1EEDi l\u1EA1i...",
    email_resent: {
      message: "Email x\xE1c nh\u1EADn \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi l\u1EA1i. Vui l\xF2ng ki\u1EC3m tra h\u1ED9p th\u01B0 c\u1EE7a b\u1EA1n.",
      title: "\u0110\xE3 G\u1EEDi L\u1EA1i Email X\xE1c Nh\u1EADn"
    },
    error: {
      email: "Vui l\xF2ng nh\u1EADp email.",
      email_exists: "\u0110\xF3 l\xE0 m\u1ED9t email \u0111\xE3 t\u1ED3n t\u1EA1i.",
      email_exists_message: "Vui l\xF2ng nh\u1EADp l\u1EA1i email.",
      email_invalid: "N\xF3 kh\xF4ng ph\u1EA3i l\xE0 \u0111\u1ECBnh d\u1EA1ng email ph\xF9 h\u1EE3p.",
      email_not_confirmed: "X\xE1c th\u1EF1c email ch\u01B0a \u0111\u01B0\u1EE3c ho\xE0n th\xE0nh. \nVui l\xF2ng ki\u1EC3m tra email.",
      email_personal: "Orblit l\xE0 d\u1ECBch v\u1EE5 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng theo c\u1EA5p t\u1ED5 ch\u1EE9c. Ch\xFAng t\xF4i khuy\u1EBFn ngh\u1ECB \u0111\u0103ng k\xFD b\u1EB1ng email c\xF4ng vi\u1EC7c \u0111\u1EC3 chuy\u1EC3n \u0111\u1ED5i m\u01B0\u1EE3t m\xE0 khi c\xF4ng ty b\u1EA1n \u0111\u0103ng k\xFD.",
      email_personal_contact: "Li\xEAn h\u1EC7 v\u1EDBi \u0111\u1ED9i ng\u0169 c\u1EE7a ch\xFAng t\xF4i",
      email_personal_help: "N\u1EBFu b\u1EA1n kh\xF4ng c\xF3 email c\xF4ng ty, ho\u1EB7c email c\xF4ng ty c\u1EE7a b\u1EA1n b\u1ECB ch\u1EB7n, vui l\xF2ng cho \u0111\u1ED9i ng\u0169 c\u1EE7a ch\xFAng t\xF4i bi\u1EBFt.",
      invalid_login_credentials: "Email ho\u1EB7c m\u1EADt kh\u1EA9u sai.",
      login: "L\u1ED7i \u0111\u0103ng nh\u1EADp",
      logout: "C\xF3 m\u1ED9t l\u1ED7i trong qu\xE1 tr\xECnh \u0111\u0103ng xu\u1EA5t.",
      password_confirm_invalid: "M\u1EADt kh\u1EA9u kh\xF4ng kh\u1EDBp.",
      password_invalid: "M\u1EADt kh\u1EA9u ph\u1EA3i c\xF3 \xEDt nh\u1EA5t 6 k\xFD t\u1EF1.",
      same_password: "M\u1EADt kh\u1EA9u m\u1EDBi gi\u1ED1ng nh\u01B0 m\u1EADt kh\u1EA9u hi\u1EC7n c\xF3.",
      title: "L\u1ED7i x\u1EA3y ra trong khi \u0111\u0103ng k\xFD",
      too_many_requests: "C\xF3 qu\xE1 nhi\u1EC1u n\u1ED7 l\u1EF1c \u0111\u0103ng nh\u1EADp. \nH\xE3y th\u1EED l\u1EA1i sau m\u1ED9t th\u1EDDi gian.",
      update_failed: "Kh\xF4ng c\u1EADp nh\u1EADt th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng."
    },
    helper: {
      email: "Orblit l\xE0 d\u1ECBch v\u1EE5 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng theo c\u1EA5p t\u1ED5 ch\u1EE9c, v\xEC v\u1EADy ch\xFAng t\xF4i ch\u1EC9 ch\u1EA5p nh\u1EADn \u0111\u0103ng k\xFD b\u1EB1ng email c\xF4ng ty."
    },
    label: {
      confirmPassword: "X\xE1c minh m\u1EADt kh\u1EA9u",
      email: "Email",
      name: "T\xEAn",
      password: "M\u1EADt kh\u1EA9u"
    },
    placeholder: {
      company_email: "Nh\u1EADp \u0111\u1ECBa ch\u1EC9 email c\xF4ng ty c\u1EE7a b\u1EA1n",
      confirmPassword: "Nh\u1EADp l\u1EA1i m\u1EADt kh\u1EA9u c\u1EE7a b\u1EA1n",
      email: "Nh\u1EADp \u0111\u1ECBa ch\u1EC9 email c\u1EE7a b\u1EA1n",
      name: "Alex",
      password: "Nh\u1EADp m\u1EADt kh\u1EA9u c\u1EE7a b\u1EA1n"
    },
    required: "* M\u1EE5c trong m\xE0n h\xECnh \u0111\u01B0\u1EE3c y\xEAu c\u1EA7u \u0111\u1EC3 nh\u1EADp.",
    title: "Th\xF4ng tin th\xE0nh vi\xEAn \u0111\u1EA7u v\xE0o"
  },
  signup_inquiry: {
    action: {
      cancel: "H\u1EE7y",
      submit: "G\u1EEDi"
    },
    complete: {
      confirm: "OK",
      description: "Ch\xFAng t\xF4i s\u1EBD xem x\xE9t v\xE0 ph\u1EA3n h\u1ED3i s\u1EDBm. C\u1EA3m \u01A1n b\u1EA1n.",
      title: "Tin nh\u1EAFn c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi"
    },
    description: "H\xE3y cho ch\xFAng t\xF4i bi\u1EBFt n\u1EBFu b\u1EA1n kh\xF4ng c\xF3 email c\xF4ng ty ho\u1EB7c email c\xF4ng ty c\u1EE7a b\u1EA1n b\u1ECB ch\u1EB7n. Ch\xFAng t\xF4i s\u1EBD ph\u1EA3n h\u1ED3i trong v\xF2ng 2 ng\xE0y l\xE0m vi\u1EC7c.",
    error: {
      email_invalid: "Vui l\xF2ng nh\u1EADp \u0111\u1ECBa ch\u1EC9 email h\u1EE3p l\u1EC7.",
      submit_failed: "G\u1EEDi kh\xF4ng th\xE0nh c\xF4ng. Vui l\xF2ng th\u1EED l\u1EA1i sau gi\xE2y l\xE1t."
    },
    field: {
      company: "C\xF4ng ty",
      email: "Email",
      message: "N\u1ED9i dung",
      situation: "T\xECnh hu\u1ED1ng c\u1EE7a b\u1EA1n l\xE0 g\xEC?"
    },
    placeholder: {
      company: "T\xEAn c\xF4ng ty c\u1EE7a b\u1EA1n (t\xF9y ch\u1ECDn)",
      email: "example@company.com",
      message: "B\u1EA5t k\u1EF3 \u0111i\u1EC1u g\xEC b\u1EA1n mu\u1ED1n cho ch\xFAng t\xF4i bi\u1EBFt (t\xF9y ch\u1ECDn)"
    },
    situation_option: {
      blocked_company_email: "Email c\xF4ng ty c\u1EE7a t\xF4i \u0111ang b\u1ECB ch\u1EB7n",
      etc: "Kh\xE1c",
      personal_email: "T\xF4i mu\u1ED1n \u0111\u0103ng k\xFD b\u1EB1ng email c\xE1 nh\xE2n"
    },
    title: "Li\xEAn h\u1EC7 v\u1EDBi ch\xFAng t\xF4i"
  },
  user_setting: {
    general: "T\u1ED5ng quan",
    integration: "T\xEDch h\u1EE3p",
    label: {
      email: "E-mail",
      language: "Ng\xF4n ng\u1EEF",
      mbti: "Mbti",
      name: "T\xEAn",
      oneonone_email: "Nh\u1EADn th\xF4ng b\xE1o qua email",
      password: "M\u1EADt kh\u1EA9u",
      phone: "S\u1ED1 \u0111i\u1EC7n tho\u1EA1i",
      planning_time: "Nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch",
      set_days: "\u0110\u1EB7t Ng\xE0y",
      set_time: "\u0110\u1EB7t Th\u1EDDi Giany",
      timezone: "M\xFAi gi\u1EDD"
    },
    description: {
      oneonone_email: "Nh\u1EADn email c\xE1c th\xF4ng b\xE1o li\xEAn quan \u0111\u1EBFn cu\u1ED9c h\u1ECDp 1on1."
    },
    notification: "",
    notification_message: {
      error: {
        message: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 khi l\u01B0u th\xF4ng tin h\u1ED3 s\u01A1 c\u1EE7a b\u1EA1n. Vui l\xF2ng th\u1EED l\u1EA1i.",
        title: "C\u1EADp Nh\u1EADt Th\u1EA5t B\u1EA1i"
      },
      name_error: "T\xEAn kh\xF4ng th\u1EC3 \u0111\u1EC3 tr\u1ED1ng. Vui l\xF2ng nh\u1EADp t\xEAn c\u1EE7a b\u1EA1n.",
      planning_time: {
        error: {
          message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi c\u1EADp nh\u1EADt nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch c\u1EE7a b\u1EA1n.",
          title: "Kh\xF4ng th\u1EC3 c\u1EADp nh\u1EADt nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch"
        },
        inProgress: "\u0110ang c\u1EADp nh\u1EADt nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch",
        success: {
          message: "Nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt th\xE0nh c\xF4ng.",
          title: "\u0110\xE3 c\u1EADp nh\u1EADt nh\u1EAFc nh\u1EDF l\u1EADp k\u1EBF ho\u1EA1ch"
        }
      },
      success: {
        message: "Nh\u1EEFng thay \u0111\u1ED5i c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u th\xE0nh c\xF4ng.",
        title: "\u0110\xE3 C\u1EADp Nh\u1EADt H\u1ED3 S\u01A1"
      },
      title: "C\u1EADp Nh\u1EADt H\u1ED3 S\u01A1"
    },
    placeholder: {
      email: "Vui l\xF2ng nh\u1EADp email c\u1EE7a b\u1EA1n",
      language: "Vui l\xF2ng ch\u1ECDn ng\xF4n ng\u1EEF c\u1EE7a b\u1EA1n",
      mbti: "Vui l\xF2ng ch\u1ECDn MBTI c\u1EE7a b\u1EA1n",
      name: "Vui l\xF2ng nh\u1EADp t\xEAn c\u1EE7a b\u1EA1n",
      phone: "Vui l\xF2ng nh\u1EADp s\u1ED1 \u0111i\u1EC7n tho\u1EA1i c\u1EE7a b\u1EA1n"
    },
    profile_card: {
      edit: "Ch\u1EC9nh s\u1EEDa",
      title: "Th\u1EBB h\u1ED3 s\u01A1"
    },
    profile_image: "H\xECnh \u1EA3nh h\u1ED3 s\u01A1",
    push_notifications: {
      go_to_settings: "Vui l\xF2ng cho ph\xE9p quy\u1EC1n th\xF4ng b\xE1o trong c\xE0i \u0111\u1EB7t thi\u1EBFt b\u1ECB \u0111\u1EC3 nh\u1EADn th\xF4ng b\xE1o.",
      go_to_settings_title: "C\u1EA7n quy\u1EC1n th\xF4ng b\xE1o",
      title: "Th\xF4ng b\xE1o \u0111\u1EA9y"
    },
    title: "H\u1ED3 s\u01A1"
  },
  onboarding: {
    logout: "\u0110\u0103ng xu\u1EA5t",
    back: "Quay l\u1EA1i",
    intent: {
      title: "Ch\xE0o m\u1EEBng, {{name}}",
      title_fallback: "Ch\xE0o m\u1EEBng b\u1EA1n",
      subtitle: "H\xE3y cho ch\xFAng t\xF4i bi\u1EBFt b\u1EA1n \u0111ang b\u1EAFt \u0111\u1EA7u t\u1EEB \u0111\xE2u, ch\xFAng t\xF4i s\u1EBD thi\u1EBFt l\u1EADp ph\xF9 h\u1EE3p cho b\u1EA1n.",
      option_leader: {
        title: "T\xF4i mu\u1ED1n tri\u1EC3n khai 1on1 cho nh\xF3m c\u1EE7a m\xECnh",
        description: "T\u1EA1o workspace cho nh\xF3m c\u1EE7a b\u1EA1n"
      },
      option_member: {
        title: "T\xF4i tham gia qua l\u1EDDi m\u1EDDi",
        description: "Tham gia nh\xF3m \u0111\xE3 c\xF3"
      }
    },
    workspace_name: {
      title: "T\xEAn workspace c\u1EE7a b\u1EA1n l\xE0 g\xEC?",
      subtitle: "Ch\xFAng t\xF4i khuy\u1EBFn ngh\u1ECB d\xF9ng t\xEAn nh\xF3m, t\u1ED5 ch\u1EE9c ho\u1EB7c c\xF4ng ty.",
      placeholder: "T\xEAn nh\xF3m ho\u1EB7c t\u1ED5 ch\u1EE9c",
      helper_changeable: "B\u1EA1n c\xF3 th\u1EC3 thay \u0111\u1ED5i sau trong c\xE0i \u0111\u1EB7t workspace.",
      helper_solo: "B\u1EA1n c\xF3 th\u1EC3 tr\u1EA3i nghi\u1EC7m m\u1ED9t m\xECnh tr\u01B0\u1EDBc khi m\u1EDDi c\xE1c th\xE0nh vi\xEAn",
      cta: "T\u1EA1o workspace",
      error_required: "Vui l\xF2ng nh\u1EADp t\xEAn workspace",
      error_create_failed: "T\u1EA1o workspace th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i."
    },
    member_waiting: {
      title: "\u0110ang ch\u1EDD l\u1EDDi m\u1EDDi",
      body: "Ch\xFAng t\xF4i s\u1EBD g\u1EEDi email khi l\u1EDDi m\u1EDDi workspace \u0111\u1EBFn v\u1EDBi b\u1EA1n.",
      signup_email_label: "Email \u0111\xE3 \u0111\u0103ng k\xFD c\u1EE7a b\u1EA1n",
      actually_leader_question: "B\u1EA1n mu\u1ED1n t\u1EA1o workspace v\xE0 kh\xE1m ph\xE1 th\u1EED kh\xF4ng?",
      actually_leader_cta: "T\u1EA1o workspace"
    }
  }
};

// src/locales/vi-VN/comment.json
var comment_default3 = {
  comment: {
    header_text: "\u0111\xE3 \u0111\u1EC3 l\u1EA1i m\u1ED9t b\xECnh lu\u1EADn.",
    placeholder: "Vui l\xF2ng \u0111\u1EC3 l\u1EA1i nh\u1EADn x\xE9t",
    submit: "G\u1EEDi",
    no_comments: "Ch\u01B0a c\xF3 b\xECnh lu\u1EADn n\xE0o. H\xE3y l\xE0 ng\u01B0\u1EDDi \u0111\u1EA7u ti\xEAn b\xECnh lu\u1EADn!",
    no_user: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn \u0111\u1EC3 \u0111\u1EC1 c\u1EADp"
  },
  reply: {
    add: "Th\xEAm tr\u1EA3 l\u1EDDi",
    header_text: "\u0111\xE3 \u0111\u1EC3 l\u1EA1i m\u1ED9t c\xE2u tr\u1EA3 l\u1EDDi.",
    placeholder: "Vui l\xF2ng \u0111\u1EC3 l\u1EA1i tr\u1EA3 l\u1EDDi",
    title: "Tr\u1EA3 l\u1EDDi"
  },
  notifications: {
    comment_success_title: "\u0110\xE3 t\u1EA1o b\xECnh lu\u1EADn",
    comment_success_message: "B\xECnh lu\u1EADn c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\u0103ng th\xE0nh c\xF4ng.",
    comment_error_title: "B\xECnh lu\u1EADn th\u1EA5t b\u1EA1i",
    comment_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi \u0111\u0103ng b\xECnh lu\u1EADn c\u1EE7a b\u1EA1n."
  },
  modal: {
    delete: {
      message: "B\u1EA1n c\xF3 ch\u1EAFc l\xE0 b\u1EA1n mu\u1ED1n x\xF3a b\xECnh lu\u1EADn n\xE0y?",
      reply_message: "B\u1EA1n c\xF3 ch\u1EAFc l\xE0 b\u1EA1n mu\u1ED1n x\xF3a b\xECnh lu\u1EADn n\xE0y?\nC\xE1c c\xE2u tr\u1EA3 l\u1EDDi trong b\xECnh lu\u1EADn c\u0169ng s\u1EBD b\u1ECB x\xF3a.",
      title: "X\xF3a b\xECnh lu\u1EADn"
    }
  }
};

// src/locales/vi-VN/note.json
var note_default3 = {
  title: "Ghi ch\xFA",
  create: {
    title: "T\u1EA1o ghi ch\xFA",
    in_new_window: "T\u1EA1o trong c\u1EEDa s\u1ED5 m\u1EDBi",
    success: "T\u1EA1o ghi ch\xFA th\xE0nh c\xF4ng",
    success_message: "Ghi ch\xFA m\u1EDBi \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o.",
    fail: "T\u1EA1o ghi ch\xFA th\u1EA5t b\u1EA1i",
    fail_message: "Kh\xF4ng th\u1EC3 t\u1EA1o ghi ch\xFA. Vui l\xF2ng th\u1EED l\u1EA1i."
  },
  actions: {
    menu: "Menu ghi ch\xFA",
    export_pdf: "Xu\u1EA5t sang PDF",
    delete: "X\xF3a",
    delete_note: "X\xF3a ghi ch\xFA",
    delete_title: "X\xF3a ghi ch\xFA",
    delete_confirm: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n x\xF3a ghi ch\xFA n\xE0y kh\xF4ng?",
    cancel: "H\u1EE7y",
    open_new_window: "M\u1EDF trong c\u1EEDa s\u1ED5 m\u1EDBi",
    notifications: {
      delete_success: "X\xF3a ghi ch\xFA th\xE0nh c\xF4ng",
      delete_success_message: "Ghi ch\xFA \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
      delete_fail: "X\xF3a ghi ch\xFA th\u1EA5t b\u1EA1i",
      delete_fail_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a ghi ch\xFA. Vui l\xF2ng th\u1EED l\u1EA1i.",
      export_fail: "Xu\u1EA5t th\u1EA5t b\u1EA1i",
      export_fail_message: "Kh\xF4ng th\u1EC3 t\xECm th\u1EA5y phi\xEAn b\u1EA3n tr\xECnh so\u1EA1n th\u1EA3o."
    }
  },
  pdf: {
    errors: {
      no_editor: "Kh\xF4ng c\xF3 tr\xECnh so\u1EA1n th\u1EA3o.",
      no_method: "Kh\xF4ng t\xECm th\u1EA5y ph\u01B0\u01A1ng th\u1EE9c blocksToFullHTML.",
      unsupported: "T\xEDnh n\u0103ng chuy\u1EC3n \u0111\u1ED5i HTML kh\xF4ng kh\u1EA3 d\u1EE5ng.",
      convert_fail: "Kh\xF4ng th\u1EC3 chuy\u1EC3n \u0111\u1ED5i n\u1ED9i dung.",
      module_load_fail: "Kh\xF4ng th\u1EC3 t\u1EA3i m\xF4-\u0111un html2pdf",
      unknown: "L\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh",
      pdf_create_fail: "Kh\xF4ng th\u1EC3 t\u1EA1o t\u1EADp tin PDF:",
      html_convert_fail: "Kh\xF4ng th\u1EC3 chuy\u1EC3n \u0111\u1ED5i n\u1ED9i dung tr\xECnh so\u1EA1n th\u1EA3o sang HTML:",
      container_remove_fail: "L\u1ED7i khi x\xF3a container:"
    },
    notifications: {
      export_success: "Xu\u1EA5t PDF th\xE0nh c\xF4ng",
      export_success_message: "T\u1EADp tin {{title}}.pdf \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA3i xu\u1ED1ng.",
      export_fail: "Xu\u1EA5t PDF th\u1EA5t b\u1EA1i",
      convert_fail: "L\u1ED7i khi chuy\u1EC3n \u0111\u1ED5i sang PDF:"
    }
  },
  auth: {
    required: "C\u1EA7n ph\u1EA3i x\xE1c th\u1EF1c",
    login_required: "C\u1EA7n \u0111\u0103ng nh\u1EADp \u0111\u1EC3 t\u1EA1o ghi ch\xFA."
  },
  info: {
    required: "C\u1EA7n ph\u1EA3i th\xF4ng tin",
    id_required: "C\u1EA7n ph\u1EA3i ID M\u1EE5c ti\xEAu ho\u1EB7c ID To-do."
  },
  ai: {
    summary: {
      title: "T\u1ED5ng k\u1EBFt AI",
      generating: "\u0110ang t\u1EA1o...",
      regenerate: "T\u1EA1o l\u1EA1i",
      regenerate_message: "T\u1ED5ng k\u1EBFt ch\u01B0a \u0111\u01B0\u1EE3c t\u1EA1o. Vui l\xF2ng th\u1EED l\u1EA1i.",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o b\u1EA3n t\xF3m t\u1EAFt. Vui l\xF2ng th\u1EED l\u1EA1i sau."
    },
    translate: {
      button: "D\u1ECBch"
    },
    chat: {
      search_placeholder: "T\xECm ki\u1EBFm tr\xEAn web...",
      ask_placeholder: "H\u1ECFi b\u1EA5t c\u1EE9 \u0111i\u1EC1u g\xEC...",
      disable_search: "T\u1EAFt ch\u1EBF \u0111\u1ED9 t\xECm ki\u1EBFm",
      enable_search: "T\xECm ki\u1EBFm tr\xEAn web",
      send: "G\u1EEDi",
      search_preparing: "\u0110ang chu\u1EA9n b\u1ECB t\xECm ki\u1EBFm...",
      searching: "\u0110ang t\xECm ki\u1EBFm",
      search_complete: "T\xECm ki\u1EBFm ho\xE0n t\u1EA5t",
      error: "R\u1EA5t ti\u1EBFc, \u0111\xE3 x\u1EA3y ra l\u1ED7i."
    },
    share: {
      no_title: "Kh\xF4ng c\xF3 ti\xEAu \u0111\u1EC1",
      share_suffix: "Chia s\u1EBB li\xEAn k\u1EBFt ghi ch\xFA",
      new_link: "Li\xEAn k\u1EBFt m\u1EDBi",
      copy: "Sao ch\xE9p",
      copied: "\u0110\xE3 sao ch\xE9p",
      share_button: "Chia s\u1EBB li\xEAn k\u1EBFt",
      generating: "\u0110ang t\u1EA1o b\u1EA3n t\xF3m t\u1EAFt...",
      modal_title: "Chia s\u1EBB ghi ch\xFA",
      url_share: "Chia s\u1EBB URL"
    },
    email: {
      send: "G\u1EEDi qua email",
      generating: "\u0110ang t\u1EA1o b\u1EA3n t\xF3m t\u1EAFt...",
      error: "Kh\xF4ng th\u1EC3 m\u1EDF Gmail. L\u1ED7i",
      greeting: "Xin ch\xE0o, t\xF4i l\xE0 {{name}}.",
      body: "T\xF4i \u0111ang chia s\u1EBB b\u1EA3n t\xF3m t\u1EAFt v\xE0 li\xEAn k\u1EBFt \u0111\u1EBFn ghi ch\xFA: {{title}}.",
      link_text: "Li\xEAn k\u1EBFt ghi ch\xFA",
      thanks: "C\u1EA3m \u01A1n.",
      from: "Tr\xE2n tr\u1ECDng,\n{{name}}"
    },
    task: {
      create: "T\u1EA1o nhi\u1EC7m v\u1EE5"
    }
  }
};

// src/locales/vi-VN/activity.json
var activity_default3 = {
  and: " v\xE0 ",
  update: "<1> \u0111\xE3 thay \u0111\u1ED5i </1><0>{{label}}</0><1> th\xE0nh </1><0>{{value}}</0>.",
  actor: "<0> {{name}} </0>",
  change: "<1> \u0111\xE3 thay \u0111\u1ED5i </1> <0> {{label}} </0> <1> th\xE0nh </1> <0> {{value}}</0>.",
  change_label: "<1> \u0111\xE3 thay \u0111\u1ED5i </1> <0> {{nh\xE3n}} </0>.",
  delete: "<1> \u0111\xE3 x\xF3a </1> <0> {{nh\xE3n}} </0>.",
  due_date: {
    delete: "<1> \u0111\xE3 x\xF3a </1> <0> H\u1EA1n ch\xF3t </0>",
    update: "<1> \u0111\xE3 thay \u0111\u1ED5i </1> <0> H\u1EA1n ch\xF3t </0> <1> th\xE0nh </1> <0> {{value}}</0>."
  },
  indicator: {
    add: "<1> \u0111\xE3 th\xEAm </1> <0> Ch\u1EC9 b\xE1o </0>",
    default: "\u0111\xE3 c\u1EADp nh\u1EADt ch\u1EC9 s\u1ED1",
    update: "<1> \u0111\xE3 c\u1EADp nh\u1EADt </1> <0> Ch\u1EC9 b\xE1o </0>"
  },
  remove_project: "<1> \u0111\xE3 x\xF3a k\u1EBFt n\u1ED1i v\u1EDBi </1><0>d\u1EF1 \xE1n.</0>",
  set: "<1> \u0111\xE3 \u0111\u1EB7t </1> <0> {{nh\xE3n}} </0> <1> th\xE0nh </1> <0> {{value}}</0>.",
  set_label: "<1> \u0111\xE3 \u0111\u1EB7t </1> <0> {{nh\xE3n}} </0>.",
  share: {
    fallback_text: {
      changed: "Tr\u1EA1ng th\xE1i chia s\u1EBB nhi\u1EC7m v\u1EE5 \u0111\xE3 thay \u0111\u1ED5i.",
      share: "chia s\u1EBB nhi\u1EC7m v\u1EE5 v\u1EDBi \u0111\u1ED9i.",
      unshare: "Kh\xF4ng chia s\u1EBB nhi\u1EC7m v\u1EE5 v\u1EDBi \u0111\u1ED9i."
    },
    share_end: ".",
    share_start: "\u0111\xE3 chia s\u1EBB nhi\u1EC7m v\u1EE5 v\u1EDBi",
    unshare_end: ".",
    unshare_start: "\u0111\xE3 ng\u1EEBng chia s\u1EBB nhi\u1EC7m v\u1EE5 v\u1EDBi"
  },
  task: {
    create: "\u0111\xE3 t\u1EA1o ra m\u1ED9t nhi\u1EC7m v\u1EE5.",
    delete: "\u0111\xE3 x\xF3a nhi\u1EC7m v\u1EE5."
  },
  note: {
    create: "<1> \u0111\xE3 t\u1EA1o m\u1ED9t </1> <0> L\u01B0u \xFD. </0>"
  },
  title: "Ho\u1EA1t \u0111\u1ED9ng"
};

// src/locales/vi-VN/workspace.json
var workspace_default3 = {
  component: {
    add_button: {
      create: "T\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c",
      description: "T\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi<br />\u0111\u1EC3 qu\u1EA3n l\xFD nh\xF3m v\xE0 d\u1EF1 \xE1n"
    },
    create_dialog: {
      cancel_button: "H\u1EE7y",
      create_button: "T\u1EA1o",
      description_label: "M\xF4 t\u1EA3",
      description_placeholder: "Nh\u1EADp m\xF4 t\u1EA3 cho kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y",
      error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c.",
      error_title: "Kh\xF4ng th\u1EC3 t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c",
      name_label: "T\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
      name_placeholder: "Nh\u1EADp t\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
      name_required: "Vui l\xF2ng nh\u1EADp t\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
      success_message: "Kh\xF4ng gian l\xE0m vi\u1EC7c \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
      success_title: "\u0110\xE3 t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c",
      title: "T\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi",
      warn_message: "Vui l\xF2ng b\u1EADt c\xF4ng t\u1EAFc 'Active' tr\xEAn trang C\xE0i \u0111\u1EB7t Kh\xF4ng gian l\xE0m vi\u1EC7c!",
      warn_title: "C\u1EA7n k\xEDch ho\u1EA1t t\xE0i kho\u1EA3n th\xE0nh vi\xEAn."
    },
    item: {
      create_new: "T\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi",
      create_team: "T\u1EA1o nh\xF3m",
      feedback: "Ph\u1EA3n h\u1ED3i",
      no_teams: "Kh\xF4ng c\xF3 nh\xF3m n\xE0o",
      performance: "Hi\u1EC7u su\u1EA5t",
      report: {
        personal: "B\xE1o c\xE1o c\xE1 nh\xE2n",
        team: "B\xE1o c\xE1o nh\xF3m",
        title: "B\xE1o c\xE1o"
      },
      setting: "C\xE0i \u0111\u1EB7t kh\xF4ng gian l\xE0m vi\u1EC7c",
      title: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
      view_invitations: "Ki\u1EC3m tra l\u1EDDi m\u1EDDi ({{count}})"
    },
    main_info: {
      image_update_error: "C\u1EADp nh\u1EADt \u1EA3nh kh\xF4ng gian l\xE0m vi\u1EC7c th\u1EA5t b\u1EA1i:",
      image_update_success: "C\u1EADp nh\u1EADt \u1EA3nh kh\xF4ng gian l\xE0m vi\u1EC7c th\xE0nh c\xF4ng:",
      name_update_error: "C\u1EADp nh\u1EADt t\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c th\u1EA5t b\u1EA1i:",
      name_update_success: "C\u1EADp nh\u1EADt t\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c th\xE0nh c\xF4ng:"
    }
  },
  error: {
    access_check_failed: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi ki\u1EC3m tra quy\u1EC1n truy c\u1EADp.",
    already_member: "Ng\u01B0\u1EDDi d\xF9ng \u0111\xE3 l\xE0 th\xE0nh vi\xEAn c\u1EE7a kh\xF4ng gian l\xE0m vi\u1EC7c.",
    cannot_check_team_access: "Kh\xF4ng th\u1EC3 ki\u1EC3m tra quy\u1EC1n truy c\u1EADp nh\xF3m.",
    create_failed: "Kh\xF4ng th\u1EC3 t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c.",
    data_not_found_after_create: "Kh\xF4ng t\xECm th\u1EA5y d\u1EEF li\u1EC7u sau khi t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c.",
    data_not_found_after_update: "Kh\xF4ng t\xECm th\u1EA5y d\u1EEF li\u1EC7u sau khi c\u1EADp nh\u1EADt kh\xF4ng gian l\xE0m vi\u1EC7c.",
    failed_to_get_workspace_list: "Kh\xF4ng th\u1EC3 l\u1EA5y danh s\xE1ch kh\xF4ng gian l\xE0m vi\u1EC7c.",
    invite_failed_title: "Th\u1EA5t b\u1EA1i khi m\u1EDDi",
    delete_failed: "Kh\xF4ng th\u1EC3 x\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c ho\u1EB7c kh\xF4ng gian l\xE0m vi\u1EC7c \u0111\xE3 b\u1ECB x\xF3a.",
    delete_blocked_by_subscription: "Workspace n\xE0y c\xF3 g\xF3i \u0111\u0103ng k\xFD \u0111ang ho\u1EA1t \u0111\u1ED9ng ho\u1EB7c \u0111ang ch\u1EDD h\u1EE7y. Ch\u1EC9 x\xF3a sau khi g\xF3i \u0111\u0103ng k\xFD \u0111\xE3 k\u1EBFt th\xFAc ho\xE0n to\xE0n.",
    unauthorized_user: "Kh\xF4ng t\xECm th\u1EA5y ng\u01B0\u1EDDi d\xF9ng \u0111\xE3 x\xE1c th\u1EF1c.",
    unknown_create_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh trong qu\xE1 tr\xECnh t\u1EA1o kh\xF4ng gian l\xE0m vi\u1EC7c.",
    unknown_update_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh trong qu\xE1 tr\xECnh c\u1EADp nh\u1EADt kh\xF4ng gian l\xE0m vi\u1EC7c.",
    update_failed: "Kh\xF4ng th\u1EC3 c\u1EADp nh\u1EADt kh\xF4ng gian l\xE0m vi\u1EC7c.",
    user_already_member: "Ng\u01B0\u1EDDi d\xF9ng \u0111\xE3 l\xE0 th\xE0nh vi\xEAn c\u1EE7a kh\xF4ng gian l\xE0m vi\u1EC7c.",
    user_not_found: "Kh\xF4ng t\xECm th\u1EA5y ng\u01B0\u1EDDi d\xF9ng \u0111\u1EC3 m\u1EDDi.",
    user_not_found_for_invitation: "Kh\xF4ng t\xECm th\u1EA5y ng\u01B0\u1EDDi d\xF9ng \u0111\u1EC3 m\u1EDDi.",
    user_not_logged_in: "Ng\u01B0\u1EDDi d\xF9ng ch\u01B0a \u0111\u0103ng nh\u1EADp.",
    workspace_not_found: "Kh\xF4ng t\xECm th\u1EA5y kh\xF4ng gian l\xE0m vi\u1EC7c.",
    transfer_ownership_failed: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh x\u1EED l\xFD vi\u1EC7c chuy\u1EC3n giao quy\u1EC1n s\u1EDF h\u1EEFu."
  },
  setting: {
    referral: {
      title: "Li\xEAn k\u1EBFt gi\u1EDBi thi\u1EC7u",
      description: "Khi ai \u0111\xF3 \u0111\u0103ng k\xFD b\u1EB1ng li\xEAn k\u1EBFt n\xE0y v\xE0 t\u1EA1o workspace, workspace n\xE0y c\xF3 th\u1EC3 ph\xE2n t\xEDch th\xEAm 3 cu\u1ED9c h\u1ECDp.",
      copy_button: "Sao ch\xE9p li\xEAn k\u1EBFt",
      copied: "\u0110\xE3 sao ch\xE9p"
    },
    access_denied: "Ch\u1EC9 th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c m\u1EDBi c\xF3 th\u1EC3 truy c\u1EADp.",
    delete_modal: {
      confirm: "X\xF3a",
      description: "H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c v\xE0 s\u1EBD x\xF3a v\u0129nh vi\u1EC5n t\u1EA5t c\u1EA3 c\xE1c d\u1EF1 \xE1n v\xE0 nhi\u1EC7m v\u1EE5 trong kh\xF4ng gian l\xE0m vi\u1EC7c.",
      description_1on1: "T\u1EA5t c\u1EA3 th\xE0nh vi\xEAn s\u1EBD m\u1EA5t quy\u1EC1n truy c\u1EADp ngay l\u1EADp t\u1EE9c v\xE0 c\xE1c b\u1EA3n ghi 1on1 c\u1EE7a kh\xF4ng gian l\xE0m vi\u1EC7c s\u1EBD kh\xF4ng c\xF2n kh\u1EA3 d\u1EE5ng. Kh\xF4ng h\u1ED7 tr\u1EE3 kh\xF4i ph\u1EE5c sau khi x\xF3a.",
      member_warning: "Quy\u1EC1n truy c\u1EADp c\u1EE7a {{count}} th\xE0nh vi\xEAn \u0111ang ho\u1EA1t \u0111\u1ED9ng kh\xE1c c\u0169ng s\u1EBD b\u1ECB thu h\u1ED3i.",
      name_confirmation_label: "T\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
      name_confirmation_description: "Nh\u1EADp ch\xEDnh x\xE1c {{name}} \u0111\u1EC3 ti\u1EBFp t\u1EE5c.",
      message: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n x\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y?",
      error_title: "X\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c th\u1EA5t b\u1EA1i",
      error_message: "Kh\xF4ng th\u1EC3 x\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c.",
      subscription_warning: "Kh\xF4ng th\u1EC3 x\xF3a workspace cho \u0111\u1EBFn khi g\xF3i \u0111\u0103ng k\xFD k\u1EBFt th\xFAc ho\xE0n to\xE0n. H\xE3y h\u1EE7y g\xF3i \u0111\u0103ng k\xFD tr\u01B0\u1EDBc v\xE0 th\u1EED l\u1EA1i sau ng\xE0y {{endDate}}. Hi\u1EC7n t\u1EA1i g\xF3i h\u1ED7 tr\u1EE3 {{memberCount}} th\xE0nh vi\xEAn.",
      subscription_inquiry: "N\u1EBFu c\xF3 th\u1EAFc m\u1EAFc, vui l\xF2ng li\xEAn h\u1EC7 jjchoi@reversemountain.co.kr.",
      title: "X\xE1c nh\u1EADn x\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c"
    },
    general: {
      workspace_name: "Kh\xF4ng gian l\xE0m vi\u1EC7c"
    },
    invitation_cancel_modal: {
      confirm: "H\u1EE7y l\u1EDDi m\u1EDDi",
      confirm_message: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n h\u1EE7y l\u1EDDi m\u1EDDi \u0111\xE3 g\u1EEDi \u0111\u1EBFn {{email}}?",
      description: "Khi \u0111\xE3 h\u1EE7y, ng\u01B0\u1EDDi d\xF9ng s\u1EBD kh\xF4ng th\u1EC3 tham gia kh\xF4ng gian l\xE0m vi\u1EC7c.",
      title: "H\u1EE7y l\u1EDDi m\u1EDDi"
    },
    loading_state: {
      loading_message: "\u0110ang t\u1EA3i th\xF4ng tin kh\xF4ng gian l\xE0m vi\u1EC7c..."
    },
    member_remove_modal: {
      confirm_message: "B\u1EA1n c\xF3 mu\u1ED1n x\xF3a {{name}} kh\u1ECFi kh\xF4ng gian l\xE0m vi\u1EC7c kh\xF4ng?",
      description: "Th\xE0nh vi\xEAn b\u1ECB x\xF3a s\u1EBD kh\xF4ng c\xF2n quy\u1EC1n truy c\u1EADp v\xE0o kh\xF4ng gian l\xE0m vi\u1EC7c v\xE0 c\xE1c nh\xF3m li\xEAn quan.<br />Th\xF4ng tin To-do li\xEAn k\u1EBFt v\u1EDBi kh\xF4ng gian l\xE0m vi\u1EC7c v\u1EABn c\xF2n trong l\u1ECBch tr\xECnh c\u1EE7a th\xE0nh vi\xEAn,<br />nh\u01B0ng th\xE0nh vi\xEAn b\u1ECB x\xF3a kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa ho\u1EB7c x\xF3a ch\xFAng, v\xE0 kh\xF4ng th\u1EC3 xem ghi ch\xFA.",
      title: "X\xF3a th\xE0nh vi\xEAn"
    },
    member_table: {
      access_denied: "T\u1EEB ch\u1ED1i truy c\u1EADp.",
      active: "Ho\u1EA1t \u0111\u1ED9ng",
      cancel_invitation: "H\u1EE7y l\u1EDDi m\u1EDDi",
      description: "M\xF4 t\u1EA3",
      empty_message: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn n\xE0o trong kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y",
      enter_description: "Nh\u1EADp ch\u1EE9c danh c\xF4ng vi\u1EC7c, s\u1ED1 n\u0103m ph\u1EE5c v\u1EE5, v.v.",
      invite_button: "M\u1EDDi th\xE0nh vi\xEAn",
      invited: "\u0110\xE3 m\u1EDDi",
      invited_user: "Ng\u01B0\u1EDDi d\xF9ng \u0111\u01B0\u1EE3c m\u1EDDi",
      manager: "Qu\u1EA3n l\xFD",
      loading_message: "\u0110ang t\u1EA3i danh s\xE1ch th\xE0nh vi\xEAn...",
      name: "T\xEAn",
      pending: "\u0110ang ch\u1EDD",
      remove_member: "X\xF3a th\xE0nh vi\xEAn",
      role: "Vai tr\xF2",
      select_manager: "Ch\u1ECDn qu\u1EA3n l\xFD",
      status: "Tr\u1EA1ng th\xE1i",
      team: "Nh\xF3m",
      title: "Th\xE0nh vi\xEAn",
      filter_team: "L\u1ECDc theo nh\xF3m"
    },
    bulk_action: {
      selected_count: "\u0110\xE3 ch\u1ECDn {{count}}",
      change_leader: "\u0110\u1ED5i qu\u1EA3n l\xFD h\xE0ng lo\u1EA1t",
      change_role: "\u0110\u1ED5i vai tr\xF2 h\xE0ng lo\u1EA1t",
      deselect: "B\u1ECF ch\u1ECDn t\u1EA5t c\u1EA3",
      select_role: "Ch\u1ECDn vai tr\xF2",
      select_leader: "Ch\u1ECDn qu\u1EA3n l\xFD",
      cancel: "H\u1EE7y",
      apply: "\xC1p d\u1EE5ng",
      success: "C\u1EADp nh\u1EADt h\xE0ng lo\u1EA1t ho\xE0n t\u1EA5t",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh c\u1EADp nh\u1EADt h\xE0ng lo\u1EA1t"
    },
    not_found: "Kh\xF4ng t\xECm th\u1EA5y kh\xF4ng gian l\xE0m vi\u1EC7c.",
    tab_container: {
      create_team: "T\u1EA1o nh\xF3m",
      invite_member: "M\u1EDDi th\xE0nh vi\xEAn",
      member: "Qu\u1EA3n l\xFD th\xE0nh vi\xEAn",
      members: "Th\xE0nh vi\xEAn",
      team: "Qu\u1EA3n l\xFD nh\xF3m",
      teams: "Nh\xF3m",
      custom: "T\xF9y ch\u1EC9nh"
    },
    custom: {
      analysis_visibility: {
        title: "Ph\u1EA3n h\u1ED3i cho tr\u01B0\u1EDFng nh\xF3m sau cu\u1ED9c h\u1ECDp",
        description: "Ch\u1ECDn ai c\xF3 th\u1EC3 xem ph\u1EA3n h\u1ED3i cho tr\u01B0\u1EDFng nh\xF3m trong tab ph\xE2n t\xEDch. T\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u v\xE0 ph\u1EA3n h\u1ED3i cho th\xE0nh vi\xEAn kh\xF4ng b\u1ECB \u1EA3nh h\u01B0\u1EDFng. Thay \u0111\u1ED5i \u0111\u01B0\u1EE3c \xE1p d\u1EE5ng ngay l\u1EADp t\u1EE9c cho t\u1EA5t c\u1EA3 cu\u1ED9c h\u1ECDp, bao g\u1ED3m c\u1EA3 cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc \u0111\xE2y.",
        leader_only: "Ch\u1EC9 hi\u1EC3n th\u1ECB cho tr\u01B0\u1EDFng nh\xF3m",
        both: "Hi\u1EC3n th\u1ECB cho c\u1EA3 tr\u01B0\u1EDFng nh\xF3m v\xE0 th\xE0nh vi\xEAn",
        per_leader: "C\xE0i \u0111\u1EB7t theo t\u1EEBng tr\u01B0\u1EDFng nh\xF3m",
        per_leader_checkbox: "Chia s\u1EBB ph\u1EA3n h\u1ED3i cho tr\u01B0\u1EDFng nh\xF3m t\u1EEB cu\u1ED9c h\u1ECDp t\xF4i ch\u1EE7 tr\xEC v\u1EDBi c\xE1c th\xE0nh vi\xEAn.",
        save_success: "\u0110\xE3 l\u01B0u c\xE0i \u0111\u1EB7t hi\u1EC3n th\u1ECB ph\u1EA3n h\u1ED3i cho tr\u01B0\u1EDFng nh\xF3m.",
        save_error: "Kh\xF4ng th\u1EC3 l\u01B0u c\xE0i \u0111\u1EB7t."
      },
      question_draft: {
        title: "Ch\u1EBF \u0111\u1ED9 nh\xE1p c\xE2u h\u1ECFi",
        description: "Thay \u0111\u1ED5i \u0111\u01B0\u1EE3c \xE1p d\u1EE5ng ngay l\u1EADp t\u1EE9c cho t\u1EA5t c\u1EA3 cu\u1ED9c h\u1ECDp trong workspace.",
        default_mode: "M\u1EB7c \u0111\u1ECBnh: C\xE2u h\u1ECFi \u0111\u01B0\u1EE3c chia s\u1EBB v\u1EDBi \u0111\u1ED1i t\xE1c theo th\u1EDDi gian th\u1EF1c.",
        draft_mode: "Ch\u1EBF \u0111\u1ED9 nh\xE1p: C\xE2u h\u1ECFi ch\u1EC9 \u0111\u01B0\u1EE3c chia s\u1EBB sau khi nh\u1EA5n 'C\xF4ng b\u1ED1 c\xE2u h\u1ECFi'.",
        info_title: "V\u1EC1 ch\u1EBF \u0111\u1ED9 nh\xE1p c\xE2u h\u1ECFi",
        info_1: "H\u1EEFu \xEDch khi b\u1EA1n mu\u1ED1n ho\xE0n thi\u1EC7n t\u1EA5t c\u1EA3 c\xE2u h\u1ECFi tr\u01B0\u1EDBc khi chia s\u1EBB c\xF9ng l\xFAc.",
        info_2: "C\xF4ng b\u1ED1: Ngay c\u1EA3 khi b\u1EA1n c\xF4ng b\u1ED1, c\xE2u h\u1ECFi nh\xE1p c\u1EE7a \u0111\u1ED1i t\xE1c v\u1EABn \u1EA9n cho \u0111\u1EBFn khi h\u1ECD c\xF4ng b\u1ED1.",
        info_3: "T\u1EF1 \u0111\u1ED9ng c\xF4ng b\u1ED1: N\u1EBFu b\u1EA1n b\u1EAFt \u0111\u1EA7u cu\u1ED9c h\u1ECDp m\xE0 kh\xF4ng c\xF4ng b\u1ED1, t\u1EA5t c\u1EA3 b\u1EA3n nh\xE1p s\u1EBD t\u1EF1 \u0111\u1ED9ng \u0111\u01B0\u1EE3c chia s\u1EBB.",
        info_4: "Sau khi c\xF4ng b\u1ED1: C\xE1c c\xE2u h\u1ECFi \u0111\u01B0\u1EE3c th\xEAm ho\u1EB7c ch\u1EC9nh s\u1EEDa sau \u0111\xF3 s\u1EBD \u0111\u01B0\u1EE3c chia s\u1EBB theo th\u1EDDi gian th\u1EF1c.",
        save_success: "\u0110\xE3 l\u01B0u c\xE0i \u0111\u1EB7t ch\u1EBF \u0111\u1ED9 nh\xE1p c\xE2u h\u1ECFi.",
        save_error: "Kh\xF4ng th\u1EC3 l\u01B0u c\xE0i \u0111\u1EB7t."
      }
    },
    team_table: {
      add_member: "Th\xEAm th\xE0nh vi\xEAn",
      create_button: "T\u1EA1o nh\xF3m",
      delete_team: "X\xF3a nh\xF3m",
      empty: "Kh\xF4ng c\xF3 nh\xF3m n\xE0o",
      empty_message: "Kh\xF4ng c\xF3 nh\xF3m n\xE0o",
      loading_message: "\u0110ang t\u1EA3i danh s\xE1ch nh\xF3m...",
      member_count: "Th\xE0nh vi\xEAn",
      name: "T\xEAn",
      project_count: "D\u1EF1 \xE1n",
      title: "Nh\xF3m",
      edit: "Qu\u1EA3n l\xFD nh\xF3m",
      edit_member: "Qu\u1EA3n l\xFD th\xE0nh vi\xEAn",
      expand_all: "M\u1EDF t\u1EA5t c\u1EA3",
      collapse_all: "G\u1EADp t\u1EA5t c\u1EA3"
    },
    title: "Th\xF4ng tin kh\xF4ng gian l\xE0m vi\u1EC7c",
    transfer_ownership: {
      title: "Chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu kh\xF4ng gian l\xE0m vi\u1EC7c",
      message: "Chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu s\u1EBD d\u1EABn \u0111\u1EBFn nh\u1EEFng thay \u0111\u1ED5i sau:",
      warning_list: {
        item1: "Th\xE0nh vi\xEAn \u0111\u01B0\u1EE3c ch\u1ECDn s\u1EBD tr\u1EDF th\xE0nh ch\u1EE7 s\u1EDF h\u1EEFu m\u1EDBi",
        item2: "Vai tr\xF2 c\u1EE7a b\u1EA1n s\u1EBD \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh Qu\u1EA3n tr\u1ECB vi\xEAn",
        item3: "\u0110\u1EC3 tr\u1EDF th\xE0nh ch\u1EE7 s\u1EDF h\u1EEFu l\u1EA1i, b\u1EA1n ph\u1EA3i y\xEAu c\u1EA7u t\u1EEB ch\u1EE7 s\u1EDF h\u1EEFu m\u1EDBi"
      },
      confirm_message: "Chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu cho {{name}}?",
      success: "\u0110\xE3 chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu th\xE0nh c\xF4ng",
      error: "Kh\xF4ng th\u1EC3 chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu",
      select_new_owner: "Ch\u1ECDn ch\u1EE7 s\u1EDF h\u1EEFu m\u1EDBi",
      confirm_button: "Chuy\u1EC3n quy\u1EC1n s\u1EDF h\u1EEFu"
    },
    warning: {
      delete_button: "X\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c",
      delete_description: "X\xF3a s\u1EBD lo\u1EA1i b\u1ECF t\u1EA5t c\u1EA3 d\u1EF1 \xE1n v\xE0 nhi\u1EC7m v\u1EE5.\nC\xE1c To-do c\u1EE7a th\xE0nh vi\xEAn v\u1EABn c\xF2n.",
      delete_description_1on1: "X\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c s\u1EBD thu h\u1ED3i quy\u1EC1n truy c\u1EADp c\u1EE7a t\u1EA5t c\u1EA3 th\xE0nh vi\xEAn v\xE0 khi\u1EBFn c\xE1c b\u1EA3n ghi 1on1 kh\xF4ng c\xF2n kh\u1EA3 d\u1EE5ng.",
      delete_title: "X\xF3a kh\xF4ng gian l\xE0m vi\u1EC7c",
      tooltip: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n x\xF3a kh\xF4ng? \u{1F97A}"
    }
  }
};

// src/locales/vi-VN/planner.json
var planner_default3 = {
  page: {
    title: "Planner"
  },
  item: {
    comment: "B\xECnh lu\u1EADn",
    note: "Ghi ch\xFA",
    description: "M\xF4 t\u1EA3"
  },
  calendar: {
    title: "L\u1ECBch"
  },
  todo: {
    selectTeam: "Vui l\xF2ng ch\u1ECDn m\u1ED9t \u0111\u1ED9i",
    markAsComplete: "\u0110\xE1nh d\u1EA5u l\xE0 ho\xE0n th\xE0nh",
    todayPlanned: "C\xE1c To-do h\xF4m nay",
    addTodo: "Th\xEAm To-do h\xF4m nay",
    adding: "\u0110ang th\xEAm...",
    setDate: "\u0110\u1EB7t ng\xE0y",
    incomplete: "C\xE1c To-do ch\u01B0a ho\xE0n th\xE0nh",
    updating: "\u0110ang c\u1EADp nh\u1EADt To-do",
    pleaseWait: "Vui l\xF2ng \u0111\u1EE3i...",
    updateFailed: "C\u1EADp nh\u1EADt To-do th\u1EA5t b\u1EA1i",
    updateError: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi c\u1EADp nh\u1EADt To-do.",
    updateComplete: "C\u1EADp nh\u1EADt To-do ho\xE0n th\xE0nh",
    addedToDate: "\u0110\xE3 th\xEAm To-do v\xE0o ng\xE0y \u0111\xE3 ch\u1ECDn."
  },
  timeBlock: {
    collapse: "Thu g\u1ECDn",
    expand: "M\u1EDF r\u1ED9ng",
    viewMore: "Xem th\xEAm {{count}}"
  },
  error: {
    loadFailed: "Kh\xF4ng th\u1EC3 t\u1EA3i k\u1EBF ho\u1EA1ch."
  },
  dragAndDrop: {
    notifications: {
      updateAction: "C\u1EADp nh\u1EADt To-do",
      timeUpdateSuccess: "Th\xF4ng tin th\u1EDDi gian c\u1EE7a To-do \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt th\xE0nh c\xF4ng.",
      timeChangeImpossible: "Kh\xF4ng th\u1EC3 thay \u0111\u1ED5i th\u1EDDi gian",
      timeSetImpossible: "Kh\xF4ng th\u1EC3 \u0111\u1EB7t th\u1EDDi gian",
      timeOccupied: "Khung gi\u1EDD n\xE0y \u0111\xE3 c\xF3 To-do kh\xE1c chi\u1EBFm d\u1EE5ng.",
      addToPlanner: "Th\xEAm To-do v\xE0o h\xF4m nay",
      addToPlannerSuccess: "To-do \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0o th\u1EDDi gian b\u1EA1n \u0111\xE3 ch\u1ECDn.",
      addToTodayList: "Th\xEAm To-do v\xE0o danh s\xE1ch h\xF4m nay",
      addToTodayListSuccess: "To-do \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0o danh s\xE1ch To-do c\u1EE7a h\xF4m nay.",
      changeGoal: "Thay \u0111\u1ED5i k\u1EBFt n\u1ED1i Nhi\u1EC7m v\u1EE5",
      changeGoalSuccess: "K\u1EBFt n\u1ED1i Nhi\u1EC7m v\u1EE5 \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i."
    }
  }
};

// src/locales/vi-VN/member_invitation.json
var member_invitation_default3 = {
  invitation_page: {
    already_responded: "B\u1EA1n \u0111\xE3 ph\u1EA3n h\u1ED3i l\u1EDDi m\u1EDDi n\xE0y.",
    current_account: "Hi\u1EC7n \u0111ang \u0111\u0103ng nh\u1EADp v\u1EDBi",
    expired_invitation: "M\u1EDDi n\xE0y \u0111\xE3 b\u1ECB h\u1EE7y b\u1ECF ho\u1EB7c kh\xF4ng t\u1ED3n t\u1EA1i.",
    go_home: "Tr\u1EDF v\u1EC1 Trang ch\u1EE7",
    home: "Trang ch\u1EE7",
    loading: "\u0110ang t\u1EA3i...",
    logout: "\u0110\u0103ng xu\u1EA5t"
  },
  invitation_response: {
    accept_completed: "\u0110\xE3 ch\u1EA5p nh\u1EADn l\u1EDDi m\u1EDDi",
    accept_failed: "Ch\u1EA5p nh\u1EADn l\u1EDDi m\u1EDDi th\u1EA5t b\u1EA1i",
    email_mismatch: "Email l\u1EDDi m\u1EDDi v\xE0 email \u0111\u0103ng nh\u1EADp kh\xF4ng kh\u1EDBp.",
    error_during_acceptance: "L\u1ED7i khi ch\u1EA5p nh\u1EADn l\u1EDDi m\u1EDDi:",
    error_occurred: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh x\u1EED l\xFD. Vui l\xF2ng l\xE0m m\u1EDBi trang v\xE0 th\u1EED l\u1EA1i.",
    joined_workspace: "B\u1EA1n \u0111\xE3 tham gia kh\xF4ng gian l\xE0m vi\u1EC7c b\u1EB1ng c\xE1ch ch\u1EA5p nh\u1EADn l\u1EDDi m\u1EDDi.",
    participation_failed: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 trong qu\xE1 tr\xECnh tham gia.",
    reject_completed: "M\u1EDDi t\u1EEB ch\u1ED1i",
    reject_failed: "Kh\xF4ng th\u1EC3 t\u1EEB ch\u1ED1i l\u1EDDi m\u1EDDi",
    rejected: "B\u1EA1n \u0111\xE3 t\u1EEB ch\u1ED1i l\u1EDDi m\u1EDDi tham gia kh\xF4ng gian l\xE0m vi\u1EC7c."
  },
  invite_results: {
    all_already_members: "T\u1EA5t c\u1EA3 email \u0111\u1EC1u \u0111\xE3 l\xE0 th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
    all_failed: "M\u1EDDi th\u1EA5t b\u1EA1i",
    all_failed_message: "T\u1EA5t c\u1EA3 l\u1EDDi m\u1EDDi \u0111\u1EC1u th\u1EA5t b\u1EA1i",
    all_sent: "\u0110\xE3 g\u1EEDi l\u1EDDi m\u1EDDi \u0111\u1EBFn {{count}} ng\u01B0\u1EDDi",
    already_invited: "L\u1EDDi m\u1EDDi \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn \u0111\u1ECBa ch\u1EC9 email n\xE0y.",
    already_member: "{{email}} \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\u0103ng k\xFD l\xE0 th\xE0nh vi\xEAn.",
    auth_required: "Y\xEAu c\u1EA7u x\xE1c th\u1EF1c.",
    copied: "\u0110\xE3 sao ch\xE9p!",
    copy: "Sao ch\xE9p",
    default_inviter: "Ng\u01B0\u1EDDi m\u1EDDi",
    email_failed_copy_link: "G\u1EEDi email th\u1EA5t b\u1EA1i. Vui l\xF2ng sao ch\xE9p li\xEAn k\u1EBFt b\xEAn d\u01B0\u1EDBi v\xE0 chia s\u1EBB tr\u1EF1c ti\u1EBFp.",
    email_required: "Vui l\xF2ng nh\u1EADp \u0111\u1ECBa ch\u1EC9 email",
    email_send_failed_see_link: "G\u1EEDi email th\u1EA5t b\u1EA1i. Vui l\xF2ng ki\u1EC3m tra li\xEAn k\u1EBFt b\xEAn d\u01B0\u1EDBi.",
    failed_message: "M\u1EDDi th\u1EA5t b\u1EA1i",
    invalid_email_error: "M\u1EDDi th\u1EA5t b\u1EA1i",
    invalid_email_message: "Vui l\xF2ng nh\u1EADp \u0111\u1ECBa ch\u1EC9 email h\u1EE3p l\u1EC7",
    invitation_link: "Li\xEAn k\u1EBFt m\u1EDDi",
    invitation_sent: "Email m\u1EDDi \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn {{email}}",
    invite_failure: "M\u1EDDi {{email}} th\u1EA5t b\u1EA1i: {{errorMessage}}",
    invite_success: "M\u1EDDi {{email}} th\xE0nh c\xF4ng",
    invite_waiting: "L\u1EDDi m\u1EDDi \u0111ang ch\u1EDD",
    member_invited: "\u0110\xE3 m\u1EDDi th\xE0nh vi\xEAn v\xE0o kh\xF4ng gian l\xE0m vi\u1EC7c",
    members_invited: "\u0110\xE3 m\u1EDDi {{count}} th\xE0nh vi\xEAn v\xE0o {{targetName}}",
    partial_failure: "M\u1ED9t s\u1ED1 l\u1EDDi m\u1EDDi th\u1EA5t b\u1EA1i",
    partial_result: "{{successCount}} l\u1EDDi m\u1EDDi th\xE0nh c\xF4ng, {{failCount}} th\u1EA5t b\u1EA1i",
    partial_success: "M\u1ED9t s\u1ED1 l\u1EDDi m\u1EDDi th\xE0nh c\xF4ng",
    processing: "\u0110ang x\u1EED l\xFD l\u1EDDi m\u1EDDi...",
    success: "M\u1EDDi th\xE0nh c\xF4ng",
    warning: "C\u1EA3nh b\xE1o"
  },
  member_invite_modal: {
    activate_description: "N\u1EBFu b\u1ECF ch\u1ECDn, th\xE0nh vi\xEAn s\u1EBD \u0111\u01B0\u1EE3c th\xEAm \u1EDF tr\u1EA1ng th\xE1i kh\xF4ng ho\u1EA1t \u0111\u1ED9ng (kh\xF4ng t\xEDnh ph\xED)",
    activate_on_accept: "M\u1EDDi v\u1EDBi tr\u1EA1ng th\xE1i ho\u1EA1t \u0111\u1ED9ng",
    additional_charge: "Ph\xED b\u1ED5 sung cho {{count}} ng\u01B0\u1EDDi: {{amount}} KRW",
    already_members_description: "{{count}} th\xE0nh vi\xEAn sau \u0111\xE2y \u0111\xE3 c\xF3 trong kh\xF4ng gian l\xE0m vi\u1EC7c v\xE0 s\u1EBD b\u1ECB lo\u1EA1i kh\u1ECFi l\u1EDDi m\u1EDDi",
    already_members_title: "\u0110\xE3 l\xE0 th\xE0nh vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c",
    cancel: "H\u1EE7y",
    email_description: "Ph\xE2n c\xE1ch nhi\u1EC1u email b\u1EB1ng d\u1EA5u ph\u1EA9y (,) \u0111\u1EC3 m\u1EDDi nhi\u1EC1u ng\u01B0\u1EDDi c\xF9ng l\xFAc.",
    email_input: "\u0110\u1ECBa ch\u1EC9 Email",
    email_placeholder: "example@mail.com, example@mail.com ...",
    emails_description: "Ph\xE2n c\xE1ch nhi\u1EC1u email b\u1EB1ng d\u1EA5u ph\u1EA9y (,) \u0111\u1EC3 m\u1EDDi nhi\u1EC1u ng\u01B0\u1EDDi c\xF9ng l\xFAc",
    emails_input: "\u0110\u1ECBa ch\u1EC9 Email",
    invalid_email: "\u0110\u1ECBa ch\u1EC9 email kh\xF4ng h\u1EE3p l\u1EC7: {{email}}",
    invite: "M\u1EDDi",
    invite_members: "M\u1EDDi th\xE0nh vi\xEAn v\xE0o {{targetName}}",
    no_charge: "Kh\xF4ng t\xEDnh ph\xED b\u1ED5 sung",
    payment_info: "Th\xF4ng tin thanh to\xE1n",
    preview_count: "\u0110ang m\u1EDDi {{count}} th\xE0nh vi\xEAn",
    preview_title: "Th\xE0nh vi\xEAn s\u1EBD \u0111\u01B0\u1EE3c m\u1EDDi",
    slots_used: "S\u1EED d\u1EE5ng {{count}} slot",
    assign_manager: "Ch\u1EC9 \u0111\u1ECBnh qu\u1EA3n l\xFD"
  },
  team: "Nh\xF3m",
  workspace: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
  workspace_invitation: {
    accept: "Ch\u1EA5p nh\u1EADn",
    invited_to: "B\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c m\u1EDDi v\xE0o kh\xF4ng gian l\xE0m vi\u1EC7c.",
    reject: "T\u1EEB ch\u1ED1i",
    role: "Vai tr\xF2",
    title: "L\u1EDDi m\u1EDDi Kh\xF4ng gian l\xE0m vi\u1EC7c"
  }
};

// src/locales/vi-VN/landing.json
var landing_default3 = {
  title_image: "/images/landing/title_text_en.png",
  macbook_image: "/images/landing/macbook_en.png",
  feature1: {
    title: "T\u1EA1o b\xE1o c\xE1o AI",
    description: "Ch\u1EA5m d\u1EE9t 'c\xF4ng vi\u1EC7c tr\xF9ng l\u1EB7p cho b\xE1o c\xE1o'!<br />AI ph\xE2n t\xEDch k\u1EBF ho\u1EA1ch c\xF4ng vi\u1EC7c v\xE0 d\u1EEF li\u1EC7u nhi\u1EC7m v\u1EE5 \u0111\u1EC3 t\u1EF1 \u0111\u1ED9ng t\u1EA1o b\xE1o c\xE1o.",
    imageSrc: "/images/landing/contents_en/content_1.png",
    imageAlt: "T\u1EA1o b\xE1o c\xE1o AI"
  },
  feature2: {
    title: "Qu\u1EA3n l\xFD d\u1EEF li\u1EC7u/c\xF4ng vi\u1EC7c k\u1EBFt n\u1ED1i v\u1EDBi m\u1EE5c ti\xEAu t\u1ED5 ch\u1EE9c",
    description: "Thi\u1EBFt k\u1EBF v\xE0 k\u1EBFt n\u1ED1i c\xF4ng vi\u1EC7c xoay quanh m\u1EE5c ti\xEAu c\u1EE7a nh\xF3m \u0111\u1EC3 xem m\u1ECDi ng\u01B0\u1EDDi \u0111\xF3ng g\xF3p nh\u01B0 th\u1EBF n\xE0o theo th\u1EDFi gian th\u1EF1c. T\u1EA1o h\u1EC7 th\u1ED1ng trong Orblit n\u01A1i m\u1EE5c ti\xEAu nh\xF3m CASCADE xu\u1ED1ng v\xE0 c\xE1c th\xE0nh vi\xEAn c\xF3 th\u1EC3 \u0111\xF3ng g\xF3p T\u1EF0 DO.",
    imageSrc: "/images/landing/contents_en/content_2.png",
    imageAlt: "Qu\u1EA3n l\xFD d\u1EEF li\u1EC7u/c\xF4ng vi\u1EC7c k\u1EBFt n\u1ED1i v\u1EDBi m\u1EE5c ti\xEAu t\u1ED5 ch\u1EE9c"
  },
  feature3: {
    title: "T\xEDn d\u1EE5ng & Ph\u1EA3n h\u1ED3i",
    description: "Ph\u1EA3n h\u1ED3i 'k\u1ECBp th\u1EDFi' v\xE0 'c\u1EE5 th\u1EC3' d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n.<br />X\xE2y d\u1EF1ng h\u1EC7 th\u1ED1ng trao \u0111\u1ED5i ph\u1EA3n h\u1ED3i nhanh ch\xF3ng v\xE0 r\xF5 r\xE0ng, kh\xF4ng ph\u1EA3i ph\u1EA3n h\u1ED3i m\u01A1 h\u1ED3 v\xE0 chung chung.",
    imageSrc: "/images/landing/contents_en/content_3.png",
    imageAlt: "T\xEDn d\u1EE5ng & Ph\u1EA3n h\u1ED3i"
  },
  contact: "Xem gi\u1EDBi thi\u1EC7u",
  start: "B\u1EAFt \u0111\u1EA7u mi\u1EC5n ph\xED",
  announcement: {
    title: "H\u1ED7 tr\u1EE3 ch\xEDnh ph\u1EE7 l\xEAn \u0111\u1EBFn 80% cho phi\u1EBFu \u0111i\u1EC7n to\xE1n \u0111\xE1m m\xE2y",
    button: "Li\xEAn h\u1EC7 ch\xFAng t\xF4i"
  },
  hero: "ORBLIT: Data-driven HR linking \u2018team goals and individual tasks\u2019",
  cta: {
    first: "Ngay b\xE2y gi\u1EDD,",
    second: "v\u1EBD qu\u1EF9 \u0111\u1EA1o ri\xEAng c\u1EE7a b\u1EA1n."
  },
  company: {
    name: "C\xF4ng ty Reversemountain",
    address: "Seoul Startup Hub M Plus, S\u1ED1 14, \u0111\u01B0\u1EDDng Magokjungang 8-ro, qu\u1EADn Gangseo, Seoul, H\xE0n Qu\u1ED1c",
    representative: "Gi\xE1m \u0111\u1ED1c Gyeongmin Kim"
  },
  onelanding: {
    title: "1on1",
    move: "Move"
  },
  buttons: {
    useService: "Use Service",
    requestDemo: "Request Demo"
  },
  heroSection: {
    mainTitle: {
      line1: "T\u1EA1m bi\u1EC7t vi\u1EC7c chu\u1EA9n b\u1ECB v\xE0 t\u1ED5 ch\u1EE9c 1on1 l\xE3ng ph\xED,",
      line2: "v\xE0 nh\u1EEFng bu\u1ED5i 1on1 kh\xF4ng d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n."
    },
    app_mainTitle: {
      line1: "Vi\u1EC7c chu\u1EA9n b\u1ECB v\xE0 t\u1ED5ng k\u1EBFt 1on1 t\u1ED1n s\u1EE9c,",
      line2: "nh\u1EEFng bu\u1ED5i 1on1 xa r\u1EDDi qu\u1EA3n l\xFD/ph\xE1t tri\u1EC3n,",
      line3: "h\xE3y n\xF3i l\u1EDDi t\u1EA1m bi\u1EC7t."
    },
    subTitle: {
      line1: "\u0110\u1EEBng lo l\u1EAFng v\u1EC1 vi\u1EC7c ghi ch\xE9p, h\xE3y t\u1EADp trung tho\u1EA3i m\xE1i v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n.",
      line2: "Ch\xFAng t\xF4i s\u1EBD t\xF3m t\u1EAFt cu\u1ED9c \u0111\u1ED1i tho\u1EA1i t\u1EADp trung v\xE0o c\xE2u h\u1ECFi v\xE0 th\u1EADm ch\xED cung c\u1EA5p c\xE1c m\u1EE5c h\xE0nh \u0111\u1ED9ng!"
    },
    app_subTitle: {
      line1: "\u0110\u1EEBng lo l\u1EAFng v\u1EC1 vi\u1EC7c ghi ch\xE9p, h\xE3y t\u1EADp trung tho\u1EA3i m\xE1i v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n.",
      line2: "Ch\xFAng t\xF4i s\u1EBD t\xF3m t\u1EAFt cu\u1ED9c \u0111\u1ED1i tho\u1EA1i t\u1EADp trung v\xE0o c\xE2u h\u1ECFi",
      line3: "v\xE0 th\u1EADm ch\xED cung c\u1EA5p c\xE1c m\u1EE5c h\xE0nh \u0111\u1ED9ng!"
    }
  },
  valueProposition: "Value Proposition",
  features: {
    feature1: {
      title: "Ph\u1EA3n h\u1ED3i hu\u1EA5n luy\u1EC7n AI cho l\xE3nh \u0111\u1EA1o",
      description: [
        "B\u1EA1n c\xF3 mu\u1ED1n tr\u1EDF th\xE0nh m\u1ED9t nh\xE0 l\xE3nh \u0111\u1EA1o t\u1ED1t h\u01A1n t\u1EEBng b\u01B0\u1EDBc m\u1ED9t kh\xF4ng?",
        "Ch\xFAng t\xF4i s\u1EBD ph\xE2n t\xEDch t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u, n\u1ED9i dung, gi\u1ECDng \u0111i\u1EC7u v\xE0 nhi\u1EC1u h\u01A1n n\u1EEFa \u0111\u1EC3 cung c\u1EA5p c\xE1c ph\u01B0\u01A1ng ph\xE1p c\u1EA3i thi\u1EC7n c\xF3 th\u1EC3 th\u1EF1c hi\u1EC7n!"
      ],
      app_description: [
        "\u0110\u1EEBng \u0111\u1EC3 cu\u1ED9c tr\xF2 chuy\u1EC7n l\u1EB7p \u0111i l\u1EB7p l\u1EA1i, h\xE3y ti\u1EBFn h\xE0nh 1:1 ph\xF9 h\u1EE3p v\u1EDBi t\u1EEBng ng\u01B0\u1EDDi!",
        "Ph\u1EA3n \xE1nh l\u1ECBch h\u1ECDp, t\xECnh hu\u1ED1ng v\xE0 ch\u1EE7 \u0111\u1EC1 m\xE0 h\u1ECD mong mu\u1ED1n,",
        "Ch\xFAng t\xF4i s\u1EBD t\u1EA1o h\u01B0\u1EDBng d\u1EABn v\xE0 b\u1ED9 c\xE2u h\u1ECFi t\xF9y ch\u1EC9nh cho b\u1EA1n!"
      ]
    },
    feature2: {
      title: "T\u1EA1o c\xE2u h\u1ECFi t\xF9y ch\u1EC9nh b\u1EB1ng AI",
      description: [
        "AI s\u1EBD t\u1EA1o ra nh\u1EEFng c\xE2u h\u1ECFi t\xF9y ch\u1EC9nh cho b\u1EA1n!",
        "Ch\xFAng t\xF4i s\u1EBD t\u1EA1o h\u01B0\u1EDBng d\u1EABn v\xE0 b\u1EA3ng c\xE2u h\u1ECFi t\xF9y ch\u1EC9nh d\u1EF1a tr\xEAn h\u1ED3 s\u01A1 cu\u1ED9c h\u1ECDp, t\xECnh hu\u1ED1ng v\xE0 ch\u1EE7 \u0111\u1EC1 mong mu\u1ED1n!"
      ],
      app_description: [
        "Thay v\xEC nh\u1EEFng cu\u1ED9c tr\xF2 chuy\u1EC7n l\u1EB7p \u0111i l\u1EB7p l\u1EA1i, h\xE3y ti\u1EBFn h\xE0nh 1on1 ph\xF9 h\u1EE3p v\u1EDBi t\u1EEBng c\xE1 nh\xE2n!",
        "Ch\xFAng t\xF4i s\u1EBD t\u1EA1o h\u01B0\u1EDBng d\u1EABn v\xE0 b\u1EA3ng c\xE2u h\u1ECFi t\xF9y ch\u1EC9nh ph\u1EA3n \xE1nh",
        "h\u1ED3 s\u01A1 cu\u1ED9c h\u1ECDp, t\xECnh hu\u1ED1ng v\xE0 ch\u1EE7 \u0111\u1EC1 mong mu\u1ED1n c\u1EE7a \u0111\u1ED1i ph\u01B0\u01A1ng!"
      ]
    },
    feature3: {
      title: "B\xE1o c\xE1o ph\xE2n t\xEDch AI v\xE0 b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box",
      description: [
        "Qu\u1EA3n l\xFD chi\u1EBFn l\u01B0\u1EE3c c\xE1c c\u01A1 h\u1ED9i t\u0103ng tr\u01B0\u1EDFng v\xE0 r\u1EE7i ro \u1EA9n!",
        "Ch\xFAng t\xF4i s\u1EBD cung c\u1EA5p b\xE1o c\xE1o ph\xE2n t\xEDch ch\u1EE9a \u0111\u1ED9ng l\u1EF1c n\u1ED9i t\u1EA1i, \u0111i\u1EC3m m\u1EA1nh v\xE0 \u0111i\u1EC3m y\u1EBFu c\u1EE7a th\xE0nh vi\xEAn,",
        "v\xE0 b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box \u0111\u1EC3 qu\u1EA3n l\xFD t\u0103ng tr\u01B0\u1EDFng!"
      ],
      app_description: [
        "Qu\u1EA3n l\xFD chi\u1EBFn l\u01B0\u1EE3c c\xE1c c\u01A1 h\u1ED9i t\u0103ng tr\u01B0\u1EDFng v\xE0 r\u1EE7i ro ti\u1EC1m \u1EA9n!",
        "Ch\xFAng t\xF4i s\u1EBD cung c\u1EA5p b\xE1o c\xE1o ph\xE2n t\xEDch ch\u1EE9a \u0111\u1ED9ng l\u1EF1c n\u1ED9i t\u1EA1i, \u0111i\u1EC3m m\u1EA1nh v\xE0 \u0111i\u1EC3m y\u1EBFu c\u1EE7a c\xE1c th\xE0nh vi\xEAn,",
        "v\xE0 b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box \u0111\u1EC3 qu\u1EA3n l\xFD t\u0103ng tr\u01B0\u1EDFng!"
      ]
    }
  },
  bottomSection: {
    item1: {
      line1: "Cu\u1ED9c h\u1ECDp 1on1 tr\u01B0\u1EDBc v\xE0 sau",
      highlight1: "",
      line2: "",
      highlight2: "t\u1EF1 \u0111\u1ED9ng h\xF3a chu\u1EA9n b\u1ECB/t\u1ED5 ch\u1EE9c"
    },
    item2: {
      line1: "T\u1EA1o ra nh\u1EEFng nh\xE0 l\xE3nh \u0111\u1EA1o t\u1ED1t h\u01A1n v\u1EDBi",
      line2: "",
      highlight: "hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o AI t\xF9y ch\u1EC9nh"
    },
    item3: {
      line1: "Ph\xE2n t\xEDch d\u1EEF li\u1EC7u 1on1 \u0111\u1EC3",
      line2: "cung c\u1EA5p",
      highlight: "b\xE1o c\xE1o 9Box v\xE0 th\xE0nh vi\xEAn nh\xF3m"
    }
  },
  footer: {
    companyName: "Reverse Mountain Co., Ltd.",
    address: "Seoul Startup Hub M Plus, S\u1ED1 14, \u0111\u01B0\u1EDDng Magokjungang 8-ro, qu\u1EADn Gangseo, Seoul, H\xE0n Qu\u1ED1c",
    representative: "Gi\xE1m \u0111\u1ED1c Gyeongmin Kim",
    email: "gmkim@reversemountain.co.kr",
    copyright: "2025 REVERSEMOUNTAIN ALL RIGHTS RESERVED."
  },
  card: {
    card1: {
      text_normal: "Tr\u01B0\u1EDBc v\xE0 sau c\xE1c cu\u1ED9c h\u1ECDp 1on1 ",
      text_bold: "T\u1EF1 \u0111\u1ED9ng h\xF3a c\xF4ng vi\u1EC7c chu\u1EA9n b\u1ECB/t\u1ED5 ch\u1EE9c"
    },
    card2: {
      text_normal: "T\u1EA1o ra nh\u1EEFng nh\xE0 l\xE3nh \u0111\u1EA1o t\u1ED1t h\u01A1n v\u1EDBi ",
      text_bold: "Hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o AI c\xE1 nh\xE2n h\xF3a"
    },
    card3: {
      text_normal1: "Ph\xE2n t\xEDch d\u1EEF li\u1EC7u 1on1 \u0111\u1EC3",
      text_normal2: "cung c\u1EA5p ",
      text_bold: "B\xE1o c\xE1o 9-box v\xE0 th\xE0nh vi\xEAn nh\xF3m"
    }
  },
  landingMerged: {
    title: "T\u0103ng m\u1EADt \u0111\u1ED9 t\xE0i n\u0103ng",
    subtitle: "Gi\u1EA3i ph\xE1p ph\u1EA3n h\u1ED3i AI v\xE0 cu\u1ED9c h\u1ECDp 1on1",
    leadershipText: {
      line1: "Ti\u1EBFt ki\u1EC7m ",
      line2: "th\u1EDDi gian",
      line3: " c\u1EE7a l\xE3nh \u0111\u1EA1o, v\xE0 ",
      line4: "t\u1ED1i \u0111a h\xF3a s\u1EF1 ph\xE1t tri\u1EC3n v\xE0 g\u1EAFn k\u1EBFt",
      line5: "."
    },
    card: {
      title1: "Orblit 1on1",
      description1: [
        "T\u1EA1m bi\u1EC7t vi\u1EC7c chu\u1EA9n b\u1ECB v\xE0 t\u1ED5 ch\u1EE9c 1on1 t\u1ED1n th\u1EDFi gian,",
        "v\xE0 c\xE1c cu\u1ED9c h\u1ECDp 1on1 kh\xF4ng d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n."
      ],
      title2: "Orblit Works",
      description2: [
        "Tho\xE1t kh\u1ECFi s\u1EF1 k\xE9m hi\u1EC7u qu\u1EA3 do tr\u1EA1ng th\xE1i c\xF4ng vi\u1EC7c kh\xF4ng r\xF5 r\xE0ng,",
        "vi\u1EBFt b\xE1o c\xE1o t\u1ED1n th\u1EDFi gian, v\xE0 ph\u1EA3n h\u1ED3i",
        "m\u01A1 h\u1ED3 v\xE0 h\xECnh th\u1EE9c"
      ],
      title3: "Orblit Feedback",
      description3: [
        "Tho\xE1t kh\u1ECFi c\xE1c \u0111\xE1nh gi\xE1 h\xECnh th\u1EE9c, m\u1ED9t l\u1EA7n",
        "v\xE0 ph\u1EA3n h\u1ED3i kh\xF4ng d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n."
      ]
    },
    benefits: {
      title: "L\u1EE3i \xEDch ",
      highlight: "ngay l\u1EADp t\u1EE9c ",
      suffix: "c\u1EA3m nh\u1EADn \u0111\u01B0\u1EE3c sau khi tri\u1EC3n khai"
    },
    carousel: {
      card1: {
        title: "T\u1EF1 \u0111\u1ED9ng h\xF3a c\xF4ng vi\u1EC7c t\xE0i li\u1EC7u li\xEAn quan \u0111\u1EBFn 1on1",
        description1: "Gi\u1EA3m 20 gi\u1EDD m\u1ED7i th\xE1ng cho c\xF4ng vi\u1EC7c t\xE0i li\u1EC7u qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t",
        description2: [
          "Tr\u01B0\u1EDFng nh\xF3m qu\u1EA3n l\xFD 8 th\xE0nh vi\xEAn",
          "t\u1EA1i m\u1ED9t c\xF4ng ty v\u1EEBa"
        ],
        description3: [
          "B\xE1o c\xE1o \u0111\u01B0\u1EE3c ho\xE0n th\xE0nh t\u1EF1 \u0111\u1ED9ng sau c\xE1c cu\u1ED9c h\u1ECDp 1on1.",
          "T\xF4i \u0111\u1EC3 c\xF4ng vi\u1EC7c t\xE0i li\u1EC7u cho Orblit",
          "v\xE0 c\xF3 th\u1EC3 t\u1EADp trung h\u01A1n v\xE0o ch\xEDnh c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
          "S\u1EF1 h\xE0i l\xF2ng c\u1EE7a th\xE0nh vi\xEAn nh\xF3m c\u0169ng \u0111\xE3 t\u0103ng l\xEAn."
        ]
      },
      card2: {
        title: "T\u0103ng c\u01B0\u1EDDng kh\u1EA3 n\u0103ng qu\u1EA3n l\xFD l\xE3nh \u0111\u1EA1o",
        description1: "T\u0103ng 127% s\u1EF1 h\xE0i l\xF2ng v\u1EC1 qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t nh\xE2n vi\xEAn sau khi s\u1EED d\u1EE5ng Orblit",
        description2: [
          "Gi\xE1m \u0111\u1ED1c \u0111i\u1EC1u h\xE0nh c\u1EE7a m\u1ED9t startup Series A",
          "v\u1EDBi 120 nh\xE2n vi\xEAn"
        ],
        description3: [
          "Kh\u1EA3 n\u0103ng qu\u1EA3n l\xFD l\xE3nh \u0111\u1EA1o lu\xF4n l\xE0 m\u1ED9t H\u1ED9p \u0110en.",
          "T\xF4i lu\xF4n lo l\u1EAFng v\xEC t\u1ED5n th\u1EA5t c\xF3 th\u1EC3 \u0111\xE1ng k\u1EC3 n\u1EBFu m\u1ECDi th\u1EE9 di\u1EC5n ra kh\xF4ng t\u1ED1t.",
          "Nh\u01B0ng b\xE2y gi\u1EDD Orblit hu\u1EA5n luy\u1EC7n c\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o, v\xE0 t\xF4i c\xF3 th\u1EC3",
          "hi\u1EC3u \u0111\u01B0\u1EE3c t\xECnh h\xECnh c\u1EE7a c\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o."
        ]
      },
      card3: {
        title: "Hi\u1EC7n \u0111\u1EA1i h\xF3a v\u0103n h\xF3a qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t",
        description1: "Hi\u1EC7n \u0111\u1EA1i h\xF3a v\u0103n h\xF3a qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t t\u1ED5 ch\u1EE9c bao g\u1ED3m tri\u1EC3n khai cu\u1ED9c h\u1ECDp 1on1",
        description2: [],
        description3: [
          "Gi\u1EA5y, CNTT, H\xF3a ch\u1EA5t, X\xE2y d\u1EF1ng / 5.700 nh\xE2n vi\xEAn / Doanh nghi\u1EC7p l\u1EDBn",
          "Ti\u1EBFn h\xE0nh \u0111\u1ED5i m\u1EDBi m\u1EDF v\xE0 thi\u1EBFt k\u1EBF m\u1EDF r\u1ED9ng v\u1EDBi \u0111\u1ED9i ng\u0169 chi\u1EBFn l\u01B0\u1EE3c"
        ]
      },
      card4: {
        title: "Tri\u1EC3n khai qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u",
        description1: "Thu th\u1EADp d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c c\u1EE7a nh\xE2n vi\xEAn v\xE0 tri\u1EC3n khai qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t s\u1EED d\u1EE5ng n\xF3",
        description2: [],
        description3: [
          "S\u1EA3n xu\u1EA5t nh\xF4m / 4.000 nh\xE2n vi\xEAn / H\xE0n Qu\u1ED1c, Vi\u1EC7t Nam",
          "Ti\u1EBFn h\xE0nh PoC c\xF3 tr\u1EA3 ph\xED v\u1EDBi b\u1ED9 ph\u1EADn k\u1EBF ho\u1EA1ch, nh\xE2n s\u1EF1 v\xE0 t\u1ED5ng v\u1EE5 t\u1EA1i Vi\u1EC7t Nam"
        ]
      }
    }
  },
  landingFB: {
    title: [
      "\u0110\xE1nh gi\xE1 h\xECnh th\u1EE9c v\xE0 m\u1ED9t l\u1EA7n",
      "Tho\xE1t kh\u1ECFi ph\u1EA3n h\u1ED3i kh\xF4ng d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n."
    ],
    subtitle: [
      "\u0110\xE1nh gi\xE1 h\xECnh th\u1EE9c v\xE0 m\u1ED9t l\u1EA7n,",
      "kh\xF4ng d\u1EABn \u0111\u1EBFn",
      "s\u1EF1 ph\xE1t tri\u1EC3n."
    ],
    subtitle_app: [
      "D\u1EEF li\u1EC7u c\u1EE5 th\u1EC3 \u1EDF d\u1EA1ng d\u1EC5 ch\u1EA5p nh\u1EADn nh\u1EA5t.",
      "Hu\u1EA5n luy\u1EC7n vi\xEAn ph\u1EA3n h\u1ED3i AI s\u1EBD h\u01B0\u1EDBng d\u1EABn b\u1EA1n \u0111\u1EBFn ph\u1EA3n h\u1ED3i t\u1ED1i \u01B0u!"
    ],
    section: {
      title1: "Ch\u1EA5m \u0111i\u1EC3m v\xE0 t\u1ED1i \u01B0u h\xF3a ph\u1EA3n h\u1ED3i",
      description1: [
        "H\xE3y b\u1ECF \u0111i g\xE1nh n\u1EB7ng ph\u1EA3i t\u1EF1 vi\u1EBFt ph\u1EA3n h\u1ED3i ho\xE0n h\u1EA3o.",
        "Ch\xFAng t\xF4i s\u1EBD x\xE1c \u0111\u1ECBnh c\xE1c l\u0129nh v\u1EF1c c\u1EA7n c\u1EA3i thi\u1EC7n trong b\u1EA3n nh\xE1p c\u1EE7a b\u1EA1n v\xE0 t\u1ED1i \u01B0u h\xF3a bi\u1EC3u \u0111\u1EA1t theo t\xEDnh c\xE1ch MBTI c\u1EE7a nh\xE2n vi\xEAn!"
      ],
      app_description1: [
        "H\xE3y b\u1ECF \u0111i g\xE1nh n\u1EB7ng ph\u1EA3i t\u1EF1 vi\u1EBFt ph\u1EA3n h\u1ED3i ho\xE0n h\u1EA3o.",
        "Ch\xFAng t\xF4i s\u1EBD x\xE1c \u0111\u1ECBnh c\xE1c l\u0129nh v\u1EF1c c\u1EA7n c\u1EA3i thi\u1EC7n trong b\u1EA3n nh\xE1p c\u1EE7a b\u1EA1n v\xE0 t\u1ED1i \u01B0u h\xF3a bi\u1EC3u \u0111\u1EA1t",
        "theo t\xEDnh c\xE1ch MBTI c\u1EE7a nh\xE2n vi\xEAn!"
      ],
      title2: "K\u1EBFt n\u1ED1i d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c v\xE0 \u0111\xE1nh gi\xE1 \u0111\xE8n giao th\xF4ng",
      description2: [
        "Ph\u1EA3n h\u1ED3i r\xF5 r\xE0ng v\xE0 c\u1EE5 th\u1EC3 d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n.",
        "K\u1EBFt n\u1ED1i d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c v\u1EDBi ph\u1EA3n h\u1ED3i v\xE0 duy tr\xEC s\u1EF1 \u0111\u1ED3ng thu\u1EADn v\u1EC1 hi\u1EC7u su\u1EA5t."
      ],
      app_description2: [
        "Ph\u1EA3n h\u1ED3i r\xF5 r\xE0ng v\xE0 c\u1EE5 th\u1EC3 d\u1EABn \u0111\u1EBFn s\u1EF1 ph\xE1t tri\u1EC3n.",
        "K\u1EBFt n\u1ED1i d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c v\u1EDBi ph\u1EA3n h\u1ED3i,",
        "v\xE0 duy tr\xEC s\u1EF1 \u0111\u1ED3ng thu\u1EADn v\u1EC1 hi\u1EC7u su\u1EA5t."
      ]
    },
    card: {
      card1: {
        text_bold1: "B\u1ED5 sung \u0111i\u1EC3m y\u1EBFu c\u1EE7a t\u1EEBng l\xE3nh \u0111\u1EA1o \u0111\u1EC3",
        text_bold2: "cung c\u1EA5p ph\u1EA3n h\u1ED3i ch\u1EA5t l\u01B0\u1EE3ng cao",
        text_normal: "c\xF3 th\u1EC3"
      },
      card2: {
        text_normal1: "H\u1EC7 th\u1ED1ng \u0111\xE1nh gi\xE1 \u0111\xE8n giao th\xF4ng",
        text_normal2: "theo nhi\u1EC7m v\u1EE5 \u0111\u1EC3 duy tr\xEC",
        text_normal3: "s\u1EF1 \u0111\u1ED3ng thu\u1EADn v\u1EC1 hi\u1EC7u su\u1EA5t gi\u1EEFa",
        text_bold: " tr\u01B0\u1EDFng nh\xF3m v\xE0 th\xE0nh vi\xEAn nh\xF3m"
      },
      card3: {
        text_normal: "T\u1ED1i \u01B0u h\xF3a ph\u1EA3n h\u1ED3i t\xF9y ch\u1EC9nh MBTI \u0111\u1EC3",
        text_bold: "t\u1ED1i \u0111a h\xF3a s\u1EF1 ch\u1EA5p nh\u1EADn ph\u1EA3n h\u1ED3i"
      }
    }
  },
  landingWorks: {
    title: [
      "S\u1EF1 kh\xF4ng hi\u1EC7u qu\u1EA3 t\u1EEB tr\u1EA1ng th\xE1i c\xF4ng vi\u1EC7c kh\xF4ng r\xF5 r\xE0ng,",
      "Tho\xE1t kh\u1ECFi vi\u1EC7c vi\u1EBFt b\xE1o c\xE1o t\u1EBB nh\u1EA1t v\xE0 ph\u1EA3n h\u1ED3i m\u01A1 h\u1ED3."
    ],
    title_app: [
      "Tho\xE1t kh\u1ECFi s\u1EF1 k\xE9m hi\u1EC7u qu\u1EA3 do t\xECnh tr\u1EA1ng c\xF4ng vi\u1EC7c kh\xF4ng r\xF5 r\xE0ng,",
      "vi\u1EC7c vi\u1EBFt b\xE1o c\xE1o t\u1ED1n th\u1EDFi gian,",
      "v\xE0 nh\u1EEFng ph\u1EA3n h\u1ED3i m\u01A1 h\u1ED3."
    ],
    subtitle: [
      "'3 ph\xFAt m\u1ED7i ng\xE0y', t\u0103ng c\u01B0\u1EDDng hi\u1EC7p l\u1EF1c c\u1ED9ng t\xE1c v\u1EDBi scrum h\xE0ng ng\xE0y!",
      "T\u1EEB b\xE1o c\xE1o m\u1ED9t nh\u1EA5p chu\u1ED9t \u0111\u1EBFn ph\u1EA3n h\u1ED3i t\u1EA1o \u0111\u1ED9ng l\u1EF1c, ch\xFAng t\xF4i s\u1EBD gi\xFAp b\u1EA1n!"
    ],
    subtitle_app: [
      "'3 ph\xFAt m\u1ED7i ng\xE0y', t\u0103ng c\u01B0\u1EDDng hi\u1EC7p l\u1EF1c c\u1ED9ng t\xE1c v\u1EDBi scrum h\xE0ng ng\xE0y!",
      "T\u1EEB b\xE1o c\xE1o m\u1ED9t nh\u1EA5p chu\u1ED9t",
      "\u0111\u1EBFn ph\u1EA3n h\u1ED3i t\u1EA1o \u0111\u1ED9ng l\u1EF1c, ch\xFAng t\xF4i s\u1EBD gi\xFAp b\u1EA1n!"
    ],
    section: {
      title1: "Chia s\u1EBB \u0111\xF3ng g\xF3p theo th\u1EDFi gian th\u1EF1c",
      description1: [
        "Qu\u1EA3n l\xFD nhi\u1EC7m v\u1EE5 k\u1EBFt n\u1ED1i v\u1EDBi m\u1EE5c ti\xEAu nh\xF3m v\xE0 chia s\u1EBB theo th\u1EDFi gian th\u1EF1c.",
        "B\u1EA1n c\xF3 th\u1EC3 ti\u1EBFn h\xE0nh scrum h\xE0ng ng\xE0y k\u1EF9 l\u01B0\u1EE1ng v\xE0 d\u1EC5 d\xE0ng v\u1EDBi AI!"
      ],
      app_description1: [
        "Qu\u1EA3n l\xFD nhi\u1EC7m v\u1EE5 k\u1EBFt n\u1ED1i v\u1EDBi m\u1EE5c ti\xEAu nh\xF3m v\xE0 chia s\u1EBB theo th\u1EDFi gian th\u1EF1c.",
        "B\u1EA1n c\xF3 th\u1EC3 ti\u1EBFn h\xE0nh scrum h\xE0ng ng\xE0y k\u1EF9 l\u01B0\u1EE1ng v\xE0 d\u1EC5 d\xE0ng v\u1EDBi AI!"
      ],
      title2: "T\u1EF1 \u0111\u1ED9ng t\u1EA1o b\xE1o c\xE1o b\u1EB1ng AI",
      description2: [
        "T\u1EA1o b\xE1o c\xE1o t\u1EBB nh\u1EA1t ch\u1EC9 v\u1EDBi m\u1ED9t c\xFA nh\u1EA5p chu\u1ED9t!",
        "AI t\u1EA1o b\u1EA3n nh\xE1p b\xE1o c\xE1o b\u1EB1ng c\xE1ch k\u1EBFt h\u1EE3p tr\u1EA1ng th\xE1i c\xF4ng vi\u1EC7c, nh\u1EADt k\xFD giao ti\u1EBFp v\xE0 d\u1EEF li\u1EC7u t\xE0i li\u1EC7u."
      ],
      app_description2: [
        "T\u1EA1o b\xE1o c\xE1o t\u1EBB nh\u1EA1t ch\u1EC9 v\u1EDBi m\u1ED9t c\xFA nh\u1EA5p chu\u1ED9t!",
        "AI t\u1EA1o b\u1EA3n nh\xE1p b\xE1o c\xE1o b\u1EB1ng c\xE1ch k\u1EBFt h\u1EE3p tr\u1EA1ng th\xE1i c\xF4ng vi\u1EC7c,",
        "nh\u1EADt k\xFD giao ti\u1EBFp v\xE0 d\u1EEF li\u1EC7u t\xE0i li\u1EC7u."
      ],
      title3: "T\u1ED1i \u01B0u h\xF3a ph\u1EA3n h\u1ED3i AI",
      description3: [
        "\u0110\u01B0a ra v\xE0 nh\u1EADn ph\u1EA3n h\u1ED3i c\u1EE5 th\u1EC3 d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c.",
        "B\u1EA1n c\u0169ng c\xF3 th\u1EC3 n\xE2ng cao n\u1ED9i dung v\xE0 t\u1ED1i \u01B0u h\xF3a bi\u1EC3u \u0111\u1EA1t v\u1EDBi AI!"
      ],
      app_description3: [
        "\u0110\u01B0a ra v\xE0 nh\u1EADn ph\u1EA3n h\u1ED3i c\u1EE5 th\u1EC3 d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c.",
        "B\u1EA1n c\u0169ng c\xF3 th\u1EC3 n\xE2ng cao n\u1ED9i dung v\xE0 t\u1ED1i \u01B0u h\xF3a bi\u1EC3u \u0111\u1EA1t v\u1EDBi AI!"
      ]
    },
    card: {
      card1: {
        text_bold1: "Chia s\u1EBB tr\u1EA1ng th\xE1i c\xF4ng vi\u1EC7c minh b\u1EA1ch",
        text_normal1: "\u0111\u1EC3",
        text_normal2: "c\u1EA3i thi\u1EC7n hi\u1EC7p l\u1EF1c nh\xF3m"
      },
      card2: {
        text_normal: "Ch\u1EC9 v\u1EDBi 3 ph\xFAt ghi ch\xE9p h\xE0ng ng\xE0y,",
        text_bold: "t\u1EF1 \u0111\u1ED9ng h\xF3a c\xE1c t\xE1c v\u1EE5 t\u1EA1o b\xE1o c\xE1o"
      },
      card3: {
        text_normal: "C\u1EA3i thi\u1EC7n hi\u1EC7p l\u1EF1c nh\xF3m v\u1EDBi",
        text_bold: "ph\u1EA3n h\u1ED3i AI li\xEAn k\u1EBFt d\u1EEF li\u1EC7u c\xF4ng vi\u1EC7c"
      }
    }
  },
  price: {
    title: "Ch\xFAng t\xF4i cung c\u1EA5p c\xE1c g\xF3i gi\xE1 SaaS h\u1EE3p l\xFD<br />v\xE0 PoC, tri\u1EC3n khai, \u0111\xE0o t\u1EA1o t\xF9y ch\u1EC9nh.",
    apptitle: "Ch\xFAng t\xF4i cung c\u1EA5p c\xE1c g\xF3i gi\xE1 SaaS h\u1EE3p l\xFD<br />v\xE0 PoC, tri\u1EC3n khai, \u0111\xE0o t\u1EA1o<br />t\xF9y ch\u1EC9nh.",
    contacts: "\u0110\u1ED1i v\u1EDBi tri\u1EC3n khai t\u1EA1i ch\u1ED7, PoC t\xF9y ch\u1EC9nh, y\xEAu c\u1EA7u \u0111\xE0o t\u1EA1o/h\u1ED9i th\u1EA3o<br />jjchoi@reversemountain.co.kr",
    badge: "G\xF3i Doanh nghi\u1EC7p",
    workspaceBilling: "T\xEDnh ph\xED theo workspace",
    cards: [
      {
        id: "feedback",
        title: "Orblit Feedback",
        price: 6e3,
        unit: "m\u1ED7i th\xE0nh vi\xEAn",
        period: "th\xE1ng",
        features: [
          {
            text: "T\u0103ng c\u01B0\u1EDDng n\u1ED9i dung ph\u1EA3n h\u1ED3i v\u1EDBi AI chuy\xEAn nghi\u1EC7p"
          },
          {
            text: "Mi\u1EC5n ph\xED khi s\u1EED d\u1EE5ng Works AI"
          }
        ],
        buttonText: "Li\xEAn h\u1EC7"
      },
      {
        id: "standard",
        title: "Orblit Works",
        price: 1e4,
        unit: "m\u1ED7i th\xE0nh vi\xEAn",
        period: "th\xE1ng",
        features: [
          {
            text: "Qu\u1EA3n l\xFD c\xF4ng vi\u1EC7c c\xE1 nh\xE2n + chia s\u1EBB tr\u1EA1ng th\xE1i th\u1EDFi gian th\u1EF1c"
          },
          {
            text: "AI t\u1EF1 \u0111\u1ED9ng t\u1EA1o b\xE1o c\xE1o h\xE0ng ng\xE0y/h\xE0ng tu\u1EA7n/h\xE0ng th\xE1ng"
          },
          {
            text: "Bao g\u1ED3m Feedback AI (gi\xE1 tr\u1ECB \u20A96,000/th\xE1ng)"
          }
        ],
        buttonText: "Li\xEAn h\u1EC7"
      },
      {
        id: "premium",
        title: "Orblit 1on1",
        iconBgColor: "#EDF0FE",
        iconColor: "#4C6EF5",
        price: 8e3,
        unit: "m\u1ED7i th\xE0nh vi\xEAn",
        period: "th\xE1ng",
        features: [
          {
            text: "Chuy\u1EC3n v\u0103n b\u1EA3n cu\u1ED9c h\u1ECDp b\u1EB1ng AI (STT)"
          },
          {
            text: "Ph\u01B0\u01A1ng ph\xE1p t\xF9y ch\u1EC9nh cho t\u1EEBng th\xE0nh vi\xEAn nh\xF3m"
          },
          {
            text: "Ghi l\u1EA1i + t\xF3m t\u1EAFt + ph\xE2n t\xEDch n\u1ED9i dung 1on1"
          },
          {
            text: "B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n tr\u1EA1ng th\xE1i th\xE0nh vi\xEAn d\u1EF1a tr\xEAn n\u1ED9i dung 1on1"
          },
          {
            text: "AI t\xF9y ch\u1EC9nh \u0111\u1EC3 c\u1EA3i thi\u1EC7n k\u1EF9 n\u0103ng l\xE3nh \u0111\u1EA1o"
          }
        ],
        buttonText: "Li\xEAn h\u1EC7"
      }
    ]
  },
  landingNonAdopted: {
    title: 'B\u1EA1n \u0111ang do d\u1EF1 v\u1EC1 vi\u1EC7c \xE1p d\u1EE5ng <span style="color: #228BE6">cu\u1ED9c h\u1ECDp 1on1?</span>',
    title_app: 'B\u1EA1n \u0111ang do d\u1EF1 v\u1EC1 vi\u1EC7c \xE1p d\u1EE5ng <span style="color: #228BE6">cu\u1ED9c h\u1ECDp 1on1?</span>',
    subtitle: 'AI Orblit kh\xF4ng ch\u1EC9 gi\xFAp b\u1EA1n kh\u1EDFi \u0111\u1EA7u th\xE0nh c\xF4ng,<br />m\xE0 c\xF2n <span style="color: #228BE6">h\u1ED7 tr\u1EE3 s\u1EF1 ph\xE1t tri\u1EC3n k\u1EF9 n\u0103ng l\xE3nh \u0111\u1EA1o c\u1EE7a b\u1EA1n.</span>',
    subtitle_app: 'AI c\u1EE7a Orblit<br /> kh\xF4ng ch\u1EC9 gi\xFAp kh\u1EDFi \u0111\u1EA7u th\xE0nh c\xF4ng, m\xE0 c\xF2n <span style="color: #228BE6">\u0111\u1ED3ng h\xE0nh c\xF9ng<br />s\u1EF1 ph\xE1t tri\u1EC3n n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o c\u1EE7a tr\u01B0\u1EDFng nh\xF3m</span>.',
    question: "B\u1EA1n c\xF3 \u0111ang do d\u1EF1 th\u1EF1c hi\u1EC7n cu\u1ED9c h\u1ECDp 1on1 v\xEC nh\u1EEFng r\xE0o c\u1EA3n n\xE0y kh\xF4ng?",
    question_app: "B\u1EA1n c\xF3 \u0111ang do d\u1EF1 \nth\u1EF1c hi\u1EC7n 1on1 v\xEC nh\u1EEFng r\xE0o c\u1EA3n n\xE0y kh\xF4ng?",
    balloons: {
      balloon1: {
        text: 'T\xF4i bi\u1EBFt cu\u1ED9c h\u1ECDp 1on1 s\u1EBD c\xF3 l\u1EE3i cho c\u1EA3 t\xF4i v\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m,<br />nh\u01B0ng t\xF4i <span style="font-weight: 600">s\u1EE3 b\u1EAFt \u0111\u1EA7u v\xEC thi\u1EBFu kinh nghi\u1EC7m.</span>',
        text_app: 'T\xF4i bi\u1EBFt cu\u1ED9c h\u1ECDp 1on1 s\u1EBD c\xF3 l\u1EE3i cho c\u1EA3 t\xF4i v\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m,<br />nh\u01B0ng t\xF4i <span style="font-weight: 600">s\u1EE3 b\u1EAFt \u0111\u1EA7u v\xEC thi\u1EBFu kinh nghi\u1EC7m.</span>'
      },
      balloon2: {
        text: 'T\xF4i \u0111\xE3 c\xF3 qu\xE1 nhi\u1EC1u c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD, <span style="font-weight: 600">t\xF4i ng\u1EA7n ng\u1EA1i th\xEAm c\xF4ng vi\u1EC7c v\u1EDBi c\xE1c cu\u1ED9c h\u1ECDp 1on1.</span>',
        text_app: 'T\xF4i \u0111\xE3 c\xF3 qu\xE1 nhi\u1EC1u c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD, <span style="font-weight: 600">t\xF4i ng\u1EA7n ng\u1EA1i th\xEAm c\xF4ng vi\u1EC7c v\u1EDBi c\xE1c cu\u1ED9c h\u1ECDp 1on1.</span>'
      },
      balloon3: {
        text: 'Th\u1EADt kh\xF3 \u0111\u1EC3 hi\u1EC3u tr\u1EA1ng th\xE1i hi\u1EC7n t\u1EA1i v\xE0 ti\u1EC1m n\u0103ng c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.<br /><span style="font-weight: 600">T\xF4i ch\u1EC9 \u0111ang \u0111o\xE1n ai \u0111ang g\u1EB7p kh\xF3 kh\u0103n v\xE0 ai c\xF3 ti\u1EC1m n\u0103ng ph\xE1t tri\u1EC3n.</span>',
        text_app: 'Th\u1EADt kh\xF3 \u0111\u1EC3 hi\u1EC3u tr\u1EA1ng th\xE1i hi\u1EC7n t\u1EA1i v\xE0 ti\u1EC1m n\u0103ng c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.<br /><span style="font-weight: 600">T\xF4i ch\u1EC9 \u0111ang \u0111o\xE1n ai \u0111ang g\u1EB7p kh\xF3 kh\u0103n v\xE0 ai c\xF3 ti\u1EC1m n\u0103ng ph\xE1t tri\u1EC3n.</span>'
      },
      balloon4: {
        text: 'T\xF4i lo l\u1EAFng r\u1EB1ng <span style="font-weight: 600">ch\xFAng t\xF4i s\u1EBD d\xE0nh th\u1EDFi gian qu\xFD b\xE1u ch\u1EC9 \u0111\u1EC3 tr\xF2 chuy\u1EC7n m\xE0 kh\xF4ng c\xF3 k\u1EBFt qu\u1EA3 th\u1EF1c s\u1EF1 n\xE0o.</span>',
        text_app: 'T\xF4i lo l\u1EAFng r\u1EB1ng <span style="font-weight: 600">ch\xFAng t\xF4i s\u1EBD d\xE0nh th\u1EDFi gian qu\xFD b\xE1u ch\u1EC9 \u0111\u1EC3 tr\xF2 chuy\u1EC7n m\xE0 kh\xF4ng c\xF3 k\u1EBFt qu\u1EA3 th\u1EF1c s\u1EF1 n\xE0o.</span>'
      }
    },
    nontitle: `Orblit bi\u1EBFn t\u1EA5t c\u1EA3 g\xE1nh n\u1EB7ng c\u1EE7a c\xE1c cu\u1ED9c h\u1ECDp 1on1 th\xE0nh <span style="color: #228BE6">'c\u01A1 h\u1ED9i ph\xE1t tri\u1EC3n'</span>.`,
    nontitle_mobile: `Orblit bi\u1EBFn t\u1EA5t c\u1EA3 g\xE1nh n\u1EB7ng c\u1EE7a c\xE1c cu\u1ED9c h\u1ECDp 1on1<br />th\xE0nh <span style="color: #228BE6">'c\u01A1 h\u1ED9i ph\xE1t tri\u1EC3n'</span>.`,
    nontitle_desktop: `Orblit bi\u1EBFn t\u1EA5t c\u1EA3 g\xE1nh n\u1EB7ng c\u1EE7a c\xE1c cu\u1ED9c h\u1ECDp 1on1 th\xE0nh <span style="color: #228BE6">'c\u01A1 h\u1ED9i ph\xE1t tri\u1EC3n'</span>.`
  },
  nonAdoptedCards: [
    {
      number: "1",
      title: "Orblit cung c\u1EA5p c\xE1c m\u1EABu c\xE2u h\u1ECFi t\u1EADp trung v\xE0o s\u1EF1 ph\xE1t tri\u1EC3n v\xE0 ch\u1EA9n \u0111o\xE1n nh\xE2n vi\xEAn,<br />v\xE0 AI t\u1EA1o ra c\xE1c c\xE2u h\u1ECFi ph\xF9 h\u1EE3p v\u1EDBi ch\u1EE7 \u0111\u1EC1 b\u1EA1n mong mu\u1ED1n.",
      appTitle: "Orblit cung c\u1EA5p c\xE1c m\u1EABu c\xE2u h\u1ECFi t\u1EADp trung v\xE0o<br />s\u1EF1 ph\xE1t tri\u1EC3n v\xE0 ch\u1EA9n \u0111o\xE1n nh\xE2n vi\xEAn, v\xE0 AI t\u1EA1o ra<br />c\xE1c c\xE2u h\u1ECFi ph\xF9 h\u1EE3p v\u1EDBi ch\u1EE7 \u0111\u1EC1 b\u1EA1n mong mu\u1ED1n.",
      description: "AI Orblit 1on1 \u0111\u1EC1 xu\u1EA5t m\u1ECDi th\u1EE9 t\u1EEB ch\u1EA9n \u0111o\xE1n t\xECnh h\xECnh th\xE0nh vi\xEAn nh\xF3m<br />\u0111\u1EBFn c\xE1c c\xE2u h\u1ECFi hu\u1EA5n luy\u1EC7n th\xFAc \u0111\u1EA9y s\u1EF1 ph\xE1t tri\u1EC3n. C\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m c\xF3 th\u1EC3<br />t\u1EADp trung ho\xE0n to\xE0n v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n m\xE0 kh\xF4ng c\u1EA7n lo l\u1EAFng v\u1EC1 vi\u1EC7c n\xF3i g\xEC.",
      appDescription: "AI Orblit 1on1 \u0111\u1EC1 xu\u1EA5t m\u1ECDi th\u1EE9 t\u1EEB ch\u1EA9n \u0111o\xE1n t\xECnh h\xECnh th\xE0nh vi\xEAn nh\xF3m<br />\u0111\u1EBFn c\xE1c c\xE2u h\u1ECFi hu\u1EA5n luy\u1EC7n th\xFAc \u0111\u1EA9y s\u1EF1 ph\xE1t tri\u1EC3n. C\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m c\xF3 th\u1EC3<br />t\u1EADp trung ho\xE0n to\xE0n v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n m\xE0 kh\xF4ng c\u1EA7n lo l\u1EAFng v\u1EC1 vi\u1EC7c n\xF3i g\xEC.",
      imageSrc: "/images/landing_m/en/nonadopted01.png",
      imageAlt: "H\u01B0\u1EDBng d\u1EABn cu\u1ED9c h\u1ECDp 1on1"
    },
    {
      number: "2",
      title: "AI x\u1EED l\xFD t\u1EA5t c\u1EA3 vi\u1EC7c ghi ch\xE9p v\xE0 t\u1ED5 ch\u1EE9c cho b\u1EA1n.",
      appTitle: "AI x\u1EED l\xFD t\u1EA5t c\u1EA3 vi\u1EC7c ghi ch\xE9p v\xE0 t\u1ED5 ch\u1EE9c cho b\u1EA1n.",
      description: "Khi cu\u1ED9c h\u1ECDp k\u1EBFt th\xFAc, b\u1EA3n ghi, t\xF3m t\u1EAFt ch\xEDnh v\xE0 c\xE1c m\u1EE5c h\xE0nh \u0111\u1ED9ng<br />\u0111\u01B0\u1EE3c t\u1EA1o ra t\u1EF1 \u0111\u1ED9ng. C\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m c\xF3 th\u1EC3 t\u1EADp trung v\xE0o vi\u1EC7c t\u1EA1o ra<br />th\xE0nh t\u1EF1u ti\u1EBFp theo m\xE0 kh\xF4ng c\u1EA7n l\xE0m th\xEAm c\xF4ng vi\u1EC7c t\xE0i li\u1EC7u sau cu\u1ED9c h\u1ECDp.",
      appDescription: "Khi cu\u1ED9c h\u1ECDp k\u1EBFt th\xFAc, b\u1EA3n ghi, t\xF3m t\u1EAFt ch\xEDnh v\xE0 c\xE1c m\u1EE5c h\xE0nh \u0111\u1ED9ng<br />\u0111\u01B0\u1EE3c t\u1EA1o ra t\u1EF1 \u0111\u1ED9ng. C\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m c\xF3 th\u1EC3 t\u1EADp trung v\xE0o vi\u1EC7c t\u1EA1o ra<br />th\xE0nh t\u1EF1u ti\u1EBFp theo m\xE0 kh\xF4ng c\u1EA7n l\xE0m th\xEAm c\xF4ng vi\u1EC7c t\xE0i li\u1EC7u sau cu\u1ED9c h\u1ECDp.",
      imageSrc: "/images/landing_m/en/nonadopted02.png",
      imageAlt: "Ghi ch\xE9p t\u1EF1 \u0111\u1ED9ng b\u1EB1ng AI"
    },
    {
      number: "3",
      title: "Sau cu\u1ED9c h\u1ECDp, m\u1ED9t 'B\xE1o c\xE1o L\xE3nh \u0111\u1EA1o' \u0111\u01B0\u1EE3c ph\xE1t h\xE0nh cho c\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m.",
      appTitle: "Sau cu\u1ED9c h\u1ECDp, m\u1ED9t 'B\xE1o c\xE1o L\xE3nh \u0111\u1EA1o'<br />\u0111\u01B0\u1EE3c ph\xE1t h\xE0nh cho c\xE1c nh\xE0 l\xE3nh \u0111\u1EA1o nh\xF3m.",
      description: '\u0110\xE2y l\xE0 c\u1ED1t l\xF5i c\u1EE7a Orblit. AI ph\xE2n t\xEDch t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u c\u1EE7a b\u1EA1n,<br />c\xE1c lo\u1EA1i c\xE2u h\u1ECFi, v.v., \u0111\u1EC3 cung c\u1EA5p hu\u1EA5n luy\u1EC7n t\xF9y ch\u1EC9nh gi\xFAp b\u1EA1n ph\xE1t tri\u1EC3n<br />nh\u01B0 m\u1ED9t "nh\xE0 l\xE3nh \u0111\u1EA1o bi\u1EBFt l\u1EAFng nghe" v\xE0 "nh\xE0 l\xE3nh \u0111\u1EA1o h\u1ED7 tr\u1EE3 s\u1EF1 ph\xE1t tri\u1EC3n".<br />Khi b\u1EA1n ti\u1EBFn h\xE0nh nhi\u1EC1u cu\u1ED9c h\u1ECDp 1on1 h\u01A1n, kh\u1EA3 n\u0103ng l\xE3nh \u0111\u1EA1o c\u1EE7a b\u1EA1n c\u0169ng ph\xE1t tri\u1EC3n.',
      appDescription: '\u0110\xE2y l\xE0 c\u1ED1t l\xF5i c\u1EE7a Orblit. AI ph\xE2n t\xEDch t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u c\u1EE7a b\u1EA1n,<br />c\xE1c lo\u1EA1i c\xE2u h\u1ECFi, v.v., \u0111\u1EC3 cung c\u1EA5p hu\u1EA5n luy\u1EC7n t\xF9y ch\u1EC9nh gi\xFAp b\u1EA1n ph\xE1t tri\u1EC3n<br />nh\u01B0 m\u1ED9t "nh\xE0 l\xE3nh \u0111\u1EA1o bi\u1EBFt l\u1EAFng nghe" v\xE0 "nh\xE0 l\xE3nh \u0111\u1EA1o h\u1ED7 tr\u1EE3 s\u1EF1 ph\xE1t tri\u1EC3n".<br />Khi b\u1EA1n ti\u1EBFn h\xE0nh nhi\u1EC1u cu\u1ED9c h\u1ECDp 1on1 h\u01A1n, kh\u1EA3 n\u0103ng l\xE3nh \u0111\u1EA1o c\u1EE7a b\u1EA1n c\u0169ng ph\xE1t tri\u1EC3n.',
      imageSrc: "/images/landing_m/en/nonadopted03.png",
      imageAlt: "B\xE1o c\xE1o L\xE3nh \u0111\u1EA1o"
    },
    {
      number: "4",
      title: "Ch\xFAng t\xF4i cung c\u1EA5p b\xE1o c\xE1o IC ph\xE2n t\xEDch t\u1EEBng th\xE0nh vi\xEAn nh\xF3m.",
      appTitle: "Ch\xFAng t\xF4i cung c\u1EA5p b\xE1o c\xE1o IC ph\xE2n t\xEDch t\u1EEBng th\xE0nh vi\xEAn nh\xF3m.",
      description: "AI ph\xE2n t\xEDch c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u1EC3 t\u1EA1o ra b\xE1o c\xE1o ph\xE1c th\u1EA3o nh\u1EEFng g\xEC th\xFAc \u0111\u1EA9y th\xE0nh vi\xEAn nh\xF3m,<br />\u0111i\u1EC3m m\u1EA1nh, \u0111i\u1EC3m y\u1EBFu c\u1EE7a h\u1ECD v\xE0 c\xE1ch qu\u1EA3n l\xFD h\u1ECD. Th\xF4ng tin c\xF2n thi\u1EBFu \u0111\u01B0\u1EE3c<br />\u0111\u1EC1 xu\u1EA5t l\xE0m ch\u1EE7 \u0111\u1EC1 cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo, cho ph\xE9p qu\u1EA3n l\xFD s\xE2u s\u1EAFc c\xE1c th\xE0nh vi\xEAn nh\xF3m.",
      appDescription: "AI ph\xE2n t\xEDch c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u1EC3 t\u1EA1o ra b\xE1o c\xE1o ph\xE1c th\u1EA3o nh\u1EEFng g\xEC th\xFAc \u0111\u1EA9y th\xE0nh vi\xEAn nh\xF3m,<br />\u0111i\u1EC3m m\u1EA1nh, \u0111i\u1EC3m y\u1EBFu c\u1EE7a h\u1ECD v\xE0 c\xE1ch qu\u1EA3n l\xFD h\u1ECD.<br />Th\xF4ng tin c\xF2n thi\u1EBFu \u0111\u01B0\u1EE3c \u0111\u1EC1 xu\u1EA5t l\xE0m ch\u1EE7 \u0111\u1EC1 cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo,<br />cho ph\xE9p qu\u1EA3n l\xFD s\xE2u s\u1EAFc c\xE1c th\xE0nh vi\xEAn nh\xF3m.",
      imageSrc: "/images/landing_m/en/nonadopted04.png",
      imageAlt: "B\xE1o c\xE1o IC"
    },
    {
      number: "5",
      title: "X\xE1c \u0111\u1ECBnh ti\u1EC1m n\u0103ng v\xE0 t\xEDn hi\u1EC7u r\u1EE7i ro c\u1EE7a th\xE0nh vi\xEAn nh\xF3m th\xF4ng qua d\u1EEF li\u1EC7u.",
      appTitle: "X\xE1c \u0111\u1ECBnh ti\u1EC1m n\u0103ng v\xE0 t\xEDn hi\u1EC7u r\u1EE7i ro c\u1EE7a th\xE0nh vi\xEAn nh\xF3m th\xF4ng qua d\u1EEF li\u1EC7u.",
      description: "D\u1EF1a tr\xEAn ph\xE2n t\xEDch AI v\u1EC1 t\u1EEBng th\xE0nh vi\xEAn nh\xF3m,<br />b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box hi\u1EC3n th\u1ECB tr\u1EF1c quan hi\u1EC7u su\u1EA5t v\xE0 ti\u1EC1m n\u0103ng c\u1EE7a th\xE0nh vi\xEAn nh\xF3m.<br />B\u1EA1n c\xF3 th\u1EC3 x\xE1c \u0111\u1ECBnh v\xE0 ch\u1EE7 \u0111\u1ED9ng qu\u1EA3n l\xFD c\xE1c th\xE0nh vi\xEAn nh\xF3m c\xF3 d\u1EA5u hi\u1EC7u<br />'ngh\u1EC9 vi\u1EC7c im l\u1EB7ng' ho\u1EB7c nh\u1EEFng ng\u01B0\u1EDDi n\xEAn \u0111\u01B0\u1EE3c ph\xE1t tri\u1EC3n nh\u01B0 nh\xE2n t\xE0i ch\xEDnh, t\u1EA5t c\u1EA3 d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u.",
      appDescription: "D\u1EF1a tr\xEAn ph\xE2n t\xEDch AI v\u1EC1 t\u1EEBng th\xE0nh vi\xEAn nh\xF3m,<br />b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box hi\u1EC3n th\u1ECB tr\u1EF1c quan hi\u1EC7u su\u1EA5t v\xE0 ti\u1EC1m n\u0103ng c\u1EE7a th\xE0nh vi\xEAn nh\xF3m.<br />B\u1EA1n c\xF3 th\u1EC3 x\xE1c \u0111\u1ECBnh v\xE0 ch\u1EE7 \u0111\u1ED9ng qu\u1EA3n l\xFD c\xE1c th\xE0nh vi\xEAn nh\xF3m c\xF3 d\u1EA5u hi\u1EC7u<br />'ngh\u1EC9 vi\u1EC7c im l\u1EB7ng' ho\u1EB7c nh\u1EEFng ng\u01B0\u1EDDi n\xEAn \u0111\u01B0\u1EE3c ph\xE1t tri\u1EC3n nh\u01B0 nh\xE2n t\xE0i ch\xEDnh,<br />t\u1EA5t c\u1EA3 d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u.",
      imageSrc: "/images/landing_m/en/nonadopted05.png",
      imageAlt: "B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n ph\xE2n t\xEDch th\xE0nh vi\xEAn nh\xF3m"
    }
  ],
  landingAdopted: {
    title: 'AI bi\u1EBFn <span style="color: #228BE6">ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o nh\xF3m th\xE0nh hu\u1EA5n luy\u1EC7n vi\xEAn c\xF3 n\u0103ng l\u1EF1c v\xE0 th\xE0nh vi\xEAn nh\xF3m th\xE0nh nh\xE2n t\xE0i ch\xEDnh</span>.',
    subtitle: 'V\u1EDBi Orblit, chuy\u1EC3n \u0111\u1ED5i ph\u1EA3n h\u1ED3i v\xE0 cu\u1ED9c h\u1ECDp 1on1 \u0111\u1EA7y th\xE1ch th\u1EE9c<br />th\xE0nh <span style="color: #228BE6">\u0111\u1ED9ng l\u1EF1c ph\xE1t tri\u1EC3n m\u1EA1nh m\u1EBD nh\u1EA5t c\u1EE7a nh\xF3m b\u1EA1n.</span>',
    subtitle_app: 'V\u1EDBi Orblit, chuy\u1EC3n \u0111\u1ED5i ph\u1EA3n h\u1ED3i v\xE0 cu\u1ED9c h\u1ECDp 1on1 \u0111\u1EA7y th\xE1ch th\u1EE9c<br />th\xE0nh <span style="color: #228BE6">\u0111\u1ED9ng l\u1EF1c ph\xE1t tri\u1EC3n<br />m\u1EA1nh m\u1EBD nh\u1EA5t c\u1EE7a nh\xF3m b\u1EA1n.</span>',
    question: "B\u1EA1n c\xF3 \u0111ang t\u1EF1 m\xECnh v\u1EADt l\u1ED9n v\u1EDBi nh\u1EEFng lo ng\u1EA1i n\xE0y kh\xF4ng?",
    balloons: [
      {
        text: 'Ph\u1EA3n h\u1ED3i t\xF4i \u0111\u01B0a ra c\xF3 th\u1EC3 kh\xF4ng gi\xFAp c\xE1c th\xE0nh vi\xEAn trong nh\xF3m ph\xE1t tri\u1EC3n, <span style="font-weight: 600">t\xF4i lu\xF4n do d\u1EF1 v\xEC n\xF3 c\xF3 th\u1EC3 l\xE0m cho m\u1ED1i quan h\u1EC7 c\u1EE7a ch\xFAng t\xF4i tr\u1EDF n\xEAn kh\xF3 x\u1EED.</span>',
        text_app: 'Ph\u1EA3n h\u1ED3i t\xF4i \u0111\u01B0a ra c\xF3 th\u1EC3 kh\xF4ng gi\xFAp c\xE1c th\xE0nh vi\xEAn trong nh\xF3m ph\xE1t tri\u1EC3n,<br /><span style="font-weight: 600">t\xF4i lu\xF4n do d\u1EF1 v\xEC n\xF3 c\xF3 th\u1EC3 l\xE0m cho m\u1ED1i quan h\u1EC7 c\u1EE7a ch\xFAng t\xF4i tr\u1EDF n\xEAn kh\xF3 x\u1EED.</span>'
      },
      {
        text: '<span style="font-weight: 600">M\u1EA5t qu\xE1 nhi\u1EC1u th\u1EDDi gian \u0111\u1EC3 chu\u1EA9n b\u1ECB v\xE0 t\u1ED5 ch\u1EE9c ghi ch\xFA 1on1 <br /></span>T\xF4i kh\xF4ng th\u1EC3 t\u1ED5 ch\u1EE9c ch\xFAng \u0111\xFAng c\xE1ch, v\xEC v\u1EADy n\u1ED9i dung th\u01B0\u1EDDng b\u1ECB m\u1EA5t sau cu\u1ED9c h\u1ECDp',
        text_app: '<span style="font-weight: 600">M\u1EA5t qu\xE1 nhi\u1EC1u th\u1EDDi gian \u0111\u1EC3 chu\u1EA9n b\u1ECB v\xE0 t\u1ED5 ch\u1EE9c ghi ch\xFA 1on1 <br /></span>T\xF4i kh\xF4ng th\u1EC3 t\u1ED5 ch\u1EE9c ch\xFAng \u0111\xFAng c\xE1ch, v\xEC v\u1EADy<br />n\u1ED9i dung th\u01B0\u1EDDng b\u1ECB m\u1EA5t sau cu\u1ED9c h\u1ECDp'
      },
      {
        text: 'T\xF4i \u0111\xE3 c\xF3 cu\u1ED9c h\u1ECDp 1on1 v\xE0 t\u1ED5 ch\u1EE9c c\xE1c ghi ch\xFA, <span style="font-weight: 600">nh\u01B0ng t\xF4i kh\xF4ng bi\u1EBFt ph\u1EA3i l\xE0m g\xEC v\u1EDBi ch\xFAng sau \u0111\xF3.</span>',
        text_app: 'T\xF4i \u0111\xE3 c\xF3 cu\u1ED9c h\u1ECDp 1on1 v\xE0 t\u1ED5 ch\u1EE9c c\xE1c ghi ch\xFA,<br /><span style="font-weight: 600">nh\u01B0ng t\xF4i kh\xF4ng bi\u1EBFt ph\u1EA3i l\xE0m g\xEC v\u1EDBi ch\xFAng sau \u0111\xF3.</span>'
      },
      {
        text: 'T\xF4i ch\u1EAFc ch\u1EAFn \u0111ang c\xF3 c\xE1c cu\u1ED9c h\u1ECDp 1on1, <span style="font-weight: 600">nh\u01B0ng kh\xF4ng c\xF3 k\u1EF9 n\u0103ng chuy\xEAn nghi\u1EC7p,<br />c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n c\u1EA3m th\u1EA5y h\u1EDDi h\u1EE3t v\xE0 t\xF4i b\u1ECF l\u1EE1 c\u1EA3m x\xFAc th\u1EF1c s\u1EF1 c\u1EE7a th\xE0nh vi\xEAn nh\xF3m.</span>',
        text_app: 'T\xF4i ch\u1EAFc ch\u1EAFn \u0111ang c\xF3 c\xE1c cu\u1ED9c h\u1ECDp 1on1, <span style="font-weight: 600">nh\u01B0ng kh\xF4ng c\xF3 k\u1EF9 n\u0103ng chuy\xEAn nghi\u1EC7p,<br />c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n c\u1EA3m th\u1EA5y h\u1EDDi h\u1EE3t v\xE0 t\xF4i b\u1ECF l\u1EE1 c\u1EA3m x\xFAc th\u1EF1c s\u1EF1 c\u1EE7a th\xE0nh vi\xEAn nh\xF3m.</span>'
      },
      {
        text: 'CEO c\xF3 k\u1EF3 v\u1ECDng, <span style="font-weight: 600">nh\u01B0ng t\xF4i ch\u01B0a bao gi\u1EDD \u0111\u01B0\u1EE3c \u0111\xE0o t\u1EA1o l\xE3nh \u0111\u1EA1o \u0111\xFAng c\xE1ch, v\xEC v\u1EADy t\xF4i lu\xF4n c\u1EA3m th\u1EA5y lo l\u1EAFng khi \u0111\u1EBFn l\xFAc 1on1.</span>',
        text_app: 'CEO c\xF3 k\u1EF3 v\u1ECDng,<br /><span style="font-weight: 600">nh\u01B0ng t\xF4i ch\u01B0a bao gi\u1EDD \u0111\u01B0\u1EE3c \u0111\xE0o t\u1EA1o l\xE3nh \u0111\u1EA1o \u0111\xFAng c\xE1ch, v\xEC v\u1EADy t\xF4i lu\xF4n c\u1EA3m th\u1EA5y lo l\u1EAFng khi \u0111\u1EBFn l\xFAc 1on1.</span>'
      }
    ],
    adopted_title: "Orblit h\u1ED7 tr\u1EE3 s\u1EF1 ph\xE1t tri\u1EC3n c\u1EE7a c\u1EA3 ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o nh\xF3m v\xE0 th\xE0nh vi\xEAn nh\xF3m.",
    adopted_title_app: "Orblit<br />h\u1ED7 tr\u1EE3 s\u1EF1 ph\xE1t tri\u1EC3n c\u1EE7a c\u1EA3 ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o nh\xF3m v\xE0 th\xE0nh vi\xEAn nh\xF3m."
  },
  adoptedCards: [
    {
      number: "1",
      title: "B\u1EA1n c\xF3 m\u1ED9t hu\u1EA5n luy\u1EC7n vi\xEAn l\xE3nh \u0111\u1EA1o ch\u1EC9 d\xE0nh ri\xEAng cho b\u1EA1n.",
      appTitle: "B\u1EA1n c\xF3 m\u1ED9t hu\u1EA5n luy\u1EC7n vi\xEAn l\xE3nh \u0111\u1EA1o ch\u1EC9 d\xE0nh ri\xEAng cho b\u1EA1n.",
      description: "Sau cu\u1ED9c h\u1ECDp 1on1, AI s\u1EBD ph\xE2n t\xEDch phong c\xE1ch tr\xF2 chuy\u1EC7n c\u1EE7a b\u1EA1n v\xE0 cung c\u1EA5p<br />hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c c\xE1 nh\xE2n h\xF3a. Kh\xF4ng c\u1EA7n \u0111\xE0o t\u1EA1o ri\xEAng, b\u1EA1n c\xF3 th\u1EC3 ph\xE1t tri\u1EC3n nhanh nh\u1EA5t<br />th\xF4ng qua th\u1EF1c h\xE0nh th\u1EF1c t\u1EBF v\xE0 tr\u1EDF th\xE0nh m\u1ED9t nh\xE0 l\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c c\xE1c th\xE0nh vi\xEAn trong nh\xF3m tin t\u01B0\u1EDFng.",
      appDescription: "Sau cu\u1ED9c h\u1ECDp 1on1, AI s\u1EBD ph\xE2n t\xEDch phong c\xE1ch tr\xF2 chuy\u1EC7n c\u1EE7a b\u1EA1n v\xE0 cung c\u1EA5p<br />hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c c\xE1 nh\xE2n h\xF3a. Kh\xF4ng c\u1EA7n \u0111\xE0o t\u1EA1o ri\xEAng, b\u1EA1n c\xF3 th\u1EC3 ph\xE1t tri\u1EC3n nhanh nh\u1EA5t<br />th\xF4ng qua th\u1EF1c h\xE0nh th\u1EF1c t\u1EBF v\xE0 tr\u1EDF th\xE0nh m\u1ED9t nh\xE0 l\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c c\xE1c th\xE0nh vi\xEAn trong nh\xF3m tin t\u01B0\u1EDFng.",
      imageSrc: "/images/landing_m/en/adopted001.png",
      imageAlt: "H\u01B0\u1EDBng d\u1EABn cu\u1ED9c h\u1ECDp 1on1"
    },
    {
      number: "2",
      title: "Ch\xFAng t\xF4i cung c\u1EA5p 'd\u1EEF li\u1EC7u t\u0103ng tr\u01B0\u1EDFng' \u0111\u1EC3 \u0111\xE1nh th\u1EE9c ti\u1EC1m n\u0103ng c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
      appTitle: "Ch\xFAng t\xF4i cung c\u1EA5p 'd\u1EEF li\u1EC7u t\u0103ng tr\u01B0\u1EDFng' \u0111\u1EC3 \u0111\xE1nh th\u1EE9c ti\u1EC1m n\u0103ng c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
      description: "AI ph\xE2n t\xEDch c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n 1on1 v\xE0 h\u1ED3 s\u01A1 c\xF4ng vi\u1EC7c \u0111\u1EC3 t\u1EA1o b\xE1o c\xE1o v\u1EC1 \u0111i\u1EC3m m\u1EA1nh v\xE0 l\u0129nh v\u1EF1c c\u1EA7n c\u1EA3i thi\u1EC7n c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m,<br />v\xE0 b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box ch\u1EA9n \u0111o\xE1n n\u0103ng l\u1EF1c v\xE0 t\xECnh tr\u1EA1ng c\u1EE7a h\u1ECD. D\u1EF1a tr\xEAn d\u1EEF li\u1EC7u n\xE0y,<br />b\u1EA1n c\xF3 th\u1EC3 thi\u1EBFt k\u1EBF con \u0111\u01B0\u1EDDng ph\xE1t tri\u1EC3n thi\u1EBFt y\u1EBFu cho t\u1EEBng th\xE0nh vi\xEAn trong nh\xF3m v\xE0 ph\xE1t huy t\u1ED1i \u0111a ti\u1EC1m n\u0103ng c\u1EE7a h\u1ECD.",
      appDescription: "AI ph\xE2n t\xEDch c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n 1on1 v\xE0 h\u1ED3 s\u01A1 c\xF4ng vi\u1EC7c \u0111\u1EC3 t\u1EA1o b\xE1o c\xE1o v\u1EC1 \u0111i\u1EC3m m\u1EA1nh<br />v\xE0 l\u0129nh v\u1EF1c c\u1EA7n c\u1EA3i thi\u1EC7n c\u1EE7a c\xE1c th\xE0nh vi\xEAn trong nh\xF3m, v\xE0 b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n 9Box ch\u1EA9n \u0111o\xE1n n\u0103ng l\u1EF1c v\xE0 t\xECnh tr\u1EA1ng c\u1EE7a h\u1ECD.<br />D\u1EF1a tr\xEAn d\u1EEF li\u1EC7u n\xE0y, b\u1EA1n c\xF3 th\u1EC3 thi\u1EBFt k\u1EBF con \u0111\u01B0\u1EDDng ph\xE1t tri\u1EC3n thi\u1EBFt y\u1EBFu cho t\u1EEBng th\xE0nh vi\xEAn trong nh\xF3m<br />v\xE0 ph\xE1t huy t\u1ED1i \u0111a ti\u1EC1m n\u0103ng c\u1EE7a h\u1ECD.",
      imageSrc: "/images/landing_m/en/adopted002.png",
      imageAlt: "Ghi \xE2m t\u1EF1 \u0111\u1ED9ng AI"
    },
    {
      number: "3",
      title: "Ngay c\u1EA3 b\xEAn ngo\xE0i c\xE1c cu\u1ED9c h\u1ECDp 1on1, AI c\u0169ng gi\xFAp \u0111\u01B0a ra ph\u1EA3n h\u1ED3i l\xE0nh m\u1EA1nh.",
      appTitle: "Ngay c\u1EA3 b\xEAn ngo\xE0i c\xE1c cu\u1ED9c h\u1ECDp 1on1, AI c\u0169ng gi\xFAp \u0111\u01B0a ra ph\u1EA3n h\u1ED3i l\xE0nh m\u1EA1nh.",
      description: "AI Ph\u1EA3n h\u1ED3i c\u1EE7a Orblit c\u0169ng gi\xFAp \xEDch khi vi\u1EBFt ph\u1EA3n h\u1ED3i c\xF4ng vi\u1EC7c th\xF4ng th\u01B0\u1EDDng.<br />Thay v\xEC nh\u1EEFng bi\u1EC3u hi\u1EC7n c\u1EA3m t\xEDnh, n\xF3 \u0111\u1EC1 xu\u1EA5t c\xE1c b\u1EA3n nh\xE1p ph\u1EA3n h\u1ED3i mang t\xEDnh x\xE2y d\u1EF1ng d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u,<br />t\u1EA1o ra m\u1ED9t v\u0103n h\xF3a ph\u1EA3n h\u1ED3i t\xEDch c\u1EF1c m\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m c\xF3 th\u1EC3 c\u1EDFi m\u1EDF ch\u1EA5p nh\u1EADn.",
      appDescription: "AI Ph\u1EA3n h\u1ED3i c\u1EE7a Orblit c\u0169ng gi\xFAp \xEDch khi vi\u1EBFt ph\u1EA3n h\u1ED3i c\xF4ng vi\u1EC7c th\xF4ng th\u01B0\u1EDDng.<br />Thay v\xEC nh\u1EEFng bi\u1EC3u hi\u1EC7n c\u1EA3m t\xEDnh, n\xF3 \u0111\u1EC1 xu\u1EA5t c\xE1c b\u1EA3n nh\xE1p ph\u1EA3n h\u1ED3i mang t\xEDnh x\xE2y d\u1EF1ng d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u,<br />t\u1EA1o ra m\u1ED9t v\u0103n h\xF3a ph\u1EA3n h\u1ED3i t\xEDch c\u1EF1c m\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m c\xF3 th\u1EC3 c\u1EDFi m\u1EDF ch\u1EA5p nh\u1EADn.",
      imageSrc: "/images/landing_m/en/adopted003.png",
      imageAlt: "B\xE1o c\xE1o l\xE3nh \u0111\u1EA1o"
    },
    {
      number: "4",
      title: "H\xE3y \u0111\u1EC3 AI lo t\u1EA5t c\u1EA3 c\xE1c c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD v\xE0 ch\u1EC9 t\u1EADp trung v\xE0o 's\u1EF1 t\u0103ng tr\u01B0\u1EDFng'.",
      appTitle: "H\xE3y \u0111\u1EC3 AI lo t\u1EA5t c\u1EA3 c\xE1c c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD v\xE0 ch\u1EC9 t\u1EADp trung v\xE0o 's\u1EF1 t\u0103ng tr\u01B0\u1EDFng'.",
      description: "Ghi ch\xFA cu\u1ED9c h\u1ECDp v\xE0 h\u1ED3 s\u01A1 ph\u1EA3n h\u1ED3i \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng s\u1EAFp x\u1EBFp v\xE0 t\xEDch l\u0169y nh\u01B0 t\xE0i s\u1EA3n.<br />B\u1EA1n ho\xE0n to\xE0n tho\xE1t kh\u1ECFi c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD t\u1EBB nh\u1EA1t v\xE0 c\xF3 th\u1EC3 ch\u1EC9 t\u1EADp trung v\xE0o b\u1EA3n ch\u1EA5t quan tr\u1ECDng nh\u1EA5t c\u1EE7a 's\u1EF1 t\u0103ng tr\u01B0\u1EDFng'<br />cho c\u1EA3 tr\u01B0\u1EDFng nh\xF3m v\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
      appDescription: "Ghi ch\xFA cu\u1ED9c h\u1ECDp v\xE0 h\u1ED3 s\u01A1 ph\u1EA3n h\u1ED3i \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng s\u1EAFp x\u1EBFp v\xE0 t\xEDch l\u0169y nh\u01B0 t\xE0i s\u1EA3n.<br />B\u1EA1n ho\xE0n to\xE0n tho\xE1t kh\u1ECFi c\xF4ng vi\u1EC7c gi\u1EA5y t\u1EDD t\u1EBB nh\u1EA1t v\xE0 c\xF3 th\u1EC3 ch\u1EC9 t\u1EADp trung v\xE0o b\u1EA3n ch\u1EA5t quan tr\u1ECDng nh\u1EA5t c\u1EE7a 's\u1EF1 t\u0103ng tr\u01B0\u1EDFng'<br />cho c\u1EA3 tr\u01B0\u1EDFng nh\xF3m v\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
      imageSrc: "/images/landing_m/en/adopted004.png",
      imageAlt: "B\xE1o c\xE1o IC"
    }
  ],
  landingMenu: {
    product: "S\u1EA3n ph\u1EA9m",
    solution: "Gi\u1EA3i ph\xE1p",
    adopted: "Nh\xF3m \u0111ang th\u1EF1c hi\u1EC7n 1on1",
    nonadopted: "Nh\xF3m ch\u01B0a \xE1p d\u1EE5ng",
    pricing: "Gi\xE1",
    insight: "Insight",
    youtube: "YouTube",
    blog: "Blog",
    newsletter: "B\u1EA3n tin"
  }
};

// src/locales/vi-VN/ai.json
var ai_default3 = {
  chat: {
    history: "L\u1ECBch s\u1EED tr\xF2 chuy\u1EC7n",
    noHandler: "Phi\xEAn {{id}} \u0111\xE3 \u0111\u01B0\u1EE3c ch\u1ECDn, nh\u01B0ng kh\xF4ng c\xF3 tr\xECnh x\u1EED l\xFD \u0111\u01B0\u1EE3c cung c\u1EA5p",
    toggleCollapse: "Tr\xECnh x\u1EED l\xFD thu g\u1ECDn kh\xF4ng \u0111\u01B0\u1EE3c cung c\u1EA5p",
    newChat: "Cu\u1ED9c tr\xF2 chuy\u1EC7n m\u1EDBi",
    emptyState: "Ch\u01B0a c\xF3 phi\xEAn tr\xF2 chuy\u1EC7n n\xE0o",
    loading: "\u0110ang t\u1EA3i phi\xEAn tr\xF2 chuy\u1EC7n...",
    error: "L\u1ED7i khi t\u1EA3i phi\xEAn tr\xF2 chuy\u1EC7n",
    delete: "X\xF3a tr\xF2 chuy\u1EC7n",
    rename: "\u0110\u1ED5i t\xEAn tr\xF2 chuy\u1EC7n",
    confirmDelete: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y kh\xF4ng?",
    search: "T\xECm ki\u1EBFm tr\xF2 chuy\u1EC7n",
    greeting: "Xin ch\xE0o <strong>{{name}}</strong>!",
    thinking: "\u0110ang suy ngh\u0129",
    backToPrevious: "Quay l\u1EA1i",
    errorMessage: "\u0110\xE3 x\u1EA3y ra l\u1ED7i: {{message}}",
    aiRequestError: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\u1EED l\xFD y\xEAu c\u1EA7u c\u1EE7a b\u1EA1n. Vui l\xF2ng th\u1EED l\u1EA1i sau."
  },
  features: {
    featureSelected: "T\xEDnh n\u0103ng {{id}} \u0111\xE3 \u0111\u01B0\u1EE3c ch\u1ECDn, nh\u01B0ng kh\xF4ng c\xF3 tr\xECnh x\u1EED l\xFD \u0111\u01B0\u1EE3c cung c\u1EA5p",
    getTodo: {
      title: "Xem l\u1ECBch tr\xECnh",
      description: "Xem l\u1ECBch tr\xECnh c\u1EE7a b\u1EA1n v\xE0 c\xE1c th\xE0nh vi\xEAn trong nh\xF3m.",
      example1: 'ex) "T\xF4i \u0111\xE3 l\xE0m g\xEC v\xE0o th\u1EE9 N\u0103m tu\u1EA7n tr\u01B0\u1EDBc?"',
      example2: 'ex) "Cho t\xF4i bi\u1EBFt \u0111\u1ED9i c\u1EE7a t\xF4i \u0111ang l\xE0m g\xEC h\xF4m nay"'
    },
    createTodo: {
      title: "Th\xEAm c\xF4ng vi\u1EC7c",
      description: "D\u1EC5 d\xE0ng th\xEAm c\xF4ng vi\u1EC7c m\u1EDBi.",
      instruction1: "Nh\u1EADp y\xEAu c\u1EA7u t\u1EA1o l\u1ECBch tr\xECnh bao g\u1ED3m ng\xE0y, gi\u1EDD v\xE0 ti\xEAu \u0111\u1EC1.",
      instruction2: "Bao g\u1ED3m th\xF4ng tin v\u1EC1 \u0111\u1ECBa \u0111i\u1EC3m ho\u1EB7c ng\u01B0\u1EDDi tham d\u1EF1 s\u1EBD t\u1EA1o l\u1ECBch tr\xECnh ch\xEDnh x\xE1c h\u01A1n.",
      example: 'ex) "L\xEAn l\u1ECBch h\u1ECDi \u1ED9 l\u1EADp k\u1EBF ho\u1EA1ch s\u1EA3n ph\u1EA9m v\xE0o ng\xE0y mai t\u1EEB 2 gi\u1EDD \u0111\u1EBFn 4 gi\u1EDD chi\u1EC1u"'
    },
    createReport: {
      title: "T\u1EA1o b\xE1o c\xE1o",
      description: "T\u1EA1o b\xE1o c\xE1o v\u1EC1 d\u1EF1 \xE1n v\xE0 c\xF4ng vi\u1EC7c.",
      instructions: "Ch\u1ECDn lo\u1EA1i b\xE1o c\xE1o v\xE0 nh\xF3m, sau \u0111\xF3 nh\u1EA5p v\xE0o n\xFAt t\u1EA1o b\xE1o c\xE1o.",
      alternativeInstructions: "Ho\u1EB7c tr\u1EF1c ti\u1EBFp nh\u1EADp y\xEAu c\u1EA7u bao g\u1ED3m \u0111\u1ECBnh d\u1EA1ng v\xE0 kho\u1EA3ng th\u1EDDi gian b\u1EA1n mu\u1ED1n.",
      reportType: "Lo\u1EA1i b\xE1o c\xE1o",
      dailyReport: "B\xE1o c\xE1o h\xE0ng ng\xE0y",
      weeklyReport: "B\xE1o c\xE1o h\xE0ng tu\u1EA7n",
      date: "Ng\xE0y",
      selectWorkspace: "Ch\u1ECDn kh\xF4ng gian l\xE0m vi\u1EC7c",
      noWorkspaces: "Kh\xF4ng c\xF3 kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0o",
      generateReport: "T\u1EA1o b\xE1o c\xE1o",
      todayDailyReportPersonal: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a t\xF4i cho h\xF4m nay",
      todayDailyReportTeam: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a {{teamName}} cho h\xF4m nay",
      yesterdayDailyReportPersonal: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a t\xF4i cho h\xF4m qua",
      yesterdayDailyReportTeam: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a {{teamName}} cho h\xF4m qua",
      dateDailyReportPersonal: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a t\xF4i cho {{date}}",
      dateDailyReportTeam: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng ng\xE0y c\u1EE7a {{teamName}} cho {{date}}",
      weeklyReportPersonal: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng tu\u1EA7n c\u1EE7a t\xF4i cho {{dateRange}}",
      weeklyReportTeam: "Vi\u1EBFt b\xE1o c\xE1o h\xE0ng tu\u1EA7n c\u1EE7a {{teamName}} cho {{dateRange}}"
    },
    dateRangeSelector: {
      date: "Ng\xE0y",
      selectedDate: "Ng\xE0y \u0111\xE3 ch\u1ECDn",
      apply: "\xC1p d\u1EE5ng",
      cancel: "H\u1EE7y",
      clear: "X\xF3a",
      weeklyReportMaxDaysError: "B\xE1o c\xE1o h\xE0ng tu\u1EA7n kh\xF4ng th\u1EC3 v\u01B0\u1EE3t qu\xE1 7 ng\xE0y."
    },
    searchNote: {
      title: "T\xECm ki\u1EBFm ghi ch\xFA",
      description: "T\xECm ki\u1EBFm v\xE0 t\xECm ghi ch\xFA c\u1EE7a b\u1EA1n m\u1ED9t c\xE1ch nhanh ch\xF3ng.",
      instruction: "Th\xEAm # tr\u01B0\u1EDBc t\u1EEB kh\xF3a t\xECm ki\u1EBFm \u0111\u1EC3 t\xECm ki\u1EBFm ghi ch\xFA.",
      placeholder: "T\xECm ki\u1EBFm ghi ch\xFA  #t\u1EEB kh\xF3a",
      examplePrefix: "ex) T\xECm ki\u1EBFm ghi ch\xFA ",
      exampleTag: "#cu\u1ED9c h\u1ECDi \u1ED9 b\xE1n h\xE0ng h\xE0ng tu\u1EA7n"
    }
  },
  common: {
    send: "G\u1EEDi",
    cancel: "H\u1EE7y",
    save: "L\u01B0u",
    delete: "X\xF3a",
    edit: "Ch\u1EC9nh s\u1EEDa",
    loading: "\u0110ang t\u1EA3i...",
    error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i"
  },
  tools: {
    completed: "Ho\xE0n th\xE0nh",
    running: "\u0110ang ch\u1EA1y",
    loading: "\u0110ang ch\u1EA1y...",
    inputParameters: "Tham s\u1ED1 \u0111\u1EA7u v\xE0o",
    visualizationResults: "K\u1EBFt qu\u1EA3 tr\u1EF1c quan h\xF3a:",
    iframeMode: "Ch\u1EBF \u0111\u1ED9 iframe",
    directRendering: "Hi\u1EC3n th\u1ECB tr\u1EF1c ti\u1EBFp",
    loadingVisualizationData: "\u0110ang t\u1EA3i d\u1EEF li\u1EC7u tr\u1EF1c quan h\xF3a...",
    unableToDisplayVisualization: "Kh\xF4ng th\u1EC3 hi\u1EC3n th\u1ECB d\u1EEF li\u1EC7u tr\u1EF1c quan h\xF3a.",
    filename: "T\xEAn t\u1EC7p:",
    viewRawHtml: "Xem HTML g\u1ED1c",
    viewRendered: "Xem hi\u1EC3n th\u1ECB",
    renderingError: "L\u1ED7i hi\u1EC3n th\u1ECB n\u1ED9i dung",
    searchResults: "K\u1EBFt qu\u1EA3 t\xECm ki\u1EBFm",
    noResults: "Kh\xF4ng t\xECm th\u1EA5y k\u1EBFt qu\u1EA3",
    items: "m\u1EE5c",
    item: "m\u1EE5c",
    showMore: "Xem th\xEAm",
    showLess: "Thu g\u1ECDn",
    note: "Ghi ch\xFA",
    visualization: "Tr\u1EF1c quan h\xF3a"
  },
  reportCreation: {
    error: "L\u1ED7i",
    success: "Th\xE0nh c\xF4ng",
    loginRequired: "Y\xEAu c\u1EA7u \u0111\u0103ng nh\u1EADp",
    workspaceRequired: "C\u1EA7n th\xF4ng tin kh\xF4ng gian l\xE0m vi\u1EC7c",
    createButtonClicked: "N\xFAt t\u1EA1o \u0111\xE3 \u0111\u01B0\u1EE3c nh\u1EA5p",
    reportCreated: "{{reportType}} \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o",
    reportCreationError: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o {{reportType}}",
    creationComplete: "T\u1EA1o ho\xE0n t\u1EA5t",
    aiGeneratedReport: "{{reportType}} do AI t\u1EA1o ra \u0111\xE3 s\u1EB5n s\xE0ng",
    clickToAdd: "Nh\u1EA5p v\xE0o n\xFAt b\xEAn d\u01B0\u1EDBi \u0111\u1EC3 th\xEAm {{reportType}}",
    createReport: "T\u1EA1o {{reportType}}",
    workspaceInfo: "C\u1EA7n th\xF4ng tin kh\xF4ng gian l\xE0m vi\u1EC7c"
  },
  searchNote: {
    results: "K\u1EBFt qu\u1EA3 t\xECm ki\u1EBFm",
    itemsFound: "{{count}} m\u1EE5c \u0111\u01B0\u1EE3c t\xECm th\u1EA5y",
    noTitle: "Kh\xF4ng c\xF3 ti\xEAu \u0111\u1EC1",
    noAuthor: "Kh\xF4ng c\xF3 t\xE1c gi\u1EA3",
    viewNote: "Xem ghi ch\xFA"
  },
  todoCreation: {
    loading: "\u0110ang t\u1EA3i th\xF4ng tin c\xF4ng vi\u1EC7c...",
    loadError: "Kh\xF4ng th\u1EC3 t\u1EA3i th\xF4ng tin c\xF4ng vi\u1EC7c",
    creationComplete: "Ho\xE0n th\xE0nh t\u1EA1o c\xF4ng vi\u1EC7c",
    createdAt: "\u0110\u01B0\u1EE3c t\u1EA1o l\xFAc",
    schedule: "L\u1ECBch tr\xECnh",
    category: "Danh m\u1EE5c",
    defaultCategory: "Danh m\u1EE5c",
    goToPlanner: "\u0110i \u0111\u1EBFn l\u1ECBch"
  },
  textInput: {
    placeholder: "Nh\u1EADp tin nh\u1EAFn c\u1EE7a b\u1EA1n...",
    send: "G\u1EEDi",
    errorMessage: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi g\u1EEDi tin nh\u1EAFn. Vui l\xF2ng th\u1EED l\u1EA1i."
  },
  logs: {
    enterKeyUpdate: "Ph\xEDm Enter: C\u1EADp nh\u1EADt v\u1EDBi n\u1ED9i dung m\u1EDBi nh\u1EA5t",
    onSendCallback: "G\u1ECDi h\xE0m onSend - tin nh\u1EAFn \u0111\u1EC3 g\u1EEDi",
    inputFieldCleared: "\u0110\xE3 x\xF3a tr\u01B0\u1EDDng nh\u1EADp li\u1EC7u sau khi g\u1EEDi tin nh\u1EAFn",
    messageSendFailed: "G\u1EEDi tin nh\u1EAFn th\u1EA5t b\u1EA1i"
  },
  settings: {
    title: "C\xE0i \u0111\u1EB7t AI",
    language: "Ng\xF4n ng\u1EEF",
    theme: "Ch\u1EE7 \u0111\u1EC1",
    notifications: "Th\xF4ng b\xE1o",
    privacy: "Quy\u1EC1n ri\xEAng t\u01B0"
  }
};

// src/locales/vi-VN/notification.json
var notification_default3 = {
  defaultMessage: "B\u1EA1n \u0111\xE3 nh\u1EADn \u0111\u01B0\u1EE3c th\xF4ng b\xE1o m\u1EDBi",
  title: "Th\xF4ng b\xE1o",
  emptyState: "Kh\xF4ng c\xF3 th\xF4ng b\xE1o",
  markAsRead: "\u0110\xE1nh d\u1EA5u \u0111\xE3 \u0111\u1ECDc",
  unread_count: "Ch\u01B0a \u0111\u1ECDc ({{count}})",
  all: "T\u1EA5t c\u1EA3",
  timeAgo: "{{time}} tr\u01B0\u1EDBc",
  workspace: {
    invite: "{{inviter_name}} \u0111\xE3 m\u1EDDi b\u1EA1n v\xE0o <0>{{workspace_name}}</0>",
    join: "<0>{{joiner_name}}</0> \u0111\xE3 tham gia <0>{{workspace_name}}</0>",
    out: "B\u1EA1n \u0111\xE3 b\u1ECB x\xF3a kh\u1ECFi <0>{{workspace_name}}</0>"
  },
  team: {
    invite: "{{inviter_name}} \u0111\xE3 m\u1EDDi b\u1EA1n v\xE0o nh\xF3m <0>{{team_name}}</0> trong <0>{{workspace_name}}</0>",
    out: "B\u1EA1n \u0111\xE3 b\u1ECB x\xF3a kh\u1ECFi nh\xF3m <0>{{team_name}}</0> trong <0>{{workspace_name}}</0>"
  },
  task: {
    priorityChanged: "M\u1EE9c \u0111\u1ED9 \u01B0u ti\xEAn c\u1EE7a <0>{{goal_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{new_priority}}</0> b\u1EDFi {{changer_name}}",
    stateChanged: "Tr\u1EA1ng th\xE1i c\u1EE7a <0>{{goal_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{new_state}}</0> b\u1EDFi {{changer_name}}",
    deadlineChanged: "Th\u1EDDi h\u1EA1n c\u1EE7a <0>{{goal_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{date_range}}</0> b\u1EDFi {{changer_name}}",
    leaderChanged: "B\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c ch\u1EC9 \u0111\u1ECBnh l\xE0m ng\u01B0\u1EDDi qu\u1EA3n l\xFD c\u1EE7a <0>{{goal_title}}</0>",
    deadlineReminder: {
      upcoming: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> c\xF2n <0>{{days_delta}}</0> ng\xE0y",
      today: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> l\xE0 <0>h\xF4m nay</0>",
      overdue: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> \u0111\xE3 qu\xE1 h\u1EA1n <0>{{days_delta}}</0> ng\xE0y"
    }
  },
  todo: {
    assigned: "{{assigner_name}} \u0111\xE3 giao <0>{{todo_title}}</0> cho b\u1EA1n",
    stateChanged: "{{changer_name}} \u0111\xE3 thay \u0111\u1ED5i tr\u1EA1ng th\xE1i c\u1EE7a <0>{{todo_title}}</0> th\xE0nh <0>{{new_state}}</0>",
    deadlineChanged: "{{changer_name}} \u0111\xE3 thay \u0111\u1ED5i th\u1EDDi h\u1EA1n c\u1EE7a <0>{{todo_title}}</0> th\xE0nh <0>{{date_range}}</0>",
    timeChanged: "{{changer_name}} \u0111\xE3 thay \u0111\u1ED5i th\u1EDDi gian c\u1EE7a <0>{{todo_title}}</0>",
    deadlineReminder: {
      upcoming: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> c\xF2n <0>{{days_delta}}</0> ng\xE0y",
      today: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> l\xE0 <0>h\xF4m nay</0>",
      overdue: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> \u0111\xE3 qu\xE1 h\u1EA1n <0>{{days_delta}}</0> ng\xE0y"
    },
    plan_time: "\u0110\xE3 \u0111\u1EBFn l\xFAc l\u1EADp k\u1EBF ho\u1EA1ch cho h\xF4m nay."
  },
  project: {
    priorityChanged: "M\u1EE9c \u0111\u1ED9 \u01B0u ti\xEAn c\u1EE7a <0>{{project_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{new_priority}}</0> b\u1EDFi {{changer_name}}",
    stateChanged: "Tr\u1EA1ng th\xE1i c\u1EE7a <0>{{project_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{new_state}}</0> b\u1EDFi {{changer_name}}",
    deadlineChanged: "Th\u1EDDi h\u1EA1n c\u1EE7a <0>{{project_title}}</0> \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i th\xE0nh <0>{{date_range}}</0> b\u1EDFi {{changer_name}}",
    leaderChanged: "B\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c ch\u1EC9 \u0111\u1ECBnh l\xE0m ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o c\u1EE7a <0>{{project_title}}</0>",
    deadlineReminder: {
      upcoming: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> c\xF2n <0>{{days_delta}}</0> ng\xE0y",
      today: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> l\xE0 <0>h\xF4m nay</0>",
      overdue: "Th\u1EDDi h\u1EA1n cho <0>{{title}}</0> \u0111\xE3 qu\xE1 h\u1EA1n <0>{{days_delta}}</0> ng\xE0y"
    }
  },
  communication: {
    comment: {
      goal: "<0>{{commenter_name}}</0> \u0111\xE3 b\xECnh lu\u1EADn v\u1EC1 <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      todo: "<0>{{commenter_name}}</0> \u0111\xE3 b\xECnh lu\u1EADn v\u1EC1 <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      project: "<0>{{commenter_name}}</0> \u0111\xE3 b\xECnh lu\u1EADn v\u1EC1 <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      note: "<0>{{commenter_name}}</0> \u0111\xE3 b\xECnh lu\u1EADn v\u1EC1 <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'",
      default: "<0>{{commenter_name}}</0> \u0111\xE3 b\xECnh lu\u1EADn v\u1EC1 <0>{{parent_title}}</0>: '<0>{{comment_text}}</0>'"
    },
    reply: "<0>{{replier_name}}</0> \u0111\xE3 tr\u1EA3 l\u1EDDi: '<0>{{reply_text}}</0>'",
    mention: "<0>{{mentioner_name}}</0> \u0111\xE3 \u0111\u1EC1 c\u1EADp \u0111\u1EBFn b\u1EA1n trong <0>{{context_title}}</0>",
    plan_request: "<0>{{requester_name}}</0> \u0111\xE3 y\xEAu c\u1EA7u b\u1EA1n c\u1EADp nh\u1EADt Planner c\u1EE7a b\u1EA1n cho h\xF4m nay!",
    comment_mention: {
      note: "<0>{{mentioner_name}}</0> \u0111\xE3 \u0111\u1EC1 c\u1EADp \u0111\u1EBFn b\u1EA1n trong b\xECnh lu\u1EADn <0>{{context_title}}</0>: '<0>{{comment_text}}</0>'",
      todo: "<0>{{mentioner_name}}</0> \u0111\xE3 \u0111\u1EC1 c\u1EADp \u0111\u1EBFn b\u1EA1n trong b\xECnh lu\u1EADn <0>{{context_title}}</0>: '<0>{{comment_text}}</0>'",
      goal: "<0>{{mentioner_name}}</0> \u0111\xE3 \u0111\u1EC1 c\u1EADp \u0111\u1EBFn b\u1EA1n trong b\xECnh lu\u1EADn <0>{{context_title}}</0>: '<0>{{comment_text}}</0>'"
    },
    feedback_comment: "<0>{{commenter_name}}</0> \u0111\xE3 \u0111\u1EC3 l\u1EA1i b\xECnh lu\u1EADn v\u1EC1 <0>{{feedback_content}}</0>: '<0>{{comment_text}}</0>'",
    feedback_reply: "<0>{{commenter_name}}</0> \u0111\xE3 ph\u1EA3n h\u1ED3i v\u1EC1 <0>{{feedback_content}}</0>: '<0>{{reply_text}}</0>'",
    daily_scrum_comment: "<0>{{commenter_name}}</0> \u0111\xE3 \u0111\u1EC3 l\u1EA1i b\xECnh lu\u1EADn trong Daily Scrum ng\xE0y <0>{{daily_scrum_date}}</0>: '<0>{{comment_text}}</0>'",
    daily_scrum_reply: "<0>{{commenter_name}}</0> \u0111\xE3 ph\u1EA3n h\u1ED3i trong Daily Scrum ng\xE0y <0>{{daily_scrum_date}}</0>: '<0>{{reply_text}}</0>'"
  },
  system: {
    announcement: "[Th\xF4ng b\xE1o] {{title}}",
    security: "C\u1EA3nh b\xE1o b\u1EA3o m\u1EADt t\xE0i kho\u1EA3n: {{message}}"
  },
  performance: {
    feedback_request: "<0>{{requester_name}}</0> \u0111\xE3 y\xEAu c\u1EA7u b\u1EA1n \u0111\xE1nh gi\xE1 ph\u1EA3n h\u1ED3i. <0>{{content}}</0>",
    feedback_received: "<0>{{writer_name}}</0> \u0111\xE3 g\u1EEDi ph\u1EA3n h\u1ED3i cho b\u1EA1n. <0>{{content}}</0>",
    feedback_updated: "<0>{{writer_name}}</0> \u0111\xE3 c\u1EADp nh\u1EADt v\xE0 g\u1EEDi l\u1EA1i ph\u1EA3n h\u1ED3i. <0>{{content}}</0>",
    feedback_request_updated: "<0>{{requester_name}}</0> \u0111\xE3 c\u1EADp nh\u1EADt v\xE0 g\u1EEDi l\u1EA1i y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i. <0>{{content}}</0>",
    feedback_request_rejected: "<0>{{name}}</0> \u0111\xE3 t\u1EEB ch\u1ED1i y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i c\u1EE7a b\u1EA1n.",
    report_receive: "<0>{{writer_name}}</0> \u0111\xE3 g\u1EEDi cho b\u1EA1n <0>{{report_type}}</0>.",
    report_updated: "<0>{{writer_name}}</0> \u0111\xE3 c\u1EADp nh\u1EADt v\xE0 g\u1EEDi l\u1EA1i <0>{{report_type}}</0>.",
    report_request: "<0>{{requester_name}}</0> \u0111\xE3 y\xEAu c\u1EA7u b\u1EA1n g\u1EEDi <0>{{report_type}}</0>.",
    feedback_reaction: "<0>{{reactioner_name}}</0> \u0111\xE3 ph\u1EA3n h\u1ED3i v\u1EC1 <0>{{feedback_content}}</0>."
  },
  typeLabel: {
    account_security: "B\u1EA3o m\u1EADt",
    comment: {
      goal: "B\xECnh lu\u1EADn nhi\u1EC7m v\u1EE5",
      note: "B\xECnh lu\u1EADn ghi ch\xFA",
      todo: "B\xECnh lu\u1EADn vi\u1EC7c"
    },
    feedback_received: "Ph\u1EA3n h\u1ED3i",
    feedback_updated: "Ph\u1EA3n h\u1ED3i",
    feedback_request: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    feedback_request_updated: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    feedback_request_rejected: "T\u1EEB ch\u1ED1i ph\u1EA3n h\u1ED3i",
    goal_deadline_changed: "H\u1EA1n nhi\u1EC7m v\u1EE5",
    goal_deadline_reminder: "Nh\u1EAFc h\u1EA1n nhi\u1EC7m v\u1EE5",
    goal_leader_changed: "Qu\u1EA3n l\xFD nhi\u1EC7m v\u1EE5",
    goal_priority_changed: "\u01AFu ti\xEAn nhi\u1EC7m v\u1EE5",
    goal_state_changed: "Tr\u1EA1ng th\xE1i nhi\u1EC7m v\u1EE5",
    mention: {
      note: "\u0110\u1EC1 c\u1EADp ghi ch\xFA"
    },
    comment_mention: {
      note: "\u0110\u1EC1 c\u1EADp b\xECnh lu\u1EADn",
      todo: "\u0110\u1EC1 c\u1EADp vi\u1EC7c",
      goal: "\u0110\u1EC1 c\u1EADp nhi\u1EC7m v\u1EE5"
    },
    feedback_comment: "Ph\u1EA3n h\u1ED3i b\xECnh lu\u1EADn",
    daily_scrum_comment: "B\xECnh lu\u1EADn Daily Scrum",
    plan_request: "Y\xEAu c\u1EA7u Planner",
    plan_time: "L\u1ECBch ng\xE0y",
    project_deadline_changed: "H\u1EA1n d\u1EF1 \xE1n",
    project_deadline_reminder: "Nh\u1EAFc h\u1EA1n d\u1EF1 \xE1n",
    project_leader_changed: "L\xE3nh \u0111\u1EA1o d\u1EF1 \xE1n",
    project_priority_changed: "\u01AFu ti\xEAn d\u1EF1 \xE1n",
    project_state_changed: "Tr\u1EA1ng th\xE1i d\u1EF1 \xE1n",
    reply: "Tr\u1EA3 l\u1EDDi",
    report_receive: "B\xE1o c\xE1o m\u1EDBi",
    report_request: "Y\xEAu c\u1EA7u b\xE1o c\xE1o",
    report_updated: "C\u1EADp nh\u1EADt b\xE1o c\xE1o",
    system_announcement: "Th\xF4ng b\xE1o",
    team_invite: "M\u1EDDi nh\xF3m",
    team_out: "R\u1EDDi nh\xF3m",
    todo_assigned: "Giao vi\u1EC7c",
    todo_deadline_changed: "H\u1EA1n vi\u1EC7c",
    todo_deadline_reminder: "Nh\u1EAFc h\u1EA1n vi\u1EC7c",
    todo_state_changed: "Tr\u1EA1ng th\xE1i vi\u1EC7c",
    todo_time_changed: "Th\u1EDDi gian vi\u1EC7c",
    workspace_invite: "M\u1EDDi workspace",
    workspace_join: "Tham gia workspace",
    workspace_out: "R\u1EDDi workspace",
    feedback_reaction: "Ph\u1EA3n \u1EE9ng ph\u1EA3n h\u1ED3i"
  }
};

// src/locales/vi-VN/dailyscrum.json
var dailyscrum_default3 = {
  template: {
    template_list: "Danh s\xE1ch m\u1EABu",
    manage_template: "Qu\u1EA3n l\xFD m\u1EABu",
    new_template: "M\u1EABu m\u1EDBi",
    create_template: "T\u1EA1o m\u1EABu m\u1EDBi",
    edit_template: "Ch\u1EC9nh s\u1EEDa m\u1EABu",
    select_template: "Ch\u1ECDn m\u1EABu",
    select_template_message: "Vui l\xF2ng ch\u1ECDn m\u1ED9t m\u1EABu \u0111\u1EC3 xem chi ti\u1EBFt.",
    no_templates: "Kh\xF4ng c\xF3 m\u1EABu n\xE0o kh\u1EA3 d\u1EE5ng",
    default: "M\u1EB7c \u0111\u1ECBnh",
    edit: "Ch\u1EC9nh s\u1EEDa",
    delete: "X\xF3a",
    no_permission: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n ch\u1EC9nh s\u1EEDa m\u1EABu n\xE0y.",
    delete_success: "X\xF3a m\u1EABu",
    delete_success_message: "M\u1EABu \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
    delete_error: "X\xF3a th\u1EA5t b\u1EA1i",
    delete_error_message: "Kh\xF4ng th\u1EC3 x\xF3a m\u1EABu. Vui l\xF2ng th\u1EED l\u1EA1i.",
    default_template_notice: "Kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa ho\u1EB7c x\xF3a c\xE1c m\u1EABu m\u1EB7c \u0111\u1ECBnh.",
    default_templates: "M\u1EABu m\u1EB7c \u0111\u1ECBnh",
    team_templates: "M\u1EABu nh\xF3m",
    create_success: "T\u1EA1o m\u1EABu th\xE0nh c\xF4ng",
    create_success_message: "M\u1EABu \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
    create_error: "T\u1EA1o m\u1EABu th\u1EA5t b\u1EA1i",
    create_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o m\u1EABu.",
    update_success: "C\u1EADp nh\u1EADt m\u1EABu",
    update_success_message: "M\u1EABu \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt th\xE0nh c\xF4ng.",
    update_error: "C\u1EADp nh\u1EADt m\u1EABu th\u1EA5t b\u1EA1i",
    update_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi c\u1EADp nh\u1EADt m\u1EABu.",
    create_first_template: "T\u1EA1o M\u1EABu \u0110\u1EA7u Ti\xEAn",
    remove_block: "X\xF3a Kh\u1ED1i",
    validation_error: "L\u1ED7i x\xE1c th\u1EF1c",
    title_required: "Ti\xEAu \u0111\u1EC1 l\xE0 b\u1EAFt bu\u1ED9c",
    default_template_text: "M\u1EABu m\u1EB7c \u0111\u1ECBnh hi\u1EC7n t\u1EA1i c\u1EE7a nh\xF3m",
    user_team_info_missing: "Thi\u1EBFu th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng ho\u1EB7c nh\xF3m",
    user_info_missing: "Thi\u1EBFu th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng",
    user_template_info_missing: "Thi\u1EBFu th\xF4ng tin ng\u01B0\u1EDDi d\xF9ng ho\u1EB7c m\u1EABu",
    created_date: "Ng\xE0y t\u1EA1o",
    set_as_default: "\u0110\u1EB7t l\xE0m m\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m",
    default_set_success: "\u0110\xE3 \u0111\u1EB7t m\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m",
    default_set_success_message: "M\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m \u0111\xE3 \u0111\u01B0\u1EE3c thi\u1EBFt l\u1EADp th\xE0nh c\xF4ng.",
    default_set_error: "Thi\u1EBFt l\u1EADp m\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m th\u1EA5t b\u1EA1i",
    default_set_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi thi\u1EBFt l\u1EADp m\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m.",
    team_info_missing: "Thi\u1EBFu th\xF4ng tin nh\xF3m",
    default_template_team_notice: "Khi \u0111\u01B0\u1EE3c \u0111\u1EB7t l\xE0m m\u1EABu m\u1EB7c \u0111\u1ECBnh c\u1EE7a nh\xF3m, n\xF3 s\u1EBD \u0111\u01B0\u1EE3c \xE1p d\u1EE5ng cho to\xE0n b\u1ED9 nh\xF3m.",
    delete_confirmation: "X\xF3a m\u1EABu",
    delete_template_message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a m\u1EABu n\xE0y kh\xF4ng? H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c.",
    delete_default_template_message: "M\u1EABu n\xE0y hi\u1EC7n \u0111ang \u0111\u01B0\u1EE3c \u0111\u1EB7t l\xE0m m\u1EABu c\u1EE7a nh\xF3m. B\u1EA1n c\xF3 mu\u1ED1n x\xF3a n\xF3 kh\xF4ng?"
  },
  form: {
    title: "Ti\xEAu \u0111\u1EC1",
    title_placeholder: "Nh\u1EADp ti\xEAu \u0111\u1EC1 m\u1EABu",
    content: "N\u1ED9i dung",
    content_placeholder: "Nh\u1EADp n\u1ED9i dung m\u1EABu",
    save: "L\u01B0u",
    cancel: "H\u1EE7y",
    back: "Quay l\u1EA1i",
    required: "B\u1EAFt bu\u1ED9c",
    optional: "T\xF9y ch\u1ECDn",
    required_field: "Tr\u01B0\u1EDDng n\xE0y l\xE0 b\u1EAFt bu\u1ED9c",
    placeholder: {
      title: "Nh\u1EADp ti\xEAu \u0111\u1EC1",
      content: "Nh\u1EADp n\u1ED9i dung"
    }
  },
  modal: {
    create_template: "T\u1EA1o m\u1EABu m\u1EDBi",
    edit_template: "Ch\u1EC9nh s\u1EEDa m\u1EABu",
    delete_confirmation: "X\xF3a m\u1EABu",
    delete_confirmation_message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a m\u1EABu n\xE0y kh\xF4ng? H\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c.",
    confirm: "X\xE1c nh\u1EADn",
    cancel: "H\u1EE7y",
    daily_scrum: "Daily Scrum"
  },
  dailyscrum: {
    title: "Daily Scrum",
    write_title: "Vi\u1EBFt Daily Scrum",
    cancel: "H\u1EE7y",
    complete: "Ho\xE0n th\xE0nh",
    save: "L\u01B0u",
    ai_generate: "T\u1EA1o Daily Scrum b\u1EB1ng AI",
    writing_mode: "Ch\u1EBF \u0111\u1ED9 vi\u1EBFt",
    content_required: "Vui l\xF2ng nh\u1EADp n\u1ED9i dung daily scrum.",
    save_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u. Vui l\xF2ng th\u1EED l\u1EA1i.",
    write_today_scrum: "Vui l\xF2ng vi\u1EBFt daily scrum c\u1EE7a h\xF4m nay",
    loading: "\u0110ang t\u1EA3i daily scrums...",
    error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA3i d\u1EEF li\u1EC7u.",
    no_scrums: "Kh\xF4ng c\xF3 daily scrum n\xE0o \u0111\u01B0\u1EE3c vi\u1EBFt v\xE0o",
    unknown_user: "Ng\u01B0\u1EDDi d\xF9ng kh\xF4ng x\xE1c \u0111\u1ECBnh",
    user: "Ng\u01B0\u1EDDi d\xF9ng",
    edit: "Ch\u1EC9nh s\u1EEDa",
    delete: "X\xF3a",
    item_update_success: "C\u1EADp nh\u1EADt ho\xE0n t\u1EA5t",
    item_update_success_message: "Daily scrum \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt th\xE0nh c\xF4ng.",
    item_update_error: "C\u1EADp nh\u1EADt th\u1EA5t b\u1EA1i",
    item_update_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi c\u1EADp nh\u1EADt daily scrum.",
    item_delete_success: "X\xF3a ho\xE0n t\u1EA5t",
    item_delete_success_message: "M\u1EE5c daily scrum \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
    item_delete_error: "X\xF3a th\u1EA5t b\u1EA1i",
    item_delete_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a m\u1EE5c daily scrum.",
    delete_item_title: "X\xF3a Daily Scrum",
    delete_item_message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a m\u1EE5c daily scrum n\xE0y kh\xF4ng?\nN\u1ED9i dung \u0111\xE3 x\xF3a kh\xF4ng th\u1EC3 kh\xF4i ph\u1EE5c.",
    unsaved_content_title: "H\u1EE7y c\xE1c thay \u0111\u1ED5i?",
    unsaved_content_message: "B\u1EA1n c\xF3 c\xE1c thay \u0111\u1ED5i ch\u01B0a \u0111\u01B0\u1EE3c l\u01B0u. B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n h\u1EE7y ch\xFAng kh\xF4ng?",
    continue_writing: "Ti\u1EBFp t\u1EE5c ch\u1EC9nh s\u1EEDa",
    exit: "\u0110\xF3ng v\xE0 h\u1EE7y b\u1ECF"
  },
  todolist: {
    loading: "\u0110ang t\u1EA3i...",
    yesterday_tasks: "C\xF4ng vi\u1EC7c h\xF4m qua",
    today_tasks: "C\xF4ng vi\u1EC7c h\xF4m nay",
    task_history: "L\u1ECBch s\u1EED c\xF4ng vi\u1EC7c"
  }
};

// src/locales/vi-VN/feedback.json
var feedback_default3 = {
  title: "Nh\u1EADn x\xE9t",
  setting: {
    title: "C\xE0i \u0111\u1EB7t ph\u1EA3n h\u1ED3i",
    coreValue: {
      title: "Gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      setup: "Thi\u1EBFt l\u1EADp gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      empty: "Kh\xF4ng c\xF3 gi\xE1 tr\u1ECB c\u1ED1t l\xF5i n\xE0o.",
      tooltip: "\u0110\xE2y l\xE0 nh\u1EEFng gi\xE1 tr\u1ECB c\u1ED1t l\xF5i m\xE0 t\u1ED5 ch\u1EE9c v\xE0 c\xE1c th\xE0nh vi\xEAn c\u1EE7a ch\xFAng ta h\u01B0\u1EDBng t\u1EDBi. Khi g\u1EEDi ph\u1EA3n h\u1ED3i, b\u1EA1n c\xF3 th\u1EC3 ch\u1ECDn c\xE1c gi\xE1 tr\u1ECB m\xE0 th\xE0nh vi\xEAn \u0111\xE3 th\u1EF1c hi\u1EC7n t\u1ED1t ho\u1EB7c c\u1EA7n c\u1EA3i thi\u1EC7n, v\xE0 \u0111\u01B0a ch\xFAng v\xE0o ph\u1EA3n h\u1ED3i c\u1EE7a b\u1EA1n.",
      addTitle: "Th\xEAm gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      placeholder: "V\xED d\u1EE5: Th\u1EED th\xE1ch, Ch\u1EE7 \u0111\u1ED9ng, v.v.",
      addButton: "Th\xEAm",
      addSuccess: "\u0110\xE3 th\xEAm gi\xE1 tr\u1ECB c\u1ED1t l\xF5i th\xE0nh c\xF4ng",
      addSuccessMessage: "'{{value}}' \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0o l\xE0m gi\xE1 tr\u1ECB c\u1ED1t l\xF5i.",
      addError: "Kh\xF4ng th\u1EC3 th\xEAm gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      addErrorMessage: "Gi\xE1 tr\u1ECB c\u1ED1t l\xF5i n\xE0y \u0111\xE3 t\u1ED3n t\u1EA1i.",
      deleteSuccess: "\u0110\xE3 x\xF3a gi\xE1 tr\u1ECB c\u1ED1t l\xF5i th\xE0nh c\xF4ng",
      deleteSuccessMessage: "Gi\xE1 tr\u1ECB c\u1ED1t l\xF5i '{{value}}' \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
      deleteError: "Kh\xF4ng th\u1EC3 x\xF3a gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      deleteConfirmTitle: "X\xF3a gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
      deleteConfirmMessage: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a gi\xE1 tr\u1ECB c\u1ED1t l\xF5i '{{value}}'?",
      deleteNote: "C\xE1c gi\xE1 tr\u1ECB c\u1ED1t l\xF5i \u0111\xE3 x\xF3a kh\xF4ng th\u1EC3 kh\xF4i ph\u1EE5c, nh\u01B0ng \u0111i\u1EC1u n\xE0y s\u1EBD kh\xF4ng \u1EA3nh h\u01B0\u1EDFng \u0111\u1EBFn ph\u1EA3n h\u1ED3i \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi.",
      deleteConfirm: "X\xF3a"
    }
  },
  common: {
    cancel: "H\u1EE7y"
  },
  action: {
    requestFeedback: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    sendFeedback: "G\u1EEDi ph\u1EA3n h\u1ED3i",
    addBookmark: "\u0110\xE1nh d\u1EA5u",
    cancelRequest: "H\u1EE7y y\xEAu c\u1EA7u",
    delete: "X\xF3a b\u1ECF",
    doneEdit: "Ch\u1EC9nh s\u1EEDa ho\xE0n th\xE0nh",
    edit: "Bi\xEAn t\u1EADp",
    editRequest: "Ch\u1EC9nh s\u1EEDa y\xEAu c\u1EA7u",
    removeBookmark: "X\xF3a d\u1EA5u trang",
    resend: "Chia r\u1EBD",
    editFeedback: "Ch\u1EC9nh s\u1EEDa ph\u1EA3n h\u1ED3i"
  },
  placeHolder: {
    received: "B\u1EA1n ch\u01B0a nh\u1EADn \u0111\u01B0\u1EE3c ph\u1EA3n h\u1ED3i n\xE0o!",
    sent: "B\u1EA1n ch\u01B0a g\u1EEDi ph\u1EA3n h\u1ED3i n\xE0o!",
    receivedRequest: "B\u1EA1n ch\u01B0a nh\u1EADn \u0111\u01B0\u1EE3c y\xEAu c\u1EA7u n\xE0o!",
    sentRequest: "B\u1EA1n ch\u01B0a g\u1EEDi y\xEAu c\u1EA7u n\xE0o!"
  },
  tabs: {
    received: "Nh\u1EADn \u0111\u01B0\u1EE3c ph\u1EA3n h\u1ED3i",
    receivedRequest: "Nh\u1EADn \u0111\u01B0\u1EE3c y\xEAu c\u1EA7u",
    sent: "\u0110\xE3 g\u1EEDi ph\u1EA3n h\u1ED3i",
    sentRequest: "\u0110\xE3 g\u1EEDi y\xEAu c\u1EA7u"
  },
  deleteFeedback: {
    receiver_message: "Ph\u1EA3n h\u1ED3i n\xE0y s\u1EBD \u0111\u01B0\u1EE3c x\xF3a kh\u1ECFi danh s\xE1ch c\u1EE7a b\u1EA1n. \nNg\u01B0\u1EDDi g\u1EEDi v\u1EABn c\xF3 th\u1EC3 nh\xECn th\u1EA5y n\xF3.",
    sender_message: "Ph\u1EA3n h\u1ED3i n\xE0y s\u1EBD \u0111\u01B0\u1EE3c x\xF3a kh\u1ECFi danh s\xE1ch c\u1EE7a b\u1EA1n. \nNg\u01B0\u1EDDi nh\u1EADn v\u1EABn s\u1EBD c\xF3 th\u1EC3 nh\xECn th\u1EA5y n\xF3.",
    title: "X\xF3a ph\u1EA3n h\u1ED3i",
    error: "Kh\xF4ng x\xF3a ph\u1EA3n h\u1ED3i. \nH\xE3y th\u1EED l\u1EA1i.",
    success: "Ph\u1EA3n h\u1ED3i \u0111\xE3 x\xF3a th\xE0nh c\xF4ng."
  },
  bookmark: {
    add: {
      message: "\u0110\xE1nh d\u1EA5u ph\u1EA3n h\u1ED3i n\xE0y?",
      success: "Th\xEAm th\xE0nh c\xF4ng v\xE0o d\u1EA5u trang.",
      title: "Th\xEAm v\xE0o d\u1EA5u trang"
    },
    error: "Kh\xF4ng c\u1EADp nh\u1EADt tr\u1EA1ng th\xE1i \u0111\xE1nh d\u1EA5u.",
    placeHolder: "H\xE3y th\u1EED \u0111\xE1nh d\u1EA5u ph\u1EA3n h\u1ED3i\n\nB\u1EA1n \u0111\xE3 nh\u1EADn \u0111\u01B0\u1EE3c.",
    remove: {
      message: "X\xF3a ph\u1EA3n h\u1ED3i n\xE0y kh\u1ECFi d\u1EA5u trang?",
      success: "X\xF3a th\xE0nh c\xF4ng kh\u1ECFi d\u1EA5u trang.",
      title: "X\xF3a d\u1EA5u trang"
    },
    title: "\u0110\xE1nh d\u1EA5u ph\u1EA3n h\u1ED3i"
  },
  cancelRequest: {
    message: "B\u1EA1n c\xF3 ch\u1EAFc l\xE0 b\u1EA1n mu\u1ED1n h\u1EE7y y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i n\xE0y?",
    title: "H\u1EE7y y\xEAu c\u1EA7u",
    error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong khi h\u1EE7y y\xEAu c\u1EA7u.",
    success: "\u0110\xE3 h\u1EE7y b\u1ECF th\xE0nh c\xF4ng y\xEAu c\u1EA7u."
  },
  coreValue: {
    allowMultiple: "Nhi\u1EC1u l\u1EF1a ch\u1ECDn cho ph\xE9p",
    negative: "C\xE1c l\u0129nh v\u1EF1c \u0111\u1EC3 c\u1EA3i thi\u1EC7n",
    positive: "Ph\u1EA3n h\u1ED3i t\xEDch c\u1EF1c"
  },
  deleteRequest: {
    message: "B\u1EA1n c\xF3 ch\u1EAFc l\xE0 b\u1EA1n mu\u1ED1n x\xF3a y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i n\xE0y?",
    title: "X\xF3a y\xEAu c\u1EA7u",
    error: "Kh\xF4ng x\xF3a y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i. \nH\xE3y th\u1EED l\u1EA1i.",
    success: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i \u0111\xE3 x\xF3a th\xE0nh c\xF4ng."
  },
  editFeedback: {
    title: "Ch\u1EC9nh s\u1EEDa ph\u1EA3n h\u1ED3i"
  },
  editRequest: {
    title: "Ch\u1EC9nh s\u1EEDa y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i"
  },
  feedbackCard: {
    requestTitle: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    seeRequest: "Xem y\xEAu c\u1EA7u"
  },
  feedbackModal: {
    feedbackPlaceHolder: "Nh\u1EADp ph\u1EA3n h\u1ED3i c\u1EE7a b\u1EA1n v\xE0o \u0111\xE2y.",
    positivePlaceHolder: "\u0110i\u1EC1u g\xEC n\u1ED5i b\u1EADt trong c\xF4ng vi\u1EC7c n\xE0y?",
    negativePlaceHolder: "\u0110i\u1EC1u g\xEC c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c c\u1EA3i thi\u1EC7n trong c\xF4ng vi\u1EC7c n\xE0y?",
    receiver: "Ng\u01B0\u1EDDi nh\u1EADn ph\u1EA3n h\u1ED3i",
    receiverPlaceHolder: "Vui l\xF2ng ch\u1ECDn ng\u01B0\u1EDDi nh\u1EADn ph\u1EA3n h\u1ED3i",
    requestPlaceHolder: "Nh\u1EADp y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i c\u1EE7a b\u1EA1n v\xE0o \u0111\xE2y.",
    requestReceiver: "Y\xEAu c\u1EA7u ng\u01B0\u1EDDi nh\u1EADn",
    requestReceiverPlaceHolder: "Vui l\xF2ng ch\u1ECDn ng\u01B0\u1EDDi nh\u1EADn y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    requestTitle: "Y\xEAu c\u1EA7u chi ti\u1EBFt",
    selectCoreValue: "Ch\u1ECDn gi\xE1 tr\u1ECB c\u1ED1t l\xF5i",
    selectReceiver: "Ch\u1ECDn ng\u01B0\u1EDDi nh\u1EADn",
    selectRequestReceiver: "Ch\u1ECDn Y\xEAu c\u1EA7u Ng\u01B0\u1EDDi nh\u1EADn",
    todoList: "{{name}} 'S L\u1ECBch s\u1EED vi\u1EC7c c\u1EA7n l\xE0m",
    todoListDescription: {
      feedback: "Ch\u1ECDn to-dos li\xEAn quan \u0111\u1EBFn ph\u1EA3n h\u1ED3i c\u1EE7a b\u1EA1n.",
      request: "Ch\u1ECDn to-dos b\u1EA1n mu\u1ED1n ph\u1EA3n h\u1ED3i.",
      respond: "Vi\u1EC7c l\xE0m \u0111\u01B0\u1EE3c bao g\u1ED3m trong y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i."
    },
    aiOptimization: "T\u1ED1i \u01B0u h\xF3a AI",
    feedbackScoring: "Ghi \u0111i\u1EC3m ph\u1EA3n h\u1ED3i",
    selectedTodo: "\u0110\xE3 ch\u1ECDn To-dos ({{count}})",
    unsavedContent: "Thay \u0111\u1ED5i ch\u01B0a l\u01B0u",
    unsavedContentDetail: "B\u1EA1n c\xF3 thay \u0111\u1ED5i ch\u01B0a l\u01B0u. B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n h\u1EE7y c\xE1c thay \u0111\u1ED5i n\xE0y?",
    refreshRequest: {
      button: "L\xE0m cho kh\u1ECFe l\u1EA1i",
      tooltip: "Y\xEAu c\u1EA7u \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt.\nL\xE0m m\u1EDBi n\xF3 \u0111\u1EC3 \u0111\u01B0a ra n\u1ED9i dung m\u1EDBi nh\u1EA5t!"
    }
  },
  rejectFeedback: {
    message: "T\u1EEB ch\u1ED1i y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i t\u1EEB {{name}}?",
    title: "T\u1EEB ch\u1ED1i y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i",
    error: "Kh\xF4ng t\u1EEB ch\u1ED1i y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i. \nH\xE3y th\u1EED l\u1EA1i.",
    success: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i b\u1ECB t\u1EEB ch\u1ED1i th\xE0nh c\xF4ng."
  },
  sendFeedback: {
    title: "G\u1EEDi ph\u1EA3n h\u1ED3i \u0111\u1EBFn {{name}}",
    error: "Kh\xF4ng g\u1EEDi ph\u1EA3n h\u1ED3i. \nH\xE3y th\u1EED l\u1EA1i.",
    success: "Ph\u1EA3n h\u1ED3i \u0111\u01B0\u1EE3c g\u1EEDi th\xE0nh c\xF4ng.",
    requestInvalid: "\u0110\xE3 h\u1EE7y b\u1ECF l\u1EA1i",
    requestInvalidDetail: "Ng\u01B0\u1EDDi y\xEAu c\u1EA7u \u0111\xE3 h\u1EE7y y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i.\nB\u1EA1n c\xF3 mu\u1ED1n g\u1EEDi ph\u1EA3n h\u1ED3i m\u1EB7c d\xF9 kh\xF4ng?"
  },
  summary: {
    coreValue: "{{name}} c\xE1c gi\xE1 tr\u1ECB l\xF5i",
    subTitle: "V\u1EC1 {{name}}",
    title: "T\xF3m t\u1EAFt ph\u1EA3n h\u1ED3i AI"
  },
  error: {
    notPermission: "B\u1EA1n kh\xF4ng c\xF3 quy\u1EC1n. Ch\u1EC9 qu\u1EA3n tr\u1ECB vi\xEAn ho\u1EB7c ch\u1EE7 s\u1EDF h\u1EEFu m\u1EDBi c\xF3 th\u1EC3 s\u1EEDa \u0111\u1ED5i.",
    notFound: "\u0110\xE3 x\u1EA3y ra l\u1ED7i kh\xF4ng x\xE1c \u0111\u1ECBnh. Vui l\xF2ng th\u1EED l\u1EA1i sau."
  },
  todoBadgeList: {
    collapse: "S\u1EE5p \u0111\u1ED5",
    seeMore: "{{count}} Th\xEAm",
    expand: "M\u1EDF r\u1ED9ng"
  },
  todoList: {
    feedbackPlaceHolder: "Kh\xF4ng c\xF3 TOS \u0111\u01B0\u1EE3c t\xECm th\u1EA5y trong giai \u0111o\u1EA1n n\xE0y.",
    goalTitlePlaceHolder: "Kh\xF4ng c\xF3 nhi\u1EC7m v\u1EE5",
    requestPlaceHolder: "Kh\xF4ng c\xF3 to-dos bao g\u1ED3m trong y\xEAu c\u1EA7u n\xE0y.",
    select: "L\u1EF1a ch\u1ECDn",
    unselect: "Di d\u1EDDi",
    selectAll: "Ch\u1ECDn t\u1EA5t c\u1EA3",
    unselectAll: "Kh\xF4ng ch\u1ECDn t\u1EA5t c\u1EA3"
  },
  sendRequest: {
    error: "Kh\xF4ng g\u1EEDi y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i. \nH\xE3y th\u1EED l\u1EA1i.",
    success: "Y\xEAu c\u1EA7u ph\u1EA3n h\u1ED3i \u0111\u01B0\u1EE3c g\u1EEDi th\xE0nh c\xF4ng."
  }
};

// src/locales/vi-VN/drive.json
var drive_default3 = {
  search_placeholder: "T\xECm ki\u1EBFm ghi ch\xFA theo ti\xEAu \u0111\u1EC1",
  select_workspace: "Ch\u1ECDn kh\xF4ng gian l\xE0m vi\u1EC7c",
  select_location: "Ch\u1ECDn v\u1ECB tr\xED",
  location_todo: "Vi\u1EC7c c\u1EA7n l\xE0m",
  location_goal: "Nhi\u1EC7m v\u1EE5",
  start_date: "Ng\xE0y b\u1EAFt \u0111\u1EA7u",
  end_date: "Ng\xE0y k\u1EBFt th\xFAc",
  no_notes_found: "Kh\xF4ng t\xECm th\u1EA5y ghi ch\xFA n\xE0o",
  no_notes: "Kh\xF4ng t\xECm th\u1EA5y ghi ch\xFA n\xE0o",
  created_by: "T\u1EA1o b\u1EDFi",
  load_more: "T\u1EA3i th\xEAm",
  showing_results: "Hi\u1EC3n th\u1ECB {{count}} trong t\u1ED5ng s\u1ED1 {{total}} k\u1EBFt qu\u1EA3",
  filter: {
    title: "B\u1ED9 l\u1ECDc",
    workspace: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
    creator: "T\u1EA1o b\u1EDFi",
    location: "V\u1ECB tr\xED",
    date: "Ng\xE0y t\u1EA1o",
    bookmark: "\u0110\xE1nh d\u1EA5u"
  },
  date_today: "H\xF4m nay",
  date_last7days: "7 ng\xE0y qua",
  date_last14days: "14 ng\xE0y qua",
  date_last30days: "30 ng\xE0y qua",
  column: {
    title: "Ti\xEAu \u0111\u1EC1",
    creator: "T\u1EA1o b\u1EDFi",
    workspace: "Kh\xF4ng gian l\xE0m vi\u1EC7c",
    location: "V\u1ECB tr\xED",
    created_at: "Ng\xE0y t\u1EA1o",
    updated_at: "Ng\xE0y c\u1EADp nh\u1EADt"
  },
  my_created_notes: "Ghi ch\xFA g\u1EA7n \u0111\xE2y c\u1EE7a t\xF4i",
  recently_viewed_notes: "Ghi ch\xFA \u0111\xE3 xem g\u1EA7n \u0111\xE2y",
  bookmarked_notes: "Ghi ch\xFA \u0111\xE3 \u0111\xE1nh d\u1EA5u",
  no_my_notes: "Kh\xF4ng c\xF3 ghi ch\xFA n\xE0o do b\u1EA1n t\u1EA1o",
  no_recently_viewed: "Kh\xF4ng c\xF3 ghi ch\xFA n\xE0o \u0111\u01B0\u1EE3c xem g\u1EA7n \u0111\xE2y",
  no_bookmarked: "Kh\xF4ng c\xF3 ghi ch\xFA n\xE0o \u0111\u01B0\u1EE3c \u0111\xE1nh d\u1EA5u",
  my_bookmark: "Ghi ch\xFA \u0111\xE3 \u0111\xE1nh d\u1EA5u"
};

// src/locales/vi-VN/oneonone.json
var oneonone_default3 = {
  action_items: {
    add: "Th\xEAm h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
    all_items: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng ({{count}})",
    banner_title: "B\u1EA1n c\xF3 h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng c\u1EA7n xem l\u1EA1i",
    banner_action: "Xem",
    assignee_change: "Thay \u0111\u1ED5i ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch",
    assignee_change_failed: "Thay \u0111\u1ED5i ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch th\u1EA5t b\u1EA1i.",
    assignee_changed: "Ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch \u0111\xE3 \u0111\u01B0\u1EE3c thay \u0111\u1ED5i.",
    delete_confirm: "B\u1EA1n c\xF3 mu\u1ED1n x\xF3a m\u1EE5c h\xE0nh \u0111\u1ED9ng n\xE0y kh\xF4ng?",
    delete_message: "M\u1EE5c h\xE0nh \u0111\u1ED9ng \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
    new_title: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng m\u1EDBi",
    no_action_items: "Kh\xF4ng c\xF3 h\xE0nh \u0111\u1ED9ng c\u1EA7n l\xE0m.",
    old_title: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng c\u0169",
    tab_title: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
    title_label: "Ti\xEAu \u0111\u1EC1 h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
    title_placeholder: "Nh\u1EADp m\u1EE5c h\xE0nh \u0111\u1ED9ng",
    title_required: "Vui l\xF2ng nh\u1EADp n\u1ED9i dung",
    user_not_found: "Kh\xF4ng th\u1EC3 t\xECm th\u1EA5y ng\u01B0\u1EDDi d\xF9ng, kh\xF4ng th\u1EC3 thay \u0111\u1ED5i ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch.",
    retro: {
      title: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng tr\u01B0\u1EDBc",
      mobile_guide: "H\xE3y \u0111\xE1nh d\u1EA5u c\xE1c h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng \u0111\xE3 ho\xE0n th\xE0nh.\nN\u1EBFu kh\xF4ng c\xF2n th\u1EF1c hi\u1EC7n n\u1EEFa, h\xE3y nh\u1EA5n v\xE0o h\u1EA1ng m\u1EE5c \u0111\xF3 v\xE0 ch\u1ECDn Ng\u1EEBng xem.",
      ai_estimate: "AI d\u1EF1 \u0111o\xE1n",
      ai_estimate_completed: "C\xF3 th\u1EC3 \u0111\xE3 xong",
      ai_nudge_tooltip: "D\u1EF1a tr\xEAn cu\u1ED9c tr\xF2 chuy\u1EC7n trong bu\u1ED5i h\u1ECDp, h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng n\xE0y c\xF3 v\u1EBB \u0111\xE3 ho\xE0n th\xE0nh \u2014 h\xE3y \u0111\xE1nh d\u1EA5u ho\xE0n th\xE0nh n\u1EBFu \u0111\xFAng.",
      dismiss: "Ng\u1EEBng xem",
      undo: "Ho\xE0n t\xE1c",
      checkin: {
        guide: "Tr\u01B0\u1EDBc khi b\u1EAFt \u0111\u1EA7u, h\xE3y c\xF9ng xem l\u1EA1i c\xE1c h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng ti\u1EBFp n\u1ED1i t\u1EEB bu\u1ED5i h\u1ECDp tr\u01B0\u1EDBc nh\xE9?\nH\xE3y \u0111\xE1nh d\u1EA5u nh\u1EEFng h\u1EA1ng m\u1EE5c \u0111\xE3 ho\xE0n th\xE0nh.",
        done: "\u0110\xE3 xem xong",
        settled_group: "{{count}} h\u1EA1ng m\u1EE5c \u0111\xE3 x\u1EED l\xFD tr\u01B0\u1EDBc",
        save_failed: "Kh\xF4ng l\u01B0u \u0111\u01B0\u1EE3c. Vui l\xF2ng th\u1EED l\u1EA1i sau gi\xE2y l\xE1t.",
        revisit_badge: "\u0110ang ti\u1EBFn h\xE0nh {{count}} bu\u1ED5i h\u1ECDp li\xEAn ti\u1EBFp",
        revisit_question: "H\u1EA1ng m\u1EE5c n\xE0y v\u1EABn \u0111ang trong tr\u1EA1ng th\xE1i ti\u1EBFn h\xE0nh. H\xE3y c\xF9ng trao \u0111\u1ED5i xem \u0111ang v\u01B0\u1EDBng \u1EDF \u0111\xE2u nh\xE9?",
        revisit_summary: "Kh\xE1 nhi\u1EC1u h\u1EA1ng m\u1EE5c \u0111\xE3 ti\u1EBFp n\u1ED1i t\u1EEB nh\u1EEFng bu\u1ED5i h\u1ECDp tr\u01B0\u1EDBc. L\u1EA7n n\xE0y h\xE3y c\xF9ng nhau x\u1EED l\xFD nh\xE9?"
      },
      verdict: {
        pending: "Ch\u01B0a b\u1EAFt \u0111\u1EA7u",
        progressing: "\u0110ang ti\u1EBFn h\xE0nh",
        completed: "Ho\xE0n th\xE0nh",
        not_discussed: "Ch\u01B0a b\xE0n \u0111\u1EBFn",
        dismissed: "\u0110\xE3 ng\u1EEBng"
      }
    },
    overview: {
      title: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
      description: "Xem v\xE0 qu\u1EA3n l\xFD c\xE1c h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng t\u1EEB bu\u1ED5i h\u1ECDp theo t\u1EEBng th\xE0nh vi\xEAn.",
      no_pairs: "Ch\u01B0a c\xF3 th\xE0nh vi\xEAn 1on1 n\xE0o. T\u1EA1o bu\u1ED5i h\u1ECDp v\xE0 h\u1ECD s\u1EBD xu\u1EA5t hi\u1EC7n \u1EDF \u0111\xE2y.",
      empty: "Ch\u01B0a c\xF3 h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng n\xE0o v\u1EDBi th\xE0nh vi\xEAn n\xE0y.",
      empty_open: "Kh\xF4ng c\xF3 h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng \u0111ang ti\u1EBFn h\xE0nh.",
      stat_open: "\u0110ang ti\u1EBFn h\xE0nh",
      stat_completed: "Ho\xE0n th\xE0nh",
      next_meeting: "Bu\u1ED5i h\u1ECDp ti\u1EBFp theo",
      group_open: "\u0110ang ti\u1EBFn h\xE0nh",
      group_completed: "Ho\xE0n th\xE0nh",
      group_dismissed: "\u0110\xE3 ng\u1EEBng",
      sort_hint: "K\xE9o d\xE0i l\xE2u nh\u1EA5t tr\u01B0\u1EDBc",
      pair_sort_label: "S\u1EAFp x\u1EBFp",
      pair_sort_open: "Nhi\u1EC1u vi\u1EC7c \u0111ang l\xE0m nh\u1EA5t",
      pair_sort_upcoming: "Bu\u1ED5i h\u1ECDp s\u1EAFp t\u1EDBi",
      pair_sort_recent: "Bu\u1ED5i h\u1ECDp g\u1EA7n \u0111\xE2y",
      ongoing_since: "T\u1EEB {{date}}",
      ongoing_since_tooltip: "M\u1EE5c n\xE0y ti\u1EBFp di\u1EC5n t\u1EEB bu\u1ED5i h\u1ECDp ng\xE0y {{date}}",
      completed_date: "Ho\xE0n th\xE0nh {{date}}",
      show_more: "Xem th\xEAm {{count}} m\u1EE5c",
      last_meeting: "Bu\u1ED5i h\u1ECDp g\u1EA7n nh\u1EA5t {{date}}",
      upcoming_meeting: "Ti\u1EBFp theo: {{date}}",
      rel_today: "h\xF4m nay",
      rel_yesterday: "h\xF4m qua",
      rel_tomorrow: "ng\xE0y mai",
      rel_days_ago: "{{count}} ng\xE0y tr\u01B0\u1EDBc",
      rel_weeks_ago: "{{count}} tu\u1EA7n tr\u01B0\u1EDBc",
      rel_months_ago: "{{count}} th\xE1ng tr\u01B0\u1EDBc",
      rel_years_ago: "{{count}} n\u0103m tr\u01B0\u1EDBc",
      rel_days_later: "trong {{count}} ng\xE0y",
      rel_weeks_later: "trong {{count}} tu\u1EA7n",
      rel_months_later: "trong {{count}} th\xE1ng",
      no_last_meeting: "Ch\u01B0a c\xF3 bu\u1ED5i h\u1ECDp",
      no_upcoming: "Ch\u01B0a c\xF3 bu\u1ED5i h\u1ECDp ti\u1EBFp theo",
      tbd: "Ch\u01B0a \u0111\u1ECBnh",
      topic_open_label: "Ch\u1EE7 \u0111\u1EC1 \u0111ang ti\u1EBFn h\xE0nh",
      topic_unclassified: "Ch\u01B0a ph\xE2n lo\u1EA1i",
      no_recent_items: "Kh\xF4ng c\xF3 ghi nh\u1EADn trong 3 th\xE1ng qua",
      topic_breakdown_title: "Ch\u1EE7 \u0111\u1EC1 trong 3 th\xE1ng qua \xB7 {{count}}",
      topic_breakdown_info: "Ch\u1EE7 \u0111\u1EC1 \u0111\xE3 trao \u0111\u1ED5i trong 3 th\xE1ng qua, kh\xF4ng ph\u1EE5 thu\u1ED9c v\xE0o ti\u1EBFn \u0111\u1ED9.",
      timeline_meeting_upcoming: "Bu\u1ED5i h\u1ECDp {{date}} (s\u1EAFp t\u1EDBi)",
      timeline_open_meeting: "\u0110i t\u1EDBi bu\u1ED5i h\u1ECDp",
      timeline_created: "\u0110\xE3 t\u1EA1o h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng n\xE0y",
      timeline_upcoming_check: "S\u1EBD c\xF9ng xem l\u1EA1i m\u1EE5c n\xE0y trong bu\u1ED5i h\u1ECDp",
      timeline_no_record: "Kh\xF4ng c\xF3 ghi ch\xFA n\xE0o",
      timeline_ai_estimate: "AI d\u1EF1 \u0111o\xE1n: {{state}}",
      timeline_confirmed_completed: "\u0110\xE3 \u0111\xE1nh d\u1EA5u ho\xE0n th\xE0nh",
      timeline_confirmed_dismissed: "\u0110\xE3 \u0111\xE1nh d\u1EA5u ng\u1EEBng xem",
      timeline_confirmed_completed_on: "\u0110\xE3 \u0111\xE1nh d\u1EA5u ho\xE0n th\xE0nh v\xE0o {{date}}",
      timeline_confirmed_dismissed_on: "\u0110\xE3 \u0111\xE1nh d\u1EA5u ng\u1EEBng xem v\xE0o {{date}}",
      timeline_confirmed_generic: "\u0110\xE3 \u0111\xE1nh d\u1EA5u {{state}}",
      timeline_no_history: "M\u1EE5c n\xE0y ch\u1EC9 c\xF2n l\u1EA1i b\u1EA3n ghi ho\xE0n th\xE0nh.",
      linked_from_label: "Ti\u1EBFp n\u1ED1i t\u1EEB h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng tr\u01B0\u1EDBc",
      linked_to_label: "\u0110\u01B0\u1EE3c ti\u1EBFp n\u1ED1i b\u1EDFi h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng m\u1EDBi",
      topic_info: "AI ph\xE2n lo\u1EA1i ch\u1EE7 \u0111\u1EC1 c\u1EE7a h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng d\u1EF1a tr\xEAn n\u1ED9i dung cu\u1ED9c h\u1ECDp. S\u1ED1 li\u1EC7u t\xEDnh c\xE1c m\u1EE5c \u0111ang ti\u1EBFn h\xE0nh, v\xE0 khi ch\u1ECDn m\u1ED9t ch\u1EE7 \u0111\u1EC1 th\xEC c\xE1c m\u1EE5c ho\xE0n th\xE0nh v\xE0 \u0111\xE3 ng\u1EEBng c\u0169ng \u0111\u01B0\u1EE3c l\u1ECDc theo.",
      load_failed: "Kh\xF4ng t\u1EA3i \u0111\u01B0\u1EE3c h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng. Vui l\xF2ng th\u1EED l\u1EA1i sau.",
      save_failed: "Kh\xF4ng l\u01B0u \u0111\u01B0\u1EE3c thay \u0111\u1ED5i. Vui l\xF2ng th\u1EED l\u1EA1i sau."
    }
  },
  ai_analysis: {
    failed: {
      title: "Ph\xE2n t\xEDch AI th\u1EA5t b\u1EA1i",
      message: "Vui l\xF2ng th\u1EED l\u1EA1i.",
      retry_button: "Th\u1EED l\u1EA1i ph\xE2n t\xEDch",
      max_retries_reached: "\u0110\xE3 \u0111\u1EA1t gi\u1EDBi h\u1EA1n s\u1ED1 l\u1EA7n th\u1EED l\u1EA1i. Vui l\xF2ng ki\u1EC3m tra l\u1EA1i sau."
    },
    additional_analysis: "Th\xF4ng tin ph\xE2n t\xEDch b\u1ED5 sung s\u1EBD \u0111\u01B0\u1EE3c hi\u1EC3n th\u1ECB \u1EDF \u0111\xE2y",
    ai_summary_title: "T\xF3m t\u1EAFt AI",
    brief_view: "T\xF3m l\u01B0\u1EE3c",
    expanded_view: "M\u1EDF r\u1ED9ng",
    analysis: "Ph\xE2n t\xEDch",
    auto_generated: "T\u1EF1 \u0111\u1ED9ng t\u1EA1o",
    close: "\u0110\xF3ng",
    conversation_content: "N\u1ED9i dung cu\u1ED9c h\u1ED9i tho\u1EA1i",
    copied: "\u0110\xE3 sao ch\xE9p",
    copy_transcript: "Sao ch\xE9p",
    core_content: "N\u1ED9i dung c\u1ED1t l\xF5i",
    decisions_made: "Quy\u1EBFt \u0111\u1ECBnh \u0111\xE3 \u0111\u01B0a ra",
    detailed_summary: "T\xF3m t\u1EAFt chi ti\u1EBFt",
    edited: "\u0110\xE3 ch\u1EC9nh s\u1EEDa",
    feedback_for_leader: "Ph\u1EA3n h\u1ED3i cho ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o",
    improvement_needed: "C\u1EA7n c\u1EA3i thi\u1EC7n",
    leader: "Ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o",
    member_feedback: {
      title: "Ph\u1EA3n h\u1ED3i cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo",
      private_badge: "Ch\u1EC9 b\u1EA1n xem \u0111\u01B0\u1EE3c",
      intro: "Nh\xECn l\u1EA1i cu\u1ED9c h\u1ECDp n\xE0y, ch\xFAng t\xF4i \u0111\xE3 chu\u1EA9n b\u1ECB {{count}} ph\u1EA3n h\u1ED3i cho b\u1EA1n. H\xE3y tham kh\u1EA3o khi chu\u1EA9n b\u1ECB cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo.",
      nudge: {
        message: "{{count}} ph\u1EA3n h\u1ED3i ch\u1EC9 m\xECnh b\u1EA1n xem \u0111\u01B0\u1EE3c \u0111\xE3 s\u1EB5n s\xE0ng. Ch\xFAng t\xF4i nh\xECn l\u1EA1i nh\u1EEFng g\xEC b\u1EA1n \u0111\xE3 n\xF3i trong cu\u1ED9c h\u1ECDp n\xE0y v\xE0 ghi l\u1EA1i \u0111i\u1EC1u n\xEAn th\u1EED l\u1EA7n sau.",
        message_mobile: "{{count}} ph\u1EA3n h\u1ED3i ch\u1EC9 m\xECnh b\u1EA1n xem \u0111\u01B0\u1EE3c \u0111\xE3 s\u1EB5n s\xE0ng.",
        button: "Xem ph\u1EA3n h\u1ED3i"
      },
      label: {
        strength: "N\xF3i r\u1EA5t t\u1ED1t",
        not_raised: "Ch\u01B0a n\xF3i ra",
        improve: "Th\u1EED n\xF3i c\xE1ch n\xE0y",
        settled: "\u0110\xE3 c\xF3 k\u1EBFt lu\u1EADn",
        open_issue: "\u0110\u1EC3 l\u1EA7n sau b\xE0n ti\u1EBFp",
        unknown: "\u0110\xE1ng nh\xECn l\u1EA1i"
      },
      source: {
        meeting: "T\u1EEB cu\u1ED9c h\u1ECDp",
        my_prep: "T\u1EEB b\u1EA3n chu\u1EA9n b\u1ECB c\u1EE7a b\u1EA1n",
        prep_chat: "T\u1EEB cu\u1ED9c tr\xF2 chuy\u1EC7n chu\u1EA9n b\u1ECB",
        unknown: "B\u1ED1i c\u1EA3nh"
      }
    },
    loading: {
      analyzing_qa: "\u0110ang ph\xE2n t\xEDch c\xE2u h\u1ECFi v\xE0 c\xE2u tr\u1EA3 l\u1EDDi...",
      analyzing_title: "\u0110ang ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp 1on1",
      browser_notification: "th\xF4ng b\xE1o tr\xECnh duy\u1EC7t",
      calculating_ratio: "\u0110ang t\xEDnh to\xE1n t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u...",
      complete_notification_body: 'Ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp "{{title}}" \u0111\xE3 ho\xE0n t\u1EA5t.',
      complete_notification_body_default: "Ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp AI \u0111\xE3 ho\xE0n t\u1EA5t.",
      complete_notification_title: "Ph\xE2n t\xEDch AI ho\xE0n t\u1EA5t",
      converting_text: "\u0110ang chuy\u1EC3n \u0111\u1ED5i cu\u1ED9c h\u1ED9i tho\u1EA1i th\xE0nh v\u0103n b\u1EA3n...",
      deriving_insights: "\u0110ang r\xFAt ra th\xF4ng tin chi ti\u1EBFt t\u1EEB AI...",
      description: "M\u1EA5t 2\u20135 ph\xFAt t\xF9y \u0111\u1ED9 d\xE0i b\u1EA3n ghi \xE2m.\nCh\xFAng t\xF4i s\u1EBD th\xF4ng b\xE1o qua email v\xE0 push \u1EE9ng d\u1EE5ng khi ho\xE0n t\u1EA5t,\nb\u1EA1n c\u1EE9 tho\u1EA3i m\xE1i l\xE0m vi\u1EC7c kh\xE1c nh\xE9!",
      description_mobile: "Th\u01B0\u1EDDng m\u1EA5t kho\u1EA3ng 5 ph\xFAt,\nv\xE0 c\xF3 th\u1EC3 l\xE2u h\u01A1n t\xF9y v\xE0o \u0111i\u1EC1u ki\u1EC7n m\xE1y ch\u1EE7.\nB\u1EA1n c\xF3 th\u1EC3 chuy\u1EC3n sang m\xE0n h\xECnh kh\xE1c!",
      extracting_action_items: "\u0110ang tr\xEDch xu\u1EA5t h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng...",
      finding_topics: "\u0110ang x\xE1c \u0111\u1ECBnh ch\u1EE7 \u0111\u1EC1 ch\xEDnh...",
      generating_title: "\u0110ang t\u1EA1o ti\xEAu \u0111\u1EC1 cu\u1ED9c h\u1ECDp...",
      loading_recording: "\u0110ang t\u1EA3i t\u1EC7p ghi \xE2m...",
      organizing_feedback: "\u0110ang t\u1ED5 ch\u1EE9c ph\u1EA3n h\u1ED3i c\u1EE7a ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o...",
      retry_failed_message: "Vui l\xF2ng th\u1EED l\u1EA1i.",
      retry_failed_title: "Th\u1EED l\u1EA1i th\u1EA5t b\u1EA1i",
      summarizing: "\u0110ang t\xF3m t\u1EAFt n\u1ED9i dung...",
      tip_title: "\u0110\u1ECDc trong khi ch\u1EDD",
      tip_subtitle: "B\xE0i vi\u1EBFt v\u1EC1 l\xE3nh \u0111\u1EA1o t\u1EEB Orblit",
      title: "AI \u0111ang ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp 1on1 c\u1EE7a b\u1EA1n"
    },
    meeting_content: "N\u1ED9i dung",
    member: "Th\xE0nh vi\xEAn",
    no_data: "Ch\u01B0a c\xF3 k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch AI. Vui l\xF2ng ki\u1EC3m tra l\u1EA1i sau.",
    no_voice_detected: "Kh\xF4ng ph\xE1t hi\u1EC7n gi\u1ECDng n\xF3i",
    no_voice_detected_description: "Kh\xF4ng ph\xE1t hi\u1EC7n gi\u1ECDng n\xF3i trong b\u1EA3n ghi \xE2m\nn\xEAn kh\xF4ng th\u1EC3 cung c\u1EA5p k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch.",
    no_meeting_data: "Kh\xF4ng t\xECm th\u1EA5y d\u1EEF li\u1EC7u cu\u1ED9c h\u1ECDp.",
    no_questions: "Kh\xF4ng c\xF3 c\xE2u h\u1ECFi v\xE0 c\xE2u tr\u1EA3 l\u1EDDi n\xE0o \u0111\u01B0\u1EE3c vi\u1EBFt.",
    no_transcript_available: "B\u1EA3n ghi kh\xF4ng kh\u1EA3 d\u1EE5ng",
    notification: {
      save_error: "L\u01B0u th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
      title: "T\xF3m t\u1EAFt AI"
    },
    tab_feedback_question: "N\u1ED9i dung n\xE0y c\xF3 h\u1EEFu \xEDch kh\xF4ng?",
    tab_feedback_question_summary: "B\u1EA3n t\xF3m t\u1EAFt n\xE0y c\xF3 h\u1EEFu \xEDch kh\xF4ng?",
    tab_feedback_question_analysis: "B\u1EA3n ph\xE2n t\xEDch n\xE0y c\xF3 h\u1EEFu \xEDch kh\xF4ng?",
    tab_feedback_up: "H\u1EEFu \xEDch",
    tab_feedback_down: "Ch\u01B0a t\u1ED1t",
    tab_feedback_submitted_title: "\u0110\xE3 g\u1EEDi ph\u1EA3n h\u1ED3i!",
    tab_feedback_submitted_message: "C\u1EA3m \u01A1n \xFD ki\u1EBFn c\u1EE7a b\u1EA1n",
    positive_feedback: "\u0110i\u1EC3m t\u1ED1t",
    recording_completed: "Ghi \xE2m ho\xE0n t\u1EA5t",
    speaking_ratio: "T\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u",
    summary: "T\xF3m t\u1EAFt",
    support_needs: "Nhu c\u1EA7u h\u1ED7 tr\u1EE3/Tr\u1EDF ng\u1EA1i",
    title: "Ph\xE2n t\xEDch AI",
    transcript_disclaimer: "\u0110\xE2y l\xE0 b\u1EA3n ghi tham kh\u1EA3o \u0111\u01B0\u1EE3c t\u1EA1o t\u1EF1 \u0111\u1ED9ng t\u1EEB l\u1EDDi n\xF3i th\u1EF1c t\u1EBF. Nh\xE3n ng\u01B0\u1EDDi n\xF3i, ch\xEDnh t\u1EA3 v\xE0 c\xE1c chi ti\u1EBFt kh\xE1c c\xF3 th\u1EC3 kh\xF4ng kh\u1EDBp ho\xE0n to\xE0n v\u1EDBi cu\u1ED9c tr\xF2 chuy\u1EC7n th\u1EF1c t\u1EBF.",
    transcript_processing: "B\u1EA3n ghi \u0111ang \u0111\u01B0\u1EE3c x\u1EED l\xFD. Vui l\xF2ng th\u1EED l\u1EA1i sau.",
    transcript_title: "Xem to\xE0n b\u1ED9 b\u1EA3n ghi",
    view_transcript: "Xem b\u1EA3n g\u1ED1c"
  },
  aria: {
    create_meeting: "T\u1EA1o cu\u1ED9c h\u1ECDp",
    create_one_on_one: "T\u1EA1o 1on1 m\u1EDBi"
  },
  counterpart: {
    empty: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn n\xE0o \u0111\u1EC3 ch\u1ECDn.",
    invite_reward: "M\u1EDDi m\u1ED9t \u0111\u1ED3ng \u0111\u1ED9i v\xE0o kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y \u0111\u1EC3 ch\u1EC9 \u0111\u1ECBnh l\xE0m \u0111\u1ED1i t\xE1c cu\u1ED9c h\u1ECDp.\nKhi h\u1ECD ch\u1EA5p nh\u1EADn, b\u1EA1n s\u1EBD nh\u1EADn th\xEAm 2 l\u01B0\u1EE3t ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp.",
    invite_where: "\u1EDE thanh b\xEAn tr\xE1i, nh\u1EA5n 'M\u1EDDi th\xE0nh vi\xEAn' \u0111\u1EC3 m\u1EDDi \u0111\u1ED3ng \u0111\u1ED9i c\u1EE7a b\u1EA1n.",
    group_my_member: "Th\xE0nh vi\xEAn tr\u1EF1c thu\u1ED9c",
    group_pair: "Th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3",
    group_same_team: "Th\xE0nh vi\xEAn c\xF9ng nh\xF3m",
    label: "\u0110\u1ED1i t\xE1c trong cu\u1ED9c h\u1ECDp l\xE0 ai?"
  },
  self_meeting: {
    or: "ho\u1EB7c",
    mode_self: "T\u1EA1o m\xE0 kh\xF4ng c\xF3 \u0111\u1ED1i t\xE1c",
    self_description: "B\u1EA3n d\xF9ng th\u1EED m\u1ED9t m\xECnh. 1on1 th\u1EF1c s\u1EF1 di\u1EC5n ra c\xF9ng th\xE0nh vi\xEAn b\u1EA1n \u0111\xE3 m\u1EDDi.",
    balance_label: "C\xF2n {{count}} l\u1EA7n",
    balance_banner: "B\u1EA1n c\xF2n {{count}} cu\u1ED9c h\u1ECDp c\xF3 th\u1EC3 ph\xE2n t\xEDch",
    no_balance: "B\u1EA1n \u0111\xE3 d\xF9ng h\u1EBFt s\u1ED1 l\u01B0\u1EE3t ph\xE2n t\xEDch. V\u1EDBi g\xF3i tr\u1EA3 ph\xED, b\u1EA1n c\xF3 th\u1EC3 ph\xE2n t\xEDch kh\xF4ng gi\u1EDBi h\u1EA1n.",
    balance_warning: "B\u1EA1n \u0111\xE3 d\xF9ng h\u1EBFt s\u1ED1 l\u01B0\u1EE3t ph\xE2n t\xEDch. Cu\u1ED9c h\u1ECDp n\xE0y di\u1EC5n ra m\xE0 kh\xF4ng ghi \xE2m hay ph\xE2n t\xEDch AI. V\u1EDBi g\xF3i tr\u1EA3 ph\xED, b\u1EA1n c\xF3 th\u1EC3 ph\xE2n t\xEDch kh\xF4ng gi\u1EDBi h\u1EA1n.",
    card_badge: "Ch\u01B0a ch\u1EC9 \u0111\u1ECBnh",
    label: "(Ch\u01B0a ch\u1EC9 \u0111\u1ECBnh)",
    detail_nudge: "M\u1EDDi \u0111\u1ED3ng \u0111\u1ED9i \u0111\u1EC3 ghi l\u1EA1i c\u1EA3 th\xF4ng tin \u0111\u1ED1i t\xE1c",
    update_description: "Cu\u1ED9c h\u1ECDp n\xE0y ch\u01B0a ch\u1EC9 \u0111\u1ECBnh \u0111\u1ED1i t\xE1c.\nB\u1EA1n ch\u1EC9 c\xF3 th\u1EC3 ch\u1EC9nh s\u1EEDa ng\xE0y v\xE0 gi\u1EDD.",
    planned_card_label: "Ch\u01B0a c\xF3 \u0111\u1ED1i t\xE1c",
    planned_card_description: "Ch\u01B0a ch\u1EC9 \u0111\u1ECBnh \u0111\u1ED1i t\xE1c"
  },
  date: {
    days_ago: "{{count}} ng\xE0y tr\u01B0\u1EDBc",
    days_left: "{{count}} ng\xE0y n\u1EEFa",
    label: "H\xE3y ch\u1ECDn ng\xE0y cu\u1ED9c h\u1ECDp.",
    overdue: "Tr\u1EC5 {{count}} ng\xE0y",
    today: "H\xF4m nay",
    tomorrow: "Ng\xE0y mai",
    yesterday: "H\xF4m qua"
  },
  day_of_week: {
    label: "H\xE3y ch\u1ECDn ng\xE0y trong tu\u1EA7n cho cu\u1ED9c h\u1ECDp."
  },
  dialog: {
    complete_button: "\u0110\xE1nh d\u1EA5u ho\xE0n th\xE0nh",
    complete_description: "B\u1EA1n \u0111\xE3 ho\xE0n th\xE0nh cu\u1ED9c h\u1ECDp n\xE0y ch\u01B0a?\nCu\u1ED9c h\u1ECDp s\u1EBD \u0111\u01B0\u1EE3c \u0111\xE1nh d\u1EA5u l\xE0 ho\xE0n th\xE0nh v\xE0 h\u1ED3 s\u01A1 s\u1EBD \u0111\u01B0\u1EE3c l\u01B0u l\u1EA1i.",
    complete_error: "Kh\xF4ng th\u1EC3 ho\xE0n th\xE0nh cu\u1ED9c h\u1ECDp. Vui l\xF2ng l\xE0m m\u1EDBi v\xE0 th\u1EED l\u1EA1i.",
    create: "T\u1EA1o cu\u1ED9c h\u1ECDp",
    date_format: "D [th\xE1ng] M",
    delete_description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a cu\u1ED9c h\u1ECDp 1on1 n\xE0y kh\xF4ng?",
    delete_error: "X\xF3a cu\u1ED9c h\u1ECDp 1on1 th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
    delete_success: "Cu\u1ED9c h\u1ECDp 1on1 \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
    delete_title: "X\xF3a cu\u1ED9c h\u1ECDp 1on1",
    leader: "L\xE3nh \u0111\u1EA1o",
    meeting_time_label: "Th\u1EDDi gian h\u1ECDp",
    member: "Th\xE0nh vi\xEAn",
    no_members: "Kh\xF4ng c\xF3 th\xE0nh vi\xEAn n\xE0o.",
    role_label: "Vai tr\xF2 c\u1EE7a t\xF4i",
    search_member_placeholder: "Nh\u1EADp t\xEAn ho\u1EB7c \u0111\u1ECBa ch\u1EC9 email.",
    select_counterpart: "Ch\u1ECDn \u0111\u1ED1i t\xE1c cu\u1ED9c h\u1ECDp",
    select_date: "Ch\u1ECDn ng\xE0y h\u1ECDp v\u1EDBi {{name}}",
    select_date_self: "Ch\u1ECDn ng\xE0y h\u1ECDp",
    select_time: "Ch\u1ECDn th\u1EDDi gian h\u1ECDp ng\xE0y {{date}} v\u1EDBi {{name}}.",
    select_time_self: "Ch\u1ECDn th\u1EDDi gian h\u1ECDp ng\xE0y {{date}}.",
    title: "T\u1EA1o cu\u1ED9c h\u1ECDp 1on1",
    update_title: "C\u1EADp nh\u1EADt cu\u1ED9c h\u1ECDp 1on1"
  },
  duration: {
    minutes: "{{value}} ph\xFAt"
  },
  error: {
    api_endpoint_missing: "URL \u0111i\u1EC3m cu\u1ED1i API ch\u01B0a \u0111\u01B0\u1EE3c c\u1EA5u h\xECnh.",
    api_request_failed: "Y\xEAu c\u1EA7u API th\u1EA5t b\u1EA1i",
    api_response_error: "Ph\u1EA3n h\u1ED3i l\u1ED7i API",
    invalid_input: "Ph\xE1t hi\u1EC7n \u0111\u1EA7u v\xE0o kh\xF4ng h\u1EE3p l\u1EC7.",
    meeting_id_missing: "Cu\u1ED9c h\u1ECDp tr\u1EF1c ti\u1EBFp kh\xF4ng th\u1EC3 t\xECm th\u1EA5y.",
    question_generation_failed: {
      message: "Kh\xF4ng th\u1EC3 t\u1EA1o c\xE2u h\u1ECFi. Vui l\xF2ng l\xE0m m\u1EDBi trang v\xE0 th\u1EED l\u1EA1i.",
      title: "T\u1EA1o c\xE2u h\u1ECFi AI th\u1EA5t b\u1EA1i"
    }
  },
  frequency: {
    biweekly: "M\u1ED7i 2 tu\u1EA7n",
    every_3_weeks: "M\u1ED7i 3 tu\u1EA7n",
    every_5_weeks: "M\u1ED7i 5 tu\u1EA7n",
    every_6_weeks: "M\u1ED7i 6 tu\u1EA7n",
    every_7_weeks: "M\u1ED7i 7 tu\u1EA7n",
    every_8_weeks: "M\u1ED7i 8 tu\u1EA7n",
    label: "H\xE3y ch\u1ECDn k\u1EF3 h\u1ECDp.",
    monthly: "H\xE0ng th\xE1ng (m\u1ED7i 4 tu\u1EA7n)",
    no_schedule: "Kh\xF4ng c\xF3 l\u1ECBch \u0111\u1ECBnh k\u1EF3",
    placeholder: "Ch\u1ECDn k\u1EF3 h\u1ECDp",
    weekly: "H\xE0ng tu\u1EA7n"
  },
  leader: {
    counterpart: "\u0110\u1ED1i t\xE1c",
    info: "Ng\u01B0\u1EDDi d\u1EABn d\u1EAFt c\xF3 quy\u1EC1n b\u1EAFt \u0111\u1EA7u v\xE0 k\u1EBFt th\xFAc, ki\u1EC3m tra m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt c\xF4ng vi\u1EC7c v\xE0 m\u1EE9c \u0111\u1ED9 h\xE0i l\xF2ng ph\xE1t tri\u1EC3n c\u1EE7a \u0111\u1ED1i t\xE1c, v\xE0 nh\u1EADn th\xF4ng tin ph\xE2n t\xEDch 1on1.",
    label: "H\xE3y ch\u1ECDn ng\u01B0\u1EDDi d\u1EABn d\u1EAFt.",
    me: "T\xF4i"
  },
  meeting_type: {
    label: "Vui l\xF2ng ch\u1ECDn lo\u1EA1i cu\u1ED9c h\u1ECDp.",
    offline: "Tr\u1EF1c ti\u1EBFp",
    online: "Tr\u1EF1c tuy\u1EBFn",
    video_link_placeholder: "D\xE1n li\xEAn k\u1EBFt Google Meet",
    video_link_description: "Hi\u1EC7n t\u1EA1i ch\u1EC9 h\u1ED7 tr\u1EE3 li\xEAn k\u1EBFt Google Meet. (Zoom s\u1EBD \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3 sau)",
    invalid_meet_link: "Vui l\xF2ng nh\u1EADp li\xEAn k\u1EBFt Google Meet h\u1EE3p l\u1EC7.",
    create_meet: "T\u1EA1o cu\u1ED9c h\u1ECDp m\u1EDBi",
    create_meet_link: "T\u1EA1o li\xEAn k\u1EBFt Google Meet",
    creating_meet_link: "\u0110ang t\u1EA1o li\xEAn k\u1EBFt...",
    meet_link_created: "\u0110\xE3 t\u1EA1o li\xEAn k\u1EBFt Google Meet.",
    meet_link_error: "Kh\xF4ng th\u1EC3 t\u1EA1o li\xEAn k\u1EBFt Meet.",
    additional_scope_required: "C\u1EA7n quy\u1EC1n b\u1ED5 sung \u0111\u1EC3 s\u1EED d\u1EE5ng t\xEDnh n\u0103ng h\u1ECDp video.",
    meet_scope_granted: "\u0110\xE3 k\u1EBFt n\u1ED1i quy\u1EC1n Google Meet.",
    grant_permission: "C\u1EA5p quy\u1EC1n",
    no_link: "Ch\u01B0a c\xF3 li\xEAn k\u1EBFt cu\u1ED9c h\u1ECDp n\xE0o.",
    add_link: "Th\xEAm",
    edit_link: "S\u1EEDa li\xEAn k\u1EBFt",
    add_link_placeholder: "Nh\u1EADp li\xEAn k\u1EBFt Google Meet",
    add_link_save: "L\u01B0u",
    copy_link: "Sao ch\xE9p li\xEAn k\u1EBFt",
    link_copied: "\u0110\xE3 sao ch\xE9p li\xEAn k\u1EBFt cu\u1ED9c h\u1ECDp.",
    link_saved: "\u0110\xE3 l\u01B0u li\xEAn k\u1EBFt cu\u1ED9c h\u1ECDp.",
    link_save_error: "Kh\xF4ng th\u1EC3 l\u01B0u li\xEAn k\u1EBFt cu\u1ED9c h\u1ECDp.",
    auto_generate_meet: "T\u1EF1 \u0111\u1ED9ng t\u1EA1o Google Meet",
    auto_generate_meet_desc: "Li\xEAn k\u1EBFt Meet s\u1EBD \u0111\u01B0\u1EE3c t\u1EA1o b\u1EB1ng t\xE0i kho\u1EA3n {{email}}",
    manual_input: "Nh\u1EADp th\u1EE7 c\xF4ng",
    or_manual_input: "ho\u1EB7c nh\u1EADp li\xEAn k\u1EBFt th\u1EE7 c\xF4ng",
    connect_calendar_hint: "K\u1EBFt n\u1ED1i Google Calendar \u0111\u1EC3 t\u1EF1 \u0111\u1ED9ng t\u1EA1o li\xEAn k\u1EBFt Meet.",
    connect_calendar_action: "K\u1EBFt n\u1ED1i ngay",
    not_organizer_hint: "Cu\u1ED9c h\u1ECDp n\xE0y n\u1EB1m tr\xEAn l\u1ECBch c\u1EE7a ng\u01B0\u1EDDi kia n\xEAn kh\xF4ng th\u1EC3 t\u1EF1 \u0111\u1ED9ng t\u1EA1o li\xEAn k\u1EBFt. Vui l\xF2ng nh\u1EADp th\u1EE7 c\xF4ng b\xEAn d\u01B0\u1EDBi.",
    join_meeting: "Tham gia cu\u1ED9c h\u1ECDp",
    complete_meeting: "Ho\xE0n th\xE0nh cu\u1ED9c h\u1ECDp 1on1",
    complete_meeting_tooltip: "N\u1EBFu b\u1EA1n \u0111\xE3 k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp Meet, vui l\xF2ng ho\xE0n th\xE0nh cu\u1ED9c h\u1ECDp n\xE0y!",
    guide: "Tham gia cu\u1ED9c h\u1ECDp qua li\xEAn k\u1EBFt v\xE0 nh\u1EA5n n\xFAt B\u1EAFt \u0111\u1EA7u ghi h\xECnh \u0111\u1EC3 b\u1EADt t\xEDnh n\u0103ng ghi h\xECnh. C\u1EA7n c\xF3 b\u1EA3n ghi \u0111\u1EC3 ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp.",
    guide_link: "C\xE1ch ghi h\xECnh",
    join_issue: "B\u1EA1n kh\xF4ng th\u1EC3 tham gia?",
    join_issue_description: "N\u1EBFu b\u1EA1n th\u1EA5y 'Y\xEAu c\u1EA7u tham gia' thay v\xEC 'Tham gia ngay' khi m\u1EDF li\xEAn k\u1EBFt, h\xE3y ki\u1EC3m tra xem t\xE0i kho\u1EA3n Google hi\u1EC7n \u0111ang \u0111\u0103ng nh\u1EADp tr\xEAn tr\xECnh duy\u1EC7t c\xF3 tr\xF9ng v\u1EDBi t\xE0i kho\u1EA3n \u0111\xE3 t\u1EA1o li\xEAn k\u1EBFt Meet hay kh\xF4ng.",
    complete_dialog: {
      title: "B\u1EA1n \u0111\xE3 k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp Meet ch\u01B0a?",
      description: "Khi t\u1EC7p ghi h\xECnh s\u1EB5n s\xE0ng tr\xEAn Google Drive (kho\u1EA3ng 10\u201315 ph\xFAt), ch\xFAng t\xF4i s\u1EBD g\u1EEDi email.\nSau khi nh\u1EADn email, nh\u1EA5n [Nh\u1EADp t\u1EC7p ghi h\xECnh] \u0111\u1EC3 ch\u1ECDn t\u1EC7p \u2014 ph\xE2n t\xEDch AI s\u1EBD b\u1EAFt \u0111\u1EA7u."
    },
    recording_guide_dialog: {
      title: "C\xE1ch ghi h\xECnh tr\xEAn Google Meet",
      as_of_date: "T\xEDnh \u0111\u1EBFn th\xE1ng 4 n\u0103m 2026",
      learn_more: "T\xECm hi\u1EC3u th\xEAm",
      pre_join_emphasis: "Ch\u1EC9 c\xE1c cu\u1ED9c h\u1ECDp c\xF3 ghi h\xECnh m\u1EDBi \u0111\u01B0\u1EE3c ph\xE2n t\xEDch b\u1EB1ng AI",
      pre_join_subtext_action: "H\xE3y nh\u1EA5n [Ghi h\xECnh] ngay khi cu\u1ED9c h\u1ECDp b\u1EAFt \u0111\u1EA7u",
      pre_join_subtext_plan: "Ghi h\xECnh ch\u1EC9 kh\u1EA3 d\u1EE5ng tr\xEAn g\xF3i Google Workspace Business Standard tr\u1EDF l\xEAn",
      dont_show_again: "Kh\xF4ng hi\u1EC3n th\u1ECB l\u1EA1i",
      join_now: "Tham gia cu\u1ED9c h\u1ECDp",
      acknowledge: "\u0110\xE3 hi\u1EC3u",
      cancel: "H\u1EE7y"
    },
    import_status: {
      polling: "\u0110ang ghi h\xECnh tr\xEAn Google Meet.",
      processing: "\u0110ang chu\u1EA9n b\u1ECB t\u1EC7p ghi h\xECnh Meet... (th\u01B0\u1EDDng 5\u201310 ph\xFAt)",
      ready: "T\u1EC7p ghi h\xECnh \u0111\xE3 s\u1EB5n s\xE0ng. Vui l\xF2ng nh\u1EA5n n\xFAt nh\u1EADp t\u1EC7p.",
      ready_hint: "N\u1EBFu b\u1EA1n kh\xF4ng ph\u1EA3i l\xE0 ng\u01B0\u1EDDi b\u1EAFt \u0111\u1EA7u ghi h\xECnh, vi\u1EC7c \u0111\xEDnh k\xE8m c\xF3 th\u1EC3 th\u1EA5t b\u1EA1i. Khuy\u1EBFn ngh\u1ECB ng\u01B0\u1EDDi \u0111\xE3 b\u1EAFt \u0111\u1EA7u ghi h\xECnh th\u1EF1c hi\u1EC7n \u0111\xEDnh k\xE8m.",
      importing: "\u0110ang nh\u1EADp t\u1EC7p ghi h\xECnh t\u1EEB Google Drive...",
      completed: "T\u1EC7p ghi h\xECnh Meet \u0111\xE3 s\u1EB5n s\xE0ng",
      not_found: "Kh\xF4ng t\xECm th\u1EA5y t\u1EC7p ghi h\xECnh Meet.",
      failed: "Nh\u1EADp t\u1EC7p ghi h\xECnh Meet th\u1EA5t b\u1EA1i",
      manual_upload_hint: "N\u1EBFu b\u1EA1n c\xF3 t\u1EC7p ghi h\xECnh tr\xEAn m\xE1y, h\xE3y d\xF9ng 'T\u1EA3i t\u1EC7p ghi h\xECnh' \u1EDF b\xEAn d\u01B0\u1EDBi."
    }
  },
  meet_recording: {
    picker: {
      button_label: "Nh\u1EADp t\u1EC7p ghi h\xECnh",
      api_key_missing_title: "Ch\u01B0a c\u1EA5u h\xECnh Picker",
      api_key_missing_message: "Thi\u1EBFu kh\xF3a API Google Picker. Vui l\xF2ng li\xEAn h\u1EC7 qu\u1EA3n tr\u1ECB vi\xEAn.",
      load_error_title: "Kh\xF4ng th\u1EC3 t\u1EA3i Picker",
      load_error_message: "Kh\xF4ng t\u1EA3i \u0111\u01B0\u1EE3c Google Picker. Vui l\xF2ng th\u1EED l\u1EA1i sau.",
      attach_success_title: "\u0110\xE3 \u0111\xEDnh k\xE8m t\u1EC7p ghi h\xECnh",
      attach_success_message: "\u0110ang t\u1EA3i t\u1EC7p t\u1EEB Drive v\xE0 b\u1EAFt \u0111\u1EA7u ph\xE2n t\xEDch AI.",
      attach_error_title: "\u0110\xEDnh k\xE8m t\u1EC7p ghi h\xECnh th\u1EA5t b\u1EA1i",
      attach_error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi \u0111\xEDnh k\xE8m t\u1EC7p ghi h\xECnh.",
      attach_error_no_access: "T\u1EC7p n\xE0y c\xF3 th\u1EC3 n\u1EB1m trong Drive c\u1EE7a ng\u01B0\u1EDDi kh\xE1c. Vui l\xF2ng y\xEAu c\u1EA7u ng\u01B0\u1EDDi \u0111\xE3 b\u1EAFt \u0111\u1EA7u ghi h\xECnh th\u1EF1c hi\u1EC7n \u0111\xEDnh k\xE8m."
    }
  },
  meeting: {
    meeting_home: "Trang ch\u1EE7",
    no_previous_meeting: "Kh\xF4ng c\xF3 l\u1ECBch s\u1EED cu\u1ED9c h\u1ECDp"
  },
  meeting_detail: {
    prep: "Chu\u1EA9n b\u1ECB",
    previous_action_items: "M\u1EE5c h\xE0nh \u0111\u1ED9ng tr\u01B0\u1EDBc",
    add_question: {
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi th\xEAm c\xE2u h\u1ECFi.",
      success: "C\xE2u h\u1ECFi \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0o danh s\xE1ch c\xE2u h\u1ECFi \u1EDF tab N\u1ED9i dung.",
      title: "Th\xEAm c\xE2u h\u1ECFi",
      success_bulk: "\u0110\xE3 th\xEAm {{count}} c\xE2u h\u1ECFi v\xE0o danh s\xE1ch c\xE2u h\u1ECFi \u1EDF tab N\u1ED9i dung."
    },
    ai_generate: "T\u1EA1o c\xE2u h\u1ECFi AI",
    ai_generated: "\u0110\u01B0\u1EE3c t\u1EA1o b\u1EDFi AI",
    answer_placeholder: "\u0110\u1EC3 ch\u1EC9nh s\u1EEDa c\xE2u tr\u1EA3 l\u1EDDi, h\xE3y nh\u1EA5n v\xE0o \u0111\xE2y.",
    answer_placeholder_new: "Vui l\xF2ng nh\u1EADp c\xE2u tr\u1EA3 l\u1EDDi.",
    bring_template: "Nh\u1EADp m\u1EABu",
    delete_recommended_question: {
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a c\xE2u h\u1ECFi.",
      title: "X\xF3a c\xE2u h\u1ECFi \u0111\u1EC1 xu\u1EA5t"
    },
    editing_placeholder: "B\u1EA1n c\xF3 th\u1EC3 nh\u1EADp c\xE2u tr\u1EA3 l\u1EDDi sau khi l\u01B0u c\xE2u h\u1ECFi.",
    end_meeting: {
      action_button: "K\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp 1on1",
      description: "B\u1EA1n c\xF3 mu\u1ED1n k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp m\u1ED9t-\u0111\u1ED1i-m\u1ED9t kh\xF4ng?",
      error_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp.",
      error_title: "K\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp th\u1EA5t b\u1EA1i",
      network_notice: "B\u1EA3n ghi \xE2m s\u1EBD \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng t\u1EA3i l\xEAn khi cu\u1ED9c h\u1ECDp k\u1EBFt th\xFAc. M\xF4i tr\u01B0\u1EDDng m\u1EA1ng \u1ED5n \u0111\u1ECBnh l\xE0 \u0111i\u1EC1u ki\u1EC7n l\xFD t\u01B0\u1EDFng \u0111\u1EC3 t\u1EA3i l\xEAn.",
      title: "Th\xF4ng b\xE1o k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp",
      other_device_title: "C\xF3 v\u1EBB thi\u1EBFt b\u1ECB kh\xE1c \u0111ang ghi \xE2m",
      other_device_body: "Vui l\xF2ng k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp tr\xEAn thi\u1EBFt b\u1ECB \u0111ang ghi \xE2m. N\u1EBFu k\u1EBFt th\xFAc t\u1EA1i \u0111\xE2y m\xE0 kh\xF4ng c\xF3 b\u1EA3n ghi, cu\u1ED9c h\u1ECDp s\u1EBD kh\xF4ng th\u1EC3 \u0111\u01B0\u1EE3c ph\xE2n t\xEDch.",
      other_device_end_anyway: "K\u1EBFt th\xFAc kh\xF4ng c\u1EA7n ghi \xE2m"
    },
    is_editing: "{{editor}} \u0111ang ch\u1EC9nh s\u1EEDa...",
    leader: "L\xE3nh \u0111\u1EA1o",
    my_note: "L\u01B0u \xFD c\u1EE7a ri\xEAng t\xF4i",
    my_note_placeholder: "\u0110\xE2y l\xE0 m\u1ED9t b\u1EA3n ghi nh\u1EDB ch\u1EC9 c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c nh\xECn th\u1EA5y.",
    past_meetings_title: "C\xE1c cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc v\u1EDBi {{name}}",
    past_meetings_title_generic: "C\xE1c cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc",
    past_meetings_empty: "Kh\xF4ng c\xF3 b\u1EA3n ghi n\xE0o tr\u01B0\u1EDBc cu\u1ED9c h\u1ECDp n\xE0y",
    past_meetings_self_unavailable_title: "Kh\xF4ng th\u1EC3 xem b\u1EA3n ghi cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc",
    past_meetings_self_unavailable: "B\u1EA1n c\xF3 th\u1EC3 xem b\u1EA3n ghi cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc trong c\xE1c cu\u1ED9c h\u1ECDp 1:1 \u0111\xE3 ch\u1EC9 \u0111\u1ECBnh \u0111\u1ED1i ph\u01B0\u01A1ng.",
    question_delete_modal: {
      description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a c\xE2u h\u1ECFi n\xE0y kh\xF4ng?",
      title: "X\xF3a c\xE2u h\u1ECFi"
    },
    question_placeholder: "Vui l\xF2ng nh\u1EADp c\xE2u h\u1ECFi",
    question_request: "Y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi",
    question_request_success: "Email y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi \u0111\xE3 \u0111\u01B0\u1EE3c g\u1EEDi th\xE0nh c\xF4ng.",
    question_request_failed: "G\u1EEDi y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi th\u1EA5t b\u1EA1i.\nVui l\xF2ng th\u1EED l\u1EA1i sau.",
    questions: "C\xE2u h\u1ECFi",
    recording_duration: "\u0110\xE3 ghi \xE2m",
    recording_time: "\u0110ang ghi \xE2m",
    draft_badge: "B\u1EA3n nh\xE1p",
    draft_onboarding: "C\xE2u h\u1ECFi c\u1EE7a b\u1EA1n ch\u1EC9 hi\u1EC3n th\u1ECB v\u1EDBi \u0111\u1ED1i t\xE1c sau khi nh\u1EA5n 'C\xF4ng b\u1ED1 c\xE2u h\u1ECFi'.",
    publish_questions: "C\xF4ng b\u1ED1 c\xE2u h\u1ECFi",
    publish_questions_confirm: "{{count}} c\xE2u h\u1ECFi nh\xE1p s\u1EBD \u0111\u01B0\u1EE3c chia s\u1EBB v\u1EDBi \u0111\u1ED1i t\xE1c,\nv\xE0 email y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi c\u0169ng s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi.\nB\u1EA1n c\xF3 mu\u1ED1n c\xF4ng b\u1ED1 kh\xF4ng?",
    confirm_questions_success: "\u0110\xE3 x\xE1c nh\u1EADn c\xE2u h\u1ECFi th\xE0nh c\xF4ng.",
    send_questions: "Y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi",
    send_questions_confirm: "G\u1EEDi email y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi cho \u0111\u1ED1i t\xE1c?",
    send_questions_confirm_mobile: "B\u1EA1n c\xF3 th\u1EC3 y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi qua email v\xE0 th\xF4ng b\xE1o.\nB\u1EA1n c\xF3 mu\u1ED1n g\u1EEDi y\xEAu c\u1EA7u xem x\xE9t cho \u0111\u1ED1i t\xE1c kh\xF4ng?",
    start_meeting: {
      action_button: "B\u1EAFt \u0111\u1EA7u cu\u1ED9c h\u1ECDp",
      agree_description: "Vui l\xF2ng ki\u1EC3m tra xem b\u1EA1n \u0111\xE3 c\xF3 \u0111\u01B0\u1EE3c s\u1EF1 \u0111\u1ED3ng \xFD c\u1EE7a \u0111\u1ED1i t\xE1c ch\u01B0a.\nN\u1EBFu kh\xF4ng c\xF3 s\u1EF1 \u0111\u1ED3ng \xFD chung, cu\u1ED9c h\u1ECDp s\u1EBD ti\u1EBFn h\xE0nh m\xE0 kh\xF4ng ghi \xE2m.",
      agree_title: "T\xF4i \u0111\xE3 c\xF3 \u0111\u01B0\u1EE3c s\u1EF1 \u0111\u1ED3ng \xFD c\u1EE7a \u0111\u1ED1i t\xE1c",
      ai_consent_title: "T\xF4i \u0111\u1ED3ng \xFD r\u1EB1ng d\u1EEF li\u1EC7u ghi \xE2m s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn c\xE1c d\u1ECBch v\u1EE5 AI b\xEAn ngo\xE0i \u0111\u1EC3 t\u1EA1o bi\xEAn b\u1EA3n v\xE0 t\xF3m t\u1EAFt cu\u1ED9c h\u1ECDp.",
      ai_consent_details_toggle: "Xem chi ti\u1EBFt",
      ai_consent_details_body: "\xC2m thanh \u0111\xE3 ghi v\xE0 v\u0103n b\u1EA3n \u0111\u01B0\u1EE3c chuy\u1EC3n \u0111\u1ED5i s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn c\xE1c d\u1ECBch v\u1EE5 AI b\xEAn ngo\xE0i sau \u0111\xE2y \u0111\u1EC3 t\u1EA1o bi\xEAn b\u1EA3n cu\u1ED9c h\u1ECDp, t\xF3m t\u1EAFt n\u1ED9i dung v\xE0 ph\xE2n t\xEDch t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u.\n- AssemblyAI Inc. (chuy\u1EC3n gi\u1ECDng n\xF3i th\xE0nh v\u0103n b\u1EA3n)\n- Google LLC (Vertex AI/Gemini)\n- OpenAI OpCo, L.L.C.\n- Anthropic PBC\n\nS\u1EF1 \u0111\u1ED3ng \xFD c\u1EE7a b\u1EA1n \u0111\u01B0\u1EE3c l\u01B0u tr\xEAn thi\u1EBFt b\u1ECB n\xE0y. B\u1EA1n s\u1EBD \u0111\u01B0\u1EE3c h\u1ECFi l\u1EA1i n\u1EBFu ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt thay \u0111\u1ED5i.",
      ai_consent_privacy_policy_link: "Xem ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt",
      description_record_on: "Bi\xEAn b\u1EA3n cu\u1ED9c h\u1ECDp v\xE0 t\xF3m t\u1EAFt n\u1ED9i dung s\u1EBD \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng t\u1EA1o sau cu\u1ED9c h\u1ECDp.",
      description_record_off: "B\u1EA1n v\u1EABn c\xF3 th\u1EC3 ti\u1EBFn h\xE0nh m\xE0 kh\xF4ng c\u1EA7n ghi \xE2m.\nN\u1EBFu b\u1EA1n b\u1EADt ghi \xE2m, ch\xFAng t\xF4i s\u1EBD h\u1ED7 tr\u1EE3 bi\xEAn b\u1EA3n cu\u1ED9c h\u1ECDp v\xE0 t\xF3m t\u1EAFt n\u1ED9i dung.",
      draft_notice: "B\u1EAFt \u0111\u1EA7u cu\u1ED9c h\u1ECDp s\u1EBD c\xF4ng b\u1ED1 t\u1EA5t c\u1EA3 c\xE2u h\u1ECFi nh\xE1p.\nVui l\xF2ng x\xF3a c\xE2u h\u1ECFi b\u1EA1n kh\xF4ng mu\u1ED1n chia s\u1EBB tr\u01B0\u1EDBc khi b\u1EAFt \u0111\u1EA7u.",
      record: "T\xEDnh n\u0103ng ghi \xE2m",
      recording_notice: "S\u1EED d\u1EE5ng \u1EE9ng d\u1EE5ng \u1EDF ch\u1EBF \u0111\u1ED9 n\u1EC1n khi \u0111ang ghi \xE2m kh\xF4ng sao, nh\u01B0ng vui l\xF2ng kh\xF4ng tho\xE1t \u1EE9ng d\u1EE5ng \u2014 t\u1EC7p ghi \xE2m c\xF3 th\u1EC3 b\u1ECB m\u1EA5t.",
      title: "B\u1EA1n c\xF3 mu\u1ED1n b\u1EAFt \u0111\u1EA7u cu\u1ED9c h\u1ECDp kh\xF4ng?"
    },
    start_meeting_button: "B\u1EAFt \u0111\u1EA7u ghi \xE2m",
    survey: {
      button: {
        later: "\u0110\u1EC3 sau",
        save: "L\u01B0u"
      },
      engagement: {
        description: "{{name}} h\xE0i l\xF2ng v\xE0 g\u1EAFn k\u1EBFt v\u1EDBi c\xF4ng vi\u1EC7c v\xE0 c\xF4ng ty hi\u1EC7n t\u1EA1i \u1EDF m\u1EE9c \u0111\u1ED9 n\xE0o?",
        title: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt c\xF4ng vi\u1EC7c"
      },
      growth: {
        description: "{{name}} c\u1EA3m th\u1EA5y m\xECnh \u0111ang ph\xE1t tri\u1EC3n v\xE0 ti\u1EBFn b\u1ED9 trong t\u1ED5 ch\u1EE9c \u1EDF m\u1EE9c \u0111\u1ED9 n\xE0o?",
        title: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n"
      },
      notifications: {
        delete: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
        error: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3 \u0111\u0103ng th\u1EA5t b\u1EA1i.",
        success: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\u0103ng th\xE0nh c\xF4ng.",
        title: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3",
        update: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt."
      },
      rating: {
        bad: "T\u1EC7",
        fair: "Kh\xE1",
        good: "T\u1ED1t",
        very_bad: "R\u1EA5t t\u1EC7",
        very_good: "R\u1EA5t t\u1ED1t"
      },
      regular_checkin: "Ki\u1EC3m tra \u0111\u1ECBnh k\u1EF3",
      select_rating: "Ch\u1ECDn \u0111\xE1nh gi\xE1",
      title: "Cu\u1ED9c h\u1ECDp \u0111\xE3 k\u1EBFt th\xFAc!"
    },
    pair_frequency: {
      set_label: "Thi\u1EBFt l\u1EADp chu k\u1EF3 1on1",
      manage_title: "Chu k\u1EF3 1on1 v\u1EDBi {{name}}",
      button: {
        save: "L\u01B0u",
        remove: "G\u1EE1 chu k\u1EF3"
      },
      remove_confirm: {
        title: "G\u1EE1 chu k\u1EF3 1on1",
        description: "G\u1EE1 chu k\u1EF3 1on1 v\u1EDBi {{name}}? B\u1EA1n s\u1EBD kh\xF4ng c\xF2n nh\u1EADn nh\u1EAFc nh\u1EDF khi \u0111\u1EBFn l\xFAc \u0111\u1EB7t l\u1ECBch bu\u1ED5i h\u1ECDp ti\u1EBFp theo."
      },
      notification: {
        title: "Chu k\u1EF3 1on1",
        saved: "\u0110\xE3 l\u01B0u chu k\u1EF3 h\u1ECDp.",
        removed: "\u0110\xE3 g\u1EE1 chu k\u1EF3 1on1.",
        error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i. Vui l\xF2ng th\u1EED l\u1EA1i sau."
      }
    },
    next_meeting_nudge: {
      create_meeting: {
        title: "Bu\u1ED5i 1on1 v\u1EDBi {{name}} \u0111\xE3 k\u1EBFt th\xFAc",
        description: "B\u1EA1n c\xF3 mu\u1ED1n \u0111\u1EB7t l\u1ECBch cho bu\u1ED5i ti\u1EBFp theo ngay b\xE2y gi\u1EDD kh\xF4ng?",
        date_label: "Ng\xE0y",
        date_hint: "\u0110\u01B0\u1EE3c \u0111\u1EC1 xu\u1EA5t theo chu k\u1EF3 {{weeks}} tu\u1EA7n b\u1EA1n \u0111\xE3 \u0111\u1EB7t.",
        date_hint_other: "Theo chu k\u1EF3 {{weeks}} tu\u1EA7n b\u1EA1n \u0111\xE3 \u0111\u1EB7t, ng\xE0y \u0111\u1EC1 xu\u1EA5t l\xE0 {{date}}.",
        suggested_label: "Ng\xE0y \u0111\u1EC1 xu\u1EA5t theo chu k\u1EF3 {{weeks}} tu\u1EA7n c\u1EE7a b\u1EA1n",
        time_label: "Gi\u1EDD",
        time_placeholder: "Ch\u1ECDn gi\u1EDD",
        duration_label: "Th\u1EDDi l\u01B0\u1EE3ng",
        button: {
          later: "\u0110\u1EC3 sau",
          edit_schedule: "Ch\u1EC9nh l\u1ECBch",
          confirm_suggested: "\u0110\u1EB7t theo l\u1ECBch \u0111\u1EC1 xu\u1EA5t",
          confirm: "\u0110\u1EB7t l\u1ECBch bu\u1ED5i ti\u1EBFp theo"
        },
        notification: {
          title: "\u0110\u1EB7t l\u1ECBch bu\u1ED5i ti\u1EBFp theo",
          success: "Bu\u1ED5i h\u1ECDp ti\u1EBFp theo \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o.",
          error: "Kh\xF4ng th\u1EC3 t\u1EA1o bu\u1ED5i h\u1ECDp. Vui l\xF2ng t\u1EA1o t\u1EEB danh s\xE1ch bu\u1ED5i h\u1ECDp."
        }
      },
      setup_pair: {
        title: "Duy tr\xEC g\u1EB7p g\u1EE1 {{name}} \u0111\u1EC1u \u0111\u1EB7n",
        description: "C\xF9ng nhau quy\u1EBFt \u0111\u1ECBnh t\u1EA7n su\u1EA5t g\u1EB7p g\u1EE1.\nCh\xFAng t\xF4i khuy\xEAn n\xEAn ch\u1ECDn 2 tu\u1EA7n/l\u1EA7n \u0111\u1EC3 cu\u1ED9c tr\xF2 chuy\u1EC7n lu\xF4n \u0111\u01B0\u1EE3c ti\u1EBFp n\u1ED1i.",
        frequency_label: "Chu k\u1EF3 h\u1ECDp",
        frequency_hint: "G\u1EB7p th\u01B0\u1EDDng xuy\xEAn m\u1ED7i 2 tu\u1EA7n, d\xF9 ng\u1EAFn h\u01A1n, th\u01B0\u1EDDng hi\u1EC7u qu\u1EA3 h\u01A1n.",
        reminder_notice: "Khi \u0111\xE3 l\u01B0u chu k\u1EF3, ch\xFAng t\xF4i s\u1EBD g\u1EEDi email v\xE0 th\xF4ng b\xE1o \u0111\u1EA9y nh\u1EAFc b\u1EA1n n\u1EBFu bu\u1ED5i h\u1ECDp ti\u1EBFp theo ch\u01B0a \u0111\u01B0\u1EE3c l\xEAn l\u1ECBch.",
        button: {
          later: "\u0110\u1EC3 sau",
          confirm: "Thi\u1EBFt l\u1EADp chu k\u1EF3"
        },
        notification: {
          title: "Chu k\u1EF3 h\u1ECDp",
          success: "\u0110\xE3 thi\u1EBFt l\u1EADp chu k\u1EF3 h\u1ECDp.",
          success_mobile: "\u0110\xE3 thi\u1EBFt l\u1EADp chu k\u1EF3 h\u1ECDp. B\u1EA1n c\xF3 th\u1EC3 thay \u0111\u1ED5i b\u1EA5t c\u1EE9 l\xFAc n\xE0o \u1EDF \u0111\u1EA7u m\xE0n h\xECnh bu\u1ED5i h\u1ECDp.",
          error: "Kh\xF4ng th\u1EC3 thi\u1EBFt l\u1EADp chu k\u1EF3. Vui l\xF2ng thi\u1EBFt l\u1EADp l\u1EA1i \u1EDF b\u1EA3ng b\xEAn ph\u1EA3i trong m\xE0n h\xECnh bu\u1ED5i h\u1ECDp.",
          error_mobile: "Kh\xF4ng th\u1EC3 thi\u1EBFt l\u1EADp chu k\u1EF3. Vui l\xF2ng th\u1EED l\u1EA1i sau."
        }
      }
    }
  },
  meeting_pdf: {
    modal_title: "L\u01B0u d\u01B0\u1EDBi d\u1EA1ng PDF",
    modal_description: "Ch\u1ECDn c\xE1c m\u1EE5c \u0111\u1EC3 \u0111\u01B0a v\xE0o t\u1EC7p.",
    header_title: "Cu\u1ED9c h\u1ECDp 1on1",
    prep: "Chu\u1EA9n b\u1ECB",
    prep_summary: "T\xF3m t\u1EAFt",
    prep_detail: "Chi ti\u1EBFt",
    condition: "T\xECnh tr\u1EA1ng",
    topic: "Ch\u1EE7 \u0111\u1EC1 cu\u1ED9c h\u1ECDp",
    questions: "C\xE2u h\u1ECFi & Tr\u1EA3 l\u1EDDi",
    ai_summary: "T\xF3m t\u1EAFt AI",
    action_items: "M\u1EE5c h\xE0nh \u0111\u1ED9ng",
    previous_action_items: "M\u1EE5c h\xE0nh \u0111\u1ED9ng tr\u01B0\u1EDBc",
    current_action_items: "M\u1EE5c h\xE0nh \u0111\u1ED9ng hi\u1EC7n t\u1EA1i",
    due_date: "H\u1EA1n",
    summary: "T\xF3m t\u1EAFt",
    decisions_made: "Quy\u1EBFt \u0111\u1ECBnh",
    support_needs: "C\u1EA7n h\u1ED7 tr\u1EE3",
    analysis: "Ph\xE2n t\xEDch",
    speaking_ratio: "T\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u",
    positive_feedback: "\u0110i\u1EC3m m\u1EA1nh",
    negative_feedback: "C\u1EA7n c\u1EA3i thi\u1EC7n",
    download: "T\u1EA3i xu\u1ED1ng",
    error: "Kh\xF4ng th\u1EC3 t\u1EA1o t\u1EC7p PDF. Vui l\xF2ng th\u1EED l\u1EA1i."
  },
  meeting_page: {
    complete: "\u0110\xE1nh d\u1EA5u ho\xE0n th\xE0nh cu\u1ED9c h\u1ECDp",
    download: "L\u01B0u d\u01B0\u1EDBi d\u1EA1ng PDF",
    edit: "\u0110\u1ED5i l\u1ECBch h\u1EB9n",
    empty_records: "Ch\u01B0a c\xF3 h\u1ED3 s\u01A1 cu\u1ED9c h\u1ECDp \u0111\xE3 ho\xE0n th\xE0nh.",
    empty_upcoming: "Kh\xF4ng c\xF3 cu\u1ED9c h\u1ECDp s\u1EAFp t\u1EDBi.",
    meeting_item: "M\u1EE5c cu\u1ED9c h\u1ECDp",
    my_meetings: "Cu\u1ED9c h\u1ECDp c\u1EE7a t\xF4i",
    my_records: "H\u1ED3 s\u01A1 cu\u1ED9c h\u1ECDp c\u1EE7a t\xF4i",
    table: {
      actual_date: "Ng\xE0y th\u1EF1c t\u1EBF",
      actual_duration: "Th\u1EDDi l\u01B0\u1EE3ng th\u1EF1c t\u1EBF",
      ai_summary: "T\xF3m t\u1EAFt AI",
      counterpart: "\u0110\u1ED1i t\xE1c",
      edit_summary: "Ch\u1EC9nh s\u1EEDa t\xF3m t\u1EAFt",
      edit_summary_error: "C\u1EADp nh\u1EADt t\xF3m t\u1EAFt th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
      edit_summary_placeholder: "Nh\u1EADp t\xF3m t\u1EAFt",
      edit_summary_success: "T\xF3m t\u1EAFt \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt.",
      in_progress: "\u0110ang di\u1EC5n ra",
      title: "Ti\xEAu \u0111\u1EC1"
    },
    title: "Cu\u1ED9c h\u1ECDp 1on1",
    unscheduled: "Ch\u01B0a l\xEAn l\u1ECBch",
    untitled: "Kh\xF4ng c\xF3 ti\xEAu \u0111\u1EC1",
    upcoming: "Cu\u1ED9c h\u1ECDp s\u1EAFp t\u1EDBi",
    placeholder_first: "T\u1EA1o 1on1 \u0111\u1EA7u ti\xEAn"
  },
  member_add_dialog: {
    add: "Th\xEAm th\xE0nh vi\xEAn",
    title: "Th\xEAm th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3"
  },
  member_report: {
    create_button: "T\u1EA1o b\xE1o c\xE1o m\u1EDBi",
    create_dialog: {
      all_meetings_selected: "T\u1EA5t c\u1EA3 {{total}} cu\u1ED9c h\u1ECDp",
      cancel: "H\u1EE7y",
      create_report: "T\u1EA1o b\xE1o c\xE1o",
      creating: "\u0110ang t\u1EA1o...",
      creation_failed: "T\u1EA1o b\xE1o c\xE1o th\u1EA5t b\u1EA1i",
      data_collection_period: "Ch\u1ECDn th\u1EDDi gian thu th\u1EADp d\u1EEF li\u1EC7u",
      description: "T\u1EA1o b\xE1o c\xE1o hi\u1EC7u su\u1EA5t cho th\xE0nh vi\xEAn nh\xF3m c\u1EE7a b\u1EA1n.",
      duration: "Th\u1EDDi l\u01B0\u1EE3ng",
      error: "Kh\xF4ng th\u1EC3 t\u1EA1o b\xE1o c\xE1o. Vui l\xF2ng th\u1EED l\u1EA1i.",
      fill_all_fields: "Vui l\xF2ng \u0111i\u1EC1n \u0111\u1EA7y \u0111\u1EE7 th\xF4ng tin",
      max_three_months: "D\u1EEF li\u1EC7u c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c ch\u1ECDn t\u1ED1i \u0111a 3 th\xE1ng",
      meetings_count: "{{selected}}/{{total}} cu\u1ED9c h\u1ECDp",
      member_not_found: "Kh\xF4ng t\xECm th\u1EA5y th\xE0nh vi\xEAn \u0111\xE3 ch\u1ECDn",
      minutes: "{{minutes}} ph\xFAt",
      no_direct_reports: "Kh\xF4ng c\xF3 b\xE1o c\xE1o tr\u1EF1c ti\u1EBFp",
      no_meetings_in_period: "Kh\xF4ng c\xF3 cu\u1ED9c h\u1ECDp 1on1 trong kho\u1EA3ng th\u1EDDi gian n\xE0y",
      no_meetings_selected: "Kh\xF4ng c\xF3 cu\u1ED9c h\u1ECDp n\xE0o \u0111\u01B0\u1EE3c ch\u1ECDn",
      no_title: "Kh\xF4ng c\xF3 ti\xEAu \u0111\u1EC1",
      report_created: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng",
      select_meetings: "Vui l\xF2ng ch\u1ECDn c\xE1c cu\u1ED9c h\u1ECDp 1on1",
      select_member: "Ch\u1ECDn th\xE0nh vi\xEAn",
      select_member_and_period: "Vui l\xF2ng ch\u1ECDn th\xE0nh vi\xEAn v\xE0 th\u1EDDi gian tr\u01B0\u1EDBc",
      selected_meetings: "1on1 \u0111\xE3 ch\u1ECDn",
      success: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
      title: "T\u1EA1o b\xE1o c\xE1o m\u1EDBi",
      topic: "Ch\u1EE7 \u0111\u1EC1"
    },
    date_period: {
      custom: "T\xF9y ch\u1ECDn th\u1EDDi gian",
      last_1_month: "1 th\xE1ng g\u1EA7n nh\u1EA5t",
      last_3_months: "3 th\xE1ng g\u1EA7n nh\u1EA5t",
      select_period: "Ch\u1ECDn th\u1EDDi gian"
    },
    detail: {
      action_items: "H\xE0nh \u0111\u1ED9ng c\u1EA7n l\xE0m",
      ai_loading: {
        analyzing_conversation: "\u0110ang ph\xE2n t\xEDch n\u1ED9i dung cu\u1ED9c tr\xF2 chuy\u1EC7n...",
        analyzing_growth: "\u0110ang ph\xE2n t\xEDch \u0111i\u1EC3m ph\xE1t tri\u1EC3n c\u1EE7a th\xE0nh vi\xEAn...",
        collecting_data: "\u0110ang thu th\u1EADp d\u1EEF li\u1EC7u cu\u1ED9c h\u1ECDp 1on1...",
        creating_management_plan: "\u0110ang t\u1EA1o k\u1EBF ho\u1EA1ch qu\u1EA3n l\xFD t\xF9y ch\u1EC9nh...",
        deriving_insights: "\u0110ang r\xFAt ra th\xF4ng tin chi ti\u1EBFt v\u1EC1 l\xE3nh \u0111\u1EA1o...",
        evaluating_flight_risk: "\u0110ang \u0111\xE1nh gi\xE1 r\u1EE7i ro ngh\u1EC9 vi\u1EC7c...",
        finalizing_report: "\u0110ang ho\xE0n thi\u1EC7n b\xE1o c\xE1o...",
        generating_topics: "\u0110ang t\u1EA1o ch\u1EE7 \u0111\u1EC1 \u0111\u1EC1 xu\u1EA5t cho 1on1 ti\u1EBFp theo...",
        identifying_topics: "\u0110ang x\xE1c \u0111\u1ECBnh c\xE1c ch\u1EE7 \u0111\u1EC1 ch\xEDnh...",
        organizing_performance: "\u0110ang t\u1ED5 ch\u1EE9c hi\u1EC7u su\u1EA5t v\xE0 c\u1EA3i ti\u1EBFn..."
      },
      back_to_list: "Quay l\u1EA1i danh s\xE1ch",
      cancel: "H\u1EE7y",
      contribution: "\u0110\xF3ng g\xF3p",
      delete: "X\xF3a",
      delete_confirm: "B\u1EA1n c\xF3 ch\u1EAFc mu\u1ED1n x\xF3a b\xE1o c\xE1o n\xE0y kh\xF4ng?",
      delete_failed: "X\xF3a th\u1EA5t b\u1EA1i",
      delete_failed_message: "Kh\xF4ng th\u1EC3 x\xF3a b\xE1o c\xE1o.",
      delete_success: "\u0110\xE3 x\xF3a",
      delete_success_message: "B\xE1o c\xE1o \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
      demotivation_factors: "Y\u1EBFu t\u1ED1 gi\u1EA3m \u0111\u1ED9ng l\u1EF1c",
      emotion_wellbeing: "C\u1EA3m x\xFAc/S\u1EE9c kh\u1ECFe",
      error_title: "Kh\xF4ng th\u1EC3 t\u1EA1o b\xE1o c\xE1o",
      error_description: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh ph\xE2n t\xEDch AI.\nVui l\xF2ng th\u1EED l\u1EA1i.",
      export_pdf: "Xu\u1EA5t PDF",
      flight_risk: "R\u1EE7i ro ngh\u1EC9 vi\u1EC7c",
      goals: "M\u1EE5c ti\xEAu",
      growth: "Ph\xE1t tri\u1EC3n",
      interested_projects: "D\u1EF1 \xE1n quan t\xE2m",
      interests: "S\u1EDF th\xEDch",
      loading_description: "\u0110ang t\u1EA1o b\xE1o c\xE1o t\u1EEB d\u1EEF li\u1EC7u cu\u1ED9c h\u1ECDp 1on1 c\u1EE7a b\u1EA1n.\nVui l\xF2ng \u0111\u1EE3i trong gi\xE2y l\xE1t.",
      management_plan: "K\u1EBF ho\u1EA1ch qu\u1EA3n l\xFD t\xF9y ch\u1EC9nh",
      member: "Th\xE0nh vi\xEAn",
      member_report: "B\xE1o c\xE1o c\u1EE7a {{name}}",
      motivation: "\u0110\u1ED9ng l\u1EF1c",
      motivation_factors: "Y\u1EBFu t\u1ED1 \u0111\u1ED9ng l\u1EF1c",
      network: "M\u1EA1ng l\u01B0\u1EDBi",
      next_topics: "Ch\u1EE7 \u0111\u1EC1 \u0111\u1EC1 xu\u1EA5t cho 1on1 ti\u1EBFp theo",
      no_action_items: "Kh\xF4ng c\xF3 h\xE0nh \u0111\u1ED9ng c\u1EA7n l\xE0m.",
      no_content: "Kh\xF4ng c\xF3 n\u1ED9i dung.",
      no_data: "Kh\xF4ng c\xF3 d\u1EEF li\u1EC7u",
      no_topics: "Kh\xF4ng c\xF3 ch\u1EE7 \u0111\u1EC1 \u0111\u1EC1 xu\u1EA5t.",
      not_found: "Kh\xF4ng t\xECm th\u1EA5y b\xE1o c\xE1o.",
      personal: "C\xE1 nh\xE2n",
      retry: "Th\u1EED l\u1EA1i",
      skills: "K\u1EF9 n\u0103ng",
      strength_weakness: "\u0110i\u1EC3m m\u1EA1nh v\xE0 \u0110i\u1EC3m y\u1EBFu",
      strengths: "\u0110i\u1EC3m m\u1EA1nh",
      tab: {
        detail: "Chi ti\u1EBFt",
        summary: "T\xF3m t\u1EAFt"
      },
      weaknesses: "\u0110i\u1EC3m y\u1EBFu",
      work_blockers: "Tr\u1EDF ng\u1EA1i c\xF4ng vi\u1EC7c",
      work_content: "N\u1ED9i dung c\xF4ng vi\u1EC7c",
      work_performance: "Hi\u1EC7u su\u1EA5t c\xF4ng vi\u1EC7c"
    },
    empty: {
      no_reports: "Ch\u01B0a c\xF3 b\xE1o c\xE1o n\xE0o \u0111\u01B0\u1EE3c t\u1EA1o."
    },
    empty_state: "Ch\u01B0a c\xF3 b\xE1o c\xE1o n\xE0o. Nh\u1EA5p v\xE0o 'T\u1EA1o b\xE1o c\xE1o m\u1EDBi' \u0111\u1EC3 b\u1EAFt \u0111\u1EA7u.",
    list: {
      date: "Ng\xE0y t\u1EA1o",
      member: "Th\xE0nh vi\xEAn",
      status: "Tr\u1EA1ng th\xE1i",
      title: "Danh s\xE1ch b\xE1o c\xE1o"
    },
    statistics: {
      avg_duration: "Th\u1EDDi gian TB",
      completed_meetings: "1on1 \u0111\xE3 ho\xE0n th\xE0nh",
      hours: " gi\u1EDD",
      meeting_count: "S\u1ED1 cu\u1ED9c h\u1ECDp",
      minutes: " ph\xFAt",
      seconds: " gi\xE2y",
      times: "{{count}} l\u1EA7n",
      together_1on1: "1on1 c\xF9ng nhau"
    },
    table: {
      created_date: "Ng\xE0y t\u1EA1o",
      header: {
        content: "N\u1ED9i dung",
        member: "Th\xE0nh vi\xEAn",
        period: "Th\u1EDDi gian",
        register_date: "Ng\xE0y t\u1EA1o"
      },
      ic_report: "B\xE1o c\xE1o IC",
      leader_report: "B\xE1o c\xE1o Leader",
      member: "Th\xE0nh vi\xEAn",
      period: "Th\u1EDDi gian",
      report_type: "Lo\u1EA1i b\xE1o c\xE1o"
    },
    title: "B\xE1o c\xE1o th\xE0nh vi\xEAn"
  },
  member_update_dialog: {
    title: "C\u1EADp nh\u1EADt th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3"
  },
  mobile: {
    tabs: {
      prep: "Chu\u1EA9n b\u1ECB",
      content: "N\u1ED9i dung",
      analysis: "Ph\xE2n t\xEDch",
      action_item: "H\xE0nh \u0111\u1ED9ng",
      note: "Ghi ch\xFA",
      qna: "C\xE2u h\u1ECFi",
      summary: "T\xF3m t\u1EAFt",
      transcript: "B\u1EA3n ghi"
    },
    my_note: {
      placeholder: "H\xE3y vi\u1EBFt ghi ch\xFA ri\xEAng \u0111\u1EC3 tham kh\u1EA3o trong bu\u1ED5i h\u1ECDp.",
      save_failed: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u ghi ch\xFA c\u1EE7a b\u1EA1n. Vui l\xF2ng th\u1EED l\u1EA1i sau."
    }
  },
  recording: {
    badge: "Ghi \xE2m",
    credit_notice: "Khi k\u1EBFt th\xFAc ghi \xE2m, ph\xE2n t\xEDch s\u1EBD t\u1EF1 \u0111\u1ED9ng b\u1EAFt \u0111\u1EA7u v\xE0 d\xF9ng 1 l\u01B0\u1EE3t ph\xE2n t\xEDch.",
    upload_confirm: {
      title: "Ph\xE2n t\xEDch file n\xE0y nh\xE9?",
      body: "Ch\xFAng t\xF4i s\u1EBD b\u1EAFt \u0111\u1EA7u ph\xE2n t\xEDch bu\u1ED5i h\u1ECDp v\u1EDBi file \u0111\xE3 ch\u1ECDn ngay.",
      credit_notice: "Khi b\u1EAFt \u0111\u1EA7u ph\xE2n t\xEDch, 1 l\u01B0\u1EE3t ph\xE2n t\xEDch s\u1EBD \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng.",
      cancel: "H\u1EE7y",
      confirm: "B\u1EAFt \u0111\u1EA7u ph\xE2n t\xEDch"
    },
    error: "L\u1ED7i ghi \xE2m",
    error_mic_switch: "Kh\xF4ng th\u1EC3 chuy\u1EC3n \u0111\u1ED5i micro. Vui l\xF2ng ki\u1EC3m tra k\u1EBFt n\u1ED1i micro.",
    error_browser_not_supported: "Tr\xECnh duy\u1EC7t n\xE0y kh\xF4ng h\u1ED7 tr\u1EE3 ghi \xE2m.",
    error_mic_permission: "Vui l\xF2ng ki\u1EC3m tra quy\u1EC1n truy c\u1EADp micr\xF4.",
    error_update_failed: "Kh\xF4ng th\u1EC3 c\u1EADp nh\u1EADt th\xF4ng tin cu\u1ED9c h\u1ECDp.",
    error_save_failed: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u.",
    error_network_offline: "K\u1EBFt n\u1ED1i m\u1EA1ng b\u1ECB m\u1EA5t. Ghi \xE2m \u0111\xE3 d\u1EEBng.",
    error_recorder_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh ghi \xE2m.",
    error_track_ended: "Ghi \xE2m \u0111\xE3 d\u1EEBng do m\u1EA5t k\u1EBFt n\u1ED1i micr\xF4.",
    in_progress: "\u0110ang ghi \xE2m",
    owned_elsewhere: "\u0110ang ghi \xE2m \u1EDF tab kh\xE1c.",
    max_duration_reached: "\u0110\xE3 \u0111\u1EA1t th\u1EDDi gian ghi \xE2m t\u1ED1i \u0111a ({{hours}} gi\u1EDD). Ghi \xE2m \u0111\xE3 t\u1EF1 \u0111\u1ED9ng d\u1EEBng. K\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp \u0111\u1EC3 ph\xE2n t\xEDch b\u1EA3n ghi \xE2m hi\u1EC7n c\xF3.",
    mic_selector: {
      level_label: "M\u1EE9c \u0111\u1EA7u v\xE0o micro",
      placeholder: "Ch\u1ECDn m\u1ED9t micro",
      silence_warning: "Kh\xF4ng ph\xE1t hi\u1EC7n \xE2m thanh t\u1EEB micro n\xE0y.\nVui l\xF2ng ki\u1EC3m tra tr\u1EA1ng th\xE1i micro.",
      no_device_warning: "Kh\xF4ng c\xF3 micro kh\u1EA3 d\u1EE5ng. Vui l\xF2ng k\u1EBFt n\u1ED1i micro ho\u1EB7c ki\u1EC3m tra quy\u1EC1n truy c\u1EADp micro c\u1EE7a tr\xECnh duy\u1EC7t.",
      unknown_device: "Micro {{id}}",
      title: "Ch\u1ECDn micro"
    },
    pause: "T\u1EA1m d\u1EEBng",
    pending: {
      description: "C\xF3 b\u1EA3n ghi \xE2m tr\u01B0\u1EDBc \u0111\xF3 ({{duration}}) ch\u01B0a \u0111\u01B0\u1EE3c t\u1EA3i l\xEAn. B\u1EA1n c\xF3 mu\u1ED1n t\u1EA3i l\xEAn ngay b\xE2y gi\u1EDD kh\xF4ng?",
      description_mobile: "C\xF3 b\u1EA3n ghi \xE2m tr\u01B0\u1EDBc \u0111\xF3 ch\u01B0a \u0111\u01B0\u1EE3c t\u1EA3i l\xEAn. Vui l\xF2ng t\u1EA3i l\xEAn ho\u1EB7c x\xF3a b\u1EA3n ghi \xE2m.",
      description_corrupted: "B\u1EA3n ghi \xE2m c\u1EE7a cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc \u0111\xF3 kh\xF4ng \u0111\u01B0\u1EE3c l\u01B0u \u0111\xFAng c\xE1ch. B\u1EA1n c\xF3 th\u1EC3 b\u1EAFt \u0111\u1EA7u l\u1EA1i cu\u1ED9c h\u1ECDp t\u1EEB \u0111\u1EA7u, ho\u1EB7c k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp m\xE0 kh\xF4ng c\xF3 b\u1EA3n ghi \xE2m.",
      description_discard: "X\xF3a s\u1EBD b\u1EAFt \u0111\u1EA7u l\u1EA1i cu\u1ED9c h\u1ECDp t\u1EEB \u0111\u1EA7u.",
      description_restore: "T\u1EA3i l\xEAn s\u1EBD k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp.",
      discard: "X\xF3a",
      restore: "T\u1EA3i l\xEAn",
      title: "Ph\xE1t hi\u1EC7n b\u1EA3n ghi \xE2m ch\u01B0a l\u01B0u",
      restart_from_beginning: "B\u1EAFt \u0111\u1EA7u l\u1EA1i t\u1EEB \u0111\u1EA7u",
      end_without_recording: "K\u1EBFt th\xFAc kh\xF4ng c\u1EA7n ghi \xE2m"
    },
    restart: "Ghi \xE2m l\u1EA1i",
    save: "L\u01B0u",
    start: "B\u1EAFt \u0111\u1EA7u",
    stop: "D\u1EEBng",
    title: "Ghi \xE2m cu\u1ED9c h\u1ECDp",
    title_with_name: "1:1 v\u1EDBi {{name}}",
    title_with_name_mobile: "1:1 v\u1EDBi {{name}} \u0111ang di\u1EC5n ra",
    title_self_meeting: "1:1 \u0111ang di\u1EC5n ra",
    upload_recording: {
      cancel_upload_message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n h\u1EE7y t\u1EA3i l\xEAn kh\xF4ng?",
      cancel_upload_title: "H\u1EE7y t\u1EA3i l\xEAn",
      continue_upload: "\u0110ang t\u1EA3i l\xEAn...",
      description: "N\u1EBFu b\u1EA1n \u0111\xE3 c\xF3 b\u1EA3n ghi \xE2m, vui l\xF2ng t\u1EA3i l\xEAn.\nAI s\u1EBD ph\xE2n t\xEDch b\u1EA3n ghi \xE2m v\xE0 t\u1EF1 \u0111\u1ED9ng t\u1EA1o t\xF3m t\u1EAFt cu\u1ED9c h\u1ECDp.",
      duration_error: {
        description: "T\u1EC7p ghi \xE2m \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3 t\u1ED1i \u0111a {{hours}} gi\u1EDD.",
        title: "Th\u1EDDi gian ghi \xE2m v\u01B0\u1EE3t qu\xE1 gi\u1EDBi h\u1EA1n"
      },
      duration_warn: {
        description: "Th\xF4ng tin t\u1EC7p kh\xF4ng th\u1EC3 \u0111\u1ECDc \u0111\u01B0\u1EE3c, do \u0111\xF3 th\u1EDDi gian ch\xEDnh x\xE1c kh\xF4ng th\u1EC3 \u0111\u01B0\u1EE3c hi\u1EC3n th\u1ECB.",
        error: "T\u1EC7p \xE2m thanh kh\xF4ng th\u1EC3 \u0111\u1ECDc \u0111\u01B0\u1EE3c",
        title: "Th\xF4ng tin \u0111\u1ED9 d\xE0i \xE2m thanh kh\xF4ng t\xECm th\u1EA5y"
      },
      network_error: {
        alert: "K\u1EBFt n\u1ED1i m\u1EA1ng kh\xF4ng \u1ED5n \u0111\u1ECBnh. Vui l\xF2ng ki\u1EC3m tra k\u1EBFt n\u1ED1i v\xE0 th\u1EED l\u1EA1i.",
        description: "Vui l\xF2ng ki\u1EC3m tra k\u1EBFt n\u1ED1i m\u1EA1ng c\u1EE7a b\u1EA1n.",
        error: "K\u1EBFt n\u1ED1i m\u1EA1ng \u0111\xE3 \u0111\u01B0\u1EE3c kh\xF4i ph\u1EE5c, nh\u01B0ng t\u1EA3i l\xEAn v\u1EABn th\u1EA5t b\u1EA1i. Vui l\xF2ng l\xE0m m\u1EDBi v\xE0 th\u1EED l\u1EA1i.",
        failed: "T\u1EC7p kh\xF4ng th\u1EC3 t\u1EA3i l\xEAn sau khi kh\xF4i ph\u1EE5c k\u1EBFt n\u1ED1i m\u1EA1ng.",
        reconnected: "K\u1EBFt n\u1ED1i m\u1EA1ng \u0111\xE3 \u0111\u01B0\u1EE3c kh\xF4i ph\u1EE5c.",
        retry: "\u0110ang th\u1EED l\u1EA1i t\u1EA3i l\xEAn...",
        success: "T\u1EC7p \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA3i l\xEAn th\xE0nh c\xF4ng sau khi kh\xF4i ph\u1EE5c k\u1EBFt n\u1ED1i m\u1EA1ng. Cu\u1ED9c h\u1ECDp \u0111\xE3 k\u1EBFt th\xFAc.",
        title: "L\u1ED7i m\u1EA1ng"
      },
      size_error: {
        description: "K\xEDch th\u01B0\u1EDBc t\u1EC7p kh\xF4ng \u0111\u01B0\u1EE3c v\u01B0\u1EE3t qu\xE1 1GB.",
        title: "L\u1ED7i k\xEDch th\u01B0\u1EDBc t\u1EC7p"
      },
      start_upload: "B\u1EAFt \u0111\u1EA7u t\u1EA3i l\xEAn",
      title: "T\u1EA3i l\xEAn b\u1EA3n ghi \xE2m",
      type_error: {
        description: "Ch\u1EC9 c\xF3 th\u1EC3 t\u1EA3i l\xEAn t\u1EC7p \xE2m thanh.",
        title: "L\u1ED7i lo\u1EA1i t\u1EC7p"
      },
      upload_cancelled: "T\u1EA3i l\xEAn \u0111\xE3 b\u1ECB h\u1EE7y",
      upload_cancelled_message: "T\u1EA3i l\xEAn \u0111\xE3 b\u1ECB h\u1EE7y.",
      upload_error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA3i l\xEAn t\u1EC7p. Vui l\xF2ng l\xE0m m\u1EDBi v\xE0 th\u1EED l\u1EA1i.",
      upload_success: "T\u1EC7p \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA3i l\xEAn th\xE0nh c\xF4ng v\xE0 cu\u1ED9c h\u1ECDp \u0111\xE3 k\u1EBFt th\xFAc."
    },
    silence_warning: {
      leader: "Kh\xF4ng ph\xE1t hi\u1EC7n \u0111\u1EA7u v\xE0o micro. Vui l\xF2ng ki\u1EC3m tra c\xE0i \u0111\u1EB7t micro.",
      member: "Kh\xF4ng ph\xE1t hi\u1EC7n \xE2m thanh trong b\u1EA3n ghi \xE2m. Vui l\xF2ng y\xEAu c\u1EA7u ng\u01B0\u1EDDi ch\u1EE7 tr\xEC ki\u1EC3m tra micro."
    },
    warn: {
      message: "S\u1EAFp \u0111\u1EA1t th\u1EDDi gian ghi \xE2m t\u1ED1i \u0111a ({{hours}} gi\u1EDD). Ghi \xE2m s\u1EBD t\u1EF1 \u0111\u1ED9ng d\u1EEBng khi \u0111\u1EA1t {{hours}} gi\u1EDD.",
      title: "Th\xF4ng b\xE1o gi\u1EDBi h\u1EA1n th\u1EDDi gian ghi \xE2m"
    },
    permission_required_title: "C\u1EA7n quy\u1EC1n truy c\u1EADp micr\xF4",
    permission_required_description: "Vui l\xF2ng cho ph\xE9p truy c\u1EADp micr\xF4 trong C\xE0i \u0111\u1EB7t.",
    notification_permission_required_title: "C\u1EA7n quy\u1EC1n th\xF4ng b\xE1o",
    notification_permission_required_description: "C\u1EA7n quy\u1EC1n th\xF4ng b\xE1o \u0111\u1EC3 hi\u1EC3n th\u1ECB tr\u1EA1ng th\xE1i khi ghi \xE2m \u1EDF ch\u1EBF \u0111\u1ED9 n\u1EC1n. Vui l\xF2ng cho ph\xE9p th\xF4ng b\xE1o trong C\xE0i \u0111\u1EB7t.",
    go_to_settings: "\u0110i t\u1EDBi C\xE0i \u0111\u1EB7t",
    start_failed_title: "Kh\xF4ng th\u1EC3 b\u1EAFt \u0111\u1EA7u ghi \xE2m",
    stop_failed_title: "Kh\xF4ng th\u1EC3 d\u1EEBng ghi \xE2m",
    retry_message: "Vui l\xF2ng th\u1EED l\u1EA1i.",
    start_timeout_message: "Tr\xECnh ghi \xE2m kh\xF4ng ph\u1EA3n h\u1ED3i. Vui l\xF2ng \u0111\xF3ng ho\xE0n to\xE0n \u1EE9ng d\u1EE5ng r\u1ED3i m\u1EDF l\u1EA1i.",
    stop_timeout_message: "T\u1EC7p ghi \xE2m v\u1EABn \u0111ang \u0111\u01B0\u1EE3c l\u01B0u. Vui l\xF2ng \u0111\u1EE3i m\u1ED9t l\xE1t r\u1ED3i th\u1EED k\u1EBFt th\xFAc l\u1EA1i. N\u1EBFu v\u1EABn ti\u1EBFp di\u1EC5n, h\xE3y \u0111\xF3ng ho\xE0n to\xE0n \u1EE9ng d\u1EE5ng r\u1ED3i m\u1EDF l\u1EA1i v\xE0 l\xE0m theo h\u01B0\u1EDBng d\u1EABn kh\xF4i ph\u1EE5c b\u1EA3n ghi \xE2m.",
    uploading_title: "\u0110ang l\u01B0u b\u1EA3n ghi \xE2m...",
    upload_failed_title: "L\u01B0u b\u1EA3n ghi \xE2m th\u1EA5t b\u1EA1i.",
    retry_upload_button: "T\u1EA3i l\xEAn l\u1EA1i",
    no_network_title: "Kh\xF4ng c\xF3 k\u1EBFt n\u1ED1i m\u1EA1ng",
    no_network_message: "Vui l\xF2ng ki\u1EC3m tra k\u1EBFt n\u1ED1i m\u1EA1ng v\xE0 th\u1EED l\u1EA1i.",
    file_missing_title: "Kh\xF4ng t\xECm th\u1EA5y t\u1EC7p ghi \xE2m",
    file_missing_message: "T\u1EC7p ghi \xE2m \u0111\xE3 b\u1ECB x\xF3a kh\u1ECFi thi\u1EBFt b\u1ECB n\xEAn kh\xF4ng th\u1EC3 ph\xE2n t\xEDch. Cu\u1ED9c h\u1ECDp s\u1EBD \u0111\u01B0\u1EE3c k\u1EBFt th\xFAc nh\u01B0 hi\u1EC7n t\u1EA1i."
  },
  right_panel: {
    add_member: "Th\xEAm 1on1 \u0111\u1EC3 th\xEAm th\xE0nh vi\xEAn",
    add_pair: {
      error: "Th\xEAm th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
      success: "Th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm.",
      title: "Th\xEAm th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3"
    },
    delete_pair_modal: {
      description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 n\xE0y kh\xF4ng?",
      error: "X\xF3a th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
      success: "Th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
      title: "X\xF3a th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3"
    },
    empty_action_items: "Ch\u01B0a c\xF3 h\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng.",
    member_list_aria: "Danh s\xE1ch th\xE0nh vi\xEAn 1on1",
    member_subtitle: "{{count}} l\u1EA7n 1on1 \u2022 {{days}} ng\xE0y tr\u01B0\u1EDBc",
    more_actions: "Th\xEAm",
    no_history: "Kh\xF4ng c\xF3 l\u1ECBch s\u1EED",
    recurring_title: "Th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 ({{count}})",
    update_pair: {
      error: "C\u1EADp nh\u1EADt th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 th\u1EA5t b\u1EA1i. Vui l\xF2ng th\u1EED l\u1EA1i.",
      success: "Th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3 \u0111\xE3 \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt.",
      title: "C\u1EADp nh\u1EADt th\xE0nh vi\xEAn 1on1 \u0111\u1ECBnh k\u1EF3"
    }
  },
  sidebar: {
    action_items: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
    create_one_on_one: "T\u1EA1o 1on1 m\u1EDBi",
    dashboard: "B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n",
    guide: {
      guide_button: "H\u01B0\u1EDBng d\u1EABn",
      service_name: "Orblit 1on1"
    },
    invite_members: "M\u1EDDi th\xE0nh vi\xEAn",
    member_report: "B\xE1o c\xE1o th\xE0nh vi\xEAn",
    nine_box: "9Box",
    one_on_one_meeting: "Cu\u1ED9c h\u1ECDp 1on1",
    guide_menu: {
      trigger: "Tr\u1EE3 gi\xFAp",
      leader_guide: "H\u01B0\u1EDBng d\u1EABn cho qu\u1EA3n l\xFD",
      member_guide: "H\u01B0\u1EDBng d\u1EABn cho th\xE0nh vi\xEAn",
      service_guide: "H\u01B0\u1EDBng d\u1EABn chi ti\u1EBFt",
      voc: "Li\xEAn h\u1EC7 h\u1ED7 tr\u1EE3"
    },
    settings: "C\xE0i \u0111\u1EB7t",
    update_note: "C\u1EADp nh\u1EADt",
    whats_new: {
      dismiss: "\u0110\xF3ng",
      go_to_feature: "",
      headline: "Ph\u1EA3n h\u1ED3i d\xE0nh cho b\u1EA1n sau m\u1ED7i cu\u1ED9c h\u1ECDp",
      message: "Th\xE0nh vi\xEAn c\u0169ng s\u1EBD nh\u1EADn \u0111\u01B0\u1EE3c ph\u1EA3n h\u1ED3i cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo",
      title: "WHAT'S NEW !",
      view_update_note: "Xem ghi ch\xFA c\u1EADp nh\u1EADt"
    }
  },
  voc: {
    title: "Li\xEAn h\u1EC7 v\u1EDBi Orblit",
    description: "B\u1EA1n c\xF3 c\xE2u h\u1ECFi ho\u1EB7c g\u1EB7p \u0111i\u1EC1u g\xEC b\u1EA5t ti\u1EC7n khi s\u1EED d\u1EE5ng Orblit? H\xE3y \u0111\u1EC3 l\u1EA1i n\u1ED9i dung, ch\xFAng t\xF4i s\u1EBD ph\u1EA3n h\u1ED3i qua email \u0111\xE3 \u0111\u0103ng k\xFD.",
    field: {
      message: "N\u1ED9i dung li\xEAn h\u1EC7",
      message_hint: "N\u1EBFu c\xF3 l\u1ED7i x\u1EA3y ra, vi\u1EC7c m\xF4 t\u1EA3 t\xECnh hu\u1ED1ng tr\u01B0\u1EDBc v\xE0 sau \u0111\xF3 trong ph\u1EA1m vi b\u1EA1n th\u1EA5y tho\u1EA3i m\xE1i s\u1EBD gi\xFAp ch\xFAng t\xF4i x\xE1c \u0111\u1ECBnh nguy\xEAn nh\xE2n."
    },
    placeholder: {
      message: "H\xE3y chia s\u1EBB c\xE2u h\u1ECFi ho\u1EB7c \u0111i\u1EC1u b\u1EA1n \u0111ang g\u1EB7p ph\u1EA3i."
    },
    contact_notice: "Ph\u1EA3n h\u1ED3i s\u1EBD \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn email b\u1EA1n \u0111\xE3 \u0111\u0103ng k\xFD.",
    action: {
      cancel: "H\u1EE7y",
      submit: "G\u1EEDi li\xEAn h\u1EC7",
      confirm: "X\xE1c nh\u1EADn"
    },
    complete: {
      title: "Y\xEAu c\u1EA7u c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c ti\u1EBFp nh\u1EADn",
      description: "Ch\xFAng t\xF4i s\u1EBD xem x\xE9t v\xE0 ph\u1EA3n h\u1ED3i qua email b\u1EA1n \u0111\xE3 \u0111\u0103ng k\xFD."
    },
    error: {
      submit_failed: "Kh\xF4ng th\u1EC3 g\u1EEDi y\xEAu c\u1EA7u. Vui l\xF2ng th\u1EED l\u1EA1i sau \xEDt ph\xFAt."
    }
  },
  template: {
    add_questions: "Th\xEAm c\xE2u h\u1ECFi",
    add_questions_button: "S\u1EED d\u1EE5ng c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn ({{count}})",
    add_questions_error: "Th\xEAm c\xE2u h\u1ECFi th\u1EA5t b\u1EA1i",
    ai_template_input_section: {
      ai_suggestion: "\u0110\u1EC1 xu\u1EA5t AI",
      context: "C\xF3 b\u1ED1i c\u1EA3nh ho\u1EB7c t\xECnh hu\u1ED1ng n\xE0o \u0111\u1EC3 tham kh\u1EA3o cho vi\u1EC7c t\u1EA1o c\xE2u h\u1ECFi kh\xF4ng?",
      context_placeholder: " \u2022 C\xE1c t\xECnh hu\u1ED1ng v\u1EA5n \u0111\u1EC1 (hi\u1EC7u su\u1EA5t, h\u1EE3p t\xE1c, d\u1EA5u hi\u1EC7u chuy\u1EC3n vi\u1EC7c, v.v.)\n \u2022 Ti\u1EBFn \u0111\u1ED9 d\u1EF1 \xE1n\n \u2022 M\u1ED1i quan t\xE2m v\u1EC1 s\u1EF1 nghi\u1EC7p v\xE0 ph\xE1t tri\u1EC3n",
      counterpart: "H\xE3y cho ch\xFAng t\xF4i bi\u1EBFt v\u1EC1 \u0111\u1ED1i t\xE1c cu\u1ED9c h\u1ECDp c\u1EE7a b\u1EA1n.",
      counterpart_placeholder: "M\xF4 t\u1EA3",
      create: "T\u1EA1o Template AI",
      details: "\u0110i\u1EC1u ch\u1EC9nh chi ti\u1EBFt",
      hide_label: "Thu g\u1ECDn",
      include_multiple_choices: "Bao g\u1ED3m c\xE1c c\xE2u h\u1ECFi tr\u1EAFc nghi\u1EC7m",
      last_meeting_content: "N\u1ED9i dung tr\u01B0\u1EDBc",
      meeting_prep: "B\u1EA3n chu\u1EA9n b\u1ECB",
      meeting_prep_tooltip: "N\u1ED9i dung b\u1EA3n chu\u1EA9n b\u1ECB s\u1EBD \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 t\u1EA1o c\xE2u h\u1ECFi. N\u1EBFu b\u1EA1n mu\u1ED1n th\xEAm th\xF4ng tin, vui l\xF2ng nh\u1EADp b\xEAn d\u01B0\u1EDBi.",
      previous_meeting: "B\u1EA3n ghi 1on1 tr\u01B0\u1EDBc",
      question_count: {
        Advanced: "Chi ti\u1EBFt",
        Simple: "\u0110\u01A1n gi\u1EA3n",
        Standard: "Ti\xEAu chu\u1EA9n",
        title: "S\u1ED1 c\xE2u h\u1ECFi \u0111\u01B0\u1EE3c t\u1EA1o"
      },
      question_style: "Vui l\xF2ng ch\u1ECDn ki\u1EC3u c\xE2u h\u1ECFi c\u1EE7a b\u1EA1n.",
      question_types: {
        "Action/Implementation-focused": {
          description: "B\u1EA1n s\u1EBD th\u1EF1c hi\u1EC7n nh\u1EEFng h\xE0nh \u0111\u1ED9ng c\u1EE5 th\u1EC3 n\xE0o trong 3 th\xE1ng t\u1EDBi?",
          title: "H\xE0nh \u0111\u1ED9ng/Th\u1EF1c hi\u1EC7n"
        },
        "Experience/Story-based": {
          description: "Tr\u1EA3i nghi\u1EC7m d\u1EF1 \xE1n \u0111\xE1ng nh\u1EDB nh\u1EA5t g\u1EA7n \u0111\xE2y c\u1EE7a b\u1EA1n l\xE0 g\xEC?",
          title: "Kinh nghi\u1EC7m/C\xE2u chuy\u1EC7n"
        },
        "Growth/Goal-oriented": {
          description: "B\u1EA1n mu\u1ED1n tr\u1EDF th\xE0nh g\xEC sau m\u1ED9t n\u0103m n\u1EEFa?",
          title: "Ph\xE1t tri\u1EC3n/M\u1EE5c ti\xEAu"
        },
        "Multiple-choice": {
          description: "H\xE3y \u0111\xE1nh gi\xE1 m\u1EE9c \u0111\u1ED9 h\xE0i l\xF2ng c\u1EE7a b\u1EA1n v\u1EDBi c\xF4ng vi\u1EC7c hi\u1EC7n t\u1EA1i tr\xEAn thang \u0111i\u1EC3m 1-5",
          title: "\u0110\xE1nh gi\xE1"
        },
        "Reflection/Thought-provoking": {
          description: "\u0110i\u1EC1u g\xEC b\u1EA1n c\u1EA3m th\u1EA5y c\xF3 \xFD ngh\u0129a nh\u1EA5t trong vai tr\xF2 hi\u1EC7n t\u1EA1i?",
          title: "Suy ng\u1EABm/T\u01B0 duy"
        },
        "Relationship/Collaboration": {
          description: "\u0110i\u1EC1u g\xEC h\u1EEFu \xEDch nh\u1EA5t khi h\u1EE3p t\xE1c v\u1EDBi \u0111\u1ED3ng nghi\u1EC7p?",
          title: "M\u1ED1i quan h\u1EC7/H\u1EE3p t\xE1c"
        },
        title: "Lo\u1EA1i c\xE2u h\u1ECFi"
      },
      recommended: "\u0110\u1EC1 xu\u1EA5t",
      show_label: "Xem th\xEAm",
      subject: "Vui l\xF2ng ch\u1ECDn ch\u1EE7 \u0111\u1EC1 b\u1EA1n mu\u1ED1n th\u1EA3o lu\u1EADn.",
      subject_default_value: "\u0110\xE2y l\xE0 ch\u1EE7 \u0111\u1EC1 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 t\u1EA1o ra c\xE1c c\xE2u h\u1ECFi cho cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc \u0111\xF3.",
      subject_options: {
        action_review: "Ki\u1EC3m tra c\xE1c nhi\u1EC7m v\u1EE5 c\u1EA7n th\u1EF1c hi\u1EC7n",
        feedback_coaching: "Ph\u1EA3n h\u1ED3i v\xE0 Hu\u1EA5n luy\u1EC7n",
        growth_career: "T\xECnh tr\u1EA1ng c\xF4ng vi\u1EC7c v\xE0 c\xE1c r\xE0o c\u1EA3n",
        relationship: "M\u1ED1i quan h\u1EC7 v\xE0 C\u1EADp nh\u1EADt g\u1EA7n \u0111\xE2y",
        satisfaction_motivation: "S\u1EF1 h\xE0i l\xF2ng trong c\xF4ng vi\u1EC7c v\xE0 \u0111\u1ED9ng l\u1EF1c",
        work_status: "Work Status and Obstacles"
      },
      subject_placeholder: "Ch\u1ECDn m\u1ED9t ch\u1EE7 \u0111\u1EC1",
      title: "T\u1EA1o Template AI",
      tone: {
        Casual: "Tho\u1EA3i m\xE1i",
        Formal: "L\u1ECBch s\u1EF1",
        title: "Gi\u1ECDng \u0111i\u1EC7u & C\xE1ch th\u1EE9c"
      },
      use: "S\u1EED d\u1EE5ng"
    },
    ai_template_output_section: {
      create_template_button: "S\u1EED d\u1EE5ng c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn ({{count}})",
      done: "\u0110\xE3 t\u1EA1o!",
      generate_questions_prompt: {
        description: "AI s\u1EBD t\u1EA1o c\xE2u h\u1ECFi v\xE0 h\u01B0\u1EDBng d\u1EABn s\u1EED d\u1EE5ng ph\xF9 h\u1EE3p cho cu\u1ED9c h\u1ECDp c\u1EE7a b\u1EA1n.",
        title: "\u0110i\u1EC1n v\xE0o bi\u1EC3u m\u1EABu b\xEAn tr\xE1i v\xE0 t\u1EA1o c\xE2u h\u1ECFi"
      },
      guide: {
        description: "AI \u0111ang ph\xE2n t\xEDch c\xE1c c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn v\xE0\nvi\u1EBFt h\u01B0\u1EDBng d\u1EABn cu\u1ED9c h\u1ECDp 1:1 c\xE1 nh\xE2n h\xF3a.",
        generate_guide: "T\u1EA1o h\u01B0\u1EDBng d\u1EABn s\u1EED d\u1EE5ng c\xE2u h\u1ECFi",
        loading: "\u0110ang t\u1EA1o h\u01B0\u1EDBng d\u1EABn...",
        title: "H\u01B0\u1EDBng d\u1EABn s\u1EED d\u1EE5ng c\xE2u h\u1ECFi"
      },
      no_questions: "Kh\xF4ng c\xF3 c\xE2u h\u1ECFi \u0111\u01B0\u1EE3c t\u1EA1o.",
      title: "Template AI"
    },
    copied_to_clipboard: "\u0110\xE3 sao ch\xE9p v\xE0o clipboard",
    copied_to_clipboard_message: "H\u01B0\u1EDBng d\u1EABn \u0111\xE3 \u0111\u01B0\u1EE3c sao ch\xE9p.",
    copy: "Sao ch\xE9p",
    create: "T\u1EA1o m\u1EDBi",
    create_page: {
      add_question: "Th\xEAm c\xE2u h\u1ECFi",
      delete_question: "X\xF3a c\xE2u h\u1ECFi",
      delete_question_description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a c\xE2u h\u1ECFi n\xE0y kh\xF4ng?",
      description_placeholder: "Vui l\xF2ng nh\u1EADp m\xF4 t\u1EA3 m\u1EABu",
      owner: "Ch\u1EE7 s\u1EDF h\u1EEFu",
      owner_user: "M\u1EABu c\u1EE7a t\xF4i",
      owner_workspace: "M\u1EABu c\u1EE7a nh\xF3m",
      question_placeholder: "Vui l\xF2ng nh\u1EADp c\xE2u h\u1ECFi",
      save_button: "L\u01B0u m\u1EABu",
      title: "T\u1EA1o m\u1EABu m\u1EDBi",
      title_placeholder: "Vui l\xF2ng nh\u1EADp ti\xEAu \u0111\u1EC1 m\u1EABu"
    },
    delete_modal: {
      description: "M\u1ED9t khi \u0111\xE3 x\xF3a, m\u1EABu s\u1EBD kh\xF4ng th\u1EC3 kh\xF4i ph\u1EE5c. B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a kh\xF4ng?",
      error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi x\xF3a m\u1EABu.",
      success: "M\u1EABu \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a.",
      title: "X\xF3a m\u1EABu"
    },
    edit_page: {
      save_button: "L\u01B0u",
      title: "Ch\u1EC9nh s\u1EEDa m\u1EABu"
    },
    generating_loader: {
      analyzing_prep: "\u0110ang ph\xE2n t\xEDch b\u1EA3n chu\u1EA9n b\u1ECB c\u1EE7a \u0111\u1ED1i t\xE1c...",
      applying_prompt: "\u0110ang \xE1p d\u1EE5ng y\xEAu c\u1EA7u c\u1EE7a b\u1EA1n...",
      checking_profile: "\u0110ang tham kh\u1EA3o phong c\xE1ch giao ti\u1EBFp v\xE0 s\u1EDF th\xEDch c\u1EE7a \u0111\u1ED1i t\xE1c...",
      checking_session: "\u0110ang ki\u1EC3m tra b\u1EA3n ghi cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc...",
      composing_topics: "\u0110ang so\u1EA1n c\xE2u h\u1ECFi d\u1EF1a tr\xEAn ch\u1EE7 \u0111\u1EC1 b\u1EA1n \u0111\xE3 ch\u1ECDn...",
      finalizing: "\u0110ang ho\xE0n thi\u1EC7n c\xE2u h\u1ECFi...",
      refining_tone: "\u0110ang \u0111i\u1EC1u ch\u1EC9nh c\xE2u h\u1ECFi ph\xF9 h\u1EE3p v\u1EDBi gi\u1ECDng \u0111i\u1EC7u cu\u1ED9c tr\xF2 chuy\u1EC7n..."
    },
    owner_user: "M\u1EABu c\u1EE7a t\xF4i",
    owner_workspace: "M\u1EABu c\u1EE7a nh\xF3m",
    page_title: "Nh\u1EADp m\u1EABu",
    placeholder: "Vui l\xF2ng ch\u1ECDn m\u1ED9t m\u1EABu.",
    question_list: "Danh s\xE1ch c\xE2u h\u1ECFi",
    refresh_message: "Vui l\xF2ng l\xE0m m\u1EDBi trang v\xE0 th\u1EED l\u1EA1i.",
    register_date: "Ng\xE0y t\u1EA1o",
    success: "C\xE2u h\u1ECFi \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm th\xE0nh c\xF4ng.",
    system: "Orblit"
  },
  time: {
    label: "H\xE3y ch\u1ECDn th\u1EDDi gian cu\u1ED9c h\u1ECDp."
  },
  onboarding_tour: {
    next: "Ti\u1EBFp theo",
    resume_banner: {
      body: "B\u1EA1n c\xF3 m\u1ED9t h\u01B0\u1EDBng d\u1EABn \u0111ang dang d\u1EDF.",
      action: "Ti\u1EBFp t\u1EE5c"
    },
    stages: {
      create: "T\u1EA1o bu\u1ED5i h\u1ECDp",
      questions: "Chu\u1EA9n b\u1ECB c\xE2u h\u1ECFi",
      record: "Ghi \xE2m",
      analysis: "Xem ph\xE2n t\xEDch"
    },
    create_meeting: {
      title: "C\xF9ng chu\u1EA9n b\u1ECB bu\u1ED5i 1:1 \u0111\u1EA7u ti\xEAn nh\xE9?",
      body: "Ch\xFAng t\xF4i s\u1EBD h\u01B0\u1EDBng d\u1EABn b\u1EA1n c\xE1ch th\u1EF1c hi\u1EC7n 1:1 hi\u1EC7u qu\u1EA3.\nH\xE3y b\u1EAFt \u0111\u1EA7u b\u1EB1ng c\xE1ch nh\u1EA5n \u201CT\u1EA1o bu\u1ED5i 1:1 \u0111\u1EA7u ti\xEAn\u201D."
    },
    self_meeting: {
      title: "B\u1EA1n kh\xF4ng c\u1EA7n ch\u1ECDn \u0111\u1ED1i ph\u01B0\u01A1ng",
      body: "Ch\xFAng t\xF4i khuy\xEAn b\u1EA1n n\xEAn m\u1EDDi th\xE0nh vi\xEAn \u0111\u1EC3 chia s\u1EBB c\xE2u h\u1ECFi v\xE0 c\xE2u tr\u1EA3 l\u1EDDi 1:1,\nnh\u01B0ng b\u1EA1n c\u0169ng c\xF3 th\u1EC3 th\u1EED m\u1ED9t m\xECnh tr\u01B0\u1EDBc khi m\u1EDDi ai."
    },
    self_meeting_has_members: {
      title: "B\xE2y gi\u1EDD h\xE3y th\u1EED m\xE0 kh\xF4ng ch\u1ECDn \u0111\u1ED1i ph\u01B0\u01A1ng nh\xE9",
      body: "Th\xF4ng th\u01B0\u1EDDng b\u1EA1n s\u1EBD ch\u1ECDn m\u1ED9t \u0111\u1ED1i ph\u01B0\u01A1ng t\u1EEB c\xE1c th\xE0nh vi\xEAn \u1EDF tr\xEAn cho bu\u1ED5i 1on1.\nV\xEC \u0111\xE2y l\xE0 ph\u1EA7n h\u01B0\u1EDBng d\u1EABn, h\xE3y th\u1EED 'T\u1EA1o m\xE0 kh\xF4ng c\xF3 \u0111\u1ED1i t\xE1c' \u0111\u1EC3 tr\u1EA3i nghi\u1EC7m nhanh."
    },
    credit_info: {
      title: "B\u1EA1n c\xF3 th\u1EC3 ph\xE2n t\xEDch {{remaining}} bu\u1ED5i h\u1ECDp",
      body: "V\u1EDBi g\xF3i tr\u1EA3 ph\xED, ch\xFAng t\xF4i ph\xE2n t\xEDch n\u1ED9i dung bu\u1ED5i h\u1ECDp v\xE0 \u0111\u01B0a ph\u1EA3n h\u1ED3i cho ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o. Qu\xE0 ch\xE0o m\u1EEBng: 3 l\u01B0\u1EE3t ph\xE2n t\xEDch.\nM\u1EDDi \u0111\u1ED3ng nghi\u1EC7p ho\u1EB7c gi\u1EDBi thi\u1EC7u ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o kh\xE1c \u0111\u1EC3 nh\u1EADn th\xEAm."
    },
    create_submit: {
      title: "Gi\u1EDD h\xE3y t\u1EA1o bu\u1ED5i h\u1ECDp nh\xE9",
      body: "B\u1EA1n ch\u01B0a ch\u1EC9 \u0111\u1ECBnh th\xE0nh vi\xEAn c\xF9ng tham gia n\xEAn c\u1EE9 t\u1EA1o ngay.\nV\u1EDBi \u0111\u1ED3ng nghi\u1EC7p th\u1EADt, h\xE3y ch\u1ECDn ng\xE0y v\xE0 gi\u1EDD tr\u01B0\u1EDBc."
    },
    add_question: {
      title: "\u0110\xE2y l\xE0 n\u01A1i b\u1EA1n chu\u1EA9n b\u1ECB v\xE0 ti\u1EBFn h\xE0nh 1on1",
      body: "B\u1EA1n c\u0169ng c\xF3 th\u1EC3 t\u1EF1 nh\u1EADp c\xE2u h\u1ECFi t\u1EA1i \u0111\xE2y.\nGhi c\xE2u h\u1ECFi v\xE0 l\u01B0u c\xE2u tr\u1EA3 l\u1EDDi khi cu\u1ED9c tr\xF2 chuy\u1EC7n di\u1EC5n ra."
    },
    load_template: {
      title: "Bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn? B\u1EAFt \u0111\u1EA7u v\u1EDBi c\xE2u h\u1ECFi g\u1EE3i \xFD",
      body: "B\u1EA1n c\xF3 th\u1EC3 t\u1EF1 vi\u1EBFt c\xE2u h\u1ECFi,\nnh\u01B0ng d\xF9ng c\xE2u h\u1ECFi c\xF3 s\u1EB5n s\u1EBD d\u1EC5 h\u01A1n."
    },
    select_template: {
      title: "Ch\xFAng t\xF4i g\u1EE3i \xFD m\u1EABu n\xE0y cho bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn",
      body: "Nh\u1EA5n v\xE0o th\u1EBB \u0111\u1EC3 xem m\u1EABu c\xF3 nh\u1EEFng c\xE2u h\u1ECFi n\xE0o."
    },
    use_template_questions: {
      title: "Th\xEAm c\xE1c c\xE2u h\u1ECFi n\xE0y v\xE0o bu\u1ED5i h\u1ECDp?",
      body: "Nh\u1EA5n \u201CD\xF9ng c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn\u201D\n\u0111\u1EC3 th\xEAm c\xE1c c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn v\xE0o bu\u1ED5i h\u1ECDp."
    },
    start_recording: {
      title: "B\u1EA1n ch\u1EC9 c\u1EA7n ghi \xE2m\u2014ph\u1EA7n c\xF2n l\u1EA1i Orblit lo",
      body: "Trong bu\u1ED5i h\u1ECDp th\u1EADt, nh\u1EA5n \u201CB\u1EAFt \u0111\u1EA7u ghi \xE2m\u201D ho\u1EB7c t\u1EA3i l\xEAn file ghi \xE2m,\nOrblit s\u1EBD t\u1EF1 s\u1EAFp x\u1EBFp v\xE0 ph\xE2n t\xEDch cho b\u1EA1n.\n\n\u0110\xE2y l\xE0 h\u01B0\u1EDBng d\u1EABn\u2014th\u1EED m\u1ED9t b\u1EA3n ghi \xE2m m\u1EABu \u0111\u1EC3 xem c\xE1ch ph\xE2n t\xEDch nh\xE9?\n(B\u1EA3n tr\u1EA3i nghi\u1EC7m m\u1EABu kh\xF4ng d\xF9ng l\u01B0\u1EE3t ph\xE2n t\xEDch n\xE0o.)",
      analyze_sample: "Th\u1EED ph\xE2n t\xEDch v\u1EDBi m\u1EABu",
      analyze_sample_failed: "Ph\xE2n t\xEDch m\u1EABu th\u1EA5t b\u1EA1i. Vui l\xF2ng nh\u1EA5n l\u1EA1i \u201CTh\u1EED ph\xE2n t\xEDch v\u1EDBi m\u1EABu\u201D."
    },
    sample_analyzing_screen: {
      title: "\u0110ang chu\u1EA9n b\u1ECB ph\xE2n t\xEDch m\u1EABu",
      body: "Vui l\xF2ng \u0111\u1EE3i m\u1ED9t ch\xFAt \u2014 ch\xFAng t\xF4i s\u1EBD hi\u1EC3n th\u1ECB k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch m\u1EABu ngay."
    },
    view_summary: {
      title: "Xem t\xF3m t\u1EAFt c\u1EE7a bu\u1ED5i h\u1ECDp n\xE0y nh\xE9?",
      body: "Nh\u1EA5n v\xE0o tab 'T\xF3m t\u1EAFt'.\nCh\xFAng t\xF4i s\u1EBD t\u1ED5ng h\u1EE3p nh\u1EEFng n\u1ED9i dung ch\xEDnh v\xE0 quy\u1EBFt \u0111\u1ECBnh t\u1EEB cu\u1ED9c tr\xF2 chuy\u1EC7n trong nh\xE1y m\u1EAFt."
    },
    view_analysis: {
      title: "Xem c\u1EA3 ph\u1EA7n ph\xE2n t\xEDch bu\u1ED5i h\u1ECDp n\u1EEFa nh\xE9",
      body: "Nh\u1EA5n v\xE0o tab 'Ph\xE2n t\xEDch'.\nXem b\u1EA1n \u0111\xE3 kh\u01A1i g\u1EE3i \u0111\u01B0\u1EE3c bao nhi\xEAu t\u1EEB th\xE0nh vi\xEAn, c\xF9ng \u0111i\u1EC3m m\u1EA1nh v\xE0 \u0111i\u1EC3m c\u1EA7n c\u1EA3i thi\u1EC7n c\u1EE7a b\u1EA1n v\u1EDBi vai tr\xF2 ng\u01B0\u1EDDi d\u1EABn d\u1EAFt."
    },
    celebration: {
      title: "B\u1EA1n \u0111\xE3 ho\xE0n th\xE0nh ph\u1EA7n h\u01B0\u1EDBng d\u1EABn!",
      body: "Gi\u1EDD h\xE3y chu\u1EA9n b\u1ECB m\u1ED9t bu\u1ED5i 1on1 th\u1EADt v\u1EDBi Orblit.\nM\u1EDDi \u0111\u1ED3ng \u0111\u1ED9i \u0111\u1EC3 c\xF9ng chia s\u1EBB c\xE2u h\u1ECFi v\xE0 c\xE2u tr\u1EA3 l\u1EDDi.",
      confirm: "Xong"
    }
  },
  onboarding_intro: {
    title: "D\u1EABn d\u1EAFt b\u1EB1ng tr\xF2 chuy\u1EC7n, kh\xF4ng ph\u1EA3i ki\u1EC3m so\xE1t",
    body_value: "M\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n 1on1 v\u1EDBi th\xE0nh vi\xEAn x\xE2y d\u1EF1ng hi\u1EC7u su\u1EA5t\nv\xE0 ni\u1EC1m tin c\u1EE7a \u0111\u1ED9i ng\u0169 h\u01A1n c\u1EA3 nh\u1EEFng cu\u1ED9c h\u1ECDp hay ch\u1EC9 th\u1ECB.",
    body_promise: "T\u1EEB chu\u1EA9n b\u1ECB \u0111\u1EBFn ph\xE2n t\xEDch, Orblit lo h\u1EBFt.\n{{name}} ch\u1EC9 c\u1EA7n t\u1EADp trung v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n.",
    page2_title: "Ch\u1EC9 2 ph\xFAt, d\u1EA1o m\u1ED9t v\xF2ng th\u1EED nh\xE9?",
    page2_subtitle: "B\u1EA1n c\xF3 th\u1EC3 tr\u1EA3i nghi\u1EC7m Orblit ngay b\xE2y gi\u1EDD.\nL\xE0m quen v\u1EDBi c\xE1ch d\xF9ng qua h\u01B0\u1EDBng d\u1EABn ng\u1EAFn.",
    steps: {
      create: "T\u1EA1o m\u1ED9t cu\u1ED9c h\u1ECDp 1on1",
      questions: "Ch\u1ECDn c\xE2u h\u1ECFi \u0111\u1EC3 tr\xF2 chuy\u1EC7n",
      record: "Th\u1EED ph\xE2n t\xEDch v\u1EDBi b\u1EA3n ghi \xE2m m\u1EABu",
      analysis: "Xem c\xE1ch ph\xE2n t\xEDch ho\u1EA1t \u0111\u1ED9ng"
    },
    next: "Ti\u1EBFp theo",
    guide_link: "B\u1EA1n c\xF3 mu\u1ED1n t\xECm hi\u1EC3u th\xEAm m\u1ED9t ch\xFAt v\u1EC1 1on1 kh\xF4ng?",
    skip: "B\u1ECF qua",
    skip_tooltip: "Khoan \u0111\xE3! Th\u1EADt s\u1EF1 ch\u1EC9 m\u1EA5t 2 ph\xFAt th\xF4i.\nCh\u1EC9 c\u1EA7n 2 ph\xFAt \u0111\u1EC3 l\xE0m quen v\u1EDBi Orblit",
    cta: "B\u1EAFt \u0111\u1EA7u"
  }
};

// src/locales/vi-VN/ninebox.json
var ninebox_default3 = {
  grid: {
    performance_potential: {
      title: "Performance & Potential",
      x_label: "Performance",
      y_label: "Potential",
      star: "Star",
      star_desc: "Top performer with the highest potential \u2014 a key talent in the organization",
      developing_top: "Developing Top",
      developing_top_desc: "Shows strong performance with high potential to grow into a next-generation leader",
      potential_top: "Potential Top",
      potential_top_desc: "Currently low performance, but has very high learning ability and growth potential",
      strong: "Strong",
      strong_desc: "Delivers outstanding results in their current role as a reliable performer",
      developing: "Developing",
      developing_desc: "Serves as the backbone of the organization with steady performance and expected growth",
      potential: "Potential",
      potential_desc: "Has basic potential but needs additional training or role adjustment to deliver results",
      dependable: "Dependable",
      dependable_desc: "Performs well but is specialized in their current role \u2014 better suited for an expert track than vertical promotion",
      inconsistent: "Inconsistent",
      inconsistent_desc: "Performance is unstable; needs capability development or motivation support",
      unsatisfactory: "Unsatisfactory",
      unsatisfactory_desc: "Both performance and potential are low \u2014 consider role reassignment or performance management"
    },
    flight_risk: {
      title: "Flight Risk Matrix",
      x_label: "Impact of Departure",
      y_label: "Flight Risk",
      critical: "Critical",
      critical_desc: "Both flight risk and impact are at the highest level. Immediate retention strategy required",
      act_fast: "Act Fast",
      act_fast_desc: "Clear signs of attrition. Even with moderate impact, prompt meetings and improved treatment are needed",
      watch_closely: "Watch Closely",
      watch_closely_desc: "Low impact but high likelihood of leaving \u2014 identify root causes and monitor closely",
      alert: "Alert",
      alert_desc: "Signs of attrition detected in a high-impact employee. Intensive relationship management needed",
      manage: "Manage",
      manage_desc: "Standard level of risk management with periodic satisfaction checks",
      monitor: "Monitor",
      monitor_desc: "Not urgent, but monitor for potential flight risk factors",
      key_talent: "Key Talent",
      key_talent_desc: "Very high impact but low flight risk. Maintain stability through ongoing rewards and vision",
      stable: "Stable",
      stable_desc: "Well-settled in the organization with low risk",
      low_risk: "Low Risk",
      low_risk_desc: "Both flight risk and impact are low \u2014 lower priority for management resources"
    }
  },
  right_section: {
    direct_members: "c\u1EA5p d\u01B0\u1EDBi tr\u1EF1c ti\u1EBFp",
    indirect_members: "c\u1EA5p d\u01B0\u1EDBi gi\xE1n ti\u1EBFp"
  },
  member_detail: {
    member_info: "Th\xF4ng tin th\xE0nh vi\xEAn",
    leader: "Qu\u1EA3n l\xFD",
    work_engagement: "S\u1EF1 tham gia c\xF4ng vi\u1EC7c",
    growth_satisfaction: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n",
    history: "L\u1ECBch s\u1EED",
    loading: "\u0110ang t\u1EA3i...",
    no_history: "Kh\xF4ng c\xF3 l\u1ECBch s\u1EED"
  },
  filter: {
    all_members: "Hi\u1EC3n th\u1ECB t\u1EA5t c\u1EA3 th\xE0nh vi\xEAn",
    direct_members_only: "Ch\u1EC9 hi\u1EC3n th\u1ECB c\u1EA5p d\u01B0\u1EDBi tr\u1EF1c ti\u1EBFp"
  },
  last_update: "C\u1EADp nh\u1EADt g\u1EA7n \u0111\xE2y"
};

// src/locales/vi-VN/template.json
var template_default3 = {
  oneonone: {
    career_development: {
      title: "M\u1EABu Ph\xE1t Tri\u1EC3n S\u1EF1 Nghi\u1EC7p",
      questions: [
        "Kh\xEDa c\u1EA1nh t\u1ED1t nh\u1EA5t v\xE0 kh\xEDa c\u1EA1nh \u0111\xE1ng ti\u1EBFc nh\u1EA5t trong c\xF4ng vi\u1EC7c c\u1EE7a b\u1EA1n k\u1EC3 t\u1EEB cu\u1ED9c h\u1ECDp tr\u01B0\u1EDBc l\xE0 g\xEC?",
        "B\u1EA1n mu\u1ED1n tham gia ho\u1EB7c tham gia s\xE2u h\u01A1n v\xE0o d\u1EF1 \xE1n n\xE0o?",
        "C\xF3 \u0111i\u1EC1u g\xEC b\u1EA1n th\u1EF1c s\u1EF1 mu\u1ED1n th\u1EED nh\u01B0ng ch\u01B0a th\u1EC3 l\xE0m do thi\u1EBFu th\u1EDDi gian ho\u1EB7c ngu\u1ED3n l\u1EF1c kh\xF4ng?",
        "M\u1EE5c ti\xEAu ngh\u1EC1 nghi\u1EC7p n\xE0o b\u1EA1n mu\u1ED1n \u0111\u1EA1t \u0111\u01B0\u1EE3c trong 6-12 th\xE1ng t\u1EDBi v\xE0 l\xFD do b\u1EA1n ngh\u0129 nh\u01B0 v\u1EADy l\xE0 g\xEC?",
        "B\u1EA1n c\xF3 th\u1EC3 chia s\u1EBB b\u1EA5t k\u1EF3 m\u1EE5c ti\xEAu ho\u1EB7c k\u1EBF ho\u1EA1ch n\xE0o cho 5 n\u0103m t\u1EDBi kh\xF4ng?",
        "C\xF3 \u0111i\u1EC1u g\xEC t\xF4i c\xF3 th\u1EC3 l\xE0m \u0111\u1EC3 gi\xFAp \u0111\u1EE1 s\u1EF1 ph\xE1t tri\u1EC3n ngh\u1EC1 nghi\u1EC7p c\u1EE7a b\u1EA1n kh\xF4ng?",
        "B\u1EA1n \u0111ang l\xE0m g\xEC cho b\u1EA3n th\xE2n m\xECnh nh\u1EEFng ng\xE0y n\xE0y?",
        "M\u1ED9t \u0111i\u1EC1u b\u1EA1n mu\u1ED1n l\xE0m nhi\u1EC1u h\u01A1n ngo\xE0i c\xF4ng vi\u1EC7c trong th\xE1ng t\u1EDBi l\xE0 g\xEC?"
      ]
    },
    growth_planning: {
      title: "M\u1EABu Th\u1EA3o Lu\u1EADn K\u1EBF Ho\u1EA1ch Ph\xE1t Tri\u1EC3n",
      questions: [
        "\u0110i\u1EC1u g\xEC b\u1EA1n c\u1EA3m th\u1EA5y t\xEDch c\u1EF1c nh\u1EA5t v\u1EC1 vai tr\xF2 hi\u1EC7n t\u1EA1i c\u1EE7a m\xECnh?",
        "Kh\xEDa c\u1EA1nh kh\xF3 kh\u0103n nh\u1EA5t trong vai tr\xF2 hi\u1EC7n t\u1EA1i c\u1EE7a b\u1EA1n l\xE0 g\xEC?",
        "C\u01A1 h\u1ED9i ho\u1EB7c kh\u1EA3 n\u0103ng m\u1EDBi n\xE0o b\u1EA1n \u0111\xE3 ph\xE1t hi\u1EC7n trong c\xF4ng vi\u1EC7c hi\u1EC7n t\u1EA1i c\u1EE7a m\xECnh?",
        "M\u1EE5c ti\xEAu kinh doanh n\xE0o b\u1EA1n mu\u1ED1n \u0111\u1EA1t \u0111\u01B0\u1EE3c trong c\xF4ng vi\u1EC7c?",
        "M\u1EE5c ti\xEAu c\xE1 nh\xE2n n\xE0o b\u1EA1n mu\u1ED1n ho\xE0n th\xE0nh ngo\xE0i c\xF4ng vi\u1EC7c?",
        "Ch\u1EE7 \u0111\u1EC1 n\xE0o khi\u1EBFn b\u1EA1n quan t\xE2m nh\u1EA5t g\u1EA7n \u0111\xE2y, ho\u1EB7c k\u1EF9 n\u0103ng/tr\u1EA3i nghi\u1EC7m n\xE0o b\u1EA1n mu\u1ED1n h\u1ECDc?",
        "Nh\u1EEFng m\u1EA1ng l\u01B0\u1EDBi n\xE0o b\u1EA1n hi\u1EC7n \u0111ang s\u1EED d\u1EE5ng? Nh\u1EEFng m\u1EA1ng l\u01B0\u1EDBi n\xE0o b\u1EA1n mu\u1ED1n m\u1EDF r\u1ED9ng trong t\u01B0\u01A1ng lai?",
        "B\u1EA1n mu\u1ED1n \u0111\u1ED3ng nghi\u1EC7p nh\xECn nh\u1EADn b\u1EA1n nh\u01B0 th\u1EBF n\xE0o? C\xF3 kh\xEDa c\u1EA1nh n\xE0o b\u1EA1n mu\u1ED1n duy tr\xEC v\xE0 kh\xEDa c\u1EA1nh n\xE0o b\u1EA1n mu\u1ED1n thay \u0111\u1ED5i kh\xF4ng?",
        "H\xE3y x\xE1c \u0111\u1ECBnh c\xE1c h\xE0nh \u0111\u1ED9ng c\u1EE5 th\u1EC3 v\u1EC1 c\xE1ch b\u1EA1n c\xF3 th\u1EC3 ph\xE1t tri\u1EC3n."
      ]
    },
    retention_management: {
      title: "M\u1EABu Qu\u1EA3n L\xFD Gi\u1EEF Ch\xE2n Nh\xE2n Vi\xEAn",
      questions: [
        "Kho\u1EA3nh kh\u1EAFc th\u1ECFa m\xE3n nh\u1EA5t trong c\xF4ng vi\u1EC7c g\u1EA7n \u0111\xE2y c\u1EE7a b\u1EA1n l\xE0 g\xEC v\xE0 t\u1EA1i sao?",
        "C\xF4ng vi\u1EC7c n\xE0o b\u1EA1n th\xEDch nh\u1EA5t v\xE0 c\xF4ng vi\u1EC7c n\xE0o b\u1EA1n t\u01B0\u01A1ng \u0111\u1ED1i \xEDt \u01B0a th\xEDch h\u01A1n?",
        "B\u1EA1n ngh\u0129 kh\xEDa c\u1EA1nh t\u1ED1t nh\u1EA5t khi l\xE0m vi\u1EC7c t\u1EA1i c\xF4ng ty ch\xFAng ta l\xE0 g\xEC v\xE0 \u0111i\u1EC1u g\xEC c\u1EA7n c\u1EA3i thi\u1EC7n?",
        "N\u1EBFu b\u1EA1n c\xF3 th\u1EC3 thay \u0111\u1ED5i m\u1ED9t \u0111i\u1EC1u t\u1EA1i c\xF4ng ty, \u0111\xF3 s\u1EBD l\xE0 g\xEC?",
        "B\u1EA1n mu\u1ED1n \u0111\u01B0\u1EE3c c\xF4ng nh\u1EADn trong l\u0129nh v\u1EF1c n\xE0o v\u1EC1 m\u1EB7t chuy\xEAn m\xF4n v\xE0 lo\u1EA1i h\xECnh c\xF4ng nh\u1EADn n\xE0o th\xFAc \u0111\u1EA9y b\u1EA1n nhi\u1EC1u nh\u1EA5t?",
        "C\xF3 t\xE0i n\u0103ng ho\u1EB7c \u0111i\u1EC3m m\u1EA1nh n\xE0o m\xE0 b\u1EA1n c\u1EA3m th\u1EA5y ch\u01B0a \u0111\u01B0\u1EE3c ph\xE1t huy \u0111\u1EA7y \u0111\u1EE7 trong vai tr\xF2 ho\u1EB7c tr\xE1ch nhi\u1EC7m hi\u1EC7n t\u1EA1i c\u1EE7a m\xECnh kh\xF4ng?",
        "Nh\u1EEFng y\u1EBFu t\u1ED1 n\xE0o th\xFAc \u0111\u1EA9y \u0111\u1ED9ng l\u1EF1c c\u1EE7a b\u1EA1n t\u1EA1i c\xF4ng ty ch\xFAng ta, v\xE0 ng\u01B0\u1EE3c l\u1EA1i, nh\u1EEFng y\u1EBFu t\u1ED1 n\xE0o l\xE0m gi\u1EA3m nhi\u1EC7t huy\u1EBFt c\u1EE7a b\u1EA1n?",
        "N\u1EBFu b\u1EA1n r\u1EDDi kh\u1ECFi c\xF4ng ty ch\xFAng ta, l\xFD do ch\xEDnh s\u1EBD l\xE0 g\xEC?",
        "H\xE3y x\xE1c \u0111\u1ECBnh c\xE1c h\xE0nh \u0111\u1ED9ng c\u1EE5 th\u1EC3 \u0111\u1EC3 th\u1EF1c hi\u1EC7n tr\u01B0\u1EDBc cu\u1ED9c h\u1ECDp ti\u1EBFp theo c\u1EE7a ch\xFAng ta."
      ]
    },
    performance_energy: {
      title: "M\u1EABu N\u0103ng L\u1EF1c, Hi\u1EC7u Su\u1EA5t v\xE0 N\u0103ng L\u01B0\u1EE3ng",
      questions: [
        'B\u1EA1n c\xF3 th\u1EC3 chia s\u1EBB m\u1ED9t kho\u1EA3nh kh\u1EAFc g\u1EA7n \u0111\xE2y khi b\u1EA1n ph\xE1t huy \u0111i\u1EC3m m\u1EA1nh c\u1EE7a m\xECnh v\xE0 c\u1EA3m th\u1EA5y "M\xECnh \u0111\xE3 l\xE0m t\u1ED1t"?',
        'C\xF3 l\u0129nh v\u1EF1c n\xE0o b\u1EA1n g\u1EA7n \u0111\xE2y c\u1EA3m th\u1EA5y "M\xECnh mu\u1ED1n l\xE0m t\u1ED1t h\u01A1n," ho\u1EB7c \u0111i\u1EC1u g\xEC \u0111\xF3 b\u1EA1n ngh\u0129 "N\u1EBFu quay l\u1EA1i, m\xECnh s\u1EBD th\u1EED l\xE0m theo c\xE1ch n\xE0y"?',
        "N\u1EBFu b\u1EA1n ph\u1EA3i v\u1EAFng m\u1EB7t m\u1ED9t tu\u1EA7n, nh\u1EEFng c\xF4ng vi\u1EC7c n\xE0o c\xF3 kh\u1EA3 n\u0103ng b\u1ECB d\u1EEBng l\u1EA1i, v\xE0 b\u1EA1n c\u1EA3m th\u1EA5y y\xEAn t\xE2m khi giao cho ai?",
        "C\xF3 vai tr\xF2/d\u1EF1 \xE1n/c\xF4ng vi\u1EC7c n\xE0o b\u1EA1n mu\u1ED1n th\u1EED, d\xF9 ch\u1EC9 \u1EDF quy m\xF4 nh\u1ECF?",
        "Vui l\xF2ng chia s\u1EBB 2-3 c\xF4ng vi\u1EC7c quan tr\u1ECDng nh\u1EA5t m\xE0 b\u1EA1n \u0111\xE3 th\u1EF1c hi\u1EC7n trong hai tu\u1EA7n qua.",
        "\u0110\xF3ng g\xF3p n\xE0o c\u1EE7a b\u1EA1n m\xE0 b\u1EA1n ngh\u0129 l\xE0 c\u1EA7n thi\u1EBFt cho nh\xF3m ho\u1EB7c d\u1ECBch v\u1EE5, ngay c\u1EA3 khi n\xF3 kh\xF4ng hi\u1EC7n r\xF5 b\u1EC1 m\u1EB7t?",
        "Nh\u1EEFng v\u1EA5n \u0111\u1EC1 n\xE0o \u0111\xE3 l\xE0m ch\u1EADm ho\u1EB7c c\u1EA3n tr\u1EDF ti\u1EBFn \u0111\u1ED9 c\xF4ng vi\u1EC7c c\u1EE7a b\u1EA1n? N\u1EBFu c\xF3 th\u1EC3 gi\u1EA3i quy\u1EBFt, b\u1EA1n mu\u1ED1n gi\u1EA3i quy\u1EBFt ch\xFAng nh\u01B0 th\u1EBF n\xE0o?",
        "B\u1EA1n c\xF3 th\u1EC3 chia s\u1EBB m\u1ED9t tr\u01B0\u1EDDng h\u1EE3p g\u1EA7n \u0111\xE2y khi b\u1EA1n \u0111\xE3 t\xE1c \u0111\u1ED9ng t\xEDch c\u1EF1c \u0111\u1EBFn hi\u1EC7u su\u1EA5t c\u1EE7a \u0111\u1ED3ng nghi\u1EC7p?",
        "\u0110i\u1EC1u g\xEC \u0111\xE3 th\xFAc \u0111\u1EA9y b\u1EA1n trong giai \u0111o\u1EA1n v\u1EEBa qua?",
        'N\u1EBFu b\u1EA1n c\xF3 th\u1EC3 thay \u0111\u1ED5i "ch\u1EC9 m\u1ED9t \u0111i\u1EC1u" v\u1EC1 quy tr\xECnh ho\u1EB7c v\u0103n h\xF3a hi\u1EC7n t\u1EA1i c\u1EE7a nh\xF3m, \u0111\xF3 s\u1EBD l\xE0 g\xEC?',
        "Khi n\xE0o b\u1EA1n c\u1EA3m th\u1EA5y t\u1EADp trung nh\u1EA5t g\u1EA7n \u0111\xE2y? Ng\u01B0\u1EE3c l\u1EA1i, khi n\xE0o b\u1EA1n c\u1EA3m th\u1EA5y th\xE1ch th\u1EE9c nh\u1EA5t ho\u1EB7c ki\u1EC7t s\u1EE9c nh\u1EA5t?"
      ]
    },
    teamwork_collaboration: {
      title: "M\u1EABu L\xE0m Vi\u1EC7c Nh\xF3m v\xE0 H\u1EE3p T\xE1c",
      questions: [
        "\u0110\xF3ng g\xF3p \u0111\u1ED9c \u0111\xE1o c\u1EE7a b\u1EA1n cho s\u1EF1 th\xE0nh c\xF4ng c\u1EE7a nh\xF3m m\xE0 ng\u01B0\u1EDDi kh\xE1c c\xF3 th\u1EC3 ch\u01B0a bi\u1EBFt l\xE0 g\xEC?",
        "\u0110\u1ED3ng nghi\u1EC7p n\xE0o b\u1EA1n c\u1EA3m th\u1EA5y c\xF3 s\u1EF1 ph\u1ED1i h\u1EE3p t\u1ED1t khi l\xE0m vi\u1EC7c c\xF9ng g\u1EA7n \u0111\xE2y? \u0110i\u1EC1u g\xEC khi\u1EBFn b\u1EA1n c\u1EA3m th\u1EA5y nh\u01B0 v\u1EADy?",
        "Ph\u1EA3n h\u1ED3i n\xE0o t\u1EEB \u0111\u1ED3ng nghi\u1EC7p \u0111\xE3 h\u1EEFu \xEDch nh\u1EA5t \u0111\u1ED1i v\u1EDBi b\u1EA1n?",
        "Kh\xEDa c\u1EA1nh t\xEDch c\u1EF1c nh\u1EA5t trong v\u0103n h\xF3a nh\xF3m c\u1EE7a ch\xFAng ta m\xE0 b\u1EA1n hy v\u1ECDng ch\xFAng ta s\u1EBD ti\u1EBFp t\u1EE5c duy tr\xEC l\xE0 g\xEC?",
        "G\u1EA7n \u0111\xE2y c\xF3 l\xFAc n\xE0o b\u1EA1n c\u1EA7n s\u1EF1 gi\xFAp \u0111\u1EE1 t\u1EEB ai \u0111\xF3 trong c\xF4ng vi\u1EC7c nh\u01B0ng c\u1EA3m th\u1EA5y kh\xF3 y\xEAu c\u1EA7u kh\xF4ng? \u0110i\u1EC1u g\xEC khi\u1EBFn n\xF3 tr\u1EDF n\xEAn kh\xF3 kh\u0103n?",
        "B\u1EA1n cho r\u1EB1ng \u0111i\u1EC3m m\u1EA1nh c\u1EE7a m\xECnh khi l\xE0m vi\u1EC7c v\u1EDBi c\xE1c th\xE0nh vi\xEAn kh\xE1c trong nh\xF3m l\xE0 g\xEC? V\xE0 b\u1EA1n mu\u1ED1n ph\xE1t tri\u1EC3n th\xEAm kh\xEDa c\u1EA1nh n\xE0o?",
        "G\u1EA7n \u0111\xE2y b\u1EA1n c\xF3 tr\u1EA3i nghi\u1EC7m gi\u1EA3i quy\u1EBFt s\u1EF1 b\u1EA5t \u0111\u1ED3ng quan \u0111i\u1EC3m ho\u1EB7c xung \u0111\u1ED9t trong nh\xF3m m\u1ED9t c\xE1ch t\xEDch c\u1EF1c kh\xF4ng? B\u1EA1n \u0111\xE3 \u0111\xF3ng vai tr\xF2 g\xEC trong qu\xE1 tr\xECnh \u0111\xF3?",
        "B\u1EA1n c\xF3 \xFD t\u01B0\u1EDFng m\u1EDBi n\xE0o mu\u1ED1n th\u1EED \u0111\u1EC3 \u0111\u01B0a s\u1EF1 h\u1EE3p t\xE1c c\u1EE7a nh\xF3m l\xEAn m\u1ED9t t\u1EA7m cao m\u1EDBi kh\xF4ng?",
        "D\u1EF1a tr\xEAn nh\u1EEFng g\xEC ch\xFAng ta \u0111\xE3 th\u1EA3o lu\u1EADn h\xF4m nay, ch\xFAng ta c\xF3 th\u1EC3 t\u1EA1o ra nh\u1EEFng h\xE0nh \u0111\u1ED9ng c\u1EE5 th\u1EC3 n\xE0o c\xF9ng nhau cho s\u1EF1 ph\xE1t tri\u1EC3n trong t\u01B0\u01A1ng lai?"
      ]
    },
    performance_improvement_plan: {
      title: "K\u1EBF ho\u1EA1ch C\u1EA3i thi\u1EC7n Hi\u1EC7u su\u1EA5t",
      questions: [
        "Nh\xECn v\xE0o hi\u1EC7u su\u1EA5t c\u1EE7a b\u1EA1n trong [C\xF4ng vi\u1EC7c ch\xEDnh] su\u1ED1t [Kho\u1EA3ng th\u1EDDi gian] v\u1EEBa qua, k\u1EBFt qu\u1EA3 c\u1EE7a b\u1EA1n l\xE0 (B), so v\u1EDBi m\u1EE5c ti\xEAu l\xE0 (A). T\xF4i mu\u1ED1n nghe suy ngh\u0129 c\u1EE7a b\u1EA1n v\u1EC1 d\u1EEF li\u1EC7u n\xE0y v\xE0 hi\u1EC7u su\u1EA5t c\u1EE7a b\u1EA1n.",
        "B\u1EA1n c\u1EA3m th\u1EA5y \u0111\xE2u l\xE0 th\xE1ch th\u1EE9c ch\xEDnh ho\u1EB7c tr\u1EDF ng\u1EA1i ch\u1EE7 y\u1EBFu khi\u1EBFn b\u1EA1n g\u1EB7p kh\xF3 kh\u0103n trong vi\u1EC7c \u0111\u1EA1t \u0111\u01B0\u1EE3c m\u1EE5c ti\xEAu hi\u1EC7u su\u1EA5t?",
        "B\u1EA1n c\xF3 c\u1EA3m th\u1EA5y vai tr\xF2 hi\u1EC7n t\u1EA1i c\u1EE7a m\xECnh ph\xF9 h\u1EE3p v\u1EDBi \u0111i\u1EC3m m\u1EA1nh v\xE0 n\u0103ng l\u1EF1c c\u1EE7a b\u1EA1n kh\xF4ng? Hay c\xF3 ph\u1EA7n n\xE0o trong c\xF4ng vi\u1EC7c m\xE0 b\u1EA1n c\u1EA3m th\u1EA5y th\u1EF1c s\u1EF1 kh\xF4ng ph\xF9 h\u1EE3p v\u1EDBi m\xECnh?",
        "B\u1EA1n \u0111\xE3 t\u1EF1 m\xECnh n\u1ED7 l\u1EF1c nh\u1EEFng g\xEC cho \u0111\u1EBFn nay \u0111\u1EC3 c\u1EA3i thi\u1EC7n hi\u1EC7u su\u1EA5t c\u1EE7a m\xECnh?",
        "S\u1EF1 h\u1ED7 tr\u1EE3 n\xE0o t\u1EEB t\xF4i ho\u1EB7c c\xF4ng ty s\u1EBD h\u1EEFu \xEDch nh\u1EA5t cho b\u1EA1n \u0111\u1EC3 c\u1EA3i thi\u1EC7n hi\u1EC7u su\u1EA5t?",
        "Trong [Th\u1EDDi gian c\u1EA3i thi\u1EC7n] t\u1EDBi, l\u0129nh v\u1EF1c ch\xEDnh m\xE0 b\u1EA1n mu\u1ED1n t\u1EADp trung c\u1EA3i thi\u1EC7n l\xE0 g\xEC, v\xE0 m\u1EE9c m\u1EE5c ti\xEAu c\u1EE5 th\u1EC3 b\u1EA1n mu\u1ED1n \u0111\u1EA1t \u0111\u01B0\u1EE3c l\xE0 g\xEC?",
        "\u0110\u1EC3 \u0111\u1EA1t \u0111\u01B0\u1EE3c m\u1EE5c ti\xEAu \u0111\xF3, 3 h\xE0nh \u0111\u1ED9ng m\u1EDBi ho\u1EB7c th\xF3i quen c\u1EA7n thay \u0111\u1ED5i m\xE0 b\u1EA1n c\xF3 th\u1EC3 b\u1EAFt \u0111\u1EA7u th\u1EF1c hi\u1EC7n ngay trong tu\u1EA7n n\xE0y l\xE0 g\xEC?",
        "\u0110\u1EC3 ki\u1EC3m tra xem c\xE1c k\u1EBF ho\u1EA1ch n\xE0y c\xF3 \u0111ang \u0111\u01B0\u1EE3c th\u1EF1c hi\u1EC7n t\u1ED1t kh\xF4ng v\xE0 \u0111\u1EC3 t\xF4i \u0111\u01B0a ra ph\u1EA3n h\u1ED3i, ch\xFAng ta thi\u1EBFt l\u1EADp c\xE1c bu\u1ED5i 1-1 ki\u1EC3m tra, c\xF3 th\u1EC3 l\xE0 [m\u1ED7i tu\u1EA7n m\u1ED9t l\u1EA7n / hai tu\u1EA7n m\u1ED9t l\u1EA7n], trong [Kho\u1EA3ng th\u1EDDi gian] t\u1EDBi th\xEC th\u1EBF n\xE0o?",
        "C\xE1c ghi ch\xFA t\u1EEB cu\u1ED9c trao \u0111\u1ED5i c\u1EE7a ch\xFAng ta h\xF4m nay s\u1EBD c\xF3 tr\xEAn Orblit 1on1. B\u1EA1n c\xF2n \u0111i\u1EC1u g\xEC kh\xE1c mu\u1ED1n n\xF3i kh\xF4ng?"
      ]
    },
    performance_improvement_check: {
      title: "Ki\u1EC3m tra C\u1EA3i thi\u1EC7n Hi\u1EC7u su\u1EA5t",
      questions: [
        "M\u1ECDi vi\u1EC7c di\u1EC5n ra th\u1EBF n\xE0o v\u1EDBi [L\u0129nh v\u1EF1c c\u1EA3i thi\u1EC7n ch\xEDnh] m\xE0 ch\xFAng ta \u0111\xE3 th\u1ED1ng nh\u1EA5t t\u1EADp trung v\xE0o trong [Th\u1EDDi gian ki\u1EC3m tra] v\u1EEBa qua?",
        "Trong s\u1ED1 c\xE1c h\xE0nh \u0111\u1ED9ng ch\xFAng ta \u0111\xE3 l\xEAn k\u1EBF ho\u1EA1ch, c\xF3 nh\u1EEFng \u0111i\u1EC1u g\xEC b\u1EA1n c\u1EA3m th\u1EA5y m\xECnh \u0111\xE3 th\u1EF1c hi\u1EC7n t\u1ED1t ho\u1EB7c th\u1EA5y c\xF3 hi\u1EC7u qu\u1EA3?",
        "Ng\u01B0\u1EE3c l\u1EA1i, nh\u1EEFng th\xE1ch th\u1EE9c l\u1EDBn nh\u1EA5t ho\u1EB7c tr\u1EDF ng\u1EA1i b\u1EA5t ng\u1EDD n\xE0o b\u1EA1n g\u1EB7p ph\u1EA3i khi th\u1EF1c hi\u1EC7n k\u1EBF ho\u1EA1ch?",
        "Theo quan s\xE1t c\u1EE7a t\xF4i, t\xF4i th\u1EA5y c\xF3 s\u1EF1 c\u1EA3i thi\u1EC7n \u1EDF [\u0110i\u1EC3m c\u1EA3i thi\u1EC7n], nh\u01B0ng [\u0110i\u1EC3m c\u1EA7n c\u1EA3i thi\u1EC7n] d\u01B0\u1EDDng nh\u01B0 v\u1EABn c\xF2n l\xE0 m\u1ED9t th\xE1ch th\u1EE9c. Suy ngh\u0129 c\u1EE7a b\u1EA1n v\u1EC1 \u0111i\u1EC1u n\xE0y l\xE0 g\xEC?",
        "[H\u1ED7 tr\u1EE3 \u0111\xE3 h\u1EE9a] m\xE0 t\xF4i \u0111\u01B0a ra l\u1EA7n tr\u01B0\u1EDBc c\xF3 h\u1EEFu \xEDch kh\xF4ng? B\u1EA1n c\xF3 c\u1EA7n th\xEAm h\u1ED7 tr\u1EE3 n\xE0o ho\u1EB7c c\xF3 l\u1EBD m\u1ED9t lo\u1EA1i tr\u1EE3 gi\xFAp kh\xE1c kh\xF4ng?",
        "Khi b\u1EA1n tr\u1EA3i qua qu\xE1 tr\xECnh n\xE0y trong [Th\u1EDDi gian ki\u1EC3m tra], c\xF3 \u0111i\u1EC1u g\xEC b\u1EA1n c\u1EA3m th\u1EA5y m\xECnh h\u1ECDc \u0111\u01B0\u1EE3c ho\u1EB7c nh\u1EADn ra kh\xF4ng?",
        "Ch\xFAng ta n\xEAn t\u1EADp trung h\u01A1n m\u1ED9t ch\xFAt v\xE0o \u0111i\u1EC1u g\xEC t\u1EEB nay \u0111\u1EBFn bu\u1ED5i ki\u1EC3m tra ti\u1EBFp theo?",
        "C\xE1c ghi ch\xFA t\u1EEB cu\u1ED9c th\u1EA3o lu\u1EADn c\u1EE7a ch\xFAng ta h\xF4m nay s\u1EBD c\xF3 tr\xEAn Orblit 1on1. B\u1EA1n c\xF2n \u0111i\u1EC1u g\xEC kh\xE1c mu\u1ED1n b\u1ED5 sung kh\xF4ng?"
      ]
    },
    first_oneonone: {
      title: "M\u1EABu 1on1 \u0110\u1EA7u ti\xEAn",
      description: "L\u1EA7n \u0111\u1EA7u hai b\u1EA1n ng\u1ED3i l\u1EA1i v\u1EDBi nhau. G\u1ED3m nh\u1EEFng c\xE2u h\u1ECFi \u0111\u1EC3 hi\u1EC3u nhau h\u01A1n v\xE0 t\u1EA1o b\u1EA7u kh\xF4ng kh\xED tho\u1EA3i m\xE1i, c\u1EDFi m\u1EDF cho nh\u1EEFng bu\u1ED5i 1on1 s\u1EAFp t\u1EDBi.",
      questions: [
        "S\u1EAFp t\u1EDBi m\xECnh mu\u1ED1n c\xF3 nh\u1EEFng bu\u1ED5i tr\xF2 chuy\u1EC7n m\u1ED9t-m\u1ED9t \u0111\u1EC1u \u0111\u1EB7n nh\u01B0 th\u1EBF n\xE0y v\u1EDBi b\u1EA1n. \u0110\xE2y kh\xF4ng ph\u1EA3i l\xE0 bu\u1ED5i \u0111\xE1nh gi\xE1 hay ki\u1EC3m tra c\xF4ng vi\u1EC7c, m\xE0 l\xE0 kho\u1EA3ng th\u1EDDi gian \u0111\u1EC3 b\u1EA1n tho\u1EA3i m\xE1i chia s\u1EBB nh\u1EEFng \u0111i\u1EC1u b\u1EA1n c\u1EA3m nh\u1EADn trong qu\xE1 tr\xECnh l\xE0m vi\u1EC7c, v\xE0 \u0111\u1EC3 ch\xFAng ta c\xF9ng b\xE0n xem m\xECnh c\xF3 th\u1EC3 h\u1ED7 tr\u1EE3 b\u1EA1n nh\u01B0 th\u1EBF n\xE0o. B\u1EA1n c\u1EE9 b\u1EAFt \u0111\u1EA7u t\u1EEB b\u1EA5t c\u1EE9 ch\u1EE7 \u0111\u1EC1 n\xE0o th\u1EA5y tho\u1EA3i m\xE1i nh\xE9, d\xF9 l\xE0 v\u1EC1 c\xF4ng vi\u1EC7c, s\u1EF1 ph\u1ED1i h\u1EE3p hay nh\u1EEFng tr\u0103n tr\u1EDF v\u1EC1 \u0111\u1ECBnh h\u01B0\u1EDBng ngh\u1EC1 nghi\u1EC7p.",
        "V\xEC \u0111\xE2y l\xE0 bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn n\xEAn m\xECnh c\u1EE9 xem \u0111\xE2y l\xE0 d\u1ECBp nh\u1EB9 nh\xE0ng \u0111\u1EC3 hi\u1EC3u nhau h\u01A1n nh\xE9. Tr\u01B0\u1EDBc \u0111\xE2y b\u1EA1n \u0111\xE3 t\u1EEBng c\xF3 nh\u1EEFng bu\u1ED5i 1on1 ch\u01B0a? N\u1EBFu c\xF3 th\xEC m\xECnh r\u1EA5t mu\u1ED1n bi\u1EBFt \u0111i\u1EC1u g\xEC khi\u1EBFn b\u1EA1n th\u1EA5y h\u1EEFu \xEDch v\xE0 \u0111i\u1EC1u g\xEC c\xF2n khi\u1EBFn b\u1EA1n th\u1EA5y ti\u1EBFc.",
        "Trong nh\u1EEFng vi\u1EC7c b\u1EA1n \u0111ang ph\u1EE5 tr\xE1ch g\u1EA7n \u0111\xE2y, c\xF3 vi\u1EC7c n\xE0o \u0111ang ti\u1EBFn tri\u1EC3n t\u1ED1t, v\xE0 c\xF3 vi\u1EC7c n\xE0o \u0111ang h\u01A1i b\u1ECB v\u01B0\u1EDBng ho\u1EB7c khi\u1EBFn b\u1EA1n b\u1EADn t\xE2m kh\xF4ng?",
        "G\u1EA7n \u0111\xE2y c\xF3 vi\u1EC7c g\xEC b\u1EA1n mu\u1ED1n l\xE0m nhi\u1EC1u h\u01A1n, ho\u1EB7c \u0111i\u1EC1u g\xEC m\u1EDBi m\u1EBB b\u1EA1n mu\u1ED1n th\u1EED kh\xF4ng?",
        "B\u1EA1n c\xF3 c\xE1ch giao ti\u1EBFp n\xE0o \u01B0a th\xEDch khi l\xE0m vi\u1EC7c kh\xF4ng? Ch\u1EB3ng h\u1EA1n nh\u01B0 b\u1EA1n th\u1EA5y tho\u1EA3i m\xE1i h\u01A1n khi nh\u1EAFn tin, hay th\xEDch trao \u0111\u1ED5i tr\u1EF1c ti\u1EBFp ngay?",
        "C\xF3 \u0111i\u1EC1u b\u1EA5t ti\u1EC7n nh\u1ECF n\xE0o hay tr\u1EDF ng\u1EA1i n\xE0o m\xE0 m\xECnh c\xF3 th\u1EC3 gi\xFAp b\u1EA1n ngay b\xE2y gi\u1EDD kh\xF4ng? (v\xED d\u1EE5: giao ti\u1EBFp v\u1EDBi ph\xF2ng ban kh\xE1c, quy\u1EC1n truy c\u1EADp, thi\u1EBFt b\u1ECB, v.v.)",
        "N\u1EBFu sau n\xE0y c\xF3 \u0111i\u1EC1u g\xEC v\u1EC1 c\xE1ch l\xE0m vi\u1EC7c c\u1EE7a nh\xF3m m\xECnh m\xE0 b\u1EA1n mong mu\u1ED1n \u0111\u01B0\u1EE3c c\u1EA3i thi\u1EC7n h\u01A1n, b\u1EA1n c\u1EE9 tho\u1EA3i m\xE1i cho m\xECnh bi\u1EBFt nh\xE9. N\u1EBFu b\xE2y gi\u1EDD ch\u01B0a ngh\u0129 ra ngay th\xEC c\u0169ng kh\xF4ng sao c\u1EA3.",
        "Bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn h\xF4m nay v\u1EDBi b\u1EA1n th\u1EBF n\xE0o? N\u1EBFu c\xF2n c\xE2u h\u1ECFi n\xE0o hay \u0111i\u1EC1u g\xEC kh\xE1c mu\u1ED1n chia s\u1EBB th\xEC b\u1EA1n c\u1EE9 t\u1EF1 nhi\xEAn nh\xE9. M\xECnh ngh\u0129 s\u1EBD r\u1EA5t t\u1ED1t n\u1EBFu ch\xFAng ta duy tr\xEC nh\u1EEFng bu\u1ED5i nh\u01B0 th\u1EBF n\xE0y kho\u1EA3ng m\u1ED9t ti\u1EBFng, hai tu\u1EA7n m\u1ED9t l\u1EA7n \u2014 b\u1EA1n th\u1EA5y sao?"
      ]
    }
  }
};

// src/locales/vi-VN/payment.json
var payment_default3 = {
  cancel_subscription_modal: {
    cancel: "Quay l\u1EA1i",
    confirm: "H\u1EE7y \u0111\u0103ng k\xFD",
    inquiry: "N\u1EBFu c\xF3 th\u1EAFc m\u1EAFc, vui l\xF2ng li\xEAn h\u1EC7 jjchoi@reversemountain.co.kr.",
    message: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n h\u1EE7y \u0111\u0103ng k\xFD kh\xF4ng?",
    subscription_info: "Workspace n\xE0y c\xF3 th\u1EC3 s\u1EED d\u1EE5ng cho {{memberCount}} th\xE0nh vi\xEAn v\u1EDBi t\xEDnh n\u0103ng tr\u1EA3 ph\xED \u0111\u1EBFn {{endDate}}.",
    title: "X\xE1c nh\u1EADn h\u1EE7y \u0111\u0103ng k\xFD"
  },
  confirm_modal: {
    activate_message: "B\u1EA1n c\xF3 mu\u1ED1n k\xEDch ho\u1EA1t th\xE0nh vi\xEAn n\xE0y kh\xF4ng?",
    activate_prorated_charge: "S\u1ED1 ti\u1EC1n t\xEDnh theo t\u1EF7 l\u1EC7 s\u1EBD \u0111\u01B0\u1EE3c t\xEDnh ph\xED.",
    activate_slot_available: "S\u1EED d\u1EE5ng slot kh\u1EA3 d\u1EE5ng.",
    activate_title: "K\xEDch ho\u1EA1t th\xE0nh vi\xEAn",
    available_slots: "S\u1ED1 slot kh\u1EA3 d\u1EE5ng: {{count}}",
    cancel: "H\u1EE7y",
    confirm: "X\xE1c nh\u1EADn",
    deactivate_message: "B\u1EA1n c\xF3 mu\u1ED1n v\xF4 hi\u1EC7u h\xF3a th\xE0nh vi\xEAn n\xE0y kh\xF4ng?",
    deactivate_slot_message: "Khi v\xF4 hi\u1EC7u h\xF3a, 1 slot kh\u1EA3 d\u1EE5ng s\u1EBD \u0111\u01B0\u1EE3c th\xEAm v\xE0 c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 k\xEDch ho\u1EA1t th\xE0nh vi\xEAn kh\xE1c.",
    deactivate_title: "V\xF4 hi\u1EC7u h\xF3a th\xE0nh vi\xEAn"
  },
  notifications: {
    activate_member_error_title: "K\xEDch ho\u1EA1t th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i",
    aws_marketplace_cancel_message: "\u0110\u0103ng k\xFD AWS Marketplace c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c h\u1EE7y tr\u1EF1c ti\u1EBFp t\u1EEB AWS Console.",
    aws_marketplace_cancel_path: "AWS Console \u2192 AWS Marketplace Subscriptions \u2192 Manage Subscriptions",
    aws_marketplace_cancel_title: "H\u1EE7y \u0111\u0103ng k\xFD AWS Marketplace",
    deactivate_member_error_title: "V\xF4 hi\u1EC7u h\xF3a th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i",
    member_remove_error_message: "X\xF3a th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i.",
    member_remove_error_title: "X\xF3a th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i",
    member_removed_message: "Th\xE0nh vi\xEAn \u0111\xE3 \u0111\u01B0\u1EE3c x\xF3a th\xE0nh c\xF4ng.",
    member_removed_title: "\u0110\xE3 x\xF3a th\xE0nh vi\xEAn",
    purchase_slots_error_message: "Kh\xF4ng th\u1EC3 mua slot.",
    purchase_slots_error_title: "Mua th\u1EA5t b\u1EA1i",
    purchase_slots_success_message: "{{count}} slot \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm.",
    purchase_slots_success_title: "\u0110\xE3 mua slot",
    slot_added_after_deactivate: "1 slot \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xE0 c\xF3 th\u1EC3 \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 k\xEDch ho\u1EA1t th\xE0nh vi\xEAn kh\xE1c.",
    slot_added_message: "1 slot \u0111\xE3 \u0111\u01B0\u1EE3c th\xEAm v\xEC th\xE0nh vi\xEAn b\u1ECB x\xF3a \u0111ang \u1EDF tr\u1EA1ng th\xE1i ho\u1EA1t \u0111\u1ED9ng.",
    slot_added_title: "\u0110\xE3 th\xEAm slot",
    subscription_cancel_error_message: "Kh\xF4ng th\u1EC3 h\u1EE7y \u0111\u0103ng k\xFD.",
    subscription_cancel_error_title: "H\u1EE7y th\u1EA5t b\u1EA1i",
    subscription_cancelled_message: "C\xF3 th\u1EC3 s\u1EED d\u1EE5ng \u0111\u1EBFn {{endDate}}.",
    subscription_cancelled_title: "\u0110\xE3 h\u1EE7y \u0111\u0103ng k\xFD",
    subscription_exists_message: "Kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y \u0111\xE3 c\xF3 g\xF3i \u0111\u0103ng k\xFD \u0111ang ho\u1EA1t \u0111\u1ED9ng.",
    subscription_exists_title: "\u0110\xE3 \u0111\u0103ng k\xFD",
    subscription_reactivate_error_message: "Kh\xF4ng th\u1EC3 k\xEDch ho\u1EA1t l\u1EA1i \u0111\u0103ng k\xFD.",
    subscription_reactivate_error_title: "K\xEDch ho\u1EA1t l\u1EA1i th\u1EA5t b\u1EA1i",
    subscription_reactivated_message: "\u0110\u0103ng k\xFD c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c k\xEDch ho\u1EA1t l\u1EA1i.",
    subscription_reactivated_title: "\u0110\xE3 k\xEDch ho\u1EA1t l\u1EA1i \u0111\u0103ng k\xFD",
    toggle_member_error_message: "K\xEDch ho\u1EA1t/v\xF4 hi\u1EC7u h\xF3a th\xE0nh vi\xEAn th\u1EA5t b\u1EA1i."
  },
  payment_fail: {
    default_message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh x\u1EED l\xFD thanh to\xE1n.",
    error_code: "M\xE3 l\u1ED7i",
    go_home_button: "V\u1EC1 trang ch\u1EE7",
    order_id: "M\xE3 \u0111\u01A1n h\xE0ng",
    retry_button: "Th\u1EED l\u1EA1i",
    title: "Thanh to\xE1n th\u1EA5t b\u1EA1i"
  },
  payment_page: {
    activate_members_first: "Vui l\xF2ng k\xEDch ho\u1EA1t th\xE0nh vi\xEAn 1on1 tr\u01B0\u1EDBc",
    active_members: "Th\xE0nh vi\xEAn 1on1 ho\u1EA1t \u0111\u1ED9ng",
    currency: "KRW",
    loading: "\u0110ang t\u1EA3i...",
    loading_members: "\u0110ang t\u1EA3i th\xF4ng tin th\xE0nh vi\xEAn...",
    monthly: {
      description: "Thanh to\xE1n t\u1EF1 \u0111\u1ED9ng h\xE0ng th\xE1ng",
      label: "Thanh to\xE1n h\xE0ng th\xE1ng",
      per_month: "/th\xE1ng"
    },
    no_members_warning: "Vui l\xF2ng k\xEDch ho\u1EA1t th\xE0nh vi\xEAn 1on1 trong c\xE0i \u0111\u1EB7t kh\xF4ng gian l\xE0m vi\u1EC7c tr\u01B0\u1EDBc.",
    payment_summary: {
      monthly: "S\u1ED1 ti\u1EC1n thanh to\xE1n h\xE0ng th\xE1ng",
      per_year_suffix: "/n\u0103m",
      yearly: "S\u1ED1 ti\u1EC1n thanh to\xE1n h\xE0ng n\u0103m"
    },
    price_per_member_monthly: "Gi\xE1 h\xE0ng th\xE1ng m\u1ED7i th\xE0nh vi\xEAn",
    register_button: "\u0110\u0103ng k\xFD th\u1EBB v\xE0 thanh to\xE1n {{amount}}",
    select_billing_cycle: "Ch\u1ECDn chu k\u1EF3 thanh to\xE1n",
    service_description: "Th\u1EBB c\u1EE7a b\u1EA1n s\u1EBD t\u1EF1 \u0111\u1ED9ng \u0111\u01B0\u1EE3c t\xEDnh ph\xED sau khi \u0111\u0103ng k\xFD.",
    service_title: "\u0110\u0103ng k\xFD d\u1ECBch v\u1EE5 1on1",
    title: "\u0110\u0103ng k\xFD th\u1EBB thanh to\xE1n \u0111\u1ECBnh k\u1EF3",
    yearly: {
      description: "Thanh to\xE1n m\u1ED9t l\u1EA7n m\u1ED7i n\u0103m",
      label: "Thanh to\xE1n h\xE0ng n\u0103m",
      per_year: "/n\u0103m"
    }
  },
  payment_success: {
    auto_payment_info: "Thanh to\xE1n t\u1EF1 \u0111\u1ED9ng s\u1EBD \u0111\u01B0\u1EE3c x\u1EED l\xFD b\u1EB1ng th\u1EBB \u0111\xE3 \u0111\u0103ng k\xFD.",
    billing_cycle: "Chu k\u1EF3 thanh to\xE1n",
    check_setting_info: "B\u1EA1n c\xF3 th\u1EC3 ki\u1EC3m tra th\xF4ng tin thanh to\xE1n trong c\xE0i \u0111\u1EB7t kh\xF4ng gian l\xE0m vi\u1EC7c.",
    error_description: "\u0110\xE3 x\u1EA3y ra l\u1ED7i trong qu\xE1 tr\xECnh x\u1EED l\xFD thanh to\xE1n.\nVui l\xF2ng th\u1EED l\u1EA1i, v\xE0 n\u1EBFu v\u1EA5n \u0111\u1EC1 v\u1EABn ti\u1EBFp di\u1EC5n, h\xE3y cho ch\xFAng t\xF4i bi\u1EBFt qua Tr\u1EE3 gi\xFAp > Li\xEAn h\u1EC7 h\u1ED7 tr\u1EE3.",
    error_title: "Chi ti\u1EBFt l\u1ED7i",
    failed_title: "Thanh to\xE1n th\u1EA5t b\u1EA1i",
    go_home_button: "V\u1EC1 trang ch\u1EE7",
    home_button: "Trang ch\u1EE7",
    invalid_auth_error: "Th\xF4ng tin x\xE1c th\u1EF1c kh\xF4ng h\u1EE3p l\u1EC7.",
    monthly_auto: "H\xE0ng th\xE1ng (Thanh to\xE1n t\u1EF1 \u0111\u1ED9ng h\xE0ng th\xE1ng)",
    processing_message: "Vui l\xF2ng \u0111\u1EE3i...",
    processing_title: "\u0110ang x\u1EED l\xFD thanh to\xE1n",
    provisioning_failed_message: "\u0110\xE3 x\u1EA3y ra s\u1EF1 c\u1ED1 trong qu\xE1 tr\xECnh k\xEDch ho\u1EA1t g\xF3i \u0111\u0103ng k\xFD v\xE0 \u0111\u1ED9i ng\u0169 c\u1EE7a ch\xFAng t\xF4i \u0111ang ki\u1EC3m tra. Thanh to\xE1n c\u1EE7a b\u1EA1n \u0111\xE3 ho\xE0n t\u1EA5t n\xEAn b\u1EA1n kh\xF4ng c\u1EA7n thanh to\xE1n l\u1EA1i. N\u1EBFu c\xF3 th\u1EAFc m\u1EAFc, h\xE3y cho ch\xFAng t\xF4i bi\u1EBFt qua Tr\u1EE3 gi\xFAp > Li\xEAn h\u1EC7 h\u1ED7 tr\u1EE3.",
    provisioning_failed_title: "Vi\u1EC7c k\xEDch ho\u1EA1t g\xF3i \u0111\u0103ng k\xFD \u0111ang b\u1ECB ch\u1EADm tr\u1EC5. Vui l\xF2ng \u0111\u1EE3i trong gi\xE2y l\xE1t.",
    registration_complete_message: "Thanh to\xE1n \u0111\u1ECBnh k\u1EF3 c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c \u0111\u0103ng k\xFD th\xE0nh c\xF4ng.",
    registration_complete_title: "\u0110\xE3 \u0111\u0103ng k\xFD thanh to\xE1n \u0111\u1ECBnh k\u1EF3",
    retry_button: "Th\u1EED l\u1EA1i",
    success_title: "Thanh to\xE1n ho\xE0n t\u1EA5t!",
    view_payment_button: "Xem th\xF4ng tin thanh to\xE1n",
    yearly_auto: "H\xE0ng n\u0103m (Thanh to\xE1n t\u1EF1 \u0111\u1ED9ng h\xE0ng n\u0103m)"
  },
  purchase_slots: {
    button: "Mua Slot",
    cancel_button: "H\u1EE7y",
    confirm_button: "Mua",
    description: "Mua tr\u01B0\u1EDBc slot \u0111\u1EC3 k\xEDch ho\u1EA1t th\xE0nh vi\xEAn m\xE0 kh\xF4ng c\u1EA7n thanh to\xE1n th\xEAm.",
    estimated_amount: "S\u1ED1 ti\u1EC1n thanh to\xE1n d\u1EF1 ki\u1EBFn",
    modal_title: "Mua Slot",
    slot_count_label: "S\u1ED1 l\u01B0\u1EE3ng slot c\u1EA7n mua"
  },
  subscription: {
    alert: {
      at_risk_message: "Thanh to\xE1n kh\xF4ng th\xE0nh c\xF4ng. Vui l\xF2ng ki\u1EC3m tra h\u1EA1n m\u1EE9c ho\u1EB7c tr\u1EA1ng th\xE1i th\u1EBB \u0111\xE3 \u0111\u0103ng k\xFD, v\xE0 n\u1EBFu s\u1EF1 c\u1ED1 v\u1EABn ti\u1EBFp di\u1EC5n, h\xE3y cho ch\xFAng t\xF4i bi\u1EBFt qua Tr\u1EE3 gi\xFAp > Li\xEAn h\u1EC7 h\u1ED7 tr\u1EE3.",
      at_risk_title: "Ph\xE1t hi\u1EC7n v\u1EA5n \u0111\u1EC1 thanh to\xE1n",
      pending_cancellation_message: "C\xF3 th\u1EC3 s\u1EED d\u1EE5ng \u0111\u1EBFn {{endDate}}.",
      pending_cancellation_title: "\u0110\u0103ng k\xFD ch\u1EDD h\u1EE7y"
    },
    available_slots: "S\u1ED1 slot kh\u1EA3 d\u1EE5ng",
    billing_cycle: {
      label: "Chu k\u1EF3 thanh to\xE1n",
      monthly: "H\xE0ng th\xE1ng",
      yearly: "H\xE0ng n\u0103m"
    },
    cancel_button: "H\u1EE7y \u0111\u0103ng k\xFD",
    card_company_placeholder: "C\xF4ng ty th\u1EBB",
    description: "Qu\u1EA3n l\xFD \u0111\u0103ng k\xFD v\xE0 thanh to\xE1n kh\xF4ng gian l\xE0m vi\u1EC7c.",
    members_unit: "slot",
    next_billing_date: "Ng\xE0y thanh to\xE1n ti\u1EBFp theo",
    no_payment_method: "Kh\xF4ng c\xF3 ph\u01B0\u01A1ng th\u1EE9c thanh to\xE1n",
    paid_members: "S\u1ED1 slot \u0111ang s\u1EED d\u1EE5ng",
    pay_button: "Thanh to\xE1n",
    payment_method: "Ph\u01B0\u01A1ng th\u1EE9c thanh to\xE1n",
    reactivate_button: "K\xEDch ho\u1EA1t l\u1EA1i \u0111\u0103ng k\xFD",
    recent_payments: "L\u1ECBch s\u1EED thanh to\xE1n g\u1EA7n \u0111\xE2y",
    registered_card: "Th\u1EBB \u0111\xE3 \u0111\u0103ng k\xFD",
    slots_unit: "slot",
    status: {
      active: "\u0110ang ho\u1EA1t \u0111\u1ED9ng",
      at_risk: "V\u1EA5n \u0111\u1EC1 thanh to\xE1n",
      cancelled: "\u0110\xE3 h\u1EE7y",
      label: "Tr\u1EA1ng th\xE1i \u0111\u0103ng k\xFD",
      pending_cancellation: "Ch\u1EDD h\u1EE7y"
    },
    title: "Qu\u1EA3n l\xFD Thanh to\xE1n",
    total_subscribed_slots: "T\u1ED5ng s\u1ED1 slot \u0111\u0103ng k\xFD"
  },
  subscription_required: {
    app_confirm: "X\xE1c nh\u1EADn",
    app_description: "T\xEDnh n\u0103ng n\xE0y c\xF3 th\u1EC3 s\u1EED d\u1EE5ng khi kh\xF4ng gian l\xE0m vi\u1EC7c \u0111\xE3 \u0111\u0103ng k\xFD g\xF3i d\u1ECBch v\u1EE5.\nVui l\xF2ng li\xEAn h\u1EC7 qu\u1EA3n tr\u1ECB vi\xEAn kh\xF4ng gian l\xE0m vi\u1EC7c \u0111\u1EC3 bi\u1EBFt th\xEAm chi ti\u1EBFt.",
    app_title: "C\u1EA7n \u0111\u0103ng k\xFD g\xF3i d\u1ECBch v\u1EE5",
    button: {
      cancel: "H\u1EE7y",
      go_to_payment: "S\u1EED d\u1EE5ng g\xF3i tr\u1EA3 ph\xED"
    },
    description: {
      ai_question: "Nhanh ch\xF3ng t\u1EA1o ra c\xE1c c\xE2u h\u1ECFi phong ph\xFA, \u0111\u01B0\u1EE3c c\xE1 nh\xE2n h\xF3a ph\xF9 h\u1EE3p v\u1EDBi t\u1EEBng th\xE0nh vi\xEAn.",
      recording: "V\u1EDBi t\xEDnh n\u0103ng ghi \xE2m, ch\xFAng t\xF4i t\u1EF1 \u0111\u1ED9ng t\u1ED5 ch\u1EE9c c\xE2u tr\u1EA3 l\u1EDDi theo t\u1EEBng c\xE2u h\u1ECFi, cung c\u1EA5p t\xF3m t\u1EAFt to\xE0n b\u1ED9 cu\u1ED9c h\u1ECDp v\xE0 t\u1EA1o c\xE1c m\u1EE5c h\xE0nh \u0111\u1ED9ng sau cu\u1ED9c h\u1ECDp c\u1EE7a b\u1EA1n.",
      report: "T\u1EF1 \u0111\u1ED9ng t\u1EA1o b\xE1o c\xE1o v\u1EC1 \u0111\u1ED9ng l\u1EF1c, \u0111i\u1EC3m m\u1EA1nh, s\u1EF1 ph\xE1t tri\u1EC3n c\u1EE7a th\xE0nh vi\xEAn v\xE0 nhi\u1EC1u h\u01A1n n\u1EEFa b\u1EB1ng c\xE1ch t\u1ED5ng h\u1EE3p l\u1ECBch s\u1EED cu\u1ED9c h\u1ECDp 1on1.",
      prep: "D\u1EC5 d\xE0ng chuy\u1EC3n ti\u1EBFp th\xF4ng tin cu\u1ED9c h\u1ECDp cho l\xE3nh \u0111\u1EA1o th\xF4ng qua kh\u1EA3o s\xE1t \u0111\u01A1n gi\u1EA3n v\xE0 tr\xF2 chuy\u1EC7n AI."
    },
    feature: {
      ai_question: "T\u1EA1o c\xE2u h\u1ECFi AI",
      recording: "T\u1EA3i l\xEAn t\u1EC7p ghi \xE2m",
      report: "T\u1EA1o b\xE1o c\xE1o th\xE0nh vi\xEAn",
      prep: "B\u1EA3n chu\u1EA9n b\u1ECB"
    },
    title: "C\xF3 s\u1EB5n trong g\xF3i tr\u1EA3 ph\xED!"
  },
  trial: {
    days_remaining: "C\xF2n {{days}} ng\xE0y d\xF9ng th\u1EED.",
    end_date: "Ng\xE0y k\u1EBFt th\xFAc: {{date}}",
    expired_message: "Th\u1EDDi gian d\xF9ng th\u1EED mi\u1EC5n ph\xED \u0111\xE3 k\u1EBFt th\xFAc. C\u1EA7n c\xF3 g\xF3i \u0111\u0103ng k\xFD \u0111\u1EC3 ti\u1EBFp t\u1EE5c s\u1EED d\u1EE5ng c\xE1c t\xEDnh n\u0103ng AI.",
    expired_title: "Th\u1EDDi gian d\xF9ng th\u1EED \u0111\xE3 k\u1EBFt th\xFAc",
    last_day: "H\xF4m nay l\xE0 ng\xE0y cu\u1ED1i c\xF9ng!",
    title: "Th\u1EDDi gian d\xF9ng th\u1EED mi\u1EC5n ph\xED",
    warning: "C\xE1c t\xEDnh n\u0103ng AI s\u1EBD kh\xF4ng kh\u1EA3 d\u1EE5ng sau khi th\u1EDDi gian d\xF9ng th\u1EED k\u1EBFt th\xFAc."
  },
  credit: {
    title: "Cu\u1ED9c h\u1ECDp c\xF3 th\u1EC3 ph\xE2n t\xEDch",
    balance: "C\xF2n {{count}} l\u01B0\u1EE3t.",
    description: "M\u1ED7i l\u1EA7n ghi \xE2m cu\u1ED9c h\u1ECDp & ph\xE2n t\xEDch AI s\u1EBD tr\u1EEB 1 l\u01B0\u1EE3t. Thanh to\xE1n \u0111\u1EC3 d\xF9ng kh\xF4ng gi\u1EDBi h\u1EA1n."
  }
};

// src/locales/vi-VN/aws_marketplace.json
var aws_marketplace_default3 = {
  landing: {
    page_title: "AWS Marketplace",
    page_subtitle: "Orblit for AWS",
    loading: {
      checking: "\u0110ang ki\u1EC3m tra th\xF4ng tin AWS Marketplace...",
      loading_workspaces: "\u0110ang t\u1EA3i workspace...",
      connecting: "\u0110ang k\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD AWS Marketplace..."
    },
    auth_required: {
      title: "Y\xEAu c\u1EA7u \u0111\u0103ng nh\u1EADp",
      description: "Vui l\xF2ng \u0111\u0103ng nh\u1EADp \u0111\u1EC3 k\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD AWS Marketplace c\u1EE7a b\u1EA1n.",
      login_button: "\u0110\u0103ng nh\u1EADp",
      signup_button: "\u0110\u0103ng k\xFD"
    },
    no_workspace: {
      title: "Kh\xF4ng t\xECm th\u1EA5y workspace",
      description: "B\u1EA1n c\u1EA7n t\u1EA1o workspace tr\u01B0\u1EDBc \u0111\u1EC3 k\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD AWS Marketplace.",
      instruction: "Sau khi t\u1EA1o workspace, vui l\xF2ng quay l\u1EA1i trang n\xE0y.",
      button: "T\u1EA1o workspace"
    },
    no_owner: {
      title: "Y\xEAu c\u1EA7u quy\u1EC1n ch\u1EE7 s\u1EDF h\u1EEFu",
      description: "B\u1EA1n c\u1EA7n quy\u1EC1n ch\u1EE7 s\u1EDF h\u1EEFu \u0111\u1EC3 k\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD AWS Marketplace.",
      instruction: "N\u1EBFu b\u1EA1n ch\u01B0a c\xF3 workspace n\xE0o, vui l\xF2ng t\u1EA1o m\u1EDBi. Sau khi t\u1EA1o workspace, quay l\u1EA1i trang n\xE0y.",
      button: "T\u1EA1o workspace"
    },
    already_subscribed: {
      title: "\u0110\xE3 \u0111\u0103ng k\xFD",
      description: "\u0110\u0103ng k\xFD AWS Marketplace c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i v\u1EDBi workspace.",
      go_home: "V\u1EC1 trang ch\u1EE7"
    },
    success: {
      title: "K\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD th\xE0nh c\xF4ng",
      description: "\u0110\u0103ng k\xFD AWS Marketplace c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i th\xE0nh c\xF4ng.",
      redirect_message: "B\u1EA1n s\u1EBD \u0111\u01B0\u1EE3c chuy\u1EC3n \u0111\u1EBFn workspace c\u1EE7a m\xECnh ngay l\u1EADp t\u1EE9c."
    },
    error: {
      title: "\u0110\xE3 x\u1EA3y ra l\u1ED7i",
      no_token: "Kh\xF4ng t\xECm th\u1EA5y token AWS Marketplace. Vui l\xF2ng truy c\u1EADp qua li\xEAn k\u1EBFt ch\xEDnh x\xE1c.",
      button: "V\u1EC1 trang ch\u1EE7"
    },
    notifications: {
      success_title: "K\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD th\xE0nh c\xF4ng",
      success_message: "\u0110\u0103ng k\xFD AWS Marketplace c\u1EE7a b\u1EA1n \u0111\xE3 \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i th\xE0nh c\xF4ng.",
      error_title: "K\u1EBFt n\u1ED1i th\u1EA5t b\u1EA1i",
      error_message: "Kh\xF4ng th\u1EC3 k\u1EBFt n\u1ED1i \u0111\u0103ng k\xFD AWS Marketplace."
    }
  }
};

// src/locales/vi-VN/dashboard.json
var dashboard_default3 = {
  title: "B\u1EA3ng \u0111i\u1EC1u khi\u1EC3n",
  range_selector: {
    custom_label: "T\xF9y ch\u1EC9nh",
    modal_title: "Ch\u1ECDn kho\u1EA3ng th\u1EDDi gian t\xF9y ch\u1EC9nh",
    date_range_label: "Ng\xE0y b\u1EAFt \u0111\u1EA7u ~ Ng\xE0y k\u1EBFt th\xFAc",
    date_range_placeholder: "Ch\u1ECDn kho\u1EA3ng th\u1EDDi gian",
    cancel: "H\u1EE7y",
    apply: "\xC1p d\u1EE5ng"
  },
  periods: {
    "1month": "Th\xE1ng n\xE0y",
    "3months": "3 th\xE1ng g\u1EA7n nh\u1EA5t",
    "6months": "6 th\xE1ng g\u1EA7n nh\u1EA5t",
    "1year": "1 n\u0103m g\u1EA7n nh\u1EA5t"
  },
  days_until: {
    passed: "\u0110\xE3 qua",
    today: "H\xF4m nay",
    tomorrow: "Ng\xE0y mai",
    days_later: "Sau {{days}} ng\xE0y"
  },
  alerts: {
    title: "Ki\u1EC3m tra tr\u1EA1ng th\xE1i",
    no_meeting: {
      title: "Kh\xF4ng ho\u1EA1t \u0111\u1ED9ng l\xE2u (2+ th\xE1ng)",
      item_with_days: "{{leaderName}}(Qu\u1EA3n l\xFD) - {{memberName}}, {{days}} ng\xE0y",
      item_no_history: "{{leaderName}}(Qu\u1EA3n l\xFD) - {{memberName}}, Kh\xF4ng c\xF3 l\u1ECBch s\u1EED cu\u1ED9c h\u1ECDp"
    },
    low_vital: {
      title: "Th\xE0nh vi\xEAn c\xF3 ch\u1EC9 s\u1ED1 th\u1EA5p",
      item_with_leader: "{{memberName}} (Qu\u1EA3n l\xFD: {{leaderName}})",
      item_no_leader: "{{memberName}} (Hi\u1EC7n kh\xF4ng c\xF3 qu\u1EA3n l\xFD)",
      tooltip: "Th\xE0nh vi\xEAn c\xF3 \u0111i\u1EC3m trung b\xECnh ch\u1EC9 s\u1ED1 (m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt c\xF4ng vi\u1EC7c, s\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n) d\u01B0\u1EDBi 3 trong kho\u1EA3ng th\u1EDDi gian \u0111\xE3 ch\u1ECDn."
    },
    vital_drop: {
      title: "Th\xE0nh vi\xEAn gi\u1EA3m ch\u1EC9 s\u1ED1 \u0111\u1ED9t ng\u1ED9t",
      item_with_leader: "{{memberName}} (Qu\u1EA3n l\xFD: {{leaderName}})",
      item_no_leader: "{{memberName}} (Hi\u1EC7n kh\xF4ng c\xF3 qu\u1EA3n l\xFD)",
      tooltip: "Th\xE0nh vi\xEAn c\xF3 \u0111i\u1EC3m ch\u1EC9 s\u1ED1 m\u1EDBi nh\u1EA5t (m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt c\xF4ng vi\u1EC7c, s\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n) gi\u1EA3m 2 \u0111i\u1EC3m tr\u1EDF l\xEAn so v\u1EDBi l\u1EA7n tr\u01B0\u1EDBc."
    },
    count: "{{count}}",
    view_all: "Xem t\u1EA5t c\u1EA3",
    full_list: "Danh s\xE1ch \u0111\u1EA7y \u0111\u1EE7 ({{count}})"
  },
  meeting_table: {
    title: "Danh s\xE1ch t\u1EA5t c\u1EA3 cu\u1ED9c h\u1ECDp 1:1",
    tabs: {
      as_leader: "V\u1EDBi t\u01B0 c\xE1ch qu\u1EA3n l\xFD",
      as_member: "V\u1EDBi t\u01B0 c\xE1ch th\xE0nh vi\xEAn"
    },
    filter: {
      placeholder: "Ch\u1ECDn \u0111\u1ED1i t\xE1c cu\u1ED9c h\u1ECDp",
      all: "T\u1EA5t c\u1EA3 \u0111\u1ED1i t\xE1c",
      direct_only: "Ch\u1EC9 b\xE1o c\xE1o tr\u1EF1c ti\u1EBFp"
    },
    columns: {
      counterpart: "\u0110\u1ED1i t\xE1c",
      relationship: "M\u1ED1i quan h\u1EC7",
      date: "Ng\xE0y",
      duration: "Th\u1EDDi l\u01B0\u1EE3ng",
      action_items: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng",
      engagement: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt",
      growth: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n",
      summary: "T\xF3m t\u1EAFt cu\u1ED9c h\u1ECDp"
    },
    relationship: {
      direct: "Tr\u1EF1c ti\u1EBFp",
      other: "Kh\xE1c"
    },
    duration_minutes: "{{minutes}} ph\xFAt",
    direct_filter_hint: "Ch\u1EC9 c\xE1c m\u1ED1i quan h\u1EC7 tr\u1EF1c ti\u1EBFp \u0111\u01B0\u1EE3c hi\u1EC3n th\u1ECB theo m\u1EB7c \u0111\u1ECBnh. Thay \u0111\u1ED5i b\u1ED9 l\u1ECDc \u0111\u1EC3 xem t\u1EA5t c\u1EA3 d\u1EEF li\u1EC7u."
  },
  hero_metrics: {
    execution_rate: {
      title: "T\u1EF7 l\u1EC7 th\u1EF1c hi\u1EC7n 1:1",
      value: "{{value}}%",
      subtitle: "{{executed}}/{{total}} th\xE0nh vi\xEAn"
    },
    average_cycle: {
      title: "Chu k\u1EF3 h\u1ECDp trung b\xECnh",
      value_weeks: "{{weeks}} tu\u1EA7n",
      target_weeks: "M\u1EE5c ti\xEAu {{weeks}} tu\u1EA7n",
      no_target: "Ch\u01B0a \u0111\u1EB7t chu k\u1EF3 m\u1EE5c ti\xEAu",
      tooltip: "Gi\xE1 tr\u1ECB trung b\xECnh c\u1EE7a chu k\u1EF3 h\u1ECDp m\u1EE5c ti\xEAu \u0111\u01B0\u1EE3c \u0111\u1EB7t b\u1EDFi c\xE1c m\u1ED1i quan h\u1EC7 tr\u1EF1c ti\u1EBFp."
    },
    completed_meetings: {
      title: "Cu\u1ED9c h\u1ECDp \u0111\xE3 ho\xE0n th\xE0nh",
      value_count: "{{count}}",
      average_duration: "Trung b\xECnh {{minutes}} ph\xFAt"
    },
    vital: {
      title: "Ch\u1EC9 s\u1ED1",
      value_score: "{{score}}/5.0",
      engagement_score: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt {{score}}/5.0",
      growth_score: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n {{score}}/5.0"
    }
  },
  leader_insights: {
    title: "Ch\u0103m s\xF3c nh\xF3m",
    direct_members_count: "{{count}} b\xE1o c\xE1o tr\u1EF1c ti\u1EBFp",
    management_status: {
      title: "T\xECnh tr\u1EA1ng qu\u1EA3n l\xFD",
      target_cycle: {
        label: "\u0110\xE3 \u0111\u1EB7t chu k\u1EF3 m\u1EE5c ti\xEAu",
        count: "{{set}}/{{total}} th\xE0nh vi\xEAn",
        not_set: "Ch\u01B0a \u0111\u1EB7t: {{names}}"
      },
      cycle_compliance: {
        label: "Tu\xE2n th\u1EE7 chu k\u1EF3",
        count: "{{compliant}}/{{total}} th\xE0nh vi\xEAn",
        delayed: "Tr\u1EC5: {{items}}"
      },
      delayed_member: "{{name}}(+{{days}} ng\xE0y)",
      scheduled_meetings: {
        label: "\u0110\xE3 l\xEAn l\u1ECBch h\u1ECDp",
        count: "{{count}} th\xE0nh vi\xEAn"
      },
      vital_stable: {
        label: "Ch\u1EC9 s\u1ED1 \u1ED5n \u0111\u1ECBnh (4+ \u0111i\u1EC3m)",
        count: "{{stable}}/{{total}} th\xE0nh vi\xEAn",
        improvable: "C\xF3 th\u1EC3 c\u1EA3i thi\u1EC7n: {{items}}",
        need_attention: "C\u1EA7n ch\xFA \xFD: {{items}}"
      },
      member_score: "{{name}}(tb. {{score}} \u0111i\u1EC3m)"
    },
    care_quality: {
      title: "Ch\u1EA5t l\u01B0\u1EE3ng ch\u0103m s\xF3c",
      avg_meeting_duration: "Th\u1EDDi l\u01B0\u1EE3ng h\u1ECDp trung b\xECnh",
      duration_value: "{{minutes}} ph\xFAt",
      leader_action_items: "Ho\xE0n th\xE0nh h\u1EA1ng m\u1EE5c c\u1EE7a qu\u1EA3n l\xFD",
      member_action_items: "Ho\xE0n th\xE0nh h\u1EA1ng m\u1EE5c c\u1EE7a nh\xF3m",
      action_items_count: "{{completed}}/{{total}}",
      member_vital_avg: "Ch\u1EC9 s\u1ED1 trung b\xECnh c\u1EE7a nh\xF3m",
      vital_value: "{{score}}/5.0"
    }
  },
  stats_table: {
    title: "T\xECnh tr\u1EA1ng cu\u1ED9c h\u1ECDp 1:1",
    hint: "Th\u1ED1ng k\xEA d\u1EEF li\u1EC7u cu\u1ED9c h\u1ECDp 1:1 cho c\xE1c m\u1ED1i quan h\u1EC7 tr\u1EF1c ti\u1EBFp.",
    tabs: {
      by_leader: "Theo qu\u1EA3n l\xFD",
      by_member: "Theo th\xE0nh vi\xEAn"
    },
    columns: {
      leader: "Qu\u1EA3n l\xFD",
      member: "Th\xE0nh vi\xEAn",
      direct_leader: "Qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp",
      direct_members: "Th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp",
      completed_count: "\u0110\xE3 ho\xE0n th\xE0nh",
      last_meeting: "Cu\u1ED9c h\u1ECDp cu\u1ED1i",
      avg_duration: "Th\u1EDDi l\u01B0\u1EE3ng TB",
      avg_cycle: "Chu k\u1EF3 TB",
      target_cycle: "Chu k\u1EF3 m\u1EE5c ti\xEAu",
      action_items: "H\u1EA1ng m\u1EE5c",
      engagement: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt",
      growth: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n"
    },
    values: {
      count: "{{count}}",
      minutes: "{{minutes}} ph\xFAt",
      weeks: "{{weeks}} tu\u1EA7n",
      not_set: "Ch\u01B0a \u0111\u1EB7t",
      action_items_rate: "{{rate}}% ({{completed}} / {{total}})",
      overdue_notification: "\u0110\xE3 qu\xE1 {{overdueDays}} ng\xE0y so v\u1EDBi chu k\u1EF3 m\u1EE5c ti\xEAu.",
      cycle_notification: "Chu k\u1EF3 1:1 trung b\xECnh v\u01B0\u1EE3t qu\xE1 chu k\u1EF3 m\u1EE5c ti\xEAu."
    }
  },
  meeting_count_chart: {
    title: "S\u1ED1 cu\u1ED9c h\u1ECDp 1:1 theo th\xE1ng",
    legend: {
      total: "T\u1EA5t c\u1EA3",
      with_direct_leader: "Quan h\u1EC7 tr\u1EF1c ti\u1EBFp"
    },
    placeholder: "Kh\xF4ng c\xF3 cu\u1ED9c h\u1ECDp 1:1 n\xE0o ho\xE0n th\xE0nh trong kho\u1EA3ng th\u1EDDi gian n\xE0y."
  },
  member_header: {
    direct_leader: "Qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp",
    direct_members: "Th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp"
  },
  member_insights: {
    title: "Ph\xE1t tri\u1EC3n c\xE1 nh\xE2n",
    management_status: {
      title: "T\xECnh tr\u1EA1ng",
      target_cycle: "Chu k\u1EF3 m\u1EE5c ti\xEAu",
      target_cycle_weeks: "{{weeks}} tu\u1EA7n",
      not_set: "Ch\u01B0a \u0111\u1EB7t",
      last_meeting: "Cu\u1ED9c h\u1ECDp cu\u1ED1i",
      cycle_overdue: "Qu\xE1 h\u1EA1n",
      scheduled_meeting: "Cu\u1ED9c h\u1ECDp \u0111\xE3 l\xEAn l\u1ECBch"
    },
    recent_vital: {
      title: "Ch\u1EC9 s\u1ED1 g\u1EA7n \u0111\xE2y",
      tooltip_line1: "\u0110i\u1EC3m m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt v\xE0 s\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n m\u1EDBi nh\u1EA5t.",
      tooltip_line2: "T\u1ED1i \u0111a 5 \u0111i\u1EC3m, do qu\u1EA3n l\xFD nh\u1EADp sau cu\u1ED9c h\u1ECDp 1:1.",
      engagement: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt",
      growth: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n",
      no_data: "Kh\xF4ng c\xF3 d\u1EEF li\u1EC7u",
      no_comparison: "(Kh\xF4ng c\xF3 d\u1EEF li\u1EC7u so s\xE1nh)",
      previous: "(Tr\u01B0\u1EDBc: {{score}})"
    },
    metrics: {
      avg_meeting_duration: "Th\u1EDDi l\u01B0\u1EE3ng h\u1ECDp TB",
      duration_minutes: "{{minutes}} ph\xFAt",
      action_items_rate: "Ho\xE0n th\xE0nh h\u1EA1ng m\u1EE5c"
    }
  },
  member_overview: {
    title: "T\u1ED5ng quan 1:1",
    tooltip_all: "T\u1ED5ng quan c\xE1c cu\u1ED9c h\u1ECDp 1:1 c\u1EE7a {{name}} v\u1EDBi qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp.",
    tooltip_leader: "T\u1ED5ng quan c\xE1c cu\u1ED9c h\u1ECDp 1:1 c\u1EE7a {{name}} v\u1EDBi qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp.",
    tooltip_members: "T\u1ED5ng quan c\xE1c cu\u1ED9c h\u1ECDp 1:1 c\u1EE7a {{name}} v\u1EDBi th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp.",
    completed_meetings: "Cu\u1ED9c h\u1ECDp \u0111\xE3 ho\xE0n th\xE0nh",
    meetings_with_members: "Cu\u1ED9c h\u1ECDp v\u1EDBi th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp",
    meetings_with_leader: "Cu\u1ED9c h\u1ECDp v\u1EDBi qu\u1EA3n l\xFD",
    avg_duration: "Th\u1EDDi l\u01B0\u1EE3ng h\u1ECDp TB",
    count: "{{count}}",
    duration_minutes: "{{minutes}} ph\xFAt"
  },
  vital_chart: {
    members_vital_title: "Xu h\u01B0\u1EDBng ch\u1EC9 s\u1ED1 c\u1EE7a th\xE0nh vi\xEAn",
    members_vital_description: "\u0110i\u1EC3m trung b\xECnh h\xE0ng th\xE1ng v\u1EC1 m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt v\xE0 s\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n m\xE0 {{name}} \u0111\xE3 nh\u1EADp sau c\xE1c cu\u1ED9c h\u1ECDp 1:1 v\u1EDBi th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp.",
    user_vital_title: "Xu h\u01B0\u1EDBng ch\u1EC9 s\u1ED1 c\xE1 nh\xE2n",
    user_vital_description: "\u0110i\u1EC3m trung b\xECnh h\xE0ng th\xE1ng v\u1EC1 m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt v\xE0 s\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n c\u1EE7a {{name}}, do qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp nh\u1EADp sau c\xE1c cu\u1ED9c h\u1ECDp 1:1.",
    vital_title: "Xu h\u01B0\u1EDBng ch\u1EC9 s\u1ED1",
    legend: {
      engagement: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt",
      growth: "S\u1EF1 h\xE0i l\xF2ng v\u1EC1 ph\xE1t tri\u1EC3n"
    }
  },
  error: {
    member_not_found: "Kh\xF4ng t\xECm th\u1EA5y th\xE0nh vi\xEAn.",
    member_not_found_message: "Th\xE0nh vi\xEAn n\xE0y kh\xF4ng thu\u1ED9c v\u1EC1 kh\xF4ng gian l\xE0m vi\u1EC7c n\xE0y."
  },
  excel_download: {
    button: "T\u1EA3i Excel",
    modal_title: "T\u1EA3i Excel",
    period_label: "Kho\u1EA3ng th\u1EDDi gian",
    team_label: "T\u1ED5 ch\u1EE9c / Nh\xF3m",
    data_select_label: "Ch\u1ECDn d\u1EEF li\u1EC7u c\u1EA7n xu\u1EA5t",
    sheet_manager: "T\xECnh tr\u1EA1ng 1on1 theo qu\u1EA3n l\xFD",
    sheet_member: "T\xECnh tr\u1EA1ng 1on1 theo th\xE0nh vi\xEAn",
    sheet_meeting_log: "T\u1EA5t c\u1EA3 nh\u1EADt k\xFD cu\u1ED9c h\u1ECDp 1on1",
    include_summary: "Bao g\u1ED3m t\xF3m t\u1EAFt cu\u1ED9c h\u1ECDp",
    include_summary_desc: "T\xF3m t\u1EAFt cu\u1ED9c h\u1ECDp AI s\u1EBD \u0111\u01B0\u1EE3c bao g\u1ED3m. X\u1EED l\xFD c\u1EA9n th\u1EADn v\xEC c\xF3 th\u1EC3 ch\u1EE9a th\xF4ng tin nh\u1EA1y c\u1EA3m.",
    download: "T\u1EA3i xu\u1ED1ng",
    error: "Kh\xF4ng th\u1EC3 t\u1EA1o t\u1EC7p Excel. Vui l\xF2ng th\u1EED l\u1EA1i."
  },
  hr: {
    title: "T\xECnh h\xECnh 1on1",
    excel_button: "Excel",
    full_excel_button: "Excel to\xE0n b\u1ED9",
    subhead: "{{leaders}} qu\u1EA3n l\xFD \xB7 {{period}} \xB7 {{scope}}",
    scope: {
      direct: "Quan h\u1EC7 tr\u1EF1c ti\u1EBFp",
      all: "T\u1EA5t c\u1EA3 bu\u1ED5i h\u1ECDp",
      tooltip: "Quan h\u1EC7 tr\u1EF1c ti\u1EBFp: ch\u1EC9 t\xEDnh c\xE1c bu\u1ED5i 1on1 gi\u1EEFa qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn \u0111\u01B0\u1EE3c li\xEAn k\u1EBFt trong s\u01A1 \u0111\u1ED3 t\u1ED5 ch\u1EE9c.\nT\u1EA5t c\u1EA3 bu\u1ED5i h\u1ECDp: t\xEDnh m\u1ECDi bu\u1ED5i 1on1 b\u1EA5t k\u1EC3 s\u01A1 \u0111\u1ED3 t\u1ED5 ch\u1EE9c."
    },
    units: {
      count: "{{value}}",
      people: "{{value}}",
      minutes: "{{minutes}} ph\xFAt",
      hours_minutes: "{{hours}} gi\u1EDD {{minutes}} ph\xFAt",
      weeks: "{{weeks}} tu\u1EA7n",
      percent: "{{value}}%",
      days_ago: "{{days}} ng\xE0y tr\u01B0\u1EDBc",
      today: "H\xF4m nay",
      no_meeting: "Ch\u01B0a c\xF3 bu\u1ED5i h\u1ECDp"
    },
    activation: {
      meeting_count: "T\u1ED5ng s\u1ED1 bu\u1ED5i",
      total_duration: "T\u1ED5ng th\u1EDDi l\u01B0\u1EE3ng",
      execution_rate: "T\u1EF7 l\u1EC7 tham gia",
      execution_hint: "{{executed}}/{{total}} ng\u01B0\u1EDDi \u0111\xE3 h\u1ECDp \xEDt nh\u1EA5t 1 l\u1EA7n"
    },
    quality: {
      pre_question_rate: "T\u1EF7 l\u1EC7 \u0111\u0103ng c\xE2u h\u1ECFi tr\u01B0\u1EDBc c\u1EE7a qu\u1EA3n l\xFD",
      prep_rate: "T\u1EF7 l\u1EC7 chu\u1EA9n b\u1ECB c\u1EE7a th\xE0nh vi\xEAn",
      action_item_rate: "T\u1EF7 l\u1EC7 ho\xE0n th\xE0nh h\u1EA1ng m\u1EE5c",
      avg_duration: "TB m\u1ED7i bu\u1ED5i",
      duration_hint: "d\u1EF1a tr\xEAn {{value}} bu\u1ED5i c\xF3 ghi th\u1EDDi l\u01B0\u1EE3ng"
    },
    leader_list: {
      description: "Ch\u1EC9 hi\u1EC3n th\u1ECB \u0111\u1ED9 l\u1EDBn c\u1EE7a gi\xE1 tr\u1ECB v\xE0 v\u1ECB tr\xED so v\u1EDBi trung v\u1ECB ho\u1EB7c trung b\xECnh c\u1EE7a t\u1ED5 ch\u1EE9c. Kh\xF4ng \u0111\xE1nh gi\xE1 t\u1ED1t hay k\xE9m, kh\xF4ng x\u1EBFp h\u1EA1ng. Nh\u1EA5n v\xE0o m\u1ED9t h\xE0ng \u0111\u1EC3 xem theo t\u1EEBng th\xE0nh vi\xEAn.",
      sort: {
        coverage: "\xCDt th\xE0nh vi\xEAn \u0111\u01B0\u1EE3c g\u1EB7p nh\u1EA5t",
        last: "L\xE2u ch\u01B0a h\u1ECDp nh\u1EA5t",
        cycle: "Chu k\u1EF3 d\xE0i nh\u1EA5t",
        perMember: "TB m\u1ED7i th\xE0nh vi\xEAn th\u1EA5p nh\u1EA5t",
        name: "Theo t\xEAn"
      },
      zero_filter: "Ch\u1EC9 0 bu\u1ED5i h\u1ECDp ({{count}} ng\u01B0\u1EDDi)",
      trend_column: "Xu h\u01B0\u1EDBng \xB7 {{count}} th\xE1ng",
      trend_sub: "S\u1ED1 bu\u1ED5i h\u1ECDp/th\xE1ng tr\xEAn m\u1ED7i th\xE0nh vi\xEAn",
      coverage_sub: "T\u1EF7 l\u1EC7 trong s\u1ED1 ph\u1EE5 tr\xE1ch",
      median: "Trung v\u1ECB {{value}}",
      average: "TB {{value}}",
      basis: {
        median: "Trung v\u1ECB",
        average: "Trung b\xECnh"
      },
      member_count: "Ph\u1EE5 tr\xE1ch {{count}} ng\u01B0\u1EDDi",
      member_count_all: "\u0110\u1ED1i t\xE1c {{count}} ng\u01B0\u1EDDi",
      empty: {
        not_computable: "Kh\xF4ng t\xEDnh \u0111\u01B0\u1EE3c",
        no_meetings: "0 bu\u1ED5i h\u1ECDp",
        no_value: "Kh\xF4ng c\xF3 d\u1EEF li\u1EC7u"
      },
      legend: {
        bar: "Gi\xE1 tr\u1ECB",
        median: "Trung v\u1ECB to\xE0n c\xF4ng ty (ch\u1EC9 t\xEDnh qu\u1EA3n l\xFD c\xF3 d\u1EEF li\u1EC7u)",
        average: "Trung b\xECnh to\xE0n c\xF4ng ty (ch\u1EC9 t\xEDnh qu\u1EA3n l\xFD c\xF3 d\u1EEF li\u1EC7u)",
        pips: "\u0110\xE3 g\u1EB7p / ph\u1EE5 tr\xE1ch",
        showing: "\u0110ang hi\u1EC3n th\u1ECB {{count}} qu\u1EA3n l\xFD \xB7 Qu\u1EA3n l\xFD 0 bu\u1ED5i h\u1ECDp v\u1EABn \u1EDF trong danh s\xE1ch"
      },
      zero_row: "Kh\xF4ng c\xF3 bu\u1ED5i h\u1ECDp trong k\u1EF3 \xB7 bu\u1ED5i g\u1EA7n nh\u1EA5t {{last}}",
      zero_row_never: "Kh\xF4ng c\xF3 bu\u1ED5i h\u1ECDp trong k\u1EF3 \xB7 ch\u01B0a t\u1EEBng c\xF3 bu\u1ED5i h\u1ECDp",
      team_empty: "Kh\xF4ng c\xF3 qu\u1EA3n l\xFD n\xE0o trong nh\xF3m \u0111\xE3 ch\u1ECDn"
    },
    leader_stats: {
      title: "T\xECnh h\xECnh th\u1EF1c hi\u1EC7n theo qu\u1EA3n l\xFD",
      column: {
        leader: "Qu\u1EA3n l\xFD",
        meeting_count: "S\u1ED1 bu\u1ED5i",
        total_count: "T\u1ED5ng s\u1ED1 bu\u1ED5i",
        total_duration: "T\u1ED5ng th\u1EDDi l\u01B0\u1EE3ng",
        avg_duration: "TB m\u1ED7i bu\u1ED5i",
        coverage: "\u0110\xE3 g\u1EB7p",
        per_member: "TB m\u1ED7i th\xE0nh vi\xEAn",
        avg_cycle: "Chu k\u1EF3 TB",
        last_meeting: "Bu\u1ED5i g\u1EA7n nh\u1EA5t",
        action_item: "H\u1EA1ng m\u1EE5c h\xE0nh \u0111\u1ED9ng"
      }
    },
    leader_detail: {
      quality_title: "{{name}} \xB7 ch\u1EA5t l\u01B0\u1EE3ng 1on1",
      team_title: "Theo th\xE0nh vi\xEAn \xB7 {{count}} ng\u01B0\u1EDDi",
      excel_button: "Excel chi ti\u1EBFt qu\u1EA3n l\xFD",
      pre_question_rate: "T\u1EF7 l\u1EC7 \u0111\u0103ng c\xE2u h\u1ECFi tr\u01B0\u1EDBc",
      column_monthly: "Theo th\xE1ng",
      column: {
        member: "Th\xE0nh vi\xEAn",
        target_cycle: "Chu k\u1EF3 m\u1EE5c ti\xEAu",
        last_meeting: "Bu\u1ED5i g\u1EA7n nh\u1EA5t",
        prep_rate: "T\u1EF7 l\u1EC7 chu\u1EA9n b\u1ECB"
      },
      heat_caption: "\xD4 theo th\xE1ng b\u1EAFt \u0111\u1EA7u t\u1EEB th\xE1ng c\u0169 nh\u1EA5t \xB7 {{first}} \u2192 {{last}} \xB7 \u0110\u1EADm h\u01A1n ngh\u0129a l\xE0 nhi\u1EC1u bu\u1ED5i h\u1ECDp h\u01A1n trong th\xE1ng \u0111\xF3",
      heat_tooltip: "{{count}} bu\u1ED5i h\u1ECDp trong {{month}}",
      meeting_list_button: "L\u1ECBch s\u1EED bu\u1ED5i h\u1ECDp ({{count}})"
    },
    meeting_list: {
      title: "L\u1ECBch s\u1EED bu\u1ED5i h\u1ECDp",
      count: "{{count}} bu\u1ED5i h\u1ECDp ho\xE0n th\xE0nh trong k\u1EF3",
      count_person: "{{count}} bu\u1ED5i h\u1ECDp c\xF3 {{name}} tham gia",
      subtitle: "{{period}} \xB7 {{scope}} \xB7 theo k\u1EF3 v\xE0 ti\xEAu ch\xED \u0111\xE3 ch\u1ECDn \u1EDF tr\xEAn",
      search_placeholder: "T\xECm t\xEAn qu\u1EA3n l\xFD ho\u1EB7c th\xE0nh vi\xEAn",
      search_empty: "Kh\xF4ng t\xECm th\u1EA5y ng\u01B0\u1EDDi ph\xF9 h\u1EE3p",
      role: {
        leader: "Qu\u1EA3n l\xFD",
        member: "Th\xE0nh vi\xEAn",
        both: "Qu\u1EA3n l\xFD \xB7 Th\xE0nh vi\xEAn"
      },
      person_summary: "L\xE0 qu\u1EA3n l\xFD {{leader}} bu\u1ED5i \xB7 l\xE0 th\xE0nh vi\xEAn {{member}} bu\u1ED5i",
      column: {
        date: "Ng\xE0y",
        leader: "Qu\u1EA3n l\xFD",
        member: "Th\xE0nh vi\xEAn",
        relation: "Quan h\u1EC7",
        recording: "Ghi \xE2m",
        duration: "Th\u1EDDi l\u01B0\u1EE3ng"
      },
      relation_other: "Kh\xE1c",
      recorded: "Ghi \xE2m",
      recording_count: "{{count}} c\xF3 ghi \xE2m",
      recording_filter: {
        all: "T\u1EA5t c\u1EA3",
        with: "C\xF3 ghi \xE2m",
        without: "Kh\xF4ng ghi \xE2m"
      },
      group: {
        week: "Th\xE1ng {{month}} tu\u1EA7n {{week}}",
        month: "Th\xE1ng {{month}}",
        meta: "{{count}} bu\u1ED5i \xB7 {{leaders}} qu\u1EA3n l\xFD \xB7 {{duration}}"
      },
      legend: {
        relation_other: "Ch\u1EC9 bu\u1ED5i h\u1ECDp ngo\xE0i quan h\u1EC7 tr\u1EF1c ti\u1EBFp \u0111\u01B0\u1EE3c ghi \u2018Kh\xE1c\u2019",
        relation_direct: "Theo quan h\u1EC7 tr\u1EF1c ti\u1EBFp n\xEAn kh\xF4ng c\xF3 c\u1ED9t quan h\u1EC7",
        recording: "Bu\u1ED5i h\u1ECDp kh\xF4ng ghi \xE2m s\u1EBD \u0111\u1EC3 tr\u1ED1ng",
        duration: "\u2018\u2014\u2019 l\xE0 bu\u1ED5i kh\xF4ng ghi th\u1EDDi l\u01B0\u1EE3ng"
      },
      shown: "{{shown}} / {{total}}",
      load_more: "Xem th\xEAm",
      empty: {
        title: "Kh\xF4ng c\xF3 bu\u1ED5i h\u1ECDp n\xE0o kh\u1EDBp \u0111i\u1EC1u ki\u1EC7n n\xE0y",
        body: "Kh\xF4ng c\xF3 bu\u1ED5i 1on1 n\xE0o ho\xE0n th\xE0nh trong {{period}}. H\xE3y m\u1EDF r\u1ED9ng k\u1EF3 ho\u1EB7c \u0111\u1ED5i ti\xEAu ch\xED \u1EDF tr\xEAn sang \u2018T\u1EA5t c\u1EA3 bu\u1ED5i h\u1ECDp\u2019.",
        body_person: "{{name}} kh\xF4ng c\xF3 bu\u1ED5i h\u1ECDp ho\xE0n th\xE0nh n\xE0o trong {{period}}. H\xE3y m\u1EDF r\u1ED9ng k\u1EF3 ho\u1EB7c b\u1ECF b\u1ED9 l\u1ECDc ng\u01B0\u1EDDi tham gia.",
        clear_filter: "B\u1ECF b\u1ED9 l\u1ECDc ng\u01B0\u1EDDi tham gia",
        clear_recording: "B\u1ECF b\u1ED9 l\u1ECDc ghi \xE2m",
        widen_period: "M\u1EDF r\u1ED9ng l\xEAn 12 th\xE1ng"
      }
    },
    empty_state: {
      badge: "Ch\u01B0a c\xF3 d\u1EEF li\u1EC7u",
      title: "Ch\u01B0a c\xF3 1on1 n\xE0o \u0111\u1EC3 t\u1ED5ng h\u1EE3p",
      description: "Trang n\xE0y s\u1EBD \u0111\u01B0\u1EE3c l\u1EA5p \u0111\u1EA7y khi quan h\u1EC7 qu\u1EA3n l\xFD - th\xE0nh vi\xEAn \u0111\u01B0\u1EE3c k\u1EBFt n\u1ED1i v\xE0 bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn ho\xE0n th\xE0nh. Hi\u1EC7n c\u1EA7n hai vi\u1EC7c.",
      step1_title: "Ch\u1EC9 \u0111\u1ECBnh qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp cho th\xE0nh vi\xEAn",
      step1_progress: "Hi\u1EC7n {{assigned}}/{{total}} th\xE0nh vi\xEAn \u0111\xE3 c\xF3 qu\u1EA3n l\xFD",
      step1_link: "\u0110\u1EBFn c\xE0i \u0111\u1EB7t",
      step2_title: "Ho\xE0n th\xE0nh bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn",
      step2_sub: "Ch\u1EC9 t\xEDnh t\u1EEB c\xE1c bu\u1ED5i h\u1ECDp \u0111\xE3 ho\xE0n th\xE0nh",
      step2_link: "T\u1EA1o 1on1",
      preview_title: "Khi c\xF3 d\u1EEF li\u1EC7u s\u1EBD hi\u1EC3n th\u1ECB nh\u01B0 sau"
    },
    excel: {
      sheet: {
        activation: "HoatDong",
        leader_stats: "TongQuanQuanLy",
        leader_detail: "ChiTietThanhVien",
        leader_pairs: "ThanhVienTheoQuanLy",
        meetings: "DanhSachHop"
      },
      file: {
        leader_stats: "TongQuanQuanLy",
        leader_detail: "ChiTietThanhVien",
        meetings: "DanhSachHop",
        full: "TinhHinh1on1"
      },
      value: {
        yes: "Y",
        no: "N"
      },
      column: {
        month: "Th\xE1ng",
        meeting_count: "S\u1ED1 bu\u1ED5i",
        total_duration: "T\u1ED5ng th\u1EDDi l\u01B0\u1EE3ng (ph\xFAt)",
        total: "T\u1ED5ng",
        scope: "Ph\u1EA1m vi",
        execution_rate: "Tham gia (\u0111\xE3 tham gia/t\u1ED5ng)",
        leader_name: "Qu\u1EA3n l\xFD",
        leader_email: "Email qu\u1EA3n l\xFD",
        org_leader: "Qu\u1EA3n l\xFD trong s\u01A1 \u0111\u1ED3",
        avg_duration: "TB m\u1ED7i bu\u1ED5i (ph\xFAt)",
        member_count: "S\u1ED1 th\xE0nh vi\xEAn",
        met_member_count: "S\u1ED1 th\xE0nh vi\xEAn \u0111\xE3 g\u1EB7p",
        coverage: "\u0110\u1ED9 bao ph\u1EE7 (%)",
        per_member: "TB s\u1ED1 bu\u1ED5i m\u1ED7i th\xE0nh vi\xEAn",
        avg_cycle: "Chu k\u1EF3 TB (tu\u1EA7n)",
        target_cycle: "Chu k\u1EF3 m\u1EE5c ti\xEAu (tu\u1EA7n)",
        last_meeting_date: "Ng\xE0y bu\u1ED5i g\u1EA7n nh\u1EA5t",
        days_since: "S\u1ED1 ng\xE0y t\u1EEB bu\u1ED5i g\u1EA7n nh\u1EA5t",
        pre_question_rate: "T\u1EF7 l\u1EC7 c\xE2u h\u1ECFi chu\u1EA9n b\u1ECB (%)",
        action_item_done: "H\u1EA1ng m\u1EE5c ho\xE0n th\xE0nh",
        action_item_total: "T\u1ED5ng h\u1EA1ng m\u1EE5c",
        action_item_rate: "T\u1EF7 l\u1EC7 ho\xE0n th\xE0nh (%)",
        member_name: "Th\xE0nh vi\xEAn",
        member_email: "Email th\xE0nh vi\xEAn",
        direct_relation: "Quan h\u1EC7 tr\u1EF1c ti\u1EBFp",
        date: "Ng\xE0y",
        recording: "C\xF3 ghi \xE2m",
        duration: "Th\u1EDDi l\u01B0\u1EE3ng (ph\xFAt)",
        prep_rate: "T\u1EF7 l\u1EC7 chu\u1EA9n b\u1ECB (%)"
      }
    }
  }
};

// src/locales/vi-VN/profile_card.json
var profile_card_default3 = {
  communicationStyle: {
    category: "Phong c\xE1ch giao ti\u1EBFp \u01B0a th\xEDch",
    question: "Khi th\u1EA3o lu\u1EADn c\xF4ng vi\u1EC7c v\u1EDBi qu\u1EA3n l\xFD, c\xE1ch ti\u1EBFp c\u1EADn n\xE0o gi\xFAp b\u1EA1n t\u1EADp trung t\u1ED1t nh\u1EA5t?",
    SUMMARY: {
      label: "T\xF3m t\u1EAFt ch\xEDnh",
      description: "T\xF3m l\u1EA1i, \u0111i\u1EC3m ch\xEDnh c\u1EE7a nhi\u1EC7m v\u1EE5 n\xE0y l\xE0 nh\u01B0 v\u1EADy, v\xE0 ch\xFAng ta s\u1EBD t\u1EADp trung v\xE0o \u0111\xE2y tr\u01B0\u1EDBc."
    },
    CONTEXT: {
      label: "Ng\u1EEF c\u1EA3nh chi ti\u1EBFt",
      description: "\u0110\u1EC3 t\xF4i gi\u1EA3i th\xEDch t\u1EEBng b\u01B0\u1EDBc v\u1EC1 b\u1ED1i c\u1EA3nh v\xE0 l\u1ECBch s\u1EED c\u1EE7a nhi\u1EC7m v\u1EE5 n\xE0y tr\u01B0\u1EDBc."
    },
    DATA: {
      label: "D\u1EF1a tr\xEAn d\u1EEF li\u1EC7u",
      description: "H\xE3y c\xF9ng xem d\u1EEF li\u1EC7u v\xE0 t\xE0i li\u1EC7u tham kh\u1EA3o \u0111\u1EC3 x\xE1c nh\u1EADn h\u01B0\u1EDBng \u0111i c\u1EE7a nhi\u1EC7m v\u1EE5."
    },
    DISCUSSION: {
      label: "D\u1EF1a tr\xEAn th\u1EA3o lu\u1EADn",
      description: "B\u1EA1n ngh\u0129 g\xEC v\u1EC1 nhi\u1EC7m v\u1EE5 n\xE0y? H\xE3y th\u1EA3o lu\u1EADn v\xE0 c\xF9ng l\u1EADp k\u1EBF ho\u1EA1ch h\xE0nh \u0111\u1ED9ng."
    }
  },
  feedbackStyle: {
    category: "Ph\u1EA3n h\u1ED3i hi\u1EC7u qu\u1EA3",
    question: "Khi nh\u1EADn ph\u1EA3n h\u1ED3i t\u1EEB qu\u1EA3n l\xFD, phong c\xE1ch n\xE0o hi\u1EC7u qu\u1EA3 nh\u1EA5t v\u1EDBi b\u1EA1n?",
    DIRECT: {
      label: "C\u1EA3i thi\u1EC7n tr\u1EF1c ti\u1EBFp",
      description: "Ph\u1EA7n A c\xF3 v\u1EBB thi\u1EBFu logic. \u0110i\u1EC1u n\xE0y c\u1EA7n \u0111\u01B0\u1EE3c s\u1EEDa \u0111\u1ED5i theo c\xE1ch n\xE0y."
    },
    POSITIVE: {
      label: "C\u1EE7ng c\u1ED1 t\xEDch c\u1EF1c",
      description: "T\u1ED5ng th\u1EC3 r\u1EA5t t\u1ED1t. S\u1EBD tuy\u1EC7t v\u1EDDi n\u1EBFu b\u1EA1n c\xF3 th\u1EC3 c\u1EA3i thi\u1EC7n th\xEAm ph\u1EA7n n\xE0y m\u1ED9t ch\xFAt."
    },
    SOLUTION: {
      label: "H\u01B0\u1EDBng gi\u1EA3i ph\xE1p",
      description: "\u0110\xE2y c\xF3 v\u1EBB l\xE0 v\u1EA5n \u0111\u1EC1. C\xF3 th\u1EC3 c\xF3 m\u1ED9t s\u1ED1 l\u1EF1a ch\u1ECDn nh\u01B0 th\u1EBF n\xE0y\u2014ch\xFAng ta t\xECm ph\u01B0\u01A1ng \xE1n kh\xE1c nh\xE9?"
    },
    SELF_REFLECTION: {
      label: "T\u1EF1 suy ng\u1EABm",
      description: "B\u1EA1n ngh\u0129 g\xEC v\u1EC1 \u0111i\u1EC1u n\xE0y? B\u1EA1n c\xF3 th\u1EC3 l\xE0m g\xEC kh\xE1c \u0111\u1EC3 c\u1EA3i thi\u1EC7n?"
    }
  },
  careerOrientation: {
    category: "\u0110\u1ECBnh h\u01B0\u1EDBng ngh\u1EC1 nghi\u1EC7p",
    question: "B\u1EA1n mu\u1ED1n ph\xE1t tri\u1EC3n nh\u01B0 th\u1EBF n\xE0o trong c\xF4ng ty trong t\u01B0\u01A1ng lai?",
    SPECIALIST: {
      label: "Chuy\xEAn gia",
      description: "T\xF4i mu\u1ED1n ph\xE1t tri\u1EC3n chuy\xEAn m\xF4n s\xE2u trong m\u1ED9t k\u1EF9 n\u0103ng ho\u1EB7c vai tr\xF2 c\u1EE5 th\u1EC3."
    },
    LEADER: {
      label: "L\xE3nh \u0111\u1EA1o",
      description: "T\xF4i mu\u1ED1n \u0111\u1EA1t \u0111\u01B0\u1EE3c k\u1EBFt qu\u1EA3 c\xF9ng v\u1EDBi c\xE1c th\xE0nh vi\xEAn trong nh\xF3m v\xE0 ph\xE1t tri\u1EC3n k\u1EF9 n\u0103ng qu\u1EA3n l\xFD \u0111\u1EC3 gi\xFAp h\u1ECD ph\xE1t tri\u1EC3n."
    },
    GENERALIST: {
      label: "\u0110i\u1EC1u ph\u1ED1i vi\xEAn",
      description: "T\xF4i mu\u1ED1n \u0111\u1EA3m nh\u1EADn vai tr\xF2 k\u1EBFt n\u1ED1i v\xE0 \u0111i\u1EC1u ph\u1ED1i nhi\u1EC1u l\u0129nh v\u1EF1c \u0111\u1EC3 gi\u1EA3i quy\u1EBFt c\xE1c v\u1EA5n \u0111\u1EC1 ph\u1EE9c t\u1EA1p."
    },
    EXPLORER: {
      label: "Ng\u01B0\u1EDDi ti\xEAn phong",
      description: "T\xF4i mu\u1ED1n gi\u1EDBi thi\u1EC7u c\xF4ng ngh\u1EC7 m\u1EDBi ho\u1EB7c x\xE2y d\u1EF1ng m\xF4 h\xECnh kinh doanh t\u1EEB \u0111\u1EA7u."
    }
  },
  motivation: {
    category: "Y\u1EBFu t\u1ED1 \u0111\u1ED9ng l\u1EF1c",
    question: "Khi n\xE0o b\u1EA1n c\u1EA3m th\u1EA5y c\xF3 \u0111\u1ED9ng l\u1EF1c nh\u1EA5t?",
    GROWTH: {
      label: "H\u1ECDc h\u1ECFi v\xE0 ph\xE1t tri\u1EC3n",
      description: "T\xF4i c\u1EA3m th\u1EA5y vui khi h\u1ECDc \u0111\u01B0\u1EE3c \u0111i\u1EC1u m\u1EDBi v\xE0 c\u1EA3m th\u1EA5y k\u1EF9 n\u0103ng c\u1EE7a m\xECnh \u0111\u01B0\u1EE3c n\xE2ng cao."
    },
    AUTONOMY: {
      label: "Quy\u1EC1n h\u1EA1n v\xE0 t\u1EF1 ch\u1EE7",
      description: "T\xF4i c\u1EA3m th\u1EA5y tr\xE1ch nhi\u1EC7m v\xE0 \u0111am m\xEA khi \u0111\u01B0\u1EE3c tin t\u01B0\u1EDFng v\xE0 giao to\xE0n quy\u1EC1n."
    },
    RECOGNITION: {
      label: "S\u1EF1 c\xF4ng nh\u1EADn v\xE0 danh ti\u1EBFng",
      description: "T\xF4i c\u1EA3m th\u1EA5y t\u1EF1 h\xE0o khi nh\u1EEFng \u0111\xF3ng g\xF3p v\xE0 n\u0103ng l\u1EF1c c\u1EE7a t\xF4i \u0111\u01B0\u1EE3c \u0111\u1ED3ng nghi\u1EC7p v\xE0 qu\u1EA3n l\xFD c\xF4ng nh\u1EADn."
    },
    IMPACT: {
      label: "\xDD ngh\u0129a v\xE0 t\u1EA7m \u1EA3nh h\u01B0\u1EDFng",
      description: "T\xF4i c\u1EA3m th\u1EA5y th\u1ECFa m\xE3n khi c\xF4ng vi\u1EC7c c\u1EE7a t\xF4i th\u1EF1c s\u1EF1 gi\xFAp \xEDch cho kh\xE1ch h\xE0ng ho\u1EB7c \u0111\xF3ng g\xF3p cho c\xF4ng ty."
    }
  },
  create_page: {
    title: "T\u1EA1o Th\u1EBB h\u1ED3 s\u01A1",
    description: "Ho\xE0n th\xE0nh th\u1EBB h\u1ED3 s\u01A1 c\u1EE7a ri\xEAng b\u1EA1n b\u1EB1ng c\xE1ch tr\u1EA3 l\u1EDDi c\xE1c c\xE2u h\u1ECFi \u0111\u01A1n gi\u1EA3n",
    progress: "C\xE2u h\u1ECFi {{currentStep}} / {{allStep}}",
    save_and_leave: "L\u01B0u v\xE0 tho\xE1t"
  },
  no_profile_card: {
    title: "B\u1EA1n ch\u01B0a thi\u1EBFt l\u1EADp th\u1EBB h\u1ED3 s\u01A1. Ch\u1EC9 m\u1EA5t ch\u01B0a \u0111\u1EBFn 1 ph\xFAt!",
    title_mobile: "B\u1EA1n ch\u01B0a thi\u1EBFt l\u1EADp th\u1EBB h\u1ED3 s\u01A1.\nCh\u1EC9 m\u1EA5t ch\u01B0a \u0111\u1EBFn 1 ph\xFAt!",
    create: "T\u1EA1o"
  },
  error: {
    update: {
      title: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u th\u1EBB h\u1ED3 s\u01A1.",
      message: "Vui l\xF2ng l\xE0m m\u1EDBi v\xE0 th\u1EED l\u1EA1i. N\u1EBFu s\u1EF1 c\u1ED1 v\u1EABn ti\u1EBFp di\u1EC5n, vui l\xF2ng li\xEAn h\u1EC7 b\u1ED9 ph\u1EADn h\u1ED7 tr\u1EE3."
    }
  },
  meeting_prep: {
    collapsed: "Chatbot tham kh\u1EA3o c\xE1ch trao \u0111\u1ED5i m\xE0 {{name}} \u01B0a th\xEDch v\xE0 \u0111\u1ECBnh h\u01B0\u1EDBng ph\xE1t tri\u1EC3n s\u1EAFp t\u1EDBi \u0111\u1EC3 c\u1EE5 th\u1EC3 h\xF3a n\u1ED9i dung s\u1EBD trao \u0111\u1ED5i trong bu\u1ED5i 1on1.",
    expand: "Xem chi ti\u1EBFt",
    collapse: "Thu g\u1ECDn",
    empty_collapsed: "Thi\u1EBFt l\u1EADp h\u1ED3 s\u01A1 \u0111\u1EC3 chatbot gi\xFAp b\u1EA1n t\xECm ra n\u1ED9i dung s\u1EBD trao \u0111\u1ED5i trong bu\u1ED5i 1on1, ph\xF9 h\u1EE3p v\u1EDBi ri\xEAng b\u1EA1n.",
    empty_action: "Thi\u1EBFt l\u1EADp"
  }
};

// src/locales/vi-VN/meeting_prep.json
var meeting_prep_default3 = {
  meeting_status: {
    question_confirm_requested: "Y\xEAu c\u1EA7u xem l\u1EA1i c\xE2u h\u1ECFi",
    waiting_counterpart_confirm: "\u0110ang ch\u1EDD xem l\u1EA1i c\xE2u h\u1ECFi",
    meeting_ready: "S\u1EB5n s\xE0ng h\u1ECDp!",
    write_prep: "Vi\u1EBFt chu\u1EA9n b\u1ECB",
    prep_completed: "\u0110\xE3 chu\u1EA9n b\u1ECB xong",
    waiting_member_prep: "\u0110ang ch\u1EDD b\u1EA3n chu\u1EA9n b\u1ECB c\u1EE7a th\xE0nh vi\xEAn",
    review_prep_and_create_questions: "Xem chu\u1EA9n b\u1ECB & t\u1EA1o c\xE2u h\u1ECFi"
  },
  meeting_status_tooltip: {
    question_confirm_requested: "Vui l\xF2ng nh\u1EA5n n\xFAt 'X\xE1c nh\u1EADn c\xE2u h\u1ECFi' \u1EDF cu\u1ED1i trang cu\u1ED9c h\u1ECDp.",
    waiting_counterpart_confirm: "Chu\u1EA9n b\u1ECB cu\u1ED9c h\u1ECDp s\u1EBD ho\xE0n t\u1EA5t khi \u0111\u1ED1i t\xE1c x\xE1c nh\u1EADn xong.",
    meeting_ready: "Ti\u1EBFn h\xE0nh cu\u1ED9c h\u1ECDp d\u1EF1a tr\xEAn n\u1ED9i dung \u0111\xE3 chu\u1EA9n b\u1ECB.",
    write_prep: "S\u1EED d\u1EE5ng chatbot \u0111\u1EC3 t\u1ED5 ch\u1EE9c c\xF4ng vi\u1EC7c, t\xECnh h\xECnh v\xE0 ch\u1EE7 \u0111\u1EC1 th\u1EA3o lu\u1EADn \u0111\u1EC3 chia s\u1EBB v\u1EDBi \u0111\u1ED1i t\xE1c.",
    prep_completed: "\u0110\u1ED1i t\xE1c s\u1EBD chu\u1EA9n b\u1ECB c\xE2u h\u1ECFi d\u1EF1a tr\xEAn n\u1ED9i dung \u0111\xE3 chia s\u1EBB.\nH\xE3y th\xEAm c\xE2u h\u1ECFi n\u1EBFu c\xF3 \u0111i\u1EC1u b\u1EA1n mu\u1ED1n th\u1EA3o lu\u1EADn th\xEAm.",
    waiting_member_prep: "Khi th\xE0nh vi\xEAn ho\xE0n th\xE0nh chu\u1EA9n b\u1ECB, b\u1EA1n c\xF3 th\u1EC3 xem c\xE2u h\u1ECFi g\u1EE3i \xFD v\xE0 h\u01B0\u1EDBng d\u1EABn h\u1ECDp.",
    review_prep_and_create_questions: "S\u1EED d\u1EE5ng h\u01B0\u1EDBng d\u1EABn h\u1ECDp, c\xE2u h\u1ECFi g\u1EE3i \xFD v\xE0 m\u1EABu \u0111\u1EC3 t\u1EA1o c\xE2u h\u1ECFi."
  },
  confirm_questions: {
    banner: "N\u1EBFu \u0111\xE3 xem xong t\u1EA5t c\u1EA3 c\xE2u h\u1ECFi, nh\u1EA5n n\xFAt 'X\xE1c nh\u1EADn c\xE2u h\u1ECFi' \u0111\u1EC3 th\xF4ng b\xE1o cho \u0111\u1ED1i t\xE1c!",
    button: "X\xE1c nh\u1EADn c\xE2u h\u1ECFi"
  },
  create_page: {
    chat_placeholder: "Nh\u1EADp c\xE2u tr\u1EA3 l\u1EDDi c\u1EE7a b\u1EA1n...",
    create_button: "T\u1EA1o chu\u1EA9n b\u1ECB h\u1ECDp",
    error: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi t\u1EA1o chu\u1EA9n b\u1ECB. Vui l\xF2ng l\xE0m m\u1EDBi v\xE0 th\u1EED l\u1EA1i.",
    success: "Chu\u1EA9n b\u1ECB \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o th\xE0nh c\xF4ng.",
    saved: "\u0110\xE3 l\u01B0u",
    saving: "\u0110ang l\u01B0u...",
    send_button: "G\u1EEDi",
    update: "C\u1EADp nh\u1EADt chu\u1EA9n b\u1ECB h\u1ECDp",
    edit: "Ch\u1EC9nh s\u1EEDa",
    header_title: "Vi\u1EBFt b\u1EA3n chu\u1EA9n b\u1ECB",
    header_description: "H\xE3y tho\u1EA3i m\xE1i k\u1EC3 v\u1EDBi chatbot nh\u1EEFng vi\u1EC7c \u0111\xE3 x\u1EA3y ra trong c\xF4ng vi\u1EC7c g\u1EA7n \u0111\xE2y, nh\u1EEFng suy ngh\u0129 hay tr\u0103n tr\u1EDF n\u1EA3y sinh, v\xE0 nh\u1EEFng \u0111i\u1EC1u b\u1EA1n mu\u1ED1n trao \u0111\u1ED5i v\u1EDBi ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o.",
    chat_helper: "H\xE3y b\u1EAFt \u0111\u1EA7u t\u1EEB c\xF4ng vi\u1EC7c ho\u1EB7c tr\u0103n tr\u1EDF n\xE0o \u0111ang hi\u1EC7n l\xEAn trong \u0111\u1EA7u b\u1EA1n. Ngay c\u1EA3 khi ch\u1EE7 \u0111\u1EC1 ch\u01B0a r\xF5 r\xE0ng, b\u1EA1n v\u1EABn c\xF3 th\u1EC3 c\xF9ng chatbot t\xECm ra n\xF3.",
    draft_empty: "Sau khi tr\xF2 chuy\u1EC7n, h\xE3y nh\u1EA5n T\u1EA1o b\u1EA3n chu\u1EA9n b\u1ECB \u2014 AI s\u1EBD t\xF3m t\u1EAFt cu\u1ED9c tr\xF2 chuy\u1EC7n th\xE0nh b\u1EA3n nh\xE1p. B\u1EA1n c\xF3 th\u1EC3 xem l\u1EA1i, ch\u1EC9nh s\u1EEDa r\u1ED3i chia s\u1EBB v\u1EDBi ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o."
  },
  delete_dialog: {
    description: "B\u1EA1n c\xF3 ch\u1EAFc ch\u1EAFn mu\u1ED1n x\xF3a b\u1EA3n chu\u1EA9n b\u1ECB n\xE0y kh\xF4ng?",
    title: "X\xF3a b\u1EA3n chu\u1EA9n b\u1ECB"
  },
  shared_prep: {
    leader_intro_title: "B\u1EA3n chu\u1EA9n b\u1ECB do th\xE0nh vi\xEAn chia s\u1EBB",
    member_intro_title: "B\u1EA3n chu\u1EA9n b\u1ECB \u0111\xE3 chia s\u1EBB v\u1EDBi ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o",
    leader_intro_description: "H\xE3y \u0111\u1ECDc tr\u01B0\u1EDBc bu\u1ED5i h\u1ECDp \u0111\u1EC3 n\u1EAFm ch\u1EE7 \u0111\u1EC1 th\xE0nh vi\xEAn mu\u1ED1n trao \u0111\u1ED5i v\xE0 k\u1EBFt qu\u1EA3 h\u1ECD mong mu\u1ED1n \u0111\u1EA1t \u0111\u01B0\u1EE3c t\u1EEB cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y."
  },
  prep_shared_nudge: {
    message: "{{name}} \u0111\xE3 chia s\u1EBB b\u1EA3n chu\u1EA9n b\u1ECB. H\u01B0\u1EDBng d\u1EABn cu\u1ED9c h\u1ECDp v\xE0 c\xE2u h\u1ECFi g\u1EE3i \xFD \u0111\xE3 s\u1EB5n s\xE0ng cho b\u1EA1n.",
    button: "Xem b\u1EA3n chu\u1EA9n b\u1ECB",
    message_mobile: "{{name}} \u0111\xE3 chia s\u1EBB b\u1EA3n chu\u1EA9n b\u1ECB",
    view: "Xem"
  },
  ai_summary: {
    button: "T\xF3m t\u1EAFt b\u1EB1ng AI",
    cta_helper: "N\u1EBFu prep d\xE0i, b\u1EA1n c\xF3 th\u1EC3 t\u1EA1o b\u1EA3n t\xF3m t\u1EAFt b\u1EB1ng AI \u0111\u1EC3 xem l\u01B0\u1EDBt tr\u01B0\u1EDBc.",
    tab_summary: "T\xF3m t\u1EAFt AI",
    tab_original: "B\u1EA3n g\u1ED1c"
  },
  prep_stage: {
    needs_focus_hint: "H\xE3y cho ch\xFAng t\xF4i bi\u1EBFt m\u1ED9t \u0111i\u1EC1u b\u1EA1n mu\u1ED1n n\xF3i \u0111\u1EBFn \u0111\u1EA7u ti\xEAn trong bu\u1ED5i 1on1 n\xE0y. N\u1EBFu ch\u01B0a c\xF3 ch\u1EE7 \u0111\u1EC1 c\u1EE5 th\u1EC3, b\u1EA1n c\xF3 th\u1EC3 vi\u1EBFt v\u1EC1 vi\u1EC7c khi\u1EBFn b\u1EA1n b\u1EADn t\xE2m nh\u1EA5t g\u1EA7n \u0111\xE2y ho\u1EB7c vi\u1EC7c c\u1EA7n \xFD ki\u1EBFn c\u1EE7a ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o.",
    ready_generate: "H\xE3y t\u1ED5ng h\u1EE3p th\xE0nh b\u1EA3n chu\u1EA9n b\u1ECB",
    ready_update: "H\xE3y c\u1EADp nh\u1EADt v\xE0o b\u1EA3n chu\u1EA9n b\u1ECB",
    ready_continue: "T\xF4i mu\u1ED1n tr\xF2 chuy\u1EC7n th\xEAm m\u1ED9t ch\xFAt"
  },
  placeholder: {
    member_before: {
      title: "H\xE3y s\u1EAFp x\u1EBFp tr\u01B0\u1EDBc nh\u1EEFng \u0111i\u1EC1u b\u1EA1n mu\u1ED1n chia s\u1EBB trong bu\u1ED5i 1on1 n\xE0y",
      title_mobile: "S\u1EAFp x\u1EBFp tr\u01B0\u1EDBc n\u1ED9i dung cho bu\u1ED5i 1on1",
      description: "Chu\u1EA9n b\u1ECB tr\u01B0\u1EDBc gi\xFAp ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o n\u1EAFm \u0111\u01B0\u1EE3c t\xECnh h\xECnh c\u1EE7a b\u1EA1n v\xE0 chu\u1EA9n b\u1ECB cho bu\u1ED5i tr\xF2 chuy\u1EC7n. B\u1EA1n s\u1EBD trao \u0111\u1ED5i s\xE2u h\u01A1n trong th\u1EDDi gian ng\u1EAFn.",
      button: "Vi\u1EBFt b\u1EA3n chu\u1EA9n b\u1ECB",
      how: "Tr\xF2 chuy\u1EC7n ri\xEAng t\u01B0 v\u1EDBi chatbot \u0111\u1EC3 t\u1ED5ng h\u1EE3p b\u1EA3n chu\u1EA9n b\u1ECB. H\xE3y xem l\u1EA1i, ch\u1EC9nh s\u1EEDa r\u1ED3i chia s\u1EBB."
    },
    deadline: {
      far: "H\xE3y chia s\u1EBB tr\u01B0\u1EDBc ng\xE0y h\u1ECDp. Sau khi chia s\u1EBB s\u1EBD kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa.",
      tomorrow: "H\xE3y chia s\u1EBB h\xF4m nay. Sau khi chia s\u1EBB s\u1EBD kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa.",
      today: "H\xE3y chia s\u1EBB tr\u01B0\u1EDBc bu\u1ED5i h\u1ECDp. Sau khi chia s\u1EBB s\u1EBD kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa."
    },
    member_not_shared: {
      title: "H\xE3y xem l\u1EA1i b\u1EA3n chu\u1EA9n b\u1ECB v\xE0 chia s\u1EBB v\u1EDBi ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o",
      description: "Khi \u0111\u01B0\u1EE3c chia s\u1EBB, ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o c\xF3 th\u1EC3 n\u1EAFm ch\u1EE7 \u0111\u1EC1 tr\u1ECDng t\xE2m v\xE0 s\u1EF1 h\u1ED7 tr\u1EE3 b\u1EA1n c\u1EA7n.",
      button: "Vi\u1EBFt ti\u1EBFp"
    },
    leader_waiting: {
      title: "Khi th\xE0nh vi\xEAn chia s\u1EBB b\u1EA3n chu\u1EA9n b\u1ECB, b\u1EA1n c\xF3 th\u1EC3 xem t\u1EA1i \u0111\xE2y",
      description: "B\u1EA1n s\u1EBD nh\u1EADn \u0111\u01B0\u1EE3c nh\u1EEFng \u0111i\u1EC1u th\xE0nh vi\xEAn mu\u1ED1n trao \u0111\u1ED5i c\xF9ng h\u01B0\u1EDBng d\u1EABn ti\u1EBFn h\xE0nh bu\u1ED5i h\u1ECDp ph\xF9 h\u1EE3p. H\xE3y \u0111\u1ECDc tr\u01B0\u1EDBc \u0111\u1EC3 chu\u1EA9n b\u1ECB cho cu\u1ED9c tr\xF2 chuy\u1EC7n.",
      hint: "Bu\u1ED5i 1on1 v\u1EABn di\u1EC5n ra nh\u01B0 d\u1EF1 ki\u1EBFn ngay c\u1EA3 khi ch\u01B0a c\xF3 b\u1EA3n chu\u1EA9n b\u1ECB."
    }
  },
  help: {
    trigger: "B\u1EA3n chu\u1EA9n b\u1ECB \u0111\u01B0\u1EE3c d\xF9ng nh\u01B0 th\u1EBF n\xE0o?",
    title: "B\u1EA3n chu\u1EA9n b\u1ECB l\xE0 g\xEC?",
    confirm: "\u0110\xE3 hi\u1EC3u",
    private_badge_member: "Ch\u1EC9 m\xECnh b\u1EA1n xem \u0111\u01B0\u1EE3c",
    private_badge_leader: "Ch\u1EC9 th\xE0nh vi\xEAn xem \u0111\u01B0\u1EE3c",
    shared_badge_member: "Ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o xem \u0111\u01B0\u1EE3c",
    shared_badge_leader: "B\u1EA1n xem \u0111\u01B0\u1EE3c",
    member: {
      lead: "B\u1EA3n chu\u1EA9n b\u1ECB l\xE0 ghi ch\xFA b\u1EA1n vi\u1EBFt tr\u01B0\u1EDBc bu\u1ED5i 1on1, t\xF3m t\u1EAFt nh\u1EEFng \u0111i\u1EC1u b\u1EA1n mu\u1ED1n trao \u0111\u1ED5i.",
      step1_title: "B\u1EA1n tr\xF2 chuy\u1EC7n ri\xEAng v\u1EDBi chatbot",
      step1_description: "AI d\u1EF1a v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u1EC3 t\u1EA1o b\u1EA3n nh\xE1p. Ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o kh\xF4ng xem \u0111\u01B0\u1EE3c cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y.",
      step2_title: "B\u1EA1n xem l\u1EA1i v\xE0 ch\u1EC9nh s\u1EEDa b\u1EA3n nh\xE1p",
      step2_description: "B\u1EA1n c\xF3 th\u1EC3 s\u1EEDa n\u1ED9i dung tr\u01B0\u1EDBc khi chia s\u1EBB.",
      boundary: "Khi b\u1EA1n nh\u1EA5n Chia s\u1EBB",
      step3_title: "Ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o nh\u1EADn \u0111\u01B0\u1EE3c b\u1EA3n chu\u1EA9n b\u1ECB",
      step3_description: "Ch\u1EC9 b\u1EA3n chu\u1EA9n b\u1ECB b\u1EA1n \u0111\xE3 chia s\u1EBB m\u1EDBi \u0111\u01B0\u1EE3c g\u1EEDi \u0111i.",
      step4: "Ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o chu\u1EA9n b\u1ECB cu\u1ED9c tr\xF2 chuy\u1EC7n v\u1EDBi h\u01B0\u1EDBng d\u1EABn v\xE0 c\xE2u h\u1ECFi g\u1EE3i \xFD \u0111i k\xE8m.",
      note: "Sau khi chia s\u1EBB, b\u1EA1n kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa. H\xE3y xem l\u1EA1i k\u1EF9 tr\u01B0\u1EDBc khi chia s\u1EBB."
    },
    leader: {
      lead: "B\u1EA3n chu\u1EA9n b\u1ECB l\xE0 ghi ch\xFA th\xE0nh vi\xEAn vi\u1EBFt tr\u01B0\u1EDBc bu\u1ED5i 1on1, t\xF3m t\u1EAFt nh\u1EEFng \u0111i\u1EC1u mu\u1ED1n trao \u0111\u1ED5i.",
      step1_title: "Th\xE0nh vi\xEAn tr\xF2 chuy\u1EC7n ri\xEAng v\u1EDBi chatbot",
      step1_description: "AI d\u1EF1a v\xE0o cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u1EC3 t\u1EA1o b\u1EA3n nh\xE1p. B\u1EA1n kh\xF4ng xem \u0111\u01B0\u1EE3c cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y.",
      step2_title: "Th\xE0nh vi\xEAn xem l\u1EA1i v\xE0 ch\u1EC9nh s\u1EEDa b\u1EA3n nh\xE1p",
      step2_description: "Th\xE0nh vi\xEAn s\u1EEDa n\u1ED9i dung tr\u01B0\u1EDBc khi chia s\u1EBB.",
      boundary: "Khi th\xE0nh vi\xEAn chia s\u1EBB",
      step3_title: "B\u1EA3n chu\u1EA9n b\u1ECB \u0111\u01B0\u1EE3c g\u1EEDi \u0111\u1EBFn",
      step3_description: "B\u1EA1n ch\u1EC9 xem \u0111\u01B0\u1EE3c b\u1EA3n chu\u1EA9n b\u1ECB th\xE0nh vi\xEAn \u0111\xE3 chia s\u1EBB.",
      step4: "H\u01B0\u1EDBng d\u1EABn ti\u1EBFn h\xE0nh v\xE0 c\xE2u h\u1ECFi g\u1EE3i \xFD \u0111\u01B0\u1EE3c cung c\u1EA5p k\xE8m theo. H\xE3y tham kh\u1EA3o khi chu\u1EA9n b\u1ECB.",
      note: "N\u1EBFu ch\u01B0a th\u1EA5y b\u1EA3n chu\u1EA9n b\u1ECB, ngh\u0129a l\xE0 th\xE0nh vi\xEAn ch\u01B0a chia s\u1EBB."
    }
  },
  guide: {
    title: "H\u01B0\u1EDBng d\u1EABn h\u1ECDp",
    description: "D\u1EF1a tr\xEAn b\u1EA3n chu\u1EA9n b\u1ECB th\xE0nh vi\xEAn \u0111\xE3 chia s\u1EBB v\xE0 h\u1ED3 s\u01A1 c\u1EE7a h\u1ECD, AI \u0111\u1EC1 xu\u1EA5t ti\u1EBFn tr\xECnh cho cu\u1ED9c h\u1ECDp \u2014 b\u1EAFt \u0111\u1EA7u t\u1EEB c\xE2u chuy\u1EC7n n\xE0o, \u0111\xE0o s\xE2u \u0111i\u1EC1u g\xEC v\xE0 k\u1EBFt th\xFAc ra sao.",
    flow: "Ti\u1EBFn tr\xECnh \u0111\u1EC1 xu\u1EA5t",
    overview: "T\u1ED5ng quan",
    stage: {
      OPENING: "M\u1EDF \u0111\u1EA7u",
      MAIN: "Trao \u0111\u1ED5i ch\xEDnh",
      DEEPEN: "\u0110\xE0o s\xE2u",
      CLOSING: "K\u1EBFt th\xFAc"
    },
    key_insight: "Th\xF4ng tin quan tr\u1ECDng",
    approach: "\u0110\u1EC1 xu\u1EA5t & L\u01B0u \xFD",
    tip: "M\u1EB9o h\u1ED9i tho\u1EA1i"
  },
  profile_card: {
    title: "H\u1ED3 s\u01A1 th\xE0nh vi\xEAn"
  },
  error: {
    access: "Ch\u1EC9 th\xE0nh vi\xEAn m\u1EDBi c\xF3 th\u1EC3 vi\u1EBFt b\u1EA3n chu\u1EA9n b\u1ECB."
  },
  mobile: {
    tabs: {
      chat: "Tr\xF2 chuy\u1EC7n",
      summary: "Chu\u1EA9n b\u1ECB"
    },
    title: "Vi\u1EBFt chu\u1EA9n b\u1ECB"
  },
  question_recommend: {
    description: "\u0110\xE2y l\xE0 nh\u1EEFng c\xE2u h\u1ECFi \u0111\u01B0\u1EE3c chu\u1EA9n b\u1ECB theo t\u1EEBng giai \u0111o\u1EA1n c\u1EE7a ti\u1EBFn tr\xECnh \u0111\u1EC1 xu\u1EA5t. H\xE3y \u0111\xE1nh d\u1EA5u ch\u1ECDn nh\u1EEFng c\xE2u h\u1ECFi b\u1EA1n mu\u1ED1n th\xEAm v\xE0o danh s\xE1ch c\xE2u h\u1ECFi c\u1EE7a cu\u1ED9c h\u1ECDp. Th\xE0nh vi\xEAn s\u1EBD kh\xF4ng th\u1EA5y cho \u0111\u1EBFn khi \u0111\u01B0\u1EE3c th\xEAm.",
    title: "C\xE2u h\u1ECFi g\u1EE3i \xFD",
    grouped_title: "Ti\u1EBFn tr\xECnh \u0111\u1EC1 xu\u1EA5t \xB7 {{count}} c\xE2u h\u1ECFi",
    check_tooltip: "Th\xEAm l\xE0m c\xE2u h\u1ECFi cho cu\u1ED9c h\u1ECDp",
    delete_tooltip: "X\xF3a kh\u1ECFi danh s\xE1ch g\u1EE3i \xFD",
    adopt_all: "D\xF9ng t\u1EA5t c\u1EA3",
    adopt_all_confirm: "Th\xEAm t\u1EA5t c\u1EA3 {{count}} c\xE2u h\u1ECFi g\u1EE3i \xFD v\xE0o danh s\xE1ch c\xE2u h\u1ECFi c\u1EE7a cu\u1ED9c h\u1ECDp?",
    adopt_confirm: "Th\xEAm {{count}} c\xE2u h\u1ECFi \u0111\xE3 ch\u1ECDn v\xE0o danh s\xE1ch c\xE2u h\u1ECFi c\u1EE7a cu\u1ED9c h\u1ECDp?",
    delete_confirm: "X\xF3a c\xE2u h\u1ECFi n\xE0y kh\u1ECFi danh s\xE1ch g\u1EE3i \xFD?\nThao t\xE1c n\xE0y kh\xF4ng th\u1EC3 ho\xE0n t\xE1c.",
    selected_count: "\u0110\xE3 ch\u1ECDn {{count}} c\xE2u h\u1ECFi",
    select_all: "Ch\u1ECDn t\u1EA5t c\u1EA3 c\xE2u h\u1ECFi",
    unselect_all: "B\u1ECF ch\u1ECDn t\u1EA5t c\u1EA3 c\xE2u h\u1ECFi",
    view_in_content: "Xem trong tab N\u1ED9i dung"
  },
  share: {
    description: "Khi chia s\u1EBB, l\xE3nh \u0111\u1EA1o s\u1EBD xem \u0111\u01B0\u1EE3c b\u1EA3n chu\u1EA9n b\u1ECB v\xE0 b\u1EA1n s\u1EBD kh\xF4ng th\u1EC3 ch\u1EC9nh s\u1EEDa n\u1EEFa.",
    done: "Chia s\u1EBB",
    error: {
      message: "\u0110\xE3 x\u1EA3y ra l\u1ED7i khi l\u01B0u v\xE0 chia s\u1EBB. Vui l\xF2ng th\u1EED l\u1EA1i sau.",
      title: "L\u01B0u th\u1EA5t b\u1EA1i"
    },
    success: {
      message: "Chu\u1EA9n b\u1ECB \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u v\xE0 chia s\u1EBB v\u1EDBi l\xE3nh \u0111\u1EA1o.",
      title: "\u0110\xE3 l\u01B0u & chia s\u1EBB"
    },
    requested: {
      title: "\u0110ang l\u01B0u & chia s\u1EBB b\u1EA3n chu\u1EA9n b\u1ECB",
      message: "Chu\u1EA9n b\u1ECB \u0111\xE3 \u0111\u01B0\u1EE3c l\u01B0u. S\u1EBD \u0111\u01B0\u1EE3c chia s\u1EBB v\u1EDBi l\xE3nh \u0111\u1EA1o trong gi\xE2y l\xE1t."
    },
    title: "Chia s\u1EBB v\u1EDBi l\xE3nh \u0111\u1EA1o"
  },
  survey: {
    skip: "T\xF4i v\u1EABn ch\u01B0a r\xF5",
    skip_description: "T\xF4i s\u1EBD v\u1EEBa tr\xF2 chuy\u1EC7n v\u1EDBi chatbot v\u1EEBa t\xECm ch\u1EE7 \u0111\u1EC1.",
    section_title: "Nguy\xEAn li\u1EC7u tr\xF2 chuy\u1EC7n",
    section_helper: "Nh\u1EEFng l\u1EF1a ch\u1ECDn n\xE0y gi\xFAp t\xECm ra \u0111i\u1EC1u \u0111ang quan tr\u1ECDng v\u1EDBi b\u1EA1n v\xE0 c\u1EE5 th\u1EC3 h\xF3a n\u1ED9i dung s\u1EBD trao \u0111\u1ED5i trong bu\u1ED5i 1on1. Ch\u1EC9 c\u1EA7n ch\u1ECDn nh\u1EEFng m\u1EE5c b\u1EA1n \u0111\xE3 c\xF3 c\xE2u tr\u1EA3 l\u1EDDi.",
    section_expand: "M\u1EDF r\u1ED9ng",
    section_collapse: "Thu g\u1ECDn",
    section_optional: "T\xF9y ch\u1ECDn",
    summary_profile: "\u0110\xE3 \xE1p d\u1EE5ng h\u1ED3 s\u01A1",
    summary_topic: "\u0110\xE3 ch\u1ECDn {{count}}",
    summary_empty: "Ch\u01B0a ch\u1ECDn m\u1EE5c n\xE0o",
    label_profile: "H\u1ED3 s\u01A1",
    label_survey: "Ch\u1EE7 \u0111\u1EC1"
  },
  survey_answers: {
    condition: {
      category: "T\xECnh tr\u1EA1ng c\xF4ng vi\u1EC7c",
      question: "T\xECnh tr\u1EA1ng c\xF4ng vi\u1EC7c c\u1EE7a b\u1EA1n g\u1EA7n \u0111\xE2y th\u1EBF n\xE0o?",
      helper: "T\xECnh tr\u1EA1ng b\u1EA1n ch\u1ECDn l\xE0 th\xF4ng tin tham kh\u1EA3o gi\xFAp cu\u1ED9c tr\xF2 chuy\u1EC7n v\u1EDBi chatbot, v\xE0 kh\xF4ng \u0111\u01B0\u1EE3c chia s\u1EBB nguy\xEAn v\u1EB9n cho ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o. Ngay c\u1EA3 khi n\xF3 li\xEAn quan \u0111\u1EBFn \u0111i\u1EC1u b\u1EA1n s\u1EAFp trao \u0111\u1ED5i, b\u1EA1n v\u1EABn t\u1EF1 ch\u1ECDn c\xF3 \u0111\u01B0a v\xE0o b\u1EA3n chu\u1EA9n b\u1ECB hay kh\xF4ng.",
      VERY_GOOD: "R\u1EA5t t\u1ED1t",
      GOOD: "T\u1ED1t",
      NORMAL: "B\xECnh th\u01B0\u1EDDng",
      TIRED: "M\u1EC7t m\u1ECFi",
      VERY_BAD: "R\u1EA5t t\u1EC7"
    },
    topic: {
      category: "Ch\u1EE7 \u0111\u1EC1 h\u1ECDp",
      question: "B\u1EA1n mu\u1ED1n n\xF3i v\u1EC1 \u0111i\u1EC1u g\xEC nh\u1EA5t trong bu\u1ED5i 1on1 n\xE0y?",
      helper: "H\xE3y ch\u1ECDn ch\u1EE7 \u0111\u1EC1 g\u1EA7n v\u1EDBi c\u1EA3m nh\u1EADn c\u1EE7a b\u1EA1n l\xFAc n\xE0y. N\u1EBFu v\u1EABn ch\u01B0a r\xF5, b\u1EA1n c\xF3 th\u1EC3 v\u1EEBa tr\xF2 chuy\u1EC7n v\u1EDBi chatbot v\u1EEBa t\xECm ra c\u0169ng \u0111\u01B0\u1EE3c.",
      PERFORMANCE_FEEDBACK: "Hi\u1EC7u su\u1EA5t & Ph\u1EA3n h\u1ED3i",
      PERFORMANCE_FEEDBACK_description: "T\xF4i mu\u1ED1n c\xF9ng nh\xECn l\u1EA1i k\u1EBFt qu\u1EA3 c\xF4ng vi\u1EC7c g\u1EA7n \u0111\xE2y v\xE0 c\xE1ch l\xE0m vi\u1EC7c, \u0111\u1ED3ng th\u1EDDi nh\u1EADn ph\u1EA3n h\u1ED3i v\u1EC1 \u0111i\u1EC3m \u0111\xE3 l\xE0m t\u1ED1t v\xE0 \u0111i\u1EC3m c\u1EA7n c\u1EA3i thi\u1EC7n.",
      GROWTH_CAREER: "Ph\xE1t tri\u1EC3n & S\u1EF1 nghi\u1EC7p",
      GROWTH_CAREER_description: "T\xF4i mu\u1ED1n trao \u0111\u1ED5i v\u1EC1 n\u0103ng l\u1EF1c mu\u1ED1n ph\xE1t tri\u1EC3n, \u0111\u1ECBnh h\u01B0\u1EDBng s\u1EF1 nghi\u1EC7p v\xE0 nh\u1EEFng tr\u1EA3i nghi\u1EC7m c\u1EA7n thi\u1EBFt.",
      WORK_ISSUES: "Tr\u0103n tr\u1EDF c\xF4ng vi\u1EC7c & H\u1ED7 tr\u1EE3",
      WORK_ISSUES_description: "T\xF4i mu\u1ED1n xem x\xE9t nh\u1EEFng kh\xF3 kh\u0103n \u0111ang b\u1EBF t\u1EAFc ho\u1EB7c l\u1EB7p \u0111i l\u1EB7p l\u1EA1i trong c\xF4ng vi\u1EC7c, v\xE0 t\xECm h\u01B0\u1EDBng gi\u1EA3i quy\u1EBFt hay s\u1EF1 h\u1ED7 tr\u1EE3 c\u1EA7n thi\u1EBFt.",
      TEAM_CULTURE: "Giao ti\u1EBFp & V\u0103n h\xF3a nh\xF3m",
      TEAM_CULTURE_description: "T\xF4i mu\u1ED1n trao \u0111\u1ED5i v\u1EC1 c\u1EA3m nh\u1EADn c\u1EE7a m\xECnh \u0111\u1ED1i v\u1EDBi c\xE1ch giao ti\u1EBFp, h\u1EE3p t\xE1c hay b\u1EA7u kh\xF4ng kh\xED c\u1EE7a nh\xF3m, v\xE0 nh\u1EEFng thay \u0111\u1ED5i t\xF4i mong mu\u1ED1n.",
      ROLE_RESPONSIBILITY: "Vai tr\xF2 & Tr\xE1ch nhi\u1EC7m",
      ROLE_RESPONSIBILITY_description: "T\xF4i mu\u1ED1n l\xE0m r\xF5 vai tr\xF2 v\xE0 tr\xE1ch nhi\u1EC7m \u0111\u01B0\u1EE3c k\u1EF3 v\u1ECDng \u1EDF m\xECnh, th\u1EE9 t\u1EF1 \u01B0u ti\xEAn c\xF4ng vi\u1EC7c v\xE0 ph\u1EA1m vi ra quy\u1EBFt \u0111\u1ECBnh."
    }
  },
  title: "B\u1EA3n chu\u1EA9n b\u1ECB"
};

// src/locales/vi-VN/oneonone_notification.json
var oneonone_notification_default3 = {
  dday_reminder: {
    title: "Nh\u1EAFc nh\u1EDF cu\u1ED9c h\u1ECDp",
    body_dday: "B\u1EA1n c\xF3 cu\u1ED9c h\u1ECDp 1on1 v\u1EDBi {{partnerName}} h\xF4m nay l\xFAc {{time}}.",
    body_d1: "B\u1EA1n c\xF3 cu\u1ED9c h\u1ECDp 1on1 v\u1EDBi {{partnerName}} v\xE0o ng\xE0y mai l\xFAc {{time}}.",
    body_dn: "B\u1EA1n c\xF3 cu\u1ED9c h\u1ECDp 1on1 v\u1EDBi {{partnerName}} sau {{daysUntil}} ng\xE0y."
  },
  meeting_add: {
    title: "Cu\u1ED9c h\u1ECDp m\u1EDBi",
    body: "Cu\u1ED9c h\u1ECDp 1on1 m\u1EDBi v\u1EDBi {{partnerName}} \u0111\xE3 \u0111\u01B0\u1EE3c t\u1EA1o."
  },
  meeting_change: {
    title: "Cu\u1ED9c h\u1ECDp \u0111\u01B0\u1EE3c c\u1EADp nh\u1EADt",
    body: "Cu\u1ED9c h\u1ECDp 1on1 v\u1EDBi {{partnerName}} \u0111\xE3 \u0111\u01B0\u1EE3c d\u1EDDi sang {{newSchedule}}.",
    body_type_only: "H\xECnh th\u1EE9c h\u1ECDp v\u1EDBi {{partnerName}} \u0111\xE3 \u0111\u1ED5i th\xE0nh {{newMeetingTypeLabel}}.",
    body_both: "Cu\u1ED9c h\u1ECDp 1on1 v\u1EDBi {{partnerName}} \u0111\xE3 \u0111\u01B0\u1EE3c d\u1EDDi sang {{newSchedule}} ({{newMeetingTypeLabel}}).",
    meeting_type_offline: "tr\u1EF1c ti\u1EBFp",
    meeting_type_online: "tr\u1EF1c tuy\u1EBFn"
  },
  meeting_cancel: {
    title: "Cu\u1ED9c h\u1ECDp \u0111\xE3 h\u1EE7y",
    body: "{{partnerName}} \u0111\xE3 h\u1EE7y cu\u1ED9c h\u1ECDp 1on1."
  },
  prep_share: {
    title: "Chu\u1EA9n b\u1ECB \u0111\xE3 chia s\u1EBB",
    body: "{{partnerName}} \u0111\xE3 chia s\u1EBB b\u1EA3n chu\u1EA9n b\u1ECB."
  },
  question_request: {
    title: "Y\xEAu c\u1EA7u xem c\xE2u h\u1ECFi",
    body: "{{partnerName}} \u0111\xE3 y\xEAu c\u1EA7u xem x\xE9t c\xE2u h\u1ECFi cu\u1ED9c h\u1ECDp."
  },
  recording_ready: {
    title: "T\u1EC7p ghi h\xECnh \u0111\xE3 s\u1EB5n s\xE0ng",
    body: "T\u1EC7p ghi h\xECnh cu\u1ED9c h\u1ECDp v\u1EDBi {{partnerName}} \u0111\xE3 s\u1EB5n s\xE0ng. Nh\u1EA5n \u0111\u1EC3 nh\u1EADp v\xE0 b\u1EAFt \u0111\u1EA7u ph\xE2n t\xEDch AI."
  },
  analysis_complete: {
    title: "Ph\xE2n t\xEDch ho\xE0n t\u1EA5t",
    body: "Ph\xE2n t\xEDch cu\u1ED9c h\u1ECDp v\u1EDBi {{partnerName}} \u0111\xE3 ho\xE0n t\u1EA5t. Nh\u1EA5n \u0111\u1EC3 xem k\u1EBFt qu\u1EA3."
  },
  member_feedback_ready: {
    title: "Ph\u1EA3n h\u1ED3i AI \u0111\xE3 s\u1EB5n s\xE0ng",
    body: "Xem ph\u1EA3n h\u1ED3i AI t\u1EEB cu\u1ED9c h\u1ECDp v\u1EDBi {{partnerName}} v\xE0 nh\xECn l\u1EA1i cu\u1ED9c h\u1ECDp n\xE0y."
  },
  in_progress_incomplete_reminder: {
    title: "Vui l\xF2ng k\u1EBFt th\xFAc cu\u1ED9c h\u1ECDp",
    body: "Cu\u1ED9c h\u1ECDp v\u1EDBi {{partnerName}} v\u1EABn \u0111ang \u1EDF tr\u1EA1ng th\xE1i \u0111ang di\u1EC5n ra."
  },
  cycle_reminder: {
    title: "H\xE3y t\u1EA1o cu\u1ED9c h\u1ECDp 1on1 ti\u1EBFp theo",
    body: "\u0110\xE3 \u0111\u1EBFn chu k\u1EF3 1on1 \u0111\u1ECBnh k\u1EF3 v\u1EDBi {{partnerName}}. T\u1EA1o cu\u1ED9c h\u1ECDp \u0111\u1EC3 duy tr\xEC giao ti\u1EBFp \u0111\u1EC1u \u0111\u1EB7n."
  },
  empty: "Kh\xF4ng c\xF3 th\xF4ng b\xE1o m\u1EDBi.",
  fallback: {
    title: "Th\xF4ng b\xE1o m\u1EDBi",
    body: "B\u1EA1n \u0111\xE3 nh\u1EADn \u0111\u01B0\u1EE3c th\xF4ng b\xE1o m\u1EDBi."
  }
};

// src/locales/vi-VN/landing_new.json
var landing_new_default3 = {
  change: {
    pill: "Orblit \u2014 c\xF4ng c\u1EE5 qu\u1EA3n l\xFD thay \u0111\u1ED5i lan t\u1ECFa t\u1EA7m nh\xECn \u0111\u1EBFn m\u1ECDi ng\xF3c ng\xE1ch",
    heroTitle: "Chi\u1EBFn l\u01B0\u1EE3c kh\xF4ng n\u1EB1m \u1EDF th\xF4ng b\xE1o, m\xE0 \u0111\u01B0\u1EE3c th\u1EF1c thi trong 1on1.",
    heroSubtitle: "T\u1EEB t\u1EA7m nh\xECn c\u1EE7a ban l\xE3nh \u0111\u1EA1o \u0111\u1EBFn nh\xE2n vi\xEAn m\u1EDBi nh\u1EA5t, Orblit thi\u1EBFt k\u1EBF con \u0111\u01B0\u1EDDng \u1EA5y.",
    problem: {
      title: "L\xFD do th\u1EADt s\u1EF1 khi\u1EBFn chi\u1EBFn l\u01B0\u1EE3c kh\xF3 th\u1EF1c thi",
      quotes: [
        "M\u1ED9t th\xE1ng sau bu\u1ED5i town hall, nh\xE2n vi\xEAn m\u1EDBi kh\xF4ng c\xF2n nh\u1EDB \u0111\u1ECBnh h\u01B0\u1EDBng c\xF4ng ty.",
        "S\xE1u tu\u1EA7n sau kh\xF3a \u0111\xE0o t\u1EA1o l\xE3nh \u0111\u1EA1o, 90% quay l\u1EA1i th\xF3i quen c\u0169.",
        "HR kh\xF4ng bi\u1EBFt \u0111\u1ED9i n\xE0o \u0111ang r\u1EA1n n\u1EE9t cho \u0111\u1EBFn khi c\xF3 k\u1EBFt qu\u1EA3 \u0111\xE1nh gi\xE1 360.",
        "Ch\u1EC9 khi nh\xE2n t\xE0i ch\u1EE7 ch\u1ED1t xin ngh\u1EC9 ta m\u1EDBi b\u1EAFt \u0111\u1EA7u h\u1ECFi l\xFD do th\u1EADt s\u1EF1."
      ]
    },
    layers: {
      title: "Thay \u0111\u1ED5i ph\u1EA3i di\u1EC5n ra \u0111\u1ED3ng th\u1EDDi \u1EDF c\u1EA3 ba t\u1EA7ng",
      items: [
        {
          title: "Ban l\xE3nh \u0111\u1EA1o (Top)",
          desc: "\u0110\u01B0a th\xF4ng \u0111i\u1EC7p chi\u1EBFn l\u01B0\u1EE3c v\xE0o agenda 1on1"
        },
        {
          title: "HR (Middle)",
          desc: "Gi\xE1m s\xE1t giao ti\u1EBFp v\xE0 m\u1EE9c g\u1EAFn k\u1EBFt to\xE0n t\u1ED5 ch\u1EE9c"
        },
        {
          title: "Qu\u1EA3n l\xFD tr\u1EF1c ti\u1EBFp (Frontline)",
          desc: "Di\u1EC5n gi\u1EA3i v\xE0 truy\u1EC1n \u0111\u1EA1t th\xF4ng \u0111i\u1EC7p trong \u0111\u1ED9i c\u1EE7a m\xECnh"
        }
      ]
    },
    toolbox: {
      title: "B\u1ED9 c\xF4ng c\u1EE5 m\u1EDBi cho HR",
      items: [
        {
          title: "\u0110\u1ECBnh tuy\u1EBFn t\u1EEB tr\xEAn xu\u1ED1ng",
          desc: "T\u1EF1 \u0111\u1ED9ng nh\xFAng m\u1EE5c ti\xEAu c\xF4ng ty v\xE0 th\xF4ng \u0111i\u1EC7p chi\u1EBFn l\u01B0\u1EE3c c\u1ED1t l\xF5i v\xE0o ph\u1EA7n chu\u1EA9n b\u1ECB 1on1, \u0111\u1EC3 chi\u1EBFn l\u01B0\u1EE3c \u0111\u1EBFn \u0111\u01B0\u1EE3c t\u1EEBng nh\xE2n vi\xEAn."
        },
        {
          title: "Quan s\xE1t theo chi\u1EC1u ngang",
          desc: "Gi\xE1m s\xE1t t\u1EA7n su\u1EA5t giao ti\u1EBFp c\u1EE7a t\u1EEBng \u0111\u1ED9i v\xE0 t\xECnh tr\u1EA1ng l\xE3nh \u0111\u1EA1o theo th\u1EDDi gian th\u1EF1c tr\xEAn dashboard to\xE0n c\xF4ng ty."
        },
        {
          title: "Th\u1EA5u hi\u1EC3u t\u1EEB d\u01B0\u1EDBi l\xEAn",
          desc: "Ph\xE1t hi\u1EC7n c\xE1c t\xEDn hi\u1EC7u t\u1ED5 ch\u1EE9c \u2014 d\u1EA5u hi\u1EC7u ki\u1EC7t s\u1EE9c, nguy c\u01A1 ngh\u1EC9 vi\u1EC7c, thay \u0111\u1ED5i tinh th\u1EA7n \u2014 t\u1EEB d\u1EEF li\u1EC7u 1on1 t\xEDch l\u0169y."
        },
        {
          title: "H\xE0nh \u0111\u1ED9ng HR",
          desc: "Can thi\u1EC7p trong kho\u1EA3ng th\u1EDDi gian v\xE0ng nh\u1EDD c\xE1c c\u1EA3nh b\xE1o nh\u01B0 nguy c\u01A1 ngh\u1EC9 vi\u1EC7c."
        }
      ]
    },
    cases: {
      title: "Hi\u1EC7u qu\u1EA3 tri\u1EC3n khai th\u1EF1c t\u1EBF",
      h: {
        name: "C\xF4ng ty th\xE0nh vi\xEAn T\u1EADp \u0111o\xE0n H",
        sub: "B\u1EB1ng ch\u1EE9ng thay \u0111\u1ED5i v\u0103n h\xF3a",
        body: "H\u1ECD \u0111\xE3 \u0111\u1EA1t \u0111\u01B0\u1EE3c[d] [b=#2563eb]s\u1EF1 tri\u1EC3n khai \u1ED5n \u0111\u1ECBnh v\xE0 k\xEDch ho\u1EA1t ch\u01B0\u01A1ng tr\xECnh 1on1[/b] m\xE0 c\xE1c gi\u1EA3i ph\xE1p kh\xE1c kh\xF4ng th\u1EC3 mang l\u1EA1i."
      },
      s: {
        name: "C\xF4ng ty th\xE0nh vi\xEAn T\u1EADp \u0111o\xE0n S",
        sub: "Chuy\u1EC3n \u0111\u1ED5i ch\xEDnh h\u1EA1 t\u1EA7ng nh\xE2n s\u1EF1",
        body: "H\u1ECD \u0111\xE3 [b=#4f46e5]t\xE1i c\u1EA5u tr\xFAc qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t quanh 1on1[/b],[d] n\xE2ng cao n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o tuy\u1EBFn \u0111\u1EA7u."
      }
    },
    next: {
      label: "Next Step",
      title: "M\u1ECDi thay \u0111\u1ED5i \u0111\u1EC1u b\u1EAFt \u0111\u1EA7u t\u1EEB m\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n",
      cta: "T\xECm hi\u1EC3u Giao ti\u1EBFp"
    }
  },
  communication: {
    pill: "Orblit \u2014 c\xF4ng c\u1EE5 gi\xFAp cu\u1ED9c tr\xF2 chuy\u1EC7n l\xE0nh m\u1EA1nh lan t\u1ECFa kh\u1EAFp t\u1ED5 ch\u1EE9c",
    heroTitle: "Giao ti\u1EBFp kh\xF4ng ph\u1EA3i t\xEDnh c\xE1ch \u2014 \u0111\xF3 l\xE0 k\u1EF9 n\u0103ng.",
    heroSubtitle: "Orblit gi\xFAp m\u1ECDi qu\u1EA3n l\xFD b\u1EAFt \u0111\u1EA7u m\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n t\u1ED1t.",
    problem: {
      title: "1on1 ng\u01B0\u1EE3ng ng\u1EADp kh\xF4ng ch\u1EC9 l\xE0 v\u1EA5n \u0111\u1EC1 c\u1EE7a b\u1EA1n",
      subtitle: "\u0110\xF3 kh\xF4ng ph\u1EA3i l\u1ED7i c\xE1 nh\xE2n \u2014 m\xE0 l\xE0 v\u1EA5n \u0111\u1EC1 c\u1EA5u tr\xFAc.",
      quotes: [
        "Ch\u1EB3ng bi\u1EBFt n\xF3i g\xEC, cu\u1ED1i c\xF9ng ch\u1EC9 n\xF3i chuy\u1EC7n th\u1EDDi ti\u1EBFt.",
        'H\u1ECFi g\xEC c\u0169ng ch\u1EC9 nh\u1EADn l\u1EA1i "Em \u1ED5n \u1EA1."',
        "T\xF4i g\xF3p \xFD, tu\u1EA7n sau n\xE9t m\u1EB7t h\u1ECD \u0111\xE3 c\u1EE9ng l\u1EA1i.",
        "T\xF4i kh\xF4ng c\xF3 c\xE1ch n\xE0o kh\xE1ch quan[d] \u0111\u1EC3 bi\u1EBFt m\xECnh c\xF3 \u0111ang l\xE0m t\u1ED1t kh\xF4ng."
      ]
    },
    needs: {
      title: "M\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n t\u1ED1t c\u1EA7n ba \u0111i\u1EC1u",
      items: [
        {
          title: "1. C\xE2u h\u1ECFi \u0111\u01B0\u1EE3c chu\u1EA9n b\u1ECB",
          desc: 'Kh\xF4ng ph\u1EA3i "D\u1EA1o n\xE0y sao?" m\u01A1 h\u1ED3 \u2014 m\xE0[d] c\xE2u h\u1ECFi ph\xF9 h\u1EE3p v\u1EDBi t\u1EEBng ng\u01B0\u1EDDi, t\u1EEBng t\xECnh hu\u1ED1ng'
        },
        {
          title: "2. B\u1EA7u kh\xF4ng kh\xED an to\xE0n",
          desc: "M\u1ED9t c\u1EA5u tr\xFAc chu\u1EA9n b\u1ECB s\u1EB5n \u0111\u1EC3[d] nh\xE2n vi\xEAn ch\u1EE7 \u0111\u1ED9ng n\xF3i tr\u01B0\u1EDBc"
        },
        {
          title: "3. Ph\u1EA3n h\u1ED3i kh\xE1ch quan",
          desc: "T\u1EA5m g\u01B0\u01A1ng \u0111\u1EC3 nh\xECn l\u1EA1i[d] c\xE1ch giao ti\u1EBFp c\u1EE7a ch\xEDnh b\u1EA1n sau \u0111\xF3"
        }
      ]
    },
    how: {
      title: "Ho\u1EA1t \u0111\u1ED9ng th\u1EBF n\xE0o?",
      steps: [
        {
          tag: "Tr\u01B0\u1EDBc",
          title: "Chu\u1EA9n b\u1ECB h\u1ECDp b\u1EB1ng AI + c\xE2u h\u1ECFi g\u1EE3i \xFD",
          desc: "Orblit ph\xE2n t\xEDch t\xE2m tr\u1EA1ng v\xE0 tr\u0103n tr\u1EDF m\xE0 nh\xE2n vi\xEAn ghi tr\u01B0\u1EDBc, r\u1ED3i \u0111\u01B0a cho qu\u1EA3n l\xFD m\u1ED9t h\u01B0\u1EDBng d\u1EABn ph\xF9 h\u1EE3p."
        },
        {
          tag: "Trong",
          title: "STT t\u1EF1 \u0111\u1ED9ng x\xF3a g\xE1nh n\u1EB7ng ghi ch\xE9p",
          desc: "Qu\u1EA3n l\xFD kh\xF4ng c\xF2n ph\u1EA3i ghi ch\xE9p. Ch\u1EC9 t\u1EADp trung l\u1EAFng nghe v\xE0 k\u1EBFt n\u1ED1i."
        },
        {
          tag: "Sau",
          title: "Hu\u1EA5n luy\u1EC7n l\xE3nh \u0111\u1EA1o t\u1EEB ph\xE2n t\xEDch l\u1EDDi n\xF3i",
          desc: '"T\u1EF7 l\u1EC7 n\xF3i c\u1EE7a t\xF4i? T\xF4i \u0111\xE3 h\u1ECFi t\u1ED1t ch\u01B0a?" \u2014 nh\u1EADn b\xE1o c\xE1o v\xE0 hu\u1EA5n luy\u1EC7n d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u.'
        },
        {
          tag: "T\xEDch l\u0169y",
          title: "B\xE1o c\xE1o n\u0103ng l\u1EF1c giao ti\u1EBFp",
          desc: "Theo d\xF5i \u0111\u01B0\u1EDDng cong ph\xE1t tri\u1EC3n qua s\xE1u chi\u1EC1u: l\u1EAFng nghe, \u0111\u1EB7t c\xE2u h\u1ECFi, \u0111\u1ED3ng c\u1EA3m, ph\u1EA3n h\u1ED3i v\xE0 h\u01A1n th\u1EBF."
        }
      ]
    },
    scenario: {
      title: "Bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn c\u1EE7a tr\u01B0\u1EDFng nh\xF3m Nam",
      p1: 'Nam e ng\u1EA1i bu\u1ED5i 1on1 \u0111\u1EA7u ti\xEAn v\u1EDBi nh\xE2n vi\xEAn m\u1EDBi v\xE0o s\xE1u th\xE1ng. Nh\u01B0ng Orblit ph\xE2n t\xEDch ph\u1EA7n chu\u1EA9n b\u1ECB c\u1EE7a nh\xE2n vi\xEAn v\xE0 ch\u1EC9 ra [h]"h\u1ECD \u0111ang b\u1ED1i r\u1ED1i v\u1EC1 vi\u1EC7c c\u0103n ch\u1EC9nh OKR g\u1EA7n \u0111\xE2y"[/h], n\xEAn Nam m\u1EDF \u0111\u1EA7u b\u1EB1ng c\xE2u h\u1ECFi g\u1EE3i \xFD: [u]"M\u1EE5c ti\xEAu n\xE0o hi\u1EC7n ch\u01B0a r\xF5 r\xE0ng nh\u1EA5t v\u1EDBi b\u1EA1n?"[/u]',
      p2: 'Ba m\u01B0\u01A1i ph\xFAt sau, l\u1EA7n \u0111\u1EA7u ti\xEAn Nam hi\u1EC3u \u0111\u01B0\u1EE3c [b=#1a1a1a]"\u0111i\u1EC3m m\xE0 ng\u01B0\u1EDDi n\xE0y th\u1EADt s\u1EF1 b\u1EBF t\u1EAFc."[/b] Orblit t\u1EF1 \u0111\u1ED9ng s\u1EAFp x\u1EBFp c\xE1c \u0111\u1EA7u vi\u1EC7c sau bu\u1ED5i n\xF3i chuy\u1EC7n, n\xEAn Nam c\xF3 th\u1EC3 t\u1EADp trung 100% v\xE0o vi\u1EC7c l\u1EAFng nghe.'
    },
    transform: {
      title: "Ng\u01B0\u1EDDi qu\u1EA3n l\xFD thay \u0111\u1ED5i ra sao khi 1on1 c\u1EE7a Orblit b\xE9n r\u1EC5",
      asis: "Ng\u01B0\u1EDDi \u0111\xE1nh gi\xE1 m\u1ED9t chi\u1EC1u[d] \xE1p \u0111\u1EB7t c\xE2u tr\u1EA3 l\u1EDDi \u0111\xFAng",
      tobe: "Ng\u01B0\u1EDDi \u0111\u1ED3ng h\xE0nh kh\u01A1i d\u1EADy[d] ti\u1EC1m n\u0103ng \u1EA9n gi\u1EA5u",
      points: [
        {
          before: '"Bi\u1EBFt n\xF3i g\xEC \u0111\xE2y?" \u2014[d] n\u1ED7i e ng\u1EA1i mu\u1ED1n n\xE9 tr\xE1nh',
          after: "S\u1EF1 t\u1EF1 tin c\u1EE7a m\u1ED9t qu\u1EA3n l\xFD[d] \u0111\u01B0\u1EE3c trang b\u1ECB d\u1EEF li\u1EC7u v\xE0 h\u01B0\u1EDBng d\u1EABn"
        },
        {
          before: "N\u1ED7i b\u1EA5t an khi kh\xF4ng bi\u1EBFt[d] nh\xE2n vi\xEAn th\u1EADt s\u1EF1 ngh\u0129 g\xEC",
          after: "S\u1EF1 tin t\u01B0\u1EDFng v\xE0 an t\xE2m khi th\u1EA5u hi\u1EC3u[d] nh\xE2n vi\xEAn qua giao ti\u1EBFp minh b\u1EA1ch"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "Tr\xF2 chuy\u1EC7n t\u1ED1t ch\u1EC9 l\xE0 kh\u1EDFi \u0111\u1EA7u. Ti\u1EBFp theo l\xE0 g\xEC?",
      cta: "T\xECm hi\u1EC3u T\u0103ng tr\u01B0\u1EDFng"
    }
  },
  growth: {
    pill: "Orblit \u2014 c\xF4ng c\u1EE5 qu\u1EA3n l\xFD t\u0103ng tr\u01B0\u1EDFng nu\xF4i d\u01B0\u1EE1ng nh\xE2n vi\xEAn qua tr\xF2 chuy\u1EC7n",
    heroTitle: "T\u0103ng tr\u01B0\u1EDFng kh\xF4ng ph\u1EA3i s\u1EF1 ki\u1EC7n \u2014 n\xF3 t\xEDch l\u0169y.",
    heroSubtitle: "Kh\xF4ng ph\u1EA3i \u0111\xE1nh gi\xE1 m\u1ED7i n\u0103m m\u1ED9t l\u1EA7n \u2014 ch\xEDnh 1on1 h\u1EB1ng tu\u1EA7n t\u1EA1o n\xEAn t\u0103ng tr\u01B0\u1EDFng.",
    problem: {
      title: "V\xEC sao \u0111\xE1nh gi\xE1 \u1EDF c\xF4ng ty ta lu\xF4n \u1ED3n \xE0o?",
      quotes: [
        "Nh\xE2n vi\xEAn b\u1ECB \u0111\xE1nh gi\xE1 m\u1ED7i n\u0103m m\u1ED9t l\u1EA7n v\xE0 kh\xF4ng t\xE2m ph\u1EE5c",
        "Qu\u1EA3n l\xFD v\u1EAFt ki\u1EC7t c\u1EA3 n\u0103m th\xE0nh t\xEDch ngay tr\u01B0\u1EDBc bu\u1ED5i \u0111\xE1nh gi\xE1",
        "Tr\xF2 chuy\u1EC7n r\u1EA5t t\u1ED1t, nh\u01B0ng m\u1ED9t th\xE1ng sau \u0111\u1EA7u vi\u1EC7c b\u1ED1c h\u01A1i",
        "C\u1EA5p qu\u1EA3n l\xFD kh\xF4ng nh\u1EADn ra 'ngh\u1EC9 vi\u1EC7c trong im l\u1EB7ng' cho \u0111\u1EBFn khi qu\xE1 mu\u1ED9n"
      ]
    },
    cpm: {
      title: "Qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t li\xEAn t\u1EE5c",
      body: "C\xE1c c\xF4ng ty h\xE0ng \u0111\u1EA7u th\u1EBF gi\u1EDBi \u0111ang chuy\u1EC3n t\u1EEB \u0111\xE1nh gi\xE1 m\u1ED7i n\u0103m m\u1ED9t l\u1EA7n[d] sang m\xF4 h\xECnh [b=#1a1a1a]qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t li\xEAn t\u1EE5c[/b]."
    },
    cycle: {
      title: "T\u1EF1 \u0111\u1ED9ng h\xF3a v\xF2ng t\u0103ng tr\u01B0\u1EDFng",
      items: [
        {
          title: "Cam k\u1EBFt: \u0111\u1EA7u vi\u1EC7c",
          desc: "\u0110\u1EA7u vi\u1EC7c \u0111\u01B0\u1EE3c t\u1EA1o t\u1EF1 \u0111\u1ED9ng ngay sau bu\u1ED5i 1on1."
        },
        {
          title: "Theo d\xF5i: t\u1EF1 \u0111\u1ED9ng g\u1ECDi l\u1EA1i",
          desc: "Bu\u1ED5i 1on1 ti\u1EBFp theo t\u1EF1 \u0111\u1ED9ng l\u1EA5y l\u1EA1i \u0111\u1EA7u vi\u1EC7c l\u1EA7n tr\u01B0\u1EDBc."
        },
        {
          title: "T\xEDch l\u0169y: b\xE1o c\xE1o AI",
          desc: "Ph\xE2n t\xEDch \u0111\u1ED9ng l\u1EF1c, \u0111i\u1EC3m m\u1EA1nh\xB7y\u1EBFu v\xE0 nguy c\u01A1 ngh\u1EC9 vi\u1EC7c c\u1EE7a t\u1EEBng ng\u01B0\u1EDDi."
        },
        {
          title: "Ghi nh\u1EADn: kho th\xE0nh t\xEDch",
          desc: "Nh\u1EEFng \u0111\xF3ng g\xF3p r\u1EDDi r\u1EA1c \u0111\u01B0\u1EE3c s\u1EAFp x\u1EBFp th\xE0nh c\u0103n c\u1EE9 \u0111\xE1nh gi\xE1 t\u1EF1 \u0111\u1ED9ng."
        },
        {
          title: "Ph\xE2n b\u1ED1: qu\u1EA3n l\xFD theo nh\xF3m",
          desc: "Qu\u1EA3n l\xFD ph\xE2n b\u1ED1 v\xE0 t\u0103ng tr\u01B0\u1EDFng theo nh\xF3m d\u1EF1a tr\xEAn m\xF4 h\xECnh 9-Box."
        }
      ]
    },
    data: {
      title: "S\u1EF1 t\u0103ng tr\u01B0\u1EDFng c\u1EE7a m\u1ED9t ng\u01B0\u1EDDi, nh\xECn qua d\u1EEF li\u1EC7u",
      card: {
        avatar: "H",
        name: "Tr\u1EA7n Quang Huy",
        role: "Nh\xF3m Marketing \xB7 N\u0103m 2",
        goalLabel: "M\u1EE5c ti\xEAu tr\u1ECDng y\u1EBFu",
        goal: "Gi\xE0nh 10 kh\xE1ch h\xE0ng m\u1EDBi tr\u1ECDng y\u1EBFu trong Q3",
        progressLabel: "Ti\u1EBFn \u0111\u1ED9",
        progress: "\u0110\xE3 t\xECm 12 kh\xE1ch h\xE0ng m\u1EE5c ti\xEAu, ho\xE0n t\u1EA5t 8 bu\u1ED5i g\u1EB7p \u2014 \u0111\xFAng h\u01B0\u1EDBng",
        tasksLabel: "\u0110ang th\u1EF1c hi\u1EC7n",
        tasks: [
          {
            bold: "\u0110\u1EC1 xu\u1EA5t ri\xEAng cho C\xF4ng ty B",
            rest: " \u2014 b\u1ED5 sung n\u1ED9i dung, g\u1EEDi trong tu\u1EA7n n\xE0y"
          },
          {
            bold: "Kh\xE1ch h\xE0ng inbound m\u1EDBi",
            rest: " \u2014 g\u1ECDi theo d\xF5i (3 l\u1EA7n/tu\u1EA7n)"
          }
        ],
        motivationLabel: "\u0110\u1ED9ng l\u1EF1c ch\xEDnh",
        motivations: ["T\u1EF1 ch\u1EE7", "Th\xE0nh t\u1EF1u"],
        riskLabel: "Flight Risk",
        riskValue: "\u1ED4n \u0111\u1ECBnh (Low)"
      },
      features: [
        "N\u1EAFm [b=#1a1a1a]ti\u1EBFn \u0111\u1ED9 m\u1EE5c ti\xEAu theo th\u1EDDi gian th\u1EF1c[/b] c\u1EE7a Huy v\xE0 [b=#1a1a1a]c\xF4ng vi\u1EC7c[/b] anh \u1EA5y \u0111ang t\u1EADp trung \u2014 t\u1EA5t c\u1EA3 trong m\u1ED9t c\xE1i nh\xECn.",
        "T\xECm xem [b=#1a1a1a]\u0111\u1ED9ng l\u1EF1c[/b] n\xE0o th\xFAc \u0111\u1EA9y Huy v\xE0 ph\u1EA3n \xE1nh v\xE0o c\xE1ch ph\xE2n c\xF4ng c\xF4ng vi\u1EC7c.",
        "Ph\xE1t hi\u1EC7n khi [b=#1a1a1a]nguy c\u01A1 ngh\u1EC9 vi\u1EC7c[/b] b\u1EAFt \u0111\u1EA7u t\u0103ng v\xE0 ph\u1EA3n \u1EE9ng tr\u01B0\u1EDBc khi qu\xE1 mu\u1ED9n."
      ]
    },
    transformMember: {
      title: "Nh\xE2n vi\xEAn thay \u0111\u1ED5i ra sao khi 1on1 c\u1EE7a Orblit b\xE9n r\u1EC5",
      asis: "Ng\u01B0\u1EDDi th\u1EF1c thi th\u1EE5 \u0111\u1ED9ng[d] ng\u1EADp trong vi\u1EC7c \u0111\u01B0\u1EE3c giao",
      tobe: "Ng\u01B0\u1EDDi l\xE0m ch\u1EE7 s\u1EF1 ph\xE1t tri\u1EC3n, hi\u1EC3u \xFD ngh\u0129a[d] v\xE0 v\u1EBD n\xEAn l\u1ED9 tr\xECnh",
      points: [
        {
          before: "Lo l\u1EAFng l\xFAc \u0111\xE1nh gi\xE1,[d] \u0111o\xE1n m\xF2 \xFD c\u1EE7a s\u1EBFp",
          after: "S\u1EF1 \u1ED5n \u0111\u1ECBnh c\xF3 th\u1EC3 d\u1EF1 \u0111o\xE1n nh\u1EDD hi\u1EC3u r\xF5[d] v\u1ECB tr\xED v\xE0 k\u1EBFt qu\u1EA3 c\u1EE7a m\xECnh"
        },
        {
          before: "N\u1ED7i c\xF4 l\u1EADp khi n\u1ED7 l\u1EF1c[d] m\xE0 kh\xF4ng ai ghi nh\u1EADn",
          after: "S\u1EF1 g\u1EAFn k\u1EBFt v\xE0 hi\u1EC7u n\u0103ng khi[d] c\u1EA3m nh\u1EADn \u0111\xF3ng g\xF3p \u0111\u01B0\u1EE3c nh\xECn th\u1EA5y"
        }
      ]
    },
    transformHr: {
      title: "HR thay \u0111\u1ED5i ra sao khi 1on1 c\u1EE7a Orblit b\xE9n r\u1EC5",
      asis: "Ng\u01B0\u1EDDi qu\u1EA3n tr\u1ECB h\xE0i l\xF2ng[d] v\u1EDBi vi\u1EC7c v\u1EADn h\xE0nh quy \u0111\u1ECBnh",
      tobe: "\u0110\u1ED1i t\xE1c chi\u1EBFn l\u01B0\u1EE3c ti\u1EBFp s\u1EE9c[d] cho k\u1EBFt qu\u1EA3 th\u1EF1c \u0111\u1ECBa c\u1EE7a qu\u1EA3n l\xFD",
      points: [
        {
          before: "B\u1ECB \u0111\u1ED9ng tr\u01B0\u1EDBc \u0111\u01A1n xin ngh\u1EC9 \u0111\u1ED9t ng\u1ED9t c\u1EE7a nh\xE2n t\xE0i[d] ch\u1EE7 ch\u1ED1t",
          after: "Ph\xE1t hi\u1EC7n s\u1EDBm t\xEDn hi\u1EC7u r\u1EDDi b\u1ECF[d] v\xE0 ph\xF2ng v\u1EC7 ch\u1EE7 \u0111\u1ED9ng"
        },
        {
          before: "Kh\xF4ng c\xF3 c\xE1ch n\xE0o th\u1EA5y th\u1EF1c tr\u1EA1ng th\u1EADt s\u1EF1[d] \u1EDF hi\u1EC7n tr\u01B0\u1EDDng",
          after: "Ph\xE2n t\xEDch HR li\xEAn t\u1EE5c gi\xFAp c\u1EE7ng c\u1ED1[d] s\u1EE9c kh\u1ECFe v\xE0 ti\u1EC1m n\u0103ng t\u1ED5 ch\u1EE9c"
        }
      ]
    },
    next: {
      label: "Next Step",
      title: "Khi t\u1EEBng c\xE1 nh\xE2n t\u0103ng tr\u01B0\u1EDFng, t\u1ED5 ch\u1EE9c \u0111\u1ED5i thay. Ti\u1EBFp theo l\xE0 g\xEC?",
      cta: "T\xECm hi\u1EC3u Qu\u1EA3n l\xFD thay \u0111\u1ED5i"
    }
  },
  home: {
    hero: {
      badge: "Powered by AI",
      title: "Gi\u1EA3i ph\xE1p n\xE2ng n\u0103ng l\u1EF1c[m]l\xE3nh \u0111\u1EA1o qua [n]1on1[/n]",
      subtitle: "C\u1ED1t l\xF5i c\u1EE7a l\xE3nh \u0111\u1EA1o l\xE0 giao ti\u1EBFp.",
      primaryCta: "B\u1EAFt \u0111\u1EA7u mi\u1EC5n ph\xED",
      secondaryCta: "Nh\u1EADn brochure",
      browserUrl: "1on1.orblit.space",
      tabs: [
        {
          id: "skill",
          label: "Coaching c\xE1 nh\xE2n h\xF3a",
          title: "AI coaching & h\u01B0\u1EDBng d\u1EABn \u0111\u1ED1i tho\u1EA1i",
          desc: "Khi cu\u1ED9c tr\xF2 chuy\u1EC7n tr\u1EDF n\xEAn kh\xF3 kh\u0103n v\xEC c\xF3 nhi\u1EC1u bi\u1EBFn s\u1ED1 theo t\u1EEBng ng\u01B0\u1EDDi v\xE0 t\xECnh hu\u1ED1ng, AI g\u1EE3i \xFD c\xE2u h\u1ECFi [n]1on1[/n] ph\xF9 h\u1EE3p v\u1EDBi t\u1EEBng th\xE0nh vi\xEAn v\xE0 m\u1EDF \u0111\xFAng h\u01B0\u1EDBng.",
          sentence: "B\u1EA1n b\u0103n kho\u0103n n\xEAn h\u1ECFi g\xEC, hay cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\xE3 \u1ED5n ch\u01B0a?\n\u0110\u1EC3 AI \u0111\u1ECDc phong c\xE1ch \u0111\u1ED1i tho\u1EA1i c\u1EE7a b\u1EA1n v\xE0 \u0111\u01B0a ra coaching c\xE1 nh\xE2n h\xF3a."
        },
        {
          id: "time",
          label: "AI chu\u1EA9n b\u1ECB & t\u1ED5ng h\u1EE3p",
          title: "T\u1EF1 \u0111\u1ED9ng ghi tr\u1EF1c ti\u1EBFp m\u1ED9t ch\u1EA1m",
          desc: "T\u1EEB kh\xE2u chu\u1EA9n b\u1ECB \u0111\u1EBFn s\u1ED1 h\xF3a sau bu\u1ED5i h\u1ECDp, n\xF3 c\u1EAFt gi\u1EA3m v\xF4 s\u1ED1 chi ph\xED c\u01A1 h\u1ED9i \u0111\u1EC3 qu\u1EA3n l\xFD to\xE0n t\xE2m cho cu\u1ED9c tr\xF2 chuy\u1EC7n.",
          sentence: "Kh\xF4ng \u0111\u1EE7 th\u1EDDi gian chu\u1EA9n b\u1ECB v\xE0 t\u1ED5ng h\u1EE3p cho m\u1ED7i bu\u1ED5i h\u1ECDp?\nAI l\xE0m thay c\xE1c b\u01B0\u1EDBc r\u01B0\u1EDDm r\xE0 \u2014 t\u1EEB chu\u1EA9n b\u1ECB, ghi ch\xE9p \u0111\u1EBFn t\xF3m t\u1EAFt."
        },
        {
          id: "context",
          label: "H\u01B0\u1EDBng d\u1EABn theo th\xE0nh vi\xEAn",
          title: "AI g\u1EE3i \xFD ch\u01B0\u01A1ng tr\xECnh h\u1ECDp ri\xEAng",
          desc: "N\u1EAFm r\xF5 tr\u01B0\u1EDBc t\u1EEBng th\xE0nh vi\xEAn \u0111ang \u1EDF t\xECnh hu\u1ED1ng n\xE0o v\xE0 mu\u1ED1n chia s\u1EBB \u0111i\u1EC1u g\xEC quan tr\u1ECDng v\u1EDBi qu\u1EA3n l\xFD.",
          sentence: "B\u1EF1c b\u1ED9i v\xEC kh\xF4ng bi\u1EBFt d\u1EA1o n\xE0y th\xE0nh vi\xEAn \u0111ang tr\u0103n tr\u1EDF \u0111i\u1EC1u g\xEC?\nAI t\u1ED5ng h\u1EE3p th\xF4ng tin h\u1ECD \u0111\xE3 ghi v\xE0 \u0111\u1EC1 xu\u1EA5t h\u01B0\u1EDBng d\u1EABn k\xE8m c\xE2u h\u1ECFi."
        },
        {
          id: "continuity",
          label: "Qu\u1EA3n l\xFD li\xEAn t\u1EE5c",
          title: "T\u0103ng tr\u01B0\u1EDFng li\xEAn t\u1EE5c, kh\xF4ng tr\xF4i m\u1EA5t",
          desc: "\u0110\u1EC3 c\xE1c cam k\u1EBFt h\xE0nh \u0111\u1ED9ng sau bu\u1ED5i h\u1ECDp kh\xF4ng b\u1ECB b\u1ECF qu\xEAn hay tr\xF4i m\u1EA5t, n\xF3 k\u1EBFt n\u1ED1i m\u01B0\u1EE3t m\xE0 t\u1EDBi bu\u1ED5i sau v\xE0 t\u1EDBi c\xE1c ch\u1EC9 s\u1ED1 hi\u1EC7u qu\u1EA3/\u1ED5n \u0111\u1ECBnh t\u1ED5ng h\u1EE3p.",
          sentence: "Nh\u1EEFng cu\u1ED9c tr\xF2 chuy\u1EC7n qu\xFD gi\xE1 l\u1EA1i k\u1EBFt th\xFAc m\u1ED9t l\u1EA7n r\u1ED3i th\xF4i?\nCh\xFAng t\xF4i cung c\u1EA5p b\xE1o c\xE1o t\u1EEB d\u1EEF li\u1EC7u t\xEDch l\u0169y v\xE0 g\u1EE3i \xFD ch\u1EE7 \u0111\u1EC1 bu\u1ED5i sau."
        }
      ],
      sidebar: {
        menu: ["1on1", "B\xE1o c\xE1o th\xE0nh vi\xEAn", "9 Box", "Dashboard"],
        settings: "C\xE0i \u0111\u1EB7t",
        profileInitial: "H",
        profileName: "L\xEA Thu H\xE0"
      },
      skillPanel: {
        memberInitial: "M",
        memberName: "Nguy\u1EC5n Minh Anh",
        memberRatio: "53.6%",
        leaderAvatar: "\u{1F469}\u200D\u{1F4BC}",
        leaderName: "L\xEA Thu H\xE0",
        leaderRatio: "46.4%",
        ownerAvatarAlt: "Ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch action item",
        feedbackTitle: "Ph\u1EA3n h\u1ED3i cho qu\u1EA3n l\xFD",
        strengthLabel: "\u0110i\u1EC3m m\u1EA1nh",
        strengthTitle: "\u0110\xE3 t\u1EA1o \u0111\u1EE7 kh\xF4ng gian \u0111\u1EC3 th\xE0nh vi\xEAn n\xF3i",
        strengthBody: "Qu\u1EA3n l\xFD L\xEA Thu H\xE0 gi\u1EEF t\u1EF7 l\u1EC7 ph\xE1t bi\u1EC3u c\u1EE7a m\xECnh \u1EDF m\u1EE9c 46.4%, th\u1EA5p h\u01A1n th\xE0nh vi\xEAn (53.6%), n\xEAn \u0111\xE3 \u0111\u1EC3 l\u1EA1i \u0111\u1EE7 kh\xF4ng gian \u0111\u1EC3 th\xE0nh vi\xEAn ch\u1EE7 \u0111\u1ED9ng d\u1EABn d\u1EAFt cu\u1ED9c tr\xF2 chuy\u1EC7n. Kh\u1EA3 n\u0103ng gi\xFAp th\xE0nh vi\xEAn chia s\u1EBB tho\u1EA3i m\xE1i l\xE0 \u0111i\u1EC3m m\u1EA1nh r\u1EA5t r\xF5.",
        improvementLabel: "C\u1EA7n b\u1ED5 sung",
        improvementTitle: "Thi\u1EBFu ch\u1ED1t h\xE0nh \u0111\u1ED9ng ti\u1EBFp theo v\xE0 \u0111\u1ED3ng thu\u1EADn",
        improvementBody: "C\xE1c kh\xF3 kh\u0103n hay \xFD t\u01B0\u1EDFng n\u1EA3y ra trong cu\u1ED9c tr\xF2 chuy\u1EC7n ch\u01B0a \u0111\u01B0\u1EE3c ch\u1ED1t th\xE0nh h\xE0nh \u0111\u1ED9ng ti\u1EBFp theo (Action Item) c\u1EE5 th\u1EC3 v\xE0 th\u1ED1ng nh\u1EA5t r\xF5 r\xE0ng. \u1EDE bu\u1ED5i t\u1EDBi, h\xE3y th\u1EED \u0111\u1ECBnh ngh\u0129a c\xE1c cam k\u1EBFt c\u1EE5 th\u1EC3 \u0111\u1EC3 c\xF9ng th\u1EF1c hi\u1EC7n v\xE0 th\u1ED1ng nh\u1EA5t r\xF5 r\xE0ng."
      },
      timePanel: {
        summaryTitle: "AI t\xF3m t\u1EAFt",
        meetingTitle: "\u0110i\u1EC1u ch\u1EC9nh l\u1EA1i \u01B0u ti\xEAn d\u1EF1 \xE1n dashboard",
        coreLabel: "\u0110i\u1EC3m ch\xEDnh",
        coreBody: "Bu\u1ED5i h\u1ECDp x\xE1c nh\u1EADn \xE1p l\u1EF1c c\xF4ng vi\u1EC7c m\xE0 Minh Anh \u0111ang ch\u1ECBu do d\u1EF1 \xE1n c\u1EA3i t\u1ED5 dashboard v\xE0 nhi\u1EC1u vi\u1EC7c ch\u1EA1y song song, \u0111\u1ED3ng th\u1EDDi th\u1ED1ng nh\u1EA5t c\u1EA7n s\u1EAFp x\u1EBFp l\u1EA1i \u01B0u ti\xEAn \u0111\u1EC3 t\u0103ng hi\u1EC7u qu\u1EA3.",
        decisionLabel: "Quy\u1EBFt \u0111\u1ECBnh",
        decisions: [
          "Minh Anh s\u1EBD d\u1EABn d\u1EAFt vi\u1EC7c s\u1EAFp l\u1EA1i \u01B0u ti\xEAn cho dashboard.",
          "N\u1EBFu qu\xE1 t\u1EA3i, s\u1EBD b\u1ED1 tr\xED th\xEAm ng\u01B0\u1EDDi h\u1ED7 tr\u1EE3."
        ],
        supportLabel: "V\u1EA5n \u0111\u1EC1 v\xE0 h\u1ED7 tr\u1EE3 c\u1EA7n thi\u1EBFt",
        issueTag: "[V\u1EA5n \u0111\u1EC1]",
        issueBody: "Qu\xE1 nhi\u1EC1u \u0111\u1EA7u vi\u1EC7c dashboard di\u1EC5n ra c\xF9ng l\xFAc g\xE2y ph\xE2n t\xE1n t\u1EADp trung v\xE0 \xE1p l\u1EF1c ti\u1EBFn \u0111\u1ED9.",
        supportTag: "[H\u1ED7 tr\u1EE3]",
        supportBody: "Trong qu\xE1 tr\xECnh s\u1EAFp l\u1EA1i \u01B0u ti\xEAn, s\u1EBD \u0111i\u1EC1u ph\u1ED1i h\u1ED7 tr\u1EE3 t\u1EEB th\xE0nh vi\xEAn kh\xE1c khi c\u1EA7n.",
        actionTitle: "H\xE0nh \u0111\u1ED9ng m\u1EDBi",
        actionAdd: "Th\xEAm h\xE0nh \u0111\u1ED9ng m\u1EDBi",
        actionItems: [
          {
            text: "Kh\u1EAFc ph\u1EE5c l\u1ED7i ngu\u1ED3n d\u1EEF li\u1EC7u c\u1EE7a dashboard OKR",
            ai: false
          },
          {
            text: "T\u1EA1o m\u1EABu t\xE0i li\u1EC7u chu\u1EA9n",
            ai: true
          },
          {
            text: "R\xE0 so\xE1t chu k\u1EF3 h\u1ECDp \u0111i\u1EC1u ph\u1ED1i l\u1ECBch gi\u1EEFa c\xE1c team",
            ai: true
          },
          {
            text: "\u0110\xE1nh gi\xE1 ti\u1EBFn \u0111\u1ED9 c\u1ED9t m\u1ED1c dashboard theo tu\u1EA7n",
            ai: true
          }
        ]
      },
      contextPanel: {
        cards: [
          {
            label: "C\xE1ch giao ti\u1EBFp ph\xF9 h\u1EE3p",
            value: "Nhi\u1EC1u b\u1ED1i c\u1EA3nh chi ti\u1EBFt"
          },
          {
            label: "Ph\u1EA3n h\u1ED3i hi\u1EC7u qu\u1EA3",
            value: "\u0110\u01B0a gi\u1EA3i ph\xE1p c\u1EE5 th\u1EC3"
          },
          {
            label: "H\u01B0\u1EDBng ngh\u1EC1 nghi\u1EC7p",
            value: "Con \u0111\u01B0\u1EDDng l\xE3nh \u0111\u1EA1o"
          },
          {
            label: "\u0110\u1ED9ng l\u1EF1c ch\xEDnh",
            value: "S\u1EF1 ghi nh\u1EADn"
          }
        ],
        conditionLabel: "\u{1F464} Tr\u1EA1ng th\xE1i:",
        conditionValue: "M\u1EE9c \u0111\u1ED9 t\u1EADp trung cao v\xE0 n\u0103ng l\u01B0\u1EE3ng t\xEDch c\u1EF1c",
        topicLabel: "\u{1F3AF} Ch\u1EE7 \u0111\u1EC1 ch\xEDnh:",
        topicValue: "Ti\u1EBFn \u0111\u1ED9 v\xE0 ph\xE2n t\xEDch n\xE2ng c\u1EA5p chatbot",
        progressTitle: "1. T\xECnh h\xECnh c\xF4ng vi\u1EC7c ch\xEDnh",
        progressItems: [
          "\u0110\xE3 ho\xE0n t\u1EA5t QA cho ph\u1EA7n n\xE2ng c\u1EA5p AI, \u0111ang tri\u1EC3n khai v\xE0 \u1ED5n \u0111\u1ECBnh tr\xEAn m\xE1y ch\u1EE7 ch\xEDnh",
          "\u0110\xE3 ph\xE1t h\xE0nh t\xEDnh n\u0103ng c\u1ED1t l\xF5i c\u1EE7a chatbot v\xE0 \u0111ang th\u1EED nghi\u1EC7m th\xEAm c\xE1c k\u1ECBch b\u1EA3n m\u1EDBi"
        ],
        challengeTitle: "2. Kh\xF3 kh\u0103n \u0111ang g\u1EB7p trong d\u1EF1 \xE1n",
        challengeItems: [
          "Y\xEAu c\u1EA7u g\u1EA5p v\xE0 th\u1EDDi gian chu\u1EA9n b\u1ECB ng\u1EAFn t\u1EA1o ra \xE1p l\u1EF1c c\xF4ng vi\u1EC7c.",
          "Thi\u1EBFu ti\xEAu ch\xED \u0111\xE1nh gi\xE1 ch\u1EA5t l\u01B0\u1EE3ng prompt l\xE0m gi\u1EDBi h\u1EA1n vi\u1EC7c theo d\xF5i \u0111\u1ED9 \u1ED5n \u0111\u1ECBnh."
        ],
        guideTitle: "AI h\u01B0\u1EDBng d\u1EABn cu\u1ED9c h\u1ECDp",
        insightTitle: "Insight ch\xEDnh",
        insightBody: "D\xF9 \u0111ang t\u1EA1o ra k\u1EBFt qu\u1EA3 c\xF4ng vi\u1EC7c t\u1ED1t, vi\u1EC7c thi\u1EBFu chu\u1EA9n x\xE1c nh\u1EADn r\xF5 r\xE0ng \u0111ang t\u1EA1o ra c\u1EA3m gi\xE1c b\u1EA5t an \xE2m th\u1EA7m.",
        recommendTitle: "Khuy\u1EBFn ngh\u1ECB",
        recommendBody: "H\xE3y b\u1EAFt \u0111\u1EA7u b\u1EB1ng l\u1EDDi ch\xFAc m\u1EEBng vi\u1EC7c ph\xE1t h\xE0nh, sau \u0111\xF3 h\u1ECFi v\u1EC1 ph\u1EA3n \u1EE9ng c\u1EE7a ng\u01B0\u1EDDi d\xF9ng v\xE0 l\u1EAFng nghe k\u1EF9 c\xE1c \u0111i\u1EC3m \u0111ang g\xE2y kh\xF3."
      },
      continuityPanel: {
        title: "B\xE1o c\xE1o c\u1EE7a Nguy\u1EC5n Minh Anh",
        flightRiskTitle: "Nguy c\u01A1 ngh\u1EC9 vi\u1EC7c",
        flightRiskLevel: "Th\u1EA5p",
        flightRiskBody: "Minh Anh \u0111ang ch\u1EE7 \u0111\u1ED9ng x\xE1c \u0111\u1ECBnh l\u1EA1i \u01B0u ti\xEAn c\u1EE7a d\u1EF1 \xE1n dashboard v\xE0 h\u01B0\u1EDBng t\u1EDBi \u0111\xF3ng g\xF3p r\xF5 r\xE0ng, n\xEAn nguy c\u01A1 r\u1EDDi \u0111i hi\u1EC7n r\u1EA5t th\u1EA5p.",
        motivationTitle: "Y\u1EBFu t\u1ED1 t\u1EA1o \u0111\u1ED9ng l\u1EF1c",
        motivationTags: [
          "Quy\u1EC1n ch\u1EE7 \u0111\u1ED9ng",
          "T\xE1c \u0111\u1ED9ng r\xF5 r\xE0ng",
          "C\u01A1 h\u1ED9i h\u1ECDc h\u1ECFi",
          "\u0110\u01B0\u1EE3c l\xE3nh \u0111\u1EA1o ghi nh\u1EADn"
        ],
        demotivationTitle: "Y\u1EBFu t\u1ED1 l\xE0m gi\u1EA3m \u0111\u1ED9ng l\u1EF1c",
        demotivationTags: [
          "M\u1EE5c ti\xEAu m\u01A1 h\u1ED3",
          "Thi\u1EBFu ngu\u1ED3n l\u1EF1c",
          "Giao ti\u1EBFp k\xE9m hi\u1EC7u qu\u1EA3"
        ],
        strengthTitle: "\u0110i\u1EC3m m\u1EA1nh",
        strengthTags: [
          "Gi\u1EA3i quy\u1EBFt v\u1EA5n \u0111\u1EC1",
          "Hi\u1EC3u s\u1EA3n ph\u1EA9m",
          "Th\xE1i \u0111\u1ED9 ch\u1EE7 \u0111\u1ED9ng"
        ],
        weaknessTitle: "R\u1EE7i ro",
        weaknessTags: [
          "Qu\xE1 nhi\u1EC1u tr\xE1ch nhi\u1EC7m \u0111i\u1EC1u ph\u1ED1i",
          "M\u1EE5c ti\xEAu tr\u1EEBu t\u01B0\u1EE3ng",
          "\xC1p l\u1EF1c ti\u1EBFn \u0111\u1ED9"
        ]
      }
    },
    scrollReveal: {
      lines: [
        "T\u1EA1i Orblit, [n]1on1[/n] kh\xF4ng ch\u1EC9 l\xE0 bu\u1ED5i g\u1EB7p m\u1EB7t.",
        "B\u1ED1i c\u1EA3nh c\u1EE7a cu\u1ED9c tr\xF2 chuy\u1EC7n tr\u1EDF th\xE0nh d\u1EEF li\u1EC7u,",
        "v\xE0 ch\xEDnh d\u1EEF li\u1EC7u \u0111\xF3 d\u1EABn d\u1EAFt",
        "s\u1EF1 t\u0103ng tr\u01B0\u1EDFng r\xF5 r\xE0ng nh\u1EA5t c\u1EE7a t\u1ED5 ch\u1EE9c."
      ],
      mobileBreakWords: ["Orblit,"]
    },
    coreProblem: {
      title: "C\xE1c bu\u1ED5i [n]1on1[/n] c\u1EE7a b\u1EA1n[m]\u0111\xE3 th\u1EADt s\u1EF1 hi\u1EC7u qu\u1EA3 ch\u01B0a?",
      subtitle: "\u0110\xE2y l\xE0 ho\u1EA1t \u0111\u1ED9ng r\u1EA5t quan tr\u1ECDng v\u1EDBi c\u1EA3 qu\u1EA3n l\xFD l\u1EABn th\xE0nh vi\xEAn, nh\u01B0ng trong th\u1EF1c t\u1EBF l\u1EA1i d\u1EC5 th\u1EA5t b\u1EA1i v\xEC nhi\u1EC1u r\xE0o c\u1EA3n.",
      items: [
        {
          title: "C\xF3 ph\u1EA3i ch\xFAng v\u1EABn ch\u1EC9 d\u1EEBng \u1EDF b\xE1o c\xE1o hi\u1EC7u su\u1EA5t h\xECnh th\u1EE9c?",
          desc: "H\u01A1n 80% c\xE1c bu\u1ED5i [n]1on1[/n] v\u1EABn ch\u1EC9 \u1EDF m\u1EE9c trao \u0111\u1ED5i hi\u1EC7u su\u1EA5t theo qu\xFD,[m] v\xE0 hi\u1EC7u qu\u1EA3 gi\u1EA3m r\u1EA5t nhanh khi chu k\u1EF3 k\xE9o d\xE0i."
        },
        {
          title: "C\xF3 ph\u1EA3i g\xE1nh n\u1EB7ng c\u1EE7a qu\u1EA3n l\xFD v\u1EABn \u0111ang b\u1ECB b\u1ECF m\u1EB7c?",
          desc: "M\u1ED7i qu\u1EA3n l\xFD c\xF3 th\u1EC3 ti\xEAu t\u1ED1n t\u1EDBi 210 gi\u1EDD m\u1ED7i n\u0103m cho vi\u1EC7c h\xE0nh ch\xEDnh xoay quanh [n]1on1[/n],[m] l\u1EA5y m\u1EA5t th\u1EDDi gian d\xE0nh cho l\xE3nh \u0111\u1EA1o th\u1EF1c s\u1EF1."
        },
        {
          title: "D\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n v\u1EABn ph\xE2n m\u1EA3nh v\xE0 d\u1EC5 bi\u1EBFn m\u1EA5t?",
          desc: "H\u01A1n 70% doanh nghi\u1EC7p ch\u01B0a c\xF3 h\u1EC7 th\u1ED1ng d\u1EEF li\u1EC7u h\xF3a [n]1on1[/n] \u0111\xFAng ngh\u0129a,[m] v\xE0 \u0111i\u1EC1u \u0111\xF3 k\xE9o m\u1EE9c h\xE0i l\xF2ng c\u1EE7a ng\u01B0\u1EDDi d\xF9ng xu\u1ED1ng d\u01B0\u1EDBi 10%."
        }
      ]
    },
    flowTabs: {
      title: "V\xF2ng l\u1EB7p t\u0103ng tr\u01B0\u1EDFng[m]li\xEAn t\u1EE5c cho [n]1on1[/n]",
      subtitle: "Xem to\xE0n b\u1ED9 chu k\u1EF3 t\u0103ng tr\u01B0\u1EDFng trong m\u1ED9t khung nh\xECn, t\u1EEB chu\u1EA9n b\u1ECB tr\u01B0\u1EDBc h\u1ECDp \u0111\u1EBFn theo d\xF5i sau h\u1ECDp, m\xE0 kh\xF4ng b\u1ECB \u0111\u1EE9t m\u1EA1ch.",
      sections: [
        {
          step: "STEP 01",
          title: "Tr\u01B0\u1EDBc h\u1ECDp",
          subtitle: "AI h\u1ED7 tr\u1EE3 chu\u1EA9n b\u1ECB t\u1ED1t h\u01A1n",
          description: "Khi l\u1ECBch [n]1on1[/n] \u0111\u01B0\u1EE3c t\u1EA1o, AI gom l\u1EA1i b\u1ED1i c\u1EA3nh quan tr\u1ECDng c\u1EE7a th\xE0nh vi\xEAn v\xE0 \u0111\u01B0a cho qu\u1EA3n l\xFD c\xE2u h\u1ECFi c\xF9ng h\u01B0\u1EDBng d\u1EABn ph\xF9 h\u1EE3p \u0111\u1EC3 d\xF9ng ngay.",
          features: [
            "C\u1EA3nh b\xE1o l\u1ECBch tr\u1EF1c ti\u1EBFp",
            "AI chatbot t\u1ED5ng h\u1EE3p b\u1ED1i c\u1EA3nh",
            "C\xE2u h\u1ECFi c\xE1 nh\xE2n h\xF3a"
          ],
          nodes: [
            {
              tagLabel: "Trigger",
              tagStatus: "Scheduled",
              tagColor: "gray",
              icon: "solar:calendar-date-bold-duotone",
              title: "Ph\xE1t hi\u1EC7n l\u1ECBch v\xE0 g\u1EEDi nh\u1EAFc",
              subtitle: "H\u1EC7 th\u1ED1ng nh\u1EADn bi\u1EBFt l\u1ECBch [n]1on1[/n] v\xE0 g\u1EEDi th\xF4ng b\xE1o ngay.",
              muted: true
            },
            {
              tagLabel: "AI Chatbot",
              tagStatus: "Analyzing",
              tagColor: "blue",
              icon: "solar:chat-square-like-bold-duotone",
              title: "AI chatbot gom b\u1ED1i c\u1EA3nh th\xE0nh vi\xEAn",
              subtitle: "L\u1ECBch s\u1EED trao \u0111\u1ED5i v\xE0 t\xEDn hi\u1EC7u m\u1EDBi nh\u1EA5t \u0111\u01B0\u1EE3c t\xF3m l\u1EA1i th\xE0nh m\u1ED9t b\u1EA3n briefing ng\u1EAFn."
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "H\u01B0\u1EDBng d\u1EABn v\xE0 c\xE2u h\u1ECFi ph\xF9 h\u1EE3p",
              subtitle: "H\u1EC7 th\u1ED1ng g\u1EE3i \xFD agenda v\xE0 c\xE2u h\u1ECFi ph\xF9 h\u1EE3p nh\u1EA5t cho bu\u1ED5i h\u1ECDp n\xE0y.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 02",
          title: "Trong h\u1ECDp",
          subtitle: "T\u1EADp trung tr\u1ECDn v\u1EB9n v\xE0o \u0111\u1ED1i tho\u1EA1i",
          description: "V\xEC AI t\u1EF1 x\u1EED l\xFD vi\u1EC7c ghi ch\xE9p, qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn kh\xF4ng c\xF2n b\u1ECB k\xE9o \u0111i b\u1EDFi b\xE0n ph\xEDm m\xE0 c\xF3 th\u1EC3 t\u1EADp trung v\xE0o l\u1EAFng nghe v\xE0 \u0111\u1ED1i tho\u1EA1i th\u1EADt s\u1EF1.",
          features: [
            "Ghi \xE2m cu\u1ED9c h\u1ECDp",
            "Kh\xF4ng gian ghi ch\xFA chung",
            "STT tr\u1EF1c ti\u1EBFp"
          ],
          nodes: [
            {
              tagLabel: "Action",
              tagStatus: "Live",
              tagColor: "red",
              icon: "solar:play-circle-bold-duotone",
              title: "B\u1EAFt \u0111\u1EA7u ghi cu\u1ED9c h\u1ECDp",
              subtitle: "C\xF3 th\u1EC3 b\u1EAFt \u0111\u1EA7u ghi \xE2m ho\u1EB7c ghi h\xECnh ngay khi bu\u1ED5i h\u1ECDp m\u1EDF ra."
            },
            {
              tagLabel: "Editor",
              tagStatus: "Syncing",
              tagColor: "blue",
              icon: "solar:pen-new-round-bold-duotone",
              title: "Kh\xF4ng gian ghi chung",
              subtitle: "Qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn c\xF3 th\u1EC3 c\xF9ng ghi l\u1EA1i ch\u1EE7 \u0111\u1EC1 quan tr\u1ECDng theo th\u1EDDi gian th\u1EF1c.",
              muted: true
            },
            {
              tagLabel: "AI Engine",
              tagStatus: "STT",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI chuy\u1EC3n l\u1EDDi n\xF3i th\xE0nh v\u0103n b\u1EA3n",
              subtitle: "AI ghi l\u1EA1i n\u1ED9i dung cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u1EC3 kh\xF4ng m\u1EA5t \u0111i \u0111i\u1EC1u quan tr\u1ECDng n\xE0o.",
              accent: true
            }
          ]
        },
        {
          step: "STEP 03",
          title: "Sau h\u1ECDp",
          subtitle: "C\xF3 ngay t\xF3m t\u1EAFt v\xE0 insight",
          description: "Ngay khi bu\u1ED5i h\u1ECDp k\u1EBFt th\xFAc, AI ho\xE0n thi\u1EC7n b\u1EA3n t\xF3m t\u1EAFt t\u1EEB to\xE0n b\u1ED9 cu\u1ED9c tr\xF2 chuy\u1EC7n v\xE0 h\u1ED7 tr\u1EE3 t\u1EEB vi\u1EC7c t\u1EA1o action item \u0111\u1EBFn coaching cho qu\u1EA3n l\xFD.",
          features: [
            "AI t\xF3m t\u1EAFt",
            "Action item",
            "Coaching l\xE3nh \u0111\u1EA1o"
          ],
          timelineNodes: [
            {
              tagLabel: "AI Analyst",
              tagStatus: "Structured",
              tagColor: "blue",
              icon: "solar:document-text-bold-duotone",
              title: "AI t\xF3m t\u1EAFt v\xE0 c\u1EA5u tr\xFAc n\u1ED9i dung",
              subtitle: "To\xE0n b\u1ED9 cu\u1ED9c tr\xF2 chuy\u1EC7n \u0111\u01B0\u1EE3c n\xE9n l\u1EA1i th\xE0nh m\u1ED9t b\u1EA3n t\xF3m t\u1EAFt d\u1EC5 d\xF9ng.",
              marker: "dot"
            },
            {
              tagLabel: "Action Items",
              tagStatus: "Organized",
              tagColor: "green",
              icon: "solar:check-square-bold-duotone",
              title: "T\u1EF1 \u0111\u1ED9ng r\xFAt ra h\xE0nh \u0111\u1ED9ng",
              subtitle: "C\xE1c b\u01B0\u1EDBc ti\u1EBFp theo \u0111\u01B0\u1EE3c t\xE1ch ra v\xE0 g\xE1n ng\u01B0\u1EDDi ph\u1EE5 tr\xE1ch t\u1EF1 \u0111\u1ED9ng.",
              marker: "mic"
            },
            {
              tagLabel: "AI Coach",
              tagStatus: "Coached",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "AI coaching cho qu\u1EA3n l\xFD",
              subtitle: "K\u1ECBch b\u1EA3n [n]1on1[/n] v\xE0 c\xE1ch giao ti\u1EBFp \u0111\u01B0\u1EE3c ph\xE2n t\xEDch th\xE0nh ph\u1EA3n h\u1ED3i c\u1EE5 th\u1EC3.",
              marker: "dot",
              accent: true
            }
          ]
        },
        {
          step: "STEP 04",
          title: "Duy tr\xEC",
          subtitle: "V\xF2ng l\u1EB7p gi\xFAp t\u0103ng tr\u01B0\u1EDFng kh\xF4ng ng\u1EAFt qu\xE3ng",
          description: "Khi d\u1EEF li\u1EC7u h\u1ECDp \u0111\u01B0\u1EE3c t\xEDch l\u0169y, n\xF3 tr\u1EDF th\xE0nh t\xE0i s\u1EA3n. B\xE1o c\xE1o th\xE0nh vi\xEAn v\xE0 dashboard \u0111\u01B0\u1EE3c t\u1EA1o t\u1EEB \u0111\xF3 r\u1ED3i quay ng\u01B0\u1EE3c l\u1EA1i h\u1ED7 tr\u1EE3 ngay cho bu\u1ED5i [n]1on1[/n] ti\u1EBFp theo.",
          features: [
            "B\xE1o c\xE1o insight th\xE0nh vi\xEAn",
            "Dashboard t\u1ED5 ch\u1EE9c",
            "T\u1EF1 n\u1ED1i sang bu\u1ED5i h\u1ECDp sau"
          ],
          topBadge: "D\u1EEF li\u1EC7u h\u1ECDp [n]1on1[/n] t\xEDch l\u0169y",
          bottomBadge: "Insight quay l\u1EA1i h\u1ED7 tr\u1EE3 bu\u1ED5i [n]1on1[/n] ti\u1EBFp theo",
          nodes: [
            {
              tagLabel: "AI Insight",
              tagStatus: "Generated",
              tagColor: "purple",
              icon: "solar:magic-stick-3-bold-duotone",
              title: "B\xE1o c\xE1o insight th\xE0nh vi\xEAn",
              subtitle: "L\u1ECBch s\u1EED h\u1ECDp \u0111\u01B0\u1EE3c k\u1EBFt h\u1EE3p th\xE0nh b\xE1o c\xE1o v\u1EC1 \u0111\u1ED9ng l\u1EF1c, \u0111i\u1EC3m m\u1EA1nh, kho\u1EA3ng tr\u1ED1ng v\xE0 r\u1EE7i ro.",
              accent: true
            },
            {
              tagLabel: "Dashboard",
              tagStatus: "Updated",
              tagColor: "blue",
              icon: "solar:chart-square-bold-duotone",
              title: "Dashboard t\u1ED5 ch\u1EE9c",
              subtitle: "Ti\u1EBFn \u0111\u1ED9 h\u1ECDp v\xE0 ch\u1EC9 s\u1ED1 l\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c tr\u1EF1c quan h\xF3a theo team v\xE0 b\u1ED9 ph\u1EADn."
            }
          ]
        }
      ]
    },
    coreBenefits: {
      title: "\u0110i\u1EC1u b\u1EAFt \u0111\u1EA7u c\xF9ng Orblit[m]l\xE0 m\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n l\xE0nh m\u1EA1nh h\u01A1n",
      subtitle: "N\xF3 t\u1EA1o ra thay \u0111\u1ED5i th\u1EF1c ch\u1EA5t v\xE0 c\xF3 \xFD ngh\u0129a cho qu\u1EA3n l\xFD, th\xE0nh vi\xEAn v\xE0 HR.",
      items: [
        {
          target: "Qu\u1EA3n l\xFD",
          title: "Ng\u01B0\u1EDDi coach cho t\u0103ng tr\u01B0\u1EDFng",
          desc: "Qu\u1EA3n l\xFD tho\xE1t kh\u1ECFi g\xE1nh n\u1EB7ng chu\u1EA9n b\u1ECB v\xE0 ghi ch\xE9p \u0111\u1EC3 t\u1EADp trung ho\xE0n to\xE0n v\xE0o vi\u1EC7c l\u1EAFng nghe v\xE0 th\u1EA5u hi\u1EC3u th\xE0nh vi\xEAn."
        },
        {
          target: "Th\xE0nh vi\xEAn",
          title: "B\u1EA1n \u0111\u1ED3ng h\xE0nh t\u0103ng tr\u01B0\u1EDFng",
          desc: "Th\xE0nh vi\xEAn b\u1EDBt s\u1EE3 b\u1ECB \u0111\xE1nh gi\xE1, c\xF3 th\u1EC3 n\xF3i th\u1EADt v\u1EC1 s\u1EF1 nghi\u1EC7p v\xE0 tr\u0103n tr\u1EDF c\u1EE7a m\xECnh, \u0111\u1ED3ng th\u1EDDi c\u1EA3m th\u1EA5y an to\xE0n h\u01A1n v\u1EC1 m\u1EB7t t\xE2m l\xFD."
        },
        {
          target: "HR",
          title: "Ki\u1EBFn tr\xFAc s\u01B0 t\u1ED5 ch\u1EE9c d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u",
          desc: "HR kh\xF4ng ch\u1EC9 d\u1EEBng \u1EDF vi\u1EC7c theo d\xF5i s\u1ED1 bu\u1ED5i h\u1ECDp m\xE0 c\xF3 th\u1EC3 v\u1EADn h\xE0nh t\u1ED5 ch\u1EE9c m\u1ED9t c\xE1ch ch\u1EE7 \u0111\u1ED9ng v\xE0 chi\u1EBFn l\u01B0\u1EE3c h\u01A1n nh\u1EDD d\u1EEF li\u1EC7u."
        }
      ]
    },
    customerInterviews: {
      badge: "Customer story",
      companyPrefix: "Kh\xE1ch h\xE0ng",
      goToSlide: "\u0110i t\u1EDBi slide {{number}}",
      items: [
        {
          company: "Hansol Home Deco",
          person: "Gyuho Seo, Tr\u01B0\u1EDFng nh\xF3m",
          title: "Thi\u1EBFt l\u1EADp v\u0103n h\xF3a one-on-one k\u1EBFt n\u1ED1i t\u1EA7m nh\xECn v\xE0 chi\u1EBFn l\u01B0\u1EE3c",
          body: "\u0110\u1EC3 t\u1EA7m nh\xECn v\xE0 chi\u1EBFn l\u01B0\u1EE3c c\u1EE7a t\u1ED5 ch\u1EE9c \u0111\u01B0\u1EE3c chuy\u1EC3n h\xF3a th\xE0nh h\xE0nh \u0111\u1ED9ng c\u1EE7a t\u1EEBng th\xE0nh vi\xEAn,\nc\u1EA7n c\xF3 s\u1EF1 \u0111\u1ED3ng b\u1ED9 m\u1EE5c ti\xEAu th\xF4ng qua nh\u1EEFng cu\u1ED9c tr\xF2 chuy\u1EC7n one-on-one li\xEAn t\u1EE5c.\nHansol Home Deco s\u1EED d\u1EE5ng Orblit \u0111\u1EC3 t\u0103ng c\u01B0\u1EDDng n\u0103ng l\u1EF1c one-on-one c\u1EE7a ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o,\nthi\u1EBFt l\u1EADp v\u0103n h\xF3a tr\xF2 chuy\u1EC7n \u0111\u1ECBnh k\u1EF3 v\u1EDBi c\xE1c th\xE0nh vi\xEAn,\nt\u1EA1o n\u1EC1n t\u1EA3ng \u0111\u1EC3 chi\u1EBFn l\u01B0\u1EE3c c\u1EE7a t\u1ED5 ch\u1EE9c \u0111\u01B0\u1EE3c th\u1EF1c thi t\u1EA1i hi\u1EC7n tr\u01B0\u1EDDng."
        },
        {
          company: "Undermilli",
          person: "Hojong Lee, CEO",
          title: "Bi\u1EBFn cu\u1ED9c tr\xF2 chuy\u1EC7n m\u01A1 h\u1ED3 c\u1EE7a qu\u1EA3n l\xFD m\u1EDBi th\xE0nh t\u01B0\u01A1ng t\xE1c hi\u1EC7u qu\u1EA3",
          body: "N\xF3i chuy\u1EC7n phi\u1EBFm th\xEC d\u1EC5,\nnh\u01B0ng '\u0111\u1ED1i tho\u1EA1i v\xEC t\u0103ng tr\u01B0\u1EDFng v\xE0 hi\u1EC7u su\u1EA5t' l\u1EA1i m\u01A1 h\u1ED3 v\xE0 kh\xF3 h\u01A1n ta t\u01B0\u1EDFng.\nOrblit \u0111\xE3 d\u1EF1ng khung cho nh\u1EEFng cu\u1ED9c \u0111\u1ED1i tho\u1EA1i hi\u1EC7u qu\u1EA3\nd\u1EF1a tr\xEAn th\xF4ng tin th\xE0nh vi\xEAn \u0111\u1EA7y \u0111\u1EE7, kh\xF4ng b\u1ECF s\xF3t.\n\u0110\u1EB7c bi\u1EC7t, AI coaching d\u1EF1a tr\xEAn ph\xE1t ng\xF4n th\u1EF1c t\u1EBF\n\u0111\xE3 tr\u1EDF th\xE0nh ch\u1ED7 d\u1EF1a cho s\u1EF1 t\u1EF1 nh\u1EADn th\u1EE9c v\xE0 t\u0103ng tr\u01B0\u1EDFng c\u1EE7a c\xE1c qu\u1EA3n l\xFD m\u1EDBi."
        },
        {
          company: "Grassmedi",
          person: "Jinsik Choi, CEO",
          title: "Bi\u1EBFn vi\u1EC7c qu\u1EA3n l\xFD m\u01A1 h\u1ED3 th\xE0nh gi\u1EA3i ph\xE1p ch\u1EAFc ch\u1EAFn nh\u1EA5t",
          body: "N\u1ED7i tr\u0103n tr\u1EDF l\u1EDBn nh\u1EA5t c\u1EE7a ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o\u2014'nh\xE2n s\u1EF1' v\xE0 '\u1EE7y quy\u1EC1n'\u2014\nt\xECm \u0111\u01B0\u1EE3c l\u1EDDi gi\u1EA3i r\xF5 r\xE0ng nh\u1EA5t \u1EDF c\xE1c bu\u1ED5i 1on1 v\u1EDBi th\xE0nh vi\xEAn.\nGiao ti\u1EBFp th\u1EADt g\u1EA7n \u0111\u1EC3 \u0111o l\u01B0\u1EDDng hi\u1EC7u su\u1EA5t v\xE0 h\u1ED7 tr\u1EE3 t\u0103ng tr\u01B0\u1EDFng,\nOrblit \u0111\xE3 bi\u1EBFn vi\u1EC7c qu\u1EA3n l\xFD t\u1EEBng m\u01A1 h\u1ED3 th\xE0nh gi\u1EA3i ph\xE1p ch\u1EAFc ch\u1EAFn v\xE0 c\u1EE5 th\u1EC3 nh\u1EA5t."
        }
      ]
    },
    whatIsOrblit: {
      title: "Orblit kh\xF4ng ch\u1EC9 l\xE0[m]m\u1ED9t c\xF4ng c\u1EE5 t\u1EF1 \u0111\u1ED9ng h\xF3a.",
      body: "\u0110\xF3 l\xE0 c\xF4ng c\u1EE5 giao ti\u1EBFp gi\xFAp cu\u1ED9c tr\xF2 chuy\u1EC7n l\xE0nh m\u1EA1nh lan t\u1ECFa trong t\u1ED5 ch\u1EE9c,[m] l\xE0 c\xF4ng c\u1EE5 t\u0103ng tr\u01B0\u1EDFng bi\u1EBFn \u0111\u1ED1i tho\u1EA1i th\xE0nh ti\u1EBFn b\u1ED9,[m] v\xE0 l\xE0 c\xF4ng c\u1EE5 thay \u0111\u1ED5i \u0111\u01B0a t\u1EA7m nh\xECn l\xE3nh \u0111\u1EA1o t\u1EDBi tuy\u1EBFn \u0111\u1EA7u.",
      nodeTitle: "L\xE3nh \u0111\u1EA1o \u0111\u01B0\u1EE3c d\u1EABn d\u1EAFt b\u1EDFi [n]1on1[/n]",
      closing: "T\u1EA5t c\u1EA3 nh\u1EEFng \u0111i\u1EC1u n\xE0y b\u1EAFt \u0111\u1EA7u t\u1EEB[m]n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o v\u1EEFng ch\u1EAFc d\u1EF1a tr\xEAn [n]1on1[/n].",
      cards: [
        {
          to: "/why/communication",
          title: "Giao ti\u1EBFp",
          desc: "Gi\xFAp cu\u1ED9c tr\xF2 chuy\u1EC7n l\xE0nh m\u1EA1nh lan kh\u1EAFp t\u1ED5 ch\u1EE9c. Qu\u1EA3n l\xFD bi\u1EBFt n\xEAn h\u1ECFi g\xEC, c\xF2n th\xE0nh vi\xEAn c\u1EA3m th\u1EA5y \u0111\u1EE7 an to\xE0n \u0111\u1EC3 n\xF3i \u0111i\u1EC1u m\xECnh th\u1EADt s\u1EF1 ngh\u0129."
        },
        {
          to: "/why/growth",
          title: "T\u0103ng tr\u01B0\u1EDFng",
          desc: "Bi\u1EBFn cu\u1ED9c tr\xF2 chuy\u1EC7n th\xE0nh t\u0103ng tr\u01B0\u1EDFng. Nh\u1EEFng cam k\u1EBFt trong bu\u1ED5i h\u1ECDp kh\xF4ng b\u1ECB tr\xF4i m\u1EA5t m\xE0 \u0111\u01B0\u1EE3c n\u1ED1i sang hi\u1EC7u su\u1EA5t, \u0111\xE1nh gi\xE1 v\xE0 ph\xE1t tri\u1EC3n."
        },
        {
          to: "/why/change",
          title: "Thay \u0111\u1ED5i",
          desc: "Lan t\u1ECFa t\u1EA7m nh\xECn v\xE0 chi\u1EBFn l\u01B0\u1EE3c c\u1EE7a ban l\xE3nh \u0111\u1EA1o ra to\xE0n t\u1ED5 ch\u1EE9c v\xE0 gi\xFAp k\u1EBFt qu\u1EA3 \u0111\xF3 \u0111\u01B0\u1EE3c nh\xECn th\u1EA5y t\u1EDBi t\u1EADn tuy\u1EBFn \u0111\u1EA7u."
        }
      ]
    },
    impactStats: {
      title: "T\xE1c \u0111\u1ED9ng v\u0103n h\xF3a[br]r\xF5 r\xE0ng nh\u1EA5t.",
      subtitle: "Khi ch\u1EA5t l\u01B0\u1EE3ng [n]1on1[/n] \u0111\u01B0\u1EE3c n\xE2ng l\xEAn v\xE0 vi\u1EC7c \u0111o l\u01B0\u1EDDng \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng h\xF3a v\u1EDBi Orblit, ch\xEAnh l\u1EC7ch hi\u1EC7u qu\u1EA3 s\u1EBD hi\u1EC7n ra r\u1EA5t nhanh.",
      stats: [
        {
          value: 200,
          suffix: "%",
          label: "T\u1EF7 l\u1EC7 k\xEDch ho\u1EA1t"
        },
        {
          value: 2,
          suffix: "X",
          label: "M\u1EE9c h\xE0i l\xF2ng"
        },
        {
          value: 84,
          prefix: "-",
          suffix: "%",
          label: "Th\u1EDDi gian h\xE0nh ch\xEDnh"
        },
        {
          value: 20,
          prefix: "-",
          suffix: "%",
          label: "T\u1EF7 l\u1EC7 ngh\u1EC9 vi\u1EC7c"
        }
      ]
    },
    momentumProgram: {
      eyebrow: "Gi\u1EA3i ph\xE1p tri\u1EC3n khai [n]1on1[/n]",
      title: "Orblit Momentum Program",
      plan: "(k\u1EBF ho\u1EA1ch 3 th\xE1ng)",
      body: "\u0110\xE2y l\xE0 g\xF3i 3 th\xE1ng gi\xFAp t\u1ED5 ch\u1EE9c c\u1EE7a b\u1EA1n \u0111\u01B0a [n]1on1[/n], th\xF3i quen c\u1ED1t l\xF5i c\u1EE7a l\xE3nh \u0111\u1EA1o b\u1EC1n v\u1EEFng, v\xE0o th\u1EF1c t\u1EBF theo t\u1EEBng b\u01B0\u1EDBc m\xE0 kh\xF4ng g\xE2y qu\xE1 t\u1EA3i.[br]\u0110\u1EC3 v\u01B0\u1EE3t qua gi\u1EDBi h\u1EA1n c\u1EE7a c\xE1c bu\u1ED5i \u0111\xE0o t\u1EA1o doanh nghi\u1EC7p m\u1ED9t l\u1EA7n, ch\xFAng t\xF4i g\u1EAFn ch\u1EB7t s\u1EA3n ph\u1EA9m Orblit v\u1EDBi qu\xE1 tr\xECnh enablement th\u1EF1c chi\u1EBFn.",
      points: [
        {
          title: "Hu\u1EA5n luy\u1EC7n mindset v\xE0 k\u1EF9 n\u0103ng cho c\u1EA3 qu\u1EA3n l\xFD l\u1EABn th\xE0nh vi\xEAn",
          description: "Ch\xFAng t\xF4i x\xE2y d\u1EF1ng s\u1EF1 \u0111\u1ED3ng thu\u1EADn s\xE2u s\u1EAFc v\u1EC1 l\xFD do nh\u1EEFng cu\u1ED9c tr\xF2 chuy\u1EC7n n\xE0y quan tr\u1ECDng. Qu\u1EA3n l\xFD c\xF2n nh\u1EADn \u0111\u01B0\u1EE3c k\u1EF9 thu\u1EADt l\u1EAFng nghe, ph\u1EA3n h\u1ED3i v\xE0 \u0111\u1EB7t c\xE2u h\u1ECFi c\xF3 th\u1EC3 d\xF9ng ngay."
        },
        {
          title: "Ng\u0103n t\xE1c \u0111\u1ED9ng ng\u1EAFn h\u1EA1n b\u1EB1ng vi\u1EC7c d\xF9ng s\u1EA3n ph\u1EA9m",
          description: "\u0110\u1ED9i ng\u0169 \xE1p d\u1EE5ng ngay v\xE0o c\xF4ng vi\u1EC7c v\xE0 ch\u1EA1y c\xE1c bu\u1ED5i [n]1on1[/n] th\u1EADt. C\xE2u h\u1ECFi c\xE1 nh\xE2n h\xF3a v\xE0 v\xF2ng ph\u1EA3n h\u1ED3i li\xEAn t\u1EE5c gi\xFAp n\u0103ng l\u1EF1c t\u0103ng d\u1EA7n theo th\u1EDDi gian."
        },
        {
          title: "B\xE1o c\xE1o d\u1EEF li\u1EC7u th\u1EF1c \u0111\u1ECBa v\xE0 \u0111\xE0o t\u1EA1o n\u1ED1i ti\u1EBFp ph\xF9 h\u1EE3p",
          description: "Ch\xFAng t\xF4i cung c\u1EA5p b\xE1o c\xE1o d\u1EF1a tr\xEAn d\u1EEF li\u1EC7u [n]1on1[/n] t\xEDch l\u0169y, r\u1ED3i n\u1ED1i c\xE1c v\u1EA5n \u0111\u1EC1 \u0111\u01B0\u1EE3c ph\xE1t hi\u1EC7n sang ch\u01B0\u01A1ng tr\xECnh ti\u1EBFp n\u1ED1i ph\xF9 h\u1EE3p v\u1EDBi t\u1ED5 ch\u1EE9c."
        }
      ],
      imageAlt: "Hu\u1EA5n luy\u1EC7n vi\xEAn nam ng\u01B0\u1EDDi ch\xE2u \xC1 \u0111ang d\u1EABn d\u1EAFt workshop 1on1 v\xE0 l\xE3nh \u0111\u1EA1o trong ph\xF2ng h\u1ECDp hi\u1EC7n \u0111\u1EA1i",
      fallbackEyebrow: "Momentum Program",
      fallbackTitle: "\u0110\xE0o t\u1EA1o, s\u1EA3n ph\u1EA9m v\xE0 b\xE1o c\xE1o trong c\xF9ng m\u1ED9t[br]ch\u01B0\u01A1ng tr\xECnh tri\u1EC3n khai [n]1on1[/n]",
      cta: "Xem brochure ch\u01B0\u01A1ng tr\xECnh"
    },
    logos: {
      title: "\u0110\u01B0\u1EE3c \u0111\u1ED3ng thi\u1EBFt k\u1EBF c\xF9ng c\xE1c chuy\xEAn gia HR h\xE0ng \u0111\u1EA7u trong v\xE0 ngo\xE0i n\u01B0\u1EDBc",
      items: [
        {
          name: "Dell",
          desc: "[C\u1EF1u Ph\xF3 ch\u1EE7 t\u1ECBch APAC]"
        },
        {
          name: "SAMSUNG",
          desc: "[C\u1EF1u l\xE3nh \u0111\u1EA1o HR / L&D t\u1EA1i Samsung]"
        },
        {
          name: "PennState",
          desc: "[Gi\u1EA3ng vi\xEAn HR / ph\xE1t tri\u1EC3n t\u1ED5 ch\u1EE9c]"
        },
        {
          name: "George Mason",
          desc: "[Gi\u1EA3ng vi\xEAn t\xE2m l\xFD h\u1ECDc c\xF4ng nghi\u1EC7p - t\u1ED5 ch\u1EE9c]"
        }
      ]
    },
    security: {
      title: "D\u1EEF li\u1EC7u c\u1EE7a doanh nghi\u1EC7p b\u1EA1n[br]\u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7 \u1EDF m\u1EE9c cao nh\u1EA5t.",
      features: [
        {
          title: "M\xE3 h\xF3a d\u1EEF li\u1EC7u",
          description: "T\u1EA5t c\u1EA3 b\u1EA3n ghi cu\u1ED9c h\u1ECDp \u0111\u01B0\u1EE3c m\xE3 h\xF3a AES-256 khi l\u01B0u tr\u1EEF v\xE0 TLS 1.3 khi truy\u1EC1n t\u1EA3i. D\u1EEF li\u1EC7u c\u1EE7a b\u1EA1n \u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7 to\xE0n di\u1EC7n t\u1EEB khi t\u1EA1o \u0111\u1EBFn khi l\u01B0u tr\u1EEF."
        },
        {
          title: "C\xE1ch ly d\u1EEF li\u1EC7u theo Workspace",
          description: "Row-Level Security (RLS) v\xE0 Role-Based Access Control (RBAC) \u0111\u1EA3m b\u1EA3o t\xE1ch bi\u1EC7t ho\xE0n to\xE0n d\u1EEF li\u1EC7u gi\u1EEFa c\xE1c workspace. D\u1EEF li\u1EC7u kh\xE1ch h\xE0ng \u0111\u01B0\u1EE3c l\u01B0u trong DB chuy\xEAn d\u1EE5ng c\u1EE7a Orblit, c\xE1ch ly v\u1EADt l\xFD v\u1EDBi DB hi\u1EC7n c\xF3 c\u1EE7a doanh nghi\u1EC7p."
        },
        {
          title: "Kh\xF4ng s\u1EED d\u1EE5ng cho hu\u1EA5n luy\u1EC7n AI",
          description: "D\u1EEF li\u1EC7u cu\u1ED9c h\u1ECDp c\u1EE7a b\u1EA1n kh\xF4ng bao gi\u1EDD \u0111\u01B0\u1EE3c s\u1EED d\u1EE5ng \u0111\u1EC3 hu\u1EA5n luy\u1EC7n ho\u1EB7c c\u1EA3i thi\u1EC7n m\xF4 h\xECnh AI. Th\u1ECFa thu\u1EADn doanh nghi\u1EC7p v\u1EDBi nh\xE0 cung c\u1EA5p ph\xE2n t\xEDch AI (Google Vertex AI) nghi\xEAm c\u1EA5m vi\u1EC7c hu\u1EA5n luy\u1EC7n tr\xEAn d\u1EEF li\u1EC7u kh\xE1ch h\xE0ng."
        },
        {
          title: "Ki\u1EC3m so\xE1t truy c\u1EADp theo vai tr\xF2",
          description: "RBAC \u0111\u01B0\u1EE3c \xE1p d\u1EE5ng \u0111\u1EC3 qu\u1EA3n l\xFD quy\u1EC1n truy c\u1EADp d\u1EEF li\u1EC7u trong t\u1ED5 ch\u1EE9c c\u1EE7a b\u1EA1n."
        },
        {
          title: "T\u1EF1 \u0111\u1ED9ng x\xF3a b\u1EA3n ghi \xE2m",
          description: "C\xE1c file ghi \xE2m \u0111\u01B0\u1EE3c t\u1EF1 \u0111\u1ED9ng x\xF3a trong v\xF2ng 3 ng\xE0y sau khi ph\xE2n t\xEDch ho\xE0n t\u1EA5t. Ch\u1EC9 gi\u1EEF l\u1EA1i k\u1EBFt qu\u1EA3 ph\xE2n t\xEDch v\u0103n b\u1EA3n \u0111\xE3 chuy\u1EC3n \u0111\u1ED5i."
        },
        {
          title: "Nh\u1EADt k\xFD ki\u1EC3m to\xE1n l\u1ECBch s\u1EED thay \u0111\u1ED5i",
          description: "M\u1ECDi thay \u0111\u1ED5i \u0111\u1ED1i v\u1EDBi d\u1EEF li\u1EC7u quan tr\u1ECDng \u0111\u01B0\u1EE3c ghi l\u1EA1i t\u1EF1 \u0111\u1ED9ng. Gi\xE1 tr\u1ECB tr\u01B0\u1EDBc/sau, th\u1EDDi gian v\xE0 ng\u01B0\u1EDDi th\u1EF1c hi\u1EC7n \u0111\u01B0\u1EE3c l\u01B0u \u0111\u1EC3 truy v\u1EBFt \u0111\u1EA7y \u0111\u1EE7, v\xE0 c\xE1c b\u1EA3n ghi n\xE0y kh\xF4ng th\u1EC3 b\u1ECB s\u1EEDa \u0111\u1ED5i."
        },
        {
          title: "B\u1EA3o m\u1EADt k\xEAnh truy\u1EC1n th\xF4ng",
          description: "T\u1EA5t c\u1EA3 API ch\u1EC9 giao ti\u1EBFp qua HTTPS \u2014 truy c\u1EADp HTTP b\u1ECB ch\u1EB7n. Ch\xEDnh s\xE1ch CORS gi\u1EDBi h\u1EA1n truy c\u1EADp cho c\xE1c t\xEAn mi\u1EC1n \u0111\u01B0\u1EE3c \u1EE7y quy\u1EC1n, v\u1EADn h\xE0nh tr\xEAn h\u1EA1 t\u1EA7ng qu\u1EA3n l\xFD t\u1EA1i khu v\u1EF1c Seoul (Supabase & Google Cloud)."
        }
      ]
    },
    faq: {
      title: "C\xE2u h\u1ECFi th\u01B0\u1EDDng g\u1EB7p",
      subtitle: "Gi\u1EA3i \u0111\xE1p nh\u1EEFng \u0111i\u1EC1u m\u1ECDi ng\u01B0\u1EDDi h\u1ECFi nhi\u1EC1u nh\u1EA5t v\u1EC1 [n]1on1[/n] v\xE0 Orblit.",
      items: [
        {
          q: "[n]1on1[/n] ch\xEDnh x\xE1c l\xE0 g\xEC?[m]C\xF3 ph\u1EA3i ch\u1EC9 l\xE0 tr\xF2 chuy\u1EC7n nh\u1EB9 trong b\u1EEFa tr\u01B0a kh\xF4ng?",
          a: "Kh\xF4ng. Nh\u1EEFng cu\u1ED9c tr\xF2 chuy\u1EC7n ng\u1EABu h\u1EE9ng trong b\u1EEFa tr\u01B0a hay l\xFAc l\xE0m vi\u1EC7c kh\xF4ng ph\u1EA3i l\xE0 \u0111i\u1EC1u ch\xFAng t\xF4i g\u1ECDi l\xE0 [n]1on1[/n]. [n]1on1[/n] l\xE0 kho\u1EA3ng th\u1EDDi gian \u0111\u01B0\u1EE3c d\xE0nh ri\xEAng, n\u01A1i qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn \u0111\u1ED1i tho\u1EA1i s\xE2u, ch\xEDnh th\u1EE9c v\xE0 hai chi\u1EC1u v\u1EC1 c\xF4ng vi\u1EC7c l\u1EABn con ng\u01B0\u1EDDi."
        },
        {
          q: "V\xEC sao team c\u1EA7n l\xE0m [n]1on1[/n]?",
          a: "C\xF3 hai l\xFD do l\u1EDBn. Th\u1EE9 nh\u1EA5t, n\xF3 gi\xFAp c\u0103n ch\u1EC9nh \u0111\u1ECBnh h\u01B0\u1EDBng v\xE0 m\u1EE5c ti\xEAu gi\u1EEFa t\u1ED5 ch\u1EE9c, team v\xE0 c\xE1 nh\xE2n. Th\u1EE9 hai, n\xF3 gi\xFAp m\u1ECDi ng\u01B0\u1EDDi th\u1EA5y \xFD ngh\u0129a trong c\xF4ng vi\u1EC7c, c\u1EA3m nh\u1EADn s\u1EF1 ph\xE1t tri\u1EC3n v\xE0 gi\u1EA3m nguy c\u01A1 r\u1EDDi \u0111i tr\u01B0\u1EDBc khi v\u1EA5n \u0111\u1EC1 b\u1ED9c l\u1ED9."
        },
        {
          q: "Kh\xE1c g\xEC v\u1EDBi c\xE1c bu\u1ED5i review m\xE0 c\xF4ng ty t\xF4i \u0111\xE3 l\xE0m?",
          a: "C\xE1c bu\u1ED5i review truy\u1EC1n th\u1ED1ng th\u01B0\u1EDDng di\u1EC5n ra theo chu k\u1EF3 d\xE0i v\xE0 ch\u1EE7 y\u1EBFu \u0111\u1EC3 th\xF4ng b\xE1o k\u1EBFt qu\u1EA3. [n]1on1[/n] di\u1EC5n ra \u0111\u1EC1u \u0111\u1EB7n h\u01A1n, t\u1EADp trung v\xE0o s\u1EF1 th\u1EA5u hi\u1EC3u l\u1EABn nhau, qu\u1EA3n l\xFD qu\xE1 tr\xECnh v\xE0 t\u0103ng tr\u01B0\u1EDFng th\xF4ng qua \u0111\u1ED1i tho\u1EA1i hai chi\u1EC1u."
        },
        {
          q: "\u0110i\u1EC1u n\xE0y li\xEAn quan g\xEC t\u1EDBi n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o?",
          a: "L\xE3nh \u0111\u1EA1o c\u1EA7n ni\u1EC1m tin. M\u1ED9t cu\u1ED9c tr\xF2 chuy\u1EC7n m\u1ED9t - m\u1ED9t b\u1EA3o v\u1EC7 v\xE0 n\xE2ng \u0111\u1EE1 ti\u1EBFng n\xF3i c\u1EE7a th\xE0nh vi\xEAn l\xE0 m\u1ED9t trong nh\u1EEFng c\xE1ch m\u1EA1nh nh\u1EA5t v\xE0 \u0111\xE1ng tin c\u1EADy nh\u1EA5t \u0111\u1EC3 x\xE2y d\u1EF1ng ni\u1EC1m tin \u0111\xF3, v\u01B0\u1EE3t xa ch\u1EC9 \u0111\u1EA1o m\u1ED9t chi\u1EC1u tr\u01B0\u1EDBc \u0111\xE1m \u0111\xF4ng."
        },
        {
          q: "AI c\xF3 thay qu\u1EA3n l\xFD tr\xF2 chuy\u1EC7n v\xE0 coaching lu\xF4n kh\xF4ng?",
          a: "Kh\xF4ng. Ng\u01B0\u1EDDi qu\u1EA3n l\xFD v\u1EABn l\xE0 ch\u1EE7 th\u1EC3 c\u1EE7a cu\u1ED9c tr\xF2 chuy\u1EC7n v\xE0 coaching. AI \u0111\xF3ng vai tr\xF2 c\xF4ng c\u1EE5 h\u1ED7 tr\u1EE3 m\u1EA1nh, gi\xFAp c\u1EAFt m\u1EA1nh ph\u1EA7n h\xE0nh ch\xEDnh v\xE0 t\xE0i li\u1EC7u h\xF3a \u0111\u1EC3 qu\u1EA3n l\xFD t\u1EADp trung ho\xE0n to\xE0n v\xE0o vi\u1EC7c d\u1EABn d\u1EAFt."
        },
        {
          q: "Ch\xFAng t\xF4i r\u1EA5t b\u1EADn. N\u1EBFu kh\xF3 l\xE0m [n]1on1[/n] h\u1EB1ng tu\u1EA7n th\xEC v\u1EABn d\xF9ng \u0111\u01B0\u1EE3c ch\u1EE9?",
          a: "C\xF3. T\u1EA7n su\u1EA5t cao h\u01A1n s\u1EBD hi\u1EC7u qu\u1EA3 h\u01A1n, nh\u01B0ng ngay c\u1EA3 khi b\u1EA1n d\xF9ng cho c\xE1c bu\u1ED5i review hay \u0111\xE1nh gi\xE1 c\xF3 chu k\u1EF3 d\xE0i h\u01A1n, b\u1EA1n v\u1EABn s\u1EBD c\xF3 quy tr\xECnh kh\xE1ch quan v\xE0 c\xF3 c\u1EA5u tr\xFAc h\u01A1n r\u1EA5t nhi\u1EC1u so v\u1EDBi c\xE1ch c\u0169."
        }
      ]
    },
    cta: {
      title: "L\xE3nh \u0111\u1EA1o b\u1EAFt \u0111\u1EA7u [m]t\u1EEB ch\xEDnh bu\u1ED5i [n]1on1[/n].",
      body: "H\xE3y \u0111\u1EC3 Orblit AI lo ph\u1EA7n t\xE0i li\u1EC7u [m]\u0111\u1EC3 qu\u1EA3n l\xFD c\xF3 th\u1EC3 t\u1EADp trung [m]v\xE0o coaching v\xE0 nh\u1EEFng cu\u1ED9c \u0111\u1ED1i tho\u1EA1i ch\xE2n th\u1EADt.",
      placeholder: "Nh\u1EADp email c\xF4ng vi\u1EC7c c\u1EE7a b\u1EA1n",
      button: "Nh\u1EADn brochure",
      footnote: "B\u1EA5t k\u1EF3 ai c\u0169ng c\xF3 th\u1EC3 d\xF9ng th\u1EED c\xE1c t\xEDnh n\u0103ng c\u1ED1t l\xF5i ngay sau khi \u0111\u0103ng k\xFD.",
      footer: {
        companyName: "C\xF4ng ty Reversemountain",
        address: "Seoul Startup Hub M-Plus, 14 Magokjungang 8-ro, Gangseo-gu, Seoul",
        representative: "Gi\xE1m \u0111\u1ED1c: Min Kim",
        email: "gmkim@reversemountain.co.kr",
        copyright: "2026 REVERSEMOUNTAIN ALL RIGHTS RESERVED.",
        links: [
          {
            label: "\u0110i\u1EC1u kho\u1EA3n d\u1ECBch v\u1EE5",
            href: "https://orblit.space/v3/service_terms_en.html"
          },
          {
            label: "Ch\xEDnh s\xE1ch b\u1EA3o m\u1EADt",
            href: "https://orblit.space/v4/privacy_policy_en.html"
          },
          {
            label: "Li\xEAn h\u1EC7 t\u01B0 v\u1EA5n",
            href: "https://featpaper.com/l/QJusPa7y"
          }
        ]
      }
    }
  },
  navbar: {
    service: "S\u1EA3n ph\u1EA9m",
    whyOrblit: "T\u1EA1i sao Orblit",
    communication: "Giao ti\u1EBFp",
    growth: "Ph\xE1t tri\u1EC3n",
    change: "Qu\u1EA3n l\xFD thay \u0111\u1ED5i",
    company: "ReversMountain",
    pricing: "B\u1EA3ng gi\xE1",
    resources: "T\xE0i nguy\xEAn",
    blog: "Blog",
    newsletter: "B\u1EA3n tin",
    youtube: "YouTube",
    cta: "B\u1EAFt \u0111\u1EA7u",
    toggleMenu: "M\u1EDF ho\u1EB7c \u0111\xF3ng menu"
  },
  company: {
    hero: {
      alt: "Company Culture Background",
      title: "\u0110\u1EC3 con ng\u01B0\u1EDDi t\u1ECFa s\xE1ng[m]tr\xEAn qu\u1EF9 \u0111\u1EA1o v\u0103n h\xF3a",
      subtitle: "Orbit Your Culture,[m] Light Your People"
    },
    missionVision: {
      missionLabel: "Our mission",
      missionBody: "Th\xF4ng qua giao ti\u1EBFp l\xE0nh m\u1EA1nh, ch\xFAng t\xF4i gi\xFAp m\u1ED7i ng\u01B0\u1EDDi t\xECm th\u1EA5y \xFD ngh\u0129a trong c\xF4ng vi\u1EC7c v\xE0 duy tr\xEC s\u1EF1 g\u1EAFn k\u1EBFt s\xE2u s\u1EAFc,[m] \u0111\u1ED3ng th\u1EDDi x\xE2y d\u1EF1ng m\u1ED9t v\u0103n h\xF3a n\u01A1i th\xE0nh qu\u1EA3 t\u1EA1o ra trong h\xE0nh tr\xECnh \u0111\xF3 \u0111\u01B0\u1EE3c ghi nh\u1EADn tr\u1ECDn v\u1EB9n.",
      visionLabel: "Our vision",
      visionBody: "Ch\xFAng t\xF4i t\u1EA1o ra m\u1ED9t th\u1EBF gi\u1EDBi n\u01A1i m\u1ED7i c\xE1 nh\xE2n kh\xE1m ph\xE1 \u0111\u01B0\u1EE3c ti\u1EC1m n\u0103ng ri\xEAng c\u1EE7a m\xECnh[m] v\xE0 k\u1EBFt n\u1ED1i v\u1EDBi c\u01A1 h\u1ED9i ph\xF9 h\u1EE3p nh\u1EA5t \u0111\u1EC3 t\u1ECFa s\xE1ng."
    },
    culture: {
      badge: "Our Culture",
      items: [
        {
          num: "01",
          title: "Ch\xFAng t\xF4i t\xF4n tr\u1ECDng n\u0103ng l\u1EF1c[br]v\xE0 chi\u1EC1u s\xE2u, kh\xF4ng ph\u1EA3i ch\u1EE9c danh.",
          lines: [
            "Ch\xFAng t\xF4i tin v\xE0o n\u0103ng l\u1EF1c hi\u1EC7n t\u1EA1i nhi\u1EC1u h\u01A1n l\xE0 th\xE2m ni\xEAn hay c\u1EA5p b\u1EADc.",
            "Ch\xFAng t\xF4i tin r\u1EB1ng uy t\xEDn c\u1EE7a ng\u01B0\u1EDDi l\xE3nh \u0111\u1EA1o kh\xF4ng \u0111\u1EBFn t\u1EEB m\u1EC7nh l\u1EC7nh,[m] m\xE0 \u0111\u1EBFn t\u1EEB insight \u0111\u1EE7 s\u1EAFc b\xE9n \u0111\u1EC3 \u0111\u1ED9i ng\u0169 \u0111\u1ED3ng c\u1EA3m[m] v\xE0 t\u1EEB vi\u1EC7c l\xE0m g\u01B0\u01A1ng."
          ]
        },
        {
          num: "02",
          title: "Ch\xFAng t\xF4i tranh lu\u1EADn l\xE0nh m\u1EA1nh,[br]r\u1ED3i c\xF9ng ch\u1EA1y v\u1EC1 m\u1ED9t h\u01B0\u1EDBng.",
          lines: [
            "Mu\u1ED1n \u0111i \u0111\u1EBFn k\u1EBFt lu\u1EADn t\u1ED1t h\u01A1n th\xEC \xFD ki\u1EBFn th\u1EB3ng th\u1EAFn l\xE0 \u0111i\u1EC1u b\u1EAFt bu\u1ED9c.[m] Trong ph\xF2ng h\u1ECDp, h\xE3y tranh lu\u1EADn quy\u1EBFt li\u1EC7t m\xE0 kh\xF4ng c\u1EA7n nh\xECn n\xE9t m\u1EB7t nhau tr\u01B0\u1EDBc.",
            "Nh\u01B0ng sau khi \u0111\xE3 ch\u1ED1t h\u01B0\u1EDBng \u0111i, ch\xFAng t\xF4i tin nhau[m] v\xE0 c\xF9ng d\u1ED1c s\u1EE9c nh\u01B0 th\u1EC3 \u0111\xF3 l\xE0 \xFD t\u01B0\u1EDFng c\u1EE7a ch\xEDnh m\xECnh."
          ]
        },
        {
          num: "03",
          title: "Ch\xFAng t\xF4i v\u01B0\u1EE3t qua th\xF3i quen[br]\u0111\u1EC3 t\u1EADp trung v\xE0o b\u1EA3n ch\u1EA5t.",
          lines: [
            "Thay v\xEC n\xF3i \u201Cx\u01B0a nay v\u1EABn v\u1EADy\u201D, ch\xFAng t\xF4i h\u1ECFi \u201Ct\u1EA1i sao?\u201D",
            "Thay v\xEC l\xE0m theo ng\u01B0\u1EDDi kh\xE1c, ch\xFAng t\xF4i \u0111\xE0o s\xE2u v\xE0o b\u1EA3n ch\u1EA5t v\u1EA5n \u0111\u1EC1[m] v\xE0 t\xECm ra l\u1EDDi gi\u1EA3i ph\xF9 h\u1EE3p, hi\u1EC7u qu\u1EA3 nh\u1EA5t cho ch\xEDnh m\xECnh."
          ]
        }
      ]
    },
    cta: {
      title: "Nh\u1EEFng ng\u01B0\u1EDDi mu\u1ED1n c\xF9ng ch\xFAng t\xF4i [m]x\xE2y n\xEAn m\u1ED9t t\u1ED5 ch\u1EE9c t\u1ED1t h\u01A1n",
      body: "H\xE3y ph\xE1t tri\u1EC3n c\xF9ng Reversemountain [m]v\xE0 d\u1EABn d\u1EAFt m\u1ED9t c\xE1ch l\xE0m vi\u1EC7c m\u1EDBi.",
      button: "Xem trang tuy\u1EC3n d\u1EE5ng"
    }
  },
  pricing: {
    heroTitle: "Kh\u1EDFi \u0111\u1EA7u nh\u1EB9 nh\xE0ng,[br]ch\u1ECDn g\xF3i ph\xF9 h\u1EE3p v\u1EDBi team c\u1EE7a b\u1EA1n",
    heroSubtitle: "T\u1EEB tr\u1EA3i nghi\u1EC7m team mi\u1EC5n ph\xED \u0111\u1EBFn g\xF3i Business kh\xF4ng gi\u1EDBi h\u1EA1n cho t\u0103ng tr\u01B0\u1EDFng b\u1EC1n v\u1EEFng,[d]ch\xFAng t\xF4i \u0111\u1EC1 xu\u1EA5t gi\u1EA3i ph\xE1p m\xE0 l\xE3nh \u0111\u1EA1o th\u1EADt s\u1EF1 c\u1EA7n.",
    billing: {
      monthly: "Thanh to\xE1n th\xE1ng",
      annual: "Thanh to\xE1n n\u0103m",
      annualSavings: "Thanh to\xE1n n\u0103m gi\xFAp ti\u1EBFt ki\u1EC7m 17% chi ph\xED."
    },
    desktop: {
      introTitle: "C\xF4ng c\u1EE5 t\u1ED1i \u01B0u cho 1on1 th\xE0nh c\xF4ng",
      introSubtitle: "H\xE3y ch\u1ECDn g\xF3i gi\xFAp n\xE2ng c\u1EA5p v\u0103n h\xF3a h\u1ECDp c\u1EE7a team b\u1EA1n.",
      ctaText: "\u0110\u0103ng k\xFD ngay \u0111\u1EC3 tr\u1EA3i nghi\u1EC7m v\u0103n h\xF3a h\u1ECDp 1on1 ph\xF9 h\u1EE3p v\u1EDBi team c\u1EE7a b\u1EA1n."
    },
    plans: {
      basic: {
        badge: "Tr\u1EA3i nghi\u1EC7m 1on1",
        title: "G\xF3i Basic",
        price: "Mi\u1EC5n ph\xED",
        note: "Kh\xF4ng c\u1EA7n th\u1EBB t\xEDn d\u1EE5ng, b\u1EAFt \u0111\u1EA7u ngay sau khi \u0111\u0103ng k\xFD",
        cta: "B\u1EAFt \u0111\u1EA7u mi\u1EC5n ph\xED",
        highlights: {
          aiTitle: "D\xF9ng th\u1EED chu tr\xECnh 1on1 b\u1EB1ng AI",
          aiDesc: "Cung c\u1EA5p 3 l\u01B0\u1EE3t ghi \xE2m/ph\xE2n t\xEDch mi\u1EC5n ph\xED khi \u0111\u0103ng k\xFD, th\xEAm 2 l\u01B0\u1EE3t m\u1ED7i khi m\u1EDDi th\xE0nh vi\xEAn.",
          commonTitle: "Bao g\u1ED3m m\u1ECDi t\xEDnh n\u0103ng 1on1 c\u01A1 b\u1EA3n",
          commonDesc: "M\u1EABu c\xE2u h\u1ECFi, dashboard, qu\u1EA3n l\xFD th\xE0nh vi\xEAn 9-Box v\xE0 ghi ch\xFA chung \u0111\u1EC1u \u0111\u01B0\u1EE3c cung c\u1EA5p.",
          note: "* Khi d\xF9ng h\u1EBFt l\u01B0\u1EE3t, t\xEDnh n\u0103ng AI s\u1EBD b\u1ECB gi\u1EDBi h\u1EA1n. C\xE1c t\xEDnh n\u0103ng 1on1 c\u01A1 b\u1EA3n v\u1EABn d\xF9ng \u0111\u01B0\u1EE3c."
        }
      },
      business: {
        badge: "G\xF3i \u0111\u1EC1 xu\u1EA5t",
        title: "G\xF3i Business",
        annualPrice: "80.000",
        monthlyPrice: "8.000",
        annualSuffix: "KRW / th\xE0nh vi\xEAn \xB7 n\u0103m",
        monthlySuffix: "KRW / th\xE0nh vi\xEAn \xB7 th\xE1ng",
        note: "Thanh to\xE1n theo workspace",
        cta: "D\xF9ng th\u1EED",
        recommended: "RECOMMENDED",
        highlights: {
          aiTitle: "Kh\xF4ng gi\u1EDBi h\u1EA1n chu tr\xECnh 1on1 b\u1EB1ng AI",
          aiDesc: "Kh\xF4ng gi\u1EDBi h\u1EA1n g\u1EE3i \xFD c\xE2u h\u1ECFi, ghi \xE2m STT, t\xF3m t\u1EAFt th\u1EDDi gian th\u1EF1c, ph\u1EA3n h\u1ED3i coaching l\xE3nh \u0111\u1EA1o v\xE0 nhi\u1EC1u t\xEDnh n\u0103ng kh\xE1c.",
          commonTitle: "Bao g\u1ED3m m\u1ECDi t\xEDnh n\u0103ng 1on1 c\u01A1 b\u1EA3n",
          commonDesc: "M\u1EABu c\xE2u h\u1ECFi, dashboard, qu\u1EA3n l\xFD th\xE0nh vi\xEAn 9-Box v\xE0 ghi ch\xFA chung \u0111\u1EC1u \u0111\u01B0\u1EE3c cung c\u1EA5p."
        }
      }
    },
    sections: {
      ai: "T\xEDnh n\u0103ng AI th\xF4ng minh gi\xFAp t\u1ED1i \u0111a h\xF3a hi\u1EC7u su\u1EA5t v\xE0 hi\u1EC7u qu\u1EA3 cu\u1ED9c h\u1ECDp",
      common: "T\xEDnh n\u0103ng c\u01A1 b\u1EA3n h\u1ED7 tr\u1EE3 \u0111\u1EB7t chu k\u1EF3, ti\u1EBFn h\xE0nh v\xE0 qu\u1EA3n l\xFD 1on1"
    },
    trial: {
      badge: "C\xF3 d\xF9ng th\u1EED",
      lines: [
        "C\xF3 s\u1EB5n 3 l\u01B0\u1EE3t khi \u0111\u0103ng k\xFD",
        "+2 l\u01B0\u1EE3t khi m\u1EDDi th\xEAm th\xE0nh vi\xEAn"
      ],
      note: "* Khi d\xF9ng h\u1EBFt l\u01B0\u1EE3t, t\xEDnh n\u0103ng AI s\u1EBD b\u1ECB gi\u1EDBi h\u1EA1n. C\xE1c t\xEDnh n\u0103ng 1on1 c\u01A1 b\u1EA3n v\u1EABn d\xF9ng \u0111\u01B0\u1EE3c."
    },
    businessAi: {
      badge: "Kh\xF4ng gi\u1EDBi h\u1EA1n"
    },
    comparison: {
      title: "So s\xE1nh t\xEDnh n\u0103ng chi ti\u1EBFt",
      featureColumn: "T\xEDnh n\u0103ng",
      basicColumn: "Basic",
      businessColumn: "Business",
      trialValue: "D\xF9ng th\u1EED",
      unlimitedValue: "Kh\xF4ng gi\u1EDBi h\u1EA1n"
    },
    aiFeatures: [
      {
        name: "Chatbot chu\u1EA9n b\u1ECB bu\u1ED5i h\u1ECDp",
        desc: "AI h\u1ED7 tr\u1EE3 chu\u1EA9n b\u1ECB cu\u1ED9c h\u1ECDp qua chat.",
        phase: "Tr\u01B0\u1EDBc h\u1ECDp",
        phaseKey: "before",
        role: "Th\xE0nh vi\xEAn"
      },
      {
        name: "G\u1EE3i \xFD c\xE2u h\u1ECFi d\u1EF1a tr\xEAn prep",
        desc: "G\u1EE3i \xFD c\xE2u h\u1ECFi ph\xF9 h\u1EE3p d\u1EF1a tr\xEAn n\u1ED9i dung prep c\u1EE7a th\xE0nh vi\xEAn v\xE0 l\u1ECBch s\u1EED 1on1.",
        phase: "Tr\u01B0\u1EDBc h\u1ECDp",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "H\u01B0\u1EDBng d\u1EABn theo t\u1EEBng th\xE0nh vi\xEAn",
        desc: "\u0110\u1EC1 xu\u1EA5t h\u01B0\u1EDBng tr\xF2 chuy\u1EC7n d\u1EF1a tr\xEAn prep v\xE0 \u0111\u1EB7c \u0111i\u1EC3m c\u1EE7a th\xE0nh vi\xEAn.",
        phase: "Tr\u01B0\u1EDBc h\u1ECDp",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "T\u1EA1o template c\xE1 nh\xE2n h\xF3a b\u1EB1ng AI",
        desc: "B\u1ED9 c\xE2u h\u1ECFi \u0111\u01B0\u1EE3c t\u1ED1i \u01B0u cho \u0111\u1EB7c \u0111i\u1EC3m c\xE1 nh\xE2n v\xE0 t\xECnh hu\u1ED1ng hi\u1EC7n t\u1EA1i.",
        phase: "Tr\u01B0\u1EDBc h\u1ECDp",
        phaseKey: "before",
        role: "Leader"
      },
      {
        name: "Tr\xEDch xu\u1EA5t script cu\u1ED9c tr\xF2 chuy\u1EC7n (STT)",
        desc: "Con ng\u01B0\u1EDDi t\u1EADp trung giao ti\u1EBFp, AI ghi nh\u1EADn v\xE0 s\u1EAFp x\u1EBFp n\u1ED9i dung.",
        phase: "Trong h\u1ECDp",
        phaseKey: "during"
      },
      {
        name: "T\u1ED5ng h\u1EE3p c\xE2u h\u1ECFi-tr\u1EA3 l\u1EDDi ch\xEDnh",
        desc: "T\u1EF1 \u0111\u1ED9ng gh\xE9p c\xE2u h\u1ECFi v\u1EDBi c\xE2u tr\u1EA3 l\u1EDDi v\xE0 ghi l\u1EA1i c\u1EA3 c\xE2u h\u1ECFi m\u1EDBi.",
        phase: "Trong h\u1ECDp",
        phaseKey: "during"
      },
      {
        name: "T\xF3m t\u1EAFt chi ti\u1EBFt v\xE0 action item",
        desc: "Nhanh ch\xF3ng t\xF3m t\u1EAFt n\u1ED9i dung h\u1ECDp v\xE0 action item c\u1EE7a hai b\xEAn.",
        phase: "Trong h\u1ECDp",
        phaseKey: "during"
      },
      {
        name: "Coaching l\xE3nh \u0111\u1EA1o c\xE1 nh\xE2n h\xF3a",
        desc: "Ph\xE2n t\xEDch ph\xE1t ng\xF4n \u0111\u1EC3 ch\u1EC9 ra \u0111i\u1EC3m m\u1EA1nh v\xE0 \u0111i\u1EC3m c\u1EA7n c\u1EA3i thi\u1EC7n trong cu\u1ED9c tr\xF2 chuy\u1EC7n.",
        phase: "Trong h\u1ECDp",
        phaseKey: "during",
        role: "Leader"
      },
      {
        name: "B\xE1o c\xE1o th\xE0nh vi\xEAn",
        desc: "T\u1ED5ng h\u1EE3p c\xE1c bu\u1ED5i h\u1ECDp \u0111\u1EC3 \u0111\u1EC1 xu\u1EA5t r\u1EE7i ro ngh\u1EC9 vi\u1EC7c, c\xE1ch qu\u1EA3n l\xFD v\xE0 nhi\u1EC1u n\u1ED9i dung kh\xE1c.",
        phase: "Sau h\u1ECDp",
        phaseKey: "after",
        role: "Leader"
      }
    ],
    commonFeatures: [
      {
        name: "Qu\u1EA3n l\xFD chu k\u1EF3 h\u1ECDp",
        desc: "Thi\u1EBFt l\u1EADp chu k\u1EF3 v\xE0 nh\u1EAFc qua email/push cho c\xE1c cu\u1ED9c h\u1ECDp \u0111\u1ECBnh k\u1EF3.",
        icon: "calendar"
      },
      {
        name: "M\u1EABu c\xE2u h\u1ECFi 1on1 c\u01A1 b\u1EA3n",
        desc: "Khung tr\xF2 chuy\u1EC7n theo t\u1EEBng t\xECnh hu\u1ED1ng \u0111\u1EC3 giao ti\u1EBFp tr\u01A1n tru h\u01A1n.",
        icon: "compass",
        role: "Leader"
      },
      {
        name: "Kh\xF4ng gian ghi ch\xFA chung",
        desc: "Ghi ch\xFA h\u1ECDp chung \u0111\u1EC3 leader v\xE0 th\xE0nh vi\xEAn c\xF9ng ch\u1EC9nh s\u1EEDa.",
        icon: "pencil"
      },
      {
        name: "Ghi nh\u1EADn/theo d\xF5i tr\u1EA1ng th\xE1i sau h\u1ECDp",
        desc: "Theo d\xF5i \u0111\u1EC1u \u0111\u1EB7n m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt v\xE0 h\xE0i l\xF2ng c\u1EE7a th\xE0nh vi\xEAn m\u1ED9t c\xE1ch nh\u1EB9 nh\xE0ng.",
        icon: "check",
        role: "Leader"
      },
      {
        name: "Dashboard t\xECnh tr\u1EA1ng h\u1ECDp",
        desc: "Theo d\xF5i ti\u1EBFn \u0111\u1ED9 1on1 v\xE0 tr\u1EA1ng th\xE1i th\xE0nh vi\xEAn trong t\u1ED5 ch\u1EE9c.",
        icon: "chart",
        role: "Leader"
      },
      {
        name: "Qu\u1EA3n l\xFD th\xE0nh vi\xEAn 9-Box",
        desc: "Ma tr\u1EADn xem nhanh hi\u1EC7u su\u1EA5t-ti\u1EC1m n\u0103ng v\xE0 r\u1EE7i ro ngh\u1EC9 vi\u1EC7c-m\u1EE9c \u0111\u1ED9 \u1EA3nh h\u01B0\u1EDFng.",
        icon: "grid",
        role: "Leader"
      }
    ]
  },
  why1on1: {
    cardCta: "Xem chi ti\u1EBFt",
    heroTitle: "L\xE3nh \u0111\u1EA1o kh\xF4ng \u0111\u01B0\u1EE3c t\u1EA1o ra[m]trong ph\xF2ng h\u1ECDp.[br]N\xF3 \u0111\u01B0\u1EE3c t\u1EA1o ra trong [n]1on1[/n].",
    heroSubtitle: "Orblit thi\u1EBFt k\u1EBF giao ti\u1EBFp, t\u0103ng tr\u01B0\u1EDFng v\xE0 thay \u0111\u1ED5i th\xF4ng qua [n]1on1[/n].",
    statsTitle: "S\u1EE9c m\u1EA1nh c\u1EE7a [n]1on1[/n] qua d\u1EEF li\u1EC7u",
    stats: [
      {
        src: "Gallup",
        stat: "70%",
        color: "#2563eb",
        preLines: ["C\u1EE7a s\u1EF1 kh\xE1c bi\u1EC7t v\u1EC1 m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt"],
        post: " \u0111\u1EBFn t\u1EEB l\xE3nh \u0111\u1EA1o"
      },
      {
        src: "Andy Grove",
        stat: "80 gi\u1EDD",
        color: "#9333ea",
        preLines: [
          "M\u1ED9t bu\u1ED5i [n]1on1[/n] 90 ph\xFAt c\u1EE7a qu\u1EA3n l\xFD \u0111\u1ECBnh h\xECnh",
          "ch\u1EB7ng ti\u1EBFp theo"
        ],
        post: " c\u1EE7a th\xE0nh vi\xEAn"
      },
      {
        src: "Adobe",
        stat: "30%",
        color: "#059669",
        preLines: [
          "Sau khi \xE1p d\u1EE5ng [n]1on1[/n] \u0111\u1EC1u \u0111\u1EB7n,",
          "t\u1EF7 l\u1EC7 ngh\u1EC9 vi\u1EC7c t\u1EF1 nguy\u1EC7n"
        ],
        post: " \u0111\xE3 gi\u1EA3m"
      }
    ],
    areas: {
      title: "[n]1on1[/n] t\u1ED1t kh\xF4ng ch\u1EC9 l\xE0 m\u1ED9t bu\u1ED5i g\u1EB7p m\u1EB7t",
      subtitle: "B\xEAn trong n\xF3, ba d\u1EA1ng c\xF4ng vi\u1EC7c l\xE3nh \u0111\u1EA1o di\u1EC5n ra c\xF9ng l\xFAc.",
      cards: [
        {
          to: "/why/communication",
          title: "Giao ti\u1EBFp",
          desc: "Qu\u1EA3n l\xFD bi\u1EBFt n\xEAn n\xF3i g\xEC, c\xF2n th\xE0nh vi\xEAn c\u1EA3m th\u1EA5y \u0111\u1EE7 an to\xE0n \u0111\u1EC3 n\xF3i ra \u0111i\u1EC1u m\xECnh th\u1EF1c s\u1EF1 ngh\u0129."
        },
        {
          to: "/why/growth",
          title: "T\u0103ng tr\u01B0\u1EDFng",
          desc: "Nh\u1EEFng cam k\u1EBFt trong cu\u1ED9c tr\xF2 chuy\u1EC7n kh\xF4ng b\u1ECB tr\xF4i m\u1EA5t m\xE0 \u0111\u01B0\u1EE3c n\u1ED1i sang hi\u1EC7u su\u1EA5t, \u0111\xE1nh gi\xE1 v\xE0 ph\xE1t tri\u1EC3n."
        },
        {
          to: "/why/change",
          title: "Thay \u0111\u1ED5i",
          desc: "Chi\u1EBFn l\u01B0\u1EE3c t\u1EEB l\xE3nh \u0111\u1EA1o c\u1EA5p cao \u0111\u01B0\u1EE3c truy\u1EC1n t\u1EDBi tuy\u1EBFn \u0111\u1EA7u th\xF4ng qua c\xE1c qu\u1EA3n l\xFD v\xE0 \u0111\u01B0\u1EE3c bi\u1EBFn th\xE0nh th\u1EF1c thi r\xF5 r\xE0ng."
        }
      ]
    },
    approach: {
      eyebrow: "Our Approach",
      title: "\u0110\xE2y kh\xF4ng ph\u1EA3i t\u1EF1 \u0111\u1ED9ng h\xF3a.[m]\u0110\xE2y l\xE0 t\u0103ng c\u01B0\u1EDDng n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o.",
      commonTitle: "C\xE1ch ti\u1EBFp c\u1EADn th\u01B0\u1EDDng th\u1EA5y",
      commonItems: [
        "C\xF4ng c\u1EE5 ch\u1EC9 ch\xE9p l\u1EA1i cu\u1ED9c tr\xF2 chuy\u1EC7n m\u1ED9t c\xE1ch m\xE1y m\xF3c",
        "\u1EE8ng d\u1EE5ng checklist \u0111\u1EC3 qu\u1EA3n l\xFD x\xE1c nh\u1EADn",
        "C\xF4ng c\u1EE5 ghi ch\xFA chung kh\xF4ng th\u1EC3 ph\xE2n t\xEDch"
      ],
      orblitBadge: "Orblit",
      orblitTitle: "C\xE1ch ti\u1EBFp c\u1EADn c\u1EE7a Orblit",
      orblitItems: [
        "N\xE2ng n\u0103ng l\u1EF1c l\xE3nh \u0111\u1EA1o b\u1EB1ng c\xE2u h\u1ECFi v\xE0 ph\u1EA3n h\u1ED3i \u0111\xE3 \u0111\u01B0\u1EE3c chu\u1EA9n b\u1ECB",
        "T\u0103ng kh\u1EA3 n\u0103ng nh\xECn th\u1EA5y t\xECnh tr\u1EA1ng t\u1ED5 ch\u1EE9c qua d\u1EEF li\u1EC7u",
        "Thi\u1EBFt l\u1EADp h\u1EC7 th\u1ED1ng qu\u1EA3n l\xFD hi\u1EC7u su\u1EA5t li\xEAn t\u1EE5c v\xE0 ph\xF2ng v\u1EC7 r\u1EDDi b\u1ECF"
      ]
    }
  }
};

// src/locales/vi-VN/feature_tutorial.json
var feature_tutorial_default3 = {
  actions: {
    replay: "Xem h\u01B0\u1EDBng d\u1EABn",
    mediaUnavailable: "Kh\xF4ng c\xF3 ph\u01B0\u01A1ng ti\u1EC7n h\u01B0\u1EDBng d\u1EABn."
  },
  recording: {
    dashboard: {
      fixture: {
        users: {
          owner: "Minh Nguyen",
          leaderOne: "Linh Tran",
          leaderTwo: "Nam Pham",
          memberOne: "An Le",
          memberTwo: "Mai Vo",
          memberThree: "Khoa Dang",
          memberFour: "Vy Hoang",
          memberFive: "Long Do"
        },
        meetings: {
          q1Priorities: "\u01AFu ti\xEAn Q1 v\xE0 onboarding",
          executionReview: "\u0110\xE1nh gi\xE1 th\u1EF1c thi",
          growthPlan: "K\u1EBF ho\u1EA1ch ph\xE1t tri\u1EC3n",
          workload: "\u0110i\u1EC1u ch\u1EC9nh kh\u1ED1i l\u01B0\u1EE3ng c\xF4ng vi\u1EC7c",
          priorityReset: "S\u1EAFp x\u1EBFp l\u1EA1i \u01B0u ti\xEAn",
          roadmap: "L\u1ED9 tr\xECnh n\u1EC1n t\u1EA3ng",
          handoff: "B\xE0n giao d\u1EF1 \xE1n",
          rituals: "Nghi th\u1EE9c l\xE0m vi\u1EC7c nh\xF3m",
          reliability: "\u0110\xE1nh gi\xE1 \u0111\u1ED9 tin c\u1EADy",
          career: "\u0110\u1ECBnh h\u01B0\u1EDBng ngh\u1EC1 nghi\u1EC7p",
          coaching: "Coaching cho qu\u1EA3n l\xFD",
          leadership: "\u0110\u1ED3ng b\u1ED9 l\xE3nh \u0111\u1EA1o"
        }
      }
    },
    member_report: {
      fixture: {
        reports: {
          primary: {
            recommendations: [
              "Thi\u1EBFt k\u1EBF nhi\u1EC7m v\u1EE5 ph\xE1t tri\u1EC3n: Giao m\u1ED9t d\u1EF1 \xE1n t\u1ED1i \u01B0u h\xF3a c\u01A1 s\u1EDF d\u1EEF li\u1EC7u nh\u1ECF, n\u01A1i h\u1ECD c\xF3 th\u1EC3 ph\xE2n t\xEDch \u0111i\u1EC3m ngh\u1EBDn truy v\u1EA5n trong b\u1ED1i c\u1EA3nh l\u01B0u l\u01B0\u1EE3ng cao v\xE0 ki\u1EC3m ch\u1EE9ng t\xE1c \u0111\u1ED9ng c\u1EA3i thi\u1EC7n.",
              "B\u1EA3o v\u1EC7 th\u1EDDi gian review: Trong tu\u1EA7n release, h\xE3y kh\xF3a ri\xEAng th\u1EDDi gian review code b\xEAn c\u1EA1nh th\u1EDDi gian tri\u1EC3n khai \u0111\u1EC3 ph\u1EA3n h\u1ED3i ch\u1EA5t l\u01B0\u1EE3ng kh\xF4ng b\u1ECB tr\u1EC5.",
              "M\u1EDF r\u1ED9ng kinh nghi\u1EC7m v\u1EADn h\xE0nh: Cho h\u1ECD tham gia di\u1EC5n t\u1EADp s\u1EF1 c\u1ED1 ho\u1EB7c rehearsal rollback \u0111\u1EC3 tr\u1EA3i nghi\u1EC7m r\u1ED9ng h\u01A1n v\u1EC1 \u0111\u1ED9 tin c\u1EADy d\u1ECBch v\u1EE5.",
              "L\xE0m r\xF5 t\xE1c \u0111\u1ED9ng: Nh\u1EAFc r\xF5 c\xE1c k\u1EBFt qu\u1EA3 nh\u01B0 c\u1EA3i thi\u1EC7n CI v\xE0 r\xFAt ng\u1EAFn th\u1EDDi gian tri\u1EC3n khai trong retro nh\xF3m \u0111\u1EC3 h\u1ECD th\u1EA5y \u0111\xF3ng g\xF3p k\u1EF9 thu\u1EADt g\u1EAFn v\u1EDBi k\u1EBFt qu\u1EA3 t\u1ED5 ch\u1EE9c."
            ]
          },
          collaboration: {
            recommendations: [
              "Nh\u1ECBp \u0111\u1ED3ng b\u1ED9 ban \u0111\u1EA7u: C\u1ED1 \u0111\u1ECBnh m\u1ED9t kickoff ng\u1EAFn v\u1EDBi c\xE1c nh\xF3m li\xEAn quan \u0111\u1EC3 th\u1ED1ng nh\u1EA5t m\u1EE5c ti\xEAu, ph\u1EA1m vi tr\xE1ch nhi\u1EC7m v\xE0 ti\xEAu ch\xED ho\xE0n th\xE0nh tr\u01B0\u1EDBc khi b\u1EAFt \u0111\u1EA7u.",
              "B\u1ED1i c\u1EA3nh thay \u0111\u1ED5i \u01B0u ti\xEAn: Khi \u01B0u ti\xEAn thay \u0111\u1ED5i, h\xE3y chia s\u1EBB l\xFD do, k\u1EBFt qu\u1EA3 k\u1EF3 v\u1ECDng v\xE0 vi\u1EC7c n\xE0o s\u1EBD gi\u1EA3m \u01B0u ti\xEAn trong tu\u1EA7n \u0111\xF3 \u0111\u1EC3 gi\u1EA3m l\xE0m l\u1EA1i.",
              "L\xE0m r\xF5 \u0111i\u1EC3m ch\u1EA1m h\u1EE3p t\xE1c: X\xE1c \u0111\u1ECBnh tr\u01B0\u1EDBc ng\u01B0\u1EDDi ra quy\u1EBFt \u0111\u1ECBnh v\xE0 k\xEAnh trao \u0111\u1ED5i b\u1EA5t \u0111\u1ED3ng b\u1ED9 \u0111\u1EC3 c\xE2u h\u1ECFi kh\xF4ng b\u1ECB ph\xE2n t\xE1n."
            ]
          }
        },
        summary: {
          strengths: ["Tinh th\u1EA7n s\u1EDF h\u1EEFu", "T\u01B0 duy h\u1EC7 th\u1ED1ng"],
          weaknesses: ["Th\u1EDDi gian review", "\u0110\u1ED3ng b\u1ED9 li\xEAn nh\xF3m"],
          growthGoals: [
            "X\u1EED l\xFD d\u1EEF li\u1EC7u quy m\xF4 l\u1EDBn",
            "V\u1EADn h\xE0nh c\u01A1 s\u1EDF d\u1EEF li\u1EC7u"
          ],
          growthSkills: ["T\u1ED1i \u01B0u truy v\u1EA5n", "\u1EE8ng ph\xF3 s\u1EF1 c\u1ED1"],
          workContents: [
            "\u0110\u1ED9 tin c\u1EADy n\u1EC1n t\u1EA3ng",
            "\u1ED4n \u0111\u1ECBnh m\xF4-\u0111un thanh to\xE1n"
          ],
          workBlockers: [
            "L\u1ECBch release g\u1EA5p",
            "Nh\xF3m reviewer h\u1EA1n ch\u1EBF"
          ],
          workContribution: [
            "R\xFAt ng\u1EAFn th\u1EDDi gian tri\u1EC3n khai",
            "C\u1EA3i thi\u1EC7n pipeline CI"
          ],
          motivationFactors: ["Th\u1EED th\xE1ch k\u1EF9 thu\u1EADt", "T\u1EF1 ch\u1EE7"],
          demotivationFactors: [
            "Chuy\u1EC3n \u0111\u1ED5i ng\u1EEF c\u1EA3nh th\u01B0\u1EDDng xuy\xEAn",
            "Thay \u0111\u1ED5i \u01B0u ti\xEAn ch\u01B0a r\xF5"
          ],
          personalEmotion: ["\u1ED4n \u0111\u1ECBnh", "T\xEDch c\u1EF1c"],
          personalNetwork: ["Coi tr\u1ECDng h\u1EE3p t\xE1c \u0111\u1ED3ng nghi\u1EC7p"],
          personalInterest: ["Du l\u1ECBch", "C\u1ED9ng \u0111\u1ED3ng h\u1ECDc t\u1EADp"],
          growthInterestedProjects: [
            "Pipeline d\u1EEF li\u1EC7u l\u01B0u l\u01B0\u1EE3ng cao"
          ],
          flightRiskLevel: "Th\u1EA5p",
          flightRiskSummary: "M\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt \u1ED5n \u0111\u1ECBnh khi c\xF3 c\u01A1 h\u1ED9i ph\xE1t tri\u1EC3n k\u1EF9 thu\u1EADt r\xF5 r\xE0ng."
        },
        analysis: {
          motivation: {
            factors: [
              "\u0110\u1ED9 kh\xF3 k\u1EF9 thu\u1EADt: H\u1ECD h\u1EE9ng th\xFA h\u01A1n v\u1EDBi vi\u1EC7c t\xECm \u0111i\u1EC3m ngh\u1EBDn hi\u1EC7u n\u0103ng v\xE0 n\xE2ng \u0111\u1ED9 tin c\u1EADy h\u1EC7 th\u1ED1ng so v\u1EDBi c\xE1c nhi\u1EC7m v\u1EE5 t\xEDnh n\u0103ng l\u1EB7p l\u1EA1i. \u0110\u1ED9ng l\u1EF1c t\u0103ng l\xEAn khi h\u1ECD c\xF3 th\u1EC3 l\u1EA7n theo lu\u1ED3ng d\u1EEF li\u1EC7u, gi\u1EA3i th\xEDch nguy\xEAn nh\xE2n v\xE0 x\xE1c nh\u1EADn c\u1EA3i thi\u1EC7n b\u1EB1ng ch\u1EC9 s\u1ED1.",
              "T\u1EF1 ch\u1EE7 v\xE0 tin t\u01B0\u1EDFng: H\u1ECD ph\u1EA3n h\u1ED3i t\xEDch c\u1EF1c khi \u0111\u01B0\u1EE3c t\u1EF1 ph\xE1t hi\u1EC7n c\u01A1 h\u1ED9i c\u1EA3i thi\u1EC7n, \u0111\u1EC1 xu\u1EA5t c\xE1ch l\xE0m v\xE0 ch\u1ECBu tr\xE1ch nhi\u1EC7m th\u1EF1c thi. M\u1EE5c ti\xEAu r\xF5 r\xE0ng \u0111i k\xE8m quy\u1EC1n quy\u1EBFt \u0111\u1ECBnh gi\xFAp nh\u1ECBp l\xE0m vi\u1EC7c \u1ED5n \u0111\u1ECBnh h\u01A1n.",
              "T\xE1c \u0111\u1ED9ng nh\xECn th\u1EA5y \u0111\u01B0\u1EE3c: Nh\u1EEFng k\u1EBFt qu\u1EA3 nh\u01B0 r\xFAt ng\u1EAFn th\u1EDDi gian CI ho\u1EB7c l\xE0m r\xF5 \u0111\u01B0\u1EDDng rollback t\u1EA1o th\xEAm \u0111\u1ED9ng l\u1EF1c v\xEC l\u1EE3i \xEDch \u0111\u01B0\u1EE3c c\u1EA3 nh\xF3m nh\xECn th\u1EA5y."
            ],
            demotivationFactors: [
              "Chuy\u1EC3n \u0111\u1ED5i ng\u1EEF c\u1EA3nh th\u01B0\u1EDDng xuy\xEAn: G\u1EA7n cu\u1ED1i release, \u1ED5n \u0111\u1ECBnh thanh to\xE1n, review v\xE0 c\xE2u h\u1ECFi v\u1EADn h\xE0nh c\xF3 th\u1EC3 \u0111\u1EBFn c\xF9ng l\xFAc, l\xE0m \u0111\u1EE9t m\u1EA1ch t\u1EADp trung v\xE0 nhanh ch\xF3ng t\u0103ng m\u1EC7t m\u1ECFi.",
              "Thay \u0111\u1ED5i \u01B0u ti\xEAn ch\u01B0a r\xF5: Khi y\xEAu c\u1EA7u t\u1EEB nh\xF3m li\xEAn quan \u0111\u1EBFn mu\u1ED9n m\xE0 thi\u1EBFu b\u1ED1i c\u1EA3nh ho\u1EB7c ti\xEAu ch\xED ho\xE0n th\xE0nh, h\u1ECD kh\xF3 \u0111\xE1nh gi\xE1 m\u1EE9c ch\u1EA5t l\u01B0\u1EE3ng ph\xF9 h\u1EE3p.",
              "Ngh\u1EBDn review: Khi nh\xF3m reviewer h\u1EA1n ch\u1EBF v\xE0 ph\u1EA3n h\u1ED3i \u0111\u1EBFn ch\u1EADm, h\u1ECD ph\u1EA3i kh\xF4i ph\u1EE5c l\u1EA1i b\u1ED1i c\u1EA3nh c\u1EE7a vi\u1EC7c \u0111\xE3 ho\xE0n th\xE0nh v\xE0 c\xE1c vi\u1EC7c ti\u1EBFp theo c\u0169ng b\u1ECB ch\u1EADm."
            ]
          },
          strengthWeakness: {
            strengths: [
              "Tinh th\u1EA7n s\u1EDF h\u1EEFu: H\u1ECD theo s\xE1t c\xF4ng vi\u1EC7c \u0111\u1ED9 tin c\u1EADy t\u1EEB thi\u1EBFt k\u1EBF, tri\u1EC3n khai \u0111\u1EBFn theo d\xF5i sau rollout v\xE0 ti\u1EBFp t\u1EE5c ki\u1EC3m tra xem k\u1EBFt qu\u1EA3 c\xF3 duy tr\xEC hay kh\xF4ng.",
              "T\u01B0 duy h\u1EC7 th\u1ED1ng: H\u1ECD c\xE2n nh\u1EAFc m\u1ED7i thay \u0111\u1ED5i \u1EA3nh h\u01B0\u1EDFng th\u1EBF n\xE0o \u0111\u1EBFn pipeline tri\u1EC3n khai, \u1EE9ng ph\xF3 s\u1EF1 c\u1ED1 v\xE0 chi ph\xED b\u1EA3o tr\xEC d\xE0i h\u1EA1n.",
              "T\xE0i li\u1EC7u h\xF3a v\xE0 chia s\u1EBB: H\u1ECD s\u1EAFp x\u1EBFp \u0111\u01B0\u1EDDng rollback v\xE0 checkpoint release \u0111\u1EC3 c\u1EA3 nh\xF3m c\xF3 th\u1EC3 ph\u1EA3n \u1EE9ng theo c\xF9ng m\u1ED9t playbook.",
              "Chuy\u1EC3n h\xF3a h\u1ECDc h\u1ECFi: Ngay c\u1EA3 v\u1EDBi v\u1EA5n \u0111\u1EC1 v\u1EADn h\xE0nh DB ch\u01B0a quen, h\u1ECD thu h\u1EB9p nguy\xEAn nh\xE2n b\u1EB1ng ch\u1EC9 s\u1ED1 v\xE0 log r\u1ED3i chuy\u1EC3n th\xE0nh h\u01B0\u1EDBng d\u1EABn cho c\xF4ng vi\u1EC7c sau."
            ],
            weaknesses: [
              "Qu\u1EA3n l\xFD th\u1EDDi gian review: V\xEC h\u1ECD \u0111\xE0o s\xE2u v\xE0o ph\u1EA7n vi\u1EC7c c\u1EE7a m\xECnh, review code cho \u0111\u1ED3ng nghi\u1EC7p c\xF3 th\u1EC3 b\u1ECB \u0111\u1EA9y l\xF9i n\u1EBFu khung review kh\xF4ng \u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7 t\u1EEB s\u1EDBm.",
              "\u0110\u1ED3ng b\u1ED9 h\u1EE3p t\xE1c ban \u0111\u1EA7u: C\xE1c vi\u1EC7c c\xF3 nhi\u1EC1u ph\u1EE5 thu\u1ED9c li\xEAn nh\xF3m s\u1EBD hi\u1EC7u qu\u1EA3 h\u01A1n n\u1EBFu m\u1EE5c ti\xEAu v\xE0 ti\xEAu ch\xED ho\xE0n th\xE0nh \u0111\u01B0\u1EE3c th\u1ED1ng nh\u1EA5t s\u1EDBm h\u01A1n.",
              "\u1EE6y quy\u1EC1n v\u1EA5n \u0111\u1EC1 v\u1EADn h\xE0nh: H\u1ECD c\xF3 xu h\u01B0\u1EDBng t\u1EF1 x\u1EED l\xFD c\xE2u h\u1ECFi kh\u1EA9n c\u1EA5p, n\xEAn c\xE1c b\u01B0\u1EDBc ph\u1EA3n \u1EE9ng l\u1EB7p l\u1EA1i c\u1EA7n \u0111\u01B0\u1EE3c chuy\u1EC3n giao cho nh\xF3m t\xEDch c\u1EF1c h\u01A1n."
            ]
          },
          growth: {
            goals: "H\u1ECD mu\u1ED1n v\u01B0\u1EE3t ra ngo\xE0i vi\u1EC7c tri\u1EC3n khai \u1ED5n \u0111\u1ECBnh v\xE0 tr\u1EF1c ti\u1EBFp x\u1EED l\xFD c\xE1ch c\u1EA5u tr\xFAc l\u01B0u tr\u1EEF/truy xu\u1EA5t d\u1EEF li\u1EC7u t\u1EA1o ra \u0111i\u1EC3m ngh\u1EBDn khi l\u01B0u l\u01B0\u1EE3ng cao. M\u1EE5c ti\xEAu ph\xE1t tri\u1EC3n kh\xF4ng ch\u1EC9 l\xE0 s\u1EEDa m\u1ED9t truy v\u1EA5n, m\xE0 l\xE0 tr\u1EA3i nghi\u1EC7m end-to-end t\u1EEB \u0111\u1ECBnh ngh\u0129a ch\u1EC9 s\u1ED1, ph\xE2n t\xEDch nguy\xEAn nh\xE2n \u0111\u1EBFn ki\u1EC3m ch\u1EE9ng tr\u01B0\u1EDBc v\xE0 sau c\u1EA3i thi\u1EC7n.",
            skills: "H\u1ECD mu\u1ED1n ph\xE1t tri\u1EC3n c\xE1c k\u1EF9 n\u0103ng n\u1EB1m gi\u1EEFa ph\xE1t tri\u1EC3n v\xE0 v\u1EADn h\xE0nh, g\u1ED3m thi\u1EBFt k\u1EBF index DB, \u0111\u1ECDc execution plan, ki\u1EC3m ch\u1EE9ng t\u1EA3i tr\u01B0\u1EDBc release v\xE0 giao ti\u1EBFp trong s\u1EF1 c\u1ED1. V\u1EDBi c\xE1c khu v\u1EF1c c\xF3 t\xE1c \u0111\u1ED9ng l\u1EDBn \u0111\u1EBFn ng\u01B0\u1EDDi d\xF9ng nh\u01B0 thanh to\xE1n v\xE0 \u0111\u1ED9 tin c\u1EADy n\u1EC1n t\u1EA3ng, h\u1ECD c\u1EA7n luy\u1EC7n t\u1EADp c\xE1ch l\xE0m r\xF5 c\u01A1 s\u1EDF ra quy\u1EBFt \u0111\u1ECBnh.",
            interestedProjects: "H\u1ECD quan t\xE2m \u0111\u1EBFn c\xE1c d\u1EF1 \xE1n c\xF3 k\u1EBFt qu\u1EA3 \u0111o \u0111\u01B0\u1EE3c v\xE0 \u1EA3nh h\u01B0\u1EDFng \u0111\u1EBFn c\u1EA3 nh\xF3m, nh\u01B0 kh\u1EA3 n\u0103ng quan s\xE1t pipeline d\u1EEF li\u1EC7u, dashboard theo d\xF5i truy v\u1EA5n ch\u1EADm ho\u1EB7c t\u1EF1 \u0111\u1ED9ng h\xF3a rehearsal rollback."
          },
          workPerformance: {
            contents: "Ph\u1EA1m vi c\xF4ng vi\u1EC7c: H\u1ECD t\u1EADp trung gi\u1EA3m r\u1EE7i ro tr\u01B0\u1EDBc v\xE0 sau release trong \u0111\u1ED9 tin c\u1EADy n\u1EC1n t\u1EA3ng v\xE0 \u1ED5n \u0111\u1ECBnh m\xF4-\u0111un thanh to\xE1n. C\xF4ng vi\u1EC7c bao g\u1ED3m c\u1EA3i thi\u1EC7n pipeline CI, t\xE0i li\u1EC7u h\xF3a \u0111\u01B0\u1EDDng rollback, ki\u1EC3m tra \u0111i\u1EC3m ngh\u1EBDn x\u1EED l\xFD d\u1EEF li\u1EC7u v\xE0 chu\u1EA9n h\xF3a h\u01B0\u1EDBng d\u1EABn ph\u1EA3n \u1EE9ng v\u1EADn h\xE0nh.",
            contribution: [
              "Hi\u1EC7u qu\u1EA3 tri\u1EC3n khai: H\u1ECD lo\u1EA1i b\u1ECF c\xE1c b\u01B0\u1EDBc l\u1EB7p trong pipeline CI, r\xFAt ng\u1EAFn th\u1EDDi gian ch\u1EDD tri\u1EC3n khai v\xE0 l\xE0m r\xF5 checkpoint trong release.",
              "\u0110\u1ED9 tin c\u1EADy v\u1EADn h\xE0nh: H\u1ECD t\xE0i li\u1EC7u h\xF3a \u0111\u01B0\u1EDDng rollback c\u1EE7a m\xF4-\u0111un thanh to\xE1n v\xE0 tr\xECnh t\u1EF1 ki\u1EC3m tra s\u1EF1 c\u1ED1 \u0111\u1EC3 nh\xF3m ph\u1EA3n \u1EE9ng t\u1EEB c\xF9ng m\u1ED9t g\xF3c nh\xECn khi c\xF3 v\u1EA5n \u0111\u1EC1.",
              "Hi\u1EC3n th\u1ECB \u0111i\u1EC3m ngh\u1EBDn d\u1EEF li\u1EC7u: H\u1ECD t\xE1i hi\u1EC7n lu\u1ED3ng truy xu\u1EA5t ch\u1EADm v\xE0 s\u1EAFp x\u1EBFp ch\u1EC9 s\u1ED1 gi\xFAp nh\xF3m th\u1EA3o lu\u1EADn \u01B0u ti\xEAn t\u1ED1i \u01B0u h\xF3a DB.",
              "Ch\u1EA5t l\u01B0\u1EE3ng h\u1EE3p t\xE1c: Trong c\xE1c bu\u1ED5i l\xE0m vi\u1EC7c v\u1EDBi nh\xF3m li\xEAn quan, h\u1ECD t\xE1ch r\xF5 r\xE0ng r\xE0ng bu\u1ED9c k\u1EF9 thu\u1EADt v\xE0 t\xE1c \u0111\u1ED9ng l\u1ECBch tr\xECnh \u0111\u1EC3 cung c\u1EA5p th\xF4ng tin c\u1EA7n cho quy\u1EBFt \u0111\u1ECBnh."
            ],
            blockers: [
              "\xC1p l\u1EF1c l\u1ECBch release: Kho\u1EA3ng \u1ED5n \u0111\u1ECBnh ng\u1EAFn khi\u1EBFn review v\xE0 retro d\u1EC5 b\u1ECB \u0111\u1EA9y l\xF9i, v\xE0 \xFD t\u01B0\u1EDFng c\u1EA3i thi\u1EC7n c\xF3 th\u1EC3 tr\xF4i sang chu k\u1EF3 sau.",
              "Thi\u1EBFu reviewer: Ch\u1EC9 m\u1ED9t s\u1ED1 \xEDt reviewer hi\u1EC3u r\xF5 khu v\u1EF1c n\xE0y, khi\u1EBFn th\u1EDDi gian ch\u1EDD ph\u1EA3n h\u1ED3i d\xE0i h\u01A1n v\xE0 chi ph\xED chia s\u1EBB b\u1ED1i c\u1EA3nh cho thay \u0111\u1ED5i ph\u1EE9c t\u1EA1p t\u0103ng l\xEAn.",
              "Ph\u1EE5 thu\u1ED9c li\xEAn nh\xF3m: C\xF4ng vi\u1EC7c thanh to\xE1n v\xE0 n\u1EC1n t\u1EA3ng ph\u1EE5 thu\u1ED9c v\xE0o l\u1ECBch tr\xECnh v\xE0 \u0111\u1ECBnh ngh\u0129a d\u1EEF li\u1EC7u c\u1EE7a nh\xF3m li\xEAn quan, n\xEAn \u0111\u1ED3ng b\u1ED9 mu\u1ED9n d\u1EC5 d\u1EABn \u0111\u1EBFn \u0111i\u1EC1u ch\u1EC9nh ph\u1EA1m vi l\u1EB7p l\u1EA1i."
            ]
          },
          personal: {
            emotionWellbeing: "Tr\u1EA1ng th\xE1i c\u1EA3m x\xFAc t\u1ED5ng th\u1EC3 \u1ED5n \u0111\u1ECBnh v\xE0 th\xE1i \u0111\u1ED9 v\u1EDBi c\xF4ng vi\u1EC7c t\xEDch c\u1EF1c. Tuy nhi\xEAn trong tu\u1EA7n release, c\xE2u h\u1ECFi kh\u1EA9n c\u1EA5p v\xE0 review ch\u1ED3ng l\xEAn nhau t\u1EA1o t\xEDn hi\u1EC7u m\u1EC7t m\u1ECFi, n\xEAn th\u1EDDi gian h\u1ED3i ph\u1EE5c sau c\xE1c vi\u1EC7c \u1ED5n \u0111\u1ECBnh quan tr\u1ECDng s\u1EBD gi\xFAp duy tr\xEC t\u1EADp trung.",
            network: "H\u1ECD th\xEDch th\u1EA3o lu\u1EADn k\u1EF9 thu\u1EADt v\u1EDBi \u0111\u1ED3ng nghi\u1EC7p v\xE0 ch\u1EE7 \u0111\u1ED9ng h\u01A1n trong vi\u1EC7c nh\u1EDD h\u1ED7 tr\u1EE3 ho\u1EB7c \u0111\u01B0a \xFD ki\u1EBFn khi b\u1ED1i c\u1EA3nh \u0111\u01B0\u1EE3c chia s\u1EBB s\u1EDBm. V\u1EDBi c\xE1c vi\u1EC7c n\u1EC1n t\u1EA3ng/thanh to\xE1n c\xF3 nhi\u1EC1u nh\xF3m tham gia, h\u1ECD th\u01B0\u1EDDng d\xF9ng ph\u1EA7n t\xF3m t\u1EAFt \u0111\xE3 vi\u1EBFt l\xE0m c\u01A1 s\u1EDF h\u1EE3p t\xE1c.",
            interest: "H\u1ECD th\xEDch du l\u1ECBch v\xE0 c\xE1c c\u1ED9ng \u0111\u1ED3ng h\u1ECDc t\u1EADp k\u1EF9 thu\u1EADt, \u0111\u1ED3ng th\u1EDDi th\u01B0\u1EDDng k\u1EBFt n\u1ED1i n\u0103ng l\u01B0\u1EE3ng ngo\xE0i c\xF4ng vi\u1EC7c v\u1EDBi h\u1ECDc h\u1ECFi v\xE0 gi\u1EA3i quy\u1EBFt v\u1EA5n \u0111\u1EC1. G\u1EA7n \u0111\xE2y h\u1ECD quan t\xE2m nhi\u1EC1u \u0111\u1EBFn \u0111\u1ED9 tin c\u1EADy v\u1EADn h\xE0nh, x\u1EED l\xFD d\u1EEF li\u1EC7u v\xE0 c\u1EA3i thi\u1EC7n n\u0103ng su\u1EA5t ph\xE1t tri\u1EC3n."
          },
          nextTopics: [
            "D\u1EF1 \xE1n t\u1ED1i \u01B0u h\xF3a DB: th\u1ED1ng nh\u1EA5t ph\u1EA1m vi ph\xE2n t\xEDch \u0111i\u1EC3m ngh\u1EBDn v\xE0 ch\u1EC9 s\u1ED1 th\xE0nh c\xF4ng h\u1ECD c\xF3 th\u1EC3 s\u1EDF h\u1EEFu trong qu\xFD n\xE0y.",
            "Th\u1EDDi gian review: th\u1ED1ng nh\u1EA5t khung review \u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7 v\xE0 c\xE1ch ph\xE2n b\u1ED5 reviewer tr\u01B0\u1EDBc release ti\u1EBFp theo.",
            "Ph\u1EA3n \u1EE9ng v\u1EADn h\xE0nh: li\u1EC7t k\xEA c\xE1c c\xE2u h\u1ECFi thanh to\xE1n/n\u1EC1n t\u1EA3ng l\u1EB7p l\u1EA1i c\xF3 th\u1EC3 t\xE0i li\u1EC7u h\xF3a ho\u1EB7c t\u1EF1 \u0111\u1ED9ng h\xF3a.",
            "Ph\u1EA3n h\u1ED3i ph\xE1t tri\u1EC3n: \u0111\u01B0a ph\u1EA3n h\u1ED3i c\u1EE5 th\u1EC3 v\u1EC1 vi\u1EC7c c\u1EA3i thi\u1EC7n CI v\xE0 \u1ED5n \u0111\u1ECBnh h\u1EC7 th\u1ED1ng \u0111\xE3 \u1EA3nh h\u01B0\u1EDFng th\u1EBF n\xE0o \u0111\u1EBFn k\u1EBFt qu\u1EA3 c\u1EE7a nh\xF3m."
          ],
          flightRiskDescription: "Nguy c\u01A1 r\u1EDDi \u0111i hi\u1EC7n \u1EDF m\u1EE9c th\u1EA5p. H\u1ECD \u0111ang x\u1EED l\xFD c\xE1c v\u1EA5n \u0111\u1EC1 k\u1EF9 thu\u1EADt c\xF3 \xFD ngh\u0129a, v\xE0 c\xE1c c\u1EA3i thi\u1EC7n c\u1EE7a h\u1ECD li\xEAn k\u1EBFt r\xF5 v\u1EDBi tri\u1EC3n khai nhanh h\u01A1n v\xE0 \u0111\u1ED9 tin c\u1EADy t\u1ED1t h\u01A1n, gi\xFAp m\u1EE9c \u0111\u1ED9 g\u1EAFn k\u1EBFt \u1ED5n \u0111\u1ECBnh. Tuy nhi\xEAn \xE1p l\u1EF1c release, ngh\u1EBDn review v\xE0 \u01B0u ti\xEAn li\xEAn nh\xF3m ch\u01B0a r\xF5 n\u1EBFu l\u1EB7p l\u1EA1i c\xF3 th\u1EC3 t\xEDch l\u0169y m\u1EC7t m\u1ECFi, n\xEAn c\u1EA7n thi\u1EBFt k\u1EBF \u0111\u1ED3ng th\u1EDDi nhi\u1EC7m v\u1EE5 ph\xE1t tri\u1EC3n v\xE0 th\u1EDDi gian review \u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7."
        },
        sidePanel: {
          actionItems: {
            shareRollbackPlan: "Chia s\u1EBB k\u1EBF ho\u1EA1ch rollback release",
            prepareDbOptimizationScope: "So\u1EA1n ph\u1EA1m vi t\u1ED1i \u01B0u h\xF3a c\u01A1 s\u1EDF d\u1EEF li\u1EC7u",
            alignReviewWindow: "Th\u1ED1ng nh\u1EA5t khung review \u0111\u01B0\u1EE3c b\u1EA3o v\u1EC7 tr\u01B0\u1EDBc release"
          }
        },
        createDialog: {
          meetings: {
            q2Okr: "Ki\u1EC3m tra gi\u1EEFa k\u1EF3 OKR Q2",
            paymentRetro: "Nh\xECn l\u1EA1i vi\u1EC7c \u1ED5n \u0111\u1ECBnh m\xF4-\u0111un thanh to\xE1n",
            growthPlan: "C\u0103n ch\u1EC9nh k\u1EBF ho\u1EA1ch ph\xE1t tri\u1EC3n"
          }
        }
      }
    }
  },
  features: {
    dashboard: {
      title: "Xem t\u1ED5ng quan t\xECnh tr\u1EA1ng 1on1 c\u1EE7a t\u1ED5 ch\u1EE9c.",
      description: "Xem t\u1EF7 l\u1EC7 th\u1EF1c hi\u1EC7n t\u1ED5ng th\u1EC3 v\xE0 t\xECnh tr\u1EA1ng chi ti\u1EBFt c\u1EE7a t\u1EEBng c\xE1 nh\xE2n tr\xEAn b\u1EA3ng \u0111i\u1EC1u khi\u1EC3n.",
      steps: {
        overview: {
          title: "Xem t\u1ED5ng quan t\xECnh tr\u1EA1ng 1on1",
          description: "Ki\u1EC3m tra t\u1EF7 l\u1EC7 th\u1EF1c hi\u1EC7n, chu k\u1EF3 h\u1ECDp trung b\xECnh, s\u1ED1 bu\u1ED5i \u0111\xE3 ho\xE0n th\xE0nh v\xE0 Vital \u1EDF c\xF9ng m\u1ED9t n\u01A1i."
        },
        table: {
          title: "Xem theo qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn",
          description: "D\xF9ng b\u1EA3ng \u0111\u1EC3 so s\xE1nh t\xECnh tr\u1EA1ng theo qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn, r\u1ED3i m\u1EDF trang chi ti\u1EBFt t\u1EEB t\xEAn."
        },
        detail: {
          title: "T\xECm hi\u1EC3u s\xE2u \u1EDF trang chi ti\u1EBFt",
          description: "Xem t\xECnh tr\u1EA1ng 1on1 v\u1EDBi vai tr\xF2 qu\u1EA3n l\xFD v\xE0 th\xE0nh vi\xEAn, xu h\u01B0\u1EDBng Vital v\xE0 t\xF3m t\u1EAFt t\u1EEBng bu\u1ED5i h\u1ECDp."
        }
      }
    },
    member_report: {
      title: "T\u1EA1o b\xE1o c\xE1o v\u1EC1 th\xE0nh vi\xEAn t\u1EEB d\u1EEF li\u1EC7u 1on1",
      description: "AI \u0111\u1ECDc c\xE1c cu\u1ED9c tr\xF2 chuy\u1EC7n 1on1 v\xE0 t\u1ED5ng h\u1EE3p b\xE1o c\xE1o ph\xF9 h\u1EE3p v\u1EC1 c\xE1ch ch\u0103m s\xF3c th\xE0nh vi\xEAn, \u0111\u1ED9ng l\u1EF1c, \u0111i\u1EC3m m\u1EA1nh v\xE0 \u0111\xF3ng g\xF3p.",
      steps: {
        create: {
          title: "T\u1EA1o b\xE1o c\xE1o m\u1EDBi",
          description: "T\u1EA1o b\xE1o c\xE1o \u0111\u1EC3 hi\u1EC3u s\xE2u h\u01A1n v\u1EC1 th\xE0nh vi\xEAn d\u1EF1a tr\xEAn c\xE1c bu\u1ED5i 1on1 tr\u01B0\u1EDBc \u0111\xE2y."
        },
        select: {
          title: "Ch\u1ECDn kho\u1EA3ng th\u1EDDi gian v\xE0 bu\u1ED5i h\u1ECDp",
          description: "Ch\u1ECDn c\xE1c bu\u1ED5i 1on1 l\xE0m d\u1EEF li\u1EC7u cho b\xE1o c\xE1o, r\u1ED3i nh\u1EA5n t\u1EA1o b\xE1o c\xE1o."
        },
        insights: {
          title: "Xem th\xF4ng tin AI",
          description: "Xem th\xF4ng tin t\u1ED5ng h\u1EE3p, r\u1ED3i ti\u1EBFp t\u1EE5c \u0111\u1EBFn ch\u1EE7 \u0111\u1EC1 1on1 ti\u1EBFp theo v\xE0 m\u1EE5c h\xE0nh \u0111\u1ED9ng."
        }
      }
    },
    ninebox: {
      title: "Qu\u1EA3n l\xFD nh\xE2n t\xE0i v\u1EDBi 9Box",
      description: "Di chuy\u1EC3n th\xE0nh vi\xEAn tr\u1EF1c ti\u1EBFp \u0111\u1EC3 qu\u1EA3n l\xFD ph\xE2n b\u1ED1 nh\xE2n t\xE0i v\xE0 c\xE1c nh\xF3m r\u1EE7i ro c\u1EE7a t\u1ED5 ch\u1EE9c.",
      steps: {
        performance: {
          title: "S\u1EAFp x\u1EBFp theo hi\u1EC7u su\u1EA5t v\xE0 ti\u1EC1m n\u0103ng",
          description: "Ph\xE2n lo\u1EA1i nh\xE2n t\xE0i theo hi\u1EC7u su\u1EA5t v\xE0 ti\u1EC1m n\u0103ng \u0111\u1EC3 bi\u1EBFt ai l\xE0 nh\xE2n t\xE0i ch\u1EE7 ch\u1ED1t c\u1EE7a t\u1ED5 ch\u1EE9c."
        },
        switch: {
          title: "Chuy\u1EC3n \u0111\u1ED5i ma tr\u1EADn",
          description: "D\xF9ng c\xE1c tab ph\xEDa tr\xEAn \u0111\u1EC3 chuy\u1EC3n \u0111\u1ED5i gi\u1EEFa hai g\xF3c nh\xECn qu\u1EA3n l\xFD nh\xE2n t\xE0i."
        },
        flightRisk: {
          title: "S\u1EAFp x\u1EBFp tr\xEAn ma tr\u1EADn nguy c\u01A1 r\u1EDDi \u0111i",
          description: "X\xE1c \u0111\u1ECBnh th\xE0nh vi\xEAn c\u1EA7n ch\xFA \xFD d\u1EF1a tr\xEAn t\xE1c \u0111\u1ED9ng n\u1EBFu h\u1ECD r\u1EDDi t\u1ED5 ch\u1EE9c."
        }
      }
    }
  },
  update_nudge: {
    title: "Ph\u1EA3n h\u1ED3i d\xE0nh cho b\u1EA1n sau m\u1ED7i cu\u1ED9c h\u1ECDp",
    description: "Khi bu\u1ED5i 1on1 c\xF3 ghi \xE2m \u0111\u01B0\u1EE3c ph\xE2n t\xEDch, th\xE0nh vi\xEAn c\u0169ng s\u1EBD nh\u1EADn \u0111\u01B0\u1EE3c ph\u1EA3n h\u1ED3i cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo.",
    close: "\u0110\xF3ng",
    steps: {
      step1: {
        title: "Ph\u1EA3n h\u1ED3i cho cu\u1ED9c h\u1ECDp ti\u1EBFp theo",
        description: "Nh\u1EEFng \u0111i\u1EC1u b\u1EA1n \u0111\xE3 chu\u1EA9n b\u1ECB cho cu\u1ED9c h\u1ECDp n\xE0y nh\u01B0ng ch\u01B0a k\u1ECBp n\xEAu, ch\u1EE7 \u0111\u1EC1 n\xEAn \u0111\u01B0a ra l\u1EA7n sau, v\xE0 h\u01A1n th\u1EBF n\u1EEFa \u2014 \u0111\u01B0\u1EE3c s\u1EAFp x\u1EBFp th\xE0nh c\xE1c th\u1EBB trong tab Ph\xE2n t\xEDch. Ch\u1EC9 m\xECnh b\u1EA1n xem \u0111\u01B0\u1EE3c ph\u1EA3n h\u1ED3i n\xE0y."
      }
    }
  }
};

// src/locales/vi-VN/index.ts
var vi_VN_default = {
  common: common_default3,
  task: task_default3,
  project: project_default3,
  team: team_default3,
  todo: todo_default3,
  report: report_default3,
  auth: auth_default3,
  comment: comment_default3,
  note: note_default3,
  activity: activity_default3,
  workspace: workspace_default3,
  planner: planner_default3,
  member_invitation: member_invitation_default3,
  landing: landing_default3,
  ai: ai_default3,
  notification: notification_default3,
  dailyscrum: dailyscrum_default3,
  feedback: feedback_default3,
  drive: drive_default3,
  oneonone: oneonone_default3,
  ninebox: ninebox_default3,
  template: template_default3,
  payment: payment_default3,
  aws_marketplace: aws_marketplace_default3,
  dashboard: dashboard_default3,
  profile_card: profile_card_default3,
  meeting_prep: meeting_prep_default3,
  oneonone_notification: oneonone_notification_default3,
  landing_new: landing_new_default3,
  feature_tutorial: feature_tutorial_default3
};

// src/translation/default-pack.json
var default_pack_default = {
  "default-language": "ko-KR",
  "default-translations": {
    "ko-KR": {
      "ko-KR": "\uD55C\uAD6D\uC5B4",
      "en-US": "\uC601\uC5B4",
      "vi-VN": "\uBCA0\uD2B8\uB0A8\uC5B4"
    },
    "en-US": {
      "ko-KR": "Korean",
      "en-US": "English",
      "vi-VN": "Vietnamese"
    },
    "vi-VN": {
      "ko-KR": "Ti\u1EBFng H\xE0n",
      "en-US": "Ti\u1EBFng Anh",
      "vi-VN": "Ti\u1EBFng Vi\u1EC7t"
    }
  }
};

// src/translation/i18n.ts
var resources = {
  "ko-KR": ko_KR_default,
  "en-US": en_US_default,
  "vi-VN": vi_VN_default
};
var toSupportedLanguage = (lng) => {
  if (lng.startsWith("ko")) return "ko-KR";
  if (lng.startsWith("en")) return "en-US";
  if (lng.startsWith("vi")) return "vi-VN";
  return "en-US";
};
var languageDetectorOptions = {
  // 명시적으로 선택한 언어(localStorage)만 따르고, 없으면 기본값(fallbackLng: 한국어)으로 노출.
  // 브라우저 언어(navigator)는 따르지 않음 → 첫 방문·구글봇은 항상 한국어가 기본.
  // 랜딩(SEO)이 없는 앱은 applyBrowserLanguageIfUnset()으로 브라우저 언어를 따를 수 있음.
  order: ["localStorage"],
  checkWhitelist: true,
  // 허용된 언어만 사용
  convertDetectedLanguage: toSupportedLanguage
};
var namespaces = [
  "common",
  "auth",
  "task",
  "project",
  "team",
  "todo",
  "report",
  "comment",
  "note",
  "activity",
  "landing",
  "ai",
  "notification",
  "workspace",
  "oneonone",
  "template",
  "payment",
  "aws_marketplace",
  "oneonone_notification",
  "landing_new",
  "feature_tutorial"
];
i18next.use(LanguageDetector).use(initReactI18next).init({
  resources,
  fallbackLng: default_pack_default["default-language"],
  ns: namespaces,
  defaultNS: "common",
  detection: languageDetectorOptions,
  returnNull: false,
  returnEmptyString: false,
  nsSeparator: ":",
  // name space 구분자
  pluralSeparator: "_",
  // 복수형 구분자 설정
  // {{}} 안에 변수를 넣을 수 있도록 설정
  // ex: t('{{count}}개 선택')
  interpolation: {
    prefix: "{{",
    suffix: "}}",
    escapeValue: false
    // React에서는 이미 XSS 방지를 하므로 false로 설정
  },
  parseMissingKeyHandler(key, defaultValue) {
    console.warn("Missing i18n key:", key, defaultValue);
    return key;
  }
}).then(() => {
  return i18next;
}).catch((error) => {
  console.error("i18next initialization failed:", error);
  throw error;
});
var BROWSER_LNG_APPLIED_KEY = "i18nBrowserLngApplied";
var applyBrowserLanguageIfUnset = () => {
  if (typeof window === "undefined") return;
  if (localStorage.getItem(BROWSER_LNG_APPLIED_KEY)) return;
  localStorage.setItem(BROWSER_LNG_APPLIED_KEY, "1");
  const browserLng = navigator.languages?.[0] ?? navigator.language;
  if (!browserLng) return;
  const lng = toSupportedLanguage(browserLng);
  if (lng !== i18next.language) void i18next.changeLanguage(lng);
};
var i18n_default = i18next;

// src/workspace/workspaceService.ts
var createBaseWorkspaceService = () => {
  return {
    async createWorkspace(name, description, profileImageUrl) {
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        if (!userId) {
          throw new Error(
            i18n_default.t("workspace:error.unauthorized_user")
          );
        }
        const { data: data2, error } = await supabase.rpc("create_workspace", {
          workspace_name: name,
          workspace_description: description || "",
          workspace_profile_image_url: profileImageUrl || void 0,
          user_id: userId
        });
        if (error) {
          throw new Error(
            `${i18n_default.t("workspace:error.create_failed")}, ${error.message}`
          );
        }
        if (!data2 || data2.length === 0) {
          throw new Error(
            i18n_default.t("workspace:error.data_not_found_after_create")
          );
        }
        const workspace = data2[0];
        return await this.getWorkspaceById(workspace.id);
      } catch (error) {
        console.error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0DD\uC131 \uC2E4\uD328:", error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(
          i18n_default.t("workspace:error.unknown_create_error")
        );
      }
    },
    // async getWorkspaceBasicInfo(workspaceId: string): Promise<Workspace> {
    //     const { data, error } = await supabase
    //         .from('workspace')
    //         .select('*') // 멤버, 팀 제외
    //         .eq('id', workspaceId)
    //         .eq('enable', true)
    //         .maybeSingle();
    //     if (error) throw error;
    //     if (!data) {
    //         throw new Error(
    //             `${i18next.t('workspace:error.workspace_not_found')}, ${workspaceId}`
    //         );
    //     }
    //     return data;
    // },
    async getWorkspaceById(workspaceId) {
      try {
        const { data: data2, error } = await supabase.from("workspace").select(
          `*,
                    workspace_member:workspace_member(*, 
                        user!user_id(id, name, profile_image_url, email),
                        leader:user_id(id, name, profile_image_url, email)
                    ),
                    teams:team(*)
                `
        ).eq("enable", true).eq("id", workspaceId).filter("workspace_member.enable", "eq", true).filter("teams.enable", "eq", true);
        if (error) throw error;
        if (!data2) {
          throw new Error(
            `${i18n_default.t("workspace:error.workspace_not_found")}, ${workspaceId}`
          );
        }
        return data2[0];
      } catch (error) {
        console.error(`\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC870\uD68C \uC2E4\uD328 ${workspaceId}`, error);
        throw error;
      }
    },
    async getMyWorkspaces() {
      try {
        const {
          data: { user }
        } = await supabase.auth.getUser();
        if (!user)
          throw new Error(
            i18n_default.t("workspace:error.user_not_logged_in")
          );
        const { data: memberWorkspaces, error: memberError } = await supabase.from("workspace_member").select("workspace_id").eq("user_id", user.id).eq("enable", true);
        if (memberError) throw memberError;
        if (!memberWorkspaces || memberWorkspaces.length === 0) {
          return [];
        }
        const workspaceIds = memberWorkspaces.map(
          (item) => item.workspace_id
        );
        const { data: workspaces, error: workspaceError } = await supabase.from("workspace").select(
          `*,
                    workspace_member:workspace_member(*, 
                        user!user_id(id, name, profile_image_url, email),
                        leader:leader_id(id, name, profile_image_url, email)
                    ),
                    teams:team(*)
                `
        ).eq("enable", true).in("id", workspaceIds).filter("workspace_member.enable", "eq", true).filter("teams.enable", "eq", true);
        if (workspaceError) throw workspaceError;
        if (!workspaces || workspaces.length === 0) {
          return [];
        }
        return workspaces.map((workspace) => ({
          ...workspace,
          workspace_member: workspace.workspace_member?.map((member) => ({
            ...member,
            user: member.user,
            leader: member.leader
          })).sort((a, b) => {
            const nameA = a.user?.name || "";
            const nameB = b.user?.name || "";
            return nameA.localeCompare(nameB, void 0, {
              numeric: true
            });
          }),
          teams: workspace.teams?.map((team) => ({
            ...team
          }))
        }));
      } catch (error) {
        console.error("\uB0B4 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBAA9\uB85D \uC870\uD68C \uC2E4\uD328:", error);
        throw new Error(
          i18n_default.t("workspace:error.failed_to_get_workspace_list")
        );
      }
    },
    async getWorkspaceDetailsById(workspaceId) {
      try {
        const { data: data2, error } = await supabase.from("workspace").select(
          `
                    *,
                    workspace_member!workspace_id(*,
                        user!user_id(id, name, profile_image_url, email)
                    ),
                    teams:team!workspace_id(*, 
                        project_count:project_sharing(count),
                        team_member(*, user:user_id(id, name, profile_image_url, email))
                    ),
                    workspace_core_value!workspace_id(*)
                `
        ).eq("id", workspaceId).eq("enable", true).filter("workspace_member.enable", "eq", true).filter("teams.enable", "eq", true).filter("teams.team_member.enable", "eq", true).filter("workspace_core_value.enable", "eq", true);
        if (error) throw error;
        const workspaceWithDetails = data2?.map((workspace) => ({
          ...workspace,
          workspace_member: workspace.workspace_member?.map((member) => ({
            ...member,
            user: member.user
          })).sort(
            (a, b) => a.user.name.localeCompare(b.user.name, "ko")
          ) || [],
          teams: workspace.teams?.map((team) => ({
            ...team,
            project_count: team.project_count?.[0]?.count || 0,
            team_member: team.team_member?.map((member) => ({
              ...member,
              user: member.user
            })).sort((a, b) => {
              const nameA = a.user?.name || "";
              const nameB = b.user?.name || "";
              return nameA.localeCompare(
                nameB,
                void 0,
                {
                  numeric: true
                }
              );
            }) || []
          })) || []
        }))?.[0] || null;
        return workspaceWithDetails;
      } catch (error) {
        console.error(
          `\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4\uC758 \uC0C1\uC138 \uC815\uBCF4 \uC870\uD68C \uC2E4\uD328 ${workspaceId}`,
          error
        );
        throw error;
      }
    },
    async updateWorkspace(id, name, description, profileImageUrl) {
      try {
        const result = await supabase.from("workspace").update({
          name,
          description,
          profile_image_url: profileImageUrl
        }).eq("id", id).select();
        if (result.error) {
          throw new Error(i18n_default.t("workspace:error.update_failed"));
        }
        if (!result.data || result.data.length === 0) {
          throw new Error(
            i18n_default.t("workspace:error.data_not_found_after_update")
          );
        }
        const workspace = result.data[0];
        return workspace;
      } catch (error) {
        console.error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328:", error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(
          i18n_default.t("workspace:error.unknown_update_error")
        );
      }
    },
    async deleteWorkspace(workspaceId) {
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        if (!userId) {
          throw new Error(
            i18n_default.t("workspace:error.unauthorized_user")
          );
        }
        const { data: blockingSubscription, error: subscriptionError } = await supabase.from("workspace_subscription").select("id").eq("workspace_id", workspaceId).in("status", [
          "ACTIVE",
          "AT_RISK",
          "PAUSED",
          "PENDING_CANCELLATION"
        ]).limit(1).maybeSingle();
        if (subscriptionError) throw subscriptionError;
        if (blockingSubscription) {
          throw new Error(
            i18n_default.t(
              "workspace:error.delete_blocked_by_subscription"
            )
          );
        }
        const now = (/* @__PURE__ */ new Date()).toISOString();
        const { error, count } = await supabase.from("workspace").update(
          {
            enable: false,
            update_date: now,
            update_id: userId
          },
          { count: "exact" }
        ).eq("id", workspaceId).eq("enable", true);
        if (error) throw error;
        if (count !== 1) {
          throw new Error(i18n_default.t("workspace:error.delete_failed"));
        }
        return workspaceId;
      } catch (error) {
        console.error(`\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C \uC2E4\uD328 ${workspaceId}`, error);
        throw error;
      }
    },
    async getWorkspaceMembers(workspaceId) {
      try {
        const { data: data2, error } = await supabase.from("workspace_member").select(
          `
                    *,
                    user!user_id(
                        id,
                        name,
                        profile_image_url,
                        email
                    ),
                    leader:user_id(
                        id,
                        name,
                        profile_image_url,
                        email
                    )
                `
        ).eq("workspace_id", workspaceId).eq("enable", true);
        if (error) throw error;
        return data2 || [];
      } catch (error) {
        console.error(
          `\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84 \uC870\uD68C \uC2E4\uD328 ${workspaceId}`,
          error
        );
        throw error;
      }
    },
    /**
     * 워크스페이스 멤버 목록 조회 (페이징 + 검색)
     */
    async getWorkspaceMembersPaginated(workspaceId, page, limit = 10, search, teamIds) {
      try {
        const searchParams = {
          workspaceId,
          page,
          limit
        };
        if (search?.trim()) {
          searchParams.search = search.trim();
        }
        if (teamIds?.length) {
          searchParams.teamIds = teamIds.join(",");
        }
        const response = await apiClient.get("workspace/members", { searchParams }).json();
        return { data: response.data, meta: response.meta };
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw new Error(error.message);
        }
        throw error;
      }
    },
    /**
     * 단일 멤버 조회
     * @param workspaceId 워크스페이스 ID
     * @param userId 조회할 멤버의 UID
     */
    async getWorkspaceMember({
      workspaceId,
      userId
    }) {
      const { data: data2, error } = await supabase.from("workspace_member").select(`*, user!user_id (id, name, profile_image_url, email)`).eq("workspace_id", workspaceId).eq("user_id", userId).eq("enable", true).single();
      if (error) throw error;
      if (!data2) {
        throw new Error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84 \uC870\uD68C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
      }
      return data2;
    },
    async addWorkspaceMember(workspaceId, email, role = "member", leaderId, registerId) {
      try {
        let workspaceMemberId = "";
        const { data: userData, error: userError } = await supabase.from("user").select("id").eq("email", email).maybeSingle();
        if (userError) throw userError;
        if (!userData) {
          throw new Error(
            `${i18n_default.t(
              "workspace:error.user_not_found_for_invitation"
            )}, ${email}`
          );
        }
        const { data: existingMember, error: memberCheckError } = await supabase.from("workspace_member").select("id, enable").eq("workspace_id", workspaceId).eq("user_id", userData.id).maybeSingle();
        if (memberCheckError) throw memberCheckError;
        if (existingMember) {
          if (existingMember.enable) {
            throw new Error(
              i18n_default.t("workspace:error.user_already_member")
            );
          } else {
            workspaceMemberId = existingMember.id;
          }
        }
        const actualRegisterId = registerId || (await supabase.auth.getUser()).data.user?.id || "";
        const now = (/* @__PURE__ */ new Date()).toISOString();
        const { data: data2, error: insertError } = await supabase.from("workspace_member").upsert(
          {
            id: workspaceMemberId || crypto.randomUUID(),
            workspace_id: workspaceId,
            user_id: userData.id,
            role,
            register_id: actualRegisterId,
            register_date: now,
            update_id: actualRegisterId,
            update_date: now,
            enable: true,
            ...leaderId ? { leader_id: leaderId } : {}
          },
          {
            onConflict: "workspace_id,user_id",
            ignoreDuplicates: false
            // 충돌 발생 시 기존 레코드를 새 값으로 업데이트
          }
        ).select();
        if (insertError) throw insertError;
        return data2[0];
      } catch (error) {
        console.error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84 \uCD08\uB300 \uC2E4\uD328:", error);
        throw error;
      }
    },
    async removeWorkspaceMember(workspaceId, userId) {
      try {
        await apiClient.delete(`workspace/members/${userId}`, {
          searchParams: { workspaceId }
        }).json();
        return true;
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw new Error(error.message);
        }
        throw error;
      }
    },
    async updateWorkspaceMemberRole(workspaceId, userId, role) {
      try {
        await apiClient.put(`workspace/members/${userId}/role`, {
          json: { workspaceId, role }
        }).json();
        return true;
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw new Error(error.message);
        }
        throw error;
      }
    },
    /**
     * 워크스페이스 오너 변경 rpc 함수 호출
     */
    async transferWorkspaceOwnership(workspaceId, oldOwnerId, newOwnerId) {
      try {
        const { error } = await supabase.rpc(
          "transfer_workspace_ownership",
          {
            p_workspace_id: workspaceId,
            p_old_owner_id: oldOwnerId,
            p_new_owner_id: newOwnerId
          }
        );
        if (error) {
          throw new Error(
            i18n_default.t("workspace.error.transfer_ownership_failed")
          );
        }
        return true;
      } catch (e) {
        console.error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC624\uB108 \uAD8C\uD55C \uC591\uB3C4 \uC2E4\uD328:", e);
        throw e;
      }
    },
    /**
     * 사용자의 워크스페이스 role 단일 조회
     */
    async getUserRoleInWorkspace(workspaceId, userId) {
      const { data: data2 } = await supabase.from("workspace_member").select("role").eq("workspace_id", workspaceId).eq("user_id", userId).eq("enable", true).single();
      return data2?.role ?? null;
    },
    /**
     * 팀 ID를 통해 해당 팀이 속한 워크스페이스의 멤버인지 확인합니다.
     * @param teamId 확인할 팀 ID
     * @param userId 확인할 사용자 ID
     * @returns 워크스페이스 멤버십 여부와 관련 정보를 담은 객체
     */
    async checkUserWorkspaceMembership(teamId, userId) {
      try {
        const { data: data2, error } = await supabase.from("user_team_access").select("workspace_id").eq("team_id", teamId).eq("user_id", userId).maybeSingle();
        if (error) {
          console.error("\uD300 \uC811\uADFC \uAD8C\uD55C \uD655\uC778 \uC2E4\uD328:", error);
          return {
            isAllowed: false,
            error: i18n_default.t(
              "workspace:error.cannot_check_team_access"
            )
          };
        }
        return {
          isAllowed: !!data2,
          workspaceId: data2?.workspace_id || void 0
        };
      } catch (error) {
        console.error("\uD300 \uC811\uADFC \uAD8C\uD55C \uD655\uC778 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        return {
          isAllowed: false,
          error: i18n_default.t("workspace:error.access_check_failed")
        };
      }
    },
    /**
     * 목표 ID를 통해 해당 목표가 속한 팀의 워크스페이스 정보를 조회합니다.
     * @param goalId 조회할 목표 ID
     * @returns 워크스페이스 정보
     */
    async getWorkspaceByGoalId(goalId) {
      try {
        const { data: goalData, error: goalError } = await supabase.from("goal").select("team_id").eq("id", goalId).eq("enable", true).single();
        if (goalError || !goalData?.team_id) {
          console.error(
            "\uBAA9\uD45C \uC815\uBCF4 \uC870\uD68C \uC2E4\uD328:",
            goalError ? goalError.message : "\uD300 ID \uC5C6\uC74C"
          );
          return null;
        }
        const teamId = goalData.team_id;
        const { data: teamData, error: teamError } = await supabase.from("team").select("workspace_id").eq("id", teamId).eq("enable", true).single();
        if (teamError || !teamData?.workspace_id) {
          console.error(
            "\uD300 \uC815\uBCF4 \uC870\uD68C \uC2E4\uD328:",
            teamError ? teamError.message : "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 ID \uC5C6\uC74C"
          );
          return null;
        }
        const workspaceId = teamData.workspace_id;
        return await this.getWorkspaceById(workspaceId);
      } catch (error) {
        console.error(
          "\uBAA9\uD45C ID\uB85C \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC870\uD68C \uC2E4\uD328:",
          error instanceof Error ? error.message : String(error)
        );
        return null;
      }
    },
    /**
     * 목표 ID와 사용자 ID를 통해 해당 사용자가 목표가 속한 워크스페이스의 멤버인지 확인합니다.
     * @param goalId 확인할 목표 ID
     * @param userId 확인할 사용자 ID
     * @returns 워크스페이스 멤버십 여부(boolean)
     */
    async checkWorkspaceMembershipByGoalId(goalId, userId) {
      try {
        const workspaceData = await this.getWorkspaceByGoalId(goalId);
        if (!workspaceData || !workspaceData.workspace_member)
          return false;
        const targetUserWorkspaceMember = workspaceData.workspace_member.find(
          (member) => member.user && member.user.id === userId
        );
        return !!targetUserWorkspaceMember;
      } catch (error) {
        console.error("\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uC2ED \uD655\uC778 \uC2E4\uD328:", error);
        return false;
      }
    },
    /**
     * 두 사용자가 같은 워크스페이스의 멤버인지 확인합니다.
     * @param userId1 첫 번째 사용자 ID
     * @param userId2 두 번째 사용자 ID
     * @returns 같은 워크스페이스 멤버인지 여부와 관련 정보를 담은 객체
     */
    async checkSameWorkspaceMembership(userId1, userId2) {
      try {
        if (userId1 === userId2) {
          return { isSameWorkspace: true };
        }
        const { data: workspaces1, error: error1 } = await supabase.from("workspace_member").select("workspace_id").eq("user_id", userId1).eq("enable", true);
        if (error1) throw error1;
        if (!workspaces1 || workspaces1.length === 0) {
          return { isSameWorkspace: false };
        }
        const workspaceIds = workspaces1.map(
          (item) => item.workspace_id
        );
        const { data: membership, error: error2 } = await supabase.from("workspace_member").select("id").eq("user_id", userId2).in("workspace_id", workspaceIds).eq("enable", true).limit(1);
        if (error2) throw error2;
        return {
          isSameWorkspace: !!membership && membership.length > 0,
          workspaceIds: !!membership && membership.length > 0 ? workspaceIds : []
        };
      } catch (error) {
        console.error(
          "\uAC19\uC740 \uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBA64\uBC84\uC2ED \uD655\uC778 \uC911 \uC624\uB958 \uBC1C\uC0DD:",
          error
        );
        return {
          isSameWorkspace: false,
          error: error instanceof Error ? error.message : String(error)
        };
      }
    },
    /**
     * 사용자가 오너 권한을 가진 활성화된 워크스페이스가 존재하는지 체크합니다.
     * (유저 탈퇴 시 체크, 존재할 경우 다른 멤버에게 권한 위임하도록 안내합니다.)
     */
    async hasBlockingWorkspace(userId) {
      const { data: ownedWorkspaces, error } = await supabase.from("workspace_member").select(
        `
                    workspace_id,
                    workspace:workspace_id!inner(id, name, enable)
                `
      ).eq("user_id", userId).eq("role", "owner").eq("enable", true).eq("workspace.enable", true);
      if (error) throw error;
      const workspacesWithCounts = await Promise.all(
        (ownedWorkspaces || []).map(async (ws) => {
          const { count } = await supabase.from("workspace_member").select("*", { count: "exact", head: true }).eq("workspace_id", ws.workspace_id).eq("enable", true).neq("user_id", userId);
          return {
            id: ws.workspace_id,
            name: ws.workspace.name,
            otherMembersCount: count || 0
          };
        })
      );
      const blockingWorkspaces = workspacesWithCounts.filter(
        (ws) => ws.otherMembersCount > 0
      );
      const blockingWorkspaceNames = blockingWorkspaces.map((ws) => ws.name).join(", ");
      return {
        hasBlockingWorkspace: blockingWorkspaces.length > 0,
        ownedWorkspaces: blockingWorkspaces,
        errorMessage: blockingWorkspaces.length > 0 ? i18n_default.t(
          "auth:delete_account.notification.error.has_blocking_workspace",
          {
            workspaceNames: blockingWorkspaceNames
          }
        ) : void 0
      };
    }
  };
};

// src/auth/authService.ts
var createBaseAuthService = (i18n2) => {
  return {
    async getCurrentUser() {
      try {
        const {
          data: { session }
        } = await supabase.auth.getSession();
        if (!session) return null;
        const { data: user, error } = await supabase.from("user").select("*").eq("id", session.user.id).single();
        if (error) throw error;
        return user;
      } catch (error) {
        console.error("\uC720\uC800 \uC815\uBCF4 \uC870\uD68C \uC911 \uC5D0\uB7EC \uBC1C\uC0DD:", error);
        return null;
      }
    },
    async checkEmailExists(email) {
      try {
        const { data: exists } = await supabase.rpc(
          "check_email_exists",
          {
            check_email: email.trim()
          }
        );
        return exists === true;
      } catch (error) {
        console.error("\uC774\uBA54\uC77C \uC911\uBCF5 \uD655\uC778 \uC911 \uC5D0\uB7EC \uBC1C\uC0DD:", error);
        return false;
      }
    },
    async login({ email, password }) {
      try {
        const { data: data2, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;
        const user = await this.getCurrentUser();
        if (!user) throw new Error("User not found");
        return user;
      } catch (error) {
        let errorMessage = i18n2.t("common:error.unknown_error");
        if (error instanceof AuthError) {
          switch (error.message) {
            case "Invalid login credentials":
              errorMessage = i18n2.t(
                "auth:sign_up_form.error.invalid_login_credentials"
              );
              break;
            case "Email not confirmed":
              errorMessage = i18n2.t(
                "auth:sign_up_form.error.email_not_confirmed"
              );
              break;
            case "Too many requests":
            case "Request rate limit reached":
              errorMessage = i18n2.t(
                "auth:sign_up_form.error.too_many_requests"
              );
              break;
            default:
              errorMessage = `${i18n2.t("auth:sign_up_form.error.login")}: ${error.message}`;
          }
        }
        console.error("\uB85C\uADF8\uC778 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw new Error(errorMessage);
      }
    },
    async signUp({
      email,
      password,
      name,
      language,
      timezone: timezone2,
      referralCode,
      emailRedirectOrigin
    }) {
      const languageCode = language.split("-")[0];
      try {
        const { data: data2, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // 지정된 origin이 없으면 현재 사용자가 보고 있는 URL로 리다이렉션
            emailRedirectTo: `${emailRedirectOrigin ?? window.location.origin}/auth/callback`,
            data: {
              name,
              language_code: languageCode,
              // 인증메일 발송용
              language,
              // 유저 테이블 저장용
              timezone: timezone2,
              // 추천 링크 코드(snake_case) — handle_new_user 의 ->>'referral_code' 와 일치.
              // 값이 있을 때만 포함.
              ...referralCode ? { referral_code: referralCode } : {}
            }
          }
        });
        if (error) throw error;
        return data2.user;
      } catch (error) {
        console.error("\uD68C\uC6D0\uAC00\uC785 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw error;
      }
    },
    async logout() {
      try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      } catch (error) {
        console.error("\uB85C\uADF8\uC544\uC6C3 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw error;
      }
    },
    // 추가: 사용자 정보 조회 유틸리티 메서드
    async getUserById(id) {
      try {
        const { data: user, error } = await supabase.from("user").select("*").eq("id", id).maybeSingle();
        if (error) throw error;
        return user;
      } catch (error) {
        console.error("\uC0AC\uC6A9\uC790 ID \uC870\uD68C \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        return null;
      }
    },
    async getUserByEmail(email) {
      try {
        const { data: user, error } = await supabase.from("user").select("*").eq("email", email).maybeSingle();
        if (error) throw error;
        return user;
      } catch (error) {
        console.error("\uC0AC\uC6A9\uC790 \uC774\uBA54\uC77C \uC870\uD68C \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        return null;
      }
    },
    async updateUserData(userId, userData) {
      try {
        const { data: data2, error } = await supabase.from("user").update(userData).eq("id", userId).select("*").single();
        if (error) throw error;
        if (userData.language) {
          const { error: metaError } = await supabase.auth.updateUser(
            {
              data: {
                language_code: userData.language.split("-")[0]
              }
            }
          );
          if (metaError) {
            console.error(
              "\uC778\uC99D \uBA54\uD0C0\uB370\uC774\uD130 language_code \uAC31\uC2E0 \uC2E4\uD328:",
              metaError
            );
          }
        }
        return data2;
      } catch (error) {
        console.error("\uC0AC\uC6A9\uC790 \uC815\uBCF4 \uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw error;
      }
    },
    async updatePassword(newPassword) {
      try {
        if (!newPassword || newPassword.length < 6) {
          console.error("\uC720\uD6A8\uD558\uC9C0 \uC54A\uC740 \uBE44\uBC00\uBC88\uD638:", {
            length: newPassword?.length
          });
          throw new Error(
            i18n_default.t("auth:sign_up_form.error.password_invalid")
          );
        }
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });
        if (error) {
          console.error("\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD \uC624\uB958:", error);
          let errorMessage = i18n_default.t(
            "auth:change_password.notification.error"
          );
          if (error instanceof AuthError) {
            switch (error.message) {
              case "New password should be different from the old password.":
                errorMessage = i18n_default.t(
                  "auth:sign_up_form.error.same_password"
                );
                break;
              case "Password should be at least 6 characters":
                errorMessage = i18n_default.t(
                  "auth:sign_up_form.error.password_invalid"
                );
                break;
              default:
                errorMessage = `i18next.t('auth:change_password.notification.error'): ${error.message}`;
            }
          }
          throw new Error(errorMessage);
        }
        console.log("\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD \uC131\uACF5");
      } catch (error) {
        console.error("\uBE44\uBC00\uBC88\uD638 \uBCC0\uACBD \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw error;
      }
    },
    // 계정 탈퇴 메서드
    async deleteAccount(userId) {
      try {
        const workspaceService = createBaseWorkspaceService();
        const { hasBlockingWorkspace, errorMessage } = await workspaceService.hasBlockingWorkspace(userId);
        if (hasBlockingWorkspace) {
          throw new Error(errorMessage);
        }
        await apiClient.post("auth/delete-account", {
          json: { userId }
        });
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch (signOutError) {
          console.warn("\uB85C\uADF8\uC544\uC6C3 \uC5D0\uB7EC \uBC1C\uC0DD (\uBB34\uC2DC):", signOutError);
        }
        console.log("\uACC4\uC815 \uD0C8\uD1F4 \uCC98\uB9AC \uC644\uB8CC");
      } catch (error) {
        console.error("\uACC4\uC815 \uD0C8\uD1F4 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        if (error instanceof ApiClientError) {
          throw new Error(error.message);
        }
        throw error;
      }
    },
    // 비밀번호 재설정 이메일 발송
    async resetPasswordForEmail(email) {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(
          email,
          {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        );
        if (error) throw error;
        console.log("\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uC774\uBA54\uC77C \uBC1C\uC1A1 \uC644\uB8CC:", email);
      } catch (error) {
        console.error(
          "\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815 \uC774\uBA54\uC77C \uBC1C\uC1A1 \uC911 \uC624\uB958 \uBC1C\uC0DD:",
          error
        );
        throw error;
      }
    },
    // 인증 이메일 재전송
    async resendConfirmationEmail(email) {
      try {
        const { error } = await supabase.auth.resend({
          type: "signup",
          email
        });
        if (error) throw error;
        console.log("\uC778\uC99D \uC774\uBA54\uC77C\uC774 \uC7AC\uC804\uC1A1\uB418\uC5C8\uC2B5\uB2C8\uB2E4!:", email);
      } catch (error) {
        console.error("\uC778\uC99D \uC774\uBA54\uC77C \uC7AC\uC804\uC1A1 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
        throw error;
      }
    }
  };
};

// src/auth/authStore.ts
import { create } from "zustand";
var createBaseAuthStore = (authService, options) => {
  return create()((set) => {
    const checkUser = async () => {
      set(
        (state) => ({
          ...state,
          isLoading: true
        })
      );
      try {
        const user = await authService.getCurrentUser();
        set(
          (state) => ({
            ...state,
            user,
            isLoading: false
          })
        );
        return user;
      } catch (error) {
        set(
          (state) => ({
            ...state,
            user: null,
            isLoading: false,
            error: error instanceof Error ? error : new Error("Unknown error")
          })
        );
        return null;
      }
    };
    const updateUser = (userData) => {
      set(
        (state) => ({
          ...state,
          user: state.user ? { ...state.user, ...userData } : null
        })
      );
    };
    const logout = () => {
      set(
        (state) => ({
          ...state,
          user: null,
          isLoading: false,
          error: null
        })
      );
    };
    if (options?.autoCheckUser !== false) {
      void checkUser();
    }
    return {
      user: null,
      isLoading: true,
      error: null,
      checkUser,
      updateUser,
      logout
    };
  });
};
var dummyAuthService = {
  getCurrentUser: () => Promise.resolve(null)
};
var useAuthStore = createBaseAuthStore(dummyAuthService);

// src/auth/signupInquiryService.ts
var SIGNUP_INQUIRY_SITUATIONS = [
  "personal_email",
  "blocked_company_email",
  "etc"
];
var signupInquiryService = {
  async submit(payload) {
    await apiClient.post("signup-inquiry", { json: payload });
  }
};

// src/utils/navigation.ts
var navigateInstance = null;
var setNavigate = (navigate) => {
  navigateInstance = navigate;
};
var safeNavigate = (path, options) => {
  if (navigateInstance) {
    void navigateInstance(path, options);
  } else {
    console.warn(
      "\uB124\uBE44\uAC8C\uC774\uC158 \uD568\uC218\uAC00 \uC124\uC815\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4. Router\uAC00 \uB9C8\uC6B4\uD2B8\uB41C \uD6C4\uC5D0 \uC0AC\uC6A9\uD574\uC8FC\uC138\uC694."
    );
  }
};

// src/services/apiInterceptor.ts
var checkSessionExpiry = async () => {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;
  if (!session) {
    const authStore = useAuthStore.getState();
    authStore.logout();
    return false;
  }
  return true;
};
var useSessionCheck = () => {
  const { logout } = useAuthStore();
  const checkSession = async () => {
    const isValid = await checkSessionExpiry();
    if (!isValid) {
      logout();
      safeNavigate("/auth/login", { replace: true });
      return false;
    }
    return true;
  };
  return { checkSession };
};
var withSessionCheck = async (apiCall) => {
  const sessionValid = await checkSessionExpiry();
  if (!sessionValid) {
    throw new Error("\uC138\uC158\uC774 \uB9CC\uB8CC\uB418\uC5C8\uC2B5\uB2C8\uB2E4.");
  }
  return apiCall();
};
var useAutoSessionCheck = () => {
  const { user, isLoading, logout } = useAuthStore();
  const checkAndRedirect = async () => {
    if (isLoading || !user) {
      return;
    }
    try {
      const isValid = await checkSessionExpiry();
      if (!isValid && user) {
        logout();
        safeNavigate("/auth/login", { replace: true });
      }
    } catch (error) {
      console.error("\uC138\uC158 \uD655\uC778 \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
      logout();
      safeNavigate("/auth/login", { replace: true });
    }
  };
  return { checkAndRedirect };
};

// src/services/supabase_database.ts
var Constants = {
  graphql_public: {
    Enums: {}
  },
  public: {
    Enums: {
      payment_event_type: [
        "PAYMENT_SUCCESS",
        "PAYMENT_FAILED",
        "SUBSCRIPTION_CREATED",
        "SUBSCRIPTION_CANCELLED",
        "BILLING_KEY_ISSUED",
        "MARKETPLACE_SUBSCRIBE",
        "MARKETPLACE_UNSUBSCRIBE",
        "MARKETPLACE_UPDATE",
        "MARKETPLACE_ENTITLEMENT_UPDATED",
        "MARKETPLACE_METERING_ERROR",
        "SUBSCRIPTION_CANCELLATION_SCHEDULED",
        "SUBSCRIPTION_EXPIRED",
        "SUBSCRIPTION_REACTIVATED",
        "SUBSCRIPTION_UPDATE_FAILED",
        "MARKETPLACE_UNSUBSCRIBE_PENDING"
      ]
    }
  }
};

// src/services/supabase_uploadfile.ts
import imageCompression from "browser-image-compression";
function getBucket() {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return import.meta.env.VITE_SUPABASE_BUCKET || "";
  }
  return "";
}
var compressImage = async (file) => {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  try {
    const compressionFunc = imageCompression;
    const result = await compressionFunc(file, options);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : typeof error === "object" && error !== null ? JSON.stringify(error) : "Unknown error occurred";
    console.error("Error compressing image:", errorMessage);
    return file;
  }
};
var generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const extension = originalName.split(".").pop() || "";
  const sanitizedName = originalName.split(".")[0].replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
  return `${timestamp}_${sanitizedName}.${extension}`;
};
var uploadFile = async (path, file) => {
  try {
    let fileName = generateUniqueFileName(file.name);
    if (path.includes("teams") && path.includes("profile_image")) {
      const extension = file.name.split(".").pop() || "";
      fileName = `team_profile.${extension}`;
    }
    const fullPath = `${path}/${fileName}`;
    const { data: data2, error } = await supabase.storage.from(getBucket()).upload(fullPath, file, {
      cacheControl: "3600",
      upsert: true
    });
    if (error) {
      throw error;
    }
    if (!data2) {
      throw new Error("Upload successful but no data returned");
    }
    const {
      data: { publicUrl }
    } = supabase.storage.from(getBucket()).getPublicUrl(data2.path);
    const finalPublicUrl = `${publicUrl}?t=${Date.now()}`;
    return {
      publicUrl: finalPublicUrl,
      path: data2.path
    };
  } catch (error) {
    console.error(
      "Error uploading file:",
      error instanceof Error ? error.message : error
    );
    throw error;
  }
};
var deleteFile = async (path) => {
  try {
    const { error } = await supabase.storage.from(getBucket()).remove([path]);
    if (error) {
      console.error("Error deleting file:", error);
      return false;
    }
    return true;
  } catch (error) {
    console.error(
      "Error deleting file:",
      error instanceof Error ? error.message : error
    );
    return false;
  }
};
var extractPathFromUrl = (url) => {
  try {
    const urlWithoutQuery = url.split("?")[0];
    const bucketPattern = new RegExp(`/${getBucket()}/(.+)$`);
    const match = urlWithoutQuery.match(bucketPattern);
    if (match?.[1]) {
      return match[1];
    }
    return null;
  } catch (error) {
    console.error("URL\uC5D0\uC11C \uACBD\uB85C \uCD94\uCD9C \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
    return null;
  }
};
var useFileUpload = () => {
  const uploadImage = async (file, path, options) => {
    try {
      const compressedFile = await compressImage(file);
      console.log("compressedFile", compressedFile);
      const result = await uploadFile(path, compressedFile);
      console.log("result", result);
      options?.onSuccess?.(result);
      return result;
    } catch (error) {
      options?.onError?.(error);
      throw error;
    }
  };
  const handleImageChange = async (event, path, options) => {
    console.log(event, path, options);
    const file = event.target.files?.[0];
    console.log(file);
    console.log(file);
    if (!file) return;
    try {
      const result = await uploadImage(file, path, options);
      return result;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    } finally {
      event.target.value = "";
    }
  };
  const deleteImage = async (imageUrl) => {
    if (!imageUrl) return false;
    const path = extractPathFromUrl(imageUrl);
    if (!path) {
      console.error(
        "\uC774\uBBF8\uC9C0 URL\uC5D0\uC11C \uACBD\uB85C\uB97C \uCD94\uCD9C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4:",
        imageUrl
      );
      return false;
    }
    return await deleteFile(path);
  };
  return {
    uploadImage,
    handleImageChange,
    deleteImage
  };
};

// src/services/eventTrackingService.ts
var _cachedAccessToken = null;
var DWELL_MEASUREMENT = {
  measureVersion: 2,
  /**
   * 마지막 활동 이후 이 시간까지는 계속 보고 있는 것으로 간주한다.
   * CMR trig_b 기준(30초)보다 낮아야 활동 한 번만으로는 트리거가 통과되지 않는다.
   */
  idleTimeoutMs: 2e4,
  /** 활성 시간 누적 주기 */
  tickMs: 1e3,
  /** 이 값 미만은 전송하지 않는다 (리렌더·오조작 노이즈 제거) */
  minDwellMs: 1e3
};
var _isDisabledOverride = null;
var _platform = "web";
function configureEventTracking(config) {
  if (config.isDisabled) _isDisabledOverride = config.isDisabled;
  if (config.platform) _platform = config.platform;
}
var isTrackingDisabled = () => {
  if (_isDisabledOverride) return _isDisabledOverride();
  const env = typeof import.meta !== "undefined" ? import.meta.env : void 0;
  if (!env) return false;
  return env.DEV && env.VITE_ENABLE_EVENT_TRACKING !== "true";
};
var enrichProperties = (properties) => ({
  ...properties,
  client_occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
  platform: _platform
});
var eventTrackingService = {
  /**
   * 유저 행동 이벤트를 one_on_one_events 테이블에 기록한다.
   *
   * @param userId - 이벤트를 발생시킨 유저 ID
   * @param workspaceId - 워크스페이스 ID
   * @param eventName - 이벤트 이름
   * @param properties - 이벤트별 속성 (EventPropertyMap 참고, 없으면 `{}`)
   * @param options - 선택적 맥락 (meetingId 등)
   */
  async track(userId, workspaceId, eventName, properties, options = {}) {
    if (isTrackingDisabled()) return;
    const enrichedProperties = enrichProperties(properties);
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        _cachedAccessToken = session.access_token;
      }
      const { error } = await supabase.from("one_on_one_events").insert({
        user_id: userId,
        workspace_id: workspaceId,
        event_name: eventName,
        meeting_id: options.meetingId ?? null,
        properties: enrichedProperties
      });
      if (error) {
        console.error("\uC774\uBCA4\uD2B8 \uB85C\uAE45 \uC2E4\uD328:", error);
      }
    } catch (e) {
      console.error("\uC774\uBCA4\uD2B8 \uB85C\uAE45 \uC5D0\uB7EC:", e);
    }
  },
  /**
   * 페이지 이탈(beforeunload/visibilitychange) 시 사용하는 동기 전송.
   * navigator.sendBeacon은 브라우저가 닫혀도 요청이 완료되도록 보장한다.
   * 웹 전용 — RN fetch에는 keepalive가 없어 모바일은 track()을 쓴다.
   */
  trackBeacon(userId, workspaceId, eventName, properties, options = {}) {
    if (isTrackingDisabled()) return;
    const enrichedProperties = enrichProperties(properties);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseAnonKey) return;
      const token = _cachedAccessToken || supabaseAnonKey;
      const url = `${supabaseUrl}/rest/v1/one_on_one_events`;
      const body = JSON.stringify({
        user_id: userId,
        workspace_id: workspaceId,
        event_name: eventName,
        meeting_id: options.meetingId ?? null,
        properties: enrichedProperties
      });
      const headers = {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${token}`,
        Prefer: "return=minimal"
      };
      void fetch(url, {
        method: "POST",
        headers,
        body,
        keepalive: true
      });
    } catch {
    }
  }
};

// src/utils/commonUtils.ts
var arraysEqual = (a, b, comparator) => {
  if (a.length !== b.length) return false;
  if (a.length > 0 && typeof a[0] === "number") {
    const aCopy = [...a];
    const bCopy = [...b];
    aCopy.sort((x, y) => x - y);
    bCopy.sort((x, y) => x - y);
    return aCopy.every((val, idx) => val === bCopy[idx]);
  }
  if (comparator) {
    return a.every((val, idx) => comparator(val, b[idx]));
  }
  return a.every((val, idx) => val === b[idx]);
};
var detectBrowserLanguage = (defaultLanguage = "en-US") => {
  let language = typeof localStorage !== "undefined" ? localStorage.getItem("i18nextLng") : null;
  if (!language) {
    language = typeof navigator !== "undefined" ? navigator.language : defaultLanguage;
  }
  if (language.includes("-")) {
    return language;
  }
  switch (language) {
    case "ko":
      return "ko-KR";
    case "en":
      return "en-US";
    case "vi":
      return "vi-VN";
    default:
      return defaultLanguage;
  }
};
var detectBrowserTimezone = () => {
  if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return "UTC";
};
var isDevelopment = () => {
  return process.env.NODE_ENV === "development" || window.location.hostname === "localhost" || // window.location.port === '3000' ||
  window.location.port === "8080" || // window.location.port === '3001' ||
  window.location.port === "8081";
};

// src/translation/index.ts
var i18n = i18n_default;
var defaultPack = default_pack_default;
var translation_default = i18n_default;

// src/utils/dateUtils.ts
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import i18next2 from "i18next";
dayjs.extend(utc);
dayjs.extend(timezone);
var formatDate = (date, options) => {
  const defaultOptions = {
    showYear: true,
    showMonth: true,
    showDay: true,
    showWeekday: true,
    shortenMonth: false
    // weekdayPrefix: '(',
    // weekdaySuffix: ')',
  };
  const opts = { ...defaultOptions, ...options };
  const currentLang = i18next2.language || "ko-KR";
  const dateOptions = {};
  if (opts.showYear) dateOptions.year = "numeric";
  if (opts.showMonth)
    dateOptions.month = opts.shortenMonth ? "numeric" : "long";
  if (opts.showDay) dateOptions.day = "numeric";
  if (opts.showWeekday) dateOptions.weekday = "long";
  return date.toLocaleDateString(currentLang, dateOptions);
};
var getStartOfDay = (date) => {
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};
var getEndOfDay = (date) => {
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};
var getEndOfMonth = (date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
};
var isSameDate = (date1, date2) => {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  } catch (e) {
    console.error("Date comparison failed:", date1, date2, e);
    return false;
  }
};
var formatMeetingDateTime = (date, locale, { withTime = true } = {}) => date.toLocaleDateString(locale, {
  month: "long",
  day: "numeric",
  weekday: "long",
  ...withTime && { hour: "numeric", minute: "2-digit" }
});
var isBeforeDate = (date1, date2) => {
  return new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()) < new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
};
var isAfterDate = (date1, date2) => {
  return new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()) > new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
};
var checkIsAfterToday = (due_date) => {
  const now = /* @__PURE__ */ new Date();
  now.setHours(0, 0, 0, 0);
  const dueDate = new Date(due_date);
  dueDate.setHours(0, 0, 0, 0);
  return now < dueDate;
};
var getWeekRange = (date) => {
  const startOfWeek = new Date(date);
  const day = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
  startOfWeek.setDate(diff);
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(endOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return {
    startDate: startOfWeek.toISOString(),
    endDate: endOfWeek.toISOString()
  };
};
var calculateDday = (endDate) => {
  if (!endDate) return null;
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(endDate);
  deadline.setHours(0, 0, 0, 0);
  const diffTime = deadline.getTime() - today.getTime();
  const diffDays = Math.floor(diffTime / (1e3 * 60 * 60 * 24));
  if (diffDays === 0) {
    return "D-Day";
  } else if (diffDays < 0) {
    return `D+${Math.abs(diffDays)}`;
  } else {
    return `D-${diffDays}`;
  }
};
function getRelativeDateText(date) {
  const today = /* @__PURE__ */ new Date();
  const todayDateString = today.toDateString();
  const dateString = date.toDateString();
  if (dateString === todayDateString) {
    return translation_default.t("date_picker.today");
  }
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateString === tomorrow.toDateString()) {
    return translation_default.t("date_picker.tomorrow");
  }
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateString === yesterday.toDateString()) {
    return translation_default.t("date_picker.yesterday");
  }
  return null;
}
function formatDateWithRelative(date, options) {
  const locale = translation_default.language || "ko-KR";
  if (options) return date.toLocaleDateString(locale, options);
  const relativeText = getRelativeDateText(date);
  if (relativeText) {
    return relativeText;
  }
  const defaultOptions = {
    month: "long",
    day: "numeric",
    weekday: "long"
  };
  const finalOptions = options || defaultOptions;
  return date.toLocaleDateString(locale, finalOptions);
}
var formatTimeAgo = (date) => {
  const now = /* @__PURE__ */ new Date();
  const targetDate = typeof date === "string" ? new Date(date) : date;
  const diffInMs = now.getTime() - targetDate.getTime();
  const seconds = Math.floor(diffInMs / 1e3);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  const currentLang = i18next2.language || "ko-KR";
  if (years > 0) {
    return currentLang.startsWith("ko") ? `${years}\uB144\uC804` : currentLang.startsWith("vi") ? `${years} n\u0103m tr\u01B0\u1EDBc` : `${years} year${years > 1 ? "s" : ""} ago`;
  }
  if (months > 0) {
    return currentLang.startsWith("ko") ? `${months}\uB2EC\uC804` : currentLang.startsWith("vi") ? `${months} th\xE1ng tr\u01B0\u1EDBc` : `${months} month${months > 1 ? "s" : ""} ago`;
  }
  if (days > 0) {
    return currentLang.startsWith("ko") ? `${days}\uC77C\uC804` : currentLang.startsWith("vi") ? `${days} ng\xE0y tr\u01B0\u1EDBc` : `${days} day${days > 1 ? "s" : ""} ago`;
  }
  if (hours > 0) {
    return currentLang.startsWith("ko") ? `${hours}\uC2DC\uAC04\uC804` : currentLang.startsWith("vi") ? `${hours} gi\u1EDD tr\u01B0\u1EDBc` : `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (minutes > 0) {
    return currentLang.startsWith("ko") ? `${minutes}\uBD84\uC804` : currentLang.startsWith("vi") ? `${minutes} ph\xFAt tr\u01B0\u1EDBc` : `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }
  return currentLang.startsWith("ko") ? "\uBC29\uAE08\uC804" : currentLang.startsWith("vi") ? "v\u1EEBa xong" : "just now";
};

// src/utils/i18nUtils.ts
import { ko, enUS, vi } from "date-fns/locale";
var getResolvedLanguage = () => {
  const fallback = defaultPack["default-language"];
  let current = translation_default.language;
  if (!current && typeof window !== "undefined") {
    try {
      current = localStorage.getItem("i18nextLng") || "";
    } catch (_) {
      current = "";
    }
    if (!current && typeof navigator !== "undefined") {
      current = navigator.language;
    }
  }
  return current || fallback;
};
var getDateLocale = () => {
  const currentLang = getResolvedLanguage();
  if (currentLang.startsWith("ko")) return "ko-KR";
  if (currentLang.startsWith("en")) return "en-US";
  if (currentLang.startsWith("vi")) return "vi-VN";
  return defaultPack["default-language"];
};
var getDateFnsLocale = () => {
  const currentLang = getResolvedLanguage();
  const lang = currentLang.split("-")[0];
  switch (lang) {
    case "ko":
      return ko;
    case "en":
      return enUS;
    case "vi":
      return vi;
    default:
      return ko;
  }
};
var getFullLanguageName = () => {
  const currentLang = getResolvedLanguage();
  const lang = currentLang.split("-")[0];
  switch (lang) {
    case "ko":
      return "Korean";
    case "en":
      return "English";
    case "vi":
      return "Vietnamese";
    default:
      return "Korean";
  }
};

// src/utils/workspaceUtils.ts
function findAllSubordinates(startUserIds, allMembers, includeStartUsers = false) {
  const subordinatesSet = /* @__PURE__ */ new Set();
  const startIds = Array.isArray(startUserIds) ? startUserIds : [startUserIds];
  const queue = [...startIds];
  const processedIds = new Set(startIds);
  if (!includeStartUsers) {
    startIds.forEach((id) => subordinatesSet.delete(id));
  }
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId) continue;
    const childMembers = allMembers.filter(
      (member) => member.leader_id === currentId
    );
    for (const childMember of childMembers) {
      const userId = childMember.user_id || childMember.user?.id;
      if (!userId) continue;
      subordinatesSet.add(userId);
      if (!processedIds.has(userId)) {
        queue.push(userId);
        processedIds.add(userId);
      }
    }
  }
  return subordinatesSet;
}

// src/utils/agreementUrls.ts
var getServiceTermsUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/service_terms_ko.html";
    default:
      return "/v3/service_terms_en.html";
  }
};
var getRecordingConsentUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/recording_consent_ko.html";
    default:
      return "/v3/recording_consent_en.html";
  }
};
var getThirdPartyProvisionUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/third_party_provision_ko.html";
    default:
      return "/v3/third_party_provision_en.html";
  }
};
var getOverseasTransferUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/overseas_transfer_ko.html";
    default:
      return "/v3/overseas_transfer_en.html";
  }
};
var getPersonalInfoCollectionUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/personal_info_collection_ko.html";
    default:
      return "/v3/personal_info_collection_en.html";
  }
};
var getMarketingConsentUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/marketing_consent_ko.html";
    default:
      return "/v3/marketing_consent_en.html";
  }
};
var getAdvertisingConsentUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v3/advertising_consent_ko.html";
    default:
      return "/v3/advertising_consent_en.html";
  }
};
var getPrivacyPolicyUrl = (language) => {
  switch (language) {
    case "ko-KR":
      return "/v4/privacy_policy_ko.html";
    default:
      return "/v4/privacy_policy_en.html";
  }
};
var getAgreements = (language, t) => [
  {
    title: t("auth:agreement.age"),
    isRequired: true,
    url: "",
    type: "age"
  },
  {
    title: t("auth:agreement.service"),
    isRequired: true,
    url: getServiceTermsUrl(language),
    type: "service"
  },
  {
    title: t("auth:agreement.recording"),
    isRequired: true,
    url: getRecordingConsentUrl(language),
    type: "recording"
  },
  {
    title: t("auth:agreement.thirdParty"),
    isRequired: true,
    url: getThirdPartyProvisionUrl(language),
    type: "thirdParty"
  },
  {
    title: t("auth:agreement.overseas"),
    isRequired: true,
    url: getOverseasTransferUrl(language),
    type: "overseas"
  },
  {
    title: t("auth:agreement.personalInfo"),
    isRequired: false,
    url: getPersonalInfoCollectionUrl(language),
    type: "personalInfo"
  },
  {
    title: t("auth:agreement.marketing"),
    isRequired: false,
    url: getMarketingConsentUrl(language),
    type: "marketing"
  },
  {
    title: t("auth:agreement.advertising"),
    isRequired: false,
    url: getAdvertisingConsentUrl(language),
    type: "advertising"
  }
];

// src/utils/userUtils.ts
var DELETED_USER_ID = "deleted-user";
var isUserBase = (value) => {
  return value !== null && typeof value === "object" && "id" in value && "name" in value && "profile_image_url" in value;
};
var findUserBaseFields = (data2) => {
  const fields = [];
  const userFieldPatterns = [
    "user",
    "leader",
    "member",
    "sender",
    "receiver",
    "owner",
    "creator",
    "author"
  ];
  for (const key in data2) {
    if (Object.prototype.hasOwnProperty.call(data2, key)) {
      const value = data2[key];
      const keyStr = String(key).toLowerCase();
      if (isUserBase(value)) {
        fields.push(key);
      } else if ((value === null || value === void 0) && userFieldPatterns.some((pattern) => keyStr === pattern)) {
        fields.push(key);
      } else if (Array.isArray(value) && value.length > 0) {
        let hasUserBaseFields = false;
        for (const item of value) {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            const innerFields = findUserBaseFields(
              item
            );
            if (innerFields.length > 0) {
              hasUserBaseFields = true;
              break;
            }
          }
        }
        if (hasUserBaseFields) {
          fields.push(key);
        }
      }
    }
  }
  return fields;
};
var withSafeUsers = (data2, userFields, t) => {
  const result = { ...data2 };
  userFields.forEach((field) => {
    if (field in result) {
      const value = result[field];
      if (Array.isArray(value)) {
        result[field] = value.map((item) => {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            const innerUserFields = findUserBaseFields(
              item
            );
            if (innerUserFields.length > 0) {
              return withSafeUsers(
                item,
                innerUserFields,
                t
              );
            }
          }
          return item;
        });
      } else if (isUserBase(value)) {
        result[field] = value ?? createDeletedUser(t);
      } else if (value === null || value === void 0) {
        result[field] = createDeletedUser(t);
      }
    } else {
      if (isDevelopment()) {
        console.warn(
          `[withSafeUsers] Field "${String(field)}" does not exist in data object.`,
          "\nAvailable fields:",
          Object.keys(data2)
        );
      }
    }
  });
  return result;
};
var withSafeUsersArray = (dataArray, t, options) => {
  if (dataArray.length === 0) return dataArray;
  let userFields = options?.userFields;
  if (!userFields) {
    for (const item of dataArray) {
      const detectedFields = findUserBaseFields(item);
      if (detectedFields.length > 0) {
        userFields = detectedFields;
        break;
      }
    }
    userFields = userFields ?? [];
  }
  return dataArray.map((data2) => withSafeUsers(data2, userFields, t));
};
var getDisplayName = (user, defaultName = "-") => {
  return user?.name || defaultName;
};
var getProfileImageUrl = (user, defaultImageUrl = "") => {
  return user?.profile_image_url || defaultImageUrl;
};
var isUserActive = (user) => {
  return user !== null && user !== void 0;
};
var createDeletedUser = (t) => ({
  id: DELETED_USER_ID,
  name: t("common:deletedUser"),
  profile_image_url: ""
});
var toSafeUserArray = (user) => {
  if (user == null) return [];
  const users = Array.isArray(user) ? user : [user];
  return users.filter((u) => u != null);
};
var getUserFromWorkspace = (userId, workspaceMembers, t) => {
  if (userId === null) {
    return createDeletedUser(t);
  }
  const member = workspaceMembers?.find((m) => m.user_id === userId);
  return member?.user ?? null;
};

// src/utils/workspaceMemberUtils.ts
var getEnabledWorkspaceMemberIds = async (workspaceId) => {
  const { data: enableWorkspaceMembers } = await supabase.from("workspace_member").select("user_id").eq("workspace_id", workspaceId).eq("enable", true);
  return enableWorkspaceMembers?.map((member) => member.user_id) ?? [];
};
var getEnabledWorkspaceMemberIdsBatch = async (workspaceIds) => {
  const uniqueWorkspaceIds = [...new Set(workspaceIds)];
  const { data: enableWorkspaceMembers } = await supabase.from("workspace_member").select("user_id, workspace_id").in("workspace_id", uniqueWorkspaceIds).eq("enable", true);
  const result = {};
  uniqueWorkspaceIds.forEach((id) => {
    result[id] = [];
  });
  enableWorkspaceMembers?.forEach((member) => {
    if (!result[member.workspace_id]) {
      result[member.workspace_id] = [];
    }
    result[member.workspace_id].push(member.user_id);
  });
  return result;
};
var filterUserByWorkspaceMembership = (user, enabledUserIds) => {
  return user && enabledUserIds.includes(user.id) ? user : null;
};
var filterUsersByWorkspaceMembership = (users, enabledUserIds) => {
  return users.map(
    (user) => user && enabledUserIds.includes(user.id) ? user : null
  );
};
var applyWorkspaceMembershipFilter = async (data2, workspaceId, userFields) => {
  const enabledUserIds = await getEnabledWorkspaceMemberIds(workspaceId);
  return data2.map((item) => {
    const filteredItem = { ...item };
    userFields.forEach((field) => {
      const user = item[field];
      filteredItem[field] = filterUserByWorkspaceMembership(
        user,
        enabledUserIds
      );
    });
    return filteredItem;
  });
};
var applyWorkspaceMembershipFilterBatch = async (data2, userFields) => {
  const workspaceIds = data2.map((item) => item.workspace_id);
  const enabledUserIdsByWorkspace = await getEnabledWorkspaceMemberIdsBatch(workspaceIds);
  return data2.map((item) => {
    const filteredItem = { ...item };
    const enabledUserIds = enabledUserIdsByWorkspace[item.workspace_id] || [];
    userFields.forEach((field) => {
      const user = item[field];
      filteredItem[field] = filterUserByWorkspaceMembership(
        user,
        enabledUserIds
      );
    });
    return filteredItem;
  });
};

// src/utils/recordingLogger.ts
var isWebDevelopment = () => {
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
    return true;
  }
  if (typeof window !== "undefined" && window.location) {
    const { hostname, port } = window.location;
    return hostname === "localhost" || port === "8080" || port === "8081";
  }
  return false;
};
var defaultConfig = {
  getUserAgent: () => typeof navigator !== "undefined" ? navigator.userAgent : null,
  getPageUrl: () => typeof window !== "undefined" ? window.location.href : null,
  isDevelopment: isWebDevelopment
};
var loggerConfig = defaultConfig;
function configureRecordingLogger(config) {
  loggerConfig = { ...loggerConfig, ...config };
}
var normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name
    };
  }
  return {
    message: "Unknown error",
    stack: void 0,
    name: void 0
  };
};
var logRecordingEvent = async ({
  severity,
  errorType,
  errorMessage,
  meetingId,
  userId,
  workspaceId,
  metadata
}) => {
  if (loggerConfig.isDevelopment()) {
    console.log("[RecordingLogger] dev \uD658\uACBD - \uC801\uC7AC \uC2A4\uD0B5:", {
      severity,
      errorType,
      errorMessage
    });
    return;
  }
  try {
    let memoryInfo = {};
    if ("memory" in performance && performance.memory) {
      const memory = performance.memory;
      memoryInfo = {
        memory_used_mb: Math.round(
          memory.usedJSHeapSize / (1024 * 1024)
        ),
        memory_total_mb: Math.round(
          memory.totalJSHeapSize / (1024 * 1024)
        ),
        memory_limit_mb: Math.round(
          memory.jsHeapSizeLimit / (1024 * 1024)
        )
      };
    }
    await supabase.from("error_logs").insert({
      severity,
      error_type: errorType,
      error_message: errorMessage,
      feature_module: "oneonone-recording",
      user_id: userId,
      workspace_id: workspaceId,
      page_url: loggerConfig.getPageUrl(),
      user_agent: loggerConfig.getUserAgent(),
      error_context: {
        meeting_id: meetingId,
        timestamp: (/* @__PURE__ */ new Date()).toISOString(),
        ...metadata
      },
      status: "new",
      register_date: (/* @__PURE__ */ new Date()).toISOString(),
      ...memoryInfo
    });
  } catch (error) {
    console.error("[RecordingLogger] Failed to log event:", error);
  }
};
var logEmptyChunk = async (meetingId, userId, workspaceId, metadata) => {
  await logRecordingEvent({
    severity: "warning",
    errorType: "RECORDING_EMPTY_CHUNK",
    errorMessage: metadata?.emptyChunkCount ? `Empty chunks during recording: ${metadata.emptyChunkCount} total` : "Empty chunk received during recording",
    meetingId,
    userId,
    workspaceId,
    metadata
  });
};
var logMemoryWarning = async (meetingId, userId, workspaceId, metadata) => {
  await logRecordingEvent({
    severity: "warning",
    errorType: "RECORDING_MEMORY_WARNING",
    errorMessage: `Recording approaching storage limit: ${metadata?.currentSize || 0} bytes`,
    meetingId,
    userId,
    workspaceId,
    metadata
  });
};
var logSizeMismatch = async (meetingId, userId, workspaceId, metadata) => {
  await logRecordingEvent({
    severity: "error",
    errorType: "RECORDING_SIZE_MISMATCH",
    errorMessage: `Recording size too small: ${metadata?.actualSize || 0} bytes for ${metadata?.expectedDuration || 0}s`,
    meetingId,
    userId,
    workspaceId,
    metadata
  });
};
var logUploadFailed = async (meetingId, error, userId, workspaceId, metadata) => {
  const { message, stack, name } = normalizeError(error);
  await logRecordingEvent({
    severity: "error",
    errorType: "RECORDING_UPLOAD_FAILED",
    errorMessage: `Failed to upload recording: ${message}`,
    meetingId,
    userId,
    workspaceId,
    metadata: {
      ...metadata,
      errorStack: stack,
      errorName: name
    }
  });
};
var logTrackEnded = async (meetingId, userId, workspaceId, metadata) => {
  await logRecordingEvent({
    severity: "error",
    errorType: "RECORDING_TRACK_ENDED",
    errorMessage: `Audio track ended unexpectedly after ${metadata?.recordingDuration || 0}s`,
    meetingId,
    userId,
    workspaceId,
    metadata
  });
};
var logRecorderError = async (meetingId, error, userId, workspaceId, metadata) => {
  const { message, stack, name } = normalizeError(error);
  await logRecordingEvent({
    severity: "error",
    errorType: "RECORDING_RECORDER_ERROR",
    errorMessage: `MediaRecorder error: ${message}`,
    meetingId,
    userId,
    workspaceId,
    metadata: {
      ...metadata,
      errorStack: stack,
      errorName: name
    }
  });
};
var logCacheLimitExceeded = async (userId, metadata) => {
  await logRecordingEvent({
    severity: "warning",
    errorType: "BROADCAST_CACHE_SIZE_WARNING",
    errorMessage: `Channel cache size exceeded threshold: ${metadata?.currentSize || 0} / ${metadata?.threshold || 0}`,
    userId,
    metadata
  });
};
var logSubscriptionError = async (userId, metadata) => {
  await logRecordingEvent({
    severity: "error",
    errorType: "BROADCAST_SUBSCRIPTION_ERROR",
    errorMessage: `Failed to subscribe to channel after ${metadata?.maxAttempts ?? 0} attempts: ${metadata?.subscriptionErrorMessage ?? "unknown"}`,
    userId,
    metadata
  });
};

// src/utils/validationUtils.ts
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_REGEX.test(email.trim());
}
var PERSONAL_EMAIL_DOMAINS = /* @__PURE__ */ new Set([
  // 글로벌
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "yahoo.co.kr",
  "yahoo.co.jp",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "yandex.com",
  "yandex.ru",
  "gmx.com",
  "gmx.net",
  "mail.com",
  // 한국
  "naver.com",
  "daum.net",
  "hanmail.net",
  "kakao.com",
  "nate.com",
  "outlook.kr",
  "hotmail.kr",
  "live.kr",
  "hanafos.com",
  "korea.com",
  "paran.com",
  "dreamwiz.com",
  "empal.com",
  "lycos.co.kr",
  "chol.com",
  "hitel.net"
]);
function isPersonalEmailDomain(email) {
  const domain = email.trim().toLowerCase().split("@")[1];
  if (!domain) return false;
  return PERSONAL_EMAIL_DOMAINS.has(domain);
}

// src/utils/networkError.ts
var networkErrorPatterns = [
  "Failed to fetch",
  "NetworkError",
  "ERR_INTERNET_DISCONNECTED",
  "ERR_NETWORK_CHANGED",
  "net::ERR_INTERNET_DISCONNECTED",
  "The operation was aborted",
  // RN fetch 실패 시 메시지
  "Network request failed"
];
var getErrorMessage = (err) => {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  }
  if (err && typeof err === "object") {
    try {
      return JSON.stringify(err);
    } catch {
      try {
        return err.toString();
      } catch {
        return "[Unknown Error Object]";
      }
    }
  }
  return String(err);
};
var getErrorProperty = (err, prop) => {
  return err && typeof err === "object" && prop in err ? err[prop] : void 0;
};
var detectNetworkError = (error) => {
  const errorMessage = getErrorMessage(error);
  const errorName = getErrorProperty(error, "name");
  const errorCode = getErrorProperty(error, "code");
  const errorStatus = getErrorProperty(error, "status");
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return offline || errorName === "TypeError" || // fetch 실패 시 TypeError ("Network request failed" 등)
  errorName === "AbortError" || // 타임아웃/abort
  errorCode === "NETWORK_ERROR" || errorStatus === 0 || // 네트워크 단절 시 status 0
  typeof errorStatus === "number" && errorStatus >= 500 || // 서버 오류 (5xx)
  networkErrorPatterns.some((pattern) => errorMessage.includes(pattern));
};
var NetworkError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "NetworkError";
  }
};

// src/types/guards.ts
function isNotNull(value) {
  return value !== null && value !== void 0;
}
function isString(value) {
  return typeof value === "string";
}
function isNumber(value) {
  return typeof value === "number" && !isNaN(value);
}
function isBoolean(value) {
  return typeof value === "boolean";
}
function isArray(value) {
  return Array.isArray(value);
}
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isUserBase2(value) {
  return isObject(value) && isString(value.id) && isString(value.name) && (value.profile_image_url === null || isString(value.profile_image_url));
}
function isUser(value) {
  if (!isUserBase2(value) || !isObject(value)) {
    return false;
  }
  const userValue = value;
  return isString(userValue.email) && isString(userValue.created_at) && isString(userValue.updated_at);
}
function isError(value) {
  return value instanceof Error;
}
function isNonEmptyString(value) {
  return isString(value) && value.trim().length > 0;
}
function isPositiveNumber(value) {
  return isNumber(value) && value > 0;
}
function isValidId(value) {
  return isNonEmptyString(value);
}
function isNonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0;
}
function isPromise(value) {
  return value != null && typeof value === "object" && typeof value.then === "function" && typeof value.catch === "function";
}
function isValidDateString(value) {
  return isString(value) && !isNaN(Date.parse(value));
}

// src/components/selectors/DatePickerSelector.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Popover,
  PopoverDropdown,
  PopoverTarget,
  Text
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import {
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight
} from "@tabler/icons-react";
import { jsx, jsxs } from "react/jsx-runtime";
function getRelativeDateText2(date) {
  const today = /* @__PURE__ */ new Date();
  const todayDateString = today.toDateString();
  const dateString = date.toDateString();
  if (dateString === todayDateString) return i18n.t("date_picker.today");
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateString === tomorrow.toDateString())
    return i18n.t("date_picker.tomorrow");
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateString === yesterday.toDateString())
    return i18n.t("date_picker.yesterday");
  return null;
}
function formatDateWithRelative2(date, options) {
  const locale = i18n.language || "ko-KR";
  if (options) return date.toLocaleDateString(locale, options);
  const relativeText = getRelativeDateText2(date);
  if (relativeText) return relativeText;
  const defaultOptions = {
    month: "long",
    day: "numeric",
    weekday: "long"
  };
  return date.toLocaleDateString(locale, defaultOptions);
}
function getDateCellStyle(date, selectedDate) {
  const day = date.getDay();
  const isWeekend = day === 0 || day === 6;
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  const dateToCheck = new Date(date);
  dateToCheck.setHours(0, 0, 0, 0);
  const isToday = !isBeforeDate(dateToCheck, today) && !isAfterDate(dateToCheck, today);
  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
  const styleObj = {
    fontSize: "calc(var(--day-size) / 2.8)"
  };
  if (isWeekend) {
    styleObj.color = "var(--mantine-color-red-6)";
  }
  if (isToday) {
    styleObj.fontWeight = "bold";
  }
  if (isSelected) {
    styleObj.width = "var(--day-size, var(--day-size-sm))";
    styleObj.height = "var(--day-size, var(--day-size-sm))";
    styleObj.display = "inline-flex";
    styleObj.justifyContent = "center";
    styleObj.alignItems = "center";
    styleObj.borderRadius = "var(--mantine-radius-default)";
    styleObj.backgroundColor = "var(--mantine-primary-color-filled)";
    styleObj.color = "var(--mantine-primary-color-contrast)";
  }
  return styleObj;
}
function DatePickerSelector({
  selectedDate,
  actionIcon,
  onDateChange,
  withinPortal = true,
  editable = true,
  showSelectedDate = true,
  showTodayButton = true,
  showNavigationButtons = false,
  dateFormatOptions = { year: "numeric", month: "long" },
  todayButtonText,
  calendarIconSize = 20,
  arrowIconSize = 10,
  navigationArrowSize = 14,
  buttonSize = "compact-xs",
  buttonRadius = "md",
  buttonVariant = "default",
  className
}) {
  const { t } = useTranslation();
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const goToday = () => {
    onDateChange(/* @__PURE__ */ new Date());
  };
  return /* @__PURE__ */ jsxs(Group, { justify: "center", gap: "sm", className, children: [
    showSelectedDate && /* @__PURE__ */ jsx(Text, { fw: 600, size: "md", children: formatDateWithRelative2(selectedDate, dateFormatOptions) }),
    /* @__PURE__ */ jsxs(
      Popover,
      {
        opened: datePickerOpened,
        onChange: setDatePickerOpened,
        withinPortal,
        children: [
          /* @__PURE__ */ jsx(PopoverTarget, { children: actionIcon ? /* @__PURE__ */ jsx(
            Box,
            {
              onClick: () => editable && setDatePickerOpened(true),
              style: { cursor: "pointer" },
              children: actionIcon
            }
          ) : /* @__PURE__ */ jsx(
            ActionIcon,
            {
              variant: "subtle",
              color: "gray",
              size: "md",
              onClick: () => editable && setDatePickerOpened(true),
              "aria-label": t("date_picker.open", "\uB0A0\uC9DC \uC120\uD0DD \uC5F4\uAE30"),
              children: /* @__PURE__ */ jsx(IconCalendarEvent, { size: calendarIconSize })
            }
          ) }),
          /* @__PURE__ */ jsx(PopoverDropdown, { children: /* @__PURE__ */ jsx(
            DatePicker,
            {
              firstDayOfWeek: 0,
              value: selectedDate,
              onChange: (value) => {
                onDateChange(value || /* @__PURE__ */ new Date());
                setDatePickerOpened(false);
              },
              renderDay: (date) => {
                const styleObj = getDateCellStyle(
                  date,
                  selectedDate
                );
                return /* @__PURE__ */ jsx("div", { style: styleObj, children: date.getDate() });
              }
            }
          ) })
        ]
      }
    ),
    showNavigationButtons && /* @__PURE__ */ jsxs(Group, { gap: 2, children: [
      /* @__PURE__ */ jsx(
        ActionIcon,
        {
          color: "gray",
          variant: "subtle",
          onClick: () => onDateChange(
            new Date(selectedDate.getTime() - 864e5)
          ),
          "aria-label": t("date_picker.prev_day", "\uC774\uC804 \uB0A0\uC9DC"),
          children: /* @__PURE__ */ jsx(
            IconChevronLeft,
            {
              stroke: 3,
              size: navigationArrowSize
            }
          )
        }
      ),
      /* @__PURE__ */ jsx(
        ActionIcon,
        {
          color: "gray",
          variant: "subtle",
          onClick: () => onDateChange(
            new Date(selectedDate.getTime() + 864e5)
          ),
          "aria-label": t("date_picker.next_day", "\uB2E4\uC74C \uB0A0\uC9DC"),
          children: /* @__PURE__ */ jsx(
            IconChevronRight,
            {
              stroke: 3,
              size: navigationArrowSize
            }
          )
        }
      )
    ] }),
    showTodayButton && /* @__PURE__ */ jsx(
      Button,
      {
        variant: buttonVariant,
        onClick: goToday,
        size: buttonSize,
        radius: buttonRadius,
        style: {
          visibility: isBeforeDate(selectedDate, /* @__PURE__ */ new Date()) || isAfterDate(selectedDate, /* @__PURE__ */ new Date()) ? "visible" : "hidden"
        },
        children: /* @__PURE__ */ jsxs(Group, { gap: 4, children: [
          isAfterDate(selectedDate, /* @__PURE__ */ new Date()) && /* @__PURE__ */ jsx(
            IconChevronLeft,
            {
              stroke: 2,
              size: navigationArrowSize
            }
          ),
          todayButtonText ?? t("date_picker.today"),
          isBeforeDate(selectedDate, /* @__PURE__ */ new Date()) && /* @__PURE__ */ jsx(
            IconChevronRight,
            {
              stroke: 2,
              size: navigationArrowSize
            }
          )
        ] })
      }
    )
  ] });
}

// src/components/selectors/LanguageSelector.tsx
import { Group as Group2, Menu, Text as Text2, UnstyledButton } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useState as useState2 } from "react";
import { useTranslation as useTranslation2 } from "react-i18next";
import { US, KR, VN } from "country-flag-icons/react/3x2";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var data = [
  { label: "English", value: "en-US", icon: /* @__PURE__ */ jsx2(US, { width: 20 }) },
  { label: "\uD55C\uAD6D\uC5B4", value: "ko-KR", icon: /* @__PURE__ */ jsx2(KR, { width: 20 }) },
  { label: "Ti\u1EBFng Vi\u1EC7t", value: "vi-VN", icon: /* @__PURE__ */ jsx2(VN, { width: 20 }) }
];
var LanguageSelector = ({
  value,
  onChange,
  withLabel = true
}) => {
  const { t } = useTranslation2(["auth", "common"]);
  const [menuOpened, setMenuOpened] = useState2(false);
  const items = data.map((item) => /* @__PURE__ */ jsx2(
    Menu.Item,
    {
      leftSection: item.icon,
      onClick: () => onChange(item.value),
      children: item.label
    },
    item.value
  ));
  return /* @__PURE__ */ jsxs2(Group2, { justify: "space-between", align: "center", children: [
    withLabel && /* @__PURE__ */ jsx2(Text2, { children: t("auth:user_setting.label.language") }),
    /* @__PURE__ */ jsxs2(
      Menu,
      {
        onOpen: () => setMenuOpened(true),
        onClose: () => setMenuOpened(false),
        radius: "md",
        width: "target",
        withinPortal: true,
        children: [
          /* @__PURE__ */ jsx2(Menu.Target, { children: /* @__PURE__ */ jsxs2(
            UnstyledButton,
            {
              w: withLabel ? 260 : "100%",
              bd: "1px solid #D9D9D9",
              display: "flex",
              py: 8,
              px: 12,
              c: "#ADB5BD",
              style: {
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "4px"
              },
              "data-expanded": menuOpened || void 0,
              children: [
                /* @__PURE__ */ jsxs2(Group2, { gap: "xs", children: [
                  data.find((item) => item.value === value)?.icon,
                  data.find((item) => item.value === value)?.label
                ] }),
                /* @__PURE__ */ jsx2(IconChevronDown, { size: 16, stroke: 1.5 })
              ]
            }
          ) }),
          /* @__PURE__ */ jsx2(Menu.Dropdown, { children: items })
        ]
      }
    )
  ] });
};

// src/components/UserAvatars/UserAvatar.tsx
import { Avatar, Tooltip } from "@mantine/core";
import { IconArrowsDiff, IconUser } from "@tabler/icons-react";
import { useState as useState3 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var UserAvatar = ({
  name,
  imageUrl,
  size = 16,
  radius = 50,
  onClick,
  style,
  showChangeIcon = false,
  tooltipLabel
}) => {
  const [imageError, setImageError] = useState3(false);
  const handleImageError = () => {
    setImageError(true);
  };
  return /* @__PURE__ */ jsx3(Tooltip, { label: tooltipLabel, withArrow: true, disabled: !tooltipLabel, children: /* @__PURE__ */ jsxs3(
    "span",
    {
      style: {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: onClick ? "pointer" : "default",
        position: "relative",
        ...style
      },
      onClick,
      children: [
        imageUrl && !imageError ? /* @__PURE__ */ jsx3(
          "img",
          {
            src: imageUrl,
            alt: "User Avatar",
            onError: handleImageError,
            style: {
              width: `${size}px`,
              height: `${size}px`,
              objectFit: "cover",
              borderRadius: `${radius}px`
            }
          }
        ) : name ? /* @__PURE__ */ jsx3(
          Avatar,
          {
            name,
            size,
            radius,
            color: "white",
            bg: "#228BE6"
          }
        ) : /* @__PURE__ */ jsx3(IconUser, { size }),
        showChangeIcon && onClick && /* @__PURE__ */ jsx3(
          "span",
          {
            className: "change-icon",
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              opacity: 0,
              transition: "opacity 0.2s"
            },
            onMouseOver: (e) => {
              e.currentTarget.style.opacity = "1";
            },
            onMouseOut: (e) => {
              e.currentTarget.style.opacity = "0";
            },
            children: /* @__PURE__ */ jsx3(IconArrowsDiff, { size: size * 0.6, color: "white" })
          }
        )
      ]
    }
  ) });
};

// src/components/UserAvatars/UserAvatarGroup.tsx
import {
  Avatar as Avatar2,
  Group as Group3,
  Menu as Menu2,
  Stack,
  Text as Text3,
  Tooltip as Tooltip2
} from "@mantine/core";
import { useTranslation as useTranslation3 } from "react-i18next";
import { IconChevronDown as IconChevronDown2, IconUser as IconUser2 } from "@tabler/icons-react";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var UserAvatarGroup = ({
  users,
  size = 24,
  spacing = "sm",
  onUserClick,
  showDropdownMenu = false,
  showCount = false,
  countLabel,
  className
}) => {
  const { t } = useTranslation3(["team"]);
  const uniqueUsers = [
    ...new Map(
      users.filter(
        (user) => user != null
      ).map((user) => [user.id, user])
    ).values()
  ];
  const handleUserClick = (userId) => {
    if (onUserClick) {
      onUserClick(userId);
    }
  };
  const renderAvatar = (user) => /* @__PURE__ */ jsx4(Tooltip2, { label: user.name, withArrow: true, children: user.profile_image_url ? /* @__PURE__ */ jsx4(
    Avatar2,
    {
      radius: "xl",
      src: user.profile_image_url || void 0,
      size,
      style: onUserClick ? { cursor: "pointer" } : void 0,
      onClick: onUserClick ? () => handleUserClick(user.id) : void 0
    }
  ) : user.name ? /* @__PURE__ */ jsx4(
    Avatar2,
    {
      name: user.name,
      size,
      radius: "xl",
      color: "white",
      bg: "#228BE6"
    }
  ) : /* @__PURE__ */ jsx4(
    Avatar2,
    {
      radius: "xl",
      size,
      bg: "transparent",
      styles: {
        root: { border: "none" },
        placeholder: { backgroundColor: "transparent" }
      },
      children: /* @__PURE__ */ jsx4(IconUser2, { size: size * 0.9 })
    }
  ) }, user.id);
  const renderDropdownMenu = () => /* @__PURE__ */ jsxs4(Menu2, { position: "bottom-end", withinPortal: true, offset: 10, children: [
    /* @__PURE__ */ jsx4(Menu2.Target, { children: /* @__PURE__ */ jsxs4(Group3, { gap: 4, style: { cursor: "pointer" }, children: [
      /* @__PURE__ */ jsxs4(Avatar2, { radius: "xl", size, children: [
        "+",
        uniqueUsers.length - 4
      ] }),
      /* @__PURE__ */ jsx4(
        IconChevronDown2,
        {
          size: 14,
          color: "var(--mantine-color-dimmed)"
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx4(Menu2.Dropdown, { children: /* @__PURE__ */ jsx4(Stack, { gap: "xs", p: "xs", children: uniqueUsers.map((user) => /* @__PURE__ */ jsx4(
      Menu2.Item,
      {
        onClick: onUserClick ? () => handleUserClick(user.id) : void 0,
        style: onUserClick ? { cursor: "pointer" } : void 0,
        children: /* @__PURE__ */ jsxs4(Group3, { gap: "sm", children: [
          user.profile_image_url ? /* @__PURE__ */ jsx4(
            Avatar2,
            {
              radius: "xl",
              name: user.name,
              src: user.profile_image_url,
              size: size - 4
            }
          ) : /* @__PURE__ */ jsx4(
            Avatar2,
            {
              radius: "xl",
              name: user.name,
              size: size - 4,
              bg: "transparent",
              styles: {
                root: { border: "none" },
                placeholder: {
                  backgroundColor: "transparent"
                }
              },
              children: /* @__PURE__ */ jsx4(IconUser2, { size: (size - 4) * 0.9 })
            }
          ),
          /* @__PURE__ */ jsx4(Text3, { size: "sm", children: user.name })
        ] })
      },
      user.id
    )) }) })
  ] });
  const renderSimpleMoreAvatar = () => /* @__PURE__ */ jsx4(
    Tooltip2,
    {
      label: uniqueUsers.slice(4).map((user) => user.name).join(", "),
      withArrow: true,
      children: /* @__PURE__ */ jsxs4(Avatar2, { radius: "xl", size, children: [
        "+",
        uniqueUsers.length - 4
      ] })
    }
  );
  return /* @__PURE__ */ jsxs4(Group3, { gap: "xs", className, children: [
    /* @__PURE__ */ jsx4(Tooltip2.Group, { openDelay: 300, closeDelay: 100, children: /* @__PURE__ */ jsxs4(Avatar2.Group, { spacing, children: [
      uniqueUsers.slice(0, 4).map(renderAvatar),
      uniqueUsers.length > 4 && (showDropdownMenu ? renderDropdownMenu() : renderSimpleMoreAvatar())
    ] }) }),
    showCount && uniqueUsers.length > 0 && /* @__PURE__ */ jsx4(Text3, { size: "xs", children: countLabel ? countLabel(uniqueUsers.length) : t("team:member.pages.team.member_count", {
      count: uniqueUsers.length
    }) })
  ] });
};

// src/components/ActionMenu.tsx
import { ActionIcon as ActionIcon2, Menu as Menu3 } from "@mantine/core";
import { IconDots } from "@tabler/icons-react";
import React from "react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var ActionMenu = ({
  items,
  size = "xs",
  radius = "sm",
  variant = "subtle",
  position = "bottom-end",
  title,
  actionIcon,
  customMenuTarget,
  withArrow = true,
  onMenuChange,
  withinPortal = true
}) => {
  return /* @__PURE__ */ jsxs5(
    Menu3,
    {
      position,
      shadow: "md",
      withArrow,
      onChange: onMenuChange,
      withinPortal,
      radius: "xs",
      children: [
        /* @__PURE__ */ jsx5(Menu3.Target, { children: customMenuTarget ?? /* @__PURE__ */ jsx5(
          ActionIcon2,
          {
            onClick: (e) => e.stopPropagation(),
            size,
            variant,
            tabIndex: -1,
            radius,
            children: actionIcon ?? /* @__PURE__ */ jsx5(
              IconDots,
              {
                size: size === "xs" ? 14 : 18,
                color: "black"
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsxs5(Menu3.Dropdown, { children: [
          title && /* @__PURE__ */ jsx5(Menu3.Label, { children: title }),
          items.map((item) => {
            if ("isCustom" in item && item.isCustom) {
              return /* @__PURE__ */ jsx5(React.Fragment, { children: item.customComponent }, item.id);
            }
            const standardItem = item;
            return /* @__PURE__ */ jsx5(
              Menu3.Item,
              {
                leftSection: standardItem.icon,
                rightSection: standardItem.rightSection,
                color: standardItem.color,
                onClick: standardItem.onClick,
                fz: "xs",
                children: standardItem.label
              },
              standardItem.id
            );
          })
        ] })
      ]
    }
  );
};

// src/components/ConfirmationModal.tsx
import { Button as Button2, Group as Group4, Modal, Stack as Stack2, Text as Text4 } from "@mantine/core";
import { useTranslation as useTranslation4 } from "react-i18next";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var ConfirmationModal = ({
  opened,
  onClose,
  onReject,
  onConfirm,
  title,
  message = "",
  confirmText,
  confirmButtonColor = "blue",
  confirmDisabled = false,
  cancelText,
  loading = false,
  centered = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  overlayProps
}) => {
  const { t } = useTranslation4();
  return /* @__PURE__ */ jsx6(
    Modal,
    {
      opened,
      onClose: () => !loading && onClose(),
      title: /* @__PURE__ */ jsx6(Text4, { fw: 700, size: "lg", children: title }),
      centered,
      closeOnClickOutside: !loading && closeOnClickOutside,
      closeOnEscape: !loading && closeOnEscape,
      overlayProps,
      radius: 4,
      styles: { overlay: { zIndex: 301 }, inner: { zIndex: 301 } },
      trapFocus: false,
      children: /* @__PURE__ */ jsxs6(Stack2, { children: [
        typeof message === "string" ? /* @__PURE__ */ jsx6(Text4, { component: "span", style: { whiteSpace: "pre-wrap" }, children: message }) : message,
        /* @__PURE__ */ jsxs6(Group4, { justify: "flex-end", mt: "md", gap: "sm", children: [
          /* @__PURE__ */ jsx6(
            Button2,
            {
              variant: "default",
              radius: "md",
              onClick: () => !loading && (onReject || onClose)(),
              loading,
              disabled: loading,
              children: cancelText ? cancelText : t("button.no")
            }
          ),
          /* @__PURE__ */ jsx6(
            Button2,
            {
              onClick: () => !loading && onConfirm(),
              radius: "md",
              color: confirmButtonColor,
              loading,
              disabled: loading || confirmDisabled,
              children: confirmText ? confirmText : t("button.yes")
            }
          )
        ] })
      ] })
    }
  );
};

// src/components/ProfileImageUploader.tsx
import { Box as Box2, Center, FileButton, Loader } from "@mantine/core";
import { useState as useState4 } from "react";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var ProfileImageUploader = ({
  name,
  imageUrl,
  onImageChange,
  uploadPath,
  size = 40,
  isEditable = true
}) => {
  const [isUploading, setIsUploading] = useState4(false);
  const [previewImage, setPreviewImage] = useState4(null);
  const { uploadImage, deleteImage } = useFileUpload();
  const handleImageChange = async (file) => {
    console.log("Selected file:", file);
    if (!file) return;
    const imageUrl2 = URL.createObjectURL(file);
    setPreviewImage(imageUrl2);
    try {
      setIsUploading(true);
      if (imageUrl2) {
        const deleteResult = await deleteImage(imageUrl2);
      }
      const result = await uploadImage(file, uploadPath, {
        // onSuccess: (uploadResult) => {
        //     console.log('이미지 업로드 성공:', uploadResult);
        // },
        // onError: (error) => {
        //     console.error('이미지 업로드 실패:', error);
        // }
      });
      onImageChange(result.publicUrl);
    } catch (error) {
      console.error("\uC774\uBBF8\uC9C0 \uBCC0\uACBD \uC911 \uC624\uB958 \uBC1C\uC0DD:", error);
      setPreviewImage(null);
    } finally {
      setIsUploading(false);
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
        setPreviewImage(null);
      }
    }
  };
  return /* @__PURE__ */ jsx7(Center, { children: isEditable ? /* @__PURE__ */ jsx7(
    FileButton,
    {
      onChange: (file) => {
        void handleImageChange(file);
      },
      accept: "image/png,image/jpeg,image/jpg",
      disabled: isUploading,
      children: (props) => /* @__PURE__ */ jsxs7(
        Box2,
        {
          style: {
            position: "relative",
            cursor: isUploading ? "wait" : "pointer",
            borderRadius: "8px",
            overflow: "hidden"
          },
          ...props,
          children: [
            /* @__PURE__ */ jsx7(
              UserAvatar,
              {
                name,
                imageUrl: previewImage || imageUrl || void 0,
                size
              }
            ),
            isUploading && /* @__PURE__ */ jsx7(
              Box2,
              {
                style: {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  borderRadius: "md"
                },
                children: /* @__PURE__ */ jsx7(
                  Loader,
                  {
                    type: "dots",
                    size: "sm",
                    color: "white"
                  }
                )
              }
            )
          ]
        }
      )
    }
  ) : (
    // <Avatar
    //     src={imageUrl || undefined}
    //     size={size}
    //     radius="md"
    //     alt="프로필 이미지"
    // />
    /* @__PURE__ */ jsx7(
      UserAvatar,
      {
        name,
        imageUrl: imageUrl || void 0,
        size
      }
    )
  ) });
};

// src/components/WarningComponent.tsx
import { Box as Box3, Button as Button3, Divider, Group as Group5, Text as Text5, Tooltip as Tooltip3 } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Fragment, jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var WarningComponent = ({
  title,
  description,
  buttonText,
  tooltipText,
  onButtonClick,
  dividerLabel
}) => {
  const defaultDividerLabel = /* @__PURE__ */ jsxs8(Fragment, { children: [
    /* @__PURE__ */ jsx8(IconAlertTriangle, { size: 14 }),
    /* @__PURE__ */ jsx8(Text5, { size: "sm", c: "dimmed", ml: 5, children: "Warning" })
  ] });
  return /* @__PURE__ */ jsxs8(Fragment, { children: [
    /* @__PURE__ */ jsx8(
      Divider,
      {
        my: "md",
        variant: "solid",
        labelPosition: "center",
        label: dividerLabel || defaultDividerLabel
      }
    ),
    /* @__PURE__ */ jsx8(
      Box3,
      {
        style: {
          borderRadius: "16px",
          backgroundColor: "#F9F9F9",
          padding: "16px 24px"
        },
        children: /* @__PURE__ */ jsxs8(Group5, { justify: "space-between", gap: "xl", wrap: "wrap", children: [
          /* @__PURE__ */ jsxs8(Group5, { gap: "md", children: [
            /* @__PURE__ */ jsx8(Text5, { fw: 500, size: "md", c: "dimmed", children: title }),
            /* @__PURE__ */ jsx8(Text5, { size: "xs", c: "dimmed", style: { whiteSpace: "pre-wrap" }, children: description })
          ] }),
          tooltipText ? /* @__PURE__ */ jsx8(
            Tooltip3,
            {
              label: tooltipText,
              position: "right",
              radius: "md",
              withArrow: true,
              arrowSize: 5,
              transitionProps: { transition: "fade", duration: 200 },
              children: /* @__PURE__ */ jsx8(
                Button3,
                {
                  variant: "filled",
                  color: "gray",
                  radius: "md",
                  onClick: onButtonClick,
                  children: buttonText
                }
              )
            }
          ) : /* @__PURE__ */ jsx8(
            Button3,
            {
              variant: "filled",
              color: "gray",
              radius: "md",
              onClick: onButtonClick,
              children: buttonText
            }
          )
        ] })
      }
    )
  ] });
};

// src/constants/status.ts
var StatusCode = /* @__PURE__ */ ((StatusCode2) => {
  StatusCode2["BACKLOG"] = "BACKLOG";
  StatusCode2["PLANNED"] = "PLANNED";
  StatusCode2["IN_PROGRESS"] = "IN_PROGRESS";
  StatusCode2["AT_RISK"] = "AT_RISK";
  StatusCode2["PAUSED"] = "PAUSED";
  StatusCode2["COMPLETED"] = "COMPLETED";
  StatusCode2["CANCELED"] = "CANCELED";
  return StatusCode2;
})(StatusCode || {});
var StatusCodeLabel = {
  ["BACKLOG" /* BACKLOG */]: translation_default.t("field.status.backlog"),
  ["PLANNED" /* PLANNED */]: translation_default.t("field.status.planned"),
  ["IN_PROGRESS" /* IN_PROGRESS */]: translation_default.t("field.status.in_progress"),
  ["AT_RISK" /* AT_RISK */]: translation_default.t("field.status.at_risk"),
  ["PAUSED" /* PAUSED */]: translation_default.t("field.status.paused"),
  ["COMPLETED" /* COMPLETED */]: translation_default.t("field.status.completed"),
  ["CANCELED" /* CANCELED */]: translation_default.t("field.status.canceled")
};
var ActiveStatusCodes = [
  "PLANNED" /* PLANNED */,
  "IN_PROGRESS" /* IN_PROGRESS */,
  "AT_RISK" /* AT_RISK */
];
var CompleteStatusCodes = [
  "COMPLETED" /* COMPLETED */,
  "CANCELED" /* CANCELED */
];
var InvitationStatusCode = /* @__PURE__ */ ((InvitationStatusCode2) => {
  InvitationStatusCode2["INVITED"] = "INVITED";
  InvitationStatusCode2["ACCEPTED"] = "ACCEPTED";
  InvitationStatusCode2["REJECTED"] = "REJECTED";
  return InvitationStatusCode2;
})(InvitationStatusCode || {});

// src/constants/recordingImportStatus.ts
var RECORDING_IMPORT_STATUS = {
  NONE: "none",
  POLLING: "polling",
  PROCESSING: "processing",
  READY: "ready",
  IMPORTING: "importing",
  COMPLETED: "completed",
  NOT_FOUND: "not_found",
  FAILED: "failed"
};

// src/constants/member.ts
var MemberRole = /* @__PURE__ */ ((MemberRole2) => {
  MemberRole2["OWNER"] = "owner";
  MemberRole2["ADMIN"] = "admin";
  MemberRole2["MANAGER"] = "manager";
  MemberRole2["MEMBER"] = "member";
  MemberRole2["INVITED"] = "invited";
  return MemberRole2;
})(MemberRole || {});
var MemberBadgeColor = {
  ["owner" /* OWNER */]: "blue",
  ["admin" /* ADMIN */]: "yellow",
  ["manager" /* MANAGER */]: "teal",
  ["member" /* MEMBER */]: "violet",
  ["invited" /* INVITED */]: "gray"
};
var EditableRoles = [
  "admin" /* ADMIN */,
  "owner" /* OWNER */,
  "manager" /* MANAGER */
];
var roleOptions = [
  { value: "owner", label: "OWNER" },
  { value: "admin", label: "ADMIN" },
  { value: "manager", label: "MANAGER" },
  { value: "member", label: "MEMBER" }
];
var getUserRole = (userId, workspaceMembers = []) => {
  if (!userId || !workspaceMembers.length) return "member" /* MEMBER */;
  const member = workspaceMembers.find((member2) => member2.user_id === userId);
  return member?.role || "member" /* MEMBER */;
};
var hasEditPermission = (userId, workspaceMembers = []) => {
  const userRole = getUserRole(userId, workspaceMembers);
  return EditableRoles.includes(userRole);
};
var hasAdminPermission = (userId, workspaceMembers = []) => {
  const userRole = getUserRole(userId, workspaceMembers);
  return userRole === "owner" /* OWNER */ || userRole === "admin" /* ADMIN */;
};
var hasManagerPermission = (userId, workspaceMembers = []) => {
  const userRole = getUserRole(userId, workspaceMembers);
  return userRole === "owner" /* OWNER */ || userRole === "admin" /* ADMIN */ || userRole === "manager" /* MANAGER */;
};
var canManageTargetMember = (actorRole, targetRole) => {
  if (actorRole === "owner" /* OWNER */ || actorRole === "admin" /* ADMIN */)
    return targetRole !== "owner" /* OWNER */;
  if (actorRole === "manager" /* MANAGER */)
    return targetRole === "member" /* MEMBER */;
  return false;
};

// src/constants/profileCard.ts
var CommunicationStyle = /* @__PURE__ */ ((CommunicationStyle2) => {
  CommunicationStyle2["SUMMARY"] = "SUMMARY";
  CommunicationStyle2["CONTEXT"] = "CONTEXT";
  CommunicationStyle2["DATA"] = "DATA";
  CommunicationStyle2["DISCUSSION"] = "DISCUSSION";
  return CommunicationStyle2;
})(CommunicationStyle || {});
var FeedbackStyle = /* @__PURE__ */ ((FeedbackStyle2) => {
  FeedbackStyle2["DIRECT"] = "DIRECT";
  FeedbackStyle2["POSITIVE"] = "POSITIVE";
  FeedbackStyle2["SOLUTION"] = "SOLUTION";
  FeedbackStyle2["SELF_REFLECTION"] = "SELF_REFLECTION";
  return FeedbackStyle2;
})(FeedbackStyle || {});
var CareerOrientation = /* @__PURE__ */ ((CareerOrientation2) => {
  CareerOrientation2["SPECIALIST"] = "SPECIALIST";
  CareerOrientation2["LEADER"] = "LEADER";
  CareerOrientation2["GENERALIST"] = "GENERALIST";
  CareerOrientation2["EXPLORER"] = "EXPLORER";
  return CareerOrientation2;
})(CareerOrientation || {});
var Motivation = /* @__PURE__ */ ((Motivation2) => {
  Motivation2["GROWTH"] = "GROWTH";
  Motivation2["AUTONOMY"] = "AUTONOMY";
  Motivation2["RECOGNITION"] = "RECOGNITION";
  Motivation2["IMPACT"] = "IMPACT";
  return Motivation2;
})(Motivation || {});
var PROFILE_CARD_FIELD_ORDER = [
  "communicationStyle",
  "feedbackStyle",
  "careerOrientation",
  "motivation"
];
var PROFILE_CARD_EMOJI = {
  communicationStyle: "\u{1F4AC}",
  feedbackStyle: "\u2728",
  careerOrientation: "\u{1F3AF}",
  motivation: "\u{1F525}"
};
var PROFILE_CARD_OPTIONS = {
  communicationStyle: Object.values(CommunicationStyle),
  feedbackStyle: Object.values(FeedbackStyle),
  careerOrientation: Object.values(CareerOrientation),
  motivation: Object.values(Motivation)
};

// src/constants/oneonone.ts
var QuestionSourceType = /* @__PURE__ */ ((QuestionSourceType2) => {
  QuestionSourceType2["TEMPLATE"] = "TEMPLATE";
  QuestionSourceType2["MANUAL"] = "MANUAL";
  QuestionSourceType2["AI_GENERATED"] = "AI_GENERATED";
  QuestionSourceType2["PREP_GENERATED"] = "PREP_GENERATED";
  QuestionSourceType2["VERBAL"] = "VERBAL";
  return QuestionSourceType2;
})(QuestionSourceType || {});
var ONE_ON_ONE_RECORDINGS_BUCKET = "oneonone-recordings";
var ANALYSIS_REQUEST_LOST_PREFIX = "[client_network]";
var PREP_SUMMARY_MIN_CONTENT_LENGTH = 600;

// src/constants/aiApi.ts
var AI_API = {
  BASE_URL: "https://orblit-1on1-ai-486678076140.asia-northeast3.run.app/api",
  ENDPOINTS: {
    // 질문 생성
    TEMPLATE_GENERATE: "/template/premium/generate",
    TEMPLATE_RECOMMEND: "/template/premium/recommend",
    // 미팅 분석
    ANALYZE: "/analyze",
    // 프렙
    PREP_CHAT: "/prep/chat/stream",
    // 리포트
    IC_REPORT: "/ic-report"
  }
};
var AI_API_ENDPOINTS = {
  /**
   * 질문 생성 (PRO)
   */
  TEMPLATE_GENERATE: `${AI_API.BASE_URL}${AI_API.ENDPOINTS.TEMPLATE_GENERATE}`,
  /**
   * 미팅 주제 및 프롬프트 추천 (PRO)
   */
  TEMPLATE_RECOMMEND: `${AI_API.BASE_URL}${AI_API.ENDPOINTS.TEMPLATE_RECOMMEND}`,
  /**
   * 미팅 프렙 채팅
   */
  PREP_CHAT: `${AI_API.BASE_URL}${AI_API.ENDPOINTS.PREP_CHAT}`,
  /**
   * 미팅 분석
   */
  ANALYZIE: `${AI_API.BASE_URL}${AI_API.ENDPOINTS.ANALYZE}`,
  /**
   * 멤버 리포트
   */
  IC_REPORT: `${AI_API.BASE_URL}${AI_API.ENDPOINTS.IC_REPORT}`
};

// src/constants/subscription.ts
var SubscriptionStatus = /* @__PURE__ */ ((SubscriptionStatus2) => {
  SubscriptionStatus2["ACTIVE"] = "ACTIVE";
  SubscriptionStatus2["AT_RISK"] = "AT_RISK";
  SubscriptionStatus2["PENDING_CANCELLATION"] = "PENDING_CANCELLATION";
  SubscriptionStatus2["CANCELLED"] = "CANCELLED";
  return SubscriptionStatus2;
})(SubscriptionStatus || {});
var ACTIVE_SUBSCRIPTION_STATUSES = [
  "ACTIVE" /* ACTIVE */,
  "AT_RISK" /* AT_RISK */,
  "PENDING_CANCELLATION" /* PENDING_CANCELLATION */
];
var PAYMENT_ERROR_CODE = {
  /** 이미 활성 구독이 있어 결제수단 등록을 거절 */
  SUBSCRIPTION_EXISTS: "SUBSCRIPTION_EXISTS",
  /** 결제는 완료됐으나 이후 DB 처리 실패 → 사용자 재시도 금지, 운영에서 복구 */
  PAID_BUT_PROVISIONING_FAILED: "PAID_BUT_PROVISIONING_FAILED"
};
var MEMBERSHIP_FEATURES = {
  FREE: /* @__PURE__ */ new Set([]),
  LIGHT: /* @__PURE__ */ new Set(["ai-question", "prep"]),
  PRO: /* @__PURE__ */ new Set(["recording", "ai-question", "report", "prep"])
};
var TEAM_UNLOCKED_FEATURES = /* @__PURE__ */ new Set([
  "ai-question",
  "report",
  "prep"
]);

// src/workspace/workspaceStore.ts
import { create as create2 } from "zustand";
var createBaseWorkspaceStore = () => {
  const workspaceService = createBaseWorkspaceService();
  return create2()((set, get) => {
    const findWorkspaceInList = (workspaceId) => {
      const { workspaces } = get();
      return workspaces.find((ws) => ws.id === workspaceId);
    };
    const handleError = (error, errorMessage) => {
      console.error(errorMessage, error);
      return error instanceof Error ? error.message : errorMessage;
    };
    return {
      workspaces: [],
      currentWorkspace: null,
      loading: false,
      error: null,
      fetchWorkspaces: async () => {
        const { loading } = get();
        if (loading) return;
        set((state) => ({
          ...state,
          loading: true,
          error: null
        }));
        try {
          const workspaces = await workspaceService.getMyWorkspaces();
          const { currentWorkspace } = get();
          if (currentWorkspace) {
            const updatedCurrentWorkspace = workspaces.find(
              (ws) => ws.id === currentWorkspace.id
            );
            if (updatedCurrentWorkspace) {
              if (JSON.stringify(updatedCurrentWorkspace) !== JSON.stringify(currentWorkspace)) {
                set((state) => ({
                  ...state,
                  workspaces,
                  currentWorkspace: updatedCurrentWorkspace,
                  loading: false
                }));
                return;
              }
            }
          }
          if (!currentWorkspace && workspaces.length > 0) {
            set((state) => ({
              ...state,
              workspaces,
              currentWorkspace: workspaces[0],
              loading: false
            }));
            return;
          }
          set((state) => ({
            ...state,
            workspaces,
            loading: false
          }));
        } catch (error) {
          set((state) => ({
            ...state,
            loading: false,
            error: handleError(
              error,
              "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uBAA9\uB85D\uC744 \uAC00\uC838\uC624\uB294\uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
            )
          }));
        }
      },
      setCurrentWorkspace: (workspace) => {
        const { workspaces, currentWorkspace } = get();
        if (workspace && currentWorkspace && workspace.id === currentWorkspace.id && JSON.stringify(workspace) === JSON.stringify(currentWorkspace)) {
          return;
        }
        if (workspace) {
          const updatedWorkspace = findWorkspaceInList(workspace.id);
          set((state) => ({
            ...state,
            currentWorkspace: updatedWorkspace || workspace
          }));
        } else if (workspaces.length > 0) {
          set((state) => ({
            ...state,
            currentWorkspace: workspaces[0]
          }));
        } else {
          set((state) => ({
            ...state,
            currentWorkspace: null
          }));
        }
      },
      updateWorkspaceInList: async (updatedWorkspace) => {
        const targetWorkspace = await workspaceService.getWorkspaceById(
          updatedWorkspace.id
        );
        if (!targetWorkspace) return;
        const { workspaces, currentWorkspace } = get();
        const updatedWorkspaces = workspaces.map(
          (workspaceItem) => workspaceItem.id === targetWorkspace.id ? targetWorkspace : workspaceItem
        );
        if (currentWorkspace?.id === updatedWorkspace.id) {
          set((state) => ({
            ...state,
            workspaces: updatedWorkspaces,
            currentWorkspace: targetWorkspace
          }));
        } else {
          set((state) => ({
            ...state,
            workspaces: updatedWorkspaces
          }));
        }
      },
      deleteWorkspace: async (workspaceId) => {
        set((state) => ({
          ...state,
          loading: true,
          error: null
        }));
        try {
          await workspaceService.deleteWorkspace(workspaceId);
          const { workspaces, currentWorkspace } = get();
          const updatedWorkspaces = workspaces.filter(
            (workspaceItem) => workspaceItem.id !== workspaceId
          );
          if (currentWorkspace?.id === workspaceId) {
            const newCurrentWorkspace = updatedWorkspaces.length > 0 ? updatedWorkspaces[0] : null;
            set((state) => ({
              ...state,
              workspaces: updatedWorkspaces,
              currentWorkspace: newCurrentWorkspace,
              loading: false
            }));
          } else {
            set((state) => ({
              ...state,
              workspaces: updatedWorkspaces,
              loading: false
            }));
          }
        } catch (error) {
          set((state) => ({
            ...state,
            loading: false,
            error: handleError(
              error,
              "\uC6CC\uD06C\uC2A4\uD398\uC774\uC2A4 \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
            )
          }));
        }
      }
    };
  });
};

// src/workspace/workspaceCoreValueService.ts
var workspaceCoreValueService = {
  /**
   * 워크스페이스에 핵심 가치를 추가합니다.
   * @param workspaceId 워크스페이스 ID
   * @param name 핵심 가치 이름
   * @returns 추가된 핵심 가치 정보
   */
  async addWorkspaceCoreValue(workspaceId, name) {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) {
        throw new Error("\uC778\uC99D\uB41C \uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.");
      }
      const { data: data2, error } = await supabase.from("workspace_core_value").insert({
        workspace_id: workspaceId,
        name,
        register_id: userId,
        enable: true
      }).select();
      if (error) {
        throw new Error(`${error.message}`);
      }
      if (!data2 || data2.length === 0) {
        throw new Error("NOT_FOUND_ERROR");
      }
      return data2[0];
    } catch (error) {
      console.error("\uD575\uC2EC \uAC00\uCE58 \uCD94\uAC00 \uC2E4\uD328:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        "NOT_FOUND_ERROR"
      );
    }
  },
  /**
   * 워크스페이스에서 핵심 가치를 삭제합니다.
   * 피드백에 이미 등록된 핵심 가치는 소프트 삭제, 그렇지 않은 경우 하드 삭제를 수행합니다.
   * @param coreValueId 삭제할 핵심 가치 ID
   * @returns 성공 여부
   */
  async deleteWorkspaceCoreValue(coreValueId) {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) {
        throw new Error("\uC778\uC99D\uB41C \uC0AC\uC6A9\uC790\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.");
      }
      const { data: feedbackCoreValues, error: checkError } = await supabase.from("feedback_core_value").select("id").eq("core_value_id", coreValueId).eq("enable", true).limit(1);
      if (checkError) {
        throw new Error(
          `${checkError.message}`
        );
      }
      if (feedbackCoreValues && feedbackCoreValues.length > 0) {
        const { data: data2, error } = await supabase.from("workspace_core_value").update({
          enable: false,
          update_id: userId,
          update_date: (/* @__PURE__ */ new Date()).toISOString()
        }).eq("id", coreValueId).select();
        if (error) {
          throw new Error(
            `${error.message}`
          );
        }
        if (!data2 || data2.length === 0) {
          throw new Error("NOT_PERMISSION_ERROR");
        }
      } else {
        const { error } = await supabase.from("workspace_core_value").delete().eq("id", coreValueId);
        if (error) {
          throw new Error(
            `${error.message}`
          );
        }
      }
      return true;
    } catch (error) {
      console.error("\uD575\uC2EC \uAC00\uCE58 \uC0AD\uC81C \uC2E4\uD328:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        "NOT_FOUND_ERROR"
      );
    }
  },
  /**
   * 워크스페이스의 모든 핵심 가치를 조회합니다.
   * @param workspaceId 워크스페이스 ID
   * @returns 핵심 가치 목록
   */
  async getWorkspaceCoreValues(workspaceId) {
    try {
      const { data: data2, error } = await supabase.from("workspace_core_value").select("*").eq("workspace_id", workspaceId).eq("enable", true).order("register_date", { ascending: false });
      if (error) {
        throw new Error(`${error.message}`);
      }
      return data2 || [];
    } catch (error) {
      console.error("\uD575\uC2EC \uAC00\uCE58 \uC870\uD68C \uC2E4\uD328:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        "NOT_FOUND_ERROR"
      );
    }
  }
};

// src/workspace/useCurrentWorkspace.ts
import { useCallback, useEffect, useState as useState5 } from "react";
function createUseCurrentWorkspace(deps) {
  const { useMyWorkspaces, useWorkspaceStore } = deps;
  const useWorkspaceIdFromUrl = deps.useWorkspaceIdFromUrl ?? (() => void 0);
  const useCurrentWorkspace = (userId) => {
    const getCurrentWorkspaceKey = useCallback(
      (id) => `current_workspace_${id}`,
      []
    );
    const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
    const [initialLoadDone, setInitialLoadDone] = useState5(false);
    const { data: workspaces = [], isLoading: isWorkspacesLoading } = useMyWorkspaces();
    const workspaceIdFromUrl = useWorkspaceIdFromUrl?.();
    useEffect(() => {
      if (!userId || isWorkspacesLoading || initialLoadDone) return;
      const savedWorkspaceId = localStorage.getItem(
        getCurrentWorkspaceKey(userId)
      );
      if (workspaces?.length > 0) {
        if (workspaceIdFromUrl) {
          const urlWorkspace = workspaces.find(
            (ws) => ws.id === workspaceIdFromUrl
          );
          if (urlWorkspace) {
            setCurrentWorkspace(urlWorkspace);
            setInitialLoadDone(true);
            return;
          }
        }
        if (savedWorkspaceId) {
          const savedWorkspace = workspaces.find(
            (ws) => ws.id === savedWorkspaceId
          );
          if (savedWorkspace) {
            setCurrentWorkspace(savedWorkspace);
            setInitialLoadDone(true);
            return;
          }
        }
        if (!currentWorkspace) {
          setCurrentWorkspace(workspaces[0]);
          setInitialLoadDone(true);
        }
      }
    }, [
      userId,
      workspaces,
      isWorkspacesLoading,
      currentWorkspace,
      setCurrentWorkspace,
      workspaceIdFromUrl,
      getCurrentWorkspaceKey,
      initialLoadDone,
      setInitialLoadDone
    ]);
    useEffect(() => {
      if (currentWorkspace && userId) {
        localStorage.setItem(
          getCurrentWorkspaceKey(userId),
          currentWorkspace.id
        );
      }
    }, [currentWorkspace, userId, getCurrentWorkspaceKey]);
    return {
      currentWorkspace,
      setCurrentWorkspace,
      isLoading: isWorkspacesLoading || workspaces.length > 0 && !initialLoadDone,
      workspaces
    };
  };
  return useCurrentWorkspace;
}

// src/workspace/analysisSettingService.ts
var analysisSettingService = {
  async getWorkspaceAnalysisSetting(workspaceId) {
    try {
      const response = await apiClient.get("workspace/custom-setting", {
        searchParams: { workspaceId }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  },
  async upsertWorkspaceAnalysisSetting(workspaceId, visibilityMode) {
    try {
      const response = await apiClient.put("workspace/custom-setting", {
        json: {
          workspaceId,
          analysisVisibility: visibilityMode
        }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  },
  async getLeaderAnalysisSharing(workspaceId, leaderId) {
    try {
      const response = await apiClient.get("workspace/leader-analysis-sharing", {
        searchParams: { workspaceId, leaderId }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  },
  async upsertLeaderAnalysisSharing(workspaceId, sharingEnabled) {
    try {
      const response = await apiClient.put("workspace/leader-analysis-sharing", {
        json: { workspaceId, sharingEnabled }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  },
  /**
   * MeetingSection용: 워크스페이스 설정 + 리더 공유 설정을 통합 조회
   */
  async getAnalysisVisibility(workspaceId, leaderId) {
    try {
      const response = await apiClient.get("workspace/analysis-visibility", {
        searchParams: { workspaceId, leaderId }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  }
};

// src/workspace/analysisVisibility.ts
function canSeeAnalysis({
  visibility,
  currentUserId,
  leaderId
}) {
  if (!currentUserId) return false;
  const isLeader = currentUserId === leaderId;
  if (isLeader) return true;
  if (!visibility) return false;
  switch (visibility.mode) {
    case "both":
      return true;
    case "per_leader":
      return visibility.leaderSharingEnabled;
    case "leader_only":
      return false;
    default: {
      visibility.mode;
      return false;
    }
  }
}

// src/workspace/questionDraftSettingService.ts
var questionDraftSettingService = {
  async getQuestionDraftSetting(workspaceId) {
    try {
      const response = await apiClient.get("workspace/question-draft-setting", {
        searchParams: { workspaceId }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  },
  async updateQuestionDraftSetting(workspaceId, enabled) {
    try {
      const response = await apiClient.put("workspace/question-draft-setting", {
        json: { workspaceId, enabled }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  }
};

// src/member_invitation/memberInvitationService.ts
var createBaseMemberInvitationService = () => ({
  async createMemberInvitation(params) {
    const {
      workspaceId,
      teamId,
      invitation_type,
      email,
      role = "member",
      register_id,
      initial_active_status = true,
      // 기본값: 활성화
      leader_id
    } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    try {
      const { data: data2, error } = await supabase.from("member_invitation").insert({
        id: crypto.randomUUID(),
        workspace_id: workspaceId,
        team_id: teamId,
        invitation_type,
        email,
        role,
        register_id,
        register_date: now,
        update_date: now,
        initial_active_status,
        // 활성화 여부 추가
        ...leader_id ? { leader_id } : {}
      }).select().single();
      if (error) throw error;
      return data2;
    } catch (error) {
      console.error("Error inviting member:", error);
      throw error;
    }
  },
  async getInvitationById(invitationId) {
    try {
      const data2 = await apiClient.get(`payment/invitation/${invitationId}`).json();
      return { ...data2, enable: true };
    } catch (error) {
      console.error("Error fetching invitation:", error);
      return null;
    }
  },
  async getInvitations(filter = {}) {
    try {
      let query = supabase.from("member_invitation").select(`
                    
                    *,
                    workspace:workspace_id(
                        id,
                        name,
                        profile_image_url
                    ),
                    team:team_id(
                        id,
                        name,
                        profile_image_url
                    ),
                    register_user:register_id(
                        id,
                        name,
                        profile_image_url
                    )
                `);
      if (filter.email) {
        query = query.eq("email", filter.email);
      }
      if (filter.workspaceId) {
        query = query.eq("workspace_id", filter.workspaceId);
      }
      if (filter.teamId) {
        query = query.eq("team_id", filter.teamId);
      }
      if (filter.statusCode) {
        query = query.eq("status_code", filter.statusCode);
      }
      if (filter.invitationType) {
        query = query.eq("invitation_type", filter.invitationType);
      }
      if (filter.userId) {
        query = query.eq("register_id", filter.userId);
      }
      const { data: data2, error } = await query.order("register_date", {
        ascending: false
      });
      if (error) throw error;
      return data2;
    } catch (error) {
      console.error("Error fetching invitations:", error);
      return [];
    }
  },
  async updateInvitationStatus(invitationId, statusCode, userId) {
    try {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      const updates = {
        status_code: statusCode,
        update_date: now,
        ...userId ? { update_id: userId } : {}
      };
      const { data: data2, error } = await supabase.from("member_invitation").update(updates).eq("id", invitationId).select().single();
      if (error) throw error;
      return data2;
    } catch (error) {
      console.error("Error updating invitation status:", error);
      return null;
    }
  },
  async acceptInvitation(invitationId, userId) {
    console.log(`User ${userId} accepting invitation ${invitationId}`);
    try {
      const invitation = await this.getInvitationById(invitationId);
      if (!invitation) {
        throw new Error("\uCD08\uB300\uC7A5\uC744 \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.");
      }
      if (invitation.status_code !== "INVITED" /* INVITED */) {
        throw new Error("\uC774\uBBF8 \uCC98\uB9AC\uB41C \uCD08\uB300\uC7A5\uC785\uB2C8\uB2E4.");
      }
      const response = await apiClient.post("payment/accept-invitation", {
        json: { invitationId }
      }).json();
      if (!response.success) {
        throw new Error(response.message || "\uCD08\uB300 \uC218\uB77D\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
      }
      return await this.getInvitationById(invitationId);
    } catch (error) {
      console.error("Error accepting invitation:", error);
      if (error instanceof ApiClientError) {
        throw new Error(error.message || "\uCD08\uB300 \uC218\uB77D \uCC98\uB9AC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
      }
      throw error;
    }
  },
  async rejectInvitation(invitationId, userId) {
    return await this.updateInvitationStatus(
      invitationId,
      "REJECTED" /* REJECTED */,
      userId
    );
  },
  async deleteInvitation(invitationId) {
    try {
      const { error } = await supabase.from("member_invitation").delete().eq("id", invitationId);
      if (error) {
        console.error("Error deleting invitation:", error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error("Error deleting invitation:", error);
      return false;
    }
  },
  async checkExistingInvitation(email, workspaceId, invitationType) {
    try {
      const query = supabase.from("member_invitation").select("id").eq("email", email).eq("status_code", "INVITED" /* INVITED */).eq("workspace_id", workspaceId).eq("invitation_type", invitationType);
      const { data: data2, error } = await query;
      if (error) throw error;
      return data2 && data2.length > 0;
    } catch (error) {
      console.error("Error checking existing invitation:", error);
      return false;
    }
  }
});

// src/profileCard/services/profileCardService.ts
var createProfileCardService = (apiClient2) => {
  const client = apiClient2 || apiClient;
  return {
    /**
     * 자신의 프로필 카드 조회
     */
    async getMyProfileCard() {
      const response = await client.get("profile-card").json();
      return response?.data ?? null;
    },
    /**
     * 미팅 멤버의 프로필 카드 조회
     * 사용자가 해당 미팅의 리더일 경우에만 조회 가능
     */
    async getMemberProfileCard({
      meetingId
    }) {
      const response = await client.get("profile-card/member", {
        searchParams: { meetingId }
      }).json();
      return response?.data ?? null;
    },
    /**
     * 프로필 카드 저장/수정
     */
    async saveProfileCard(profileCardData) {
      const response = await client.post("profile-card", {
        json: profileCardData
      }).json();
      return response;
    }
  };
};
var profileCardService = createProfileCardService();

// src/oneonone/types/aiTypes.ts
var MEMBER_FEEDBACK_LABELS = [
  "strength",
  "not_raised",
  "improve",
  "settled",
  "open_issue"
];
var MEMBER_FEEDBACK_SOURCES = ["meeting", "my_prep", "prep_chat"];

// src/oneonone/types/meetingPrepTypes.ts
var WorkCondition = /* @__PURE__ */ ((WorkCondition2) => {
  WorkCondition2["VERY_GOOD"] = "VERY_GOOD";
  WorkCondition2["GOOD"] = "GOOD";
  WorkCondition2["NORMAL"] = "NORMAL";
  WorkCondition2["TIRED"] = "TIRED";
  WorkCondition2["VERY_BAD"] = "VERY_BAD";
  return WorkCondition2;
})(WorkCondition || {});
var MeetingTopic = /* @__PURE__ */ ((MeetingTopic2) => {
  MeetingTopic2["PERFORMANCE_FEEDBACK"] = "PERFORMANCE_FEEDBACK";
  MeetingTopic2["GROWTH_CAREER"] = "GROWTH_CAREER";
  MeetingTopic2["WORK_ISSUES"] = "WORK_ISSUES";
  MeetingTopic2["TEAM_CULTURE"] = "TEAM_CULTURE";
  MeetingTopic2["ROLE_RESPONSIBILITY"] = "ROLE_RESPONSIBILITY";
  return MeetingTopic2;
})(MeetingTopic || {});
var PREP_SURVEY_FIELD_ORDER = [
  "condition",
  "topic"
];
var PREP_SURVEY_OPTIONS = {
  condition: Object.values(WorkCondition),
  topic: Object.values(MeetingTopic)
};
var PREP_GUIDE_STAGE_KEYS = [
  "OPENING",
  "MAIN",
  "DEEPEN",
  "CLOSING"
];
var LEGACY_MEETING_GUIDE_FIELDS = [
  "key_insight",
  "approach",
  "tip"
];

// src/oneonone/types/notificationTypes.ts
var NOTIFICATION_TYPE_SET = {
  meeting_add: true,
  meeting_change: true,
  meeting_cancel: true,
  question_request: true,
  prep_share: true,
  dday_reminder: true,
  recording_ready: true,
  analysis_complete: true,
  member_feedback_ready: true,
  in_progress_incomplete_reminder: true,
  cycle_reminder: true
};
function isOneOnOneNotificationType(value) {
  return value in NOTIFICATION_TYPE_SET;
}

// src/oneonone/types/googleCalendarTypes.ts
var GOOGLE_CALENDAR_PROVIDER = "google";
var GOOGLE_SCOPES = {
  CALENDAR: "https://www.googleapis.com/auth/calendar",
  MEET_READONLY: "https://www.googleapis.com/auth/meetings.space.readonly",
  DRIVE_FILE: "https://www.googleapis.com/auth/drive.file"
};
var GOOGLE_CALENDAR_SCOPES = [GOOGLE_SCOPES.CALENDAR];
var GOOGLE_MEET_RECORDING_SCOPES = [
  GOOGLE_SCOPES.MEET_READONLY,
  GOOGLE_SCOPES.DRIVE_FILE
];
var GOOGLE_ALL_SCOPES = [
  ...GOOGLE_CALENDAR_SCOPES,
  ...GOOGLE_MEET_RECORDING_SCOPES
];
function hasRequiredScopes(grantedScope, requiredScopes) {
  if (!grantedScope) return false;
  const granted = grantedScope.split(" ");
  return requiredScopes.every((s) => granted.includes(s));
}

// src/oneonone/types/actionItemProgressTypes.ts
var PROGRESS_VERDICT_VALUES = [
  "pending",
  "progressing",
  "completed",
  "not_discussed"
];
var PROGRESS_VERDICT_KEYS = PROGRESS_VERDICT_VALUES;
var CONFIRMED_PROGRESS_VALUES = [
  ...PROGRESS_VERDICT_VALUES,
  "dismissed"
];
var TERMINAL_CONFIRMED_PROGRESS = ["completed", "dismissed"];
var TERMINAL_CONFIRMED_PROGRESS_KEYS = TERMINAL_CONFIRMED_PROGRESS;
var isTerminalConfirmedProgress = (state) => state !== null && TERMINAL_CONFIRMED_PROGRESS_KEYS.includes(state);
var PURPOSE_KEY_VALUES = [
  "relationship",
  "work_status",
  "growth_career",
  "satisfaction_motivation",
  "feedback_coaching",
  "action_review"
];
var PURPOSE_KEYS = PURPOSE_KEY_VALUES;

// src/oneonone/types/actionItemOverviewTypes.ts
var TOPIC_STATS_WINDOW_DAYS = 90;

// src/oneonone/types/realtimeMeetingTypes.ts
var REALTIME_DEBOUNCE_DELAY_MS = 500;
var REALTIME_PRESENCE_THROTTLE_MS = 40;
function stringToColor(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 16777215).toString(16).toUpperCase();
  return `#${"00000".substring(0, 6 - c.length) + c}`;
}
function getGlobalMeetingChannelName(userId) {
  return `user_meetings:${userId}`;
}

// src/oneonone/types/repeatNudgeTypes.ts
function resolveNextMeetingNudgeData({
  pairMembers,
  isLoading,
  isError: isError2,
  counterpartId,
  now = /* @__PURE__ */ new Date()
}) {
  if (!counterpartId) return { status: "skip" };
  if (isError2) return { status: "unavailable" };
  if (isLoading || pairMembers === void 0) return { status: "loading" };
  const existingPair = pairMembers.find(
    (pair) => pair.counterpart.id === counterpartId
  );
  const frequencyWeeks = existingPair?.meeting_frequency ?? null;
  if (!existingPair || frequencyWeeks === null) {
    return {
      status: "ready",
      branch: "frequency_unset",
      hasExistingPair: Boolean(existingPair)
    };
  }
  const suggestedDate = new Date(now);
  suggestedDate.setDate(suggestedDate.getDate() + frequencyWeeks * 7);
  return {
    status: "ready",
    branch: "frequency_set",
    frequencyWeeks,
    suggestedDate
  };
}

// src/oneonone/utils/durationUtils.ts
var minutesToSeconds = (minutes) => {
  return minutes * 60;
};
var secondsToMinutes = (seconds) => {
  return Math.round(seconds / 60);
};
var secondsToHHMMSS = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};
var secondsToReadableTime = (seconds, translations) => {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const remainingSeconds = totalSeconds % 60;
  const parts = [];
  const t = translations || { hours: "\uC2DC\uAC04", minutes: "\uBD84", seconds: "\uCD08" };
  if (hours > 0) {
    parts.push(`${hours}${t.hours}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}${t.minutes}`);
  }
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds}${t.seconds}`);
  }
  return parts.join(" ");
};
var secondsToMMSS = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};
var calculateDurationSeconds = (startDate, endDate = /* @__PURE__ */ new Date()) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error("Invalid date provided for duration calculation");
  }
  const durationMs = Math.max(0, end.getTime() - start.getTime());
  return Math.floor(durationMs / 1e3);
};

// src/oneonone/services/oneOnOneMeetingService.ts
var createOneOnOneMeetingService = () => ({
  /**
   * 미팅 생성 (백엔드 API 호출)
   * 이메일 + 푸시 알림을 포함한 미팅 생성
   */
  async createMeeting(params) {
    const response = await apiClient.post("oneonone/meeting/create", { json: params }).json();
    if (!response.success) {
      throw new Error("\uBBF8\uD305 \uC0DD\uC131\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return response.data;
  },
  /**
   * 미팅 일정 변경 (백엔드 API 호출)
   * 이메일 + 푸시 알림을 포함한 일정 변경
   */
  async rescheduleMeeting(params) {
    const response = await apiClient.post("oneonone/meeting/reschedule", { json: params }).json();
    if (!response.success) {
      throw new Error("\uBBF8\uD305 \uC77C\uC815 \uBCC0\uACBD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return response.data;
  },
  /**
   * 미팅 상세 조회
   * @param meetingId 미팅 ID
   * @returns 미팅 상세 정보
   */
  async getMeetingById(meetingId) {
    const { data: data2, error } = await supabase.from("one_on_one_meeting").select(
      `
                *,
                member:member_id(id, name, profile_image_url),
                leader:leader_id(id, name, profile_image_url),
                questions:one_on_one_meeting_question(
                    *,
                    user:user!register_id(id, name, profile_image_url),
                    answer:one_on_one_meeting_answer(
                        *,
                        user:user!register_id(id, name, profile_image_url)
                    )
                ),
                member_feedback:one_on_one_member_feedback(*)
                `
    ).eq("id", meetingId).order("order_index", {
      referencedTable: "one_on_one_member_feedback",
      ascending: true
    }).maybeSingle();
    if (error) throw error;
    if (!data2) return null;
    const enabledUserIds = await getEnabledWorkspaceMemberIds(
      data2.workspace_id
    );
    const currentUserId = (await supabase.auth.getUser()).data.user?.id || null;
    const visibleQuestions = data2.questions?.filter((question) => {
      if (question.register_id === currentUserId) return true;
      return question.is_published !== false;
    }) || [];
    const questionsWithUser = visibleQuestions.map((question) => {
      const filteredUser = enabledUserIds.includes(
        question.user?.id ?? ""
      ) ? question.user : null;
      const rawAnswer = Array.isArray(question.answer) ? question.answer[0] || null : question.answer;
      const filteredAnswer = rawAnswer ? {
        ...rawAnswer,
        user: enabledUserIds.includes(
          rawAnswer.user?.id ?? ""
        ) ? rawAnswer.user : null
      } : null;
      return {
        ...question,
        user: filteredUser,
        answer: filteredAnswer
      };
    }).sort((a, b) => a.order_index - b.order_index) || [];
    const processedData = {
      ...data2,
      member: !!data2.member_id && enabledUserIds.includes(data2.member_id) ? data2.member : null,
      leader: !!data2.leader_id && enabledUserIds.includes(data2.leader_id) ? data2.leader : null,
      questions: questionsWithUser,
      member_feedback: data2.member_feedback ?? []
    };
    return processedData;
  },
  /**
   * 내 미팅 목록 조회
   * @param workspaceId 워크스페이스 ID
   * @returns 미팅 목록
   */
  async getMyMeetings(workspaceId) {
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    if (!userId) throw new Error("User not found");
    const { data: data2, error } = await supabase.from("one_on_one_meeting").select(
      `
                *,
                member:member_id(id, name, email, profile_image_url),
                leader:leader_id(id, name, email, profile_image_url),
                workspace:workspace_id(id, name, profile_image_url)
            `
    ).or(`member_id.eq.${userId},leader_id.eq.${userId}`).eq("workspace_id", workspaceId).order("scheduled_date", { ascending: true });
    if (error) throw error;
    const dataWithWorkspaceId = data2.map((item) => ({
      ...item,
      workspace_id: workspaceId
    }));
    const processedData = await applyWorkspaceMembershipFilterBatch(
      dataWithWorkspaceId,
      ["member", "leader"]
    );
    const result = processedData.sort((a, b) => {
      if (a.actual_date && b.actual_date) {
        return new Date(b.actual_date).getTime() - new Date(a.actual_date).getTime();
      } else if (a.actual_date && !b.actual_date) {
        return -1;
      } else if (!a.actual_date && b.actual_date) {
        return 1;
      }
      return 0;
    });
    return result;
  },
  /**
   * 현재 사용자와 특정 멤버의 1on1 미팅 목록 조회
   * @param workspaceId 워크스페이스 ID
   * @param memberId 멤버 ID
   * @returns 미팅 목록
   */
  async getMyMeetingsByCounterpartId(workspaceId, memberId) {
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    if (!userId) throw new Error("User not found");
    const { data: data2, error } = await supabase.from("one_on_one_meeting").select(
      `
                *,
                member:member_id(id, name, email, profile_image_url),
                leader:leader_id(id, name, email, profile_image_url),
                workspace:workspace_id(id, name, profile_image_url),
                survey:one_on_one_meeting_survey(
                    *
                )
            `
    ).or(
      `and(leader_id.eq.${memberId},member_id.eq.${userId}),and(leader_id.eq.${userId},member_id.eq.${memberId})`
    ).eq("workspace_id", workspaceId).order("scheduled_date", { ascending: false });
    if (error) throw error;
    const dataWithWorkspaceId = data2.map((item) => ({
      ...item,
      workspace_id: workspaceId
    }));
    const processedData = await applyWorkspaceMembershipFilterBatch(
      dataWithWorkspaceId,
      ["member", "leader"]
    );
    return processedData;
  },
  /**
   * 현재 사용자의 진행중인 미팅 조회
   * @param memberId 멤버 ID
   * @param workspaceId 워크스페이스 ID
   * @returns 진행중인 미팅 (없으면 null)
   */
  async getMyInProgressMeeting(memberId) {
    const { data: data2, error } = await supabase.from("one_on_one_meeting").select(
      `
                *,
                member:member_id(id, name, email, profile_image_url),
                leader:leader_id(id, name, email, profile_image_url),
                workspace:workspace_id(id, name, profile_image_url)
            `
    ).or(`member_id.eq.${memberId},leader_id.eq.${memberId}`).eq("status_code", "IN_PROGRESS" /* IN_PROGRESS */).order("actual_date", { ascending: false }).limit(1).maybeSingle();
    if (error) throw error;
    return data2;
  },
  /**
   * 미팅 업데이트
   * @param params 미팅 업데이트 파라미터 (scheduled_duration은 분 단위, actual_duration은 초 단위)
   * @returns 업데이트된 미팅
   */
  async updateMeeting(params) {
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const scheduledDuration = params.scheduled_duration != null ? minutesToSeconds(params.scheduled_duration) : void 0;
    const actualDuration = params.actual_duration == null ? void 0 : params.actual_duration;
    const payload = {
      ...params,
      scheduled_duration: scheduledDuration,
      // undefined일 경우 기존 값 덮어쓰지 않고 필드 업데이트가 제외됨
      actual_duration: actualDuration,
      update_id: userId,
      update_date: now
    };
    if (!params.id) throw new Error("Meeting ID is required");
    const { data: data2, error } = await supabase.from("one_on_one_meeting").update(payload).eq("id", params.id).select().single();
    if (error) throw error;
    return data2;
  },
  /**
   * 미팅 삭제 (백엔드 API 호출)
   * 이메일 + 푸시 알림 포함
   * @param id 미팅 ID
   * @returns 삭제된 미팅 ID
   */
  async deleteMeeting(id) {
    const response = await apiClient.delete(`oneonone/meeting/${id}`).json();
    if (!response.success) {
      throw new Error("\uBBF8\uD305 \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return id;
  },
  /**
   * 미팅 질문 추가 (백엔드 API 호출)
   * 다양한 소스(템플릿, 수동, AI 생성, 구두)에서 질문을 추가
   * @returns 추가된 질문들의 정보 (ID 포함)
   */
  async addMeetingQuestions(params) {
    const response = await apiClient.post("oneonone/meeting/questions", { json: params }).json();
    if (!response.success) {
      throw new Error("\uC9C8\uBB38 \uCD94\uAC00\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return response.data;
  },
  /**
   * 질문 공개 (초안 → 공개)
   */
  async publishQuestions(meetingId, options) {
    const response = await apiClient.post("oneonone/meeting/questions/publish", {
      json: {
        meetingId,
        skipNotification: options?.skipNotification,
        publishAll: options?.publishAll
      }
    }).json();
    if (!response.success) {
      throw new Error("\uC9C8\uBB38 \uACF5\uAC1C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return response.data;
  },
  /**
   * 질문 확인 완료
   */
  async confirmQuestions(meetingId) {
    const response = await apiClient.post("oneonone/meeting/questions/confirm", {
      json: { meetingId }
    }).json();
    if (!response.success) {
      throw new Error("\uC9C8\uBB38 \uD655\uC778 \uC644\uB8CC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
  },
  /**
   * 추천 질문 제거 (백엔드 API 호출)
   * AI가 생성한 추천 질문을 미팅 질문으로 추가하지 않고 제거만 할 때 사용
   */
  async removeRecommendedQuestions(params) {
    const response = await apiClient.post("oneonone/recommended-questions/remove", { json: params }).json();
    if (!response.success) {
      throw new Error("\uCD94\uCC9C \uC9C8\uBB38 \uC81C\uAC70\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
  },
  /**
   * 미팅 개별 질문 업데이트
   */
  async updateMeetingQuestion(params) {
    const { questionId, question, order_index } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    const updateData = { update_id: userId, update_date: now };
    if (question !== void 0) updateData.question = question;
    if (order_index !== void 0) updateData.order_index = order_index;
    const { error } = await supabase.from("one_on_one_meeting_question").update(updateData).eq("id", questionId);
    if (error) throw error;
  },
  /**
   * 질문 순서 일괄 업데이트
   */
  async updateQuestionsOrder(params) {
    const { questionOrders } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    for (const { questionId, order_index } of questionOrders) {
      const { error } = await supabase.from("one_on_one_meeting_question").update({
        order_index,
        update_id: userId,
        update_date: now
      }).eq("id", questionId);
      if (error) throw error;
    }
  },
  /**
   * 미팅 질문 하드 삭제
   */
  async deleteMeetingQuestion(questionId, meetingId) {
    const { error } = await supabase.from("one_on_one_meeting_question").delete().eq("id", questionId).eq("meeting_id", meetingId);
    if (error) throw error;
  },
  /**
   * 미팅 답변 추가/수정
   */
  async upsertMeetingAnswer(params) {
    const { meetingId, questionId, answer } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    const { data: existing } = await supabase.from("one_on_one_meeting_answer").select("id").eq("meeting_question_id", questionId).maybeSingle();
    if (existing) {
      const { error: error2 } = await supabase.from("one_on_one_meeting_answer").update({ answer, update_id: userId, update_date: now }).eq("id", existing.id);
      if (error2) throw error2;
      return;
    }
    const { error } = await supabase.from("one_on_one_meeting_answer").insert({
      meeting_id: meetingId,
      meeting_question_id: questionId,
      answer,
      register_id: userId,
      register_date: now
    });
    if (error) throw error;
  },
  /**
   * AI 생성 질문 수정
   */
  async updateAIQuestion(params) {
    const { questionId, question } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    const { error } = await supabase.from("one_on_one_meeting_question").update({
      question,
      is_ai_edited: true,
      update_id: userId,
      update_date: now
    }).eq("id", questionId);
    if (error) throw error;
  },
  /**
   * AI 생성 답변(ai_summary) 수정
   */
  async updateAIAnswerSummary(params) {
    const { answerId, ai_summary } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id || null;
    const { error } = await supabase.from("one_on_one_meeting_answer").update({
      ai_summary,
      is_ai_edited: true,
      update_id: userId,
      update_date: now
    }).eq("id", answerId);
    if (error) throw error;
  },
  /**
   * 나만의 노트 조회
   */
  async getMyNote(meetingId, userId) {
    const { data: data2, error } = await supabase.from("one_on_one_meeting_note").select("*").eq("meeting_id", meetingId).eq("user_id", userId).maybeSingle();
    if (error) throw error;
    return data2;
  },
  /**
   * 나만의 노트 생성/업데이트 (upsert)
   * unique 제약조건 (meeting_id, user_id)를 사용하여
   * 존재하면 업데이트, 없으면 생성
   */
  async upsertMyNote(meetingId, userId, note) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { error } = await supabase.from("one_on_one_meeting_note").upsert(
      {
        meeting_id: meetingId,
        user_id: userId,
        content: note,
        is_private: true,
        register_id: userId,
        register_date: now
      },
      {
        onConflict: "meeting_id,user_id"
      }
    );
    if (error) throw error;
  }
});
var oneOnOneMeetingService = createOneOnOneMeetingService();

// src/oneonone/services/oneOnOneMemberService.ts
var createOneOnOneMemberService = () => ({
  async getStatusesByWorkspaceId(workspaceId) {
    const { data: data2, error } = await supabase.from("one_on_one_member").select(
      `
                workspace_member_id,
                enable,
                workspace_member!inner(workspace_id)
            `
    ).eq("workspace_member.workspace_id", workspaceId).eq("workspace_member.enable", true);
    if (error) throw error;
    const statusMap = {};
    for (const row of data2 ?? []) {
      statusMap[row.workspace_member_id] = Boolean(row.enable);
    }
    return statusMap;
  },
  async getActiveMemberCountByWorkspaceId(workspaceId) {
    const { count, error } = await supabase.from("one_on_one_member").select("workspace_member!inner(workspace_id)", {
      count: "exact",
      head: true
    }).eq("workspace_member.workspace_id", workspaceId).eq("workspace_member.enable", true).eq("enable", true);
    if (error) throw error;
    return count ?? 0;
  },
  async setEnableByWorkspaceMemberId(workspaceMemberId, enable) {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id ?? null;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    if (enable) {
      const { data: existing } = await supabase.from("one_on_one_member").select("workspace_member_id, register_date").eq("workspace_member_id", workspaceMemberId).maybeSingle();
      if (existing) {
        const { error: error2 } = await supabase.from("one_on_one_member").update({
          enable: true,
          update_id: userId,
          update_date: now
        }).eq("workspace_member_id", workspaceMemberId);
        if (error2) throw error2;
      } else {
        const payload = {
          workspace_member_id: workspaceMemberId,
          enable: true,
          register_id: userId,
          register_date: now,
          update_id: userId,
          update_date: now
        };
        const { error: error2 } = await supabase.from("one_on_one_member").insert(payload);
        if (error2) throw error2;
      }
      return;
    }
    const { error } = await supabase.from("one_on_one_member").update({ enable: false, update_id: userId, update_date: now }).eq("workspace_member_id", workspaceMemberId);
    if (error) throw error;
  },
  /**
   * 주기적 원온원 멤버 생성
   */
  async createOneOnOnePair(workspaceId, counterpartId, meetingFrequency) {
    const { data: auth } = await supabase.auth.getUser();
    const registerId = auth.user?.id ?? null;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    if (!registerId) throw new Error("userId is required");
    const { error } = await supabase.from("one_on_one_pair").insert({
      counterpart_id: counterpartId,
      meeting_frequency: meetingFrequency,
      register_date: now,
      register_id: registerId,
      workspace_id: workspaceId
    });
    if (error) throw error;
  },
  /**
   * 주기적 원온원 멤버 업데이트
   */
  async updateOneOnOnePair(workspaceId, counterpartId, meetingFrequency) {
    const { data: auth } = await supabase.auth.getUser();
    const currentUserId = auth.user?.id ?? null;
    if (!currentUserId) throw new Error("userId is required");
    const { error } = await supabase.from("one_on_one_pair").update({
      meeting_frequency: meetingFrequency,
      update_id: currentUserId,
      update_date: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("workspace_id", workspaceId).or(
      `and(register_id.eq.${currentUserId},counterpart_id.eq.${counterpartId}),and(register_id.eq.${counterpartId},counterpart_id.eq.${currentUserId})`
    );
    if (error) throw error;
  },
  /**
   * 주기적 원온원 멤버 삭제
   */
  async deleteOneOnOnePair(workspaceId, counterpartId) {
    const { data: auth } = await supabase.auth.getUser();
    const currentUserId = auth.user?.id ?? null;
    if (!currentUserId) throw new Error("userId is required");
    const { error } = await supabase.from("one_on_one_pair").delete().eq("workspace_id", workspaceId).or(
      `and(register_id.eq.${currentUserId},counterpart_id.eq.${counterpartId}),and(register_id.eq.${counterpartId},counterpart_id.eq.${currentUserId})`
    );
    if (error) throw error;
  },
  /**
   * 주기적 원온원 멤버 조회
   *
   * NestJS 백엔드 API를 통해 최적화된 배치 쿼리로 조회합니다.
   * 기존 N+1 쿼리 문제를 해결하여 성능이 크게 개선되었습니다.
   */
  async getMyOneOnOnePair(workspaceId, _userId) {
    try {
      const response = await apiClient.get("oneonone/pairs", {
        searchParams: { workspaceId }
      }).json();
      return response.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message);
      }
      throw error;
    }
  }
});
var oneOnOneMemberService = createOneOnOneMemberService();

// src/oneonone/services/oneOnOneEmailService.ts
var createOneOnOneEmailService = () => ({
  // 워크스페이스 초대 일괄 발송
  async sendBulkWorkspaceInvite(params) {
    try {
      await apiClient.post("email/bulk-workspace-invite", {
        json: params
      });
    } catch (err) {
      if (err instanceof ApiClientError) {
        throw new Error(
          err.message || "\uCD08\uB300 \uC774\uBA54\uC77C \uC77C\uAD04 \uBC1C\uC1A1\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."
        );
      }
      throw err;
    }
  }
});
var oneOnOneEmailService = createOneOnOneEmailService();

// src/oneonone/services/meetingPrepService.ts
var meetingPrepService = {
  /**
   * 미팅 프렙 존재 여부 체크, 프렙이 있고 리더와 공유한 미팅 id만 반환
   */
  async getExistingPrepMeetingIds(meetingIds) {
    const response = await apiClient.post("oneonone/prep/statuses", {
      json: { meetingIds }
    }).json();
    const sharedPrepMeetingIds = response?.data?.filter((item) => item.hasPrepContent && item.isShared)?.map((item) => item.meetingId);
    return new Set(sharedPrepMeetingIds);
  },
  // 미팅 프렙 데이터 조회
  async getMeetingPrep(meetingId) {
    const response = await apiClient.get("oneonone/prep/detail", {
      searchParams: { meetingId }
    }).json();
    return response?.data ?? null;
  },
  /**
   * 미팅 프렙 서베이 저장/수정
   */
  async savePrepSurvey(surveyData) {
    const response = await apiClient.post("oneonone/prep/survey", {
      json: surveyData
    }).json();
    return response;
  },
  /**
   * 프렙 채팅 세션 시작
   * 백엔드가 프렙 레코드를 확보(upsert)하고 완성형 채팅 시작 요청 바디를
   * 돌려준다. 매 세션 시작마다 호출하고, chatRequest는 AI 서버에 그대로 전달한다.
   */
  async startPrepChatSession({
    meetingId
  }) {
    const response = await apiClient.post("oneonone/prep/session", {
      json: { meetingId }
    }).json();
    return response;
  },
  /**
   * 프렙 (요약) 생성 - AI
   */
  async generatePrepContent({
    prepId
  }) {
    const response = await apiClient.post("oneonone/prep/generate", {
      json: { prepId }
    }).json();
    return response;
  },
  /**
   * 프렙 요약 생성 - AI 결과를 prep_summary 컬럼에 저장
   */
  async generatePrepSummary({
    prepId
  }) {
    const response = await apiClient.post("oneonone/prep/summary", {
      json: { prepId }
    }).json();
    return response;
  },
  /**
   * 프렙 공유 (리더와)
   */
  async shareMeetingPrep({ prepId }) {
    await apiClient.post("oneonone/prep/share", {
      json: { prepId }
    }).json();
  },
  /**
   * 프렙 내용 업데이트
   */
  async updatePrepContent({
    prepId,
    prepContent
  }) {
    await apiClient.patch("oneonone/prep/update", {
      json: { prepId, prepContent }
    }).json();
  }
};

// src/oneonone/utils/sseParser.ts
function parseSSELine(line) {
  if (!line.startsWith("data: ")) return null;
  const data2 = line.slice(6).trim();
  if (!data2 || data2 === "[DONE]") return null;
  try {
    return JSON.parse(data2);
  } catch {
    console.warn("[SSEParser] JSON \uD30C\uC2F1 \uC2E4\uD328:", data2);
    return null;
  }
}
async function processSSEStream(reader, options) {
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      if (options.signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;
        const event = parseSSELine(trimmedLine);
        if (event) {
          options.onEvent(event);
        }
      }
    }
    if (buffer.trim()) {
      const event = parseSSELine(buffer);
      if (event) {
        options.onEvent(event);
      }
    }
    options.onComplete?.();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    options.onError?.(error);
  } finally {
    reader.releaseLock();
  }
}
async function fetchSSE(url, options, customFetch) {
  const fetchFn = customFetch ?? fetch;
  const response = await fetchFn(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...options.headers
    }
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SSE \uC694\uCCAD \uC2E4\uD328: ${response.status} ${errorText}`);
  }
  if (!response.body) {
    throw new Error("\uC751\uB2F5 \uBCF8\uBB38\uC774 \uC5C6\uC2B5\uB2C8\uB2E4");
  }
  return response;
}

// src/oneonone/services/prepChatService.ts
async function getAccessToken2() {
  const { data: data2 } = await supabase.auth.getSession();
  return data2.session?.access_token ?? null;
}
function createEventHandler(callbacks) {
  let accumulated = "";
  return (event) => {
    switch (event.type) {
      case "start":
        callbacks.onStart?.(
          event.meeting_id,
          event.is_new_conversation
        );
        break;
      case "chunk":
        accumulated += event.content;
        callbacks.onChunk?.(event.content, accumulated);
        break;
      case "messages":
        callbacks.onMessages?.(event.messages);
        break;
      case "end":
        callbacks.onEnd?.(event.prep_stage);
        break;
      case "error":
        callbacks.onError?.(event.message);
        break;
      default:
        event;
    }
  };
}
var chattingService = {
  /**
   * 첫 채팅 / 재진입 시 요청
   *
   * request는 세션 시작 엔드포인트가 조립해 준 바디를 그대로 전달한다.
   */
  async sendInitial(request, callbacks, signal, customFetch) {
    try {
      const token = await getAccessToken2();
      const response = await fetchSSE(
        AI_API_ENDPOINTS.PREP_CHAT,
        {
          method: "POST",
          headers: {
            ...token && { Authorization: `Bearer ${token}` }
          },
          body: JSON.stringify(request),
          signal
        },
        customFetch
      );
      const reader = response.body.getReader();
      await processSSEStream(reader, {
        onEvent: createEventHandler(callbacks),
        onError: (error) => {
          callbacks.onError?.(error.message, error);
        },
        onComplete: () => callbacks.onEnd?.(),
        signal
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      const err = error instanceof Error ? error : new Error("\uCC44\uD305 \uC5F0\uACB0\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4");
      callbacks.onError?.(err.message, err);
    }
  },
  /**
   * 후속 메시지 전송
   */
  async sendFollowUp(request, callbacks, signal, customFetch) {
    try {
      const token = await getAccessToken2();
      const response = await fetchSSE(
        AI_API_ENDPOINTS.PREP_CHAT,
        {
          method: "POST",
          headers: {
            ...token && { Authorization: `Bearer ${token}` }
          },
          body: JSON.stringify(request),
          signal
        },
        customFetch
      );
      const reader = response.body.getReader();
      await processSSEStream(reader, {
        onEvent: createEventHandler(callbacks),
        onError: (error) => {
          callbacks.onError?.(error.message, error);
        },
        onComplete: () => callbacks.onEnd?.(),
        signal
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      const err = error instanceof Error ? error : new Error("\uCC44\uD305 \uC5F0\uACB0\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4");
      callbacks.onError?.(err.message, err);
    }
  }
};

// src/oneonone/services/aiProfileService.ts
var aiProfileService = {
  /**
   * 리더와 멤버의 가장 최신 프로필 한개씩 조회
   */
  async getAiProfiles({
    leaderId,
    memberId,
    workspaceId
  }) {
    if (!leaderId || !memberId || !workspaceId) {
      return { leaderProfile: null, memberProfile: null };
    }
    const [leaderResult, memberResult] = await Promise.all([
      supabase.from("one_on_one_ai_profile").select("*").eq("is_leader", true).eq("user_id", leaderId).eq("partner_id", memberId).eq("workspace_id", workspaceId).order("register_date", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("one_on_one_ai_profile").select("*").eq("is_leader", false).eq("user_id", memberId).eq("partner_id", leaderId).eq("workspace_id", workspaceId).order("register_date", { ascending: false }).limit(1).maybeSingle()
    ]);
    if (leaderResult.error) {
      console.error("Leader profile fetch failed:", leaderResult.error);
    }
    if (memberResult.error) {
      console.error("Member profile fetch failed:", memberResult.error);
    }
    return {
      leaderProfile: leaderResult.data,
      memberProfile: memberResult.data
    };
  }
};

// src/oneonone/services/googleCalendarService.ts
var GOOGLE_CALENDAR_API_BASE = "https://www.googleapis.com/calendar/v3";
var AccountMismatchError = class extends Error {
  constructor(existingEmail, attemptedEmail) {
    super(
      `Google account mismatch: existing=${existingEmail}, attempted=${attemptedEmail}`
    );
    this.name = "AccountMismatchError";
    this.existingEmail = existingEmail;
    this.attemptedEmail = attemptedEmail;
  }
};
function createGoogleCalendarService(refreshAccessToken) {
  async function getIntegration(userId) {
    const { data: data2, error } = await supabase.from("user_oauth_token").select("*").eq("user_id", userId).eq("provider", GOOGLE_CALENDAR_PROVIDER).eq("enable", true).maybeSingle();
    if (error) {
      console.error("getIntegration \uC5D0\uB7EC:", error);
      throw error;
    }
    return data2;
  }
  async function getProviderEmail(userId) {
    const { data: data2, error } = await supabase.from("user_oauth_email").select("provider_email").eq("user_id", userId).eq("provider", GOOGLE_CALENDAR_PROVIDER).maybeSingle();
    if (error) {
      console.error("getProviderEmail \uC5D0\uB7EC:", error);
      return null;
    }
    if (!data2?.provider_email) {
      return null;
    }
    return data2.provider_email;
  }
  async function getUserEmail(accessToken) {
    try {
      const response = await fetch(
        "https://www.googleapis.com/oauth2/v2/userinfo",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      if (!response.ok) {
        console.warn("Failed to fetch user email");
        return null;
      }
      const data2 = await response.json();
      return data2.email || null;
    } catch (error) {
      console.warn("Error fetching user email:", error);
      return null;
    }
  }
  async function getUserTimeZone(accessToken) {
    try {
      const response = await fetch(
        `${GOOGLE_CALENDAR_API_BASE}/users/me/settings/timezone`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      if (!response.ok) {
        console.warn("Failed to fetch user timezone, using default");
        return "Asia/Seoul";
      }
      const data2 = await response.json();
      return data2.value || "Asia/Seoul";
    } catch (error) {
      console.warn("Error fetching timezone:", error);
      return "Asia/Seoul";
    }
  }
  async function saveIntegration(userId, tokenResponse) {
    const accessTokenExpiresAt = new Date(
      Date.now() + tokenResponse.expires_in * 1e3
    ).toISOString();
    const refreshTokenExpiresAt = tokenResponse.refresh_token ? new Date(Date.now() + 180 * 24 * 60 * 60 * 1e3).toISOString() : null;
    const providerEmail = await getUserEmail(tokenResponse.access_token);
    const existing = await getIntegration(userId);
    if (existing?.provider_email && providerEmail) {
      const normalize = (email) => email.trim().toLowerCase();
      if (normalize(existing.provider_email) !== normalize(providerEmail)) {
        throw new AccountMismatchError(
          existing.provider_email,
          providerEmail
        );
      }
    }
    const tokenData = {
      user_id: userId,
      provider: GOOGLE_CALENDAR_PROVIDER,
      provider_email: providerEmail,
      access_token: tokenResponse.access_token,
      refresh_token: tokenResponse.refresh_token || null,
      token_type: tokenResponse.token_type || "Bearer",
      scope: tokenResponse.scope || null,
      access_token_expires_at: accessTokenExpiresAt,
      refresh_token_expires_at: refreshTokenExpiresAt,
      enable: true,
      last_used_at: (/* @__PURE__ */ new Date()).toISOString()
    };
    const { data: data2, error } = await supabase.from("user_oauth_token").upsert(tokenData, { onConflict: "user_id,provider" }).select().single();
    if (error) throw error;
    return data2;
  }
  async function disconnectIntegration(userId) {
    const { error } = await supabase.from("user_oauth_token").update({ enable: false }).eq("user_id", userId).eq("provider", GOOGLE_CALENDAR_PROVIDER);
    if (error) throw error;
  }
  async function getValidAccessToken(userId) {
    let token = await getIntegration(userId);
    if (!token) {
      throw new Error("No calendar integration found");
    }
    if (!token.access_token_expires_at) {
      return token.access_token;
    }
    const accessTokenExpiresAt = new Date(token.access_token_expires_at);
    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1e3);
    if (accessTokenExpiresAt <= fiveMinutesFromNow) {
      token = await refreshAccessToken(userId);
    }
    await supabase.from("user_oauth_token").update({ last_used_at: (/* @__PURE__ */ new Date()).toISOString() }).eq("user_id", userId).eq("provider", GOOGLE_CALENDAR_PROVIDER);
    return token.access_token;
  }
  async function createEvent(userId, event) {
    const accessToken = await getValidAccessToken(userId);
    const url = event.conferenceData ? `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events?conferenceDataVersion=1` : `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(event)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create calendar event: ${errorText}`);
    }
    return await response.json();
  }
  async function listEvents(userId, options) {
    const accessToken = await getValidAccessToken(userId);
    const params = new URLSearchParams({
      timeMin: options?.timeMin || (/* @__PURE__ */ new Date()).toISOString(),
      ...options?.timeMax && { timeMax: options.timeMax },
      maxResults: String(options?.maxResults || 10),
      singleEvents: "true",
      orderBy: "startTime"
    });
    const response = await fetch(
      `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events?${params}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to list calendar events: ${errorText}`);
    }
    const data2 = await response.json();
    return data2.items || [];
  }
  async function updateEvent(userId, eventId, event) {
    const accessToken = await getValidAccessToken(userId);
    const url = event.conferenceData ? `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events/${eventId}?conferenceDataVersion=1` : `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events/${eventId}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(event)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update calendar event: ${errorText}`);
    }
    return await response.json();
  }
  async function deleteEvent(userId, eventId) {
    const accessToken = await getValidAccessToken(userId);
    const response = await fetch(
      `${GOOGLE_CALENDAR_API_BASE}/calendars/primary/events/${eventId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    if (!response.ok) {
      if (response.status === 410) {
        console.log("GoogleCalendar event already deleted:", eventId);
        return;
      }
      const errorText = await response.text();
      throw new Error(`Failed to delete calendar event: ${errorText}`);
    }
  }
  return {
    getIntegration,
    getProviderEmail,
    getUserEmail,
    getUserTimeZone,
    saveIntegration,
    disconnectIntegration,
    getValidAccessToken,
    createEvent,
    listEvents,
    updateEvent,
    deleteEvent
  };
}

// src/oneonone/services/oneOnOneSurveyService.ts
var oneOnOneSurveyService = {
  async createSurvey(params) {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const payload = {
      meeting_id: params.meetingId,
      engagement_level: params.engagementLevel,
      growth_satisfaction: params.growthSatisfaction,
      register_id: userId,
      register_date: now,
      update_id: userId,
      update_date: now
    };
    const { data: survey, error } = await supabase.from("one_on_one_meeting_survey").insert(payload).select().single();
    if (error) {
      console.error("Error creating survey:", error);
      throw error;
    }
    return survey;
  },
  async getSurveyByMeetingId(meetingId) {
    const { data: survey, error } = await supabase.from("one_on_one_meeting_survey").select("*").eq("meeting_id", meetingId).maybeSingle();
    if (error) {
      if (error.code === "PGRST116" || error.message?.includes("406")) {
        console.warn(
          "Survey not found or access denied for meeting:",
          meetingId
        );
        return null;
      }
      console.error("Error fetching survey:", error);
      return null;
    }
    return survey;
  },
  async getSurveyByMeetingIds(meetingIds) {
    const { data: surveys, error } = await supabase.from("one_on_one_meeting_survey").select(
      `
                *,
                meeting:one_on_one_meeting!inner(*)
                `
    ).in("meeting_id", meetingIds);
    if (error) {
      console.error("\uBBF8\uD305 id\uBCC4 \uC11C\uBCA0\uC774 \uC870\uD68C \uC911 \uC624\uB958 \uBC1C\uC0DD: ", error);
      throw error;
    }
    return surveys;
  },
  async getSurveyById(id) {
    const { data: survey, error } = await supabase.from("one_on_one_meeting_survey").select("*").eq("id", id).single();
    if (error) {
      if (error.code === "PGRST116") {
        return null;
      }
      console.error("Error fetching survey:", error);
      throw error;
    }
    return survey;
  },
  async updateSurvey(params) {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const updateData = {
      ...params.engagementLevel !== void 0 && {
        engagement_level: params.engagementLevel
      },
      ...params.growthSatisfaction !== void 0 && {
        growth_satisfaction: params.growthSatisfaction
      },
      update_id: userId,
      update_date: now
    };
    const { data: survey, error } = await supabase.from("one_on_one_meeting_survey").update(updateData).eq("id", params.surveyId).select().single();
    if (error) {
      console.error("Error updating survey:", error);
      throw error;
    }
    return survey;
  },
  async deleteSurvey(id) {
    const { error } = await supabase.from("one_on_one_meeting_survey").delete().eq("id", id);
    if (error) {
      console.error("Error deleting survey:", error);
      throw error;
    }
  },
  async getSurveysByMemberId(memberId, workspaceId) {
    const { data: surveys, error } = await supabase.from("one_on_one_meeting_survey").select(
      `
                *,
                meeting:one_on_one_meeting!inner(
                    id,
                    leader_id,
                    member_id,
                    scheduled_date,
                    actual_date
                )
            `
    ).eq("meeting.member_id", memberId).eq("meeting.workspace_id", workspaceId).order("register_date", { ascending: false }).limit(8);
    if (error) {
      console.error("Error fetching member surveys:", error);
      throw error;
    }
    return surveys;
  }
};

// src/oneonone/services/oneOnOneActionItemService.ts
var oneOnOneActionItemService = {
  /**
   * 특정 1:1 미팅의 액션 아이템 목록 조회
   */
  async getActionItemsByMeetingId(meetingId) {
    const { data: data2, error } = await supabase.from("one_on_one_action_item").select(
      `
                    *,
                    user:user_id(id, name, profile_image_url),
                    meeting:meeting_id(workspace_id)
                `
    ).eq("meeting_id", meetingId).order("register_date", { ascending: true }).order("id", { ascending: true });
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC870\uD68C \uC2E4\uD328: ${error.message}`);
    }
    if (data2.length === 0) return [];
    const workspaceId = data2[0].meeting.workspace_id;
    const processedData = await applyWorkspaceMembershipFilter(
      data2,
      workspaceId,
      ["user"]
    );
    return processedData;
  },
  /**
   * 지난 액션 아이템 조회: 이전 미팅의 미완료 항목 및 미팅 이후 완료된 항목
   */
  async getPreviousMeetingActionItems(workspaceId, currentMeetingId, currentMeetingDate, myUserId, otherUserId) {
    if (!myUserId && !otherUserId) return [];
    const userFilters = [];
    if (myUserId && otherUserId) {
      userFilters.push(
        `and(leader_id.eq.${myUserId},member_id.eq.${otherUserId})`,
        `and(leader_id.eq.${otherUserId},member_id.eq.${myUserId})`
      );
    } else {
      const userId = myUserId ?? otherUserId;
      userFilters.push(
        `leader_id.eq.${userId}`,
        `member_id.eq.${userId}`
      );
    }
    const { data: previousMeetings, error: previousMeetingError } = await supabase.from("one_on_one_meeting").select("id, actual_date, scheduled_date").or(userFilters.join(",")).eq("workspace_id", workspaceId).neq("id", currentMeetingId).or(
      `actual_date.lt.${currentMeetingDate},and(actual_date.is.null,scheduled_date.lt.${currentMeetingDate})`
    ).order("actual_date", { ascending: false, nullsFirst: false }).order("scheduled_date", { ascending: false });
    if (previousMeetingError) {
      throw new Error(
        `\uC774\uC804 \uBBF8\uD305 \uC870\uD68C \uC2E4\uD328: ${previousMeetingError.message}`
      );
    }
    if (!previousMeetings || previousMeetings.length === 0) {
      return [];
    }
    const previousMeetingIds = previousMeetings.map((meeting) => meeting.id);
    const latestPreviousMeeting = previousMeetings[0];
    const latestMeetingDate = latestPreviousMeeting.actual_date || latestPreviousMeeting.scheduled_date;
    const { data: actionItems, error: actionItemError } = await supabase.from("one_on_one_action_item").select(
      `
                    *,
                    user:user_id(id, name, profile_image_url)
                `
    ).in("meeting_id", previousMeetingIds).or(
      `status_code.neq.COMPLETED,and(status_code.eq.COMPLETED,completed_date.gt.${latestMeetingDate})`
    ).order("register_date", { ascending: false });
    if (actionItemError) {
      throw new Error(
        `\uC774\uC804 \uBBF8\uD305 \uC561\uC158 \uC544\uC774\uD15C \uC870\uD68C \uC2E4\uD328: ${actionItemError.message}`
      );
    }
    const dataWithWorkspaceId = actionItems.map((item) => ({
      ...item,
      workspace_id: workspaceId
    }));
    const processedData = await applyWorkspaceMembershipFilterBatch(
      dataWithWorkspaceId,
      ["user"]
    );
    return processedData;
  },
  /**
   * 사용자에게 할당된 모든 액션 아이템 조회
   */
  async getActionItemsByUserIds(workspaceId, userIds) {
    const { data: data2, error } = await supabase.from("one_on_one_action_item").select(
      `
                    *,
                    user:user_id(id, name, profile_image_url),
                    meeting:one_on_one_meeting!inner(workspace_id)
                `
    ).filter("meeting.workspace_id", "eq", workspaceId).in("user_id", userIds).order("register_date", { ascending: false });
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC870\uD68C \uC2E4\uD328: ${error.message}`);
    }
    const processedData = await applyWorkspaceMembershipFilter(
      data2,
      workspaceId,
      ["user"]
    );
    return processedData;
  },
  /**
   * meeting_ids에 해당하는 액션 아이템 조회
   * @param params
   * @param meetingIds
   * @returns
   */
  async getActionItemsByMeetingIds(meetingIds) {
    if (meetingIds.length === 0) {
      return [];
    }
    const { data: data2, error } = await supabase.from("one_on_one_action_item").select(
      `
                    *,
                    user:user_id(id, name, profile_image_url),
                    meeting:meeting_id(workspace_id)
                `
    ).in("meeting_id", meetingIds).order("register_date", { ascending: false });
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC870\uD68C \uC2E4\uD328: ${error.message}`);
    }
    const dataWithWorkspaceId = data2.map((item) => ({
      ...item,
      workspace_id: item.meeting.workspace_id
    }));
    const processedData = await applyWorkspaceMembershipFilterBatch(
      dataWithWorkspaceId,
      ["user"]
    );
    return processedData;
  },
  /**
   * 리더와 멤버의 미완료 액션아이템 조회
   * @param params
   * @returns
   */
  async getIncompleteActionItemsByUserIds(workspaceId, userIds) {
    const { data: data2, error } = await supabase.from("one_on_one_action_item").select(
      `
                    *,
                    user:user_id(id, name, profile_image_url),
                    meeting:one_on_one_meeting!inner(workspace_id)
                `
    ).filter("meeting.workspace_id", "eq", workspaceId).neq("status_code", "COMPLETED" /* COMPLETED */).is("completed_date", null).in("user_id", userIds).in("meeting.leader_id", userIds).in("meeting.member_id", userIds).order("register_date", { ascending: false });
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC870\uD68C \uC2E4\uD328: ${error.message}`);
    }
    const processedData = await applyWorkspaceMembershipFilter(
      data2,
      workspaceId,
      ["user"]
    );
    return processedData;
  },
  /**
   * 새로운 액션 아이템 생성
   */
  async createActionItem(params) {
    const { data: data2, error } = await supabase.from("one_on_one_action_item").insert({
      meeting_id: params.meetingId,
      title: params.title,
      user_id: params.userId || null,
      due_date: params.dueDate || null,
      status_code: "PLANNED",
      is_ai: params.isAi || false
    }).select().single();
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC0DD\uC131 \uC2E4\uD328: ${error.message}`);
    }
    return data2;
  },
  /**
   * 액션 아이템 업데이트
   */
  async updateActionItem(params) {
    const updateData = {};
    if (params.title !== void 0) updateData.title = params.title;
    if (params.userId !== void 0) updateData.user_id = params.userId;
    if (params.statusCode !== void 0)
      updateData.status_code = params.statusCode;
    if (params.dueDate !== void 0) updateData.due_date = params.dueDate;
    if (params.completedDate !== void 0)
      updateData.completed_date = params.completedDate;
    const { data: data2, error } = await supabase.from("one_on_one_action_item").update(updateData).eq("id", params.id).select().single();
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328: ${error.message}`);
    }
    return data2;
  },
  /**
   * 액션 아이템 삭제
   */
  async deleteActionItem(id) {
    const { error } = await supabase.from("one_on_one_action_item").delete().eq("id", id);
    if (error) {
      throw new Error(`\uC561\uC158 \uC544\uC774\uD15C \uC0AD\uC81C \uC2E4\uD328: ${error.message}`);
    }
  }
};

// src/oneonone/services/oneOnOneActionItemProgressService.ts
var oneOnOneActionItemProgressService = {
  /**
   * 특정 미팅의 회고 진척 목록 조회. 카드는 이 결과를 그대로 렌더한다.
   */
  async getProgressByMeeting(meetingId) {
    const res = await apiClient.get(`oneonone/meeting/${meetingId}/action-item-progress`).json();
    return res.data ?? [];
  },
  /**
   * 진척 확정. v1은 'completed'만 보낸다(완료 시 본체 동기화).
   */
  async confirmProgress(params) {
    return setProgress(params, params.progress);
  },
  /**
   * 영구 회고제외(dismissed). 후보로 다시 안 뜬다. 삭제가 아니라 마킹.
   */
  async dismissProgress(key) {
    return setProgress(key, "dismissed");
  },
  /**
   * 확정 되돌림 → confirmed_progress=null(미확인). 직전이 'completed'였다면
   * 본체 완료도 복원된다(백엔드 거울).
   */
  async undoProgress(key) {
    return setProgress(key, null);
  },
  /**
   * 여러 항목의 진척을 한 번에 저장(체크인 일괄 기록·되돌리기).
   * 배치 엔드포인트가 없어 항목별 호출을 병렬로 보낸다. 일부만 실패해도 성공분은
   * 저장된 채로 남으므로, 호출부는 실패 여부와 무관하게 재조회해 실제 상태를 맞춘다.
   */
  async setProgressBatch(meetingId, entries) {
    const results = await Promise.allSettled(
      entries.map(
        ({ actionItemId, progress }) => setProgress({ actionItemId, meetingId }, progress)
      )
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      throw new Error(
        `\uD68C\uACE0 \uC9C4\uCC99 \uC77C\uAD04 \uC800\uC7A5 \uC2E4\uD328 (${failed}/${entries.length})`
      );
    }
  },
  /**
   * 미팅(회고 기록) 없이 본체만 완료/그만 보기/되돌리기.
   * 모아보기의 "회고 전 새 약속"처럼 progress 행이 없는 항목용.
   * `PATCH /oneonone/action-item/:id/progress`
   */
  async setProgressWithoutMeeting(actionItemId, value) {
    const res = await apiClient.patch(`oneonone/action-item/${actionItemId}/progress`, {
      json: { value }
    }).json();
    if (!res.data) {
      throw new Error("\uC561\uC158 \uC544\uC774\uD15C \uC0C1\uD0DC \uC800\uC7A5 \uC2E4\uD328");
    }
    return res.data;
  }
};
async function setProgress({ actionItemId, meetingId }, value) {
  const res = await apiClient.post(
    `oneonone/meeting/${meetingId}/action-item/${actionItemId}/progress`,
    { json: { confirmedProgress: value } }
  ).json();
  if (!res.data) {
    throw new Error("\uD68C\uACE0 \uC9C4\uCC99 \uC800\uC7A5 \uC2E4\uD328");
  }
  return res.data;
}

// src/oneonone/services/actionItemOverviewService.ts
var actionItemOverviewService = {
  /** 페어별 액션 아이템 집계 목록 (좌측 페어 목록) */
  async getPairSummaries(workspaceId) {
    const res = await apiClient.get("oneonone/action-items/pair-summary", {
      searchParams: { workspaceId }
    }).json();
    return res.data ?? [];
  },
  /** 특정 페어의 액션 아이템 상세 (우측 패널: 그룹 + 타임라인) */
  async getPairDetail(workspaceId, counterpartId) {
    const res = await apiClient.get("oneonone/action-items/by-pair", {
      searchParams: { workspaceId, counterpartId }
    }).json();
    return res.data ?? { openItems: [], completedItems: [], dismissedItems: [] };
  }
};

// src/oneonone/utils/templateLocalization.ts
function localizeSystemTemplate(template, t) {
  if (!template.is_system || !template.template_key) {
    return template;
  }
  const templateKey = template.template_key;
  try {
    const localizedTitle = t(`template:oneonone.${templateKey}.title`, {
      defaultValue: template.title
      // fallback: DB 데이터
    });
    const title = localizedTitle === `oneonone.${templateKey}.title` ? template.title : localizedTitle;
    const localizedDescription = t(
      `template:oneonone.${templateKey}.description`,
      {
        defaultValue: template.description ?? ""
        // fallback: DB 데이터
      }
    );
    const description = localizedDescription === `oneonone.${templateKey}.description` ? template.description ?? "" : localizedDescription;
    const translatedQuestions = t(
      `template:oneonone.${templateKey}.questions`,
      {
        returnObjects: true,
        defaultValue: template.questions.map((q) => q.question)
      }
    );
    const questions = template.questions.map((q, index) => ({
      ...q,
      question: translatedQuestions[index] || q.question
      // fallback
    }));
    return {
      ...template,
      title,
      description,
      questions
    };
  } catch (error) {
    console.warn(
      `Failed to localize system template: ${templateKey}`,
      error
    );
    return template;
  }
}
function localizeTemplates(templates, t) {
  return templates.map((template) => {
    if (template.is_system && template.template_key) {
      return localizeSystemTemplate(template, t);
    }
    return template;
  });
}

// src/oneonone/services/templateService.ts
var templateService = {
  /**
   * 유저의 템플릿 목록 조회
   * @param userId 유저 ID
   * @param workspaceId 워크스페이스 ID
   * @param t i18next 번역 함수
   * @returns 템플릿 목록 (시스템 템플릿은 다국어 적용됨)
   * userId, workspaceId 기준으로 조회
   * is_system이 true인 것은 항상 포함
   */
  async getTemplates(userId, workspaceId, t) {
    const { data: data2, error } = await supabase.from("one_on_one_template").select(
      `
                    *,
                    questions:one_on_one_template_question(
                        *
                    )
                `
    ).or(
      `user_id.eq.${userId},workspace_id.eq.${workspaceId},is_system.eq.true`
    ).eq("enable", true).order("register_date", { ascending: false });
    if (error) throw error;
    const templateWithQuestions = data2?.map((template) => ({
      ...template,
      questions: template.questions?.sort(
        (a, b) => a.order_index - b.order_index
      )
    }));
    return localizeTemplates(templateWithQuestions, t);
  },
  /**
   * 새 템플릿 생성 (트랜잭션 사용)
   * @param templateParams 템플릿과 질문 정보
   * @returns 생성된 템플릿과 질문들
   */
  async createTemplate(templateParams) {
    const { template, questions } = templateParams;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    try {
      const { data: createdTemplate, error: templateError } = await supabase.from("one_on_one_template").insert({
        ...template,
        enable: true,
        is_system: false,
        register_id: userId,
        register_date: now
      }).select().single();
      if (templateError) throw templateError;
      if (questions.length > 0) {
        const questionsToInsert = questions.map((question, index) => ({
          ...question,
          template_id: createdTemplate.id,
          order_index: index,
          register_id: userId,
          register_date: now
        }));
        const { data: createdQuestions, error: questionsError } = await supabase.from("one_on_one_template_question").insert(questionsToInsert).select();
        if (questionsError) {
          await supabase.from("one_on_one_template").delete().eq("id", createdTemplate.id);
          throw questionsError;
        }
        return {
          ...createdTemplate,
          questions: createdQuestions.sort(
            (a, b) => a.order_index - b.order_index
          )
        };
      }
      return {
        ...createdTemplate,
        questions: []
      };
    } catch (error) {
      console.error("\uD15C\uD50C\uB9BF \uC0DD\uC131 \uC2E4\uD328:", error);
      throw error;
    }
  },
  /**
   * 템플릿 수정
   * @param template 수정할 템플릿 정보
   * @returns 수정된 템플릿
   */
  async updateTemplate(params) {
    const { templateId, templateFields, questions } = params;
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    try {
      const { data: updatedTemplate, error: templateError } = await supabase.from("one_on_one_template").update({
        ...templateFields,
        update_date: now,
        update_id: userId
      }).eq("id", templateId).select().single();
      if (templateError) throw templateError;
      const { error: deleteError } = await supabase.from("one_on_one_template_question").delete().eq("template_id", templateId);
      if (deleteError) throw deleteError;
      if ((questions ?? []).length > 0) {
        const questionsToInsert = questions.map((question, index) => ({
          template_id: templateId,
          question: question.question,
          order_index: index,
          register_id: userId,
          register_date: now
        }));
        const { data: newQuestions, error: questionsError } = await supabase.from("one_on_one_template_question").insert(questionsToInsert).select();
        if (questionsError) throw questionsError;
        return {
          ...updatedTemplate,
          questions: newQuestions.sort(
            (a, b) => a.order_index - b.order_index
          )
        };
      }
      return {
        ...updatedTemplate,
        questions: []
      };
    } catch (error) {
      console.error("\uD15C\uD50C\uB9BF \uC218\uC815 \uC2E4\uD328:", error);
      throw error;
    }
  }
};

// src/oneonone/services/tabFeedbackService.ts
var tabFeedbackService = {
  /** 미팅 × 탭에 대한 기존 피드백 조회 */
  async getFeedback(meetingId, userId, tab) {
    const { data: data2 } = await supabase.from("one_on_one_meeting_tab_feedback").select("tab, feedback").eq("meeting_id", meetingId).eq("user_id", userId).eq("tab", tab).maybeSingle();
    return data2;
  },
  /** 피드백 저장 (upsert). 성공 시 true, 실패 시 false 반환. */
  async saveFeedback(meetingId, userId, tab, feedback) {
    const { error } = await supabase.from("one_on_one_meeting_tab_feedback").upsert(
      {
        meeting_id: meetingId,
        user_id: userId,
        tab,
        feedback,
        updated_at: (/* @__PURE__ */ new Date()).toISOString()
      },
      { onConflict: "meeting_id,user_id,tab" }
    );
    if (error) {
      console.error("\uD53C\uB4DC\uBC31 \uC800\uC7A5 \uC2E4\uD328:", error);
      return false;
    }
    return true;
  },
  /** 피드백 삭제 (토글 해제 시). 성공 시 true, 실패 시 false 반환. */
  async removeFeedback(meetingId, userId, tab) {
    const { error } = await supabase.from("one_on_one_meeting_tab_feedback").delete().eq("meeting_id", meetingId).eq("user_id", userId).eq("tab", tab);
    if (error) {
      console.error("\uD53C\uB4DC\uBC31 \uC0AD\uC81C \uC2E4\uD328:", error);
      return false;
    }
    return true;
  }
};

// src/oneonone/services/oneOnOneAIService.ts
import dayjs2 from "dayjs";
var hasServerResponse = (error) => error instanceof ApiClientError || "response" in error;
var oneOnOneAIService = {
  /**
   * 전체 AI 분석 프로세스를 실행합니다.
   * AI 서버에 분석 요청을 보내고, 서버가 백그라운드에서 분석 및 DB 업데이트를 처리합니다.
   * 클라이언트는 폴링을 통해 analysis_status_code가 COMPLETED가 되는 것을 감지합니다.
   *
   * @param meetingId 미팅 ID
   * @param hasRecording 녹음 데이터 존재 여부 (false면 제목 생성만)
   * @param language 사용자 언어 설정
   * @returns 성공 여부와 에러 정보
   */
  async processOneOnOneMeeting({
    meetingId,
    hasRecording = true,
    language
  }) {
    try {
      const meeting = await oneOnOneAIService.getMeetingInfo(meetingId);
      if (!meeting.leader || !meeting.member && !meeting.is_self_meeting) {
        console.warn("[AI] \uCC38\uC5EC\uC790 \uC815\uBCF4 \uB204\uB77D, \uBD84\uC11D \uC2A4\uD0B5:", meetingId);
        try {
          await oneOnOneMeetingService.updateMeeting({
            id: meetingId,
            analysis_status_code: "AT_RISK" /* AT_RISK */
          });
        } catch {
        }
        return {
          success: false,
          error: new Error("Missing participant info")
        };
      }
      const questions = await oneOnOneAIService.getMeetingQAData(meetingId);
      const { qaData } = oneOnOneAIService.prepareForAI(questions);
      await oneOnOneAIService.callAIAnalysis({
        meetingId,
        meeting,
        qaData,
        hasRecording,
        language
      });
      console.log(
        "\u2705 AI analysis request sent successfully. Server will process in background."
      );
      return { success: true };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error("Unknown error occurred");
      console.error(
        "[processOneOnOneMeeting] Failed to request AI analysis:",
        errorObj
      );
      try {
        await oneOnOneMeetingService.updateMeeting({
          id: meetingId,
          analysis_status_code: "AT_RISK" /* AT_RISK */,
          analysis_error_message: errorObj.message
        });
      } catch {
      }
      return { success: false, error: errorObj };
    }
  },
  /**
   * 미팅의 Q&A 데이터를 조회합니다
   */
  async getMeetingQAData(meetingId) {
    const { data: questions, error } = await supabase.from("one_on_one_meeting_question").select(
      `
                id,
                question,
                order_index,
                one_on_one_meeting_answer(
                    id,
                    answer
                )
            `
    ).eq("meeting_id", meetingId).order("order_index");
    if (error) throw error;
    if (!questions) return [];
    return questions;
  },
  /**
   * AI 분석을 위한 데이터를 준비합니다
   */
  prepareForAI(questions) {
    const questionMapping = {};
    const qaData = questions.map((q) => {
      questionMapping[q.order_index] = {
        question_id: q.id,
        answer_id: q.one_on_one_meeting_answer?.id ?? void 0
      };
      return {
        index: q.order_index,
        question: q.question,
        answer: q.one_on_one_meeting_answer?.answer ?? ""
      };
    });
    return { qaData, questionMapping };
  },
  /**
   * 미팅 정보를 조회합니다
   */
  async getMeetingInfo(meetingId) {
    const { data: meeting, error } = await supabase.from("one_on_one_meeting").select(
      `
                *,
                member:member_id(id, name, profile_image_url),
                leader:leader_id(id, name, profile_image_url),
                workspace:workspace_id(id, name, profile_image_url)
            `
    ).eq("id", meetingId).single();
    if (error) throw error;
    if (!meeting) throw new Error("Meeting not found");
    return meeting;
  },
  /**
   * 미팅 분석을 요청합니다 (백엔드 → AI 서버 fire-and-forget)
   * @throws {Error} API 호출 실패 시
   */
  async callAIAnalysis(params) {
    const { meetingId, meeting, qaData, hasRecording, language } = params;
    if (!meeting.leader?.id || !meeting.member?.id && !meeting.is_self_meeting) {
      throw new Error("Missing participant info for AI analysis");
    }
    const onlyTitle = !hasRecording;
    const backendRequest = {
      meetingId,
      recordingUrl: onlyTitle ? null : meeting.recording_url || null,
      qaPairs: qaData.length > 0 ? JSON.stringify(qaData) : null,
      meetingInfo: {
        leader: {
          id: meeting.leader.id,
          name: meeting.leader.name
        },
        member: meeting.member?.id ? {
          id: meeting.member.id,
          name: meeting.member.name
        } : null,
        actualDate: meeting.actual_date ? dayjs2(meeting.actual_date).format("YYYY-MM-DD HH:mm") : null,
        actualDuration: meeting.actual_duration || null
      },
      onlyTitle,
      language
    };
    try {
      await apiClient.post("oneonone/meeting/analyze", {
        json: backendRequest
      });
      console.log(
        "\u2705 AI analysis request accepted (background processing)"
      );
    } catch (error) {
      if (error instanceof Error && !hasServerResponse(error)) {
        throw new Error(
          `${ANALYSIS_REQUEST_LOST_PREFIX} ${error.message}`
        );
      }
      if (error instanceof Error) {
        throw new Error(`AI API call failed: ${error.message}`);
      }
      throw new Error("AI API call failed with unknown error");
    }
  }
};

// src/oneonone/utils/ratingUtils.ts
var RATING_COLORS = {
  1: "#FA5252",
  // Red - Very Bad
  2: "#FD7E14",
  // Orange - Bad
  3: "#FCC419",
  // Yellow - Fair
  4: "#20C997",
  // Teal - Good
  5: "#339AF0",
  // Blue - Very Good
  default: "#CED4DA"
  // Gray - No rating
};
var RATING_VALUES = [1, 2, 3, 4, 5];
var getRatingColor = (value) => {
  if (!value || value < 1 || value > 5) {
    return RATING_COLORS.default;
  }
  return RATING_COLORS[value];
};
var createRatingOptions = (getRatingLabelFn) => {
  return RATING_VALUES.map((value) => ({
    value,
    label: getRatingLabelFn(value),
    color: getRatingColor(value)
  }));
};
var getRatingLabel = (value, t) => {
  switch (value) {
    case 1:
      return t("oneonone:meeting_detail.survey.rating.very_bad");
    case 2:
      return t("oneonone:meeting_detail.survey.rating.bad");
    case 3:
      return t("oneonone:meeting_detail.survey.rating.fair");
    case 4:
      return t("oneonone:meeting_detail.survey.rating.good");
    case 5:
      return t("oneonone:meeting_detail.survey.rating.very_good");
    default:
      return "";
  }
};

// src/oneonone/utils/globalMeetingBroadcast.ts
var channelCache = /* @__PURE__ */ new Map();
var SUBSCRIBE_TIMEOUT_MS = 5e3;
var MAX_ATTEMPTS = 2;
var CACHE_SIZE_WARNING_THRESHOLD = 20;
async function createCachedChannel(memberId) {
  if (channelCache.size >= CACHE_SIZE_WARNING_THRESHOLD) {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    void logCacheLimitExceeded(userId, {
      currentSize: channelCache.size,
      threshold: CACHE_SIZE_WARNING_THRESHOLD,
      cachedMemberIds: [...channelCache.keys()]
    });
  }
  const channelName = getGlobalMeetingChannelName(memberId);
  const channel = supabase.channel(channelName, {
    config: { broadcast: { ack: true } }
  });
  let settled = false;
  const subscribed = new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      channelCache.delete(memberId);
      void supabase.removeChannel(channel);
      reject(
        new Error(`\uCC44\uB110 \uAD6C\uB3C5 \uD0C0\uC784\uC544\uC6C3 (${SUBSCRIBE_TIMEOUT_MS}ms \uCD08\uACFC)`)
      );
    }, SUBSCRIBE_TIMEOUT_MS);
    channel.subscribe((status, error) => {
      if (settled) return;
      if (status === "SUBSCRIBED") {
        settled = true;
        clearTimeout(timeoutId);
        resolve();
      } else if (status === "TIMED_OUT" || status === "CHANNEL_ERROR" || status === "CLOSED") {
        settled = true;
        clearTimeout(timeoutId);
        channelCache.delete(memberId);
        void supabase.removeChannel(channel);
        reject(new Error(`\uCC44\uB110 \uAD6C\uB3C5 \uC2E4\uD328: ${status}`));
      } else if (error) {
        settled = true;
        clearTimeout(timeoutId);
        channelCache.delete(memberId);
        void supabase.removeChannel(channel);
        reject(error);
      }
    });
  });
  return { channel, subscribed };
}
async function broadcastToMember(memberId, eventType, payload) {
  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let entry = channelCache.get(memberId);
    if (!entry) {
      entry = await createCachedChannel(memberId);
      channelCache.set(memberId, entry);
    }
    try {
      await entry.subscribed;
      await entry.channel.send({
        type: "broadcast",
        event: eventType,
        payload
      });
      return;
    } catch (error) {
      lastError = error;
      if (channelCache.get(memberId) === entry) {
        channelCache.delete(memberId);
        void supabase.removeChannel(entry.channel);
      }
      console.warn(
        `[broadcast] ${eventType} attempt ${attempt}/${MAX_ATTEMPTS} failed:`,
        error
      );
    }
  }
  const userId = (await supabase.auth.getUser()).data.user?.id;
  void logSubscriptionError(userId, {
    maxAttempts: MAX_ATTEMPTS,
    memberId,
    meetingEventType: eventType,
    subscriptionErrorMessage: lastError instanceof Error ? lastError.message : String(lastError)
  });
  throw lastError;
}
async function broadcastMeetingStatusChanged(params) {
  const { meetingId, statusCode, leaderId, memberId } = params;
  const userId = (await supabase.auth.getUser()).data.user?.id || "anonymous";
  const payload = {
    meetingId,
    statusCode,
    leaderId,
    memberId,
    userId,
    timestamp: Date.now()
  };
  await broadcastToMember(memberId, "meeting_status_changed", payload);
}
async function broadcastRecordingStatusChanged(params) {
  const { meetingId, recordingStatusCode, leaderId, memberId } = params;
  const userId = (await supabase.auth.getUser()).data.user?.id || "anonymous";
  const payload = {
    meetingId,
    recordingStatusCode,
    leaderId,
    memberId,
    userId,
    timestamp: Date.now()
  };
  await broadcastToMember(memberId, "recording_status_changed", payload);
}

// src/oneonone/utils/meetingStatusUtils.ts
function getMeetingStatus(isLeader, hasPrep, flags) {
  const {
    myRequested,
    myConfirmed,
    counterpartRequested,
    counterpartConfirmed
  } = flags;
  const myRequestSatisfied = !myRequested || counterpartConfirmed;
  const counterpartRequestSatisfied = !counterpartRequested || myConfirmed;
  const anyRequested = myRequested || counterpartRequested;
  if (anyRequested && myRequestSatisfied && counterpartRequestSatisfied) {
    return {
      key: "meeting_ready",
      tone: "ready",
      isMeetingReady: true
    };
  }
  if (counterpartRequested && !myConfirmed) {
    return {
      key: "question_confirm_requested",
      tone: "attention",
      isMeetingReady: false
    };
  }
  if (myRequested && !counterpartConfirmed) {
    return {
      key: "waiting_counterpart_confirm",
      tone: "neutral",
      isMeetingReady: false
    };
  }
  if (isLeader) {
    return hasPrep ? {
      key: "review_prep_and_create_questions",
      tone: "attention",
      isMeetingReady: false
    } : {
      key: "waiting_member_prep",
      tone: "neutral",
      isMeetingReady: false
    };
  }
  return hasPrep ? {
    key: "prep_completed",
    tone: "neutral",
    isMeetingReady: false
  } : {
    key: "write_prep",
    tone: "urgent",
    isMeetingReady: false
  };
}
function getMeetingStatusFromMeeting(meeting, isLeader, hasPrep) {
  const flags = {
    myRequested: isLeader ? meeting.leader_question_requested : meeting.member_question_requested,
    myConfirmed: isLeader ? meeting.leader_question_confirmed : meeting.member_question_confirmed,
    counterpartRequested: isLeader ? meeting.member_question_requested : meeting.leader_question_requested,
    counterpartConfirmed: isLeader ? meeting.member_question_confirmed : meeting.leader_question_confirmed
  };
  return getMeetingStatus(isLeader, hasPrep, flags);
}

// src/oneonone/utils/meetingAnalysisUtils.ts
var hasRecordingAnalysis = (meeting) => !!meeting.recording_duration && meeting.status_code === "COMPLETED" /* COMPLETED */ && !!meeting.analysis_status_code && meeting.analysis_status_code !== "PLANNED" /* PLANNED */;

// src/oneonone/utils/meetingPrepUtils.ts
import dayjs3 from "dayjs";
var resolveMeetingGuide = (guide) => {
  if (!guide) return { version: "none", guide: null };
  const flow = guide.flow;
  if (Array.isArray(flow)) {
    return { version: "structured", guide: { ...guide, flow } };
  }
  if (typeof flow === "string") {
    return { version: "transitional", guide: { ...guide, flow } };
  }
  return { version: "legacy", guide };
};
var isPrepGuideStageKey = (value) => PREP_GUIDE_STAGE_KEYS.includes(value);
var getPrepShareDeadline = (scheduledDate) => {
  if (!scheduledDate) return null;
  const daysLeft = dayjs3(scheduledDate).startOf("day").diff(dayjs3().startOf("day"), "day");
  if (daysLeft >= 2) return "far";
  if (daysLeft === 1) return "tomorrow";
  return "today";
};
var derivePrepChatCta = ({
  prepId,
  messages,
  isStreaming,
  sessionPrepStage,
  storedPrepStage
}) => ({
  prepStage: sessionPrepStage ?? storedPrepStage ?? null,
  canGenerate: Boolean(prepId) && messages.some((msg) => msg.role === "user") && messages[messages.length - 1]?.role === "assistant" && !isStreaming,
  userMessageCount: messages.filter((msg) => msg.role === "user").length
});
var hasNewChatSinceGenerate = ({
  generatedAtCount,
  userMessageCount
}) => generatedAtCount === null || userMessageCount > generatedAtCount;

// src/oneonone/utils/memberFeedbackUtils.ts
var isKnownLabel = (label) => MEMBER_FEEDBACK_LABELS.includes(label);
var isKnownSource = (source) => MEMBER_FEEDBACK_SOURCES.includes(source);
var getMemberFeedbackLabelKey = (label) => `ai_analysis.member_feedback.label.${isKnownLabel(label) ? label : "unknown"}`;
var getMemberFeedbackSourceKey = (source) => `ai_analysis.member_feedback.source.${isKnownSource(source) ? source : "unknown"}`;

// src/oneonone/utils/repeatNudgeCooldown.ts
var REPEAT_NUDGE_COOLDOWN_POLICY = {
  /** 이 횟수만큼 연속으로 '나중에'를 누르면 쿨다운 시작 */
  dismissThreshold: 2,
  /** 쿨다운 기간(일). 여러 멤버와 며칠 사이에 몰아서 하는 미팅 동안 넛지가 연달아 뜨는 걸 막는다. */
  cooldownDays: 14
};
var INITIAL_REPEAT_NUDGE_COOLDOWN_STATE = {
  dismissStreak: 0,
  cooldownUntil: null
};
function parseRepeatNudgeCooldownState(raw) {
  if (!raw) return INITIAL_REPEAT_NUDGE_COOLDOWN_STATE;
  try {
    const parsed = JSON.parse(raw);
    return {
      dismissStreak: typeof parsed.dismissStreak === "number" && parsed.dismissStreak >= 0 ? parsed.dismissStreak : 0,
      cooldownUntil: typeof parsed.cooldownUntil === "string" ? parsed.cooldownUntil : null
    };
  } catch {
    return INITIAL_REPEAT_NUDGE_COOLDOWN_STATE;
  }
}
function isRepeatNudgeSuppressed(state, now = /* @__PURE__ */ new Date()) {
  if (!state.cooldownUntil) return false;
  const until = new Date(state.cooldownUntil).getTime();
  return Number.isFinite(until) && until > now.getTime();
}
function recordRepeatNudgeDismiss(state, now = /* @__PURE__ */ new Date()) {
  const dismissStreak = state.dismissStreak + 1;
  if (dismissStreak < REPEAT_NUDGE_COOLDOWN_POLICY.dismissThreshold) {
    return { dismissStreak, cooldownUntil: state.cooldownUntil };
  }
  const until = new Date(now);
  until.setDate(until.getDate() + REPEAT_NUDGE_COOLDOWN_POLICY.cooldownDays);
  return { dismissStreak, cooldownUntil: until.toISOString() };
}
function recordRepeatNudgeConfirm() {
  return INITIAL_REPEAT_NUDGE_COOLDOWN_STATE;
}

// src/oneonone/utils/meetingFrequencyLabel.ts
var MEETING_FREQUENCY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];
var DEFAULT_FREQUENCY_WEEKS = 2;
var FREQUENCY_KEY_BY_WEEKS = {
  1: "oneonone:frequency.weekly",
  2: "oneonone:frequency.biweekly",
  3: "oneonone:frequency.every_3_weeks",
  4: "oneonone:frequency.monthly",
  5: "oneonone:frequency.every_5_weeks",
  6: "oneonone:frequency.every_6_weeks",
  7: "oneonone:frequency.every_7_weeks",
  8: "oneonone:frequency.every_8_weeks"
};
function getMeetingFrequencyLabel(weeks, t) {
  if (weeks === null) return t("oneonone:frequency.no_schedule");
  const key = FREQUENCY_KEY_BY_WEEKS[weeks];
  return key ? t(key) : `Every ${weeks} weeks`;
}
function getMeetingFrequencyOptions(t) {
  return MEETING_FREQUENCY_OPTIONS.map((weeks) => ({
    value: String(weeks),
    label: getMeetingFrequencyLabel(weeks, t)
  }));
}

// src/oneonone/hooks/useRepeatNudgeCooldown.ts
import { useCallback as useCallback2, useEffect as useEffect2, useMemo, useState as useState6 } from "react";
var isPromise2 = (value) => typeof value?.then === "function";
function useRepeatNudgeCooldown(userId, storage) {
  const [state, setState] = useState6(() => {
    if (!userId) return INITIAL_REPEAT_NUDGE_COOLDOWN_STATE;
    const loaded = storage.read(userId);
    return isPromise2(loaded) ? INITIAL_REPEAT_NUDGE_COOLDOWN_STATE : loaded;
  });
  useEffect2(() => {
    if (!userId) {
      setState(INITIAL_REPEAT_NUDGE_COOLDOWN_STATE);
      return;
    }
    const loaded = storage.read(userId);
    if (!isPromise2(loaded)) {
      setState(loaded);
      return;
    }
    let cancelled = false;
    void loaded.then((resolved) => {
      if (!cancelled) setState(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, storage]);
  const persist = useCallback2(
    (next) => {
      setState(next);
      if (userId) void storage.write(userId, next);
    },
    [userId, storage]
  );
  return useMemo(
    () => ({
      state,
      isSuppressed: () => isRepeatNudgeSuppressed(state),
      recordDismiss: () => persist(recordRepeatNudgeDismiss(state)),
      recordConfirm: () => persist(recordRepeatNudgeConfirm())
    }),
    [state, persist]
  );
}

// src/subscription/useSubscriptionStatus.ts
import { useCallback as useCallback3, useEffect as useEffect3, useState as useState7 } from "react";
var useSubscriptionStatus = (workspaceId, accessContext) => {
  const [status, setStatus] = useState7("none");
  const [isLoading, setIsLoading] = useState7(true);
  const [trialEndDate, setTrialEndDate] = useState7(null);
  const [daysRemaining, setDaysRemaining] = useState7(null);
  const checkSubscriptionStatus = useCallback3(async () => {
    if (!workspaceId) {
      setStatus("none");
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const {
        data: { user }
      } = await supabase.auth.getUser();
      if (!user) {
        setStatus("none");
        setIsLoading(false);
        return;
      }
      const { data: subscription, error: subError } = await supabase.from("workspace_subscription").select("*").eq("workspace_id", workspaceId).in("status", [...ACTIVE_SUBSCRIPTION_STATUSES]).maybeSingle();
      if (subError) throw subError;
      const { data: workspaceData } = await supabase.from("workspace").select("trial_days").eq("id", workspaceId).single();
      const wsData = workspaceData;
      if (subscription) {
        setStatus("active");
        setTrialEndDate(null);
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }
      const trialDays = wsData?.trial_days;
      if (!trialDays) {
        setStatus("none");
        setTrialEndDate(null);
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }
      const { data: oneOnOneMembers, error: memberError } = await supabase.from("one_on_one_member").select("register_date, workspace_member!inner(workspace_id)").eq("enable", true).eq("workspace_member.workspace_id", workspaceId).order("register_date", { ascending: true }).limit(1);
      if (memberError) throw memberError;
      if (!oneOnOneMembers || oneOnOneMembers.length === 0) {
        setStatus("none");
        setTrialEndDate(null);
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }
      const KST_OFFSET_MS = 9 * 60 * 60 * 1e3;
      const MS_PER_DAY = 24 * 60 * 60 * 1e3;
      const toKstDayIndex = (ms) => Math.floor((ms + KST_OFFSET_MS) / MS_PER_DAY);
      const registerDayKst = toKstDayIndex(
        new Date(oneOnOneMembers[0].register_date).getTime()
      );
      const trialEndDayKst = registerDayKst + trialDays;
      const todayKst = toKstDayIndex(Date.now());
      const remaining = trialEndDayKst - todayKst;
      const trialEnd = new Date(
        trialEndDayKst * MS_PER_DAY - KST_OFFSET_MS + 12 * 60 * 60 * 1e3
      );
      setTrialEndDate(trialEnd);
      setDaysRemaining(remaining);
      if (remaining >= 0) {
        setStatus("trial");
      } else {
        setStatus("expired");
      }
    } catch (error) {
      console.error("Error checking subscription status:", error);
      setStatus("none");
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);
  useEffect3(() => {
    void checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);
  const isSubscribed = status === "trial" || status === "active";
  const hasTeamMember = accessContext?.hasTeamMember ?? false;
  const balance = accessContext?.balance ?? 0;
  const recordingUsesCredits = !isSubscribed;
  const canUseFeature = useCallback3(
    (feature) => {
      if (isSubscribed) return true;
      if (feature === "recording") return balance > 0;
      return hasTeamMember && balance > 0 && TEAM_UNLOCKED_FEATURES.has(feature);
    },
    [isSubscribed, hasTeamMember, balance]
  );
  return {
    status,
    isSubscribed,
    recordingUsesCredits,
    canUseFeature,
    balance,
    isLoading,
    trialEndDate,
    daysRemaining,
    refetch: checkSubscriptionStatus
  };
};

// src/credit/creditService.ts
var creditService = {
  /**
   * 워크스페이스 잔여 크레딧 조회 (게이트 1: 녹음 시작 전 사전 차단 용도)
   * balance = credit_ledger SUM(delta)
   */
  async getBalance(workspaceId) {
    const response = await apiClient.get("credit/balance", { searchParams: { workspaceId } }).json();
    if (!response.success) {
      throw new Error("\uC794\uC5EC \uD06C\uB808\uB527 \uC870\uD68C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.");
    }
    return response.data;
  }
};
export {
  ACTIVE_SUBSCRIPTION_STATUSES,
  AI_API_ENDPOINTS,
  ANALYSIS_REQUEST_LOST_PREFIX,
  AccountMismatchError,
  ActionMenu,
  ActiveStatusCodes,
  ApiClientError,
  CONFIRMED_PROGRESS_VALUES,
  CareerOrientation,
  CommunicationStyle,
  CompleteStatusCodes,
  ConfirmationModal,
  Constants,
  DEFAULT_FREQUENCY_WEEKS,
  DELETED_USER_ID,
  DWELL_MEASUREMENT,
  DatePickerSelector,
  EditableRoles,
  FeedbackStyle,
  GOOGLE_ALL_SCOPES,
  GOOGLE_CALENDAR_PROVIDER,
  GOOGLE_CALENDAR_SCOPES,
  GOOGLE_MEET_RECORDING_SCOPES,
  GOOGLE_SCOPES,
  INITIAL_REPEAT_NUDGE_COOLDOWN_STATE,
  InvitationStatusCode,
  LEGACY_MEETING_GUIDE_FIELDS,
  LanguageSelector,
  MEETING_FREQUENCY_OPTIONS,
  MEMBERSHIP_FEATURES,
  MEMBER_FEEDBACK_LABELS,
  MEMBER_FEEDBACK_SOURCES,
  MeetingTopic,
  MemberBadgeColor,
  MemberRole,
  Motivation,
  NetworkError,
  ONE_ON_ONE_RECORDINGS_BUCKET,
  PAYMENT_ERROR_CODE,
  PREP_GUIDE_STAGE_KEYS,
  PREP_SUMMARY_MIN_CONTENT_LENGTH,
  PREP_SURVEY_FIELD_ORDER,
  PREP_SURVEY_OPTIONS,
  PROFILE_CARD_EMOJI,
  PROFILE_CARD_FIELD_ORDER,
  PROFILE_CARD_OPTIONS,
  PROGRESS_VERDICT_KEYS,
  PURPOSE_KEYS,
  ProfileImageUploader,
  QuestionSourceType,
  RATING_VALUES,
  REALTIME_DEBOUNCE_DELAY_MS,
  REALTIME_PRESENCE_THROTTLE_MS,
  RECORDING_IMPORT_STATUS,
  REPEAT_NUDGE_COOLDOWN_POLICY,
  SIGNUP_INQUIRY_SITUATIONS,
  StatusCode,
  StatusCodeLabel,
  SubscriptionStatus,
  TEAM_UNLOCKED_FEATURES,
  TERMINAL_CONFIRMED_PROGRESS,
  TOPIC_STATS_WINDOW_DAYS,
  UserAvatar,
  UserAvatarGroup,
  WarningComponent,
  WorkCondition,
  actionItemOverviewService,
  aiProfileService,
  analysisSettingService,
  apiClient,
  applyBrowserLanguageIfUnset,
  applyWorkspaceMembershipFilter,
  applyWorkspaceMembershipFilterBatch,
  arraysEqual,
  broadcastMeetingStatusChanged,
  broadcastRecordingStatusChanged,
  calculateDday,
  calculateDurationSeconds,
  canManageTargetMember,
  canSeeAnalysis,
  chattingService,
  checkIsAfterToday,
  checkSessionExpiry,
  configureEventTracking,
  configureRecordingLogger,
  createApiClient,
  createBaseAuthService,
  createBaseAuthStore,
  createBaseMemberInvitationService,
  createBaseWorkspaceService,
  createBaseWorkspaceStore,
  createDeletedUser,
  createGoogleCalendarService,
  createOneOnOneEmailService,
  createOneOnOneMeetingService,
  createOneOnOneMemberService,
  createProfileCardService,
  createRatingOptions,
  createUseCurrentWorkspace,
  creditService,
  defaultPack,
  deleteFile,
  derivePrepChatCta,
  detectBrowserLanguage,
  detectBrowserTimezone,
  detectNetworkError,
  eventTrackingService,
  extractPathFromUrl,
  fetchSSE,
  filterUserByWorkspaceMembership,
  filterUsersByWorkspaceMembership,
  findAllSubordinates,
  findUserBaseFields,
  formatDate,
  formatDateWithRelative,
  formatMeetingDateTime,
  formatTimeAgo,
  generateUniqueFileName,
  getAdvertisingConsentUrl,
  getAgreements,
  getApiClient,
  getDateFnsLocale,
  getDateLocale,
  getDisplayName,
  getEnabledWorkspaceMemberIds,
  getEnabledWorkspaceMemberIdsBatch,
  getEndOfDay,
  getEndOfMonth,
  getFullLanguageName,
  getGlobalMeetingChannelName,
  getMarketingConsentUrl,
  getMeetingFrequencyLabel,
  getMeetingFrequencyOptions,
  getMeetingStatus,
  getMeetingStatusFromMeeting,
  getMemberFeedbackLabelKey,
  getMemberFeedbackSourceKey,
  getOverseasTransferUrl,
  getPersonalInfoCollectionUrl,
  getPrepShareDeadline,
  getPrivacyPolicyUrl,
  getProfileImageUrl,
  getRatingColor,
  getRatingLabel,
  getRecordingConsentUrl,
  getRelativeDateText,
  getServiceTermsUrl,
  getStartOfDay,
  getThirdPartyProvisionUrl,
  getUserFromWorkspace,
  getUserRole,
  getWeekRange,
  hasAdminPermission,
  hasEditPermission,
  hasManagerPermission,
  hasNewChatSinceGenerate,
  hasRecordingAnalysis,
  hasRequiredScopes,
  i18n,
  isAfterDate,
  isArray,
  isBeforeDate,
  isBoolean,
  isDevelopment,
  isError,
  isNonEmptyArray,
  isNonEmptyString,
  isNotNull,
  isNumber,
  isObject,
  isOneOnOneNotificationType,
  isPersonalEmailDomain,
  isPositiveNumber,
  isPrepGuideStageKey,
  isPromise,
  isRepeatNudgeSuppressed,
  isSameDate,
  isString,
  isTerminalConfirmedProgress,
  isUser,
  isUserActive,
  isUserBase2 as isUserBase,
  isValidDateString,
  isValidEmail,
  isValidId,
  localizeSystemTemplate,
  localizeTemplates,
  logCacheLimitExceeded,
  logEmptyChunk,
  logMemoryWarning,
  logRecorderError,
  logSizeMismatch,
  logSubscriptionError,
  logTrackEnded,
  logUploadFailed,
  meetingPrepService,
  minutesToSeconds,
  oneOnOneAIService,
  oneOnOneActionItemProgressService,
  oneOnOneActionItemService,
  oneOnOneEmailService,
  oneOnOneMeetingService,
  oneOnOneMemberService,
  oneOnOneSurveyService,
  parseRepeatNudgeCooldownState,
  parseSSELine,
  processSSEStream,
  profileCardService,
  publicApiClient,
  questionDraftSettingService,
  recordRepeatNudgeConfirm,
  recordRepeatNudgeDismiss,
  resolveMeetingGuide,
  resolveNextMeetingNudgeData,
  resources,
  roleOptions,
  safeNavigate,
  secondsToHHMMSS,
  secondsToMMSS,
  secondsToMinutes,
  secondsToReadableTime,
  setApiClient,
  setNavigate,
  setSupabaseClient,
  signupInquiryService,
  stringToColor,
  supabase,
  tabFeedbackService,
  templateService,
  toSafeUserArray,
  toSupportedLanguage,
  uploadFile,
  useAuthStore,
  useAutoSessionCheck,
  useFileUpload,
  useRepeatNudgeCooldown,
  useSessionCheck,
  useSubscriptionStatus,
  withSafeUsers,
  withSafeUsersArray,
  withSessionCheck,
  workspaceCoreValueService
};
//# sourceMappingURL=index.mjs.map